/*!
 * jQuery JavaScript Library v3.6.0
 * https://jquery.com/
 *
 * Includes Sizzle.js
 * https://sizzlejs.com/
 *
 * Copyright OpenJS Foundation and other contributors
 * Released under the MIT license
 * https://jquery.org/license
 *
 * Date: 2021-03-02T17:08Z
 */
(function (global, factory) {
  'use strict';

  if (typeof module === 'object' && typeof module.exports === 'object') {
    // For CommonJS and CommonJS-like environments where a proper `window`
    // is present, execute the factory and get jQuery.
    // For environments that do not have a `window` with a `document`
    // (such as Node.js), expose a factory as module.exports.
    // This accentuates the need for the creation of a real `window`.
    // e.g. var jQuery = require("jquery")(window);
    // See ticket #14549 for more info.
    module.exports = global.document
      ? factory(global, true)
      : function (w) {
          if (!w.document) {
            throw new Error('jQuery requires a window with a document');
          }
          return factory(w);
        };
  } else {
    factory(global);
  }

  // Pass this if window is not defined yet
})(typeof window !== 'undefined' ? window : this, function (window, noGlobal) {
  // Edge <= 12 - 13+, Firefox <=18 - 45+, IE 10 - 11, Safari 5.1 - 9+, iOS 6 - 9.1
  // throw exceptions when non-strict code (e.g., ASP.NET 4.5) accesses strict mode
  // arguments.callee.caller (trac-13335). But as of jQuery 3.0 (2016), strict mode should be common
  // enough that all such attempts are guarded in a try block.
  'use strict';

  var arr = [];

  var getProto = Object.getPrototypeOf;

  var slice = arr.slice;

  var flat = arr.flat
    ? function (array) {
        return arr.flat.call(array);
      }
    : function (array) {
        return arr.concat.apply([], array);
      };

  var push = arr.push;

  var indexOf = arr.indexOf;

  var class2type = {};

  var toString = class2type.toString;

  var hasOwn = class2type.hasOwnProperty;

  var fnToString = hasOwn.toString;

  var ObjectFunctionString = fnToString.call(Object);

  var support = {};

  var isFunction = function isFunction(obj) {
    // Support: Chrome <=57, Firefox <=52
    // In some browsers, typeof returns "function" for HTML <object> elements
    // (i.e., `typeof document.createElement( "object" ) === "function"`).
    // We don't want to classify *any* DOM node as a function.
    // Support: QtWeb <=3.8.5, WebKit <=534.34, wkhtmltopdf tool <=0.12.5
    // Plus for old WebKit, typeof returns "function" for HTML collections
    // (e.g., `typeof document.getElementsByTagName("div") === "function"`). (gh-4756)
    return (
      typeof obj === 'function' &&
      typeof obj.nodeType !== 'number' &&
      typeof obj.item !== 'function'
    );
  };

  var isWindow = function isWindow(obj) {
    return obj != null && obj === obj.window;
  };

  var document = window.document;

  var preservedScriptAttributes = {
    type: true,
    src: true,
    nonce: true,
    noModule: true,
  };

  function DOMEval(code, node, doc) {
    doc = doc || document;

    var i,
      val,
      script = doc.createElement('script');

    script.text = code;
    if (node) {
      for (i in preservedScriptAttributes) {
        // Support: Firefox 64+, Edge 18+
        // Some browsers don't support the "nonce" property on scripts.
        // On the other hand, just using `getAttribute` is not enough as
        // the `nonce` attribute is reset to an empty string whenever it
        // becomes browsing-context connected.
        // See https://github.com/whatwg/html/issues/2369
        // See https://html.spec.whatwg.org/#nonce-attributes
        // The `node.getAttribute` check was added for the sake of
        // `jQuery.globalEval` so that it can fake a nonce-containing node
        // via an object.
        val = node[i] || (node.getAttribute && node.getAttribute(i));
        if (val) {
          script.setAttribute(i, val);
        }
      }
    }
    doc.head.appendChild(script).parentNode.removeChild(script);
  }

  function toType(obj) {
    if (obj == null) {
      return obj + '';
    }

    // Support: Android <=2.3 only (functionish RegExp)
    return typeof obj === 'object' || typeof obj === 'function'
      ? class2type[toString.call(obj)] || 'object'
      : typeof obj;
  }
  /* global Symbol */
  // Defining this global in .eslintrc.json would create a danger of using the global
  // unguarded in another place, it seems safer to define global only for this module

  var version = '3.6.0',
    // Define a local copy of jQuery
    jQuery = function (selector, context) {
      // The jQuery object is actually just the init constructor 'enhanced'
      // Need init if jQuery is called (just allow error to be thrown if not included)
      return new jQuery.fn.init(selector, context);
    };

  jQuery.fn = jQuery.prototype = {
    // The current version of jQuery being used
    jquery: version,

    constructor: jQuery,

    // The default length of a jQuery object is 0
    length: 0,

    toArray: function () {
      return slice.call(this);
    },

    // Get the Nth element in the matched element set OR
    // Get the whole matched element set as a clean array
    get: function (num) {
      // Return all the elements in a clean array
      if (num == null) {
        return slice.call(this);
      }

      // Return just the one element from the set
      return num < 0 ? this[num + this.length] : this[num];
    },

    // Take an array of elements and push it onto the stack
    // (returning the new matched element set)
    pushStack: function (elems) {
      // Build a new jQuery matched element set
      var ret = jQuery.merge(this.constructor(), elems);

      // Add the old object onto the stack (as a reference)
      ret.prevObject = this;

      // Return the newly-formed element set
      return ret;
    },

    // Execute a callback for every element in the matched set.
    each: function (callback) {
      return jQuery.each(this, callback);
    },

    map: function (callback) {
      return this.pushStack(
        jQuery.map(this, function (elem, i) {
          return callback.call(elem, i, elem);
        })
      );
    },

    slice: function () {
      return this.pushStack(slice.apply(this, arguments));
    },

    first: function () {
      return this.eq(0);
    },

    last: function () {
      return this.eq(-1);
    },

    even: function () {
      return this.pushStack(
        jQuery.grep(this, function (_elem, i) {
          return (i + 1) % 2;
        })
      );
    },

    odd: function () {
      return this.pushStack(
        jQuery.grep(this, function (_elem, i) {
          return i % 2;
        })
      );
    },

    eq: function (i) {
      var len = this.length,
        j = +i + (i < 0 ? len : 0);
      return this.pushStack(j >= 0 && j < len ? [this[j]] : []);
    },

    end: function () {
      return this.prevObject || this.constructor();
    },

    // For internal use only.
    // Behaves like an Array's method, not like a jQuery method.
    push: push,
    sort: arr.sort,
    splice: arr.splice,
  };

  jQuery.extend = jQuery.fn.extend = function () {
    var options,
      name,
      src,
      copy,
      copyIsArray,
      clone,
      target = arguments[0] || {},
      i = 1,
      length = arguments.length,
      deep = false;

    // Handle a deep copy situation
    if (typeof target === 'boolean') {
      deep = target;

      // Skip the boolean and the target
      target = arguments[i] || {};
      i++;
    }

    // Handle case when target is a string or something (possible in deep copy)
    if (typeof target !== 'object' && !isFunction(target)) {
      target = {};
    }

    // Extend jQuery itself if only one argument is passed
    if (i === length) {
      target = this;
      i--;
    }

    for (; i < length; i++) {
      // Only deal with non-null/undefined values
      if ((options = arguments[i]) != null) {
        // Extend the base object
        for (name in options) {
          copy = options[name];

          // Prevent Object.prototype pollution
          // Prevent never-ending loop
          if (name === '__proto__' || target === copy) {
            continue;
          }

          // Recurse if we're merging plain objects or arrays
          if (deep && copy && (jQuery.isPlainObject(copy) || (copyIsArray = Array.isArray(copy)))) {
            src = target[name];

            // Ensure proper type for the source value
            if (copyIsArray && !Array.isArray(src)) {
              clone = [];
            } else if (!copyIsArray && !jQuery.isPlainObject(src)) {
              clone = {};
            } else {
              clone = src;
            }
            copyIsArray = false;

            // Never move original objects, clone them
            target[name] = jQuery.extend(deep, clone, copy);

            // Don't bring in undefined values
          } else if (copy !== undefined) {
            target[name] = copy;
          }
        }
      }
    }

    // Return the modified object
    return target;
  };

  jQuery.extend({
    // Unique for each copy of jQuery on the page
    expando: 'jQuery' + (version + Math.random()).replace(/\D/g, ''),

    // Assume jQuery is ready without the ready module
    isReady: true,

    error: function (msg) {
      throw new Error(msg);
    },

    noop: function () {},

    isPlainObject: function (obj) {
      var proto, Ctor;

      // Detect obvious negatives
      // Use toString instead of jQuery.type to catch host objects
      if (!obj || toString.call(obj) !== '[object Object]') {
        return false;
      }

      proto = getProto(obj);

      // Objects with no prototype (e.g., `Object.create( null )`) are plain
      if (!proto) {
        return true;
      }

      // Objects with prototype are plain iff they were constructed by a global Object function
      Ctor = hasOwn.call(proto, 'constructor') && proto.constructor;
      return typeof Ctor === 'function' && fnToString.call(Ctor) === ObjectFunctionString;
    },

    isEmptyObject: function (obj) {
      var name;

      for (name in obj) {
        return false;
      }
      return true;
    },

    // Evaluates a script in a provided context; falls back to the global one
    // if not specified.
    globalEval: function (code, options, doc) {
      DOMEval(code, { nonce: options && options.nonce }, doc);
    },

    each: function (obj, callback) {
      var length,
        i = 0;

      if (isArrayLike(obj)) {
        length = obj.length;
        for (; i < length; i++) {
          if (callback.call(obj[i], i, obj[i]) === false) {
            break;
          }
        }
      } else {
        for (i in obj) {
          if (callback.call(obj[i], i, obj[i]) === false) {
            break;
          }
        }
      }

      return obj;
    },

    // results is for internal usage only
    makeArray: function (arr, results) {
      var ret = results || [];

      if (arr != null) {
        if (isArrayLike(Object(arr))) {
          jQuery.merge(ret, typeof arr === 'string' ? [arr] : arr);
        } else {
          push.call(ret, arr);
        }
      }

      return ret;
    },

    inArray: function (elem, arr, i) {
      return arr == null ? -1 : indexOf.call(arr, elem, i);
    },

    // Support: Android <=4.0 only, PhantomJS 1 only
    // push.apply(_, arraylike) throws on ancient WebKit
    merge: function (first, second) {
      var len = +second.length,
        j = 0,
        i = first.length;

      for (; j < len; j++) {
        first[i++] = second[j];
      }

      first.length = i;

      return first;
    },

    grep: function (elems, callback, invert) {
      var callbackInverse,
        matches = [],
        i = 0,
        length = elems.length,
        callbackExpect = !invert;

      // Go through the array, only saving the items
      // that pass the validator function
      for (; i < length; i++) {
        callbackInverse = !callback(elems[i], i);
        if (callbackInverse !== callbackExpect) {
          matches.push(elems[i]);
        }
      }

      return matches;
    },

    // arg is for internal usage only
    map: function (elems, callback, arg) {
      var length,
        value,
        i = 0,
        ret = [];

      // Go through the array, translating each of the items to their new values
      if (isArrayLike(elems)) {
        length = elems.length;
        for (; i < length; i++) {
          value = callback(elems[i], i, arg);

          if (value != null) {
            ret.push(value);
          }
        }

        // Go through every key on the object,
      } else {
        for (i in elems) {
          value = callback(elems[i], i, arg);

          if (value != null) {
            ret.push(value);
          }
        }
      }

      // Flatten any nested arrays
      return flat(ret);
    },

    // A global GUID counter for objects
    guid: 1,

    // jQuery.support is not used in Core but other projects attach their
    // properties to it so it needs to exist.
    support: support,
  });

  if (typeof Symbol === 'function') {
    jQuery.fn[Symbol.iterator] = arr[Symbol.iterator];
  }

  // Populate the class2type map
  jQuery.each(
    'Boolean Number String Function Array Date RegExp Object Error Symbol'.split(' '),
    function (_i, name) {
      class2type['[object ' + name + ']'] = name.toLowerCase();
    }
  );

  function isArrayLike(obj) {
    // Support: real iOS 8.2 only (not reproducible in simulator)
    // `in` check used to prevent JIT error (gh-2145)
    // hasOwn isn't used here due to false negatives
    // regarding Nodelist length in IE
    var length = !!obj && 'length' in obj && obj.length,
      type = toType(obj);

    if (isFunction(obj) || isWindow(obj)) {
      return false;
    }

    return (
      type === 'array' ||
      length === 0 ||
      (typeof length === 'number' && length > 0 && length - 1 in obj)
    );
  }
  var Sizzle =
    /*!
     * Sizzle CSS Selector Engine v2.3.6
     * https://sizzlejs.com/
     *
     * Copyright JS Foundation and other contributors
     * Released under the MIT license
     * https://js.foundation/
     *
     * Date: 2021-02-16
     */
    (function (window) {
      var i,
        support,
        Expr,
        getText,
        isXML,
        tokenize,
        compile,
        select,
        outermostContext,
        sortInput,
        hasDuplicate,
        // Local document vars
        setDocument,
        document,
        docElem,
        documentIsHTML,
        rbuggyQSA,
        rbuggyMatches,
        matches,
        contains,
        // Instance-specific data
        expando = 'sizzle' + 1 * new Date(),
        preferredDoc = window.document,
        dirruns = 0,
        done = 0,
        classCache = createCache(),
        tokenCache = createCache(),
        compilerCache = createCache(),
        nonnativeSelectorCache = createCache(),
        sortOrder = function (a, b) {
          if (a === b) {
            hasDuplicate = true;
          }
          return 0;
        },
        // Instance methods
        hasOwn = {}.hasOwnProperty,
        arr = [],
        pop = arr.pop,
        pushNative = arr.push,
        push = arr.push,
        slice = arr.slice,
        // Use a stripped-down indexOf as it's faster than native
        // https://jsperf.com/thor-indexof-vs-for/5
        indexOf = function (list, elem) {
          var i = 0,
            len = list.length;
          for (; i < len; i++) {
            if (list[i] === elem) {
              return i;
            }
          }
          return -1;
        },
        booleans =
          'checked|selected|async|autofocus|autoplay|controls|defer|disabled|hidden|' +
          'ismap|loop|multiple|open|readonly|required|scoped',
        // Regular expressions

        // http://www.w3.org/TR/css3-selectors/#whitespace
        whitespace = '[\\x20\\t\\r\\n\\f]',
        // https://www.w3.org/TR/css-syntax-3/#ident-token-diagram
        identifier =
          '(?:\\\\[\\da-fA-F]{1,6}' + whitespace + '?|\\\\[^\\r\\n\\f]|[\\w-]|[^\0-\\x7f])+',
        // Attribute selectors: http://www.w3.org/TR/selectors/#attribute-selectors
        attributes =
          '\\[' +
          whitespace +
          '*(' +
          identifier +
          ')(?:' +
          whitespace +
          // Operator (capture 2)
          '*([*^$|!~]?=)' +
          whitespace +
          // "Attribute values must be CSS identifiers [capture 5]
          // or strings [capture 3 or capture 4]"
          '*(?:\'((?:\\\\.|[^\\\\\'])*)\'|"((?:\\\\.|[^\\\\"])*)"|(' +
          identifier +
          '))|)' +
          whitespace +
          '*\\]',
        pseudos =
          ':(' +
          identifier +
          ')(?:\\((' +
          // To reduce the number of selectors needing tokenize in the preFilter, prefer arguments:
          // 1. quoted (capture 3; capture 4 or capture 5)
          '(\'((?:\\\\.|[^\\\\\'])*)\'|"((?:\\\\.|[^\\\\"])*)")|' +
          // 2. simple (capture 6)
          '((?:\\\\.|[^\\\\()[\\]]|' +
          attributes +
          ')*)|' +
          // 3. anything else (capture 2)
          '.*' +
          ')\\)|)',
        // Leading and non-escaped trailing whitespace, capturing some non-whitespace characters preceding the latter
        rwhitespace = new RegExp(whitespace + '+', 'g'),
        rtrim = new RegExp(
          '^' + whitespace + '+|((?:^|[^\\\\])(?:\\\\.)*)' + whitespace + '+$',
          'g'
        ),
        rcomma = new RegExp('^' + whitespace + '*,' + whitespace + '*'),
        rcombinators = new RegExp(
          '^' + whitespace + '*([>+~]|' + whitespace + ')' + whitespace + '*'
        ),
        rdescend = new RegExp(whitespace + '|>'),
        rpseudo = new RegExp(pseudos),
        ridentifier = new RegExp('^' + identifier + '$'),
        matchExpr = {
          ID: new RegExp('^#(' + identifier + ')'),
          CLASS: new RegExp('^\\.(' + identifier + ')'),
          TAG: new RegExp('^(' + identifier + '|[*])'),
          ATTR: new RegExp('^' + attributes),
          PSEUDO: new RegExp('^' + pseudos),
          CHILD: new RegExp(
            '^:(only|first|last|nth|nth-last)-(child|of-type)(?:\\(' +
              whitespace +
              '*(even|odd|(([+-]|)(\\d*)n|)' +
              whitespace +
              '*(?:([+-]|)' +
              whitespace +
              '*(\\d+)|))' +
              whitespace +
              '*\\)|)',
            'i'
          ),
          bool: new RegExp('^(?:' + booleans + ')$', 'i'),

          // For use in libraries implementing .is()
          // We use this for POS matching in `select`
          needsContext: new RegExp(
            '^' +
              whitespace +
              '*[>+~]|:(even|odd|eq|gt|lt|nth|first|last)(?:\\(' +
              whitespace +
              '*((?:-\\d)?\\d*)' +
              whitespace +
              '*\\)|)(?=[^-]|$)',
            'i'
          ),
        },
        rhtml = /HTML$/i,
        rinputs = /^(?:input|select|textarea|button)$/i,
        rheader = /^h\d$/i,
        rnative = /^[^{]+\{\s*\[native \w/,
        // Easily-parseable/retrievable ID or TAG or CLASS selectors
        rquickExpr = /^(?:#([\w-]+)|(\w+)|\.([\w-]+))$/,
        rsibling = /[+~]/,
        // CSS escapes
        // http://www.w3.org/TR/CSS21/syndata.html#escaped-characters
        runescape = new RegExp('\\\\[\\da-fA-F]{1,6}' + whitespace + '?|\\\\([^\\r\\n\\f])', 'g'),
        funescape = function (escape, nonHex) {
          var high = '0x' + escape.slice(1) - 0x10000;

          return nonHex
            ? // Strip the backslash prefix from a non-hex escape sequence
              nonHex
            : // Replace a hexadecimal escape sequence with the encoded Unicode code point
            // Support: IE <=11+
            // For values outside the Basic Multilingual Plane (BMP), manually construct a
            // surrogate pair
            high < 0
            ? String.fromCharCode(high + 0x10000)
            : String.fromCharCode((high >> 10) | 0xd800, (high & 0x3ff) | 0xdc00);
        },
        // CSS string/identifier serialization
        // https://drafts.csswg.org/cssom/#common-serializing-idioms
        rcssescape = /([\0-\x1f\x7f]|^-?\d)|^-$|[^\0-\x1f\x7f-\uFFFF\w-]/g,
        fcssescape = function (ch, asCodePoint) {
          if (asCodePoint) {
            // U+0000 NULL becomes U+FFFD REPLACEMENT CHARACTER
            if (ch === '\0') {
              return '\uFFFD';
            }

            // Control characters and (dependent upon position) numbers get escaped as code points
            return ch.slice(0, -1) + '\\' + ch.charCodeAt(ch.length - 1).toString(16) + ' ';
          }

          // Other potentially-special ASCII characters get backslash-escaped
          return '\\' + ch;
        },
        // Used for iframes
        // See setDocument()
        // Removing the function wrapper causes a "Permission Denied"
        // error in IE
        unloadHandler = function () {
          setDocument();
        },
        inDisabledFieldset = addCombinator(
          function (elem) {
            return elem.disabled === true && elem.nodeName.toLowerCase() === 'fieldset';
          },
          { dir: 'parentNode', next: 'legend' }
        );

      // Optimize for push.apply( _, NodeList )
      try {
        push.apply((arr = slice.call(preferredDoc.childNodes)), preferredDoc.childNodes);

        // Support: Android<4.0
        // Detect silently failing push.apply
        // eslint-disable-next-line no-unused-expressions
        arr[preferredDoc.childNodes.length].nodeType;
      } catch (e) {
        push = {
          apply: arr.length
            ? // Leverage slice if possible
              function (target, els) {
                pushNative.apply(target, slice.call(els));
              }
            : // Support: IE<9
              // Otherwise append directly
              function (target, els) {
                var j = target.length,
                  i = 0;

                // Can't trust NodeList.length
                while ((target[j++] = els[i++])) {}
                target.length = j - 1;
              },
        };
      }

      function Sizzle(selector, context, results, seed) {
        var m,
          i,
          elem,
          nid,
          match,
          groups,
          newSelector,
          newContext = context && context.ownerDocument,
          // nodeType defaults to 9, since context defaults to document
          nodeType = context ? context.nodeType : 9;

        results = results || [];

        // Return early from calls with invalid selector or context
        if (
          typeof selector !== 'string' ||
          !selector ||
          (nodeType !== 1 && nodeType !== 9 && nodeType !== 11)
        ) {
          return results;
        }

        // Try to shortcut find operations (as opposed to filters) in HTML documents
        if (!seed) {
          setDocument(context);
          context = context || document;

          if (documentIsHTML) {
            // If the selector is sufficiently simple, try using a "get*By*" DOM method
            // (excepting DocumentFragment context, where the methods don't exist)
            if (nodeType !== 11 && (match = rquickExpr.exec(selector))) {
              // ID selector
              if ((m = match[1])) {
                // Document context
                if (nodeType === 9) {
                  if ((elem = context.getElementById(m))) {
                    // Support: IE, Opera, Webkit
                    // TODO: identify versions
                    // getElementById can match elements by name instead of ID
                    if (elem.id === m) {
                      results.push(elem);
                      return results;
                    }
                  } else {
                    return results;
                  }

                  // Element context
                } else {
                  // Support: IE, Opera, Webkit
                  // TODO: identify versions
                  // getElementById can match elements by name instead of ID
                  if (
                    newContext &&
                    (elem = newContext.getElementById(m)) &&
                    contains(context, elem) &&
                    elem.id === m
                  ) {
                    results.push(elem);
                    return results;
                  }
                }

                // Type selector
              } else if (match[2]) {
                push.apply(results, context.getElementsByTagName(selector));
                return results;

                // Class selector
              } else if (
                (m = match[3]) &&
                support.getElementsByClassName &&
                context.getElementsByClassName
              ) {
                push.apply(results, context.getElementsByClassName(m));
                return results;
              }
            }

            // Take advantage of querySelectorAll
            if (
              support.qsa &&
              !nonnativeSelectorCache[selector + ' '] &&
              (!rbuggyQSA || !rbuggyQSA.test(selector)) &&
              // Support: IE 8 only
              // Exclude object elements
              (nodeType !== 1 || context.nodeName.toLowerCase() !== 'object')
            ) {
              newSelector = selector;
              newContext = context;

              // qSA considers elements outside a scoping root when evaluating child or
              // descendant combinators, which is not what we want.
              // In such cases, we work around the behavior by prefixing every selector in the
              // list with an ID selector referencing the scope context.
              // The technique has to be used as well when a leading combinator is used
              // as such selectors are not recognized by querySelectorAll.
              // Thanks to Andrew Dupont for this technique.
              if (nodeType === 1 && (rdescend.test(selector) || rcombinators.test(selector))) {
                // Expand context for sibling selectors
                newContext =
                  (rsibling.test(selector) && testContext(context.parentNode)) || context;

                // We can use :scope instead of the ID hack if the browser
                // supports it & if we're not changing the context.
                if (newContext !== context || !support.scope) {
                  // Capture the context ID, setting it first if necessary
                  if ((nid = context.getAttribute('id'))) {
                    nid = nid.replace(rcssescape, fcssescape);
                  } else {
                    context.setAttribute('id', (nid = expando));
                  }
                }

                // Prefix every selector in the list
                groups = tokenize(selector);
                i = groups.length;
                while (i--) {
                  groups[i] = (nid ? '#' + nid : ':scope') + ' ' + toSelector(groups[i]);
                }
                newSelector = groups.join(',');
              }

              try {
                push.apply(results, newContext.querySelectorAll(newSelector));
                return results;
              } catch (qsaError) {
                nonnativeSelectorCache(selector, true);
              } finally {
                if (nid === expando) {
                  context.removeAttribute('id');
                }
              }
            }
          }
        }

        // All others
        return select(selector.replace(rtrim, '$1'), context, results, seed);
      }

      /**
       * Create key-value caches of limited size
       * @returns {function(string, object)} Returns the Object data after storing it on itself with
       *	property name the (space-suffixed) string and (if the cache is larger than Expr.cacheLength)
       *	deleting the oldest entry
       */
      function createCache() {
        var keys = [];

        function cache(key, value) {
          // Use (key + " ") to avoid collision with native prototype properties (see Issue #157)
          if (keys.push(key + ' ') > Expr.cacheLength) {
            // Only keep the most recent entries
            delete cache[keys.shift()];
          }
          return (cache[key + ' '] = value);
        }
        return cache;
      }

      /**
       * Mark a function for special use by Sizzle
       * @param {Function} fn The function to mark
       */
      function markFunction(fn) {
        fn[expando] = true;
        return fn;
      }

      /**
       * Support testing using an element
       * @param {Function} fn Passed the created element and returns a boolean result
       */
      function assert(fn) {
        var el = document.createElement('fieldset');

        try {
          return !!fn(el);
        } catch (e) {
          return false;
        } finally {
          // Remove from its parent by default
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }

          // release memory in IE
          el = null;
        }
      }

      /**
       * Adds the same handler for all of the specified attrs
       * @param {String} attrs Pipe-separated list of attributes
       * @param {Function} handler The method that will be applied
       */
      function addHandle(attrs, handler) {
        var arr = attrs.split('|'),
          i = arr.length;

        while (i--) {
          Expr.attrHandle[arr[i]] = handler;
        }
      }

      /**
       * Checks document order of two siblings
       * @param {Element} a
       * @param {Element} b
       * @returns {Number} Returns less than 0 if a precedes b, greater than 0 if a follows b
       */
      function siblingCheck(a, b) {
        var cur = b && a,
          diff = cur && a.nodeType === 1 && b.nodeType === 1 && a.sourceIndex - b.sourceIndex;

        // Use IE sourceIndex if available on both nodes
        if (diff) {
          return diff;
        }

        // Check if b follows a
        if (cur) {
          while ((cur = cur.nextSibling)) {
            if (cur === b) {
              return -1;
            }
          }
        }

        return a ? 1 : -1;
      }

      /**
       * Returns a function to use in pseudos for input types
       * @param {String} type
       */
      function createInputPseudo(type) {
        return function (elem) {
          var name = elem.nodeName.toLowerCase();
          return name === 'input' && elem.type === type;
        };
      }

      /**
       * Returns a function to use in pseudos for buttons
       * @param {String} type
       */
      function createButtonPseudo(type) {
        return function (elem) {
          var name = elem.nodeName.toLowerCase();
          return (name === 'input' || name === 'button') && elem.type === type;
        };
      }

      /**
       * Returns a function to use in pseudos for :enabled/:disabled
       * @param {Boolean} disabled true for :disabled; false for :enabled
       */
      function createDisabledPseudo(disabled) {
        // Known :disabled false positives: fieldset[disabled] > legend:nth-of-type(n+2) :can-disable
        return function (elem) {
          // Only certain elements can match :enabled or :disabled
          // https://html.spec.whatwg.org/multipage/scripting.html#selector-enabled
          // https://html.spec.whatwg.org/multipage/scripting.html#selector-disabled
          if ('form' in elem) {
            // Check for inherited disabledness on relevant non-disabled elements:
            // * listed form-associated elements in a disabled fieldset
            //   https://html.spec.whatwg.org/multipage/forms.html#category-listed
            //   https://html.spec.whatwg.org/multipage/forms.html#concept-fe-disabled
            // * option elements in a disabled optgroup
            //   https://html.spec.whatwg.org/multipage/forms.html#concept-option-disabled
            // All such elements have a "form" property.
            if (elem.parentNode && elem.disabled === false) {
              // Option elements defer to a parent optgroup if present
              if ('label' in elem) {
                if ('label' in elem.parentNode) {
                  return elem.parentNode.disabled === disabled;
                } else {
                  return elem.disabled === disabled;
                }
              }

              // Support: IE 6 - 11
              // Use the isDisabled shortcut property to check for disabled fieldset ancestors
              return (
                elem.isDisabled === disabled ||
                // Where there is no isDisabled, check manually
                /* jshint -W018 */
                (elem.isDisabled !== !disabled && inDisabledFieldset(elem) === disabled)
              );
            }

            return elem.disabled === disabled;

            // Try to winnow out elements that can't be disabled before trusting the disabled property.
            // Some victims get caught in our net (label, legend, menu, track), but it shouldn't
            // even exist on them, let alone have a boolean value.
          } else if ('label' in elem) {
            return elem.disabled === disabled;
          }

          // Remaining elements are neither :enabled nor :disabled
          return false;
        };
      }

      /**
       * Returns a function to use in pseudos for positionals
       * @param {Function} fn
       */
      function createPositionalPseudo(fn) {
        return markFunction(function (argument) {
          argument = +argument;
          return markFunction(function (seed, matches) {
            var j,
              matchIndexes = fn([], seed.length, argument),
              i = matchIndexes.length;

            // Match elements found at the specified indexes
            while (i--) {
              if (seed[(j = matchIndexes[i])]) {
                seed[j] = !(matches[j] = seed[j]);
              }
            }
          });
        });
      }

      /**
       * Checks a node for validity as a Sizzle context
       * @param {Element|Object=} context
       * @returns {Element|Object|Boolean} The input node if acceptable, otherwise a falsy value
       */
      function testContext(context) {
        return context && typeof context.getElementsByTagName !== 'undefined' && context;
      }

      // Expose support vars for convenience
      support = Sizzle.support = {};

      /**
       * Detects XML nodes
       * @param {Element|Object} elem An element or a document
       * @returns {Boolean} True iff elem is a non-HTML XML node
       */
      isXML = Sizzle.isXML = function (elem) {
        var namespace = elem && elem.namespaceURI,
          docElem = elem && (elem.ownerDocument || elem).documentElement;

        // Support: IE <=8
        // Assume HTML when documentElement doesn't yet exist, such as inside loading iframes
        // https://bugs.jquery.com/ticket/4833
        return !rhtml.test(namespace || (docElem && docElem.nodeName) || 'HTML');
      };

      /**
       * Sets document-related variables once based on the current document
       * @param {Element|Object} [doc] An element or document object to use to set the document
       * @returns {Object} Returns the current document
       */
      setDocument = Sizzle.setDocument = function (node) {
        var hasCompare,
          subWindow,
          doc = node ? node.ownerDocument || node : preferredDoc;

        // Return early if doc is invalid or already selected
        // Support: IE 11+, Edge 17 - 18+
        // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
        // two documents; shallow comparisons work.
        // eslint-disable-next-line eqeqeq
        if (doc == document || doc.nodeType !== 9 || !doc.documentElement) {
          return document;
        }

        // Update global variables
        document = doc;
        docElem = document.documentElement;
        documentIsHTML = !isXML(document);

        // Support: IE 9 - 11+, Edge 12 - 18+
        // Accessing iframe documents after unload throws "permission denied" errors (jQuery #13936)
        // Support: IE 11+, Edge 17 - 18+
        // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
        // two documents; shallow comparisons work.
        // eslint-disable-next-line eqeqeq
        if (
          preferredDoc != document &&
          (subWindow = document.defaultView) &&
          subWindow.top !== subWindow
        ) {
          // Support: IE 11, Edge
          if (subWindow.addEventListener) {
            subWindow.addEventListener('unload', unloadHandler, false);

            // Support: IE 9 - 10 only
          } else if (subWindow.attachEvent) {
            subWindow.attachEvent('onunload', unloadHandler);
          }
        }

        // Support: IE 8 - 11+, Edge 12 - 18+, Chrome <=16 - 25 only, Firefox <=3.6 - 31 only,
        // Safari 4 - 5 only, Opera <=11.6 - 12.x only
        // IE/Edge & older browsers don't support the :scope pseudo-class.
        // Support: Safari 6.0 only
        // Safari 6.0 supports :scope but it's an alias of :root there.
        support.scope = assert(function (el) {
          docElem.appendChild(el).appendChild(document.createElement('div'));
          return (
            typeof el.querySelectorAll !== 'undefined' &&
            !el.querySelectorAll(':scope fieldset div').length
          );
        });

        /* Attributes
	---------------------------------------------------------------------- */

        // Support: IE<8
        // Verify that getAttribute really returns attributes and not properties
        // (excepting IE8 booleans)
        support.attributes = assert(function (el) {
          el.className = 'i';
          return !el.getAttribute('className');
        });

        /* getElement(s)By*
	---------------------------------------------------------------------- */

        // Check if getElementsByTagName("*") returns only elements
        support.getElementsByTagName = assert(function (el) {
          el.appendChild(document.createComment(''));
          return !el.getElementsByTagName('*').length;
        });

        // Support: IE<9
        support.getElementsByClassName = rnative.test(document.getElementsByClassName);

        // Support: IE<10
        // Check if getElementById returns elements by name
        // The broken getElementById methods don't pick up programmatically-set names,
        // so use a roundabout getElementsByName test
        support.getById = assert(function (el) {
          docElem.appendChild(el).id = expando;
          return !document.getElementsByName || !document.getElementsByName(expando).length;
        });

        // ID filter and find
        if (support.getById) {
          Expr.filter['ID'] = function (id) {
            var attrId = id.replace(runescape, funescape);
            return function (elem) {
              return elem.getAttribute('id') === attrId;
            };
          };
          Expr.find['ID'] = function (id, context) {
            if (typeof context.getElementById !== 'undefined' && documentIsHTML) {
              var elem = context.getElementById(id);
              return elem ? [elem] : [];
            }
          };
        } else {
          Expr.filter['ID'] = function (id) {
            var attrId = id.replace(runescape, funescape);
            return function (elem) {
              var node =
                typeof elem.getAttributeNode !== 'undefined' && elem.getAttributeNode('id');
              return node && node.value === attrId;
            };
          };

          // Support: IE 6 - 7 only
          // getElementById is not reliable as a find shortcut
          Expr.find['ID'] = function (id, context) {
            if (typeof context.getElementById !== 'undefined' && documentIsHTML) {
              var node,
                i,
                elems,
                elem = context.getElementById(id);

              if (elem) {
                // Verify the id attribute
                node = elem.getAttributeNode('id');
                if (node && node.value === id) {
                  return [elem];
                }

                // Fall back on getElementsByName
                elems = context.getElementsByName(id);
                i = 0;
                while ((elem = elems[i++])) {
                  node = elem.getAttributeNode('id');
                  if (node && node.value === id) {
                    return [elem];
                  }
                }
              }

              return [];
            }
          };
        }

        // Tag
        Expr.find['TAG'] = support.getElementsByTagName
          ? function (tag, context) {
              if (typeof context.getElementsByTagName !== 'undefined') {
                return context.getElementsByTagName(tag);

                // DocumentFragment nodes don't have gEBTN
              } else if (support.qsa) {
                return context.querySelectorAll(tag);
              }
            }
          : function (tag, context) {
              var elem,
                tmp = [],
                i = 0,
                // By happy coincidence, a (broken) gEBTN appears on DocumentFragment nodes too
                results = context.getElementsByTagName(tag);

              // Filter out possible comments
              if (tag === '*') {
                while ((elem = results[i++])) {
                  if (elem.nodeType === 1) {
                    tmp.push(elem);
                  }
                }

                return tmp;
              }
              return results;
            };

        // Class
        Expr.find['CLASS'] =
          support.getElementsByClassName &&
          function (className, context) {
            if (typeof context.getElementsByClassName !== 'undefined' && documentIsHTML) {
              return context.getElementsByClassName(className);
            }
          };

        /* QSA/matchesSelector
	---------------------------------------------------------------------- */

        // QSA and matchesSelector support

        // matchesSelector(:active) reports false when true (IE9/Opera 11.5)
        rbuggyMatches = [];

        // qSa(:focus) reports false when true (Chrome 21)
        // We allow this because of a bug in IE8/9 that throws an error
        // whenever `document.activeElement` is accessed on an iframe
        // So, we allow :focus to pass through QSA all the time to avoid the IE error
        // See https://bugs.jquery.com/ticket/13378
        rbuggyQSA = [];

        if ((support.qsa = rnative.test(document.querySelectorAll))) {
          // Build QSA regex
          // Regex strategy adopted from Diego Perini
          assert(function (el) {
            var input;

            // Select is set to empty string on purpose
            // This is to test IE's treatment of not explicitly
            // setting a boolean content attribute,
            // since its presence should be enough
            // https://bugs.jquery.com/ticket/12359
            docElem.appendChild(el).innerHTML =
              "<a id='" +
              expando +
              "'></a>" +
              "<select id='" +
              expando +
              "-\r\\' msallowcapture=''>" +
              "<option selected=''></option></select>";

            // Support: IE8, Opera 11-12.16
            // Nothing should be selected when empty strings follow ^= or $= or *=
            // The test attribute must be unknown in Opera but "safe" for WinRT
            // https://msdn.microsoft.com/en-us/library/ie/hh465388.aspx#attribute_section
            if (el.querySelectorAll("[msallowcapture^='']").length) {
              rbuggyQSA.push('[*^$]=' + whitespace + '*(?:\'\'|"")');
            }

            // Support: IE8
            // Boolean attributes and "value" are not treated correctly
            if (!el.querySelectorAll('[selected]').length) {
              rbuggyQSA.push('\\[' + whitespace + '*(?:value|' + booleans + ')');
            }

            // Support: Chrome<29, Android<4.4, Safari<7.0+, iOS<7.0+, PhantomJS<1.9.8+
            if (!el.querySelectorAll('[id~=' + expando + '-]').length) {
              rbuggyQSA.push('~=');
            }

            // Support: IE 11+, Edge 15 - 18+
            // IE 11/Edge don't find elements on a `[name='']` query in some cases.
            // Adding a temporary attribute to the document before the selection works
            // around the issue.
            // Interestingly, IE 10 & older don't seem to have the issue.
            input = document.createElement('input');
            input.setAttribute('name', '');
            el.appendChild(input);
            if (!el.querySelectorAll("[name='']").length) {
              rbuggyQSA.push(
                '\\[' + whitespace + '*name' + whitespace + '*=' + whitespace + '*(?:\'\'|"")'
              );
            }

            // Webkit/Opera - :checked should return selected option elements
            // http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
            // IE8 throws error here and will not see later tests
            if (!el.querySelectorAll(':checked').length) {
              rbuggyQSA.push(':checked');
            }

            // Support: Safari 8+, iOS 8+
            // https://bugs.webkit.org/show_bug.cgi?id=136851
            // In-page `selector#id sibling-combinator selector` fails
            if (!el.querySelectorAll('a#' + expando + '+*').length) {
              rbuggyQSA.push('.#.+[+~]');
            }

            // Support: Firefox <=3.6 - 5 only
            // Old Firefox doesn't throw on a badly-escaped identifier.
            el.querySelectorAll('\\\f');
            rbuggyQSA.push('[\\r\\n\\f]');
          });

          assert(function (el) {
            el.innerHTML =
              "<a href='' disabled='disabled'></a>" +
              "<select disabled='disabled'><option/></select>";

            // Support: Windows 8 Native Apps
            // The type and name attributes are restricted during .innerHTML assignment
            var input = document.createElement('input');
            input.setAttribute('type', 'hidden');
            el.appendChild(input).setAttribute('name', 'D');

            // Support: IE8
            // Enforce case-sensitivity of name attribute
            if (el.querySelectorAll('[name=d]').length) {
              rbuggyQSA.push('name' + whitespace + '*[*^$|!~]?=');
            }

            // FF 3.5 - :enabled/:disabled and hidden elements (hidden elements are still enabled)
            // IE8 throws error here and will not see later tests
            if (el.querySelectorAll(':enabled').length !== 2) {
              rbuggyQSA.push(':enabled', ':disabled');
            }

            // Support: IE9-11+
            // IE's :disabled selector does not pick up the children of disabled fieldsets
            docElem.appendChild(el).disabled = true;
            if (el.querySelectorAll(':disabled').length !== 2) {
              rbuggyQSA.push(':enabled', ':disabled');
            }

            // Support: Opera 10 - 11 only
            // Opera 10-11 does not throw on post-comma invalid pseudos
            el.querySelectorAll('*,:x');
            rbuggyQSA.push(',.*:');
          });
        }

        if (
          (support.matchesSelector = rnative.test(
            (matches =
              docElem.matches ||
              docElem.webkitMatchesSelector ||
              docElem.mozMatchesSelector ||
              docElem.oMatchesSelector ||
              docElem.msMatchesSelector)
          ))
        ) {
          assert(function (el) {
            // Check to see if it's possible to do matchesSelector
            // on a disconnected node (IE 9)
            support.disconnectedMatch = matches.call(el, '*');

            // This should fail with an exception
            // Gecko does not error, returns false instead
            matches.call(el, "[s!='']:x");
            rbuggyMatches.push('!=', pseudos);
          });
        }

        rbuggyQSA = rbuggyQSA.length && new RegExp(rbuggyQSA.join('|'));
        rbuggyMatches = rbuggyMatches.length && new RegExp(rbuggyMatches.join('|'));

        /* Contains
	---------------------------------------------------------------------- */
        hasCompare = rnative.test(docElem.compareDocumentPosition);

        // Element contains another
        // Purposefully self-exclusive
        // As in, an element does not contain itself
        contains =
          hasCompare || rnative.test(docElem.contains)
            ? function (a, b) {
                var adown = a.nodeType === 9 ? a.documentElement : a,
                  bup = b && b.parentNode;
                return (
                  a === bup ||
                  !!(
                    bup &&
                    bup.nodeType === 1 &&
                    (adown.contains
                      ? adown.contains(bup)
                      : a.compareDocumentPosition && a.compareDocumentPosition(bup) & 16)
                  )
                );
              }
            : function (a, b) {
                if (b) {
                  while ((b = b.parentNode)) {
                    if (b === a) {
                      return true;
                    }
                  }
                }
                return false;
              };

        /* Sorting
	---------------------------------------------------------------------- */

        // Document order sorting
        sortOrder = hasCompare
          ? function (a, b) {
              // Flag for duplicate removal
              if (a === b) {
                hasDuplicate = true;
                return 0;
              }

              // Sort on method existence if only one input has compareDocumentPosition
              var compare = !a.compareDocumentPosition - !b.compareDocumentPosition;
              if (compare) {
                return compare;
              }

              // Calculate position if both inputs belong to the same document
              // Support: IE 11+, Edge 17 - 18+
              // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
              // two documents; shallow comparisons work.
              // eslint-disable-next-line eqeqeq
              compare =
                (a.ownerDocument || a) == (b.ownerDocument || b)
                  ? a.compareDocumentPosition(b)
                  : // Otherwise we know they are disconnected
                    1;

              // Disconnected nodes
              if (
                compare & 1 ||
                (!support.sortDetached && b.compareDocumentPosition(a) === compare)
              ) {
                // Choose the first element that is related to our preferred document
                // Support: IE 11+, Edge 17 - 18+
                // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
                // two documents; shallow comparisons work.
                // eslint-disable-next-line eqeqeq
                if (
                  a == document ||
                  (a.ownerDocument == preferredDoc && contains(preferredDoc, a))
                ) {
                  return -1;
                }

                // Support: IE 11+, Edge 17 - 18+
                // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
                // two documents; shallow comparisons work.
                // eslint-disable-next-line eqeqeq
                if (
                  b == document ||
                  (b.ownerDocument == preferredDoc && contains(preferredDoc, b))
                ) {
                  return 1;
                }

                // Maintain original order
                return sortInput ? indexOf(sortInput, a) - indexOf(sortInput, b) : 0;
              }

              return compare & 4 ? -1 : 1;
            }
          : function (a, b) {
              // Exit early if the nodes are identical
              if (a === b) {
                hasDuplicate = true;
                return 0;
              }

              var cur,
                i = 0,
                aup = a.parentNode,
                bup = b.parentNode,
                ap = [a],
                bp = [b];

              // Parentless nodes are either documents or disconnected
              if (!aup || !bup) {
                // Support: IE 11+, Edge 17 - 18+
                // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
                // two documents; shallow comparisons work.
                /* eslint-disable eqeqeq */
                return a == document
                  ? -1
                  : b == document
                  ? 1
                  : /* eslint-enable eqeqeq */
                  aup
                  ? -1
                  : bup
                  ? 1
                  : sortInput
                  ? indexOf(sortInput, a) - indexOf(sortInput, b)
                  : 0;

                // If the nodes are siblings, we can do a quick check
              } else if (aup === bup) {
                return siblingCheck(a, b);
              }

              // Otherwise we need full lists of their ancestors for comparison
              cur = a;
              while ((cur = cur.parentNode)) {
                ap.unshift(cur);
              }
              cur = b;
              while ((cur = cur.parentNode)) {
                bp.unshift(cur);
              }

              // Walk down the tree looking for a discrepancy
              while (ap[i] === bp[i]) {
                i++;
              }

              return i
                ? // Do a sibling check if the nodes have a common ancestor
                  siblingCheck(ap[i], bp[i])
                : // Otherwise nodes in our document sort first
                // Support: IE 11+, Edge 17 - 18+
                // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
                // two documents; shallow comparisons work.
                /* eslint-disable eqeqeq */
                ap[i] == preferredDoc
                ? -1
                : bp[i] == preferredDoc
                ? 1
                : /* eslint-enable eqeqeq */
                  0;
            };

        return document;
      };

      Sizzle.matches = function (expr, elements) {
        return Sizzle(expr, null, null, elements);
      };

      Sizzle.matchesSelector = function (elem, expr) {
        setDocument(elem);

        if (
          support.matchesSelector &&
          documentIsHTML &&
          !nonnativeSelectorCache[expr + ' '] &&
          (!rbuggyMatches || !rbuggyMatches.test(expr)) &&
          (!rbuggyQSA || !rbuggyQSA.test(expr))
        ) {
          try {
            var ret = matches.call(elem, expr);

            // IE 9's matchesSelector returns false on disconnected nodes
            if (
              ret ||
              support.disconnectedMatch ||
              // As well, disconnected nodes are said to be in a document
              // fragment in IE 9
              (elem.document && elem.document.nodeType !== 11)
            ) {
              return ret;
            }
          } catch (e) {
            nonnativeSelectorCache(expr, true);
          }
        }

        return Sizzle(expr, document, null, [elem]).length > 0;
      };

      Sizzle.contains = function (context, elem) {
        // Set document vars if needed
        // Support: IE 11+, Edge 17 - 18+
        // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
        // two documents; shallow comparisons work.
        // eslint-disable-next-line eqeqeq
        if ((context.ownerDocument || context) != document) {
          setDocument(context);
        }
        return contains(context, elem);
      };

      Sizzle.attr = function (elem, name) {
        // Set document vars if needed
        // Support: IE 11+, Edge 17 - 18+
        // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
        // two documents; shallow comparisons work.
        // eslint-disable-next-line eqeqeq
        if ((elem.ownerDocument || elem) != document) {
          setDocument(elem);
        }

        var fn = Expr.attrHandle[name.toLowerCase()],
          // Don't get fooled by Object.prototype properties (jQuery #13807)
          val =
            fn && hasOwn.call(Expr.attrHandle, name.toLowerCase())
              ? fn(elem, name, !documentIsHTML)
              : undefined;

        return val !== undefined
          ? val
          : support.attributes || !documentIsHTML
          ? elem.getAttribute(name)
          : (val = elem.getAttributeNode(name)) && val.specified
          ? val.value
          : null;
      };

      Sizzle.escape = function (sel) {
        return (sel + '').replace(rcssescape, fcssescape);
      };

      Sizzle.error = function (msg) {
        throw new Error('Syntax error, unrecognized expression: ' + msg);
      };

      /**
       * Document sorting and removing duplicates
       * @param {ArrayLike} results
       */
      Sizzle.uniqueSort = function (results) {
        var elem,
          duplicates = [],
          j = 0,
          i = 0;

        // Unless we *know* we can detect duplicates, assume their presence
        hasDuplicate = !support.detectDuplicates;
        sortInput = !support.sortStable && results.slice(0);
        results.sort(sortOrder);

        if (hasDuplicate) {
          while ((elem = results[i++])) {
            if (elem === results[i]) {
              j = duplicates.push(i);
            }
          }
          while (j--) {
            results.splice(duplicates[j], 1);
          }
        }

        // Clear input after sorting to release objects
        // See https://github.com/jquery/sizzle/pull/225
        sortInput = null;

        return results;
      };

      /**
       * Utility function for retrieving the text value of an array of DOM nodes
       * @param {Array|Element} elem
       */
      getText = Sizzle.getText = function (elem) {
        var node,
          ret = '',
          i = 0,
          nodeType = elem.nodeType;

        if (!nodeType) {
          // If no nodeType, this is expected to be an array
          while ((node = elem[i++])) {
            // Do not traverse comment nodes
            ret += getText(node);
          }
        } else if (nodeType === 1 || nodeType === 9 || nodeType === 11) {
          // Use textContent for elements
          // innerText usage removed for consistency of new lines (jQuery #11153)
          if (typeof elem.textContent === 'string') {
            return elem.textContent;
          } else {
            // Traverse its children
            for (elem = elem.firstChild; elem; elem = elem.nextSibling) {
              ret += getText(elem);
            }
          }
        } else if (nodeType === 3 || nodeType === 4) {
          return elem.nodeValue;
        }

        // Do not include comment or processing instruction nodes

        return ret;
      };

      Expr = Sizzle.selectors = {
        // Can be adjusted by the user
        cacheLength: 50,

        createPseudo: markFunction,

        match: matchExpr,

        attrHandle: {},

        find: {},

        relative: {
          '>': { dir: 'parentNode', first: true },
          ' ': { dir: 'parentNode' },
          '+': { dir: 'previousSibling', first: true },
          '~': { dir: 'previousSibling' },
        },

        preFilter: {
          ATTR: function (match) {
            match[1] = match[1].replace(runescape, funescape);

            // Move the given value to match[3] whether quoted or unquoted
            match[3] = (match[3] || match[4] || match[5] || '').replace(runescape, funescape);

            if (match[2] === '~=') {
              match[3] = ' ' + match[3] + ' ';
            }

            return match.slice(0, 4);
          },

          CHILD: function (match) {
            /* matches from matchExpr["CHILD"]
				1 type (only|nth|...)
				2 what (child|of-type)
				3 argument (even|odd|\d*|\d*n([+-]\d+)?|...)
				4 xn-component of xn+y argument ([+-]?\d*n|)
				5 sign of xn-component
				6 x of xn-component
				7 sign of y-component
				8 y of y-component
			*/
            match[1] = match[1].toLowerCase();

            if (match[1].slice(0, 3) === 'nth') {
              // nth-* requires argument
              if (!match[3]) {
                Sizzle.error(match[0]);
              }

              // numeric x and y parameters for Expr.filter.CHILD
              // remember that false/true cast respectively to 0/1
              match[4] = +(match[4]
                ? match[5] + (match[6] || 1)
                : 2 * (match[3] === 'even' || match[3] === 'odd'));
              match[5] = +(match[7] + match[8] || match[3] === 'odd');

              // other types prohibit arguments
            } else if (match[3]) {
              Sizzle.error(match[0]);
            }

            return match;
          },

          PSEUDO: function (match) {
            var excess,
              unquoted = !match[6] && match[2];

            if (matchExpr['CHILD'].test(match[0])) {
              return null;
            }

            // Accept quoted arguments as-is
            if (match[3]) {
              match[2] = match[4] || match[5] || '';

              // Strip excess characters from unquoted arguments
            } else if (
              unquoted &&
              rpseudo.test(unquoted) &&
              // Get excess from tokenize (recursively)
              (excess = tokenize(unquoted, true)) &&
              // advance to the next closing parenthesis
              (excess = unquoted.indexOf(')', unquoted.length - excess) - unquoted.length)
            ) {
              // excess is a negative index
              match[0] = match[0].slice(0, excess);
              match[2] = unquoted.slice(0, excess);
            }

            // Return only captures needed by the pseudo filter method (type and argument)
            return match.slice(0, 3);
          },
        },

        filter: {
          TAG: function (nodeNameSelector) {
            var nodeName = nodeNameSelector.replace(runescape, funescape).toLowerCase();
            return nodeNameSelector === '*'
              ? function () {
                  return true;
                }
              : function (elem) {
                  return elem.nodeName && elem.nodeName.toLowerCase() === nodeName;
                };
          },

          CLASS: function (className) {
            var pattern = classCache[className + ' '];

            return (
              pattern ||
              ((pattern = new RegExp(
                '(^|' + whitespace + ')' + className + '(' + whitespace + '|$)'
              )) &&
                classCache(className, function (elem) {
                  return pattern.test(
                    (typeof elem.className === 'string' && elem.className) ||
                      (typeof elem.getAttribute !== 'undefined' && elem.getAttribute('class')) ||
                      ''
                  );
                }))
            );
          },

          ATTR: function (name, operator, check) {
            return function (elem) {
              var result = Sizzle.attr(elem, name);

              if (result == null) {
                return operator === '!=';
              }
              if (!operator) {
                return true;
              }

              result += '';

              /* eslint-disable max-len */

              return operator === '='
                ? result === check
                : operator === '!='
                ? result !== check
                : operator === '^='
                ? check && result.indexOf(check) === 0
                : operator === '*='
                ? check && result.indexOf(check) > -1
                : operator === '$='
                ? check && result.slice(-check.length) === check
                : operator === '~='
                ? (' ' + result.replace(rwhitespace, ' ') + ' ').indexOf(check) > -1
                : operator === '|='
                ? result === check || result.slice(0, check.length + 1) === check + '-'
                : false;
              /* eslint-enable max-len */
            };
          },

          CHILD: function (type, what, _argument, first, last) {
            var simple = type.slice(0, 3) !== 'nth',
              forward = type.slice(-4) !== 'last',
              ofType = what === 'of-type';

            return first === 1 && last === 0
              ? // Shortcut for :nth-*(n)
                function (elem) {
                  return !!elem.parentNode;
                }
              : function (elem, _context, xml) {
                  var cache,
                    uniqueCache,
                    outerCache,
                    node,
                    nodeIndex,
                    start,
                    dir = simple !== forward ? 'nextSibling' : 'previousSibling',
                    parent = elem.parentNode,
                    name = ofType && elem.nodeName.toLowerCase(),
                    useCache = !xml && !ofType,
                    diff = false;

                  if (parent) {
                    // :(first|last|only)-(child|of-type)
                    if (simple) {
                      while (dir) {
                        node = elem;
                        while ((node = node[dir])) {
                          if (ofType ? node.nodeName.toLowerCase() === name : node.nodeType === 1) {
                            return false;
                          }
                        }

                        // Reverse direction for :only-* (if we haven't yet done so)
                        start = dir = type === 'only' && !start && 'nextSibling';
                      }
                      return true;
                    }

                    start = [forward ? parent.firstChild : parent.lastChild];

                    // non-xml :nth-child(...) stores cache data on `parent`
                    if (forward && useCache) {
                      // Seek `elem` from a previously-cached index

                      // ...in a gzip-friendly way
                      node = parent;
                      outerCache = node[expando] || (node[expando] = {});

                      // Support: IE <9 only
                      // Defend against cloned attroperties (jQuery gh-1709)
                      uniqueCache = outerCache[node.uniqueID] || (outerCache[node.uniqueID] = {});

                      cache = uniqueCache[type] || [];
                      nodeIndex = cache[0] === dirruns && cache[1];
                      diff = nodeIndex && cache[2];
                      node = nodeIndex && parent.childNodes[nodeIndex];

                      while (
                        (node =
                          (++nodeIndex && node && node[dir]) ||
                          // Fallback to seeking `elem` from the start
                          (diff = nodeIndex = 0) ||
                          start.pop())
                      ) {
                        // When found, cache indexes on `parent` and break
                        if (node.nodeType === 1 && ++diff && node === elem) {
                          uniqueCache[type] = [dirruns, nodeIndex, diff];
                          break;
                        }
                      }
                    } else {
                      // Use previously-cached element index if available
                      if (useCache) {
                        // ...in a gzip-friendly way
                        node = elem;
                        outerCache = node[expando] || (node[expando] = {});

                        // Support: IE <9 only
                        // Defend against cloned attroperties (jQuery gh-1709)
                        uniqueCache = outerCache[node.uniqueID] || (outerCache[node.uniqueID] = {});

                        cache = uniqueCache[type] || [];
                        nodeIndex = cache[0] === dirruns && cache[1];
                        diff = nodeIndex;
                      }

                      // xml :nth-child(...)
                      // or :nth-last-child(...) or :nth(-last)?-of-type(...)
                      if (diff === false) {
                        // Use the same loop as above to seek `elem` from the start
                        while (
                          (node =
                            (++nodeIndex && node && node[dir]) ||
                            (diff = nodeIndex = 0) ||
                            start.pop())
                        ) {
                          if (
                            (ofType ? node.nodeName.toLowerCase() === name : node.nodeType === 1) &&
                            ++diff
                          ) {
                            // Cache the index of each encountered element
                            if (useCache) {
                              outerCache = node[expando] || (node[expando] = {});

                              // Support: IE <9 only
                              // Defend against cloned attroperties (jQuery gh-1709)
                              uniqueCache =
                                outerCache[node.uniqueID] || (outerCache[node.uniqueID] = {});

                              uniqueCache[type] = [dirruns, diff];
                            }

                            if (node === elem) {
                              break;
                            }
                          }
                        }
                      }
                    }

                    // Incorporate the offset, then check against cycle size
                    diff -= last;
                    return diff === first || (diff % first === 0 && diff / first >= 0);
                  }
                };
          },

          PSEUDO: function (pseudo, argument) {
            // pseudo-class names are case-insensitive
            // http://www.w3.org/TR/selectors/#pseudo-classes
            // Prioritize by case sensitivity in case custom pseudos are added with uppercase letters
            // Remember that setFilters inherits from pseudos
            var args,
              fn =
                Expr.pseudos[pseudo] ||
                Expr.setFilters[pseudo.toLowerCase()] ||
                Sizzle.error('unsupported pseudo: ' + pseudo);

            // The user may use createPseudo to indicate that
            // arguments are needed to create the filter function
            // just as Sizzle does
            if (fn[expando]) {
              return fn(argument);
            }

            // But maintain support for old signatures
            if (fn.length > 1) {
              args = [pseudo, pseudo, '', argument];
              return Expr.setFilters.hasOwnProperty(pseudo.toLowerCase())
                ? markFunction(function (seed, matches) {
                    var idx,
                      matched = fn(seed, argument),
                      i = matched.length;
                    while (i--) {
                      idx = indexOf(seed, matched[i]);
                      seed[idx] = !(matches[idx] = matched[i]);
                    }
                  })
                : function (elem) {
                    return fn(elem, 0, args);
                  };
            }

            return fn;
          },
        },

        pseudos: {
          // Potentially complex pseudos
          not: markFunction(function (selector) {
            // Trim the selector passed to compile
            // to avoid treating leading and trailing
            // spaces as combinators
            var input = [],
              results = [],
              matcher = compile(selector.replace(rtrim, '$1'));

            return matcher[expando]
              ? markFunction(function (seed, matches, _context, xml) {
                  var elem,
                    unmatched = matcher(seed, null, xml, []),
                    i = seed.length;

                  // Match elements unmatched by `matcher`
                  while (i--) {
                    if ((elem = unmatched[i])) {
                      seed[i] = !(matches[i] = elem);
                    }
                  }
                })
              : function (elem, _context, xml) {
                  input[0] = elem;
                  matcher(input, null, xml, results);

                  // Don't keep the element (issue #299)
                  input[0] = null;
                  return !results.pop();
                };
          }),

          has: markFunction(function (selector) {
            return function (elem) {
              return Sizzle(selector, elem).length > 0;
            };
          }),

          contains: markFunction(function (text) {
            text = text.replace(runescape, funescape);
            return function (elem) {
              return (elem.textContent || getText(elem)).indexOf(text) > -1;
            };
          }),

          // "Whether an element is represented by a :lang() selector
          // is based solely on the element's language value
          // being equal to the identifier C,
          // or beginning with the identifier C immediately followed by "-".
          // The matching of C against the element's language value is performed case-insensitively.
          // The identifier C does not have to be a valid language name."
          // http://www.w3.org/TR/selectors/#lang-pseudo
          lang: markFunction(function (lang) {
            // lang value must be a valid identifier
            if (!ridentifier.test(lang || '')) {
              Sizzle.error('unsupported lang: ' + lang);
            }
            lang = lang.replace(runescape, funescape).toLowerCase();
            return function (elem) {
              var elemLang;
              do {
                if (
                  (elemLang = documentIsHTML
                    ? elem.lang
                    : elem.getAttribute('xml:lang') || elem.getAttribute('lang'))
                ) {
                  elemLang = elemLang.toLowerCase();
                  return elemLang === lang || elemLang.indexOf(lang + '-') === 0;
                }
              } while ((elem = elem.parentNode) && elem.nodeType === 1);
              return false;
            };
          }),

          // Miscellaneous
          target: function (elem) {
            var hash = window.location && window.location.hash;
            return hash && hash.slice(1) === elem.id;
          },

          root: function (elem) {
            return elem === docElem;
          },

          focus: function (elem) {
            return (
              elem === document.activeElement &&
              (!document.hasFocus || document.hasFocus()) &&
              !!(elem.type || elem.href || ~elem.tabIndex)
            );
          },

          // Boolean properties
          enabled: createDisabledPseudo(false),
          disabled: createDisabledPseudo(true),

          checked: function (elem) {
            // In CSS3, :checked should return both checked and selected elements
            // http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
            var nodeName = elem.nodeName.toLowerCase();
            return (
              (nodeName === 'input' && !!elem.checked) || (nodeName === 'option' && !!elem.selected)
            );
          },

          selected: function (elem) {
            // Accessing this property makes selected-by-default
            // options in Safari work properly
            if (elem.parentNode) {
              // eslint-disable-next-line no-unused-expressions
              elem.parentNode.selectedIndex;
            }

            return elem.selected === true;
          },

          // Contents
          empty: function (elem) {
            // http://www.w3.org/TR/selectors/#empty-pseudo
            // :empty is negated by element (1) or content nodes (text: 3; cdata: 4; entity ref: 5),
            //   but not by others (comment: 8; processing instruction: 7; etc.)
            // nodeType < 6 works because attributes (2) do not appear as children
            for (elem = elem.firstChild; elem; elem = elem.nextSibling) {
              if (elem.nodeType < 6) {
                return false;
              }
            }
            return true;
          },

          parent: function (elem) {
            return !Expr.pseudos['empty'](elem);
          },

          // Element/input types
          header: function (elem) {
            return rheader.test(elem.nodeName);
          },

          input: function (elem) {
            return rinputs.test(elem.nodeName);
          },

          button: function (elem) {
            var name = elem.nodeName.toLowerCase();
            return (name === 'input' && elem.type === 'button') || name === 'button';
          },

          text: function (elem) {
            var attr;
            return (
              elem.nodeName.toLowerCase() === 'input' &&
              elem.type === 'text' &&
              // Support: IE<8
              // New HTML5 attribute values (e.g., "search") appear with elem.type === "text"
              ((attr = elem.getAttribute('type')) == null || attr.toLowerCase() === 'text')
            );
          },

          // Position-in-collection
          first: createPositionalPseudo(function () {
            return [0];
          }),

          last: createPositionalPseudo(function (_matchIndexes, length) {
            return [length - 1];
          }),

          eq: createPositionalPseudo(function (_matchIndexes, length, argument) {
            return [argument < 0 ? argument + length : argument];
          }),

          even: createPositionalPseudo(function (matchIndexes, length) {
            var i = 0;
            for (; i < length; i += 2) {
              matchIndexes.push(i);
            }
            return matchIndexes;
          }),

          odd: createPositionalPseudo(function (matchIndexes, length) {
            var i = 1;
            for (; i < length; i += 2) {
              matchIndexes.push(i);
            }
            return matchIndexes;
          }),

          lt: createPositionalPseudo(function (matchIndexes, length, argument) {
            var i = argument < 0 ? argument + length : argument > length ? length : argument;
            for (; --i >= 0; ) {
              matchIndexes.push(i);
            }
            return matchIndexes;
          }),

          gt: createPositionalPseudo(function (matchIndexes, length, argument) {
            var i = argument < 0 ? argument + length : argument;
            for (; ++i < length; ) {
              matchIndexes.push(i);
            }
            return matchIndexes;
          }),
        },
      };

      Expr.pseudos['nth'] = Expr.pseudos['eq'];

      // Add button/input type pseudos
      for (i in { radio: true, checkbox: true, file: true, password: true, image: true }) {
        Expr.pseudos[i] = createInputPseudo(i);
      }
      for (i in { submit: true, reset: true }) {
        Expr.pseudos[i] = createButtonPseudo(i);
      }

      // Easy API for creating new setFilters
      function setFilters() {}
      setFilters.prototype = Expr.filters = Expr.pseudos;
      Expr.setFilters = new setFilters();

      tokenize = Sizzle.tokenize = function (selector, parseOnly) {
        var matched,
          match,
          tokens,
          type,
          soFar,
          groups,
          preFilters,
          cached = tokenCache[selector + ' '];

        if (cached) {
          return parseOnly ? 0 : cached.slice(0);
        }

        soFar = selector;
        groups = [];
        preFilters = Expr.preFilter;

        while (soFar) {
          // Comma and first run
          if (!matched || (match = rcomma.exec(soFar))) {
            if (match) {
              // Don't consume trailing commas as valid
              soFar = soFar.slice(match[0].length) || soFar;
            }
            groups.push((tokens = []));
          }

          matched = false;

          // Combinators
          if ((match = rcombinators.exec(soFar))) {
            matched = match.shift();
            tokens.push({
              value: matched,

              // Cast descendant combinators to space
              type: match[0].replace(rtrim, ' '),
            });
            soFar = soFar.slice(matched.length);
          }

          // Filters
          for (type in Expr.filter) {
            if (
              (match = matchExpr[type].exec(soFar)) &&
              (!preFilters[type] || (match = preFilters[type](match)))
            ) {
              matched = match.shift();
              tokens.push({
                value: matched,
                type: type,
                matches: match,
              });
              soFar = soFar.slice(matched.length);
            }
          }

          if (!matched) {
            break;
          }
        }

        // Return the length of the invalid excess
        // if we're just parsing
        // Otherwise, throw an error or return tokens
        return parseOnly
          ? soFar.length
          : soFar
          ? Sizzle.error(selector)
          : // Cache the tokens
            tokenCache(selector, groups).slice(0);
      };

      function toSelector(tokens) {
        var i = 0,
          len = tokens.length,
          selector = '';
        for (; i < len; i++) {
          selector += tokens[i].value;
        }
        return selector;
      }

      function addCombinator(matcher, combinator, base) {
        var dir = combinator.dir,
          skip = combinator.next,
          key = skip || dir,
          checkNonElements = base && key === 'parentNode',
          doneName = done++;

        return combinator.first
          ? // Check against closest ancestor/preceding element
            function (elem, context, xml) {
              while ((elem = elem[dir])) {
                if (elem.nodeType === 1 || checkNonElements) {
                  return matcher(elem, context, xml);
                }
              }
              return false;
            }
          : // Check against all ancestor/preceding elements
            function (elem, context, xml) {
              var oldCache,
                uniqueCache,
                outerCache,
                newCache = [dirruns, doneName];

              // We can't set arbitrary data on XML nodes, so they don't benefit from combinator caching
              if (xml) {
                while ((elem = elem[dir])) {
                  if (elem.nodeType === 1 || checkNonElements) {
                    if (matcher(elem, context, xml)) {
                      return true;
                    }
                  }
                }
              } else {
                while ((elem = elem[dir])) {
                  if (elem.nodeType === 1 || checkNonElements) {
                    outerCache = elem[expando] || (elem[expando] = {});

                    // Support: IE <9 only
                    // Defend against cloned attroperties (jQuery gh-1709)
                    uniqueCache = outerCache[elem.uniqueID] || (outerCache[elem.uniqueID] = {});

                    if (skip && skip === elem.nodeName.toLowerCase()) {
                      elem = elem[dir] || elem;
                    } else if (
                      (oldCache = uniqueCache[key]) &&
                      oldCache[0] === dirruns &&
                      oldCache[1] === doneName
                    ) {
                      // Assign to newCache so results back-propagate to previous elements
                      return (newCache[2] = oldCache[2]);
                    } else {
                      // Reuse newcache so results back-propagate to previous elements
                      uniqueCache[key] = newCache;

                      // A match means we're done; a fail means we have to keep checking
                      if ((newCache[2] = matcher(elem, context, xml))) {
                        return true;
                      }
                    }
                  }
                }
              }
              return false;
            };
      }

      function elementMatcher(matchers) {
        return matchers.length > 1
          ? function (elem, context, xml) {
              var i = matchers.length;
              while (i--) {
                if (!matchers[i](elem, context, xml)) {
                  return false;
                }
              }
              return true;
            }
          : matchers[0];
      }

      function multipleContexts(selector, contexts, results) {
        var i = 0,
          len = contexts.length;
        for (; i < len; i++) {
          Sizzle(selector, contexts[i], results);
        }
        return results;
      }

      function condense(unmatched, map, filter, context, xml) {
        var elem,
          newUnmatched = [],
          i = 0,
          len = unmatched.length,
          mapped = map != null;

        for (; i < len; i++) {
          if ((elem = unmatched[i])) {
            if (!filter || filter(elem, context, xml)) {
              newUnmatched.push(elem);
              if (mapped) {
                map.push(i);
              }
            }
          }
        }

        return newUnmatched;
      }

      function setMatcher(preFilter, selector, matcher, postFilter, postFinder, postSelector) {
        if (postFilter && !postFilter[expando]) {
          postFilter = setMatcher(postFilter);
        }
        if (postFinder && !postFinder[expando]) {
          postFinder = setMatcher(postFinder, postSelector);
        }
        return markFunction(function (seed, results, context, xml) {
          var temp,
            i,
            elem,
            preMap = [],
            postMap = [],
            preexisting = results.length,
            // Get initial elements from seed or context
            elems =
              seed || multipleContexts(selector || '*', context.nodeType ? [context] : context, []),
            // Prefilter to get matcher input, preserving a map for seed-results synchronization
            matcherIn =
              preFilter && (seed || !selector)
                ? condense(elems, preMap, preFilter, context, xml)
                : elems,
            matcherOut = matcher
              ? // If we have a postFinder, or filtered seed, or non-seed postFilter or preexisting results,
                postFinder || (seed ? preFilter : preexisting || postFilter)
                ? // ...intermediate processing is necessary
                  []
                : // ...otherwise use results directly
                  results
              : matcherIn;

          // Find primary matches
          if (matcher) {
            matcher(matcherIn, matcherOut, context, xml);
          }

          // Apply postFilter
          if (postFilter) {
            temp = condense(matcherOut, postMap);
            postFilter(temp, [], context, xml);

            // Un-match failing elements by moving them back to matcherIn
            i = temp.length;
            while (i--) {
              if ((elem = temp[i])) {
                matcherOut[postMap[i]] = !(matcherIn[postMap[i]] = elem);
              }
            }
          }

          if (seed) {
            if (postFinder || preFilter) {
              if (postFinder) {
                // Get the final matcherOut by condensing this intermediate into postFinder contexts
                temp = [];
                i = matcherOut.length;
                while (i--) {
                  if ((elem = matcherOut[i])) {
                    // Restore matcherIn since elem is not yet a final match
                    temp.push((matcherIn[i] = elem));
                  }
                }
                postFinder(null, (matcherOut = []), temp, xml);
              }

              // Move matched elements from seed to results to keep them synchronized
              i = matcherOut.length;
              while (i--) {
                if (
                  (elem = matcherOut[i]) &&
                  (temp = postFinder ? indexOf(seed, elem) : preMap[i]) > -1
                ) {
                  seed[temp] = !(results[temp] = elem);
                }
              }
            }

            // Add elements to results, through postFinder if defined
          } else {
            matcherOut = condense(
              matcherOut === results
                ? matcherOut.splice(preexisting, matcherOut.length)
                : matcherOut
            );
            if (postFinder) {
              postFinder(null, results, matcherOut, xml);
            } else {
              push.apply(results, matcherOut);
            }
          }
        });
      }

      function matcherFromTokens(tokens) {
        var checkContext,
          matcher,
          j,
          len = tokens.length,
          leadingRelative = Expr.relative[tokens[0].type],
          implicitRelative = leadingRelative || Expr.relative[' '],
          i = leadingRelative ? 1 : 0,
          // The foundational matcher ensures that elements are reachable from top-level context(s)
          matchContext = addCombinator(
            function (elem) {
              return elem === checkContext;
            },
            implicitRelative,
            true
          ),
          matchAnyContext = addCombinator(
            function (elem) {
              return indexOf(checkContext, elem) > -1;
            },
            implicitRelative,
            true
          ),
          matchers = [
            function (elem, context, xml) {
              var ret =
                (!leadingRelative && (xml || context !== outermostContext)) ||
                ((checkContext = context).nodeType
                  ? matchContext(elem, context, xml)
                  : matchAnyContext(elem, context, xml));

              // Avoid hanging onto element (issue #299)
              checkContext = null;
              return ret;
            },
          ];

        for (; i < len; i++) {
          if ((matcher = Expr.relative[tokens[i].type])) {
            matchers = [addCombinator(elementMatcher(matchers), matcher)];
          } else {
            matcher = Expr.filter[tokens[i].type].apply(null, tokens[i].matches);

            // Return special upon seeing a positional matcher
            if (matcher[expando]) {
              // Find the next relative operator (if any) for proper handling
              j = ++i;
              for (; j < len; j++) {
                if (Expr.relative[tokens[j].type]) {
                  break;
                }
              }
              return setMatcher(
                i > 1 && elementMatcher(matchers),
                i > 1 &&
                  toSelector(
                    // If the preceding token was a descendant combinator, insert an implicit any-element `*`
                    tokens.slice(0, i - 1).concat({ value: tokens[i - 2].type === ' ' ? '*' : '' })
                  ).replace(rtrim, '$1'),
                matcher,
                i < j && matcherFromTokens(tokens.slice(i, j)),
                j < len && matcherFromTokens((tokens = tokens.slice(j))),
                j < len && toSelector(tokens)
              );
            }
            matchers.push(matcher);
          }
        }

        return elementMatcher(matchers);
      }

      function matcherFromGroupMatchers(elementMatchers, setMatchers) {
        var bySet = setMatchers.length > 0,
          byElement = elementMatchers.length > 0,
          superMatcher = function (seed, context, xml, results, outermost) {
            var elem,
              j,
              matcher,
              matchedCount = 0,
              i = '0',
              unmatched = seed && [],
              setMatched = [],
              contextBackup = outermostContext,
              // We must always have either seed elements or outermost context
              elems = seed || (byElement && Expr.find['TAG']('*', outermost)),
              // Use integer dirruns iff this is the outermost matcher
              dirrunsUnique = (dirruns += contextBackup == null ? 1 : Math.random() || 0.1),
              len = elems.length;

            if (outermost) {
              // Support: IE 11+, Edge 17 - 18+
              // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
              // two documents; shallow comparisons work.
              // eslint-disable-next-line eqeqeq
              outermostContext = context == document || context || outermost;
            }

            // Add elements passing elementMatchers directly to results
            // Support: IE<9, Safari
            // Tolerate NodeList properties (IE: "length"; Safari: <number>) matching elements by id
            for (; i !== len && (elem = elems[i]) != null; i++) {
              if (byElement && elem) {
                j = 0;

                // Support: IE 11+, Edge 17 - 18+
                // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
                // two documents; shallow comparisons work.
                // eslint-disable-next-line eqeqeq
                if (!context && elem.ownerDocument != document) {
                  setDocument(elem);
                  xml = !documentIsHTML;
                }
                while ((matcher = elementMatchers[j++])) {
                  if (matcher(elem, context || document, xml)) {
                    results.push(elem);
                    break;
                  }
                }
                if (outermost) {
                  dirruns = dirrunsUnique;
                }
              }

              // Track unmatched elements for set filters
              if (bySet) {
                // They will have gone through all possible matchers
                if ((elem = !matcher && elem)) {
                  matchedCount--;
                }

                // Lengthen the array for every element, matched or not
                if (seed) {
                  unmatched.push(elem);
                }
              }
            }

            // `i` is now the count of elements visited above, and adding it to `matchedCount`
            // makes the latter nonnegative.
            matchedCount += i;

            // Apply set filters to unmatched elements
            // NOTE: This can be skipped if there are no unmatched elements (i.e., `matchedCount`
            // equals `i`), unless we didn't visit _any_ elements in the above loop because we have
            // no element matchers and no seed.
            // Incrementing an initially-string "0" `i` allows `i` to remain a string only in that
            // case, which will result in a "00" `matchedCount` that differs from `i` but is also
            // numerically zero.
            if (bySet && i !== matchedCount) {
              j = 0;
              while ((matcher = setMatchers[j++])) {
                matcher(unmatched, setMatched, context, xml);
              }

              if (seed) {
                // Reintegrate element matches to eliminate the need for sorting
                if (matchedCount > 0) {
                  while (i--) {
                    if (!(unmatched[i] || setMatched[i])) {
                      setMatched[i] = pop.call(results);
                    }
                  }
                }

                // Discard index placeholder values to get only actual matches
                setMatched = condense(setMatched);
              }

              // Add matches to results
              push.apply(results, setMatched);

              // Seedless set matches succeeding multiple successful matchers stipulate sorting
              if (
                outermost &&
                !seed &&
                setMatched.length > 0 &&
                matchedCount + setMatchers.length > 1
              ) {
                Sizzle.uniqueSort(results);
              }
            }

            // Override manipulation of globals by nested matchers
            if (outermost) {
              dirruns = dirrunsUnique;
              outermostContext = contextBackup;
            }

            return unmatched;
          };

        return bySet ? markFunction(superMatcher) : superMatcher;
      }

      compile = Sizzle.compile = function (selector, match /* Internal Use Only */) {
        var i,
          setMatchers = [],
          elementMatchers = [],
          cached = compilerCache[selector + ' '];

        if (!cached) {
          // Generate a function of recursive functions that can be used to check each element
          if (!match) {
            match = tokenize(selector);
          }
          i = match.length;
          while (i--) {
            cached = matcherFromTokens(match[i]);
            if (cached[expando]) {
              setMatchers.push(cached);
            } else {
              elementMatchers.push(cached);
            }
          }

          // Cache the compiled function
          cached = compilerCache(selector, matcherFromGroupMatchers(elementMatchers, setMatchers));

          // Save selector and tokenization
          cached.selector = selector;
        }
        return cached;
      };

      /**
       * A low-level selection function that works with Sizzle's compiled
       *  selector functions
       * @param {String|Function} selector A selector or a pre-compiled
       *  selector function built with Sizzle.compile
       * @param {Element} context
       * @param {Array} [results]
       * @param {Array} [seed] A set of elements to match against
       */
      select = Sizzle.select = function (selector, context, results, seed) {
        var i,
          tokens,
          token,
          type,
          find,
          compiled = typeof selector === 'function' && selector,
          match = !seed && tokenize((selector = compiled.selector || selector));

        results = results || [];

        // Try to minimize operations if there is only one selector in the list and no seed
        // (the latter of which guarantees us context)
        if (match.length === 1) {
          // Reduce context if the leading compound selector is an ID
          tokens = match[0] = match[0].slice(0);
          if (
            tokens.length > 2 &&
            (token = tokens[0]).type === 'ID' &&
            context.nodeType === 9 &&
            documentIsHTML &&
            Expr.relative[tokens[1].type]
          ) {
            context = (Expr.find['ID'](token.matches[0].replace(runescape, funescape), context) ||
              [])[0];
            if (!context) {
              return results;

              // Precompiled matchers will still verify ancestry, so step up a level
            } else if (compiled) {
              context = context.parentNode;
            }

            selector = selector.slice(tokens.shift().value.length);
          }

          // Fetch a seed set for right-to-left matching
          i = matchExpr['needsContext'].test(selector) ? 0 : tokens.length;
          while (i--) {
            token = tokens[i];

            // Abort if we hit a combinator
            if (Expr.relative[(type = token.type)]) {
              break;
            }
            if ((find = Expr.find[type])) {
              // Search, expanding context for leading sibling combinators
              if (
                (seed = find(
                  token.matches[0].replace(runescape, funescape),
                  (rsibling.test(tokens[0].type) && testContext(context.parentNode)) || context
                ))
              ) {
                // If seed is empty or no tokens remain, we can return early
                tokens.splice(i, 1);
                selector = seed.length && toSelector(tokens);
                if (!selector) {
                  push.apply(results, seed);
                  return results;
                }

                break;
              }
            }
          }
        }

        // Compile and execute a filtering function if one is not provided
        // Provide `match` to avoid retokenization if we modified the selector above
        (compiled || compile(selector, match))(
          seed,
          context,
          !documentIsHTML,
          results,
          !context || (rsibling.test(selector) && testContext(context.parentNode)) || context
        );
        return results;
      };

      // One-time assignments

      // Sort stability
      support.sortStable = expando.split('').sort(sortOrder).join('') === expando;

      // Support: Chrome 14-35+
      // Always assume duplicates if they aren't passed to the comparison function
      support.detectDuplicates = !!hasDuplicate;

      // Initialize against the default document
      setDocument();

      // Support: Webkit<537.32 - Safari 6.0.3/Chrome 25 (fixed in Chrome 27)
      // Detached nodes confoundingly follow *each other*
      support.sortDetached = assert(function (el) {
        // Should return 1, but returns 4 (following)
        return el.compareDocumentPosition(document.createElement('fieldset')) & 1;
      });

      // Support: IE<8
      // Prevent attribute/property "interpolation"
      // https://msdn.microsoft.com/en-us/library/ms536429%28VS.85%29.aspx
      if (
        !assert(function (el) {
          el.innerHTML = "<a href='#'></a>";
          return el.firstChild.getAttribute('href') === '#';
        })
      ) {
        addHandle('type|href|height|width', function (elem, name, isXML) {
          if (!isXML) {
            return elem.getAttribute(name, name.toLowerCase() === 'type' ? 1 : 2);
          }
        });
      }

      // Support: IE<9
      // Use defaultValue in place of getAttribute("value")
      if (
        !support.attributes ||
        !assert(function (el) {
          el.innerHTML = '<input/>';
          el.firstChild.setAttribute('value', '');
          return el.firstChild.getAttribute('value') === '';
        })
      ) {
        addHandle('value', function (elem, _name, isXML) {
          if (!isXML && elem.nodeName.toLowerCase() === 'input') {
            return elem.defaultValue;
          }
        });
      }

      // Support: IE<9
      // Use getAttributeNode to fetch booleans when getAttribute lies
      if (
        !assert(function (el) {
          return el.getAttribute('disabled') == null;
        })
      ) {
        addHandle(booleans, function (elem, name, isXML) {
          var val;
          if (!isXML) {
            return elem[name] === true
              ? name.toLowerCase()
              : (val = elem.getAttributeNode(name)) && val.specified
              ? val.value
              : null;
          }
        });
      }

      return Sizzle;
    })(window);

  jQuery.find = Sizzle;
  jQuery.expr = Sizzle.selectors;

  // Deprecated
  jQuery.expr[':'] = jQuery.expr.pseudos;
  jQuery.uniqueSort = jQuery.unique = Sizzle.uniqueSort;
  jQuery.text = Sizzle.getText;
  jQuery.isXMLDoc = Sizzle.isXML;
  jQuery.contains = Sizzle.contains;
  jQuery.escapeSelector = Sizzle.escape;

  var dir = function (elem, dir, until) {
    var matched = [],
      truncate = until !== undefined;

    while ((elem = elem[dir]) && elem.nodeType !== 9) {
      if (elem.nodeType === 1) {
        if (truncate && jQuery(elem).is(until)) {
          break;
        }
        matched.push(elem);
      }
    }
    return matched;
  };

  var siblings = function (n, elem) {
    var matched = [];

    for (; n; n = n.nextSibling) {
      if (n.nodeType === 1 && n !== elem) {
        matched.push(n);
      }
    }

    return matched;
  };

  var rneedsContext = jQuery.expr.match.needsContext;

  function nodeName(elem, name) {
    return elem.nodeName && elem.nodeName.toLowerCase() === name.toLowerCase();
  }
  var rsingleTag = /^<([a-z][^\/\0>:\x20\t\r\n\f]*)[\x20\t\r\n\f]*\/?>(?:<\/\1>|)$/i;

  // Implement the identical functionality for filter and not
  function winnow(elements, qualifier, not) {
    if (isFunction(qualifier)) {
      return jQuery.grep(elements, function (elem, i) {
        return !!qualifier.call(elem, i, elem) !== not;
      });
    }

    // Single element
    if (qualifier.nodeType) {
      return jQuery.grep(elements, function (elem) {
        return (elem === qualifier) !== not;
      });
    }

    // Arraylike of elements (jQuery, arguments, Array)
    if (typeof qualifier !== 'string') {
      return jQuery.grep(elements, function (elem) {
        return indexOf.call(qualifier, elem) > -1 !== not;
      });
    }

    // Filtered directly for both simple and complex selectors
    return jQuery.filter(qualifier, elements, not);
  }

  jQuery.filter = function (expr, elems, not) {
    var elem = elems[0];

    if (not) {
      expr = ':not(' + expr + ')';
    }

    if (elems.length === 1 && elem.nodeType === 1) {
      return jQuery.find.matchesSelector(elem, expr) ? [elem] : [];
    }

    return jQuery.find.matches(
      expr,
      jQuery.grep(elems, function (elem) {
        return elem.nodeType === 1;
      })
    );
  };

  jQuery.fn.extend({
    find: function (selector) {
      var i,
        ret,
        len = this.length,
        self = this;

      if (typeof selector !== 'string') {
        return this.pushStack(
          jQuery(selector).filter(function () {
            for (i = 0; i < len; i++) {
              if (jQuery.contains(self[i], this)) {
                return true;
              }
            }
          })
        );
      }

      ret = this.pushStack([]);

      for (i = 0; i < len; i++) {
        jQuery.find(selector, self[i], ret);
      }

      return len > 1 ? jQuery.uniqueSort(ret) : ret;
    },
    filter: function (selector) {
      return this.pushStack(winnow(this, selector || [], false));
    },
    not: function (selector) {
      return this.pushStack(winnow(this, selector || [], true));
    },
    is: function (selector) {
      return !!winnow(
        this,

        // If this is a positional/relative selector, check membership in the returned set
        // so $("p:first").is("p:last") won't return true for a doc with two "p".
        typeof selector === 'string' && rneedsContext.test(selector)
          ? jQuery(selector)
          : selector || [],
        false
      ).length;
    },
  });

  // Initialize a jQuery object

  // A central reference to the root jQuery(document)
  var rootjQuery,
    // A simple way to check for HTML strings
    // Prioritize #id over <tag> to avoid XSS via location.hash (#9521)
    // Strict HTML recognition (#11290: must start with <)
    // Shortcut simple #id case for speed
    rquickExpr = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]+))$/,
    init = (jQuery.fn.init = function (selector, context, root) {
      var match, elem;

      // HANDLE: $(""), $(null), $(undefined), $(false)
      if (!selector) {
        return this;
      }

      // Method init() accepts an alternate rootjQuery
      // so migrate can support jQuery.sub (gh-2101)
      root = root || rootjQuery;

      // Handle HTML strings
      if (typeof selector === 'string') {
        if (selector[0] === '<' && selector[selector.length - 1] === '>' && selector.length >= 3) {
          // Assume that strings that start and end with <> are HTML and skip the regex check
          match = [null, selector, null];
        } else {
          match = rquickExpr.exec(selector);
        }

        // Match html or make sure no context is specified for #id
        if (match && (match[1] || !context)) {
          // HANDLE: $(html) -> $(array)
          if (match[1]) {
            context = context instanceof jQuery ? context[0] : context;

            // Option to run scripts is true for back-compat
            // Intentionally let the error be thrown if parseHTML is not present
            jQuery.merge(
              this,
              jQuery.parseHTML(
                match[1],
                context && context.nodeType ? context.ownerDocument || context : document,
                true
              )
            );

            // HANDLE: $(html, props)
            if (rsingleTag.test(match[1]) && jQuery.isPlainObject(context)) {
              for (match in context) {
                // Properties of context are called as methods if possible
                if (isFunction(this[match])) {
                  this[match](context[match]);

                  // ...and otherwise set as attributes
                } else {
                  this.attr(match, context[match]);
                }
              }
            }

            return this;

            // HANDLE: $(#id)
          } else {
            elem = document.getElementById(match[2]);

            if (elem) {
              // Inject the element directly into the jQuery object
              this[0] = elem;
              this.length = 1;
            }
            return this;
          }

          // HANDLE: $(expr, $(...))
        } else if (!context || context.jquery) {
          return (context || root).find(selector);

          // HANDLE: $(expr, context)
          // (which is just equivalent to: $(context).find(expr)
        } else {
          return this.constructor(context).find(selector);
        }

        // HANDLE: $(DOMElement)
      } else if (selector.nodeType) {
        this[0] = selector;
        this.length = 1;
        return this;

        // HANDLE: $(function)
        // Shortcut for document ready
      } else if (isFunction(selector)) {
        return root.ready !== undefined
          ? root.ready(selector)
          : // Execute immediately if ready is not present
            selector(jQuery);
      }

      return jQuery.makeArray(selector, this);
    });

  // Give the init function the jQuery prototype for later instantiation
  init.prototype = jQuery.fn;

  // Initialize central reference
  rootjQuery = jQuery(document);

  var rparentsprev = /^(?:parents|prev(?:Until|All))/,
    // Methods guaranteed to produce a unique set when starting from a unique set
    guaranteedUnique = {
      children: true,
      contents: true,
      next: true,
      prev: true,
    };

  jQuery.fn.extend({
    has: function (target) {
      var targets = jQuery(target, this),
        l = targets.length;

      return this.filter(function () {
        var i = 0;
        for (; i < l; i++) {
          if (jQuery.contains(this, targets[i])) {
            return true;
          }
        }
      });
    },

    closest: function (selectors, context) {
      var cur,
        i = 0,
        l = this.length,
        matched = [],
        targets = typeof selectors !== 'string' && jQuery(selectors);

      // Positional selectors never match, since there's no _selection_ context
      if (!rneedsContext.test(selectors)) {
        for (; i < l; i++) {
          for (cur = this[i]; cur && cur !== context; cur = cur.parentNode) {
            // Always skip document fragments
            if (
              cur.nodeType < 11 &&
              (targets
                ? targets.index(cur) > -1
                : // Don't pass non-elements to Sizzle
                  cur.nodeType === 1 && jQuery.find.matchesSelector(cur, selectors))
            ) {
              matched.push(cur);
              break;
            }
          }
        }
      }

      return this.pushStack(matched.length > 1 ? jQuery.uniqueSort(matched) : matched);
    },

    // Determine the position of an element within the set
    index: function (elem) {
      // No argument, return index in parent
      if (!elem) {
        return this[0] && this[0].parentNode ? this.first().prevAll().length : -1;
      }

      // Index in selector
      if (typeof elem === 'string') {
        return indexOf.call(jQuery(elem), this[0]);
      }

      // Locate the position of the desired element
      return indexOf.call(
        this,

        // If it receives a jQuery object, the first element is used
        elem.jquery ? elem[0] : elem
      );
    },

    add: function (selector, context) {
      return this.pushStack(jQuery.uniqueSort(jQuery.merge(this.get(), jQuery(selector, context))));
    },

    addBack: function (selector) {
      return this.add(selector == null ? this.prevObject : this.prevObject.filter(selector));
    },
  });

  function sibling(cur, dir) {
    while ((cur = cur[dir]) && cur.nodeType !== 1) {}
    return cur;
  }

  jQuery.each(
    {
      parent: function (elem) {
        var parent = elem.parentNode;
        return parent && parent.nodeType !== 11 ? parent : null;
      },
      parents: function (elem) {
        return dir(elem, 'parentNode');
      },
      parentsUntil: function (elem, _i, until) {
        return dir(elem, 'parentNode', until);
      },
      next: function (elem) {
        return sibling(elem, 'nextSibling');
      },
      prev: function (elem) {
        return sibling(elem, 'previousSibling');
      },
      nextAll: function (elem) {
        return dir(elem, 'nextSibling');
      },
      prevAll: function (elem) {
        return dir(elem, 'previousSibling');
      },
      nextUntil: function (elem, _i, until) {
        return dir(elem, 'nextSibling', until);
      },
      prevUntil: function (elem, _i, until) {
        return dir(elem, 'previousSibling', until);
      },
      siblings: function (elem) {
        return siblings((elem.parentNode || {}).firstChild, elem);
      },
      children: function (elem) {
        return siblings(elem.firstChild);
      },
      contents: function (elem) {
        if (
          elem.contentDocument != null &&
          // Support: IE 11+
          // <object> elements with no `data` attribute has an object
          // `contentDocument` with a `null` prototype.
          getProto(elem.contentDocument)
        ) {
          return elem.contentDocument;
        }

        // Support: IE 9 - 11 only, iOS 7 only, Android Browser <=4.3 only
        // Treat the template element as a regular one in browsers that
        // don't support it.
        if (nodeName(elem, 'template')) {
          elem = elem.content || elem;
        }

        return jQuery.merge([], elem.childNodes);
      },
    },
    function (name, fn) {
      jQuery.fn[name] = function (until, selector) {
        var matched = jQuery.map(this, fn, until);

        if (name.slice(-5) !== 'Until') {
          selector = until;
        }

        if (selector && typeof selector === 'string') {
          matched = jQuery.filter(selector, matched);
        }

        if (this.length > 1) {
          // Remove duplicates
          if (!guaranteedUnique[name]) {
            jQuery.uniqueSort(matched);
          }

          // Reverse order for parents* and prev-derivatives
          if (rparentsprev.test(name)) {
            matched.reverse();
          }
        }

        return this.pushStack(matched);
      };
    }
  );
  var rnothtmlwhite = /[^\x20\t\r\n\f]+/g;

  // Convert String-formatted options into Object-formatted ones
  function createOptions(options) {
    var object = {};
    jQuery.each(options.match(rnothtmlwhite) || [], function (_, flag) {
      object[flag] = true;
    });
    return object;
  }

  /*
   * Create a callback list using the following parameters:
   *
   *	options: an optional list of space-separated options that will change how
   *			the callback list behaves or a more traditional option object
   *
   * By default a callback list will act like an event callback list and can be
   * "fired" multiple times.
   *
   * Possible options:
   *
   *	once:			will ensure the callback list can only be fired once (like a Deferred)
   *
   *	memory:			will keep track of previous values and will call any callback added
   *					after the list has been fired right away with the latest "memorized"
   *					values (like a Deferred)
   *
   *	unique:			will ensure a callback can only be added once (no duplicate in the list)
   *
   *	stopOnFalse:	interrupt callings when a callback returns false
   *
   */
  jQuery.Callbacks = function (options) {
    // Convert options from String-formatted to Object-formatted if needed
    // (we check in cache first)
    options = typeof options === 'string' ? createOptions(options) : jQuery.extend({}, options);

    var // Flag to know if list is currently firing
      firing,
      // Last fire value for non-forgettable lists
      memory,
      // Flag to know if list was already fired
      fired,
      // Flag to prevent firing
      locked,
      // Actual callback list
      list = [],
      // Queue of execution data for repeatable lists
      queue = [],
      // Index of currently firing callback (modified by add/remove as needed)
      firingIndex = -1,
      // Fire callbacks
      fire = function () {
        // Enforce single-firing
        locked = locked || options.once;

        // Execute callbacks for all pending executions,
        // respecting firingIndex overrides and runtime changes
        fired = firing = true;
        for (; queue.length; firingIndex = -1) {
          memory = queue.shift();
          while (++firingIndex < list.length) {
            // Run callback and check for early termination
            if (list[firingIndex].apply(memory[0], memory[1]) === false && options.stopOnFalse) {
              // Jump to end and forget the data so .add doesn't re-fire
              firingIndex = list.length;
              memory = false;
            }
          }
        }

        // Forget the data if we're done with it
        if (!options.memory) {
          memory = false;
        }

        firing = false;

        // Clean up if we're done firing for good
        if (locked) {
          // Keep an empty list if we have data for future add calls
          if (memory) {
            list = [];

            // Otherwise, this object is spent
          } else {
            list = '';
          }
        }
      },
      // Actual Callbacks object
      self = {
        // Add a callback or a collection of callbacks to the list
        add: function () {
          if (list) {
            // If we have memory from a past run, we should fire after adding
            if (memory && !firing) {
              firingIndex = list.length - 1;
              queue.push(memory);
            }

            (function add(args) {
              jQuery.each(args, function (_, arg) {
                if (isFunction(arg)) {
                  if (!options.unique || !self.has(arg)) {
                    list.push(arg);
                  }
                } else if (arg && arg.length && toType(arg) !== 'string') {
                  // Inspect recursively
                  add(arg);
                }
              });
            })(arguments);

            if (memory && !firing) {
              fire();
            }
          }
          return this;
        },

        // Remove a callback from the list
        remove: function () {
          jQuery.each(arguments, function (_, arg) {
            var index;
            while ((index = jQuery.inArray(arg, list, index)) > -1) {
              list.splice(index, 1);

              // Handle firing indexes
              if (index <= firingIndex) {
                firingIndex--;
              }
            }
          });
          return this;
        },

        // Check if a given callback is in the list.
        // If no argument is given, return whether or not list has callbacks attached.
        has: function (fn) {
          return fn ? jQuery.inArray(fn, list) > -1 : list.length > 0;
        },

        // Remove all callbacks from the list
        empty: function () {
          if (list) {
            list = [];
          }
          return this;
        },

        // Disable .fire and .add
        // Abort any current/pending executions
        // Clear all callbacks and values
        disable: function () {
          locked = queue = [];
          list = memory = '';
          return this;
        },
        disabled: function () {
          return !list;
        },

        // Disable .fire
        // Also disable .add unless we have memory (since it would have no effect)
        // Abort any pending executions
        lock: function () {
          locked = queue = [];
          if (!memory && !firing) {
            list = memory = '';
          }
          return this;
        },
        locked: function () {
          return !!locked;
        },

        // Call all callbacks with the given context and arguments
        fireWith: function (context, args) {
          if (!locked) {
            args = args || [];
            args = [context, args.slice ? args.slice() : args];
            queue.push(args);
            if (!firing) {
              fire();
            }
          }
          return this;
        },

        // Call all the callbacks with the given arguments
        fire: function () {
          self.fireWith(this, arguments);
          return this;
        },

        // To know if the callbacks have already been called at least once
        fired: function () {
          return !!fired;
        },
      };

    return self;
  };

  function Identity(v) {
    return v;
  }
  function Thrower(ex) {
    throw ex;
  }

  function adoptValue(value, resolve, reject, noValue) {
    var method;

    try {
      // Check for promise aspect first to privilege synchronous behavior
      if (value && isFunction((method = value.promise))) {
        method.call(value).done(resolve).fail(reject);

        // Other thenables
      } else if (value && isFunction((method = value.then))) {
        method.call(value, resolve, reject);

        // Other non-thenables
      } else {
        // Control `resolve` arguments by letting Array#slice cast boolean `noValue` to integer:
        // * false: [ value ].slice( 0 ) => resolve( value )
        // * true: [ value ].slice( 1 ) => resolve()
        resolve.apply(undefined, [value].slice(noValue));
      }

      // For Promises/A+, convert exceptions into rejections
      // Since jQuery.when doesn't unwrap thenables, we can skip the extra checks appearing in
      // Deferred#then to conditionally suppress rejection.
    } catch (value) {
      // Support: Android 4.0 only
      // Strict mode functions invoked without .call/.apply get global-object context
      reject.apply(undefined, [value]);
    }
  }

  jQuery.extend({
    Deferred: function (func) {
      var tuples = [
          // action, add listener, callbacks,
          // ... .then handlers, argument index, [final state]
          ['notify', 'progress', jQuery.Callbacks('memory'), jQuery.Callbacks('memory'), 2],
          [
            'resolve',
            'done',
            jQuery.Callbacks('once memory'),
            jQuery.Callbacks('once memory'),
            0,
            'resolved',
          ],
          [
            'reject',
            'fail',
            jQuery.Callbacks('once memory'),
            jQuery.Callbacks('once memory'),
            1,
            'rejected',
          ],
        ],
        state = 'pending',
        promise = {
          state: function () {
            return state;
          },
          always: function () {
            deferred.done(arguments).fail(arguments);
            return this;
          },
          catch: function (fn) {
            return promise.then(null, fn);
          },

          // Keep pipe for back-compat
          pipe: function (/* fnDone, fnFail, fnProgress */) {
            var fns = arguments;

            return jQuery
              .Deferred(function (newDefer) {
                jQuery.each(tuples, function (_i, tuple) {
                  // Map tuples (progress, done, fail) to arguments (done, fail, progress)
                  var fn = isFunction(fns[tuple[4]]) && fns[tuple[4]];

                  // deferred.progress(function() { bind to newDefer or newDefer.notify })
                  // deferred.done(function() { bind to newDefer or newDefer.resolve })
                  // deferred.fail(function() { bind to newDefer or newDefer.reject })
                  deferred[tuple[1]](function () {
                    var returned = fn && fn.apply(this, arguments);
                    if (returned && isFunction(returned.promise)) {
                      returned
                        .promise()
                        .progress(newDefer.notify)
                        .done(newDefer.resolve)
                        .fail(newDefer.reject);
                    } else {
                      newDefer[tuple[0] + 'With'](this, fn ? [returned] : arguments);
                    }
                  });
                });
                fns = null;
              })
              .promise();
          },
          then: function (onFulfilled, onRejected, onProgress) {
            var maxDepth = 0;
            function resolve(depth, deferred, handler, special) {
              return function () {
                var that = this,
                  args = arguments,
                  mightThrow = function () {
                    var returned, then;

                    // Support: Promises/A+ section 2.3.3.3.3
                    // https://promisesaplus.com/#point-59
                    // Ignore double-resolution attempts
                    if (depth < maxDepth) {
                      return;
                    }

                    returned = handler.apply(that, args);

                    // Support: Promises/A+ section 2.3.1
                    // https://promisesaplus.com/#point-48
                    if (returned === deferred.promise()) {
                      throw new TypeError('Thenable self-resolution');
                    }

                    // Support: Promises/A+ sections 2.3.3.1, 3.5
                    // https://promisesaplus.com/#point-54
                    // https://promisesaplus.com/#point-75
                    // Retrieve `then` only once
                    then =
                      returned &&
                      // Support: Promises/A+ section 2.3.4
                      // https://promisesaplus.com/#point-64
                      // Only check objects and functions for thenability
                      (typeof returned === 'object' || typeof returned === 'function') &&
                      returned.then;

                    // Handle a returned thenable
                    if (isFunction(then)) {
                      // Special processors (notify) just wait for resolution
                      if (special) {
                        then.call(
                          returned,
                          resolve(maxDepth, deferred, Identity, special),
                          resolve(maxDepth, deferred, Thrower, special)
                        );

                        // Normal processors (resolve) also hook into progress
                      } else {
                        // ...and disregard older resolution values
                        maxDepth++;

                        then.call(
                          returned,
                          resolve(maxDepth, deferred, Identity, special),
                          resolve(maxDepth, deferred, Thrower, special),
                          resolve(maxDepth, deferred, Identity, deferred.notifyWith)
                        );
                      }

                      // Handle all other returned values
                    } else {
                      // Only substitute handlers pass on context
                      // and multiple values (non-spec behavior)
                      if (handler !== Identity) {
                        that = undefined;
                        args = [returned];
                      }

                      // Process the value(s)
                      // Default process is resolve
                      (special || deferred.resolveWith)(that, args);
                    }
                  },
                  // Only normal processors (resolve) catch and reject exceptions
                  process = special
                    ? mightThrow
                    : function () {
                        try {
                          mightThrow();
                        } catch (e) {
                          if (jQuery.Deferred.exceptionHook) {
                            jQuery.Deferred.exceptionHook(e, process.stackTrace);
                          }

                          // Support: Promises/A+ section 2.3.3.3.4.1
                          // https://promisesaplus.com/#point-61
                          // Ignore post-resolution exceptions
                          if (depth + 1 >= maxDepth) {
                            // Only substitute handlers pass on context
                            // and multiple values (non-spec behavior)
                            if (handler !== Thrower) {
                              that = undefined;
                              args = [e];
                            }

                            deferred.rejectWith(that, args);
                          }
                        }
                      };

                // Support: Promises/A+ section 2.3.3.3.1
                // https://promisesaplus.com/#point-57
                // Re-resolve promises immediately to dodge false rejection from
                // subsequent errors
                if (depth) {
                  process();
                } else {
                  // Call an optional hook to record the stack, in case of exception
                  // since it's otherwise lost when execution goes async
                  if (jQuery.Deferred.getStackHook) {
                    process.stackTrace = jQuery.Deferred.getStackHook();
                  }
                  window.setTimeout(process);
                }
              };
            }

            return jQuery
              .Deferred(function (newDefer) {
                // progress_handlers.add( ... )
                tuples[0][3].add(
                  resolve(
                    0,
                    newDefer,
                    isFunction(onProgress) ? onProgress : Identity,
                    newDefer.notifyWith
                  )
                );

                // fulfilled_handlers.add( ... )
                tuples[1][3].add(
                  resolve(0, newDefer, isFunction(onFulfilled) ? onFulfilled : Identity)
                );

                // rejected_handlers.add( ... )
                tuples[2][3].add(
                  resolve(0, newDefer, isFunction(onRejected) ? onRejected : Thrower)
                );
              })
              .promise();
          },

          // Get a promise for this deferred
          // If obj is provided, the promise aspect is added to the object
          promise: function (obj) {
            return obj != null ? jQuery.extend(obj, promise) : promise;
          },
        },
        deferred = {};

      // Add list-specific methods
      jQuery.each(tuples, function (i, tuple) {
        var list = tuple[2],
          stateString = tuple[5];

        // promise.progress = list.add
        // promise.done = list.add
        // promise.fail = list.add
        promise[tuple[1]] = list.add;

        // Handle state
        if (stateString) {
          list.add(
            function () {
              // state = "resolved" (i.e., fulfilled)
              // state = "rejected"
              state = stateString;
            },

            // rejected_callbacks.disable
            // fulfilled_callbacks.disable
            tuples[3 - i][2].disable,

            // rejected_handlers.disable
            // fulfilled_handlers.disable
            tuples[3 - i][3].disable,

            // progress_callbacks.lock
            tuples[0][2].lock,

            // progress_handlers.lock
            tuples[0][3].lock
          );
        }

        // progress_handlers.fire
        // fulfilled_handlers.fire
        // rejected_handlers.fire
        list.add(tuple[3].fire);

        // deferred.notify = function() { deferred.notifyWith(...) }
        // deferred.resolve = function() { deferred.resolveWith(...) }
        // deferred.reject = function() { deferred.rejectWith(...) }
        deferred[tuple[0]] = function () {
          deferred[tuple[0] + 'With'](this === deferred ? undefined : this, arguments);
          return this;
        };

        // deferred.notifyWith = list.fireWith
        // deferred.resolveWith = list.fireWith
        // deferred.rejectWith = list.fireWith
        deferred[tuple[0] + 'With'] = list.fireWith;
      });

      // Make the deferred a promise
      promise.promise(deferred);

      // Call given func if any
      if (func) {
        func.call(deferred, deferred);
      }

      // All done!
      return deferred;
    },

    // Deferred helper
    when: function (singleValue) {
      var // count of uncompleted subordinates
        remaining = arguments.length,
        // count of unprocessed arguments
        i = remaining,
        // subordinate fulfillment data
        resolveContexts = Array(i),
        resolveValues = slice.call(arguments),
        // the primary Deferred
        primary = jQuery.Deferred(),
        // subordinate callback factory
        updateFunc = function (i) {
          return function (value) {
            resolveContexts[i] = this;
            resolveValues[i] = arguments.length > 1 ? slice.call(arguments) : value;
            if (!--remaining) {
              primary.resolveWith(resolveContexts, resolveValues);
            }
          };
        };

      // Single- and empty arguments are adopted like Promise.resolve
      if (remaining <= 1) {
        adoptValue(singleValue, primary.done(updateFunc(i)).resolve, primary.reject, !remaining);

        // Use .then() to unwrap secondary thenables (cf. gh-3000)
        if (
          primary.state() === 'pending' ||
          isFunction(resolveValues[i] && resolveValues[i].then)
        ) {
          return primary.then();
        }
      }

      // Multiple arguments are aggregated like Promise.all array elements
      while (i--) {
        adoptValue(resolveValues[i], updateFunc(i), primary.reject);
      }

      return primary.promise();
    },
  });

  // These usually indicate a programmer mistake during development,
  // warn about them ASAP rather than swallowing them by default.
  var rerrorNames = /^(Eval|Internal|Range|Reference|Syntax|Type|URI)Error$/;

  jQuery.Deferred.exceptionHook = function (error, stack) {
    // Support: IE 8 - 9 only
    // Console exists when dev tools are open, which can happen at any time
    if (window.console && window.console.warn && error && rerrorNames.test(error.name)) {
      window.console.warn('jQuery.Deferred exception: ' + error.message, error.stack, stack);
    }
  };

  jQuery.readyException = function (error) {
    window.setTimeout(function () {
      throw error;
    });
  };

  // The deferred used on DOM ready
  var readyList = jQuery.Deferred();

  jQuery.fn.ready = function (fn) {
    readyList
      .then(fn)

      // Wrap jQuery.readyException in a function so that the lookup
      // happens at the time of error handling instead of callback
      // registration.
      .catch(function (error) {
        jQuery.readyException(error);
      });

    return this;
  };

  jQuery.extend({
    // Is the DOM ready to be used? Set to true once it occurs.
    isReady: false,

    // A counter to track how many items to wait for before
    // the ready event fires. See #6781
    readyWait: 1,

    // Handle when the DOM is ready
    ready: function (wait) {
      // Abort if there are pending holds or we're already ready
      if (wait === true ? --jQuery.readyWait : jQuery.isReady) {
        return;
      }

      // Remember that the DOM is ready
      jQuery.isReady = true;

      // If a normal DOM Ready event fired, decrement, and wait if need be
      if (wait !== true && --jQuery.readyWait > 0) {
        return;
      }

      // If there are functions bound, to execute
      readyList.resolveWith(document, [jQuery]);
    },
  });

  jQuery.ready.then = readyList.then;

  // The ready event handler and self cleanup method
  function completed() {
    document.removeEventListener('DOMContentLoaded', completed);
    window.removeEventListener('load', completed);
    jQuery.ready();
  }

  // Catch cases where $(document).ready() is called
  // after the browser event has already occurred.
  // Support: IE <=9 - 10 only
  // Older IE sometimes signals "interactive" too soon
  if (
    document.readyState === 'complete' ||
    (document.readyState !== 'loading' && !document.documentElement.doScroll)
  ) {
    // Handle it asynchronously to allow scripts the opportunity to delay ready
    window.setTimeout(jQuery.ready);
  } else {
    // Use the handy event callback
    document.addEventListener('DOMContentLoaded', completed);

    // A fallback to window.onload, that will always work
    window.addEventListener('load', completed);
  }

  // Multifunctional method to get and set values of a collection
  // The value/s can optionally be executed if it's a function
  var access = function (elems, fn, key, value, chainable, emptyGet, raw) {
    var i = 0,
      len = elems.length,
      bulk = key == null;

    // Sets many values
    if (toType(key) === 'object') {
      chainable = true;
      for (i in key) {
        access(elems, fn, i, key[i], true, emptyGet, raw);
      }

      // Sets one value
    } else if (value !== undefined) {
      chainable = true;

      if (!isFunction(value)) {
        raw = true;
      }

      if (bulk) {
        // Bulk operations run against the entire set
        if (raw) {
          fn.call(elems, value);
          fn = null;

          // ...except when executing function values
        } else {
          bulk = fn;
          fn = function (elem, _key, value) {
            return bulk.call(jQuery(elem), value);
          };
        }
      }

      if (fn) {
        for (; i < len; i++) {
          fn(elems[i], key, raw ? value : value.call(elems[i], i, fn(elems[i], key)));
        }
      }
    }

    if (chainable) {
      return elems;
    }

    // Gets
    if (bulk) {
      return fn.call(elems);
    }

    return len ? fn(elems[0], key) : emptyGet;
  };

  // Matches dashed string for camelizing
  var rmsPrefix = /^-ms-/,
    rdashAlpha = /-([a-z])/g;

  // Used by camelCase as callback to replace()
  function fcamelCase(_all, letter) {
    return letter.toUpperCase();
  }

  // Convert dashed to camelCase; used by the css and data modules
  // Support: IE <=9 - 11, Edge 12 - 15
  // Microsoft forgot to hump their vendor prefix (#9572)
  function camelCase(string) {
    return string.replace(rmsPrefix, 'ms-').replace(rdashAlpha, fcamelCase);
  }
  var acceptData = function (owner) {
    // Accepts only:
    //  - Node
    //    - Node.ELEMENT_NODE
    //    - Node.DOCUMENT_NODE
    //  - Object
    //    - Any
    return owner.nodeType === 1 || owner.nodeType === 9 || !+owner.nodeType;
  };

  function Data() {
    this.expando = jQuery.expando + Data.uid++;
  }

  Data.uid = 1;

  Data.prototype = {
    cache: function (owner) {
      // Check if the owner object already has a cache
      var value = owner[this.expando];

      // If not, create one
      if (!value) {
        value = {};

        // We can accept data for non-element nodes in modern browsers,
        // but we should not, see #8335.
        // Always return an empty object.
        if (acceptData(owner)) {
          // If it is a node unlikely to be stringify-ed or looped over
          // use plain assignment
          if (owner.nodeType) {
            owner[this.expando] = value;

            // Otherwise secure it in a non-enumerable property
            // configurable must be true to allow the property to be
            // deleted when data is removed
          } else {
            Object.defineProperty(owner, this.expando, {
              value: value,
              configurable: true,
            });
          }
        }
      }

      return value;
    },
    set: function (owner, data, value) {
      var prop,
        cache = this.cache(owner);

      // Handle: [ owner, key, value ] args
      // Always use camelCase key (gh-2257)
      if (typeof data === 'string') {
        cache[camelCase(data)] = value;

        // Handle: [ owner, { properties } ] args
      } else {
        // Copy the properties one-by-one to the cache object
        for (prop in data) {
          cache[camelCase(prop)] = data[prop];
        }
      }
      return cache;
    },
    get: function (owner, key) {
      return key === undefined
        ? this.cache(owner)
        : // Always use camelCase key (gh-2257)
          owner[this.expando] && owner[this.expando][camelCase(key)];
    },
    access: function (owner, key, value) {
      // In cases where either:
      //
      //   1. No key was specified
      //   2. A string key was specified, but no value provided
      //
      // Take the "read" path and allow the get method to determine
      // which value to return, respectively either:
      //
      //   1. The entire cache object
      //   2. The data stored at the key
      //
      if (key === undefined || (key && typeof key === 'string' && value === undefined)) {
        return this.get(owner, key);
      }

      // When the key is not a string, or both a key and value
      // are specified, set or extend (existing objects) with either:
      //
      //   1. An object of properties
      //   2. A key and value
      //
      this.set(owner, key, value);

      // Since the "set" path can have two possible entry points
      // return the expected data based on which path was taken[*]
      return value !== undefined ? value : key;
    },
    remove: function (owner, key) {
      var i,
        cache = owner[this.expando];

      if (cache === undefined) {
        return;
      }

      if (key !== undefined) {
        // Support array or space separated string of keys
        if (Array.isArray(key)) {
          // If key is an array of keys...
          // We always set camelCase keys, so remove that.
          key = key.map(camelCase);
        } else {
          key = camelCase(key);

          // If a key with the spaces exists, use it.
          // Otherwise, create an array by matching non-whitespace
          key = key in cache ? [key] : key.match(rnothtmlwhite) || [];
        }

        i = key.length;

        while (i--) {
          delete cache[key[i]];
        }
      }

      // Remove the expando if there's no more data
      if (key === undefined || jQuery.isEmptyObject(cache)) {
        // Support: Chrome <=35 - 45
        // Webkit & Blink performance suffers when deleting properties
        // from DOM nodes, so set to undefined instead
        // https://bugs.chromium.org/p/chromium/issues/detail?id=378607 (bug restricted)
        if (owner.nodeType) {
          owner[this.expando] = undefined;
        } else {
          delete owner[this.expando];
        }
      }
    },
    hasData: function (owner) {
      var cache = owner[this.expando];
      return cache !== undefined && !jQuery.isEmptyObject(cache);
    },
  };
  var dataPriv = new Data();

  var dataUser = new Data();

  //	Implementation Summary
  //
  //	1. Enforce API surface and semantic compatibility with 1.9.x branch
  //	2. Improve the module's maintainability by reducing the storage
  //		paths to a single mechanism.
  //	3. Use the same single mechanism to support "private" and "user" data.
  //	4. _Never_ expose "private" data to user code (TODO: Drop _data, _removeData)
  //	5. Avoid exposing implementation details on user objects (eg. expando properties)
  //	6. Provide a clear path for implementation upgrade to WeakMap in 2014

  var rbrace = /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,
    rmultiDash = /[A-Z]/g;

  function getData(data) {
    if (data === 'true') {
      return true;
    }

    if (data === 'false') {
      return false;
    }

    if (data === 'null') {
      return null;
    }

    // Only convert to a number if it doesn't change the string
    if (data === +data + '') {
      return +data;
    }

    if (rbrace.test(data)) {
      return JSON.parse(data);
    }

    return data;
  }

  function dataAttr(elem, key, data) {
    var name;

    // If nothing was found internally, try to fetch any
    // data from the HTML5 data-* attribute
    if (data === undefined && elem.nodeType === 1) {
      name = 'data-' + key.replace(rmultiDash, '-$&').toLowerCase();
      data = elem.getAttribute(name);

      if (typeof data === 'string') {
        try {
          data = getData(data);
        } catch (e) {}

        // Make sure we set the data so it isn't changed later
        dataUser.set(elem, key, data);
      } else {
        data = undefined;
      }
    }
    return data;
  }

  jQuery.extend({
    hasData: function (elem) {
      return dataUser.hasData(elem) || dataPriv.hasData(elem);
    },

    data: function (elem, name, data) {
      return dataUser.access(elem, name, data);
    },

    removeData: function (elem, name) {
      dataUser.remove(elem, name);
    },

    // TODO: Now that all calls to _data and _removeData have been replaced
    // with direct calls to dataPriv methods, these can be deprecated.
    _data: function (elem, name, data) {
      return dataPriv.access(elem, name, data);
    },

    _removeData: function (elem, name) {
      dataPriv.remove(elem, name);
    },
  });

  jQuery.fn.extend({
    data: function (key, value) {
      var i,
        name,
        data,
        elem = this[0],
        attrs = elem && elem.attributes;

      // Gets all values
      if (key === undefined) {
        if (this.length) {
          data = dataUser.get(elem);

          if (elem.nodeType === 1 && !dataPriv.get(elem, 'hasDataAttrs')) {
            i = attrs.length;
            while (i--) {
              // Support: IE 11 only
              // The attrs elements can be null (#14894)
              if (attrs[i]) {
                name = attrs[i].name;
                if (name.indexOf('data-') === 0) {
                  name = camelCase(name.slice(5));
                  dataAttr(elem, name, data[name]);
                }
              }
            }
            dataPriv.set(elem, 'hasDataAttrs', true);
          }
        }

        return data;
      }

      // Sets multiple values
      if (typeof key === 'object') {
        return this.each(function () {
          dataUser.set(this, key);
        });
      }

      return access(
        this,
        function (value) {
          var data;

          // The calling jQuery object (element matches) is not empty
          // (and therefore has an element appears at this[ 0 ]) and the
          // `value` parameter was not undefined. An empty jQuery object
          // will result in `undefined` for elem = this[ 0 ] which will
          // throw an exception if an attempt to read a data cache is made.
          if (elem && value === undefined) {
            // Attempt to get data from the cache
            // The key will always be camelCased in Data
            data = dataUser.get(elem, key);
            if (data !== undefined) {
              return data;
            }

            // Attempt to "discover" the data in
            // HTML5 custom data-* attrs
            data = dataAttr(elem, key);
            if (data !== undefined) {
              return data;
            }

            // We tried really hard, but the data doesn't exist.
            return;
          }

          // Set the data...
          this.each(function () {
            // We always store the camelCased key
            dataUser.set(this, key, value);
          });
        },
        null,
        value,
        arguments.length > 1,
        null,
        true
      );
    },

    removeData: function (key) {
      return this.each(function () {
        dataUser.remove(this, key);
      });
    },
  });

  jQuery.extend({
    queue: function (elem, type, data) {
      var queue;

      if (elem) {
        type = (type || 'fx') + 'queue';
        queue = dataPriv.get(elem, type);

        // Speed up dequeue by getting out quickly if this is just a lookup
        if (data) {
          if (!queue || Array.isArray(data)) {
            queue = dataPriv.access(elem, type, jQuery.makeArray(data));
          } else {
            queue.push(data);
          }
        }
        return queue || [];
      }
    },

    dequeue: function (elem, type) {
      type = type || 'fx';

      var queue = jQuery.queue(elem, type),
        startLength = queue.length,
        fn = queue.shift(),
        hooks = jQuery._queueHooks(elem, type),
        next = function () {
          jQuery.dequeue(elem, type);
        };

      // If the fx queue is dequeued, always remove the progress sentinel
      if (fn === 'inprogress') {
        fn = queue.shift();
        startLength--;
      }

      if (fn) {
        // Add a progress sentinel to prevent the fx queue from being
        // automatically dequeued
        if (type === 'fx') {
          queue.unshift('inprogress');
        }

        // Clear up the last queue stop function
        delete hooks.stop;
        fn.call(elem, next, hooks);
      }

      if (!startLength && hooks) {
        hooks.empty.fire();
      }
    },

    // Not public - generate a queueHooks object, or return the current one
    _queueHooks: function (elem, type) {
      var key = type + 'queueHooks';
      return (
        dataPriv.get(elem, key) ||
        dataPriv.access(elem, key, {
          empty: jQuery.Callbacks('once memory').add(function () {
            dataPriv.remove(elem, [type + 'queue', key]);
          }),
        })
      );
    },
  });

  jQuery.fn.extend({
    queue: function (type, data) {
      var setter = 2;

      if (typeof type !== 'string') {
        data = type;
        type = 'fx';
        setter--;
      }

      if (arguments.length < setter) {
        return jQuery.queue(this[0], type);
      }

      return data === undefined
        ? this
        : this.each(function () {
            var queue = jQuery.queue(this, type, data);

            // Ensure a hooks for this queue
            jQuery._queueHooks(this, type);

            if (type === 'fx' && queue[0] !== 'inprogress') {
              jQuery.dequeue(this, type);
            }
          });
    },
    dequeue: function (type) {
      return this.each(function () {
        jQuery.dequeue(this, type);
      });
    },
    clearQueue: function (type) {
      return this.queue(type || 'fx', []);
    },

    // Get a promise resolved when queues of a certain type
    // are emptied (fx is the type by default)
    promise: function (type, obj) {
      var tmp,
        count = 1,
        defer = jQuery.Deferred(),
        elements = this,
        i = this.length,
        resolve = function () {
          if (!--count) {
            defer.resolveWith(elements, [elements]);
          }
        };

      if (typeof type !== 'string') {
        obj = type;
        type = undefined;
      }
      type = type || 'fx';

      while (i--) {
        tmp = dataPriv.get(elements[i], type + 'queueHooks');
        if (tmp && tmp.empty) {
          count++;
          tmp.empty.add(resolve);
        }
      }
      resolve();
      return defer.promise(obj);
    },
  });
  var pnum = /[+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|)/.source;

  var rcssNum = new RegExp('^(?:([+-])=|)(' + pnum + ')([a-z%]*)$', 'i');

  var cssExpand = ['Top', 'Right', 'Bottom', 'Left'];

  var documentElement = document.documentElement;

  var isAttached = function (elem) {
      return jQuery.contains(elem.ownerDocument, elem);
    },
    composed = { composed: true };

  // Support: IE 9 - 11+, Edge 12 - 18+, iOS 10.0 - 10.2 only
  // Check attachment across shadow DOM boundaries when possible (gh-3504)
  // Support: iOS 10.0-10.2 only
  // Early iOS 10 versions support `attachShadow` but not `getRootNode`,
  // leading to errors. We need to check for `getRootNode`.
  if (documentElement.getRootNode) {
    isAttached = function (elem) {
      return (
        jQuery.contains(elem.ownerDocument, elem) ||
        elem.getRootNode(composed) === elem.ownerDocument
      );
    };
  }
  var isHiddenWithinTree = function (elem, el) {
    // isHiddenWithinTree might be called from jQuery#filter function;
    // in that case, element will be second argument
    elem = el || elem;

    // Inline style trumps all
    return (
      elem.style.display === 'none' ||
      (elem.style.display === '' &&
        // Otherwise, check computed style
        // Support: Firefox <=43 - 45
        // Disconnected elements can have computed display: none, so first confirm that elem is
        // in the document.
        isAttached(elem) &&
        jQuery.css(elem, 'display') === 'none')
    );
  };

  function adjustCSS(elem, prop, valueParts, tween) {
    var adjusted,
      scale,
      maxIterations = 20,
      currentValue = tween
        ? function () {
            return tween.cur();
          }
        : function () {
            return jQuery.css(elem, prop, '');
          },
      initial = currentValue(),
      unit = (valueParts && valueParts[3]) || (jQuery.cssNumber[prop] ? '' : 'px'),
      // Starting value computation is required for potential unit mismatches
      initialInUnit =
        elem.nodeType &&
        (jQuery.cssNumber[prop] || (unit !== 'px' && +initial)) &&
        rcssNum.exec(jQuery.css(elem, prop));

    if (initialInUnit && initialInUnit[3] !== unit) {
      // Support: Firefox <=54
      // Halve the iteration target value to prevent interference from CSS upper bounds (gh-2144)
      initial = initial / 2;

      // Trust units reported by jQuery.css
      unit = unit || initialInUnit[3];

      // Iteratively approximate from a nonzero starting point
      initialInUnit = +initial || 1;

      while (maxIterations--) {
        // Evaluate and update our best guess (doubling guesses that zero out).
        // Finish if the scale equals or crosses 1 (making the old*new product non-positive).
        jQuery.style(elem, prop, initialInUnit + unit);
        if ((1 - scale) * (1 - (scale = currentValue() / initial || 0.5)) <= 0) {
          maxIterations = 0;
        }
        initialInUnit = initialInUnit / scale;
      }

      initialInUnit = initialInUnit * 2;
      jQuery.style(elem, prop, initialInUnit + unit);

      // Make sure we update the tween properties later on
      valueParts = valueParts || [];
    }

    if (valueParts) {
      initialInUnit = +initialInUnit || +initial || 0;

      // Apply relative offset (+=/-=) if specified
      adjusted = valueParts[1]
        ? initialInUnit + (valueParts[1] + 1) * valueParts[2]
        : +valueParts[2];
      if (tween) {
        tween.unit = unit;
        tween.start = initialInUnit;
        tween.end = adjusted;
      }
    }
    return adjusted;
  }

  var defaultDisplayMap = {};

  function getDefaultDisplay(elem) {
    var temp,
      doc = elem.ownerDocument,
      nodeName = elem.nodeName,
      display = defaultDisplayMap[nodeName];

    if (display) {
      return display;
    }

    temp = doc.body.appendChild(doc.createElement(nodeName));
    display = jQuery.css(temp, 'display');

    temp.parentNode.removeChild(temp);

    if (display === 'none') {
      display = 'block';
    }
    defaultDisplayMap[nodeName] = display;

    return display;
  }

  function showHide(elements, show) {
    var display,
      elem,
      values = [],
      index = 0,
      length = elements.length;

    // Determine new display value for elements that need to change
    for (; index < length; index++) {
      elem = elements[index];
      if (!elem.style) {
        continue;
      }

      display = elem.style.display;
      if (show) {
        // Since we force visibility upon cascade-hidden elements, an immediate (and slow)
        // check is required in this first loop unless we have a nonempty display value (either
        // inline or about-to-be-restored)
        if (display === 'none') {
          values[index] = dataPriv.get(elem, 'display') || null;
          if (!values[index]) {
            elem.style.display = '';
          }
        }
        if (elem.style.display === '' && isHiddenWithinTree(elem)) {
          values[index] = getDefaultDisplay(elem);
        }
      } else {
        if (display !== 'none') {
          values[index] = 'none';

          // Remember what we're overwriting
          dataPriv.set(elem, 'display', display);
        }
      }
    }

    // Set the display of the elements in a second loop to avoid constant reflow
    for (index = 0; index < length; index++) {
      if (values[index] != null) {
        elements[index].style.display = values[index];
      }
    }

    return elements;
  }

  jQuery.fn.extend({
    show: function () {
      return showHide(this, true);
    },
    hide: function () {
      return showHide(this);
    },
    toggle: function (state) {
      if (typeof state === 'boolean') {
        return state ? this.show() : this.hide();
      }

      return this.each(function () {
        if (isHiddenWithinTree(this)) {
          jQuery(this).show();
        } else {
          jQuery(this).hide();
        }
      });
    },
  });
  var rcheckableType = /^(?:checkbox|radio)$/i;

  var rtagName = /<([a-z][^\/\0>\x20\t\r\n\f]*)/i;

  var rscriptType = /^$|^module$|\/(?:java|ecma)script/i;

  (function () {
    var fragment = document.createDocumentFragment(),
      div = fragment.appendChild(document.createElement('div')),
      input = document.createElement('input');

    // Support: Android 4.0 - 4.3 only
    // Check state lost if the name is set (#11217)
    // Support: Windows Web Apps (WWA)
    // `name` and `type` must use .setAttribute for WWA (#14901)
    input.setAttribute('type', 'radio');
    input.setAttribute('checked', 'checked');
    input.setAttribute('name', 't');

    div.appendChild(input);

    // Support: Android <=4.1 only
    // Older WebKit doesn't clone checked state correctly in fragments
    support.checkClone = div.cloneNode(true).cloneNode(true).lastChild.checked;

    // Support: IE <=11 only
    // Make sure textarea (and checkbox) defaultValue is properly cloned
    div.innerHTML = '<textarea>x</textarea>';
    support.noCloneChecked = !!div.cloneNode(true).lastChild.defaultValue;

    // Support: IE <=9 only
    // IE <=9 replaces <option> tags with their contents when inserted outside of
    // the select element.
    div.innerHTML = '<option></option>';
    support.option = !!div.lastChild;
  })();

  // We have to close these tags to support XHTML (#13200)
  var wrapMap = {
    // XHTML parsers do not magically insert elements in the
    // same way that tag soup parsers do. So we cannot shorten
    // this by omitting <tbody> or other required elements.
    thead: [1, '<table>', '</table>'],
    col: [2, '<table><colgroup>', '</colgroup></table>'],
    tr: [2, '<table><tbody>', '</tbody></table>'],
    td: [3, '<table><tbody><tr>', '</tr></tbody></table>'],

    _default: [0, '', ''],
  };

  wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
  wrapMap.th = wrapMap.td;

  // Support: IE <=9 only
  if (!support.option) {
    wrapMap.optgroup = wrapMap.option = [1, "<select multiple='multiple'>", '</select>'];
  }

  function getAll(context, tag) {
    // Support: IE <=9 - 11 only
    // Use typeof to avoid zero-argument method invocation on host objects (#15151)
    var ret;

    if (typeof context.getElementsByTagName !== 'undefined') {
      ret = context.getElementsByTagName(tag || '*');
    } else if (typeof context.querySelectorAll !== 'undefined') {
      ret = context.querySelectorAll(tag || '*');
    } else {
      ret = [];
    }

    if (tag === undefined || (tag && nodeName(context, tag))) {
      return jQuery.merge([context], ret);
    }

    return ret;
  }

  // Mark scripts as having already been evaluated
  function setGlobalEval(elems, refElements) {
    var i = 0,
      l = elems.length;

    for (; i < l; i++) {
      dataPriv.set(
        elems[i],
        'globalEval',
        !refElements || dataPriv.get(refElements[i], 'globalEval')
      );
    }
  }

  var rhtml = /<|&#?\w+;/;

  function buildFragment(elems, context, scripts, selection, ignored) {
    var elem,
      tmp,
      tag,
      wrap,
      attached,
      j,
      fragment = context.createDocumentFragment(),
      nodes = [],
      i = 0,
      l = elems.length;

    for (; i < l; i++) {
      elem = elems[i];

      if (elem || elem === 0) {
        // Add nodes directly
        if (toType(elem) === 'object') {
          // Support: Android <=4.0 only, PhantomJS 1 only
          // push.apply(_, arraylike) throws on ancient WebKit
          jQuery.merge(nodes, elem.nodeType ? [elem] : elem);

          // Convert non-html into a text node
        } else if (!rhtml.test(elem)) {
          nodes.push(context.createTextNode(elem));

          // Convert html into DOM nodes
        } else {
          tmp = tmp || fragment.appendChild(context.createElement('div'));

          // Deserialize a standard representation
          tag = (rtagName.exec(elem) || ['', ''])[1].toLowerCase();
          wrap = wrapMap[tag] || wrapMap._default;
          tmp.innerHTML = wrap[1] + jQuery.htmlPrefilter(elem) + wrap[2];

          // Descend through wrappers to the right content
          j = wrap[0];
          while (j--) {
            tmp = tmp.lastChild;
          }

          // Support: Android <=4.0 only, PhantomJS 1 only
          // push.apply(_, arraylike) throws on ancient WebKit
          jQuery.merge(nodes, tmp.childNodes);

          // Remember the top-level container
          tmp = fragment.firstChild;

          // Ensure the created nodes are orphaned (#12392)
          tmp.textContent = '';
        }
      }
    }

    // Remove wrapper from fragment
    fragment.textContent = '';

    i = 0;
    while ((elem = nodes[i++])) {
      // Skip elements already in the context collection (trac-4087)
      if (selection && jQuery.inArray(elem, selection) > -1) {
        if (ignored) {
          ignored.push(elem);
        }
        continue;
      }

      attached = isAttached(elem);

      // Append to fragment
      tmp = getAll(fragment.appendChild(elem), 'script');

      // Preserve script evaluation history
      if (attached) {
        setGlobalEval(tmp);
      }

      // Capture executables
      if (scripts) {
        j = 0;
        while ((elem = tmp[j++])) {
          if (rscriptType.test(elem.type || '')) {
            scripts.push(elem);
          }
        }
      }
    }

    return fragment;
  }

  var rtypenamespace = /^([^.]*)(?:\.(.+)|)/;

  function returnTrue() {
    return true;
  }

  function returnFalse() {
    return false;
  }

  // Support: IE <=9 - 11+
  // focus() and blur() are asynchronous, except when they are no-op.
  // So expect focus to be synchronous when the element is already active,
  // and blur to be synchronous when the element is not already active.
  // (focus and blur are always synchronous in other supported browsers,
  // this just defines when we can count on it).
  function expectSync(elem, type) {
    return (elem === safeActiveElement()) === (type === 'focus');
  }

  // Support: IE <=9 only
  // Accessing document.activeElement can throw unexpectedly
  // https://bugs.jquery.com/ticket/13393
  function safeActiveElement() {
    try {
      return document.activeElement;
    } catch (err) {}
  }

  function on(elem, types, selector, data, fn, one) {
    var origFn, type;

    // Types can be a map of types/handlers
    if (typeof types === 'object') {
      // ( types-Object, selector, data )
      if (typeof selector !== 'string') {
        // ( types-Object, data )
        data = data || selector;
        selector = undefined;
      }
      for (type in types) {
        on(elem, type, selector, data, types[type], one);
      }
      return elem;
    }

    if (data == null && fn == null) {
      // ( types, fn )
      fn = selector;
      data = selector = undefined;
    } else if (fn == null) {
      if (typeof selector === 'string') {
        // ( types, selector, fn )
        fn = data;
        data = undefined;
      } else {
        // ( types, data, fn )
        fn = data;
        data = selector;
        selector = undefined;
      }
    }
    if (fn === false) {
      fn = returnFalse;
    } else if (!fn) {
      return elem;
    }

    if (one === 1) {
      origFn = fn;
      fn = function (event) {
        // Can use an empty set, since event contains the info
        jQuery().off(event);
        return origFn.apply(this, arguments);
      };

      // Use same guid so caller can remove using origFn
      fn.guid = origFn.guid || (origFn.guid = jQuery.guid++);
    }
    return elem.each(function () {
      jQuery.event.add(this, types, fn, data, selector);
    });
  }

  /*
   * Helper functions for managing events -- not part of the public interface.
   * Props to Dean Edwards' addEvent library for many of the ideas.
   */
  jQuery.event = {
    global: {},

    add: function (elem, types, handler, data, selector) {
      var handleObjIn,
        eventHandle,
        tmp,
        events,
        t,
        handleObj,
        special,
        handlers,
        type,
        namespaces,
        origType,
        elemData = dataPriv.get(elem);

      // Only attach events to objects that accept data
      if (!acceptData(elem)) {
        return;
      }

      // Caller can pass in an object of custom data in lieu of the handler
      if (handler.handler) {
        handleObjIn = handler;
        handler = handleObjIn.handler;
        selector = handleObjIn.selector;
      }

      // Ensure that invalid selectors throw exceptions at attach time
      // Evaluate against documentElement in case elem is a non-element node (e.g., document)
      if (selector) {
        jQuery.find.matchesSelector(documentElement, selector);
      }

      // Make sure that the handler has a unique ID, used to find/remove it later
      if (!handler.guid) {
        handler.guid = jQuery.guid++;
      }

      // Init the element's event structure and main handler, if this is the first
      if (!(events = elemData.events)) {
        events = elemData.events = Object.create(null);
      }
      if (!(eventHandle = elemData.handle)) {
        eventHandle = elemData.handle = function (e) {
          // Discard the second event of a jQuery.event.trigger() and
          // when an event is called after a page has unloaded
          return typeof jQuery !== 'undefined' && jQuery.event.triggered !== e.type
            ? jQuery.event.dispatch.apply(elem, arguments)
            : undefined;
        };
      }

      // Handle multiple events separated by a space
      types = (types || '').match(rnothtmlwhite) || [''];
      t = types.length;
      while (t--) {
        tmp = rtypenamespace.exec(types[t]) || [];
        type = origType = tmp[1];
        namespaces = (tmp[2] || '').split('.').sort();

        // There *must* be a type, no attaching namespace-only handlers
        if (!type) {
          continue;
        }

        // If event changes its type, use the special event handlers for the changed type
        special = jQuery.event.special[type] || {};

        // If selector defined, determine special event api type, otherwise given type
        type = (selector ? special.delegateType : special.bindType) || type;

        // Update special based on newly reset type
        special = jQuery.event.special[type] || {};

        // handleObj is passed to all event handlers
        handleObj = jQuery.extend(
          {
            type: type,
            origType: origType,
            data: data,
            handler: handler,
            guid: handler.guid,
            selector: selector,
            needsContext: selector && jQuery.expr.match.needsContext.test(selector),
            namespace: namespaces.join('.'),
          },
          handleObjIn
        );

        // Init the event handler queue if we're the first
        if (!(handlers = events[type])) {
          handlers = events[type] = [];
          handlers.delegateCount = 0;

          // Only use addEventListener if the special events handler returns false
          if (!special.setup || special.setup.call(elem, data, namespaces, eventHandle) === false) {
            if (elem.addEventListener) {
              elem.addEventListener(type, eventHandle);
            }
          }
        }

        if (special.add) {
          special.add.call(elem, handleObj);

          if (!handleObj.handler.guid) {
            handleObj.handler.guid = handler.guid;
          }
        }

        // Add to the element's handler list, delegates in front
        if (selector) {
          handlers.splice(handlers.delegateCount++, 0, handleObj);
        } else {
          handlers.push(handleObj);
        }

        // Keep track of which events have ever been used, for event optimization
        jQuery.event.global[type] = true;
      }
    },

    // Detach an event or set of events from an element
    remove: function (elem, types, handler, selector, mappedTypes) {
      var j,
        origCount,
        tmp,
        events,
        t,
        handleObj,
        special,
        handlers,
        type,
        namespaces,
        origType,
        elemData = dataPriv.hasData(elem) && dataPriv.get(elem);

      if (!elemData || !(events = elemData.events)) {
        return;
      }

      // Once for each type.namespace in types; type may be omitted
      types = (types || '').match(rnothtmlwhite) || [''];
      t = types.length;
      while (t--) {
        tmp = rtypenamespace.exec(types[t]) || [];
        type = origType = tmp[1];
        namespaces = (tmp[2] || '').split('.').sort();

        // Unbind all events (on this namespace, if provided) for the element
        if (!type) {
          for (type in events) {
            jQuery.event.remove(elem, type + types[t], handler, selector, true);
          }
          continue;
        }

        special = jQuery.event.special[type] || {};
        type = (selector ? special.delegateType : special.bindType) || type;
        handlers = events[type] || [];
        tmp = tmp[2] && new RegExp('(^|\\.)' + namespaces.join('\\.(?:.*\\.|)') + '(\\.|$)');

        // Remove matching events
        origCount = j = handlers.length;
        while (j--) {
          handleObj = handlers[j];

          if (
            (mappedTypes || origType === handleObj.origType) &&
            (!handler || handler.guid === handleObj.guid) &&
            (!tmp || tmp.test(handleObj.namespace)) &&
            (!selector ||
              selector === handleObj.selector ||
              (selector === '**' && handleObj.selector))
          ) {
            handlers.splice(j, 1);

            if (handleObj.selector) {
              handlers.delegateCount--;
            }
            if (special.remove) {
              special.remove.call(elem, handleObj);
            }
          }
        }

        // Remove generic event handler if we removed something and no more handlers exist
        // (avoids potential for endless recursion during removal of special event handlers)
        if (origCount && !handlers.length) {
          if (
            !special.teardown ||
            special.teardown.call(elem, namespaces, elemData.handle) === false
          ) {
            jQuery.removeEvent(elem, type, elemData.handle);
          }

          delete events[type];
        }
      }

      // Remove data and the expando if it's no longer used
      if (jQuery.isEmptyObject(events)) {
        dataPriv.remove(elem, 'handle events');
      }
    },

    dispatch: function (nativeEvent) {
      var i,
        j,
        ret,
        matched,
        handleObj,
        handlerQueue,
        args = new Array(arguments.length),
        // Make a writable jQuery.Event from the native event object
        event = jQuery.event.fix(nativeEvent),
        handlers = (dataPriv.get(this, 'events') || Object.create(null))[event.type] || [],
        special = jQuery.event.special[event.type] || {};

      // Use the fix-ed jQuery.Event rather than the (read-only) native event
      args[0] = event;

      for (i = 1; i < arguments.length; i++) {
        args[i] = arguments[i];
      }

      event.delegateTarget = this;

      // Call the preDispatch hook for the mapped type, and let it bail if desired
      if (special.preDispatch && special.preDispatch.call(this, event) === false) {
        return;
      }

      // Determine handlers
      handlerQueue = jQuery.event.handlers.call(this, event, handlers);

      // Run delegates first; they may want to stop propagation beneath us
      i = 0;
      while ((matched = handlerQueue[i++]) && !event.isPropagationStopped()) {
        event.currentTarget = matched.elem;

        j = 0;
        while ((handleObj = matched.handlers[j++]) && !event.isImmediatePropagationStopped()) {
          // If the event is namespaced, then each handler is only invoked if it is
          // specially universal or its namespaces are a superset of the event's.
          if (
            !event.rnamespace ||
            handleObj.namespace === false ||
            event.rnamespace.test(handleObj.namespace)
          ) {
            event.handleObj = handleObj;
            event.data = handleObj.data;

            ret = (
              (jQuery.event.special[handleObj.origType] || {}).handle || handleObj.handler
            ).apply(matched.elem, args);

            if (ret !== undefined) {
              if ((event.result = ret) === false) {
                event.preventDefault();
                event.stopPropagation();
              }
            }
          }
        }
      }

      // Call the postDispatch hook for the mapped type
      if (special.postDispatch) {
        special.postDispatch.call(this, event);
      }

      return event.result;
    },

    handlers: function (event, handlers) {
      var i,
        handleObj,
        sel,
        matchedHandlers,
        matchedSelectors,
        handlerQueue = [],
        delegateCount = handlers.delegateCount,
        cur = event.target;

      // Find delegate handlers
      if (
        delegateCount &&
        // Support: IE <=9
        // Black-hole SVG <use> instance trees (trac-13180)
        cur.nodeType &&
        // Support: Firefox <=42
        // Suppress spec-violating clicks indicating a non-primary pointer button (trac-3861)
        // https://www.w3.org/TR/DOM-Level-3-Events/#event-type-click
        // Support: IE 11 only
        // ...but not arrow key "clicks" of radio inputs, which can have `button` -1 (gh-2343)
        !(event.type === 'click' && event.button >= 1)
      ) {
        for (; cur !== this; cur = cur.parentNode || this) {
          // Don't check non-elements (#13208)
          // Don't process clicks on disabled elements (#6911, #8165, #11382, #11764)
          if (cur.nodeType === 1 && !(event.type === 'click' && cur.disabled === true)) {
            matchedHandlers = [];
            matchedSelectors = {};
            for (i = 0; i < delegateCount; i++) {
              handleObj = handlers[i];

              // Don't conflict with Object.prototype properties (#13203)
              sel = handleObj.selector + ' ';

              if (matchedSelectors[sel] === undefined) {
                matchedSelectors[sel] = handleObj.needsContext
                  ? jQuery(sel, this).index(cur) > -1
                  : jQuery.find(sel, this, null, [cur]).length;
              }
              if (matchedSelectors[sel]) {
                matchedHandlers.push(handleObj);
              }
            }
            if (matchedHandlers.length) {
              handlerQueue.push({ elem: cur, handlers: matchedHandlers });
            }
          }
        }
      }

      // Add the remaining (directly-bound) handlers
      cur = this;
      if (delegateCount < handlers.length) {
        handlerQueue.push({ elem: cur, handlers: handlers.slice(delegateCount) });
      }

      return handlerQueue;
    },

    addProp: function (name, hook) {
      Object.defineProperty(jQuery.Event.prototype, name, {
        enumerable: true,
        configurable: true,

        get: isFunction(hook)
          ? function () {
              if (this.originalEvent) {
                return hook(this.originalEvent);
              }
            }
          : function () {
              if (this.originalEvent) {
                return this.originalEvent[name];
              }
            },

        set: function (value) {
          Object.defineProperty(this, name, {
            enumerable: true,
            configurable: true,
            writable: true,
            value: value,
          });
        },
      });
    },

    fix: function (originalEvent) {
      return originalEvent[jQuery.expando] ? originalEvent : new jQuery.Event(originalEvent);
    },

    special: {
      load: {
        // Prevent triggered image.load events from bubbling to window.load
        noBubble: true,
      },
      click: {
        // Utilize native event to ensure correct state for checkable inputs
        setup: function (data) {
          // For mutual compressibility with _default, replace `this` access with a local var.
          // `|| data` is dead code meant only to preserve the variable through minification.
          var el = this || data;

          // Claim the first handler
          if (rcheckableType.test(el.type) && el.click && nodeName(el, 'input')) {
            // dataPriv.set( el, "click", ... )
            leverageNative(el, 'click', returnTrue);
          }

          // Return false to allow normal processing in the caller
          return false;
        },
        trigger: function (data) {
          // For mutual compressibility with _default, replace `this` access with a local var.
          // `|| data` is dead code meant only to preserve the variable through minification.
          var el = this || data;

          // Force setup before triggering a click
          if (rcheckableType.test(el.type) && el.click && nodeName(el, 'input')) {
            leverageNative(el, 'click');
          }

          // Return non-false to allow normal event-path propagation
          return true;
        },

        // For cross-browser consistency, suppress native .click() on links
        // Also prevent it if we're currently inside a leveraged native-event stack
        _default: function (event) {
          var target = event.target;
          return (
            (rcheckableType.test(target.type) &&
              target.click &&
              nodeName(target, 'input') &&
              dataPriv.get(target, 'click')) ||
            nodeName(target, 'a')
          );
        },
      },

      beforeunload: {
        postDispatch: function (event) {
          // Support: Firefox 20+
          // Firefox doesn't alert if the returnValue field is not set.
          if (event.result !== undefined && event.originalEvent) {
            event.originalEvent.returnValue = event.result;
          }
        },
      },
    },
  };

  // Ensure the presence of an event listener that handles manually-triggered
  // synthetic events by interrupting progress until reinvoked in response to
  // *native* events that it fires directly, ensuring that state changes have
  // already occurred before other listeners are invoked.
  function leverageNative(el, type, expectSync) {
    // Missing expectSync indicates a trigger call, which must force setup through jQuery.event.add
    if (!expectSync) {
      if (dataPriv.get(el, type) === undefined) {
        jQuery.event.add(el, type, returnTrue);
      }
      return;
    }

    // Register the controller as a special universal handler for all event namespaces
    dataPriv.set(el, type, false);
    jQuery.event.add(el, type, {
      namespace: false,
      handler: function (event) {
        var notAsync,
          result,
          saved = dataPriv.get(this, type);

        if (event.isTrigger & 1 && this[type]) {
          // Interrupt processing of the outer synthetic .trigger()ed event
          // Saved data should be false in such cases, but might be a leftover capture object
          // from an async native handler (gh-4350)
          if (!saved.length) {
            // Store arguments for use when handling the inner native event
            // There will always be at least one argument (an event object), so this array
            // will not be confused with a leftover capture object.
            saved = slice.call(arguments);
            dataPriv.set(this, type, saved);

            // Trigger the native event and capture its result
            // Support: IE <=9 - 11+
            // focus() and blur() are asynchronous
            notAsync = expectSync(this, type);
            this[type]();
            result = dataPriv.get(this, type);
            if (saved !== result || notAsync) {
              dataPriv.set(this, type, false);
            } else {
              result = {};
            }
            if (saved !== result) {
              // Cancel the outer synthetic event
              event.stopImmediatePropagation();
              event.preventDefault();

              // Support: Chrome 86+
              // In Chrome, if an element having a focusout handler is blurred by
              // clicking outside of it, it invokes the handler synchronously. If
              // that handler calls `.remove()` on the element, the data is cleared,
              // leaving `result` undefined. We need to guard against this.
              return result && result.value;
            }

            // If this is an inner synthetic event for an event with a bubbling surrogate
            // (focus or blur), assume that the surrogate already propagated from triggering the
            // native event and prevent that from happening again here.
            // This technically gets the ordering wrong w.r.t. to `.trigger()` (in which the
            // bubbling surrogate propagates *after* the non-bubbling base), but that seems
            // less bad than duplication.
          } else if ((jQuery.event.special[type] || {}).delegateType) {
            event.stopPropagation();
          }

          // If this is a native event triggered above, everything is now in order
          // Fire an inner synthetic event with the original arguments
        } else if (saved.length) {
          // ...and capture the result
          dataPriv.set(this, type, {
            value: jQuery.event.trigger(
              // Support: IE <=9 - 11+
              // Extend with the prototype to reset the above stopImmediatePropagation()
              jQuery.extend(saved[0], jQuery.Event.prototype),
              saved.slice(1),
              this
            ),
          });

          // Abort handling of the native event
          event.stopImmediatePropagation();
        }
      },
    });
  }

  jQuery.removeEvent = function (elem, type, handle) {
    // This "if" is needed for plain objects
    if (elem.removeEventListener) {
      elem.removeEventListener(type, handle);
    }
  };

  jQuery.Event = function (src, props) {
    // Allow instantiation without the 'new' keyword
    if (!(this instanceof jQuery.Event)) {
      return new jQuery.Event(src, props);
    }

    // Event object
    if (src && src.type) {
      this.originalEvent = src;
      this.type = src.type;

      // Events bubbling up the document may have been marked as prevented
      // by a handler lower down the tree; reflect the correct value.
      this.isDefaultPrevented =
        src.defaultPrevented ||
        (src.defaultPrevented === undefined &&
          // Support: Android <=2.3 only
          src.returnValue === false)
          ? returnTrue
          : returnFalse;

      // Create target properties
      // Support: Safari <=6 - 7 only
      // Target should not be a text node (#504, #13143)
      this.target = src.target && src.target.nodeType === 3 ? src.target.parentNode : src.target;

      this.currentTarget = src.currentTarget;
      this.relatedTarget = src.relatedTarget;

      // Event type
    } else {
      this.type = src;
    }

    // Put explicitly provided properties onto the event object
    if (props) {
      jQuery.extend(this, props);
    }

    // Create a timestamp if incoming event doesn't have one
    this.timeStamp = (src && src.timeStamp) || Date.now();

    // Mark it as fixed
    this[jQuery.expando] = true;
  };

  // jQuery.Event is based on DOM3 Events as specified by the ECMAScript Language Binding
  // https://www.w3.org/TR/2003/WD-DOM-Level-3-Events-20030331/ecma-script-binding.html
  jQuery.Event.prototype = {
    constructor: jQuery.Event,
    isDefaultPrevented: returnFalse,
    isPropagationStopped: returnFalse,
    isImmediatePropagationStopped: returnFalse,
    isSimulated: false,

    preventDefault: function () {
      var e = this.originalEvent;

      this.isDefaultPrevented = returnTrue;

      if (e && !this.isSimulated) {
        e.preventDefault();
      }
    },
    stopPropagation: function () {
      var e = this.originalEvent;

      this.isPropagationStopped = returnTrue;

      if (e && !this.isSimulated) {
        e.stopPropagation();
      }
    },
    stopImmediatePropagation: function () {
      var e = this.originalEvent;

      this.isImmediatePropagationStopped = returnTrue;

      if (e && !this.isSimulated) {
        e.stopImmediatePropagation();
      }

      this.stopPropagation();
    },
  };

  // Includes all common event props including KeyEvent and MouseEvent specific props
  jQuery.each(
    {
      altKey: true,
      bubbles: true,
      cancelable: true,
      changedTouches: true,
      ctrlKey: true,
      detail: true,
      eventPhase: true,
      metaKey: true,
      pageX: true,
      pageY: true,
      shiftKey: true,
      view: true,
      char: true,
      code: true,
      charCode: true,
      key: true,
      keyCode: true,
      button: true,
      buttons: true,
      clientX: true,
      clientY: true,
      offsetX: true,
      offsetY: true,
      pointerId: true,
      pointerType: true,
      screenX: true,
      screenY: true,
      targetTouches: true,
      toElement: true,
      touches: true,
      which: true,
    },
    jQuery.event.addProp
  );

  jQuery.each({ focus: 'focusin', blur: 'focusout' }, function (type, delegateType) {
    jQuery.event.special[type] = {
      // Utilize native event if possible so blur/focus sequence is correct
      setup: function () {
        // Claim the first handler
        // dataPriv.set( this, "focus", ... )
        // dataPriv.set( this, "blur", ... )
        leverageNative(this, type, expectSync);

        // Return false to allow normal processing in the caller
        return false;
      },
      trigger: function () {
        // Force setup before trigger
        leverageNative(this, type);

        // Return non-false to allow normal event-path propagation
        return true;
      },

      // Suppress native focus or blur as it's already being fired
      // in leverageNative.
      _default: function () {
        return true;
      },

      delegateType: delegateType,
    };
  });

  // Create mouseenter/leave events using mouseover/out and event-time checks
  // so that event delegation works in jQuery.
  // Do the same for pointerenter/pointerleave and pointerover/pointerout
  //
  // Support: Safari 7 only
  // Safari sends mouseenter too often; see:
  // https://bugs.chromium.org/p/chromium/issues/detail?id=470258
  // for the description of the bug (it existed in older Chrome versions as well).
  jQuery.each(
    {
      mouseenter: 'mouseover',
      mouseleave: 'mouseout',
      pointerenter: 'pointerover',
      pointerleave: 'pointerout',
    },
    function (orig, fix) {
      jQuery.event.special[orig] = {
        delegateType: fix,
        bindType: fix,

        handle: function (event) {
          var ret,
            target = this,
            related = event.relatedTarget,
            handleObj = event.handleObj;

          // For mouseenter/leave call the handler if related is outside the target.
          // NB: No relatedTarget if the mouse left/entered the browser window
          if (!related || (related !== target && !jQuery.contains(target, related))) {
            event.type = handleObj.origType;
            ret = handleObj.handler.apply(this, arguments);
            event.type = fix;
          }
          return ret;
        },
      };
    }
  );

  jQuery.fn.extend({
    on: function (types, selector, data, fn) {
      return on(this, types, selector, data, fn);
    },
    one: function (types, selector, data, fn) {
      return on(this, types, selector, data, fn, 1);
    },
    off: function (types, selector, fn) {
      var handleObj, type;
      if (types && types.preventDefault && types.handleObj) {
        // ( event )  dispatched jQuery.Event
        handleObj = types.handleObj;
        jQuery(types.delegateTarget).off(
          handleObj.namespace ? handleObj.origType + '.' + handleObj.namespace : handleObj.origType,
          handleObj.selector,
          handleObj.handler
        );
        return this;
      }
      if (typeof types === 'object') {
        // ( types-object [, selector] )
        for (type in types) {
          this.off(type, selector, types[type]);
        }
        return this;
      }
      if (selector === false || typeof selector === 'function') {
        // ( types [, fn] )
        fn = selector;
        selector = undefined;
      }
      if (fn === false) {
        fn = returnFalse;
      }
      return this.each(function () {
        jQuery.event.remove(this, types, fn, selector);
      });
    },
  });

  var // Support: IE <=10 - 11, Edge 12 - 13 only
    // In IE/Edge using regex groups here causes severe slowdowns.
    // See https://connect.microsoft.com/IE/feedback/details/1736512/
    rnoInnerhtml = /<script|<style|<link/i,
    // checked="checked" or checked
    rchecked = /checked\s*(?:[^=]|=\s*.checked.)/i,
    rcleanScript = /^\s*<!(?:\[CDATA\[|--)|(?:\]\]|--)>\s*$/g;

  // Prefer a tbody over its parent table for containing new rows
  function manipulationTarget(elem, content) {
    if (
      nodeName(elem, 'table') &&
      nodeName(content.nodeType !== 11 ? content : content.firstChild, 'tr')
    ) {
      return jQuery(elem).children('tbody')[0] || elem;
    }

    return elem;
  }

  // Replace/restore the type attribute of script elements for safe DOM manipulation
  function disableScript(elem) {
    elem.type = (elem.getAttribute('type') !== null) + '/' + elem.type;
    return elem;
  }
  function restoreScript(elem) {
    if ((elem.type || '').slice(0, 5) === 'true/') {
      elem.type = elem.type.slice(5);
    } else {
      elem.removeAttribute('type');
    }

    return elem;
  }

  function cloneCopyEvent(src, dest) {
    var i, l, type, pdataOld, udataOld, udataCur, events;

    if (dest.nodeType !== 1) {
      return;
    }

    // 1. Copy private data: events, handlers, etc.
    if (dataPriv.hasData(src)) {
      pdataOld = dataPriv.get(src);
      events = pdataOld.events;

      if (events) {
        dataPriv.remove(dest, 'handle events');

        for (type in events) {
          for (i = 0, l = events[type].length; i < l; i++) {
            jQuery.event.add(dest, type, events[type][i]);
          }
        }
      }
    }

    // 2. Copy user data
    if (dataUser.hasData(src)) {
      udataOld = dataUser.access(src);
      udataCur = jQuery.extend({}, udataOld);

      dataUser.set(dest, udataCur);
    }
  }

  // Fix IE bugs, see support tests
  function fixInput(src, dest) {
    var nodeName = dest.nodeName.toLowerCase();

    // Fails to persist the checked state of a cloned checkbox or radio button.
    if (nodeName === 'input' && rcheckableType.test(src.type)) {
      dest.checked = src.checked;

      // Fails to return the selected option to the default selected state when cloning options
    } else if (nodeName === 'input' || nodeName === 'textarea') {
      dest.defaultValue = src.defaultValue;
    }
  }

  function domManip(collection, args, callback, ignored) {
    // Flatten any nested arrays
    args = flat(args);

    var fragment,
      first,
      scripts,
      hasScripts,
      node,
      doc,
      i = 0,
      l = collection.length,
      iNoClone = l - 1,
      value = args[0],
      valueIsFunction = isFunction(value);

    // We can't cloneNode fragments that contain checked, in WebKit
    if (
      valueIsFunction ||
      (l > 1 && typeof value === 'string' && !support.checkClone && rchecked.test(value))
    ) {
      return collection.each(function (index) {
        var self = collection.eq(index);
        if (valueIsFunction) {
          args[0] = value.call(this, index, self.html());
        }
        domManip(self, args, callback, ignored);
      });
    }

    if (l) {
      fragment = buildFragment(args, collection[0].ownerDocument, false, collection, ignored);
      first = fragment.firstChild;

      if (fragment.childNodes.length === 1) {
        fragment = first;
      }

      // Require either new content or an interest in ignored elements to invoke the callback
      if (first || ignored) {
        scripts = jQuery.map(getAll(fragment, 'script'), disableScript);
        hasScripts = scripts.length;

        // Use the original fragment for the last item
        // instead of the first because it can end up
        // being emptied incorrectly in certain situations (#8070).
        for (; i < l; i++) {
          node = fragment;

          if (i !== iNoClone) {
            node = jQuery.clone(node, true, true);

            // Keep references to cloned scripts for later restoration
            if (hasScripts) {
              // Support: Android <=4.0 only, PhantomJS 1 only
              // push.apply(_, arraylike) throws on ancient WebKit
              jQuery.merge(scripts, getAll(node, 'script'));
            }
          }

          callback.call(collection[i], node, i);
        }

        if (hasScripts) {
          doc = scripts[scripts.length - 1].ownerDocument;

          // Reenable scripts
          jQuery.map(scripts, restoreScript);

          // Evaluate executable scripts on first document insertion
          for (i = 0; i < hasScripts; i++) {
            node = scripts[i];
            if (
              rscriptType.test(node.type || '') &&
              !dataPriv.access(node, 'globalEval') &&
              jQuery.contains(doc, node)
            ) {
              if (node.src && (node.type || '').toLowerCase() !== 'module') {
                // Optional AJAX dependency, but won't run scripts if not present
                if (jQuery._evalUrl && !node.noModule) {
                  jQuery._evalUrl(
                    node.src,
                    {
                      nonce: node.nonce || node.getAttribute('nonce'),
                    },
                    doc
                  );
                }
              } else {
                DOMEval(node.textContent.replace(rcleanScript, ''), node, doc);
              }
            }
          }
        }
      }
    }

    return collection;
  }

  function remove(elem, selector, keepData) {
    var node,
      nodes = selector ? jQuery.filter(selector, elem) : elem,
      i = 0;

    for (; (node = nodes[i]) != null; i++) {
      if (!keepData && node.nodeType === 1) {
        jQuery.cleanData(getAll(node));
      }

      if (node.parentNode) {
        if (keepData && isAttached(node)) {
          setGlobalEval(getAll(node, 'script'));
        }
        node.parentNode.removeChild(node);
      }
    }

    return elem;
  }

  jQuery.extend({
    htmlPrefilter: function (html) {
      return html;
    },

    clone: function (elem, dataAndEvents, deepDataAndEvents) {
      var i,
        l,
        srcElements,
        destElements,
        clone = elem.cloneNode(true),
        inPage = isAttached(elem);

      // Fix IE cloning issues
      if (
        !support.noCloneChecked &&
        (elem.nodeType === 1 || elem.nodeType === 11) &&
        !jQuery.isXMLDoc(elem)
      ) {
        // We eschew Sizzle here for performance reasons: https://jsperf.com/getall-vs-sizzle/2
        destElements = getAll(clone);
        srcElements = getAll(elem);

        for (i = 0, l = srcElements.length; i < l; i++) {
          fixInput(srcElements[i], destElements[i]);
        }
      }

      // Copy the events from the original to the clone
      if (dataAndEvents) {
        if (deepDataAndEvents) {
          srcElements = srcElements || getAll(elem);
          destElements = destElements || getAll(clone);

          for (i = 0, l = srcElements.length; i < l; i++) {
            cloneCopyEvent(srcElements[i], destElements[i]);
          }
        } else {
          cloneCopyEvent(elem, clone);
        }
      }

      // Preserve script evaluation history
      destElements = getAll(clone, 'script');
      if (destElements.length > 0) {
        setGlobalEval(destElements, !inPage && getAll(elem, 'script'));
      }

      // Return the cloned set
      return clone;
    },

    cleanData: function (elems) {
      var data,
        elem,
        type,
        special = jQuery.event.special,
        i = 0;

      for (; (elem = elems[i]) !== undefined; i++) {
        if (acceptData(elem)) {
          if ((data = elem[dataPriv.expando])) {
            if (data.events) {
              for (type in data.events) {
                if (special[type]) {
                  jQuery.event.remove(elem, type);

                  // This is a shortcut to avoid jQuery.event.remove's overhead
                } else {
                  jQuery.removeEvent(elem, type, data.handle);
                }
              }
            }

            // Support: Chrome <=35 - 45+
            // Assign undefined instead of using delete, see Data#remove
            elem[dataPriv.expando] = undefined;
          }
          if (elem[dataUser.expando]) {
            // Support: Chrome <=35 - 45+
            // Assign undefined instead of using delete, see Data#remove
            elem[dataUser.expando] = undefined;
          }
        }
      }
    },
  });

  jQuery.fn.extend({
    detach: function (selector) {
      return remove(this, selector, true);
    },

    remove: function (selector) {
      return remove(this, selector);
    },

    text: function (value) {
      return access(
        this,
        function (value) {
          return value === undefined
            ? jQuery.text(this)
            : this.empty().each(function () {
                if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
                  this.textContent = value;
                }
              });
        },
        null,
        value,
        arguments.length
      );
    },

    append: function () {
      return domManip(this, arguments, function (elem) {
        if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
          var target = manipulationTarget(this, elem);
          target.appendChild(elem);
        }
      });
    },

    prepend: function () {
      return domManip(this, arguments, function (elem) {
        if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
          var target = manipulationTarget(this, elem);
          target.insertBefore(elem, target.firstChild);
        }
      });
    },

    before: function () {
      return domManip(this, arguments, function (elem) {
        if (this.parentNode) {
          this.parentNode.insertBefore(elem, this);
        }
      });
    },

    after: function () {
      return domManip(this, arguments, function (elem) {
        if (this.parentNode) {
          this.parentNode.insertBefore(elem, this.nextSibling);
        }
      });
    },

    empty: function () {
      var elem,
        i = 0;

      for (; (elem = this[i]) != null; i++) {
        if (elem.nodeType === 1) {
          // Prevent memory leaks
          jQuery.cleanData(getAll(elem, false));

          // Remove any remaining nodes
          elem.textContent = '';
        }
      }

      return this;
    },

    clone: function (dataAndEvents, deepDataAndEvents) {
      dataAndEvents = dataAndEvents == null ? false : dataAndEvents;
      deepDataAndEvents = deepDataAndEvents == null ? dataAndEvents : deepDataAndEvents;

      return this.map(function () {
        return jQuery.clone(this, dataAndEvents, deepDataAndEvents);
      });
    },

    html: function (value) {
      return access(
        this,
        function (value) {
          var elem = this[0] || {},
            i = 0,
            l = this.length;

          if (value === undefined && elem.nodeType === 1) {
            return elem.innerHTML;
          }

          // See if we can take a shortcut and just use innerHTML
          if (
            typeof value === 'string' &&
            !rnoInnerhtml.test(value) &&
            !wrapMap[(rtagName.exec(value) || ['', ''])[1].toLowerCase()]
          ) {
            value = jQuery.htmlPrefilter(value);

            try {
              for (; i < l; i++) {
                elem = this[i] || {};

                // Remove element nodes and prevent memory leaks
                if (elem.nodeType === 1) {
                  jQuery.cleanData(getAll(elem, false));
                  elem.innerHTML = value;
                }
              }

              elem = 0;

              // If using innerHTML throws an exception, use the fallback method
            } catch (e) {}
          }

          if (elem) {
            this.empty().append(value);
          }
        },
        null,
        value,
        arguments.length
      );
    },

    replaceWith: function () {
      var ignored = [];

      // Make the changes, replacing each non-ignored context element with the new content
      return domManip(
        this,
        arguments,
        function (elem) {
          var parent = this.parentNode;

          if (jQuery.inArray(this, ignored) < 0) {
            jQuery.cleanData(getAll(this));
            if (parent) {
              parent.replaceChild(elem, this);
            }
          }

          // Force callback invocation
        },
        ignored
      );
    },
  });

  jQuery.each(
    {
      appendTo: 'append',
      prependTo: 'prepend',
      insertBefore: 'before',
      insertAfter: 'after',
      replaceAll: 'replaceWith',
    },
    function (name, original) {
      jQuery.fn[name] = function (selector) {
        var elems,
          ret = [],
          insert = jQuery(selector),
          last = insert.length - 1,
          i = 0;

        for (; i <= last; i++) {
          elems = i === last ? this : this.clone(true);
          jQuery(insert[i])[original](elems);

          // Support: Android <=4.0 only, PhantomJS 1 only
          // .get() because push.apply(_, arraylike) throws on ancient WebKit
          push.apply(ret, elems.get());
        }

        return this.pushStack(ret);
      };
    }
  );
  var rnumnonpx = new RegExp('^(' + pnum + ')(?!px)[a-z%]+$', 'i');

  var getStyles = function (elem) {
    // Support: IE <=11 only, Firefox <=30 (#15098, #14150)
    // IE throws on elements created in popups
    // FF meanwhile throws on frame elements through "defaultView.getComputedStyle"
    var view = elem.ownerDocument.defaultView;

    if (!view || !view.opener) {
      view = window;
    }

    return view.getComputedStyle(elem);
  };

  var swap = function (elem, options, callback) {
    var ret,
      name,
      old = {};

    // Remember the old values, and insert the new ones
    for (name in options) {
      old[name] = elem.style[name];
      elem.style[name] = options[name];
    }

    ret = callback.call(elem);

    // Revert the old values
    for (name in options) {
      elem.style[name] = old[name];
    }

    return ret;
  };

  var rboxStyle = new RegExp(cssExpand.join('|'), 'i');

  (function () {
    // Executing both pixelPosition & boxSizingReliable tests require only one layout
    // so they're executed at the same time to save the second computation.
    function computeStyleTests() {
      // This is a singleton, we need to execute it only once
      if (!div) {
        return;
      }

      container.style.cssText =
        'position:absolute;left:-11111px;width:60px;' + 'margin-top:1px;padding:0;border:0';
      div.style.cssText =
        'position:relative;display:block;box-sizing:border-box;overflow:scroll;' +
        'margin:auto;border:1px;padding:1px;' +
        'width:60%;top:1%';
      documentElement.appendChild(container).appendChild(div);

      var divStyle = window.getComputedStyle(div);
      pixelPositionVal = divStyle.top !== '1%';

      // Support: Android 4.0 - 4.3 only, Firefox <=3 - 44
      reliableMarginLeftVal = roundPixelMeasures(divStyle.marginLeft) === 12;

      // Support: Android 4.0 - 4.3 only, Safari <=9.1 - 10.1, iOS <=7.0 - 9.3
      // Some styles come back with percentage values, even though they shouldn't
      div.style.right = '60%';
      pixelBoxStylesVal = roundPixelMeasures(divStyle.right) === 36;

      // Support: IE 9 - 11 only
      // Detect misreporting of content dimensions for box-sizing:border-box elements
      boxSizingReliableVal = roundPixelMeasures(divStyle.width) === 36;

      // Support: IE 9 only
      // Detect overflow:scroll screwiness (gh-3699)
      // Support: Chrome <=64
      // Don't get tricked when zoom affects offsetWidth (gh-4029)
      div.style.position = 'absolute';
      scrollboxSizeVal = roundPixelMeasures(div.offsetWidth / 3) === 12;

      documentElement.removeChild(container);

      // Nullify the div so it wouldn't be stored in the memory and
      // it will also be a sign that checks already performed
      div = null;
    }

    function roundPixelMeasures(measure) {
      return Math.round(parseFloat(measure));
    }

    var pixelPositionVal,
      boxSizingReliableVal,
      scrollboxSizeVal,
      pixelBoxStylesVal,
      reliableTrDimensionsVal,
      reliableMarginLeftVal,
      container = document.createElement('div'),
      div = document.createElement('div');

    // Finish early in limited (non-browser) environments
    if (!div.style) {
      return;
    }

    // Support: IE <=9 - 11 only
    // Style of cloned element affects source element cloned (#8908)
    div.style.backgroundClip = 'content-box';
    div.cloneNode(true).style.backgroundClip = '';
    support.clearCloneStyle = div.style.backgroundClip === 'content-box';

    jQuery.extend(support, {
      boxSizingReliable: function () {
        computeStyleTests();
        return boxSizingReliableVal;
      },
      pixelBoxStyles: function () {
        computeStyleTests();
        return pixelBoxStylesVal;
      },
      pixelPosition: function () {
        computeStyleTests();
        return pixelPositionVal;
      },
      reliableMarginLeft: function () {
        computeStyleTests();
        return reliableMarginLeftVal;
      },
      scrollboxSize: function () {
        computeStyleTests();
        return scrollboxSizeVal;
      },

      // Support: IE 9 - 11+, Edge 15 - 18+
      // IE/Edge misreport `getComputedStyle` of table rows with width/height
      // set in CSS while `offset*` properties report correct values.
      // Behavior in IE 9 is more subtle than in newer versions & it passes
      // some versions of this test; make sure not to make it pass there!
      //
      // Support: Firefox 70+
      // Only Firefox includes border widths
      // in computed dimensions. (gh-4529)
      reliableTrDimensions: function () {
        var table, tr, trChild, trStyle;
        if (reliableTrDimensionsVal == null) {
          table = document.createElement('table');
          tr = document.createElement('tr');
          trChild = document.createElement('div');

          table.style.cssText = 'position:absolute;left:-11111px;border-collapse:separate';
          tr.style.cssText = 'border:1px solid';

          // Support: Chrome 86+
          // Height set through cssText does not get applied.
          // Computed height then comes back as 0.
          tr.style.height = '1px';
          trChild.style.height = '9px';

          // Support: Android 8 Chrome 86+
          // In our bodyBackground.html iframe,
          // display for all div elements is set to "inline",
          // which causes a problem only in Android 8 Chrome 86.
          // Ensuring the div is display: block
          // gets around this issue.
          trChild.style.display = 'block';

          documentElement.appendChild(table).appendChild(tr).appendChild(trChild);

          trStyle = window.getComputedStyle(tr);
          reliableTrDimensionsVal =
            parseInt(trStyle.height, 10) +
              parseInt(trStyle.borderTopWidth, 10) +
              parseInt(trStyle.borderBottomWidth, 10) ===
            tr.offsetHeight;

          documentElement.removeChild(table);
        }
        return reliableTrDimensionsVal;
      },
    });
  })();

  function curCSS(elem, name, computed) {
    var width,
      minWidth,
      maxWidth,
      ret,
      // Support: Firefox 51+
      // Retrieving style before computed somehow
      // fixes an issue with getting wrong values
      // on detached elements
      style = elem.style;

    computed = computed || getStyles(elem);

    // getPropertyValue is needed for:
    //   .css('filter') (IE 9 only, #12537)
    //   .css('--customProperty) (#3144)
    if (computed) {
      ret = computed.getPropertyValue(name) || computed[name];

      if (ret === '' && !isAttached(elem)) {
        ret = jQuery.style(elem, name);
      }

      // A tribute to the "awesome hack by Dean Edwards"
      // Android Browser returns percentage for some values,
      // but width seems to be reliably pixels.
      // This is against the CSSOM draft spec:
      // https://drafts.csswg.org/cssom/#resolved-values
      if (!support.pixelBoxStyles() && rnumnonpx.test(ret) && rboxStyle.test(name)) {
        // Remember the original values
        width = style.width;
        minWidth = style.minWidth;
        maxWidth = style.maxWidth;

        // Put in the new values to get a computed value out
        style.minWidth = style.maxWidth = style.width = ret;
        ret = computed.width;

        // Revert the changed values
        style.width = width;
        style.minWidth = minWidth;
        style.maxWidth = maxWidth;
      }
    }

    return ret !== undefined
      ? // Support: IE <=9 - 11 only
        // IE returns zIndex value as an integer.
        ret + ''
      : ret;
  }

  function addGetHookIf(conditionFn, hookFn) {
    // Define the hook, we'll check on the first run if it's really needed.
    return {
      get: function () {
        if (conditionFn()) {
          // Hook not needed (or it's not possible to use it due
          // to missing dependency), remove it.
          delete this.get;
          return;
        }

        // Hook needed; redefine it so that the support test is not executed again.
        return (this.get = hookFn).apply(this, arguments);
      },
    };
  }

  var cssPrefixes = ['Webkit', 'Moz', 'ms'],
    emptyStyle = document.createElement('div').style,
    vendorProps = {};

  // Return a vendor-prefixed property or undefined
  function vendorPropName(name) {
    // Check for vendor prefixed names
    var capName = name[0].toUpperCase() + name.slice(1),
      i = cssPrefixes.length;

    while (i--) {
      name = cssPrefixes[i] + capName;
      if (name in emptyStyle) {
        return name;
      }
    }
  }

  // Return a potentially-mapped jQuery.cssProps or vendor prefixed property
  function finalPropName(name) {
    var final = jQuery.cssProps[name] || vendorProps[name];

    if (final) {
      return final;
    }
    if (name in emptyStyle) {
      return name;
    }
    return (vendorProps[name] = vendorPropName(name) || name);
  }

  var // Swappable if display is none or starts with table
    // except "table", "table-cell", or "table-caption"
    // See here for display values: https://developer.mozilla.org/en-US/docs/CSS/display
    rdisplayswap = /^(none|table(?!-c[ea]).+)/,
    rcustomProp = /^--/,
    cssShow = { position: 'absolute', visibility: 'hidden', display: 'block' },
    cssNormalTransform = {
      letterSpacing: '0',
      fontWeight: '400',
    };

  function setPositiveNumber(_elem, value, subtract) {
    // Any relative (+/-) values have already been
    // normalized at this point
    var matches = rcssNum.exec(value);
    return matches
      ? // Guard against undefined "subtract", e.g., when used as in cssHooks
        Math.max(0, matches[2] - (subtract || 0)) + (matches[3] || 'px')
      : value;
  }

  function boxModelAdjustment(elem, dimension, box, isBorderBox, styles, computedVal) {
    var i = dimension === 'width' ? 1 : 0,
      extra = 0,
      delta = 0;

    // Adjustment may not be necessary
    if (box === (isBorderBox ? 'border' : 'content')) {
      return 0;
    }

    for (; i < 4; i += 2) {
      // Both box models exclude margin
      if (box === 'margin') {
        delta += jQuery.css(elem, box + cssExpand[i], true, styles);
      }

      // If we get here with a content-box, we're seeking "padding" or "border" or "margin"
      if (!isBorderBox) {
        // Add padding
        delta += jQuery.css(elem, 'padding' + cssExpand[i], true, styles);

        // For "border" or "margin", add border
        if (box !== 'padding') {
          delta += jQuery.css(elem, 'border' + cssExpand[i] + 'Width', true, styles);

          // But still keep track of it otherwise
        } else {
          extra += jQuery.css(elem, 'border' + cssExpand[i] + 'Width', true, styles);
        }

        // If we get here with a border-box (content + padding + border), we're seeking "content" or
        // "padding" or "margin"
      } else {
        // For "content", subtract padding
        if (box === 'content') {
          delta -= jQuery.css(elem, 'padding' + cssExpand[i], true, styles);
        }

        // For "content" or "padding", subtract border
        if (box !== 'margin') {
          delta -= jQuery.css(elem, 'border' + cssExpand[i] + 'Width', true, styles);
        }
      }
    }

    // Account for positive content-box scroll gutter when requested by providing computedVal
    if (!isBorderBox && computedVal >= 0) {
      // offsetWidth/offsetHeight is a rounded sum of content, padding, scroll gutter, and border
      // Assuming integer scroll gutter, subtract the rest and round down
      delta +=
        Math.max(
          0,
          Math.ceil(
            elem['offset' + dimension[0].toUpperCase() + dimension.slice(1)] -
              computedVal -
              delta -
              extra -
              0.5

            // If offsetWidth/offsetHeight is unknown, then we can't determine content-box scroll gutter
            // Use an explicit zero to avoid NaN (gh-3964)
          )
        ) || 0;
    }

    return delta;
  }

  function getWidthOrHeight(elem, dimension, extra) {
    // Start with computed style
    var styles = getStyles(elem),
      // To avoid forcing a reflow, only fetch boxSizing if we need it (gh-4322).
      // Fake content-box until we know it's needed to know the true value.
      boxSizingNeeded = !support.boxSizingReliable() || extra,
      isBorderBox =
        boxSizingNeeded && jQuery.css(elem, 'boxSizing', false, styles) === 'border-box',
      valueIsBorderBox = isBorderBox,
      val = curCSS(elem, dimension, styles),
      offsetProp = 'offset' + dimension[0].toUpperCase() + dimension.slice(1);

    // Support: Firefox <=54
    // Return a confounding non-pixel value or feign ignorance, as appropriate.
    if (rnumnonpx.test(val)) {
      if (!extra) {
        return val;
      }
      val = 'auto';
    }

    // Support: IE 9 - 11 only
    // Use offsetWidth/offsetHeight for when box sizing is unreliable.
    // In those cases, the computed value can be trusted to be border-box.
    if (
      ((!support.boxSizingReliable() && isBorderBox) ||
        // Support: IE 10 - 11+, Edge 15 - 18+
        // IE/Edge misreport `getComputedStyle` of table rows with width/height
        // set in CSS while `offset*` properties report correct values.
        // Interestingly, in some cases IE 9 doesn't suffer from this issue.
        (!support.reliableTrDimensions() && nodeName(elem, 'tr')) ||
        // Fall back to offsetWidth/offsetHeight when value is "auto"
        // This happens for inline elements with no explicit setting (gh-3571)
        val === 'auto' ||
        // Support: Android <=4.1 - 4.3 only
        // Also use offsetWidth/offsetHeight for misreported inline dimensions (gh-3602)
        (!parseFloat(val) && jQuery.css(elem, 'display', false, styles) === 'inline')) &&
      // Make sure the element is visible & connected
      elem.getClientRects().length
    ) {
      isBorderBox = jQuery.css(elem, 'boxSizing', false, styles) === 'border-box';

      // Where available, offsetWidth/offsetHeight approximate border box dimensions.
      // Where not available (e.g., SVG), assume unreliable box-sizing and interpret the
      // retrieved value as a content box dimension.
      valueIsBorderBox = offsetProp in elem;
      if (valueIsBorderBox) {
        val = elem[offsetProp];
      }
    }

    // Normalize "" and auto
    val = parseFloat(val) || 0;

    // Adjust for the element's box model
    return (
      val +
      boxModelAdjustment(
        elem,
        dimension,
        extra || (isBorderBox ? 'border' : 'content'),
        valueIsBorderBox,
        styles,

        // Provide the current computed size to request scroll gutter calculation (gh-3589)
        val
      ) +
      'px'
    );
  }

  jQuery.extend({
    // Add in style property hooks for overriding the default
    // behavior of getting and setting a style property
    cssHooks: {
      opacity: {
        get: function (elem, computed) {
          if (computed) {
            // We should always get a number back from opacity
            var ret = curCSS(elem, 'opacity');
            return ret === '' ? '1' : ret;
          }
        },
      },
    },

    // Don't automatically add "px" to these possibly-unitless properties
    cssNumber: {
      animationIterationCount: true,
      columnCount: true,
      fillOpacity: true,
      flexGrow: true,
      flexShrink: true,
      fontWeight: true,
      gridArea: true,
      gridColumn: true,
      gridColumnEnd: true,
      gridColumnStart: true,
      gridRow: true,
      gridRowEnd: true,
      gridRowStart: true,
      lineHeight: true,
      opacity: true,
      order: true,
      orphans: true,
      widows: true,
      zIndex: true,
      zoom: true,
    },

    // Add in properties whose names you wish to fix before
    // setting or getting the value
    cssProps: {},

    // Get and set the style property on a DOM Node
    style: function (elem, name, value, extra) {
      // Don't set styles on text and comment nodes
      if (!elem || elem.nodeType === 3 || elem.nodeType === 8 || !elem.style) {
        return;
      }

      // Make sure that we're working with the right name
      var ret,
        type,
        hooks,
        origName = camelCase(name),
        isCustomProp = rcustomProp.test(name),
        style = elem.style;

      // Make sure that we're working with the right name. We don't
      // want to query the value if it is a CSS custom property
      // since they are user-defined.
      if (!isCustomProp) {
        name = finalPropName(origName);
      }

      // Gets hook for the prefixed version, then unprefixed version
      hooks = jQuery.cssHooks[name] || jQuery.cssHooks[origName];

      // Check if we're setting a value
      if (value !== undefined) {
        type = typeof value;

        // Convert "+=" or "-=" to relative numbers (#7345)
        if (type === 'string' && (ret = rcssNum.exec(value)) && ret[1]) {
          value = adjustCSS(elem, name, ret);

          // Fixes bug #9237
          type = 'number';
        }

        // Make sure that null and NaN values aren't set (#7116)
        if (value == null || value !== value) {
          return;
        }

        // If a number was passed in, add the unit (except for certain CSS properties)
        // The isCustomProp check can be removed in jQuery 4.0 when we only auto-append
        // "px" to a few hardcoded values.
        if (type === 'number' && !isCustomProp) {
          value += (ret && ret[3]) || (jQuery.cssNumber[origName] ? '' : 'px');
        }

        // background-* props affect original clone's values
        if (!support.clearCloneStyle && value === '' && name.indexOf('background') === 0) {
          style[name] = 'inherit';
        }

        // If a hook was provided, use that value, otherwise just set the specified value
        if (!hooks || !('set' in hooks) || (value = hooks.set(elem, value, extra)) !== undefined) {
          if (isCustomProp) {
            style.setProperty(name, value);
          } else {
            style[name] = value;
          }
        }
      } else {
        // If a hook was provided get the non-computed value from there
        if (hooks && 'get' in hooks && (ret = hooks.get(elem, false, extra)) !== undefined) {
          return ret;
        }

        // Otherwise just get the value from the style object
        return style[name];
      }
    },

    css: function (elem, name, extra, styles) {
      var val,
        num,
        hooks,
        origName = camelCase(name),
        isCustomProp = rcustomProp.test(name);

      // Make sure that we're working with the right name. We don't
      // want to modify the value if it is a CSS custom property
      // since they are user-defined.
      if (!isCustomProp) {
        name = finalPropName(origName);
      }

      // Try prefixed name followed by the unprefixed name
      hooks = jQuery.cssHooks[name] || jQuery.cssHooks[origName];

      // If a hook was provided get the computed value from there
      if (hooks && 'get' in hooks) {
        val = hooks.get(elem, true, extra);
      }

      // Otherwise, if a way to get the computed value exists, use that
      if (val === undefined) {
        val = curCSS(elem, name, styles);
      }

      // Convert "normal" to computed value
      if (val === 'normal' && name in cssNormalTransform) {
        val = cssNormalTransform[name];
      }

      // Make numeric if forced or a qualifier was provided and val looks numeric
      if (extra === '' || extra) {
        num = parseFloat(val);
        return extra === true || isFinite(num) ? num || 0 : val;
      }

      return val;
    },
  });

  jQuery.each(['height', 'width'], function (_i, dimension) {
    jQuery.cssHooks[dimension] = {
      get: function (elem, computed, extra) {
        if (computed) {
          // Certain elements can have dimension info if we invisibly show them
          // but it must have a current display style that would benefit
          return rdisplayswap.test(jQuery.css(elem, 'display')) &&
            // Support: Safari 8+
            // Table columns in Safari have non-zero offsetWidth & zero
            // getBoundingClientRect().width unless display is changed.
            // Support: IE <=11 only
            // Running getBoundingClientRect on a disconnected node
            // in IE throws an error.
            (!elem.getClientRects().length || !elem.getBoundingClientRect().width)
            ? swap(elem, cssShow, function () {
                return getWidthOrHeight(elem, dimension, extra);
              })
            : getWidthOrHeight(elem, dimension, extra);
        }
      },

      set: function (elem, value, extra) {
        var matches,
          styles = getStyles(elem),
          // Only read styles.position if the test has a chance to fail
          // to avoid forcing a reflow.
          scrollboxSizeBuggy = !support.scrollboxSize() && styles.position === 'absolute',
          // To avoid forcing a reflow, only fetch boxSizing if we need it (gh-3991)
          boxSizingNeeded = scrollboxSizeBuggy || extra,
          isBorderBox =
            boxSizingNeeded && jQuery.css(elem, 'boxSizing', false, styles) === 'border-box',
          subtract = extra ? boxModelAdjustment(elem, dimension, extra, isBorderBox, styles) : 0;

        // Account for unreliable border-box dimensions by comparing offset* to computed and
        // faking a content-box to get border and padding (gh-3699)
        if (isBorderBox && scrollboxSizeBuggy) {
          subtract -= Math.ceil(
            elem['offset' + dimension[0].toUpperCase() + dimension.slice(1)] -
              parseFloat(styles[dimension]) -
              boxModelAdjustment(elem, dimension, 'border', false, styles) -
              0.5
          );
        }

        // Convert to pixels if value adjustment is needed
        if (subtract && (matches = rcssNum.exec(value)) && (matches[3] || 'px') !== 'px') {
          elem.style[dimension] = value;
          value = jQuery.css(elem, dimension);
        }

        return setPositiveNumber(elem, value, subtract);
      },
    };
  });

  jQuery.cssHooks.marginLeft = addGetHookIf(support.reliableMarginLeft, function (elem, computed) {
    if (computed) {
      return (
        (parseFloat(curCSS(elem, 'marginLeft')) ||
          elem.getBoundingClientRect().left -
            swap(elem, { marginLeft: 0 }, function () {
              return elem.getBoundingClientRect().left;
            })) + 'px'
      );
    }
  });

  // These hooks are used by animate to expand properties
  jQuery.each(
    {
      margin: '',
      padding: '',
      border: 'Width',
    },
    function (prefix, suffix) {
      jQuery.cssHooks[prefix + suffix] = {
        expand: function (value) {
          var i = 0,
            expanded = {},
            // Assumes a single number if not a string
            parts = typeof value === 'string' ? value.split(' ') : [value];

          for (; i < 4; i++) {
            expanded[prefix + cssExpand[i] + suffix] = parts[i] || parts[i - 2] || parts[0];
          }

          return expanded;
        },
      };

      if (prefix !== 'margin') {
        jQuery.cssHooks[prefix + suffix].set = setPositiveNumber;
      }
    }
  );

  jQuery.fn.extend({
    css: function (name, value) {
      return access(
        this,
        function (elem, name, value) {
          var styles,
            len,
            map = {},
            i = 0;

          if (Array.isArray(name)) {
            styles = getStyles(elem);
            len = name.length;

            for (; i < len; i++) {
              map[name[i]] = jQuery.css(elem, name[i], false, styles);
            }

            return map;
          }

          return value !== undefined ? jQuery.style(elem, name, value) : jQuery.css(elem, name);
        },
        name,
        value,
        arguments.length > 1
      );
    },
  });

  function Tween(elem, options, prop, end, easing) {
    return new Tween.prototype.init(elem, options, prop, end, easing);
  }
  jQuery.Tween = Tween;

  Tween.prototype = {
    constructor: Tween,
    init: function (elem, options, prop, end, easing, unit) {
      this.elem = elem;
      this.prop = prop;
      this.easing = easing || jQuery.easing._default;
      this.options = options;
      this.start = this.now = this.cur();
      this.end = end;
      this.unit = unit || (jQuery.cssNumber[prop] ? '' : 'px');
    },
    cur: function () {
      var hooks = Tween.propHooks[this.prop];

      return hooks && hooks.get ? hooks.get(this) : Tween.propHooks._default.get(this);
    },
    run: function (percent) {
      var eased,
        hooks = Tween.propHooks[this.prop];

      if (this.options.duration) {
        this.pos = eased = jQuery.easing[this.easing](
          percent,
          this.options.duration * percent,
          0,
          1,
          this.options.duration
        );
      } else {
        this.pos = eased = percent;
      }
      this.now = (this.end - this.start) * eased + this.start;

      if (this.options.step) {
        this.options.step.call(this.elem, this.now, this);
      }

      if (hooks && hooks.set) {
        hooks.set(this);
      } else {
        Tween.propHooks._default.set(this);
      }
      return this;
    },
  };

  Tween.prototype.init.prototype = Tween.prototype;

  Tween.propHooks = {
    _default: {
      get: function (tween) {
        var result;

        // Use a property on the element directly when it is not a DOM element,
        // or when there is no matching style property that exists.
        if (
          tween.elem.nodeType !== 1 ||
          (tween.elem[tween.prop] != null && tween.elem.style[tween.prop] == null)
        ) {
          return tween.elem[tween.prop];
        }

        // Passing an empty string as a 3rd parameter to .css will automatically
        // attempt a parseFloat and fallback to a string if the parse fails.
        // Simple values such as "10px" are parsed to Float;
        // complex values such as "rotate(1rad)" are returned as-is.
        result = jQuery.css(tween.elem, tween.prop, '');

        // Empty strings, null, undefined and "auto" are converted to 0.
        return !result || result === 'auto' ? 0 : result;
      },
      set: function (tween) {
        // Use step hook for back compat.
        // Use cssHook if its there.
        // Use .style if available and use plain properties where available.
        if (jQuery.fx.step[tween.prop]) {
          jQuery.fx.step[tween.prop](tween);
        } else if (
          tween.elem.nodeType === 1 &&
          (jQuery.cssHooks[tween.prop] || tween.elem.style[finalPropName(tween.prop)] != null)
        ) {
          jQuery.style(tween.elem, tween.prop, tween.now + tween.unit);
        } else {
          tween.elem[tween.prop] = tween.now;
        }
      },
    },
  };

  // Support: IE <=9 only
  // Panic based approach to setting things on disconnected nodes
  Tween.propHooks.scrollTop = Tween.propHooks.scrollLeft = {
    set: function (tween) {
      if (tween.elem.nodeType && tween.elem.parentNode) {
        tween.elem[tween.prop] = tween.now;
      }
    },
  };

  jQuery.easing = {
    linear: function (p) {
      return p;
    },
    swing: function (p) {
      return 0.5 - Math.cos(p * Math.PI) / 2;
    },
    _default: 'swing',
  };

  jQuery.fx = Tween.prototype.init;

  // Back compat <1.8 extension point
  jQuery.fx.step = {};

  var fxNow,
    inProgress,
    rfxtypes = /^(?:toggle|show|hide)$/,
    rrun = /queueHooks$/;

  function schedule() {
    if (inProgress) {
      if (document.hidden === false && window.requestAnimationFrame) {
        window.requestAnimationFrame(schedule);
      } else {
        window.setTimeout(schedule, jQuery.fx.interval);
      }

      jQuery.fx.tick();
    }
  }

  // Animations created synchronously will run synchronously
  function createFxNow() {
    window.setTimeout(function () {
      fxNow = undefined;
    });
    return (fxNow = Date.now());
  }

  // Generate parameters to create a standard animation
  function genFx(type, includeWidth) {
    var which,
      i = 0,
      attrs = { height: type };

    // If we include width, step value is 1 to do all cssExpand values,
    // otherwise step value is 2 to skip over Left and Right
    includeWidth = includeWidth ? 1 : 0;
    for (; i < 4; i += 2 - includeWidth) {
      which = cssExpand[i];
      attrs['margin' + which] = attrs['padding' + which] = type;
    }

    if (includeWidth) {
      attrs.opacity = attrs.width = type;
    }

    return attrs;
  }

  function createTween(value, prop, animation) {
    var tween,
      collection = (Animation.tweeners[prop] || []).concat(Animation.tweeners['*']),
      index = 0,
      length = collection.length;
    for (; index < length; index++) {
      if ((tween = collection[index].call(animation, prop, value))) {
        // We're done with this property
        return tween;
      }
    }
  }

  function defaultPrefilter(elem, props, opts) {
    var prop,
      value,
      toggle,
      hooks,
      oldfire,
      propTween,
      restoreDisplay,
      display,
      isBox = 'width' in props || 'height' in props,
      anim = this,
      orig = {},
      style = elem.style,
      hidden = elem.nodeType && isHiddenWithinTree(elem),
      dataShow = dataPriv.get(elem, 'fxshow');

    // Queue-skipping animations hijack the fx hooks
    if (!opts.queue) {
      hooks = jQuery._queueHooks(elem, 'fx');
      if (hooks.unqueued == null) {
        hooks.unqueued = 0;
        oldfire = hooks.empty.fire;
        hooks.empty.fire = function () {
          if (!hooks.unqueued) {
            oldfire();
          }
        };
      }
      hooks.unqueued++;

      anim.always(function () {
        // Ensure the complete handler is called before this completes
        anim.always(function () {
          hooks.unqueued--;
          if (!jQuery.queue(elem, 'fx').length) {
            hooks.empty.fire();
          }
        });
      });
    }

    // Detect show/hide animations
    for (prop in props) {
      value = props[prop];
      if (rfxtypes.test(value)) {
        delete props[prop];
        toggle = toggle || value === 'toggle';
        if (value === (hidden ? 'hide' : 'show')) {
          // Pretend to be hidden if this is a "show" and
          // there is still data from a stopped show/hide
          if (value === 'show' && dataShow && dataShow[prop] !== undefined) {
            hidden = true;

            // Ignore all other no-op show/hide data
          } else {
            continue;
          }
        }
        orig[prop] = (dataShow && dataShow[prop]) || jQuery.style(elem, prop);
      }
    }

    // Bail out if this is a no-op like .hide().hide()
    propTween = !jQuery.isEmptyObject(props);
    if (!propTween && jQuery.isEmptyObject(orig)) {
      return;
    }

    // Restrict "overflow" and "display" styles during box animations
    if (isBox && elem.nodeType === 1) {
      // Support: IE <=9 - 11, Edge 12 - 15
      // Record all 3 overflow attributes because IE does not infer the shorthand
      // from identically-valued overflowX and overflowY and Edge just mirrors
      // the overflowX value there.
      opts.overflow = [style.overflow, style.overflowX, style.overflowY];

      // Identify a display type, preferring old show/hide data over the CSS cascade
      restoreDisplay = dataShow && dataShow.display;
      if (restoreDisplay == null) {
        restoreDisplay = dataPriv.get(elem, 'display');
      }
      display = jQuery.css(elem, 'display');
      if (display === 'none') {
        if (restoreDisplay) {
          display = restoreDisplay;
        } else {
          // Get nonempty value(s) by temporarily forcing visibility
          showHide([elem], true);
          restoreDisplay = elem.style.display || restoreDisplay;
          display = jQuery.css(elem, 'display');
          showHide([elem]);
        }
      }

      // Animate inline elements as inline-block
      if (display === 'inline' || (display === 'inline-block' && restoreDisplay != null)) {
        if (jQuery.css(elem, 'float') === 'none') {
          // Restore the original display value at the end of pure show/hide animations
          if (!propTween) {
            anim.done(function () {
              style.display = restoreDisplay;
            });
            if (restoreDisplay == null) {
              display = style.display;
              restoreDisplay = display === 'none' ? '' : display;
            }
          }
          style.display = 'inline-block';
        }
      }
    }

    if (opts.overflow) {
      style.overflow = 'hidden';
      anim.always(function () {
        style.overflow = opts.overflow[0];
        style.overflowX = opts.overflow[1];
        style.overflowY = opts.overflow[2];
      });
    }

    // Implement show/hide animations
    propTween = false;
    for (prop in orig) {
      // General show/hide setup for this element animation
      if (!propTween) {
        if (dataShow) {
          if ('hidden' in dataShow) {
            hidden = dataShow.hidden;
          }
        } else {
          dataShow = dataPriv.access(elem, 'fxshow', { display: restoreDisplay });
        }

        // Store hidden/visible for toggle so `.stop().toggle()` "reverses"
        if (toggle) {
          dataShow.hidden = !hidden;
        }

        // Show elements before animating them
        if (hidden) {
          showHide([elem], true);
        }

        /* eslint-disable no-loop-func */

        anim.done(function () {
          /* eslint-enable no-loop-func */

          // The final step of a "hide" animation is actually hiding the element
          if (!hidden) {
            showHide([elem]);
          }
          dataPriv.remove(elem, 'fxshow');
          for (prop in orig) {
            jQuery.style(elem, prop, orig[prop]);
          }
        });
      }

      // Per-property setup
      propTween = createTween(hidden ? dataShow[prop] : 0, prop, anim);
      if (!(prop in dataShow)) {
        dataShow[prop] = propTween.start;
        if (hidden) {
          propTween.end = propTween.start;
          propTween.start = 0;
        }
      }
    }
  }

  function propFilter(props, specialEasing) {
    var index, name, easing, value, hooks;

    // camelCase, specialEasing and expand cssHook pass
    for (index in props) {
      name = camelCase(index);
      easing = specialEasing[name];
      value = props[index];
      if (Array.isArray(value)) {
        easing = value[1];
        value = props[index] = value[0];
      }

      if (index !== name) {
        props[name] = value;
        delete props[index];
      }

      hooks = jQuery.cssHooks[name];
      if (hooks && 'expand' in hooks) {
        value = hooks.expand(value);
        delete props[name];

        // Not quite $.extend, this won't overwrite existing keys.
        // Reusing 'index' because we have the correct "name"
        for (index in value) {
          if (!(index in props)) {
            props[index] = value[index];
            specialEasing[index] = easing;
          }
        }
      } else {
        specialEasing[name] = easing;
      }
    }
  }

  function Animation(elem, properties, options) {
    var result,
      stopped,
      index = 0,
      length = Animation.prefilters.length,
      deferred = jQuery.Deferred().always(function () {
        // Don't match elem in the :animated selector
        delete tick.elem;
      }),
      tick = function () {
        if (stopped) {
          return false;
        }
        var currentTime = fxNow || createFxNow(),
          remaining = Math.max(0, animation.startTime + animation.duration - currentTime),
          // Support: Android 2.3 only
          // Archaic crash bug won't allow us to use `1 - ( 0.5 || 0 )` (#12497)
          temp = remaining / animation.duration || 0,
          percent = 1 - temp,
          index = 0,
          length = animation.tweens.length;

        for (; index < length; index++) {
          animation.tweens[index].run(percent);
        }

        deferred.notifyWith(elem, [animation, percent, remaining]);

        // If there's more to do, yield
        if (percent < 1 && length) {
          return remaining;
        }

        // If this was an empty animation, synthesize a final progress notification
        if (!length) {
          deferred.notifyWith(elem, [animation, 1, 0]);
        }

        // Resolve the animation and report its conclusion
        deferred.resolveWith(elem, [animation]);
        return false;
      },
      animation = deferred.promise({
        elem: elem,
        props: jQuery.extend({}, properties),
        opts: jQuery.extend(
          true,
          {
            specialEasing: {},
            easing: jQuery.easing._default,
          },
          options
        ),
        originalProperties: properties,
        originalOptions: options,
        startTime: fxNow || createFxNow(),
        duration: options.duration,
        tweens: [],
        createTween: function (prop, end) {
          var tween = jQuery.Tween(
            elem,
            animation.opts,
            prop,
            end,
            animation.opts.specialEasing[prop] || animation.opts.easing
          );
          animation.tweens.push(tween);
          return tween;
        },
        stop: function (gotoEnd) {
          var index = 0,
            // If we are going to the end, we want to run all the tweens
            // otherwise we skip this part
            length = gotoEnd ? animation.tweens.length : 0;
          if (stopped) {
            return this;
          }
          stopped = true;
          for (; index < length; index++) {
            animation.tweens[index].run(1);
          }

          // Resolve when we played the last frame; otherwise, reject
          if (gotoEnd) {
            deferred.notifyWith(elem, [animation, 1, 0]);
            deferred.resolveWith(elem, [animation, gotoEnd]);
          } else {
            deferred.rejectWith(elem, [animation, gotoEnd]);
          }
          return this;
        },
      }),
      props = animation.props;

    propFilter(props, animation.opts.specialEasing);

    for (; index < length; index++) {
      result = Animation.prefilters[index].call(animation, elem, props, animation.opts);
      if (result) {
        if (isFunction(result.stop)) {
          jQuery._queueHooks(animation.elem, animation.opts.queue).stop = result.stop.bind(result);
        }
        return result;
      }
    }

    jQuery.map(props, createTween, animation);

    if (isFunction(animation.opts.start)) {
      animation.opts.start.call(elem, animation);
    }

    // Attach callbacks from options
    animation
      .progress(animation.opts.progress)
      .done(animation.opts.done, animation.opts.complete)
      .fail(animation.opts.fail)
      .always(animation.opts.always);

    jQuery.fx.timer(
      jQuery.extend(tick, {
        elem: elem,
        anim: animation,
        queue: animation.opts.queue,
      })
    );

    return animation;
  }

  jQuery.Animation = jQuery.extend(Animation, {
    tweeners: {
      '*': [
        function (prop, value) {
          var tween = this.createTween(prop, value);
          adjustCSS(tween.elem, prop, rcssNum.exec(value), tween);
          return tween;
        },
      ],
    },

    tweener: function (props, callback) {
      if (isFunction(props)) {
        callback = props;
        props = ['*'];
      } else {
        props = props.match(rnothtmlwhite);
      }

      var prop,
        index = 0,
        length = props.length;

      for (; index < length; index++) {
        prop = props[index];
        Animation.tweeners[prop] = Animation.tweeners[prop] || [];
        Animation.tweeners[prop].unshift(callback);
      }
    },

    prefilters: [defaultPrefilter],

    prefilter: function (callback, prepend) {
      if (prepend) {
        Animation.prefilters.unshift(callback);
      } else {
        Animation.prefilters.push(callback);
      }
    },
  });

  jQuery.speed = function (speed, easing, fn) {
    var opt =
      speed && typeof speed === 'object'
        ? jQuery.extend({}, speed)
        : {
            complete: fn || (!fn && easing) || (isFunction(speed) && speed),
            duration: speed,
            easing: (fn && easing) || (easing && !isFunction(easing) && easing),
          };

    // Go to the end state if fx are off
    if (jQuery.fx.off) {
      opt.duration = 0;
    } else {
      if (typeof opt.duration !== 'number') {
        if (opt.duration in jQuery.fx.speeds) {
          opt.duration = jQuery.fx.speeds[opt.duration];
        } else {
          opt.duration = jQuery.fx.speeds._default;
        }
      }
    }

    // Normalize opt.queue - true/undefined/null -> "fx"
    if (opt.queue == null || opt.queue === true) {
      opt.queue = 'fx';
    }

    // Queueing
    opt.old = opt.complete;

    opt.complete = function () {
      if (isFunction(opt.old)) {
        opt.old.call(this);
      }

      if (opt.queue) {
        jQuery.dequeue(this, opt.queue);
      }
    };

    return opt;
  };

  jQuery.fn.extend({
    fadeTo: function (speed, to, easing, callback) {
      // Show any hidden elements after setting opacity to 0
      return (
        this.filter(isHiddenWithinTree)
          .css('opacity', 0)
          .show()

          // Animate to the value specified
          .end()
          .animate({ opacity: to }, speed, easing, callback)
      );
    },
    animate: function (prop, speed, easing, callback) {
      var empty = jQuery.isEmptyObject(prop),
        optall = jQuery.speed(speed, easing, callback),
        doAnimation = function () {
          // Operate on a copy of prop so per-property easing won't be lost
          var anim = Animation(this, jQuery.extend({}, prop), optall);

          // Empty animations, or finishing resolves immediately
          if (empty || dataPriv.get(this, 'finish')) {
            anim.stop(true);
          }
        };

      doAnimation.finish = doAnimation;

      return empty || optall.queue === false
        ? this.each(doAnimation)
        : this.queue(optall.queue, doAnimation);
    },
    stop: function (type, clearQueue, gotoEnd) {
      var stopQueue = function (hooks) {
        var stop = hooks.stop;
        delete hooks.stop;
        stop(gotoEnd);
      };

      if (typeof type !== 'string') {
        gotoEnd = clearQueue;
        clearQueue = type;
        type = undefined;
      }
      if (clearQueue) {
        this.queue(type || 'fx', []);
      }

      return this.each(function () {
        var dequeue = true,
          index = type != null && type + 'queueHooks',
          timers = jQuery.timers,
          data = dataPriv.get(this);

        if (index) {
          if (data[index] && data[index].stop) {
            stopQueue(data[index]);
          }
        } else {
          for (index in data) {
            if (data[index] && data[index].stop && rrun.test(index)) {
              stopQueue(data[index]);
            }
          }
        }

        for (index = timers.length; index--; ) {
          if (timers[index].elem === this && (type == null || timers[index].queue === type)) {
            timers[index].anim.stop(gotoEnd);
            dequeue = false;
            timers.splice(index, 1);
          }
        }

        // Start the next in the queue if the last step wasn't forced.
        // Timers currently will call their complete callbacks, which
        // will dequeue but only if they were gotoEnd.
        if (dequeue || !gotoEnd) {
          jQuery.dequeue(this, type);
        }
      });
    },
    finish: function (type) {
      if (type !== false) {
        type = type || 'fx';
      }
      return this.each(function () {
        var index,
          data = dataPriv.get(this),
          queue = data[type + 'queue'],
          hooks = data[type + 'queueHooks'],
          timers = jQuery.timers,
          length = queue ? queue.length : 0;

        // Enable finishing flag on private data
        data.finish = true;

        // Empty the queue first
        jQuery.queue(this, type, []);

        if (hooks && hooks.stop) {
          hooks.stop.call(this, true);
        }

        // Look for any active animations, and finish them
        for (index = timers.length; index--; ) {
          if (timers[index].elem === this && timers[index].queue === type) {
            timers[index].anim.stop(true);
            timers.splice(index, 1);
          }
        }

        // Look for any animations in the old queue and finish them
        for (index = 0; index < length; index++) {
          if (queue[index] && queue[index].finish) {
            queue[index].finish.call(this);
          }
        }

        // Turn off finishing flag
        delete data.finish;
      });
    },
  });

  jQuery.each(['toggle', 'show', 'hide'], function (_i, name) {
    var cssFn = jQuery.fn[name];
    jQuery.fn[name] = function (speed, easing, callback) {
      return speed == null || typeof speed === 'boolean'
        ? cssFn.apply(this, arguments)
        : this.animate(genFx(name, true), speed, easing, callback);
    };
  });

  // Generate shortcuts for custom animations
  jQuery.each(
    {
      slideDown: genFx('show'),
      slideUp: genFx('hide'),
      slideToggle: genFx('toggle'),
      fadeIn: { opacity: 'show' },
      fadeOut: { opacity: 'hide' },
      fadeToggle: { opacity: 'toggle' },
    },
    function (name, props) {
      jQuery.fn[name] = function (speed, easing, callback) {
        return this.animate(props, speed, easing, callback);
      };
    }
  );

  jQuery.timers = [];
  jQuery.fx.tick = function () {
    var timer,
      i = 0,
      timers = jQuery.timers;

    fxNow = Date.now();

    for (; i < timers.length; i++) {
      timer = timers[i];

      // Run the timer and safely remove it when done (allowing for external removal)
      if (!timer() && timers[i] === timer) {
        timers.splice(i--, 1);
      }
    }

    if (!timers.length) {
      jQuery.fx.stop();
    }
    fxNow = undefined;
  };

  jQuery.fx.timer = function (timer) {
    jQuery.timers.push(timer);
    jQuery.fx.start();
  };

  jQuery.fx.interval = 13;
  jQuery.fx.start = function () {
    if (inProgress) {
      return;
    }

    inProgress = true;
    schedule();
  };

  jQuery.fx.stop = function () {
    inProgress = null;
  };

  jQuery.fx.speeds = {
    slow: 600,
    fast: 200,

    // Default speed
    _default: 400,
  };

  // Based off of the plugin by Clint Helfers, with permission.
  // https://web.archive.org/web/20100324014747/http://blindsignals.com/index.php/2009/07/jquery-delay/
  jQuery.fn.delay = function (time, type) {
    time = jQuery.fx ? jQuery.fx.speeds[time] || time : time;
    type = type || 'fx';

    return this.queue(type, function (next, hooks) {
      var timeout = window.setTimeout(next, time);
      hooks.stop = function () {
        window.clearTimeout(timeout);
      };
    });
  };

  (function () {
    var input = document.createElement('input'),
      select = document.createElement('select'),
      opt = select.appendChild(document.createElement('option'));

    input.type = 'checkbox';

    // Support: Android <=4.3 only
    // Default value for a checkbox should be "on"
    support.checkOn = input.value !== '';

    // Support: IE <=11 only
    // Must access selectedIndex to make default options select
    support.optSelected = opt.selected;

    // Support: IE <=11 only
    // An input loses its value after becoming a radio
    input = document.createElement('input');
    input.value = 't';
    input.type = 'radio';
    support.radioValue = input.value === 't';
  })();

  var boolHook,
    attrHandle = jQuery.expr.attrHandle;

  jQuery.fn.extend({
    attr: function (name, value) {
      return access(this, jQuery.attr, name, value, arguments.length > 1);
    },

    removeAttr: function (name) {
      return this.each(function () {
        jQuery.removeAttr(this, name);
      });
    },
  });

  jQuery.extend({
    attr: function (elem, name, value) {
      var ret,
        hooks,
        nType = elem.nodeType;

      // Don't get/set attributes on text, comment and attribute nodes
      if (nType === 3 || nType === 8 || nType === 2) {
        return;
      }

      // Fallback to prop when attributes are not supported
      if (typeof elem.getAttribute === 'undefined') {
        return jQuery.prop(elem, name, value);
      }

      // Attribute hooks are determined by the lowercase version
      // Grab necessary hook if one is defined
      if (nType !== 1 || !jQuery.isXMLDoc(elem)) {
        hooks =
          jQuery.attrHooks[name.toLowerCase()] ||
          (jQuery.expr.match.bool.test(name) ? boolHook : undefined);
      }

      if (value !== undefined) {
        if (value === null) {
          jQuery.removeAttr(elem, name);
          return;
        }

        if (hooks && 'set' in hooks && (ret = hooks.set(elem, value, name)) !== undefined) {
          return ret;
        }

        elem.setAttribute(name, value + '');
        return value;
      }

      if (hooks && 'get' in hooks && (ret = hooks.get(elem, name)) !== null) {
        return ret;
      }

      ret = jQuery.find.attr(elem, name);

      // Non-existent attributes return null, we normalize to undefined
      return ret == null ? undefined : ret;
    },

    attrHooks: {
      type: {
        set: function (elem, value) {
          if (!support.radioValue && value === 'radio' && nodeName(elem, 'input')) {
            var val = elem.value;
            elem.setAttribute('type', value);
            if (val) {
              elem.value = val;
            }
            return value;
          }
        },
      },
    },

    removeAttr: function (elem, value) {
      var name,
        i = 0,
        // Attribute names can contain non-HTML whitespace characters
        // https://html.spec.whatwg.org/multipage/syntax.html#attributes-2
        attrNames = value && value.match(rnothtmlwhite);

      if (attrNames && elem.nodeType === 1) {
        while ((name = attrNames[i++])) {
          elem.removeAttribute(name);
        }
      }
    },
  });

  // Hooks for boolean attributes
  boolHook = {
    set: function (elem, value, name) {
      if (value === false) {
        // Remove boolean attributes when set to false
        jQuery.removeAttr(elem, name);
      } else {
        elem.setAttribute(name, name);
      }
      return name;
    },
  };

  jQuery.each(jQuery.expr.match.bool.source.match(/\w+/g), function (_i, name) {
    var getter = attrHandle[name] || jQuery.find.attr;

    attrHandle[name] = function (elem, name, isXML) {
      var ret,
        handle,
        lowercaseName = name.toLowerCase();

      if (!isXML) {
        // Avoid an infinite loop by temporarily removing this function from the getter
        handle = attrHandle[lowercaseName];
        attrHandle[lowercaseName] = ret;
        ret = getter(elem, name, isXML) != null ? lowercaseName : null;
        attrHandle[lowercaseName] = handle;
      }
      return ret;
    };
  });

  var rfocusable = /^(?:input|select|textarea|button)$/i,
    rclickable = /^(?:a|area)$/i;

  jQuery.fn.extend({
    prop: function (name, value) {
      return access(this, jQuery.prop, name, value, arguments.length > 1);
    },

    removeProp: function (name) {
      return this.each(function () {
        delete this[jQuery.propFix[name] || name];
      });
    },
  });

  jQuery.extend({
    prop: function (elem, name, value) {
      var ret,
        hooks,
        nType = elem.nodeType;

      // Don't get/set properties on text, comment and attribute nodes
      if (nType === 3 || nType === 8 || nType === 2) {
        return;
      }

      if (nType !== 1 || !jQuery.isXMLDoc(elem)) {
        // Fix name and attach hooks
        name = jQuery.propFix[name] || name;
        hooks = jQuery.propHooks[name];
      }

      if (value !== undefined) {
        if (hooks && 'set' in hooks && (ret = hooks.set(elem, value, name)) !== undefined) {
          return ret;
        }

        return (elem[name] = value);
      }

      if (hooks && 'get' in hooks && (ret = hooks.get(elem, name)) !== null) {
        return ret;
      }

      return elem[name];
    },

    propHooks: {
      tabIndex: {
        get: function (elem) {
          // Support: IE <=9 - 11 only
          // elem.tabIndex doesn't always return the
          // correct value when it hasn't been explicitly set
          // https://web.archive.org/web/20141116233347/http://fluidproject.org/blog/2008/01/09/getting-setting-and-removing-tabindex-values-with-javascript/
          // Use proper attribute retrieval(#12072)
          var tabindex = jQuery.find.attr(elem, 'tabindex');

          if (tabindex) {
            return parseInt(tabindex, 10);
          }

          if (rfocusable.test(elem.nodeName) || (rclickable.test(elem.nodeName) && elem.href)) {
            return 0;
          }

          return -1;
        },
      },
    },

    propFix: {
      for: 'htmlFor',
      class: 'className',
    },
  });

  // Support: IE <=11 only
  // Accessing the selectedIndex property
  // forces the browser to respect setting selected
  // on the option
  // The getter ensures a default option is selected
  // when in an optgroup
  // eslint rule "no-unused-expressions" is disabled for this code
  // since it considers such accessions noop
  if (!support.optSelected) {
    jQuery.propHooks.selected = {
      get: function (elem) {
        /* eslint no-unused-expressions: "off" */

        var parent = elem.parentNode;
        if (parent && parent.parentNode) {
          parent.parentNode.selectedIndex;
        }
        return null;
      },
      set: function (elem) {
        /* eslint no-unused-expressions: "off" */

        var parent = elem.parentNode;
        if (parent) {
          parent.selectedIndex;

          if (parent.parentNode) {
            parent.parentNode.selectedIndex;
          }
        }
      },
    };
  }

  jQuery.each(
    [
      'tabIndex',
      'readOnly',
      'maxLength',
      'cellSpacing',
      'cellPadding',
      'rowSpan',
      'colSpan',
      'useMap',
      'frameBorder',
      'contentEditable',
    ],
    function () {
      jQuery.propFix[this.toLowerCase()] = this;
    }
  );

  // Strip and collapse whitespace according to HTML spec
  // https://infra.spec.whatwg.org/#strip-and-collapse-ascii-whitespace
  function stripAndCollapse(value) {
    var tokens = value.match(rnothtmlwhite) || [];
    return tokens.join(' ');
  }

  function getClass(elem) {
    return (elem.getAttribute && elem.getAttribute('class')) || '';
  }

  function classesToArray(value) {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      return value.match(rnothtmlwhite) || [];
    }
    return [];
  }

  jQuery.fn.extend({
    addClass: function (value) {
      var classes,
        elem,
        cur,
        curValue,
        clazz,
        j,
        finalValue,
        i = 0;

      if (isFunction(value)) {
        return this.each(function (j) {
          jQuery(this).addClass(value.call(this, j, getClass(this)));
        });
      }

      classes = classesToArray(value);

      if (classes.length) {
        while ((elem = this[i++])) {
          curValue = getClass(elem);
          cur = elem.nodeType === 1 && ' ' + stripAndCollapse(curValue) + ' ';

          if (cur) {
            j = 0;
            while ((clazz = classes[j++])) {
              if (cur.indexOf(' ' + clazz + ' ') < 0) {
                cur += clazz + ' ';
              }
            }

            // Only assign if different to avoid unneeded rendering.
            finalValue = stripAndCollapse(cur);
            if (curValue !== finalValue) {
              elem.setAttribute('class', finalValue);
            }
          }
        }
      }

      return this;
    },

    removeClass: function (value) {
      var classes,
        elem,
        cur,
        curValue,
        clazz,
        j,
        finalValue,
        i = 0;

      if (isFunction(value)) {
        return this.each(function (j) {
          jQuery(this).removeClass(value.call(this, j, getClass(this)));
        });
      }

      if (!arguments.length) {
        return this.attr('class', '');
      }

      classes = classesToArray(value);

      if (classes.length) {
        while ((elem = this[i++])) {
          curValue = getClass(elem);

          // This expression is here for better compressibility (see addClass)
          cur = elem.nodeType === 1 && ' ' + stripAndCollapse(curValue) + ' ';

          if (cur) {
            j = 0;
            while ((clazz = classes[j++])) {
              // Remove *all* instances
              while (cur.indexOf(' ' + clazz + ' ') > -1) {
                cur = cur.replace(' ' + clazz + ' ', ' ');
              }
            }

            // Only assign if different to avoid unneeded rendering.
            finalValue = stripAndCollapse(cur);
            if (curValue !== finalValue) {
              elem.setAttribute('class', finalValue);
            }
          }
        }
      }

      return this;
    },

    toggleClass: function (value, stateVal) {
      var type = typeof value,
        isValidValue = type === 'string' || Array.isArray(value);

      if (typeof stateVal === 'boolean' && isValidValue) {
        return stateVal ? this.addClass(value) : this.removeClass(value);
      }

      if (isFunction(value)) {
        return this.each(function (i) {
          jQuery(this).toggleClass(value.call(this, i, getClass(this), stateVal), stateVal);
        });
      }

      return this.each(function () {
        var className, i, self, classNames;

        if (isValidValue) {
          // Toggle individual class names
          i = 0;
          self = jQuery(this);
          classNames = classesToArray(value);

          while ((className = classNames[i++])) {
            // Check each className given, space separated list
            if (self.hasClass(className)) {
              self.removeClass(className);
            } else {
              self.addClass(className);
            }
          }

          // Toggle whole class name
        } else if (value === undefined || type === 'boolean') {
          className = getClass(this);
          if (className) {
            // Store className if set
            dataPriv.set(this, '__className__', className);
          }

          // If the element has a class name or if we're passed `false`,
          // then remove the whole classname (if there was one, the above saved it).
          // Otherwise bring back whatever was previously saved (if anything),
          // falling back to the empty string if nothing was stored.
          if (this.setAttribute) {
            this.setAttribute(
              'class',
              className || value === false ? '' : dataPriv.get(this, '__className__') || ''
            );
          }
        }
      });
    },

    hasClass: function (selector) {
      var className,
        elem,
        i = 0;

      className = ' ' + selector + ' ';
      while ((elem = this[i++])) {
        if (
          elem.nodeType === 1 &&
          (' ' + stripAndCollapse(getClass(elem)) + ' ').indexOf(className) > -1
        ) {
          return true;
        }
      }

      return false;
    },
  });

  var rreturn = /\r/g;

  jQuery.fn.extend({
    val: function (value) {
      var hooks,
        ret,
        valueIsFunction,
        elem = this[0];

      if (!arguments.length) {
        if (elem) {
          hooks = jQuery.valHooks[elem.type] || jQuery.valHooks[elem.nodeName.toLowerCase()];

          if (hooks && 'get' in hooks && (ret = hooks.get(elem, 'value')) !== undefined) {
            return ret;
          }

          ret = elem.value;

          // Handle most common string cases
          if (typeof ret === 'string') {
            return ret.replace(rreturn, '');
          }

          // Handle cases where value is null/undef or number
          return ret == null ? '' : ret;
        }

        return;
      }

      valueIsFunction = isFunction(value);

      return this.each(function (i) {
        var val;

        if (this.nodeType !== 1) {
          return;
        }

        if (valueIsFunction) {
          val = value.call(this, i, jQuery(this).val());
        } else {
          val = value;
        }

        // Treat null/undefined as ""; convert numbers to string
        if (val == null) {
          val = '';
        } else if (typeof val === 'number') {
          val += '';
        } else if (Array.isArray(val)) {
          val = jQuery.map(val, function (value) {
            return value == null ? '' : value + '';
          });
        }

        hooks = jQuery.valHooks[this.type] || jQuery.valHooks[this.nodeName.toLowerCase()];

        // If set returns undefined, fall back to normal setting
        if (!hooks || !('set' in hooks) || hooks.set(this, val, 'value') === undefined) {
          this.value = val;
        }
      });
    },
  });

  jQuery.extend({
    valHooks: {
      option: {
        get: function (elem) {
          var val = jQuery.find.attr(elem, 'value');
          return val != null
            ? val
            : // Support: IE <=10 - 11 only
              // option.text throws exceptions (#14686, #14858)
              // Strip and collapse whitespace
              // https://html.spec.whatwg.org/#strip-and-collapse-whitespace
              stripAndCollapse(jQuery.text(elem));
        },
      },
      select: {
        get: function (elem) {
          var value,
            option,
            i,
            options = elem.options,
            index = elem.selectedIndex,
            one = elem.type === 'select-one',
            values = one ? null : [],
            max = one ? index + 1 : options.length;

          if (index < 0) {
            i = max;
          } else {
            i = one ? index : 0;
          }

          // Loop through all the selected options
          for (; i < max; i++) {
            option = options[i];

            // Support: IE <=9 only
            // IE8-9 doesn't update selected after form reset (#2551)
            if (
              (option.selected || i === index) &&
              // Don't return options that are disabled or in a disabled optgroup
              !option.disabled &&
              (!option.parentNode.disabled || !nodeName(option.parentNode, 'optgroup'))
            ) {
              // Get the specific value for the option
              value = jQuery(option).val();

              // We don't need an array for one selects
              if (one) {
                return value;
              }

              // Multi-Selects return an array
              values.push(value);
            }
          }

          return values;
        },

        set: function (elem, value) {
          var optionSet,
            option,
            options = elem.options,
            values = jQuery.makeArray(value),
            i = options.length;

          while (i--) {
            option = options[i];

            /* eslint-disable no-cond-assign */

            if (
              (option.selected = jQuery.inArray(jQuery.valHooks.option.get(option), values) > -1)
            ) {
              optionSet = true;
            }

            /* eslint-enable no-cond-assign */
          }

          // Force browsers to behave consistently when non-matching value is set
          if (!optionSet) {
            elem.selectedIndex = -1;
          }
          return values;
        },
      },
    },
  });

  // Radios and checkboxes getter/setter
  jQuery.each(['radio', 'checkbox'], function () {
    jQuery.valHooks[this] = {
      set: function (elem, value) {
        if (Array.isArray(value)) {
          return (elem.checked = jQuery.inArray(jQuery(elem).val(), value) > -1);
        }
      },
    };
    if (!support.checkOn) {
      jQuery.valHooks[this].get = function (elem) {
        return elem.getAttribute('value') === null ? 'on' : elem.value;
      };
    }
  });

  // Return jQuery for attributes-only inclusion

  support.focusin = 'onfocusin' in window;

  var rfocusMorph = /^(?:focusinfocus|focusoutblur)$/,
    stopPropagationCallback = function (e) {
      e.stopPropagation();
    };

  jQuery.extend(jQuery.event, {
    trigger: function (event, data, elem, onlyHandlers) {
      var i,
        cur,
        tmp,
        bubbleType,
        ontype,
        handle,
        special,
        lastElement,
        eventPath = [elem || document],
        type = hasOwn.call(event, 'type') ? event.type : event,
        namespaces = hasOwn.call(event, 'namespace') ? event.namespace.split('.') : [];

      cur = lastElement = tmp = elem = elem || document;

      // Don't do events on text and comment nodes
      if (elem.nodeType === 3 || elem.nodeType === 8) {
        return;
      }

      // focus/blur morphs to focusin/out; ensure we're not firing them right now
      if (rfocusMorph.test(type + jQuery.event.triggered)) {
        return;
      }

      if (type.indexOf('.') > -1) {
        // Namespaced trigger; create a regexp to match event type in handle()
        namespaces = type.split('.');
        type = namespaces.shift();
        namespaces.sort();
      }
      ontype = type.indexOf(':') < 0 && 'on' + type;

      // Caller can pass in a jQuery.Event object, Object, or just an event type string
      event = event[jQuery.expando]
        ? event
        : new jQuery.Event(type, typeof event === 'object' && event);

      // Trigger bitmask: & 1 for native handlers; & 2 for jQuery (always true)
      event.isTrigger = onlyHandlers ? 2 : 3;
      event.namespace = namespaces.join('.');
      event.rnamespace = event.namespace
        ? new RegExp('(^|\\.)' + namespaces.join('\\.(?:.*\\.|)') + '(\\.|$)')
        : null;

      // Clean up the event in case it is being reused
      event.result = undefined;
      if (!event.target) {
        event.target = elem;
      }

      // Clone any incoming data and prepend the event, creating the handler arg list
      data = data == null ? [event] : jQuery.makeArray(data, [event]);

      // Allow special events to draw outside the lines
      special = jQuery.event.special[type] || {};
      if (!onlyHandlers && special.trigger && special.trigger.apply(elem, data) === false) {
        return;
      }

      // Determine event propagation path in advance, per W3C events spec (#9951)
      // Bubble up to document, then to window; watch for a global ownerDocument var (#9724)
      if (!onlyHandlers && !special.noBubble && !isWindow(elem)) {
        bubbleType = special.delegateType || type;
        if (!rfocusMorph.test(bubbleType + type)) {
          cur = cur.parentNode;
        }
        for (; cur; cur = cur.parentNode) {
          eventPath.push(cur);
          tmp = cur;
        }

        // Only add window if we got to document (e.g., not plain obj or detached DOM)
        if (tmp === (elem.ownerDocument || document)) {
          eventPath.push(tmp.defaultView || tmp.parentWindow || window);
        }
      }

      // Fire handlers on the event path
      i = 0;
      while ((cur = eventPath[i++]) && !event.isPropagationStopped()) {
        lastElement = cur;
        event.type = i > 1 ? bubbleType : special.bindType || type;

        // jQuery handler
        handle =
          (dataPriv.get(cur, 'events') || Object.create(null))[event.type] &&
          dataPriv.get(cur, 'handle');
        if (handle) {
          handle.apply(cur, data);
        }

        // Native handler
        handle = ontype && cur[ontype];
        if (handle && handle.apply && acceptData(cur)) {
          event.result = handle.apply(cur, data);
          if (event.result === false) {
            event.preventDefault();
          }
        }
      }
      event.type = type;

      // If nobody prevented the default action, do it now
      if (!onlyHandlers && !event.isDefaultPrevented()) {
        if (
          (!special._default || special._default.apply(eventPath.pop(), data) === false) &&
          acceptData(elem)
        ) {
          // Call a native DOM method on the target with the same name as the event.
          // Don't do default actions on window, that's where global variables be (#6170)
          if (ontype && isFunction(elem[type]) && !isWindow(elem)) {
            // Don't re-trigger an onFOO event when we call its FOO() method
            tmp = elem[ontype];

            if (tmp) {
              elem[ontype] = null;
            }

            // Prevent re-triggering of the same event, since we already bubbled it above
            jQuery.event.triggered = type;

            if (event.isPropagationStopped()) {
              lastElement.addEventListener(type, stopPropagationCallback);
            }

            elem[type]();

            if (event.isPropagationStopped()) {
              lastElement.removeEventListener(type, stopPropagationCallback);
            }

            jQuery.event.triggered = undefined;

            if (tmp) {
              elem[ontype] = tmp;
            }
          }
        }
      }

      return event.result;
    },

    // Piggyback on a donor event to simulate a different one
    // Used only for `focus(in | out)` events
    simulate: function (type, elem, event) {
      var e = jQuery.extend(new jQuery.Event(), event, {
        type: type,
        isSimulated: true,
      });

      jQuery.event.trigger(e, null, elem);
    },
  });

  jQuery.fn.extend({
    trigger: function (type, data) {
      return this.each(function () {
        jQuery.event.trigger(type, data, this);
      });
    },
    triggerHandler: function (type, data) {
      var elem = this[0];
      if (elem) {
        return jQuery.event.trigger(type, data, elem, true);
      }
    },
  });

  // Support: Firefox <=44
  // Firefox doesn't have focus(in | out) events
  // Related ticket - https://bugzilla.mozilla.org/show_bug.cgi?id=687787
  //
  // Support: Chrome <=48 - 49, Safari <=9.0 - 9.1
  // focus(in | out) events fire after focus & blur events,
  // which is spec violation - http://www.w3.org/TR/DOM-Level-3-Events/#events-focusevent-event-order
  // Related ticket - https://bugs.chromium.org/p/chromium/issues/detail?id=449857
  if (!support.focusin) {
    jQuery.each({ focus: 'focusin', blur: 'focusout' }, function (orig, fix) {
      // Attach a single capturing handler on the document while someone wants focusin/focusout
      var handler = function (event) {
        jQuery.event.simulate(fix, event.target, jQuery.event.fix(event));
      };

      jQuery.event.special[fix] = {
        setup: function () {
          // Handle: regular nodes (via `this.ownerDocument`), window
          // (via `this.document`) & document (via `this`).
          var doc = this.ownerDocument || this.document || this,
            attaches = dataPriv.access(doc, fix);

          if (!attaches) {
            doc.addEventListener(orig, handler, true);
          }
          dataPriv.access(doc, fix, (attaches || 0) + 1);
        },
        teardown: function () {
          var doc = this.ownerDocument || this.document || this,
            attaches = dataPriv.access(doc, fix) - 1;

          if (!attaches) {
            doc.removeEventListener(orig, handler, true);
            dataPriv.remove(doc, fix);
          } else {
            dataPriv.access(doc, fix, attaches);
          }
        },
      };
    });
  }
  var location = window.location;

  var nonce = { guid: Date.now() };

  var rquery = /\?/;

  // Cross-browser xml parsing
  jQuery.parseXML = function (data) {
    var xml, parserErrorElem;
    if (!data || typeof data !== 'string') {
      return null;
    }

    // Support: IE 9 - 11 only
    // IE throws on parseFromString with invalid input.
    try {
      xml = new window.DOMParser().parseFromString(data, 'text/xml');
    } catch (e) {}

    parserErrorElem = xml && xml.getElementsByTagName('parsererror')[0];
    if (!xml || parserErrorElem) {
      jQuery.error(
        'Invalid XML: ' +
          (parserErrorElem
            ? jQuery
                .map(parserErrorElem.childNodes, function (el) {
                  return el.textContent;
                })
                .join('\n')
            : data)
      );
    }
    return xml;
  };

  var rbracket = /\[\]$/,
    rCRLF = /\r?\n/g,
    rsubmitterTypes = /^(?:submit|button|image|reset|file)$/i,
    rsubmittable = /^(?:input|select|textarea|keygen)/i;

  function buildParams(prefix, obj, traditional, add) {
    var name;

    if (Array.isArray(obj)) {
      // Serialize array item.
      jQuery.each(obj, function (i, v) {
        if (traditional || rbracket.test(prefix)) {
          // Treat each array item as a scalar.
          add(prefix, v);
        } else {
          // Item is non-scalar (array or object), encode its numeric index.
          buildParams(
            prefix + '[' + (typeof v === 'object' && v != null ? i : '') + ']',
            v,
            traditional,
            add
          );
        }
      });
    } else if (!traditional && toType(obj) === 'object') {
      // Serialize object item.
      for (name in obj) {
        buildParams(prefix + '[' + name + ']', obj[name], traditional, add);
      }
    } else {
      // Serialize scalar item.
      add(prefix, obj);
    }
  }

  // Serialize an array of form elements or a set of
  // key/values into a query string
  jQuery.param = function (a, traditional) {
    var prefix,
      s = [],
      add = function (key, valueOrFunction) {
        // If value is a function, invoke it and use its return value
        var value = isFunction(valueOrFunction) ? valueOrFunction() : valueOrFunction;

        s[s.length] =
          encodeURIComponent(key) + '=' + encodeURIComponent(value == null ? '' : value);
      };

    if (a == null) {
      return '';
    }

    // If an array was passed in, assume that it is an array of form elements.
    if (Array.isArray(a) || (a.jquery && !jQuery.isPlainObject(a))) {
      // Serialize the form elements
      jQuery.each(a, function () {
        add(this.name, this.value);
      });
    } else {
      // If traditional, encode the "old" way (the way 1.3.2 or older
      // did it), otherwise encode params recursively.
      for (prefix in a) {
        buildParams(prefix, a[prefix], traditional, add);
      }
    }

    // Return the resulting serialization
    return s.join('&');
  };

  jQuery.fn.extend({
    serialize: function () {
      return jQuery.param(this.serializeArray());
    },
    serializeArray: function () {
      return this.map(function () {
        // Can add propHook for "elements" to filter or add form elements
        var elements = jQuery.prop(this, 'elements');
        return elements ? jQuery.makeArray(elements) : this;
      })
        .filter(function () {
          var type = this.type;

          // Use .is( ":disabled" ) so that fieldset[disabled] works
          return (
            this.name &&
            !jQuery(this).is(':disabled') &&
            rsubmittable.test(this.nodeName) &&
            !rsubmitterTypes.test(type) &&
            (this.checked || !rcheckableType.test(type))
          );
        })
        .map(function (_i, elem) {
          var val = jQuery(this).val();

          if (val == null) {
            return null;
          }

          if (Array.isArray(val)) {
            return jQuery.map(val, function (val) {
              return { name: elem.name, value: val.replace(rCRLF, '\r\n') };
            });
          }

          return { name: elem.name, value: val.replace(rCRLF, '\r\n') };
        })
        .get();
    },
  });

  var r20 = /%20/g,
    rhash = /#.*$/,
    rantiCache = /([?&])_=[^&]*/,
    rheaders = /^(.*?):[ \t]*([^\r\n]*)$/gm,
    // #7653, #8125, #8152: local protocol detection
    rlocalProtocol = /^(?:about|app|app-storage|.+-extension|file|res|widget):$/,
    rnoContent = /^(?:GET|HEAD)$/,
    rprotocol = /^\/\//,
    /* Prefilters
     * 1) They are useful to introduce custom dataTypes (see ajax/jsonp.js for an example)
     * 2) These are called:
     *    - BEFORE asking for a transport
     *    - AFTER param serialization (s.data is a string if s.processData is true)
     * 3) key is the dataType
     * 4) the catchall symbol "*" can be used
     * 5) execution will start with transport dataType and THEN continue down to "*" if needed
     */
    prefilters = {},
    /* Transports bindings
     * 1) key is the dataType
     * 2) the catchall symbol "*" can be used
     * 3) selection will start with transport dataType and THEN go to "*" if needed
     */
    transports = {},
    // Avoid comment-prolog char sequence (#10098); must appease lint and evade compression
    allTypes = '*/'.concat('*'),
    // Anchor tag for parsing the document origin
    originAnchor = document.createElement('a');

  originAnchor.href = location.href;

  // Base "constructor" for jQuery.ajaxPrefilter and jQuery.ajaxTransport
  function addToPrefiltersOrTransports(structure) {
    // dataTypeExpression is optional and defaults to "*"
    return function (dataTypeExpression, func) {
      if (typeof dataTypeExpression !== 'string') {
        func = dataTypeExpression;
        dataTypeExpression = '*';
      }

      var dataType,
        i = 0,
        dataTypes = dataTypeExpression.toLowerCase().match(rnothtmlwhite) || [];

      if (isFunction(func)) {
        // For each dataType in the dataTypeExpression
        while ((dataType = dataTypes[i++])) {
          // Prepend if requested
          if (dataType[0] === '+') {
            dataType = dataType.slice(1) || '*';
            (structure[dataType] = structure[dataType] || []).unshift(func);

            // Otherwise append
          } else {
            (structure[dataType] = structure[dataType] || []).push(func);
          }
        }
      }
    };
  }

  // Base inspection function for prefilters and transports
  function inspectPrefiltersOrTransports(structure, options, originalOptions, jqXHR) {
    var inspected = {},
      seekingTransport = structure === transports;

    function inspect(dataType) {
      var selected;
      inspected[dataType] = true;
      jQuery.each(structure[dataType] || [], function (_, prefilterOrFactory) {
        var dataTypeOrTransport = prefilterOrFactory(options, originalOptions, jqXHR);
        if (
          typeof dataTypeOrTransport === 'string' &&
          !seekingTransport &&
          !inspected[dataTypeOrTransport]
        ) {
          options.dataTypes.unshift(dataTypeOrTransport);
          inspect(dataTypeOrTransport);
          return false;
        } else if (seekingTransport) {
          return !(selected = dataTypeOrTransport);
        }
      });
      return selected;
    }

    return inspect(options.dataTypes[0]) || (!inspected['*'] && inspect('*'));
  }

  // A special extend for ajax options
  // that takes "flat" options (not to be deep extended)
  // Fixes #9887
  function ajaxExtend(target, src) {
    var key,
      deep,
      flatOptions = jQuery.ajaxSettings.flatOptions || {};

    for (key in src) {
      if (src[key] !== undefined) {
        (flatOptions[key] ? target : deep || (deep = {}))[key] = src[key];
      }
    }
    if (deep) {
      jQuery.extend(true, target, deep);
    }

    return target;
  }

  /* Handles responses to an ajax request:
   * - finds the right dataType (mediates between content-type and expected dataType)
   * - returns the corresponding response
   */
  function ajaxHandleResponses(s, jqXHR, responses) {
    var ct,
      type,
      finalDataType,
      firstDataType,
      contents = s.contents,
      dataTypes = s.dataTypes;

    // Remove auto dataType and get content-type in the process
    while (dataTypes[0] === '*') {
      dataTypes.shift();
      if (ct === undefined) {
        ct = s.mimeType || jqXHR.getResponseHeader('Content-Type');
      }
    }

    // Check if we're dealing with a known content-type
    if (ct) {
      for (type in contents) {
        if (contents[type] && contents[type].test(ct)) {
          dataTypes.unshift(type);
          break;
        }
      }
    }

    // Check to see if we have a response for the expected dataType
    if (dataTypes[0] in responses) {
      finalDataType = dataTypes[0];
    } else {
      // Try convertible dataTypes
      for (type in responses) {
        if (!dataTypes[0] || s.converters[type + ' ' + dataTypes[0]]) {
          finalDataType = type;
          break;
        }
        if (!firstDataType) {
          firstDataType = type;
        }
      }

      // Or just use first one
      finalDataType = finalDataType || firstDataType;
    }

    // If we found a dataType
    // We add the dataType to the list if needed
    // and return the corresponding response
    if (finalDataType) {
      if (finalDataType !== dataTypes[0]) {
        dataTypes.unshift(finalDataType);
      }
      return responses[finalDataType];
    }
  }

  /* Chain conversions given the request and the original response
   * Also sets the responseXXX fields on the jqXHR instance
   */
  function ajaxConvert(s, response, jqXHR, isSuccess) {
    var conv2,
      current,
      conv,
      tmp,
      prev,
      converters = {},
      // Work with a copy of dataTypes in case we need to modify it for conversion
      dataTypes = s.dataTypes.slice();

    // Create converters map with lowercased keys
    if (dataTypes[1]) {
      for (conv in s.converters) {
        converters[conv.toLowerCase()] = s.converters[conv];
      }
    }

    current = dataTypes.shift();

    // Convert to each sequential dataType
    while (current) {
      if (s.responseFields[current]) {
        jqXHR[s.responseFields[current]] = response;
      }

      // Apply the dataFilter if provided
      if (!prev && isSuccess && s.dataFilter) {
        response = s.dataFilter(response, s.dataType);
      }

      prev = current;
      current = dataTypes.shift();

      if (current) {
        // There's only work to do if current dataType is non-auto
        if (current === '*') {
          current = prev;

          // Convert response if prev dataType is non-auto and differs from current
        } else if (prev !== '*' && prev !== current) {
          // Seek a direct converter
          conv = converters[prev + ' ' + current] || converters['* ' + current];

          // If none found, seek a pair
          if (!conv) {
            for (conv2 in converters) {
              // If conv2 outputs current
              tmp = conv2.split(' ');
              if (tmp[1] === current) {
                // If prev can be converted to accepted input
                conv = converters[prev + ' ' + tmp[0]] || converters['* ' + tmp[0]];
                if (conv) {
                  // Condense equivalence converters
                  if (conv === true) {
                    conv = converters[conv2];

                    // Otherwise, insert the intermediate dataType
                  } else if (converters[conv2] !== true) {
                    current = tmp[0];
                    dataTypes.unshift(tmp[1]);
                  }
                  break;
                }
              }
            }
          }

          // Apply converter (if not an equivalence)
          if (conv !== true) {
            // Unless errors are allowed to bubble, catch and return them
            if (conv && s.throws) {
              response = conv(response);
            } else {
              try {
                response = conv(response);
              } catch (e) {
                return {
                  state: 'parsererror',
                  error: conv ? e : 'No conversion from ' + prev + ' to ' + current,
                };
              }
            }
          }
        }
      }
    }

    return { state: 'success', data: response };
  }

  jQuery.extend({
    // Counter for holding the number of active queries
    active: 0,

    // Last-Modified header cache for next request
    lastModified: {},
    etag: {},

    ajaxSettings: {
      url: location.href,
      type: 'GET',
      isLocal: rlocalProtocol.test(location.protocol),
      global: true,
      processData: true,
      async: true,
      contentType: 'application/x-www-form-urlencoded; charset=UTF-8',

      /*
		timeout: 0,
		data: null,
		dataType: null,
		username: null,
		password: null,
		cache: null,
		throws: false,
		traditional: false,
		headers: {},
		*/

      accepts: {
        '*': allTypes,
        text: 'text/plain',
        html: 'text/html',
        xml: 'application/xml, text/xml',
        json: 'application/json, text/javascript',
      },

      contents: {
        xml: /\bxml\b/,
        html: /\bhtml/,
        json: /\bjson\b/,
      },

      responseFields: {
        xml: 'responseXML',
        text: 'responseText',
        json: 'responseJSON',
      },

      // Data converters
      // Keys separate source (or catchall "*") and destination types with a single space
      converters: {
        // Convert anything to text
        '* text': String,

        // Text to html (true = no transformation)
        'text html': true,

        // Evaluate text as a json expression
        'text json': JSON.parse,

        // Parse text as xml
        'text xml': jQuery.parseXML,
      },

      // For options that shouldn't be deep extended:
      // you can add your own custom options here if
      // and when you create one that shouldn't be
      // deep extended (see ajaxExtend)
      flatOptions: {
        url: true,
        context: true,
      },
    },

    // Creates a full fledged settings object into target
    // with both ajaxSettings and settings fields.
    // If target is omitted, writes into ajaxSettings.
    ajaxSetup: function (target, settings) {
      return settings
        ? // Building a settings object
          ajaxExtend(ajaxExtend(target, jQuery.ajaxSettings), settings)
        : // Extending ajaxSettings
          ajaxExtend(jQuery.ajaxSettings, target);
    },

    ajaxPrefilter: addToPrefiltersOrTransports(prefilters),
    ajaxTransport: addToPrefiltersOrTransports(transports),

    // Main method
    ajax: function (url, options) {
      // If url is an object, simulate pre-1.5 signature
      if (typeof url === 'object') {
        options = url;
        url = undefined;
      }

      // Force options to be an object
      options = options || {};

      var transport,
        // URL without anti-cache param
        cacheURL,
        // Response headers
        responseHeadersString,
        responseHeaders,
        // timeout handle
        timeoutTimer,
        // Url cleanup var
        urlAnchor,
        // Request state (becomes false upon send and true upon completion)
        completed,
        // To know if global events are to be dispatched
        fireGlobals,
        // Loop variable
        i,
        // uncached part of the url
        uncached,
        // Create the final options object
        s = jQuery.ajaxSetup({}, options),
        // Callbacks context
        callbackContext = s.context || s,
        // Context for global events is callbackContext if it is a DOM node or jQuery collection
        globalEventContext =
          s.context && (callbackContext.nodeType || callbackContext.jquery)
            ? jQuery(callbackContext)
            : jQuery.event,
        // Deferreds
        deferred = jQuery.Deferred(),
        completeDeferred = jQuery.Callbacks('once memory'),
        // Status-dependent callbacks
        statusCode = s.statusCode || {},
        // Headers (they are sent all at once)
        requestHeaders = {},
        requestHeadersNames = {},
        // Default abort message
        strAbort = 'canceled',
        // Fake xhr
        jqXHR = {
          readyState: 0,

          // Builds headers hashtable if needed
          getResponseHeader: function (key) {
            var match;
            if (completed) {
              if (!responseHeaders) {
                responseHeaders = {};
                while ((match = rheaders.exec(responseHeadersString))) {
                  responseHeaders[match[1].toLowerCase() + ' '] = (
                    responseHeaders[match[1].toLowerCase() + ' '] || []
                  ).concat(match[2]);
                }
              }
              match = responseHeaders[key.toLowerCase() + ' '];
            }
            return match == null ? null : match.join(', ');
          },

          // Raw string
          getAllResponseHeaders: function () {
            return completed ? responseHeadersString : null;
          },

          // Caches the header
          setRequestHeader: function (name, value) {
            if (completed == null) {
              name = requestHeadersNames[name.toLowerCase()] =
                requestHeadersNames[name.toLowerCase()] || name;
              requestHeaders[name] = value;
            }
            return this;
          },

          // Overrides response content-type header
          overrideMimeType: function (type) {
            if (completed == null) {
              s.mimeType = type;
            }
            return this;
          },

          // Status-dependent callbacks
          statusCode: function (map) {
            var code;
            if (map) {
              if (completed) {
                // Execute the appropriate callbacks
                jqXHR.always(map[jqXHR.status]);
              } else {
                // Lazy-add the new callbacks in a way that preserves old ones
                for (code in map) {
                  statusCode[code] = [statusCode[code], map[code]];
                }
              }
            }
            return this;
          },

          // Cancel the request
          abort: function (statusText) {
            var finalText = statusText || strAbort;
            if (transport) {
              transport.abort(finalText);
            }
            done(0, finalText);
            return this;
          },
        };

      // Attach deferreds
      deferred.promise(jqXHR);

      // Add protocol if not provided (prefilters might expect it)
      // Handle falsy url in the settings object (#10093: consistency with old signature)
      // We also use the url parameter if available
      s.url = ((url || s.url || location.href) + '').replace(rprotocol, location.protocol + '//');

      // Alias method option to type as per ticket #12004
      s.type = options.method || options.type || s.method || s.type;

      // Extract dataTypes list
      s.dataTypes = (s.dataType || '*').toLowerCase().match(rnothtmlwhite) || [''];

      // A cross-domain request is in order when the origin doesn't match the current origin.
      if (s.crossDomain == null) {
        urlAnchor = document.createElement('a');

        // Support: IE <=8 - 11, Edge 12 - 15
        // IE throws exception on accessing the href property if url is malformed,
        // e.g. http://example.com:80x/
        try {
          urlAnchor.href = s.url;

          // Support: IE <=8 - 11 only
          // Anchor's host property isn't correctly set when s.url is relative
          urlAnchor.href = urlAnchor.href;
          s.crossDomain =
            originAnchor.protocol + '//' + originAnchor.host !==
            urlAnchor.protocol + '//' + urlAnchor.host;
        } catch (e) {
          // If there is an error parsing the URL, assume it is crossDomain,
          // it can be rejected by the transport if it is invalid
          s.crossDomain = true;
        }
      }

      // Convert data if not already a string
      if (s.data && s.processData && typeof s.data !== 'string') {
        s.data = jQuery.param(s.data, s.traditional);
      }

      // Apply prefilters
      inspectPrefiltersOrTransports(prefilters, s, options, jqXHR);

      // If request was aborted inside a prefilter, stop there
      if (completed) {
        return jqXHR;
      }

      // We can fire global events as of now if asked to
      // Don't fire events if jQuery.event is undefined in an AMD-usage scenario (#15118)
      fireGlobals = jQuery.event && s.global;

      // Watch for a new set of requests
      if (fireGlobals && jQuery.active++ === 0) {
        jQuery.event.trigger('ajaxStart');
      }

      // Uppercase the type
      s.type = s.type.toUpperCase();

      // Determine if request has content
      s.hasContent = !rnoContent.test(s.type);

      // Save the URL in case we're toying with the If-Modified-Since
      // and/or If-None-Match header later on
      // Remove hash to simplify url manipulation
      cacheURL = s.url.replace(rhash, '');

      // More options handling for requests with no content
      if (!s.hasContent) {
        // Remember the hash so we can put it back
        uncached = s.url.slice(cacheURL.length);

        // If data is available and should be processed, append data to url
        if (s.data && (s.processData || typeof s.data === 'string')) {
          cacheURL += (rquery.test(cacheURL) ? '&' : '?') + s.data;

          // #9682: remove data so that it's not used in an eventual retry
          delete s.data;
        }

        // Add or update anti-cache param if needed
        if (s.cache === false) {
          cacheURL = cacheURL.replace(rantiCache, '$1');
          uncached = (rquery.test(cacheURL) ? '&' : '?') + '_=' + nonce.guid++ + uncached;
        }

        // Put hash and anti-cache on the URL that will be requested (gh-1732)
        s.url = cacheURL + uncached;

        // Change '%20' to '+' if this is encoded form body content (gh-2658)
      } else if (
        s.data &&
        s.processData &&
        (s.contentType || '').indexOf('application/x-www-form-urlencoded') === 0
      ) {
        s.data = s.data.replace(r20, '+');
      }

      // Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
      if (s.ifModified) {
        if (jQuery.lastModified[cacheURL]) {
          jqXHR.setRequestHeader('If-Modified-Since', jQuery.lastModified[cacheURL]);
        }
        if (jQuery.etag[cacheURL]) {
          jqXHR.setRequestHeader('If-None-Match', jQuery.etag[cacheURL]);
        }
      }

      // Set the correct header, if data is being sent
      if ((s.data && s.hasContent && s.contentType !== false) || options.contentType) {
        jqXHR.setRequestHeader('Content-Type', s.contentType);
      }

      // Set the Accepts header for the server, depending on the dataType
      jqXHR.setRequestHeader(
        'Accept',
        s.dataTypes[0] && s.accepts[s.dataTypes[0]]
          ? s.accepts[s.dataTypes[0]] + (s.dataTypes[0] !== '*' ? ', ' + allTypes + '; q=0.01' : '')
          : s.accepts['*']
      );

      // Check for headers option
      for (i in s.headers) {
        jqXHR.setRequestHeader(i, s.headers[i]);
      }

      // Allow custom headers/mimetypes and early abort
      if (s.beforeSend && (s.beforeSend.call(callbackContext, jqXHR, s) === false || completed)) {
        // Abort if not done already and return
        return jqXHR.abort();
      }

      // Aborting is no longer a cancellation
      strAbort = 'abort';

      // Install callbacks on deferreds
      completeDeferred.add(s.complete);
      jqXHR.done(s.success);
      jqXHR.fail(s.error);

      // Get transport
      transport = inspectPrefiltersOrTransports(transports, s, options, jqXHR);

      // If no transport, we auto-abort
      if (!transport) {
        done(-1, 'No Transport');
      } else {
        jqXHR.readyState = 1;

        // Send global event
        if (fireGlobals) {
          globalEventContext.trigger('ajaxSend', [jqXHR, s]);
        }

        // If request was aborted inside ajaxSend, stop there
        if (completed) {
          return jqXHR;
        }

        // Timeout
        if (s.async && s.timeout > 0) {
          timeoutTimer = window.setTimeout(function () {
            jqXHR.abort('timeout');
          }, s.timeout);
        }

        try {
          completed = false;
          transport.send(requestHeaders, done);
        } catch (e) {
          // Rethrow post-completion exceptions
          if (completed) {
            throw e;
          }

          // Propagate others as results
          done(-1, e);
        }
      }

      // Callback for when everything is done
      function done(status, nativeStatusText, responses, headers) {
        var isSuccess,
          success,
          error,
          response,
          modified,
          statusText = nativeStatusText;

        // Ignore repeat invocations
        if (completed) {
          return;
        }

        completed = true;

        // Clear timeout if it exists
        if (timeoutTimer) {
          window.clearTimeout(timeoutTimer);
        }

        // Dereference transport for early garbage collection
        // (no matter how long the jqXHR object will be used)
        transport = undefined;

        // Cache response headers
        responseHeadersString = headers || '';

        // Set readyState
        jqXHR.readyState = status > 0 ? 4 : 0;

        // Determine if successful
        isSuccess = (status >= 200 && status < 300) || status === 304;

        // Get response data
        if (responses) {
          response = ajaxHandleResponses(s, jqXHR, responses);
        }

        // Use a noop converter for missing script but not if jsonp
        if (
          !isSuccess &&
          jQuery.inArray('script', s.dataTypes) > -1 &&
          jQuery.inArray('json', s.dataTypes) < 0
        ) {
          s.converters['text script'] = function () {};
        }

        // Convert no matter what (that way responseXXX fields are always set)
        response = ajaxConvert(s, response, jqXHR, isSuccess);

        // If successful, handle type chaining
        if (isSuccess) {
          // Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
          if (s.ifModified) {
            modified = jqXHR.getResponseHeader('Last-Modified');
            if (modified) {
              jQuery.lastModified[cacheURL] = modified;
            }
            modified = jqXHR.getResponseHeader('etag');
            if (modified) {
              jQuery.etag[cacheURL] = modified;
            }
          }

          // if no content
          if (status === 204 || s.type === 'HEAD') {
            statusText = 'nocontent';

            // if not modified
          } else if (status === 304) {
            statusText = 'notmodified';

            // If we have data, let's convert it
          } else {
            statusText = response.state;
            success = response.data;
            error = response.error;
            isSuccess = !error;
          }
        } else {
          // Extract error from statusText and normalize for non-aborts
          error = statusText;
          if (status || !statusText) {
            statusText = 'error';
            if (status < 0) {
              status = 0;
            }
          }
        }

        // Set data for the fake xhr object
        jqXHR.status = status;
        jqXHR.statusText = (nativeStatusText || statusText) + '';

        // Success/Error
        if (isSuccess) {
          deferred.resolveWith(callbackContext, [success, statusText, jqXHR]);
        } else {
          deferred.rejectWith(callbackContext, [jqXHR, statusText, error]);
        }

        // Status-dependent callbacks
        jqXHR.statusCode(statusCode);
        statusCode = undefined;

        if (fireGlobals) {
          globalEventContext.trigger(isSuccess ? 'ajaxSuccess' : 'ajaxError', [
            jqXHR,
            s,
            isSuccess ? success : error,
          ]);
        }

        // Complete
        completeDeferred.fireWith(callbackContext, [jqXHR, statusText]);

        if (fireGlobals) {
          globalEventContext.trigger('ajaxComplete', [jqXHR, s]);

          // Handle the global AJAX counter
          if (!--jQuery.active) {
            jQuery.event.trigger('ajaxStop');
          }
        }
      }

      return jqXHR;
    },

    getJSON: function (url, data, callback) {
      return jQuery.get(url, data, callback, 'json');
    },

    getScript: function (url, callback) {
      return jQuery.get(url, undefined, callback, 'script');
    },
  });

  jQuery.each(['get', 'post'], function (_i, method) {
    jQuery[method] = function (url, data, callback, type) {
      // Shift arguments if data argument was omitted
      if (isFunction(data)) {
        type = type || callback;
        callback = data;
        data = undefined;
      }

      // The url can be an options object (which then must have .url)
      return jQuery.ajax(
        jQuery.extend(
          {
            url: url,
            type: method,
            dataType: type,
            data: data,
            success: callback,
          },
          jQuery.isPlainObject(url) && url
        )
      );
    };
  });

  jQuery.ajaxPrefilter(function (s) {
    var i;
    for (i in s.headers) {
      if (i.toLowerCase() === 'content-type') {
        s.contentType = s.headers[i] || '';
      }
    }
  });

  jQuery._evalUrl = function (url, options, doc) {
    return jQuery.ajax({
      url: url,

      // Make this explicit, since user can override this through ajaxSetup (#11264)
      type: 'GET',
      dataType: 'script',
      cache: true,
      async: false,
      global: false,

      // Only evaluate the response if it is successful (gh-4126)
      // dataFilter is not invoked for failure responses, so using it instead
      // of the default converter is kludgy but it works.
      converters: {
        'text script': function () {},
      },
      dataFilter: function (response) {
        jQuery.globalEval(response, options, doc);
      },
    });
  };

  jQuery.fn.extend({
    wrapAll: function (html) {
      var wrap;

      if (this[0]) {
        if (isFunction(html)) {
          html = html.call(this[0]);
        }

        // The elements to wrap the target around
        wrap = jQuery(html, this[0].ownerDocument).eq(0).clone(true);

        if (this[0].parentNode) {
          wrap.insertBefore(this[0]);
        }

        wrap
          .map(function () {
            var elem = this;

            while (elem.firstElementChild) {
              elem = elem.firstElementChild;
            }

            return elem;
          })
          .append(this);
      }

      return this;
    },

    wrapInner: function (html) {
      if (isFunction(html)) {
        return this.each(function (i) {
          jQuery(this).wrapInner(html.call(this, i));
        });
      }

      return this.each(function () {
        var self = jQuery(this),
          contents = self.contents();

        if (contents.length) {
          contents.wrapAll(html);
        } else {
          self.append(html);
        }
      });
    },

    wrap: function (html) {
      var htmlIsFunction = isFunction(html);

      return this.each(function (i) {
        jQuery(this).wrapAll(htmlIsFunction ? html.call(this, i) : html);
      });
    },

    unwrap: function (selector) {
      this.parent(selector)
        .not('body')
        .each(function () {
          jQuery(this).replaceWith(this.childNodes);
        });
      return this;
    },
  });

  jQuery.expr.pseudos.hidden = function (elem) {
    return !jQuery.expr.pseudos.visible(elem);
  };
  jQuery.expr.pseudos.visible = function (elem) {
    return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
  };

  jQuery.ajaxSettings.xhr = function () {
    try {
      return new window.XMLHttpRequest();
    } catch (e) {}
  };

  var xhrSuccessStatus = {
      // File protocol always yields status code 0, assume 200
      0: 200,

      // Support: IE <=9 only
      // #1450: sometimes IE returns 1223 when it should be 204
      1223: 204,
    },
    xhrSupported = jQuery.ajaxSettings.xhr();

  support.cors = !!xhrSupported && 'withCredentials' in xhrSupported;
  support.ajax = xhrSupported = !!xhrSupported;

  jQuery.ajaxTransport(function (options) {
    var callback, errorCallback;

    // Cross domain only allowed if supported through XMLHttpRequest
    if (support.cors || (xhrSupported && !options.crossDomain)) {
      return {
        send: function (headers, complete) {
          var i,
            xhr = options.xhr();

          xhr.open(options.type, options.url, options.async, options.username, options.password);

          // Apply custom fields if provided
          if (options.xhrFields) {
            for (i in options.xhrFields) {
              xhr[i] = options.xhrFields[i];
            }
          }

          // Override mime type if needed
          if (options.mimeType && xhr.overrideMimeType) {
            xhr.overrideMimeType(options.mimeType);
          }

          // X-Requested-With header
          // For cross-domain requests, seeing as conditions for a preflight are
          // akin to a jigsaw puzzle, we simply never set it to be sure.
          // (it can always be set on a per-request basis or even using ajaxSetup)
          // For same-domain requests, won't change header if already provided.
          if (!options.crossDomain && !headers['X-Requested-With']) {
            headers['X-Requested-With'] = 'XMLHttpRequest';
          }

          // Set headers
          for (i in headers) {
            xhr.setRequestHeader(i, headers[i]);
          }

          // Callback
          callback = function (type) {
            return function () {
              if (callback) {
                callback = errorCallback = xhr.onload = xhr.onerror = xhr.onabort = xhr.ontimeout = xhr.onreadystatechange = null;

                if (type === 'abort') {
                  xhr.abort();
                } else if (type === 'error') {
                  // Support: IE <=9 only
                  // On a manual native abort, IE9 throws
                  // errors on any property access that is not readyState
                  if (typeof xhr.status !== 'number') {
                    complete(0, 'error');
                  } else {
                    complete(
                      // File: protocol always yields status 0; see #8605, #14207
                      xhr.status,
                      xhr.statusText
                    );
                  }
                } else {
                  complete(
                    xhrSuccessStatus[xhr.status] || xhr.status,
                    xhr.statusText,

                    // Support: IE <=9 only
                    // IE9 has no XHR2 but throws on binary (trac-11426)
                    // For XHR2 non-text, let the caller handle it (gh-2498)
                    (xhr.responseType || 'text') !== 'text' || typeof xhr.responseText !== 'string'
                      ? { binary: xhr.response }
                      : { text: xhr.responseText },
                    xhr.getAllResponseHeaders()
                  );
                }
              }
            };
          };

          // Listen to events
          xhr.onload = callback();
          errorCallback = xhr.onerror = xhr.ontimeout = callback('error');

          // Support: IE 9 only
          // Use onreadystatechange to replace onabort
          // to handle uncaught aborts
          if (xhr.onabort !== undefined) {
            xhr.onabort = errorCallback;
          } else {
            xhr.onreadystatechange = function () {
              // Check readyState before timeout as it changes
              if (xhr.readyState === 4) {
                // Allow onerror to be called first,
                // but that will not handle a native abort
                // Also, save errorCallback to a variable
                // as xhr.onerror cannot be accessed
                window.setTimeout(function () {
                  if (callback) {
                    errorCallback();
                  }
                });
              }
            };
          }

          // Create the abort callback
          callback = callback('abort');

          try {
            // Do send the request (this may raise an exception)
            xhr.send((options.hasContent && options.data) || null);
          } catch (e) {
            // #14683: Only rethrow if this hasn't been notified as an error yet
            if (callback) {
              throw e;
            }
          }
        },

        abort: function () {
          if (callback) {
            callback();
          }
        },
      };
    }
  });

  // Prevent auto-execution of scripts when no explicit dataType was provided (See gh-2432)
  jQuery.ajaxPrefilter(function (s) {
    if (s.crossDomain) {
      s.contents.script = false;
    }
  });

  // Install script dataType
  jQuery.ajaxSetup({
    accepts: {
      script:
        'text/javascript, application/javascript, ' +
        'application/ecmascript, application/x-ecmascript',
    },
    contents: {
      script: /\b(?:java|ecma)script\b/,
    },
    converters: {
      'text script': function (text) {
        jQuery.globalEval(text);
        return text;
      },
    },
  });

  // Handle cache's special case and crossDomain
  jQuery.ajaxPrefilter('script', function (s) {
    if (s.cache === undefined) {
      s.cache = false;
    }
    if (s.crossDomain) {
      s.type = 'GET';
    }
  });

  // Bind script tag hack transport
  jQuery.ajaxTransport('script', function (s) {
    // This transport only deals with cross domain or forced-by-attrs requests
    if (s.crossDomain || s.scriptAttrs) {
      var script, callback;
      return {
        send: function (_, complete) {
          script = jQuery('<script>')
            .attr(s.scriptAttrs || {})
            .prop({ charset: s.scriptCharset, src: s.url })
            .on(
              'load error',
              (callback = function (evt) {
                script.remove();
                callback = null;
                if (evt) {
                  complete(evt.type === 'error' ? 404 : 200, evt.type);
                }
              })
            );

          // Use native DOM manipulation to avoid our domManip AJAX trickery
          document.head.appendChild(script[0]);
        },
        abort: function () {
          if (callback) {
            callback();
          }
        },
      };
    }
  });

  var oldCallbacks = [],
    rjsonp = /(=)\?(?=&|$)|\?\?/;

  // Default jsonp settings
  jQuery.ajaxSetup({
    jsonp: 'callback',
    jsonpCallback: function () {
      var callback = oldCallbacks.pop() || jQuery.expando + '_' + nonce.guid++;
      this[callback] = true;
      return callback;
    },
  });

  // Detect, normalize options and install callbacks for jsonp requests
  jQuery.ajaxPrefilter('json jsonp', function (s, originalSettings, jqXHR) {
    var callbackName,
      overwritten,
      responseContainer,
      jsonProp =
        s.jsonp !== false &&
        (rjsonp.test(s.url)
          ? 'url'
          : typeof s.data === 'string' &&
            (s.contentType || '').indexOf('application/x-www-form-urlencoded') === 0 &&
            rjsonp.test(s.data) &&
            'data');

    // Handle iff the expected data type is "jsonp" or we have a parameter to set
    if (jsonProp || s.dataTypes[0] === 'jsonp') {
      // Get callback name, remembering preexisting value associated with it
      callbackName = s.jsonpCallback = isFunction(s.jsonpCallback)
        ? s.jsonpCallback()
        : s.jsonpCallback;

      // Insert callback into url or form data
      if (jsonProp) {
        s[jsonProp] = s[jsonProp].replace(rjsonp, '$1' + callbackName);
      } else if (s.jsonp !== false) {
        s.url += (rquery.test(s.url) ? '&' : '?') + s.jsonp + '=' + callbackName;
      }

      // Use data converter to retrieve json after script execution
      s.converters['script json'] = function () {
        if (!responseContainer) {
          jQuery.error(callbackName + ' was not called');
        }
        return responseContainer[0];
      };

      // Force json dataType
      s.dataTypes[0] = 'json';

      // Install callback
      overwritten = window[callbackName];
      window[callbackName] = function () {
        responseContainer = arguments;
      };

      // Clean-up function (fires after converters)
      jqXHR.always(function () {
        // If previous value didn't exist - remove it
        if (overwritten === undefined) {
          jQuery(window).removeProp(callbackName);

          // Otherwise restore preexisting value
        } else {
          window[callbackName] = overwritten;
        }

        // Save back as free
        if (s[callbackName]) {
          // Make sure that re-using the options doesn't screw things around
          s.jsonpCallback = originalSettings.jsonpCallback;

          // Save the callback name for future use
          oldCallbacks.push(callbackName);
        }

        // Call if it was a function and we have a response
        if (responseContainer && isFunction(overwritten)) {
          overwritten(responseContainer[0]);
        }

        responseContainer = overwritten = undefined;
      });

      // Delegate to script
      return 'script';
    }
  });

  // Support: Safari 8 only
  // In Safari 8 documents created via document.implementation.createHTMLDocument
  // collapse sibling forms: the second one becomes a child of the first one.
  // Because of that, this security measure has to be disabled in Safari 8.
  // https://bugs.webkit.org/show_bug.cgi?id=137337
  support.createHTMLDocument = (function () {
    var body = document.implementation.createHTMLDocument('').body;
    body.innerHTML = '<form></form><form></form>';
    return body.childNodes.length === 2;
  })();

  // Argument "data" should be string of html
  // context (optional): If specified, the fragment will be created in this context,
  // defaults to document
  // keepScripts (optional): If true, will include scripts passed in the html string
  jQuery.parseHTML = function (data, context, keepScripts) {
    if (typeof data !== 'string') {
      return [];
    }
    if (typeof context === 'boolean') {
      keepScripts = context;
      context = false;
    }

    var base, parsed, scripts;

    if (!context) {
      // Stop scripts or inline event handlers from being executed immediately
      // by using document.implementation
      if (support.createHTMLDocument) {
        context = document.implementation.createHTMLDocument('');

        // Set the base href for the created document
        // so any parsed elements with URLs
        // are based on the document's URL (gh-2965)
        base = context.createElement('base');
        base.href = document.location.href;
        context.head.appendChild(base);
      } else {
        context = document;
      }
    }

    parsed = rsingleTag.exec(data);
    scripts = !keepScripts && [];

    // Single tag
    if (parsed) {
      return [context.createElement(parsed[1])];
    }

    parsed = buildFragment([data], context, scripts);

    if (scripts && scripts.length) {
      jQuery(scripts).remove();
    }

    return jQuery.merge([], parsed.childNodes);
  };

  /**
   * Load a url into a page
   */
  jQuery.fn.load = function (url, params, callback) {
    var selector,
      type,
      response,
      self = this,
      off = url.indexOf(' ');

    if (off > -1) {
      selector = stripAndCollapse(url.slice(off));
      url = url.slice(0, off);
    }

    // If it's a function
    if (isFunction(params)) {
      // We assume that it's the callback
      callback = params;
      params = undefined;

      // Otherwise, build a param string
    } else if (params && typeof params === 'object') {
      type = 'POST';
    }

    // If we have elements to modify, make the request
    if (self.length > 0) {
      jQuery
        .ajax({
          url: url,

          // If "type" variable is undefined, then "GET" method will be used.
          // Make value of this field explicit since
          // user can override it through ajaxSetup method
          type: type || 'GET',
          dataType: 'html',
          data: params,
        })
        .done(function (responseText) {
          // Save response for use in complete callback
          response = arguments;

          self.html(
            selector
              ? // If a selector was specified, locate the right elements in a dummy div
                // Exclude scripts to avoid IE 'Permission Denied' errors
                jQuery('<div>').append(jQuery.parseHTML(responseText)).find(selector)
              : // Otherwise use the full result
                responseText
          );

          // If the request succeeds, this function gets "data", "status", "jqXHR"
          // but they are ignored because response was set above.
          // If it fails, this function gets "jqXHR", "status", "error"
        })
        .always(
          callback &&
            function (jqXHR, status) {
              self.each(function () {
                callback.apply(this, response || [jqXHR.responseText, status, jqXHR]);
              });
            }
        );
    }

    return this;
  };

  jQuery.expr.pseudos.animated = function (elem) {
    return jQuery.grep(jQuery.timers, function (fn) {
      return elem === fn.elem;
    }).length;
  };

  jQuery.offset = {
    setOffset: function (elem, options, i) {
      var curPosition,
        curLeft,
        curCSSTop,
        curTop,
        curOffset,
        curCSSLeft,
        calculatePosition,
        position = jQuery.css(elem, 'position'),
        curElem = jQuery(elem),
        props = {};

      // Set position first, in-case top/left are set even on static elem
      if (position === 'static') {
        elem.style.position = 'relative';
      }

      curOffset = curElem.offset();
      curCSSTop = jQuery.css(elem, 'top');
      curCSSLeft = jQuery.css(elem, 'left');
      calculatePosition =
        (position === 'absolute' || position === 'fixed') &&
        (curCSSTop + curCSSLeft).indexOf('auto') > -1;

      // Need to be able to calculate position if either
      // top or left is auto and position is either absolute or fixed
      if (calculatePosition) {
        curPosition = curElem.position();
        curTop = curPosition.top;
        curLeft = curPosition.left;
      } else {
        curTop = parseFloat(curCSSTop) || 0;
        curLeft = parseFloat(curCSSLeft) || 0;
      }

      if (isFunction(options)) {
        // Use jQuery.extend here to allow modification of coordinates argument (gh-1848)
        options = options.call(elem, i, jQuery.extend({}, curOffset));
      }

      if (options.top != null) {
        props.top = options.top - curOffset.top + curTop;
      }
      if (options.left != null) {
        props.left = options.left - curOffset.left + curLeft;
      }

      if ('using' in options) {
        options.using.call(elem, props);
      } else {
        curElem.css(props);
      }
    },
  };

  jQuery.fn.extend({
    // offset() relates an element's border box to the document origin
    offset: function (options) {
      // Preserve chaining for setter
      if (arguments.length) {
        return options === undefined
          ? this
          : this.each(function (i) {
              jQuery.offset.setOffset(this, options, i);
            });
      }

      var rect,
        win,
        elem = this[0];

      if (!elem) {
        return;
      }

      // Return zeros for disconnected and hidden (display: none) elements (gh-2310)
      // Support: IE <=11 only
      // Running getBoundingClientRect on a
      // disconnected node in IE throws an error
      if (!elem.getClientRects().length) {
        return { top: 0, left: 0 };
      }

      // Get document-relative position by adding viewport scroll to viewport-relative gBCR
      rect = elem.getBoundingClientRect();
      win = elem.ownerDocument.defaultView;
      return {
        top: rect.top + win.pageYOffset,
        left: rect.left + win.pageXOffset,
      };
    },

    // position() relates an element's margin box to its offset parent's padding box
    // This corresponds to the behavior of CSS absolute positioning
    position: function () {
      if (!this[0]) {
        return;
      }

      var offsetParent,
        offset,
        doc,
        elem = this[0],
        parentOffset = { top: 0, left: 0 };

      // position:fixed elements are offset from the viewport, which itself always has zero offset
      if (jQuery.css(elem, 'position') === 'fixed') {
        // Assume position:fixed implies availability of getBoundingClientRect
        offset = elem.getBoundingClientRect();
      } else {
        offset = this.offset();

        // Account for the *real* offset parent, which can be the document or its root element
        // when a statically positioned element is identified
        doc = elem.ownerDocument;
        offsetParent = elem.offsetParent || doc.documentElement;
        while (
          offsetParent &&
          (offsetParent === doc.body || offsetParent === doc.documentElement) &&
          jQuery.css(offsetParent, 'position') === 'static'
        ) {
          offsetParent = offsetParent.parentNode;
        }
        if (offsetParent && offsetParent !== elem && offsetParent.nodeType === 1) {
          // Incorporate borders into its offset, since they are outside its content origin
          parentOffset = jQuery(offsetParent).offset();
          parentOffset.top += jQuery.css(offsetParent, 'borderTopWidth', true);
          parentOffset.left += jQuery.css(offsetParent, 'borderLeftWidth', true);
        }
      }

      // Subtract parent offsets and element margins
      return {
        top: offset.top - parentOffset.top - jQuery.css(elem, 'marginTop', true),
        left: offset.left - parentOffset.left - jQuery.css(elem, 'marginLeft', true),
      };
    },

    // This method will return documentElement in the following cases:
    // 1) For the element inside the iframe without offsetParent, this method will return
    //    documentElement of the parent window
    // 2) For the hidden or detached element
    // 3) For body or html element, i.e. in case of the html node - it will return itself
    //
    // but those exceptions were never presented as a real life use-cases
    // and might be considered as more preferable results.
    //
    // This logic, however, is not guaranteed and can change at any point in the future
    offsetParent: function () {
      return this.map(function () {
        var offsetParent = this.offsetParent;

        while (offsetParent && jQuery.css(offsetParent, 'position') === 'static') {
          offsetParent = offsetParent.offsetParent;
        }

        return offsetParent || documentElement;
      });
    },
  });

  // Create scrollLeft and scrollTop methods
  jQuery.each({ scrollLeft: 'pageXOffset', scrollTop: 'pageYOffset' }, function (method, prop) {
    var top = 'pageYOffset' === prop;

    jQuery.fn[method] = function (val) {
      return access(
        this,
        function (elem, method, val) {
          // Coalesce documents and windows
          var win;
          if (isWindow(elem)) {
            win = elem;
          } else if (elem.nodeType === 9) {
            win = elem.defaultView;
          }

          if (val === undefined) {
            return win ? win[prop] : elem[method];
          }

          if (win) {
            win.scrollTo(!top ? val : win.pageXOffset, top ? val : win.pageYOffset);
          } else {
            elem[method] = val;
          }
        },
        method,
        val,
        arguments.length
      );
    };
  });

  // Support: Safari <=7 - 9.1, Chrome <=37 - 49
  // Add the top/left cssHooks using jQuery.fn.position
  // Webkit bug: https://bugs.webkit.org/show_bug.cgi?id=29084
  // Blink bug: https://bugs.chromium.org/p/chromium/issues/detail?id=589347
  // getComputedStyle returns percent when specified for top/left/bottom/right;
  // rather than make the css module depend on the offset module, just check for it here
  jQuery.each(['top', 'left'], function (_i, prop) {
    jQuery.cssHooks[prop] = addGetHookIf(support.pixelPosition, function (elem, computed) {
      if (computed) {
        computed = curCSS(elem, prop);

        // If curCSS returns percentage, fallback to offset
        return rnumnonpx.test(computed) ? jQuery(elem).position()[prop] + 'px' : computed;
      }
    });
  });

  // Create innerHeight, innerWidth, height, width, outerHeight and outerWidth methods
  jQuery.each({ Height: 'height', Width: 'width' }, function (name, type) {
    jQuery.each(
      {
        padding: 'inner' + name,
        content: type,
        '': 'outer' + name,
      },
      function (defaultExtra, funcName) {
        // Margin is only for outerHeight, outerWidth
        jQuery.fn[funcName] = function (margin, value) {
          var chainable = arguments.length && (defaultExtra || typeof margin !== 'boolean'),
            extra = defaultExtra || (margin === true || value === true ? 'margin' : 'border');

          return access(
            this,
            function (elem, type, value) {
              var doc;

              if (isWindow(elem)) {
                // $( window ).outerWidth/Height return w/h including scrollbars (gh-1729)
                return funcName.indexOf('outer') === 0
                  ? elem['inner' + name]
                  : elem.document.documentElement['client' + name];
              }

              // Get document width or height
              if (elem.nodeType === 9) {
                doc = elem.documentElement;

                // Either scroll[Width/Height] or offset[Width/Height] or client[Width/Height],
                // whichever is greatest
                return Math.max(
                  elem.body['scroll' + name],
                  doc['scroll' + name],
                  elem.body['offset' + name],
                  doc['offset' + name],
                  doc['client' + name]
                );
              }

              return value === undefined
                ? // Get width or height on the element, requesting but not forcing parseFloat
                  jQuery.css(elem, type, extra)
                : // Set width or height on the element
                  jQuery.style(elem, type, value, extra);
            },
            type,
            chainable ? margin : undefined,
            chainable
          );
        };
      }
    );
  });

  jQuery.each(
    ['ajaxStart', 'ajaxStop', 'ajaxComplete', 'ajaxError', 'ajaxSuccess', 'ajaxSend'],
    function (_i, type) {
      jQuery.fn[type] = function (fn) {
        return this.on(type, fn);
      };
    }
  );

  jQuery.fn.extend({
    bind: function (types, data, fn) {
      return this.on(types, null, data, fn);
    },
    unbind: function (types, fn) {
      return this.off(types, null, fn);
    },

    delegate: function (selector, types, data, fn) {
      return this.on(types, selector, data, fn);
    },
    undelegate: function (selector, types, fn) {
      // ( namespace ) or ( selector, types [, fn] )
      return arguments.length === 1
        ? this.off(selector, '**')
        : this.off(types, selector || '**', fn);
    },

    hover: function (fnOver, fnOut) {
      return this.mouseenter(fnOver).mouseleave(fnOut || fnOver);
    },
  });

  jQuery.each(
    (
      'blur focus focusin focusout resize scroll click dblclick ' +
      'mousedown mouseup mousemove mouseover mouseout mouseenter mouseleave ' +
      'change select submit keydown keypress keyup contextmenu'
    ).split(' '),
    function (_i, name) {
      // Handle event binding
      jQuery.fn[name] = function (data, fn) {
        return arguments.length > 0 ? this.on(name, null, data, fn) : this.trigger(name);
      };
    }
  );

  // Support: Android <=4.0 only
  // Make sure we trim BOM and NBSP
  var rtrim = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g;

  // Bind a function to a context, optionally partially applying any
  // arguments.
  // jQuery.proxy is deprecated to promote standards (specifically Function#bind)
  // However, it is not slated for removal any time soon
  jQuery.proxy = function (fn, context) {
    var tmp, args, proxy;

    if (typeof context === 'string') {
      tmp = fn[context];
      context = fn;
      fn = tmp;
    }

    // Quick check to determine if target is callable, in the spec
    // this throws a TypeError, but we will just return undefined.
    if (!isFunction(fn)) {
      return undefined;
    }

    // Simulated bind
    args = slice.call(arguments, 2);
    proxy = function () {
      return fn.apply(context || this, args.concat(slice.call(arguments)));
    };

    // Set the guid of unique handler to the same of original handler, so it can be removed
    proxy.guid = fn.guid = fn.guid || jQuery.guid++;

    return proxy;
  };

  jQuery.holdReady = function (hold) {
    if (hold) {
      jQuery.readyWait++;
    } else {
      jQuery.ready(true);
    }
  };
  jQuery.isArray = Array.isArray;
  jQuery.parseJSON = JSON.parse;
  jQuery.nodeName = nodeName;
  jQuery.isFunction = isFunction;
  jQuery.isWindow = isWindow;
  jQuery.camelCase = camelCase;
  jQuery.type = toType;

  jQuery.now = Date.now;

  jQuery.isNumeric = function (obj) {
    // As of jQuery 3.0, isNumeric is limited to
    // strings and numbers (primitives or objects)
    // that can be coerced to finite numbers (gh-2662)
    var type = jQuery.type(obj);
    return (
      (type === 'number' || type === 'string') &&
      // parseFloat NaNs numeric-cast false positives ("")
      // ...but misinterprets leading-number strings, particularly hex literals ("0x...")
      // subtraction forces infinities to NaN
      !isNaN(obj - parseFloat(obj))
    );
  };

  jQuery.trim = function (text) {
    return text == null ? '' : (text + '').replace(rtrim, '');
  };

  // Register as a named AMD module, since jQuery can be concatenated with other
  // files that may use define, but not via a proper concatenation script that
  // understands anonymous AMD modules. A named AMD is safest and most robust
  // way to register. Lowercase jquery is used because AMD module names are
  // derived from file names, and jQuery is normally delivered in a lowercase
  // file name. Do this after creating the global so that if an AMD module wants
  // to call noConflict to hide this version of jQuery, it will work.

  // Note that for maximum portability, libraries that are not jQuery should
  // declare themselves as anonymous modules, and avoid setting a global if an
  // AMD loader is present. jQuery is a special case. For more information, see
  // https://github.com/jrburke/requirejs/wiki/Updating-existing-libraries#wiki-anon

  if (typeof define === 'function' && define.amd) {
    define('jquery', [], function () {
      return jQuery;
    });
  }

  var // Map over jQuery in case of overwrite
    _jQuery = window.jQuery,
    // Map over the $ in case of overwrite
    _$ = window.$;

  jQuery.noConflict = function (deep) {
    if (window.$ === jQuery) {
      window.$ = _$;
    }

    if (deep && window.jQuery === jQuery) {
      window.jQuery = _jQuery;
    }

    return jQuery;
  };

  // Expose jQuery and $ identifiers, even in AMD
  // (#7102#comment:10, https://github.com/jquery/jquery/pull/557)
  // and CommonJS for browser emulators (#13566)
  if (typeof noGlobal === 'undefined') {
    window.jQuery = window.$ = jQuery;
  }

  return jQuery;
});

/*!
 * surveyjs - Survey JavaScript library v1.8.25
 * Copyright (c) 2015-2020 Devsoft Baltic OÜ  - http://surveyjs.io/
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */
!(function (e, t) {
  'object' == typeof exports && 'object' == typeof module
    ? (module.exports = t())
    : 'function' == typeof define && define.amd
    ? define('Survey', [], t)
    : 'object' == typeof exports
    ? (exports.Survey = t())
    : (e.Survey = t());
})(this, function () {
  return (function (e) {
    function t(r) {
      if (n[r]) return n[r].exports;
      var i = (n[r] = { i: r, l: !1, exports: {} });
      return e[r].call(i.exports, i, i.exports, t), (i.l = !0), i.exports;
    }
    var n = {};
    return (
      (t.m = e),
      (t.c = n),
      (t.i = function (e) {
        return e;
      }),
      (t.d = function (e, n, r) {
        t.o(e, n) || Object.defineProperty(e, n, { configurable: !1, enumerable: !0, get: r });
      }),
      (t.n = function (e) {
        var n =
          e && e.__esModule
            ? function () {
                return e.default;
              }
            : function () {
                return e;
              };
        return t.d(n, 'a', n), n;
      }),
      (t.o = function (e, t) {
        return Object.prototype.hasOwnProperty.call(e, t);
      }),
      (t.p = ''),
      t((t.s = 197))
    );
  })([
    function (e, t, n) {
      'use strict';
      function r(e, t) {
        function n() {
          this.constructor = e;
        }
        for (var r in t) t.hasOwnProperty(r) && (e[r] = t[r]);
        e.prototype = null === t ? Object.create(t) : ((n.prototype = t.prototype), new n());
      }
      n.d(t, 'a', function () {
        return i;
      }),
        (t.b = r),
        n.d(t, 'c', function () {
          return o;
        }),
        n.d(t, 'd', function () {
          return a;
        });
      var i =
          Object.assign ||
          function (e) {
            for (var t, n = 1, r = arguments.length; n < r; n++) {
              t = arguments[n];
              for (var i in t) Object.prototype.hasOwnProperty.call(t, i) && (e[i] = t[i]);
            }
            return e;
          },
        o = function (e, t, n, r) {
          var i,
            o = arguments.length,
            a = o < 3 ? t : null === r ? (r = Object.getOwnPropertyDescriptor(t, n)) : r;
          if ('object' == typeof Reflect && 'function' == typeof Reflect.decorate)
            a = Reflect.decorate(e, t, n, r);
          else
            for (var s = e.length - 1; s >= 0; s--)
              (i = e[s]) && (a = (o < 3 ? i(a) : o > 3 ? i(t, n, a) : i(t, n)) || a);
          return o > 3 && a && Object.defineProperty(t, n, a), a;
        },
        a = function () {
          for (var e = 0, t = 0, n = arguments.length; t < n; t++) e += arguments[t].length;
          for (var r = Array(e), i = 0, t = 0; t < n; t++)
            for (var o = arguments[t], a = 0, s = o.length; a < s; a++, i++) r[i] = o[a];
          return r;
        };
    },
    function (e, t, n) {
      'use strict';
      function r(e, t, n) {
        var r = e.getLocalizableString(n);
        r ||
          ((r = e.createLocalizableString(n, e, !0)),
          'object' == typeof t.localizable &&
            'function' == typeof t.localizable.onGetTextCallback &&
            (r.onGetTextCallback = t.localizable.onGetTextCallback));
      }
      function i(e) {
        return function (t, n) {
          e && e.localizable
            ? (Object.defineProperty(t, n, {
                get: function () {
                  return (
                    r(this, e, n),
                    this.getLocalizableStringText(n) || e.defaultValue || this[e.defaultSource]
                  );
                },
                set: function (t) {
                  r(this, e, n), this.setLocalizableStringText(n, t);
                },
              }),
              Object.defineProperty(
                t,
                !0 === e.localizable
                  ? 'loc' + n.charAt(0).toUpperCase() + n.slice(1)
                  : e.localizable.name,
                {
                  get: function () {
                    return r(this, e, n), this.getLocalizableString(n);
                  },
                }
              ))
            : Object.defineProperty(t, n, {
                get: function () {
                  return (
                    this.getPropertyValue(n) ||
                    (e ? e.defaultValue || this[e.defaultSource] : void 0)
                  );
                },
                set: function (e) {
                  this.setPropertyValue(n, e);
                },
              });
        };
      }
      var o = n(0),
        a = n(4);
      (t.e = i),
        n.d(t, 'd', function () {
          return s;
        }),
        n.d(t, 'a', function () {
          return l;
        }),
        n.d(t, 'i', function () {
          return u;
        }),
        n.d(t, 'h', function () {
          return c;
        }),
        n.d(t, 'f', function () {
          return p;
        }),
        n.d(t, 'm', function () {
          return d;
        }),
        n.d(t, 'k', function () {
          return h;
        }),
        n.d(t, 'j', function () {
          return f;
        }),
        n.d(t, 'g', function () {
          return m;
        }),
        n.d(t, 'l', function () {
          return g;
        }),
        n.d(t, 'c', function () {
          return y;
        }),
        n.d(t, 'b', function () {
          return v;
        });
      var s = (function () {
          function e(t, n, r) {
            void 0 === r && (r = !1),
              (this.name = n),
              (this.typeValue = null),
              (this.choicesValue = null),
              (this.baseValue = null),
              (this.isRequiredValue = !1),
              (this.isUniqueValue = !1),
              (this.readOnlyValue = null),
              (this.visibleValue = null),
              (this.isLocalizableValue = null),
              (this.choicesfunc = null),
              (this.dependedProperties = null),
              (this.isSerializable = !0),
              (this.isLightSerializable = !0),
              (this.isCustom = !1),
              (this.isDynamicChoices = !1),
              (this.isBindable = !1),
              (this.className = null),
              (this.alternativeName = null),
              (this.classNamePart = null),
              (this.baseClassName = null),
              (this.defaultValueValue = null),
              (this.serializationProperty = null),
              (this.displayName = null),
              (this.category = ''),
              (this.categoryIndex = -1),
              (this.visibleIndex = -1),
              (this.nextToProperty = null),
              (this.showMode = null),
              (this.maxLength = -1),
              (this.layout = null),
              (this.onGetValue = null),
              (this.onSetValue = null),
              (this.visibleIf = null),
              (this.isArray = !1),
              (this.classInfoValue = t),
              (this.isRequiredValue = r),
              (this.idValue = e.Index++);
          }
          return (
            Object.defineProperty(e.prototype, 'id', {
              get: function () {
                return this.idValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'classInfo', {
              get: function () {
                return this.classInfoValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'type', {
              get: function () {
                return this.typeValue ? this.typeValue : 'string';
              },
              set: function (e) {
                'itemvalues' === e && (e = 'itemvalue[]'),
                  (this.typeValue = e),
                  this.typeValue.indexOf('[]') === this.typeValue.length - 2 &&
                    ((this.isArray = !0),
                    (this.className = this.typeValue.substr(0, this.typeValue.length - 2)));
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'isRequired', {
              get: function () {
                return this.isRequiredValue;
              },
              set: function (e) {
                this.isRequiredValue = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'isUnique', {
              get: function () {
                return this.isUniqueValue;
              },
              set: function (e) {
                this.isUniqueValue = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'hasToUseGetValue', {
              get: function () {
                return this.onGetValue || this.serializationProperty;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'defaultValue', {
              get: function () {
                var t = this.defaultValueValue;
                return (
                  e.getItemValuesDefaultValue &&
                    y.metaData.isDescendantOf(this.className, 'itemvalue') &&
                    (t = e.getItemValuesDefaultValue(this.defaultValueValue || [])),
                  t
                );
              },
              set: function (e) {
                this.defaultValueValue = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.isDefaultValue = function (e) {
              return a.a.isValueEmpty(this.defaultValue)
                ? (!1 === e && ('boolean' == this.type || 'switch' == this.type)) ||
                    '' === e ||
                    a.a.isValueEmpty(e)
                : this.defaultValue == e;
            }),
            (e.prototype.getValue = function (e) {
              return this.onGetValue
                ? this.onGetValue(e)
                : this.serializationProperty && e[this.serializationProperty]
                ? e[this.serializationProperty].getJson()
                : e[this.name];
            }),
            (e.prototype.getPropertyValue = function (e) {
              return this.isLocalizable
                ? e[this.serializationProperty]
                  ? e[this.serializationProperty].text
                  : null
                : this.getValue(e);
            }),
            Object.defineProperty(e.prototype, 'hasToUseSetValue', {
              get: function () {
                return this.onSetValue || this.serializationProperty;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.setValue = function (e, t, n) {
              this.onSetValue
                ? this.onSetValue(e, t, n)
                : this.serializationProperty && e[this.serializationProperty]
                ? e[this.serializationProperty].setJson(t)
                : (t &&
                    'string' == typeof t &&
                    ('number' == this.type && (t = parseInt(t)),
                    ('boolean' != this.type && 'switch' != this.type) ||
                      (t = 'true' === t.toLowerCase())),
                  (e[this.name] = t));
            }),
            (e.prototype.getObjType = function (e) {
              return this.classNamePart ? e.replace(this.classNamePart, '') : e;
            }),
            (e.prototype.getClassName = function (e) {
              return (
                e && (e = e.toLowerCase()),
                this.classNamePart && e.indexOf(this.classNamePart) < 0 ? e + this.classNamePart : e
              );
            }),
            Object.defineProperty(e.prototype, 'choices', {
              get: function () {
                return this.getChoices(null);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'hasChoices', {
              get: function () {
                return !!this.choicesValue || !!this.choicesfunc;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getChoices = function (e, t) {
              return (
                void 0 === t && (t = null),
                null != this.choicesValue
                  ? this.choicesValue
                  : null != this.choicesfunc
                  ? this.choicesfunc(e, t)
                  : null
              );
            }),
            (e.prototype.setChoices = function (e, t) {
              void 0 === t && (t = null), (this.choicesValue = e), (this.choicesfunc = t);
            }),
            (e.prototype.getBaseValue = function () {
              return this.baseValue
                ? 'function' == typeof this.baseValue
                  ? this.baseValue()
                  : this.baseValue
                : '';
            }),
            (e.prototype.setBaseValue = function (e) {
              this.baseValue = e;
            }),
            Object.defineProperty(e.prototype, 'readOnly', {
              get: function () {
                return null != this.readOnlyValue && this.readOnlyValue;
              },
              set: function (e) {
                this.readOnlyValue = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.isVisible = function (e, t) {
              void 0 === t && (t = null);
              var n = !this.layout || this.layout == e;
              return !(!this.visible || !n) && (!this.visibleIf || !t || this.visibleIf(t));
            }),
            Object.defineProperty(e.prototype, 'visible', {
              get: function () {
                return null == this.visibleValue || this.visibleValue;
              },
              set: function (e) {
                this.visibleValue = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'isLocalizable', {
              get: function () {
                return null != this.isLocalizableValue && this.isLocalizableValue;
              },
              set: function (e) {
                this.isLocalizableValue = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'dataList', {
              get: function () {
                return Array.isArray(this.dataListValue) ? this.dataListValue : [];
              },
              set: function (e) {
                this.dataListValue = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.mergeWith = function (t) {
              for (var n = e.mergableValues, r = 0; r < n.length; r++) this.mergeValue(t, n[r]);
            }),
            (e.prototype.addDependedProperty = function (e) {
              this.dependedProperties || (this.dependedProperties = []),
                this.dependedProperties.indexOf(e) < 0 && this.dependedProperties.push(e);
            }),
            (e.prototype.getDependedProperties = function () {
              return this.dependedProperties ? this.dependedProperties : [];
            }),
            (e.prototype.schemaType = function () {
              return this.className
                ? 'array'
                : this.baseClassName
                ? 'array'
                : 'boolean' == this.type || 'number' == this.type
                ? this.type
                : 'string';
            }),
            (e.prototype.mergeValue = function (e, t) {
              null == this[t] && null != e[t] && (this[t] = e[t]);
            }),
            (e.Index = 1),
            (e.mergableValues = [
              'typeValue',
              'choicesValue',
              'baseValue',
              'readOnlyValue',
              'visibleValue',
              'isSerializable',
              'isLightSerializable',
              'isCustom',
              'isBindable',
              'isUnique',
              'isDynamicChoices',
              'isLocalizableValue',
              'className',
              'alternativeName',
              'layout',
              'classNamePart',
              'baseClassName',
              'defaultValue',
              'serializationProperty',
              'onGetValue',
              'onSetValue',
              'displayName',
              'category',
              'categoryIndex',
              'visibleIndex',
              'nextToProperty',
              'showMode',
              'dependedProperties',
              'visibleIf',
              'onPropertyEditorUpdate',
              'maxLength',
              'maxValue',
              'minValue',
              'dataListValue',
            ]),
            e
          );
        })(),
        l = (function () {
          function e() {}
          return (
            (e.addProperty = function (t, n) {
              t = t.toLowerCase();
              var r = e.properties;
              r[t] || (r[t] = []), r[t].push(n);
            }),
            (e.removeProperty = function (t, n) {
              t = t.toLowerCase();
              var r = e.properties;
              if (r[t])
                for (var i = r[t], o = 0; o < i.length; o++)
                  if (i[o].name == n) {
                    r[t].splice(o, 1);
                    break;
                  }
            }),
            (e.addClass = function (t, n) {
              (t = t.toLowerCase()), n && (n = n.toLowerCase()), (e.parentClasses[t] = n);
            }),
            (e.getProperties = function (t) {
              t = t.toLowerCase();
              for (var n = [], r = e.properties; t; ) {
                var i = r[t];
                if (i) for (var o = 0; o < i.length; o++) n.push(i[o]);
                t = e.parentClasses[t];
              }
              return n;
            }),
            (e.createProperties = function (t) {
              t && t.getType && e.createPropertiesCore(t, t.getType());
            }),
            (e.createPropertiesCore = function (t, n) {
              var r = e.properties;
              r[n] && e.createPropertiesInObj(t, r[n]);
              var i = e.parentClasses[n];
              i && e.createPropertiesCore(t, i);
            }),
            (e.createPropertiesInObj = function (t, n) {
              for (var r = 0; r < n.length; r++) e.createPropertyInObj(t, n[r]);
            }),
            (e.createPropertyInObj = function (e, t) {
              if (!e[t.name] && !e.hasOwnProperty(t.name))
                if (
                  t.isLocalizable &&
                  t.serializationProperty &&
                  !e[t.serializationProperty] &&
                  e.createCustomLocalizableObj
                ) {
                  e.createCustomLocalizableObj(t.name);
                  var n = {
                    get: function () {
                      return e.getLocalizableString(t.name);
                    },
                  };
                  Object.defineProperty(e, t.serializationProperty, n);
                  var r = {
                    get: function () {
                      return e.getLocalizableStringText(t.name, t.defaultValue);
                    },
                    set: function (n) {
                      e.setLocalizableStringText(t.name, n);
                    },
                  };
                  Object.defineProperty(e, t.name, r);
                } else {
                  var i = t.defaultValue,
                    o = !1;
                  if (
                    ('function' == typeof e.createNewArray &&
                      (y.metaData.isDescendantOf(t.className, 'itemvalue') &&
                        (e.createNewArray(t.name, function (n) {
                          (n.locOwner = e), (n.ownerPropertyName = t.name);
                        }),
                        (o = !0)),
                      'multiplevalues' === t.type && (e.createNewArray(t.name), (o = !0)),
                      o && (Array.isArray(i) && e.setPropertyValue(t.name, i), (i = null))),
                    e.getPropertyValue && e.setPropertyValue)
                  ) {
                    var r = {
                      get: function () {
                        return t.onGetValue ? t.onGetValue(e) : e.getPropertyValue(t.name, i);
                      },
                      set: function (n) {
                        t.onSetValue ? t.onSetValue(e, n, null) : e.setPropertyValue(t.name, n);
                      },
                    };
                    Object.defineProperty(e, t.name, r);
                  }
                }
            }),
            (e.properties = {}),
            (e.parentClasses = {}),
            e
          );
        })(),
        u = (function () {
          function e(e, t, n, r) {
            void 0 === n && (n = null),
              void 0 === r && (r = null),
              (this.name = e),
              (this.creator = n),
              (this.parentName = r),
              (this.properties = null),
              (e = e.toLowerCase()),
              this.parentName &&
                ((this.parentName = this.parentName.toLowerCase()), l.addClass(e, this.parentName)),
              (this.properties = new Array());
            for (var i = 0; i < t.length; i++) {
              var o = this.createProperty(t[i]);
              o && this.properties.push(o);
            }
          }
          return (
            (e.prototype.find = function (e) {
              for (var t = 0; t < this.properties.length; t++)
                if (this.properties[t].name == e) return this.properties[t];
              return null;
            }),
            (e.prototype.createProperty = function (t) {
              var n = 'string' == typeof t ? t : t.name;
              if (n) {
                var r = null,
                  i = n.indexOf(e.typeSymbol);
                i > -1 && ((r = n.substring(i + 1)), (n = n.substring(0, i)));
                var o = this.getIsPropertyNameRequired(n) || !!t.isRequired;
                n = this.getPropertyName(n);
                var l = new s(this, n, o);
                if ((r && (l.type = r), 'object' == typeof t)) {
                  if (
                    (t.type && (l.type = t.type),
                    a.a.isValueEmpty(t.default) || (l.defaultValue = t.default),
                    a.a.isValueEmpty(t.isSerializable) || (l.isSerializable = t.isSerializable),
                    a.a.isValueEmpty(t.isLightSerializable) ||
                      (l.isLightSerializable = t.isLightSerializable),
                    a.a.isValueEmpty(t.maxLength) || (l.maxLength = t.maxLength),
                    a.a.isValueEmpty(t.displayName) || (l.displayName = t.displayName),
                    a.a.isValueEmpty(t.category) || (l.category = t.category),
                    a.a.isValueEmpty(t.categoryIndex) || (l.categoryIndex = t.categoryIndex),
                    a.a.isValueEmpty(t.nextToProperty) || (l.nextToProperty = t.nextToProperty),
                    a.a.isValueEmpty(t.visibleIndex) || (l.visibleIndex = t.visibleIndex),
                    a.a.isValueEmpty(t.showMode) || (l.showMode = t.showMode),
                    a.a.isValueEmpty(t.maxValue) || (l.maxValue = t.maxValue),
                    a.a.isValueEmpty(t.minValue) || (l.minValue = t.minValue),
                    a.a.isValueEmpty(t.dataList) || (l.dataList = t.dataList),
                    a.a.isValueEmpty(t.isDynamicChoices) ||
                      (l.isDynamicChoices = t.isDynamicChoices),
                    a.a.isValueEmpty(t.isBindable) || (l.isBindable = t.isBindable),
                    a.a.isValueEmpty(t.isUnique) || (l.isUnique = t.isUnique),
                    (!0 !== t.visible && !1 !== t.visible) || (l.visible = t.visible),
                    t.visibleIf && (l.visibleIf = t.visibleIf),
                    t.onPropertyEditorUpdate &&
                      (l.onPropertyEditorUpdate = t.onPropertyEditorUpdate),
                    !0 === t.readOnly && (l.readOnly = !0),
                    t.choices)
                  ) {
                    var u = 'function' == typeof t.choices ? t.choices : null,
                      c = 'function' != typeof t.choices ? t.choices : null;
                    l.setChoices(c, u);
                  }
                  if (
                    (t.baseValue && l.setBaseValue(t.baseValue),
                    t.onGetValue && (l.onGetValue = t.onGetValue),
                    t.onSetValue && (l.onSetValue = t.onSetValue),
                    t.isLocalizable && (t.serializationProperty = 'loc' + l.name),
                    t.serializationProperty)
                  ) {
                    l.serializationProperty = t.serializationProperty;
                    l.serializationProperty &&
                      0 == l.serializationProperty.indexOf('loc') &&
                      (l.isLocalizable = !0);
                  }
                  t.isLocalizable && (l.isLocalizable = t.isLocalizable),
                    t.className && (l.className = t.className),
                    t.baseClassName && (l.baseClassName = t.baseClassName),
                    t.classNamePart && (l.classNamePart = t.classNamePart),
                    t.alternativeName && (l.alternativeName = t.alternativeName),
                    t.layout && (l.layout = t.layout),
                    t.dependsOn && this.addDependsOnProperties(l, t.dependsOn);
                }
                return l;
              }
            }),
            (e.prototype.addDependsOnProperties = function (e, t) {
              if (Array.isArray(t))
                for (var n = 0; n < t.length; n++) this.addDependsOnProperty(e, t[n]);
              else this.addDependsOnProperty(e, t);
            }),
            (e.prototype.addDependsOnProperty = function (e, t) {
              var n = this.find(t);
              n || (n = v.findProperty(this.parentName, t)), n && n.addDependedProperty(e.name);
            }),
            (e.prototype.getIsPropertyNameRequired = function (t) {
              return t.length > 0 && t[0] == e.requiredSymbol;
            }),
            (e.prototype.getPropertyName = function (e) {
              return this.getIsPropertyNameRequired(e) ? (e = e.slice(1)) : e;
            }),
            (e.requiredSymbol = '!'),
            (e.typeSymbol = ':'),
            e
          );
        })(),
        c = (function () {
          function e() {
            (this.classes = {}),
              (this.alternativeNames = {}),
              (this.childrenClasses = {}),
              (this.classProperties = {}),
              (this.classHashProperties = {});
          }
          return (
            (e.prototype.addClass = function (e, t, n, r) {
              void 0 === n && (n = null), void 0 === r && (r = null), (e = e.toLowerCase());
              var i = new u(e, t, n, r);
              if (((this.classes[e] = i), r)) {
                r = r.toLowerCase();
                this.childrenClasses[r] || (this.childrenClasses[r] = []),
                  this.childrenClasses[r].push(i);
              }
              return i;
            }),
            (e.prototype.removeClass = function (e) {
              var t = this.findClass(e);
              if (t && (delete this.classes[t.name], t.parentName)) {
                var n = this.childrenClasses[t.parentName].indexOf(t);
                n > -1 && this.childrenClasses[t.parentName].splice(n, 1);
              }
            }),
            (e.prototype.overrideClassCreatore = function (e, t) {
              this.overrideClassCreator(e, t);
            }),
            (e.prototype.overrideClassCreator = function (e, t) {
              e = e.toLowerCase();
              var n = this.findClass(e);
              n && (n.creator = t);
            }),
            (e.prototype.getProperties = function (e) {
              var t = this.findClass(e);
              if (!t) return [];
              var n = this.classProperties[t.name];
              return n || (this.fillPropertiesForClass(t.name), this.classProperties[t.name]);
            }),
            (e.prototype.getHashProperties = function (e) {
              var t = this.findClass(e);
              if (!t) return {};
              var n = this.classHashProperties[t.name];
              return n || (this.fillPropertiesForClass(t.name), this.classHashProperties[t.name]);
            }),
            (e.prototype.fillPropertiesForClass = function (e) {
              var t = new Array(),
                n = {};
              this.fillProperties(e, t, n),
                (this.classProperties[e] = t),
                (this.classHashProperties[e] = n);
            }),
            (e.prototype.getPropertiesByObj = function (e) {
              if (!e || !e.getType) return [];
              for (var t = {}, n = this.getProperties(e.getType()), r = 0; r < n.length; r++)
                t[n[r].name] = n[r];
              var i = e.getDynamicType ? this.getProperties(e.getDynamicType()) : null;
              if (i && i.length > 0)
                for (var r = 0; r < i.length; r++) {
                  var o = i[r];
                  t[o.name] || (t[o.name] = o);
                }
              return Object.keys(t).map(function (e) {
                return t[e];
              });
            }),
            (e.prototype.getDynamicPropertiesByObj = function (e, t) {
              if ((void 0 === t && (t = null), !e || !e.getType || (!e.getDynamicType && !t)))
                return [];
              var n = t || e.getDynamicType();
              if (!n) return [];
              var r = this.getProperties(n);
              if (!r || 0 == r.length) return [];
              for (var i = {}, o = this.getProperties(e.getType()), a = 0; a < o.length; a++)
                i[o[a].name] = o[a];
              for (var s = [], a = 0; a < r.length; a++) {
                var l = r[a];
                i[l.name] || s.push(l);
              }
              return s;
            }),
            (e.prototype.findProperty = function (e, t) {
              var n = this.getHashProperties(e),
                r = n[t];
              return r || null;
            }),
            (e.prototype.findProperties = function (e, t) {
              for (var n = [], r = this.getHashProperties(e), i = 0; i < t.length; i++) {
                var o = r[t[i]];
                o && n.push(o);
              }
              return n;
            }),
            (e.prototype.getAllPropertiesByName = function (e) {
              for (var t = new Array(), n = this.getAllClasses(), r = 0; r < n.length; r++)
                for (var i = this.findClass(n[r]), o = 0; o < i.properties.length; o++)
                  if (i.properties[o].name == e) {
                    t.push(i.properties[o]);
                    break;
                  }
              return t;
            }),
            (e.prototype.getAllClasses = function () {
              var e = new Array();
              for (var t in this.classes) e.push(t);
              return e;
            }),
            (e.prototype.createClass = function (e, t) {
              void 0 === t && (t = void 0), (e = e.toLowerCase());
              var n = this.findClass(e);
              if (!n) return null;
              if (n.creator) return n.creator(t);
              for (var r = n.parentName; r; ) {
                if (!(n = this.findClass(r))) return null;
                if (((r = n.parentName), n.creator)) return this.createCustomType(e, n.creator, t);
              }
              return null;
            }),
            (e.prototype.createCustomType = function (e, t, n) {
              void 0 === n && (n = void 0), (e = e.toLowerCase());
              var r = t(n),
                i = e,
                o = r.getTemplate ? r.getTemplate() : r.getType();
              return (
                (r.getType = function () {
                  return i;
                }),
                (r.getTemplate = function () {
                  return o;
                }),
                l.createProperties(r),
                r
              );
            }),
            (e.prototype.getChildrenClasses = function (e, t) {
              void 0 === t && (t = !1), (e = e.toLowerCase());
              var n = [];
              return this.fillChildrenClasses(e, t, n), n;
            }),
            (e.prototype.getRequiredProperties = function (e) {
              for (var t = this.getProperties(e), n = [], r = 0; r < t.length; r++)
                t[r].isRequired && n.push(t[r].name);
              return n;
            }),
            (e.prototype.addProperties = function (e, t) {
              e = e.toLowerCase();
              for (var n = this.findClass(e), r = 0; r < t.length; r++)
                this.addCustomPropertyCore(n, t[r]);
            }),
            (e.prototype.addProperty = function (e, t) {
              return this.addCustomPropertyCore(this.findClass(e), t);
            }),
            (e.prototype.addCustomPropertyCore = function (e, t) {
              if (!e) return null;
              var n = e.createProperty(t);
              return (
                n &&
                  ((n.isCustom = !0),
                  this.addPropertyToClass(e, n),
                  this.emptyClassPropertiesHash(e),
                  l.addProperty(e.name, n)),
                n
              );
            }),
            (e.prototype.removeProperty = function (e, t) {
              var n = this.findClass(e);
              if (!n) return !1;
              var r = n.find(t);
              r &&
                (this.removePropertyFromClass(n, r),
                this.emptyClassPropertiesHash(n),
                l.removeProperty(n.name, t));
            }),
            (e.prototype.addPropertyToClass = function (e, t) {
              null == e.find(t.name) && e.properties.push(t);
            }),
            (e.prototype.removePropertyFromClass = function (e, t) {
              var n = e.properties.indexOf(t);
              n < 0 || e.properties.splice(n, 1);
            }),
            (e.prototype.emptyClassPropertiesHash = function (e) {
              (this.classProperties[e.name] = null), (this.classHashProperties[e.name] = null);
              for (var t = this.getChildrenClasses(e.name), n = 0; n < t.length; n++)
                (this.classProperties[t[n].name] = null),
                  (this.classHashProperties[t[n].name] = null);
            }),
            (e.prototype.fillChildrenClasses = function (e, t, n) {
              var r = this.childrenClasses[e];
              if (r)
                for (var i = 0; i < r.length; i++)
                  (t && !r[i].creator) || n.push(r[i]), this.fillChildrenClasses(r[i].name, t, n);
            }),
            (e.prototype.findClass = function (e) {
              e = e.toLowerCase();
              var t = this.classes[e];
              if (!t) {
                var n = this.alternativeNames[e];
                if (n && n != e) return this.findClass(n);
              }
              return t;
            }),
            (e.prototype.isDescendantOf = function (e, t) {
              if (!e || !t) return !1;
              (e = e.toLowerCase()), (t = t.toLowerCase());
              var n = this.findClass(e);
              if (!n) return !1;
              var r = n;
              do {
                if (r.name === t) return !0;
                r = this.classes[r.parentName];
              } while (r);
              return !1;
            }),
            (e.prototype.addAlterNativeClassName = function (e, t) {
              this.alternativeNames[t.toLowerCase()] = e.toLowerCase();
            }),
            (e.prototype.generateSchema = function (e) {
              void 0 === e && (e = void 0), e || (e = 'survey');
              var t = this.findClass(e);
              if (!t) return null;
              var n = {
                $schema: 'http://json-schema.org/draft-07/schema#',
                title: 'SurveyJS Library json schema',
                type: 'object',
                properties: {},
                definitions: {},
              };
              return this.generateSchemaProperties(t, n.properties, n.definitions), n;
            }),
            (e.prototype.generateSchemaProperties = function (e, t, n) {
              if (e)
                for (var r = 0; r < e.properties.length; r++) {
                  var i = e.properties[r];
                  t[i.name] = this.generateSchemaProperty(i, n);
                }
            }),
            (e.prototype.generateSchemaProperty = function (e, t) {
              var n = { type: e.schemaType() };
              if (
                (e.hasChoices && (n.enum = e.getChoices(null)),
                e.className &&
                  ((n.items = { $ref: '#' + e.className }),
                  this.generateChemaClass(e.className, t)),
                e.baseClassName)
              ) {
                var r = this.getChildrenClasses(e.baseClassName, !0);
                'question' == e.baseClassName && r.push(this.findClass('panel')), (n.items = []);
                for (var i = 0; i < r.length; i++) {
                  var o = r[i].name;
                  n.items.push({ $ref: '#' + o }), this.generateChemaClass(o, t);
                }
              }
              return n;
            }),
            (e.prototype.generateChemaClass = function (e, t) {
              if (!t[e]) {
                var n = this.findClass(e);
                if (n) {
                  var r = !!n.parentName && 'base' != n.parentName;
                  r && this.generateChemaClass(n.parentName, t);
                  var i = { type: 'object', $id: '#' + e };
                  t[e] = i;
                  var o = {};
                  this.generateSchemaProperties(n, o, t),
                    r
                      ? (i.allOff = [{ $ref: '#' + n.parentName }, { properties: o }])
                      : (i.properties = o);
                }
              }
            }),
            (e.prototype.fillProperties = function (e, t, n) {
              var r = this.findClass(e);
              if (r) {
                r.parentName && this.fillProperties(r.parentName, t, n);
                for (var i = 0; i < r.properties.length; i++) {
                  var o = r.properties[i];
                  this.addPropertyCore(o, t, n), (n[o.name] = o);
                }
              }
            }),
            (e.prototype.addPropertyCore = function (e, t, n) {
              if (!n[e.name]) return void t.push(e);
              for (var r = -1, i = 0; i < t.length; i++)
                if (t[i].name == e.name) {
                  r = i;
                  break;
                }
              e.mergeWith(t[r]), (t[r] = e);
            }),
            e
          );
        })(),
        p = (function () {
          function e(e, t) {
            (this.type = e), (this.message = t), (this.description = ''), (this.at = -1);
          }
          return (
            (e.prototype.getFullDescription = function () {
              return this.message + (this.description ? '\n' + this.description : '');
            }),
            e
          );
        })(),
        d = (function (e) {
          function t(t, n) {
            var r =
              e.call(
                this,
                'unknownproperty',
                "The property '" + t + "' in class '" + n + "' is unknown."
              ) || this;
            (r.propertyName = t), (r.className = n);
            var i = y.metaData.getProperties(n);
            if (i) {
              r.description = 'The list of available properties are: ';
              for (var o = 0; o < i.length; o++)
                o > 0 && (r.description += ', '), (r.description += i[o].name);
              r.description += '.';
            }
            return r;
          }
          return n.i(o.b)(t, e), t;
        })(p),
        h = (function (e) {
          function t(t, n, r) {
            var i = e.call(this, n, r) || this;
            (i.baseClassName = t),
              (i.type = n),
              (i.message = r),
              (i.description = 'The following types are available: ');
            for (var o = y.metaData.getChildrenClasses(t, !0), a = 0; a < o.length; a++)
              a > 0 && (i.description += ', '), (i.description += "'" + o[a].name + "'");
            return (i.description += '.'), i;
          }
          return n.i(o.b)(t, e), t;
        })(p),
        f = (function (e) {
          function t(t, n) {
            var r =
              e.call(
                this,
                n,
                'missingtypeproperty',
                "The property type is missing in the object. Please take a look at property: '" +
                  t +
                  "'."
              ) || this;
            return (r.propertyName = t), (r.baseClassName = n), r;
          }
          return n.i(o.b)(t, e), t;
        })(h),
        m = (function (e) {
          function t(t, n) {
            var r =
              e.call(
                this,
                n,
                'incorrecttypeproperty',
                "The property type is incorrect in the object. Please take a look at property: '" +
                  t +
                  "'."
              ) || this;
            return (r.propertyName = t), (r.baseClassName = n), r;
          }
          return n.i(o.b)(t, e), t;
        })(h),
        g = (function (e) {
          function t(t, n) {
            var r =
              e.call(
                this,
                'requiredproperty',
                "The property '" + t + "' is required in class '" + n + "'."
              ) || this;
            return (r.propertyName = t), (r.className = n), r;
          }
          return n.i(o.b)(t, e), t;
        })(p),
        y = (function () {
          function e() {
            (this.errors = new Array()), (this.lightSerializing = !1);
          }
          return (
            Object.defineProperty(e, 'metaData', {
              get: function () {
                return e.metaDataValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.toJsonObject = function (e, t) {
              return void 0 === t && (t = !1), this.toJsonObjectCore(e, null, t);
            }),
            (e.prototype.toObject = function (e, t) {
              if ((this.toObjectCore(e, t), t.getType)) {
                var n = this.getRequiredError(e, t.getType());
                n && this.addNewError(n, e);
              }
            }),
            (e.prototype.toObjectCore = function (t, n) {
              if (t) {
                var r = null,
                  i = void 0,
                  o = !0;
                if (
                  (n.getType &&
                    ((i = n.getType()),
                    (r = e.metaData.getProperties(i)),
                    (o = !!i && !e.metaData.isDescendantOf(i, 'itemvalue'))),
                  r)
                ) {
                  n.startLoadingFromJson && n.startLoadingFromJson(),
                    (r = this.addDynamicProperties(n, t, r));
                  for (var a in t)
                    if (a !== e.typePropertyName)
                      if (a !== e.positionPropertyName) {
                        var s = this.findProperty(r, a);
                        s
                          ? this.valueToObj(t[a], n, s)
                          : o && this.addNewError(new d(a.toString(), i), t);
                      } else n[a] = t[a];
                  n.endLoadingFromJson && n.endLoadingFromJson();
                }
              }
            }),
            (e.prototype.toJsonObjectCore = function (t, n, r) {
              if ((void 0 === r && (r = !1), !t || !t.getType)) return t;
              if ('function' == typeof t.getData) return t.getData();
              var i = {};
              return (
                null == n || n.className || (i[e.typePropertyName] = n.getObjType(t.getType())),
                this.propertiesToJson(t, e.metaData.getProperties(t.getType()), i, r),
                this.propertiesToJson(t, this.getDynamicProperties(t), i, r),
                i
              );
            }),
            (e.prototype.getDynamicProperties = function (e) {
              return v.getDynamicPropertiesByObj(e);
            }),
            (e.prototype.addDynamicProperties = function (e, t, n) {
              if (!e.getDynamicPropertyName) return n;
              var r = e.getDynamicPropertyName();
              if (!r) return n;
              t[r] && (e[r] = t[r]);
              for (var i = this.getDynamicProperties(e), o = [], a = 0; a < n.length; a++)
                o.push(n[a]);
              for (var a = 0; a < i.length; a++) o.push(i[a]);
              return o;
            }),
            (e.prototype.propertiesToJson = function (e, t, n, r) {
              void 0 === r && (r = !1);
              for (var i = 0; i < t.length; i++) this.valueToJson(e, n, t[i], r);
            }),
            (e.prototype.valueToJson = function (e, t, n, r) {
              if (
                (void 0 === r && (r = !1),
                !(
                  !1 === n.isSerializable ||
                  (!1 === n.isLightSerializable && this.lightSerializing)
                ))
              ) {
                var i = n.getValue(e);
                if (r || !n.isDefaultValue(i)) {
                  if (this.isValueArray(i)) {
                    for (var o = [], a = 0; a < i.length; a++)
                      o.push(this.toJsonObjectCore(i[a], n, r));
                    i = o.length > 0 ? o : null;
                  } else i = this.toJsonObjectCore(i, n, r);
                  var s =
                    'function' == typeof e.getPropertyValue &&
                    null !== e.getPropertyValue(n.name, null);
                  ((r && s) || !n.isDefaultValue(i)) && (t[n.name] = i);
                }
              }
            }),
            (e.prototype.valueToObj = function (e, t, n) {
              if (null != e) {
                if ((this.removePos(n, e), null != n && n.hasToUseSetValue))
                  return void n.setValue(t, e, this);
                if (this.isValueArray(e)) return void this.valueToArray(e, t, n.name, n);
                var r = this.createNewObj(e, n);
                r.newObj && (this.toObjectCore(e, r.newObj), (e = r.newObj)),
                  r.error || (null != n ? n.setValue(t, e, this) : (t[n.name] = e));
              }
            }),
            (e.prototype.removePos = function (e, t) {
              !e || !e.type || e.type.indexOf('value') < 0 || this.removePosFromObj(t);
            }),
            (e.prototype.removePosFromObj = function (t) {
              if (t) {
                if (Array.isArray(t))
                  for (var n = 0; n < t.length; n++) this.removePosFromObj(t[n]);
                t[e.positionPropertyName] && delete t[e.positionPropertyName];
              }
            }),
            (e.prototype.isValueArray = function (e) {
              return e && Array.isArray(e);
            }),
            (e.prototype.createNewObj = function (t, n) {
              var r = { newObj: null, error: null },
                i = t[e.typePropertyName];
              return (
                !i && null != n && n.className && (i = n.className),
                (i = n.getClassName(i)),
                (r.newObj = i ? e.metaData.createClass(i, t) : null),
                (r.error = this.checkNewObjectOnErrors(r.newObj, t, n, i)),
                r
              );
            }),
            (e.prototype.checkNewObjectOnErrors = function (e, t, n, r) {
              var i = null;
              return (
                e
                  ? (i = this.getRequiredError(t, r))
                  : n.baseClassName &&
                    (i = r ? new m(n.name, n.baseClassName) : new f(n.name, n.baseClassName)),
                i && this.addNewError(i, t),
                i
              );
            }),
            (e.prototype.getRequiredError = function (t, n) {
              var r = e.metaData.getRequiredProperties(n);
              if (!r) return null;
              for (var i = 0; i < r.length; i++) if (!t[r[i]]) return new g(r[i], n);
              return null;
            }),
            (e.prototype.addNewError = function (t, n) {
              n && n[e.positionPropertyName] && (t.at = n[e.positionPropertyName].start),
                this.errors.push(t);
            }),
            (e.prototype.valueToArray = function (e, t, n, r) {
              t[n] && e.length > 0 && t[n].splice(0, t[n].length);
              var i = t[n] ? t[n] : [];
              this.addValuesIntoArray(e, i, r), t[n] || (t[n] = i);
            }),
            (e.prototype.addValuesIntoArray = function (e, t, n) {
              for (var r = 0; r < e.length; r++) {
                var i = this.createNewObj(e[r], n);
                i.newObj
                  ? (e[r].name && (i.newObj.name = e[r].name),
                    t.push(i.newObj),
                    this.toObjectCore(e[r], i.newObj))
                  : i.error || t.push(e[r]);
              }
            }),
            (e.prototype.findProperty = function (e, t) {
              if (!e) return null;
              for (var n = 0; n < e.length; n++) {
                var r = e[n];
                if (r.name == t || r.alternativeName == t) return r;
              }
              return null;
            }),
            (e.typePropertyName = 'type'),
            (e.positionPropertyName = 'pos'),
            (e.metaDataValue = new c()),
            e
          );
        })(),
        v = y.metaData;
    },
    function (e, t, n) {
      'use strict';
      var r = n(49);
      n.d(t, 'a', function () {
        return i;
      }),
        n.d(t, 'b', function () {
          return o;
        });
      var i = {
          currentLocaleValue: '',
          defaultLocaleValue: 'en',
          locales: {},
          localeNames: {},
          supportedLocales: [],
          get currentLocale() {
            return this.currentLocaleValue === this.defaultLocaleValue
              ? ''
              : this.currentLocaleValue;
          },
          set currentLocale(e) {
            'cz' === e && (e = 'cs'), (this.currentLocaleValue = e);
          },
          get defaultLocale() {
            return this.defaultLocaleValue;
          },
          set defaultLocale(e) {
            'cz' === e && (e = 'cs'), (this.defaultLocaleValue = e);
          },
          getLocaleStrings: function (e) {
            return this.locales[e];
          },
          getCurrentStrings: function () {
            var e = this.currentLocale
              ? this.locales[this.currentLocale]
              : this.locales[this.defaultLocale];
            return e || (e = this.locales[this.defaultLocale]), e;
          },
          getString: function (e) {
            var t = this.getCurrentStrings();
            t[e] || (t = this.locales[this.defaultLocale]);
            var n = t[e];
            return void 0 === n && (n = this.locales.en[e]), n;
          },
          getLocales: function (e) {
            void 0 === e && (e = !1);
            var t = [];
            t.push('');
            var n = this.locales;
            if (this.supportedLocales && this.supportedLocales.length > 0) {
              n = {};
              for (var r = 0; r < this.supportedLocales.length; r++)
                n[this.supportedLocales[r]] = !0;
            }
            for (var i in n) (e && i == this.defaultLocale) || t.push(i);
            return t.sort(), t;
          },
        },
        o = r.a;
      (i.locales.en = r.a), (i.localeNames.en = 'english');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = n(1);
      n.d(t, 'b', function () {
        return o;
      }),
        n.d(t, 'a', function () {
          return a;
        });
      var o = (function () {
          function e() {
            this.creatorHash = {};
          }
          return (
            Object.defineProperty(e, 'DefaultChoices', {
              get: function () {
                return [
                  r.a.getString('choices_Item') + '1',
                  r.a.getString('choices_Item') + '2',
                  r.a.getString('choices_Item') + '3',
                ];
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e, 'DefaultColums', {
              get: function () {
                var e = r.a.getString('matrix_column') + ' ';
                return [e + '1', e + '2', e + '3'];
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e, 'DefaultRows', {
              get: function () {
                var e = r.a.getString('matrix_row') + ' ';
                return [e + '1', e + '2'];
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.registerQuestion = function (e, t) {
              this.creatorHash[e] = t;
            }),
            (e.prototype.unregisterElement = function (e) {
              delete this.creatorHash[e];
            }),
            (e.prototype.clear = function () {
              this.creatorHash = {};
            }),
            (e.prototype.getAllTypes = function () {
              var e = new Array();
              for (var t in this.creatorHash) e.push(t);
              return e.sort();
            }),
            (e.prototype.createQuestion = function (e, t) {
              var n = this.creatorHash[e];
              return null == n ? null : n(t);
            }),
            (e.Instance = new e()),
            e
          );
        })(),
        a = (function () {
          function e() {
            this.creatorHash = {};
          }
          return (
            (e.prototype.registerElement = function (e, t) {
              this.creatorHash[e] = t;
            }),
            (e.prototype.clear = function () {
              this.creatorHash = {};
            }),
            (e.prototype.unregisterElement = function (e, t) {
              void 0 === t && (t = !1),
                delete this.creatorHash[e],
                o.Instance.unregisterElement(e),
                t && i.b.removeClass(e);
            }),
            (e.prototype.getAllTypes = function () {
              var e = o.Instance.getAllTypes();
              for (var t in this.creatorHash) e.push(t);
              return e.sort();
            }),
            (e.prototype.createElement = function (e, t) {
              var n = this.creatorHash[e];
              return null == n ? o.Instance.createQuestion(e, t) : n(t);
            }),
            (e.Instance = new e()),
            e
          );
        })();
    },
    function (e, t, n) {
      'use strict';
      n.d(t, 'a', function () {
        return r;
      });
      var r = (function () {
        function e() {}
        return (
          (e.isValueEmpty = function (t) {
            if (Array.isArray(t) && 0 === t.length) return !0;
            if (t && 'object' == typeof t && t.constructor === Object) {
              for (var n in t) if (!e.isValueEmpty(t[n])) return !1;
              return !0;
            }
            return (
              t && ('string' == typeof t || t instanceof String) && (t = t.trim()),
              !t && 0 !== t && !1 !== t
            );
          }),
          (e.isArrayContainsEqual = function (t, n) {
            if (!Array.isArray(t) || !Array.isArray(n)) return !1;
            if (t.length !== n.length) return !1;
            for (var r = 0; r < t.length; r++) {
              for (var i = 0; i < n.length && !e.isTwoValueEquals(t[r], n[i]); i++);
              if (i === n.length) return !1;
            }
            return !0;
          }),
          (e.isArraysEqual = function (t, n, r) {
            if ((void 0 === r && (r = !1), !Array.isArray(t) || !Array.isArray(n))) return !1;
            if (t.length !== n.length) return !1;
            if (r) {
              for (var i = [], o = [], a = 0; a < t.length; a++) i.push(t[a]), o.push(n[a]);
              i.sort(), o.sort(), (t = i), (n = o);
            }
            for (var a = 0; a < t.length; a++) if (!e.isTwoValueEquals(t[a], n[a])) return !1;
            return !0;
          }),
          (e.isTwoValueEquals = function (t, n, r) {
            if ((void 0 === r && (r = !1), t === n)) return !0;
            if (Array.isArray(t) && 0 === t.length && void 0 === n) return !0;
            if (Array.isArray(n) && 0 === n.length && void 0 === t) return !0;
            if ((void 0 === t || null === t) && '' === n) return !0;
            if ((void 0 === n || null === n) && '' === t) return !0;
            if ('string' == typeof t && 'string' == typeof n) return t == n;
            if (
              e.isConvertibleToNumber(t) &&
              e.isConvertibleToNumber(n) &&
              parseInt(t) === parseInt(n) &&
              parseFloat(t) === parseFloat(n)
            )
              return !0;
            if (
              (!e.isValueEmpty(t) && e.isValueEmpty(n)) ||
              (e.isValueEmpty(t) && !e.isValueEmpty(n))
            )
              return !1;
            if ((!0 === t || !1 === t) && 'string' == typeof n)
              return t.toString() === n.toLocaleLowerCase();
            if ((!0 === n || !1 === n) && 'string' == typeof t)
              return n.toString() === t.toLocaleLowerCase();
            if (!(t instanceof Object || n instanceof Object)) return t == n;
            if (!(t instanceof Object && n instanceof Object)) return !1;
            if (t.equals) return t.equals(n);
            if (t.toJSON && n.toJSON) return this.isTwoValueEquals(t.toJSON(), n.toJSON());
            if (Array.isArray(t) && Array.isArray(n)) return e.isArraysEqual(t, n, r);
            for (var i in t)
              if (t.hasOwnProperty(i)) {
                if (!n.hasOwnProperty(i)) return !1;
                if (t[i] !== n[i]) {
                  if ('object' != typeof t[i]) return !1;
                  if (!this.isTwoValueEquals(t[i], n[i])) return !1;
                }
              }
            for (i in n) if (n.hasOwnProperty(i) && !t.hasOwnProperty(i)) return !1;
            return !0;
          }),
          (e.randomizeArray = function (e) {
            for (var t = e.length - 1; t > 0; t--) {
              var n = Math.floor(Math.random() * (t + 1)),
                r = e[t];
              (e[t] = e[n]), (e[n] = r);
            }
            return e;
          }),
          (e.getUnbindValue = function (e) {
            return e && e instanceof Object ? JSON.parse(JSON.stringify(e)) : e;
          }),
          (e.createCopy = function (e) {
            var t = {};
            if (!e) return t;
            for (var n in e) t[n] = e[n];
            return t;
          }),
          (e.isConvertibleToNumber = function (e) {
            return void 0 !== e && null !== e && !Array.isArray(e) && !isNaN(e);
          }),
          (e.isNumber = function (e) {
            return (
              !('string' == typeof e && e && 0 == e.indexOf('0x') && e.length > 32) &&
              !isNaN(parseFloat(e)) &&
              isFinite(e)
            );
          }),
          (e.getMaxLength = function (e, t) {
            return e < 0 && (e = t), e > 0 ? e : null;
          }),
          (e.getNumberByIndex = function (t, n) {
            if (t < 0) return '';
            var r = 1,
              i = '',
              o = '.',
              a = !0,
              s = 'A',
              l = '';
            if (n) {
              l = n;
              for (var u = l.length - 1, c = !1, p = 0; p < l.length; p++)
                if (e.isCharDigit(l[p])) {
                  c = !0;
                  break;
                }
              for (
                var d = function () {
                  return (c && !e.isCharDigit(l[u])) || e.isCharNotLetterAndDigit(l[u]);
                };
                u >= 0 && d();

              )
                u--;
              var h = '';
              for (
                u < l.length - 1 && ((h = l.substr(u + 1)), (l = l.substr(0, u + 1))),
                  u = l.length - 1;
                u >= 0 && !d() && (u--, c);

              );
              (s = l.substr(u + 1)),
                (i = l.substr(0, u + 1)),
                parseInt(s) ? (r = parseInt(s)) : 1 == s.length && (a = !1),
                (h || i) && (o = h);
            }
            return a
              ? i + (t + r).toString() + o
              : i + String.fromCharCode(s.charCodeAt(0) + t) + o;
          }),
          (e.isCharNotLetterAndDigit = function (t) {
            return t.toUpperCase() == t.toLowerCase() && !e.isCharDigit(t);
          }),
          (e.isCharDigit = function (e) {
            return e >= '0' && e <= '9';
          }),
          e
        );
      })();
      String.prototype.format ||
        (String.prototype.format = function () {
          var e = arguments;
          return this.replace(/{(\d+)}/g, function (t, n) {
            return void 0 !== e[n] ? e[n] : t;
          });
        });
    },
    function (e, t, n) {
      var r, i, o;
      /*!
       * Knockout JavaScript library v3.5.0
       * (c) The Knockout.js team - http://knockoutjs.com/
       * License: MIT (http://www.opensource.org/licenses/mit-license.php)
       */
      !(function () {
        !(function (a) {
          var s = this || (0, eval)('this'),
            l = s.document,
            u = s.navigator,
            c = s.jQuery,
            p = s.JSON;
          c || 'undefined' == typeof jQuery || (c = jQuery),
            (function (a) {
              (i = [t, n]),
                (r = a),
                void 0 !== (o = 'function' == typeof r ? r.apply(t, i) : r) && (e.exports = o);
            })(function (e, t) {
              function n(e, t) {
                return (null === e || typeof e in m) && e === t;
              }
              function r(e, t) {
                var n;
                return function () {
                  n ||
                    (n = f.a.setTimeout(function () {
                      (n = a), e();
                    }, t));
                };
              }
              function i(e, t) {
                var n;
                return function () {
                  clearTimeout(n), (n = f.a.setTimeout(e, t));
                };
              }
              function o(e, t) {
                t && 'change' !== t
                  ? 'beforeChange' === t
                    ? this.oc(e)
                    : this.bb(e, t)
                  : this.pc(e);
              }
              function d(e, t) {
                null !== t && t.s && t.s();
              }
              function h(e, t) {
                var n = this.pd,
                  r = n[x];
                r.qa ||
                  (this.Pb && this.kb[t]
                    ? (n.tc(t, e, this.kb[t]), (this.kb[t] = null), --this.Pb)
                    : r.F[t] || n.tc(t, e, r.G ? { da: e } : n.Zc(e)),
                  e.Ka && e.fd());
              }
              var f = void 0 !== e ? e : {};
              (f.b = function (e, t) {
                for (var n = e.split('.'), r = f, i = 0; i < n.length - 1; i++) r = r[n[i]];
                r[n[n.length - 1]] = t;
              }),
                (f.J = function (e, t, n) {
                  e[t] = n;
                }),
                (f.version = '3.5.0'),
                f.b('version', f.version),
                (f.options = {
                  deferUpdates: !1,
                  useOnlyNativeEvents: !1,
                  foreachHidesDestroyed: !1,
                }),
                (f.a = (function () {
                  function e(e, t) {
                    for (var n in e) i.call(e, n) && t(n, e[n]);
                  }
                  function t(e, t) {
                    if (t) for (var n in t) i.call(t, n) && (e[n] = t[n]);
                    return e;
                  }
                  function n(e, t) {
                    return (e.__proto__ = t), e;
                  }
                  function r(e, t, n, r) {
                    var i = e[t].match(b) || [];
                    f.a.C(n.match(b), function (e) {
                      f.a.Oa(i, e, r);
                    }),
                      (e[t] = i.join(' '));
                  }
                  var i = Object.prototype.hasOwnProperty,
                    o = { __proto__: [] } instanceof Array,
                    d = 'function' == typeof Symbol,
                    h = {},
                    m = {};
                  (h[u && /Firefox\/2/i.test(u.userAgent) ? 'KeyboardEvent' : 'UIEvents'] = [
                    'keyup',
                    'keydown',
                    'keypress',
                  ]),
                    (h.MouseEvents = 'click dblclick mousedown mouseup mousemove mouseover mouseout mouseenter mouseleave'.split(
                      ' '
                    )),
                    e(h, function (e, t) {
                      if (t.length) for (var n = 0, r = t.length; n < r; n++) m[t[n]] = e;
                    });
                  var g,
                    y = { propertychange: !0 },
                    v =
                      l &&
                      (function () {
                        for (
                          var e = 3, t = l.createElement('div'), n = t.getElementsByTagName('i');
                          (t.innerHTML = '\x3c!--[if gt IE ' + ++e + ']><i></i><![endif]--\x3e'),
                            n[0];

                        );
                        return 4 < e ? e : a;
                      })(),
                    b = /\S+/g;
                  return {
                    Ic: ['authenticity_token', /^__RequestVerificationToken(_.*)?$/],
                    C: function (e, t, n) {
                      for (var r = 0, i = e.length; r < i; r++) t.call(n, e[r], r, e);
                    },
                    A:
                      'function' == typeof Array.prototype.indexOf
                        ? function (e, t) {
                            return Array.prototype.indexOf.call(e, t);
                          }
                        : function (e, t) {
                            for (var n = 0, r = e.length; n < r; n++) if (e[n] === t) return n;
                            return -1;
                          },
                    Lb: function (e, t, n) {
                      for (var r = 0, i = e.length; r < i; r++)
                        if (t.call(n, e[r], r, e)) return e[r];
                      return a;
                    },
                    hb: function (e, t) {
                      var n = f.a.A(e, t);
                      0 < n ? e.splice(n, 1) : 0 === n && e.shift();
                    },
                    vc: function (e) {
                      var t = [];
                      return (
                        e &&
                          f.a.C(e, function (e) {
                            0 > f.a.A(t, e) && t.push(e);
                          }),
                        t
                      );
                    },
                    Mb: function (e, t, n) {
                      var r = [];
                      if (e) for (var i = 0, o = e.length; i < o; i++) r.push(t.call(n, e[i], i));
                      return r;
                    },
                    fb: function (e, t, n) {
                      var r = [];
                      if (e)
                        for (var i = 0, o = e.length; i < o; i++)
                          t.call(n, e[i], i) && r.push(e[i]);
                      return r;
                    },
                    gb: function (e, t) {
                      if (t instanceof Array) e.push.apply(e, t);
                      else for (var n = 0, r = t.length; n < r; n++) e.push(t[n]);
                      return e;
                    },
                    Oa: function (e, t, n) {
                      var r = f.a.A(f.a.$b(e), t);
                      0 > r ? n && e.push(t) : n || e.splice(r, 1);
                    },
                    Ba: o,
                    extend: t,
                    setPrototypeOf: n,
                    zb: o ? n : t,
                    O: e,
                    Ha: function (e, t, n) {
                      if (!e) return e;
                      var r,
                        o = {};
                      for (r in e) i.call(e, r) && (o[r] = t.call(n, e[r], r, e));
                      return o;
                    },
                    Sb: function (e) {
                      for (; e.firstChild; ) f.removeNode(e.firstChild);
                    },
                    Xb: function (e) {
                      e = f.a.la(e);
                      for (
                        var t = ((e[0] && e[0].ownerDocument) || l).createElement('div'),
                          n = 0,
                          r = e.length;
                        n < r;
                        n++
                      )
                        t.appendChild(f.na(e[n]));
                      return t;
                    },
                    Ca: function (e, t) {
                      for (var n = 0, r = e.length, i = []; n < r; n++) {
                        var o = e[n].cloneNode(!0);
                        i.push(t ? f.na(o) : o);
                      }
                      return i;
                    },
                    ua: function (e, t) {
                      if ((f.a.Sb(e), t))
                        for (var n = 0, r = t.length; n < r; n++) e.appendChild(t[n]);
                    },
                    Wc: function (e, t) {
                      var n = e.nodeType ? [e] : e;
                      if (0 < n.length) {
                        for (var r = n[0], i = r.parentNode, o = 0, a = t.length; o < a; o++)
                          i.insertBefore(t[o], r);
                        for (o = 0, a = n.length; o < a; o++) f.removeNode(n[o]);
                      }
                    },
                    Ua: function (e, t) {
                      if (e.length) {
                        for (
                          t = (8 === t.nodeType && t.parentNode) || t;
                          e.length && e[0].parentNode !== t;

                        )
                          e.splice(0, 1);
                        for (; 1 < e.length && e[e.length - 1].parentNode !== t; ) e.length--;
                        if (1 < e.length) {
                          var n = e[0],
                            r = e[e.length - 1];
                          for (e.length = 0; n !== r; ) e.push(n), (n = n.nextSibling);
                          e.push(r);
                        }
                      }
                      return e;
                    },
                    Yc: function (e, t) {
                      7 > v ? e.setAttribute('selected', t) : (e.selected = t);
                    },
                    Cb: function (e) {
                      return null === e || e === a
                        ? ''
                        : e.trim
                        ? e.trim()
                        : e.toString().replace(/^[\s\xa0]+|[\s\xa0]+$/g, '');
                    },
                    Td: function (e, t) {
                      return (
                        (e = e || ''), !(t.length > e.length) && e.substring(0, t.length) === t
                      );
                    },
                    ud: function (e, t) {
                      if (e === t) return !0;
                      if (11 === e.nodeType) return !1;
                      if (t.contains) return t.contains(1 !== e.nodeType ? e.parentNode : e);
                      if (t.compareDocumentPosition)
                        return 16 == (16 & t.compareDocumentPosition(e));
                      for (; e && e != t; ) e = e.parentNode;
                      return !!e;
                    },
                    Rb: function (e) {
                      return f.a.ud(e, e.ownerDocument.documentElement);
                    },
                    jd: function (e) {
                      return !!f.a.Lb(e, f.a.Rb);
                    },
                    P: function (e) {
                      return e && e.tagName && e.tagName.toLowerCase();
                    },
                    zc: function (e) {
                      return f.onError
                        ? function () {
                            try {
                              return e.apply(this, arguments);
                            } catch (e) {
                              throw (f.onError && f.onError(e), e);
                            }
                          }
                        : e;
                    },
                    setTimeout: function (e, t) {
                      return setTimeout(f.a.zc(e), t);
                    },
                    Fc: function (e) {
                      setTimeout(function () {
                        throw (f.onError && f.onError(e), e);
                      }, 0);
                    },
                    H: function (e, t, n) {
                      var r = f.a.zc(n);
                      if (((n = y[t]), f.options.useOnlyNativeEvents || n || !c))
                        if (n || 'function' != typeof e.addEventListener) {
                          if (void 0 === e.attachEvent)
                            throw Error("Browser doesn't support addEventListener or attachEvent");
                          var i = function (t) {
                              r.call(e, t);
                            },
                            o = 'on' + t;
                          e.attachEvent(o, i),
                            f.a.I.za(e, function () {
                              e.detachEvent(o, i);
                            });
                        } else e.addEventListener(t, r, !1);
                      else g || (g = 'function' == typeof c(e).on ? 'on' : 'bind'), c(e)[g](t, r);
                    },
                    Fb: function (e, t) {
                      if (!e || !e.nodeType)
                        throw Error('element must be a DOM node when calling triggerEvent');
                      var n;
                      if (
                        ('input' === f.a.P(e) && e.type && 'click' == t.toLowerCase()
                          ? ((n = e.type), (n = 'checkbox' == n || 'radio' == n))
                          : (n = !1),
                        f.options.useOnlyNativeEvents || !c || n)
                      )
                        if ('function' == typeof l.createEvent) {
                          if ('function' != typeof e.dispatchEvent)
                            throw Error("The supplied element doesn't support dispatchEvent");
                          (n = l.createEvent(m[t] || 'HTMLEvents')),
                            n.initEvent(t, !0, !0, s, 0, 0, 0, 0, 0, !1, !1, !1, !1, 0, e),
                            e.dispatchEvent(n);
                        } else if (n && e.click) e.click();
                        else {
                          if (void 0 === e.fireEvent)
                            throw Error("Browser doesn't support triggering events");
                          e.fireEvent('on' + t);
                        }
                      else c(e).trigger(t);
                    },
                    c: function (e) {
                      return f.N(e) ? e() : e;
                    },
                    $b: function (e) {
                      return f.N(e) ? e.w() : e;
                    },
                    Eb: function (e, t, n) {
                      var i;
                      t &&
                        ('object' == typeof e.classList
                          ? ((i = e.classList[n ? 'add' : 'remove']),
                            f.a.C(t.match(b), function (t) {
                              i.call(e.classList, t);
                            }))
                          : 'string' == typeof e.className.baseVal
                          ? r(e.className, 'baseVal', t, n)
                          : r(e, 'className', t, n));
                    },
                    Ab: function (e, t) {
                      var n = f.a.c(t);
                      (null !== n && n !== a) || (n = '');
                      var r = f.h.firstChild(e);
                      !r || 3 != r.nodeType || f.h.nextSibling(r)
                        ? f.h.ua(e, [e.ownerDocument.createTextNode(n)])
                        : (r.data = n),
                        f.a.zd(e);
                    },
                    Xc: function (e, t) {
                      if (((e.name = t), 7 >= v))
                        try {
                          var n = e.name.replace(/[&<>'"]/g, function (e) {
                            return '&#' + e.charCodeAt(0) + ';';
                          });
                          e.mergeAttributes(l.createElement("<input name='" + n + "'/>"), !1);
                        } catch (e) {}
                    },
                    zd: function (e) {
                      9 <= v &&
                        ((e = 1 == e.nodeType ? e : e.parentNode),
                        e.style && (e.style.zoom = e.style.zoom));
                    },
                    vd: function (e) {
                      if (v) {
                        var t = e.style.width;
                        (e.style.width = 0), (e.style.width = t);
                      }
                    },
                    Od: function (e, t) {
                      (e = f.a.c(e)), (t = f.a.c(t));
                      for (var n = [], r = e; r <= t; r++) n.push(r);
                      return n;
                    },
                    la: function (e) {
                      for (var t = [], n = 0, r = e.length; n < r; n++) t.push(e[n]);
                      return t;
                    },
                    Da: function (e) {
                      return d ? Symbol(e) : e;
                    },
                    Xd: 6 === v,
                    Yd: 7 === v,
                    W: v,
                    Kc: function (e, t) {
                      for (
                        var n = f.a
                            .la(e.getElementsByTagName('input'))
                            .concat(f.a.la(e.getElementsByTagName('textarea'))),
                          r =
                            'string' == typeof t
                              ? function (e) {
                                  return e.name === t;
                                }
                              : function (e) {
                                  return t.test(e.name);
                                },
                          i = [],
                          o = n.length - 1;
                        0 <= o;
                        o--
                      )
                        r(n[o]) && i.push(n[o]);
                      return i;
                    },
                    Md: function (e) {
                      return 'string' == typeof e && (e = f.a.Cb(e))
                        ? p && p.parse
                          ? p.parse(e)
                          : new Function('return ' + e)()
                        : null;
                    },
                    fc: function (e, t, n) {
                      if (!p || !p.stringify)
                        throw Error(
                          "Cannot find JSON.stringify(). Some browsers (e.g., IE < 8) don't support it natively, but you can overcome this by adding a script reference to json2.js, downloadable from http://www.json.org/json2.js"
                        );
                      return p.stringify(f.a.c(e), t, n);
                    },
                    Nd: function (t, n, r) {
                      r = r || {};
                      var i = r.params || {},
                        o = r.includeFields || this.Ic,
                        a = t;
                      if ('object' == typeof t && 'form' === f.a.P(t))
                        for (var a = t.action, s = o.length - 1; 0 <= s; s--)
                          for (var u = f.a.Kc(t, o[s]), c = u.length - 1; 0 <= c; c--)
                            i[u[c].name] = u[c].value;
                      n = f.a.c(n);
                      var p = l.createElement('form');
                      (p.style.display = 'none'), (p.action = a), (p.method = 'post');
                      for (var d in n)
                        (t = l.createElement('input')),
                          (t.type = 'hidden'),
                          (t.name = d),
                          (t.value = f.a.fc(f.a.c(n[d]))),
                          p.appendChild(t);
                      e(i, function (e, t) {
                        var n = l.createElement('input');
                        (n.type = 'hidden'), (n.name = e), (n.value = t), p.appendChild(n);
                      }),
                        l.body.appendChild(p),
                        r.submitter ? r.submitter(p) : p.submit(),
                        setTimeout(function () {
                          p.parentNode.removeChild(p);
                        }, 0);
                    },
                  };
                })()),
                f.b('utils', f.a),
                f.b('utils.arrayForEach', f.a.C),
                f.b('utils.arrayFirst', f.a.Lb),
                f.b('utils.arrayFilter', f.a.fb),
                f.b('utils.arrayGetDistinctValues', f.a.vc),
                f.b('utils.arrayIndexOf', f.a.A),
                f.b('utils.arrayMap', f.a.Mb),
                f.b('utils.arrayPushAll', f.a.gb),
                f.b('utils.arrayRemoveItem', f.a.hb),
                f.b('utils.cloneNodes', f.a.Ca),
                f.b('utils.createSymbolOrString', f.a.Da),
                f.b('utils.extend', f.a.extend),
                f.b('utils.fieldsIncludedWithJsonPost', f.a.Ic),
                f.b('utils.getFormFields', f.a.Kc),
                f.b('utils.objectMap', f.a.Ha),
                f.b('utils.peekObservable', f.a.$b),
                f.b('utils.postJson', f.a.Nd),
                f.b('utils.parseJson', f.a.Md),
                f.b('utils.registerEventHandler', f.a.H),
                f.b('utils.stringifyJson', f.a.fc),
                f.b('utils.range', f.a.Od),
                f.b('utils.toggleDomNodeCssClass', f.a.Eb),
                f.b('utils.triggerEvent', f.a.Fb),
                f.b('utils.unwrapObservable', f.a.c),
                f.b('utils.objectForEach', f.a.O),
                f.b('utils.addOrRemoveItem', f.a.Oa),
                f.b('utils.setTextContent', f.a.Ab),
                f.b('unwrap', f.a.c),
                Function.prototype.bind ||
                  (Function.prototype.bind = function (e) {
                    var t = this;
                    if (1 === arguments.length)
                      return function () {
                        return t.apply(e, arguments);
                      };
                    var n = Array.prototype.slice.call(arguments, 1);
                    return function () {
                      var r = n.slice(0);
                      return r.push.apply(r, arguments), t.apply(e, r);
                    };
                  }),
                (f.a.g = new (function () {
                  var e,
                    t,
                    n = 0,
                    r = '__ko__' + new Date().getTime(),
                    i = {};
                  return (
                    f.a.W
                      ? ((e = function (e, t) {
                          var o = e[r];
                          if (!o || 'null' === o || !i[o]) {
                            if (!t) return a;
                            (o = e[r] = 'ko' + n++), (i[o] = {});
                          }
                          return i[o];
                        }),
                        (t = function (e) {
                          var t = e[r];
                          return !!t && (delete i[t], (e[r] = null), !0);
                        }))
                      : ((e = function (e, t) {
                          var n = e[r];
                          return !n && t && (n = e[r] = {}), n;
                        }),
                        (t = function (e) {
                          return !!e[r] && (delete e[r], !0);
                        })),
                    {
                      get: function (t, n) {
                        var r = e(t, !1);
                        return r && r[n];
                      },
                      set: function (t, n, r) {
                        (t = e(t, r !== a)) && (t[n] = r);
                      },
                      Tb: function (t, n, r) {
                        return (t = e(t, !0)), t[n] || (t[n] = r);
                      },
                      clear: t,
                      Z: function () {
                        return n++ + r;
                      },
                    }
                  );
                })()),
                f.b('utils.domData', f.a.g),
                f.b('utils.domData.clear', f.a.g.clear),
                (f.a.I = new (function () {
                  function e(e, t) {
                    var n = f.a.g.get(e, r);
                    return n === a && t && ((n = []), f.a.g.set(e, r, n)), n;
                  }
                  function t(t) {
                    var r = e(t, !1);
                    if (r) for (var r = r.slice(0), i = 0; i < r.length; i++) r[i](t);
                    f.a.g.clear(t),
                      f.a.I.cleanExternalData(t),
                      o[t.nodeType] && n(t.childNodes, !0);
                  }
                  function n(e, n) {
                    for (var r, i = [], o = 0; o < e.length; o++)
                      if ((!n || 8 === e[o].nodeType) && (t((i[i.length] = r = e[o])), e[o] !== r))
                        for (; o-- && -1 == f.a.A(i, e[o]); );
                  }
                  var r = f.a.g.Z(),
                    i = { 1: !0, 8: !0, 9: !0 },
                    o = { 1: !0, 9: !0 };
                  return {
                    za: function (t, n) {
                      if ('function' != typeof n) throw Error('Callback must be a function');
                      e(t, !0).push(n);
                    },
                    xb: function (t, n) {
                      var i = e(t, !1);
                      i && (f.a.hb(i, n), 0 == i.length && f.a.g.set(t, r, a));
                    },
                    na: function (e) {
                      return (
                        i[e.nodeType] && (t(e), o[e.nodeType] && n(e.getElementsByTagName('*'))), e
                      );
                    },
                    removeNode: function (e) {
                      f.na(e), e.parentNode && e.parentNode.removeChild(e);
                    },
                    cleanExternalData: function (e) {
                      c && 'function' == typeof c.cleanData && c.cleanData([e]);
                    },
                  };
                })()),
                (f.na = f.a.I.na),
                (f.removeNode = f.a.I.removeNode),
                f.b('cleanNode', f.na),
                f.b('removeNode', f.removeNode),
                f.b('utils.domNodeDisposal', f.a.I),
                f.b('utils.domNodeDisposal.addDisposeCallback', f.a.I.za),
                f.b('utils.domNodeDisposal.removeDisposeCallback', f.a.I.xb),
                (function () {
                  var e = [0, '', ''],
                    t = [1, '<table>', '</table>'],
                    n = [3, '<table><tbody><tr>', '</tr></tbody></table>'],
                    r = [1, "<select multiple='multiple'>", '</select>'],
                    i = {
                      thead: t,
                      tbody: t,
                      tfoot: t,
                      tr: [2, '<table><tbody>', '</tbody></table>'],
                      td: n,
                      th: n,
                      option: r,
                      optgroup: r,
                    },
                    o = 8 >= f.a.W;
                  (f.a.ta = function (t, n) {
                    var r;
                    if (c) {
                      if (c.parseHTML) r = c.parseHTML(t, n) || [];
                      else if ((r = c.clean([t], n)) && r[0]) {
                        for (var a = r[0]; a.parentNode && 11 !== a.parentNode.nodeType; )
                          a = a.parentNode;
                        a.parentNode && a.parentNode.removeChild(a);
                      }
                    } else {
                      (r = n) || (r = l);
                      var u,
                        a = r.parentWindow || r.defaultView || s,
                        p = f.a.Cb(t).toLowerCase(),
                        d = r.createElement('div');
                      for (
                        u =
                          ((p = p.match(/^(?:\x3c!--.*?--\x3e\s*?)*?<([a-z]+)[\s>]/)) && i[p[1]]) ||
                          e,
                          p = u[0],
                          u = 'ignored<div>' + u[1] + t + u[2] + '</div>',
                          'function' == typeof a.innerShiv
                            ? d.appendChild(a.innerShiv(u))
                            : (o && r.body.appendChild(d),
                              (d.innerHTML = u),
                              o && d.parentNode.removeChild(d));
                        p--;

                      )
                        d = d.lastChild;
                      r = f.a.la(d.lastChild.childNodes);
                    }
                    return r;
                  }),
                    (f.a.Ld = function (e, t) {
                      var n = f.a.ta(e, t);
                      return (n.length && n[0].parentElement) || f.a.Xb(n);
                    }),
                    (f.a.dc = function (e, t) {
                      if ((f.a.Sb(e), null !== (t = f.a.c(t)) && t !== a))
                        if (('string' != typeof t && (t = t.toString()), c)) c(e).html(t);
                        else
                          for (var n = f.a.ta(t, e.ownerDocument), r = 0; r < n.length; r++)
                            e.appendChild(n[r]);
                    });
                })(),
                f.b('utils.parseHtmlFragment', f.a.ta),
                f.b('utils.setHtml', f.a.dc),
                (f.aa = (function () {
                  function e(t, n) {
                    if (t)
                      if (8 == t.nodeType) {
                        var r = f.aa.Tc(t.nodeValue);
                        null != r && n.push({ sd: t, Jd: r });
                      } else if (1 == t.nodeType)
                        for (var r = 0, i = t.childNodes, o = i.length; r < o; r++) e(i[r], n);
                  }
                  var t = {};
                  return {
                    Wb: function (e) {
                      if ('function' != typeof e)
                        throw Error('You can only pass a function to ko.memoization.memoize()');
                      var n =
                        ((4294967296 * (1 + Math.random())) | 0).toString(16).substring(1) +
                        ((4294967296 * (1 + Math.random())) | 0).toString(16).substring(1);
                      return (t[n] = e), '\x3c!--[ko_memo:' + n + ']--\x3e';
                    },
                    ad: function (e, n) {
                      var r = t[e];
                      if (r === a)
                        throw Error(
                          "Couldn't find any memo with ID " +
                            e +
                            ". Perhaps it's already been unmemoized."
                        );
                      try {
                        return r.apply(null, n || []), !0;
                      } finally {
                        delete t[e];
                      }
                    },
                    bd: function (t, n) {
                      var r = [];
                      e(t, r);
                      for (var i = 0, o = r.length; i < o; i++) {
                        var a = r[i].sd,
                          s = [a];
                        n && f.a.gb(s, n),
                          f.aa.ad(r[i].Jd, s),
                          (a.nodeValue = ''),
                          a.parentNode && a.parentNode.removeChild(a);
                      }
                    },
                    Tc: function (e) {
                      return (e = e.match(/^\[ko_memo\:(.*?)\]$/)) ? e[1] : null;
                    },
                  };
                })()),
                f.b('memoization', f.aa),
                f.b('memoization.memoize', f.aa.Wb),
                f.b('memoization.unmemoize', f.aa.ad),
                f.b('memoization.parseMemoText', f.aa.Tc),
                f.b('memoization.unmemoizeDomNodeAndDescendants', f.aa.bd),
                (f.ma = (function () {
                  function e() {
                    if (i)
                      for (var e, t = i, n = 0; a < i; )
                        if ((e = r[a++])) {
                          if (a > t) {
                            if (5e3 <= ++n) {
                              (a = i),
                                f.a.Fc(
                                  Error(
                                    "'Too much recursion' after processing " + n + ' task groups.'
                                  )
                                );
                              break;
                            }
                            t = i;
                          }
                          try {
                            e();
                          } catch (e) {
                            f.a.Fc(e);
                          }
                        }
                  }
                  function t() {
                    e(), (a = i = r.length = 0);
                  }
                  var n,
                    r = [],
                    i = 0,
                    o = 1,
                    a = 0;
                  return (
                    (n = s.MutationObserver
                      ? (function (e) {
                          var t = l.createElement('div');
                          return (
                            new MutationObserver(e).observe(t, { attributes: !0 }),
                            function () {
                              t.classList.toggle('foo');
                            }
                          );
                        })(t)
                      : l && 'onreadystatechange' in l.createElement('script')
                      ? function (e) {
                          var t = l.createElement('script');
                          (t.onreadystatechange = function () {
                            (t.onreadystatechange = null),
                              l.documentElement.removeChild(t),
                              (t = null),
                              e();
                          }),
                            l.documentElement.appendChild(t);
                        }
                      : function (e) {
                          setTimeout(e, 0);
                        }),
                    {
                      scheduler: n,
                      yb: function (e) {
                        return i || f.ma.scheduler(t), (r[i++] = e), o++;
                      },
                      cancel: function (e) {
                        (e -= o - i) >= a && e < i && (r[e] = null);
                      },
                      resetForTesting: function () {
                        var e = i - a;
                        return (a = i = r.length = 0), e;
                      },
                      Rd: e,
                    }
                  );
                })()),
                f.b('tasks', f.ma),
                f.b('tasks.schedule', f.ma.yb),
                f.b('tasks.runEarly', f.ma.Rd),
                (f.Ta = {
                  throttle: function (e, t) {
                    e.throttleEvaluation = t;
                    var n = null;
                    return f.$({
                      read: e,
                      write: function (r) {
                        clearTimeout(n),
                          (n = f.a.setTimeout(function () {
                            e(r);
                          }, t));
                      },
                    });
                  },
                  rateLimit: function (e, t) {
                    var n, o, a;
                    'number' == typeof t ? (n = t) : ((n = t.timeout), (o = t.method)),
                      (e.Hb = !1),
                      (a = 'function' == typeof o ? o : 'notifyWhenChangesStop' == o ? i : r),
                      e.tb(function (e) {
                        return a(e, n, t);
                      });
                  },
                  deferred: function (e, t) {
                    if (!0 !== t)
                      throw Error(
                        "The 'deferred' extender only accepts the value 'true', because it is not supported to turn deferral off once enabled."
                      );
                    e.Hb ||
                      ((e.Hb = !0),
                      e.tb(function (t) {
                        var n,
                          r = !1;
                        return function () {
                          if (!r) {
                            f.ma.cancel(n), (n = f.ma.yb(t));
                            try {
                              (r = !0), e.notifySubscribers(a, 'dirty');
                            } finally {
                              r = !1;
                            }
                          }
                        };
                      }));
                  },
                  notify: function (e, t) {
                    e.equalityComparer = 'always' == t ? null : n;
                  },
                });
              var m = { undefined: 1, boolean: 1, number: 1, string: 1 };
              f.b('extenders', f.Ta),
                (f.gc = function (e, t, n) {
                  (this.da = e),
                    (this.kc = t),
                    (this.lc = n),
                    (this.Ib = !1),
                    (this.ab = this.Jb = null),
                    f.J(this, 'dispose', this.s),
                    f.J(this, 'disposeWhenNodeIsRemoved', this.l);
                }),
                (f.gc.prototype.s = function () {
                  this.Ib ||
                    (this.ab && f.a.I.xb(this.Jb, this.ab),
                    (this.Ib = !0),
                    this.lc(),
                    (this.da = this.kc = this.lc = this.Jb = this.ab = null));
                }),
                (f.gc.prototype.l = function (e) {
                  (this.Jb = e), f.a.I.za(e, (this.ab = this.s.bind(this)));
                }),
                (f.R = function () {
                  f.a.zb(this, g), g.ob(this);
                });
              var g = {
                ob: function (e) {
                  (e.S = { change: [] }), (e.rc = 1);
                },
                subscribe: function (e, t, n) {
                  var r = this;
                  n = n || 'change';
                  var i = new f.gc(r, t ? e.bind(t) : e, function () {
                    f.a.hb(r.S[n], i), r.cb && r.cb(n);
                  });
                  return r.Qa && r.Qa(n), r.S[n] || (r.S[n] = []), r.S[n].push(i), i;
                },
                notifySubscribers: function (e, t) {
                  if (((t = t || 'change'), 'change' === t && this.Gb(), this.Wa(t))) {
                    var n = ('change' === t && this.dd) || this.S[t].slice(0);
                    try {
                      f.v.wc();
                      for (var r, i = 0; (r = n[i]); ++i) r.Ib || r.kc(e);
                    } finally {
                      f.v.end();
                    }
                  }
                },
                mb: function () {
                  return this.rc;
                },
                Cd: function (e) {
                  return this.mb() !== e;
                },
                Gb: function () {
                  ++this.rc;
                },
                tb: function (e) {
                  var t,
                    n,
                    r,
                    i,
                    a,
                    s = this,
                    l = f.N(s);
                  s.bb || ((s.bb = s.notifySubscribers), (s.notifySubscribers = o));
                  var u = e(function () {
                    (s.Ka = !1), l && i === s && (i = s.mc ? s.mc() : s());
                    var e = n || (a && s.qb(r, i));
                    (a = n = t = !1), e && s.bb((r = i));
                  });
                  (s.pc = function (e, n) {
                    (n && s.Ka) || (a = !n),
                      (s.dd = s.S.change.slice(0)),
                      (s.Ka = t = !0),
                      (i = e),
                      u();
                  }),
                    (s.oc = function (e) {
                      t || ((r = e), s.bb(e, 'beforeChange'));
                    }),
                    (s.qc = function () {
                      a = !0;
                    }),
                    (s.fd = function () {
                      s.qb(r, s.w(!0)) && (n = !0);
                    });
                },
                Wa: function (e) {
                  return this.S[e] && this.S[e].length;
                },
                Ad: function (e) {
                  if (e) return (this.S[e] && this.S[e].length) || 0;
                  var t = 0;
                  return (
                    f.a.O(this.S, function (e, n) {
                      'dirty' !== e && (t += n.length);
                    }),
                    t
                  );
                },
                qb: function (e, t) {
                  return !this.equalityComparer || !this.equalityComparer(e, t);
                },
                toString: function () {
                  return '[object Object]';
                },
                extend: function (e) {
                  var t = this;
                  return (
                    e &&
                      f.a.O(e, function (e, n) {
                        var r = f.Ta[e];
                        'function' == typeof r && (t = r(t, n) || t);
                      }),
                    t
                  );
                },
              };
              f.J(g, 'init', g.ob),
                f.J(g, 'subscribe', g.subscribe),
                f.J(g, 'extend', g.extend),
                f.J(g, 'getSubscriptionsCount', g.Ad),
                f.a.Ba && f.a.setPrototypeOf(g, Function.prototype),
                (f.R.fn = g),
                (f.Pc = function (e) {
                  return (
                    null != e &&
                    'function' == typeof e.subscribe &&
                    'function' == typeof e.notifySubscribers
                  );
                }),
                f.b('subscribable', f.R),
                f.b('isSubscribable', f.Pc),
                (f.U = f.v = (function () {
                  function e(e) {
                    r.push(n), (n = e);
                  }
                  function t() {
                    n = r.pop();
                  }
                  var n,
                    r = [],
                    i = 0;
                  return {
                    wc: e,
                    end: t,
                    ac: function (e) {
                      if (n) {
                        if (!f.Pc(e))
                          throw Error('Only subscribable things can act as dependencies');
                        n.nd.call(n.od, e, e.ed || (e.ed = ++i));
                      }
                    },
                    K: function (n, r, i) {
                      try {
                        return e(), n.apply(r, i || []);
                      } finally {
                        t();
                      }
                    },
                    pa: function () {
                      if (n) return n.o.pa();
                    },
                    Va: function () {
                      if (n) return n.o.Va();
                    },
                    rb: function () {
                      if (n) return n.rb;
                    },
                    o: function () {
                      if (n) return n.o;
                    },
                  };
                })()),
                f.b('computedContext', f.U),
                f.b('computedContext.getDependenciesCount', f.U.pa),
                f.b('computedContext.getDependencies', f.U.Va),
                f.b('computedContext.isInitial', f.U.rb),
                f.b('computedContext.registerDependency', f.U.ac),
                f.b('ignoreDependencies', (f.Wd = f.v.K));
              var y = f.a.Da('_latestValue');
              f.sa = function (e) {
                function t() {
                  return 0 < arguments.length
                    ? (t.qb(t[y], arguments[0]) && (t.xa(), (t[y] = arguments[0]), t.wa()), this)
                    : (f.v.ac(t), t[y]);
                }
                return (
                  (t[y] = e),
                  f.a.Ba || f.a.extend(t, f.R.fn),
                  f.R.fn.ob(t),
                  f.a.zb(t, v),
                  f.options.deferUpdates && f.Ta.deferred(t, !0),
                  t
                );
              };
              var v = {
                equalityComparer: n,
                w: function () {
                  return this[y];
                },
                wa: function () {
                  this.notifySubscribers(this[y], 'spectate'), this.notifySubscribers(this[y]);
                },
                xa: function () {
                  this.notifySubscribers(this[y], 'beforeChange');
                },
              };
              f.a.Ba && f.a.setPrototypeOf(v, f.R.fn);
              var b = (f.sa.Na = '__ko_proto__');
              (v[b] = f.sa),
                (f.N = function (e) {
                  if ((e = 'function' == typeof e && e[b]) && e !== v[b] && e !== f.o.fn[b])
                    throw Error(
                      'Invalid object that looks like an observable; possibly from another Knockout instance'
                    );
                  return !!e;
                }),
                (f.Ya = function (e) {
                  return 'function' == typeof e && (e[b] === v[b] || (e[b] === f.o.fn[b] && e.Mc));
                }),
                f.b('observable', f.sa),
                f.b('isObservable', f.N),
                f.b('isWriteableObservable', f.Ya),
                f.b('isWritableObservable', f.Ya),
                f.b('observable.fn', v),
                f.J(v, 'peek', v.w),
                f.J(v, 'valueHasMutated', v.wa),
                f.J(v, 'valueWillMutate', v.xa),
                (f.Ia = function (e) {
                  if ('object' != typeof (e = e || []) || !('length' in e))
                    throw Error(
                      'The argument passed when initializing an observable array must be an array, or null, or undefined.'
                    );
                  return (e = f.sa(e)), f.a.zb(e, f.Ia.fn), e.extend({ trackArrayChanges: !0 });
                }),
                (f.Ia.fn = {
                  remove: function (e) {
                    for (
                      var t = this.w(),
                        n = [],
                        r =
                          'function' != typeof e || f.N(e)
                            ? function (t) {
                                return t === e;
                              }
                            : e,
                        i = 0;
                      i < t.length;
                      i++
                    ) {
                      var o = t[i];
                      if (r(o)) {
                        if ((0 === n.length && this.xa(), t[i] !== o))
                          throw Error('Array modified during remove; cannot remove item');
                        n.push(o), t.splice(i, 1), i--;
                      }
                    }
                    return n.length && this.wa(), n;
                  },
                  removeAll: function (e) {
                    if (e === a) {
                      var t = this.w(),
                        n = t.slice(0);
                      return this.xa(), t.splice(0, t.length), this.wa(), n;
                    }
                    return e
                      ? this.remove(function (t) {
                          return 0 <= f.a.A(e, t);
                        })
                      : [];
                  },
                  destroy: function (e) {
                    var t = this.w(),
                      n =
                        'function' != typeof e || f.N(e)
                          ? function (t) {
                              return t === e;
                            }
                          : e;
                    this.xa();
                    for (var r = t.length - 1; 0 <= r; r--) {
                      var i = t[r];
                      n(i) && (i._destroy = !0);
                    }
                    this.wa();
                  },
                  destroyAll: function (e) {
                    return e === a
                      ? this.destroy(function () {
                          return !0;
                        })
                      : e
                      ? this.destroy(function (t) {
                          return 0 <= f.a.A(e, t);
                        })
                      : [];
                  },
                  indexOf: function (e) {
                    var t = this();
                    return f.a.A(t, e);
                  },
                  replace: function (e, t) {
                    var n = this.indexOf(e);
                    0 <= n && (this.xa(), (this.w()[n] = t), this.wa());
                  },
                  sorted: function (e) {
                    var t = this().slice(0);
                    return e ? t.sort(e) : t.sort();
                  },
                  reversed: function () {
                    return this().slice(0).reverse();
                  },
                }),
                f.a.Ba && f.a.setPrototypeOf(f.Ia.fn, f.sa.fn),
                f.a.C('pop push reverse shift sort splice unshift'.split(' '), function (e) {
                  f.Ia.fn[e] = function () {
                    var t = this.w();
                    this.xa(), this.yc(t, e, arguments);
                    var n = t[e].apply(t, arguments);
                    return this.wa(), n === t ? this : n;
                  };
                }),
                f.a.C(['slice'], function (e) {
                  f.Ia.fn[e] = function () {
                    var t = this();
                    return t[e].apply(t, arguments);
                  };
                }),
                (f.Oc = function (e) {
                  return f.N(e) && 'function' == typeof e.remove && 'function' == typeof e.push;
                }),
                f.b('observableArray', f.Ia),
                f.b('isObservableArray', f.Oc),
                (f.Ta.trackArrayChanges = function (e, t) {
                  function n() {
                    function t() {
                      if (u) {
                        var t = [].concat(e.w() || []);
                        if (e.Wa('arrayChange')) {
                          var n;
                          (!l || 1 < u) && (l = f.a.Ob(i, t, e.Nb)), (n = l);
                        }
                        (i = t),
                          (l = null),
                          (u = 0),
                          n && n.length && e.notifySubscribers(n, 'arrayChange');
                      }
                    }
                    s
                      ? t()
                      : ((s = !0),
                        (o = e.notifySubscribers),
                        (e.notifySubscribers = function (e, t) {
                          return (t && 'change' !== t) || ++u, o.apply(this, arguments);
                        }),
                        (i = [].concat(e.w() || [])),
                        (l = null),
                        (r = e.subscribe(t)));
                  }
                  if (
                    ((e.Nb = {}),
                    t && 'object' == typeof t && f.a.extend(e.Nb, t),
                    (e.Nb.sparse = !0),
                    !e.yc)
                  ) {
                    var r,
                      i,
                      o,
                      s = !1,
                      l = null,
                      u = 0,
                      c = e.Qa,
                      p = e.cb;
                    (e.Qa = function (t) {
                      c && c.call(e, t), 'arrayChange' === t && n();
                    }),
                      (e.cb = function (t) {
                        p && p.call(e, t),
                          'arrayChange' !== t ||
                            e.Wa('arrayChange') ||
                            (o && ((e.notifySubscribers = o), (o = a)),
                            r && r.s(),
                            (r = null),
                            (s = !1),
                            (i = a));
                      }),
                      (e.yc = function (e, t, n) {
                        function r(e, t, n) {
                          return (i[i.length] = { status: e, value: t, index: n });
                        }
                        if (s && !u) {
                          var i = [],
                            o = e.length,
                            a = n.length,
                            c = 0;
                          switch (t) {
                            case 'push':
                              c = o;
                            case 'unshift':
                              for (t = 0; t < a; t++) r('added', n[t], c + t);
                              break;
                            case 'pop':
                              c = o - 1;
                            case 'shift':
                              o && r('deleted', e[c], c);
                              break;
                            case 'splice':
                              t = Math.min(Math.max(0, 0 > n[0] ? o + n[0] : n[0]), o);
                              for (
                                var o = 1 === a ? o : Math.min(t + (n[1] || 0), o),
                                  a = t + a - 2,
                                  c = Math.max(o, a),
                                  p = [],
                                  d = [],
                                  h = 2;
                                t < c;
                                ++t, ++h
                              )
                                t < o && d.push(r('deleted', e[t], t)),
                                  t < a && p.push(r('added', n[h], t));
                              f.a.Jc(d, p);
                              break;
                            default:
                              return;
                          }
                          l = i;
                        }
                      });
                  }
                });
              var x = f.a.Da('_state');
              f.o = f.$ = function (e, t, n) {
                function r() {
                  if (0 < arguments.length) {
                    if ('function' != typeof i)
                      throw Error(
                        "Cannot write a value to a ko.computed unless you specify a 'write' option. If you wish to read the current value, don't pass any parameters."
                      );
                    return i.apply(o.lb, arguments), this;
                  }
                  return o.qa || f.v.ac(r), (o.ka || (o.G && r.Xa())) && r.ha(), o.X;
                }
                if (
                  ('object' == typeof e ? (n = e) : ((n = n || {}), e && (n.read = e)),
                  'function' != typeof n.read)
                )
                  throw Error('Pass a function that returns the value of the ko.computed');
                var i = n.write,
                  o = {
                    X: a,
                    ra: !0,
                    ka: !0,
                    pb: !1,
                    hc: !1,
                    qa: !1,
                    vb: !1,
                    G: !1,
                    Vc: n.read,
                    lb: t || n.owner,
                    l: n.disposeWhenNodeIsRemoved || n.l || null,
                    Sa: n.disposeWhen || n.Sa,
                    Qb: null,
                    F: {},
                    V: 0,
                    Hc: null,
                  };
                return (
                  (r[x] = o),
                  (r.Mc = 'function' == typeof i),
                  f.a.Ba || f.a.extend(r, f.R.fn),
                  f.R.fn.ob(r),
                  f.a.zb(r, C),
                  n.pure
                    ? ((o.vb = !0), (o.G = !0), f.a.extend(r, w))
                    : n.deferEvaluation && f.a.extend(r, P),
                  f.options.deferUpdates && f.Ta.deferred(r, !0),
                  o.l && ((o.hc = !0), o.l.nodeType || (o.l = null)),
                  o.G || n.deferEvaluation || r.ha(),
                  o.l &&
                    r.ja() &&
                    f.a.I.za(
                      o.l,
                      (o.Qb = function () {
                        r.s();
                      })
                    ),
                  r
                );
              };
              var C = {
                  equalityComparer: n,
                  pa: function () {
                    return this[x].V;
                  },
                  Va: function () {
                    var e = [];
                    return (
                      f.a.O(this[x].F, function (t, n) {
                        e[n.La] = n.da;
                      }),
                      e
                    );
                  },
                  Ub: function (e) {
                    if (!this[x].V) return !1;
                    var t = this.Va();
                    return (
                      -1 !== f.a.A(t, e) ||
                      !!f.a.Lb(t, function (t) {
                        return t.Ub && t.Ub(e);
                      })
                    );
                  },
                  tc: function (e, t, n) {
                    if (this[x].vb && t === this)
                      throw Error("A 'pure' computed must not be called recursively");
                    (this[x].F[e] = n), (n.La = this[x].V++), (n.Ma = t.mb());
                  },
                  Xa: function () {
                    var e,
                      t,
                      n = this[x].F;
                    for (e in n)
                      if (
                        Object.prototype.hasOwnProperty.call(n, e) &&
                        ((t = n[e]), (this.Ja && t.da.Ka) || t.da.Cd(t.Ma))
                      )
                        return !0;
                  },
                  Id: function () {
                    this.Ja && !this[x].pb && this.Ja(!1);
                  },
                  ja: function () {
                    var e = this[x];
                    return e.ka || 0 < e.V;
                  },
                  Qd: function () {
                    this.Ka ? this[x].ka && (this[x].ra = !0) : this.Gc();
                  },
                  Zc: function (e) {
                    if (e.Hb) {
                      var t = e.subscribe(this.Id, this, 'dirty'),
                        n = e.subscribe(this.Qd, this);
                      return {
                        da: e,
                        s: function () {
                          t.s(), n.s();
                        },
                      };
                    }
                    return e.subscribe(this.Gc, this);
                  },
                  Gc: function () {
                    var e = this,
                      t = e.throttleEvaluation;
                    t && 0 <= t
                      ? (clearTimeout(this[x].Hc),
                        (this[x].Hc = f.a.setTimeout(function () {
                          e.ha(!0);
                        }, t)))
                      : e.Ja
                      ? e.Ja(!0)
                      : e.ha(!0);
                  },
                  ha: function (e) {
                    var t = this[x],
                      n = t.Sa,
                      r = !1;
                    if (!t.pb && !t.qa) {
                      if ((t.l && !f.a.Rb(t.l)) || (n && n())) {
                        if (!t.hc) return void this.s();
                      } else t.hc = !1;
                      t.pb = !0;
                      try {
                        r = this.yd(e);
                      } finally {
                        t.pb = !1;
                      }
                      return r;
                    }
                  },
                  yd: function (e) {
                    var t = this[x],
                      n = !1,
                      r = t.vb ? a : !t.V,
                      n = { pd: this, kb: t.F, Pb: t.V };
                    f.v.wc({ od: n, nd: h, o: this, rb: r }), (t.F = {}), (t.V = 0);
                    var i = this.xd(t, n);
                    return (
                      t.V ? (n = this.qb(t.X, i)) : (this.s(), (n = !0)),
                      n &&
                        (t.G ? this.Gb() : this.notifySubscribers(t.X, 'beforeChange'),
                        (t.X = i),
                        this.notifySubscribers(t.X, 'spectate'),
                        !t.G && e && this.notifySubscribers(t.X),
                        this.qc && this.qc()),
                      r && this.notifySubscribers(t.X, 'awake'),
                      n
                    );
                  },
                  xd: function (e, t) {
                    try {
                      var n = e.Vc;
                      return e.lb ? n.call(e.lb) : n();
                    } finally {
                      f.v.end(), t.Pb && !e.G && f.a.O(t.kb, d), (e.ra = e.ka = !1);
                    }
                  },
                  w: function (e) {
                    var t = this[x];
                    return ((t.ka && (e || !t.V)) || (t.G && this.Xa())) && this.ha(), t.X;
                  },
                  tb: function (e) {
                    f.R.fn.tb.call(this, e),
                      (this.mc = function () {
                        return this[x].G || (this[x].ra ? this.ha() : (this[x].ka = !1)), this[x].X;
                      }),
                      (this.Ja = function (e) {
                        this.oc(this[x].X),
                          (this[x].ka = !0),
                          e && (this[x].ra = !0),
                          this.pc(this, !e);
                      });
                  },
                  s: function () {
                    var e = this[x];
                    !e.G &&
                      e.F &&
                      f.a.O(e.F, function (e, t) {
                        t.s && t.s();
                      }),
                      e.l && e.Qb && f.a.I.xb(e.l, e.Qb),
                      (e.F = a),
                      (e.V = 0),
                      (e.qa = !0),
                      (e.ra = !1),
                      (e.ka = !1),
                      (e.G = !1),
                      (e.l = a),
                      (e.Sa = a),
                      (e.Vc = a),
                      this.Mc || (e.lb = a);
                  },
                },
                w = {
                  Qa: function (e) {
                    var t = this,
                      n = t[x];
                    if (!n.qa && n.G && 'change' == e) {
                      if (((n.G = !1), n.ra || t.Xa())) (n.F = null), (n.V = 0), t.ha() && t.Gb();
                      else {
                        var r = [];
                        f.a.O(n.F, function (e, t) {
                          r[t.La] = e;
                        }),
                          f.a.C(r, function (e, r) {
                            var i = n.F[e],
                              o = t.Zc(i.da);
                            (o.La = r), (o.Ma = i.Ma), (n.F[e] = o);
                          }),
                          t.Xa() && t.ha() && t.Gb();
                      }
                      n.qa || t.notifySubscribers(n.X, 'awake');
                    }
                  },
                  cb: function (e) {
                    var t = this[x];
                    t.qa ||
                      'change' != e ||
                      this.Wa('change') ||
                      (f.a.O(t.F, function (e, n) {
                        n.s && ((t.F[e] = { da: n.da, La: n.La, Ma: n.Ma }), n.s());
                      }),
                      (t.G = !0),
                      this.notifySubscribers(a, 'asleep'));
                  },
                  mb: function () {
                    var e = this[x];
                    return e.G && (e.ra || this.Xa()) && this.ha(), f.R.fn.mb.call(this);
                  },
                },
                P = {
                  Qa: function (e) {
                    ('change' != e && 'beforeChange' != e) || this.w();
                  },
                };
              f.a.Ba && f.a.setPrototypeOf(C, f.R.fn);
              var V = f.sa.Na;
              (C[V] = f.o),
                (f.Nc = function (e) {
                  return 'function' == typeof e && e[V] === C[V];
                }),
                (f.Ed = function (e) {
                  return f.Nc(e) && e[x] && e[x].vb;
                }),
                f.b('computed', f.o),
                f.b('dependentObservable', f.o),
                f.b('isComputed', f.Nc),
                f.b('isPureComputed', f.Ed),
                f.b('computed.fn', C),
                f.J(C, 'peek', C.w),
                f.J(C, 'dispose', C.s),
                f.J(C, 'isActive', C.ja),
                f.J(C, 'getDependenciesCount', C.pa),
                f.J(C, 'getDependencies', C.Va),
                (f.wb = function (e, t) {
                  return 'function' == typeof e
                    ? f.o(e, t, { pure: !0 })
                    : ((e = f.a.extend({}, e)), (e.pure = !0), f.o(e, t));
                }),
                f.b('pureComputed', f.wb),
                (function () {
                  function e(r, i, o) {
                    if (
                      ((o = o || new n()),
                      'object' != typeof (r = i(r)) ||
                        null === r ||
                        r === a ||
                        r instanceof RegExp ||
                        r instanceof Date ||
                        r instanceof String ||
                        r instanceof Number ||
                        r instanceof Boolean)
                    )
                      return r;
                    var s = r instanceof Array ? [] : {};
                    return (
                      o.save(r, s),
                      t(r, function (t) {
                        var n = i(r[t]);
                        switch (typeof n) {
                          case 'boolean':
                          case 'number':
                          case 'string':
                          case 'function':
                            s[t] = n;
                            break;
                          case 'object':
                          case 'undefined':
                            var l = o.get(n);
                            s[t] = l !== a ? l : e(n, i, o);
                        }
                      }),
                      s
                    );
                  }
                  function t(e, t) {
                    if (e instanceof Array) {
                      for (var n = 0; n < e.length; n++) t(n);
                      'function' == typeof e.toJSON && t('toJSON');
                    } else for (n in e) t(n);
                  }
                  function n() {
                    (this.keys = []), (this.values = []);
                  }
                  (f.$c = function (t) {
                    if (0 == arguments.length)
                      throw Error('When calling ko.toJS, pass the object you want to convert.');
                    return e(t, function (e) {
                      for (var t = 0; f.N(e) && 10 > t; t++) e = e();
                      return e;
                    });
                  }),
                    (f.toJSON = function (e, t, n) {
                      return (e = f.$c(e)), f.a.fc(e, t, n);
                    }),
                    (n.prototype = {
                      constructor: n,
                      save: function (e, t) {
                        var n = f.a.A(this.keys, e);
                        0 <= n ? (this.values[n] = t) : (this.keys.push(e), this.values.push(t));
                      },
                      get: function (e) {
                        return (e = f.a.A(this.keys, e)), 0 <= e ? this.values[e] : a;
                      },
                    });
                })(),
                f.b('toJS', f.$c),
                f.b('toJSON', f.toJSON),
                (f.Vd = function (e, t, n) {
                  function r(t) {
                    var r = f.wb(e, n).extend({ Ga: 'always' }),
                      i = r.subscribe(function (e) {
                        e && (i.s(), t(e));
                      });
                    return r.notifySubscribers(r.w()), i;
                  }
                  return 'function' != typeof Promise || t ? r(t.bind(n)) : new Promise(r);
                }),
                f.b('when', f.Vd),
                (function () {
                  f.u = {
                    L: function (e) {
                      switch (f.a.P(e)) {
                        case 'option':
                          return !0 === e.__ko__hasDomDataOptionValue__
                            ? f.a.g.get(e, f.f.options.Yb)
                            : 7 >= f.a.W
                            ? e.getAttributeNode('value') && e.getAttributeNode('value').specified
                              ? e.value
                              : e.text
                            : e.value;
                        case 'select':
                          return 0 <= e.selectedIndex ? f.u.L(e.options[e.selectedIndex]) : a;
                        default:
                          return e.value;
                      }
                    },
                    ya: function (e, t, n) {
                      switch (f.a.P(e)) {
                        case 'option':
                          'string' == typeof t
                            ? (f.a.g.set(e, f.f.options.Yb, a),
                              '__ko__hasDomDataOptionValue__' in e &&
                                delete e.__ko__hasDomDataOptionValue__,
                              (e.value = t))
                            : (f.a.g.set(e, f.f.options.Yb, t),
                              (e.__ko__hasDomDataOptionValue__ = !0),
                              (e.value = 'number' == typeof t ? t : ''));
                          break;
                        case 'select':
                          ('' !== t && null !== t) || (t = a);
                          for (var r, i = -1, o = 0, s = e.options.length; o < s; ++o)
                            if ((r = f.u.L(e.options[o])) == t || ('' === r && t === a)) {
                              i = o;
                              break;
                            }
                          (n || 0 <= i || (t === a && 1 < e.size)) &&
                            ((e.selectedIndex = i),
                            6 === f.a.W &&
                              f.a.setTimeout(function () {
                                e.selectedIndex = i;
                              }, 0));
                          break;
                        default:
                          (null !== t && t !== a) || (t = ''), (e.value = t);
                      }
                    },
                  };
                })(),
                f.b('selectExtensions', f.u),
                f.b('selectExtensions.readValue', f.u.L),
                f.b('selectExtensions.writeValue', f.u.ya),
                (f.m = (function () {
                  function e(e) {
                    (e = f.a.Cb(e)), 123 === e.charCodeAt(0) && (e = e.slice(1, -1)), (e += '\n,');
                    var t,
                      n = [],
                      a = e.match(r),
                      s = [],
                      l = 0;
                    if (1 < a.length) {
                      for (var u, c = 0; (u = a[c]); ++c) {
                        var p = u.charCodeAt(0);
                        if (44 === p) {
                          if (0 >= l) {
                            n.push(
                              t && s.length
                                ? { key: t, value: s.join('') }
                                : { unknown: t || s.join('') }
                            ),
                              (t = l = 0),
                              (s = []);
                            continue;
                          }
                        } else if (58 === p) {
                          if (!l && !t && 1 === s.length) {
                            t = s.pop();
                            continue;
                          }
                        } else {
                          if (
                            47 === p &&
                            1 < u.length &&
                            (47 === u.charCodeAt(1) || 42 === u.charCodeAt(1))
                          )
                            continue;
                          47 === p && c && 1 < u.length
                            ? (p = a[c - 1].match(i)) &&
                              !o[p[0]] &&
                              ((e = e.substr(e.indexOf(u) + 1)),
                              (a = e.match(r)),
                              (c = -1),
                              (u = '/'))
                            : 40 === p || 123 === p || 91 === p
                            ? ++l
                            : 41 === p || 125 === p || 93 === p
                            ? --l
                            : t || s.length || (34 !== p && 39 !== p) || (u = u.slice(1, -1));
                        }
                        s.push(u);
                      }
                      if (0 < l) throw Error('Unbalanced parentheses, braces, or brackets');
                    }
                    return n;
                  }
                  var t = ['true', 'false', 'null', 'undefined'],
                    n = /^(?:[$_a-z][$\w]*|(.+)(\.\s*[$_a-z][$\w]*|\[.+\]))$/i,
                    r = RegExp(
                      '"(?:\\\\.|[^"])*"|\'(?:\\\\.|[^\'])*\'|`(?:\\\\.|[^`])*`|/\\*(?:[^*]|\\*+[^*/])*\\*+/|//.*\n|/(?:\\\\.|[^/])+/w*|[^\\s:,/][^,"\'`{}()/:[\\]]*[^\\s,"\'`{}()/:[\\]]|[^\\s]',
                      'g'
                    ),
                    i = /[\])"'A-Za-z0-9_$]+$/,
                    o = { in: 1, return: 1, typeof: 1 },
                    a = {};
                  return {
                    Ra: [],
                    va: a,
                    Zb: e,
                    ub: function (r, i) {
                      function o(e, r) {
                        var i;
                        if (!c) {
                          var p = f.getBindingHandler(e);
                          if (p && p.preprocess && !(r = p.preprocess(r, e, o))) return;
                          (p = a[e]) &&
                            ((i = r),
                            0 <= f.a.A(t, i)
                              ? (i = !1)
                              : ((p = i.match(n)),
                                (i = null !== p && (p[1] ? 'Object(' + p[1] + ')' + p[2] : i))),
                            (p = i)),
                            p &&
                              l.push(
                                "'" +
                                  ('string' == typeof a[e] ? a[e] : e) +
                                  "':function(_z){" +
                                  i +
                                  '=_z}'
                              );
                        }
                        u && (r = 'function(){return ' + r + ' }'), s.push("'" + e + "':" + r);
                      }
                      i = i || {};
                      var s = [],
                        l = [],
                        u = i.valueAccessors,
                        c = i.bindingParams,
                        p = 'string' == typeof r ? e(r) : r;
                      return (
                        f.a.C(p, function (e) {
                          o(e.key || e.unknown, e.value);
                        }),
                        l.length && o('_ko_property_writers', '{' + l.join(',') + ' }'),
                        s.join(',')
                      );
                    },
                    Hd: function (e, t) {
                      for (var n = 0; n < e.length; n++) if (e[n].key == t) return !0;
                      return !1;
                    },
                    $a: function (e, t, n, r, i) {
                      e && f.N(e)
                        ? !f.Ya(e) || (i && e.w() === r) || e(r)
                        : (e = t.get('_ko_property_writers')) && e[n] && e[n](r);
                    },
                  };
                })()),
                f.b('expressionRewriting', f.m),
                f.b('expressionRewriting.bindingRewriteValidators', f.m.Ra),
                f.b('expressionRewriting.parseObjectLiteral', f.m.Zb),
                f.b('expressionRewriting.preProcessBindings', f.m.ub),
                f.b('expressionRewriting._twoWayBindings', f.m.va),
                f.b('jsonExpressionRewriting', f.m),
                f.b('jsonExpressionRewriting.insertPropertyAccessorsIntoJson', f.m.ub),
                (function () {
                  function e(e) {
                    return 8 == e.nodeType && o.test(i ? e.text : e.nodeValue);
                  }
                  function t(e) {
                    return 8 == e.nodeType && a.test(i ? e.text : e.nodeValue);
                  }
                  function n(n, r) {
                    for (var i = n, o = 1, a = []; (i = i.nextSibling); ) {
                      if (t(i) && (f.a.g.set(i, u, !0), 0 === --o)) return a;
                      a.push(i), e(i) && o++;
                    }
                    if (!r) throw Error('Cannot find closing comment tag to match: ' + n.nodeValue);
                    return null;
                  }
                  function r(e, t) {
                    var r = n(e, t);
                    return r ? (0 < r.length ? r[r.length - 1].nextSibling : e.nextSibling) : null;
                  }
                  var i = l && '\x3c!--test--\x3e' === l.createComment('test').text,
                    o = i
                      ? /^\x3c!--\s*ko(?:\s+([\s\S]+))?\s*--\x3e$/
                      : /^\s*ko(?:\s+([\s\S]+))?\s*$/,
                    a = i ? /^\x3c!--\s*\/ko\s*--\x3e$/ : /^\s*\/ko\s*$/,
                    s = { ul: !0, ol: !0 },
                    u = '__ko_matchedEndComment__';
                  f.h = {
                    ea: {},
                    childNodes: function (t) {
                      return e(t) ? n(t) : t.childNodes;
                    },
                    Ea: function (t) {
                      if (e(t)) {
                        t = f.h.childNodes(t);
                        for (var n = 0, r = t.length; n < r; n++) f.removeNode(t[n]);
                      } else f.a.Sb(t);
                    },
                    ua: function (t, n) {
                      if (e(t)) {
                        f.h.Ea(t);
                        for (var r = t.nextSibling, i = 0, o = n.length; i < o; i++)
                          r.parentNode.insertBefore(n[i], r);
                      } else f.a.ua(t, n);
                    },
                    Uc: function (t, n) {
                      e(t)
                        ? t.parentNode.insertBefore(n, t.nextSibling)
                        : t.firstChild
                        ? t.insertBefore(n, t.firstChild)
                        : t.appendChild(n);
                    },
                    Vb: function (t, n, r) {
                      r
                        ? e(t)
                          ? t.parentNode.insertBefore(n, r.nextSibling)
                          : r.nextSibling
                          ? t.insertBefore(n, r.nextSibling)
                          : t.appendChild(n)
                        : f.h.Uc(t, n);
                    },
                    firstChild: function (n) {
                      if (e(n)) return !n.nextSibling || t(n.nextSibling) ? null : n.nextSibling;
                      if (n.firstChild && t(n.firstChild))
                        throw Error('Found invalid end comment, as the first child of ' + n);
                      return n.firstChild;
                    },
                    nextSibling: function (n) {
                      if ((e(n) && (n = r(n)), n.nextSibling && t(n.nextSibling))) {
                        var i = n.nextSibling;
                        if (t(i) && !f.a.g.get(i, u))
                          throw Error(
                            'Found end comment without a matching opening comment, as child of ' + n
                          );
                        return null;
                      }
                      return n.nextSibling;
                    },
                    Bd: e,
                    Ud: function (e) {
                      return (e = (i ? e.text : e.nodeValue).match(o)) ? e[1] : null;
                    },
                    Rc: function (n) {
                      if (s[f.a.P(n)]) {
                        var i = n.firstChild;
                        if (i)
                          do {
                            if (1 === i.nodeType) {
                              var o;
                              o = i.firstChild;
                              var a = null;
                              if (o)
                                do {
                                  if (a) a.push(o);
                                  else if (e(o)) {
                                    var l = r(o, !0);
                                    l ? (o = l) : (a = [o]);
                                  } else t(o) && (a = [o]);
                                } while ((o = o.nextSibling));
                              if ((o = a))
                                for (a = i.nextSibling, l = 0; l < o.length; l++)
                                  a ? n.insertBefore(o[l], a) : n.appendChild(o[l]);
                            }
                          } while ((i = i.nextSibling));
                      }
                    },
                  };
                })(),
                f.b('virtualElements', f.h),
                f.b('virtualElements.allowedBindings', f.h.ea),
                f.b('virtualElements.emptyNode', f.h.Ea),
                f.b('virtualElements.insertAfter', f.h.Vb),
                f.b('virtualElements.prepend', f.h.Uc),
                f.b('virtualElements.setDomNodeChildren', f.h.ua),
                (function () {
                  (f.ga = function () {
                    this.md = {};
                  }),
                    f.a.extend(f.ga.prototype, {
                      nodeHasBindings: function (e) {
                        switch (e.nodeType) {
                          case 1:
                            return (
                              null != e.getAttribute('data-bind') || f.i.getComponentNameForNode(e)
                            );
                          case 8:
                            return f.h.Bd(e);
                          default:
                            return !1;
                        }
                      },
                      getBindings: function (e, t) {
                        var n = this.getBindingsString(e, t),
                          n = n ? this.parseBindingsString(n, t, e) : null;
                        return f.i.sc(n, e, t, !1);
                      },
                      getBindingAccessors: function (e, t) {
                        var n = this.getBindingsString(e, t),
                          n = n ? this.parseBindingsString(n, t, e, { valueAccessors: !0 }) : null;
                        return f.i.sc(n, e, t, !0);
                      },
                      getBindingsString: function (e) {
                        switch (e.nodeType) {
                          case 1:
                            return e.getAttribute('data-bind');
                          case 8:
                            return f.h.Ud(e);
                          default:
                            return null;
                        }
                      },
                      parseBindingsString: function (e, t, n, r) {
                        try {
                          var i,
                            o = this.md,
                            a = e + ((r && r.valueAccessors) || '');
                          if (!(i = o[a])) {
                            var s,
                              l = 'with($context){with($data||{}){return{' + f.m.ub(e, r) + '}}}';
                            (s = new Function('$context', '$element', l)), (i = o[a] = s);
                          }
                          return i(t, n);
                        } catch (t) {
                          throw (
                            ((t.message =
                              'Unable to parse bindings.\nBindings value: ' +
                              e +
                              '\nMessage: ' +
                              t.message),
                            t)
                          );
                        }
                      },
                    }),
                    (f.ga.instance = new f.ga());
                })(),
                f.b('bindingProvider', f.ga),
                (function () {
                  function e(e) {
                    var t = (e = f.a.g.get(e, w)) && e.M;
                    t && ((e.M = null), t.Sc());
                  }
                  function t(t, n, r) {
                    (this.node = t),
                      (this.xc = n),
                      (this.ib = []),
                      (this.T = !1),
                      n.M || f.a.I.za(t, e),
                      r && r.M && (r.M.ib.push(t), (this.Kb = r));
                  }
                  function n(e) {
                    return function () {
                      return e;
                    };
                  }
                  function r(e) {
                    return e();
                  }
                  function i(e) {
                    return f.a.Ha(f.v.K(e), function (t, n) {
                      return function () {
                        return e()[n];
                      };
                    });
                  }
                  function o(e, t, r) {
                    return 'function' == typeof e ? i(e.bind(null, t, r)) : f.a.Ha(e, n);
                  }
                  function u(e, t) {
                    return i(this.getBindings.bind(this, e, t));
                  }
                  function p(e, t) {
                    var n = f.h.firstChild(t);
                    if (n) {
                      var r,
                        i = f.ga.instance,
                        o = i.preprocessNode;
                      if (o) {
                        for (; (r = n); ) (n = f.h.nextSibling(r)), o.call(i, r);
                        n = f.h.firstChild(t);
                      }
                      for (; (r = n); ) (n = f.h.nextSibling(r)), d(e, r);
                    }
                    f.j.Ga(t, f.j.T);
                  }
                  function d(e, t) {
                    var n = e,
                      r = 1 === t.nodeType;
                    r && f.h.Rc(t),
                      (r || f.ga.instance.nodeHasBindings(t)) &&
                        (n = m(t, null, e).bindingContextForDescendants),
                      n && !x[f.a.P(t)] && p(n, t);
                  }
                  function h(e) {
                    var t = [],
                      n = {},
                      r = [];
                    return (
                      f.a.O(e, function i(o) {
                        if (!n[o]) {
                          var a = f.getBindingHandler(o);
                          a &&
                            (a.after &&
                              (r.push(o),
                              f.a.C(a.after, function (t) {
                                if (e[t]) {
                                  if (-1 !== f.a.A(r, t))
                                    throw Error(
                                      'Cannot combine the following bindings, because they have a cyclic dependency: ' +
                                        r.join(', ')
                                    );
                                  i(t);
                                }
                              }),
                              r.length--),
                            t.push({ key: o, Lc: a })),
                            (n[o] = !0);
                        }
                      }),
                      t
                    );
                  }
                  function m(e, t, n) {
                    var i = f.a.g.Tb(e, w, {}),
                      o = i.gd;
                    if (!t) {
                      if (o)
                        throw Error(
                          'You cannot apply bindings multiple times to the same element.'
                        );
                      i.gd = !0;
                    }
                    o || (i.context = n);
                    var s;
                    if (t && 'function' != typeof t) s = t;
                    else {
                      var l = f.ga.instance,
                        c = l.getBindingAccessors || u,
                        p = f.$(
                          function () {
                            return (
                              (s = t ? t(n, e) : c.call(l, e, n)) &&
                                (n[y] && n[y](), n[b] && n[b]()),
                              s
                            );
                          },
                          null,
                          { l: e }
                        );
                      (s && p.ja()) || (p = null);
                    }
                    var d,
                      m = n;
                    if (s) {
                      var g = function () {
                          return f.a.Ha(p ? p() : s, r);
                        },
                        v = p
                          ? function (e) {
                              return function () {
                                return r(p()[e]);
                              };
                            }
                          : function (e) {
                              return s[e];
                            };
                      (g.get = function (e) {
                        return s[e] && r(v(e));
                      }),
                        (g.has = function (e) {
                          return e in s;
                        }),
                        f.j.T in s &&
                          f.j.subscribe(e, f.j.T, function () {
                            var t = (0, s[f.j.T])();
                            if (t) {
                              var n = f.h.childNodes(e);
                              n.length && t(n, f.Dc(n[0]));
                            }
                          }),
                        f.j.oa in s &&
                          ((m = f.j.Bb(e, n)),
                          f.j.subscribe(e, f.j.oa, function () {
                            var t = (0, s[f.j.oa])();
                            t && f.h.firstChild(e) && t(e);
                          })),
                        (i = h(s)),
                        f.a.C(i, function (t) {
                          var n = t.Lc.init,
                            r = t.Lc.update,
                            i = t.key;
                          if (8 === e.nodeType && !f.h.ea[i])
                            throw Error(
                              "The binding '" + i + "' cannot be used with virtual elements"
                            );
                          try {
                            'function' == typeof n &&
                              f.v.K(function () {
                                var t = n(e, v(i), g, m.$data, m);
                                if (t && t.controlsDescendantBindings) {
                                  if (d !== a)
                                    throw Error(
                                      'Multiple bindings (' +
                                        d +
                                        ' and ' +
                                        i +
                                        ') are trying to control descendant bindings of the same element. You cannot use these bindings together on the same element.'
                                    );
                                  d = i;
                                }
                              }),
                              'function' == typeof r &&
                                f.$(
                                  function () {
                                    r(e, v(i), g, m.$data, m);
                                  },
                                  null,
                                  { l: e }
                                );
                          } catch (e) {
                            throw (
                              ((e.message =
                                'Unable to process binding "' +
                                i +
                                ': ' +
                                s[i] +
                                '"\nMessage: ' +
                                e.message),
                              e)
                            );
                          }
                        });
                    }
                    return (
                      (i = d === a),
                      { shouldBindDescendants: i, bindingContextForDescendants: i && m }
                    );
                  }
                  function g(e, t) {
                    return e && e instanceof f.fa ? e : new f.fa(e, a, a, t);
                  }
                  var y = f.a.Da('_subscribable'),
                    v = f.a.Da('_ancestorBindingInfo'),
                    b = f.a.Da('_dataDependency');
                  f.f = {};
                  var x = { script: !0, textarea: !0, template: !0 };
                  f.getBindingHandler = function (e) {
                    return f.f[e];
                  };
                  var C = {};
                  (f.fa = function (e, t, n, r, i) {
                    function o() {
                      var e = p ? c() : c,
                        i = f.a.c(e);
                      return (
                        t
                          ? (f.a.extend(l, t), v in t && (l[v] = t[v]))
                          : ((l.$parents = []), (l.$root = i), (l.ko = f)),
                        (l[y] = s),
                        u ? (i = l.$data) : ((l.$rawData = e), (l.$data = i)),
                        n && (l[n] = i),
                        r && r(l, t, i),
                        t && t[y] && !f.U.o().Ub(t[y]) && t[y](),
                        d && (l[b] = d),
                        l.$data
                      );
                    }
                    var s,
                      l = this,
                      u = e === C,
                      c = u ? a : e,
                      p = 'function' == typeof c && !f.N(c),
                      d = i && i.dataDependency;
                    i && i.exportDependencies
                      ? o()
                      : ((s = f.wb(o)), s.w(), s.ja() ? (s.equalityComparer = null) : (l[y] = a));
                  }),
                    (f.fa.prototype.createChildContext = function (e, t, n, r) {
                      if (
                        (!r && t && 'object' == typeof t && ((r = t), (t = r.as), (n = r.extend)),
                        t && r && r.noChildContext)
                      ) {
                        var i = 'function' == typeof e && !f.N(e);
                        return new f.fa(
                          C,
                          this,
                          null,
                          function (r) {
                            n && n(r), (r[t] = i ? e() : e);
                          },
                          r
                        );
                      }
                      return new f.fa(
                        e,
                        this,
                        t,
                        function (e, t) {
                          (e.$parentContext = t),
                            (e.$parent = t.$data),
                            (e.$parents = (t.$parents || []).slice(0)),
                            e.$parents.unshift(e.$parent),
                            n && n(e);
                        },
                        r
                      );
                    }),
                    (f.fa.prototype.extend = function (e, t) {
                      return new f.fa(
                        C,
                        this,
                        null,
                        function (t) {
                          f.a.extend(t, 'function' == typeof e ? e(t) : e);
                        },
                        t
                      );
                    });
                  var w = f.a.g.Z();
                  (t.prototype.Sc = function () {
                    this.Kb && this.Kb.M && this.Kb.M.rd(this.node);
                  }),
                    (t.prototype.rd = function (e) {
                      f.a.hb(this.ib, e), !this.ib.length && this.T && this.Bc();
                    }),
                    (t.prototype.Bc = function () {
                      (this.T = !0),
                        this.xc.M &&
                          !this.ib.length &&
                          ((this.xc.M = null),
                          f.a.I.xb(this.node, e),
                          f.j.Ga(this.node, f.j.oa),
                          this.Sc());
                    }),
                    (f.j = {
                      T: 'childrenComplete',
                      oa: 'descendantsComplete',
                      subscribe: function (e, t, n, r) {
                        return (
                          (e = f.a.g.Tb(e, w, {})),
                          e.Fa || (e.Fa = new f.R()),
                          e.Fa.subscribe(n, r, t)
                        );
                      },
                      Ga: function (e, t) {
                        var n = f.a.g.get(e, w);
                        if (n && (n.Fa && n.Fa.notifySubscribers(e, t), t == f.j.T))
                          if (n.M) n.M.Bc();
                          else if (n.M === a && n.Fa && n.Fa.Wa(f.j.oa))
                            throw Error(
                              'descendantsComplete event not supported for bindings on this node'
                            );
                      },
                      Bb: function (e, n) {
                        var r = f.a.g.Tb(e, w, {});
                        return (
                          r.M || (r.M = new t(e, r, n[v])),
                          n[v] == r
                            ? n
                            : n.extend(function (e) {
                                e[v] = r;
                              })
                        );
                      },
                    }),
                    (f.Sd = function (e) {
                      return (e = f.a.g.get(e, w)) && e.context;
                    }),
                    (f.eb = function (e, t, n) {
                      return 1 === e.nodeType && f.h.Rc(e), m(e, t, g(n));
                    }),
                    (f.kd = function (e, t, n) {
                      return (n = g(n)), f.eb(e, o(t, n, e), n);
                    }),
                    (f.Pa = function (e, t) {
                      (1 !== t.nodeType && 8 !== t.nodeType) || p(g(e), t);
                    }),
                    (f.uc = function (e, t, n) {
                      if ((!c && s.jQuery && (c = s.jQuery), 2 > arguments.length)) {
                        if (!(t = l.body))
                          throw Error(
                            'ko.applyBindings: could not find document.body; has the document been loaded?'
                          );
                      } else if (!t || (1 !== t.nodeType && 8 !== t.nodeType))
                        throw Error(
                          'ko.applyBindings: first parameter should be your view model; second parameter should be a DOM node'
                        );
                      d(g(e, n), t);
                    }),
                    (f.Cc = function (e) {
                      return !e || (1 !== e.nodeType && 8 !== e.nodeType) ? a : f.Sd(e);
                    }),
                    (f.Dc = function (e) {
                      return (e = f.Cc(e)) ? e.$data : a;
                    }),
                    f.b('bindingHandlers', f.f),
                    f.b('bindingEvent', f.j),
                    f.b('bindingEvent.subscribe', f.j.subscribe),
                    f.b('bindingEvent.startPossiblyAsyncContentBinding', f.j.Bb),
                    f.b('applyBindings', f.uc),
                    f.b('applyBindingsToDescendants', f.Pa),
                    f.b('applyBindingAccessorsToNode', f.eb),
                    f.b('applyBindingsToNode', f.kd),
                    f.b('contextFor', f.Cc),
                    f.b('dataFor', f.Dc);
                })(),
                (function (e) {
                  function t(t, r) {
                    var a,
                      s = Object.prototype.hasOwnProperty.call(i, t) ? i[t] : e;
                    s
                      ? s.subscribe(r)
                      : ((s = i[t] = new f.R()),
                        s.subscribe(r),
                        n(t, function (e, n) {
                          var r = !(!n || !n.synchronous);
                          (o[t] = { definition: e, Fd: r }),
                            delete i[t],
                            a || r
                              ? s.notifySubscribers(e)
                              : f.ma.yb(function () {
                                  s.notifySubscribers(e);
                                });
                        }),
                        (a = !0));
                  }
                  function n(e, t) {
                    r('getConfig', [e], function (n) {
                      n
                        ? r('loadComponent', [e, n], function (e) {
                            t(e, n);
                          })
                        : t(null, null);
                    });
                  }
                  function r(t, n, i, o) {
                    o || (o = f.i.loaders.slice(0));
                    var a = o.shift();
                    if (a) {
                      var s = a[t];
                      if (s) {
                        var l = !1;
                        if (
                          s.apply(
                            a,
                            n.concat(function (e) {
                              l ? i(null) : null !== e ? i(e) : r(t, n, i, o);
                            })
                          ) !== e &&
                          ((l = !0), !a.suppressLoaderExceptions)
                        )
                          throw Error(
                            'Component loaders must supply values by invoking the callback, not by returning values synchronously.'
                          );
                      } else r(t, n, i, o);
                    } else i(null);
                  }
                  var i = {},
                    o = {};
                  (f.i = {
                    get: function (n, r) {
                      var i = Object.prototype.hasOwnProperty.call(o, n) ? o[n] : e;
                      i
                        ? i.Fd
                          ? f.v.K(function () {
                              r(i.definition);
                            })
                          : f.ma.yb(function () {
                              r(i.definition);
                            })
                        : t(n, r);
                    },
                    Ac: function (e) {
                      delete o[e];
                    },
                    nc: r,
                  }),
                    (f.i.loaders = []),
                    f.b('components', f.i),
                    f.b('components.get', f.i.get),
                    f.b('components.clearCachedDefinition', f.i.Ac);
                })(),
                (function () {
                  function e(e, t, n, r) {
                    function i() {
                      0 == --s && r(a);
                    }
                    var a = {},
                      s = 2,
                      l = n.template;
                    (n = n.viewModel),
                      l
                        ? o(t, l, function (t) {
                            f.i.nc('loadTemplate', [e, t], function (e) {
                              (a.template = e), i();
                            });
                          })
                        : i(),
                      n
                        ? o(t, n, function (t) {
                            f.i.nc('loadViewModel', [e, t], function (e) {
                              (a[c] = e), i();
                            });
                          })
                        : i();
                  }
                  function n(e, t, r) {
                    if ('function' == typeof t)
                      r(function (e) {
                        return new t(e);
                      });
                    else if ('function' == typeof t[c]) r(t[c]);
                    else if ('instance' in t) {
                      var i = t.instance;
                      r(function () {
                        return i;
                      });
                    } else
                      'viewModel' in t ? n(e, t.viewModel, r) : e('Unknown viewModel value: ' + t);
                  }
                  function r(e) {
                    switch (f.a.P(e)) {
                      case 'script':
                        return f.a.ta(e.text);
                      case 'textarea':
                        return f.a.ta(e.value);
                      case 'template':
                        if (i(e.content)) return f.a.Ca(e.content.childNodes);
                    }
                    return f.a.Ca(e.childNodes);
                  }
                  function i(e) {
                    return s.DocumentFragment
                      ? e instanceof DocumentFragment
                      : e && 11 === e.nodeType;
                  }
                  function o(e, n, r) {
                    'string' == typeof n.require
                      ? t || s.require
                        ? (t || s.require)([n.require], r)
                        : e('Uses require, but no AMD loader is present')
                      : r(n);
                  }
                  function a(e) {
                    return function (t) {
                      throw Error("Component '" + e + "': " + t);
                    };
                  }
                  var u = {};
                  (f.i.register = function (e, t) {
                    if (!t) throw Error('Invalid configuration for ' + e);
                    if (f.i.sb(e)) throw Error('Component ' + e + ' is already registered');
                    u[e] = t;
                  }),
                    (f.i.sb = function (e) {
                      return Object.prototype.hasOwnProperty.call(u, e);
                    }),
                    (f.i.unregister = function (e) {
                      delete u[e], f.i.Ac(e);
                    }),
                    (f.i.Ec = {
                      getConfig: function (e, t) {
                        t(f.i.sb(e) ? u[e] : null);
                      },
                      loadComponent: function (t, n, r) {
                        var i = a(t);
                        o(i, n, function (n) {
                          e(t, i, n, r);
                        });
                      },
                      loadTemplate: function (e, t, n) {
                        if (((e = a(e)), 'string' == typeof t)) n(f.a.ta(t));
                        else if (t instanceof Array) n(t);
                        else if (i(t)) n(f.a.la(t.childNodes));
                        else if (t.element)
                          if (
                            ((t = t.element),
                            s.HTMLElement
                              ? t instanceof HTMLElement
                              : t && t.tagName && 1 === t.nodeType)
                          )
                            n(r(t));
                          else if ('string' == typeof t) {
                            var o = l.getElementById(t);
                            o ? n(r(o)) : e('Cannot find element with ID ' + t);
                          } else e('Unknown element type: ' + t);
                        else e('Unknown template value: ' + t);
                      },
                      loadViewModel: function (e, t, r) {
                        n(a(e), t, r);
                      },
                    });
                  var c = 'createViewModel';
                  f.b('components.register', f.i.register),
                    f.b('components.isRegistered', f.i.sb),
                    f.b('components.unregister', f.i.unregister),
                    f.b('components.defaultLoader', f.i.Ec),
                    f.i.loaders.push(f.i.Ec),
                    (f.i.cd = u);
                })(),
                (function () {
                  function e(e, n) {
                    var r = e.getAttribute('params');
                    if (r) {
                      var r = t.parseBindingsString(r, n, e, {
                          valueAccessors: !0,
                          bindingParams: !0,
                        }),
                        r = f.a.Ha(r, function (t) {
                          return f.o(t, null, { l: e });
                        }),
                        i = f.a.Ha(r, function (t) {
                          var n = t.w();
                          return t.ja()
                            ? f.o({
                                read: function () {
                                  return f.a.c(t());
                                },
                                write:
                                  f.Ya(n) &&
                                  function (e) {
                                    t()(e);
                                  },
                                l: e,
                              })
                            : n;
                        });
                      return Object.prototype.hasOwnProperty.call(i, '$raw') || (i.$raw = r), i;
                    }
                    return { $raw: {} };
                  }
                  (f.i.getComponentNameForNode = function (e) {
                    var t = f.a.P(e);
                    if (
                      f.i.sb(t) &&
                      (-1 != t.indexOf('-') ||
                        '[object HTMLUnknownElement]' == '' + e ||
                        (8 >= f.a.W && e.tagName === t))
                    )
                      return t;
                  }),
                    (f.i.sc = function (t, n, r, i) {
                      if (1 === n.nodeType) {
                        var o = f.i.getComponentNameForNode(n);
                        if (o) {
                          if (((t = t || {}), t.component))
                            throw Error(
                              'Cannot use the "component" binding on a custom element matching a component'
                            );
                          var a = { name: o, params: e(n, r) };
                          t.component = i
                            ? function () {
                                return a;
                              }
                            : a;
                        }
                      }
                      return t;
                    });
                  var t = new f.ga();
                  9 > f.a.W &&
                    ((f.i.register = (function (e) {
                      return function (t) {
                        return e.apply(this, arguments);
                      };
                    })(f.i.register)),
                    (l.createDocumentFragment = (function (e) {
                      return function () {
                        var t,
                          n = e(),
                          r = f.i.cd;
                        for (t in r);
                        return n;
                      };
                    })(l.createDocumentFragment)));
                })(),
                (function () {
                  function e(e, t, n) {
                    if (!(t = t.template)) throw Error("Component '" + e + "' has no template");
                    (e = f.a.Ca(t)), f.h.ua(n, e);
                  }
                  function t(e, t, n) {
                    var r = e.createViewModel;
                    return r ? r.call(e, t, n) : t;
                  }
                  var n = 0;
                  (f.f.component = {
                    init: function (r, i, o, a, s) {
                      function l() {
                        var e = u && u.dispose;
                        'function' == typeof e && e.call(u), p && p.s(), (c = u = p = null);
                      }
                      var u,
                        c,
                        p,
                        d = f.a.la(f.h.childNodes(r));
                      return (
                        f.h.Ea(r),
                        f.a.I.za(r, l),
                        f.o(
                          function () {
                            var o,
                              a,
                              h = f.a.c(i());
                            if (
                              ('string' == typeof h
                                ? (o = h)
                                : ((o = f.a.c(h.name)), (a = f.a.c(h.params))),
                              !o)
                            )
                              throw Error('No component name specified');
                            var m = f.j.Bb(r, s),
                              g = (c = ++n);
                            f.i.get(o, function (n) {
                              if (c === g) {
                                if ((l(), !n)) throw Error("Unknown component '" + o + "'");
                                e(o, n, r);
                                var i = t(n, a, { element: r, templateNodes: d });
                                (n = m.createChildContext(i, {
                                  extend: function (e) {
                                    (e.$component = i), (e.$componentTemplateNodes = d);
                                  },
                                })),
                                  i &&
                                    i.koDescendantsComplete &&
                                    (p = f.j.subscribe(r, f.j.oa, i.koDescendantsComplete, i)),
                                  (u = i),
                                  f.Pa(n, r);
                              }
                            });
                          },
                          null,
                          { l: r }
                        ),
                        { controlsDescendantBindings: !0 }
                      );
                    },
                  }),
                    (f.h.ea.component = !0);
                })();
              var k = { class: 'className', for: 'htmlFor' };
              (f.f.attr = {
                update: function (e, t) {
                  var n = f.a.c(t()) || {};
                  f.a.O(n, function (t, n) {
                    n = f.a.c(n);
                    var r = t.indexOf(':'),
                      r =
                        'lookupNamespaceURI' in e && 0 < r && e.lookupNamespaceURI(t.substr(0, r)),
                      i = !1 === n || null === n || n === a;
                    i ? (r ? e.removeAttributeNS(r, t) : e.removeAttribute(t)) : (n = n.toString()),
                      8 >= f.a.W && t in k
                        ? ((t = k[t]), i ? e.removeAttribute(t) : (e[t] = n))
                        : i || (r ? e.setAttributeNS(r, t, n) : e.setAttribute(t, n)),
                      'name' === t && f.a.Xc(e, i ? '' : n);
                  });
                },
              }),
                (function () {
                  (f.f.checked = {
                    after: ['value', 'attr'],
                    init: function (e, t, n) {
                      function r() {
                        var r = e.checked,
                          i = o();
                        if (!f.U.rb() && (r || (!l && !f.U.pa()))) {
                          var u = f.v.K(t);
                          if (c) {
                            var d = p ? u.w() : u,
                              m = h;
                            (h = i),
                              m !== i ? r && (f.a.Oa(d, i, !0), f.a.Oa(d, m, !1)) : f.a.Oa(d, i, r),
                              p && f.Ya(u) && u(d);
                          } else
                            s && (i === a ? (i = r) : r || (i = a)), f.m.$a(u, n, 'checked', i, !0);
                        }
                      }
                      function i() {
                        var n = f.a.c(t()),
                          r = o();
                        c
                          ? ((e.checked = 0 <= f.a.A(n, r)), (h = r))
                          : (e.checked = s && r === a ? !!n : o() === n);
                      }
                      var o = f.wb(function () {
                          return n.has('checkedValue')
                            ? f.a.c(n.get('checkedValue'))
                            : d
                            ? n.has('value')
                              ? f.a.c(n.get('value'))
                              : e.value
                            : void 0;
                        }),
                        s = 'checkbox' == e.type,
                        l = 'radio' == e.type;
                      if (s || l) {
                        var u = t(),
                          c = s && f.a.c(u) instanceof Array,
                          p = !(c && u.push && u.splice),
                          d = l || c,
                          h = c ? o() : a;
                        l &&
                          !e.name &&
                          f.f.uniqueName.init(e, function () {
                            return !0;
                          }),
                          f.o(r, null, { l: e }),
                          f.a.H(e, 'click', r),
                          f.o(i, null, { l: e }),
                          (u = a);
                      }
                    },
                  }),
                    (f.m.va.checked = !0),
                    (f.f.checkedValue = {
                      update: function (e, t) {
                        e.value = f.a.c(t());
                      },
                    });
                })(),
                (f.f.class = {
                  update: function (e, t) {
                    var n = f.a.Cb(f.a.c(t()));
                    f.a.Eb(e, e.__ko__cssValue, !1), (e.__ko__cssValue = n), f.a.Eb(e, n, !0);
                  },
                }),
                (f.f.css = {
                  update: function (e, t) {
                    var n = f.a.c(t());
                    null !== n && 'object' == typeof n
                      ? f.a.O(n, function (t, n) {
                          (n = f.a.c(n)), f.a.Eb(e, t, n);
                        })
                      : f.f.class.update(e, t);
                  },
                }),
                (f.f.enable = {
                  update: function (e, t) {
                    var n = f.a.c(t());
                    n && e.disabled
                      ? e.removeAttribute('disabled')
                      : n || e.disabled || (e.disabled = !0);
                  },
                }),
                (f.f.disable = {
                  update: function (e, t) {
                    f.f.enable.update(e, function () {
                      return !f.a.c(t());
                    });
                  },
                }),
                (f.f.event = {
                  init: function (e, t, n, r, i) {
                    var o = t() || {};
                    f.a.O(o, function (o) {
                      'string' == typeof o &&
                        f.a.H(e, o, function (e) {
                          var a,
                            s = t()[o];
                          if (s) {
                            try {
                              var l = f.a.la(arguments);
                              (r = i.$data), l.unshift(r), (a = s.apply(r, l));
                            } finally {
                              !0 !== a &&
                                (e.preventDefault ? e.preventDefault() : (e.returnValue = !1));
                            }
                            !1 === n.get(o + 'Bubble') &&
                              ((e.cancelBubble = !0), e.stopPropagation && e.stopPropagation());
                          }
                        });
                    });
                  },
                }),
                (f.f.foreach = {
                  Qc: function (e) {
                    return function () {
                      var t = e(),
                        n = f.a.$b(t);
                      return n && 'number' != typeof n.length
                        ? (f.a.c(t),
                          {
                            foreach: n.data,
                            as: n.as,
                            noChildContext: n.noChildContext,
                            includeDestroyed: n.includeDestroyed,
                            afterAdd: n.afterAdd,
                            beforeRemove: n.beforeRemove,
                            afterRender: n.afterRender,
                            beforeMove: n.beforeMove,
                            afterMove: n.afterMove,
                            templateEngine: f.ba.Na,
                          })
                        : { foreach: t, templateEngine: f.ba.Na };
                    };
                  },
                  init: function (e, t) {
                    return f.f.template.init(e, f.f.foreach.Qc(t));
                  },
                  update: function (e, t, n, r, i) {
                    return f.f.template.update(e, f.f.foreach.Qc(t), n, r, i);
                  },
                }),
                (f.m.Ra.foreach = !1),
                (f.h.ea.foreach = !0),
                (f.f.hasfocus = {
                  init: function (e, t, n) {
                    function r(r) {
                      e.__ko_hasfocusUpdating = !0;
                      var i = e.ownerDocument;
                      if ('activeElement' in i) {
                        var o;
                        try {
                          o = i.activeElement;
                        } catch (e) {
                          o = i.body;
                        }
                        r = o === e;
                      }
                      (i = t()),
                        f.m.$a(i, n, 'hasfocus', r, !0),
                        (e.__ko_hasfocusLastValue = r),
                        (e.__ko_hasfocusUpdating = !1);
                    }
                    var i = r.bind(null, !0),
                      o = r.bind(null, !1);
                    f.a.H(e, 'focus', i),
                      f.a.H(e, 'focusin', i),
                      f.a.H(e, 'blur', o),
                      f.a.H(e, 'focusout', o),
                      (e.__ko_hasfocusLastValue = !1);
                  },
                  update: function (e, t) {
                    var n = !!f.a.c(t());
                    e.__ko_hasfocusUpdating ||
                      e.__ko_hasfocusLastValue === n ||
                      (n ? e.focus() : e.blur(),
                      !n && e.__ko_hasfocusLastValue && e.ownerDocument.body.focus(),
                      f.v.K(f.a.Fb, null, [e, n ? 'focusin' : 'focusout']));
                  },
                }),
                (f.m.va.hasfocus = !0),
                (f.f.hasFocus = f.f.hasfocus),
                (f.m.va.hasFocus = 'hasfocus'),
                (f.f.html = {
                  init: function () {
                    return { controlsDescendantBindings: !0 };
                  },
                  update: function (e, t) {
                    f.a.dc(e, t());
                  },
                }),
                (function () {
                  function e(e, t, n) {
                    (f.f[e] = {
                      init: function (e, r, i, o, a) {
                        var s,
                          l,
                          u,
                          c,
                          p,
                          d = {};
                        if (t) {
                          o = i.get('as');
                          var h = i.get('noChildContext');
                          (p = !(o && h)),
                            (d = { as: o, noChildContext: h, exportDependencies: p });
                        }
                        return (
                          (c = (u = 'render' == i.get('completeOn')) || i.has(f.j.oa)),
                          f.o(
                            function () {
                              var i,
                                o = f.a.c(r()),
                                h = !n != !o,
                                m = !l;
                              (p || h !== s) &&
                                (c && (a = f.j.Bb(e, a)),
                                h &&
                                  ((t && !p) || (d.dataDependency = f.U.o()),
                                  (i = t
                                    ? a.createChildContext('function' == typeof o ? o : r, d)
                                    : f.U.pa()
                                    ? a.extend(null, d)
                                    : a)),
                                m && f.U.pa() && (l = f.a.Ca(f.h.childNodes(e), !0)),
                                h
                                  ? (m || f.h.ua(e, f.a.Ca(l)), f.Pa(i, e))
                                  : (f.h.Ea(e), u || f.j.Ga(e, f.j.T)),
                                (s = h));
                            },
                            null,
                            { l: e }
                          ),
                          { controlsDescendantBindings: !0 }
                        );
                      },
                    }),
                      (f.m.Ra[e] = !1),
                      (f.h.ea[e] = !0);
                  }
                  e('if'), e('ifnot', !1, !0), e('with', !0);
                })(),
                (f.f.let = {
                  init: function (e, t, n, r, i) {
                    return (t = i.extend(t)), f.Pa(t, e), { controlsDescendantBindings: !0 };
                  },
                }),
                (f.h.ea.let = !0);
              var _ = {};
              (f.f.options = {
                init: function (e) {
                  if ('select' !== f.a.P(e))
                    throw Error('options binding applies only to SELECT elements');
                  for (; 0 < e.length; ) e.remove(0);
                  return { controlsDescendantBindings: !0 };
                },
                update: function (e, t, n) {
                  function r() {
                    return f.a.fb(e.options, function (e) {
                      return e.selected;
                    });
                  }
                  function i(e, t, n) {
                    var r = typeof t;
                    return 'function' == r ? t(e) : 'string' == r ? e[t] : n;
                  }
                  function o(t, r) {
                    if (m && c) f.u.ya(e, f.a.c(n.get('value')), !0);
                    else if (h.length) {
                      var i = 0 <= f.a.A(h, f.u.L(r[0]));
                      f.a.Yc(r[0], i), m && !i && f.v.K(f.a.Fb, null, [e, 'change']);
                    }
                  }
                  var s = e.multiple,
                    l = 0 != e.length && s ? e.scrollTop : null,
                    u = f.a.c(t()),
                    c = n.get('valueAllowUnset') && n.has('value'),
                    p = n.get('optionsIncludeDestroyed');
                  t = {};
                  var d,
                    h = [];
                  c ||
                    (s
                      ? (h = f.a.Mb(r(), f.u.L))
                      : 0 <= e.selectedIndex && h.push(f.u.L(e.options[e.selectedIndex]))),
                    u &&
                      (void 0 === u.length && (u = [u]),
                      (d = f.a.fb(u, function (e) {
                        return p || e === a || null === e || !f.a.c(e._destroy);
                      })),
                      n.has('optionsCaption') &&
                        null !== (u = f.a.c(n.get('optionsCaption'))) &&
                        u !== a &&
                        d.unshift(_));
                  var m = !1;
                  (t.beforeRemove = function (t) {
                    e.removeChild(t);
                  }),
                    (u = o),
                    n.has('optionsAfterRender') &&
                      'function' == typeof n.get('optionsAfterRender') &&
                      (u = function (e, t) {
                        o(0, t), f.v.K(n.get('optionsAfterRender'), null, [t[0], e !== _ ? e : a]);
                      }),
                    f.a.cc(
                      e,
                      d,
                      function (t, r, o) {
                        return (
                          o.length && ((h = !c && o[0].selected ? [f.u.L(o[0])] : []), (m = !0)),
                          (r = e.ownerDocument.createElement('option')),
                          t === _
                            ? (f.a.Ab(r, n.get('optionsCaption')), f.u.ya(r, a))
                            : ((o = i(t, n.get('optionsValue'), t)),
                              f.u.ya(r, f.a.c(o)),
                              (t = i(t, n.get('optionsText'), o)),
                              f.a.Ab(r, t)),
                          [r]
                        );
                      },
                      t,
                      u
                    ),
                    f.v.K(function () {
                      if (c) f.u.ya(e, f.a.c(n.get('value')), !0);
                      else {
                        var t;
                        (t = s
                          ? h.length && r().length < h.length
                          : h.length && 0 <= e.selectedIndex
                          ? f.u.L(e.options[e.selectedIndex]) !== h[0]
                          : h.length || 0 <= e.selectedIndex),
                          t && f.a.Fb(e, 'change');
                      }
                    }),
                    f.a.vd(e),
                    l && 20 < Math.abs(l - e.scrollTop) && (e.scrollTop = l);
                },
              }),
                (f.f.options.Yb = f.a.g.Z()),
                (f.f.selectedOptions = {
                  after: ['options', 'foreach'],
                  init: function (e, t, n) {
                    f.a.H(e, 'change', function () {
                      var r = t(),
                        i = [];
                      f.a.C(e.getElementsByTagName('option'), function (e) {
                        e.selected && i.push(f.u.L(e));
                      }),
                        f.m.$a(r, n, 'selectedOptions', i);
                    });
                  },
                  update: function (e, t) {
                    if ('select' != f.a.P(e))
                      throw Error('values binding applies only to SELECT elements');
                    var n = f.a.c(t()),
                      r = e.scrollTop;
                    n &&
                      'number' == typeof n.length &&
                      f.a.C(e.getElementsByTagName('option'), function (e) {
                        var t = 0 <= f.a.A(n, f.u.L(e));
                        e.selected != t && f.a.Yc(e, t);
                      }),
                      (e.scrollTop = r);
                  },
                }),
                (f.m.va.selectedOptions = !0),
                (f.f.style = {
                  update: function (e, t) {
                    var n = f.a.c(t() || {});
                    f.a.O(n, function (t, n) {
                      if (((n = f.a.c(n)), (null !== n && n !== a && !1 !== n) || (n = ''), c))
                        c(e).css(t, n);
                      else if (/^--/.test(t)) e.style.setProperty(t, n);
                      else {
                        t = t.replace(/-(\w)/g, function (e, t) {
                          return t.toUpperCase();
                        });
                        var r = e.style[t];
                        (e.style[t] = n),
                          n === r || e.style[t] != r || isNaN(n) || (e.style[t] = n + 'px');
                      }
                    });
                  },
                }),
                (f.f.submit = {
                  init: function (e, t, n, r, i) {
                    if ('function' != typeof t())
                      throw Error('The value for a submit binding must be a function');
                    f.a.H(e, 'submit', function (n) {
                      var r,
                        o = t();
                      try {
                        r = o.call(i.$data, e);
                      } finally {
                        !0 !== r && (n.preventDefault ? n.preventDefault() : (n.returnValue = !1));
                      }
                    });
                  },
                }),
                (f.f.text = {
                  init: function () {
                    return { controlsDescendantBindings: !0 };
                  },
                  update: function (e, t) {
                    f.a.Ab(e, t());
                  },
                }),
                (f.h.ea.text = !0),
                (function () {
                  if (s && s.navigator) {
                    var e,
                      t,
                      n,
                      r,
                      i,
                      o = function (e) {
                        if (e) return parseFloat(e[1]);
                      },
                      l = s.navigator.userAgent;
                    (e = s.opera && s.opera.version && parseInt(s.opera.version())) ||
                      (i = o(l.match(/Edge\/([^ ]+)$/))) ||
                      o(l.match(/Chrome\/([^ ]+)/)) ||
                      (t = o(l.match(/Version\/([^ ]+) Safari/))) ||
                      (n = o(l.match(/Firefox\/([^ ]+)/))) ||
                      (r = f.a.W || o(l.match(/MSIE ([^ ]+)/))) ||
                      (r = o(l.match(/rv:([^ )]+)/)));
                  }
                  if (8 <= r && 10 > r)
                    var u = f.a.g.Z(),
                      c = f.a.g.Z(),
                      p = function (e) {
                        var t = this.activeElement;
                        (t = t && f.a.g.get(t, c)) && t(e);
                      },
                      d = function (e, t) {
                        var n = e.ownerDocument;
                        f.a.g.get(n, u) || (f.a.g.set(n, u, !0), f.a.H(n, 'selectionchange', p)),
                          f.a.g.set(e, c, t);
                      };
                  (f.f.textInput = {
                    init: function (o, s, l) {
                      function u(e, t) {
                        f.a.H(o, e, t);
                      }
                      function c() {
                        var e = f.a.c(s());
                        (null !== e && e !== a) || (e = ''),
                          g !== a && e === g
                            ? f.a.setTimeout(c, 4)
                            : o.value !== e && ((b = !0), (o.value = e), (b = !1), (y = o.value));
                      }
                      function p() {
                        m || ((g = o.value), (m = f.a.setTimeout(h, 4)));
                      }
                      function h() {
                        clearTimeout(m), (g = m = a);
                        var e = o.value;
                        y !== e && ((y = e), f.m.$a(s(), l, 'textInput', e));
                      }
                      var m,
                        g,
                        y = o.value,
                        v = 9 == f.a.W ? p : h,
                        b = !1;
                      r && u('keypress', h),
                        11 > r &&
                          u('propertychange', function (e) {
                            b || 'value' !== e.propertyName || v(e);
                          }),
                        8 == r && (u('keyup', h), u('keydown', h)),
                        d && (d(o, v), u('dragend', p)),
                        (!r || 9 <= r) && u('input', v),
                        5 > t && 'textarea' === f.a.P(o)
                          ? (u('keydown', p), u('paste', p), u('cut', p))
                          : 11 > e
                          ? u('keydown', p)
                          : 4 > n
                          ? (u('DOMAutoComplete', h), u('dragdrop', h), u('drop', h))
                          : i && 'number' === o.type && u('keydown', p),
                        u('change', h),
                        u('blur', h),
                        f.o(c, null, { l: o });
                    },
                  }),
                    (f.m.va.textInput = !0),
                    (f.f.textinput = {
                      preprocess: function (e, t, n) {
                        n('textInput', e);
                      },
                    });
                })(),
                (f.f.uniqueName = {
                  init: function (e, t) {
                    if (t()) {
                      var n = 'ko_unique_' + ++f.f.uniqueName.qd;
                      f.a.Xc(e, n);
                    }
                  },
                }),
                (f.f.uniqueName.qd = 0),
                (f.f.using = {
                  init: function (e, t, n, r, i) {
                    var o;
                    return (
                      n.has('as') &&
                        (o = { as: n.get('as'), noChildContext: n.get('noChildContext') }),
                      (t = i.createChildContext(t, o)),
                      f.Pa(t, e),
                      { controlsDescendantBindings: !0 }
                    );
                  },
                }),
                (f.h.ea.using = !0),
                (f.f.value = {
                  after: ['options', 'foreach'],
                  init: function (e, t, n) {
                    var r = f.a.P(e),
                      i = 'input' == r;
                    if (!i || ('checkbox' != e.type && 'radio' != e.type)) {
                      var o = ['change'],
                        s = n.get('valueUpdate'),
                        l = !1,
                        u = null;
                      s && ('string' == typeof s && (s = [s]), f.a.gb(o, s), (o = f.a.vc(o)));
                      var c = function () {
                        (u = null), (l = !1);
                        var r = t(),
                          i = f.u.L(e);
                        f.m.$a(r, n, 'value', i);
                      };
                      !f.a.W ||
                        !i ||
                        'text' != e.type ||
                        'off' == e.autocomplete ||
                        (e.form && 'off' == e.form.autocomplete) ||
                        -1 != f.a.A(o, 'propertychange') ||
                        (f.a.H(e, 'propertychange', function () {
                          l = !0;
                        }),
                        f.a.H(e, 'focus', function () {
                          l = !1;
                        }),
                        f.a.H(e, 'blur', function () {
                          l && c();
                        })),
                        f.a.C(o, function (t) {
                          var n = c;
                          f.a.Td(t, 'after') &&
                            ((n = function () {
                              (u = f.u.L(e)), f.a.setTimeout(c, 0);
                            }),
                            (t = t.substring(5))),
                            f.a.H(e, t, n);
                        });
                      var p;
                      (p =
                        i && 'file' == e.type
                          ? function () {
                              var n = f.a.c(t());
                              null === n || n === a || '' === n ? (e.value = '') : f.v.K(c);
                            }
                          : function () {
                              var i = f.a.c(t()),
                                o = f.u.L(e);
                              null !== u && i === u
                                ? f.a.setTimeout(p, 0)
                                : (i === o && o !== a) ||
                                  ('select' === r
                                    ? ((o = n.get('valueAllowUnset')),
                                      f.u.ya(e, i, o),
                                      o || i === f.u.L(e) || f.v.K(c))
                                    : f.u.ya(e, i));
                            }),
                        f.o(p, null, { l: e });
                    } else f.eb(e, { checkedValue: t });
                  },
                  update: function () {},
                }),
                (f.m.va.value = !0),
                (f.f.visible = {
                  update: function (e, t) {
                    var n = f.a.c(t()),
                      r = 'none' != e.style.display;
                    n && !r ? (e.style.display = '') : !n && r && (e.style.display = 'none');
                  },
                }),
                (f.f.hidden = {
                  update: function (e, t) {
                    f.f.visible.update(e, function () {
                      return !f.a.c(t());
                    });
                  },
                }),
                (function (e) {
                  f.f[e] = {
                    init: function (t, n, r, i, o) {
                      return f.f.event.init.call(
                        this,
                        t,
                        function () {
                          var t = {};
                          return (t[e] = n()), t;
                        },
                        r,
                        i,
                        o
                      );
                    },
                  };
                })('click'),
                (f.ca = function () {}),
                (f.ca.prototype.renderTemplateSource = function () {
                  throw Error('Override renderTemplateSource');
                }),
                (f.ca.prototype.createJavaScriptEvaluatorBlock = function () {
                  throw Error('Override createJavaScriptEvaluatorBlock');
                }),
                (f.ca.prototype.makeTemplateSource = function (e, t) {
                  if ('string' == typeof e) {
                    t = t || l;
                    var n = t.getElementById(e);
                    if (!n) throw Error('Cannot find template with ID ' + e);
                    return new f.B.D(n);
                  }
                  if (1 == e.nodeType || 8 == e.nodeType) return new f.B.ia(e);
                  throw Error('Unknown template type: ' + e);
                }),
                (f.ca.prototype.renderTemplate = function (e, t, n, r) {
                  return (e = this.makeTemplateSource(e, r)), this.renderTemplateSource(e, t, n, r);
                }),
                (f.ca.prototype.isTemplateRewritten = function (e, t) {
                  return (
                    !1 === this.allowTemplateRewriting ||
                    this.makeTemplateSource(e, t).data('isRewritten')
                  );
                }),
                (f.ca.prototype.rewriteTemplate = function (e, t, n) {
                  (e = this.makeTemplateSource(e, n)),
                    (t = t(e.text())),
                    e.text(t),
                    e.data('isRewritten', !0);
                }),
                f.b('templateEngine', f.ca),
                (f.ic = (function () {
                  function e(e, t, n, r) {
                    e = f.m.Zb(e);
                    for (var i = f.m.Ra, o = 0; o < e.length; o++) {
                      var a = e[o].key;
                      if (Object.prototype.hasOwnProperty.call(i, a)) {
                        var s = i[a];
                        if ('function' == typeof s) {
                          if ((a = s(e[o].value))) throw Error(a);
                        } else if (!s)
                          throw Error(
                            "This template engine does not support the '" +
                              a +
                              "' binding within its templates"
                          );
                      }
                    }
                    return (
                      (n =
                        'ko.__tr_ambtns(function($context,$element){return(function(){return{ ' +
                        f.m.ub(e, { valueAccessors: !0 }) +
                        " } })()},'" +
                        n.toLowerCase() +
                        "')"),
                      r.createJavaScriptEvaluatorBlock(n) + t
                    );
                  }
                  var t = /(<([a-z]+\d*)(?:\s+(?!data-bind\s*=\s*)[a-z0-9\-]+(?:=(?:\"[^\"]*\"|\'[^\']*\'|[^>]*))?)*\s+)data-bind\s*=\s*(["'])([\s\S]*?)\3/gi,
                    n = /\x3c!--\s*ko\b\s*([\s\S]*?)\s*--\x3e/g;
                  return {
                    wd: function (e, t, n) {
                      t.isTemplateRewritten(e, n) ||
                        t.rewriteTemplate(
                          e,
                          function (e) {
                            return f.ic.Kd(e, t);
                          },
                          n
                        );
                    },
                    Kd: function (r, i) {
                      return r
                        .replace(t, function (t, n, r, o, a) {
                          return e(a, n, r, i);
                        })
                        .replace(n, function (t, n) {
                          return e(n, '\x3c!-- ko --\x3e', '#comment', i);
                        });
                    },
                    ld: function (e, t) {
                      return f.aa.Wb(function (n, r) {
                        var i = n.nextSibling;
                        i && i.nodeName.toLowerCase() === t && f.eb(i, e, r);
                      });
                    },
                  };
                })()),
                f.b('__tr_ambtns', f.ic.ld),
                (function () {
                  (f.B = {}),
                    (f.B.D = function (e) {
                      if ((this.D = e)) {
                        var t = f.a.P(e);
                        this.Db =
                          'script' === t
                            ? 1
                            : 'textarea' === t
                            ? 2
                            : 'template' == t && e.content && 11 === e.content.nodeType
                            ? 3
                            : 4;
                      }
                    }),
                    (f.B.D.prototype.text = function () {
                      var e = 1 === this.Db ? 'text' : 2 === this.Db ? 'value' : 'innerHTML';
                      if (0 == arguments.length) return this.D[e];
                      var t = arguments[0];
                      'innerHTML' === e ? f.a.dc(this.D, t) : (this.D[e] = t);
                    });
                  var e = f.a.g.Z() + '_';
                  f.B.D.prototype.data = function (t) {
                    if (1 === arguments.length) return f.a.g.get(this.D, e + t);
                    f.a.g.set(this.D, e + t, arguments[1]);
                  };
                  var t = f.a.g.Z();
                  (f.B.D.prototype.nodes = function () {
                    var e = this.D;
                    if (0 == arguments.length) {
                      var n = f.a.g.get(e, t) || {},
                        r = n.jb || (3 === this.Db ? e.content : 4 === this.Db ? e : a);
                      return (
                        (r && !n.hd) ||
                          ((n = this.text()) &&
                            ((r = f.a.Ld(n, e.ownerDocument)),
                            this.text(''),
                            f.a.g.set(e, t, { jb: r, hd: !0 }))),
                        r
                      );
                    }
                    f.a.g.set(e, t, { jb: arguments[0] });
                  }),
                    (f.B.ia = function (e) {
                      this.D = e;
                    }),
                    (f.B.ia.prototype = new f.B.D()),
                    (f.B.ia.prototype.constructor = f.B.ia),
                    (f.B.ia.prototype.text = function () {
                      if (0 == arguments.length) {
                        var e = f.a.g.get(this.D, t) || {};
                        return e.jc === a && e.jb && (e.jc = e.jb.innerHTML), e.jc;
                      }
                      f.a.g.set(this.D, t, { jc: arguments[0] });
                    }),
                    f.b('templateSources', f.B),
                    f.b('templateSources.domElement', f.B.D),
                    f.b('templateSources.anonymousTemplate', f.B.ia);
                })(),
                (function () {
                  function e(e, t, n) {
                    var r;
                    for (t = f.h.nextSibling(t); e && (r = e) !== t; )
                      (e = f.h.nextSibling(r)), n(r, e);
                  }
                  function t(t, n) {
                    if (t.length) {
                      var r = t[0],
                        i = t[t.length - 1],
                        o = r.parentNode,
                        a = f.ga.instance,
                        s = a.preprocessNode;
                      if (s) {
                        if (
                          (e(r, i, function (e, t) {
                            var n = e.previousSibling,
                              o = s.call(a, e);
                            o &&
                              (e === r && (r = o[0] || t), e === i && (i = o[o.length - 1] || n));
                          }),
                          (t.length = 0),
                          !r)
                        )
                          return;
                        r === i ? t.push(r) : (t.push(r, i), f.a.Ua(t, o));
                      }
                      e(r, i, function (e) {
                        (1 !== e.nodeType && 8 !== e.nodeType) || f.uc(n, e);
                      }),
                        e(r, i, function (e) {
                          (1 !== e.nodeType && 8 !== e.nodeType) || f.aa.bd(e, [n]);
                        }),
                        f.a.Ua(t, o);
                    }
                  }
                  function n(e) {
                    return e.nodeType ? e : 0 < e.length ? e[0] : null;
                  }
                  function r(e, r, i, a, s) {
                    s = s || {};
                    var l = ((e && n(e)) || i || {}).ownerDocument,
                      u = s.templateEngine || o;
                    if (
                      (f.ic.wd(i, u, l),
                      (i = u.renderTemplate(i, a, s, l)),
                      'number' != typeof i.length ||
                        (0 < i.length && 'number' != typeof i[0].nodeType))
                    )
                      throw Error('Template engine must return an array of DOM nodes');
                    switch (((l = !1), r)) {
                      case 'replaceChildren':
                        f.h.ua(e, i), (l = !0);
                        break;
                      case 'replaceNode':
                        f.a.Wc(e, i), (l = !0);
                        break;
                      case 'ignoreTargetNode':
                        break;
                      default:
                        throw Error('Unknown renderMode: ' + r);
                    }
                    return (
                      l &&
                        (t(i, a),
                        s.afterRender && f.v.K(s.afterRender, null, [i, a[s.as || '$data']]),
                        'replaceChildren' == r && f.j.Ga(e, f.j.T)),
                      i
                    );
                  }
                  function i(e, t, n) {
                    return f.N(e) ? e() : 'function' == typeof e ? e(t, n) : e;
                  }
                  var o;
                  (f.ec = function (e) {
                    if (e != a && !(e instanceof f.ca))
                      throw Error('templateEngine must inherit from ko.templateEngine');
                    o = e;
                  }),
                    (f.bc = function (e, t, s, l, u) {
                      if (((s = s || {}), (s.templateEngine || o) == a))
                        throw Error('Set a template engine before calling renderTemplate');
                      if (((u = u || 'replaceChildren'), l)) {
                        var c = n(l);
                        return f.$(
                          function () {
                            var o =
                                t && t instanceof f.fa
                                  ? t
                                  : new f.fa(t, null, null, null, { exportDependencies: !0 }),
                              a = i(e, o.$data, o),
                              o = r(l, u, a, o, s);
                            'replaceNode' == u && ((l = o), (c = n(l)));
                          },
                          null,
                          {
                            Sa: function () {
                              return !c || !f.a.Rb(c);
                            },
                            l: c && 'replaceNode' == u ? c.parentNode : c,
                          }
                        );
                      }
                      return f.aa.Wb(function (n) {
                        f.bc(e, t, s, n, 'replaceNode');
                      });
                    }),
                    (f.Pd = function (e, n, o, s, l) {
                      function u(e, t) {
                        f.v.K(f.a.cc, null, [s, e, p, o, c, t]), f.j.Ga(s, f.j.T);
                      }
                      function c(e, n) {
                        t(n, d), o.afterRender && o.afterRender(n, e), (d = null);
                      }
                      function p(t, n) {
                        d = l.createChildContext(t, {
                          as: h,
                          noChildContext: o.noChildContext,
                          extend: function (e) {
                            (e.$index = n), h && (e[h + 'Index'] = n);
                          },
                        });
                        var a = i(e, t, d);
                        return r(s, 'ignoreTargetNode', a, d, o);
                      }
                      var d,
                        h = o.as,
                        m =
                          !1 === o.includeDestroyed ||
                          (f.options.foreachHidesDestroyed && !o.includeDestroyed);
                      if (m || o.beforeRemove || !f.Oc(n))
                        return f.$(
                          function () {
                            var e = f.a.c(n) || [];
                            void 0 === e.length && (e = [e]),
                              m &&
                                (e = f.a.fb(e, function (e) {
                                  return e === a || null === e || !f.a.c(e._destroy);
                                })),
                              u(e);
                          },
                          null,
                          { l: s }
                        );
                      u(n.w());
                      var g = n.subscribe(
                        function (e) {
                          u(n(), e);
                        },
                        null,
                        'arrayChange'
                      );
                      return g.l(s), g;
                    });
                  var s = f.a.g.Z(),
                    l = f.a.g.Z();
                  (f.f.template = {
                    init: function (e, t) {
                      var n = f.a.c(t());
                      if ('string' == typeof n || n.name) f.h.Ea(e);
                      else if ('nodes' in n) {
                        if (((n = n.nodes || []), f.N(n)))
                          throw Error('The "nodes" option must be a plain, non-observable array.');
                        var r = n[0] && n[0].parentNode;
                        (r && f.a.g.get(r, l)) || ((r = f.a.Xb(n)), f.a.g.set(r, l, !0)),
                          new f.B.ia(e).nodes(r);
                      } else {
                        if (((n = f.h.childNodes(e)), !(0 < n.length)))
                          throw Error(
                            'Anonymous template defined, but no template content was provided'
                          );
                        (r = f.a.Xb(n)), new f.B.ia(e).nodes(r);
                      }
                      return { controlsDescendantBindings: !0 };
                    },
                    update: function (e, t, n, r, i) {
                      var o = t();
                      (t = f.a.c(o)),
                        (n = !0),
                        (r = null),
                        'string' == typeof t
                          ? (t = {})
                          : ((o = t.name),
                            'if' in t && (n = f.a.c(t.if)),
                            n && 'ifnot' in t && (n = !f.a.c(t.ifnot))),
                        'foreach' in t
                          ? (r = f.Pd(o || e, (n && t.foreach) || [], t, e, i))
                          : n
                          ? ((n = i),
                            'data' in t &&
                              (n = i.createChildContext(t.data, {
                                as: t.as,
                                noChildContext: t.noChildContext,
                                exportDependencies: !0,
                              })),
                            (r = f.bc(o || e, n, t, e)))
                          : f.h.Ea(e),
                        (i = r),
                        (t = f.a.g.get(e, s)) && 'function' == typeof t.s && t.s(),
                        f.a.g.set(e, s, !i || (i.ja && !i.ja()) ? a : i);
                    },
                  }),
                    (f.m.Ra.template = function (e) {
                      return (
                        (e = f.m.Zb(e)),
                        (1 == e.length && e[0].unknown) || f.m.Hd(e, 'name')
                          ? null
                          : 'This template engine does not support anonymous templates nested within its templates'
                      );
                    }),
                    (f.h.ea.template = !0);
                })(),
                f.b('setTemplateEngine', f.ec),
                f.b('renderTemplate', f.bc),
                (f.a.Jc = function (e, t, n) {
                  if (e.length && t.length) {
                    var r, i, o, a, s;
                    for (r = i = 0; (!n || r < n) && (a = e[i]); ++i) {
                      for (o = 0; (s = t[o]); ++o)
                        if (a.value === s.value) {
                          (a.moved = s.index), (s.moved = a.index), t.splice(o, 1), (r = o = 0);
                          break;
                        }
                      r += o;
                    }
                  }
                }),
                (f.a.Ob = (function () {
                  function e(e, t, n, r, i) {
                    var o,
                      a,
                      s,
                      l,
                      u,
                      c = Math.min,
                      p = Math.max,
                      d = [],
                      h = e.length,
                      m = t.length,
                      g = m - h || 1,
                      y = h + m + 1;
                    for (o = 0; o <= h; o++)
                      for (l = s, d.push((s = [])), u = c(m, o + g), a = p(0, o - 1); a <= u; a++)
                        s[a] = a
                          ? o
                            ? e[o - 1] === t[a - 1]
                              ? l[a - 1]
                              : c(l[a] || y, s[a - 1] || y) + 1
                            : a + 1
                          : o + 1;
                    for (c = [], p = [], g = [], o = h, a = m; o || a; )
                      (m = d[o][a] - 1),
                        a && m === d[o][a - 1]
                          ? p.push((c[c.length] = { status: n, value: t[--a], index: a }))
                          : o && m === d[o - 1][a]
                          ? g.push((c[c.length] = { status: r, value: e[--o], index: o }))
                          : (--a, --o, i.sparse || c.push({ status: 'retained', value: t[a] }));
                    return f.a.Jc(g, p, !i.dontLimitMoves && 10 * h), c.reverse();
                  }
                  return function (t, n, r) {
                    return (
                      (r = 'boolean' == typeof r ? { dontLimitMoves: r } : r || {}),
                      (t = t || []),
                      (n = n || []),
                      t.length < n.length
                        ? e(t, n, 'added', 'deleted', r)
                        : e(n, t, 'deleted', 'added', r)
                    );
                  };
                })()),
                f.b('utils.compareArrays', f.a.Ob),
                (function () {
                  function e(e, t, n, r, i) {
                    var o = [],
                      s = f.$(
                        function () {
                          var a = t(n, i, f.a.Ua(o, e)) || [];
                          0 < o.length && (f.a.Wc(o, a), r && f.v.K(r, null, [n, a, i])),
                            (o.length = 0),
                            f.a.gb(o, a);
                        },
                        null,
                        {
                          l: e,
                          Sa: function () {
                            return !f.a.jd(o);
                          },
                        }
                      );
                    return { Y: o, $: s.ja() ? s : a };
                  }
                  var t = f.a.g.Z(),
                    n = f.a.g.Z();
                  f.a.cc = function (r, i, o, s, l, u) {
                    function c(e) {
                      (h = { Aa: e, nb: f.sa(b++) }), y.push(h), g || V.push(h);
                    }
                    function p(e) {
                      (h = m[e]), b !== h.nb.w() && P.push(h), h.nb(b++), f.a.Ua(h.Y, r), y.push(h);
                    }
                    function d(e, t) {
                      if (e)
                        for (var n = 0, r = t.length; n < r; n++)
                          f.a.C(t[n].Y, function (r) {
                            e(r, n, t[n].Aa);
                          });
                    }
                    (i = i || []), void 0 === i.length && (i = [i]), (s = s || {});
                    var h,
                      m = f.a.g.get(r, t),
                      g = !m,
                      y = [],
                      v = 0,
                      b = 0,
                      x = [],
                      C = [],
                      w = [],
                      P = [],
                      V = [],
                      k = 0;
                    if (g) f.a.C(i, c);
                    else {
                      if (!u || (m && m._countWaitingForRemove)) {
                        var _ = f.a.Mb(m, function (e) {
                          return e.Aa;
                        });
                        u = f.a.Ob(_, i, { dontLimitMoves: s.dontLimitMoves, sparse: !0 });
                      }
                      for (var T, S, R, _ = 0; (T = u[_]); _++)
                        switch (((S = T.moved), (R = T.index), T.status)) {
                          case 'deleted':
                            for (; v < R; ) p(v++);
                            S === a &&
                              ((h = m[v]),
                              h.$ && (h.$.s(), (h.$ = a)),
                              f.a.Ua(h.Y, r).length &&
                                (s.beforeRemove &&
                                  (y.push(h), k++, h.Aa === n ? (h = null) : w.push(h)),
                                h && x.push.apply(x, h.Y))),
                              v++;
                            break;
                          case 'added':
                            for (; b < R; ) p(v++);
                            S !== a ? (C.push(y.length), p(S)) : c(T.value);
                        }
                      for (; b < i.length; ) p(v++);
                      y._countWaitingForRemove = k;
                    }
                    f.a.g.set(r, t, y),
                      d(s.beforeMove, P),
                      f.a.C(x, s.beforeRemove ? f.na : f.removeNode);
                    var q, E, O;
                    try {
                      O = r.ownerDocument.activeElement;
                    } catch (e) {}
                    if (C.length)
                      for (; (_ = C.shift()) != a; ) {
                        for (h = y[_], q = a; _; )
                          if ((E = y[--_].Y) && E.length) {
                            q = E[E.length - 1];
                            break;
                          }
                        for (i = 0; (v = h.Y[i]); q = v, i++) f.h.Vb(r, v, q);
                      }
                    for (_ = 0, C = f.h.firstChild(r); (h = y[_]); _++) {
                      for (
                        h.Y || f.a.extend(h, e(r, o, h.Aa, l, h.nb)), i = 0;
                        (v = h.Y[i]);
                        C = v.nextSibling, q = v, i++
                      )
                        v !== C && f.h.Vb(r, v, q);
                      !h.Dd && l && (l(h.Aa, h.Y, h.nb), (h.Dd = !0), (q = h.Y[h.Y.length - 1]));
                    }
                    for (
                      O && r.ownerDocument.activeElement != O && O.focus(),
                        d(s.beforeRemove, w),
                        _ = 0;
                      _ < w.length;
                      ++_
                    )
                      w[_].Aa = n;
                    d(s.afterMove, P), d(s.afterAdd, V);
                  };
                })(),
                f.b('utils.setDomNodeChildrenFromArrayMapping', f.a.cc),
                (f.ba = function () {
                  this.allowTemplateRewriting = !1;
                }),
                (f.ba.prototype = new f.ca()),
                (f.ba.prototype.constructor = f.ba),
                (f.ba.prototype.renderTemplateSource = function (e, t, n, r) {
                  return (t = (9 > f.a.W ? 0 : e.nodes) ? e.nodes() : null)
                    ? f.a.la(t.cloneNode(!0).childNodes)
                    : ((e = e.text()), f.a.ta(e, r));
                }),
                (f.ba.Na = new f.ba()),
                f.ec(f.ba.Na),
                f.b('nativeTemplateEngine', f.ba),
                (function () {
                  (f.Za = function () {
                    var e = (this.Gd = (function () {
                      if (!c || !c.tmpl) return 0;
                      try {
                        if (0 <= c.tmpl.tag.tmpl.open.toString().indexOf('__')) return 2;
                      } catch (e) {}
                      return 1;
                    })());
                    (this.renderTemplateSource = function (t, n, r, i) {
                      if (((i = i || l), (r = r || {}), 2 > e))
                        throw Error(
                          'Your version of jQuery.tmpl is too old. Please upgrade to jQuery.tmpl 1.0.0pre or later.'
                        );
                      var o = t.data('precompiled');
                      return (
                        o ||
                          ((o = t.text() || ''),
                          (o = c.template(
                            null,
                            '{{ko_with $item.koBindingContext}}' + o + '{{/ko_with}}'
                          )),
                          t.data('precompiled', o)),
                        (t = [n.$data]),
                        (n = c.extend({ koBindingContext: n }, r.templateOptions)),
                        (n = c.tmpl(o, t, n)),
                        n.appendTo(i.createElement('div')),
                        (c.fragments = {}),
                        n
                      );
                    }),
                      (this.createJavaScriptEvaluatorBlock = function (e) {
                        return '{{ko_code ((function() { return ' + e + ' })()) }}';
                      }),
                      (this.addTemplate = function (e, t) {
                        l.write("<script type='text/html' id='" + e + "'>" + t + '</script>');
                      }),
                      0 < e &&
                        ((c.tmpl.tag.ko_code = { open: "__.push($1 || '');" }),
                        (c.tmpl.tag.ko_with = { open: 'with($1) {', close: '} ' }));
                  }),
                    (f.Za.prototype = new f.ca()),
                    (f.Za.prototype.constructor = f.Za);
                  var e = new f.Za();
                  0 < e.Gd && f.ec(e), f.b('jqueryTmplTemplateEngine', f.Za);
                })();
            });
        })();
      })();
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(13),
        o = n(4),
        a = n(1),
        s = n(7),
        l = n(19);
      n.d(t, 'b', function () {
        return c;
      }),
        n.d(t, 'd', function () {
          return d;
        }),
        n.d(t, 'a', function () {
          return h;
        }),
        n.d(t, 'c', function () {
          return f;
        });
      var u = (function () {
          function e(e) {
            (this.obj = e), (this.properties = null), (this.values = null);
          }
          return (
            (e.prototype.getType = function () {
              return 'bindings';
            }),
            (e.prototype.getNames = function () {
              var e = [];
              this.fillProperties();
              for (var t = 0; t < this.properties.length; t++)
                this.properties[t].isVisible('', this.obj) && e.push(this.properties[t].name);
              return e;
            }),
            (e.prototype.getProperties = function () {
              var e = [];
              this.fillProperties();
              for (var t = 0; t < this.properties.length; t++) e.push(this.properties[t]);
              return e;
            }),
            (e.prototype.setBinding = function (e, t) {
              this.values || (this.values = {}),
                t
                  ? (this.values[e] = t)
                  : (delete this.values[e],
                    0 == Object.keys(this.values).length && (this.values = null));
            }),
            (e.prototype.clearBinding = function (e) {
              this.setBinding(e, '');
            }),
            (e.prototype.isEmpty = function () {
              return !this.values;
            }),
            (e.prototype.getValueNameByPropertyName = function (e) {
              if (this.values) return this.values[e];
            }),
            (e.prototype.getPropertiesByValueName = function (e) {
              if (!this.values) return [];
              var t = [];
              for (var n in this.values) this.values[n] == e && t.push(n);
              return t;
            }),
            (e.prototype.getJson = function () {
              if (this.isEmpty()) return null;
              var e = {};
              for (var t in this.values) e[t] = this.values[t];
              return e;
            }),
            (e.prototype.setJson = function (e) {
              if (((this.values = null), e)) {
                this.values = {};
                for (var t in e) this.values[t] = e[t];
              }
            }),
            (e.prototype.fillProperties = function () {
              if (null === this.properties) {
                this.properties = [];
                for (var e = a.b.getPropertiesByObj(this.obj), t = 0; t < e.length; t++)
                  e[t].isBindable && this.properties.push(e[t]);
              }
            }),
            e
          );
        })(),
        c = (function () {
          function e() {
            (this.propertyHash = {}),
              (this.isLoadingFromJsonValue = !1),
              (this.loadingOwner = null),
              (this.onPropertyChanged = new f()),
              (this.onItemValuePropertyChanged = new f()),
              (this.isCreating = !0),
              (this.bindingsValue = new u(this)),
              a.a.createProperties(this),
              this.onBaseCreating(),
              (this.isCreating = !1);
          }
          return (
            (e.isSurveyElement = function (t) {
              return (
                !!t &&
                (Array.isArray(t)
                  ? 0 != t.length && e.isSurveyElement(t[0])
                  : !!t.getType && !!t.onPropertyChanged)
              );
            }),
            Object.defineProperty(e, 'commentPrefix', {
              get: function () {
                return s.a.commentPrefix;
              },
              set: function (e) {
                s.a.commentPrefix = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.isValueEmpty = function (e) {
              return o.a.isValueEmpty(e);
            }),
            (e.prototype.IsPropertyEmpty = function (e) {
              return '' !== e && this.isValueEmpty(e);
            }),
            (e.prototype.onBaseCreating = function () {}),
            (e.prototype.getType = function () {
              return 'base';
            }),
            Object.defineProperty(e.prototype, 'bindings', {
              get: function () {
                return this.bindingsValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.checkBindings = function (e, t) {}),
            (e.prototype.updateBindings = function (e, t) {
              var n = this.bindings.getValueNameByPropertyName(e);
              n && this.updateBindingValue(n, t);
            }),
            (e.prototype.updateBindingValue = function (e, t) {}),
            (e.prototype.getTemplate = function () {
              return this.getType();
            }),
            Object.defineProperty(e.prototype, 'isLoadingFromJson', {
              get: function () {
                return this.getIsLoadingFromJson();
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getIsLoadingFromJson = function () {
              return (
                !(!this.loadingOwner || !this.loadingOwner.isLoadingFromJson) ||
                this.isLoadingFromJsonValue
              );
            }),
            (e.prototype.startLoadingFromJson = function () {
              this.isLoadingFromJsonValue = !0;
            }),
            (e.prototype.endLoadingFromJson = function () {
              this.isLoadingFromJsonValue = !1;
            }),
            (e.prototype.toJSON = function () {
              return new a.c().toJsonObject(this);
            }),
            (e.prototype.fromJSON = function (e) {
              return new a.c().toObject(e, this);
            }),
            (e.prototype.clone = function () {
              var e = a.b.createClass(this.getType());
              return e.fromJSON(this.toJSON()), e;
            }),
            (e.prototype.getProgressInfo = function () {
              return h.createProgressInfo();
            }),
            (e.prototype.locStrsChanged = function () {
              if (this.arraysInfo)
                for (var t in this.arraysInfo) {
                  var n = this.arraysInfo[t];
                  if (n && n.isItemValues) {
                    var r = this.getPropertyValue(t);
                    r && e.itemValueLocStrChanged && e.itemValueLocStrChanged(r);
                  }
                }
              if (this.localizableStrings)
                for (var t in this.localizableStrings) {
                  var n = this.getLocalizableString(t);
                  n && n.strChanged();
                }
            }),
            (e.prototype.getPropertyValue = function (e, t) {
              void 0 === t && (t = null);
              var n = this.getPropertyValueCore(this.propertyHash, e);
              if (this.IsPropertyEmpty(n)) {
                if (null != t) return t;
                var r = a.b.findProperty(this.getType(), e);
                if (r && (!r.isCustom || !this.isCreating)) {
                  if (!this.IsPropertyEmpty(r.defaultValue) && !Array.isArray(r.defaultValue))
                    return r.defaultValue;
                  if ('boolean' == r.type || 'switch' == r.type) return !1;
                }
              }
              return n;
            }),
            (e.prototype.getPropertyValueCore = function (e, t) {
              return this.getPropertyValueCoreHandler
                ? this.getPropertyValueCoreHandler(e, t)
                : e[t];
            }),
            (e.prototype.geValueFromHash = function () {
              return this.propertyHash.value;
            }),
            (e.prototype.setPropertyValueCore = function (e, t, n) {
              this.setPropertyValueCoreHandler
                ? this.setPropertyValueCoreHandler(e, t, n)
                : (e[t] = n);
            }),
            (e.prototype.iteratePropertiesHash = function (e) {
              var t = this,
                n = [];
              for (var r in this.propertyHash) n.push(r);
              n.forEach(function (n) {
                return e(t.propertyHash, n);
              });
            }),
            (e.prototype.setPropertyValue = function (e, t) {
              var n = this.getPropertyValue(e);
              if (n && Array.isArray(n)) {
                if (this.isTwoValueEquals(n, t)) return;
                var r = this.arraysInfo[e];
                this.setArray(e, n, t, !!r && r.isItemValues, r ? r.onPush : null);
              } else
                this.setPropertyValueCore(this.propertyHash, e, t),
                  this.isTwoValueEquals(n, t) || this.propertyValueChanged(e, n, t);
            }),
            (e.prototype.clearPropertyValue = function (e) {
              this.setPropertyValueCore(this.propertyHash, e, null), delete this.propertyHash[e];
            }),
            (e.prototype.onPropertyValueChangedCallback = function (e, t, n, r, i) {}),
            (e.prototype.itemValuePropertyChanged = function (e, t, n, r) {
              this.onItemValuePropertyChanged.fire(this, {
                obj: e,
                name: t,
                oldValue: n,
                newValue: r,
                propertyName: e.ownerPropertyName,
              });
            }),
            (e.prototype.onPropertyValueChanged = function (e, t, n) {}),
            (e.prototype.propertyValueChanged = function (e, t, n, r, i) {
              if (
                !this.isLoadingFromJson &&
                (this.updateBindings(e, n),
                this.onPropertyValueChanged(e, t, n),
                this.onPropertyChanged.fire(this, { name: e, oldValue: t, newValue: n }),
                this.doPropertyValueChangedCallback &&
                  this.doPropertyValueChangedCallback(e, t, n, r, this),
                this.onPropChangeFunctions)
              )
                for (var o = 0; o < this.onPropChangeFunctions.length; o++)
                  this.onPropChangeFunctions[o].name == e && this.onPropChangeFunctions[o].func(n);
            }),
            (e.prototype.doPropertyValueChangedCallback = function (e, t, n, r, i) {
              i || (i = this);
              var o = this.getOwnerForPropertyChanged();
              o
                ? o.doPropertyValueChangedCallback(e, t, n, r, i)
                : this.onPropertyValueChangedCallback(e, t, n, i, r);
            }),
            (e.prototype.getOwnerForPropertyChanged = function () {
              for (
                var e = ['colOwner', 'locOwner', 'survey', 'owner', 'errorOwner'], t = 0;
                t < e.length;
                t++
              ) {
                var n = e[t],
                  r = this[n];
                if (r && r.doPropertyValueChangedCallback) return r;
              }
              return null;
            }),
            (e.prototype.registerFunctionOnPropertyValueChanged = function (e, t, n) {
              if (
                (void 0 === n && (n = null),
                this.onPropChangeFunctions || (this.onPropChangeFunctions = []),
                n)
              )
                for (var r = 0; r < this.onPropChangeFunctions.length; r++) {
                  var i = this.onPropChangeFunctions[r];
                  if (i.name == e && i.key == n) return void (i.func = t);
                }
              this.onPropChangeFunctions.push({ name: e, func: t, key: n });
            }),
            (e.prototype.registerFunctionOnPropertiesValueChanged = function (e, t, n) {
              void 0 === n && (n = null);
              for (var r = 0; r < e.length; r++)
                this.registerFunctionOnPropertyValueChanged(e[r], t, n);
            }),
            (e.prototype.unRegisterFunctionOnPropertyValueChanged = function (e, t) {
              if ((void 0 === t && (t = null), this.onPropChangeFunctions))
                for (var n = 0; n < this.onPropChangeFunctions.length; n++) {
                  var r = this.onPropChangeFunctions[n];
                  if (r.name == e && r.key == t)
                    return void this.onPropChangeFunctions.splice(n, 1);
                }
            }),
            (e.prototype.unRegisterFunctionOnPropertiesValueChanged = function (e, t) {
              void 0 === t && (t = null);
              for (var n = 0; n < e.length; n++)
                this.unRegisterFunctionOnPropertyValueChanged(e[n], t);
            }),
            (e.prototype.createCustomLocalizableObj = function (e) {
              !this.getLocalizableString(e) &&
                this.getLocale &&
                this.createLocalizableString(e, this);
            }),
            (e.prototype.createLocalizableString = function (e, t, n) {
              void 0 === n && (n = !1);
              var r = new i.a(t, n, e);
              return (
                this.localizableStrings || (this.localizableStrings = {}),
                (this.localizableStrings[e] = r),
                r
              );
            }),
            (e.prototype.getLocalizableString = function (e) {
              return this.localizableStrings ? this.localizableStrings[e] : null;
            }),
            (e.prototype.getLocalizableStringText = function (e, t) {
              void 0 === t && (t = '');
              var n = this.getLocalizableString(e);
              if (!n) return '';
              var r = n.text;
              return r || t;
            }),
            (e.prototype.setLocalizableStringText = function (e, t) {
              var n = this.getLocalizableString(e);
              if (n) {
                var r = n.text;
                r !== t && ((n.text = t), this.propertyValueChanged(e, r, t));
              }
            }),
            (e.prototype.addUsedLocales = function (e) {
              if (this.localizableStrings)
                for (var t in this.localizableStrings) {
                  var n = this.getLocalizableString(t);
                  n && this.AddLocStringToUsedLocales(n, e);
                }
              if (this.arraysInfo)
                for (var t in this.arraysInfo) {
                  var r = this.getPropertyValue(t);
                  if (r && r.length)
                    for (var i = 0; i < r.length; i++) {
                      var n = r[i];
                      n && n.addUsedLocales && n.addUsedLocales(e);
                    }
                }
            }),
            (e.prototype.AddLocStringToUsedLocales = function (e, t) {
              for (var n = e.getLocales(), r = 0; r < n.length; r++)
                t.indexOf(n[r]) < 0 && t.push(n[r]);
            }),
            (e.prototype.createItemValues = function (e) {
              var t = this,
                n = this.createNewArray(e, function (n) {
                  (n.locOwner = t), (n.ownerPropertyName = e);
                });
              return (this.arraysInfo[e].isItemValues = !0), n;
            }),
            (e.prototype.notifyArrayChanged = function (e, t) {
              !!e.onArrayChanged && e.onArrayChanged(t);
            }),
            (e.prototype.createNewArrayCore = function (e) {
              var t = null;
              return (
                this.createArrayCoreHandler &&
                  (t = this.createArrayCoreHandler(this.propertyHash, e)),
                t || ((t = new Array()), this.setPropertyValueCore(this.propertyHash, e, t)),
                t
              );
            }),
            (e.prototype.createNewArray = function (e, t, i) {
              void 0 === t && (t = null), void 0 === i && (i = null);
              var o = this.createNewArrayCore(e);
              this.arraysInfo || (this.arraysInfo = {}),
                (this.arraysInfo[e] = { onPush: t, isItemValues: !1 });
              var a = this;
              return (
                (o.push = function (n) {
                  var r = Object.getPrototypeOf(o).push.call(o, n);
                  t && t(n, o.length - 1);
                  var i = new p(o.length - 1, 0, [n], []);
                  return a.propertyValueChanged(e, o, o, i), a.notifyArrayChanged(o, i), r;
                }),
                (o.unshift = function (n) {
                  var r = Object.getPrototypeOf(o).unshift.call(o, n);
                  t && t(n, o.length - 1);
                  var i = new p(0, 0, [n], []);
                  return a.propertyValueChanged(e, o, o, i), a.notifyArrayChanged(o, i), r;
                }),
                (o.pop = function () {
                  var t = Object.getPrototypeOf(o).pop.call(o);
                  i && i(t);
                  var n = new p(o.length - 1, 1, [], []);
                  return a.propertyValueChanged(e, o, o, n), a.notifyArrayChanged(o, n), t;
                }),
                (o.splice = function (s, l) {
                  for (var u, c = [], d = 2; d < arguments.length; d++) c[d - 2] = arguments[d];
                  s || (s = 0), l || (l = 0);
                  var h = (u = Object.getPrototypeOf(o).splice).call.apply(
                    u,
                    n.i(r.d)([o, s, l], c)
                  );
                  if ((c || (c = []), i && h)) for (var f = 0; f < h.length; f++) i(h[f]);
                  if (t) for (var f = 0; f < c.length; f++) t(c[f], s + f);
                  var m = new p(s, l, c, h);
                  return a.propertyValueChanged(e, o, o, m), a.notifyArrayChanged(o, m), h;
                }),
                o
              );
            }),
            (e.prototype.getItemValueType = function () {}),
            (e.prototype.setArray = function (t, n, r, i, o) {
              var a = [].concat(n);
              if ((Object.getPrototypeOf(n).splice.call(n, 0, n.length), r))
                for (var s = 0; s < r.length; s++) {
                  var l = r[s];
                  i && e.createItemValue && (l = e.createItemValue(l, this.getItemValueType())),
                    Object.getPrototypeOf(n).push.call(n, l),
                    o && o(n[s]);
                }
              var u = new p(0, a.length, n, a);
              this.propertyValueChanged(t, a, n, u), this.notifyArrayChanged(n, u);
            }),
            (e.prototype.isTwoValueEquals = function (e, t, n) {
              return (
                void 0 === n && (n = !1),
                n && ((e = this.getValueInLowCase(e)), (t = this.getValueInLowCase(t))),
                o.a.isTwoValueEquals(e, t)
              );
            }),
            (e.copyObject = function (e, t) {
              for (var n in t) {
                var r = t[n];
                'object' == typeof r && ((r = {}), this.copyObject(r, t[n])), (e[n] = r);
              }
            }),
            (e.prototype.copyCssClasses = function (e, t) {
              t &&
                ('string' == typeof t || t instanceof String ? (e.root = t) : h.copyObject(e, t));
            }),
            (e.prototype.getValueInLowCase = function (e) {
              return e && 'string' == typeof e ? e.toLowerCase() : e;
            }),
            e
          );
        })(),
        p = (function () {
          function e(e, t, n, r) {
            (this.index = e),
              (this.deleteCount = t),
              (this.itemsToAdd = n),
              (this.deletedItems = r);
          }
          return e;
        })(),
        d = (function () {
          function e(e, t) {
            void 0 === e && (e = null),
              void 0 === t && (t = null),
              (this.text = e),
              (this.errorOwner = t),
              (this.visible = !0);
          }
          return (
            Object.defineProperty(e.prototype, 'locText', {
              get: function () {
                return (
                  this.locTextValue ||
                    ((this.locTextValue = new i.a(this.errorOwner, !0)),
                    (this.locTextValue.text = this.getText())),
                  this.locTextValue
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getText = function () {
              var e = this.text;
              return (
                e || (e = this.getDefaultText()),
                this.errorOwner && (e = this.errorOwner.getErrorCustomText(e, this)),
                e
              );
            }),
            (e.prototype.getErrorType = function () {
              return 'base';
            }),
            (e.prototype.getDefaultText = function () {
              return '';
            }),
            e
          );
        })(),
        h = (function (e) {
          function t(n) {
            var r = e.call(this) || this;
            (r.titleActions = []),
              (r.selectedElementInDesignValue = r),
              (r.disableDesignActions = t.CreateDisabledDesignElements),
              (r.isContentElement = !1),
              (r.name = n),
              r.createNewArray('errors');
            var i = r;
            return (
              r.registerFunctionOnPropertyValueChanged('isReadOnly', function () {
                i.onReadOnlyChanged();
              }),
              r.registerFunctionOnPropertyValueChanged('state', function () {
                i.stateChangedCallback && i.stateChangedCallback();
              }),
              r
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.createProgressInfo = function () {
              return {
                questionCount: 0,
                answeredQuestionCount: 0,
                requiredQuestionCount: 0,
                requiredAnsweredQuestionCount: 0,
              };
            }),
            (t.getProgressInfoByElements = function (e, n) {
              for (var r = t.createProgressInfo(), i = 0; i < e.length; i++)
                if (e[i].isVisible) {
                  var o = e[i].getProgressInfo();
                  (r.questionCount += o.questionCount),
                    (r.answeredQuestionCount += o.answeredQuestionCount),
                    (r.requiredQuestionCount += o.requiredQuestionCount),
                    (r.requiredAnsweredQuestionCount += o.requiredAnsweredQuestionCount);
                }
              return (
                n &&
                  r.questionCount > 0 &&
                  (0 == r.requiredQuestionCount && (r.requiredQuestionCount = 1),
                  r.answeredQuestionCount > 0 && (r.requiredAnsweredQuestionCount = 1)),
                r
              );
            }),
            (t.ScrollElementToTop = function (e) {
              if (!e || 'undefined' == typeof document) return !1;
              var t = document.getElementById(e);
              if (!t || !t.scrollIntoView) return !1;
              var n = t.getBoundingClientRect().top;
              return n < 0 && t.scrollIntoView(), n < 0;
            }),
            (t.GetFirstNonTextElement = function (e, t) {
              if ((void 0 === t && (t = !1), !e || !e.length || 0 == e.length)) return null;
              if (t) {
                var n = e[0];
                '#text' === n.nodeName && (n.data = ''),
                  (n = e[e.length - 1]),
                  '#text' === n.nodeName && (n.data = '');
              }
              for (var r = 0; r < e.length; r++)
                if ('#text' != e[r].nodeName && '#comment' != e[r].nodeName) return e[r];
              return null;
            }),
            (t.FocusElement = function (e) {
              if (!e || 'undefined' == typeof document) return !1;
              var t = document.getElementById(e);
              return !!t && (t.focus(), !0);
            }),
            Object.defineProperty(t.prototype, 'state', {
              get: function () {
                return this.getPropertyValue('state');
              },
              set: function (e) {
                this.setPropertyValue('state', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isCollapsed', {
              get: function () {
                if (!this.isDesignMode) return 'collapsed' == this.state;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isExpanded', {
              get: function () {
                return 'expanded' == this.state;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.collapse = function () {
              this.isDesignMode || (this.state = 'collapsed');
            }),
            (t.prototype.expand = function () {
              this.state = 'expanded';
            }),
            (t.prototype.toggleState = function () {
              return this.isCollapsed
                ? void this.expand()
                : this.isExpanded
                ? void this.collapse()
                : void 0;
            }),
            (t.prototype.getTitleActions = function () {
              var e = this;
              return (
                this.titleActions.push({
                  title: '',
                  action: function () {},
                  innerCss: function () {
                    var t = 'sv-expand-action';
                    return e.isExpanded && (t += ' sv-expand-action--expanded'), t;
                  },
                  visible: function () {
                    return e.isExpanded || e.isCollapsed;
                  },
                }),
                this.titleActions
              );
            }),
            (t.prototype.getTitleComponentName = function () {
              var e = 'default';
              return (
                this.survey.renderTitleActions(this) &&
                  (e = l.a.Instance.getRenderer('element', 'title-actions')),
                'default' == e ? 'sv-default-title' : e
              );
            }),
            (t.prototype.setSurveyImpl = function (e) {
              (this.surveyImplValue = e),
                this.surveyImplValue &&
                  ((this.surveyDataValue = this.surveyImplValue.geSurveyData()),
                  (this.surveyValue = this.surveyImplValue.getSurvey()),
                  (this.textProcessorValue = this.surveyImplValue.getTextProcessor()),
                  this.onSetData());
            }),
            Object.defineProperty(t.prototype, 'surveyImpl', {
              get: function () {
                return this.surveyImplValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'data', {
              get: function () {
                return this.surveyDataValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'survey', {
              get: function () {
                return this.surveyValue
                  ? this.surveyValue
                  : (this.surveyImplValue && (this.surveyValue = this.surveyImplValue.getSurvey()),
                    this.surveyValue);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isDesignMode', {
              get: function () {
                return !!this.survey && this.survey.isDesignMode;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'areInvisibleElementsShowing', {
              get: function () {
                return (
                  !!this.survey && this.survey.areInvisibleElementsShowing && !this.isContentElement
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isVisible', {
              get: function () {
                return !0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isReadOnly', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'readOnly', {
              get: function () {
                return this.getPropertyValue('readOnly', !1);
              },
              set: function (e) {
                this.readOnly != e &&
                  (this.setPropertyValue('readOnly', e),
                  this.isLoadingFromJson || this.setPropertyValue('isReadOnly', this.isReadOnly));
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onReadOnlyChanged = function () {
              this.readOnlyChangedCallback && this.readOnlyChangedCallback();
            }),
            (t.prototype.updateElementCss = function () {}),
            (t.prototype.getIsLoadingFromJson = function () {
              return (
                !!e.prototype.getIsLoadingFromJson.call(this) ||
                (!!this.survey && this.survey.isLoadingFromJson)
              );
            }),
            Object.defineProperty(t.prototype, 'name', {
              get: function () {
                return this.getPropertyValue('name', '');
              },
              set: function (e) {
                var t = this.name;
                this.setPropertyValue('name', this.getValidName(e)),
                  !this.isLoadingFromJson && t && this.onNameChanged(t);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getValidName = function (e) {
              return e;
            }),
            (t.prototype.onNameChanged = function (e) {}),
            (t.prototype.updateBindingValue = function (e, t) {
              this.data &&
                !o.a.isTwoValueEquals(t, this.data.getValue(e)) &&
                this.data.setValue(e, t, !1);
            }),
            Object.defineProperty(t.prototype, 'errors', {
              get: function () {
                return this.getPropertyValue('errors');
              },
              set: function (e) {
                this.setPropertyValue('errors', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'containsErrors', {
              get: function () {
                return this.getPropertyValue('containsErrors', !1);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.updateContainsErrors = function () {
              this.setPropertyValue('containsErrors', this.getContainsErrors());
            }),
            (t.prototype.getContainsErrors = function () {
              return this.errors.length > 0;
            }),
            (t.prototype.getElementsInDesign = function (e) {
              return void 0 === e && (e = !1), [];
            }),
            Object.defineProperty(t.prototype, 'selectedElementInDesign', {
              get: function () {
                return this.selectedElementInDesignValue;
              },
              set: function (e) {
                this.selectedElementInDesignValue = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.updateCustomWidgets = function () {}),
            (t.prototype.onSurveyLoad = function () {}),
            (t.prototype.onFirstRendering = function () {}),
            (t.prototype.endLoadingFromJson = function () {
              e.prototype.endLoadingFromJson.call(this), this.survey || this.onSurveyLoad();
            }),
            (t.prototype.setVisibleIndex = function (e) {
              return 0;
            }),
            Object.defineProperty(t.prototype, 'isPage', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isPanel', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.delete = function () {}),
            (t.prototype.removeSelfFromList = function (e) {
              if (e && Array.isArray(e)) {
                var t = e.indexOf(this);
                t > -1 && e.splice(t, 1);
              }
            }),
            Object.defineProperty(t.prototype, 'textProcessor', {
              get: function () {
                return this.textProcessorValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getProcessedHtml = function (e) {
              return e && this.textProcessor ? this.textProcessor.processText(e, !0) : e;
            }),
            (t.prototype.onSetData = function () {}),
            (t.prototype.getPage = function (e) {
              for (; e && e.parent; ) e = e.parent;
              return e && 'page' == e.getType() ? e : null;
            }),
            (t.prototype.moveToBase = function (e, t, n) {
              if ((void 0 === n && (n = null), !t)) return !1;
              e.removeElement(this);
              var r = -1;
              return (
                o.a.isNumber(n) && (r = parseInt(n)),
                -1 == r && n && n.getType && (r = t.indexOf(n)),
                t.addElement(this, r),
                !0
              );
            }),
            (t.prototype.setPage = function (e, t) {
              this.getPage(e) !== t && (e && e.removeElement(this), t && t.addElement(this, -1));
            }),
            (t.CreateDisabledDesignElements = !1),
            t
          );
        })(c),
        f = (function () {
          function e() {}
          return (
            Object.defineProperty(e.prototype, 'isEmpty', {
              get: function () {
                return null == this.callbacks || 0 == this.callbacks.length;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.fire = function (e, t) {
              if (null != this.callbacks)
                for (var n = 0; n < this.callbacks.length; n++) this.callbacks[n](e, t);
            }),
            (e.prototype.clear = function () {
              (this.callbacks = []), this.fireCallbackChanged();
            }),
            (e.prototype.add = function (e) {
              this.hasFunc(e) ||
                (null == this.callbacks && (this.callbacks = new Array()),
                this.callbacks.push(e),
                this.fireCallbackChanged());
            }),
            (e.prototype.remove = function (e) {
              if (this.hasFunc(e)) {
                var t = this.callbacks.indexOf(e, 0);
                this.callbacks.splice(t, 1), this.fireCallbackChanged();
              }
            }),
            (e.prototype.hasFunc = function (e) {
              return null != this.callbacks && this.callbacks.indexOf(e, 0) > -1;
            }),
            (e.prototype.fireCallbackChanged = function () {
              this.onCallbacksChanged && this.onCallbacksChanged();
            }),
            e
          );
        })();
    },
    function (e, t, n) {
      'use strict';
      n.d(t, 'a', function () {
        return r;
      });
      var r = {
        commentPrefix: '-Comment',
        webserviceEncodeParameters: !0,
        useCachingForChoicesRestfull: !0,
        surveyServiceUrl: 'https://api.surveyjs.io/public/v1/Survey',
        itemValueSeparator: '|',
        defaultLocaleName: 'default',
        matrixDefaultRowName: 'default',
        matrixDefaultCellType: 'dropdown',
        matrixTotalValuePostFix: '-total',
        matrixMaximumRowCount: 1e3,
        matrixMaxRowCountInCondition: 1,
        panelMaximumPanelCount: 100,
        ratingMaximumRateValueCount: 20,
        disableOnGettingChoicesFromWeb: !1,
        serializeLocalizableStringAsObject: !1,
        allowShowEmptyTitleInDesignMode: !0,
        allowShowEmptyDescriptionInDesignMode: !0,
        executeCompleteTriggerOnValueChanged: !1,
        executeSkipTriggerOnValueChanged: !0,
        readOnlyCommentRenderMode: 'textarea',
        confirmActionFunc: function (e) {
          return confirm(e);
        },
        minWidth: '300px',
        maxWidth: 'initial',
        maximumConditionRunCountOnValueChanged: 10,
        setQuestionVisibleIndexForHiddenTitle: !1,
        setQuestionVisibleIndexForHiddenNumber: !1,
        lazyRowsRendering: !1,
        showItemsInOrder: 'default',
        supportedValidators: {
          question: ['expression'],
          comment: ['text', 'regex'],
          text: ['numeric', 'text', 'regex', 'email'],
          checkbox: ['answercount'],
        },
      };
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(6)),
        a = n(4),
        s = n(15);
      n.d(t, 'a', function () {
        return l;
      });
      var l = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          (n.question = t), (n._koValue = i.observableArray());
          var r = !1;
          n._koValue.subscribe(function (e) {
            r || (n.question.value = e);
          }),
            (n.toggleStateByClick = function () {
              return (
                n.question.toggleState(),
                !!n.question.isExpanded || (!n.question.isCollapsed && void 0)
              );
            }),
            (n.toggleStateByKeyUp = function (e, t) {
              13 === t.which && n.toggleStateByClick();
            }),
            Object.defineProperty(n.question, 'koValue', {
              get: function () {
                if (!a.a.isTwoValueEquals(n._koValue(), n.getKoValue()))
                  try {
                    (r = !0), n._koValue(n.getKoValue());
                  } finally {
                    r = !1;
                  }
                return n._koValue;
              },
              enumerable: !0,
              configurable: !0,
            });
          var o = n;
          return (
            (t.surveyLoadCallback = function () {
              o.onSurveyLoad();
            }),
            (n.koTemplateName = i.pureComputed(function () {
              return o.getTemplateName();
            })),
            (n.koElementType = i.observable('survey-question')),
            (n.question.koElementType = n.koElementType),
            (n.question.koTemplateName = n.koTemplateName),
            (n.question.updateQuestion = function () {
              o.updateQuestion();
            }),
            (n.question.koCss = i.pureComputed(function () {
              return o.question.cssClasses;
            })),
            (n.question.koRootCss = i.pureComputed(function () {
              var e = o.question.cssRoot;
              return o.question.isReadOnly && (e += ' ' + o.question.cssClasses.disabled), e;
            })),
            (n.question.toggleStateByClick = n.toggleStateByClick),
            (n.question.toggleStateByKeyUp = n.toggleStateByKeyUp),
            (n.question.koErrorClass = i.pureComputed(function () {
              return o.question.cssError;
            })),
            t.registerFunctionOnPropertyValueChanged('visibleIndex', function () {
              o.onVisibleIndexChanged();
            }),
            (n.koDummy = i.observable(0)),
            (n.question.koQuestionAfterRender = function (e, t) {
              o.koQuestionAfterRender(e, t);
            }),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getKoValue = function () {
            return this.question.value;
          }),
          (t.prototype.updateQuestion = function () {
            this.updateKoDummy();
          }),
          (t.prototype.onVisibleIndexChanged = function () {
            this.updateKoDummy();
          }),
          (t.prototype.onSurveyLoad = function () {}),
          (t.prototype.getQuestionTemplate = function () {
            return this.question.getTemplate();
          }),
          (t.prototype.getTemplateName = function () {
            return this.question.customWidget &&
              !this.question.customWidget.widgetJson.isDefaultRender
              ? 'survey-widget-' + this.question.customWidget.name
              : 'survey-question-' + this.getQuestionTemplate();
          }),
          (t.prototype.getNo = function () {
            return this.question.visibleIndex > -1 ? this.question.visibleIndex + 1 + '. ' : '';
          }),
          (t.prototype.updateKoDummy = function () {
            this.koDummy(this.koDummy() + 1), this.question.locTitle.onChanged();
          }),
          (t.prototype.koQuestionAfterRender = function (e, t) {
            var n = this,
              r = o.a.GetFirstNonTextElement(e, !0);
            r &&
              (this.question.afterRenderQuestionElement(r),
              this.question.customWidget &&
                this.question.customWidget.afterRender(this.question, r),
              i.utils.domNodeDisposal.addDisposeCallback(r, function () {
                if ((n.question.beforeDestroyQuestionElement(r), n.question.customWidget))
                  try {
                    n.question.customWidget.willUnmount(n.question, r);
                  } catch (e) {
                    console.warn('Custom widget will unmount failed');
                  }
              }));
          }),
          (t.prototype.changeExpanded = function () {
            if (this.question.isCollapsed || this.question.isExpanded)
              return this.question.isCollapsed
                ? (this.question.expand(), !0)
                : (this.question.collapse(), !1);
          }),
          (t.prototype.dispose = function () {
            e.prototype.dispose.call(this),
              this.koTemplateName.dispose(),
              this.question.koCss.dispose(),
              this.question.koErrorClass.dispose(),
              this.question.unRegisterFunctionOnPropertyValueChanged('visibleIndex'),
              (this.question.updateQuestion = void 0),
              (this.question.koQuestionAfterRender = void 0);
          }),
          t
        );
      })(s.a);
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(4),
        o = n(1),
        a = n(6),
        s = n(2),
        l = n(12),
        u = n(36),
        c = n(13),
        p = n(10),
        d = n(30),
        h = n(7),
        f = n(19);
      n.d(t, 'a', function () {
        return m;
      });
      var m = (function (e) {
        function t(n) {
          var r = e.call(this, n) || this;
          (r.name = n),
            (r.conditionRunner = null),
            (r.isCustomWidgetRequested = !1),
            (r.customWidgetData = { isNeedRender: !0 }),
            (r.isReadyValue = !0),
            (r.onReadyChanged = new a.c()),
            (r.parentQuestionValue = null),
            (r.isRunningValidatorsValue = !1),
            (r.isValueChangedInSurvey = !1),
            (r.allowNotifyValueChanged = !0),
            (r.locNotificationInData = !1),
            (r.locOwner = null),
            (r.id = t.getQuestionId()),
            r.onCreating();
          var i = r;
          r.createNewArray('validators', function (e) {
            e.errorOwner = i;
          });
          var o = r.createLocalizableString('title', r, !0);
          return (
            (o.onGetTextCallback = function (e) {
              return e || (e = i.name), i.survey ? i.survey.getUpdatedQuestionTitle(i, e) : e;
            }),
            (r.locProcessedTitle = new c.a(r, !0)),
            (r.locProcessedTitle.sharedData = o),
            (r.createLocalizableString('commentText', r, !0).onGetTextCallback = function (e) {
              return e || s.a.getString('otherItemText');
            }),
            r.createLocalizableString('requiredErrorText', r),
            r.registerFunctionOnPropertyValueChanged('width', function () {
              i.updateElementCss(), i.parent && i.parent.elementWidthChanged(i);
            }),
            r.registerFunctionOnPropertiesValueChanged(['indent', 'rightIndent'], function () {
              i.onIndentChanged();
            }),
            r.registerFunctionOnPropertiesValueChanged(['hasComment', 'hasOther'], function () {
              i.initCommentFromSurvey();
            }),
            r
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.getQuestionId = function () {
            return 'sq_' + t.questionCounter++;
          }),
          (t.prototype.isReadOnlyRenderDiv = function () {
            return this.isReadOnly && 'div' === h.a.readOnlyCommentRenderMode;
          }),
          (t.prototype.getValueName = function () {
            return this.valueName ? this.valueName.toString() : this.name;
          }),
          Object.defineProperty(t.prototype, 'valueName', {
            get: function () {
              return this.getPropertyValue('valueName', '');
            },
            set: function (e) {
              var t = this.getValueName();
              this.setPropertyValue('valueName', e), this.onValueNameChanged(t);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.onValueNameChanged = function (e) {
            this.survey &&
              (this.survey.questionRenamed(this, this.name, e || this.name),
              this.initDataFromSurvey());
          }),
          (t.prototype.onNameChanged = function (e) {
            this.survey &&
              this.survey.questionRenamed(this, e, this.valueName ? this.valueName : e);
          }),
          Object.defineProperty(t.prototype, 'isReady', {
            get: function () {
              return this.isReadyValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.choicesLoaded = function () {}),
          Object.defineProperty(t.prototype, 'page', {
            get: function () {
              return this.getPage(this.parent);
            },
            set: function (e) {
              this.setPage(this.parent, e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getPanel = function () {
            return null;
          }),
          (t.prototype.delete = function () {
            this.parent && this.removeSelfFromList(this.parent.elements);
          }),
          Object.defineProperty(t.prototype, 'isFlowLayout', {
            get: function () {
              return 'flow' === this.getLayoutType();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getLayoutType = function () {
            return this.parent ? this.parent.getChildrenLayoutType() : 'row';
          }),
          (t.prototype.isLayoutTypeSupported = function (e) {
            return 'flow' !== e;
          }),
          Object.defineProperty(t.prototype, 'visible', {
            get: function () {
              return this.getPropertyValue('visible', !0);
            },
            set: function (e) {
              e != this.visible &&
                (this.setPropertyValue('visible', e),
                this.onVisibleChanged(),
                this.notifySurveyVisibilityChanged());
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.onVisibleChanged = function () {
            this.setPropertyValue('isVisible', this.isVisible),
              this.isVisible &&
                this.survey &&
                this.survey.isClearValueOnHidden &&
                this.updateValueWithDefaults(),
              !this.isVisible && this.errors && this.errors.length > 0 && (this.errors = []);
          }),
          Object.defineProperty(t.prototype, 'useDisplayValuesInTitle', {
            get: function () {
              return this.getPropertyValue('useDisplayValuesInTitle', !0);
            },
            set: function (e) {
              this.setPropertyValue('useDisplayValuesInTitle', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'visibleIf', {
            get: function () {
              return this.getPropertyValue('visibleIf', '');
            },
            set: function (e) {
              this.setPropertyValue('visibleIf', e), this.runConditions();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isVisible', {
            get: function () {
              return (
                !(this.survey && this.survey.areEmptyElementsHidden && this.isEmpty()) &&
                (this.visible || this.areInvisibleElementsShowing)
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'visibleIndex', {
            get: function () {
              return this.getPropertyValue('visibleIndex', -1);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hideNumber', {
            get: function () {
              return this.getPropertyValue('hideNumber', !1);
            },
            set: function (e) {
              this.setPropertyValue('hideNumber', e), this.notifySurveyVisibilityChanged();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isAllowTitleLeft', {
            get: function () {
              return !0;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getType = function () {
            return 'question';
          }),
          (t.prototype.moveTo = function (e, t) {
            return void 0 === t && (t = null), this.moveToBase(this.parent, e, t);
          }),
          (t.prototype.getProgressInfo = function () {
            return this.hasInput
              ? {
                  questionCount: 1,
                  answeredQuestionCount: this.isEmpty() ? 0 : 1,
                  requiredQuestionCount: this.isRequired ? 1 : 0,
                  requiredAnsweredQuestionCount: !this.isEmpty() && this.isRequired ? 1 : 0,
                }
              : e.prototype.getProgressInfo.call(this);
          }),
          (t.prototype.runConditions = function () {
            !this.data ||
              this.isLoadingFromJson ||
              this.isDesignMode ||
              (this.runCondition(this.getDataFilteredValues(), this.getDataFilteredProperties()),
              this.locStrsChanged());
          }),
          (t.prototype.setSurveyImpl = function (t) {
            e.prototype.setSurveyImpl.call(this, t),
              this.survey && this.survey.questionCreated(this),
              this.survey &&
                this.survey.isDesignMode &&
                !this.isDesignMode &&
                this.onVisibleChanged(),
              this.runConditions();
          }),
          (t.prototype.getDataFilteredValues = function () {
            return this.data ? this.data.getFilteredValues() : null;
          }),
          (t.prototype.getDataFilteredProperties = function () {
            var e = this.data ? this.data.getFilteredProperties() : {};
            return (e.question = this), e;
          }),
          Object.defineProperty(t.prototype, 'parent', {
            get: function () {
              return this.getPropertyValue('parent', null);
            },
            set: function (e) {
              this.setPropertyValue('parent', e), this.updateElementCss(), this.onParentChanged();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'parentQuestion', {
            get: function () {
              return this.parentQuestionValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setParentQuestion = function (e) {
            this.parentQuestionValue = e;
          }),
          (t.prototype.onParentChanged = function () {}),
          Object.defineProperty(t.prototype, 'hasTitle', {
            get: function () {
              return 'hidden' !== this.getTitleLocation();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'titleLocation', {
            get: function () {
              return this.getPropertyValue('titleLocation');
            },
            set: function (e) {
              var t = 'hidden' == this.titleLocation || 'hidden' == e;
              this.setPropertyValue('titleLocation', e.toLowerCase()),
                this.isLoadingFromJson || this.updateElementCss(),
                t && this.notifySurveyVisibilityChanged();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.notifySurveyVisibilityChanged = function () {
            this.survey &&
              !this.isLoadingFromJson &&
              (this.survey.questionVisibilityChanged(this, this.isVisible),
              this.survey.isClearValueOnHidden && !this.visible && this.clearValue());
          }),
          (t.prototype.getTitleLocation = function () {
            if (this.isFlowLayout) return 'hidden';
            var e = this.getTitleLocationCore();
            return 'left' !== e || this.isAllowTitleLeft || (e = 'top'), e;
          }),
          (t.prototype.getTitleLocationCore = function () {
            return 'default' !== this.titleLocation
              ? this.titleLocation
              : this.parent
              ? this.parent.getQuestionTitleLocation()
              : this.survey
              ? this.survey.questionTitleLocation
              : 'top';
          }),
          Object.defineProperty(t.prototype, 'hasTitleOnLeft', {
            get: function () {
              return this.hasTitle && 'left' === this.getTitleLocation();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasTitleOnTop', {
            get: function () {
              return this.hasTitle && 'top' === this.getTitleLocation();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasTitleOnBottom', {
            get: function () {
              return this.hasTitle && 'bottom' === this.getTitleLocation();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasTitleOnLeftTop', {
            get: function () {
              if (!this.hasTitle) return !1;
              var e = this.getTitleLocation();
              return 'left' === e || 'top' === e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'errorLocation', {
            get: function () {
              return this.survey ? this.survey.questionErrorLocation : 'top';
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasInput', {
            get: function () {
              return !0;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasSingleInput', {
            get: function () {
              return this.hasInput;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'inputId', {
            get: function () {
              return this.id + 'i';
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'title', {
            get: function () {
              return this.getLocalizableStringText('title', this.name);
            },
            set: function (e) {
              this.setLocalizableStringText('title', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locTitle', {
            get: function () {
              return this.getLocalizableString('title');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'descriptionLocation', {
            get: function () {
              return this.getPropertyValue('descriptionLocation');
            },
            set: function (e) {
              this.setPropertyValue('descriptionLocation', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasDescriptionUnderTitle', {
            get: function () {
              return 'underTitle' == this.getDescriptionLocation();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasDescriptionUnderInput', {
            get: function () {
              return 'underInput' == this.getDescriptionLocation();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getDescriptionLocation = function () {
            return 'default' !== this.descriptionLocation
              ? this.descriptionLocation
              : this.survey
              ? this.survey.questionDescriptionLocation
              : 'underTitle';
          }),
          Object.defineProperty(t.prototype, 'clickTitleFunction', {
            get: function () {
              if (this.hasInput) {
                var e = this;
                return function () {
                  return e.focus(), !0;
                };
              }
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'requiredErrorText', {
            get: function () {
              return this.getLocalizableStringText('requiredErrorText');
            },
            set: function (e) {
              this.setLocalizableStringText('requiredErrorText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locRequiredErrorText', {
            get: function () {
              return this.getLocalizableString('requiredErrorText');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'commentText', {
            get: function () {
              return this.getLocalizableStringText('commentText', s.a.getString('otherItemText'));
            },
            set: function (e) {
              this.setLocalizableStringText('commentText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locCommentText', {
            get: function () {
              return this.getLocalizableString('commentText');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getAllErrors = function () {
            return this.errors.slice();
          }),
          (t.prototype.getErrorByType = function (e) {
            for (var t = 0; t < this.errors.length; t++)
              if (this.errors[t].getErrorType() === e) return this.errors[t];
            return null;
          }),
          Object.defineProperty(t.prototype, 'customWidget', {
            get: function () {
              return (
                this.isCustomWidgetRequested ||
                  this.customWidgetValue ||
                  ((this.isCustomWidgetRequested = !0), this.updateCustomWidget()),
                this.customWidgetValue
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.updateCustomWidget = function () {
            this.customWidgetValue = d.a.Instance.getCustomWidget(this);
          }),
          Object.defineProperty(t.prototype, 'isCompositeQuestion', {
            get: function () {
              return !1;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.afterRenderQuestionElement = function (e) {
            this.survey && this.hasSingleInput && this.survey.afterRenderQuestionInput(this, e);
          }),
          (t.prototype.afterRender = function (e) {
            this.survey &&
              (this.survey.afterRenderQuestion(this, e),
              this.afterRenderQuestionCallback && this.afterRenderQuestionCallback(this, e));
          }),
          (t.prototype.beforeDestroyQuestionElement = function (e) {}),
          Object.defineProperty(t.prototype, 'processedTitle', {
            get: function () {
              var e = this.locProcessedTitle.textOrHtml;
              return e || this.name;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'fullTitle', {
            get: function () {
              return this.locTitle.renderedHtml;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'titlePattern', {
            get: function () {
              return this.survey ? this.survey.questionTitlePattern : 'numTitleRequire';
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isRequireTextOnStart', {
            get: function () {
              return this.isRequired && 'requireNumTitle' == this.titlePattern;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isRequireTextBeforeTitle', {
            get: function () {
              return this.isRequired && 'numRequireTitle' == this.titlePattern;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isRequireTextAfterTitle', {
            get: function () {
              return this.isRequired && 'numTitleRequire' == this.titlePattern;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'startWithNewLine', {
            get: function () {
              return this.getPropertyValue('startWithNewLine', !0);
            },
            set: function (e) {
              this.startWithNewLine != e && this.setPropertyValue('startWithNewLine', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cssClasses', {
            get: function () {
              var e = this.css,
                t = { error: {} };
              return (
                this.copyCssClasses(t, e.question),
                this.copyCssClasses(t.error, e.error),
                this.updateCssClasses(t, e),
                this.survey && this.survey.updateQuestionCssClasses(this, t),
                this.updateElementCssCore(t),
                t
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cssRoot', {
            get: function () {
              return this.getPropertyValue('cssRoot', '');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setCssRoot = function (e) {
            this.setPropertyValue('cssRoot', e);
          }),
          (t.prototype.getCssRoot = function (e) {
            var t = this.isFlowLayout && !this.isDesignMode ? e.flowRoot : e.mainRoot;
            return (
              t || (t = ''),
              !this.isFlowLayout &&
                this.hasTitleOnLeft &&
                e.titleLeftRoot &&
                (t += ' ' + e.titleLeftRoot),
              this.errors.length > 0 && e.hasError && (t += ' ' + e.hasError),
              e.small && !this.width && (t += ' ' + e.small),
              t
            );
          }),
          Object.defineProperty(t.prototype, 'cssHeader', {
            get: function () {
              return this.getPropertyValue('cssHeader', '');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setCssHeader = function (e) {
            this.setPropertyValue('cssHeader', e);
          }),
          (t.prototype.getCssHeader = function (e) {
            var t = e.header || '';
            return (
              this.hasTitleOnTop && e.headerTop && (t += ' ' + e.headerTop),
              this.hasTitleOnLeft && e.headerLeft && (t += ' ' + e.headerLeft),
              this.hasTitleOnBottom && e.headerBottom && (t += ' ' + e.headerBottom),
              t
            );
          }),
          Object.defineProperty(t.prototype, 'cssContent', {
            get: function () {
              return this.getPropertyValue('cssContent', '');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setCssContent = function (e) {
            this.setPropertyValue('cssContent', e);
          }),
          (t.prototype.getCssContent = function (e) {
            var t = e.content || '';
            return this.hasTitleOnLeft && e.contentLeft && (t += ' ' + e.contentLeft), t;
          }),
          Object.defineProperty(t.prototype, 'cssTitle', {
            get: function () {
              return this.getPropertyValue('cssTitle', '');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setCssTitle = function (e) {
            this.setPropertyValue('cssTitle', e);
          }),
          (t.prototype.getCssTitle = function (e) {
            var t = e.title;
            return (
              (this.isCollapsed || this.isExpanded) && (t += ' ' + e.titleExpandable),
              this.containsErrors
                ? e.titleOnError && (t += ' ' + e.titleOnError)
                : this.isAnswered && e.titleOnAnswer && (t += ' ' + e.titleOnAnswer),
              t
            );
          }),
          Object.defineProperty(t.prototype, 'cssError', {
            get: function () {
              return this.getPropertyValue('cssError', '');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setCssError = function (e) {
            this.setPropertyValue('cssError', e);
          }),
          (t.prototype.getCssError = function (e) {
            var t = e.error.root || '';
            return (
              'top' == this.errorLocation
                ? e.error.locationTop && (t += ' ' + e.error.locationTop)
                : 'bottom' === this.errorLocation &&
                  e.error.locationBottom &&
                  (t += ' ' + e.error.locationBottom),
              t
            );
          }),
          (t.prototype.updateElementCss = function () {
            this.isLoadingFromJson || this.updateElementCssCore(this.cssClasses);
          }),
          (t.prototype.updateElementCssCore = function (e) {
            this.setCssRoot(this.getCssRoot(e)),
              this.setCssHeader(this.getCssHeader(e)),
              this.setCssContent(this.getCssContent(e)),
              this.setCssTitle(this.getCssTitle(e)),
              this.setCssError(this.getCssError(e));
          }),
          (t.prototype.updateCssClasses = function (e, t) {
            if (t.question) {
              this.isRequired &&
                (t.question.required && (e.root = (e.root ? e.root + ' ' : '') + n),
                t.question.titleRequired && (e.title += ' ' + t.question.titleRequired));
              var n = t[this.getCssType()];
              if (void 0 !== n && null !== n)
                if ('string' == typeof n || n instanceof String)
                  e.root = (e.root ? e.root + ' ' : '') + n;
                else for (var r in n) e[r] = n[r];
            }
          }),
          (t.prototype.getCssType = function () {
            return this.getType();
          }),
          Object.defineProperty(t.prototype, 'css', {
            get: function () {
              return this.survey ? this.survey.getCss() : {};
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'width', {
            get: function () {
              return this.getPropertyValue('width', '');
            },
            set: function (e) {
              this.setPropertyValue('width', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'minWidth', {
            get: function () {
              return this.getPropertyValue('minWidth', h.a.minWidth);
            },
            set: function (e) {
              this.setPropertyValue('minWidth', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'maxWidth', {
            get: function () {
              return this.getPropertyValue('maxWidth', h.a.maxWidth);
            },
            set: function (e) {
              this.setPropertyValue('maxWidth', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'renderWidth', {
            get: function () {
              return this.getPropertyValue('renderWidth', '');
            },
            set: function (e) {
              this.setPropertyValue('renderWidth', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'indent', {
            get: function () {
              return this.getPropertyValue('indent', 0);
            },
            set: function (e) {
              this.setPropertyValue('indent', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'rightIndent', {
            get: function () {
              return this.getPropertyValue('rightIndent', 0);
            },
            set: function (e) {
              this.setPropertyValue('rightIndent', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'paddingLeft', {
            get: function () {
              return this.getPropertyValue('paddintLeft', '');
            },
            set: function (e) {
              this.setPropertyValue('paddintLeft', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'paddingRight', {
            get: function () {
              return this.getPropertyValue('paddingRight', '');
            },
            set: function (e) {
              this.setPropertyValue('paddingRight', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.onIndentChanged = function () {
            (this.paddingLeft = this.getIndentSize(this.indent)),
              (this.paddingRight = this.getIndentSize(this.rightIndent));
          }),
          (t.prototype.getIndentSize = function (e) {
            return e < 1 ? '' : e * this.cssClasses.indent + 'px';
          }),
          (t.prototype.focus = function (e) {
            if ((void 0 === e && (e = !1), !this.isDesignMode)) {
              this.survey && this.survey.scrollElementToTop(this, this, null, this.id);
              var t = e ? this.getFirstErrorInputElementId() : this.getFirstInputElementId();
              a.a.FocusElement(t) && this.fireCallback(this.focusCallback);
            }
          }),
          (t.prototype.fireCallback = function (e) {
            e && e();
          }),
          (t.prototype.getOthersMaxLength = function () {
            return this.survey && this.survey.maxOthersLength > 0
              ? this.survey.maxOthersLength
              : null;
          }),
          (t.prototype.onCreating = function () {}),
          (t.prototype.getFirstInputElementId = function () {
            return this.inputId;
          }),
          (t.prototype.getFirstErrorInputElementId = function () {
            return this.getFirstInputElementId();
          }),
          (t.prototype.getProcessedTextValue = function (e) {
            var n = e.name.toLocaleLowerCase();
            (e.isExists =
              -1 !== Object.keys(t.TextPreprocessorValuesMap).indexOf(n) ||
              void 0 !== this[e.name]),
              (e.value = this[t.TextPreprocessorValuesMap[n] || e.name]);
          }),
          (t.prototype.supportComment = function () {
            return !1;
          }),
          (t.prototype.supportOther = function () {
            return !1;
          }),
          Object.defineProperty(t.prototype, 'isRequired', {
            get: function () {
              return this.getPropertyValue('isRequired', !1);
            },
            set: function (e) {
              this.isRequired != e &&
                (this.setPropertyValue('isRequired', e),
                this.isLoadingFromJson || this.locTitle.onChanged());
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'requiredIf', {
            get: function () {
              return this.getPropertyValue('requiredIf', '');
            },
            set: function (e) {
              this.setPropertyValue('requiredIf', e), this.runConditions();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasComment', {
            get: function () {
              return this.getPropertyValue('hasComment', !1);
            },
            set: function (e) {
              this.supportComment() &&
                (this.setPropertyValue('hasComment', e), this.hasComment && (this.hasOther = !1));
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'id', {
            get: function () {
              return this.getPropertyValue('id');
            },
            set: function (e) {
              this.setPropertyValue('id', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'ariaTitleId', {
            get: function () {
              return this.id + '_ariaTitle';
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'ariaRole', {
            get: function () {
              return null;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasOther', {
            get: function () {
              return this.getPropertyValue('hasOther', !1);
            },
            set: function (e) {
              this.supportOther() &&
                this.hasOther != e &&
                (this.setPropertyValue('hasOther', e),
                this.hasOther && (this.hasComment = !1),
                this.hasOtherChanged());
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.hasOtherChanged = function () {}),
          Object.defineProperty(t.prototype, 'requireUpdateCommentValue', {
            get: function () {
              return this.hasComment || this.hasOther;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isReadOnly', {
            get: function () {
              var e = !!this.parent && this.parent.isReadOnly,
                t = !!this.survey && this.survey.isDisplayMode;
              return this.readOnly || e || t;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'enableIf', {
            get: function () {
              return this.getPropertyValue('enableIf', '');
            },
            set: function (e) {
              this.setPropertyValue('enableIf', e), this.runConditions();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.runCondition = function (e, t) {
            this.isDesignMode ||
              (t || (t = {}),
              (t.question = this),
              this.areInvisibleElementsShowing || this.runVisibleIfCondition(e, t),
              this.runEnableIfCondition(e, t),
              this.runRequiredIfCondition(e, t));
          }),
          (t.prototype.runVisibleIfCondition = function (e, t) {
            var n = this;
            this.visibleIf &&
              (this.conditionRunner || (this.conditionRunner = new p.a(this.visibleIf)),
              (this.conditionRunner.expression = this.visibleIf),
              (this.conditionRunner.onRunComplete = function (e) {
                n.visible = e;
              }),
              this.conditionRunner.run(e, t));
          }),
          (t.prototype.runEnableIfCondition = function (e, t) {
            var n = this;
            this.enableIf &&
              (this.conditionEnabelRunner || (this.conditionEnabelRunner = new p.a(this.enableIf)),
              (this.conditionEnabelRunner.expression = this.enableIf),
              (this.conditionEnabelRunner.onRunComplete = function (e) {
                n.readOnly = !e;
              }),
              this.conditionEnabelRunner.run(e, t));
          }),
          (t.prototype.runRequiredIfCondition = function (e, t) {
            var n = this;
            this.requiredIf &&
              (this.conditionRequiredRunner ||
                (this.conditionRequiredRunner = new p.a(this.requiredIf)),
              (this.conditionRequiredRunner.expression = this.requiredIf),
              (this.conditionRequiredRunner.onRunComplete = function (e) {
                n.isRequired = e;
              }),
              this.conditionRequiredRunner.run(e, t));
          }),
          Object.defineProperty(t.prototype, 'no', {
            get: function () {
              if (!this.hasTitle || this.hideNumber) return '';
              var e = i.a.getNumberByIndex(this.visibleIndex, this.getStartIndex());
              return this.survey && (e = this.survey.getUpdatedQuestionNo(this, e)), e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getStartIndex = function () {
            return this.parent
              ? this.parent.getQuestionStartIndex()
              : this.survey
              ? this.survey.questionStartIndex
              : '';
          }),
          (t.prototype.onSurveyLoad = function () {
            this.fireCallback(this.surveyLoadCallback),
              this.updateValueWithDefaults(),
              this.updateDisplayValue(),
              this.updateElementCss();
          }),
          (t.prototype.onSetData = function () {
            e.prototype.onSetData.call(this),
              this.initDataFromSurvey(),
              this.onSurveyValueChanged(this.value),
              this.updateValueWithDefaults(),
              this.updateElementCss();
          }),
          (t.prototype.initDataFromSurvey = function () {
            this.data &&
              (this.updateValueFromSurvey(this.data.getValue(this.getValueName())),
              this.initCommentFromSurvey());
          }),
          (t.prototype.initCommentFromSurvey = function () {
            this.data && this.requireUpdateCommentValue
              ? this.updateCommentFromSurvey(this.data.getComment(this.getValueName()))
              : this.updateCommentFromSurvey('');
          }),
          (t.prototype.runExpression = function (e) {
            if (this.survey && e) return this.survey.runExpression(e);
          }),
          Object.defineProperty(t.prototype, 'questionValue', {
            get: function () {
              return this.getPropertyValue('value');
            },
            set: function (e) {
              this.setPropertyValue('value', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'questionComment', {
            get: function () {
              return this.getPropertyValue('comment');
            },
            set: function (e) {
              this.setPropertyValue('comment', e), this.fireCallback(this.commentChangedCallback);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'value', {
            get: function () {
              return this.getValueCore();
            },
            set: function (e) {
              this.setNewValue(e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.clearValue = function () {
            (this.value = null), (this.comment = null);
          }),
          (t.prototype.unbindValue = function () {
            this.clearValue();
          }),
          (t.prototype.createValueCopy = function () {
            return this.getUnbindValue(this.value);
          }),
          (t.prototype.isEditingSurveyElement = function (e) {
            return a.b.isSurveyElement(e);
          }),
          (t.prototype.getUnbindValue = function (e) {
            return this.isEditingSurveyElement(e) ? e : i.a.getUnbindValue(e);
          }),
          (t.prototype.canClearValueAsInvisible = function () {
            return (
              (!this.isVisible || !this.isParentVisible) &&
              (!this.page || !this.page.isStarted) &&
              (!this.survey ||
                !this.valueName ||
                !this.survey.hasVisibleQuestionByValueName(this.valueName))
            );
          }),
          Object.defineProperty(t.prototype, 'isParentVisible', {
            get: function () {
              for (var e = this.parent; e; ) {
                if (!e.isVisible) return !1;
                e = e.parent;
              }
              return !0;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.clearValueIfInvisible = function () {
            this.canClearValueAsInvisible() && this.clearValue();
          }),
          Object.defineProperty(t.prototype, 'displayValue', {
            get: function () {
              return this.updateDisplayValue(), this.getPropertyValue('displayValue', '');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.updateDisplayValue = function () {
            this.isLoadingFromJson ||
              this.setPropertyValue('displayValue', this.getDisplayValue(!0));
          }),
          (t.prototype.getDisplayValue = function (e, t) {
            if ((void 0 === t && (t = void 0), this.customWidget)) {
              var n = this.customWidget.getDisplayValue(this, t);
              if (n) return n;
            }
            return (
              (t = void 0 == t ? this.createValueCopy() : t),
              i.a.isValueEmpty(t) ? '' : this.getDisplayValueCore(e, t)
            );
          }),
          (t.prototype.getDisplayValueCore = function (e, t) {
            return t;
          }),
          Object.defineProperty(t.prototype, 'defaultValue', {
            get: function () {
              return this.getPropertyValue('defaultValue');
            },
            set: function (e) {
              if (this.isValueExpression(e))
                return void (this.defaultValueExpression = e.substr(1));
              this.setPropertyValue('defaultValue', this.convertDefaultValue(e)),
                this.updateValueWithDefaults();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'defaultValueExpression', {
            get: function () {
              return this.getPropertyValue('defaultValueExpression');
            },
            set: function (e) {
              this.setPropertyValue('defaultValueExpression', e), this.updateValueWithDefaults();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getPlainData = function (e) {
            var t = this;
            if (
              (void 0 === e && (e = { includeEmpty: !0, includeQuestionTypes: !1 }),
              e.includeEmpty || !this.isEmpty())
            ) {
              var n = {
                name: this.name,
                title: this.locTitle.renderedHtml,
                value: this.value,
                displayValue: this.displayValue,
                isNode: !1,
                getString: function (e) {
                  return 'object' == typeof e ? JSON.stringify(e) : e;
                },
              };
              return (
                !0 === e.includeQuestionTypes && (n.questionType = this.getType()),
                (e.calculations || []).forEach(function (e) {
                  n[e.propertyName] = t[e.propertyName];
                }),
                this.hasComment &&
                  ((n.isNode = !0),
                  (n.data = [
                    {
                      name: 0,
                      isComment: !0,
                      title: 'Comment',
                      value: h.a.commentPrefix,
                      displayValue: this.comment,
                      getString: function (e) {
                        return 'object' == typeof e ? JSON.stringify(e) : e;
                      },
                      isNode: !1,
                    },
                  ])),
                n
              );
            }
          }),
          Object.defineProperty(t.prototype, 'correctAnswer', {
            get: function () {
              return this.getPropertyValue('correctAnswer');
            },
            set: function (e) {
              this.setPropertyValue('correctAnswer', this.convertDefaultValue(e));
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.convertDefaultValue = function (e) {
            return e;
          }),
          Object.defineProperty(t.prototype, 'quizQuestionCount', {
            get: function () {
              return this.isVisible && this.hasInput && !i.a.isValueEmpty(this.correctAnswer)
                ? this.getQuizQuestionCount()
                : 0;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'correctAnswerCount', {
            get: function () {
              return this.isEmpty() || this.isValueEmpty(this.correctAnswer)
                ? 0
                : this.getCorrectAnswerCount();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getQuizQuestionCount = function () {
            return 1;
          }),
          (t.prototype.getCorrectAnswerCount = function () {
            return this.isTwoValueEquals(this.value, this.correctAnswer, !0) ? 1 : 0;
          }),
          (t.prototype.isAnswerCorrect = function () {
            return this.correctAnswerCount == this.quizQuestionCount;
          }),
          (t.prototype.updateValueWithDefaults = function () {
            this.isLoadingFromJson ||
              (!this.isDesignMode && this.isDefaultValueEmpty()) ||
              ((this.isDesignMode || this.isEmpty()) &&
                ((this.isEmpty() && this.isDefaultValueEmpty()) ||
                  (this.survey && this.survey.isClearValueOnHidden && !this.isVisible) ||
                  this.setDefaultValue()));
          }),
          (t.prototype.getQuestionFromArray = function (e, t) {
            return null;
          }),
          (t.prototype.getDefaultValue = function () {
            return this.defaultValue;
          }),
          (t.prototype.isDefaultValueEmpty = function () {
            return !this.defaultValueExpression && this.isValueEmpty(this.defaultValue);
          }),
          (t.prototype.setDefaultValue = function () {
            this.value = this.getValueAndRunExpression(
              this.getUnbindValue(this.defaultValue),
              this.defaultValueExpression
            );
          }),
          (t.prototype.isValueExpression = function (e) {
            return !!e && 'string' == typeof e && e.length > 0 && '=' == e[0];
          }),
          (t.prototype.getValueAndRunExpression = function (e, t) {
            return (
              t && (e = this.runExpression(t)),
              e ? (e instanceof Date && (e = e.toISOString().slice(0, 10)), e) : e
            );
          }),
          Object.defineProperty(t.prototype, 'comment', {
            get: function () {
              return this.getQuestionComment();
            },
            set: function (e) {
              e && (e = e.toString().trim()), this.comment != e && this.setQuestionComment(e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getQuestionComment = function () {
            return this.questionComment;
          }),
          (t.prototype.setQuestionComment = function (e) {
            this.setNewComment(e);
          }),
          (t.prototype.isEmpty = function () {
            return this.isValueEmpty(this.value);
          }),
          Object.defineProperty(t.prototype, 'isAnswered', {
            get: function () {
              return this.getPropertyValue('isAnswered');
            },
            set: function (e) {
              this.setPropertyValue('isAnswered', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.updateIsAnswered = function () {
            this.setPropertyValue('isAnswered', this.getIsAnswered());
          }),
          (t.prototype.getIsAnswered = function () {
            return !this.isEmpty();
          }),
          Object.defineProperty(t.prototype, 'validators', {
            get: function () {
              return this.getPropertyValue('validators');
            },
            set: function (e) {
              this.setPropertyValue('validators', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getValidators = function () {
            return this.validators;
          }),
          (t.prototype.getSupportedValidators = function () {
            for (var e = [], t = this.getType(); t; ) {
              var n = h.a.supportedValidators[t];
              if (n) for (var r = n.length - 1; r >= 0; r--) e.splice(0, 0, n[r]);
              t = o.b.findClass(t).parentName;
            }
            return e;
          }),
          (t.prototype.addSupportedValidators = function (e, t) {}),
          (t.prototype.addConditionObjectsByContext = function (e, t) {
            e.push({ name: this.getValueName(), text: this.processedTitle, question: this });
          }),
          (t.prototype.getConditionJson = function (e, t) {
            void 0 === e && (e = null), void 0 === t && (t = null);
            var n = new o.c().toJsonObject(this);
            return (n.type = this.getType()), n;
          }),
          (t.prototype.hasErrors = function (e, t) {
            void 0 === e && (e = !0), void 0 === t && (t = null);
            var n = this.errors.length > 0,
              r = this.checkForErrors(!!t && !0 === t.isOnValueChanged);
            return (
              e &&
                (this.survey && this.survey.beforeSettingQuestionErrors(this, r),
                (this.errors = r)),
              this.updateContainsErrors(),
              n != r.length > 0 && this.updateElementCss(),
              this.isCollapsed && t && e && r.length > 0 && this.expand(),
              r.length > 0
            );
          }),
          Object.defineProperty(t.prototype, 'currentErrorCount', {
            get: function () {
              return this.errors.length;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'requiredText', {
            get: function () {
              return null != this.survey && this.isRequired ? this.survey.requiredText : '';
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.addError = function (e) {
            if (e) {
              var t = null;
              (t = 'string' == typeof e || e instanceof String ? new l.b(e, this.survey) : e),
                this.errors.push(t);
            }
          }),
          (t.prototype.removeError = function (e) {
            var t = this.errors,
              n = t.indexOf(e);
            -1 !== n && t.splice(n, 1);
          }),
          (t.prototype.checkForErrors = function (e) {
            var t = new Array();
            return this.isVisible && !this.isReadOnly && this.collectErrors(t, e), t;
          }),
          (t.prototype.collectErrors = function (e, t) {
            if ((this.onCheckForErrors(e, t), !(e.length > 0) && this.canRunValidators(t))) {
              var n = this.runValidators();
              if (n.length > 0) {
                e.length = 0;
                for (var r = 0; r < n.length; r++) e.push(n[r]);
              }
              if (this.survey && 0 == e.length) {
                var i = this.fireSurveyValidation();
                i && e.push(i);
              }
            }
          }),
          (t.prototype.canRunValidators = function (e) {
            return !0;
          }),
          (t.prototype.fireSurveyValidation = function () {
            return this.validateValueCallback
              ? this.validateValueCallback()
              : this.survey
              ? this.survey.validateQuestion(this)
              : null;
          }),
          (t.prototype.onCheckForErrors = function (e, t) {
            !t && this.hasRequiredError() && e.push(new l.c(this.requiredErrorText, this));
          }),
          (t.prototype.hasRequiredError = function () {
            return this.isRequired && this.isEmpty();
          }),
          Object.defineProperty(t.prototype, 'isRunningValidators', {
            get: function () {
              return this.getIsRunningValidators();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getIsRunningValidators = function () {
            return this.isRunningValidatorsValue;
          }),
          (t.prototype.runValidators = function () {
            var e = this;
            return (
              this.validatorRunner && (this.validatorRunner.onAsyncCompleted = null),
              (this.validatorRunner = new u.a()),
              (this.isRunningValidatorsValue = !0),
              (this.validatorRunner.onAsyncCompleted = function (t) {
                e.doOnAsyncCompleted(t);
              }),
              this.validatorRunner.run(this)
            );
          }),
          (t.prototype.doOnAsyncCompleted = function (e) {
            for (var t = 0; t < e.length; t++) this.errors.push(e[t]);
            (this.isRunningValidatorsValue = !1), this.raiseOnCompletedAsyncValidators();
          }),
          (t.prototype.raiseOnCompletedAsyncValidators = function () {
            this.onCompletedAsyncValidators &&
              !this.isRunningValidators &&
              (this.onCompletedAsyncValidators(this.getAllErrors().length > 0),
              (this.onCompletedAsyncValidators = null));
          }),
          (t.prototype.setNewValue = function (e) {
            var t = this.isAnswered;
            this.setNewValueInData(e),
              this.allowNotifyValueChanged && this.onValueChanged(),
              this.isAnswered != t && this.updateElementCss();
          }),
          (t.prototype.isTextValue = function () {
            return !1;
          }),
          Object.defineProperty(t.prototype, 'isSurveyInputTextUpdate', {
            get: function () {
              return !!this.survey && this.survey.isUpdateValueTextOnTyping;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getDataLocNotification = function () {
            return this.locNotificationInData
              ? this.locNotificationInData
              : !!this.isInputTextUpdate && 'text';
          }),
          Object.defineProperty(t.prototype, 'isInputTextUpdate', {
            get: function () {
              return this.isSurveyInputTextUpdate && this.isTextValue();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setNewValueInData = function (e) {
            (e = this.valueToData(e)), this.isValueChangedInSurvey || this.setValueCore(e);
          }),
          (t.prototype.getValueCore = function () {
            return this.questionValue;
          }),
          (t.prototype.setValueCore = function (e) {
            this.setQuestionValue(e),
              null != this.data &&
                (this.valueToDataCallback && (e = this.valueToDataCallback(e)),
                this.data.setValue(
                  this.getValueName(),
                  e,
                  this.getDataLocNotification(),
                  this.allowNotifyValueChanged
                ));
          }),
          (t.prototype.valueFromData = function (e) {
            return e;
          }),
          (t.prototype.valueToData = function (e) {
            return e;
          }),
          (t.prototype.onValueChanged = function () {}),
          (t.prototype.setNewComment = function (e) {
            (this.questionComment = e),
              null != this.data &&
                this.data.setComment(
                  this.getValueName(),
                  e,
                  !!this.isSurveyInputTextUpdate && 'text'
                );
          }),
          (t.prototype.getValidName = function (e) {
            return e ? e.trim().replace(/[\{\}]+/g, '') : e;
          }),
          (t.prototype.updateValueFromSurvey = function (e) {
            (e = this.getUnbindValue(e)),
              this.valueFromDataCallback && (e = this.valueFromDataCallback(e)),
              this.setQuestionValue(this.valueFromData(e));
          }),
          (t.prototype.updateCommentFromSurvey = function (e) {
            this.questionComment = e;
          }),
          (t.prototype.setQuestionValue = function (e, t) {
            void 0 === t && (t = !0),
              (this.questionValue = e),
              this.allowNotifyValueChanged && this.fireCallback(this.valueChangedCallback),
              t && this.updateIsAnswered();
          }),
          (t.prototype.onSurveyValueChanged = function (e) {
            this.isLoadingFromJson || this.updateDisplayValue();
          }),
          (t.prototype.setVisibleIndex = function (e) {
            return (
              (!this.isVisible ||
                (!this.hasTitle && !h.a.setQuestionVisibleIndexForHiddenTitle) ||
                (this.hideNumber && !h.a.setQuestionVisibleIndexForHiddenNumber)) &&
                (e = -1),
              this.setPropertyValue('visibleIndex', e),
              e < 0 ? 0 : 1
            );
          }),
          (t.prototype.removeElement = function (e) {
            return !1;
          }),
          (t.prototype.supportGoNextPageAutomatic = function () {
            return !1;
          }),
          (t.prototype.supportGoNextPageError = function () {
            return !0;
          }),
          (t.prototype.clearIncorrectValues = function () {}),
          (t.prototype.clearOnDeletingContainer = function () {}),
          (t.prototype.clearErrors = function () {
            this.errors = [];
          }),
          (t.prototype.clearUnusedValues = function () {}),
          (t.prototype.onAnyValueChanged = function (e) {}),
          (t.prototype.checkBindings = function (e, t) {
            if (!this.bindings.isEmpty() && this.data)
              for (var n = this.bindings.getPropertiesByValueName(e), r = 0; r < n.length; r++)
                this[n[r]] = t;
          }),
          (t.prototype.getLocale = function () {
            return this.survey
              ? this.survey.getLocale()
              : this.locOwner
              ? this.locOwner.getLocale()
              : '';
          }),
          (t.prototype.getMarkdownHtml = function (e, t) {
            return this.survey
              ? this.survey.getSurveyMarkdownHtml(this, e, t)
              : this.locOwner
              ? this.locOwner.getMarkdownHtml(e, t)
              : null;
          }),
          (t.prototype.getProcessedText = function (e) {
            return this.textProcessor
              ? this.textProcessor.processText(e, this.useDisplayValuesInTitle)
              : this.locOwner
              ? this.locOwner.getProcessedText(e)
              : e;
          }),
          (t.prototype.getComponentName = function () {
            return f.a.Instance.getRendererByQuestion(this);
          }),
          (t.prototype.getTitleActions = function () {
            var t = e.prototype.getTitleActions.call(this);
            return (
              (this.titleActions = this.survey.getUpdatedQuestionTitleActions(this, t)),
              this.titleActions
            );
          }),
          (t.prototype.isDefaultRendering = function () {
            return (
              !!this.customWidget ||
              'default' === this.renderAs ||
              'default' === this.getComponentName()
            );
          }),
          (t.prototype.getErrorCustomText = function (e, t) {
            return this.survey ? this.survey.getErrorCustomText(e, t) : e;
          }),
          (t.prototype.getValidatorTitle = function () {
            return null;
          }),
          Object.defineProperty(t.prototype, 'validatedValue', {
            get: function () {
              return this.value;
            },
            set: function (e) {
              this.value = e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getAllValues = function () {
            return this.data ? this.data.getAllValues() : null;
          }),
          (t.prototype.dispose = function () {}),
          (t.TextPreprocessorValuesMap = { title: 'processedTitle', require: 'requiredText' }),
          (t.questionCounter = 100),
          n.i(r.c)([n.i(o.e)({ localizable: !0 })], t.prototype, 'description', void 0),
          n.i(r.c)([n.i(o.e)({ defaultValue: 'default' })], t.prototype, 'renderAs', void 0),
          t
        );
      })(a.a);
      o.b.addClass('question', [
        '!name',
        { name: 'state', default: 'default', choices: ['default', 'collapsed', 'expanded'] },
        { name: 'visible:switch', default: !0 },
        { name: 'useDisplayValuesInTitle:boolean', default: !0, layout: 'row' },
        'visibleIf:condition',
        { name: 'width' },
        { name: 'minWidth', default: h.a.minWidth },
        { name: 'maxWidth', default: h.a.maxWidth },
        { name: 'startWithNewLine:boolean', default: !0, layout: 'row' },
        { name: 'indent:number', default: 0, choices: [0, 1, 2, 3], layout: 'row' },
        {
          name: 'page',
          isSerializable: !1,
          visibleIf: function (e) {
            var t = e ? e.survey : null;
            return !t || t.pages.length > 1;
          },
          choices: function (e) {
            var t = e ? e.survey : null;
            return t
              ? t.pages.map(function (e) {
                  return { value: e.name, text: e.title };
                })
              : [];
          },
        },
        { name: 'title:text', serializationProperty: 'locTitle', layout: 'row' },
        {
          name: 'titleLocation',
          default: 'default',
          choices: ['default', 'top', 'bottom', 'left', 'hidden'],
          layout: 'row',
        },
        { name: 'description:text', serializationProperty: 'locDescription', layout: 'row' },
        {
          name: 'descriptionLocation',
          default: 'default',
          choices: ['default', 'underInput', 'underTitle'],
        },
        {
          name: 'hideNumber:boolean',
          dependsOn: 'titleLocation',
          visibleIf: function (e) {
            if (!e) return !0;
            if ('hidden' === e.titleLocation) return !1;
            var t = e ? e.parent : null;
            if (t && 'off' === t.showQuestionNumbers) return !1;
            var n = e ? e.survey : null;
            return (
              !n || 'off' !== n.showQuestionNumbers || (!!t && 'onpanel' === t.showQuestionNumbers)
            );
          },
        },
        'valueName',
        'enableIf:condition',
        'defaultValue:value',
        { name: 'defaultValueExpression:expression', category: 'logic' },
        'correctAnswer:value',
        'isRequired:switch',
        'requiredIf:condition',
        { name: 'requiredErrorText:text', serializationProperty: 'locRequiredErrorText' },
        'readOnly:switch',
        {
          name: 'validators:validators',
          baseClassName: 'surveyvalidator',
          classNamePart: 'validator',
        },
        {
          name: 'bindings:bindings',
          serializationProperty: 'bindings',
          visibleIf: function (e) {
            return e.bindings.getNames().length > 0;
          },
        },
        { name: 'renderAs', default: 'default', visible: !1 },
      ]),
        o.b.addAlterNativeClassName('question', 'questionbase');
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(16),
        o = n(43);
      n.d(t, 'a', function () {
        return s;
      }),
        n.d(t, 'b', function () {
          return l;
        });
      var a = (function () {
          function e(e) {
            (this.processValue = new i.a()),
              (this.parser = new o.a()),
              (this.isAsyncValue = !1),
              (this.hasFunctionValue = !1),
              (this.expression = e);
          }
          return (
            Object.defineProperty(e.prototype, 'expression', {
              get: function () {
                return this.expressionValue;
              },
              set: function (e) {
                this.expression !== e &&
                  ((this.expressionValue = e),
                  (this.operand = this.parser.parseExpression(e)),
                  (this.hasFunctionValue = !!this.canRun() && this.operand.hasFunction()),
                  (this.isAsyncValue = !!this.hasFunction() && this.operand.hasAsyncFunction()));
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getVariables = function () {
              if (!this.operand) return [];
              var e = [];
              return this.operand.setVariables(e), e;
            }),
            (e.prototype.hasFunction = function () {
              return this.hasFunctionValue;
            }),
            Object.defineProperty(e.prototype, 'isAsync', {
              get: function () {
                return this.isAsyncValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.canRun = function () {
              return !!this.operand;
            }),
            (e.prototype.runCore = function (e, t) {
              var n = this;
              if ((void 0 === t && (t = null), !this.operand)) return null;
              if (
                ((this.processValue.values = e), (this.processValue.properties = t), !this.isAsync)
              )
                return this.runValues();
              (this.asyncFuncList = []), this.operand.addToAsyncList(this.asyncFuncList);
              for (var r = 0; r < this.asyncFuncList.length; r++)
                this.asyncFuncList[r].onAsyncReady = function () {
                  n.doAsyncFunctionReady();
                };
              for (var r = 0; r < this.asyncFuncList.length; r++)
                this.asyncFuncList[r].evaluateAsync(this.processValue);
              return !1;
            }),
            (e.prototype.doAsyncFunctionReady = function () {
              for (var e = 0; e < this.asyncFuncList.length; e++)
                if (!this.asyncFuncList[e].isReady) return;
              this.runValues();
            }),
            (e.prototype.runValues = function () {
              var e = this.operand.evaluate(this.processValue);
              return this.doOnComplete(e), e;
            }),
            (e.prototype.doOnComplete = function (e) {}),
            e
          );
        })(),
        s = (function (e) {
          function t() {
            return (null !== e && e.apply(this, arguments)) || this;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.run = function (e, t) {
              return void 0 === t && (t = null), 1 == this.runCore(e, t);
            }),
            (t.prototype.doOnComplete = function (e) {
              this.onRunComplete && this.onRunComplete(1 == e);
            }),
            t
          );
        })(a),
        l = (function (e) {
          function t() {
            return (null !== e && e.apply(this, arguments)) || this;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.run = function (e, t) {
              return void 0 === t && (t = null), this.runCore(e, t);
            }),
            (t.prototype.doOnComplete = function (e) {
              this.onRunComplete && this.onRunComplete(e);
            }),
            t
          );
        })(a);
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(13),
        o = n(1),
        a = n(4),
        s = n(10),
        l = n(6),
        u = n(7);
      n.d(t, 'a', function () {
        return c;
      });
      var c = (function (e) {
        function t(t, n, r) {
          void 0 === n && (n = null), void 0 === r && (r = 'itemvalue');
          var a = e.call(this) || this;
          return (
            (a.typeName = r),
            (a.ownerPropertyName = ''),
            (a.isVisibleValue = !0),
            (a.locTextValue = new i.a(null, !0)),
            (a.locTextValue.onGetTextCallback = function (e) {
              return e || (a.isValueItemEmpty ? null : a.value.toString());
            }),
            n && (a.locText.text = n),
            t && 'object' == typeof t ? a.setData(t) : (a.value = t),
            'itemvalue' != a.getType() && o.a.createProperties(a),
            a.onCreating(),
            a
          );
        }
        return (
          n.i(r.b)(t, e),
          Object.defineProperty(t, 'Separator', {
            get: function () {
              return u.a.itemValueSeparator;
            },
            set: function (e) {
              u.a.itemValueSeparator = e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.createArray = function (e) {
            var n = [];
            return t.setupArray(n, e), n;
          }),
          (t.setupArray = function (e, t) {
            (e.push = function (e) {
              var n = Array.prototype.push.call(this, e);
              return (e.locOwner = t), n;
            }),
              (e.unshift = function (e) {
                var n = Array.prototype.unshift.call(this, e);
                return (e.locOwner = t), n;
              }),
              (e.splice = function (e, i) {
                for (var o, a = [], s = 2; s < arguments.length; s++) a[s - 2] = arguments[s];
                var l = (o = Array.prototype.splice).call.apply(o, n.i(r.d)([this, e, i], a));
                a || (a = []);
                for (var u = 0; u < a.length; u++) a[u].locOwner = t;
                return l;
              });
          }),
          (t.setData = function (e, n) {
            e.length = 0;
            for (var r = 0; r < n.length; r++) {
              var i,
                a = n[r];
              (i = 'function' == typeof a.getType ? o.b.createClass(a.getType()) : new t(null)),
                i.setData(a),
                a.originalItem && (i.originalItem = a.originalItem),
                e.push(i);
            }
          }),
          (t.getData = function (e) {
            for (var t = new Array(), n = 0; n < e.length; n++) t.push(e[n].getData());
            return t;
          }),
          (t.getItemByValue = function (e, t) {
            for (var n = 0; n < e.length; n++) if (a.a.isTwoValueEquals(e[n].value, t)) return e[n];
            return null;
          }),
          (t.getTextOrHtmlByValue = function (e, n) {
            var r = t.getItemByValue(e, n);
            return null !== r ? r.locText.textOrHtml : '';
          }),
          (t.locStrsChanged = function (e) {
            for (var t = 0; t < e.length; t++) e[t].locText.strChanged();
          }),
          (t.runConditionsForItems = function (e, n, r, i, o, a) {
            return void 0 === a && (a = !0), t.runConditionsForItemsCore(e, n, r, i, o, !0, a);
          }),
          (t.runEnabledConditionsForItems = function (e, n, r, i, o) {
            return t.runConditionsForItemsCore(e, null, n, r, i, !1, !0, o);
          }),
          (t.runConditionsForItemsCore = function (e, t, n, r, i, o, a, s) {
            void 0 === a && (a = !0), r || (r = {});
            for (var l = r.item, u = r.choice, c = !1, p = 0; p < e.length; p++) {
              var d = e[p];
              (r.item = d.value), (r.choice = d.value);
              var h = !(!a || !d.getConditionRunner) && d.getConditionRunner(o);
              h || (h = n);
              var f = !0;
              h && (f = h.run(r, i)), f && s && (f = s(d)), t && f && t.push(d);
              f != (o ? d.isVisible : d.isEnabled) &&
                ((c = !0),
                o ? d.setIsVisible && d.setIsVisible(f) : d.setIsEnabled && d.setIsEnabled(f));
            }
            return l ? (r.item = l) : delete r.item, u ? (r.choice = u) : delete r.choice, c;
          }),
          (t.prototype.onCreating = function () {}),
          (t.prototype.getType = function () {
            return this.typeName ? this.typeName : 'itemvalue';
          }),
          (t.prototype.getLocale = function () {
            return this.locText.locale;
          }),
          Object.defineProperty(t.prototype, 'locText', {
            get: function () {
              return this.locTextValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setLocText = function (e) {
            this.locTextValue = e;
          }),
          Object.defineProperty(t.prototype, 'locOwner', {
            get: function () {
              return this.locText.owner;
            },
            set: function (e) {
              this.locText.owner = e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'value', {
            get: function () {
              return this.itemValue;
            },
            set: function (e) {
              var t = this.itemValue;
              if (
                ((this.itemValue = e),
                this.propertyValueChanged('value', t, this.itemValue),
                this.itemValue)
              ) {
                var n = this.itemValue.toString(),
                  r = n.indexOf(u.a.itemValueSeparator);
                r > -1
                  ? ((this.itemValue = n.slice(0, r)), (this.text = n.slice(r + 1)))
                  : this.hasText || this.locText.onChanged();
              }
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasText', {
            get: function () {
              return !!this.locText.pureText;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'pureText', {
            get: function () {
              return this.locText.pureText;
            },
            set: function (e) {
              this.text = e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'text', {
            get: function () {
              return this.locText.calculatedText;
            },
            set: function (e) {
              var t = this.locText.text;
              (this.locText.text = e), this.propertyValueChanged('text', t, e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'calculatedText', {
            get: function () {
              return this.locText.calculatedText;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getData = function () {
            var e = this.toJSON();
            return (
              e.value && e.value.pos && delete e.value.pos,
              1 != Object.keys(e).length || a.a.isValueEmpty(e.value) ? e : this.value
            );
          }),
          (t.prototype.toJSON = function () {
            var e = {},
              t = o.b.getProperties(this.getType());
            (t && 0 != t.length) || (t = o.b.getProperties('itemvalue'));
            for (var n = new o.c(), r = 0; r < t.length; r++) n.valueToJson(this, e, t[r]);
            return e;
          }),
          (t.prototype.setData = function (e) {
            if (!a.a.isValueEmpty(e))
              if (void 0 !== e.value) {
                var t = e;
                'function' == typeof e.toJSON && (t = e.toJSON()), new o.c().toObject(t, this);
              } else this.value = e;
          }),
          Object.defineProperty(t.prototype, 'visibleIf', {
            get: function () {
              return this.getPropertyValue('visibleIf', '');
            },
            set: function (e) {
              this.setPropertyValue('visibleIf', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'enableIf', {
            get: function () {
              return this.getPropertyValue('enableIf', '');
            },
            set: function (e) {
              this.setPropertyValue('enableIf', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isVisible', {
            get: function () {
              return this.isVisibleValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setIsVisible = function (e) {
            this.isVisibleValue = e;
          }),
          Object.defineProperty(t.prototype, 'isEnabled', {
            get: function () {
              return this.getPropertyValue('isEnabled', !0);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setIsEnabled = function (e) {
            this.setPropertyValue('isEnabled', e);
          }),
          (t.prototype.addUsedLocales = function (e) {
            this.AddLocStringToUsedLocales(this.locTextValue, e);
          }),
          (t.prototype.onPropertyValueChanged = function (e, t, n) {
            var r = 'itemValuePropertyChanged';
            this.locOwner && this.locOwner[r] && this.locOwner[r](this, e, t, n);
          }),
          (t.prototype.getConditionRunner = function (e) {
            return e ? this.getVisibleConditionRunner() : this.getEnableConditionRunner();
          }),
          (t.prototype.getVisibleConditionRunner = function () {
            return this.visibleIf
              ? (this.visibleConditionRunner ||
                  (this.visibleConditionRunner = new s.a(this.visibleIf)),
                (this.visibleConditionRunner.expression = this.visibleIf),
                this.visibleConditionRunner)
              : null;
          }),
          (t.prototype.getEnableConditionRunner = function () {
            return this.enableIf
              ? (this.enableConditionRunner ||
                  (this.enableConditionRunner = new s.a(this.enableIf)),
                (this.enableConditionRunner.expression = this.enableIf),
                this.enableConditionRunner)
              : null;
          }),
          Object.defineProperty(t.prototype, 'isValueItemEmpty', {
            get: function () {
              return !this.itemValue && 0 !== this.itemValue && !1 !== this.itemValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          t
        );
      })(l.b);
      (l.b.createItemValue = function (e, t) {
        var n = null;
        return (
          (n = t
            ? o.c.metaData.createClass(t, {})
            : 'function' == typeof e.getType
            ? new c(null, void 0, e.getType())
            : new c(null)),
          n.setData(e),
          n
        );
      }),
        (l.b.itemValueLocStrChanged = function (e) {
          c.locStrsChanged(e);
        }),
        (o.d.getItemValuesDefaultValue = function (e) {
          var t = new Array();
          return c.setData(t, e || []), t;
        }),
        o.b.addClass(
          'itemvalue',
          [
            'value',
            { name: 'text', serializationProperty: 'locText' },
            { name: 'visibleIf:condition', showMode: 'form' },
            { name: 'enableIf:condition', showMode: 'form' },
          ],
          function (e) {
            return new c(e);
          }
        );
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(2),
        o = n(6);
      n.d(t, 'c', function () {
        return a;
      }),
        n.d(t, 'a', function () {
          return s;
        }),
        n.d(t, 'd', function () {
          return l;
        }),
        n.d(t, 'l', function () {
          return u;
        }),
        n.d(t, 'g', function () {
          return c;
        }),
        n.d(t, 'f', function () {
          return p;
        }),
        n.d(t, 'e', function () {
          return d;
        }),
        n.d(t, 'k', function () {
          return h;
        }),
        n.d(t, 'j', function () {
          return f;
        }),
        n.d(t, 'i', function () {
          return m;
        }),
        n.d(t, 'h', function () {
          return g;
        }),
        n.d(t, 'b', function () {
          return y;
        });
      var a = (function (e) {
          function t(t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            return (r.text = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'required';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('requiredError');
            }),
            t
          );
        })(o.d),
        s = (function (e) {
          function t(t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            return (r.text = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'requireoneanswer';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('requiredErrorInPanel');
            }),
            t
          );
        })(o.d),
        l = (function (e) {
          function t(t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            return (r.text = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'requirenumeric';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('numericError');
            }),
            t
          );
        })(o.d),
        u = (function (e) {
          function t(t, n) {
            void 0 === n && (n = null);
            var r = e.call(this, null, n) || this;
            return (r.maxSize = t), (r.locText.text = r.getText()), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'exceedsize';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('exceedMaxSize').format(this.getTextSize());
            }),
            (t.prototype.getTextSize = function () {
              var e = ['Bytes', 'KB', 'MB', 'GB', 'TB'],
                t = [0, 0, 2, 3, 3];
              if (0 === this.maxSize) return '0 Byte';
              var n = Math.floor(Math.log(this.maxSize) / Math.log(1024));
              return (this.maxSize / Math.pow(1024, n)).toFixed(t[n]) + ' ' + e[n];
            }),
            t
          );
        })(o.d),
        c = (function (e) {
          function t(t, n, r) {
            void 0 === r && (r = null);
            var i = e.call(this, null, r) || this;
            return (i.status = t), (i.response = n), i;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'webrequest';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('urlRequestError').format(this.status, this.response);
            }),
            t
          );
        })(o.d),
        p = (function (e) {
          function t(t, n) {
            void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            return (r.text = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'webrequestempty';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('urlGetChoicesError');
            }),
            t
          );
        })(o.d),
        d = (function (e) {
          function t(t, n) {
            void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            return (r.text = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'otherempty';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('otherRequiredError');
            }),
            t
          );
        })(o.d),
        h = (function (e) {
          function t(t, n) {
            void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            return (r.text = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'uploadingfile';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('uploadingFile');
            }),
            t
          );
        })(o.d),
        f = (function (e) {
          function t(t, n) {
            void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            return (r.text = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'requiredinallrowserror';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('requiredInAllRowsError');
            }),
            t
          );
        })(o.d),
        m = (function (e) {
          function t(t, n) {
            void 0 === n && (n = null);
            var r = e.call(this, null, n) || this;
            return (r.minRowCount = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'minrowcounterror';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('minRowCountError').format(this.minRowCount);
            }),
            t
          );
        })(o.d),
        g = (function (e) {
          function t(t, n) {
            void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            return (r.text = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'keyduplicationerror';
            }),
            (t.prototype.getDefaultText = function () {
              return i.a.getString('keyDuplicationError');
            }),
            t
          );
        })(o.d),
        y = (function (e) {
          function t(t, n) {
            void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            return (r.text = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getErrorType = function () {
              return 'custom';
            }),
            t
          );
        })(o.d);
    },
    function (e, t, n) {
      'use strict';
      var r = n(4),
        i = n(2),
        o = n(7);
      n.d(t, 'a', function () {
        return a;
      }),
        n.d(t, 'b', function () {
          return s;
        });
      var a = (function () {
          function e(e, t, n) {
            void 0 === t && (t = !1),
              (this.owner = e),
              (this.useMarkdown = t),
              (this.name = n),
              (this.values = {}),
              (this.htmlValues = {}),
              (this.renderedText = null),
              (this.calculatedTextValue = null),
              (this.onGetTextCallback = null),
              this.onCreating();
          }
          return (
            Object.defineProperty(e, 'defaultLocale', {
              get: function () {
                return o.a.defaultLocaleName;
              },
              set: function (e) {
                o.a.defaultLocaleName = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'locale', {
              get: function () {
                return this.owner && this.owner.getLocale ? this.owner.getLocale() : '';
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.strChanged = function () {
              null !== this.renderedText &&
                ((this.calculatedTextValue = this.calText()),
                this.renderedText !== this.calculatedTextValue &&
                  ((this.renderedText = null),
                  (this.calculatedTextValue = null),
                  this.onChanged()));
            }),
            Object.defineProperty(e.prototype, 'text', {
              get: function () {
                return this.pureText;
              },
              set: function (e) {
                this.setLocaleText(this.locale, e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'calculatedText', {
              get: function () {
                return (
                  (this.renderedText =
                    null !== this.calculatedTextValue ? this.calculatedTextValue : this.calText()),
                  (this.calculatedTextValue = null),
                  this.renderedText
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.calText = function () {
              var e = this.pureText;
              return (
                e &&
                  this.owner &&
                  this.owner.getProcessedText &&
                  e.indexOf('{') > -1 &&
                  (e = this.owner.getProcessedText(e)),
                this.onGetTextCallback && (e = this.onGetTextCallback(e)),
                e
              );
            }),
            Object.defineProperty(e.prototype, 'pureText', {
              get: function () {
                var e = this.locale;
                e || (e = o.a.defaultLocaleName);
                var t = this.getValue(e);
                return (
                  t || e != o.a.defaultLocaleName || (t = this.getValue(i.a.defaultLocale)),
                  t || e === o.a.defaultLocaleName || (t = this.getValue(o.a.defaultLocaleName)),
                  t || (t = ''),
                  t
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'hasHtml', {
              get: function () {
                return this.hasHtmlValue();
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'html', {
              get: function () {
                return this.hasHtml ? this.getHtmlValue() : '';
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'isEmpty', {
              get: function () {
                return 0 == this.getValuesKeys().length;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'textOrHtml', {
              get: function () {
                return this.hasHtml ? this.getHtmlValue() : this.calculatedText;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'renderedHtml', {
              get: function () {
                return this.textOrHtml;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getLocaleText = function (e) {
              e || (e = o.a.defaultLocaleName);
              var t = this.getValue(e);
              return t || '';
            }),
            (e.prototype.setLocaleText = function (e, t) {
              t != this.getLocaleText(e) &&
                ((t &&
                  e &&
                  e != o.a.defaultLocaleName &&
                  !this.getValue(e) &&
                  t == this.getLocaleText(o.a.defaultLocaleName)) ||
                  (e || (e = o.a.defaultLocaleName),
                  delete this.htmlValues[e],
                  t
                    ? 'string' == typeof t &&
                      (e != o.a.defaultLocaleName && t == this.getLocaleText(o.a.defaultLocaleName)
                        ? this.setLocaleText(e, null)
                        : (this.setValue(e, t),
                          e == o.a.defaultLocaleName && this.deleteValuesEqualsToDefault(t)))
                    : this.getValue(e) && this.deleteValue(e),
                  this.strChanged()));
            }),
            (e.prototype.hasNonDefaultText = function () {
              var e = this.getValuesKeys();
              return 0 != e.length && (e.length > 1 || e[0] != o.a.defaultLocaleName);
            }),
            (e.prototype.getLocales = function () {
              var e = this.getValuesKeys();
              return 0 == e.length ? [] : e;
            }),
            (e.prototype.getJson = function () {
              if (this.sharedData) return this.sharedData.getJson();
              var e = this.getValuesKeys();
              return 0 == e.length
                ? null
                : 1 != e.length ||
                  e[0] != o.a.defaultLocaleName ||
                  o.a.serializeLocalizableStringAsObject
                ? this.values
                : this.values[e[0]];
            }),
            (e.prototype.setJson = function (e) {
              if (this.sharedData) return void this.sharedData.setJson(e);
              if (((this.values = {}), (this.htmlValues = {}), e)) {
                if ('string' == typeof e) this.setLocaleText(null, e);
                else for (var t in e) this.setLocaleText(t, e[t]);
                this.strChanged();
              }
            }),
            (e.prototype.equals = function (e) {
              return this.sharedData
                ? this.sharedData.equals(e)
                : !(!e || !e.values) && r.a.isTwoValueEquals(this.values, e.values);
            }),
            (e.prototype.onChanged = function () {
              this.onStrChanged && this.onStrChanged();
            }),
            (e.prototype.onCreating = function () {}),
            (e.prototype.hasHtmlValue = function () {
              if (!this.owner || !this.useMarkdown) return !1;
              var e = this.calculatedText;
              if (!e) return !1;
              var t = this.locale;
              return (
                t || (t = o.a.defaultLocaleName),
                (this.htmlValues[t] = this.owner.getMarkdownHtml(e, this.name)),
                !!this.htmlValues[t]
              );
            }),
            (e.prototype.getHtmlValue = function () {
              var e = this.locale;
              return e || (e = o.a.defaultLocaleName), this.htmlValues[e];
            }),
            (e.prototype.deleteValuesEqualsToDefault = function (e) {
              for (var t = this.getValuesKeys(), n = 0; n < t.length; n++)
                t[n] != o.a.defaultLocaleName && this.getValue(t[n]) == e && this.deleteValue(t[n]);
            }),
            (e.prototype.getValue = function (e) {
              return this.sharedData ? this.sharedData.getValue(e) : this.values[e];
            }),
            (e.prototype.setValue = function (e, t) {
              this.sharedData ? this.sharedData.setValue(e, t) : (this.values[e] = t);
            }),
            (e.prototype.deleteValue = function (e) {
              this.sharedData ? this.sharedData.deleteValue(e) : delete this.values[e];
            }),
            (e.prototype.getValuesKeys = function () {
              return this.sharedData ? this.sharedData.getValuesKeys() : Object.keys(this.values);
            }),
            (e.SerializeAsObject = !1),
            e
          );
        })(),
        s = (function () {
          function e(e) {
            (this.owner = e), (this.values = {});
          }
          return (
            Object.defineProperty(e.prototype, 'locale', {
              get: function () {
                return this.owner && this.owner.getLocale ? this.owner.getLocale() : '';
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'value', {
              get: function () {
                return this.getValue('');
              },
              set: function (e) {
                this.setValue('', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'text', {
              get: function () {
                return this.value.join('\n');
              },
              set: function (e) {
                this.value = e ? e.split('\n') : [];
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getValue = function (e) {
              if (((e = this.getLocale(e)), this.values[e])) return this.values[e];
              var t = o.a.defaultLocaleName;
              return e !== t ? this.values[t] : [];
            }),
            (e.prototype.setValue = function (e, t) {
              (e = this.getLocale(e)),
                t && 0 != t.length ? (this.values[e] = t) : delete this.values[e];
            }),
            Object.defineProperty(e.prototype, 'isEmpty', {
              get: function () {
                return 0 == this.getValuesKeys().length;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getLocale = function (e) {
              return e || (e = this.locale) || o.a.defaultLocaleName;
            }),
            (e.prototype.getJson = function () {
              var e = this.getValuesKeys();
              return 0 == e.length
                ? null
                : 1 != e.length ||
                  e[0] != o.a.defaultLocaleName ||
                  o.a.serializeLocalizableStringAsObject
                ? this.values
                : this.values[e[0]];
            }),
            (e.prototype.setJson = function (e) {
              if (((this.values = {}), e))
                if (Array.isArray(e)) this.setValue(null, e);
                else for (var t in e) this.setValue(t, e[t]);
            }),
            (e.prototype.getValuesKeys = function () {
              return Object.keys(this.values);
            }),
            e
          );
        })();
    },
    function (e, t, n) {
      'use strict';
      n.d(t, 'b', function () {
        return r;
      }),
        n.d(t, 'a', function () {
          return i;
        });
      var r = {
          currentType: '',
          getCss: function () {
            var e = this.currentType ? this[this.currentType] : i;
            return e || (e = i), e;
          },
        },
        i = {
          root: 'sv_main sv_default_css',
          container: 'sv_container',
          header: 'sv_header',
          body: 'sv_body',
          bodyEmpty: 'sv_body sv_body_empty',
          footer: 'sv_nav',
          title: '',
          description: '',
          logo: 'sv_logo',
          logoImage: 'sv_logo__image',
          headerText: 'sv_header__text',
          navigationButton: '',
          completedPage: 'sv_completed_page',
          navigation: {
            complete: 'sv_complete_btn',
            prev: 'sv_prev_btn',
            next: 'sv_next_btn',
            start: 'sv_start_btn',
            preview: 'sv_preview_btn',
            edit: 'sv_edit_btn',
          },
          progress: 'sv_progress',
          progressBar: 'sv_progress_bar',
          progressTextInBar: 'sv-hidden',
          progressButtonsContainerCenter: 'sv_progress-buttons__container-center',
          progressButtonsContainer: 'sv_progress-buttons__container',
          progressButtonsImageButtonLeft: 'sv_progress-buttons__image-button-left',
          progressButtonsImageButtonRight: 'sv_progress-buttons__image-button-right',
          progressButtonsImageButtonHidden: 'sv_progress-buttons__image-button--hidden',
          progressButtonsListContainer: 'sv_progress-buttons__list-container',
          progressButtonsList: 'sv_progress-buttons__list',
          progressButtonsListElementPassed: 'sv_progress-buttons__list-element--passed',
          progressButtonsListElementCurrent: 'sv_progress-buttons__list-element--current',
          progressButtonsPageTitle: 'sv_progress-buttons__page-title',
          progressButtonsPageDescription: 'sv_progress-buttons__page-description',
          page: { root: 'sv_p_root', title: 'sv_page_title', description: '' },
          pageTitle: 'sv_page_title',
          pageDescription: '',
          row: 'sv_row',
          question: {
            mainRoot: 'sv_q sv_qstn',
            flowRoot: 'sv_q_flow sv_qstn',
            header: '',
            headerLeft: 'title-left',
            content: '',
            contentLeft: 'content-left',
            titleLeftRoot: 'sv_qstn_left',
            requiredText: 'sv_q_required_text',
            title: 'sv_q_title',
            titleExpandable: 'sv_q_title_expandable',
            number: 'sv_q_num',
            description: 'sv_q_description',
            comment: '',
            required: '',
            titleRequired: '',
            hasError: '',
            indent: 20,
            footer: 'sv_q_footer',
            formGroup: 'form-group',
            asCell: 'sv_matrix_cell',
            icon: 'sv_question_icon',
            iconExpanded: 'sv_expanded',
          },
          panel: {
            title: 'sv_p_title',
            titleExpandable: 'sv_p_title_expandable',
            titleOnError: '',
            icon: 'sv_panel_icon',
            iconExpanded: 'sv_expanded',
            description: 'sv_p_description',
            container: 'sv_p_container',
            footer: 'sv_p_footer',
            number: 'sv_q_num',
            requiredText: 'sv_q_required_text',
          },
          error: {
            root: 'sv_q_erbox',
            icon: '',
            item: '',
            locationTop: 'sv_qstn_error_top',
            locationBottom: 'sv_qstn_error_bottom',
          },
          boolean: {
            root: 'sv_qcbc sv_qbln',
            item: 'sv-boolean',
            control: 'sv-visuallyhidden',
            itemChecked: 'sv-boolean--checked checked',
            itemIndeterminate: 'sv-boolean--indeterminate',
            itemDisabled: 'sv-boolean--disabled',
            switch: 'sv-boolean__switch',
            slider: 'sv-boolean__slider',
            label: 'sv-boolean__label ',
            disabledLabel: 'sv-boolean__label--disabled',
            materialDecorator: 'sv-item__decorator sv-boolean__decorator ',
            itemDecorator: 'sv-item__svg  sv-boolean__svg',
            checkedPath: 'sv-boolean__checked-path',
            uncheckedPath: 'sv-boolean__unchecked-path',
            indeterminatePath: 'sv-boolean__indeterminate-path',
          },
          checkbox: {
            root: 'sv_qcbc sv_qcbx',
            item: 'sv_q_checkbox',
            itemSelectAll: 'sv_q_checkbox_selectall',
            itemNone: 'sv_q_checkbox_none',
            itemChecked: 'checked',
            itemInline: 'sv_q_checkbox_inline',
            label: 'sv_q_checkbox_label',
            labelChecked: '',
            itemControl: 'sv_q_checkbox_control_item',
            itemDecorator: 'sv-hidden',
            controlLabel: 'sv_q_checkbox_control_label',
            materialDecorator: 'checkbox-material',
            other: 'sv_q_other sv_q_checkbox_other',
            column: 'sv_q_select_column',
          },
          comment: '',
          dropdown: {
            root: '',
            control: 'sv_q_dropdown_control',
            selectWrapper: 'sv_select_wrapper',
            other: 'sv_q_dd_other',
          },
          html: { root: '' },
          image: { root: 'sv_q_image', image: 'sv_image_image' },
          matrix: {
            root: 'sv_q_matrix',
            label: 'sv_q_m_label',
            itemChecked: 'checked',
            itemDecorator: 'sv-hidden',
            cellText: 'sv_q_m_cell_text',
            cellTextSelected: 'sv_q_m_cell_selected',
            cellLabel: 'sv_q_m_cell_label',
          },
          matrixdropdown: {
            root: 'sv_q_matrix_dropdown',
            cell: 'sv_matrix_cell',
            headerCell: 'sv_matrix_cell_header',
            row: 'sv_matrix_row',
            detailRow: 'sv_matrix_detail_row',
            detailRowText: 'sv_matrix_cell_detail_rowtext',
            detailCell: 'sv_matrix_cell_detail',
            detailButton: 'sv_matrix_cell_detail_button',
            detailButtonExpanded: 'sv_matrix_cell_detail_button_expanded',
            detailIcon: 'sv_detail_panel_icon',
            detailIconExpanded: 'sv_detail_expanded',
            detailPanelCell: 'sv_matrix_cell_detail_panel',
          },
          matrixdynamic: {
            root: 'sv_q_matrix_dynamic',
            button: 'sv_matrix_dynamic_button',
            buttonAdd: '',
            buttonRemove: '',
            iconAdd: '',
            iconRemove: '',
            cell: 'sv_matrix_cell',
            headerCell: 'sv_matrix_cell_header',
            row: 'sv_matrix_row',
            detailRow: 'sv_matrix_detail_row',
            detailCell: 'sv_matrix_cell_detail',
            detailButton: 'sv_matrix_cell_detail_button',
            detailButtonExpanded: 'sv_matrix_cell_detail_button_expanded',
            detailIcon: 'sv_detail_panel_icon',
            detailIconExpanded: 'sv_detail_expanded',
            detailPanelCell: 'sv_matrix_cell_detail_panel',
          },
          paneldynamic: {
            root: 'sv_panel_dynamic',
            title: 'sv_p_title',
            button: '',
            buttonAdd: 'sv-paneldynamic__add-btn',
            buttonRemove: '',
            buttonPrev: 'sv-paneldynamic__prev-btn',
            buttonNext: 'sv-paneldynamic__next-btn',
            progressContainer: 'sv-paneldynamic__progress-container',
            progress: 'sv-progress',
            progressBar: 'sv-progress__bar',
            progressText: 'sv-paneldynamic__progress-text',
          },
          multipletext: {
            root: 'sv_q_mt',
            itemTitle: 'sv_q_mt_title',
            row: 'sv_q_mt_row',
            itemValue: 'sv_q_mt_item_value sv_q_text_root',
          },
          radiogroup: {
            root: 'sv_qcbc',
            item: 'sv_q_radiogroup',
            itemChecked: 'checked',
            itemInline: 'sv_q_radiogroup_inline',
            itemDecorator: 'sv-hidden',
            label: 'sv_q_radiogroup_label',
            labelChecked: '',
            itemControl: 'sv_q_radiogroup_control_item',
            controlLabel: '',
            materialDecorator: 'circle',
            other: 'sv_q_other sv_q_radiogroup_other',
            clearButton: 'sv_q_radiogroup_clear',
            column: 'sv_q_select_column',
          },
          imagepicker: {
            root: 'sv_imgsel',
            item: 'sv_q_imgsel',
            itemChecked: 'checked',
            label: 'sv_q_imgsel_label',
            itemControl: 'sv_q_imgsel_control_item',
            image: 'sv_q_imgsel_image',
            itemInline: 'sv_q_imagepicker_inline',
            itemText: 'sv_q_imgsel_text',
            clearButton: 'sv_q_radiogroup_clear',
          },
          rating: {
            root: 'sv_q_rating',
            item: 'sv_q_rating_item',
            selected: 'active',
            minText: 'sv_q_rating_min_text',
            itemText: 'sv_q_rating_item_text',
            maxText: 'sv_q_rating_max_text',
            disabled: '',
          },
          text: 'sv_q_text_root',
          expression: '',
          file: {
            root: 'sv_q_file',
            placeholderInput: 'sv-visuallyhidden',
            preview: 'sv_q_file_preview',
            removeButton: 'sv_q_file_remove_button',
            fileInput: 'sv-visuallyhidden',
            removeFile: 'sv_q_file_remove',
            removeFileSvg: 'sv-hidden',
            fileDecorator: 'sv-file__decorator',
            fileSignBottom: 'sv-hidden',
            removeButtonBottom: 'sv-hidden',
            chooseFile: 'sv_q_file_choose_button',
            noFileChosen: 'sv_q_file_placeholder',
          },
          signaturepad: {
            root: 'sv_q_signaturepad sjs_sp_container',
            controls: 'sjs_sp_controls',
            clearButton: 'sjs_sp_clear',
          },
          saveData: { root: '', saving: '', error: '', success: '', saveAgainButton: '' },
          window: {
            root: 'sv_window',
            body: 'sv_window_content',
            header: {
              root: 'sv_window_title',
              title: '',
              button: '',
              buttonExpanded: '',
              buttonCollapsed: '',
            },
          },
        };
      r.standard = i;
    },
    function (e, t, n) {
      'use strict';
      var r = n(5);
      n.n(r);
      n.d(t, 'a', function () {
        return i;
      });
      var i = (function () {
        function e(t) {
          (this.element = t),
            t.iteratePropertiesHash(function (t, n) {
              e.doIterateProperties(t, n);
            }),
            (t.createArrayCoreHandler = function (e, n) {
              var i = r.observableArray();
              return (
                (i().onArrayChanged = function () {
                  t.isLoadingFromJson || i.notifySubscribers();
                }),
                (e[n] = i),
                i()
              );
            }),
            (t.getPropertyValueCoreHandler = function (e, t) {
              return (
                void 0 === e[t] && (e[t] = r.observable()),
                'function' == typeof e[t] ? e[t]() : e[t]
              );
            }),
            (t.setPropertyValueCoreHandler = function (e, t, n) {
              return void 0 !== e[t] ? e[t](n) : (e[t] = r.observable(n));
            });
        }
        return (
          (e.doIterateProperties = function (e, t) {
            var n = e[t];
            'function' !== n &&
              (Array.isArray(n)
                ? ((e[t] = r.observableArray(n)),
                  (n.onArrayChanged = function () {
                    return e[t].notifySubscribers();
                  }))
                : (e[t] = r.observable(n)));
          }),
          (e.prototype.dispose = function () {
            this.element.iteratePropertiesHash(function (e, t) {
              delete e[t];
            }),
              (this.element.createArrayCoreHandler = void 0),
              (this.element.getPropertyValueCoreHandler = void 0),
              (this.element.setPropertyValueCoreHandler = void 0);
          }),
          e
        );
      })();
    },
    function (e, t, n) {
      'use strict';
      var r = n(4);
      n.d(t, 'a', function () {
        return i;
      });
      var i = (function () {
        function e() {
          (this.values = null), (this.properties = null);
        }
        return (
          (e.prototype.getFirstName = function (e, t) {
            if ((void 0 === t && (t = null), !e)) return e;
            var n = '';
            if (t && (n = this.getFirstPropertyName(e, t))) return n;
            for (var r = 0; r < e.length; r++) {
              var i = e[r];
              if ('.' == i || '[' == i) break;
              n += i;
            }
            return n;
          }),
          (e.prototype.hasValue = function (e, t) {
            return (
              void 0 === t && (t = null), t || (t = this.values), this.getValueCore(e, t).hasValue
            );
          }),
          (e.prototype.getValue = function (e, t) {
            return (
              void 0 === t && (t = null), t || (t = this.values), this.getValueCore(e, t).value
            );
          }),
          (e.prototype.setValue = function (e, t, n) {
            if (t) {
              var r = this.getNonNestedObject(e, t, !0);
              r && ((e = r.value), (t = r.text), e && t && (e[t] = n));
            }
          }),
          (e.prototype.getValueInfo = function (e) {
            if (e.path)
              return (
                (e.value = this.getValueFromPath(e.path, this.values)),
                (e.hasValue = null !== e.value && !r.a.isValueEmpty(e.value)),
                void (
                  !e.hasValue &&
                  e.path.length > 1 &&
                  'length' == e.path[e.path.length - 1] &&
                  ((e.hasValue = !0), (e.value = 0))
                )
              );
            var t = this.getValueCore(e.name, this.values);
            (e.value = t.value), (e.hasValue = t.hasValue), (e.path = t.hasValue ? t.path : null);
          }),
          (e.prototype.getValueFromPath = function (e, t) {
            for (var n = 0; t && n < e.length; ) {
              var i = e[n];
              if (r.a.isNumber(i) && Array.isArray(t) && i >= t.length) return null;
              (t = t[i]), n++;
            }
            return t;
          }),
          (e.prototype.getValueCore = function (e, t) {
            var n = { hasValue: !1, value: null, path: null },
              i = t;
            if (!i && 0 !== i && !1 !== i) return n;
            e &&
              e.lastIndexOf('.length') > -1 &&
              e.lastIndexOf('.length') === e.length - '.length'.length &&
              ((n.value = 0), (n.hasValue = !0));
            var o = this.getNonNestedObject(i, e, !1);
            return o
              ? ((n.path = o.path),
                (n.value = o.text ? this.getObjectValue(o.value, o.text) : o.value),
                (n.hasValue = !r.a.isValueEmpty(n.value)),
                n)
              : n;
          }),
          (e.prototype.getNonNestedObject = function (e, t, n) {
            for (var i = this.getFirstPropertyName(t, e, n), o = i ? [i] : null; t != i && e; ) {
              if ('[' == t[0]) {
                var a = this.getObjInArray(e, t);
                if (!a) return null;
                (e = a.value), (t = a.text), o.push(a.index);
              } else {
                if (!i && t == this.getFirstName(t)) return { value: e, text: t, path: o };
                if (((e = this.getObjectValue(e, i)), r.a.isValueEmpty(e) && !n)) return null;
                t = t.substr(i.length);
              }
              t && '.' == t[0] && (t = t.substr(1)),
                (i = this.getFirstPropertyName(t, e, n)),
                i && o.push(i);
            }
            return { value: e, text: t, path: o };
          }),
          (e.prototype.getObjInArray = function (e, t) {
            if (!Array.isArray(e)) return null;
            for (var n = 1, r = ''; n < t.length && ']' != t[n]; ) (r += t[n]), n++;
            return (
              (t = n < t.length ? t.substr(n + 1) : ''),
              (n = this.getIntValue(r)),
              n < 0 || n >= e.length ? null : { value: e[n], text: t, index: n }
            );
          }),
          (e.prototype.getFirstPropertyName = function (e, t, n) {
            if ((void 0 === n && (n = !1), !e)) return e;
            if ((t || (t = {}), t.hasOwnProperty(e))) return e;
            var r = e.toLowerCase(),
              i = r[0],
              o = i.toUpperCase();
            for (var a in t) {
              var s = a[0];
              if (s === o || s === i) {
                var l = a.toLowerCase();
                if (l == r) return a;
                if (r.length <= l.length) continue;
                var u = r[l.length];
                if ('.' != u && '[' != u) continue;
                if (l == r.substr(0, l.length)) return a;
              }
            }
            if (n && '[' !== e[0]) {
              var c = e.indexOf('.');
              return c > -1 && ((e = e.substr(0, c)), (t[e] = {})), e;
            }
            return '';
          }),
          (e.prototype.getObjectValue = function (e, t) {
            return t ? e[t] : null;
          }),
          (e.prototype.getIntValue = function (e) {
            return '0' == e || ((0 | e) > 0 && e % 1 == 0) ? Number(e) : -1;
          }),
          e
        );
      })();
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(8);
      n.d(t, 'b', function () {
        return o;
      }),
        n.d(t, 'a', function () {
          return a;
        });
      var o = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return n.onCreated(), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onCreated = function () {}),
            Object.defineProperty(t.prototype, 'isOtherSelected', {
              get: function () {
                return this.question.isOtherSelected;
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(i.a),
        a = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.question.koAfterRender = n.koAfterRender), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.koAfterRender = function (e, t) {
              var n = e[0];
              '#text' == n.nodeName && (n.data = ''),
                (n = e[e.length - 1]),
                '#text' == n.nodeName && (n.data = '');
            }),
            t
          );
        })(o);
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(4),
        a = n(6),
        s = n(9),
        l = n(10),
        u = n(3),
        c = n(12),
        p = n(7),
        d = n(26);
      n.d(t, 'c', function () {
        return h;
      }),
        n.d(t, 'a', function () {
          return f;
        }),
        n.d(t, 'd', function () {
          return m;
        }),
        n.d(t, 'b', function () {
          return g;
        });
      var h = (function () {
          function e(e, t, n) {
            void 0 === n && (n = -1),
              (this.source = e),
              (this.target = t),
              (this.nestedPanelDepth = n);
          }
          return e;
        })(),
        f = (function (e) {
          function t(n) {
            var r = e.call(this) || this;
            return (
              (r.panel = n),
              (r._scrollableParent = void 0),
              (r._updateVisibility = void 0),
              (r.idValue = t.getRowId()),
              (r.isNeedRender = !p.a.lazyRowsRendering),
              (r.visible = n.areInvisibleElementsShowing),
              r.createNewArray('elements'),
              r
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.getRowId = function () {
              return 'pr_' + t.rowCounter++;
            }),
            (t.prototype.startLazyRendering = function (e) {
              var t = this;
              (this._scrollableParent = n.i(d.a)(e)),
                (this._updateVisibility = function () {
                  var r = n.i(d.b)(e, 50);
                  !t.isNeedRender && r && ((t.isNeedRender = !0), t.stopLazyRendering());
                }),
                setTimeout(function () {
                  t._scrollableParent.addEventListener('scroll', t._updateVisibility),
                    t._updateVisibility();
                }, 10);
            }),
            (t.prototype.stopLazyRendering = function () {
              this._scrollableParent &&
                this._updateVisibility &&
                this._scrollableParent.removeEventListener('scroll', this._updateVisibility),
                (this._scrollableParent = void 0),
                (this._updateVisibility = void 0);
            }),
            Object.defineProperty(t.prototype, 'id', {
              get: function () {
                return this.idValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'elements', {
              get: function () {
                return this.getPropertyValue('elements');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'visible', {
              get: function () {
                return this.getPropertyValue('visible', !0);
              },
              set: function (e) {
                this.setPropertyValue('visible', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isNeedRender', {
              get: function () {
                return this.getPropertyValue('isneedrender', !0);
              },
              set: function (e) {
                this.setPropertyValue('isneedrender', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'visibleElements', {
              get: function () {
                return this.elements.filter(function (e) {
                  return e.isVisible;
                });
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.updateVisible = function () {
              (this.visible = this.calcVisible()), this.setWidth();
            }),
            (t.prototype.addElement = function (e) {
              this.elements.push(e), this.updateVisible();
            }),
            Object.defineProperty(t.prototype, 'index', {
              get: function () {
                return this.panel.rows.indexOf(this);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setWidth = function () {
              var e = this.visibleElements.length;
              if (0 != e) {
                for (var t = 0, n = [], r = 0; r < this.elements.length; r++) {
                  var i = this.elements[r];
                  if ((this.setElementMaxMinWidth(i), i.isVisible)) {
                    var o = this.getElementWidth(i);
                    o && ((i.renderWidth = this.getRenderedWidthFromWidth(o)), n.push(i)),
                      (i.rightIndent = t < e - 1 ? 1 : 0),
                      t++;
                  } else i.renderWidth = '';
                }
                for (var r = 0; r < this.elements.length; r++) {
                  var i = this.elements[r];
                  !i.isVisible ||
                    n.indexOf(i) > -1 ||
                    (0 == n.length
                      ? (i.renderWidth = (100 / e).toFixed(6) + '%')
                      : (i.renderWidth = this.getRenderedCalcWidth(i, n, e)));
                }
              }
            }),
            (t.prototype.setElementMaxMinWidth = function (e) {
              e.width &&
                'string' == typeof e.width &&
                -1 === e.width.indexOf('%') &&
                ((e.minWidth = e.width), (e.maxWidth = e.width));
            }),
            (t.prototype.getRenderedCalcWidth = function (e, t, n) {
              for (var r = '100%', i = 0; i < t.length; i++) r += ' - ' + t[i].renderWidth;
              var o = n - t.length;
              return o > 1 && (r = '(' + r + ')/' + o.toString()), 'calc(' + r + ')';
            }),
            (t.prototype.getElementWidth = function (e) {
              var t = e.width;
              return t && 'string' == typeof t ? t.trim() : '';
            }),
            (t.prototype.getRenderedWidthFromWidth = function (e) {
              return o.a.isNumber(e) ? e + 'px' : e;
            }),
            (t.prototype.calcVisible = function () {
              return this.visibleElements.length > 0;
            }),
            (t.rowCounter = 100),
            t
          );
        })(a.b),
        m = (function (e) {
          function t(n) {
            void 0 === n && (n = '');
            var r = e.call(this, n) || this;
            return (
              (r.name = n),
              (r.isQuestionsReady = !1),
              (r.questionsValue = new Array()),
              r.createNewArray('rows'),
              (r.elementsValue = r.createNewArray(
                'elements',
                r.onAddElement.bind(r),
                r.onRemoveElement.bind(r)
              )),
              r.registerFunctionOnPropertyValueChanged(
                'questionTitleLocation',
                r.onVisibleChanged.bind(r)
              ),
              (r.id = t.getPanelId()),
              r.createLocalizableString('title', r, !0),
              r.createLocalizableString('description', r, !0),
              r.createLocalizableString('requiredErrorText', r),
              r
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.getPanelId = function () {
              return 'sp_' + t.panelCounter++;
            }),
            (t.prototype.getType = function () {
              return 'panelbase';
            }),
            (t.prototype.setSurveyImpl = function (t) {
              e.prototype.setSurveyImpl.call(this, t), this.isDesignMode && this.onVisibleChanged();
              for (var n = 0; n < this.elements.length; n++) this.elements[n].setSurveyImpl(t);
            }),
            (t.prototype.endLoadingFromJson = function () {
              e.prototype.endLoadingFromJson.call(this),
                this.markQuestionListDirty(),
                this.onRowsChanged();
            }),
            Object.defineProperty(t.prototype, 'title', {
              get: function () {
                return this.getLocalizableStringText('title');
              },
              set: function (e) {
                this.setLocalizableStringText('title', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locTitle', {
              get: function () {
                return this.getLocalizableString('title');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, '_showTitle', {
              get: function () {
                return (
                  (this.survey.showPageTitles && this.title.length > 0) ||
                  (this.isDesignMode && p.a.allowShowEmptyTitleInDesignMode)
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getTitleActions = function () {
              var t = e.prototype.getTitleActions.call(this);
              return (
                (this.titleActions = this.survey.getUpdatedPanelTitleActions(this, t)),
                this.titleActions
              );
            }),
            Object.defineProperty(t.prototype, '_showDescription', {
              get: function () {
                return (
                  (this.survey.showPageTitles && this.description.length > 0) ||
                  (this.isDesignMode &&
                    p.a.allowShowEmptyTitleInDesignMode &&
                    p.a.allowShowEmptyDescriptionInDesignMode)
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'description', {
              get: function () {
                return this.getLocalizableStringText('description');
              },
              set: function (e) {
                this.setLocalizableStringText('description', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locDescription', {
              get: function () {
                return this.getLocalizableString('description');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.locStrsChanged = function () {
              e.prototype.locStrsChanged.call(this);
              for (var t = 0; t < this.elements.length; t++) this.elements[t].locStrsChanged();
            }),
            Object.defineProperty(t.prototype, 'requiredText', {
              get: function () {
                return null != this.survey && this.isRequired ? this.survey.requiredText : '';
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'titlePattern', {
              get: function () {
                return this.survey ? this.survey.questionTitlePattern : 'numTitleRequire';
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isRequireTextOnStart', {
              get: function () {
                return this.isRequired && 'requireNumTitle' == this.titlePattern;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isRequireTextBeforeTitle', {
              get: function () {
                return this.isRequired && 'numRequireTitle' == this.titlePattern;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isRequireTextAfterTitle', {
              get: function () {
                return this.isRequired && 'numTitleRequire' == this.titlePattern;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'requiredErrorText', {
              get: function () {
                return this.getLocalizableStringText('requiredErrorText');
              },
              set: function (e) {
                this.setLocalizableStringText('requiredErrorText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locRequiredErrorText', {
              get: function () {
                return this.getLocalizableString('requiredErrorText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getLocale = function () {
              return this.survey ? this.survey.getLocale() : '';
            }),
            (t.prototype.getMarkdownHtml = function (e, t) {
              return this.survey ? this.survey.getSurveyMarkdownHtml(this, e, t) : null;
            }),
            (t.prototype.getProcessedText = function (e) {
              return this.textProcessor ? this.textProcessor.processText(e, !0) : e;
            }),
            Object.defineProperty(t.prototype, 'parent', {
              get: function () {
                return this.getPropertyValue('parent', null);
              },
              set: function (e) {
                this.setPropertyValue('parent', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'depth', {
              get: function () {
                return null == this.parent ? 0 : this.parent.depth + 1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'visibleIf', {
              get: function () {
                return this.getPropertyValue('visibleIf', '');
              },
              set: function (e) {
                this.setPropertyValue('visibleIf', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'cssClasses', {
              get: function () {
                var e = { panel: {}, error: {}, row: '' };
                return (
                  this.copyCssClasses(e.panel, this.css.panel),
                  this.copyCssClasses(e.error, this.css.error),
                  this.css.row && (e.row = this.css.row),
                  this.survey && this.survey.updatePanelCssClasses(this, e),
                  e
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'css', {
              get: function () {
                return this.survey ? this.survey.getCss() : {};
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'id', {
              get: function () {
                return this.getPropertyValue('id');
              },
              set: function (e) {
                this.setPropertyValue('id', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isPanel', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getPanel = function () {
              return this;
            }),
            (t.prototype.getLayoutType = function () {
              return 'row';
            }),
            (t.prototype.isLayoutTypeSupported = function (e) {
              return 'flow' !== e;
            }),
            Object.defineProperty(t.prototype, 'questions', {
              get: function () {
                if (!this.isQuestionsReady) {
                  this.questionsValue = [];
                  for (var e = 0; e < this.elements.length; e++) {
                    var t = this.elements[e];
                    if (t.isPanel)
                      for (var n = t.questions, r = 0; r < n.length; r++)
                        this.questionsValue.push(n[r]);
                    else this.questionsValue.push(t);
                  }
                  this.isQuestionsReady = !0;
                }
                return this.questionsValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getValidName = function (e) {
              return e ? e.trim() : e;
            }),
            (t.prototype.getQuestionByName = function (e) {
              for (var t = this.questions, n = 0; n < t.length; n++)
                if (t[n].name == e) return t[n];
              return null;
            }),
            (t.prototype.getElementByName = function (e) {
              for (var t = this.elements, n = 0; n < t.length; n++) {
                var r = t[n];
                if (r.name == e) return r;
                var i = r.getPanel();
                if (i) {
                  var o = i.getElementByName(e);
                  if (o) return o;
                }
              }
              return null;
            }),
            (t.prototype.getQuestionByValueName = function (e) {
              for (var t = this.questions, n = 0; n < t.length; n++)
                if (t[n].getValueName() == e) return t[n];
              return null;
            }),
            (t.prototype.getValue = function () {
              for (var e = {}, t = this.questions, n = 0; n < t.length; n++) {
                var r = t[n];
                if (!r.isEmpty()) {
                  var i = r.getValueName();
                  if (((e[i] = r.value), this.data)) {
                    var o = this.data.getComment(i);
                    o && (e[i + p.a.commentPrefix] = o);
                  }
                }
              }
              return e;
            }),
            (t.prototype.getComments = function () {
              var e = {};
              if (!this.data) return e;
              for (var t = this.questions, n = 0; n < t.length; n++) {
                var r = t[n],
                  i = this.data.getComment(r.getValueName());
                i && (e[r.getValueName()] = i);
              }
              return e;
            }),
            (t.prototype.clearIncorrectValues = function () {
              for (var e = 0; e < this.elements.length; e++)
                this.elements[e].clearIncorrectValues();
            }),
            (t.prototype.clearErrors = function () {
              for (var e = 0; e < this.elements.length; e++) this.elements[e].clearErrors();
              this.errors = [];
            }),
            (t.prototype.markQuestionListDirty = function () {
              (this.isQuestionsReady = !1), this.parent && this.parent.markQuestionListDirty();
            }),
            Object.defineProperty(t.prototype, 'elements', {
              get: function () {
                return this.elementsValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getElementsInDesign = function (e) {
              return void 0 === e && (e = !1), this.elements;
            }),
            (t.prototype.containsElement = function (e) {
              for (var t = 0; t < this.elements.length; t++) {
                var n = this.elements[t];
                if (n == e) return !0;
                var r = n.getPanel();
                if (r && r.containsElement(e)) return !0;
              }
              return !1;
            }),
            Object.defineProperty(t.prototype, 'isRequired', {
              get: function () {
                return this.getPropertyValue('isRequired', !1);
              },
              set: function (e) {
                this.setPropertyValue('isRequired', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'requiredIf', {
              get: function () {
                return this.getPropertyValue('requiredIf', '');
              },
              set: function (e) {
                this.setPropertyValue('requiredIf', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.hasErrors = function (e, t, n) {
              return (
                void 0 === e && (e = !0),
                void 0 === t && (t = !1),
                void 0 === n && (n = null),
                (n = n || {
                  fireCallback: e,
                  focuseOnFirstError: t,
                  firstErrorQuestion: null,
                  result: !1,
                }),
                this.hasErrorsCore(n),
                n.firstErrorQuestion && n.firstErrorQuestion.focus(!0),
                n.result
              );
            }),
            (t.prototype.hasErrorsInPanels = function (e) {
              var t = [];
              if ((this.hasRequiredError(e, t), this.survey)) {
                var n = this.survey.validatePanel(this);
                n && (t.push(n), (e.result = !0));
              }
              e.fireCallback && (this.errors = t);
            }),
            (t.prototype.getErrorCustomText = function (e, t) {
              return this.survey ? this.survey.getErrorCustomText(e, t) : e;
            }),
            (t.prototype.hasRequiredError = function (e, t) {
              if (this.isRequired) {
                var n = [];
                if ((this.addQuestionsToList(n, !0), 0 != n.length)) {
                  for (var r = 0; r < n.length; r++) if (!n[r].isEmpty()) return;
                  (e.result = !0),
                    t.push(new c.a(this.requiredErrorText, this)),
                    e.focuseOnFirstError && !e.firstErrorQuestion && (e.firstErrorQuestion = n[0]);
                }
              }
            }),
            (t.prototype.hasErrorsCore = function (e) {
              for (var t = this.elements, n = null, r = 0; r < t.length; r++)
                if (((n = t[r]), n.isVisible))
                  if (n.isPanel) n.hasErrorsCore(e);
                  else {
                    var i = n;
                    if (i.isReadOnly) continue;
                    i.hasErrors(e.fireCallback, e) &&
                      (e.focuseOnFirstError &&
                        null == e.firstErrorQuestion &&
                        (e.firstErrorQuestion = i),
                      (e.result = !0));
                  }
              this.hasErrorsInPanels(e), this.updateContainsErrors();
            }),
            (t.prototype.getContainsErrors = function () {
              var t = e.prototype.getContainsErrors.call(this);
              if (t) return t;
              for (var n = this.elements, r = 0; r < n.length; r++)
                if (n[r].containsErrors) return !0;
              return !1;
            }),
            (t.prototype.updateElementVisibility = function () {
              for (var e = 0; e < this.elements.length; e++) {
                var t = this.elements[e];
                t.setPropertyValue('isVisible', t.isVisible),
                  t.isPanel && t.updateElementVisibility();
              }
            }),
            (t.prototype.getFirstQuestionToFocus = function (e) {
              void 0 === e && (e = !1);
              for (var t = this.elements, n = 0; n < t.length; n++) {
                var r = t[n];
                if (r.isVisible)
                  if (r.isPanel) {
                    var i = r.getFirstQuestionToFocus(e);
                    if (i) return i;
                  } else {
                    var o = r;
                    if (o.hasInput && (!e || o.currentErrorCount > 0)) return o;
                  }
              }
              return null;
            }),
            (t.prototype.focusFirstQuestion = function () {
              var e = this.getFirstQuestionToFocus();
              e && e.focus();
            }),
            (t.prototype.focusFirstErrorQuestion = function () {
              var e = this.getFirstQuestionToFocus(!0);
              e && e.focus();
            }),
            (t.prototype.addQuestionsToList = function (e, t, n) {
              void 0 === t && (t = !1),
                void 0 === n && (n = !1),
                this.addElementsToList(e, t, n, !1);
            }),
            (t.prototype.addPanelsIntoList = function (e, t, n) {
              void 0 === t && (t = !1),
                void 0 === n && (n = !1),
                this.addElementsToList(e, t, n, !0);
            }),
            (t.prototype.addElementsToList = function (e, t, n, r) {
              (t && !this.visible) || this.addElementsToListCore(e, this.elements, t, n, r);
            }),
            (t.prototype.addElementsToListCore = function (e, t, n, r, i) {
              for (var o = 0; o < t.length; o++) {
                var a = t[o];
                (n && !a.visible) ||
                  (((i && a.isPanel) || (!i && !a.isPanel)) && e.push(a),
                  a.isPanel
                    ? a.addElementsToListCore(e, a.elements, n, r, i)
                    : r && this.addElementsToListCore(e, a.getElementsInDesign(!1), n, r, i));
              }
            }),
            Object.defineProperty(t.prototype, 'isActive', {
              get: function () {
                return !this.survey || this.survey.currentPage == this.root;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.updateCustomWidgets = function () {
              for (var e = 0; e < this.elements.length; e++) this.elements[e].updateCustomWidgets();
            }),
            Object.defineProperty(t.prototype, 'questionTitleLocation', {
              get: function () {
                return this.getPropertyValue('questionTitleLocation');
              },
              set: function (e) {
                this.setPropertyValue('questionTitleLocation', e.toLowerCase());
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getQuestionTitleLocation = function () {
              return this.onGetQuestionTitleLocation
                ? this.onGetQuestionTitleLocation()
                : 'default' != this.questionTitleLocation
                ? this.questionTitleLocation
                : this.parent
                ? this.parent.getQuestionTitleLocation()
                : this.survey
                ? this.survey.questionTitleLocation
                : 'top';
            }),
            (t.prototype.getStartIndex = function () {
              return this.parent
                ? this.parent.getQuestionStartIndex()
                : this.survey
                ? this.survey.questionStartIndex
                : '';
            }),
            (t.prototype.getQuestionStartIndex = function () {
              return this.getStartIndex();
            }),
            (t.prototype.getChildrenLayoutType = function () {
              return 'row';
            }),
            (t.prototype.getProgressInfo = function () {
              return a.a.getProgressInfoByElements(this.elements, this.isRequired);
            }),
            Object.defineProperty(t.prototype, 'root', {
              get: function () {
                for (var e = this; e.parent; ) e = e.parent;
                return e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.childVisibilityChanged = function () {
              this.getIsPageVisible(null) !== this.getPropertyValue('isVisible', !0) &&
                this.onVisibleChanged();
            }),
            (t.prototype.createRow = function () {
              return new f(this);
            }),
            (t.prototype.onSurveyLoad = function () {
              for (var e = 0; e < this.elements.length; e++) this.elements[e].onSurveyLoad();
              this.onElementVisibilityChanged(this);
            }),
            (t.prototype.onFirstRendering = function () {
              for (var e = 0; e < this.elements.length; e++) this.elements[e].onFirstRendering();
              this.onRowsChanged();
            }),
            Object.defineProperty(t.prototype, 'rows', {
              get: function () {
                return this.getPropertyValue('rows');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onRowsChanged = function () {
              this.isLoadingFromJson || this.setPropertyValue('rows', this.buildRows());
            }),
            (t.prototype.onAddElement = function (e, t) {
              if (
                (e.setSurveyImpl(this.surveyImpl),
                (e.parent = this),
                this.markQuestionListDirty(),
                this.updateRowsOnElementAdded(e, t),
                e.isPanel)
              ) {
                var n = e;
                this.survey && this.survey.panelAdded(n, t, this, this.root);
              } else if (this.survey) {
                var r = e;
                this.survey.questionAdded(r, t, this, this.root);
              }
              this.addElementCallback && this.addElementCallback(e);
              var i = this;
              e.registerFunctionOnPropertiesValueChanged(
                ['visible', 'isVisible'],
                function () {
                  i.onElementVisibilityChanged(e);
                },
                this.id
              ),
                e.registerFunctionOnPropertyValueChanged(
                  'startWithNewLine',
                  function () {
                    i.onElementStartWithNewLineChanged(e);
                  },
                  this.id
                ),
                this.onElementVisibilityChanged(this);
            }),
            (t.prototype.onRemoveElement = function (e) {
              (e.parent = null),
                this.markQuestionListDirty(),
                e.unRegisterFunctionOnPropertiesValueChanged(
                  ['visible', 'isVisible', 'startWithNewLine'],
                  this.id
                ),
                this.updateRowsOnElementRemoved(e),
                e.isPanel
                  ? this.survey && this.survey.panelRemoved(e)
                  : this.survey && this.survey.questionRemoved(e),
                this.removeElementCallback && this.removeElementCallback(e),
                this.onElementVisibilityChanged(this);
            }),
            (t.prototype.onElementVisibilityChanged = function (e) {
              this.isLoadingFromJson ||
                (this.updateRowsVisibility(e),
                this.childVisibilityChanged(),
                this.parent && this.parent.onElementVisibilityChanged(this));
            }),
            (t.prototype.onElementStartWithNewLineChanged = function (e) {
              this.onRowsChanged();
            }),
            (t.prototype.updateRowsVisibility = function (e) {
              for (var t = this.rows, n = 0; n < t.length; n++) {
                var r = t[n];
                if (r.elements.indexOf(e) > -1) {
                  r.updateVisible();
                  break;
                }
              }
            }),
            (t.prototype.canBuildRows = function () {
              return !this.isLoadingFromJson && 'row' == this.getChildrenLayoutType();
            }),
            (t.prototype.buildRows = function () {
              if (!this.canBuildRows()) return [];
              for (var e = new Array(), t = 0; t < this.elements.length; t++) {
                var n = this.elements[t],
                  r = 0 == t || n.startWithNewLine,
                  i = r ? this.createRow() : e[e.length - 1];
                r && e.push(i), i.addElement(n);
              }
              for (var t = 0; t < e.length; t++) e[t].updateVisible();
              return e;
            }),
            (t.prototype.updateRowsOnElementAdded = function (e, t) {
              if (this.canBuildRows()) {
                var n = new h(null, e);
                (n.target = e),
                  (n.isEdge = this.elements.length > 1),
                  this.elements.length < 2
                    ? (n.destination = this)
                    : ((n.isBottom = t > 0),
                      (n.destination = 0 == t ? this.elements[1] : this.elements[t - 1])),
                  this.dragDropAddTargetToRow(n, null);
              }
            }),
            (t.prototype.updateRowsOnElementRemoved = function (e) {
              this.canBuildRows() &&
                this.updateRowsRemoveElementFromRow(e, this.findRowByElement(e));
            }),
            (t.prototype.updateRowsRemoveElementFromRow = function (e, t) {
              if (t && t.panel) {
                var n = t.elements.indexOf(e);
                n < 0 ||
                  (t.elements.splice(n, 1),
                  t.elements.length > 0
                    ? t.updateVisible()
                    : t.index >= 0 && t.panel.rows.splice(t.index, 1));
              }
            }),
            (t.prototype.findRowByElement = function (e) {
              for (var t = this.rows, n = 0; n < t.length; n++)
                if (t[n].elements.indexOf(e) > -1) return t[n];
              return null;
            }),
            (t.prototype.elementWidthChanged = function (e) {
              if (!this.isLoadingFromJson) {
                var t = this.findRowByElement(e);
                t && t.updateVisible();
              }
            }),
            Object.defineProperty(t.prototype, 'processedTitle', {
              get: function () {
                return this.getRenderedTitle(this.locTitle.textOrHtml);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getRenderedTitle = function (e) {
              return null != this.textProcessor ? this.textProcessor.processText(e, !0) : e;
            }),
            Object.defineProperty(t.prototype, 'visible', {
              get: function () {
                return this.getPropertyValue('visible', !0);
              },
              set: function (e) {
                e !== this.visible &&
                  (this.setPropertyValue('visible', e),
                  this.setPropertyValue('isVisible', this.isVisible),
                  this.isLoadingFromJson || this.onVisibleChanged());
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onVisibleChanged = function () {
              if (
                (this.setPropertyValue('isVisible', this.isVisible),
                this.survey && this.survey.isClearValueOnHiddenContainer && !this.isLoadingFromJson)
              )
                for (var e = this.questions, t = 0; t < e.length; t++)
                  this.isVisible ? e[t].updateValueWithDefaults() : e[t].clearValue();
            }),
            Object.defineProperty(t.prototype, 'isVisible', {
              get: function () {
                return this.areInvisibleElementsShowing || this.getIsPageVisible(null);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getIsPageVisible = function (e) {
              if (!this.visible) return !1;
              for (var t = 0; t < this.elements.length; t++)
                if (this.elements[t] != e && this.elements[t].isVisible) return !0;
              return !1;
            }),
            (t.prototype.setVisibleIndex = function (e) {
              if (!this.isVisible || e < 0) {
                for (var t = 0; t < this.elements.length; t++) this.elements[t].setVisibleIndex(-1);
                return 0;
              }
              var n = e;
              e += this.beforeSetVisibleIndex(e);
              for (var r = this.getPanelStartIndex(e), i = r, t = 0; t < this.elements.length; t++)
                i += this.elements[t].setVisibleIndex(i);
              return this.isContinueNumbering() && (e += i - r), e - n;
            }),
            (t.prototype.beforeSetVisibleIndex = function (e) {
              return 0;
            }),
            (t.prototype.getPanelStartIndex = function (e) {
              return e;
            }),
            (t.prototype.isContinueNumbering = function () {
              return !0;
            }),
            Object.defineProperty(t.prototype, 'isReadOnly', {
              get: function () {
                var e = !!this.parent && this.parent.isReadOnly,
                  t = !!this.survey && this.survey.isDisplayMode;
                return this.readOnly || e || t;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onReadOnlyChanged = function () {
              for (var t = 0; t < this.elements.length; t++) {
                var n = this.elements[t];
                n.setPropertyValue('isReadOnly', n.isReadOnly);
              }
              e.prototype.onReadOnlyChanged.call(this);
            }),
            (t.prototype.updateElementCss = function () {
              for (var t = 0; t < this.elements.length; t++) {
                this.elements[t].updateElementCss();
              }
              e.prototype.updateElementCss.call(this);
            }),
            Object.defineProperty(t.prototype, 'enableIf', {
              get: function () {
                return this.getPropertyValue('enableIf', '');
              },
              set: function (e) {
                this.setPropertyValue('enableIf', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.addElement = function (e, t) {
              return (
                void 0 === t && (t = -1),
                !!this.canAddElement(e) &&
                  (t < 0 || t >= this.elements.length
                    ? this.elements.push(e)
                    : this.elements.splice(t, 0, e),
                  !0)
              );
            }),
            (t.prototype.canAddElement = function (e) {
              return !!e && e.isLayoutTypeSupported(this.getChildrenLayoutType());
            }),
            (t.prototype.addQuestion = function (e, t) {
              return void 0 === t && (t = -1), this.addElement(e, t);
            }),
            (t.prototype.addPanel = function (e, t) {
              return void 0 === t && (t = -1), this.addElement(e, t);
            }),
            (t.prototype.addNewQuestion = function (e, t, n) {
              void 0 === t && (t = null), void 0 === n && (n = -1);
              var r = u.b.Instance.createQuestion(e, t);
              return this.addQuestion(r, n) ? r : null;
            }),
            (t.prototype.addNewPanel = function (e) {
              void 0 === e && (e = null);
              var t = this.createNewPanel(e);
              return this.addPanel(t) ? t : null;
            }),
            (t.prototype.indexOf = function (e) {
              return this.elements.indexOf(e);
            }),
            (t.prototype.createNewPanel = function (e) {
              return new g(e);
            }),
            (t.prototype.removeElement = function (e) {
              var t = this.elements.indexOf(e);
              if (t < 0) {
                for (var n = 0; n < this.elements.length; n++)
                  if (this.elements[n].removeElement(e)) return !0;
                return !1;
              }
              return this.elements.splice(t, 1), !0;
            }),
            (t.prototype.removeQuestion = function (e) {
              this.removeElement(e);
            }),
            (t.prototype.runCondition = function (e, t) {
              if (!this.isDesignMode) {
                for (var n = this.elements.slice(), r = 0; r < n.length; r++)
                  n[r].runCondition(e, t);
                this.areInvisibleElementsShowing || this.runVisibleCondition(e, t),
                  this.runEnableCondition(e, t),
                  this.runRequiredCondition(e, t);
              }
            }),
            (t.prototype.runVisibleCondition = function (e, t) {
              var n = this;
              if (this.visibleIf) {
                var r = new l.a(this.visibleIf);
                (r.onRunComplete = function (e) {
                  n.visible = e;
                }),
                  r.run(e, t);
              }
            }),
            (t.prototype.runEnableCondition = function (e, t) {
              var n = this;
              if (this.enableIf) {
                var r = new l.a(this.enableIf);
                (r.onRunComplete = function (e) {
                  n.readOnly = !e;
                }),
                  r.run(e, t);
              }
            }),
            (t.prototype.runRequiredCondition = function (e, t) {
              var n = this;
              if (this.requiredIf) {
                var r = new l.a(this.requiredIf);
                (r.onRunComplete = function (e) {
                  n.isRequired = e;
                }),
                  r.run(e, t);
              }
            }),
            (t.prototype.onAnyValueChanged = function (e) {
              for (var t = this.elements, n = 0; n < t.length; n++) t[n].onAnyValueChanged(e);
            }),
            (t.prototype.checkBindings = function (e, t) {
              for (var n = this.elements, r = 0; r < n.length; r++) n[r].checkBindings(e, t);
            }),
            (t.prototype.dragDropAddTarget = function (e) {
              var t = this.dragDropFindRow(e.target);
              this.dragDropAddTargetToRow(e, t) && this.updateRowsRemoveElementFromRow(e.target, t);
            }),
            (t.prototype.dragDropFindRow = function (e) {
              if (!e || e.isPage) return null;
              for (var t = e, n = this.rows, r = 0; r < n.length; r++)
                if (n[r].elements.indexOf(t) > -1) return n[r];
              for (var r = 0; r < this.elements.length; r++) {
                var i = this.elements[r].getPanel();
                if (i) {
                  var o = i.dragDropFindRow(t);
                  if (o) return o;
                }
              }
              return null;
            }),
            (t.prototype.dragDropAddTargetToRow = function (e, t) {
              if (!e.destination) return !0;
              if (this.dragDropAddTargetToEmptyPanel(e)) return !0;
              var n = e.destination,
                r = this.dragDropFindRow(n);
              return (
                !r ||
                (e.target.startWithNewLine
                  ? this.dragDropAddTargetToNewRow(e, r, t)
                  : this.dragDropAddTargetToExistingRow(e, r, t))
              );
            }),
            (t.prototype.dragDropAddTargetToEmptyPanel = function (e) {
              if (e.destination.isPage)
                return this.dragDropAddTargetToEmptyPanelCore(this.root, e.target, e.isBottom), !0;
              var t = e.destination;
              if (t.isPanel && !e.isEdge) {
                var n = t;
                if (e.target.template === t) return !1;
                if (e.nestedPanelDepth < 0 || e.nestedPanelDepth >= n.depth)
                  return this.dragDropAddTargetToEmptyPanelCore(t, e.target, e.isBottom), !0;
              }
              return !1;
            }),
            (t.prototype.dragDropAddTargetToExistingRow = function (e, t, n) {
              var r = t.elements.indexOf(e.destination);
              if (0 == r && !e.isBottom && t.elements[0].startWithNewLine)
                return t.index > 0
                  ? ((e.isBottom = !0),
                    (t = t.panel.rows[t.index - 1]),
                    (e.destination = t.elements[t.elements.length - 1]),
                    this.dragDropAddTargetToExistingRow(e, t, n))
                  : this.dragDropAddTargetToNewRow(e, t, n);
              var i = -1;
              n == t && (i = t.elements.indexOf(e.target)), e.isBottom && r++;
              var o = this.findRowByElement(e.source);
              return (
                (o != t || o.elements.indexOf(e.source) != r) &&
                r != i &&
                (i > -1 && (t.elements.splice(i, 1), i < r && r--),
                t.elements.splice(r, 0, e.target),
                t.updateVisible(),
                i < 0)
              );
            }),
            (t.prototype.dragDropAddTargetToNewRow = function (e, t, n) {
              var r = t.panel.createRow();
              r.addElement(e.target);
              var i = t.index;
              if ((e.isBottom && i++, n && n.panel == r.panel && n.index == i)) return !1;
              var o = this.findRowByElement(e.source);
              return (
                (!o || o.panel != r.panel || 1 != o.elements.length || o.index != i) &&
                (t.panel.rows.splice(i, 0, r), !0)
              );
            }),
            (t.prototype.dragDropAddTargetToEmptyPanelCore = function (e, t, n) {
              var r = e.createRow();
              r.addElement(t),
                0 == e.elements.length || n ? e.rows.push(r) : e.rows.splice(0, 0, r);
            }),
            (t.prototype.dragDropMoveElement = function (e, t, n) {
              n > e.parent.elements.indexOf(e) && n--, this.removeElement(e), this.addElement(t, n);
            }),
            (t.prototype.dispose = function () {
              for (var e = 0; e < this.elements.length; e++) this.elements[e].dispose();
              this.elements.splice(0, this.elements.length);
            }),
            (t.panelCounter = 100),
            t
          );
        })(a.a),
        g = (function (e) {
          function t(t) {
            void 0 === t && (t = '');
            var n = e.call(this, t) || this;
            n.name = t;
            var r = n;
            return (
              n.registerFunctionOnPropertyValueChanged('width', function () {
                r.parent && r.parent.elementWidthChanged(r);
              }),
              n.registerFunctionOnPropertiesValueChanged(
                ['indent', 'innerIndent', 'rightIndent'],
                function () {
                  r.onIndentChanged();
                }
              ),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'panel';
            }),
            Object.defineProperty(t.prototype, 'contentId', {
              get: function () {
                return this.id + '_content';
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onSurveyLoad = function () {
              e.prototype.onSurveyLoad.call(this), this.onIndentChanged();
            }),
            Object.defineProperty(t.prototype, 'isPanel', {
              get: function () {
                return !0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'page', {
              get: function () {
                return this.getPage(this.parent);
              },
              set: function (e) {
                this.setPage(this.parent, e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.delete = function () {
              this.parent && this.removeSelfFromList(this.parent.elements);
            }),
            (t.prototype.moveTo = function (e, t) {
              return void 0 === t && (t = null), this.moveToBase(this.parent, e, t);
            }),
            Object.defineProperty(t.prototype, 'visibleIndex', {
              get: function () {
                return this.getPropertyValue('visibleIndex', -1);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'showNumber', {
              get: function () {
                return this.getPropertyValue('showNumber', !1);
              },
              set: function (e) {
                this.setPropertyValue('showNumber', e), this.notifySurveyOnVisibilityChanged();
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'showQuestionNumbers', {
              get: function () {
                return this.getPropertyValue('showQuestionNumbers', 'default');
              },
              set: function (e) {
                this.setPropertyValue('showQuestionNumbers', e),
                  this.notifySurveyOnVisibilityChanged();
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'questionStartIndex', {
              get: function () {
                return this.getPropertyValue('questionStartIndex', '');
              },
              set: function (e) {
                this.setPropertyValue('questionStartIndex', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getQuestionStartIndex = function () {
              return this.questionStartIndex
                ? this.questionStartIndex
                : e.prototype.getQuestionStartIndex.call(this);
            }),
            Object.defineProperty(t.prototype, 'no', {
              get: function () {
                return this.getPropertyValue('no', '');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setNo = function (e) {
              this.setPropertyValue(
                'no',
                o.a.getNumberByIndex(this.visibleIndex, this.getStartIndex())
              );
            }),
            (t.prototype.beforeSetVisibleIndex = function (e) {
              var t = -1;
              return (
                !this.showNumber || (!this.isDesignMode && this.locTitle.isEmpty) || (t = e),
                this.setPropertyValue('visibleIndex', t),
                this.setNo(t),
                t < 0 ? 0 : 1
              );
            }),
            (t.prototype.getPanelStartIndex = function (e) {
              return 'off' == this.showQuestionNumbers
                ? -1
                : 'onpanel' == this.showQuestionNumbers
                ? 0
                : e;
            }),
            (t.prototype.isContinueNumbering = function () {
              return 'off' != this.showQuestionNumbers && 'onpanel' != this.showQuestionNumbers;
            }),
            (t.prototype.notifySurveyOnVisibilityChanged = function () {
              null == this.survey ||
                this.isLoadingFromJson ||
                this.survey.panelVisibilityChanged(this, this.isVisible);
            }),
            (t.prototype.hasErrorsCore = function (t) {
              e.prototype.hasErrorsCore.call(this, t),
                this.isCollapsed && t.result && t.fireCallback && this.expand();
            }),
            (t.prototype.getRenderedTitle = function (t) {
              if (!t) {
                if (this.isCollapsed || this.isExpanded) return this.name;
                if (this.isDesignMode) return '[' + this.name + ']';
              }
              return e.prototype.getRenderedTitle.call(this, t);
            }),
            Object.defineProperty(t.prototype, 'width', {
              get: function () {
                return this.getPropertyValue('width');
              },
              set: function (e) {
                this.setPropertyValue('width', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'indent', {
              get: function () {
                return this.getPropertyValue('indent', 0);
              },
              set: function (e) {
                this.setPropertyValue('indent', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'innerIndent', {
              get: function () {
                return this.getPropertyValue('innerIndent', 0);
              },
              set: function (e) {
                this.setPropertyValue('innerIndent', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'renderWidth', {
              get: function () {
                return this.getPropertyValue('renderWidth');
              },
              set: function (e) {
                this.setPropertyValue('renderWidth', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'startWithNewLine', {
              get: function () {
                return this.getPropertyValue('startWithNewLine', !0);
              },
              set: function (e) {
                this.setPropertyValue('startWithNewLine', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'rightIndent', {
              get: function () {
                return this.getPropertyValue('rightIndent', 0);
              },
              set: function (e) {
                this.setPropertyValue('rightIndent', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'paddingLeft', {
              get: function () {
                return this.getPropertyValue('paddingLeft', '');
              },
              set: function (e) {
                this.setPropertyValue('paddingLeft', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'innerPaddingLeft', {
              get: function () {
                return this.getPropertyValue('innerPaddingLeft', '');
              },
              set: function (e) {
                this.setPropertyValue('innerPaddingLeft', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'paddingRight', {
              get: function () {
                return this.getPropertyValue('paddingRight', '');
              },
              set: function (e) {
                this.setPropertyValue('paddingRight', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onIndentChanged = function () {
              (this.innerPaddingLeft = this.getIndentSize(this.innerIndent)),
                (this.paddingLeft = this.getIndentSize(this.indent)),
                (this.paddingRight = this.getIndentSize(this.rightIndent));
            }),
            (t.prototype.getIndentSize = function (e) {
              if (e < 1) return '';
              if (!this.data) return '';
              var t = this.survey.css;
              return t ? e * t.question.indent + 'px' : '';
            }),
            (t.prototype.clearOnDeletingContainer = function () {
              this.elements.forEach(function (e) {
                (e instanceof s.a || e instanceof t) && e.clearOnDeletingContainer();
              });
            }),
            Object.defineProperty(t.prototype, 'hasEditButton', {
              get: function () {
                return !(!this.survey || 'preview' != this.survey.state) && 1 == this.depth;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.cancelPreview = function () {
              this.hasEditButton && this.survey.cancelPreviewByPage(this);
            }),
            (t.prototype.onVisibleChanged = function () {
              e.prototype.onVisibleChanged.call(this), this.notifySurveyOnVisibilityChanged();
            }),
            t
          );
        })(m);
      i.b.addClass(
        'panelbase',
        [
          'name',
          {
            name: 'elements',
            alternativeName: 'questions',
            baseClassName: 'question',
            visible: !1,
            isLightSerializable: !1,
          },
          { name: 'visible:boolean', default: !0 },
          'visibleIf:condition',
          'enableIf:condition',
          'requiredIf:condition',
          'readOnly:boolean',
          {
            name: 'questionTitleLocation',
            default: 'default',
            choices: ['default', 'top', 'bottom', 'left', 'hidden'],
          },
          { name: 'title', serializationProperty: 'locTitle' },
          { name: 'description:text', serializationProperty: 'locDescription' },
        ],
        function () {
          return new m();
        }
      ),
        i.b.addClass(
          'panel',
          [
            { name: 'state', default: 'default', choices: ['default', 'collapsed', 'expanded'] },
            'isRequired:boolean',
            { name: 'requiredErrorText:text', serializationProperty: 'locRequiredErrorText' },
            { name: 'startWithNewLine:boolean', default: !0 },
            'width',
            { name: 'innerIndent:number', default: 0, choices: [0, 1, 2, 3] },
            { name: 'indent:number', default: 0, choices: [0, 1, 2, 3] },
            {
              name: 'page',
              isSerializable: !1,
              visibleIf: function (e) {
                var t = e ? e.survey : null;
                return !t || t.pages.length > 1;
              },
              choices: function (e) {
                var t = e ? e.survey : null;
                return t
                  ? t.pages.map(function (e) {
                      return { value: e.name, text: e.title };
                    })
                  : [];
              },
            },
            'showNumber:boolean',
            {
              name: 'showQuestionNumbers',
              default: 'default',
              choices: ['default', 'onpanel', 'off'],
            },
            'questionStartIndex',
          ],
          function () {
            return new g();
          },
          'panelbase'
        );
    },
    function (e, t, n) {
      'use strict';
      n.d(t, 'a', function () {
        return r;
      });
      var r = (function () {
        function e() {
          this.renderersHash = {};
        }
        return (
          (e.prototype.unregisterRenderer = function (e, t) {
            delete this.renderersHash[e][t];
          }),
          (e.prototype.registerRenderer = function (e, t, n) {
            this.renderersHash[e] || (this.renderersHash[e] = {}), (this.renderersHash[e][t] = n);
          }),
          (e.prototype.getRenderer = function (e, t) {
            return (this.renderersHash[e] && this.renderersHash[e][t]) || 'default';
          }),
          (e.prototype.getRendererByQuestion = function (e) {
            return this.getRenderer(e.getType(), e.renderAs);
          }),
          (e.Instance = new e()),
          e
        );
      })();
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(21)),
        a = n(6),
        s = n(22),
        l = n(14),
        u = n(48),
        c = n(30),
        p = n(13),
        d = n(11),
        h = n(15),
        f = n(35);
      n.d(t, 'b', function () {
        return m;
      }),
        n.d(t, 'a', function () {
          return g;
        }),
        c.a.Instance.onCustomWidgetAdded.add(function (e) {
          e.widgetJson.isDefaultRender ||
            (e.htmlTemplate || (e.htmlTemplate = "<div>'htmlTemplate' attribute is missed.</div>"),
            new u.a().replaceText(e.htmlTemplate, 'widget', e.name));
        });
      var m = (function (e) {
        function t(t, n, r) {
          void 0 === t && (t = null), void 0 === n && (n = null), void 0 === r && (r = null);
          var o = e.call(this, t) || this;
          if (
            ((o.isFirstRender = !0),
            (o.mouseDownPage = null),
            (o.koTitleTemplate = i.observable('survey-header')),
            void 0 === i)
          )
            throw new Error('knockoutjs library is not loaded.');
          return r && (o.css = r), n && (o.renderedElement = n), o.render(n), o;
        }
        return (
          n.i(r.b)(t, e),
          Object.defineProperty(t, 'cssType', {
            get: function () {
              return l.b.currentType;
            },
            set: function (e) {
              f.a.applyTheme(e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getDataValueCore = function (t, n) {
            return this.editingObj
              ? e.prototype.getDataValueCore.call(this, t, n)
              : (void 0 === t[n] && (t[n] = i.observable()), i.unwrap(t[n]));
          }),
          (t.prototype.setDataValueCore = function (t, n, r) {
            if (this.editingObj) return void e.prototype.setDataValueCore.call(this, t, n, r);
            i.isWriteableObservable(t[n]) ? t[n](r) : (t[n] = i.observable(r));
          }),
          (t.prototype.deleteDataValueCore = function (t, n) {
            if (this.editingObj) return void e.prototype.deleteDataValueCore.call(this, t, n);
            i.isWriteableObservable(t[n]) ? t[n](void 0) : delete t[n];
          }),
          (t.prototype.onBaseCreating = function () {
            e.prototype.onBaseCreating.call(this), new h.a(this);
          }),
          (t.prototype.nextPageUIClick = function () {
            (this.mouseDownPage && this.mouseDownPage !== this.currentPage) ||
              ((this.mouseDownPage = null), this.nextPage());
          }),
          (t.prototype.nextPageMouseDown = function () {
            return (this.mouseDownPage = this.currentPage), this.navigationMouseDown();
          }),
          (t.prototype.render = function (e) {
            void 0 === e && (e = null),
              this.updateKoCurrentPage(),
              this.updateCustomWidgets(this.currentPage);
            var t = this;
            e && 'string' == typeof e && (e = document.getElementById(e)),
              e && (this.renderedElement = e),
              (e = this.renderedElement),
              t.startTimerFromUI(),
              e && t.applyBinding();
          }),
          (t.prototype.clear = function (t, n) {
            void 0 === t && (t = !0),
              void 0 === n && (n = !0),
              e.prototype.clear.call(this, t, n),
              this.render();
          }),
          (t.prototype.onLocaleChanged = function () {
            this.render();
          }),
          (t.prototype.koEventAfterRender = function (e, t) {
            t.afterRenderSurvey(e);
          }),
          (t.prototype.loadSurveyFromService = function (t, n, r) {
            void 0 === t && (t = null),
              void 0 === n && (n = null),
              void 0 === r && (r = null),
              r && (this.renderedElement = r),
              e.prototype.loadSurveyFromService.call(this, t, n);
          }),
          (t.prototype.setCompleted = function () {
            e.prototype.setCompleted.call(this), this.updateKoCurrentPage();
          }),
          (t.prototype.start = function () {
            var t = e.prototype.start.call(this);
            return this.updateKoCurrentPage(), t;
          }),
          (t.prototype.createNewPage = function (e) {
            return new s.a(e);
          }),
          (t.prototype.getHtmlTemplate = function () {
            return u.b;
          }),
          (t.prototype.onBeforeCreating = function () {
            var e = this,
              t = this;
            (this.dummyObservable = i.observable(0)),
              (this.koCurrentPage = i.observable(this.currentPage)),
              (this.isCurrentPageEmpty = i.computed(function () {
                return !!e.koCurrentPage() && 0 === e.getRows(e.koCurrentPage()).length;
              })),
              (this.koIsFirstPage = i.computed(function () {
                return e.dummyObservable(), e.isFirstPage;
              })),
              (this.koIsLastPage = i.computed(function () {
                return e.dummyObservable(), e.isLastPage;
              })),
              (this.koState = i.observable(this.state)),
              (this.koCompletedState = i.observable('')),
              (this.koCompletedStateText = i.observable('')),
              (this.koCompletedStateCss = i.observable('')),
              (this.koTimerInfoText = i.observable(this.timerInfoText)),
              (this.koAfterRenderPage = function (e, n) {
                var r = a.a.GetFirstNonTextElement(e);
                r && t.afterRenderPage(r);
              }),
              (this.koAfterRenderHeader = function (e, n) {
                var r = a.a.GetFirstNonTextElement(e);
                r && t.afterRenderHeader(r);
              });
          }),
          (t.prototype.currentPageChanged = function (t, n) {
            this.updateKoCurrentPage(),
              e.prototype.currentPageChanged.call(this, t, n),
              this.isDesignMode || this.scrollToTopOnPageChange();
          }),
          (t.prototype.pageVisibilityChanged = function (t, n) {
            e.prototype.pageVisibilityChanged.call(this, t, n), this.updateKoCurrentPage();
          }),
          (t.prototype.onLoadSurveyFromService = function () {
            this.render();
          }),
          (t.prototype.onLoadingSurveyFromService = function () {
            this.render();
          }),
          (t.prototype.setCompletedState = function (t, n) {
            e.prototype.setCompletedState.call(this, t, n),
              this.koCompletedState(this.completedState),
              this.koCompletedStateText(this.completedStateText),
              this.koCompletedStateCss(
                '' !== this.completedState ? this.css.saveData[this.completedState] : ''
              );
          }),
          (t.prototype.doTimer = function () {
            e.prototype.doTimer.call(this), this.koTimerInfoText(this.timerInfoText);
          }),
          (t.prototype.applyBinding = function () {
            this.renderedElement &&
              (this.updateKoCurrentPage(),
              i.cleanNode(this.renderedElement),
              this.isFirstRender || this.updateCurrentPageQuestions(),
              (this.isFirstRender = !1),
              i.renderTemplate(
                'survey-content',
                this,
                { afterRender: this.koEventAfterRender },
                this.renderedElement
              ));
          }),
          (t.prototype.updateKoCurrentPage = function () {
            this.isLoadingFromJson ||
              (this.dummyObservable(this.dummyObservable() + 1),
              this.currentPage !== this.koCurrentPage() && this.koCurrentPage(this.currentPage),
              this.koState(this.state));
          }),
          (t.prototype.getRows = function (e) {
            return e.koRows ? e.koRows() : e.rows;
          }),
          (t.prototype.updateCurrentPageQuestions = function () {
            for (
              var e = this.currentPage ? this.currentPage.questions : [], t = 0;
              t < e.length;
              t++
            ) {
              var n = e[t];
              n.visible && n.updateQuestion();
            }
          }),
          (t.prototype.updateSurvey = function (e, t) {
            var n = function () {
                if ('model' == i || 'children' == i) return 'continue';
                if ('css' == i)
                  return r.mergeValues(e.css, r.getCss()), r.updateElementCss(), 'continue';
                if (0 == i.indexOf('on') && r[i] && r[i].add) {
                  var t = e[i],
                    n = function (e, n) {
                      t(e, n);
                    };
                  r[i].add(n);
                } else r[i] = e[i];
              },
              r = this;
            for (var i in e) n();
            e &&
              e.data &&
              this.onValueChanged.add(function (t, n) {
                e.data[n.name] = n.value;
              });
          }),
          (t.prototype.dispose = function () {
            e.prototype.dispose.call(this),
              this.renderedElement &&
                (i.cleanNode(this.renderedElement), (this.renderedElement.innerHTML = '')),
              this.isCurrentPageEmpty.dispose(),
              this.koIsFirstPage.dispose(),
              this.koIsLastPage.dispose(),
              this.iteratePropertiesHash(function (e, t) {
                delete e[t];
              }),
              this.koCurrentPage(void 0);
          }),
          t
        );
      })(o.a);
      (p.a.prototype.onCreating = function () {
        var e = this;
        (this.koReRender = i.observable(0)),
          Object.defineProperty(e, 'koHasHtml', {
            get: function () {
              return e.koReRender(), e.hasHtml;
            },
          }),
          (this.koRenderedHtml = i.pureComputed(function () {
            return e.koReRender(), e.renderedHtml;
          }));
      }),
        (d.a.prototype.onCreating = function () {
          new h.a(this);
        }),
        (p.a.prototype.onChanged = function () {
          this.koReRender(this.koReRender() + 1);
        }),
        i.components.register('survey', {
          viewModel: {
            createViewModel: function (e, t) {
              var n = i.unwrap(e.survey);
              return (
                setTimeout(function () {
                  var e = document.createElement('div');
                  t.element.appendChild(e), n.render(e);
                }, 1),
                e.survey
              );
            },
          },
          template: u.b,
        }),
        (i.bindingHandlers.surveyProp = {
          update: function (e, t, n) {
            var r = i.utils.unwrapObservable(t()) || {};
            for (var o in r)
              if ('string' == typeof o) {
                var a = i.utils.unwrapObservable(r[o]);
                e[o] = a;
              }
          },
        }),
        (o.a.platform = 'knockout');
      var g = function (e, t) {
        (e.surveyTemplateEngine = function () {}),
          (e.surveyTemplateEngine.prototype = new e.nativeTemplateEngine()),
          (e.surveyTemplateEngine.prototype.makeTemplateSource = function (n, r) {
            if ('string' == typeof n) {
              r = r || document;
              var i = r.getElementById('survey-content-' + t);
              i ||
                ((i = document.createElement('div')),
                (i.id = 'survey-content-' + o.a.platform),
                (i.style.display = 'none'),
                (i.innerHTML = u.b),
                document.body.appendChild(i));
              for (var a, s = 0; s < i.children.length; s++)
                if (i.children[s].id === n) {
                  a = i.children[s];
                  break;
                }
              return (
                a || (a = r.getElementById(n)),
                a
                  ? new e.templateSources.domElement(a)
                  : new e.nativeTemplateEngine().makeTemplateSource(n, r)
              );
            }
            if (1 === n.nodeType || 8 === n.nodeType)
              return new e.templateSources.anonymousTemplate(n);
            throw new Error('Unknown template type: ' + n);
          });
        var n = new e.surveyTemplateEngine();
        e.setTemplateEngine(n);
      };
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(4),
        o = n(1),
        a = n(6),
        s = n(14),
        l = n(29),
        u = n(25),
        c = n(16),
        p = n(44),
        d = n(2),
        h = n(12),
        f = n(13),
        m = n(35),
        g = n(70),
        y = n(10),
        v = n(7);
      n.d(t, 'a', function () {
        return b;
      });
      var b = (function (e) {
        function t(n) {
          void 0 === n && (n = null);
          var r = e.call(this) || this;
          (r.valuesHash = {}),
            (r.variablesHash = {}),
            (r.localeValue = ''),
            (r.completedStateValue = ''),
            (r.completedStateTextValue = ''),
            (r.isTimerStarted = !1),
            (r.onCompleting = new a.c()),
            (r.onComplete = new a.c()),
            (r.onNavigateToUrl = new a.c()),
            (r.onStarted = new a.c()),
            (r.onPartialSend = new a.c()),
            (r.onCurrentPageChanging = new a.c()),
            (r.onCurrentPageChanged = new a.c()),
            (r.onValueChanging = new a.c()),
            (r.onValueChanged = new a.c()),
            (r.onVisibleChanged = new a.c()),
            (r.onPageVisibleChanged = new a.c()),
            (r.onPanelVisibleChanged = new a.c()),
            (r.onQuestionCreated = new a.c()),
            (r.onQuestionAdded = new a.c()),
            (r.onQuestionRemoved = new a.c()),
            (r.onPanelAdded = new a.c()),
            (r.onPanelRemoved = new a.c()),
            (r.onPageAdded = new a.c()),
            (r.onValidateQuestion = new a.c()),
            (r.onSettingQuestionErrors = new a.c()),
            (r.onServerValidateQuestions = new a.c()),
            (r.onValidatePanel = new a.c()),
            (r.onErrorCustomText = new a.c()),
            (r.onValidatedErrorsOnCurrentPage = new a.c()),
            (r.onProcessHtml = new a.c()),
            (r.onGetQuestionTitle = new a.c()),
            (r.onGetQuestionNo = new a.c()),
            (r.onProgressText = new a.c()),
            (r.onTextMarkdown = new a.c()),
            (r.onSendResult = new a.c()),
            (r.onGetResult = new a.c()),
            (r.onUploadFiles = new a.c()),
            (r.onDownloadFile = new a.c()),
            (r.onClearFiles = new a.c()),
            (r.onLoadChoicesFromServer = new a.c()),
            (r.onLoadedSurveyFromService = new a.c()),
            (r.onProcessTextValue = new a.c()),
            (r.onUpdateQuestionCssClasses = new a.c()),
            (r.onUpdatePanelCssClasses = new a.c()),
            (r.onUpdatePageCssClasses = new a.c()),
            (r.onAfterRenderSurvey = new a.c()),
            (r.onAfterRenderHeader = new a.c()),
            (r.onAfterRenderPage = new a.c()),
            (r.onAfterRenderQuestion = new a.c()),
            (r.onAfterRenderQuestionInput = new a.c()),
            (r.onAfterRenderPanel = new a.c()),
            (r.onMatrixRowAdded = new a.c()),
            (r.onMatrixBeforeRowAdded = new a.c()),
            (r.onMatrixRowRemoved = new a.c()),
            (r.onMatrixAllowRemoveRow = new a.c()),
            (r.onMatrixCellCreated = new a.c()),
            (r.onMatrixAfterCellRender = new a.c()),
            (r.onMatrixCellValueChanged = new a.c()),
            (r.onMatrixCellValueChanging = new a.c()),
            (r.onMatrixCellValidate = new a.c()),
            (r.onDynamicPanelAdded = new a.c()),
            (r.onDynamicPanelRemoved = new a.c()),
            (r.onTimer = new a.c()),
            (r.onTimerPanelInfoText = new a.c()),
            (r.onDynamicPanelItemValueChanged = new a.c()),
            (r.onIsAnswerCorrect = new a.c()),
            (r.onDragDropAllow = new a.c()),
            (r.onScrollingElementToTop = new a.c()),
            (r.onLocaleChangedEvent = new a.c()),
            (r.onGetQuestionTitleActions = new a.c()),
            (r.onGetPanelTitleActions = new a.c()),
            (r.jsonErrors = null),
            (r.cssValue = null),
            (r.hideRequiredErrors = !1),
            (r._isDesignMode = !1),
            (r.ignoreValidation = !1),
            (r.isNavigationButtonPressed = !1),
            (r.runningPages = null),
            (r.origionalPages = null),
            (r.isCalculatingProgressText = !1),
            (r.isTriggerIsRunning = !1),
            (r.triggerValues = null),
            (r.triggerKeys = null),
            (r.conditionValues = null),
            (r.isValueChangedOnRunningCondition = !1),
            (r.conditionRunnerCounter = 0),
            (r.conditionUpdateVisibleIndexes = !1),
            (r.conditionNotifyElementsOnAnyValueOrVariableChanged = !1),
            (r.isEndLoadingFromJson = null),
            (r.questionHashes = {
              names: {},
              namesInsensitive: {},
              valueNames: {},
              valueNamesInsensitive: {},
            }),
            (r.timerFunc = null),
            (r.timeSpent = 0);
          var i = r;
          return (
            'undefined' != typeof document && (t.stylesManager = new m.a()),
            r.createLocalizableString('title', r, !0),
            r.createLocalizableString('description', r, !0),
            r.createLocalizableString('logo', r, !1),
            r.createLocalizableString('completedHtml', r),
            r.createLocalizableString('completedBeforeHtml', r),
            r.createLocalizableString('loadingHtml', r),
            r.createLocalizableString('startSurveyText', r),
            r.createLocalizableString('pagePrevText', r),
            r.createLocalizableString('pageNextText', r),
            r.createLocalizableString('completeText', r),
            r.createLocalizableString('previewText', r),
            r.createLocalizableString('editText', r),
            r.createLocalizableString('questionTitleTemplate', r, !0),
            (r.textPreProcessor = new u.a()),
            (r.textPreProcessor.onProcess = function (e) {
              i.getProcessedTextValue(e);
            }),
            r.createNewArray(
              'pages',
              function (e) {
                i.doOnPageAdded(e);
              },
              function (e) {
                i.doOnPageRemoved(e);
              }
            ),
            r.createNewArray('triggers', function (e) {
              e.setOwner(i);
            }),
            r.createNewArray('calculatedValues', function (e) {
              e.setOwner(i);
            }),
            r.createNewArray('completedHtmlOnCondition', function (e) {
              e.locOwner = i;
            }),
            r.createNewArray('navigateToUrlOnCondition', function (e) {
              e.locOwner = i;
            }),
            r.registerFunctionOnPropertyValueChanged('firstPageIsStarted', function () {
              i.onFirstPageIsStartedChanged();
            }),
            r.registerFunctionOnPropertyValueChanged('mode', function () {
              i.onModeChanged();
            }),
            r.registerFunctionOnPropertyValueChanged('progressBarType', function () {
              i.updateProgressText();
            }),
            (r.onProgressText.onCallbacksChanged = function () {
              r.updateProgressText();
            }),
            r.onBeforeCreating(),
            n &&
              (('string' == typeof n || n instanceof String) && (n = JSON.parse(n)),
              n && n.clientId && (r.clientId = n.clientId),
              r.fromJSON(n),
              r.surveyId && r.loadSurveyFromService(r.surveyId, r.clientId)),
            r.onCreating(),
            r
          );
        }
        return (
          n.i(r.b)(t, e),
          Object.defineProperty(t.prototype, 'platformName', {
            get: function () {
              return t.platform;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'commentPrefix', {
            get: function () {
              return v.a.commentPrefix;
            },
            set: function (e) {
              v.a.commentPrefix = e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'currentPageValue', {
            get: function () {
              return this.getPropertyValue('currentPageValue', null);
            },
            set: function (e) {
              this.setPropertyValue('currentPageValue', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getType = function () {
            return 'survey';
          }),
          (t.prototype.onPropertyValueChanged = function (e, t, n) {
            'questionsOnPageMode' === e && this.onQuestionsOnPageModeChanged(t);
          }),
          Object.defineProperty(t.prototype, 'pages', {
            get: function () {
              return this.getPropertyValue('pages');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getCss = function () {
            return this.css;
          }),
          Object.defineProperty(t.prototype, 'css', {
            get: function () {
              return (
                this.cssValue ||
                  ((this.cssValue = {}), this.copyCssClasses(this.cssValue, s.b.getCss())),
                this.cssValue
              );
            },
            set: function (e) {
              this.mergeValues(e, this.css);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cssNavigationComplete', {
            get: function () {
              return this.getNavigationCss(this.css.navigationButton, this.css.navigation.complete);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cssNavigationPreview', {
            get: function () {
              return this.getNavigationCss(this.css.navigationButton, this.css.navigation.preview);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cssNavigationEdit', {
            get: function () {
              return this.getNavigationCss(this.css.navigationButton, this.css.navigation.edit);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cssNavigationPrev', {
            get: function () {
              return this.getNavigationCss(this.css.navigationButton, this.css.navigation.prev);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cssNavigationStart', {
            get: function () {
              return this.getNavigationCss(this.css.navigationButton, this.css.navigation.start);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cssNavigationNext', {
            get: function () {
              return this.getNavigationCss(this.css.navigationButton, this.css.navigation.next);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'completedCss', {
            get: function () {
              var e = this.css;
              return e.body + ' ' + e.completedPage;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getNavigationCss = function (e, t) {
            var n = '';
            return e && (n = e), t && (n += ' ' + t), n;
          }),
          Object.defineProperty(t.prototype, 'triggers', {
            get: function () {
              return this.getPropertyValue('triggers');
            },
            set: function (e) {
              this.setPropertyValue('triggers', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'calculatedValues', {
            get: function () {
              return this.getPropertyValue('calculatedValues');
            },
            set: function (e) {
              this.setPropertyValue('calculatedValues', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'surveyId', {
            get: function () {
              return this.getPropertyValue('surveyId', '');
            },
            set: function (e) {
              this.setPropertyValue('surveyId', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'surveyPostId', {
            get: function () {
              return this.getPropertyValue('surveyPostId', '');
            },
            set: function (e) {
              this.setPropertyValue('surveyPostId', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'clientId', {
            get: function () {
              return this.getPropertyValue('clientId', '');
            },
            set: function (e) {
              this.setPropertyValue('clientId', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cookieName', {
            get: function () {
              return this.getPropertyValue('cookieName', '');
            },
            set: function (e) {
              this.setPropertyValue('cookieName', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'sendResultOnPageNext', {
            get: function () {
              return this.getPropertyValue('sendResultOnPageNext', !1);
            },
            set: function (e) {
              this.setPropertyValue('sendResultOnPageNext', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'surveyShowDataSaving', {
            get: function () {
              return this.getPropertyValue('surveyShowDataSaving', !1);
            },
            set: function (e) {
              this.setPropertyValue('surveyShowDataSaving', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'focusFirstQuestionAutomatic', {
            get: function () {
              return this.getPropertyValue('focusFirstQuestionAutomatic', !0);
            },
            set: function (e) {
              this.setPropertyValue('focusFirstQuestionAutomatic', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'focusOnFirstError', {
            get: function () {
              return this.getPropertyValue('focusOnFirstError', !0);
            },
            set: function (e) {
              this.setPropertyValue('focusOnFirstError', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'showNavigationButtons', {
            get: function () {
              return this.getPropertyValue('showNavigationButtons');
            },
            set: function (e) {
              (!0 !== e && void 0 !== e) || (e = 'bottom'),
                !1 === e && (e = 'none'),
                this.setPropertyValue('showNavigationButtons', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'showPrevButton', {
            get: function () {
              return this.getPropertyValue('showPrevButton', !0);
            },
            set: function (e) {
              this.setPropertyValue('showPrevButton', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'showTitle', {
            get: function () {
              return this.getPropertyValue('showTitle', !0);
            },
            set: function (e) {
              this.setPropertyValue('showTitle', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'showPageTitles', {
            get: function () {
              return this.getPropertyValue('showPageTitles', !0);
            },
            set: function (e) {
              this.setPropertyValue('showPageTitles', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'showCompletedPage', {
            get: function () {
              return this.getPropertyValue('showCompletedPage', !0);
            },
            set: function (e) {
              this.setPropertyValue('showCompletedPage', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'navigateToUrl', {
            get: function () {
              return this.getPropertyValue('navigateToUrl');
            },
            set: function (e) {
              this.setPropertyValue('navigateToUrl', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'navigateToUrlOnCondition', {
            get: function () {
              return this.getPropertyValue('navigateToUrlOnCondition');
            },
            set: function (e) {
              this.setPropertyValue('navigateToUrlOnCondition', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getNavigateToUrl = function () {
            var e = this.getExpressionItemOnRunCondition(this.navigateToUrlOnCondition),
              t = e ? e.url : this.navigateToUrl;
            return t && (t = this.processText(t, !0)), t;
          }),
          (t.prototype.navigateTo = function () {
            var e = this.getNavigateToUrl(),
              t = { url: e };
            this.onNavigateToUrl.fire(this, t),
              t.url &&
                'undefined' != typeof window &&
                window.location &&
                (window.location.href = t.url);
          }),
          Object.defineProperty(t.prototype, 'requiredText', {
            get: function () {
              return this.getPropertyValue('requiredText', '*');
            },
            set: function (e) {
              this.setPropertyValue('requiredText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.beforeSettingQuestionErrors = function (e, t) {
            if (this.hideRequiredErrors)
              for (var n = 0; n < t.length; n++)
                'required' == t[n].getErrorType() && (t[n].visible = !1);
            this.onSettingQuestionErrors.fire(this, { question: e, errors: t });
          }),
          Object.defineProperty(t.prototype, 'questionStartIndex', {
            get: function () {
              return this.getPropertyValue('questionStartIndex', '');
            },
            set: function (e) {
              this.setPropertyValue('questionStartIndex', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'storeOthersAsComment', {
            get: function () {
              return this.getPropertyValue('storeOthersAsComment', !0);
            },
            set: function (e) {
              this.setPropertyValue('storeOthersAsComment', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'maxTextLength', {
            get: function () {
              return this.getPropertyValue('maxTextLength', 0);
            },
            set: function (e) {
              this.setPropertyValue('maxTextLength', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'maxOthersLength', {
            get: function () {
              return this.getPropertyValue('maxOthersLength', 0);
            },
            set: function (e) {
              this.setPropertyValue('maxOthersLength', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'goNextPageAutomatic', {
            get: function () {
              return this.getPropertyValue('goNextPageAutomatic', !1);
            },
            set: function (e) {
              this.setPropertyValue('goNextPageAutomatic', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'allowCompleteSurveyAutomatic', {
            get: function () {
              return this.getPropertyValue('allowCompleteSurveyAutomatic', !0);
            },
            set: function (e) {
              this.setPropertyValue('allowCompleteSurveyAutomatic', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'checkErrorsMode', {
            get: function () {
              return this.getPropertyValue('checkErrorsMode');
            },
            set: function (e) {
              this.setPropertyValue('checkErrorsMode', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'textUpdateMode', {
            get: function () {
              return this.getPropertyValue('textUpdateMode');
            },
            set: function (e) {
              this.setPropertyValue('textUpdateMode', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'clearInvisibleValues', {
            get: function () {
              return this.getPropertyValue('clearInvisibleValues');
            },
            set: function (e) {
              !0 === e && (e = 'onComplete'),
                !1 === e && (e = 'none'),
                this.setPropertyValue('clearInvisibleValues', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.clearIncorrectValues = function (e) {
            void 0 === e && (e = !1);
            for (var t = 0; t < this.pages.length; t++) this.pages[t].clearIncorrectValues();
            if (e) {
              var n = this.data,
                r = !1;
              for (var i in n)
                if (
                  !this.getQuestionByValueName(i) &&
                  !this.iscorrectValueWithPostPrefix(i, v.a.commentPrefix) &&
                  !this.iscorrectValueWithPostPrefix(i, v.a.matrixTotalValuePostFix)
                ) {
                  var o = this.getCalculatedValueByName(i);
                  (o && o.includeIntoResult) || ((r = !0), delete n[i]);
                }
              r && (this.data = n);
            }
          }),
          (t.prototype.iscorrectValueWithPostPrefix = function (e, t) {
            return (
              e.indexOf(t) === e.length - t.length &&
              !!this.getQuestionByValueName(e.substr(0, e.indexOf(t)))
            );
          }),
          Object.defineProperty(t.prototype, 'locale', {
            get: function () {
              return this.localeValue;
            },
            set: function (e) {
              (d.a.currentLocale = e),
                (this.localeValue = d.a.currentLocale),
                this.setPropertyValue('locale', this.localeValue),
                this.isLoadingFromJson ||
                  (this.locStrsChanged(),
                  this.onLocaleChanged(),
                  this.onLocaleChangedEvent.fire(this, e));
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getUsedLocales = function () {
            var e = new Array();
            this.addUsedLocales(e);
            var t = e.indexOf('default');
            if (t > -1) {
              var n = d.a.defaultLocale,
                r = e.indexOf(n);
              r > -1 && e.splice(r, 1), (t = e.indexOf('default')), (e[t] = n);
            }
            return e;
          }),
          (t.prototype.onLocaleChanged = function () {}),
          (t.prototype.getLocale = function () {
            return this.locale;
          }),
          (t.prototype.locStrsChanged = function () {
            e.prototype.locStrsChanged.call(this),
              this.currentPageValue &&
                (this.updateProgressText(),
                this.isStartedState && this.startedPage && this.startedPage.locStrsChanged(),
                this.currentPage && this.currentPage.locStrsChanged());
          }),
          (t.prototype.getMarkdownHtml = function (e, t) {
            return this.getSurveyMarkdownHtml(this, e, t);
          }),
          (t.prototype.getProcessedText = function (e) {
            return this.processText(e, !0);
          }),
          (t.prototype.getLocString = function (e) {
            return d.a.getString(e);
          }),
          (t.prototype.getErrorCustomText = function (e, t) {
            var n = { text: e, name: t.getErrorType(), error: t };
            return this.onErrorCustomText.fire(this, n), n.text;
          }),
          Object.defineProperty(t.prototype, 'emptySurveyText', {
            get: function () {
              return this.getLocString('emptySurvey');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'title', {
            get: function () {
              return this.getLocalizableStringText('title');
            },
            set: function (e) {
              this.setLocalizableStringText('title', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locTitle', {
            get: function () {
              return this.getLocalizableString('title');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'description', {
            get: function () {
              return this.getLocalizableStringText('description');
            },
            set: function (e) {
              this.setLocalizableStringText('description', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locDescription', {
            get: function () {
              return this.getLocalizableString('description');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'logo', {
            get: function () {
              return this.getLocalizableStringText('logo');
            },
            set: function (e) {
              this.setLocalizableStringText('logo', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locLogo', {
            get: function () {
              return this.getLocalizableString('logo');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'logoWidth', {
            get: function () {
              return this.getPropertyValue('logoWidth', 300);
            },
            set: function (e) {
              this.setPropertyValue('logoWidth', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'logoHeight', {
            get: function () {
              return this.getPropertyValue('logoHeight', 200);
            },
            set: function (e) {
              this.setPropertyValue('logoHeight', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'logoPosition', {
            get: function () {
              return this.getPropertyValue('logoPosition', 'left');
            },
            set: function (e) {
              this.setPropertyValue('logoPosition', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasLogo', {
            get: function () {
              return !!this.logo && 'none' !== this.logoPosition;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isLogoBefore', {
            get: function () {
              return this.hasLogo && ('left' === this.logoPosition || 'top' === this.logoPosition);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isLogoAfter', {
            get: function () {
              return (
                this.hasLogo && ('right' === this.logoPosition || 'bottom' === this.logoPosition)
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'logoClassNames', {
            get: function () {
              var e = {
                left: 'sv-logo--left',
                right: 'sv-logo--right',
                top: 'sv-logo--top',
                bottom: 'sv-logo--bottom',
              };
              return this.css.logo + ' ' + e[this.logoPosition];
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'logoFit', {
            get: function () {
              return this.getPropertyValue('logoFit');
            },
            set: function (e) {
              this.setPropertyValue('logoFit', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'titleMaxWidth', {
            get: function () {
              if (!this.isValueEmpty(this.logo)) {
                var e = this.logoWidth;
                if ('left' === this.logoPosition || 'right' === this.logoPosition)
                  return 'calc(100% - 5px - 2em - ' + e + 'px)';
              }
              return '';
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'completedHtml', {
            get: function () {
              return this.getLocalizableStringText('completedHtml');
            },
            set: function (e) {
              this.setLocalizableStringText('completedHtml', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locCompletedHtml', {
            get: function () {
              return this.getLocalizableString('completedHtml');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'completedHtmlOnCondition', {
            get: function () {
              return this.getPropertyValue('completedHtmlOnCondition');
            },
            set: function (e) {
              this.setPropertyValue('completedHtmlOnCondition', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.runExpression = function (e) {
            if (!e) return null;
            var t = this.getFilteredValues(),
              n = this.getFilteredProperties();
            return new y.b(e).run(t, n);
          }),
          (t.prototype.runCondition = function (e) {
            if (!e) return !1;
            var t = this.getFilteredValues(),
              n = this.getFilteredProperties();
            return new y.a(e).run(t, n);
          }),
          (t.prototype.runTriggers = function () {
            this.checkTriggers(this.getFilteredValues(), !1);
          }),
          Object.defineProperty(t.prototype, 'renderedCompletedHtml', {
            get: function () {
              var e = this.getExpressionItemOnRunCondition(this.completedHtmlOnCondition);
              return e ? e.html : this.completedHtml;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getExpressionItemOnRunCondition = function (e) {
            if (0 == e.length) return null;
            for (
              var t = this.getFilteredValues(), n = this.getFilteredProperties(), r = 0;
              r < e.length;
              r++
            )
              if (e[r].runCondition(t, n)) return e[r];
            return null;
          }),
          Object.defineProperty(t.prototype, 'completedBeforeHtml', {
            get: function () {
              return this.getLocalizableStringText('completedBeforeHtml');
            },
            set: function (e) {
              this.setLocalizableStringText('completedBeforeHtml', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locCompletedBeforeHtml', {
            get: function () {
              return this.getLocalizableString('completedBeforeHtml');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'loadingHtml', {
            get: function () {
              return this.getLocalizableStringText('loadingHtml');
            },
            set: function (e) {
              this.setLocalizableStringText('loadingHtml', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locLoadingHtml', {
            get: function () {
              return this.getLocalizableString('loadingHtml');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'startSurveyText', {
            get: function () {
              return this.getLocalizableStringText(
                'startSurveyText',
                this.getLocString('startSurveyText')
              );
            },
            set: function (e) {
              this.setLocalizableStringText('startSurveyText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locStartSurveyText', {
            get: function () {
              return this.getLocalizableString('startSurveyText');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'pagePrevText', {
            get: function () {
              return this.getLocalizableStringText(
                'pagePrevText',
                this.getLocString('pagePrevText')
              );
            },
            set: function (e) {
              this.setLocalizableStringText('pagePrevText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locPagePrevText', {
            get: function () {
              return this.getLocalizableString('pagePrevText');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'pageNextText', {
            get: function () {
              return this.getLocalizableStringText(
                'pageNextText',
                this.getLocString('pageNextText')
              );
            },
            set: function (e) {
              this.setLocalizableStringText('pageNextText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locPageNextText', {
            get: function () {
              return this.getLocalizableString('pageNextText');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'completeText', {
            get: function () {
              return this.getLocalizableStringText(
                'completeText',
                this.getLocString('completeText')
              );
            },
            set: function (e) {
              this.setLocalizableStringText('completeText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locCompleteText', {
            get: function () {
              return this.getLocalizableString('completeText');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'previewText', {
            get: function () {
              return this.getLocalizableStringText('previewText', this.getLocString('previewText'));
            },
            set: function (e) {
              this.setLocalizableStringText('previewText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locPreviewText', {
            get: function () {
              return this.getLocalizableString('previewText');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'editText', {
            get: function () {
              return this.getLocalizableStringText('editText', this.getLocString('editText'));
            },
            set: function (e) {
              this.setLocalizableStringText('editText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locEditText', {
            get: function () {
              return this.getLocalizableString('editText');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'questionTitlePattern', {
            get: function () {
              return this.getPropertyValue('questionTitlePattern', 'numTitleRequire');
            },
            set: function (e) {
              'numRequireTitle' !== e &&
                'requireNumTitle' !== e &&
                'numTitle' != e &&
                (e = 'numTitleRequire'),
                this.setPropertyValue('questionTitlePattern', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getQuestionTitlePatternOptions = function () {
            var e = new Array(),
              t = this.getLocString('questionTitlePatternText'),
              n = this.questionStartIndex ? this.questionStartIndex : '1.';
            return (
              e.push({ value: 'numTitleRequire', text: n + ' ' + t + ' ' + this.requiredText }),
              e.push({ value: 'numRequireTitle', text: n + ' ' + this.requiredText + ' ' + t }),
              e.push({ value: 'requireNumTitle', text: this.requiredText + ' ' + n + ' ' + t }),
              e.push({ value: 'numTitle', text: n + ' ' + t }),
              e
            );
          }),
          Object.defineProperty(t.prototype, 'questionTitleTemplate', {
            get: function () {
              return this.getLocalizableStringText('questionTitleTemplate');
            },
            set: function (e) {
              this.setLocalizableStringText('questionTitleTemplate', e),
                (this.questionTitlePattern = this.getNewTitlePattern(e)),
                (this.questionStartIndex = this.getNewQuestionTitleElement(
                  e,
                  'no',
                  this.questionStartIndex,
                  '1'
                )),
                (this.requiredText = this.getNewQuestionTitleElement(
                  e,
                  'require',
                  this.requiredText,
                  '*'
                ));
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getNewTitlePattern = function (e) {
            if (e) {
              for (var t = []; e.indexOf('{') > -1; ) {
                e = e.substr(e.indexOf('{') + 1);
                var n = e.indexOf('}');
                if (n < 0) break;
                t.push(e.substr(0, n)), (e = e.substr(n + 1));
              }
              if (t.length > 1) {
                if ('require' == t[0]) return 'requireNumTitle';
                if ('require' == t[1] && 3 == t.length) return 'numRequireTitle';
                if (t.indexOf('require') < 0) return 'numTitle';
              }
              if (1 == t.length && 'title' == t[0]) return 'numTitle';
            }
            return 'numTitleRequire';
          }),
          (t.prototype.getNewQuestionTitleElement = function (e, t, n, r) {
            if (((t = '{' + t + '}'), !e || e.indexOf(t) < 0)) return n;
            for (var i = e.indexOf(t), o = '', a = '', s = i - 1; s >= 0 && '}' != e[s]; s--);
            for (
              s < i - 1 && (o = e.substr(s + 1, i - s - 1)), i += t.length, s = i;
              s < e.length && '{' != e[s];
              s++
            );
            for (s > i && (a = e.substr(i, s - i)), s = 0; s < o.length && o.charCodeAt(s) < 33; )
              s++;
            for (o = o.substr(s), s = a.length - 1; s >= 0 && a.charCodeAt(s) < 33; ) s--;
            return (a = a.substr(0, s + 1)), o || a ? o + (n || r) + a : n;
          }),
          Object.defineProperty(t.prototype, 'locQuestionTitleTemplate', {
            get: function () {
              return this.getLocalizableString('questionTitleTemplate');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getUpdatedQuestionTitle = function (e, t) {
            if (this.onGetQuestionTitle.isEmpty) return t;
            var n = { question: e, title: t };
            return this.onGetQuestionTitle.fire(this, n), n.title;
          }),
          (t.prototype.getUpdatedQuestionNo = function (e, t) {
            if (this.onGetQuestionNo.isEmpty) return t;
            var n = { question: e, no: t };
            return this.onGetQuestionNo.fire(this, n), n.no;
          }),
          Object.defineProperty(t.prototype, 'showPageNumbers', {
            get: function () {
              return this.getPropertyValue('showPageNumbers', !1);
            },
            set: function (e) {
              e !== this.showPageNumbers &&
                (this.setPropertyValue('showPageNumbers', e), this.updateVisibleIndexes());
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'showQuestionNumbers', {
            get: function () {
              return this.getPropertyValue('showQuestionNumbers');
            },
            set: function (e) {
              (e = e.toLowerCase()),
                (e = 'onpage' === e ? 'onPage' : e) !== this.showQuestionNumbers &&
                  (this.setPropertyValue('showQuestionNumbers', e), this.updateVisibleIndexes());
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'showProgressBar', {
            get: function () {
              return this.getPropertyValue('showProgressBar');
            },
            set: function (e) {
              this.setPropertyValue('showProgressBar', e.toLowerCase());
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'progressBarType', {
            get: function () {
              return this.getPropertyValue('progressBarType');
            },
            set: function (e) {
              this.setPropertyValue('progressBarType', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isShowProgressBarOnTop', {
            get: function () {
              return (
                !!this.canShowProresBar() &&
                ('top' === this.showProgressBar || 'both' === this.showProgressBar)
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isShowProgressBarOnBottom', {
            get: function () {
              return (
                !!this.canShowProresBar() &&
                ('bottom' === this.showProgressBar || 'both' === this.showProgressBar)
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.canShowProresBar = function () {
            return !this.isShowingPreview || 'showAllQuestions' != this.showPreviewBeforeComplete;
          }),
          Object.defineProperty(t.prototype, 'processedTitle', {
            get: function () {
              return this.locTitle.renderedHtml;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'questionTitleLocation', {
            get: function () {
              return this.getPropertyValue('questionTitleLocation');
            },
            set: function (e) {
              this.setPropertyValue('questionTitleLocation', e.toLowerCase()),
                this.isLoadingFromJson || this.updateElementCss();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.updateElementCss = function () {
            for (var e = this.visiblePages, t = 0; t < e.length; t++) e[t].updateElementCss();
          }),
          Object.defineProperty(t.prototype, 'questionErrorLocation', {
            get: function () {
              return this.getPropertyValue('questionErrorLocation');
            },
            set: function (e) {
              this.setPropertyValue('questionErrorLocation', e.toLowerCase());
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'questionDescriptionLocation', {
            get: function () {
              return this.getPropertyValue('questionDescriptionLocation');
            },
            set: function (e) {
              this.setPropertyValue('questionDescriptionLocation', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'mode', {
            get: function () {
              return this.getPropertyValue('mode');
            },
            set: function (e) {
              (e = e.toLowerCase()) != this.mode &&
                (('edit' != e && 'display' != e) || this.setPropertyValue('mode', e));
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.onModeChanged = function () {
            for (var e = 0; e < this.pages.length; e++) {
              var t = this.pages[e];
              t.setPropertyValue('isReadOnly', t.isReadOnly);
            }
          }),
          Object.defineProperty(t.prototype, 'data', {
            get: function () {
              for (var e = {}, t = this.getValuesKeys(), n = 0; n < t.length; n++) {
                var r = t[n],
                  i = this.getDataValueCore(this.valuesHash, r);
                void 0 !== i && (e[r] = i);
              }
              return this.setCalcuatedValuesIntoResult(e), e;
            },
            set: function (e) {
              (this.valuesHash = {}), this.setDataCore(e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.mergeData = function (e) {
            e && this.setDataCore(e);
          }),
          (t.prototype.setDataCore = function (e) {
            if (e) for (var t in e) this.setDataValueCore(this.valuesHash, t, e[t]);
            this.updateAllQuestionsValue(),
              this.notifyAllQuestionsOnValueChanged(),
              this.notifyElementsOnAnyValueOrVariableChanged(''),
              this.runConditions();
          }),
          Object.defineProperty(t.prototype, 'editingObj', {
            get: function () {
              return this.editingObjValue;
            },
            set: function (e) {
              var t = this;
              if (this.editingObj != e) {
                if (
                  (this.editingObj &&
                    this.editingObj.onPropertyChanged.remove(this.onEditingObjPropertyChanged),
                  (this.editingObjValue = e),
                  !e)
                )
                  for (var n = this.getAllQuestions(), r = 0; r < n.length; r++) n[r].unbindValue();
                this.editingObj &&
                  (this.setDataCore({}),
                  (this.onEditingObjPropertyChanged = function (e, n) {
                    t.updateOnSetValue(n.name, n.newValue, n.oldValue);
                  }),
                  this.editingObj.onPropertyChanged.add(this.onEditingObjPropertyChanged));
              }
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isEditingSurveyElement', {
            get: function () {
              return !!this.editingObj;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setCalcuatedValuesIntoResult = function (e) {
            for (var t = 0; t < this.calculatedValues.length; t++) {
              var n = this.calculatedValues[t];
              n.includeIntoResult &&
                n.name &&
                void 0 !== this.getVariable(n.name) &&
                (e[n.name] = this.getVariable(n.name));
            }
          }),
          (t.prototype.getAllValues = function () {
            return this.data;
          }),
          (t.prototype.getPlainData = function (e) {
            void 0 === e && (e = { includeEmpty: !0, includeQuestionTypes: !1 });
            var t = [];
            return (
              this.getAllQuestions().forEach(function (n) {
                var r = n.getPlainData(e);
                r && t.push(r);
              }),
              t
            );
          }),
          (t.prototype.getFilteredValues = function () {
            var e = {};
            for (var t in this.variablesHash) e[t] = this.variablesHash[t];
            this.addCalculatedValuesIntoFilteredValues(e);
            for (var n = this.getValuesKeys(), r = 0; r < n.length; r++) {
              var t = n[r];
              e[t] = this.getDataValueCore(this.valuesHash, t);
            }
            return e;
          }),
          (t.prototype.addCalculatedValuesIntoFilteredValues = function (e) {
            for (var t = this.calculatedValues, n = 0; n < t.length; n++) e[t[n].name] = t[n].value;
          }),
          (t.prototype.getFilteredProperties = function () {
            return { survey: this };
          }),
          (t.prototype.getValuesKeys = function () {
            if (!this.editingObj) return Object.keys(this.valuesHash);
            for (var e = o.b.getPropertiesByObj(this.editingObj), t = [], n = 0; n < e.length; n++)
              t.push(e[n].name);
            return t;
          }),
          (t.prototype.getDataValueCore = function (e, t) {
            if (this.editingObj) {
              var n = o.b.findProperty(this.editingObj.getType(), t);
              return n && n.isLocalizable && n.isArray
                ? this.editingObj[t]
                : this.editingObj.getPropertyValue(t);
            }
            return e[t];
          }),
          (t.prototype.setDataValueCore = function (e, t, n) {
            if (this.editingObj) {
              var r = o.b.findProperty(this.editingObj.getType(), t);
              r && r.isLocalizable
                ? (this.editingObj[t] = n)
                : this.editingObj.setPropertyValue(t, n);
            } else e[t] = n;
          }),
          (t.prototype.deleteDataValueCore = function (e, t) {
            this.editingObj ? (this.editingObj[t] = null) : delete e[t];
          }),
          Object.defineProperty(t.prototype, 'comments', {
            get: function () {
              for (var e = {}, t = this.getValuesKeys(), n = 0; n < t.length; n++) {
                var r = t[n];
                r.indexOf(this.commentPrefix) > 0 &&
                  (e[r] = this.getDataValueCore(this.valuesHash, r));
              }
              return e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'visiblePages', {
            get: function () {
              if (this.isDesignMode) return this.pages;
              for (var e = new Array(), t = 0; t < this.pages.length; t++)
                this.pages[t].isVisible && e.push(this.pages[t]);
              return e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isEmpty', {
            get: function () {
              return 0 == this.pages.length;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'PageCount', {
            get: function () {
              return this.pageCount;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'pageCount', {
            get: function () {
              return this.pages.length;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'visiblePageCount', {
            get: function () {
              return this.visiblePages.length;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'startedPage', {
            get: function () {
              var e = this.firstPageIsStarted && this.pages.length > 0 ? this.pages[0] : null;
              return e && (e.onFirstRendering(), e.setWasShown(!0)), e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'currentPage', {
            get: function () {
              var e = this.visiblePages;
              return (
                null != this.currentPageValue &&
                  e.indexOf(this.currentPageValue) < 0 &&
                  ((this.onContainsPageCallback &&
                    this.onContainsPageCallback(this.currentPageValue)) ||
                    (this.currentPage = null)),
                null == this.currentPageValue && e.length > 0 && (this.currentPage = e[0]),
                this.currentPageValue
              );
            },
            set: function (e) {
              var t = this.getPageByObject(e);
              if (!e || t) {
                var n = this.visiblePages;
                if (!(null != t && n.indexOf(t) < 0) && t != this.currentPageValue) {
                  var r = this.currentPageValue;
                  this.currentPageChanging(t, r) &&
                    ((this.currentPageValue = t),
                    t && (t.onFirstRendering(), t.updateCustomWidgets(), t.setWasShown(!0)),
                    this.locStrsChanged(),
                    this.currentPageChanged(t, r));
                }
              }
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getPageByObject = function (e) {
            if (!e) return null;
            if (e.getType && 'page' == e.getType()) return e;
            if ('string' == typeof e || e instanceof String) return this.getPageByName(String(e));
            if (!isNaN(e)) {
              var t = Number(e),
                n = this.visiblePages;
              return e < 0 || e >= n.length ? null : n[t];
            }
            return e;
          }),
          Object.defineProperty(t.prototype, 'currentPageNo', {
            get: function () {
              return this.visiblePages.indexOf(this.currentPage);
            },
            set: function (e) {
              var t = this.visiblePages;
              e < 0 || e >= t.length || (this.currentPage = t[e]);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'questionsOrder', {
            get: function () {
              return this.getPropertyValue('questionsOrder');
            },
            set: function (e) {
              this.setPropertyValue('questionsOrder', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.focusFirstQuestion = function () {
            var e = this.currentPage;
            e && (e.scrollToTop(), e.focusFirstQuestion());
          }),
          (t.prototype.scrollToTopOnPageChange = function () {
            var e = this.currentPage;
            e && (e.scrollToTop(), this.focusFirstQuestionAutomatic && e.focusFirstQuestion());
          }),
          Object.defineProperty(t.prototype, 'state', {
            get: function () {
              return this.isLoading
                ? 'loading'
                : this.isCompleted
                ? 'completed'
                : this.isCompletedBefore
                ? 'completedbefore'
                : !this.isDesignMode && this.isEditMode && this.isStartedState && this.startedPage
                ? 'starting'
                : this.isShowingPreview
                ? this.currentPage
                  ? 'preview'
                  : 'empty'
                : this.currentPage
                ? 'running'
                : 'empty';
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isCompleted', {
            get: function () {
              return this.getPropertyValue('isCompleted', !1);
            },
            set: function (e) {
              this.setPropertyValue('isCompleted', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isShowingPreview', {
            get: function () {
              return this.getPropertyValue('isShowingPreview', !1);
            },
            set: function (e) {
              this.isShowingPreview != e &&
                (this.setPropertyValue('isShowingPreview', e), this.onShowingPreviewChanged());
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isStartedState', {
            get: function () {
              return this.getPropertyValue('isStartedState', !1);
            },
            set: function (e) {
              this.setPropertyValue('isStartedState', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isCompletedBefore', {
            get: function () {
              return this.getPropertyValue('isCompletedBefore', !1);
            },
            set: function (e) {
              this.setPropertyValue('isCompletedBefore', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isLoading', {
            get: function () {
              return this.getPropertyValue('isLoading', !1);
            },
            set: function (e) {
              this.setPropertyValue('isLoading', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'completedState', {
            get: function () {
              return this.completedStateValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'completedStateText', {
            get: function () {
              return this.completedStateTextValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setCompletedState = function (e, t) {
            (this.completedStateValue = e),
              t ||
                ('saving' == e && (t = this.getLocString('savingData')),
                'error' == e && (t = this.getLocString('savingDataError')),
                'success' == e && (t = this.getLocString('savingDataSuccess'))),
              (this.completedStateTextValue = t);
          }),
          (t.prototype.clear = function (e, t) {
            void 0 === e && (e = !0),
              void 0 === t && (t = !0),
              e && ((this.data = null), (this.variablesHash = {})),
              (this.timeSpent = 0);
            for (var n = 0; n < this.pages.length; n++)
              (this.pages[n].timeSpent = 0),
                this.pages[n].setWasShown(!1),
                (this.pages[n].passed = !1);
            (this.isCompleted = !1),
              (this.isCompletedBefore = !1),
              (this.isLoading = !1),
              (this.isStartedState = this.firstPageIsStarted),
              t && this.visiblePageCount > 0 && (this.currentPage = this.visiblePages[0]),
              e && this.updateValuesWithDefaults();
          }),
          (t.prototype.mergeValues = function (e, t) {
            if (t && e)
              for (var n in e) {
                var r = e[n];
                r && 'object' == typeof r
                  ? (t[n] || (t[n] = {}), this.mergeValues(r, t[n]))
                  : (t[n] = r);
              }
          }),
          (t.prototype.updateValuesWithDefaults = function () {
            if (!this.isDesignMode && !this.isLoading)
              for (var e = 0; e < this.pages.length; e++)
                for (var t = this.pages[e].questions, n = 0; n < t.length; n++)
                  t[n].updateValueWithDefaults();
          }),
          (t.prototype.updateCustomWidgets = function (e) {
            e && e.updateCustomWidgets();
          }),
          (t.prototype.currentPageChanging = function (e, t) {
            var n = {
              oldCurrentPage: t,
              newCurrentPage: e,
              allowChanging: !0,
              isNextPage: this.isNextPage(e, t),
              isPrevPage: this.isPrevPage(e, t),
            };
            return this.onCurrentPageChanging.fire(this, n), n.allowChanging;
          }),
          (t.prototype.currentPageChanged = function (e, t) {
            this.onCurrentPageChanged.fire(this, {
              oldCurrentPage: t,
              newCurrentPage: e,
              isNextPage: this.isNextPage(e, t),
              isPrevPage: this.isPrevPage(e, t),
            });
          }),
          (t.prototype.isNextPage = function (e, t) {
            return !(!e || !t) && e.visibleIndex == t.visibleIndex + 1;
          }),
          (t.prototype.isPrevPage = function (e, t) {
            return !(!e || !t) && e.visibleIndex + 1 == t.visibleIndex;
          }),
          (t.prototype.getProgress = function () {
            if (null == this.currentPage) return 0;
            if ('pages' !== this.progressBarType) {
              var e = this.getProgressInfo();
              return 'requiredQuestions' === this.progressBarType
                ? e.requiredQuestionCount > 1
                  ? Math.ceil((100 * e.requiredAnsweredQuestionCount) / e.requiredQuestionCount)
                  : 100
                : e.questionCount > 1
                ? Math.ceil((100 * e.answeredQuestionCount) / e.questionCount)
                : 100;
            }
            var t = this.visiblePages.indexOf(this.currentPage) + 1;
            return Math.ceil((100 * t) / this.visiblePageCount);
          }),
          Object.defineProperty(t.prototype, 'progressValue', {
            get: function () {
              return this.getPropertyValue('progressValue', 0);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isNavigationButtonsShowing', {
            get: function () {
              if (this.isDesignMode) return 'none';
              var e = this.currentPage;
              return e
                ? 'show' === e.navigationButtonsVisibility
                  ? 'bottom'
                  : 'hide' === e.navigationButtonsVisibility
                  ? 'none'
                  : this.showNavigationButtons
                : 'none';
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isEditMode', {
            get: function () {
              return 'edit' == this.mode;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isCompleteButtonVisible', {
            get: function () {
              return (
                this.isEditMode && (!this.isShowPreviewBeforeComplete || 'preview' == this.state)
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isPreviewButtonVisible', {
            get: function () {
              return this.isEditMode && this.isShowPreviewBeforeComplete && 'running' == this.state;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isCancelPreviewButtonVisible', {
            get: function () {
              return this.isEditMode && this.isShowPreviewBeforeComplete && 'preview' == this.state;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isDisplayMode', {
            get: function () {
              return 'display' == this.mode || 'preview' == this.state;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isUpdateValueTextOnTyping', {
            get: function () {
              return 'onTyping' == this.textUpdateMode;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isDesignMode', {
            get: function () {
              return this._isDesignMode;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setDesignMode = function (e) {
            (this._isDesignMode = e), this.onQuestionsOnPageModeChanged('standard');
          }),
          Object.defineProperty(t.prototype, 'showInvisibleElements', {
            get: function () {
              return this.getPropertyValue('showInvisibleElements', !1);
            },
            set: function (e) {
              var t = this.visiblePages;
              this.setPropertyValue('showInvisibleElements', e),
                this.isLoadingFromJson ||
                  (this.runConditions(), this.updateAllElementsVisibility(t));
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.updateAllElementsVisibility = function (e) {
            for (var t = 0; t < this.pages.length; t++) {
              var n = this.pages[t];
              n.updateElementVisibility(),
                e.indexOf(n) > -1 != n.isVisible &&
                  this.onPageVisibleChanged.fire(this, { page: n, visible: n.isVisible });
            }
          }),
          Object.defineProperty(t.prototype, 'areInvisibleElementsShowing', {
            get: function () {
              return this.isDesignMode || this.showInvisibleElements;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'areEmptyElementsHidden', {
            get: function () {
              return (
                this.isShowingPreview && 'showAnsweredQuestions' == this.showPreviewBeforeComplete
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasCookie', {
            get: function () {
              if (!this.cookieName || 'undefined' == typeof document) return !1;
              var e = document.cookie;
              return e && e.indexOf(this.cookieName + '=true') > -1;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setCookie = function () {
            this.cookieName &&
              'undefined' != typeof document &&
              (document.cookie = this.cookieName + '=true; expires=Fri, 31 Dec 9999 0:0:0 GMT');
          }),
          (t.prototype.deleteCookie = function () {
            this.cookieName && (document.cookie = this.cookieName + '=;');
          }),
          (t.prototype.nextPage = function () {
            return !this.isLastPage && this.doCurrentPageComplete(!1);
          }),
          (t.prototype.hasErrorsOnNavigate = function (e) {
            if (this.ignoreValidation || !this.isEditMode) return !1;
            if ('onComplete' === this.checkErrorsMode) {
              if (!this.isLastPage) return !1;
              if (this.hasErrors(!0, !0)) return !0;
            } else if (this.isCurrentPageHasErrors) return !0;
            return this.checkForAsyncQuestionValidation(e);
          }),
          (t.prototype.checkForAsyncQuestionValidation = function (e) {
            var t = this;
            this.clearAsyncValidationQuesitons();
            for (var n = this.currentPage.questions, r = 0; r < n.length; r++)
              n[r].isRunningValidators &&
                ((n[r].onCompletedAsyncValidators = function (n) {
                  t.onCompletedAsyncQuestionValidators(e, n);
                }),
                this.asyncValidationQuesitons.push(n[r]));
            return this.asyncValidationQuesitons.length > 0;
          }),
          (t.prototype.clearAsyncValidationQuesitons = function () {
            if (this.asyncValidationQuesitons)
              for (var e = this.asyncValidationQuesitons, t = 0; t < e.length; t++)
                e[t].onCompletedAsyncValidators = null;
            this.asyncValidationQuesitons = [];
          }),
          (t.prototype.onCompletedAsyncQuestionValidators = function (e, t) {
            if (t) return void this.clearAsyncValidationQuesitons();
            for (var n = this.asyncValidationQuesitons, r = 0; r < n.length; r++)
              if (n[r].isRunningValidators) return;
            this.doCurrentPageCompleteCore(e);
          }),
          Object.defineProperty(t.prototype, 'isCurrentPageHasErrors', {
            get: function () {
              return this.checkIsCurrentPageHasErrors();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.hasErrors = function (e, t) {
            void 0 === e && (e = !0), void 0 === t && (t = !1);
            for (var n = this.visiblePages, r = null, i = !1, o = 0; o < n.length; o++)
              n[o].hasErrors(e, !1) && (r || (r = n[o]), (i = !0));
            if (t && r) {
              this.currentPage = r;
              for (var a = r.questions, o = 0; o < a.length; o++)
                if (a[o].errors.length > 0) {
                  a[o].focus(!0);
                  break;
                }
            }
            return i;
          }),
          (t.prototype.ensureUniqueNames = function (e) {
            if ((void 0 === e && (e = null), null == e))
              for (var t = 0; t < this.pages.length; t++) this.ensureUniqueName(this.pages[t]);
            else this.ensureUniqueName(e);
          }),
          (t.prototype.ensureUniqueName = function (e) {
            if (
              (e.isPage && this.ensureUniquePageName(e),
              e.isPanel && this.ensureUniquePanelName(e),
              e.isPage || e.isPanel)
            )
              for (var t = e.elements, n = 0; n < t.length; n++) this.ensureUniqueNames(t[n]);
            else this.ensureUniqueQuestionName(e);
          }),
          (t.prototype.ensureUniquePageName = function (e) {
            var t = this;
            return this.ensureUniqueElementName(e, function (e) {
              return t.getPageByName(e);
            });
          }),
          (t.prototype.ensureUniquePanelName = function (e) {
            var t = this;
            return this.ensureUniqueElementName(e, function (e) {
              return t.getPanelByName(e);
            });
          }),
          (t.prototype.ensureUniqueQuestionName = function (e) {
            var t = this;
            return this.ensureUniqueElementName(e, function (e) {
              return t.getQuestionByName(e);
            });
          }),
          (t.prototype.ensureUniqueElementName = function (e, t) {
            var n = t(e.name);
            if (n && n != e) {
              for (var r = this.getNewName(e.name); t(r); ) var r = this.getNewName(e.name);
              e.name = r;
            }
          }),
          (t.prototype.getNewName = function (e) {
            for (var t = e.length; t > 0 && e[t - 1] >= '0' && e[t - 1] <= '9'; ) t--;
            var n = e.substr(0, t),
              r = 0;
            return t < e.length && (r = parseInt(e.substr(t))), r++, n + r;
          }),
          (t.prototype.checkIsCurrentPageHasErrors = function (e) {
            return void 0 === e && (e = void 0), this.checkIsPageHasErrors(this.currentPage, e);
          }),
          (t.prototype.checkIsPageHasErrors = function (e, t) {
            if ((void 0 === t && (t = void 0), void 0 === t && (t = this.focusOnFirstError), !e))
              return !0;
            var n = e.hasErrors(!0, t);
            return this.fireValidatedErrorsOnPage(e), n;
          }),
          (t.prototype.fireValidatedErrorsOnPage = function (e) {
            if (!this.onValidatedErrorsOnCurrentPage.isEmpty && e) {
              for (
                var t = e.questions, n = new Array(), r = new Array(), i = 0;
                i < t.length;
                i++
              ) {
                var o = t[i];
                if (o.errors.length > 0) {
                  n.push(o);
                  for (var a = 0; a < o.errors.length; a++) r.push(o.errors[a]);
                }
              }
              this.onValidatedErrorsOnCurrentPage.fire(this, { questions: n, errors: r, page: e });
            }
          }),
          (t.prototype.prevPage = function () {
            if (this.isFirstPage) return !1;
            this.resetNavigationButton();
            var e = this.visiblePages,
              t = e.indexOf(this.currentPage);
            this.currentPage = e[t - 1];
          }),
          (t.prototype.completeLastPage = function () {
            var e = this.doCurrentPageComplete(!0);
            return e && this.cancelPreview(), e;
          }),
          (t.prototype.navigationMouseDown = function () {
            return (this.isNavigationButtonPressed = !0), !0;
          }),
          (t.prototype.resetNavigationButton = function () {
            this.isNavigationButtonPressed = !1;
          }),
          (t.prototype.showPreview = function () {
            return (
              this.resetNavigationButton(),
              !this.hasErrorsOnNavigate(!0) &&
                !this.doServerValidation(!0, !0) &&
                ((this.isShowingPreview = !0), !0)
            );
          }),
          (t.prototype.cancelPreview = function (e) {
            void 0 === e && (e = null),
              this.isShowingPreview &&
                ((this.isShowingPreview = !1),
                i.a.isValueEmpty(e) && this.visiblePageCount > 0 && (e = this.visiblePageCount - 1),
                null !== e && (this.currentPage = e));
          }),
          (t.prototype.cancelPreviewByPage = function (e) {
            this.cancelPreview(e.originalPage);
          }),
          (t.prototype.doCurrentPageComplete = function (e) {
            return (
              !this.isValidatingOnServer &&
              (this.resetNavigationButton(),
              !this.hasErrorsOnNavigate(e) && this.doCurrentPageCompleteCore(e))
            );
          }),
          (t.prototype.doCurrentPageCompleteCore = function (e) {
            return (
              !this.doServerValidation(e) &&
              ((this.currentPage.passed = !0), e ? this.doComplete() : this.doNextPage(), !0)
            );
          }),
          Object.defineProperty(t.prototype, 'isSinglePage', {
            get: function () {
              return 'singlePage' == this.questionsOnPageMode;
            },
            set: function (e) {
              this.questionsOnPageMode = e ? 'singlePage' : 'standard';
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'questionsOnPageMode', {
            get: function () {
              return this.getPropertyValue('questionsOnPageMode', 'standard');
            },
            set: function (e) {
              this.setPropertyValue('questionsOnPageMode', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'firstPageIsStarted', {
            get: function () {
              return this.getPropertyValue('firstPageIsStarted', !1);
            },
            set: function (e) {
              this.setPropertyValue('firstPageIsStarted', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.isPageStarted = function (e) {
            return this.firstPageIsStarted && this.pages.length > 0 && this.pages[0] === e;
          }),
          Object.defineProperty(t.prototype, 'showPreviewBeforeComplete', {
            get: function () {
              return this.getPropertyValue('showPreviewBeforeComplete', 'noPreview');
            },
            set: function (e) {
              this.setPropertyValue('showPreviewBeforeComplete', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isShowPreviewBeforeComplete', {
            get: function () {
              var e = this.showPreviewBeforeComplete;
              return 'showAllQuestions' == e || 'showAnsweredQuestions' == e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.onFirstPageIsStartedChanged = function () {
            0 != this.pages.length &&
              ((this.isStartedState = this.firstPageIsStarted),
              this.pageVisibilityChanged(this.pages[0], !this.firstPageIsStarted));
          }),
          (t.prototype.onShowingPreviewChanged = function () {
            this.isDesignMode ||
              (this.isShowingPreview
                ? ((this.runningPages = this.pages.slice(0, this.pages.length)),
                  this.setupPagesForPageModes(!0))
                : (this.runningPages && this.restoreOrigionalPages(this.runningPages),
                  (this.runningPages = null)),
              this.updateAllElementsVisibility(this.pages),
              this.updateVisibleIndexes(),
              (this.currentPageNo = 0));
          }),
          (t.prototype.onQuestionsOnPageModeChanged = function (e) {
            this.isShowingPreview ||
              ('standard' == this.questionsOnPageMode || this.isDesignMode
                ? (this.origionalPages && this.restoreOrigionalPages(this.origionalPages),
                  (this.origionalPages = null))
                : ((e && 'standard' != e) ||
                    (this.origionalPages = this.pages.slice(0, this.pages.length)),
                  this.setupPagesForPageModes(this.isSinglePage)),
              this.updateVisibleIndexes());
          }),
          (t.prototype.restoreOrigionalPages = function (e) {
            this.questionHashesClear(), this.pages.splice(0, this.pages.length);
            for (var t = 0; t < e.length; t++) this.pages.push(e[t]);
          }),
          (t.prototype.setupPagesForPageModes = function (t) {
            this.questionHashesClear();
            var n = this.firstPageIsStarted ? 1 : 0;
            e.prototype.startLoadingFromJson.call(this);
            var r = this.createPagesForQuestionOnPageMode(t, n),
              i = this.pages.length - n;
            this.pages.splice(n, i);
            for (var o = 0; o < r.length; o++) this.pages.push(r[o]);
            e.prototype.endLoadingFromJson.call(this);
            for (var o = 0; o < r.length; o++) r[o].endLoadingFromJson(), r[o].setSurveyImpl(this);
            this.doElementsOnLoad();
          }),
          (t.prototype.createPagesForQuestionOnPageMode = function (e, t) {
            return e ? [this.createSinglePage(t)] : this.createPagesForEveryQuestion(t);
          }),
          (t.prototype.createSinglePage = function (e) {
            var t = this.createNewPage('all');
            t.setSurveyImpl(this);
            for (var n = e; n < this.pages.length; n++) {
              var r = this.pages[n],
                i = o.b.createClass('panel');
              (i.originalPage = r), t.addPanel(i);
              var a = new o.c().toJsonObject(r);
              new o.c().toObject(a, i), this.showPageTitles || (i.title = '');
            }
            return t;
          }),
          (t.prototype.createPagesForEveryQuestion = function (e) {
            for (var t = [], n = e; n < this.pages.length; n++) {
              var r = this.pages[n];
              r.setWasShown(!0);
              for (var i = 0; i < r.elements.length; i++) {
                var a = r.elements[i],
                  s = o.b.createClass(a.getType());
                if (s) {
                  var l = new o.c();
                  l.lightSerializing = !0;
                  var u = l.toJsonObject(r),
                    c = o.b.createClass(r.getType());
                  c.fromJSON(u),
                    (c.name = 'page' + (t.length + 1)),
                    c.setSurveyImpl(this),
                    t.push(c);
                  var p = new o.c().toJsonObject(a);
                  new o.c().toObject(p, s), c.addElement(s);
                  for (var d = 0; d < c.questions.length; d++)
                    this.questionHashesAdded(c.questions[d]);
                }
              }
            }
            return t;
          }),
          Object.defineProperty(t.prototype, 'isFirstPage', {
            get: function () {
              return null == this.currentPage || 0 == this.visiblePages.indexOf(this.currentPage);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isShowPrevButton', {
            get: function () {
              if (this.isFirstPage || !this.showPrevButton) return !1;
              var e = this.visiblePages[this.currentPageNo - 1];
              return this.getPageMaxTimeToFinish(e) <= 0;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isLastPage', {
            get: function () {
              if (null == this.currentPage) return !0;
              var e = this.visiblePages;
              return e.indexOf(this.currentPage) == e.length - 1;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.doComplete = function (e) {
            void 0 === e && (e = !1);
            var t = { allowComplete: !0, isCompleteOnTrigger: e };
            if ((this.onCompleting.fire(this, t), t.allowComplete)) {
              var n = this.hasCookie;
              this.stopTimer(), this.setCompleted(), this.clearUnusedValues(), this.setCookie();
              var r = this,
                i = !1,
                o = {
                  isCompleteOnTrigger: e,
                  showDataSaving: function (e) {
                    (i = !0), r.setCompletedState('saving', e);
                  },
                  showDataSavingError: function (e) {
                    r.setCompletedState('error', e);
                  },
                  showDataSavingSuccess: function (e) {
                    r.setCompletedState('success', e), r.navigateTo();
                  },
                  showDataSavingClear: function (e) {
                    r.setCompletedState('', '');
                  },
                };
              this.onComplete.fire(this, o),
                !n && this.surveyPostId && this.sendResult(),
                i || this.navigateTo();
            }
          }),
          (t.prototype.start = function () {
            return (
              !!this.firstPageIsStarted &&
              !this.checkIsPageHasErrors(this.startedPage, !0) &&
              ((this.isStartedState = !1),
              this.startTimerFromUI(),
              this.onStarted.fire(this, {}),
              !0)
            );
          }),
          Object.defineProperty(t.prototype, 'isValidatingOnServer', {
            get: function () {
              return this.getPropertyValue('isValidatingOnServer', !1);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setIsValidatingOnServer = function (e) {
            e != this.isValidatingOnServer &&
              (this.setPropertyValue('isValidatingOnServer', e),
              this.onIsValidatingOnServerChanged());
          }),
          (t.prototype.onIsValidatingOnServerChanged = function () {}),
          (t.prototype.doServerValidation = function (e, t) {
            if (
              (void 0 === t && (t = !1),
              !this.onServerValidateQuestions || this.onServerValidateQuestions.isEmpty)
            )
              return !1;
            if (!e && 'onComplete' === this.checkErrorsMode) return !1;
            var n = this,
              r = {
                data: {},
                errors: {},
                survey: this,
                complete: function () {
                  n.completeServerValidation(r, t);
                },
              };
            if (e && 'onComplete' === this.checkErrorsMode) r.data = this.data;
            else
              for (var i = 0; i < this.currentPage.questions.length; i++) {
                var o = this.currentPage.questions[i];
                if (o.visible) {
                  var a = this.getValue(o.getValueName());
                  this.isValueEmpty(a) || (r.data[o.getValueName()] = a);
                }
              }
            return (
              this.setIsValidatingOnServer(!0),
              'function' == typeof this.onServerValidateQuestions
                ? this.onServerValidateQuestions(this, r)
                : this.onServerValidateQuestions.fire(this, r),
              !0
            );
          }),
          (t.prototype.completeServerValidation = function (e, t) {
            if ((this.setIsValidatingOnServer(!1), e || e.survey)) {
              var n = e.survey,
                r = !1;
              if (e.errors) {
                var i = this.focusOnFirstError;
                for (var o in e.errors) {
                  var a = n.getQuestionByName(o);
                  a &&
                    a.errors &&
                    ((r = !0),
                    a.addError(new h.b(e.errors[o], this)),
                    i && ((i = !1), a.page && (this.currentPage = a.page), a.focus(!0)));
                }
                this.fireValidatedErrorsOnPage(this.currentPage);
              }
              r ||
                (t ? (this.isShowingPreview = !0) : n.isLastPage ? n.doComplete() : n.doNextPage());
            }
          }),
          (t.prototype.doNextPage = function () {
            var e = this.currentPage;
            if ((this.checkOnPageTriggers(), this.isCompleted)) this.doComplete(!0);
            else if (
              (this.sendResultOnPageNext && this.sendResult(this.surveyPostId, this.clientId, !0),
              e === this.currentPage)
            ) {
              var t = this.visiblePages,
                n = t.indexOf(this.currentPage);
              this.currentPage = t[n + 1];
            }
          }),
          (t.prototype.setCompleted = function () {
            this.isCompleted = !0;
          }),
          Object.defineProperty(t.prototype, 'processedCompletedHtml', {
            get: function () {
              var e = this.renderedCompletedHtml;
              return e
                ? this.processHtml(e)
                : '<h3>' + this.getLocString('completingSurvey') + '</h3>';
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'processedCompletedBeforeHtml', {
            get: function () {
              return this.completedBeforeHtml
                ? this.processHtml(this.completedBeforeHtml)
                : '<h3>' + this.getLocString('completingSurveyBefore') + '</h3>';
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'processedLoadingHtml', {
            get: function () {
              return this.loadingHtml
                ? this.processHtml(this.loadingHtml)
                : '<h3>' + this.getLocString('loadingSurvey') + '</h3>';
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getProgressInfo = function () {
            return a.a.getProgressInfoByElements(this.visiblePages, !1);
          }),
          Object.defineProperty(t.prototype, 'progressText', {
            get: function () {
              var e = this.getPropertyValue('progressText', '');
              return (
                e || (this.updateProgressText(), (e = this.getPropertyValue('progressText', ''))), e
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.updateProgressText = function (e) {
            void 0 === e && (e = !1),
              this.isDesignMode ||
                this.isCalculatingProgressText ||
                (e && 'pages' == this.progressBarType && this.onProgressText.isEmpty) ||
                ((this.isCalculatingProgressText = !0),
                this.setPropertyValue('progressText', this.getProgressText()),
                this.setPropertyValue('progressValue', this.getProgress()),
                (this.isCalculatingProgressText = !1));
          }),
          (t.prototype.getProgressText = function () {
            if (this.isDesignMode || null == this.currentPage) return '';
            var e = {
                questionCount: 0,
                answeredQuestionCount: 0,
                requiredQuestionCount: 0,
                requiredAnsweredQuestionCount: 0,
                text: '',
              },
              t = this.progressBarType.toLowerCase();
            if (
              'questions' === t ||
              'requiredquestions' === t ||
              'correctquestions' === t ||
              !this.onProgressText.isEmpty
            ) {
              var n = this.getProgressInfo();
              (e.questionCount = n.questionCount),
                (e.answeredQuestionCount = n.answeredQuestionCount),
                (e.requiredQuestionCount = n.requiredQuestionCount),
                (e.requiredAnsweredQuestionCount = n.requiredAnsweredQuestionCount);
            }
            return (
              (e.text = this.getProgressTextCore(e)), this.onProgressText.fire(this, e), e.text
            );
          }),
          (t.prototype.getProgressTextCore = function (e) {
            var t = this.progressBarType.toLowerCase();
            if ('questions' === t)
              return this.getLocString('questionsProgressText').format(
                e.answeredQuestionCount,
                e.questionCount
              );
            if ('requiredquestions' === t)
              return this.getLocString('questionsProgressText').format(
                e.requiredAnsweredQuestionCount,
                e.requiredQuestionCount
              );
            if ('correctquestions' === t) {
              var n = this.getCorrectedAnswerCount();
              return this.getLocString('questionsProgressText').format(n, e.questionCount);
            }
            var r = this.visiblePages,
              i = r.indexOf(this.currentPage) + 1;
            return this.getLocString('progressText').format(i, r.length);
          }),
          (t.prototype.afterRenderSurvey = function (e) {
            this.onAfterRenderSurvey.fire(this, { survey: this, htmlElement: e });
          }),
          (t.prototype.updateQuestionCssClasses = function (e, t) {
            this.onUpdateQuestionCssClasses.fire(this, { question: e, cssClasses: t });
          }),
          (t.prototype.updatePanelCssClasses = function (e, t) {
            this.onUpdatePanelCssClasses.fire(this, { panel: e, cssClasses: t });
          }),
          (t.prototype.updatePageCssClasses = function (e, t) {
            this.onUpdatePageCssClasses.fire(this, { page: e, cssClasses: t });
          }),
          (t.prototype.afterRenderPage = function (e) {
            this.onAfterRenderPage.isEmpty ||
              this.onAfterRenderPage.fire(this, { page: this.currentPage, htmlElement: e });
          }),
          (t.prototype.afterRenderHeader = function (e) {
            this.onAfterRenderHeader.isEmpty ||
              this.onAfterRenderHeader.fire(this, { htmlElement: e });
          }),
          (t.prototype.afterRenderQuestion = function (e, t) {
            this.onAfterRenderQuestion.fire(this, { question: e, htmlElement: t });
          }),
          (t.prototype.afterRenderQuestionInput = function (e, t) {
            if (!this.onAfterRenderQuestionInput.isEmpty) {
              var n = e.inputId;
              if (n && t.id !== n && 'undefined' != typeof document) {
                var r = document.getElementById(n);
                r && (t = r);
              }
              this.onAfterRenderQuestionInput.fire(this, { question: e, htmlElement: t });
            }
          }),
          (t.prototype.afterRenderPanel = function (e, t) {
            this.onAfterRenderPanel.fire(this, { panel: e, htmlElement: t });
          }),
          (t.prototype.matrixBeforeRowAdded = function (e) {
            this.onMatrixBeforeRowAdded.fire(this, e);
          }),
          (t.prototype.matrixRowAdded = function (e, t) {
            this.onMatrixRowAdded.fire(this, { question: e, row: t });
          }),
          (t.prototype.getQuestionByValueNameFromArray = function (e, t, n) {
            var r = this.getQuestionsByValueName(e);
            if (r) {
              for (var i = 0; i < r.length; i++) {
                var o = r[i].getQuestionFromArray(t, n);
                if (o) return o;
              }
              return null;
            }
          }),
          (t.prototype.matrixRowRemoved = function (e, t, n) {
            this.onMatrixRowRemoved.fire(this, { question: e, rowIndex: t, row: n });
          }),
          (t.prototype.matrixAllowRemoveRow = function (e, t, n) {
            var r = { question: e, rowIndex: t, row: n, allow: !0 };
            return this.onMatrixAllowRemoveRow.fire(this, r), r.allow;
          }),
          (t.prototype.matrixCellCreated = function (e, t) {
            (t.question = e), this.onMatrixCellCreated.fire(this, t);
          }),
          (t.prototype.matrixAfterCellRender = function (e, t) {
            (t.question = e), this.onMatrixAfterCellRender.fire(this, t);
          }),
          (t.prototype.matrixCellValueChanged = function (e, t) {
            (t.question = e), this.onMatrixCellValueChanged.fire(this, t);
          }),
          (t.prototype.matrixCellValueChanging = function (e, t) {
            (t.question = e), this.onMatrixCellValueChanging.fire(this, t);
          }),
          Object.defineProperty(t.prototype, 'isValidateOnValueChanging', {
            get: function () {
              return 'onValueChanging' === this.checkErrorsMode;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.matrixCellValidate = function (e, t) {
            return (
              (t.question = e),
              this.onMatrixCellValidate.fire(this, t),
              t.error ? new h.b(t.error, this) : null
            );
          }),
          (t.prototype.dynamicPanelAdded = function (e) {
            this.onDynamicPanelAdded.fire(this, { question: e });
          }),
          (t.prototype.dynamicPanelRemoved = function (e, t, n) {
            for (var r = n ? n.questions : [], i = 0; i < r.length; i++)
              r[i].clearOnDeletingContainer();
            this.onDynamicPanelRemoved.fire(this, { question: e, panelIndex: t, panel: n });
          }),
          (t.prototype.dynamicPanelItemValueChanged = function (e, t) {
            (t.question = e), this.onDynamicPanelItemValueChanged.fire(this, t);
          }),
          (t.prototype.dragAndDropAllow = function (e) {
            return (e.allow = !0), this.onDragDropAllow.fire(this, e), e.allow;
          }),
          (t.prototype.renderTitleActions = function (e) {
            return e.isPanel
              ? !this.onGetPanelTitleActions.isEmpty
              : !this.onGetQuestionTitleActions.isEmpty;
          }),
          (t.prototype.getUpdatedQuestionTitleActions = function (e, t) {
            var n = { question: e, titleActions: t };
            return this.onGetQuestionTitleActions.fire(this, n), n.titleActions;
          }),
          (t.prototype.getUpdatedPanelTitleActions = function (e, t) {
            var n = { panel: e, titleActions: t };
            return this.onGetPanelTitleActions.fire(this, n), n.titleActions;
          }),
          (t.prototype.scrollElementToTop = function (e, t, n, r) {
            var i = { element: e, question: t, page: n, elementId: r, cancel: !1 };
            this.onScrollingElementToTop.fire(this, i),
              i.cancel || a.a.ScrollElementToTop(i.elementId);
          }),
          (t.prototype.uploadFiles = function (e, t, n, r) {
            this.onUploadFiles.isEmpty
              ? r('error', n)
              : this.onUploadFiles.fire(this, {
                  question: e,
                  name: t,
                  files: n || [],
                  callback: r,
                }),
              this.surveyPostId && this.uploadFilesCore(t, n, r);
          }),
          (t.prototype.downloadFile = function (e, t, n) {
            this.onDownloadFile.isEmpty && !!n && n('success', t.content || t),
              this.onDownloadFile.fire(this, {
                name: e,
                content: t.content || t,
                fileValue: t,
                callback: n,
              });
          }),
          (t.prototype.clearFiles = function (e, t, n, r, i) {
            this.onClearFiles.isEmpty && !!i && i('success', n),
              this.onClearFiles.fire(this, {
                question: e,
                name: t,
                value: n,
                fileName: r,
                callback: i,
              });
          }),
          (t.prototype.updateChoicesFromServer = function (e, t, n) {
            var r = { question: e, choices: t, serverResult: n };
            return this.onLoadChoicesFromServer.fire(this, r), r.choices;
          }),
          (t.prototype.createSurveyService = function () {
            return new p.a();
          }),
          (t.prototype.uploadFilesCore = function (e, t, n) {
            var r = this,
              i = [];
            t.forEach(function (e) {
              n && n('uploading', e),
                r.createSurveyService().sendFile(r.surveyPostId, e, function (r, o) {
                  r
                    ? (i.push({ content: o, file: e }),
                      i.length === t.length && n && n('success', i))
                    : n && n('error', { response: o, file: e });
                });
            });
          }),
          (t.prototype.getPage = function (e) {
            return this.pages[e];
          }),
          (t.prototype.addPage = function (e, t) {
            void 0 === t && (t = -1),
              null != e &&
                (t < 0 || t >= this.pages.length ? this.pages.push(e) : this.pages.splice(t, 0, e));
          }),
          (t.prototype.addNewPage = function (e, t) {
            void 0 === e && (e = null), void 0 === t && (t = -1);
            var n = this.createNewPage(e);
            return this.addPage(n, t), n;
          }),
          (t.prototype.removePage = function (e) {
            var t = this.pages.indexOf(e);
            t < 0 ||
              (this.pages.splice(t, 1),
              this.currentPageValue == e &&
                (this.currentPage = this.pages.length > 0 ? this.pages[0] : null));
          }),
          (t.prototype.getQuestionByName = function (e, t) {
            if ((void 0 === t && (t = !1), !e)) return null;
            t && (e = e.toLowerCase());
            var n = t ? this.questionHashes.namesInsensitive : this.questionHashes.names,
              r = n[e];
            return r ? r[0] : null;
          }),
          (t.prototype.getQuestionByValueName = function (e, t) {
            void 0 === t && (t = !1);
            var n = this.getQuestionsByValueName(e, t);
            return n ? n[0] : null;
          }),
          (t.prototype.getQuestionsByValueName = function (e, t) {
            void 0 === t && (t = !1);
            var n = t ? this.questionHashes.valueNamesInsensitive : this.questionHashes.valueNames,
              r = n[e];
            return r || null;
          }),
          (t.prototype.getCalculatedValueByName = function (e) {
            for (var t = 0; t < this.calculatedValues.length; t++)
              if (e == this.calculatedValues[t].name) return this.calculatedValues[t];
            return null;
          }),
          (t.prototype.getQuestionsByNames = function (e, t) {
            void 0 === t && (t = !1);
            var n = [];
            if (!e) return n;
            for (var r = 0; r < e.length; r++)
              if (e[r]) {
                var i = this.getQuestionByName(e[r], t);
                i && n.push(i);
              }
            return n;
          }),
          (t.prototype.getPageByElement = function (e) {
            for (var t = 0; t < this.pages.length; t++) {
              var n = this.pages[t];
              if (n.containsElement(e)) return n;
            }
            return null;
          }),
          (t.prototype.getPageByQuestion = function (e) {
            return this.getPageByElement(e);
          }),
          (t.prototype.getPageByName = function (e) {
            for (var t = 0; t < this.pages.length; t++)
              if (this.pages[t].name == e) return this.pages[t];
            return null;
          }),
          (t.prototype.getPagesByNames = function (e) {
            var t = [];
            if (!e) return t;
            for (var n = 0; n < e.length; n++)
              if (e[n]) {
                var r = this.getPageByName(e[n]);
                r && t.push(r);
              }
            return t;
          }),
          (t.prototype.getAllQuestions = function (e, t) {
            void 0 === e && (e = !1), void 0 === t && (t = !1);
            for (var n = new Array(), r = 0; r < this.pages.length; r++)
              this.pages[r].addQuestionsToList(n, e, t);
            return n;
          }),
          (t.prototype.getQuizQuestions = function () {
            for (
              var e = new Array(), t = this.firstPageIsStarted ? 1 : 0, n = t;
              n < this.pages.length;
              n++
            )
              if (this.pages[n].isVisible)
                for (var r = this.pages[n].questions, i = 0; i < r.length; i++) {
                  var o = r[i];
                  o.quizQuestionCount > 0 && e.push(o);
                }
            return e;
          }),
          (t.prototype.getPanelByName = function (e, t) {
            void 0 === t && (t = !1);
            var n = this.getAllPanels();
            t && (e = e.toLowerCase());
            for (var r = 0; r < n.length; r++) {
              var i = n[r].name;
              if ((t && (i = i.toLowerCase()), i == e)) return n[r];
            }
            return null;
          }),
          (t.prototype.getAllPanels = function (e, t) {
            void 0 === e && (e = !1), void 0 === t && (t = !1);
            for (var n = new Array(), r = 0; r < this.pages.length; r++)
              this.pages[r].addPanelsIntoList(n, e, t);
            return n;
          }),
          (t.prototype.createNewPage = function (e) {
            return new l.a(e);
          }),
          (t.prototype.questionOnValueChanging = function (e, t) {
            if (this.onValueChanging.isEmpty) return t;
            var n = {
              name: e,
              question: this.getQuestionByValueName(e),
              value: this.getUnbindValue(t),
              oldValue: this.getValue(e),
            };
            return this.onValueChanging.fire(this, n), n.value;
          }),
          (t.prototype.updateQuestionValue = function (e, t) {
            if (!this.isLoadingFromJson) {
              var n = this.getQuestionsByValueName(e);
              if (n)
                for (var r = 0; r < n.length; r++) {
                  var i = n[r].value;
                  ((i === t && Array.isArray(i) && this.editingObj) ||
                    !this.isTwoValueEquals(i, t)) &&
                    n[r].updateValueFromSurvey(t);
                }
            }
          }),
          (t.prototype.checkQuestionErrorOnValueChanged = function (e) {
            !this.isNavigationButtonPressed &&
              ('onValueChanged' === this.checkErrorsMode || e.errors.length > 0) &&
              this.checkQuestionErrorOnValueChangedCore(e);
          }),
          (t.prototype.checkQuestionErrorOnValueChangedCore = function (e) {
            var t = e.errors.length,
              n = e.hasErrors(!0, { isOnValueChanged: !0 });
            return (
              e.page && (t > 0 || e.errors.length > 0) && this.fireValidatedErrorsOnPage(e.page), n
            );
          }),
          (t.prototype.checkErrorsOnValueChanging = function (e, t) {
            if (this.isLoadingFromJson) return !1;
            var n = this.getQuestionsByValueName(e);
            if (!n) return !1;
            for (var r = !1, i = 0; i < n.length; i++) {
              var o = n[i];
              this.isTwoValueEquals(o.value, t) || (o.value = t),
                this.checkQuestionErrorOnValueChangedCore(o) && (r = !0),
                (r = r || o.errors.length > 0);
            }
            return r;
          }),
          (t.prototype.notifyQuestionOnValueChanged = function (e, t) {
            if (!this.isLoadingFromJson) {
              var n = this.getQuestionsByValueName(e);
              if (n)
                for (var r = 0; r < n.length; r++) {
                  var i = n[r];
                  this.checkQuestionErrorOnValueChanged(i),
                    i.onSurveyValueChanged(t),
                    this.onValueChanged.fire(this, { name: e, question: i, value: t });
                }
              else this.onValueChanged.fire(this, { name: e, question: null, value: t });
              for (var r = 0; r < this.pages.length; r++) this.pages[r].checkBindings(e, t);
              this.notifyElementsOnAnyValueOrVariableChanged(e);
            }
          }),
          (t.prototype.notifyElementsOnAnyValueOrVariableChanged = function (e) {
            if ('processing' !== this.isEndLoadingFromJson) {
              if (this.isRunningConditions)
                return void (this.conditionNotifyElementsOnAnyValueOrVariableChanged = !0);
              for (var t = 0; t < this.pages.length; t++) this.pages[t].onAnyValueChanged(e);
              this.isEndLoadingFromJson || this.locStrsChanged();
            }
          }),
          (t.prototype.updateAllQuestionsValue = function () {
            for (var e = this.getAllQuestions(), t = 0; t < e.length; t++) {
              var n = e[t],
                r = n.getValueName();
              n.updateValueFromSurvey(this.getValue(r)),
                n.requireUpdateCommentValue && n.updateCommentFromSurvey(this.getComment(r));
            }
          }),
          (t.prototype.notifyAllQuestionsOnValueChanged = function () {
            for (var e = this.getAllQuestions(), t = 0; t < e.length; t++)
              e[t].onSurveyValueChanged(this.getValue(e[t].getValueName()));
          }),
          (t.prototype.checkOnPageTriggers = function () {
            for (var e = this.getCurrentPageQuestions(!0), t = {}, n = 0; n < e.length; n++) {
              var r = e[n],
                i = r.getValueName();
              t[i] = this.getValue(i);
            }
            this.addCalculatedValuesIntoFilteredValues(t), this.checkTriggers(t, !0);
          }),
          (t.prototype.getCurrentPageQuestions = function (e) {
            void 0 === e && (e = !1);
            var t = [],
              n = this.currentPage;
            if (!n) return t;
            for (var r = 0; r < n.questions.length; r++) {
              var i = n.questions[r];
              (e || i.visible) && i.name && t.push(i);
            }
            return t;
          }),
          (t.prototype.checkTriggers = function (e, t) {
            if (!this.isCompleted && 0 != this.triggers.length)
              if (this.isTriggerIsRunning) {
                this.triggerValues = this.getFilteredValues();
                for (var n in e) this.triggerKeys[n] = e[n];
              } else {
                (this.isTriggerIsRunning = !0),
                  (this.triggerKeys = e),
                  (this.triggerValues = this.getFilteredValues());
                for (var r = this.getFilteredProperties(), i = 0; i < this.triggers.length; i++) {
                  var o = this.triggers[i];
                  o.isOnNextPage == t && o.checkExpression(this.triggerKeys, this.triggerValues, r);
                }
                this.isTriggerIsRunning = !1;
              }
          }),
          (t.prototype.doElementsOnLoad = function () {
            for (var e = 0; e < this.pages.length; e++) this.pages[e].onSurveyLoad();
          }),
          Object.defineProperty(t.prototype, 'isRunningConditions', {
            get: function () {
              return !!this.conditionValues;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.runConditions = function () {
            if (
              !this.isCompleted &&
              'processing' !== this.isEndLoadingFromJson &&
              !this.isRunningConditions
            ) {
              this.conditionValues = this.getFilteredValues();
              var e = this.getFilteredProperties(),
                t = this.pages.indexOf(this.currentPageValue);
              this.runConditionsCore(e),
                this.checkIfNewPagesBecomeVisible(t),
                (this.conditionValues = null),
                this.isValueChangedOnRunningCondition &&
                this.conditionRunnerCounter < v.a.maximumConditionRunCountOnValueChanged
                  ? ((this.isValueChangedOnRunningCondition = !1),
                    this.conditionRunnerCounter++,
                    this.runConditions())
                  : ((this.isValueChangedOnRunningCondition = !1),
                    (this.conditionRunnerCounter = 0),
                    this.conditionUpdateVisibleIndexes &&
                      ((this.conditionUpdateVisibleIndexes = !1), this.updateVisibleIndexes()),
                    this.conditionNotifyElementsOnAnyValueOrVariableChanged &&
                      ((this.conditionNotifyElementsOnAnyValueOrVariableChanged = !1),
                      this.notifyElementsOnAnyValueOrVariableChanged('')));
            }
          }),
          (t.prototype.runConditionOnValueChanged = function (e, t) {
            this.isRunningConditions
              ? ((this.conditionValues[e] = t), (this.isValueChangedOnRunningCondition = !0))
              : this.runConditions();
          }),
          (t.prototype.runConditionsCore = function (e) {
            for (var t = this.pages, n = 0; n < this.calculatedValues.length; n++)
              this.calculatedValues[n].resetCalculation();
            for (var n = 0; n < this.calculatedValues.length; n++)
              this.calculatedValues[n].doCalculation(
                this.calculatedValues,
                this.conditionValues,
                e
              );
            for (var n = 0; n < t.length; n++) t[n].runCondition(this.conditionValues, e);
          }),
          (t.prototype.checkIfNewPagesBecomeVisible = function (e) {
            var t = this.pages.indexOf(this.currentPageValue);
            if (!(t <= e + 1))
              for (var n = e + 1; n < t; n++)
                if (this.pages[n].isVisible) {
                  this.currentPage = this.pages[n];
                  break;
                }
          }),
          (t.prototype.sendResult = function (e, t, n) {
            if (
              (void 0 === e && (e = null),
              void 0 === t && (t = null),
              void 0 === n && (n = !1),
              this.isEditMode &&
                (n && this.onPartialSend && this.onPartialSend.fire(this, null),
                !e && this.surveyPostId && (e = this.surveyPostId),
                e && (t && (this.clientId = t), !n || this.clientId)))
            ) {
              var r = this;
              this.surveyShowDataSaving && this.setCompletedState('saving', ''),
                this.createSurveyService().sendResult(
                  e,
                  this.data,
                  function (e, t, n) {
                    r.surveyShowDataSaving &&
                      (e ? r.setCompletedState('success', '') : r.setCompletedState('error', t)),
                      r.onSendResult.fire(r, { success: e, response: t, request: n });
                  },
                  this.clientId,
                  n
                );
            }
          }),
          (t.prototype.getResult = function (e, t) {
            var n = this;
            this.createSurveyService().getResult(e, t, function (e, t, r, i) {
              n.onGetResult.fire(n, { success: e, data: t, dataList: r, response: i });
            });
          }),
          (t.prototype.loadSurveyFromService = function (e, t) {
            void 0 === e && (e = null),
              void 0 === t && (t = null),
              e && (this.surveyId = e),
              t && (this.clientId = t);
            var n = this;
            (this.isLoading = !0),
              this.onLoadingSurveyFromService(),
              t
                ? this.createSurveyService().getSurveyJsonAndIsCompleted(
                    this.surveyId,
                    this.clientId,
                    function (e, t, r, i) {
                      (n.isLoading = !1),
                        e &&
                          ((n.isCompletedBefore = 'completed' == r),
                          n.loadSurveyFromServiceJson(t));
                    }
                  )
                : this.createSurveyService().loadSurvey(this.surveyId, function (e, t, r) {
                    (n.isLoading = !1), e && n.loadSurveyFromServiceJson(t);
                  });
          }),
          (t.prototype.loadSurveyFromServiceJson = function (e) {
            e &&
              (this.fromJSON(e),
              this.notifyAllQuestionsOnValueChanged(),
              this.onLoadSurveyFromService(),
              this.onLoadedSurveyFromService.fire(this, {}));
          }),
          (t.prototype.onLoadingSurveyFromService = function () {}),
          (t.prototype.onLoadSurveyFromService = function () {}),
          (t.prototype.updateVisibleIndexes = function () {
            if (!this.isLoadingFromJson && !this.isEndLoadingFromJson) {
              if (
                this.isRunningConditions &&
                this.onVisibleChanged.isEmpty &&
                this.onPageVisibleChanged.isEmpty
              )
                return void (this.conditionUpdateVisibleIndexes = !0);
              if (
                (this.updatePageVisibleIndexes(this.showPageNumbers),
                'onPage' == this.showQuestionNumbers)
              )
                for (var e = this.visiblePages, t = 0; t < e.length; t++) e[t].setVisibleIndex(0);
              else
                for (
                  var n = 'on' == this.showQuestionNumbers ? 0 : -1, t = 0;
                  t < this.pages.length;
                  t++
                )
                  n += this.pages[t].setVisibleIndex(n);
              this.updateProgressText(!0);
            }
          }),
          (t.prototype.updatePageVisibleIndexes = function (e) {
            for (var t = 0, n = 0; n < this.pages.length; n++) {
              var r = this.pages[n].isVisible;
              (this.pages[n].visibleIndex = r ? t++ : -1),
                (this.pages[n].num = e && r ? this.pages[n].visibleIndex + 1 : -1);
            }
          }),
          (t.prototype.fromJSON = function (e) {
            if (e) {
              this.questionHashesClear(), (this.jsonErrors = null);
              var t = new o.c();
              t.toObject(e, this), t.errors.length > 0 && (this.jsonErrors = t.errors);
            }
          }),
          (t.prototype.setJsonObject = function (e) {
            this.fromJSON(e);
          }),
          (t.prototype.endLoadingFromJson = function () {
            (this.isEndLoadingFromJson = 'processing'),
              (this.isStartedState = this.firstPageIsStarted),
              this.onQuestionsOnPageModeChanged('standard'),
              e.prototype.endLoadingFromJson.call(this),
              this.hasCookie && this.doComplete(),
              this.doElementsOnLoad(),
              (this.isEndLoadingFromJson = 'conditions'),
              this.runConditions(),
              this.notifyElementsOnAnyValueOrVariableChanged(''),
              (this.isEndLoadingFromJson = null),
              this.updateVisibleIndexes();
          }),
          (t.prototype.onBeforeCreating = function () {}),
          (t.prototype.onCreating = function () {}),
          (t.prototype.getProcessedTextValue = function (e) {
            if ((this.getProcessedTextValueCore(e), !this.onProcessTextValue.isEmpty)) {
              var t = this.isValueEmpty(e.value);
              this.onProcessTextValue.fire(this, e),
                (e.isExists = e.isExists || (t && !this.isValueEmpty(e.value)));
            }
          }),
          (t.prototype.getProcessedTextValueCore = function (e) {
            var t = e.name.toLocaleLowerCase();
            if (-1 === ['no', 'require', 'title'].indexOf(t)) {
              if ('pageno' === t) {
                e.isExists = !0;
                var n = this.currentPage;
                return void (e.value = null != n ? this.visiblePages.indexOf(n) + 1 : 0);
              }
              if ('pagecount' === t)
                return (e.isExists = !0), void (e.value = this.visiblePageCount);
              if ('locale' === t)
                return (
                  (e.isExists = !0), void (e.value = this.locale ? this.locale : d.a.defaultLocale)
                );
              if ('correctedanswers' === t || 'correctedanswercount' === t)
                return (e.isExists = !0), void (e.value = this.getCorrectedAnswerCount());
              if ('incorrectedanswers' === t || 'incorrectedanswercount' === t)
                return (e.isExists = !0), void (e.value = this.getInCorrectedAnswerCount());
              if ('questioncount' === t)
                return (e.isExists = !0), void (e.value = this.getQuizQuestionCount());
              var r = new c.a().getFirstName(t, this.data),
                i = this.getVariable(t);
              if (void 0 !== i) return (e.isExists = !0), void (e.value = i);
              r && (r = r.toLowerCase());
              var o = this.getQuestionByValueName(r, !0);
              if (o) {
                (e.isExists = !0),
                  (t = o.getValueName() + t.substr(r.length)),
                  (t = t.toLocaleLowerCase());
                var a = {};
                return (
                  (a[r] = e.returnDisplayValue ? o.getDisplayValue(!1, void 0) : o.value),
                  void (e.value = new c.a().getValue(t, a))
                );
              }
              var s = this.getValue(e.name);
              void 0 !== s && ((e.isExists = !0), (e.value = s));
            }
          }),
          (t.prototype.clearUnusedValues = function () {
            for (var e = this.getAllQuestions(), t = 0; t < e.length; t++) e[t].clearUnusedValues();
            'none' != this.clearInvisibleValues && this.clearInvisibleQuestionValues();
          }),
          (t.prototype.hasVisibleQuestionByValueName = function (e) {
            var t = this.getQuestionsByValueName(e);
            if (!t) return !1;
            for (var n = 0; n < t.length; n++) if (t[n].isVisible) return !0;
            return !1;
          }),
          (t.prototype.questionCountByValueName = function (e) {
            var t = this.getQuestionsByValueName(e);
            return t ? t.length : 0;
          }),
          (t.prototype.clearInvisibleQuestionValues = function () {
            for (var e = this.getAllQuestions(), t = 0; t < e.length; t++)
              e[t].clearValueIfInvisible();
          }),
          (t.prototype.getVariable = function (e) {
            if (!e) return null;
            e = e.toLowerCase();
            var t = this.variablesHash[e];
            return i.a.isValueEmpty(t) &&
              (e.indexOf('.') > -1 || e.indexOf('[') > -1) &&
              new c.a().hasValue(e, this.variablesHash)
              ? new c.a().getValue(e, this.variablesHash)
              : t;
          }),
          (t.prototype.setVariable = function (e, t) {
            e &&
              ((e = e.toLowerCase()),
              (this.variablesHash[e] = t),
              this.notifyElementsOnAnyValueOrVariableChanged(e),
              this.runConditionOnValueChanged(e, t));
          }),
          (t.prototype.getVariableNames = function () {
            var e = [];
            for (var t in this.variablesHash) e.push(t);
            return e;
          }),
          (t.prototype.getUnbindValue = function (e) {
            return this.editingObj ? e : i.a.getUnbindValue(e);
          }),
          (t.prototype.getValue = function (e) {
            if (!e || 0 == e.length) return null;
            var t = this.getDataValueCore(this.valuesHash, e);
            return this.getUnbindValue(t);
          }),
          (t.prototype.setValue = function (e, t, n, r) {
            void 0 === n && (n = !1), void 0 === r && (r = !0);
            var i = t;
            if (
              (r && (i = this.questionOnValueChanging(e, t)),
              ('onValueChanging' !== this.checkErrorsMode ||
                !this.checkErrorsOnValueChanging(e, i)) &&
                (this.editingObj || !this.isValueEqual(e, i) || !this.isTwoValueEquals(i, t)))
            ) {
              var o = this.getValue(e);
              this.isValueEmpty(i)
                ? this.deleteDataValueCore(this.valuesHash, e)
                : ((i = this.getUnbindValue(i)), this.setDataValueCore(this.valuesHash, e, i)),
                this.updateOnSetValue(e, i, o, n, r);
            }
          }),
          (t.prototype.updateOnSetValue = function (e, t, n, r, i) {
            if (
              (void 0 === r && (r = !1),
              void 0 === i && (i = !0),
              this.updateQuestionValue(e, t),
              !0 !== r)
            ) {
              var o = {};
              (o[e] = { newValue: t, oldValue: n }),
                this.runConditionOnValueChanged(e, t),
                this.checkTriggers(o, !1),
                i && this.notifyQuestionOnValueChanged(e, t),
                'text' !== r && this.tryGoNextPageAutomatic(e),
                this.updateProgressText(!0);
            }
          }),
          (t.prototype.isValueEqual = function (e, t) {
            ('' !== t && void 0 !== t) || (t = null);
            var n = this.getValue(e);
            return (
              ('' !== n && void 0 !== n) || (n = null),
              null === t || null === n ? t === n : this.isTwoValueEquals(t, n)
            );
          }),
          (t.prototype.doOnPageAdded = function (e) {
            e.setSurveyImpl(this),
              e.name || (e.name = this.generateNewName(this.pages, 'page')),
              this.questionHashesPanelAdded(e),
              this.updateVisibleIndexes();
            var t = { page: e };
            this.onPageAdded.fire(this, t);
          }),
          (t.prototype.doOnPageRemoved = function (e) {
            this.updateVisibleIndexes();
          }),
          (t.prototype.generateNewName = function (e, t) {
            for (var n = {}, r = 0; r < e.length; r++) n[e[r].name] = !0;
            for (var i = 1; n[t + i]; ) i++;
            return t + i;
          }),
          (t.prototype.tryGoNextPageAutomatic = function (e) {
            if (!this.isEndLoadingFromJson && this.goNextPageAutomatic && this.currentPage) {
              var t = this.getQuestionByValueName(e);
              if (
                t &&
                (!t || (t.visible && t.supportGoNextPageAutomatic())) &&
                (!t.hasErrors(!1) || t.supportGoNextPageError())
              ) {
                var n = this.getCurrentPageQuestions();
                if (!(n.indexOf(t) < 0)) {
                  for (var r = 0; r < n.length; r++) if (n[r].hasInput && n[r].isEmpty()) return;
                  this.checkIsCurrentPageHasErrors(!1) ||
                    (this.isLastPage
                      ? !0 === this.goNextPageAutomatic &&
                        this.allowCompleteSurveyAutomatic &&
                        this.completeLastPage()
                      : this.nextPage());
                }
              }
            }
          }),
          (t.prototype.getComment = function (e) {
            var t = this.data[e + this.commentPrefix];
            return null == t && (t = ''), t;
          }),
          (t.prototype.setComment = function (e, t, n) {
            if (
              (void 0 === n && (n = !1),
              t || (t = ''),
              !i.a.isTwoValueEquals(t, this.getComment(e)))
            ) {
              var r = e + this.commentPrefix;
              i.a.isValueEmpty(t)
                ? this.deleteDataValueCore(this.valuesHash, r)
                : this.setDataValueCore(this.valuesHash, r, t);
              var o = this.getQuestionsByValueName(e);
              if (o)
                for (var a = 0; a < o.length; a++)
                  o[a].updateCommentFromSurvey(t), this.checkQuestionErrorOnValueChanged(o[a]);
              'text' !== n && this.tryGoNextPageAutomatic(e);
              var s = this.getQuestionByName(e);
              s && this.onValueChanged.fire(this, { name: r, question: s, value: t });
            }
          }),
          (t.prototype.clearValue = function (e) {
            this.setValue(e, null), this.setComment(e, null);
          }),
          Object.defineProperty(t.prototype, 'clearValueOnDisableItems', {
            get: function () {
              return this.getPropertyValue('clearValueOnDisableItems', !1);
            },
            set: function (e) {
              this.setPropertyValue('clearValueOnDisableItems', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isClearValueOnHidden', {
            get: function () {
              return 'onHidden' == this.clearInvisibleValues || this.isClearValueOnHiddenContainer;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isClearValueOnHiddenContainer', {
            get: function () {
              return 'onHiddenContainer' == this.clearInvisibleValues;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.questionVisibilityChanged = function (e, t) {
            this.updateVisibleIndexes(),
              this.onVisibleChanged.fire(this, { question: e, name: e.name, visible: t });
          }),
          (t.prototype.pageVisibilityChanged = function (e, t) {
            this.isLoadingFromJson ||
              (t && !this.currentPageValue && (this.currentPageValue = this.currentPage),
              this.updateVisibleIndexes(),
              this.onPageVisibleChanged.fire(this, { page: e, visible: t }));
          }),
          (t.prototype.panelVisibilityChanged = function (e, t) {
            this.updateVisibleIndexes(),
              this.onPanelVisibleChanged.fire(this, { panel: e, visible: t });
          }),
          (t.prototype.questionCreated = function (e) {
            this.onQuestionCreated.fire(this, { question: e });
          }),
          (t.prototype.questionAdded = function (e, t, n, r) {
            e.name || (e.name = this.generateNewName(this.getAllQuestions(!1, !0), 'question')),
              e.page && this.questionHashesAdded(e),
              this.updateVisibleIndexes(),
              this.onQuestionAdded.fire(this, {
                question: e,
                name: e.name,
                index: t,
                parentPanel: n,
                rootPanel: r,
              });
          }),
          (t.prototype.questionRemoved = function (e) {
            this.questionHashesRemoved(e, e.name, e.getValueName()),
              this.updateVisibleIndexes(),
              this.onQuestionRemoved.fire(this, { question: e, name: e.name });
          }),
          (t.prototype.questionRenamed = function (e, t, n) {
            this.questionHashesRemoved(e, t, n), this.questionHashesAdded(e);
          }),
          (t.prototype.questionHashesClear = function () {
            (this.questionHashes.names = {}),
              (this.questionHashes.namesInsensitive = {}),
              (this.questionHashes.valueNames = {}),
              (this.questionHashes.valueNamesInsensitive = {});
          }),
          (t.prototype.questionHashesPanelAdded = function (e) {
            if (!this.isLoadingFromJson)
              for (var t = e.questions, n = 0; n < t.length; n++) this.questionHashesAdded(t[n]);
          }),
          (t.prototype.questionHashesAdded = function (e) {
            this.questionHashAddedCore(this.questionHashes.names, e, e.name),
              this.questionHashAddedCore(
                this.questionHashes.namesInsensitive,
                e,
                e.name.toLowerCase()
              ),
              this.questionHashAddedCore(this.questionHashes.valueNames, e, e.getValueName()),
              this.questionHashAddedCore(
                this.questionHashes.valueNamesInsensitive,
                e,
                e.getValueName().toLowerCase()
              );
          }),
          (t.prototype.questionHashesRemoved = function (e, t, n) {
            t &&
              (this.questionHashRemovedCore(this.questionHashes.names, e, t),
              this.questionHashRemovedCore(
                this.questionHashes.namesInsensitive,
                e,
                t.toLowerCase()
              )),
              n &&
                (this.questionHashRemovedCore(this.questionHashes.valueNames, e, n),
                this.questionHashRemovedCore(
                  this.questionHashes.valueNamesInsensitive,
                  e,
                  n.toLowerCase()
                ));
          }),
          (t.prototype.questionHashAddedCore = function (e, t, n) {
            var r = e[n];
            if (r) {
              var r = e[n];
              r.indexOf(t) < 0 && r.push(t);
            } else e[n] = [t];
          }),
          (t.prototype.questionHashRemovedCore = function (e, t, n) {
            var r = e[n];
            if (r) {
              var i = r.indexOf(t);
              i > -1 && r.splice(i, 1), 0 == r.length && delete e[n];
            }
          }),
          (t.prototype.panelAdded = function (e, t, n, r) {
            e.name || (e.name = this.generateNewName(this.getAllPanels(!1, !0), 'panel')),
              this.questionHashesPanelAdded(e),
              this.updateVisibleIndexes(),
              this.onPanelAdded.fire(this, {
                panel: e,
                name: e.name,
                index: t,
                parentPanel: n,
                rootPanel: r,
              });
          }),
          (t.prototype.panelRemoved = function (e) {
            this.updateVisibleIndexes(), this.onPanelRemoved.fire(this, { panel: e, name: e.name });
          }),
          (t.prototype.validateQuestion = function (e) {
            if (this.onValidateQuestion.isEmpty) return null;
            var t = { name: e.name, question: e, value: e.value, error: null };
            return this.onValidateQuestion.fire(this, t), t.error ? new h.b(t.error, this) : null;
          }),
          (t.prototype.validatePanel = function (e) {
            if (this.onValidatePanel.isEmpty) return null;
            var t = { name: e.name, panel: e, error: null };
            return this.onValidatePanel.fire(this, t), t.error ? new h.b(t.error, this) : null;
          }),
          (t.prototype.processHtml = function (e) {
            var t = { html: e };
            return this.onProcessHtml.fire(this, t), this.processText(t.html, !0);
          }),
          (t.prototype.processText = function (e, t) {
            return this.processTextEx(e, t, !1).text;
          }),
          (t.prototype.processTextEx = function (e, t, n) {
            var r = { text: this.processTextCore(e, t, n), hasAllValuesOnLastRun: !0 };
            return (r.hasAllValuesOnLastRun = this.textPreProcessor.hasAllValuesOnLastRun), r;
          }),
          (t.prototype.processTextCore = function (e, t, n) {
            return (
              void 0 === n && (n = !1),
              this.isDesignMode ? e : this.textPreProcessor.process(e, t, n)
            );
          }),
          (t.prototype.getSurveyMarkdownHtml = function (e, t, n) {
            var r = { element: e, text: t, name: n, html: null };
            return this.onTextMarkdown.fire(this, r), r.html;
          }),
          (t.prototype.getCorrectedAnswerCount = function () {
            return this.getCorrectedAnswerCountCore(!0);
          }),
          (t.prototype.getQuizQuestionCount = function () {
            for (var e = this.getQuizQuestions(), t = 0, n = 0; n < e.length; n++)
              t += e[n].quizQuestionCount;
            return t;
          }),
          (t.prototype.getInCorrectedAnswerCount = function () {
            return this.getCorrectedAnswerCountCore(!1);
          }),
          (t.prototype.getCorrectedAnswerCountCore = function (e) {
            for (
              var t = this.getQuizQuestions(),
                n = 0,
                r = { question: null, result: !1, correctAnswers: 0, incorrectAnswers: 0 },
                i = 0;
              i < t.length;
              i++
            ) {
              var o = t[i],
                a = o.quizQuestionCount;
              if (
                ((r.question = o),
                (r.correctAnswers = o.correctAnswerCount),
                (r.incorrectAnswers = a - r.correctAnswers),
                (r.result = r.question.isAnswerCorrect()),
                this.onIsAnswerCorrect.fire(this, r),
                e)
              ) {
                if (r.result || r.correctAnswers < a) {
                  var s = r.correctAnswers;
                  0 == s && r.result && (s = 1), (n += s);
                }
              } else (!r.result || r.incorrectAnswers < a) && (n += r.incorrectAnswers);
            }
            return n;
          }),
          (t.prototype.getCorrectedAnswers = function () {
            return this.getCorrectedAnswerCount();
          }),
          (t.prototype.getInCorrectedAnswers = function () {
            return this.getInCorrectedAnswerCount();
          }),
          Object.defineProperty(t.prototype, 'showTimerPanel', {
            get: function () {
              return this.getPropertyValue('showTimerPanel', 'none');
            },
            set: function (e) {
              this.setPropertyValue('showTimerPanel', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isTimerPanelShowingOnTop', {
            get: function () {
              return this.isTimerStarted && 'top' == this.showTimerPanel;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isTimerPanelShowingOnBottom', {
            get: function () {
              return this.isTimerStarted && 'bottom' == this.showTimerPanel;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'showTimerPanelMode', {
            get: function () {
              return this.getPropertyValue('showTimerPanelMode', 'all');
            },
            set: function (e) {
              this.setPropertyValue('showTimerPanelMode', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'timerInfoText', {
            get: function () {
              var e = { text: this.getTimerInfoText() };
              this.onTimerPanelInfoText.fire(this, e);
              var t = new f.a(this, !0);
              return (t.text = e.text), t.textOrHtml;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getTimerInfoText = function () {
            var e = this.currentPage;
            if (!e) return '';
            var t = this.getDisplayTime(e.timeSpent),
              n = this.getDisplayTime(this.timeSpent),
              r = this.getPageMaxTimeToFinish(e),
              i = this.getDisplayTime(r),
              o = this.getDisplayTime(this.maxTimeToFinish);
            if ('page' == this.showTimerPanelMode) return this.getTimerInfoPageText(e, t, i);
            if ('survey' == this.showTimerPanelMode) return this.getTimerInfoSurveyText(n, o);
            if ('all' == this.showTimerPanelMode) {
              if (r <= 0 && this.maxTimeToFinish <= 0)
                return this.getLocString('timerSpentAll').format(t, n);
              if (r > 0 && this.maxTimeToFinish > 0)
                return this.getLocString('timerLimitAll').format(t, i, n, o);
              return this.getTimerInfoPageText(e, t, i) + ' ' + this.getTimerInfoSurveyText(n, o);
            }
            return '';
          }),
          (t.prototype.getTimerInfoPageText = function (e, t, n) {
            return this.getPageMaxTimeToFinish(e) > 0
              ? this.getLocString('timerLimitPage').format(t, n)
              : this.getLocString('timerSpentPage').format(t, n);
          }),
          (t.prototype.getTimerInfoSurveyText = function (e, t) {
            return this.maxTimeToFinish > 0
              ? this.getLocString('timerLimitSurvey').format(e, t)
              : this.getLocString('timerSpentSurvey').format(e, t);
          }),
          (t.prototype.getDisplayTime = function (e) {
            var t = Math.floor(e / 60),
              n = e % 60,
              r = '';
            return (
              t > 0 && (r += t + ' ' + this.getLocString('timerMin')),
              r && 0 == n ? r : (r && (r += ' '), r + n + ' ' + this.getLocString('timerSec'))
            );
          }),
          (t.prototype.startTimer = function () {
            if (!this.isTimerStarted && !this.isDesignMode) {
              var e = this;
              (this.timerFunc = function () {
                e.doTimer();
              }),
                (this.isTimerStarted = !0),
                g.a.instance.start(this.timerFunc);
            }
          }),
          (t.prototype.startTimerFromUI = function () {
            'none' != this.showTimerPanel && 'running' === this.state && this.startTimer();
          }),
          (t.prototype.stopTimer = function () {
            this.isTimerStarted && ((this.isTimerStarted = !1), g.a.instance.stop(this.timerFunc));
          }),
          Object.defineProperty(t.prototype, 'maxTimeToFinish', {
            get: function () {
              return this.getPropertyValue('maxTimeToFinish', 0);
            },
            set: function (e) {
              this.setPropertyValue('maxTimeToFinish', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'maxTimeToFinishPage', {
            get: function () {
              return this.getPropertyValue('maxTimeToFinishPage', 0);
            },
            set: function (e) {
              this.setPropertyValue('maxTimeToFinishPage', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getPageMaxTimeToFinish = function (e) {
            return !e || e.maxTimeToFinish < 0
              ? 0
              : e.maxTimeToFinish > 0
              ? e.maxTimeToFinish
              : this.maxTimeToFinishPage;
          }),
          (t.prototype.doTimer = function () {
            var e = this.currentPage;
            if (
              (e && (e.timeSpent = e.timeSpent + 1),
              (this.timeSpent = this.timeSpent + 1),
              this.onTimer.fire(this, {}),
              this.maxTimeToFinish > 0 &&
                this.maxTimeToFinish == this.timeSpent &&
                this.completeLastPage(),
              e)
            ) {
              var t = this.getPageMaxTimeToFinish(e);
              t > 0 &&
                t == e.timeSpent &&
                (this.isLastPage ? this.completeLastPage() : this.nextPage());
            }
          }),
          (t.prototype.geSurveyData = function () {
            return this;
          }),
          (t.prototype.getSurvey = function () {
            return this;
          }),
          (t.prototype.getTextProcessor = function () {
            return this;
          }),
          (t.prototype.getObjects = function (e, t) {
            var n = [];
            return (
              Array.prototype.push.apply(n, this.getPagesByNames(e)),
              Array.prototype.push.apply(n, this.getQuestionsByNames(t)),
              n
            );
          }),
          (t.prototype.setTriggerValue = function (e, t, n) {
            if (e)
              if (n) this.setVariable(e, t);
              else {
                var r = this.getQuestionByName(e);
                if (r) r.value = t;
                else {
                  var i = new c.a(),
                    o = i.getFirstName(e);
                  if (o == e) this.setValue(e, t);
                  else {
                    if (!this.getQuestionByName(o)) return;
                    var a = this.getUnbindValue(this.getFilteredValues());
                    i.setValue(a, e, t), this.setValue(o, a[o]);
                  }
                }
              }
          }),
          (t.prototype.copyTriggerValue = function (e, t) {
            if (e && t) {
              var n = new c.a(),
                r = n.getValue(t, this.getFilteredValues());
              this.setTriggerValue(e, r, !1);
            }
          }),
          (t.prototype.focusQuestion = function (e) {
            var t = this.getQuestionByName(e, !0);
            return (
              !!(t && t.isVisible && t.page) &&
              ((this.currentPage = t.page),
              setTimeout(function () {
                t.focus();
              }),
              !0)
            );
          }),
          (t.prototype.dispose = function () {
            for (var e = 0; e < this.pages.length; e++) this.pages[e].dispose();
            this.pages.splice(0, this.pages.length);
          }),
          (t.stylesManager = null),
          (t.platform = 'unknown'),
          t
        );
      })(a.b);
      o.b.addClass('survey', [
        {
          name: 'locale',
          choices: function () {
            return d.a.getLocales(!0);
          },
          onGetValue: function (e) {
            return e.locale == d.a.defaultLocale ? null : e.locale;
          },
        },
        { name: 'title', serializationProperty: 'locTitle' },
        { name: 'description:text', serializationProperty: 'locDescription' },
        { name: 'logo', serializationProperty: 'locLogo' },
        { name: 'logoWidth:number', default: 300, minValue: 0 },
        { name: 'logoHeight:number', default: 200, minValue: 0 },
        { name: 'logoFit', default: 'contain', choices: ['none', 'contain', 'cover', 'fill'] },
        {
          name: 'logoPosition',
          default: 'left',
          choices: ['none', 'left', 'right', 'top', 'bottom'],
        },
        { name: 'focusFirstQuestionAutomatic:boolean', default: !0 },
        { name: 'focusOnFirstError:boolean', default: !0 },
        { name: 'completedHtml:html', serializationProperty: 'locCompletedHtml' },
        { name: 'completedBeforeHtml:html', serializationProperty: 'locCompletedBeforeHtml' },
        { name: 'completedHtmlOnCondition:htmlconditions', className: 'htmlconditionitem' },
        { name: 'loadingHtml:html', serializationProperty: 'locLoadingHtml' },
        { name: 'pages:surveypages', className: 'page' },
        {
          name: 'questions',
          alternativeName: 'elements',
          baseClassName: 'question',
          visible: !1,
          isLightSerializable: !1,
          onGetValue: function (e) {
            return null;
          },
          onSetValue: function (e, t, n) {
            var r = e.addNewPage('');
            n.toObject({ questions: t }, r);
          },
        },
        { name: 'triggers:triggers', baseClassName: 'surveytrigger', classNamePart: 'trigger' },
        { name: 'calculatedValues:calculatedvalues', className: 'calculatedvalue' },
        { name: 'surveyId', visible: !1 },
        { name: 'surveyPostId', visible: !1 },
        { name: 'surveyShowDataSaving:boolean', visible: !1 },
        'cookieName',
        'sendResultOnPageNext:boolean',
        {
          name: 'showNavigationButtons',
          default: 'bottom',
          choices: ['none', 'top', 'bottom', 'both'],
        },
        { name: 'showPrevButton:boolean', default: !0 },
        { name: 'showTitle:boolean', default: !0 },
        { name: 'showPageTitles:boolean', default: !0 },
        { name: 'showCompletedPage:boolean', default: !0 },
        'navigateToUrl',
        { name: 'navigateToUrlOnCondition:urlconditions', className: 'urlconditionitem' },
        { name: 'questionsOrder', default: 'initial', choices: ['initial', 'random'] },
        'showPageNumbers:boolean',
        { name: 'showQuestionNumbers', default: 'on', choices: ['on', 'onPage', 'off'] },
        { name: 'questionTitleLocation', default: 'top', choices: ['top', 'bottom', 'left'] },
        {
          name: 'questionDescriptionLocation',
          default: 'underTitle',
          choices: ['underInput', 'underTitle'],
        },
        { name: 'questionErrorLocation', default: 'top', choices: ['top', 'bottom'] },
        { name: 'showProgressBar', default: 'off', choices: ['off', 'top', 'bottom', 'both'] },
        {
          name: 'progressBarType',
          default: 'pages',
          choices: ['pages', 'questions', 'requiredQuestions', 'correctQuestions', 'buttons'],
        },
        { name: 'mode', default: 'edit', choices: ['edit', 'display'] },
        { name: 'storeOthersAsComment:boolean', default: !0 },
        { name: 'maxTextLength:number', default: 0, minValue: 0 },
        { name: 'maxOthersLength:number', default: 0, minValue: 0 },
        'goNextPageAutomatic:boolean',
        {
          name: 'clearInvisibleValues',
          default: 'onComplete',
          choices: ['none', 'onComplete', 'onHidden', 'onHiddenContainer'],
        },
        {
          name: 'checkErrorsMode',
          default: 'onNextPage',
          choices: ['onNextPage', 'onValueChanged', 'onValueChanging', 'onComplete'],
        },
        { name: 'textUpdateMode', default: 'onBlur', choices: ['onBlur', 'onTyping'] },
        { name: 'startSurveyText', serializationProperty: 'locStartSurveyText' },
        { name: 'pagePrevText', serializationProperty: 'locPagePrevText' },
        { name: 'pageNextText', serializationProperty: 'locPageNextText' },
        { name: 'completeText', serializationProperty: 'locCompleteText' },
        { name: 'previewText', serializationProperty: 'locPreviewText' },
        { name: 'editText', serializationProperty: 'locEditText' },
        { name: 'requiredText', default: '*' },
        {
          name: 'questionStartIndex',
          dependsOn: ['showQuestionNumbers'],
          visibleIf: function (e) {
            return !e || 'off' !== e.showQuestionNumbers;
          },
        },
        {
          name: 'questionTitlePattern',
          default: 'numTitleRequire',
          dependsOn: ['questionStartIndex', 'requiredText'],
          choices: function (e) {
            return e ? e.getQuestionTitlePatternOptions() : [];
          },
        },
        {
          name: 'questionTitleTemplate',
          visible: !1,
          isSerializable: !1,
          serializationProperty: 'locQuestionTitleTemplate',
        },
        { name: 'firstPageIsStarted:boolean', default: !1 },
        { name: 'isSinglePage:boolean', default: !1, visible: !1, isSerializable: !1 },
        {
          name: 'questionsOnPageMode',
          default: 'standard',
          choices: ['singlePage', 'standard', 'questionPerPage'],
        },
        {
          name: 'showPreviewBeforeComplete',
          default: 'noPreview',
          choices: ['noPreview', 'showAllQuestions', 'showAnsweredQuestions'],
        },
        { name: 'maxTimeToFinish:number', default: 0, minValue: 0 },
        { name: 'maxTimeToFinishPage:number', default: 0, minValue: 0 },
        { name: 'showTimerPanel', default: 'none', choices: ['none', 'top', 'bottom'] },
        { name: 'showTimerPanelMode', default: 'all', choices: ['all', 'page', 'survey'] },
      ]);
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(29)),
        a = n(18),
        s = n(1),
        l = n(6),
        u = n(3),
        c = n(15),
        p = n(7);
      n.d(t, 'b', function () {
        return f;
      }),
        n.d(t, 'a', function () {
          return m;
        });
      var d = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            (n.panel = t), new c.a(n);
            var r = n;
            return (
              (n.koGetType = function (e) {
                return r.getElementType(e);
              }),
              (n.koElementAfterRender = function (e, t) {
                return r.elementAfterRender(e, t);
              }),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getElementType = function (e) {
              return e.isPanel ? 'survey-panel' : 'survey-question';
            }),
            (t.prototype.koAfterRender = function (e, t) {
              for (var n = 0; n < e.length; n++) {
                var r = e[n];
                '#text' == r.nodeName && (r.data = '');
              }
            }),
            (t.prototype.elementAfterRender = function (e, t) {
              if (this.panel && this.panel.survey) {
                var n = l.a.GetFirstNonTextElement(e);
                if (n) {
                  var r = t;
                  r.isPanel ? this.panel.survey.afterRenderPanel(t, n) : r.afterRender(n);
                }
              }
            }),
            (t.prototype.rowAfterRender = function (e, t) {
              if (!t.isNeedRender) {
                var n = e[0].parentElement;
                t.startLazyRendering(n),
                  i.utils.domNodeDisposal.addDisposeCallback(n, function () {
                    return (t.isNeedRender = !p.a.lazyRowsRendering);
                  });
              }
            }),
            t
          );
        })(a.a),
        h = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.panel = t), n;
          }
          return n.i(r.b)(t, e), t;
        })(c.a),
        f = (function (e) {
          function t(t) {
            void 0 === t && (t = '');
            var n = e.call(this, t) || this;
            n.onCreating();
            var r = n;
            return (
              (n.koElementType = i.observable('survey-panel')),
              (n.koCss = i.pureComputed(function () {
                return r.cssClasses;
              })),
              (n.koIsCollapsed = i.observable(n.isCollapsed)),
              (n.koIsExpanded = i.observable(n.isExpanded)),
              (n.stateChangedCallback = function () {
                r.onStateChanged();
              }),
              (n.toggleStateByKeyUp = function (e, t) {
                13 === t.which && r.toggleState();
              }),
              (n.koErrorClass = i.pureComputed(function () {
                var e = r.cssClasses.error.root;
                return e || 'panel-error-root';
              })),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), (this._implementor = new h(this));
            }),
            (t.prototype.createRow = function () {
              return new d(this);
            }),
            (t.prototype.onCreating = function () {}),
            (t.prototype.onNumChanged = function (e) {
              this.locTitle.onChanged();
            }),
            (t.prototype.onStateChanged = function () {
              this.koIsCollapsed(this.isCollapsed), this.koIsExpanded(this.isExpanded);
            }),
            (t.prototype.getTitleStyle = function () {
              var e = this.cssClasses.panel.title;
              return (
                (this.koIsCollapsed() || this.koIsExpanded()) &&
                  (e += ' ' + this.cssClasses.panel.titleExpandable),
                this.containsErrors && (e += ' ' + this.cssClasses.panel.titleOnError),
                e
              );
            }),
            (t.prototype.endLoadingFromJson = function () {
              e.prototype.endLoadingFromJson.call(this), this.onStateChanged();
            }),
            (t.prototype.dispose = function () {
              this.koCss.dispose(),
                this.koErrorClass.dispose(),
                this._implementor.dispose(),
                (this._implementor = void 0);
            }),
            t
          );
        })(a.b),
        m = (function (e) {
          function t(t) {
            void 0 === t && (t = '');
            var n = e.call(this, t) || this;
            return n.onCreating(), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), (this._implementor = new c.a(this));
            }),
            (t.prototype.createRow = function () {
              return new d(this);
            }),
            (t.prototype.createNewPanel = function (e) {
              return new f(e);
            }),
            (t.prototype.onCreating = function () {}),
            (t.prototype.onNumChanged = function (e) {
              this.locTitle.onChanged();
            }),
            (t.prototype.dispose = function () {
              this._implementor.dispose(), (this._implementor = void 0);
            }),
            t
          );
        })(o.a);
      s.b.overrideClassCreator('panel', function () {
        return new f();
      }),
        s.b.overrideClassCreator('page', function () {
          return new m();
        }),
        u.a.Instance.registerElement('panel', function (e) {
          return new f(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(9),
        a = n(11),
        s = n(4),
        l = n(2),
        u = n(12),
        c = n(42),
        p = n(10),
        d = n(7);
      n.d(t, 'b', function () {
        return h;
      }),
        n.d(t, 'a', function () {
          return f;
        });
      var h = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            (n.filteredChoicesValue = null),
              (n.otherItemValue = new a.a('other')),
              (n.choicesFromUrl = null),
              (n.cachedValueForUrlRequests = null),
              (n.isChoicesLoaded = !1),
              (n.enableOnLoadingChoices = !1),
              (n.dependedQuestions = []),
              (n.isSettingDefaultValue = !1),
              (n.isSettingComment = !1),
              (n.otherQuestionActiveChoices = null),
              (n.isRunningChoices = !1),
              (n.isFirstLoadChoicesFromUrl = !0),
              (n.isUpdatingChoicesDependedQuestions = !1);
            var r = n;
            n.createItemValues('choices'),
              n.registerFunctionOnPropertyValueChanged('choices', function () {
                r.filterItems() || r.onVisibleChoicesChanged();
              }),
              n.registerFunctionOnPropertiesValueChanged(
                ['choicesFromQuestion', 'choicesFromQuestionMode'],
                function () {
                  r.onVisibleChoicesChanged();
                }
              ),
              n.registerFunctionOnPropertyValueChanged('hideIfChoicesEmpty', function () {
                r.updateVisibilityBasedOnChoices();
              }),
              n.createNewArray('visibleChoices'),
              n.setPropertyValue('choicesByUrl', n.createRestfull()),
              (n.choicesByUrl.owner = n),
              (n.choicesByUrl.loadingOwner = n);
            var i = n.createLocalizableString('otherText', n, !0);
            return (
              n.createLocalizableString('otherErrorText', n, !0),
              (n.otherItemValue.locOwner = n),
              n.otherItemValue.setLocText(i),
              (i.onGetTextCallback = function (e) {
                return e || l.a.getString('otherItemText');
              }),
              (n.choicesByUrl.beforeSendRequestCallback = function () {
                r.onBeforeSendRequest();
              }),
              (n.choicesByUrl.getResultCallback = function (e) {
                r.onLoadChoicesFromUrl(e);
              }),
              (n.choicesByUrl.updateResultCallback = function (e, t) {
                return r.survey ? r.survey.updateChoicesFromServer(r, e, t) : e;
              }),
              n.createLocalizableString('otherPlaceHolder', n),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'selectbase';
            }),
            (t.prototype.dispose = function () {
              e.prototype.dispose.call(this);
              for (var t = 0; t < this.dependedQuestions.length; t++)
                this.dependedQuestions[t].choicesFromQuestion = '';
              this.removeFromDependedQuestion(this.getQuestionWithChoices());
            }),
            (t.prototype.supportGoNextPageError = function () {
              return !this.isOtherSelected || !!this.comment;
            }),
            (t.prototype.isLayoutTypeSupported = function (e) {
              return !0;
            }),
            Object.defineProperty(t.prototype, 'otherItem', {
              get: function () {
                return this.otherItemValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isOtherSelected', {
              get: function () {
                return this.hasOther && this.getHasOther(this.renderedValue);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'choicesVisibleIf', {
              get: function () {
                return this.getPropertyValue('choicesVisibleIf', '');
              },
              set: function (e) {
                this.setPropertyValue('choicesVisibleIf', e), this.filterItems();
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'choicesEnableIf', {
              get: function () {
                return this.getPropertyValue('choicesEnableIf', '');
              },
              set: function (e) {
                this.setPropertyValue('choicesEnableIf', e), this.filterItems();
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.runCondition = function (t, n) {
              e.prototype.runCondition.call(this, t, n),
                this.runItemsEnableCondition(t, n),
                this.runItemsCondition(t, n);
            }),
            (t.prototype.isTextValue = function () {
              return !0;
            }),
            (t.prototype.setDefaultValue = function () {
              (this.isSettingDefaultValue =
                !this.isValueEmpty(this.defaultValue) && this.hasUnknownValue(this.defaultValue)),
                e.prototype.setDefaultValue.call(this),
                (this.isSettingDefaultValue = !1);
            }),
            (t.prototype.getIsMultipleValue = function () {
              return !1;
            }),
            (t.prototype.convertDefaultValue = function (e) {
              if (null == e || void 0 == e) return e;
              if (this.getIsMultipleValue()) {
                if (!Array.isArray(e)) return [e];
              } else if (Array.isArray(e) && e.length > 0) return e[0];
              return e;
            }),
            (t.prototype.filterItems = function () {
              if (this.isLoadingFromJson || !this.data || this.areInvisibleElementsShowing)
                return !1;
              var e = this.getDataFilteredValues(),
                t = this.getDataFilteredProperties();
              return this.runItemsEnableCondition(e, t), this.runItemsCondition(e, t);
            }),
            (t.prototype.runItemsCondition = function (e, t) {
              this.setConditionalChoicesRunner();
              var n = this.runConditionsForItems(e, t);
              return (
                this.filteredChoicesValue &&
                  this.filteredChoicesValue.length === this.activeChoices.length &&
                  (this.filteredChoicesValue = null),
                n && (this.onVisibleChoicesChanged(), this.clearIncorrectValues()),
                n
              );
            }),
            (t.prototype.runItemsEnableCondition = function (e, t) {
              var n = this;
              this.setConditionalEnableChoicesRunner(),
                a.a.runEnabledConditionsForItems(
                  this.activeChoices,
                  this.conditionChoicesEnableIfRunner,
                  e,
                  t,
                  function (e) {
                    return n.onEnableItemCallBack(e);
                  }
                ) && this.clearDisabledValues(),
                this.onAfterRunItemsEnableCondition();
            }),
            (t.prototype.onAfterRunItemsEnableCondition = function () {}),
            (t.prototype.onEnableItemCallBack = function (e) {
              return !0;
            }),
            (t.prototype.setConditionalChoicesRunner = function () {
              this.choicesVisibleIf
                ? (this.conditionChoicesVisibleIfRunner ||
                    (this.conditionChoicesVisibleIfRunner = new p.a(this.choicesVisibleIf)),
                  (this.conditionChoicesVisibleIfRunner.expression = this.choicesVisibleIf))
                : (this.conditionChoicesVisibleIfRunner = null);
            }),
            (t.prototype.setConditionalEnableChoicesRunner = function () {
              this.choicesEnableIf
                ? (this.conditionChoicesEnableIfRunner ||
                    (this.conditionChoicesEnableIfRunner = new p.a(this.choicesEnableIf)),
                  (this.conditionChoicesEnableIfRunner.expression = this.choicesEnableIf))
                : (this.conditionChoicesEnableIfRunner = null);
            }),
            (t.prototype.runConditionsForItems = function (e, t) {
              return (
                (this.filteredChoicesValue = []),
                a.a.runConditionsForItems(
                  this.activeChoices,
                  this.filteredChoices,
                  this.areInvisibleElementsShowing ? null : this.conditionChoicesVisibleIfRunner,
                  e,
                  t,
                  !this.survey || !this.survey.areInvisibleElementsShowing
                )
              );
            }),
            (t.prototype.getHasOther = function (e) {
              return e === this.otherItem.value;
            }),
            Object.defineProperty(t.prototype, 'validatedValue', {
              get: function () {
                return this.rendredValueToDataCore(this.value);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.createRestfull = function () {
              return new c.a();
            }),
            (t.prototype.getQuestionComment = function () {
              return this.commentValue
                ? this.commentValue
                : this.hasComment || this.getStoreOthersAsComment()
                ? e.prototype.getQuestionComment.call(this)
                : this.commentValue;
            }),
            (t.prototype.setQuestionComment = function (t) {
              this.hasComment || this.getStoreOthersAsComment()
                ? e.prototype.setQuestionComment.call(this, t)
                : this.isSettingComment ||
                  t == this.commentValue ||
                  ((this.isSettingComment = !0),
                  (this.commentValue = t),
                  this.isOtherSelected &&
                    !this.isRenderedValueSetting &&
                    (this.value = this.rendredValueToData(this.renderedValue)),
                  (this.isSettingComment = !1));
            }),
            Object.defineProperty(t.prototype, 'renderedValue', {
              get: function () {
                return this.getPropertyValue('renderedValue', null);
              },
              set: function (e) {
                this.setPropertyValue('renderedValue', e),
                  (this.value = this.rendredValueToData(e));
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setQuestionValue = function (t, n) {
              if (
                (void 0 === n && (n = !0),
                !this.isLoadingFromJson &&
                  !s.a.isTwoValueEquals(this.value, t) &&
                  (e.prototype.setQuestionValue.call(this, t, n),
                  this.setPropertyValue('renderedValue', this.rendredValueFromData(t)),
                  !this.hasComment))
              ) {
                var r = this.isOtherSelected;
                if (r && this.prevCommentValue) {
                  var i = this.prevCommentValue;
                  (this.prevCommentValue = ''), (this.comment = i);
                }
                !r &&
                  this.comment &&
                  (this.getStoreOthersAsComment() && (this.prevCommentValue = this.comment),
                  (this.comment = ''));
              }
            }),
            (t.prototype.setNewValue = function (t) {
              (t = this.valueFromData(t)),
                ((this.choicesByUrl.isRunning || this.choicesByUrl.isWaitingForParameters) &&
                  this.isValueEmpty(t)) ||
                  (this.cachedValueForUrlRequests = t),
                e.prototype.setNewValue.call(this, t);
            }),
            (t.prototype.valueFromData = function (t) {
              var n = a.a.getItemByValue(this.activeChoices, t);
              return n ? n.value : e.prototype.valueFromData.call(this, t);
            }),
            (t.prototype.rendredValueFromData = function (e) {
              return this.getStoreOthersAsComment() ? e : this.renderedValueFromDataCore(e);
            }),
            (t.prototype.rendredValueToData = function (e) {
              return this.getStoreOthersAsComment() ? e : this.rendredValueToDataCore(e);
            }),
            (t.prototype.renderedValueFromDataCore = function (e) {
              return this.hasUnknownValue(e, !0)
                ? ((this.comment = e), this.otherItem.value)
                : this.valueFromData(e);
            }),
            (t.prototype.rendredValueToDataCore = function (e) {
              return (
                e == this.otherItem.value &&
                  this.getQuestionComment() &&
                  (e = this.getQuestionComment()),
                e
              );
            }),
            (t.prototype.hasUnknownValue = function (e, t) {
              return (
                void 0 === t && (t = !1),
                !s.a.isValueEmpty(e) &&
                  (!t || e != this.otherItem.value) &&
                  null == a.a.getItemByValue(this.filteredChoices, e)
              );
            }),
            (t.prototype.isValueDisabled = function (e) {
              var t = a.a.getItemByValue(this.filteredChoices, e);
              return !!t && !t.isEnabled;
            }),
            Object.defineProperty(t.prototype, 'choicesByUrl', {
              get: function () {
                return this.getPropertyValue('choicesByUrl');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'choices', {
              get: function () {
                return this.getPropertyValue('choices');
              },
              set: function (e) {
                this.setPropertyValue('choices', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'choicesFromQuestion', {
              get: function () {
                return this.getPropertyValue('choicesFromQuestion');
              },
              set: function (e) {
                var t = this.getQuestionWithChoices();
                t && t.removeFromDependedQuestion(this),
                  this.setPropertyValue('choicesFromQuestion', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.addIntoDependedQuestion = function (e) {
              !e || e.dependedQuestions.indexOf(this) > -1 || e.dependedQuestions.push(this);
            }),
            (t.prototype.removeFromDependedQuestion = function (e) {
              if (e) {
                var t = e.dependedQuestions.indexOf(this);
                t > -1 && e.dependedQuestions.splice(t, 1);
              }
            }),
            Object.defineProperty(t.prototype, 'choicesFromQuestionMode', {
              get: function () {
                return this.getPropertyValue('choicesFromQuestionMode');
              },
              set: function (e) {
                this.setPropertyValue('choicesFromQuestionMode', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'hideIfChoicesEmpty', {
              get: function () {
                return this.getPropertyValue('hideIfChoicesEmpty', !1);
              },
              set: function (e) {
                this.setPropertyValue('hideIfChoicesEmpty', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'keepIncorrectValues', {
              get: function () {
                return this.getPropertyValue('keepIncorrectValues', !1);
              },
              set: function (e) {
                this.setPropertyValue('keepIncorrectValues', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'storeOthersAsComment', {
              get: function () {
                return this.getPropertyValue('storeOthersAsComment', 'default');
              },
              set: function (e) {
                this.setPropertyValue('storeOthersAsComment', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.hasOtherChanged = function () {
              this.onVisibleChoicesChanged();
            }),
            Object.defineProperty(t.prototype, 'choicesOrder', {
              get: function () {
                return this.getPropertyValue('choicesOrder');
              },
              set: function (e) {
                (e = e.toLowerCase()) != this.choicesOrder &&
                  (this.setPropertyValue('choicesOrder', e), this.onVisibleChoicesChanged());
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'otherText', {
              get: function () {
                return this.getLocalizableStringText('otherText', l.a.getString('otherItemText'));
              },
              set: function (e) {
                this.setLocalizableStringText('otherText', e), this.onVisibleChoicesChanged();
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locOtherText', {
              get: function () {
                return this.getLocalizableString('otherText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'otherPlaceHolder', {
              get: function () {
                return this.getLocalizableStringText('otherPlaceHolder');
              },
              set: function (e) {
                this.setLocalizableStringText('otherPlaceHolder', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locOtherPlaceHolder', {
              get: function () {
                return this.getLocalizableString('otherPlaceHolder');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'otherErrorText', {
              get: function () {
                return this.getLocalizableStringText(
                  'otherErrorText',
                  l.a.getString('otherRequiredError')
                );
              },
              set: function (e) {
                this.setLocalizableStringText('otherErrorText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locOtherErrorText', {
              get: function () {
                return this.getLocalizableString('otherErrorText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'visibleChoices', {
              get: function () {
                return this.getPropertyValue('visibleChoices', []);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'enabledChoices', {
              get: function () {
                for (var e = [], t = this.visibleChoices, n = 0; n < t.length; n++)
                  t[n].isEnabled && e.push(t[n]);
                return e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.updateVisibleChoices = function () {
              if (!this.isLoadingFromJson) {
                var e = new Array(),
                  t = this.calcVisibleChoices();
                t || (t = []);
                for (var n = 0; n < t.length; n++) e.push(t[n]);
                this.setPropertyValue('visibleChoices', e);
              }
            }),
            (t.prototype.calcVisibleChoices = function () {
              if (this.canUseFilteredChoices()) return this.filteredChoices;
              var e = this.sortVisibleChoices(this.filteredChoices.slice());
              return this.addToVisibleChoices(e), e;
            }),
            (t.prototype.canUseFilteredChoices = function () {
              return !this.hasOther && 'none' == this.choicesOrder;
            }),
            (t.prototype.addToVisibleChoices = function (e) {
              this.hasOther && e.push(this.otherItem);
            }),
            (t.prototype.getPlainData = function (t) {
              var n = this;
              void 0 === t && (t = { includeEmpty: !0, includeQuestionTypes: !1 });
              var r = e.prototype.getPlainData.call(this, t);
              if (r) {
                var i = Array.isArray(this.value) ? this.value : [this.value];
                (r.isNode = !0),
                  (r.data = (r.data || []).concat(
                    i.map(function (e, r) {
                      var i = a.a.getItemByValue(n.visibleChoices, e),
                        o = {
                          name: r,
                          title: 'Choice',
                          value: e,
                          displayValue: n.getChoicesDisplayValue(n.visibleChoices, e),
                          getString: function (e) {
                            return 'object' == typeof e ? JSON.stringify(e) : e;
                          },
                          isNode: !1,
                        };
                      return (
                        i &&
                          (t.calculations || []).forEach(function (e) {
                            o[e.propertyName] = i[e.propertyName];
                          }),
                        n.isOtherSelected &&
                          n.otherItemValue === i &&
                          ((o.isOther = !0), (o.displayValue = n.comment)),
                        o
                      );
                    })
                  ));
              }
              return r;
            }),
            (t.prototype.getDisplayValueCore = function (e, t) {
              return this.getChoicesDisplayValue(this.visibleChoices, t);
            }),
            (t.prototype.getChoicesDisplayValue = function (e, t) {
              if (t == this.otherItemValue.value)
                return this.comment ? this.comment : this.locOtherText.textOrHtml;
              var n = a.a.getTextOrHtmlByValue(e, t);
              return '' == n && t ? t : n;
            }),
            Object.defineProperty(t.prototype, 'filteredChoices', {
              get: function () {
                return this.filteredChoicesValue ? this.filteredChoicesValue : this.activeChoices;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'activeChoices', {
              get: function () {
                var e = this.getQuestionWithChoices();
                return e
                  ? (this.addIntoDependedQuestion(e),
                    (this.otherQuestionActiveChoices = this.getChoicesFromQuestion(e)),
                    this.otherQuestionActiveChoices
                      ? this.otherQuestionActiveChoices
                      : e.visibleChoices)
                  : this.choicesFromUrl
                  ? this.choicesFromUrl
                  : this.getChoices();
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getQuestionWithChoices = function () {
              if (!this.choicesFromQuestion || !this.survey) return null;
              var e = this.survey.getQuestionByName(this.choicesFromQuestion);
              return e && e.visibleChoices && e !== this ? e : null;
            }),
            (t.prototype.getChoicesFromQuestion = function (e) {
              if (
                'selected' !== this.choicesFromQuestionMode &&
                'unselected' !== this.choicesFromQuestionMode
              )
                return null;
              for (
                var t = [],
                  n = 'selected' == this.choicesFromQuestionMode,
                  r = e.visibleChoices,
                  i = 0;
                i < r.length;
                i++
              ) {
                var o = e.isItemSelected(r[i]);
                ((o && n) || (!o && !n)) && t.push(r[i]);
              }
              return t;
            }),
            (t.prototype.getChoices = function () {
              return this.choices;
            }),
            (t.prototype.supportComment = function () {
              return !0;
            }),
            (t.prototype.supportOther = function () {
              return !0;
            }),
            (t.prototype.onCheckForErrors = function (t, n) {
              e.prototype.onCheckForErrors.call(this, t, n),
                this.hasOther &&
                  this.isOtherSelected &&
                  !this.comment &&
                  t.push(new u.e(this.otherErrorText, this));
            }),
            (t.prototype.setSurveyImpl = function (t) {
              e.prototype.setSurveyImpl.call(this, t), this.runChoicesByUrl();
            }),
            (t.prototype.getStoreOthersAsComment = function () {
              return (
                !this.isSettingDefaultValue &&
                (!0 === this.storeOthersAsComment ||
                  ('default' == this.storeOthersAsComment &&
                    (null == this.survey || this.survey.storeOthersAsComment)) ||
                  (!this.choicesByUrl.isEmpty && !this.choicesFromUrl))
              );
            }),
            (t.prototype.onSurveyLoad = function () {
              e.prototype.onSurveyLoad.call(this),
                this.runChoicesByUrl(),
                this.onVisibleChoicesChanged();
            }),
            (t.prototype.onAnyValueChanged = function (t) {
              e.prototype.onAnyValueChanged.call(this, t),
                t != this.getValueName() && this.runChoicesByUrl(),
                t && t == this.choicesFromQuestion && this.onVisibleChoicesChanged();
            }),
            (t.prototype.updateValueFromSurvey = function (t) {
              var n = '';
              this.hasOther &&
                this.getStoreOthersAsComment() &&
                this.hasUnknownValue(t) &&
                !this.getHasOther(t) &&
                ((n = this.getCommentFromValue(t)), (t = this.setOtherValueIntoValue(t))),
                e.prototype.updateValueFromSurvey.call(this, t),
                n && this.setNewComment(n);
            }),
            (t.prototype.getCommentFromValue = function (e) {
              return e;
            }),
            (t.prototype.setOtherValueIntoValue = function (e) {
              return this.otherItem.value;
            }),
            (t.prototype.runChoicesByUrl = function () {
              if (this.choicesByUrl && !this.isLoadingFromJson && !this.isRunningChoices) {
                var e = this.surveyImpl ? this.surveyImpl.getTextProcessor() : this.textProcessor;
                e || (e = this.survey),
                  e &&
                    ((this.isReadyValue = this.isChoicesLoaded || this.choicesByUrl.isEmpty),
                    (this.isRunningChoices = !0),
                    this.choicesByUrl.run(e),
                    (this.isRunningChoices = !1));
              }
            }),
            (t.prototype.onBeforeSendRequest = function () {
              !0 !== d.a.disableOnGettingChoicesFromWeb ||
                this.isReadOnly ||
                ((this.enableOnLoadingChoices = !0), (this.readOnly = !0));
            }),
            (t.prototype.onLoadChoicesFromUrl = function (e) {
              if ((this.enableOnLoadingChoices && (this.readOnly = !1), !this.isReadOnly)) {
                var t = [];
                this.choicesByUrl && this.choicesByUrl.error && t.push(this.choicesByUrl.error),
                  (this.errors = t);
              }
              var n = null,
                r = !0;
              this.isFirstLoadChoicesFromUrl &&
                !this.cachedValueForUrlRequests &&
                this.defaultValue &&
                ((this.cachedValueForUrlRequests = this.defaultValue), (r = !1)),
                this.isValueEmpty(this.cachedValueForUrlRequests) &&
                  (this.cachedValueForUrlRequests = this.value),
                (this.isFirstLoadChoicesFromUrl = !1);
              var i = this.createCachedValueForUrlRequests(this.cachedValueForUrlRequests, r);
              if (
                (e && e.length > 0 && ((n = new Array()), a.a.setData(n, e)),
                (this.choicesFromUrl = n),
                this.filterItems(),
                this.onVisibleChoicesChanged(),
                n)
              ) {
                var o = this.updateCachedValueForUrlRequests(i, n);
                if (o && !this.isReadOnly) {
                  var l = !s.a.isTwoValueEquals(this.value, o.value);
                  try {
                    s.a.isValueEmpty(o.value) ||
                      ((this.allowNotifyValueChanged = !1),
                      (this.locNotificationInData = !0),
                      (this.value = void 0),
                      (this.locNotificationInData = !1)),
                      (this.allowNotifyValueChanged = l),
                      (this.value = o.value);
                  } finally {
                    this.allowNotifyValueChanged = !0;
                  }
                }
              }
              this.choicesLoaded();
            }),
            (t.prototype.createCachedValueForUrlRequests = function (e, t) {
              if (this.isValueEmpty(e)) return null;
              if (Array.isArray(e)) {
                for (var n = [], r = 0; r < e.length; r++)
                  n.push(this.createCachedValueForUrlRequests(e[r], !0));
                return n;
              }
              return { value: e, isExists: !t || !this.hasUnknownValue(e) };
            }),
            (t.prototype.updateCachedValueForUrlRequests = function (e, t) {
              if (this.isValueEmpty(e)) return null;
              if (Array.isArray(e)) {
                for (var n = [], r = 0; r < e.length; r++) {
                  var i = this.updateCachedValueForUrlRequests(e[r], t);
                  if (i && !this.isValueEmpty(i.value)) {
                    var o = i.value,
                      s = a.a.getItemByValue(t, i.value);
                    s && (o = s.value), n.push(o);
                  }
                }
                return { value: n };
              }
              var l = e.isExists && this.hasUnknownValue(e.value) ? null : e.value,
                s = a.a.getItemByValue(t, l);
              return s && (l = s.value), { value: l };
            }),
            (t.prototype.updateChoicesDependedQuestions = function () {
              if (!this.isUpdatingChoicesDependedQuestions) {
                this.isUpdatingChoicesDependedQuestions = !0;
                for (var e = 0; e < this.dependedQuestions.length; e++)
                  this.dependedQuestions[e].onVisibleChoicesChanged(),
                    this.dependedQuestions[e].updateChoicesDependedQuestions();
                this.isUpdatingChoicesDependedQuestions = !1;
              }
            }),
            (t.prototype.onVisibleChoicesChanged = function () {
              this.isLoadingFromJson ||
                (this.updateVisibleChoices(),
                this.updateVisibilityBasedOnChoices(),
                this.visibleChoicesChangedCallback && this.visibleChoicesChangedCallback(),
                this.updateChoicesDependedQuestions());
            }),
            (t.prototype.updateVisibilityBasedOnChoices = function () {
              this.hideIfChoicesEmpty &&
                (this.visible = !this.filteredChoices || this.filteredChoices.length > 0);
            }),
            (t.prototype.sortVisibleChoices = function (e) {
              var t = this.choicesOrder.toLowerCase();
              return 'asc' == t
                ? this.sortArray(e, 1)
                : 'desc' == t
                ? this.sortArray(e, -1)
                : 'random' == t
                ? this.randomizeArray(e)
                : e;
            }),
            (t.prototype.sortArray = function (e, t) {
              return e.sort(function (e, n) {
                return e.calculatedText < n.calculatedText
                  ? -1 * t
                  : e.calculatedText > n.calculatedText
                  ? 1 * t
                  : 0;
              });
            }),
            (t.prototype.randomizeArray = function (e) {
              return s.a.randomizeArray(e);
            }),
            (t.prototype.clearIncorrectValues = function () {
              this.keepIncorrectValues ||
                (this.survey && this.survey.questionCountByValueName(this.getValueName()) > 1) ||
                (this.choicesByUrl && !this.choicesByUrl.isEmpty) ||
                (this.clearIncorrectValuesCallback
                  ? this.clearIncorrectValuesCallback()
                  : this.clearIncorrectValuesCore());
            }),
            (t.prototype.clearValueIfInvisible = function () {
              e.prototype.clearValueIfInvisible.call(this), this.clearIncorrectValues();
            }),
            (t.prototype.isItemSelected = function (e) {
              return e.value === this.value;
            }),
            (t.prototype.clearDisabledValues = function () {
              this.survey && this.survey.clearValueOnDisableItems && this.clearDisabledValuesCore();
            }),
            (t.prototype.clearIncorrectValuesCore = function () {
              var e = this.value;
              this.canClearValueAnUnknow(e) && this.clearValue();
            }),
            (t.prototype.canClearValueAnUnknow = function (e) {
              return (
                !(!this.getStoreOthersAsComment() && this.isOtherSelected) &&
                this.hasUnknownValue(e, !0)
              );
            }),
            (t.prototype.clearDisabledValuesCore = function () {
              this.isValueDisabled(this.value) && this.clearValue();
            }),
            (t.prototype.clearUnusedValues = function () {
              e.prototype.clearUnusedValues.call(this),
                this.isOtherSelected || this.hasComment || (this.comment = '');
            }),
            (t.prototype.getColumnClass = function () {
              var e = this.cssClasses.column;
              return this.hasColumns && (e += ' sv-q-column-' + this.colCount), e;
            }),
            (t.prototype.getLabelClass = function (e) {
              var t = this.cssClasses.label;
              return this.isItemSelected(e) && (t += ' ' + this.cssClasses.labelChecked), t;
            }),
            (t.prototype.getControlLabelClass = function (e) {
              var t = this.cssClasses.controlLabel;
              return this.isItemSelected(e) && (t += ' ' + this.cssClasses.controlLabelChecked), t;
            }),
            Object.defineProperty(t.prototype, 'columns', {
              get: function () {
                var e = [],
                  t = this.colCount;
                if (this.hasColumns && this.visibleChoices.length > 0)
                  if ('column' == d.a.showItemsInOrder)
                    for (var n = 0, r = this.visibleChoices.length % t, i = 0; i < t; i++) {
                      for (
                        var o = [], a = n;
                        a < n + Math.floor(this.visibleChoices.length / t);
                        a++
                      )
                        o.push(this.visibleChoices[a]);
                      r > 0 && (r--, o.push(this.visibleChoices[a]), a++), (n = a), e.push(o);
                    }
                  else
                    for (var i = 0; i < t; i++) {
                      for (var o = [], a = i; a < this.visibleChoices.length; a += t)
                        o.push(this.visibleChoices[a]);
                      e.push(o);
                    }
                return e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'hasColumns', {
              get: function () {
                return this.colCount > 1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.choicesLoaded = function () {
              this.isChoicesLoaded = !0;
              var e = this.isReadyValue;
              (this.isReadyValue = !0),
                this.onReadyChanged &&
                  this.onReadyChanged.fire(this, { question: this, isReady: !0, oldIsReady: e });
            }),
            t
          );
        })(o.a),
        f = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'colCount', {
              get: function () {
                return this.getPropertyValue('colCount', this.isFlowLayout ? 0 : 1);
              },
              set: function (e) {
                e < 0 ||
                  e > 5 ||
                  this.isFlowLayout ||
                  (this.setPropertyValue('colCount', e),
                  this.fireCallback(this.colCountChangedCallback));
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getItemIndex = function (e) {
              return this.visibleChoices.indexOf(e);
            }),
            (t.prototype.onParentChanged = function () {
              e.prototype.onParentChanged.call(this),
                this.isFlowLayout && this.setPropertyValue('colCount', null);
            }),
            t
          );
        })(h);
      i.b.addClass(
        'selectbase',
        [
          { name: 'hasComment:switch', layout: 'row' },
          {
            name: 'commentText',
            dependsOn: 'hasComment',
            visibleIf: function (e) {
              return e.hasComment;
            },
            serializationProperty: 'locCommentText',
            layout: 'row',
          },
          'choicesFromQuestion:question_selectbase',
          {
            name: 'choices:itemvalue[]',
            baseValue: function () {
              return l.a.getString('choices_Item');
            },
            dependsOn: 'choicesFromQuestion',
            visibleIf: function (e) {
              return !e.choicesFromQuestion;
            },
          },
          {
            name: 'choicesFromQuestionMode',
            default: 'all',
            choices: ['all', 'selected', 'unselected'],
            dependsOn: 'choicesFromQuestion',
            visibleIf: function (e) {
              return !!e.choicesFromQuestion;
            },
          },
          {
            name: 'choicesOrder',
            default: 'none',
            choices: ['none', 'asc', 'desc', 'random'],
            dependsOn: 'choicesFromQuestion',
            visibleIf: function (e) {
              return !e.choicesFromQuestion;
            },
          },
          {
            name: 'choicesByUrl:restfull',
            className: 'ChoicesRestfull',
            onGetValue: function (e) {
              return e.choicesByUrl.getData();
            },
            onSetValue: function (e, t) {
              e.choicesByUrl.setData(t);
            },
          },
          'hideIfChoicesEmpty:boolean',
          {
            name: 'choicesVisibleIf:condition',
            dependsOn: 'choicesFromQuestion',
            visibleIf: function (e) {
              return !e.choicesFromQuestion;
            },
          },
          {
            name: 'choicesEnableIf:condition',
            dependsOn: 'choicesFromQuestion',
            visibleIf: function (e) {
              return !e.choicesFromQuestion;
            },
          },
          'hasOther:boolean',
          {
            name: 'otherPlaceHolder',
            serializationProperty: 'locOtherPlaceHolder',
            dependsOn: 'hasOther',
            visibleIf: function (e) {
              return e.hasOther;
            },
          },
          {
            name: 'otherText',
            serializationProperty: 'locOtherText',
            dependsOn: 'hasOther',
            visibleIf: function (e) {
              return e.hasOther;
            },
          },
          {
            name: 'otherErrorText',
            serializationProperty: 'locOtherErrorText',
            dependsOn: 'hasOther',
            visibleIf: function (e) {
              return e.hasOther;
            },
          },
          {
            name: 'storeOthersAsComment',
            default: 'default',
            choices: ['default', !0, !1],
            visible: !1,
          },
        ],
        null,
        'question'
      ),
        i.b.addClass(
          'checkboxbase',
          [{ name: 'colCount:number', default: 1, choices: [0, 1, 2, 3, 4, 5], layout: 'row' }],
          null,
          'selectbase'
        );
    },
    function (e, t, n) {
      'use strict';
      function r(e, t, n, r) {
        (e.storeOthersAsComment = !!n && n.storeOthersAsComment),
          (e.choices && 0 != e.choices.length) ||
            !e.choicesByUrl.isEmpty ||
            (e.choices = n.choices),
          e.choicesByUrl.isEmpty || e.choicesByUrl.run(r.getTextProcessor());
      }
      var i = n(0),
        o = n(1),
        a = n(50),
        s = n(4),
        l = n(6),
        u = n(25),
        c = n(2),
        p = n(3),
        d = n(13),
        h = n(32),
        f = n(28),
        m = n(18),
        g = n(7);
      n.d(t, 'e', function () {
        return y;
      }),
        n.d(t, 'd', function () {
          return v;
        }),
        n.d(t, 'c', function () {
          return b;
        }),
        n.d(t, 'b', function () {
          return w;
        }),
        n.d(t, 'a', function () {
          return T;
        });
      var y = {
          dropdown: {
            properties: [
              'choices',
              'choicesOrder',
              'choicesByUrl',
              'optionsCaption',
              'otherText',
              'choicesVisibleIf',
            ],
            onCellQuestionUpdate: function (e, t, n, i) {
              r(e, t, n, i),
                e.locOptionsCaption &&
                  e.locOptionsCaption.isEmpty &&
                  !n.locOptionsCaption.isEmpty &&
                  (e.optionsCaption = n.optionsCaption);
            },
          },
          checkbox: {
            properties: [
              'choices',
              'choicesOrder',
              'choicesByUrl',
              'otherText',
              'choicesVisibleIf',
              'hasSelectAll',
              'hasNone',
            ],
            onCellQuestionUpdate: function (e, t, n, i) {
              r(e, t, n, i), (e.colCount = t.colCount > -1 ? t.colCount : n.columnColCount);
            },
          },
          radiogroup: {
            properties: [
              'choices',
              'choicesOrder',
              'choicesByUrl',
              'otherText',
              'choicesVisibleIf',
            ],
            onCellQuestionUpdate: function (e, t, n, i) {
              r(e, t, n, i), (e.colCount = t.colCount > -1 ? t.colCount : n.columnColCount);
            },
          },
          text: {
            properties: ['placeHolder', 'inputType', 'maxLength', 'min', 'max', 'step'],
            onCellQuestionUpdate: function (e, t, n, r) {},
          },
          comment: {
            properties: ['placeHolder', 'rows', 'maxLength'],
            onCellQuestionUpdate: function (e, t, n, r) {},
          },
          boolean: {
            properties: ['renderAs', 'defaultValue'],
            onCellQuestionUpdate: function (e, t, n, r) {
              (e.showTitle = !0), (e.renderAs = t.renderAs);
            },
          },
          expression: {
            properties: ['expression', 'displayStyle', 'currency'],
            onCellQuestionUpdate: function (e, t, n, r) {},
          },
          rating: { properties: ['rateValues'] },
        },
        v = (function (e) {
          function t(t, n) {
            void 0 === n && (n = null);
            var r = e.call(this) || this;
            (r.colOwnerValue = null),
              (r.indexValue = -1),
              (r._isVisible = !0),
              (r._hasVisibleCell = !0);
            var i = r;
            return (
              r.createLocalizableString('totalFormat', r),
              r.registerFunctionOnPropertyValueChanged('showInMultipleColumns', function () {
                i.doShowInMultipleColumnsChanged();
              }),
              r.updateTemplateQuestion(),
              (r.name = t),
              n && (r.title = n),
              r
            );
          }
          return (
            n.i(i.b)(t, e),
            (t.getColumnTypes = function () {
              var e = [];
              for (var t in y) e.push(t);
              return e;
            }),
            (t.prototype.endLoadingFromJson = function () {
              e.prototype.endLoadingFromJson.call(this), this.templateQuestion.endLoadingFromJson();
            }),
            (t.prototype.getDynamicPropertyName = function () {
              return 'cellType';
            }),
            (t.prototype.getDynamicType = function () {
              return this.calcCellQuestionType();
            }),
            Object.defineProperty(t.prototype, 'colOwner', {
              get: function () {
                return this.colOwnerValue;
              },
              set: function (e) {
                (this.colOwnerValue = e), this.updateTemplateQuestion();
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.locStrsChanged = function () {
              e.prototype.locStrsChanged.call(this), this.locTitle.strChanged();
            }),
            (t.prototype.addUsedLocales = function (t) {
              e.prototype.addUsedLocales.call(this, t), this.templateQuestion.addUsedLocales(t);
            }),
            Object.defineProperty(t.prototype, 'index', {
              get: function () {
                return this.indexValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setIndex = function (e) {
              this.indexValue = e;
            }),
            (t.prototype.getType = function () {
              return 'matrixdropdowncolumn';
            }),
            Object.defineProperty(t.prototype, 'cellType', {
              get: function () {
                return this.getPropertyValue('cellType');
              },
              set: function (e) {
                (e = e.toLocaleLowerCase()),
                  this.setPropertyValue('cellType', e),
                  this.updateTemplateQuestion(),
                  this.colOwner && this.colOwner.onColumnCellTypeChanged(this);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'templateQuestion', {
              get: function () {
                return this.templateQuestionValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'value', {
              get: function () {
                return this.templateQuestion.name;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isVisible', {
              get: function () {
                return this._isVisible;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setIsVisible = function (e) {
              this._isVisible = e;
            }),
            Object.defineProperty(t.prototype, 'hasVisibleCell', {
              get: function () {
                return this._hasVisibleCell;
              },
              set: function (e) {
                this._hasVisibleCell = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'name', {
              get: function () {
                return this.templateQuestion.name;
              },
              set: function (e) {
                this.templateQuestion.name = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'title', {
              get: function () {
                return this.templateQuestion.title;
              },
              set: function (e) {
                this.templateQuestion.title = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locTitle', {
              get: function () {
                return this.templateQuestion.locTitle;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'fullTitle', {
              get: function () {
                return this.locTitle.textOrHtml;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isRequired', {
              get: function () {
                return this.templateQuestion.isRequired;
              },
              set: function (e) {
                this.templateQuestion.isRequired = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'requiredText', {
              get: function () {
                return this.templateQuestion.requiredText;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'requiredErrorText', {
              get: function () {
                return this.templateQuestion.requiredErrorText;
              },
              set: function (e) {
                this.templateQuestion.requiredErrorText = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locRequiredErrorText', {
              get: function () {
                return this.templateQuestion.locRequiredErrorText;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'readOnly', {
              get: function () {
                return this.templateQuestion.readOnly;
              },
              set: function (e) {
                this.templateQuestion.readOnly = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'hasOther', {
              get: function () {
                return this.templateQuestion.hasOther;
              },
              set: function (e) {
                this.templateQuestion.hasOther = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'visibleIf', {
              get: function () {
                return this.templateQuestion.visibleIf;
              },
              set: function (e) {
                this.templateQuestion.visibleIf = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'enableIf', {
              get: function () {
                return this.templateQuestion.enableIf;
              },
              set: function (e) {
                this.templateQuestion.enableIf = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'requiredIf', {
              get: function () {
                return this.templateQuestion.requiredIf;
              },
              set: function (e) {
                this.templateQuestion.requiredIf = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'showInMultipleColumns', {
              get: function () {
                return this.getPropertyValue('showInMultipleColumns', !1);
              },
              set: function (e) {
                this.setPropertyValue('showInMultipleColumns', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isSupportMultipleColumns', {
              get: function () {
                return ['checkbox', 'radiogroup'].indexOf(this.cellType) > -1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isShowInMultipleColumns', {
              get: function () {
                return this.showInMultipleColumns && this.isSupportMultipleColumns;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'validators', {
              get: function () {
                return this.templateQuestion.validators;
              },
              set: function (e) {
                this.templateQuestion.validators = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'totalType', {
              get: function () {
                return this.getPropertyValue('totalType', 'none');
              },
              set: function (e) {
                this.setPropertyValue('totalType', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'totalExpression', {
              get: function () {
                return this.getPropertyValue('totalExpression');
              },
              set: function (e) {
                this.setPropertyValue('totalExpression', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'hasTotal', {
              get: function () {
                return 'none' != this.totalType || !!this.totalExpression;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'totalFormat', {
              get: function () {
                return this.getLocalizableStringText('totalFormat', '');
              },
              set: function (e) {
                this.setLocalizableStringText('totalFormat', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locTotalFormat', {
              get: function () {
                return this.getLocalizableString('totalFormat');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'renderAs', {
              get: function () {
                return this.getPropertyValue('renderAs');
              },
              set: function (e) {
                this.setPropertyValue('renderAs', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'totalMaximumFractionDigits', {
              get: function () {
                return this.getPropertyValue('totalMaximumFractionDigits', -1);
              },
              set: function (e) {
                e < -1 || e > 20 || this.setPropertyValue('totalMaximumFractionDigits', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'totalMinimumFractionDigits', {
              get: function () {
                return this.getPropertyValue('totalMinimumFractionDigits', -1);
              },
              set: function (e) {
                e < -1 || e > 20 || this.setPropertyValue('totalMinimumFractionDigits', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'totalDisplayStyle', {
              get: function () {
                return this.getPropertyValue('totalDisplayStyle');
              },
              set: function (e) {
                this.setPropertyValue('totalDisplayStyle', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'totalCurrency', {
              get: function () {
                return this.getPropertyValue('totalCurrency');
              },
              set: function (e) {
                n.i(h.b)().indexOf(e) < 0 || this.setPropertyValue('totalCurrency', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'minWidth', {
              get: function () {
                return this.getPropertyValue('minWidth', '');
              },
              set: function (e) {
                this.setPropertyValue('minWidth', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'width', {
              get: function () {
                return this.getPropertyValue('width', '');
              },
              set: function (e) {
                this.setPropertyValue('width', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'colCount', {
              get: function () {
                return this.getPropertyValue('colCount', -1);
              },
              set: function (e) {
                e < -1 || e > 4 || this.setPropertyValue('colCount', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getLocale = function () {
              return this.colOwner ? this.colOwner.getLocale() : '';
            }),
            (t.prototype.getMarkdownHtml = function (e, t) {
              return this.colOwner ? this.colOwner.getMarkdownHtml(e, t) : null;
            }),
            (t.prototype.getProcessedText = function (e) {
              return this.colOwner ? this.colOwner.getProcessedText(e) : e;
            }),
            (t.prototype.createCellQuestion = function (e) {
              var t = this.calcCellQuestionType(),
                n = this.createNewQuestion(t);
              return this.callOnCellQuestionUpdate(n, e), n;
            }),
            (t.prototype.updateCellQuestion = function (e, t, n) {
              void 0 === n && (n = null),
                this.setQuestionProperties(e, n),
                this.callOnCellQuestionUpdate(e, t);
            }),
            (t.prototype.callOnCellQuestionUpdate = function (e, t) {
              var n = e.getType(),
                r = y[n];
              r && r.onCellQuestionUpdate && r.onCellQuestionUpdate(e, this, this.colOwner, t);
            }),
            (t.prototype.defaultCellTypeChanged = function () {
              this.updateTemplateQuestion();
            }),
            (t.prototype.calcCellQuestionType = function () {
              return 'default' !== this.cellType
                ? this.cellType
                : this.colOwner
                ? this.colOwner.getCellType()
                : g.a.matrixDefaultCellType;
            }),
            (t.prototype.updateTemplateQuestion = function () {
              var e = this.templateQuestion ? this.templateQuestion.getType() : '',
                t = this.calcCellQuestionType();
              if (t !== e) {
                this.templateQuestion && this.removeProperties(e),
                  (this.templateQuestionValue = this.createNewQuestion(t)),
                  (this.templateQuestion.locOwner = this),
                  this.addProperties(t);
                var n = this;
                this.templateQuestion.onPropertyChanged.add(function (e, t) {
                  n.propertyValueChanged(t.name, t.oldvalue, t.newValue);
                });
              }
            }),
            (t.prototype.createNewQuestion = function (e) {
              var t = o.b.createClass(e);
              return (
                t || (t = o.b.createClass('text')),
                (t.loadingOwner = this),
                this.setQuestionProperties(t),
                t
              );
            }),
            (t.prototype.setQuestionProperties = function (e, t) {
              if ((void 0 === t && (t = null), this.templateQuestion)) {
                var n = new o.c().toJsonObject(this.templateQuestion, !0);
                t && t(n), (n.type = e.getType()), new o.c().toObject(n, e);
              }
            }),
            (t.prototype.propertyValueChanged = function (t, n, r) {
              e.prototype.propertyValueChanged.call(this, t, n, r),
                'visibleChoices' != t &&
                  (null == this.colOwner ||
                    this.isLoadingFromJson ||
                    this.colOwner.onColumnPropertyChanged(this, t, r));
            }),
            (t.prototype.doShowInMultipleColumnsChanged = function () {
              null == this.colOwner ||
                this.isLoadingFromJson ||
                this.colOwner.onShowInMultipleColumnsChanged(this);
            }),
            (t.prototype.getProperties = function (e) {
              return o.b.getDynamicPropertiesByObj(this, e);
            }),
            (t.prototype.removeProperties = function (e) {
              for (var t = this.getProperties(e), n = 0; n < t.length; n++) {
                var r = t[n];
                delete this[r.name],
                  r.serializationProperty && delete this[r.serializationProperty];
              }
            }),
            (t.prototype.addProperties = function (e) {
              for (
                var t = this.templateQuestion, n = this.getProperties(e), r = 0;
                r < n.length;
                r++
              ) {
                var i = n[r];
                this.addProperty(t, i.name, !1),
                  i.serializationProperty && this.addProperty(t, i.serializationProperty, !0);
              }
            }),
            (t.prototype.addProperty = function (e, t, n) {
              var r = {
                configurable: !0,
                get: function () {
                  return e[t];
                },
              };
              n ||
                (r.set = function (n) {
                  e[t] = n;
                }),
                Object.defineProperty(this, t, r);
            }),
            t
          );
        })(l.b),
        b = (function () {
          function e(e, t, n) {
            (this.column = e),
              (this.row = t),
              (this.data = n),
              (this.questionValue = this.createQuestion(e, t, n)),
              this.questionValue.updateCustomWidget();
          }
          return (
            (e.prototype.locStrsChanged = function () {
              this.question.locStrsChanged();
            }),
            (e.prototype.createQuestion = function (e, t, n) {
              var r = n.createQuestion(this.row, this.column);
              return (
                (r.validateValueCallback = function () {
                  return n.validateCell(t, e.name, t.value);
                }),
                o.a.getProperties(e.getType()).forEach(function (t) {
                  var n = t.name;
                  void 0 !== e[n] && (r[n] = e[n]);
                }),
                r
              );
            }),
            Object.defineProperty(e.prototype, 'question', {
              get: function () {
                return this.questionValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'value', {
              get: function () {
                return this.question.value;
              },
              set: function (e) {
                this.question.value = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.runCondition = function (e, t) {
              this.question.runCondition(e, t);
            }),
            e
          );
        })(),
        x = (function (e) {
          function t(t, n, r) {
            var i = e.call(this, t, n, r) || this;
            return (i.column = t), (i.row = n), (i.data = r), i.updateCellQuestion(), i;
          }
          return (
            n.i(i.b)(t, e),
            (t.prototype.createQuestion = function (e, t, n) {
              var r = o.b.createClass('expression');
              return r.setSurveyImpl(t), r;
            }),
            (t.prototype.locStrsChanged = function () {
              this.updateCellQuestion(), e.prototype.locStrsChanged.call(this);
            }),
            (t.prototype.updateCellQuestion = function () {
              this.question.locCalculation(),
                this.column.updateCellQuestion(this.question, null, function (e) {
                  delete e.defaultValue;
                }),
                (this.question.expression = this.getTotalExpression()),
                (this.question.format = this.column.totalFormat),
                (this.question.currency = this.column.totalCurrency),
                (this.question.displayStyle = this.column.totalDisplayStyle),
                (this.question.maximumFractionDigits = this.column.totalMaximumFractionDigits),
                (this.question.minimumFractionDigits = this.column.totalMinimumFractionDigits),
                this.question.unlocCalculation();
            }),
            (t.prototype.getTotalExpression = function () {
              if (this.column.totalExpression) return this.column.totalExpression;
              if ('none' == this.column.totalType) return '';
              var e = this.column.totalType + 'InArray';
              return f.a.Instance.hasFunction(e) ? e + "({self}, '" + this.column.name + "')" : '';
            }),
            t
          );
        })(b),
        C = (function (e) {
          function t(t, n) {
            var r = e.call(this, n) || this;
            return (r.row = t), (r.variableName = n), r;
          }
          return (
            n.i(i.b)(t, e),
            Object.defineProperty(t.prototype, 'survey', {
              get: function () {
                return this.row.getSurvey();
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getValues = function () {
              return this.row.value;
            }),
            (t.prototype.getQuestionByName = function (e) {
              return this.row.getQuestionByName(e);
            }),
            (t.prototype.onCustomProcessText = function (e) {
              return e.name == w.IndexVariableName
                ? ((e.isExists = !0), (e.value = this.row.rowIndex), !0)
                : e.name == w.RowValueVariableName &&
                    ((e.isExists = !0), (e.value = this.row.rowName), !0);
            }),
            t
          );
        })(u.b),
        w = (function () {
          function e(t, n) {
            var r = this;
            (this.isSettingValue = !1),
              (this.detailPanelValue = null),
              (this.cells = []),
              (this.isCreatingDetailPanel = !1),
              (this.data = t),
              this.subscribeToChanges(n),
              (this.textPreProcessor = new C(this, e.RowVariableName)),
              (this.showHideDetailPanelClick = function () {
                r.showHideDetailPanel();
              }),
              (this.idValue = e.getId());
          }
          return (
            (e.getId = function () {
              return 'srow_' + e.idCounter++;
            }),
            Object.defineProperty(e.prototype, 'id', {
              get: function () {
                return this.idValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'rowName', {
              get: function () {
                return null;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'value', {
              get: function () {
                for (var e = {}, t = this.questions, n = 0; n < t.length; n++) {
                  var r = t[n];
                  r.isEmpty() || (e[r.getValueName()] = r.value),
                    r.comment &&
                      this.getSurvey() &&
                      this.getSurvey().storeOthersAsComment &&
                      (e[r.getValueName() + g.a.commentPrefix] = r.comment);
                }
                return e;
              },
              set: function (e) {
                (this.isSettingValue = !0), this.subscribeToChanges(e);
                for (var t = this.questions, n = 0; n < t.length; n++) {
                  var r = t[n],
                    i = e ? e[r.getValueName()] : null,
                    o = r.comment,
                    a = e ? e[r.getValueName() + g.a.commentPrefix] : '';
                  void 0 == a && (a = ''),
                    r.updateValueFromSurvey(i),
                    (a || s.a.isTwoValueEquals(o, r.comment)) && r.updateCommentFromSurvey(a),
                    r.onSurveyValueChanged(i);
                }
                this.isSettingValue = !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'locText', {
              get: function () {
                return null;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'hasPanel', {
              get: function () {
                return !!this.data && this.data.hasDetailPanel(this);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'detailPanel', {
              get: function () {
                return this.detailPanelValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'detailPanelId', {
              get: function () {
                return this.detailPanel ? this.detailPanel.id : '';
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'isDetailPanelShowing', {
              get: function () {
                return !!this.data && this.data.getIsDetailPanelShowing(this);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.setIsDetailPanelShowing = function (e) {
              this.data && this.data.setIsDetailPanelShowing(this, e);
            }),
            (e.prototype.showHideDetailPanel = function () {
              this.isDetailPanelShowing ? this.hideDetailPanel() : this.showDetailPanel();
            }),
            (e.prototype.showDetailPanel = function () {
              this.ensureDetailPanel(), this.detailPanelValue && this.setIsDetailPanelShowing(!0);
            }),
            (e.prototype.hideDetailPanel = function (e) {
              void 0 === e && (e = !1),
                this.setIsDetailPanelShowing(!1),
                e && (this.detailPanelValue = null);
            }),
            (e.prototype.ensureDetailPanel = function () {
              if (
                !this.isCreatingDetailPanel &&
                !this.detailPanelValue &&
                this.hasPanel &&
                this.data
              ) {
                (this.isCreatingDetailPanel = !0),
                  (this.detailPanelValue = this.data.createRowDetailPanel(this));
                var e = this.detailPanelValue.questions,
                  t = this.data.getRowValue(this.data.getRowIndex(this));
                if (!s.a.isValueEmpty(t))
                  for (var n = 0; n < e.length; n++) {
                    var r = e[n].getValueName();
                    s.a.isValueEmpty(t[r]) || (e[n].value = t[r]);
                  }
                this.detailPanelValue.setSurveyImpl(this), (this.isCreatingDetailPanel = !1);
              }
            }),
            (e.prototype.getAllValues = function () {
              return this.value;
            }),
            (e.prototype.getFilteredValues = function () {
              var e = this.getAllValues(),
                t = { row: e };
              for (var n in e) t[n] = e[n];
              return t;
            }),
            (e.prototype.getFilteredProperties = function () {
              return { survey: this.getSurvey(), row: this };
            }),
            (e.prototype.runCondition = function (t, n) {
              this.data && (t[e.OwnerVariableName] = this.data.value),
                (t[e.IndexVariableName] = this.rowIndex),
                (t[e.RowValueVariableName] = this.rowName),
                n || (n = {}),
                (n[e.RowVariableName] = this);
              for (var r = 0; r < this.cells.length; r++)
                (t[e.RowVariableName] = this.value), this.cells[r].runCondition(t, n);
              this.detailPanel && this.detailPanel.runCondition(t, n);
            }),
            (e.prototype.onAnyValueChanged = function (e) {
              for (var t = this.questions, n = 0; n < t.length; n++) t[n].onAnyValueChanged(e);
            }),
            (e.prototype.getDataValueCore = function (e, t) {
              var n = this.getSurvey();
              return n ? n.getDataValueCore(e, t) : e[t];
            }),
            (e.prototype.getValue = function (e) {
              var t = this.getQuestionByName(e);
              return t ? t.value : null;
            }),
            (e.prototype.setValue = function (e, t) {
              this.setValueCore(e, t, !1);
            }),
            (e.prototype.getVariable = function (e) {}),
            (e.prototype.setVariable = function (e, t) {}),
            (e.prototype.getComment = function (e) {
              var t = this.getQuestionByName(e);
              return t ? t.comment : '';
            }),
            (e.prototype.setComment = function (e, t, n) {
              this.setValueCore(e, t, !0);
            }),
            (e.prototype.setValueCore = function (t, n, r) {
              if (!this.isSettingValue) {
                var i = this.value,
                  o = r ? t + g.a.commentPrefix : t,
                  a = r ? this.getComment(t) : this.getValue(t),
                  l = this.getQuestionByName(t),
                  u = this.data.onRowChanging(this, o, i);
                if (l && !s.a.isTwoValueEquals(u, a)) r ? (l.comment = u) : (l.value = u);
                else {
                  if (
                    l &&
                    this.data.isValidateOnValueChanging &&
                    l.hasErrors(!0, { isOnValueChanged: !0 })
                  )
                    return;
                  this.data.onRowChanged(this, o, i, null == n && !l),
                    this.onAnyValueChanged(e.RowVariableName);
                }
              }
            }),
            Object.defineProperty(e.prototype, 'isEmpty', {
              get: function () {
                var e = this.value;
                if (s.a.isValueEmpty(e)) return !0;
                for (var t in e) if (void 0 !== e[t] && null !== e[t]) return !1;
                return !0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getQuestionByColumn = function (e) {
              for (var t = 0; t < this.cells.length; t++)
                if (this.cells[t].column == e) return this.cells[t].question;
              return null;
            }),
            (e.prototype.getQuestionByColumnName = function (e) {
              for (var t = 0; t < this.cells.length; t++)
                if (this.cells[t].column.name == e) return this.cells[t].question;
              return null;
            }),
            Object.defineProperty(e.prototype, 'questions', {
              get: function () {
                for (var e = [], t = 0; t < this.cells.length; t++) e.push(this.cells[t].question);
                for (
                  var n = this.detailPanel ? this.detailPanel.questions : [], t = 0;
                  t < n.length;
                  t++
                )
                  e.push(n[t]);
                return e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getQuestionByName = function (e) {
              var t = this.getQuestionByColumnName(e);
              return t || (this.detailPanel ? this.detailPanel.getQuestionByName(e) : null);
            }),
            (e.prototype.getSharedQuestionByName = function (e) {
              return this.data ? this.data.getSharedQuestionByName(e, this) : null;
            }),
            (e.prototype.clearIncorrectValues = function (e) {
              for (var t in e) {
                var n = this.getQuestionByName(t);
                if (n) {
                  var r = n.value;
                  n.clearIncorrectValues(),
                    s.a.isTwoValueEquals(r, n.value) || this.setValue(t, n.value);
                } else
                  !this.getSharedQuestionByName(t) &&
                    t.indexOf(g.a.matrixTotalValuePostFix) < 0 &&
                    this.setValue(t, null);
              }
            }),
            (e.prototype.getLocale = function () {
              return this.data ? this.data.getLocale() : '';
            }),
            (e.prototype.getMarkdownHtml = function (e, t) {
              return this.data ? this.data.getMarkdownHtml(e, t) : null;
            }),
            (e.prototype.getProcessedText = function (e) {
              return this.data ? this.data.getProcessedText(e) : e;
            }),
            (e.prototype.locStrsChanged = function () {
              for (var e = 0; e < this.cells.length; e++) this.cells[e].locStrsChanged();
              this.detailPanel && this.detailPanel.locStrsChanged();
            }),
            (e.prototype.updateCellQuestionOnColumnChanged = function (e, t, n) {
              for (var r = 0; r < this.cells.length; r++)
                if (this.cells[r].column === e)
                  return void this.updateCellOnColumnChanged(this.cells[r], t, n);
            }),
            (e.prototype.onQuestionReadOnlyChanged = function (e) {
              for (var t = this.questions, n = 0; n < t.length; n++) t[n].readOnly = e;
            }),
            (e.prototype.hasErrors = function (e, t, n) {
              var r = !1,
                i = this.cells;
              if (!i) return r;
              for (var o = 0; o < i.length; o++)
                if (i[o]) {
                  var a = i[o].question;
                  a &&
                    a.visible &&
                    ((a.onCompletedAsyncValidators = function (e) {
                      n();
                    }),
                    (t && !0 === t.isOnValueChanged && a.isEmpty()) ||
                      (r = a.hasErrors(e, t) || r));
                }
              if (this.hasPanel) {
                this.ensureDetailPanel();
                var s = this.detailPanel.hasErrors(e, !1, t);
                !t.hideErroredPanel &&
                  s &&
                  e &&
                  (t.isSingleDetailPanel && (t.hideErroredPanel = !0), this.showDetailPanel()),
                  (r = s || r);
              }
              return r;
            }),
            (e.prototype.updateCellOnColumnChanged = function (e, t, n) {
              e.question[t] = n;
            }),
            (e.prototype.buildCells = function (e) {
              this.isSettingValue = !0;
              for (var t = this.data.columns, n = 0; n < t.length; n++) {
                var r = t[n];
                if (r.isVisible) {
                  var i = this.createCell(r);
                  if ((this.cells.push(i), e && !s.a.isValueEmpty(e[r.name]))) {
                    i.question.value = e[r.name];
                    var o = r.name + g.a.commentPrefix;
                    s.a.isValueEmpty(e[o]) || (i.question.comment = e[o]);
                  }
                }
              }
              this.isSettingValue = !1;
            }),
            (e.prototype.createCell = function (e) {
              return new b(e, this, this.data);
            }),
            (e.prototype.geSurveyData = function () {
              return this;
            }),
            (e.prototype.getSurvey = function () {
              return this.data ? this.data.getSurvey() : null;
            }),
            (e.prototype.getTextProcessor = function () {
              return this.textPreProcessor;
            }),
            Object.defineProperty(e.prototype, 'rowIndex', {
              get: function () {
                return this.data ? this.data.getRowIndex(this) + 1 : -1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'editingObj', {
              get: function () {
                return this.editingObjValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.dispose = function () {
              this.editingObj &&
                (this.editingObj.onPropertyChanged.remove(this.onEditingObjPropertyChanged),
                (this.editingObjValue = null));
            }),
            (e.prototype.subscribeToChanges = function (e) {
              var t = this;
              e &&
                e.getType &&
                e.onPropertyChanged &&
                e !== this.editingObj &&
                ((this.editingObjValue = e),
                (this.onEditingObjPropertyChanged = function (e, n) {
                  t.updateOnSetValue(n.name, n.newValue);
                }),
                this.editingObj.onPropertyChanged.add(this.onEditingObjPropertyChanged));
            }),
            (e.prototype.updateOnSetValue = function (e, t) {
              this.isSettingValue = !0;
              var n = this.getQuestionByName(e);
              n && (n.value = t), (this.isSettingValue = !1);
            }),
            (e.RowVariableName = 'row'),
            (e.OwnerVariableName = 'self'),
            (e.IndexVariableName = 'rowIndex'),
            (e.RowValueVariableName = 'rowValue'),
            (e.idCounter = 1),
            e
          );
        })(),
        P = (function (e) {
          function t(t) {
            var n = e.call(this, t, null) || this;
            return n.buildCells(null), n;
          }
          return (
            n.i(i.b)(t, e),
            (t.prototype.createCell = function (e) {
              return new x(e, this, this.data);
            }),
            (t.prototype.setValue = function (e, t) {
              this.data && !this.isSettingValue && this.data.onTotalValueChanged();
            }),
            (t.prototype.runCondition = function (t, n) {
              var r,
                i = 0;
              do {
                (r = s.a.getUnbindValue(this.value)),
                  e.prototype.runCondition.call(this, t, n),
                  i++;
              } while (!s.a.isTwoValueEquals(r, this.value) && i < 3);
            }),
            (t.prototype.updateCellOnColumnChanged = function (e, t, n) {
              e.updateCellQuestion();
            }),
            t
          );
        })(w),
        V = (function () {
          function e() {
            (this.minWidth = ''),
              (this.width = ''),
              (this.colSpans = 1),
              (this.className = ''),
              (this.idValue = e.counter++);
          }
          return (
            Object.defineProperty(e.prototype, 'hasQuestion', {
              get: function () {
                return !!this.question;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'hasTitle', {
              get: function () {
                return !!this.locTitle;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'hasPanel', {
              get: function () {
                return !!this.panel;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'id', {
              get: function () {
                return this.idValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'showErrorOnTop', {
              get: function () {
                return this.showErrorOnCore('top');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'showErrorOnBottom', {
              get: function () {
                return this.showErrorOnCore('bottom');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.showErrorOnCore = function (e) {
              return this.getShowErrorLocation() == e && (!this.isChoice || this.isFirstChoice);
            }),
            (e.prototype.getShowErrorLocation = function () {
              return this.hasQuestion ? this.question.survey.questionErrorLocation : '';
            }),
            Object.defineProperty(e.prototype, 'item', {
              get: function () {
                return this.itemValue;
              },
              set: function (e) {
                (this.itemValue = e), e && (e.hideCaption = !0);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'isChoice', {
              get: function () {
                return !!this.item;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'choiceValue', {
              get: function () {
                return this.isChoice ? this.item.value : null;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'isCheckbox', {
              get: function () {
                return this.isChoice && 'checkbox' == this.question.getType();
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'isFirstChoice', {
              get: function () {
                return 0 === this.choiceIndex;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.counter = 1),
            e
          );
        })(),
        k = (function () {
          function e() {
            (this.isDetailRow = !1),
              (this.cells = []),
              (this.className = ''),
              (this.idValue = e.counter++);
          }
          return (
            Object.defineProperty(e.prototype, 'id', {
              get: function () {
                return this.idValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.counter = 1),
            e
          );
        })(),
        _ = (function (e) {
          function t(t) {
            var n = e.call(this) || this;
            return (n.matrix = t), n.createNewArray('rows'), n.build(), n;
          }
          return (
            n.i(i.b)(t, e),
            Object.defineProperty(t.prototype, 'showHeader', {
              get: function () {
                return this.getPropertyValue('showHeader');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'showFooter', {
              get: function () {
                return this.matrix.hasFooter && this.matrix.isColumnLayoutHorizontal;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'hasFooter', {
              get: function () {
                return !!this.footerRow;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'hasRemoveRows', {
              get: function () {
                return this.hasRemoveRowsValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.isRequireReset = function () {
              return (
                this.hasRemoveRows != this.matrix.canRemoveRows ||
                !this.matrix.isColumnLayoutHorizontal
              );
            }),
            Object.defineProperty(t.prototype, 'headerRow', {
              get: function () {
                return this.headerRowValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'footerRow', {
              get: function () {
                return this.footerRowValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'rows', {
              get: function () {
                return this.getPropertyValue('rows');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.build = function () {
              this.hasRemoveRowsValue = this.matrix.canRemoveRows;
              this.matrix.visibleRows;
              (this.cssClasses = this.matrix.cssClasses),
                this.buildHeader(),
                this.buildRows(),
                this.buildFooter();
            }),
            (t.prototype.onAddedRow = function () {
              this.getRenderedDataRowCount() >= this.matrix.visibleRows.length ||
                this.addHorizontalRow(
                  this.rows,
                  this.matrix.visibleRows[this.matrix.visibleRows.length - 1],
                  1 == this.matrix.visibleRows.length && !this.matrix.showHeader
                );
            }),
            (t.prototype.getRenderedDataRowCount = function () {
              for (var e = 0, t = 0; t < this.rows.length; t++) this.rows[t].isDetailRow || e++;
              return e;
            }),
            (t.prototype.onRemovedRow = function (e) {
              var t = this.getRenderedRowIndex(e);
              if (!(t < 0)) {
                var n = 1;
                t < this.rows.length - 1 && this.rows[t + 1].isDetailRow && n++,
                  this.rows.splice(t, n);
              }
            }),
            (t.prototype.onDetailPanelChangeVisibility = function (e, t) {
              var n = this.getRenderedRowIndex(e);
              if (!(n < 0)) {
                var r = n < this.rows.length - 1 && this.rows[n + 1].isDetailRow ? n + 1 : -1;
                if (!((t && r > -1) || (!t && r < 0)))
                  if (t) {
                    var i = this.createDetailPanelRow(e, this.rows[n]);
                    this.rows.splice(n + 1, 0, i);
                  } else this.rows.splice(r, 1);
              }
            }),
            (t.prototype.getRenderedRowIndex = function (e) {
              for (var t = 0; t < this.rows.length; t++) if (this.rows[t].row == e) return t;
              return -1;
            }),
            (t.prototype.buildHeader = function () {
              var e = this.matrix.isColumnLayoutHorizontal && this.matrix.showHeader,
                t = e || (this.matrix.hasRowText && !this.matrix.isColumnLayoutHorizontal);
              if ((this.setPropertyValue('showHeader', t), t)) {
                if (
                  ((this.headerRowValue = new k()),
                  this.getHasDetailPanelInRows() &&
                    this.headerRow.cells.push(this.createHeaderCell(null)),
                  this.matrix.hasRowText &&
                    this.matrix.showHeader &&
                    this.headerRow.cells.push(this.createHeaderCell(null)),
                  this.matrix.isColumnLayoutHorizontal)
                )
                  for (var n = 0; n < this.matrix.visibleColumns.length; n++) {
                    var r = this.matrix.visibleColumns[n];
                    r.hasVisibleCell &&
                      (r.isShowInMultipleColumns
                        ? this.createMutlipleColumnsHeader(r)
                        : this.headerRow.cells.push(this.createHeaderCell(r)));
                  }
                else {
                  for (var i = this.matrix.visibleRows, n = 0; n < i.length; n++)
                    this.headerRow.cells.push(this.createTextCell(i[n].locText));
                  this.matrix.hasFooter &&
                    this.headerRow.cells.push(this.createTextCell(this.matrix.getFooterText()));
                }
                this.hasRemoveRows && this.headerRow.cells.push(this.createHeaderCell(null));
              }
            }),
            (t.prototype.buildFooter = function () {
              if (this.showFooter) {
                (this.footerRowValue = new k()),
                  this.getHasDetailPanelInRows() &&
                    this.footerRow.cells.push(this.createHeaderCell(null)),
                  this.matrix.hasRowText &&
                    this.footerRow.cells.push(this.createTextCell(this.matrix.getFooterText()));
                for (var e = this.matrix.visibleTotalRow.cells, t = 0; t < e.length; t++) {
                  var n = e[t];
                  n.column.hasVisibleCell &&
                    (n.column.isShowInMultipleColumns
                      ? this.createMutlipleColumnsFooter(this.footerRow, n)
                      : this.footerRow.cells.push(this.createEditCell(n)));
                }
                this.hasRemoveRows && this.footerRow.cells.push(this.createHeaderCell(null));
              }
            }),
            (t.prototype.buildRows = function () {
              var e = this.matrix.isColumnLayoutHorizontal
                ? this.buildHorizontalRows()
                : this.buildVerticalRows();
              this.setPropertyValue('rows', e);
            }),
            (t.prototype.getHasDetailPanelInRows = function () {
              if (!this.matrix.isColumnLayoutHorizontal) return !1;
              for (var e = this.matrix.visibleRows, t = 0; t < e.length; t++)
                if (e[t].hasPanel) return !0;
              return !1;
            }),
            (t.prototype.canRemoveRow = function (e) {
              return this.matrix.canRemoveRow(e);
            }),
            (t.prototype.buildHorizontalRows = function () {
              for (var e = this.matrix.visibleRows, t = [], n = 0; n < e.length; n++)
                this.addHorizontalRow(t, e[n], 0 == n && !this.matrix.showHeader);
              return t;
            }),
            (t.prototype.addHorizontalRow = function (e, t, n) {
              var r = this.createHorizontalRow(t, n);
              (r.row = t),
                e.push(r),
                t.isDetailPanelShowing && e.push(this.createDetailPanelRow(t, r));
            }),
            (t.prototype.createHorizontalRow = function (e, t) {
              var n = new k();
              if (e.hasPanel) {
                var r = new V();
                (r.isShowHideDetail = !0),
                  (r.className = this.cssClasses.detailCell),
                  (r.row = e),
                  n.cells.push(r);
              }
              if (this.matrix.hasRowText) {
                var i = this.createTextCell(e.locText);
                n.cells.push(i),
                  t && this.setHeaderCellWidth(null, i),
                  e.hasPanel &&
                    this.cssClasses.detailRowText &&
                    (i.className && (i.className += ' '),
                    (i.className += this.cssClasses.detailRowText));
              }
              for (var o = 0; o < e.cells.length; o++) {
                var r = e.cells[o];
                if (r.column.hasVisibleCell)
                  if (r.column.isShowInMultipleColumns) this.createMutlipleEditCells(n, r);
                  else {
                    var i = this.createEditCell(r);
                    n.cells.push(i), t && this.setHeaderCellWidth(r.column, i);
                  }
              }
              return this.hasRemoveRows && n.cells.push(this.createRemoveRowCell(e)), n;
            }),
            (t.prototype.createDetailPanelRow = function (e, t) {
              var n = new k();
              (n.row = e), (n.className = this.cssClasses.detailRow), (n.isDetailRow = !0);
              var r = new V();
              this.matrix.hasRowText && (r.colSpans = 2), (r.isEmpty = !0), n.cells.push(r);
              var i = null;
              this.hasRemoveRows && ((i = new V()), (i.isEmpty = !0));
              var o = new V();
              return (
                (o.panel = e.detailPanel),
                (o.colSpans = t.cells.length - r.colSpans - (i ? i.colSpans : 0)),
                (o.className = this.cssClasses.detailPanelCell),
                n.cells.push(o),
                i && n.cells.push(i),
                n
              );
            }),
            (t.prototype.buildVerticalRows = function () {
              for (var e = this.matrix.columns, t = [], n = 0; n < e.length; n++) {
                var r = e[n];
                r.isVisible &&
                  r.hasVisibleCell &&
                  (r.isShowInMultipleColumns
                    ? this.createMutlipleVerticalRows(t, r, n)
                    : t.push(this.createVerticalRow(r, n)));
              }
              return this.hasRemoveRows && t.push(this.createVerticalRemoveRow()), t;
            }),
            (t.prototype.createMutlipleVerticalRows = function (e, t, n) {
              var r = this.getMultipleColumnChoices(t);
              if (r)
                for (var i = 0; i < r.length; i++) e.push(this.createVerticalRow(t, n, r[i], i));
            }),
            (t.prototype.createVerticalRow = function (e, t, n, r) {
              void 0 === n && (n = null), void 0 === r && (r = -1);
              var i = new k();
              if (this.matrix.showHeader) {
                var o = n ? n.locText : e.locTitle,
                  a = this.createTextCell(o);
                n || this.setRequriedToHeaderCell(e, a), i.cells.push(a);
              }
              for (var s = this.matrix.visibleRows, l = 0; l < s.length; l++) {
                var u = this.createEditCell(s[l].cells[t]);
                (u.item = n), (u.choiceIndex = r >= 0 ? r : l), i.cells.push(u);
              }
              return (
                this.matrix.hasTotal &&
                  i.cells.push(this.createEditCell(this.matrix.visibleTotalRow.cells[t])),
                i
              );
            }),
            (t.prototype.createVerticalRemoveRow = function () {
              var e = new k();
              this.matrix.showHeader && e.cells.push(this.createTextCell(null));
              for (var t = this.matrix.visibleRows, n = 0; n < t.length; n++)
                e.cells.push(this.createRemoveRowCell(t[n]));
              return this.matrix.hasTotal && e.cells.push(this.createTextCell(null)), e;
            }),
            (t.prototype.createMutlipleEditCells = function (e, t, n) {
              void 0 === n && (n = !1);
              var r = this.getMultipleColumnChoices(t.column);
              if (r)
                for (var i = 0; i < r.length; i++) {
                  var o = this.createEditCell(t);
                  n || ((o.item = r[i]), (o.choiceIndex = i)), e.cells.push(o);
                }
            }),
            (t.prototype.createEditCell = function (e) {
              var t = new V();
              (t.cell = e), (t.row = e.row), (t.question = e.question), (t.matrix = this.matrix);
              var n = e.question.cssClasses,
                r = '';
              return (
                n &&
                  ((r = ''),
                  n.itemValue && (r += ' ' + n.itemValue),
                  n.asCell && (r && (r += ''), (r += n.asCell))),
                !r && this.cssClasses.cell && (r = this.cssClasses.cell),
                (t.className = r),
                t
              );
            }),
            (t.prototype.createMutlipleColumnsFooter = function (e, t) {
              this.createMutlipleEditCells(e, t, !0);
            }),
            (t.prototype.createMutlipleColumnsHeader = function (e) {
              var t = this.getMultipleColumnChoices(e);
              if (t)
                for (var n = 0; n < t.length; n++) {
                  var r = this.createTextCell(t[n].locText);
                  this.setHeaderCell(e, r), this.headerRow.cells.push(r);
                }
            }),
            (t.prototype.getMultipleColumnChoices = function (e) {
              var t = e.templateQuestion.choices;
              if (t && Array.isArray(t) && 0 == t.length) return this.matrix.choices;
              var t = e.templateQuestion.visibleChoices;
              return t && Array.isArray(t) ? t : null;
            }),
            (t.prototype.createHeaderCell = function (e) {
              var t = this.createTextCell(e ? e.locTitle : null);
              return (
                this.setHeaderCell(e, t),
                this.cssClasses.headerCell && (t.className = this.cssClasses.headerCell),
                t
              );
            }),
            (t.prototype.setHeaderCell = function (e, t) {
              this.setHeaderCellWidth(e, t), this.setRequriedToHeaderCell(e, t);
            }),
            (t.prototype.setHeaderCellWidth = function (e, t) {
              (t.minWidth = null != e ? this.matrix.getColumnWidth(e) : ''),
                (t.width = null != e ? e.width : this.matrix.getRowTitleWidth());
            }),
            (t.prototype.setRequriedToHeaderCell = function (e, t) {
              e &&
                e.isRequired &&
                this.matrix.survey &&
                (t.requiredText = this.matrix.survey.requiredText);
            }),
            (t.prototype.createRemoveRowCell = function (e) {
              var t = new V();
              return (
                (t.row = e),
                (t.isRemoveRow = this.canRemoveRow(e)),
                this.cssClasses.cell && (t.className = this.cssClasses.cell),
                t
              );
            }),
            (t.prototype.createTextCell = function (e) {
              var t = new V();
              return (
                (t.locTitle = e || new d.a(this.matrix, !1)),
                this.cssClasses.cell && (t.className = this.cssClasses.cell),
                t
              );
            }),
            t
          );
        })(l.b),
        T = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            (n.name = t),
              (n.isRowChanging = !1),
              (n.lockResetRenderedTable = !1),
              (n.isDoingonAnyValueChanged = !1);
            var r = n;
            return (
              n.createItemValues('choices'),
              n.createLocalizableString('optionsCaption', n),
              (n.detailPanelValue = n.createNewDetailPanel()),
              (n.detailPanel.selectedElementInDesign = n),
              (n.detailPanel.renderWidth = '100%'),
              n.registerFunctionOnPropertyValueChanged('columns', function (e) {
                r.updateColumnsIndexes(e),
                  r.clearGeneratedRows(),
                  (r.generatedTotalRow = null),
                  r.resetRenderedTable(),
                  r.fireCallback(r.columnsChangedCallback);
              }),
              n.registerFunctionOnPropertiesValueChanged(
                ['columnLayout', 'addRowLocation'],
                function () {
                  r.fireCallback(r.columnLayoutChangedCallback);
                }
              ),
              n.registerFunctionOnPropertiesValueChanged(
                ['cellType', 'optionsCaption', 'columnColCount', 'rowTitleWidth'],
                function () {
                  r.clearGeneratedRows(),
                    r.resetRenderedTable(),
                    r.fireCallback(r.columnsChangedCallback);
                }
              ),
              n.registerFunctionOnPropertiesValueChanged(
                [
                  'columnLayout',
                  'showHeader',
                  'minRowCount',
                  'isReadOnly',
                  'rowCount',
                  'hasFooter',
                  'detailPanelMode',
                ],
                function () {
                  r.resetRenderedTable();
                }
              ),
              n
            );
          }
          return (
            n.i(i.b)(t, e),
            Object.defineProperty(t, 'defaultCellType', {
              get: function () {
                return g.a.matrixDefaultCellType;
              },
              set: function (e) {
                g.a.matrixDefaultCellType = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.addDefaultColumns = function (e) {
              for (var t = p.b.DefaultColums, n = 0; n < t.length; n++) e.addColumn(t[n]);
            }),
            (t.prototype.createColumnValues = function () {
              var e = this;
              return this.createNewArray('columns', function (t) {
                t.colOwner = e;
              });
            }),
            (t.prototype.getType = function () {
              return 'matrixdropdownbase';
            }),
            Object.defineProperty(t.prototype, 'hasSingleInput', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isRowsDynamic', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'columnLayout', {
              get: function () {
                return this.getPropertyValue('columnLayout');
              },
              set: function (e) {
                this.setPropertyValue('columnLayout', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'columnsLocation', {
              get: function () {
                return this.columnLayout;
              },
              set: function (e) {
                this.columnLayout = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isColumnLayoutHorizontal', {
              get: function () {
                return 'vertical' != this.columnLayout;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'detailPanelMode', {
              get: function () {
                return this.getPropertyValue('detailPanelMode', 'none');
              },
              set: function (e) {
                this.setPropertyValue('detailPanelMode', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'detailPanel', {
              get: function () {
                return this.detailPanelValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getPanel = function () {
              return this.detailPanel;
            }),
            Object.defineProperty(t.prototype, 'detailElements', {
              get: function () {
                return this.detailPanel.elements;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.createNewDetailPanel = function () {
              return new m.b();
            }),
            Object.defineProperty(t.prototype, 'hasRowText', {
              get: function () {
                return !0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getFooterText = function () {
              return null;
            }),
            Object.defineProperty(t.prototype, 'canRemoveRows', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.canRemoveRow = function (e) {
              return !0;
            }),
            (t.prototype.onRowsChanged = function () {
              this.resetRenderedTable(), e.prototype.onRowsChanged.call(this);
            }),
            (t.prototype.onStartRowAddingRemoving = function () {
              this.lockResetRenderedTable = !0;
            }),
            (t.prototype.onEndRowAdding = function () {
              (this.lockResetRenderedTable = !1),
                this.renderedTable &&
                  (this.renderedTable.isRequireReset()
                    ? this.resetRenderedTable()
                    : this.renderedTable.onAddedRow());
            }),
            (t.prototype.onEndRowRemoving = function (e) {
              (this.lockResetRenderedTable = !1),
                this.renderedTable.isRequireReset()
                  ? this.resetRenderedTable()
                  : e && this.renderedTable.onRemovedRow(e);
            }),
            Object.defineProperty(t.prototype, 'renderedTableValue', {
              get: function () {
                return this.getPropertyValue('renderedTable', null);
              },
              set: function (e) {
                this.setPropertyValue('renderedTable', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.resetRenderedTable = function () {
              this.lockResetRenderedTable ||
                this.isLoadingFromJson ||
                ((this.renderedTableValue = null),
                this.fireCallback(this.onRenderedTableResetCallback));
            }),
            (t.prototype.clearGeneratedRows = function () {
              if (this.generatedVisibleRows) {
                for (var t = 0; t < this.generatedVisibleRows.length; t++)
                  this.generatedVisibleRows[t].dispose();
                e.prototype.clearGeneratedRows.call(this);
              }
            }),
            Object.defineProperty(t.prototype, 'renderedTable', {
              get: function () {
                return (
                  this.renderedTableValue ||
                    ((this.renderedTableValue = this.createRenderedTable()),
                    this.onRenderedTableCreatedCallback &&
                      this.onRenderedTableCreatedCallback(this.renderedTableValue)),
                  this.renderedTableValue
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.createRenderedTable = function () {
              return new _(this);
            }),
            (t.prototype.onMatrixRowCreated = function (e) {
              if (this.survey)
                for (
                  var t = {
                      rowValue: e.value,
                      row: e,
                      column: null,
                      columnName: null,
                      cell: null,
                      cellQuestion: null,
                      value: null,
                    },
                    n = 0;
                  n < this.visibleColumns.length;
                  n++
                ) {
                  (t.column = this.visibleColumns[n]), (t.columnName = t.column.name);
                  var r = e.cells[n];
                  (t.cell = r),
                    (t.cellQuestion = r.question),
                    (t.value = r.value),
                    this.onCellCreatedCallback && this.onCellCreatedCallback(t),
                    this.survey.matrixCellCreated(this, t);
                }
            }),
            Object.defineProperty(t.prototype, 'cellType', {
              get: function () {
                return this.getPropertyValue('cellType', g.a.matrixDefaultCellType);
              },
              set: function (e) {
                (e = e.toLowerCase()),
                  this.cellType != e &&
                    (this.setPropertyValue('cellType', e),
                    this.updateColumnsCellType(),
                    this.fireCallback(this.updateCellsCallback));
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.updateColumnsCellType = function () {
              for (var e = 0; e < this.columns.length; e++)
                this.columns[e].defaultCellTypeChanged();
            }),
            (t.prototype.updateColumnsIndexes = function (e) {
              for (var t = 0; t < e.length; t++) e[t].setIndex(t);
            }),
            Object.defineProperty(t.prototype, 'columnColCount', {
              get: function () {
                return this.getPropertyValue('columnColCount', 0);
              },
              set: function (e) {
                e < 0 ||
                  e > 4 ||
                  (this.setPropertyValue('columnColCount', e),
                  this.fireCallback(this.updateCellsCallback));
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'columnMinWidth', {
              get: function () {
                return this.getPropertyValue('columnMinWidth', '');
              },
              set: function (e) {
                this.setPropertyValue('columnMinWidth', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'horizontalScroll', {
              get: function () {
                return this.getPropertyValue('horizontalScroll', !1);
              },
              set: function (e) {
                this.setPropertyValue('horizontalScroll', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getRequiredText = function () {
              return this.survey ? this.survey.requiredText : '';
            }),
            (t.prototype.onColumnPropertyChanged = function (e, t, n) {
              if ((this.updateHasFooter(), this.generatedVisibleRows)) {
                for (var r = 0; r < this.generatedVisibleRows.length; r++)
                  this.generatedVisibleRows[r].updateCellQuestionOnColumnChanged(e, t, n);
                this.generatedTotalRow &&
                  this.generatedTotalRow.updateCellQuestionOnColumnChanged(e, t, n),
                  this.onColumnsChanged(),
                  'isRequired' == t && this.resetRenderedTable(),
                  e.isShowInMultipleColumns && this.onShowInMultipleColumnsChanged(e);
              }
            }),
            (t.prototype.onShowInMultipleColumnsChanged = function (e) {
              this.clearGeneratedRows(), this.resetRenderedTable();
            }),
            (t.prototype.onColumnCellTypeChanged = function (e) {
              this.clearGeneratedRows(), this.resetRenderedTable();
            }),
            (t.prototype.getRowTitleWidth = function () {
              return '';
            }),
            Object.defineProperty(t.prototype, 'hasFooter', {
              get: function () {
                return this.getPropertyValue('hasFooter', !1);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.updateHasFooter = function () {
              this.setPropertyValue('hasFooter', this.hasTotal);
            }),
            Object.defineProperty(t.prototype, 'hasTotal', {
              get: function () {
                for (var e = 0; e < this.columns.length; e++)
                  if (this.columns[e].hasTotal) return !0;
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getCellType = function () {
              return this.cellType;
            }),
            (t.prototype.getConditionJson = function (t, n) {
              if ((void 0 === t && (t = null), void 0 === n && (n = null), !n))
                return e.prototype.getConditionJson.call(this);
              for (var r = '', i = n.length - 1; i >= 0 && '.' != n[i]; i--) r = n[i] + r;
              var o = this.getColumnByName(r);
              if (!o) return null;
              var a = o.createCellQuestion(null);
              return a ? a.getConditionJson(t) : null;
            }),
            (t.prototype.clearIncorrectValues = function () {
              var e = this.visibleRows;
              if (e)
                for (var t = 0; t < e.length; t++) e[t].clearIncorrectValues(this.getRowValue(t));
            }),
            (t.prototype.clearErrors = function () {
              if ((e.prototype.clearErrors.call(this), this.generatedVisibleRows))
                for (var t = 0; t < this.generatedVisibleRows.length; t++)
                  for (var n = this.generatedVisibleRows[t], r = 0; r < n.cells.length; r++)
                    n.cells[r].question.clearErrors();
            }),
            (t.prototype.runCondition = function (t, n) {
              e.prototype.runCondition.call(this, t, n);
              var r,
                i = 0;
              do {
                (r = s.a.getUnbindValue(this.totalValue)),
                  this.runCellsCondition(t, n),
                  this.runTotalsCondition(t, n),
                  i++;
              } while (!s.a.isTwoValueEquals(r, this.totalValue) && i < 3);
            }),
            (t.prototype.shouldRunColumnExpression = function () {
              return !1;
            }),
            (t.prototype.runCellsCondition = function (e, t) {
              if (this.generatedVisibleRows) {
                for (
                  var n = this.getRowConditionValues(e), r = this.generatedVisibleRows, i = 0;
                  i < r.length;
                  i++
                )
                  r[i].runCondition(n, t);
                this.checkColumnsVisibility();
              }
            }),
            (t.prototype.checkColumnsVisibility = function () {
              for (var e = !1, t = 0; t < this.visibleColumns.length; t++)
                this.visibleColumns[t].visibleIf &&
                  (e = this.isColumnVisibilityChanged(this.visibleColumns[t]) || e);
              e && this.resetRenderedTable();
            }),
            (t.prototype.isColumnVisibilityChanged = function (e) {
              for (
                var t = e.hasVisibleCell, n = !1, r = this.generatedVisibleRows, i = 0;
                i < r.length;
                i++
              ) {
                var o = r[i].cells[e.index];
                if (o && o.question && o.question.isVisible) {
                  n = !0;
                  break;
                }
              }
              return t != n && (e.hasVisibleCell = n), t != n;
            }),
            (t.prototype.runTotalsCondition = function (e, t) {
              this.generatedTotalRow &&
                this.generatedTotalRow.runCondition(this.getRowConditionValues(e), t);
            }),
            (t.prototype.getRowConditionValues = function (e) {
              var t = e;
              t || (t = {});
              var n = {};
              return (
                s.a.isValueEmpty(this.totalValue) ||
                  (n = JSON.parse(JSON.stringify(this.totalValue))),
                (t.row = {}),
                (t.totalRow = n),
                t
              );
            }),
            (t.prototype.locStrsChanged = function () {
              e.prototype.locStrsChanged.call(this);
              var t = this.generatedVisibleRows;
              if (t) {
                for (var n = this.columns, r = 0; r < n.length; r++) n[r].locStrsChanged();
                for (var r = 0; r < t.length; r++) t[r].locStrsChanged();
                this.generatedTotalRow && this.generatedTotalRow.locStrsChanged();
              }
            }),
            (t.prototype.getColumnByName = function (e) {
              for (var t = 0; t < this.columns.length; t++)
                if (this.columns[t].name == e) return this.columns[t];
              return null;
            }),
            (t.prototype.getColumnName = function (e) {
              return this.getColumnByName(e);
            }),
            (t.prototype.getColumnWidth = function (e) {
              return e.minWidth ? e.minWidth : this.columnMinWidth;
            }),
            Object.defineProperty(t.prototype, 'choices', {
              get: function () {
                return this.getPropertyValue('choices');
              },
              set: function (e) {
                this.setPropertyValue('choices', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'optionsCaption', {
              get: function () {
                return this.getLocalizableStringText(
                  'optionsCaption',
                  c.a.getString('optionsCaption')
                );
              },
              set: function (e) {
                this.setLocalizableStringText('optionsCaption', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locOptionsCaption', {
              get: function () {
                return this.getLocalizableString('optionsCaption');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'storeOthersAsComment', {
              get: function () {
                return !!this.survey && this.survey.storeOthersAsComment;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.addColumn = function (e, t) {
              void 0 === t && (t = null);
              var n = new v(e, t);
              return this.columns.push(n), n;
            }),
            (t.prototype.getVisibleRows = function () {
              var e = this;
              return this.isLoadingFromJson
                ? null
                : (this.generatedVisibleRows ||
                    ((this.generatedVisibleRows = this.generateRows()),
                    this.generatedVisibleRows.forEach(function (t) {
                      return e.onMatrixRowCreated(t);
                    }),
                    this.data &&
                      this.runCellsCondition(
                        this.data.getFilteredValues(),
                        this.data.getFilteredProperties()
                      ),
                    this.updateValueOnRowsGeneration(this.generatedVisibleRows),
                    this.updateIsAnswered()),
                  this.generatedVisibleRows);
            }),
            (t.prototype.updateValueOnRowsGeneration = function (e) {
              for (
                var t = this.createNewValue(!0), n = this.createNewValue(), r = 0;
                r < e.length;
                r++
              ) {
                var i = e[r];
                if (!i.editingObj) {
                  var o = this.getRowValue(r),
                    a = i.value;
                  this.isTwoValueEquals(o, a) ||
                    (n = this.getNewValueOnRowChanged(i, '', a, !1, n).value);
                }
              }
              this.isTwoValueEquals(t, n) ||
                ((this.isRowChanging = !0), this.setNewValue(n), (this.isRowChanging = !1));
            }),
            Object.defineProperty(t.prototype, 'totalValue', {
              get: function () {
                return this.hasTotal && this.visibleTotalRow ? this.visibleTotalRow.value : {};
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getVisibleTotalRow = function () {
              if (this.isLoadingFromJson) return null;
              if (this.hasTotal) {
                if (
                  !this.generatedTotalRow &&
                  ((this.generatedTotalRow = this.generateTotalRow()), this.data)
                ) {
                  var e = { survey: this.survey };
                  this.runTotalsCondition(this.data.getAllValues(), e);
                }
              } else this.generatedTotalRow = null;
              return this.generatedTotalRow;
            }),
            Object.defineProperty(t.prototype, 'visibleTotalRow', {
              get: function () {
                return this.getVisibleTotalRow();
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onSurveyLoad = function () {
              e.prototype.onSurveyLoad.call(this),
                this.updateColumnsIndexes(this.columns),
                this.clearGeneratedRows(),
                (this.generatedTotalRow = null),
                this.updateHasFooter();
            }),
            (t.prototype.getRowValue = function (e) {
              if (e < 0) return null;
              var t = this.visibleRows;
              if (e >= t.length) return null;
              var n = this.createNewValue();
              return this.getRowValueCore(t[e], n);
            }),
            (t.prototype.setRowValue = function (e, t) {
              if (e < 0) return null;
              var n = this.visibleRows;
              if (e >= n.length) return null;
              (n[e].value = t), this.onRowChanged(n[e], '', t, !1);
            }),
            (t.prototype.generateRows = function () {
              return null;
            }),
            (t.prototype.generateTotalRow = function () {
              return new P(this);
            }),
            (t.prototype.createNewValue = function (e) {
              void 0 === e && (e = !1);
              var t = this.value ? this.createValueCopy() : {};
              return e && this.isMatrixValueEmpty(t) ? null : t;
            }),
            (t.prototype.getRowValueCore = function (e, t, n) {
              void 0 === n && (n = !1);
              var r = t && t[e.rowName] ? t[e.rowName] : null;
              return !r && n && ((r = {}), t && (t[e.rowName] = r)), r;
            }),
            (t.prototype.getRowObj = function (e) {
              var t = this.getRowValueCore(e, this.value);
              return t && t.getType ? t : null;
            }),
            (t.prototype.getRowDisplayValue = function (e, t) {
              if (!t) return t;
              if (e.editingObj) return t;
              for (var n in t) {
                var r = e.getQuestionByName(n);
                r || (r = this.getSharedQuestionByName(n, e)), r && (t[n] = r.displayValue);
              }
              return t;
            }),
            (t.prototype.getPlainData = function (t) {
              var n = this;
              void 0 === t && (t = { includeEmpty: !0 });
              var r = e.prototype.getPlainData.call(this, t);
              return (
                r &&
                  ((r.isNode = !0),
                  (r.data = this.visibleRows.map(function (e) {
                    var r = {
                      name: e.rowName,
                      title: e.rowName,
                      value: e.value,
                      displayValue: n.getRowDisplayValue(e, e.value),
                      getString: function (e) {
                        return 'object' == typeof e ? JSON.stringify(e) : e;
                      },
                      isNode: !0,
                      data: e.cells
                        .map(function (e) {
                          return e.question.getPlainData(t);
                        })
                        .filter(function (e) {
                          return !!e;
                        }),
                    };
                    return (
                      (t.calculations || []).forEach(function (t) {
                        r[t.propertyName] = e[t.propertyName];
                      }),
                      r
                    );
                  }))),
                r
              );
            }),
            (t.prototype.getProgressInfo = function () {
              return l.a.getProgressInfoByElements(this.getCellQuestions(), this.isRequired);
            }),
            (t.prototype.getCellQuestions = function () {
              var e = this.visibleRows;
              if (!e) return [];
              for (var t = [], n = 0; n < e.length; n++)
                for (var r = e[n], i = 0; i < r.cells.length; i++) t.push(r.cells[i].question);
              return t;
            }),
            (t.prototype.onBeforeValueChanged = function (e) {}),
            (t.prototype.onSetQuestionValue = function () {
              if (
                !this.isRowChanging &&
                (this.onBeforeValueChanged(this.value),
                this.generatedVisibleRows && 0 != this.generatedVisibleRows.length)
              ) {
                this.isRowChanging = !0;
                for (
                  var e = this.createNewValue(), t = 0;
                  t < this.generatedVisibleRows.length;
                  t++
                ) {
                  var n = this.generatedVisibleRows[t];
                  this.generatedVisibleRows[t].value = this.getRowValueCore(n, e);
                }
                this.isRowChanging = !1;
              }
            }),
            (t.prototype.setQuestionValue = function (t) {
              e.prototype.setQuestionValue.call(this, t, !1),
                this.onSetQuestionValue(),
                this.updateIsAnswered();
            }),
            (t.prototype.supportGoNextPageAutomatic = function () {
              var e = this.generatedVisibleRows;
              if ((e || (e = this.visibleRows), !e)) return !0;
              for (var t = 0; t < e.length; t++) {
                var n = this.generatedVisibleRows[t].cells;
                if (n)
                  for (var r = 0; r < n.length; r++) {
                    var i = n[r].question;
                    if (i && (!i.supportGoNextPageAutomatic() || !i.value)) return !1;
                  }
              }
              return !0;
            }),
            (t.prototype.getContainsErrors = function () {
              return (
                e.prototype.getContainsErrors.call(this) ||
                this.checkForAnswersOrErrors(function (e) {
                  return e.containsErrors;
                }, !1)
              );
            }),
            (t.prototype.getIsAnswered = function () {
              return (
                e.prototype.getIsAnswered.call(this) &&
                this.checkForAnswersOrErrors(function (e) {
                  return e.isAnswered;
                }, !0)
              );
            }),
            (t.prototype.checkForAnswersOrErrors = function (e, t) {
              void 0 === t && (t = !1);
              var n = this.generatedVisibleRows;
              if (!n) return !1;
              for (var r = 0; r < n.length; r++) {
                var i = n[r].cells;
                if (i)
                  for (var o = 0; o < i.length; o++)
                    if (i[o]) {
                      var a = i[o].question;
                      if (a && a.isVisible)
                        if (e(a)) {
                          if (!t) return !0;
                        } else if (t) return !1;
                    }
              }
              return !!t;
            }),
            (t.prototype.hasErrors = function (t, n) {
              void 0 === t && (t = !0), void 0 === n && (n = null);
              var r = this.hasErrorInRows(t, n);
              return e.prototype.hasErrors.call(this, t, n) || r;
            }),
            (t.prototype.getIsRunningValidators = function () {
              if (e.prototype.getIsRunningValidators.call(this)) return !0;
              if (!this.generatedVisibleRows) return !1;
              for (var t = 0; t < this.generatedVisibleRows.length; t++) {
                var n = this.generatedVisibleRows[t].cells;
                if (n)
                  for (var r = 0; r < n.length; r++)
                    if (n[r]) {
                      var i = n[r].question;
                      if (i && i.isRunningValidators) return !0;
                    }
              }
              return !1;
            }),
            (t.prototype.getAllErrors = function () {
              var t = e.prototype.getAllErrors.call(this),
                n = this.generatedVisibleRows;
              if (null === n) return t;
              for (var r = 0; r < n.length; r++)
                for (var i = n[r], o = 0; o < i.cells.length; o++) {
                  var a = i.cells[o].question.getAllErrors();
                  a && a.length > 0 && (t = t.concat(a));
                }
              return t;
            }),
            (t.prototype.hasErrorInRows = function (e, t) {
              var n = this;
              if (!this.generatedVisibleRows) return !1;
              var r = !1;
              t || (t = {}), (t.isSingleDetailPanel = 'underRowSingle' === this.detailPanelMode);
              for (var i = 0; i < this.generatedVisibleRows.length; i++)
                r =
                  this.generatedVisibleRows[i].hasErrors(e, t, function () {
                    n.raiseOnCompletedAsyncValidators();
                  }) || r;
              return r;
            }),
            (t.prototype.getFirstInputElementId = function () {
              var t = this.getFirstCellQuestion(!1);
              return t ? t.inputId : e.prototype.getFirstInputElementId.call(this);
            }),
            (t.prototype.getFirstErrorInputElementId = function () {
              var t = this.getFirstCellQuestion(!0);
              return t ? t.inputId : e.prototype.getFirstErrorInputElementId.call(this);
            }),
            (t.prototype.getFirstCellQuestion = function (e) {
              if (!this.generatedVisibleRows) return null;
              for (var t = 0; t < this.generatedVisibleRows.length; t++)
                for (var n = this.generatedVisibleRows[t].cells, r = 0; r < n.length; r++) {
                  if (!e) return n[r].question;
                  if (n[r].question.currentErrorCount > 0) return n[r].question;
                }
              return null;
            }),
            (t.prototype.onReadOnlyChanged = function () {
              if ((e.prototype.onReadOnlyChanged.call(this), this.generateRows))
                for (var t = 0; t < this.visibleRows.length; t++)
                  this.visibleRows[t].onQuestionReadOnlyChanged(this.isReadOnly);
            }),
            (t.prototype.createQuestion = function (e, t) {
              return this.createQuestionCore(e, t);
            }),
            (t.prototype.createQuestionCore = function (e, t) {
              var n = t.createCellQuestion(e);
              return (
                this.isReadOnly && (n.readOnly = !0),
                n.setSurveyImpl(e),
                n.setParentQuestion(this),
                n
              );
            }),
            (t.prototype.deleteRowValue = function (e, t) {
              return e
                ? (delete e[t.rowName], this.isObject(e) && 0 == Object.keys(e).length ? null : e)
                : e;
            }),
            (t.prototype.onAnyValueChanged = function (e) {
              if (
                !this.isLoadingFromJson &&
                !this.isDoingonAnyValueChanged &&
                this.generatedVisibleRows
              ) {
                this.isDoingonAnyValueChanged = !0;
                for (var t = this.visibleRows, n = 0; n < t.length; n++) t[n].onAnyValueChanged(e);
                var r = this.visibleTotalRow;
                r && r.onAnyValueChanged(e), (this.isDoingonAnyValueChanged = !1);
              }
            }),
            (t.prototype.isObject = function (e) {
              return null !== e && 'object' == typeof e;
            }),
            (t.prototype.getOnCellValueChangedOptions = function (e, t, n) {
              var r = this,
                i = function (t) {
                  for (var n = 0; r.columns.length; n++)
                    if (r.columns[n].name === t) return e.cells[n].question;
                  return null;
                };
              return {
                row: e,
                columnName: t,
                rowValue: n,
                value: n ? n[t] : null,
                getCellQuestion: i,
              };
            }),
            (t.prototype.onCellValueChanged = function (e, t, n) {
              if (this.survey) {
                var r = this.getOnCellValueChangedOptions(e, t, n);
                this.onCellValueChangedCallback && this.onCellValueChangedCallback(r),
                  this.survey.matrixCellValueChanged(this, r);
              }
            }),
            (t.prototype.validateCell = function (e, t, n) {
              if (this.survey) {
                var r = this,
                  i = function (t) {
                    for (var n = 0; r.columns.length; n++)
                      if (r.columns[n].name === t) return e.cells[n].question;
                    return null;
                  },
                  o = { row: e, columnName: t, rowValue: n, value: n[t], getCellQuestion: i };
                return this.survey.matrixCellValidate(this, o);
              }
            }),
            Object.defineProperty(t.prototype, 'isValidateOnValueChanging', {
              get: function () {
                return !!this.survey && this.survey.isValidateOnValueChanging;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onRowChanging = function (e, t, n) {
              if (!this.survey) return n ? n[t] : null;
              var r = this.getOnCellValueChangedOptions(e, t, n),
                i = this.getRowValueCore(e, this.createNewValue(), !0);
              return (
                (r.oldValue = i ? i[t] : null),
                this.survey.matrixCellValueChanging(this, r),
                r.value
              );
            }),
            (t.prototype.onRowChanged = function (e, t, n, r) {
              var i = t ? this.getRowObj(e) : null;
              if (i) {
                var o = null;
                n && !r && (o = n[t]),
                  (this.isRowChanging = !0),
                  (i[t] = o),
                  (this.isRowChanging = !1),
                  this.onCellValueChanged(e, t, i);
              } else {
                var a = this.createNewValue(!0),
                  s = this.getNewValueOnRowChanged(e, t, n, r, this.createNewValue());
                if (this.isTwoValueEquals(a, s.value)) return;
                (this.isRowChanging = !0),
                  this.setNewValue(s.value),
                  (this.isRowChanging = !1),
                  t && this.onCellValueChanged(e, t, s.rowValue);
              }
            }),
            (t.prototype.getNewValueOnRowChanged = function (e, t, n, r, i) {
              var o = this.getRowValueCore(e, i, !0);
              r && delete o[t];
              for (var a = 0; a < e.cells.length; a++) {
                var s = e.cells[a].question.getValueName();
                delete o[s];
              }
              if (n) {
                n = JSON.parse(JSON.stringify(n));
                for (var s in n) this.isValueEmpty(n[s]) || (o[s] = n[s]);
              }
              return (
                this.isObject(o) && 0 === Object.keys(o).length && (i = this.deleteRowValue(i, e)),
                { value: i, rowValue: o }
              );
            }),
            (t.prototype.getRowIndex = function (e) {
              return this.generatedVisibleRows ? this.visibleRows.indexOf(e) : -1;
            }),
            (t.prototype.getElementsInDesign = function (t) {
              return (
                void 0 === t && (t = !1),
                'none' == this.detailPanelMode
                  ? e.prototype.getElementsInDesign.call(this, t)
                  : t
                  ? [this.detailPanel]
                  : this.detailElements
              );
            }),
            (t.prototype.hasDetailPanel = function (e) {
              return (
                'none' != this.detailPanelMode &&
                (!!this.isDesignMode ||
                  (this.onHasDetailPanelCallback
                    ? this.onHasDetailPanelCallback(e)
                    : this.detailElements.length > 0))
              );
            }),
            (t.prototype.getIsDetailPanelShowing = function (e) {
              if ('none' == this.detailPanelMode) return !1;
              if (this.isDesignMode) {
                var t = 0 == this.visibleRows.indexOf(e);
                return t && (e.detailPanel || e.showDetailPanel()), t;
              }
              return this.getPropertyValue('isRowShowing' + e.id, !1);
            }),
            (t.prototype.setIsDetailPanelShowing = function (e, t) {
              if (
                t != this.getIsDetailPanelShowing(e) &&
                (this.setPropertyValue('isRowShowing' + e.id, t),
                this.updateDetailPanelButtonCss(e),
                this.renderedTable && this.renderedTable.onDetailPanelChangeVisibility(e, t),
                t && 'underRowSingle' === this.detailPanelMode)
              )
                for (var n = this.visibleRows, r = 0; r < n.length; r++)
                  n[r].id !== e.id && n[r].isDetailPanelShowing && n[r].hideDetailPanel();
            }),
            (t.prototype.getDetailPanelButtonCss = function (e) {
              var t = this.getPropertyValue('detailButtonCss' + e.id);
              if (t) return t;
              var t = this.cssClasses.detailButton;
              return t || '';
            }),
            (t.prototype.getDetailPanelIconCss = function (e) {
              var t = this.getPropertyValue('detailIconCss' + e.id);
              if (t) return t;
              var t = this.cssClasses.detailIcon;
              return t || '';
            }),
            (t.prototype.updateDetailPanelButtonCss = function (e) {
              var t = this.cssClasses,
                n = t.detailIcon;
              n || (n = '');
              var r = t.detailButton;
              r || (r = ''),
                this.getIsDetailPanelShowing(e) &&
                  (t.detailIconExpanded && (n += ' ' + t.detailIconExpanded),
                  t.detailButtonExpanded && (r += ' ' + t.detailButtonExpanded)),
                this.setPropertyValue('detailIconCss' + e.id, n),
                this.setPropertyValue('detailButtonCss' + e.id, r);
            }),
            (t.prototype.createRowDetailPanel = function (e) {
              if (this.isDesignMode) return this.detailPanel;
              var t = this.createNewDetailPanel(),
                n = this.detailPanel.toJSON();
              return (
                new o.c().toObject(n, t),
                (t.renderWidth = '100%'),
                t.updateCustomWidgets(),
                this.onCreateDetailPanelCallback && this.onCreateDetailPanelCallback(e, t),
                t
              );
            }),
            (t.prototype.getSharedQuestionByName = function (e, t) {
              if (!this.survey || !this.valueName) return null;
              var n = this.getRowIndex(t);
              return n < 0
                ? null
                : this.survey.getQuestionByValueNameFromArray(this.valueName, e, n);
            }),
            (t.prototype.onTotalValueChanged = function () {
              this.data &&
                this.visibleTotalRow &&
                !this.isLoadingFromJson &&
                !this.isSett &&
                this.data.setValue(
                  this.getValueName() + g.a.matrixTotalValuePostFix,
                  this.totalValue,
                  !1
                );
            }),
            (t.prototype.getQuestionFromArray = function (e, t) {
              return t >= this.visibleRows.length ? null : this.visibleRows[t].getQuestionByName(e);
            }),
            (t.prototype.isMatrixValueEmpty = function (e) {
              if (e) {
                if (Array.isArray(e)) {
                  for (var t = 0; t < e.length; t++)
                    if (this.isObject(e[t]) && Object.keys(e[t]).length > 0) return !1;
                  return !0;
                }
                return 0 == Object.keys(e).length;
              }
            }),
            (t.prototype.getSurvey = function () {
              return this.survey;
            }),
            t
          );
        })(a.a);
      o.b.addClass(
        'matrixdropdowncolumn',
        [
          { name: '!name', isUnique: !0 },
          { name: 'title', serializationProperty: 'locTitle' },
          {
            name: 'cellType',
            default: 'default',
            choices: function () {
              var e = v.getColumnTypes();
              return e.splice(0, 0, 'default'), e;
            },
          },
          { name: 'colCount', default: -1, choices: [-1, 0, 1, 2, 3, 4] },
          'isRequired:boolean',
          { name: 'requiredErrorText:text', serializationProperty: 'locRequiredErrorText' },
          'readOnly:boolean',
          'minWidth',
          'width',
          'visibleIf:condition',
          'enableIf:condition',
          'requiredIf:condition',
          {
            name: 'showInMultipleColumns:boolean',
            dependsOn: 'cellType',
            visibleIf: function (e) {
              return !!e && e.isSupportMultipleColumns;
            },
          },
          {
            name: 'validators:validators',
            baseClassName: 'surveyvalidator',
            classNamePart: 'validator',
          },
          {
            name: 'totalType',
            default: 'none',
            choices: ['none', 'sum', 'count', 'min', 'max', 'avg'],
          },
          'totalExpression:expression',
          { name: 'totalFormat', serializationProperty: 'locTotalFormat' },
          {
            name: 'totalDisplayStyle',
            default: 'none',
            choices: ['none', 'decimal', 'currency', 'percent'],
          },
          {
            name: 'totalCurrency',
            choices: function () {
              return n.i(h.b)();
            },
            default: 'USD',
          },
          { name: 'totalMaximumFractionDigits:number', default: -1 },
          { name: 'totalMinimumFractionDigits:number', default: -1 },
          { name: 'renderAs', default: 'default', visible: !1 },
        ],
        function () {
          return new v('');
        }
      ),
        o.b.addClass(
          'matrixdropdownbase',
          [
            { name: 'columns:matrixdropdowncolumns', className: 'matrixdropdowncolumn' },
            {
              name: 'columnLayout',
              alternativeName: 'columnsLocation',
              default: 'horizontal',
              choices: ['horizontal', 'vertical'],
            },
            { name: 'detailElements', visible: !1, isLightSerializable: !1 },
            {
              name: 'detailPanelMode',
              choices: ['none', 'underRow', 'underRowSingle'],
              default: 'none',
            },
            'horizontalScroll:boolean',
            { name: 'choices:itemvalue[]' },
            { name: 'optionsCaption', serializationProperty: 'locOptionsCaption' },
            {
              name: 'cellType',
              default: 'dropdown',
              choices: function () {
                return v.getColumnTypes();
              },
            },
            { name: 'columnColCount', default: 0, choices: [0, 1, 2, 3, 4] },
            'columnMinWidth',
          ],
          function () {
            return new T('');
          },
          'matrixbase'
        );
    },
    function (e, t, n) {
      'use strict';
      var r = n(4),
        i = n(16);
      n.d(t, 'a', function () {
        return s;
      }),
        n.d(t, 'b', function () {
          return l;
        });
      var o = (function () {
          function e() {}
          return e;
        })(),
        a = (function () {
          function e(e, t) {
            (this.name = e),
              (this.returnDisplayValue = t),
              (this.isExists = !1),
              (this.canProcess = !0);
          }
          return e;
        })(),
        s = (function () {
          function e() {}
          return (
            (e.prototype.process = function (e, t, n) {
              if (
                (void 0 === t && (t = !1),
                void 0 === n && (n = !1),
                (this.hasAllValuesOnLastRunValue = !0),
                !e)
              )
                return e;
              if (!this.onProcess) return e;
              for (var i = this.getItems(e), o = i.length - 1; o >= 0; o--) {
                var s = i[o],
                  l = this.getName(e.substring(s.start + 1, s.end));
                if (l) {
                  var u = new a(l, t);
                  if ((this.onProcess(u), u.isExists)) {
                    r.a.isValueEmpty(u.value) && (this.hasAllValuesOnLastRunValue = !1);
                    var c = r.a.isValueEmpty(u.value) ? '' : u.value;
                    n && (c = encodeURIComponent(c)),
                      (e = e.substr(0, s.start) + c + e.substr(s.end + 1));
                  } else u.canProcess && (this.hasAllValuesOnLastRunValue = !1);
                }
              }
              return e;
            }),
            (e.prototype.processValue = function (e, t) {
              var n = new a(e, t);
              return this.onProcess && this.onProcess(n), n;
            }),
            Object.defineProperty(e.prototype, 'hasAllValuesOnLastRun', {
              get: function () {
                return !!this.hasAllValuesOnLastRunValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getItems = function (e) {
              for (var t = [], n = e.length, r = -1, i = '', a = 0; a < n; a++)
                if (((i = e[a]), '{' == i && (r = a), '}' == i)) {
                  if (r > -1) {
                    var s = new o();
                    (s.start = r), (s.end = a), t.push(s);
                  }
                  r = -1;
                }
              return t;
            }),
            (e.prototype.getName = function (e) {
              if (e) return e.trim();
            }),
            e
          );
        })(),
        l = (function () {
          function e(e) {
            var t = this;
            (this.variableName = e),
              (this.textPreProcessor = new s()),
              (this.textPreProcessor.onProcess = function (e) {
                t.getProcessedTextValue(e);
              });
          }
          return (
            (e.prototype.processValue = function (e, t) {
              return this.textPreProcessor.processValue(e, t);
            }),
            Object.defineProperty(e.prototype, 'survey', {
              get: function () {
                return null;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'panel', {
              get: function () {
                return null;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getValues = function () {
              return this.panel ? this.panel.getValue() : null;
            }),
            (e.prototype.getQuestionByName = function (e) {
              return this.panel ? this.panel.getQuestionByValueName(e) : null;
            }),
            (e.prototype.onCustomProcessText = function (e) {
              return !1;
            }),
            (e.prototype.getProcessedTextValue = function (e) {
              if (e && !this.onCustomProcessText(e)) {
                var t = new i.a().getFirstName(e.name);
                if (
                  ((e.isExists = t == this.variableName), (e.canProcess = e.isExists), e.canProcess)
                ) {
                  e.name = e.name.replace(this.variableName + '.', '');
                  var t = new i.a().getFirstName(e.name),
                    n = this.getQuestionByName(t),
                    r = {};
                  if (n) r[t] = e.returnDisplayValue ? n.displayValue : n.value;
                  else {
                    var o = this.panel ? this.getValues() : null;
                    o && (r[t] = o[t]);
                  }
                  e.value = new i.a().getValue(e.name, r);
                }
              }
            }),
            (e.prototype.processText = function (e, t) {
              e = this.textPreProcessor.process(e, t);
              var n = this.survey;
              return n ? n.processText(e, t) : e;
            }),
            (e.prototype.processTextEx = function (e, t) {
              e = this.processText(e, t);
              var n = this.textPreProcessor.hasAllValuesOnLastRun,
                r = { hasAllValuesOnLastRun: !0, text: e };
              return (
                this.survey && (r = this.survey.processTextEx(e, t, !1)),
                (r.hasAllValuesOnLastRun = r.hasAllValuesOnLastRun && n),
                r
              );
            }),
            e
          );
        })();
    },
    function (e, t, n) {
      'use strict';
      function r(e) {
        return l.a && l.a.confirmActionFunc ? l.a.confirmActionFunc(e) : confirm(e);
      }
      function i() {
        if ('undefined' == typeof window) return !1;
        if (void 0 === i.isIEOrEdge) {
          var e = window.navigator.userAgent,
            t = e.indexOf('MSIE '),
            n = e.indexOf('Trident/'),
            r = e.indexOf('Edge/');
          i.isIEOrEdge = r > 0 || n > 0 || t > 0;
        }
        return i.isIEOrEdge;
      }
      function o(e, t) {
        try {
          for (
            var n = atob(e.split(',')[1]),
              r = e.split(',')[0].split(':')[1].split(';')[0],
              i = new ArrayBuffer(n.length),
              o = new Uint8Array(i),
              a = 0;
            a < n.length;
            a++
          )
            o[a] = n.charCodeAt(a);
          var s = new Blob([i], { type: r });
          'undefined' != typeof window &&
            window.navigator &&
            window.navigator.msSaveBlob &&
            window.navigator.msSaveOrOpenBlob(s, t);
        } catch (e) {}
      }
      function a(e, t, n) {
        if (
          (void 0 === t && (t = 0), void 0 === n && (n = 'visible'), 'undefined' == typeof document)
        )
          return !1;
        var r = e.getBoundingClientRect(),
          i = Math.max(document.documentElement.clientHeight, window.innerHeight),
          o = r.bottom - t < 0,
          a = r.top - i + t >= 0;
        return 'above' === n ? o : 'below' === n ? a : !o && !a;
      }
      function s(e) {
        return e
          ? e.scrollHeight > e.clientHeight &&
            ('scroll' === getComputedStyle(e).overflowY || 'auto' === getComputedStyle(e).overflowY)
            ? e === document.body
              ? window
              : e
            : s(e.parentElement)
          : window;
      }
      var l = n(7);
      n.d(t, 'c', function () {
        return r;
      }),
        n.d(t, 'd', function () {
          return i;
        }),
        n.d(t, 'e', function () {
          return o;
        }),
        n.d(t, 'b', function () {
          return a;
        }),
        n.d(t, 'a', function () {
          return s;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(4),
        o = n(28),
        a = n(16);
      n.d(t, 'g', function () {
        return s;
      }),
        n.d(t, 'f', function () {
          return l;
        }),
        n.d(t, 'b', function () {
          return u;
        }),
        n.d(t, 'e', function () {
          return c;
        }),
        n.d(t, 'c', function () {
          return p;
        }),
        n.d(t, 'd', function () {
          return d;
        }),
        n.d(t, 'a', function () {
          return h;
        }),
        n.d(t, 'h', function () {
          return f;
        });
      var s = (function () {
          function e() {}
          return (
            (e.prototype.toString = function (e) {
              return void 0 === e && (e = void 0), '';
            }),
            (e.prototype.hasFunction = function () {
              return !1;
            }),
            (e.prototype.hasAsyncFunction = function () {
              return !1;
            }),
            (e.prototype.addToAsyncList = function (e) {}),
            e
          );
        })(),
        l = (function (e) {
          function t(t, n, r, i) {
            void 0 === n && (n = null), void 0 === r && (r = null), void 0 === i && (i = !1);
            var o = e.call(this) || this;
            return (
              (o.operatorName = t),
              (o.left = n),
              (o.right = r),
              (o.isArithmeticValue = i),
              (o.consumer = i ? f.binaryFunctions.arithmeticOp(t) : f.binaryFunctions[t]),
              null == o.consumer && f.throwInvalidOperatorError(t),
              o
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'binary';
            }),
            Object.defineProperty(t.prototype, 'isArithmetic', {
              get: function () {
                return this.isArithmeticValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isConjunction', {
              get: function () {
                return 'or' == this.operatorName || 'and' == this.operatorName;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'conjunction', {
              get: function () {
                return this.isConjunction ? this.operatorName : '';
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'operator', {
              get: function () {
                return this.operatorName;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'leftOperand', {
              get: function () {
                return this.left;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'rightOperand', {
              get: function () {
                return this.right;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.evaluateParam = function (e, t) {
              return null == e ? null : e.evaluate(t);
            }),
            (t.prototype.evaluate = function (e) {
              return this.consumer.call(
                this,
                this.evaluateParam(this.left, e),
                this.evaluateParam(this.right, e)
              );
            }),
            (t.prototype.toString = function (e) {
              if ((void 0 === e && (e = void 0), e)) {
                var t = e(this);
                if (t) return t;
              }
              return (
                '(' +
                f.safeToString(this.left, e) +
                ' ' +
                f.operatorToString(this.operatorName) +
                ' ' +
                f.safeToString(this.right, e) +
                ')'
              );
            }),
            (t.prototype.setVariables = function (e) {
              null != this.left && this.left.setVariables(e),
                null != this.right && this.right.setVariables(e);
            }),
            (t.prototype.hasFunction = function () {
              return (
                (!!this.left && this.left.hasFunction()) ||
                (!!this.right && this.right.hasFunction())
              );
            }),
            (t.prototype.hasAsyncFunction = function () {
              return (
                (!!this.left && this.left.hasAsyncFunction()) ||
                (!!this.right && this.right.hasAsyncFunction())
              );
            }),
            (t.prototype.addToAsyncList = function (e) {
              this.left && this.left.addToAsyncList(e), this.right && this.right.addToAsyncList(e);
            }),
            t
          );
        })(s),
        u = (function (e) {
          function t(t, n) {
            var r = e.call(this) || this;
            return (
              (r.expressionValue = t),
              (r.operatorName = n),
              (r.consumer = f.unaryFunctions[n]),
              null == r.consumer && f.throwInvalidOperatorError(n),
              r
            );
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'operator', {
              get: function () {
                return this.operatorName;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'expression', {
              get: function () {
                return this.expressionValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getType = function () {
              return 'unary';
            }),
            (t.prototype.toString = function (e) {
              if ((void 0 === e && (e = void 0), e)) {
                var t = e(this);
                if (t) return t;
              }
              return f.operatorToString(this.operatorName) + ' ' + this.expression.toString(e);
            }),
            (t.prototype.evaluate = function (e) {
              var t = this.expression.evaluate(e);
              return this.consumer.call(this, t);
            }),
            (t.prototype.setVariables = function (e) {
              this.expression.setVariables(e);
            }),
            t
          );
        })(s),
        c = (function (e) {
          function t(t) {
            var n = e.call(this) || this;
            return (n.values = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'array';
            }),
            (t.prototype.toString = function (e) {
              if ((void 0 === e && (e = void 0), e)) {
                var t = e(this);
                if (t) return t;
              }
              return (
                '[' +
                this.values
                  .map(function (t) {
                    return t.toString(e);
                  })
                  .join(', ') +
                ']'
              );
            }),
            (t.prototype.evaluate = function (e) {
              return this.values.map(function (t) {
                return t.evaluate(e);
              });
            }),
            (t.prototype.setVariables = function (e) {
              this.values.forEach(function (t) {
                t.setVariables(e);
              });
            }),
            (t.prototype.hasFunction = function () {
              return this.values.some(function (e) {
                return e.hasFunction();
              });
            }),
            (t.prototype.hasAsyncFunction = function () {
              return this.values.some(function (e) {
                return e.hasAsyncFunction();
              });
            }),
            (t.prototype.addToAsyncList = function (e) {
              this.values.forEach(function (t) {
                return t.addToAsyncList(e);
              });
            }),
            t
          );
        })(s),
        p = (function (e) {
          function t(t) {
            var n = e.call(this) || this;
            return (n.value = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'const';
            }),
            (t.prototype.toString = function (e) {
              if ((void 0 === e && (e = void 0), e)) {
                var t = e(this);
                if (t) return t;
              }
              return this.value.toString();
            }),
            Object.defineProperty(t.prototype, 'correctValue', {
              get: function () {
                return this.getCorrectValue(this.value);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.evaluate = function () {
              return this.getCorrectValue(this.value);
            }),
            (t.prototype.setVariables = function (e) {}),
            (t.prototype.getCorrectValue = function (e) {
              return e && 'string' == typeof e
                ? this.isBooleanValue(e)
                  ? 'true' === e.toLowerCase()
                  : e.length > 1 && this.isQuote(e[0]) && this.isQuote(e[e.length - 1])
                  ? e.substr(1, e.length - 2)
                  : f.isNumeric(e)
                  ? 0 == e.indexOf('0x')
                    ? parseInt(e)
                    : e.length > 1 && '0' == e[0]
                    ? e
                    : parseFloat(e)
                  : e
                : e;
            }),
            (t.prototype.isQuote = function (e) {
              return "'" == e || '"' == e;
            }),
            (t.prototype.isBooleanValue = function (e) {
              return e && ('true' === e.toLowerCase() || 'false' === e.toLowerCase());
            }),
            t
          );
        })(s),
        d = (function (e) {
          function t(n) {
            var r = e.call(this, n) || this;
            return (
              (r.variableName = n),
              (r.valueInfo = {}),
              (r.useValueAsItIs = !1),
              r.variableName &&
                r.variableName.length > 1 &&
                r.variableName[0] === t.DisableConversionChar &&
                ((r.variableName = r.variableName.substr(1)), (r.useValueAsItIs = !0)),
              r
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'variable';
            }),
            (t.prototype.toString = function (e) {
              if ((void 0 === e && (e = void 0), e)) {
                var n = e(this);
                if (n) return n;
              }
              return (
                '{' + (this.useValueAsItIs ? t.DisableConversionChar : '') + this.variableName + '}'
              );
            }),
            Object.defineProperty(t.prototype, 'variable', {
              get: function () {
                return this.variableName;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.evaluate = function (e) {
              return (
                (this.valueInfo.name = this.variableName),
                e.getValueInfo(this.valueInfo),
                this.valueInfo.hasValue ? this.getCorrectValue(this.valueInfo.value) : null
              );
            }),
            (t.prototype.setVariables = function (e) {
              e.push(this.variableName);
            }),
            (t.prototype.getCorrectValue = function (t) {
              return this.useValueAsItIs ? t : e.prototype.getCorrectValue.call(this, t);
            }),
            (t.DisableConversionChar = '#'),
            t
          );
        })(p),
        h = (function (e) {
          function t(t, n) {
            var r = e.call(this) || this;
            return (
              (r.origionalValue = t),
              (r.parameters = n),
              (r.isReadyValue = !1),
              Array.isArray(n) && 0 === n.length && (r.parameters = new c([])),
              r
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'function';
            }),
            (t.prototype.evaluateAsync = function (e) {
              var t = this;
              this.isReadyValue = !1;
              var n = new a.a();
              (n.values = i.a.createCopy(e.values)),
                (n.properties = i.a.createCopy(e.properties)),
                (n.properties.returnResult = function (e) {
                  (t.asynResult = e), (t.isReadyValue = !0), t.onAsyncReady();
                }),
                this.evaluateCore(n);
            }),
            (t.prototype.evaluate = function (e) {
              return this.isReady ? this.asynResult : this.evaluateCore(e);
            }),
            (t.prototype.evaluateCore = function (e) {
              return o.a.Instance.run(
                this.origionalValue,
                this.parameters.evaluate(e),
                e.properties
              );
            }),
            (t.prototype.toString = function (e) {
              if ((void 0 === e && (e = void 0), e)) {
                var t = e(this);
                if (t) return t;
              }
              return this.origionalValue + '(' + this.parameters.toString(e) + ')';
            }),
            (t.prototype.setVariables = function (e) {
              this.parameters.setVariables(e);
            }),
            Object.defineProperty(t.prototype, 'isReady', {
              get: function () {
                return this.isReadyValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.hasFunction = function () {
              return !0;
            }),
            (t.prototype.hasAsyncFunction = function () {
              return o.a.Instance.isAsyncFunction(this.origionalValue);
            }),
            (t.prototype.addToAsyncList = function (e) {
              this.hasAsyncFunction() && e.push(this);
            }),
            t
          );
        })(s),
        f = (function () {
          function e() {}
          return (
            (e.throwInvalidOperatorError = function (e) {
              throw new Error("Invalid operator: '" + e + "'");
            }),
            (e.safeToString = function (e, t) {
              return null == e ? '' : e.toString(t);
            }),
            (e.toOperandString = function (t) {
              return !t || e.isNumeric(t) || e.isBooleanValue(t) || (t = "'" + t + "'"), t;
            }),
            (e.isSpaceString = function (e) {
              return !!e && !e.replace(' ', '');
            }),
            (e.isNumeric = function (t) {
              return (
                (!t ||
                  !(
                    t.indexOf('-') > -1 ||
                    t.indexOf('+') > 1 ||
                    t.indexOf('*') > -1 ||
                    t.indexOf('^') > -1 ||
                    t.indexOf('/') > -1 ||
                    t.indexOf('%') > -1
                  )) &&
                !e.isSpaceString(t) &&
                i.a.isNumber(t)
              );
            }),
            (e.isBooleanValue = function (e) {
              return !!e && ('true' === e.toLowerCase() || 'false' === e.toLowerCase());
            }),
            (e.isTwoValueEquals = function (e, t) {
              return (
                'undefined' === e && (e = void 0),
                'undefined' === t && (t = void 0),
                i.a.isTwoValueEquals(e, t, !0)
              );
            }),
            (e.operatorToString = function (t) {
              var n = e.signs[t];
              return null == n ? t : n;
            }),
            (e.unaryFunctions = {
              empty: function (e) {
                return i.a.isValueEmpty(e);
              },
              notempty: function (t) {
                return !e.unaryFunctions.empty(t);
              },
              negate: function (e) {
                return !e;
              },
            }),
            (e.binaryFunctions = {
              arithmeticOp: function (t) {
                return function (n, r) {
                  i.a.isValueEmpty(n) && !e.isSpaceString(n) && (n = 'string' == typeof r ? '' : 0),
                    i.a.isValueEmpty(r) &&
                      !e.isSpaceString(r) &&
                      (r = 'string' == typeof n ? '' : 0);
                  var o = e.binaryFunctions[t];
                  return null == o ? null : o.call(this, n, r);
                };
              },
              and: function (e, t) {
                return e && t;
              },
              or: function (e, t) {
                return e || t;
              },
              plus: function (e, t) {
                return e + t;
              },
              minus: function (e, t) {
                return e - t;
              },
              mul: function (e, t) {
                return e * t;
              },
              div: function (e, t) {
                return t ? e / t : null;
              },
              mod: function (e, t) {
                return t ? e % t : null;
              },
              power: function (e, t) {
                return Math.pow(e, t);
              },
              greater: function (e, t) {
                return null != e && null != t && e > t;
              },
              less: function (e, t) {
                return null != e && null != t && e < t;
              },
              greaterorequal: function (t, n) {
                return !!e.binaryFunctions.equal(t, n) || e.binaryFunctions.greater(t, n);
              },
              lessorequal: function (t, n) {
                return !!e.binaryFunctions.equal(t, n) || e.binaryFunctions.less(t, n);
              },
              equal: function (t, n) {
                return e.isTwoValueEquals(t, n);
              },
              notequal: function (t, n) {
                return !e.binaryFunctions.equal(t, n);
              },
              contains: function (t, n) {
                return e.binaryFunctions.containsCore(t, n, !0);
              },
              notcontains: function (t, n) {
                return (!t && !i.a.isValueEmpty(n)) || e.binaryFunctions.containsCore(t, n, !1);
              },
              anyof: function (t, n) {
                if (!t && i.a.isValueEmpty(n)) return !0;
                if (!t || (!Array.isArray(t) && 0 === t.length)) return !1;
                if (i.a.isValueEmpty(n)) return !0;
                if (!Array.isArray(t)) return e.binaryFunctions.contains(n, t);
                if (!Array.isArray(n)) return e.binaryFunctions.contains(t, n);
                for (var r = 0; r < n.length; r++)
                  if (e.binaryFunctions.contains(t, n[r])) return !0;
                return !1;
              },
              allof: function (t, n) {
                if (!t && !i.a.isValueEmpty(n)) return !1;
                if (!Array.isArray(n)) return e.binaryFunctions.contains(t, n);
                for (var r = 0; r < n.length; r++)
                  if (!e.binaryFunctions.contains(t, n[r])) return !1;
                return !0;
              },
              containsCore: function (t, n, r) {
                if (!t) return !1;
                if ((t.length || (t = t.toString()), 'string' == typeof t || t instanceof String)) {
                  if (!n) return !1;
                  n = n.toString();
                  var i = t.indexOf(n) > -1;
                  return r ? i : !i;
                }
                for (var o = Array.isArray(n) ? n : [n], a = 0; a < o.length; a++) {
                  var s = 0;
                  for (n = o[a]; s < t.length && !e.isTwoValueEquals(t[s], n); s++);
                  if (s == t.length) return !r;
                }
                return r;
              },
            }),
            (e.signs = {
              less: '<',
              lessorequal: '<=',
              greater: '>',
              greaterorequal: '>=',
              equal: '==',
              notequal: '!=',
              plus: '+',
              minus: '-',
              mul: '*',
              div: '/',
              and: 'and',
              or: 'or',
              power: '^',
              mod: '%',
              negate: '!',
            }),
            e
          );
        })();
    },
    function (e, t, n) {
      'use strict';
      function r(e, t) {
        if (void 0 !== e && null !== e)
          if (Array.isArray(e)) for (var n = 0; n < e.length; n++) r(e[n], t);
          else V.a.isNumber(e) && (e = parseFloat(e)), t.push(e);
      }
      function i(e) {
        var t = [];
        r(e, t);
        for (var n = 0, i = 0; i < t.length; i++) n += t[i];
        return n;
      }
      function o(e, t) {
        var n = [];
        r(e, n);
        for (var i = void 0, o = 0; o < n.length; o++)
          void 0 === i && (i = n[o]), t ? i > n[o] && (i = n[o]) : i < n[o] && (i = n[o]);
        return i;
      }
      function a(e) {
        return o(e, !0);
      }
      function s(e) {
        return o(e, !1);
      }
      function l(e) {
        var t = [];
        return r(e, t), t.length;
      }
      function u(e) {
        var t = [];
        r(e, t);
        for (var n = 0, i = 0; i < t.length; i++) n += t[i];
        return t.length > 0 ? n / t.length : 0;
      }
      function c(e) {
        if (2 != e.length) return null;
        var t = e[0];
        if (!t) return null;
        if (!Array.isArray(t) && !Array.isArray(Object.keys(t))) return null;
        var n = e[1];
        return 'string' == typeof n || n instanceof String ? { data: t, name: n } : null;
      }
      function p(e, t) {
        var n = c(e);
        if (n) {
          var r = void 0;
          if (Array.isArray(n.data))
            for (var i = 0; i < n.data.length; i++) {
              var o = n.data[i];
              o && o[n.name] && (r = t(r, o[n.name]));
            }
          else
            for (var a in n.data) {
              var o = n.data[a];
              o && o[n.name] && (r = t(r, o[n.name]));
            }
          return r;
        }
      }
      function d(e) {
        var t = p(e, function (e, t) {
          return void 0 == e && (e = 0), +e + +t;
        });
        return void 0 !== t ? t : 0;
      }
      function h(e) {
        return p(e, function (e, t) {
          return void 0 == e ? t : e < t ? e : t;
        });
      }
      function f(e) {
        return p(e, function (e, t) {
          return void 0 == e ? t : e > t ? e : t;
        });
      }
      function m(e) {
        var t = p(e, function (e, t) {
          return void 0 == e && (e = 0), e + 1;
        });
        return void 0 !== t ? t : 0;
      }
      function g(e) {
        var t = m(e);
        return 0 == t ? 0 : d(e) / t;
      }
      function y(e) {
        return e || 3 === e.length ? (e[0] ? e[1] : e[2]) : '';
      }
      function v(e) {
        return !e && e.length < 1 ? null : e[0] ? new Date(e[0]) : null;
      }
      function b(e) {
        if (!e && e.length < 1) return null;
        if (!e[0]) return null;
        var t = new Date(e[0]),
          n = new Date(),
          r = n.getFullYear() - t.getFullYear(),
          i = n.getMonth() - t.getMonth();
        return (i < 0 || (0 === i && n.getDate() < t.getDate())) && (r -= r > 0 ? 1 : 0), r;
      }
      function x(e) {
        if (!e && e.length < 1) return !1;
        if (!e[0] || !this.survey) return !1;
        var t = e[0],
          n = this.survey.getPageByName(t);
        if ((n || (n = this.survey.getPanelByName(t)), !n)) return !1;
        for (var r = n.questions, i = 0; i < r.length; i++) if (r[i].hasErrors(!1)) return !1;
        return !0;
      }
      function C() {
        return this.survey && this.survey.isDisplayMode;
      }
      function w() {
        return new Date();
      }
      function P(e) {
        var t = new Date();
        return Array.isArray(e) && 1 == e.length && t.setDate(t.getDate() + e[0]), t;
      }
      var V = n(4);
      n.d(t, 'a', function () {
        return k;
      }),
        n.d(t, 'b', function () {
          return _;
        });
      var k = (function () {
          function e() {
            (this.functionHash = {}), (this.isAsyncHash = {});
          }
          return (
            (e.prototype.register = function (e, t, n) {
              void 0 === n && (n = !1), (this.functionHash[e] = t), n && (this.isAsyncHash[e] = !0);
            }),
            (e.prototype.unregister = function (e) {
              delete this.functionHash[e], delete this.isAsyncHash[e];
            }),
            (e.prototype.hasFunction = function (e) {
              return !!this.functionHash[e];
            }),
            (e.prototype.isAsyncFunction = function (e) {
              return !!this.isAsyncHash[e];
            }),
            (e.prototype.clear = function () {
              this.functionHash = {};
            }),
            (e.prototype.getAll = function () {
              var e = [];
              for (var t in this.functionHash) e.push(t);
              return e.sort();
            }),
            (e.prototype.run = function (e, t, n) {
              void 0 === n && (n = null);
              var r = this.functionHash[e];
              if (!r) return null;
              var i = { func: r };
              if (n) for (var o in n) i[o] = n[o];
              return i.func(t);
            }),
            (e.Instance = new e()),
            e
          );
        })(),
        _ = k.Instance.register;
      k.Instance.register('sum', i),
        k.Instance.register('min', a),
        k.Instance.register('max', s),
        k.Instance.register('count', l),
        k.Instance.register('avg', u),
        k.Instance.register('sumInArray', d),
        k.Instance.register('minInArray', h),
        k.Instance.register('maxInArray', f),
        k.Instance.register('countInArray', m),
        k.Instance.register('avgInArray', g),
        k.Instance.register('iif', y),
        k.Instance.register('getDate', v),
        k.Instance.register('age', b),
        k.Instance.register('isContainerReady', x),
        k.Instance.register('isDisplayMode', C),
        k.Instance.register('currentDate', w),
        k.Instance.register('today', P);
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(4),
        a = n(18);
      n.d(t, 'a', function () {
        return s;
      });
      var s = (function (e) {
        function t(t) {
          void 0 === t && (t = '');
          var n = e.call(this, t) || this;
          (n.name = t), (n.hasShownValue = !1), (n.isRandomizing = !1), (n.timeSpent = 0);
          var r = n;
          return (
            (n.locTitle.onGetTextCallback = function (e) {
              return r.num > 0 ? r.num + '. ' + e : e;
            }),
            n.createLocalizableString('navigationTitle', n, !0),
            n.createLocalizableString('navigationDescription', n, !0),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getType = function () {
            return 'page';
          }),
          (t.prototype.toString = function () {
            return this.name;
          }),
          Object.defineProperty(t.prototype, 'isPage', {
            get: function () {
              return !0;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'navigationTitle', {
            get: function () {
              return this.getLocalizableStringText('navigationTitle');
            },
            set: function (e) {
              this.setLocalizableStringText('navigationTitle', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locNavigationTitle', {
            get: function () {
              return this.getLocalizableString('navigationTitle');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'navigationDescription', {
            get: function () {
              return this.getLocalizableStringText('navigationDescription');
            },
            set: function (e) {
              this.setLocalizableStringText('navigationDescription', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locNavigationDescription', {
            get: function () {
              return this.getLocalizableString('navigationDescription');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'passed', {
            get: function () {
              return this.getPropertyValue('passed', !1);
            },
            set: function (e) {
              this.setPropertyValue('passed', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.delete = function () {
            this.survey && this.removeSelfFromList(this.survey.pages);
          }),
          (t.prototype.onFirstRendering = function () {
            this.wasShown || e.prototype.onFirstRendering.call(this);
          }),
          Object.defineProperty(t.prototype, 'visibleIndex', {
            get: function () {
              return this.getPropertyValue('visibleIndex', -1);
            },
            set: function (e) {
              this.setPropertyValue('visibleIndex', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isStarted', {
            get: function () {
              return this.survey && this.survey.isPageStarted(this);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cssClasses', {
            get: function () {
              var e = this.css,
                t = { page: {}, pageTitle: '', pageDescription: '', row: '' };
              return (
                this.copyCssClasses(t.page, e.page),
                e.pageTitle && (t.pageTitle = e.pageTitle),
                e.pageDescription && (t.pageDescription = e.pageDescription),
                e.row && (t.row = e.row),
                this.survey && this.survey.updatePageCssClasses(this, t),
                t
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getIsPageVisible = function (t) {
            return !this.isStarted && e.prototype.getIsPageVisible.call(this, t);
          }),
          Object.defineProperty(t.prototype, 'num', {
            get: function () {
              return this.getPropertyValue('num', -1);
            },
            set: function (e) {
              this.num != e && (this.setPropertyValue('num', e), this.onNumChanged(e));
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'navigationButtonsVisibility', {
            get: function () {
              return this.getPropertyValue('navigationButtonsVisibility');
            },
            set: function (e) {
              this.setPropertyValue('navigationButtonsVisibility', e.toLowerCase());
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'wasShown', {
            get: function () {
              return this.hasShownValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasShown', {
            get: function () {
              return this.wasShown;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setWasShown = function (e) {
            e != this.hasShownValue &&
              ((this.hasShownValue = e),
              this.isDesignMode ||
                (1 == e && this.areQuestionsRandomized && this.randomizeElements()));
          }),
          (t.prototype.randomizeElements = function () {
            if (!this.isRandomizing) {
              this.isRandomizing = !0;
              for (var e = [], t = this.elements, n = 0; n < t.length; n++) e.push(t[n]);
              var r = o.a.randomizeArray(e);
              this.elements.splice(0, this.elements.length);
              for (var n = 0; n < r.length; n++) this.elements.push(r[n]);
              this.isRandomizing = !1;
            }
          }),
          Object.defineProperty(t.prototype, 'areQuestionsRandomized', {
            get: function () {
              return (
                'random' ==
                ('default' == this.questionsOrder && this.survey
                  ? this.survey.questionsOrder
                  : this.questionsOrder)
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'questionsOrder', {
            get: function () {
              return this.getPropertyValue('questionsOrder');
            },
            set: function (e) {
              this.setPropertyValue('questionsOrder', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.scrollToTop = function () {
            this.survey && this.survey.scrollElementToTop(this, null, this, this.id);
          }),
          (t.prototype.getPanels = function (e, t) {
            void 0 === e && (e = !1), void 0 === t && (t = !1);
            var n = new Array();
            return this.addPanelsIntoList(n, e, t), n;
          }),
          Object.defineProperty(t.prototype, 'maxTimeToFinish', {
            get: function () {
              return this.getPropertyValue('maxTimeToFinish', 0);
            },
            set: function (e) {
              this.setPropertyValue('maxTimeToFinish', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.onNumChanged = function (e) {}),
          (t.prototype.onVisibleChanged = function () {
            this.isRandomizing ||
              (e.prototype.onVisibleChanged.call(this),
              null != this.survey && this.survey.pageVisibilityChanged(this, this.isVisible));
          }),
          (t.prototype.dragDropStart = function (e, t, n) {
            void 0 === n && (n = -1), (this.dragDropInfo = new a.c(e, t, n));
          }),
          (t.prototype.dragDropMoveTo = function (e, t, n) {
            if ((void 0 === t && (t = !1), void 0 === n && (n = !1), !this.dragDropInfo)) return !1;
            if (
              ((this.dragDropInfo.destination = e),
              (this.dragDropInfo.isBottom = t),
              (this.dragDropInfo.isEdge = n),
              this.correctDragDropInfo(this.dragDropInfo),
              !this.dragDropCanDropTagert())
            )
              return !1;
            if (!this.dragDropCanDropSource() || !this.dragDropAllowFromSurvey()) {
              if (this.dragDropInfo.source) {
                var r = this.dragDropFindRow(this.dragDropInfo.target);
                this.updateRowsRemoveElementFromRow(this.dragDropInfo.target, r);
              }
              return !1;
            }
            return this.dragDropAddTarget(this.dragDropInfo), !0;
          }),
          (t.prototype.correctDragDropInfo = function (e) {
            if (e.destination) {
              var t = e.destination.isPanel ? e.destination : null;
              t && (e.target.isLayoutTypeSupported(t.getChildrenLayoutType()) || (e.isEdge = !0));
            }
          }),
          (t.prototype.dragDropAllowFromSurvey = function () {
            var e = this.dragDropInfo.destination;
            if (!e || !this.survey) return !0;
            var t = null,
              n = null,
              r = e.isPage || (!this.dragDropInfo.isEdge && e.isPanel) ? e : e.parent;
            if (!e.isPage) {
              var i = e.parent;
              if (i) {
                var o = i.elements,
                  a = o.indexOf(e);
                a > -1 &&
                  ((t = e),
                  (n = e),
                  this.dragDropInfo.isBottom
                    ? (t = a < o.length - 1 ? o[a + 1] : null)
                    : (n = a > 0 ? o[a - 1] : null));
              }
            }
            var s = {
              target: this.dragDropInfo.target,
              source: this.dragDropInfo.source,
              parent: r,
              insertAfter: n,
              insertBefore: t,
            };
            return this.survey.dragAndDropAllow(s);
          }),
          (t.prototype.dragDropFinish = function (e) {
            if ((void 0 === e && (e = !1), this.dragDropInfo)) {
              var t = this.dragDropInfo.target,
                n = this.dragDropFindRow(t),
                r = this.dragDropGetElementIndex(t, n);
              if ((this.updateRowsRemoveElementFromRow(t, n), !e && n)) {
                var i = this.dragDropInfo.source,
                  o = !1;
                i &&
                  i.parent &&
                  ((o = n.panel == i.parent),
                  o ? (n.panel.dragDropMoveElement(i, t, r), (r = -1)) : i.parent.removeElement(i)),
                  r > -1 && n.panel.addElement(t, r);
              }
              return (this.dragDropInfo = null), e ? null : t;
            }
          }),
          (t.prototype.dragDropGetElementIndex = function (e, t) {
            if (!t) return -1;
            var n = t.elements.indexOf(e);
            if (0 == t.index) return n;
            var r = t.panel.rows[t.index - 1],
              i = r.elements[r.elements.length - 1];
            return n + t.panel.elements.indexOf(i) + 1;
          }),
          (t.prototype.dragDropCanDropTagert = function () {
            var e = this.dragDropInfo.destination;
            return !(e && !e.isPage) || this.dragDropCanDropCore(this.dragDropInfo.target, e);
          }),
          (t.prototype.dragDropCanDropSource = function () {
            var e = this.dragDropInfo.source;
            if (!e) return !0;
            var t = this.dragDropInfo.destination;
            return (
              !!this.dragDropCanDropCore(e, t) &&
              this.dragDropCanDropNotNext(
                e,
                t,
                this.dragDropInfo.isEdge,
                this.dragDropInfo.isBottom
              )
            );
          }),
          (t.prototype.dragDropCanDropCore = function (e, t) {
            if (!t) return !0;
            if (this.dragDropIsSameElement(t, e)) return !1;
            if (e.isPanel) {
              var n = e;
              if (n.containsElement(t) || n.getElementByName(t.name)) return !1;
            }
            return !0;
          }),
          (t.prototype.dragDropCanDropNotNext = function (e, t, n, r) {
            if (!t || (t.isPanel && !n)) return !0;
            if (e.parent !== t.parent) return !0;
            var i = e.parent,
              o = i.elements.indexOf(e),
              a = i.elements.indexOf(t);
            return a < o && !r && a--, r && a++, o < a ? a - o > 1 : o - a > 0;
          }),
          (t.prototype.dragDropIsSameElement = function (e, t) {
            return e == t || e.name == t.name;
          }),
          t
        );
      })(a.d);
      i.b.addClass(
        'page',
        [
          {
            name: 'navigationButtonsVisibility',
            default: 'inherit',
            choices: ['inherit', 'show', 'hide'],
          },
          { name: 'questionsOrder', default: 'default', choices: ['default', 'initial', 'random'] },
          { name: 'maxTimeToFinish:number', default: 0, minValue: 0 },
          {
            name: 'navigationTitle',
            visibleIf: function (e) {
              return !!e.survey && 'buttons' === e.survey.progressBarType;
            },
            serializationProperty: 'locNavigationTitle',
          },
          {
            name: 'navigationDescription',
            visibleIf: function (e) {
              return !!e.survey && 'buttons' === e.survey.progressBarType;
            },
            serializationProperty: 'locNavigationDescription',
          },
        ],
        function () {
          return new s();
        },
        'panelbase'
      );
    },
    function (e, t, n) {
      'use strict';
      var r = n(6);
      n.d(t, 'b', function () {
        return i;
      }),
        n.d(t, 'a', function () {
          return o;
        });
      var i = (function () {
          function e(e, t) {
            (this.name = e),
              (this.widgetJson = t),
              (this.isFirstRender = !0),
              (this.htmlTemplate = t.htmlTemplate ? t.htmlTemplate : '');
          }
          return (
            (e.prototype.afterRender = function (e, t) {
              var n = this;
              this.isFirstRender &&
                ((this.isFirstRender = !1),
                e.survey.onLocaleChangedEvent.add(function () {
                  n.widgetJson.willUnmount(e, t), n.widgetJson.afterRender(e, t);
                })),
                this.widgetJson.afterRender && this.widgetJson.afterRender(e, t);
            }),
            (e.prototype.willUnmount = function (e, t) {
              this.widgetJson.willUnmount && this.widgetJson.willUnmount(e, t);
            }),
            (e.prototype.getDisplayValue = function (e, t) {
              return (
                void 0 === t && (t = void 0),
                this.widgetJson.getDisplayValue ? this.widgetJson.getDisplayValue(e, t) : null
              );
            }),
            (e.prototype.isFit = function (e) {
              return (
                !(!this.isLibraryLoaded() || !this.widgetJson.isFit) && this.widgetJson.isFit(e)
              );
            }),
            (e.prototype.init = function () {
              this.widgetJson.init && this.widgetJson.init();
            }),
            (e.prototype.activatedByChanged = function (e) {
              this.isLibraryLoaded() &&
                this.widgetJson.activatedByChanged &&
                this.widgetJson.activatedByChanged(e);
            }),
            (e.prototype.isLibraryLoaded = function () {
              return !this.widgetJson.widgetIsLoaded || 1 == this.widgetJson.widgetIsLoaded();
            }),
            Object.defineProperty(e.prototype, 'isDefaultRender', {
              get: function () {
                return this.widgetJson.isDefaultRender;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'pdfQuestionType', {
              get: function () {
                return this.widgetJson.pdfQuestionType;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'pdfRender', {
              get: function () {
                return this.widgetJson.pdfRender;
              },
              enumerable: !0,
              configurable: !0,
            }),
            e
          );
        })(),
        o = (function () {
          function e() {
            (this.widgetsValues = []),
              (this.widgetsActivatedBy = {}),
              (this.onCustomWidgetAdded = new r.c());
          }
          return (
            Object.defineProperty(e.prototype, 'widgets', {
              get: function () {
                return this.widgetsValues;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.add = function (e, t) {
              void 0 === t && (t = 'property'), this.addCustomWidget(e, t);
            }),
            (e.prototype.addCustomWidget = function (e, t) {
              void 0 === t && (t = 'property');
              var n = e.name;
              n || (n = 'widget_' + this.widgets.length + 1);
              var r = new i(n, e);
              this.widgetsValues.push(r),
                r.init(),
                (this.widgetsActivatedBy[n] = t),
                r.activatedByChanged(t),
                this.onCustomWidgetAdded.fire(r, null);
            }),
            (e.prototype.getActivatedBy = function (e) {
              var t = this.widgetsActivatedBy[e];
              return t || 'property';
            }),
            (e.prototype.setActivatedBy = function (e, t) {
              if (e && t) {
                var n = this.getCustomWidgetByName(e);
                n && ((this.widgetsActivatedBy[e] = t), n.activatedByChanged(t));
              }
            }),
            (e.prototype.clear = function () {
              this.widgetsValues = [];
            }),
            (e.prototype.getCustomWidgetByName = function (e) {
              for (var t = 0; t < this.widgets.length; t++)
                if (this.widgets[t].name == e) return this.widgets[t];
              return null;
            }),
            (e.prototype.getCustomWidget = function (e) {
              for (var t = 0; t < this.widgetsValues.length; t++)
                if (this.widgetsValues[t].isFit(e)) return this.widgetsValues[t];
              return null;
            }),
            (e.Instance = new e()),
            e
          );
        })();
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(3),
        a = n(23),
        s = n(2),
        l = n(11);
      n.d(t, 'a', function () {
        return u;
      });
      var u = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          (n.name = t), (n.minMaxChoices = []), n.createLocalizableString('optionsCaption', n);
          var r = n;
          return (
            n.registerFunctionOnPropertiesValueChanged(
              ['choicesMin', 'choicesMax', 'choicesStep'],
              function () {
                r.onVisibleChoicesChanged();
              }
            ),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          Object.defineProperty(t.prototype, 'showOptionsCaption', {
            get: function () {
              return this.getPropertyValue('showOptionsCaption', !0);
            },
            set: function (e) {
              this.setPropertyValue('showOptionsCaption', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'optionsCaption', {
            get: function () {
              return this.getLocalizableStringText(
                'optionsCaption',
                s.a.getString('optionsCaption')
              );
            },
            set: function (e) {
              this.setLocalizableStringText('optionsCaption', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locOptionsCaption', {
            get: function () {
              return this.getLocalizableString('optionsCaption');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getType = function () {
            return 'dropdown';
          }),
          Object.defineProperty(t.prototype, 'selectedItem', {
            get: function () {
              return this.isEmpty() ? null : l.a.getItemByValue(this.visibleChoices, this.value);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.supportGoNextPageAutomatic = function () {
            return !0;
          }),
          (t.prototype.getChoices = function () {
            var t = e.prototype.getChoices.call(this);
            if (this.choicesMax <= this.choicesMin) return t;
            for (var n = [], r = 0; r < t.length; r++) n.push(t[r]);
            if (
              0 === this.minMaxChoices.length ||
              this.minMaxChoices.length !==
                (this.choicesMax - this.choicesMin) / this.choicesStep + 1
            ) {
              this.minMaxChoices = [];
              for (var r = this.choicesMin; r <= this.choicesMax; r += this.choicesStep)
                this.minMaxChoices.push(new l.a(r));
            }
            return (n = n.concat(this.minMaxChoices));
          }),
          Object.defineProperty(t.prototype, 'choicesMin', {
            get: function () {
              return this.getPropertyValue('choicesMin', 0);
            },
            set: function (e) {
              this.setPropertyValue('choicesMin', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'choicesMax', {
            get: function () {
              return this.getPropertyValue('choicesMax', 0);
            },
            set: function (e) {
              this.setPropertyValue('choicesMax', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'choicesStep', {
            get: function () {
              return this.getPropertyValue('choicesStep', 1);
            },
            set: function (e) {
              e < 1 && (e = 1), this.setPropertyValue('choicesStep', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          t
        );
      })(a.b);
      i.b.addClass(
        'dropdown',
        [
          { name: 'optionsCaption', serializationProperty: 'locOptionsCaption' },
          { name: 'showOptionsCaption:boolean', default: !0 },
          { name: 'choicesMin:number', default: 0 },
          { name: 'choicesMax:number', default: 0 },
          { name: 'choicesStep:number', default: 1, minValue: 1 },
        ],
        function () {
          return new u('');
        },
        'selectbase'
      ),
        o.b.Instance.registerQuestion('dropdown', function (e) {
          var t = new u(e);
          return (t.choices = o.b.DefaultChoices), t;
        });
    },
    function (e, t, n) {
      'use strict';
      function r() {
        return [
          'AED',
          'AFN',
          'ALL',
          'AMD',
          'ANG',
          'AOA',
          'ARS',
          'AUD',
          'AWG',
          'AZN',
          'BAM',
          'BBD',
          'BDT',
          'BGN',
          'BHD',
          'BIF',
          'BMD',
          'BND',
          'BOB',
          'BOV',
          'BRL',
          'BSD',
          'BTN',
          'BWP',
          'BYN',
          'BZD',
          'CAD',
          'CDF',
          'CHE',
          'CHF',
          'CHW',
          'CLF',
          'CLP',
          'CNY',
          'COP',
          'COU',
          'CRC',
          'CUC',
          'CUP',
          'CVE',
          'CZK',
          'DJF',
          'DKK',
          'DOP',
          'DZD',
          'EGP',
          'ERN',
          'ETB',
          'EUR',
          'FJD',
          'FKP',
          'GBP',
          'GEL',
          'GHS',
          'GIP',
          'GMD',
          'GNF',
          'GTQ',
          'GYD',
          'HKD',
          'HNL',
          'HRK',
          'HTG',
          'HUF',
          'IDR',
          'ILS',
          'INR',
          'IQD',
          'IRR',
          'ISK',
          'JMD',
          'JOD',
          'JPY',
          'KES',
          'KGS',
          'KHR',
          'KMF',
          'KPW',
          'KRW',
          'KWD',
          'KYD',
          'KZT',
          'LAK',
          'LBP',
          'LKR',
          'LRD',
          'LSL',
          'LYD',
          'MAD',
          'MDL',
          'MGA',
          'MKD',
          'MMK',
          'MNT',
          'MOP',
          'MRO',
          'MUR',
          'MVR',
          'MWK',
          'MXN',
          'MXV',
          'MYR',
          'MZN',
          'NAD',
          'NGN',
          'NIO',
          'NOK',
          'NPR',
          'NZD',
          'OMR',
          'PAB',
          'PEN',
          'PGK',
          'PHP',
          'PKR',
          'PLN',
          'PYG',
          'QAR',
          'RON',
          'RSD',
          'RUB',
          'RWF',
          'SAR',
          'SBD',
          'SCR',
          'SDG',
          'SEK',
          'SGD',
          'SHP',
          'SLL',
          'SOS',
          'SRD',
          'SSP',
          'STD',
          'SVC',
          'SYP',
          'SZL',
          'THB',
          'TJS',
          'TMT',
          'TND',
          'TOP',
          'TRY',
          'TTD',
          'TWD',
          'TZS',
          'UAH',
          'UGX',
          'USD',
          'USN',
          'UYI',
          'UYU',
          'UZS',
          'VEF',
          'VND',
          'VUV',
          'WST',
          'XAF',
          'XAG',
          'XAU',
          'XBA',
          'XBB',
          'XBC',
          'XBD',
          'XCD',
          'XDR',
          'XOF',
          'XPD',
          'XPF',
          'XPT',
          'XSU',
          'XTS',
          'XUA',
          'XXX',
          'YER',
          'ZAR',
          'ZMW',
          'ZWL',
        ];
      }
      var i = n(0),
        o = n(4),
        a = n(9),
        s = n(1),
        l = n(3),
        u = n(10);
      n.d(t, 'a', function () {
        return c;
      }),
        (t.b = r);
      var c = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          (n.name = t), n.createLocalizableString('format', n);
          var r = n;
          return (
            n.registerFunctionOnPropertyValueChanged('expression', function () {
              r.expressionRunner && (r.expressionRunner = new u.b(r.expression));
            }),
            n
          );
        }
        return (
          n.i(i.b)(t, e),
          (t.prototype.getType = function () {
            return 'expression';
          }),
          Object.defineProperty(t.prototype, 'hasInput', {
            get: function () {
              return !1;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'format', {
            get: function () {
              return this.getLocalizableStringText('format', '');
            },
            set: function (e) {
              this.setLocalizableStringText('format', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locFormat', {
            get: function () {
              return this.getLocalizableString('format');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'expression', {
            get: function () {
              return this.getPropertyValue('expression', '');
            },
            set: function (e) {
              this.setPropertyValue('expression', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.locCalculation = function () {
            this.expressionIsRunning = !0;
          }),
          (t.prototype.unlocCalculation = function () {
            this.expressionIsRunning = !1;
          }),
          (t.prototype.runCondition = function (t, n) {
            var r = this;
            e.prototype.runCondition.call(this, t, n),
              this.expression &&
                !this.expressionIsRunning &&
                (this.locCalculation(),
                this.expressionRunner || (this.expressionRunner = new u.b(this.expression)),
                (this.expressionRunner.onRunComplete = function (e) {
                  o.a.isTwoValueEquals(e, r.value) || (r.value = e), r.unlocCalculation();
                }),
                this.expressionRunner.run(t, n));
          }),
          (t.prototype.hasErrors = function (e, t) {
            return void 0 === e && (e = !0), void 0 === t && (t = null), !1;
          }),
          (t.prototype.getAllErrors = function () {
            return [];
          }),
          Object.defineProperty(t.prototype, 'maximumFractionDigits', {
            get: function () {
              return this.getPropertyValue('maximumFractionDigits', -1);
            },
            set: function (e) {
              e < -1 || e > 20 || this.setPropertyValue('maximumFractionDigits', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'minimumFractionDigits', {
            get: function () {
              return this.getPropertyValue('minimumFractionDigits', -1);
            },
            set: function (e) {
              e < -1 || e > 20 || this.setPropertyValue('minimumFractionDigits', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getDisplayValueCore = function (e, t) {
            var n = this.isValueEmpty(t) ? this.defaultValue : t;
            if (this.isValueEmpty(n)) return '';
            var r = this.getValueAsStr(n);
            return this.format ? this.format.format(r) : r;
          }),
          Object.defineProperty(t.prototype, 'displayStyle', {
            get: function () {
              return this.getPropertyValue('displayStyle');
            },
            set: function (e) {
              this.setPropertyValue('displayStyle', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'currency', {
            get: function () {
              return this.getPropertyValue('currency');
            },
            set: function (e) {
              r().indexOf(e) < 0 || this.setPropertyValue('currency', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'useGrouping', {
            get: function () {
              return this.getPropertyValue('useGrouping', !0);
            },
            set: function (e) {
              this.setPropertyValue('useGrouping', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getValueAsStr = function (e) {
            if ('date' == this.displayStyle) {
              var t = new Date(e);
              if (t && t.toLocaleDateString) return t.toLocaleDateString();
            }
            if ('none' != this.displayStyle && o.a.isNumber(e)) {
              var n = this.getLocale();
              n || (n = 'en');
              var r = {
                style: this.displayStyle,
                currency: this.currency,
                useGrouping: this.useGrouping,
              };
              return (
                this.maximumFractionDigits > -1 &&
                  (r.maximumFractionDigits = this.maximumFractionDigits),
                this.minimumFractionDigits > -1 &&
                  (r.minimumFractionDigits = this.minimumFractionDigits),
                e.toLocaleString(n, r)
              );
            }
            return e.toString();
          }),
          t
        );
      })(a.a);
      s.b.addClass(
        'expression',
        [
          'expression:expression',
          { name: 'format', serializationProperty: 'locFormat' },
          {
            name: 'displayStyle',
            default: 'none',
            choices: ['none', 'decimal', 'currency', 'percent', 'date'],
          },
          {
            name: 'currency',
            choices: function () {
              return r();
            },
            default: 'USD',
          },
          { name: 'maximumFractionDigits:number', default: -1 },
          { name: 'minimumFractionDigits:number', default: -1 },
          { name: 'useGrouping:boolean', default: !0 },
          { name: 'enableIf', visible: !1 },
          { name: 'isRequired', visible: !1 },
          { name: 'readOnly', visible: !1 },
          { name: 'requiredErrorText', visible: !1 },
          { name: 'validators', visible: !1 },
          { name: 'defaultValue', visible: !1 },
          { name: 'correctAnswer', visible: !1 },
          { name: 'requiredIf', visible: !1 },
        ],
        function () {
          return new c('');
        },
        'question'
      ),
        l.b.Instance.registerQuestion('expression', function (e) {
          return new c(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(3),
        o = n(1),
        a = n(9),
        s = n(13),
        l = n(4),
        u = n(36);
      n.d(t, 'a', function () {
        return c;
      });
      var c = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (
              (n.name = t),
              n.createLocalizableString('placeHolder', n),
              (n.locDataListValue = new s.b(n)),
              n.registerFunctionOnPropertiesValueChanged(
                ['min', 'max', 'inputType', 'minValueExpression', 'maxValueExpression'],
                function () {
                  n.setRenderedMinMax();
                }
              ),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.isTextValue = function () {
              return ['text', 'number', 'password'].indexOf(this.inputType) > -1;
            }),
            (t.prototype.getType = function () {
              return 'text';
            }),
            (t.prototype.onSurveyLoad = function () {
              e.prototype.onSurveyLoad.call(this), this.setRenderedMinMax();
            }),
            Object.defineProperty(t.prototype, 'inputType', {
              get: function () {
                return this.getPropertyValue('inputType');
              },
              set: function (e) {
                (e = e.toLowerCase()),
                  'datetime_local' == e && (e = 'datetime-local'),
                  this.setPropertyValue('inputType', e.toLowerCase()),
                  this.isLoadingFromJson ||
                    ((this.min = void 0), (this.max = void 0), (this.step = void 0));
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'textUpdateMode', {
              get: function () {
                return this.getPropertyValue('textUpdateMode');
              },
              set: function (e) {
                this.setPropertyValue('textUpdateMode', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isSurveyInputTextUpdate', {
              get: function () {
                return 'default' == this.textUpdateMode
                  ? !!this.survey && this.survey.isUpdateValueTextOnTyping
                  : 'onTyping' == this.textUpdateMode;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getValidators = function () {
              var t = e.prototype.getValidators.call(this);
              return (
                'email' !== this.inputType ||
                  this.validators.some(function (e) {
                    return 'emailvalidator' === e.getType();
                  }) ||
                  t.push(new u.b()),
                t
              );
            }),
            (t.prototype.isLayoutTypeSupported = function (e) {
              return !0;
            }),
            Object.defineProperty(t.prototype, 'maxLength', {
              get: function () {
                return this.getPropertyValue('maxLength');
              },
              set: function (e) {
                this.setPropertyValue('maxLength', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getMaxLength = function () {
              return l.a.getMaxLength(this.maxLength, this.survey ? this.survey.maxTextLength : -1);
            }),
            Object.defineProperty(t.prototype, 'size', {
              get: function () {
                return this.getPropertyValue('size');
              },
              set: function (e) {
                this.setPropertyValue('size', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'autoComplete', {
              get: function () {
                return this.getPropertyValue('autoComplete', '');
              },
              set: function (e) {
                this.setPropertyValue('autoComplete', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'min', {
              get: function () {
                return this.getPropertyValue('min');
              },
              set: function (e) {
                if (this.isValueExpression(e)) return void (this.minValueExpression = e.substr(1));
                this.setPropertyValue('min', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'max', {
              get: function () {
                return this.getPropertyValue('max');
              },
              set: function (e) {
                if (this.isValueExpression(e)) return void (this.maxValueExpression = e.substr(1));
                this.setPropertyValue('max', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'minValueExpression', {
              get: function () {
                return this.getPropertyValue('minValueExpression', '');
              },
              set: function (e) {
                this.setPropertyValue('minValueExpression', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'maxValueExpression', {
              get: function () {
                return this.getPropertyValue('maxValueExpression', '');
              },
              set: function (e) {
                this.setPropertyValue('maxValueExpression', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'renderedMin', {
              get: function () {
                return this.getPropertyValue('renderedMin');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'renderedMax', {
              get: function () {
                return this.getPropertyValue('renderedMax');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setRenderedMinMax = function () {
              this.setPropertyValue(
                'renderedMin',
                this.getValueAndRunExpression(this.min, this.minValueExpression)
              );
              var e = this.getValueAndRunExpression(this.max, this.maxValueExpression);
              e ||
                ('date' !== this.inputType && 'datetime-local' !== this.inputType) ||
                (e = '2999-12-31'),
                this.setPropertyValue('renderedMax', e);
            }),
            Object.defineProperty(t.prototype, 'step', {
              get: function () {
                return this.getPropertyValue('step');
              },
              set: function (e) {
                this.setPropertyValue('step', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'renderedStep', {
              get: function () {
                return l.a.isValueEmpty(this.step) ? 'any' : this.step;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.isEmpty = function () {
              return e.prototype.isEmpty.call(this) || '' === this.value;
            }),
            (t.prototype.supportGoNextPageAutomatic = function () {
              return ['date', 'datetime', 'datetime-local'].indexOf(this.inputType) < 0;
            }),
            (t.prototype.supportGoNextPageError = function () {
              return ['date', 'datetime', 'datetime-local'].indexOf(this.inputType) < 0;
            }),
            Object.defineProperty(t.prototype, 'placeHolder', {
              get: function () {
                return this.getLocalizableStringText('placeHolder');
              },
              set: function (e) {
                this.setLocalizableStringText('placeHolder', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locPlaceHolder', {
              get: function () {
                return this.getLocalizableString('placeHolder');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'dataList', {
              get: function () {
                return this.locDataList.value;
              },
              set: function (e) {
                this.locDataList.value = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locDataList', {
              get: function () {
                return this.locDataListValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'dataListId', {
              get: function () {
                return this.locDataList.isEmpty ? '' : this.id + '_datalist';
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.canRunValidators = function (e) {
              return this.errors.length > 0 || !e || this.supportGoNextPageError();
            }),
            (t.prototype.setNewValue = function (t) {
              (t = this.correctValueType(t)), e.prototype.setNewValue.call(this, t);
            }),
            (t.prototype.correctValueType = function (e) {
              return e && ('number' == this.inputType || 'range' == this.inputType)
                ? l.a.isNumber(e)
                  ? parseFloat(e)
                  : ''
                : e;
            }),
            t
          );
        })(a.a),
        p = ['number', 'range', 'date', 'datetime-local', 'month', 'time', 'week'];
      o.b.addClass(
        'text',
        [
          {
            name: 'inputType',
            default: 'text',
            choices: [
              'color',
              'date',
              'datetime',
              'datetime-local',
              'email',
              'month',
              'number',
              'password',
              'range',
              'tel',
              'text',
              'time',
              'url',
              'week',
            ],
          },
          { name: 'size:number', default: 25 },
          {
            name: 'textUpdateMode',
            default: 'default',
            choices: ['default', 'onBlur', 'onTyping'],
            dependsOn: 'inputType',
            visibleIf: function (e) {
              return !!e && 'text' == e.inputType;
            },
          },
          {
            name: 'autoComplete',
            dataList: [
              'name',
              'honorific-prefix',
              'given-name',
              'additional-name',
              'family-name',
              'honorific-suffix',
              'nickname',
              'organization-title',
              'username',
              'new-password',
              'current-password',
              'organization',
              'street-address',
              'address-line1',
              'address-line2',
              'address-line3',
              'address-level4',
              'address-level3',
              'address-level2',
              'address-level1',
              'country',
              'country-name',
              'postal-code',
              'cc-name',
              'cc-given-name',
              'cc-additional-name',
              'cc-family-name',
              'cc-number',
              'cc-exp',
              'cc-exp-month',
              'cc-exp-year',
              'cc-csc',
              'cc-type',
              'transaction-currency',
              'transaction-amount',
              'language',
              'bday',
              'bday-day',
              'bday-month',
              'bday-year',
              'sex',
              'url',
              'photo',
              'tel',
              'tel-country-code',
              'tel-national',
              'tel-area-code',
              'tel-local',
              'tel-local-prefix',
              'tel-local-suffix',
              'tel-extension',
              'email',
              'impp',
            ],
          },
          {
            name: 'min',
            dependsOn: 'inputType',
            visibleIf: function (e) {
              return !!e && -1 !== p.indexOf(e.inputType);
            },
            onPropertyEditorUpdate: function (e, t) {
              t.inputType = e.inputType;
            },
          },
          {
            name: 'max',
            dependsOn: 'inputType',
            visibleIf: function (e) {
              return !!e && -1 !== p.indexOf(e.inputType);
            },
            onPropertyEditorUpdate: function (e, t) {
              t.inputType = e.inputType;
            },
          },
          {
            name: 'minValueExpression:expression',
            category: 'logic',
            dependsOn: 'inputType',
            visibleIf: function (e) {
              return !!e && -1 !== p.indexOf(e.inputType);
            },
          },
          {
            name: 'maxValueExpression:expression',
            category: 'logic',
            dependsOn: 'inputType',
            visibleIf: function (e) {
              return !!e && -1 !== p.indexOf(e.inputType);
            },
          },
          {
            name: 'step:number',
            dependsOn: 'inputType',
            visibleIf: function (e) {
              return !!e && 'number' === e.inputType;
            },
          },
          { name: 'maxLength:number', default: -1 },
          {
            name: 'placeHolder',
            serializationProperty: 'locPlaceHolder',
            dependsOn: 'inputType',
            visibleIf: function (e) {
              return !!e && 'text' === e.inputType;
            },
          },
          {
            name: 'dataList:string[]',
            serializationProperty: 'locDataList',
            dependsOn: 'inputType',
            visibleIf: function (e) {
              return !!e && 'text' === e.inputType;
            },
          },
        ],
        function () {
          return new c('');
        },
        'question'
      ),
        i.b.Instance.registerQuestion('text', function (e) {
          return new c(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(9),
        o = n(1);
      n.d(t, 'a', function () {
        return a;
      });
      var a = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          return (n.name = t), n;
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getType = function () {
            return 'nonvalue';
          }),
          Object.defineProperty(t.prototype, 'hasInput', {
            get: function () {
              return !1;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasTitle', {
            get: function () {
              return !1;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getTitleLocation = function () {
            return '';
          }),
          Object.defineProperty(t.prototype, 'hasComment', {
            get: function () {
              return !1;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.hasErrors = function (e, t) {
            return void 0 === e && (e = !0), void 0 === t && (t = null), !1;
          }),
          (t.prototype.getAllErrors = function () {
            return [];
          }),
          (t.prototype.supportGoNextPageAutomatic = function () {
            return !1;
          }),
          (t.prototype.addConditionObjectsByContext = function (e, t) {}),
          (t.prototype.getConditionJson = function (e, t) {
            return void 0 === e && (e = null), void 0 === t && (t = null), null;
          }),
          t
        );
      })(i.a);
      o.b.addClass(
        'nonvalue',
        [
          { name: 'title', visible: !1 },
          { name: 'description', visible: !1 },
          { name: 'valueName', visible: !1 },
          { name: 'enableIf', visible: !1 },
          { name: 'defaultValue', visible: !1 },
          { name: 'correctAnswer', visible: !1 },
          { name: 'isRequired', visible: !1, isSerializable: !1 },
          { name: 'requiredErrorText', visible: !1 },
          { name: 'readOnly', visible: !1 },
          { name: 'requiredIf', visible: !1 },
          { name: 'validators', visible: !1 },
          { name: 'titleLocation', visible: !1 },
          { name: 'useDisplayValuesInTitle', visible: !1 },
        ],
        function () {
          return new a('');
        },
        'question'
      );
    },
    function (e, t, n) {
      'use strict';
      var r = n(14);
      n.d(t, 'a', function () {
        return i;
      });
      var i = (function () {
        function e() {
          (this.sheet = null),
            e.Enabled &&
              ((this.sheet = e.findSheet(e.SurveyJSStylesSheetId)),
              this.sheet ||
                ((this.sheet = e.createSheet(e.SurveyJSStylesSheetId)),
                this.initializeStyles(this.sheet)));
        }
        return (
          (e.findSheet = function (e) {
            if ('undefined' == typeof document) return null;
            for (var t = 0; t < document.styleSheets.length; t++)
              if (document.styleSheets[t].ownerNode && document.styleSheets[t].ownerNode.id === e)
                return document.styleSheets[t];
            return null;
          }),
          (e.createSheet = function (e) {
            var t = document.createElement('style');
            return (
              (t.id = e),
              t.appendChild(document.createTextNode('')),
              document.head.appendChild(t),
              t.sheet
            );
          }),
          (e.applyTheme = function (t, n) {
            void 0 === t && (t = 'default'), void 0 === n && (n = '.sv_main');
            var i;
            if (
              ('modern' === t && (n = '.sv-root-modern '),
              -1 !== ['bootstrap', 'bootstrapmaterial', 'modern'].indexOf(t)
                ? ((i = e[t + 'ThemeCss']), (r.b.currentType = t))
                : ((i = e.ThemeCss), (r.b.currentType = 'standard')),
              e.Enabled)
            ) {
              var o = e.findSheet(t + n);
              if (!o) {
                o = e.createSheet(t + n);
                var a = e.ThemeColors[t] || e.ThemeColors.default;
                Object.keys(i).forEach(function (e) {
                  var t = i[e];
                  Object.keys(a).forEach(function (e) {
                    return (t = t.replace(new RegExp('\\' + e, 'g'), a[e]));
                  });
                  try {
                    o.insertRule(n + e + ' { ' + t + ' }', 0);
                  } catch (e) {}
                });
              }
            }
          }),
          (e.prototype.initializeStyles = function (t) {
            e.Enabled &&
              (Object.keys(e.Styles).forEach(function (n) {
                try {
                  t.insertRule(n + ' { ' + e.Styles[n] + ' }', 0);
                } catch (e) {}
              }),
              Object.keys(e.Media).forEach(function (n) {
                try {
                  t.insertRule(e.Media[n].media + ' { ' + n + ' { ' + e.Media[n].style + ' } }', 0);
                } catch (e) {}
              }));
          }),
          (e.SurveyJSStylesSheetId = 'surveyjs-styles'),
          (e.Styles = {
            '.sv_main span': 'word-break: break-word;',
            '.sv_main legend': 'border: none; margin: 0;',
            '.sv_bootstrap_css .sv_qstn': 'padding: 0.5em 1em 1.5em 1em;',
            '.sv_bootstrap_css .sv_qcbc input[type=checkbox], .sv_bootstrap_css .sv_qcbc input[type=radio]':
              'vertical-align: middle; margin-top: -1px',
            '.sv_bootstrap_css .sv_qstn fieldset': 'display: block;',
            '.sv_bootstrap_css .sv_qstn  .sv_q_checkbox_inline, .sv_bootstrap_css .sv_qstn .sv_q_radiogroup_inline':
              'display: inline-block;',
            '.sv_bootstrap_css .sv-paneldynamic__progress-container ':
              'position: relative; margin-right: 250px; margin-left: 40px; margin-top: 10px;',
            '.sv_main.sv_bootstrapmaterial_css .sv_q_radiogroup_control_label':
              'display: inline; position: static;',
            '.sv_main.sv_bootstrapmaterial_css .checkbox': 'margin-top:10px;margin-bottom:10px;',
            '.sv_row': 'clear: both; min-width:300px;',
            '.sv_row .sv_qstn': 'float: left',
            '.sv_row .sv_qstn:last-child': 'float: none',
            '.sv_qstn':
              'display: inline-block; vertical-align: top; overflow: auto; min-width:300px;',
            '.sv_p_container': 'display: inline-block; vertical-align: top; min-width:300px;',
            '.sv_q_title .sv_question_icon': 'float: right; margin-right: 1em;',
            '.sv_q_title .sv_question_icon::before':
              "content: ''; background-repeat: no-repeat; background-position: center; padding: 0.5em; display: inline-block; background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4NCjwhLS0gR2VuZXJhdG9yOiBBZG9iZSBJbGx1c3RyYXRvciAyMi4wLjEsIFNWRyBFeHBvcnQgUGx1Zy1JbiAuIFNWRyBWZXJzaW9uOiA2LjAwIEJ1aWxkIDApICAtLT4NCjxzdmcgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiDQoJIHZpZXdCb3g9IjAgMCAxMCAxMCIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMTAgMTA7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4NCjxzdHlsZSB0eXBlPSJ0ZXh0L2NzcyI+DQoJLnN0MHtmaWxsOiM2RDcwNzI7fQ0KPC9zdHlsZT4NCjxwb2x5Z29uIGNsYXNzPSJzdDAiIHBvaW50cz0iMiwyIDAsNCA1LDkgMTAsNCA4LDIgNSw1ICIvPg0KPC9zdmc+DQo=);",
            '.sv_q_title .sv_question_icon.sv_expanded::before': 'transform: rotate(180deg);',
            '.sv_qbln .checkbox-material': 'margin-right: 3px;',
            '.sv_qcbx .checkbox-material': 'margin-right: 5px;',
            '.sv_qcbx .checkbox label': 'justify-content: left; display: inline-block;',
            '.sv_qstn .radio label': 'justify-content: left; display: inline-block;',
            '.sv_qstn .sv_q_imgsel > label img': 'pointer-events: none;',
            '.sv_qstn .sv_q_imgsel.sv_q_imagepicker_inline': 'display: inline-block;',
            '.sv_qstn label.sv_q_m_label':
              'position: absolute; margin: 0; display: block; width: 100%;',
            '.sv_qstn td': 'position: relative;',
            '.sv_q_mt_item_value': 'float: left;',
            '[dir="rtl"] .sv_q_mt_item_value': 'float: right;',
            '.sv_qstn.sv_qstn_left': 'margin-top: 0.75em;',
            '.sv_qstn .title-left': 'float: left; margin-right: 1em;',
            '[dir="rtl"] .sv_qstn .title-left': 'float: right; margin-left: 1em;',
            '.sv_qstn .content-left': 'overflow: hidden',
            '.sv_q_radiogroup_inline .sv_q_radiogroup_other': 'display: inline-block;',
            '.sv_q_checkbox_inline .sv_q_checkbox_other': 'display: inline-block;',
            '.sv_q_checkbox_inline, .sv_q_radiogroup_inline, .sv_q_imagepicker_inline':
              'line-height: 2.5em;',
            '.form-inline .sv_q_checkbox_inline:not(:last-child)': 'margin-right: 1em;',
            '.form-inline .sv_q_radiogroup_inline:not(:last-child)': 'margin-right: 1em;',
            '.sv_imgsel .sv_q_imagepicker_inline:not(:last-child)': 'margin-right: 1em;',
            '.sv_qstn fieldset': 'border: none; margin: 0; padding: 0;',
            '.sv_qstn .sv_q_file_placeholder': 'display:none',
            '.sv_p_title': 'padding-left: 1em; padding-bottom: 0.3em;',
            '.sv_p_title_expandable': 'cursor: pointer;',
            '.sv_q_title_expandable': 'cursor: pointer;',
            '.sv_p_title .sv_panel_icon': 'float: right; margin-right: 1em;',
            '.sv_p_title .sv_panel_icon::before':
              "content: ''; background-repeat: no-repeat; background-position: center; padding: 0.5em; display: inline-block; background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4NCjwhLS0gR2VuZXJhdG9yOiBBZG9iZSBJbGx1c3RyYXRvciAyMi4wLjEsIFNWRyBFeHBvcnQgUGx1Zy1JbiAuIFNWRyBWZXJzaW9uOiA2LjAwIEJ1aWxkIDApICAtLT4NCjxzdmcgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiDQoJIHZpZXdCb3g9IjAgMCAxMCAxMCIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMTAgMTA7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4NCjxzdHlsZSB0eXBlPSJ0ZXh0L2NzcyI+DQoJLnN0MHtmaWxsOiM2RDcwNzI7fQ0KPC9zdHlsZT4NCjxwb2x5Z29uIGNsYXNzPSJzdDAiIHBvaW50cz0iMiwyIDAsNCA1LDkgMTAsNCA4LDIgNSw1ICIvPg0KPC9zdmc+DQo=);",
            '.sv_p_title .sv_panel_icon.sv_expanded::before': 'transform: rotate(180deg);',
            '.sv_p_footer': 'padding-left: 1em; padding-bottom: 1em;padding-top: 1em;',
            '.sv_matrix_cell_detail_button': 'position: relative',
            '.sv_detail_panel_icon':
              'display: block; position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 14px; height: 14px;',
            '.sv_detail_panel_icon::before':
              "content: ''; background-repeat: no-repeat; background-position: center; width: 14px; height: 14px; display: block; transform: rotate(270deg); background-image: url(\"data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8'%3F%3E%3C!-- Generator: Adobe Illustrator 21.1.0, SVG Export Plug-In . SVG Version: 6.00 Build 0) --%3E%3Csvg version='1.1' id='Layer_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' viewBox='0 0 15 15' style='enable-background:new 0 0 15 15;' xml:space='preserve'%3E%3Cstyle type='text/css'%3E .st0%7Bfill:%23FFFFFF;%7D%0A%3C/style%3E%3Cpolygon class='st0' points='14,5.5 12.6,4.1 7.5,9.1 2.4,4.1 1,5.5 7.5,12 '/%3E%3C/svg%3E%0A\");",
            '.sv_detail_panel_icon.sv_detail_expanded::before': 'transform: rotate(0deg)',
            '.sv_q_file > input[type=file], .sv_q_file > button': 'display: inline-block;',
            '.sv_q_file_preview':
              'display: inline-block; vertical-align: top; border: 1px solid lightgray; padding: 5px; margin-top: 10px;',
            '.sv_q_file_preview > a':
              'display: block; overflow: hidden; vertical-align: top; white-space: nowrap; text-overflow: ellipsis;',
            '.sv_q_file_remove_button': 'line-height: normal;',
            '.sv_q_file_remove': 'display: block; cursor: pointer;',
            '.sv_q_m_cell_text': 'cursor: pointer;',
            '.sv_q_dd_other': 'margin-top: 1em;',
            '.sv_q_dd_other input': 'width: 100%;',
            '.sv_qstn .sv-q-col-1, .sv-question .sv-q-col-1':
              'width: 100%; display: inline-block; padding-right: 1em; box-sizing: border-box; word-break: break-word;',
            '.sv_qstn .sv-q-col-2, .sv-question .sv-q-col-2':
              'width: 50%; display: inline-block; padding-right: 1em; box-sizing: border-box; word-break: break-word;',
            '.sv_qstn .sv-q-col-3, .sv-question .sv-q-col-3':
              'width: 33.33333%; display: inline-block; padding-right: 1em; box-sizing: border-box; word-break: break-word;',
            '.sv_qstn .sv-q-col-4, .sv-question .sv-q-col-4':
              'width: 25%; display: inline-block; padding-right: 1em; box-sizing: border-box; word-break: break-word;',
            '.sv_qstn .sv-q-col-5, .sv-question .sv-q-col-5':
              'width: 20%; display: inline-block; padding-right: 1em; box-sizing: border-box; word-break: break-word;',
            '.sv_qstn .sv-q-column-1, .sv-question .sv-q-column-1':
              'width: 100%; max-width: 100%; display: inline-block; padding-right: 1em; box-sizing: border-box; word-break: break-word;',
            '.sv_qstn .sv-q-column-2, .sv-question .sv-q-column-2':
              'max-width: 50%; display: inline-block; padding-right: 1em; box-sizing: border-box; word-break: break-word;',
            '.sv_qstn .sv-q-column-3, .sv-question .sv-q-column-3':
              'max-width: 33.33333%; display: inline-block; padding-right: 1em; box-sizing: border-box; word-break: break-word;',
            '.sv_qstn .sv-q-column-4, .sv-question .sv-q-column-4':
              'max-width: 25%; display: inline-block; padding-right: 1em; box-sizing: border-box; word-break: break-word;',
            '.sv_qstn .sv-q-column-5, .sv-question .sv-q-column-5':
              'max-width: 20%; display: inline-block; padding-right: 1em; box-sizing: border-box; word-break: break-word;',
            '.sv_qstn .sv_q_file_input': 'color: transparent;',
            '.sv_qstn .sv_q_imgsel label > div':
              'overflow: hidden; white-space: nowrap; text-overflow: ellipsis; padding: 4px; border: 1px solid lightgray; border-radius: 4px;',
            '.sv_qstn .sv_q_imgsel label > div > img, .sv_qstn .sv_q_imgsel label > div > embed':
              'display: block;',
            '.sv_qstn table tr td .sv_q_m_cell_label':
              'position: absolute; left: 0; right: 0; top: 0; bottom: 0;',
            'f-panel': 'padding: 0.5em 1em; display: inline-block; line-height: 2em;',
            '.sv_progress_bar > span': 'white-space: nowrap;',
            '.sv_progress-buttons__container-center': 'text-align: center;',
            '.sv_progress-buttons__container':
              'display: inline-block; font-size:0; width: 100%; max-width: 1100px; white-space: nowrap; overflow: hidden;',
            '.sv_progress-buttons__image-button-left':
              'display: inline-block; vertical-align: top; margin-top: 22px; font-size: 14px; width: 16px; height: 16px; cursor: pointer; background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4NCjwhLS0gR2VuZXJhdG9yOiBBZG9iZSBJbGx1c3RyYXRvciAyMi4wLjEsIFNWRyBFeHBvcnQgUGx1Zy1JbiAuIFNWRyBWZXJzaW9uOiA2LjAwIEJ1aWxkIDApICAtLT4NCjxzdmcgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiDQoJIHZpZXdCb3g9IjAgMCAxNiAxNiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMTYgMTY7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4NCjxwb2x5Z29uIHBvaW50cz0iMTEsMTIgOSwxNCAzLDggOSwyIDExLDQgNyw4ICIvPg0KPC9zdmc+DQo=);',
            '.sv_progress-buttons__image-button-right':
              'display: inline-block; vertical-align: top; margin-top: 22px; font-size: 14px; width: 16px; height: 16px; cursor: pointer; background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4NCjwhLS0gR2VuZXJhdG9yOiBBZG9iZSBJbGx1c3RyYXRvciAyMi4wLjEsIFNWRyBFeHBvcnQgUGx1Zy1JbiAuIFNWRyBWZXJzaW9uOiA2LjAwIEJ1aWxkIDApICAtLT4NCjxzdmcgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiDQoJIHZpZXdCb3g9IjAgMCAxNiAxNiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMTYgMTY7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4NCjxwb2x5Z29uIHBvaW50cz0iNSw0IDcsMiAxMyw4IDcsMTQgNSwxMiA5LDggIi8+DQo8L3N2Zz4NCg==);',
            '.sv_progress-buttons__image-button--hidden': 'visibility: hidden;',
            '.sv_progress-buttons__list-container':
              'max-width: calc(100% - 36px); display: inline-block; overflow: hidden;',
            '.sv_progress-buttons__list':
              'display: inline-block; width: max-content; padding-left: 28px; padding-right: 28px; margin-top: 14px; margin-bottom: 14px;',
            '.sv_progress-buttons__list li':
              "width: 138px; font-size: 14px; font-family: 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif; position: relative; text-align: center; vertical-align: top; display: inline-block;",
            '.sv_progress-buttons__list li:before':
              "width: 24px; height: 24px; content: ''; line-height: 30px; border: 3px solid #8dd9ca; display: block; margin: 0 auto 10px auto; border-radius: 50%; box-sizing: content-box; background-color: #8dd9ca; cursor: pointer;",
            '.sv_progress-buttons__list li:after':
              "width: 73%; height: 3px; content: ''; position: absolute; background-color: #d4d4d4; top: 15px; left: -36.5%;",
            '.sv_progress-buttons__list li:first-child:after': 'content: none;',
            '.sv_progress-buttons__list .sv_progress-buttons__page-title':
              'width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: $text-color; font-weight: bold;',
            '.sv_progress-buttons__list .sv_progress-buttons__page-description':
              'width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: $text-color;',
            '.sv_progress-buttons__list li.sv_progress-buttons__list-element--passed:before':
              'border-color: #1ab394; background-color: #1ab394;',
            '.sv_progress-buttons__list li.sv_progress-buttons__list-element--passed + li:after':
              'background-color: #8dd9ca;',
            '.sv_progress-buttons__list li.sv_progress-buttons__list-element--current:before':
              'border-color: #1ab394; background-color: white;',
            '.sv_progress-buttons__list li.sv_progress-buttons__list-element--passed.sv_progress-buttons__list-element--current:before':
              'border-color: #1ab394; background-color: white;',
            '.sv_qstn .sv_q_select_column':
              'display: inline-block; vertical-align: top; min-width: 10%;',
            '.sv_qstn .sv_q_select_column > *:not(.sv_technical)': 'display: block;',
            '.sv_main .sv_container .sv_body .sv_p_root .sv_qstn .sv_q_select_column textarea':
              'margin-left: 0; padding-left: 0; line-height: initial;',
            '.sv_main .sv-hidden': 'display: none !important;',
            '.sv_main .sv-visuallyhidden':
              'position: absolute; height: 1px !important; width: 1px !important; overflow: hidden; clip: rect(1px 1px 1px 1px); clip: rect(1px, 1px, 1px, 1px);',
            '.sv_main .sv-progress': 'height: 0.19em; background-color: $header-background-color;',
            '.sv_main .sv-progress__bar':
              'background-color: $main-color; height: 100%; position: relative;',
            '.sv_main .sv-paneldynamic__progress-container':
              'position: relative; display: inline-block; width: calc(100% - 250px); margin-top: 20px;',
            '.sv_main .sv-paneldynamic__add-btn': 'float: right;',
            '.sv_main .sv-paneldynamic__add-btn--list-mode': 'float: none; margin-top: 0;',
            '.sv_main .sv-paneldynamic__remove-btn ': 'margin-top: 1.25em;',
            '.sv_main .sv-paneldynamic__prev-btn, .sv_main .sv-paneldynamic__next-btn':
              'box-sizing: border-box; display: inline-block; cursor: pointer; width: 0.7em; top: -0.28em; position: absolute;',
            '.sv_main .sv-paneldynamic__prev-btn': 'left: -1.3em; transform: rotate(90deg);',
            '.sv_main .sv-paneldynamic__next-btn ': 'right: -1.3em; transform: rotate(270deg);',
            '.sv_main .sv-paneldynamic__prev-btn.sv-paneldynamic__prev-btn--disabled, .sv_main .sv-paneldynamic__next-btn.sv-paneldynamic__next-btn--disabled':
              'cursor: auto;',
            '.sv_main .sv-paneldynamic__progress-text':
              'font-weight: bold; font-size: 0.87em; margin-top: 0.69em; margin-left: 4em',
            '.sv_main .sv-boolean__switch':
              'display: inline-block; box-sizing: border-box; width: 63px; height: 24px; margin-right: 17px; margin-left: 21px; padding: 2px 3px; vertical-align: middle; border-radius: 12px; cursor: pointer;',
            '.sv_main .sv-boolean__slider':
              'display: inline-block; width: 20px; height: 20px; transition-duration: .4s; transition-property: margin-left; border: none; border-radius: 100%;',
            '.sv_main .sv-boolean__label': 'vertical-align: middle; cursor: pointer;',
            '.sv_main .sv-boolean--indeterminate  .sv-boolean__slider':
              'margin-left: calc(50% - 10px);',
            '.sv_main .sv-boolean--checked  .sv-boolean__slider': 'margin-left: calc(100% - 20px);',
            "[dir='rtl'] .sv-boolean__label ": 'float: right;',
            "[dir='rtl'] .sv-boolean--indeterminate .sv-boolean__slider":
              'margin-right: calc(50% - 0.625em);',
            "[dir='rtl'] .sv-boolean--checked .sv-boolean__slider":
              'margin-right: calc(100% - 1.25em);',
            "[dir='rtl'] .sv-boolean__switch": 'float: right;',
            "[style*='direction:rtl'] .sv-boolean__label ": 'float: right;',
            "[style*='direction:rtl'] .sv-boolean--indeterminate .sv-boolean__slider":
              'margin-right: calc(50% - 0.625em);',
            "[style*='direction:rtl'] .sv-boolean--checked .sv-boolean__slider":
              'margin-right: calc(100% - 1.25em);',
            "[style*='direction:rtl'] .sv-boolean__switch": 'float: right;',
            '.sv_main .sv_q_num': '',
            '.sv_main .sv_q_num + span': '',
            '.sv_main .sjs_sp_container': 'position: relative;',
            '.sv_main .sjs_sp_controls': 'position: absolute; left: 0; bottom: 0;',
            '.sv_main .sjs_sp_controls > button': 'user-select: none;',
            '.sv_main .sjs_sp_container>div>canvas:focus': 'outline: none;',
            '.sv_main .sv_logo': '',
            '.sv_main .sv-logo--left':
              'display: inline-block; vertical-align: top; margin-right: 2em;',
            '.sv_main .sv-logo--right':
              'display: inline-block; vertical-align: top; margin-left: 2em; float: right;',
            '.sv_main .sv-logo--right+.sv-logo--right-tail': 'clear: both;',
            '.sv_main .sv-logo--top': 'display: block; width: 100%; text-align: center;',
            '.sv_main .sv-logo--bottom': 'display: block; width: 100%; text-align: center;',
            '.sv_main .sv_header__text':
              'display: inline-block; vertical-align: top; max-width: 100%',
          }),
          (e.Media = {
            '.sv_qstn fieldset .sv-q-col-1': {
              style: 'width: 100%;',
              media: '@media only screen and (max-width: 480px)',
            },
            '.sv_qstn fieldset .sv-q-col-2': {
              style: 'width: 100%;',
              media: '@media only screen and (max-width: 480px)',
            },
            '.sv_qstn fieldset .sv-q-col-3': {
              style: 'width: 100%;',
              media: '@media only screen and (max-width: 480px)',
            },
            '.sv_qstn fieldset .sv-q-col-4': {
              style: 'width: 100%;',
              media: '@media only screen and (max-width: 480px)',
            },
            '.sv_qstn fieldset .sv-q-col-5': {
              style: 'width: 100%;',
              media: '@media only screen and (max-width: 480px)',
            },
            '.sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn': {
              style: 'display: block; width: 100% !important;',
              media: '@media (max-width: 600px)',
            },
            '.sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn .title-left': {
              style: 'float: none;',
              media: '@media (max-width: 600px)',
            },
            '.sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn .sv_q_radiogroup_inline, .sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn .sv_q_checkbox_inline, .sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn .sv_q_imagepicker_inline': {
              style: 'display: block;',
              media: '@media (max-width: 600px)',
            },
            '.sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn table.table': {
              style: 'display: block;',
              media: '@media (max-width: 600px)',
            },
            '.sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn table.table thead': {
              style: 'display: none;',
              media: '@media (max-width: 600px)',
            },
            '.sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn table.table tbody, .sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn table.table tr, .sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn table.table td': {
              style: 'display: block;',
              media: '@media (max-width: 600px)',
            },
            '.sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn table.table:not(.sv_q_matrix) td:before': {
              style: 'content: attr(headers);',
              media: '@media (max-width: 600px)',
            },
            '.sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn table.sv_q_matrix td:after': {
              style: 'content: attr(headers); padding-left: 1em',
              media: '@media (max-width: 600px)',
            },
            '.sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn .radio label, .sv_main .sv_container .panel-body.card-block .sv_row .sv_qstn .checkbox label': {
              style: 'line-height: 12px; vertical-align: top;',
              media: '@media (max-width: 600px)',
            },
            '.sv_qstn label.sv_q_m_label': {
              style: 'display: inline;',
              media: '@media (max-width: 600px)',
            },
            '.sv_main .sv_custom_header': {
              style: 'display: none;',
              media: '@media (max-width: 1300px)',
            },
            '.sv_main .sv_container .sv_header h3': {
              style: 'font-size: 1.5em;',
              media: '@media (max-width: 1300px)',
            },
            '.sv_main .sv_container .sv_header h3 span': {
              style: 'font-size: 0.75em;',
              media: '@media (max-width: 700px)',
            },
            '.sv_main.sv_bootstrap_css .sv-progress__text': {
              style: 'margin-left: 8em;',
              media: '@media (min-width: 768px)',
            },
            '.sv_row': {
              style: ' display: flex; flex-wrap: wrap;',
              media: '@supports (display: flex)',
            },
            '.sv-vue-row-additional-div': {
              style: ' display: flex; flex-wrap: wrap; flex-basis: 100%;',
              media: '@supports (display: flex)',
            },
            '.sv-row > .sv-row__panel, .sv-row__question:not(:last-child)': {
              style: 'float: left;',
              media: '@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)',
            },
            "[dir='rtl'],[style*='direction:rtl'] .sv-row__question:not(:last-child)": {
              style: 'float: right;',
              media: '@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)',
            },
            '.sv-row > .sv-row__panel, .sv-row__question': {
              style: 'width: 100% !important; padding-right: 0 !important;',
              media: '@media only screen and (max-width: 600px)',
            },
          }),
          (e.ThemeColors = {
            default: {
              '$header-background-color': '#e7e7e7',
              '$body-container-background-color': '#f4f4f4',
              '$main-color': '#1ab394',
              '$main-hover-color': '#0aa384',
              '$body-background-color': 'white',
              '$inputs-background-color': 'white',
              '$text-color': '#6d7072',
              '$text-input-color': '#6d7072',
              '$header-color': '#6d7072',
              '$border-color': '#e7e7e7',
              '$error-color': '#ed5565',
              '$error-background-color': '#fd6575',
              '$progress-text-color': '#9d9d9d',
              '$disable-color': '#dbdbdb',
              '$disabled-label-color': 'rgba(64, 64, 64, 0.5)',
              '$slider-color': 'white',
              '$disabled-switch-color': '#9f9f9f',
              '$disabled-slider-color': '#cfcfcf',
            },
            orange: {
              '$header-background-color': '#4a4a4a',
              '$body-container-background-color': '#f8f8f8',
              '$main-color': '#f78119',
              '$main-hover-color': '#e77109',
              '$body-background-color': 'white',
              '$inputs-background-color': 'white',
              '$text-color': '#4a4a4a',
              '$text-input-color': '#4a4a4a',
              '$header-color': '#f78119',
              '$border-color': '#e7e7e7',
              '$error-color': '#ed5565',
              '$error-background-color': '#fd6575',
              '$progress-text-color': '#9d9d9d',
              '$disable-color': '#dbdbdb',
              '$disabled-label-color': 'rgba(64, 64, 64, 0.5)',
              '$slider-color': 'white',
              '$disabled-switch-color': '#9f9f9f',
              '$disabled-slider-color': '#cfcfcf',
            },
            darkblue: {
              '$header-background-color': '#d9d8dd',
              '$body-container-background-color': '#f6f7f2',
              '$main-color': '#3c4f6d',
              '$main-hover-color': '#2c3f5d',
              '$body-background-color': 'white',
              '$inputs-background-color': 'white',
              '$text-color': '#4a4a4a',
              '$text-input-color': '#4a4a4a',
              '$header-color': '#6d7072',
              '$border-color': '#e7e7e7',
              '$error-color': '#ed5565',
              '$error-background-color': '#fd6575',
              '$progress-text-color': '#9d9d9d',
              '$disable-color': '#dbdbdb',
              '$disabled-label-color': 'rgba(64, 64, 64, 0.5)',
              '$slider-color': 'white',
              '$disabled-switch-color': '#9f9f9f',
              '$disabled-slider-color': '#cfcfcf',
            },
            darkrose: {
              '$header-background-color': '#ddd2ce',
              '$body-container-background-color': '#f7efed',
              '$main-color': '#68656e',
              '$main-hover-color': '#58555e',
              '$body-background-color': 'white',
              '$inputs-background-color': 'white',
              '$text-color': '#4a4a4a',
              '$text-input-color': '#4a4a4a',
              '$header-color': '#6d7072',
              '$border-color': '#e7e7e7',
              '$error-color': '#ed5565',
              '$error-background-color': '#fd6575',
              '$progress-text-color': '#9d9d9d',
              '$disable-color': '#dbdbdb',
              '$disabled-label-color': 'rgba(64, 64, 64, 0.5)',
              '$slider-color': 'white',
              '$disabled-switch-color': '#9f9f9f',
              '$disabled-slider-color': '#cfcfcf',
            },
            stone: {
              '$header-background-color': '#cdccd2',
              '$body-container-background-color': '#efedf4',
              '$main-color': '#0f0f33',
              '$main-hover-color': '#191955',
              '$body-background-color': 'white',
              '$inputs-background-color': 'white',
              '$text-color': '#0f0f33',
              '$text-input-color': '#0f0f33',
              '$header-color': '#0f0f33',
              '$border-color': '#e7e7e7',
              '$error-color': '#ed5565',
              '$error-background-color': '#fd6575',
              '$progress-text-color': '#9d9d9d',
              '$disable-color': '#dbdbdb',
              '$disabled-label-color': 'rgba(64, 64, 64, 0.5)',
              '$slider-color': 'white',
              '$disabled-switch-color': '#9f9f9f',
              '$disabled-slider-color': '#cfcfcf',
            },
            winter: {
              '$header-background-color': '#82b8da',
              '$body-container-background-color': '#dae1e7',
              '$main-color': '#3c3b40',
              '$main-hover-color': '#1e1d20',
              '$body-background-color': 'white',
              '$inputs-background-color': 'white',
              '$text-color': '#000',
              '$text-input-color': '#000',
              '$header-color': '#000',
              '$border-color': '#e7e7e7',
              '$error-color': '#ed5565',
              '$error-background-color': '#fd6575',
              '$disable-color': '#dbdbdb',
              '$progress-text-color': '#9d9d9d',
              '$disabled-label-color': 'rgba(64, 64, 64, 0.5)',
              '$slider-color': 'white',
              '$disabled-switch-color': '#9f9f9f',
              '$disabled-slider-color': '#cfcfcf',
            },
            winterstone: {
              '$header-background-color': '#323232',
              '$body-container-background-color': '#f8f8f8',
              '$main-color': '#5ac8fa',
              '$main-hover-color': '#06a1e7',
              '$body-background-color': 'white',
              '$inputs-background-color': 'white',
              '$text-color': '#000',
              '$text-input-color': '#000',
              '$header-color': '#fff',
              '$border-color': '#e7e7e7',
              '$error-color': '#ed5565',
              '$error-background-color': '#fd6575',
              '$disable-color': '#dbdbdb',
              '$progress-text-color': '#9d9d9d',
              '$disabled-label-color': 'rgba(64, 64, 64, 0.5)',
              '$slider-color': 'white',
              '$disabled-switch-color': '#9f9f9f',
              '$disabled-slider-color': '#cfcfcf',
            },
            modern: {
              '$main-color': '#1ab394',
              '$add-button-color': '#1948b3',
              '$remove-button-color': '#ff1800',
              '$disable-color': '#dbdbdb',
              '$progress-text-color': '#9d9d9d',
              '$disabled-label-color': 'rgba(64, 64, 64, 0.5)',
              '$slider-color': 'white',
              '$disabled-switch-color': '#9f9f9f',
              '$disabled-slider-color': '#cfcfcf',
              '$error-color': '#d52901',
              '$text-color': '#404040',
              '$light-text-color': '#fff',
              '$button-text-color': '#fff',
              '$checkmark-color': '#fff',
              '$matrix-text-checked-color': '#fff',
              '$text-input-color': '#404040',
              '$inputs-background-color': 'transparent',
              '$main-hover-color': '#9f9f9f',
              '$body-container-background-color': '#f4f4f4',
              '$text-border-color': '#d4d4d4',
              '$disabled-text-color': 'rgba(64, 64, 64, 0.5)',
              '$border-color': 'rgb(64, 64, 64, 0.5)',
              '$dropdown-border-color': '#d4d4d4',
              '$header-background-color': '#e7e7e7',
              '$answer-background-color': 'rgba(26, 179, 148, 0.2)',
              '$error-background-color': 'rgba(213, 41, 1, 0.2)',
              '$radio-checked-color': '#404040',
              '$clean-button-color': '#1948b3',
              '$body-background-color': '#ffffff',
            },
            bootstrap: {
              '$main-color': '#18a689',
              '$text-color': '#404040;',
              '$text-input-color': '#404040;',
              '$progress-text-color': '#9d9d9d',
              '$disable-color': '#dbdbdb',
              '$header-background-color': '#e7e7e7',
              '$disabled-label-color': 'rgba(64, 64, 64, 0.5)',
              '$slider-color': 'white',
              '$disabled-switch-color': '#9f9f9f',
              '$disabled-slider-color': '#cfcfcf',
            },
            bootstrapmaterial: {
              '$main-color': '#18a689',
              '$text-color': '#404040;',
              '$text-input-color': '#404040;',
              '$progress-text-color': '#9d9d9d',
              '$disable-color': '#dbdbdb',
              '$header-background-color': '#e7e7e7',
              '$disabled-label-color': 'rgba(64, 64, 64, 0.5)',
              '$slider-color': 'white',
              '$disabled-switch-color': '#9f9f9f',
              '$disabled-slider-color': '#cfcfcf',
              '$body-background-color': '#ffffff',
            },
          }),
          (e.ThemeCss = {
            '.sv_default_css': 'background-color: $body-container-background-color;',
            '.sv_default_css hr': 'border-color: $border-color;',
            ".sv_default_css input[type='button'], .sv_default_css button":
              'color: $body-background-color; background-color: $main-color;',
            ".sv_default_css input[type='button']:hover, .sv_default_css button:hover":
              'background-color: $main-hover-color;',
            '.sv_default_css .sv_header': 'color: $header-color;',
            '.sv_default_css .sv_custom_header': 'background-color: $header-background-color;',
            '.sv_default_css .sv_container': 'color: $text-color;',
            '.sv_default_css .sv_body':
              'background-color: $body-background-color; border-color: $main-color;',
            '.sv_default_css .sv_progress': 'background-color: $border-color;',
            '.sv_default_css .sv_progress_bar': 'background-color: $main-color;',
            '.sv_default_css .sv_p_root > .sv_row': 'border-color: $border-color;',
            '.sv_default_css .sv_p_root > .sv_row:nth-child(odd)':
              'background-color: $body-background-color;',
            '.sv_default_css .sv_p_root > .sv_row:nth-child(even)':
              'background-color: $body-container-background-color;',
            '.sv_default_css .sv_q_other input':
              'color: $text-color; -webkit-text-fill-color: $text-color; border-color: $border-color; background-color: $inputs-background-color;',
            '.sv_default_css .sv_q_text_root':
              'color: $text-color; -webkit-text-fill-color: $text-color; border-color: $border-color; background-color: $inputs-background-color;',
            '.sv_default_css .sv_q_dropdown_control':
              'color: $text-input-color; border-color: $border-color; background-color: $inputs-background-color;',
            ".sv_default_css input[type='text']":
              'color: $text-color; -webkit-text-fill-color: $text-color; border-color: $border-color; background-color: $inputs-background-color;',
            '.sv_default_css select':
              'color: $text-color; border-color: $border-color; background-color: $inputs-background-color;',
            '.sv_default_css textarea':
              'color: $text-input-color; -webkit-text-fill-color: $text-input-color; border-color: $border-color; background-color: $inputs-background-color;',
            ".sv_default_css input:not([type='button']):not([type='reset']):not([type='submit']):not([type='image']):not([type='checkbox']):not([type='radio'])":
              'border: 1px solid $border-color; background-color: $inputs-background-color;color: $text-input-color; -webkit-text-fill-color: $text-input-color;',
            ".sv_default_css input:not([type='button']):not([type='reset']):not([type='submit']):not([type='image']):not([type='checkbox']):not([type='radio']):focus":
              'border: 1px solid $main-color;',
            '.sv_default_css .sv_container .sv_body .sv_p_root .sv_q .sv_select_wrapper .sv_q_dropdown_control ':
              'background-color: $inputs-background-color;',
            '.sv_default_css .sv_q_other input:focus': 'border-color: $main-color;',
            '.sv_default_css .sv_q_text_root:focus': 'border-color: $main-color;',
            '.sv_default_css .sv_q_dropdown_control:focus': 'border-color: $main-color;',
            ".sv_default_css input[type='text']:focus": 'border-color: $main-color;',
            '.sv_default_css .sv_container .sv_body .sv_p_root .sv_q input[type="radio"]:focus, .sv_default_css .sv_container .sv_body .sv_p_root .sv_q input[type="checkbox"]:focus':
              'outline: 1px dotted $main-color;',
            '.sv_default_css select:focus': 'border-color: $main-color;',
            '.sv_default_css textarea:focus': 'border-color: $main-color;',
            '.sv_default_css .sv_select_wrapper': 'background-color: $body-background-color;',
            '.sv_default_css .sv_select_wrapper::before': 'background-color: $main-color;',
            '.sv_default_css .sv_q_rating_item.active .sv_q_rating_item_text':
              'background-color: $main-hover-color; border-color: $main-hover-color; color: $body-background-color;',
            '.sv_default_css .sv_q_rating_item .sv_q_rating_item_text':
              'border-color: $border-color;',
            '.sv_default_css .sv_q_rating_item .sv_q_rating_item_text:hover':
              'border-color: $main-hover-color;',
            '.sv_default_css table.sv_q_matrix tr': 'border-color: $border-color;',
            '.sv_default_css table.sv_q_matrix_dropdown tr': 'border-color: $border-color;',
            '.sv_default_css table.sv_q_matrix_dynamic tr': 'border-color: $border-color;',
            '.sv_default_css .sv_q_m_cell_selected':
              'color: $body-background-color; background-color: $main-hover-color;',
            '.sv_main .sv_q_file_remove:hover': 'color: $main-color;',
            '.sv_main .sv_q_file_choose_button':
              'color: $body-background-color; background-color: $main-color;',
            '.sv_main .sv_q_file_choose_button:hover': 'background-color: $main-hover-color;',
            '.sv_main .sv_q_imgsel.checked label>div': 'background-color: $main-color',
            '.sv_default_css .sv_p_description': 'padding-left: 1.29em;',
            '.sv_main .sv-progress': 'background-color: $header-background-color;',
            '.sv_main .sv-progress__bar': 'background-color: $main-color;',
            '.sv_main .sv-paneldynamic__prev-btn.sv-paneldynamic__prev-btn--disabled, .sv_main .sv-paneldynamic__next-btn.sv-paneldynamic__next-btn--disabled':
              'fill: $disable-color;',
            '.sv_main .sv-paneldynamic__progress-text': 'color: $progress-text-color;',
            '.sv_main .sv-paneldynamic__prev-btn, .sv_main .sv-paneldynamic__next-btn':
              'fill: $text-color',
            '.sv_main .sv-boolean__switch': 'background-color: $main-color;',
            '.sv_main .sv-boolean__slider': 'background-color: $slider-color;',
            '.sv_main .sv-boolean__label--disabled': 'color: $disabled-label-color;',
            '.sv_main .sv-boolean--disabled .sv-boolean__switch':
              'background-color: $disabled-switch-color;',
            '.sv_main .sv-boolean--disabled .sv-boolean__slider':
              'background-color: $disabled-slider-color;',
            '.sv_main .sv_matrix_detail_row':
              'background-color: #ededed; border-top: 1px solid $header-background-color; border-bottom: 1px solid $header-background-color;',
          }),
          (e.modernThemeCss = {
            '.sv-boolean__switch': 'background-color: $main-color;',
            '.sv-boolean__slider': 'background-color: $slider-color;',
            '.sv-boolean__label--disabled': 'color: $disabled-label-color;',
            '.sv-boolean--disabled .sv-boolean__switch':
              'background-color: $disabled-switch-color;',
            '.sv-boolean--disabled .sv-boolean__slider':
              'background-color: $disabled-slider-color;',
            '.sv-btn': 'color: $button-text-color;',
            '.sv-checkbox__svg': 'border-color: $border-color; fill: transparent;',
            '.sv-checkbox--allowhover:hover .sv-checkbox__svg':
              'background-color: $main-hover-color; fill: $checkmark-color;',
            '.sv-checkbox--checked .sv-checkbox__svg':
              'background-color: $main-color; fill: $checkmark-color;',
            '.sv-checkbox--checked.sv-checkbox--disabled .sv-checkbox__svg':
              'background-color: $disable-color; fill: $checkmark-color;',
            '.sv-checkbox--disabled .sv-checkbox__svg': 'border-color: $disable-color;',
            '.sv-comment': 'border-color: $text-border-color;',
            '.sv-comment:focus': 'border-color: $main-color;',
            '.sv-completedpage':
              'color: $text-color; background-color: $body-container-background-color;',
            '.sv-container-modern': 'color: $text-color;',
            '.sv-container-modern__title': 'color: $main-color;',
            '.sv-description': 'color: $disabled-text-color;',
            '.sv-dropdown': 'border-bottom: 0.06em solid $text-border-color;',
            '.sv-dropdown:focus': 'border-color: $dropdown-border-color;',
            '.sv-dropdown--error': 'border-color: $error-color; color: $error-color;',
            '.sv-dropdown--error::placeholder': 'color: $error-color;',
            '.sv-dropdown--error::-ms-input-placeholder': 'color: $error-color;',
            '.sv-file__decorator': 'background-color: $body-container-background-color;',
            '.sv-file__clean-btn': 'background-color: $remove-button-color;',
            '.sv-file__choose-btn': 'background-color: $add-button-color;',
            '.sv-file__choose-btn--disabled': 'background-color: $disable-color;',
            '.sv-file__remove-svg': 'fill: #ff1800;',
            '.sv-file__sign a': 'color: $text-color;',
            '.sv-footer__complete-btn': 'background-color: $main-color;',
            '.sv-footer__next-btn': 'background-color: $main-color;',
            '.sv-footer__prev-btn': 'background-color: $main-color;',
            '.sv-footer__start-btn': 'background-color: $main-color;',
            '.sv-footer__preview-btn': 'background-color: $main-color;',
            '.sv-footer__edit-btn': 'background-color: $main-color;',
            '.sv-imagepicker__item--allowhover:hover .sv-imagepicker__image':
              'background-color: $main-hover-color; border-color: $main-hover-color;',
            '.sv-imagepicker__item--checked .sv-imagepicker__image':
              'background-color: $main-color; border-color: $main-color;',
            '.sv-imagepicker__item--disabled.sv-imagepicker__item--checked .sv-imagepicker__image':
              'background-color: $disable-color; border-color: $disable-color;',
            '.sv-item__control:focus + .sv-item__decorator': 'border-color: $main-color;',
            '.sv-matrix__text--checked':
              'color: $matrix-text-checked-color; background-color: $main-color;',
            '.sv-matrix__text--disabled.sv-matrix__text--checked':
              'background-color: $disable-color;',
            '.sv-matrixdynamic__add-btn': 'background-color: $add-button-color;',
            '.sv-matrixdynamic__remove-btn': 'background-color: $remove-button-color;',
            '.sv-paneldynamic__add-btn': 'background-color: $add-button-color;',
            '.sv-paneldynamic__remove-btn': 'background-color: $remove-button-color;',
            '.sv-paneldynamic__prev-btn, .sv-paneldynamic__next-btn': 'fill: $text-color;',
            '.sv-paneldynamic__prev-btn--disabled, .sv-paneldynamic__next-btn--disabled':
              'fill: $disable-color;',
            '.sv-paneldynamic__progress-text': 'color: $progress-text-color;',
            '.sv-progress': 'background-color: $header-background-color;',
            '.sv-progress__bar': 'background-color: $main-color;',
            '.sv-progress__text': 'color: $progress-text-color;',
            '.sv-question__erbox': 'color: $error-color;',
            '.sv-question__title--answer': 'background-color: $answer-background-color;',
            '.sv-question__title--error': 'background-color: $error-background-color;',
            '.sv-panel__title--error': 'background-color: $error-background-color;',
            '.sv-radio__svg': 'border-color: $border-color; fill: transparent;',
            '.sv-radio--allowhover:hover .sv-radio__svg': 'fill: $border-color;',
            '.sv-radio--checked .sv-radio__svg':
              'border-color: $radio-checked-color; fill: $radio-checked-color;',
            '.sv-radio--disabled .sv-radio__svg': 'border-color: $disable-color;',
            '.sv-radio--disabled.sv-radio--checked .sv-radio__svg': 'fill: $disable-color;',
            '.sv-rating': 'color: $text-color;',
            '.sv-rating input:focus + .sv-rating__min-text + .sv-rating__item-text, .sv-rating input:focus + .sv-rating__item-text':
              'outline-color: $main-color;',
            '.sv-rating__item-text':
              'color: $main-hover-color; border: solid 0.1875em $main-hover-color;',
            '.sv-rating__item-text:hover':
              'background-color: $main-hover-color; color: $body-background-color;',
            '.sv-rating__item--selected .sv-rating__item-text':
              'background-color: $main-color; color: $body-background-color; border-color: $main-color;',
            '.sv-rating--disabled .sv-rating__item-text':
              'color: $disable-color; border-color: $disable-color;',
            '.sv-rating--disabled .sv-rating__item-text:hover': 'background-color: transparent;',
            '.sv-rating--disabled .sv-rating__item-text:hover .sv-rating__item--selected .sv-rating__item-text':
              'background-color: $disable-color; color: $body-background-color;',
            '::-webkit-scrollbar': 'background-color: $main-hover-color;',
            '::-webkit-scrollbar-thumb': 'background: $main-color;',
            '.sv-selectbase__clear-btn': 'background-color: $clean-button-color;',
            '.sv-table': 'background-color: rgba($main-hover-color, 0.1);',
            '.sv-text:focus': 'border-color: $main-color;',
            '.sv-text[type="date"]::-webkit-calendar-picker-indicator':
              'color: transparent; background: transparent;',
            '.sv-text--error': 'color: $error-color; border-color: $error-color;',
            '.sv-text--error::placeholder': 'color: $error-color;',
            '.sv-text--error::-ms-placeholder': 'color: $error-color;',
            '.sv-text--error:-ms-placeholder': 'color: $error-color;',
            'input.sv-text, textarea.sv-comment, select.sv-dropdown':
              'color: $text-input-color; background-color: $inputs-background-color;',
            '.sv-text::placeholder': 'color: $text-input-color;',
            '.sv-text::-ms-placeholder': 'color: $text-input-color;',
            '.sv-text:-ms-placeholder': 'color: $text-input-color;',
            '.sv-table__row--detail': 'background-color: $header-background-color;',
          }),
          (e.bootstrapThemeCss = {
            '.sv_main .sv_q_imgsel.checked label>div': 'background-color: $main-color',
            '.sv_main .sv_p_description': 'padding-left: 1.66em;',
            '.sv_main .sv_qstn_error_bottom': 'margin-top: 20px; margin-bottom: 0;',
            '.sv_main .progress': 'width: 60%;',
            '.sv_main .progress-bar': 'width: auto; margin-left: 2px; margin-right: 2px;',
            '.sv_main .table>tbody>tr>td': 'min-width: 90px;',
            '.sv_main f-panel .sv_qstn': 'padding: 0; vertical-align: middle;',
            '.sv_main .sv_q_image': 'display: inline-block;',
            '.sv_main .sv-progress': 'background-color: $header-background-color;',
            '.sv_main .sv-progress__bar': 'background-color: $main-color;',
            '.sv_main .sv-paneldynamic__prev-btn.sv-paneldynamic__prev-btn--disabled, .sv_main .sv-paneldynamic__next-btn.sv-paneldynamic__next-btn--disabled':
              'fill: $disable-color;',
            '.sv_main .sv-paneldynamic__progress-text': 'color: $progress-text-color;',
            '.sv_main .sv-paneldynamic__prev-btn, .sv_main .sv-paneldynamic__next-btn':
              'fill: $text-color',
            '.sv_main .sv-boolean__switch': 'background-color: $main-color;',
            '.sv_main .sv-boolean__slider': 'background-color: $slider-color;',
            '.sv_main .sv-boolean__label--disabled': 'color: $disabled-label-color;',
            '.sv_main .sv-boolean--disabled .sv-boolean__switch':
              'background-color: $disabled-switch-color;',
            '.sv_main .sv-boolean--disabled  .sv-boolean__slider':
              'background-color: $disabled-slider-color;',
            '.sv_main .sv_matrix_detail_row':
              'background-color: #ededed; border-top: 1px solid $header-background-color; border-bottom: 1px solid $header-background-color;',
          }),
          (e.bootstrapmaterialThemeCss = {
            '.sv_main.sv_bootstrapmaterial_css .form-group.is-focused .form-control':
              'linear-gradient(0deg, $main-color 2px, $main-color 0),linear-gradient(0deg, #D2D2D2 1px, transparent 0);',
            '.sv_main.sv_bootstrapmaterial_css .sv_qstn': 'margin-bottom: 1rem;',
            '.sv_main.sv_bootstrapmaterial_css .sv_qstn label.sv_q_m_label': 'height: 100%;',
            '.sv_main.sv_bootstrapmaterial_css .sv_q_image': 'display: inline-block;',
            '.sv_main.sv_bootstrapmaterial_css .checkbox input[type=checkbox]:checked + .checkbox-material .check':
              'border-color: $main-color;',
            '.sv_main.sv_bootstrapmaterial_css label.checkbox-inline input[type=checkbox]:checked + .checkbox-material .check':
              'border-color: $main-color;',
            '.sv_main.sv_bootstrapmaterial_css .checkbox input[type=checkbox]:checked + .checkbox-material .check:before':
              'color: $main-color;',
            '.sv_main.sv_bootstrapmaterial_css label.checkbox-inline input[type=checkbox]:checked + .checkbox-material .check:before':
              'color: $main-color;',
            '.sv_main.sv_bootstrapmaterial_css .radio input[type=radio]:checked ~ .circle':
              'border-color: $main-color;',
            '.sv_main.sv_bootstrapmaterial_css label.radio-inline input[type=radio]:checked ~ .circle':
              'border-color: $main-color;',
            '.sv_main.sv_bootstrapmaterial_css .radio input[type=radio]:checked ~ .check':
              'background-color: $main-color;',
            '.sv_main.sv_bootstrapmaterial_css label.radio-inline input[type=radio]:checked ~ .check':
              'background-color: $main-color;',
            '.sv_main.sv_bootstrapmaterial_css .btn-default.active':
              'background-color: $main-color; color: $body-background-color;',
            '.sv_main.sv_bootstrapmaterial_css .btn-default:active':
              'background-color: $main-color; color: $body-background-color;',
            '.sv_main.sv_bootstrapmaterial_css .btn-secondary.active':
              'background-color: $main-color; color: $body-background-color;',
            '.sv_main.sv_bootstrapmaterial_css .btn-secondary:active':
              'background-color: $main-color; color: $body-background-color;',
            '.sv_main.sv_bootstrapmaterial_css .open>.dropdown-toggle.btn-default':
              'background-color: $main-color; color: $body-background-color;',
            ".sv_main.sv_bootstrapmaterial_css input[type='button'].btn-primary, .sv_main.sv_bootstrapmaterial_css button.btn-primary":
              'color: $body-background-color; background-color: $main-color;',
            ".sv_main.sv_bootstrapmaterial_css input[type='button'].btn-primary:hover, .sv_main.sv_bootstrapmaterial_css button.btn-primary:hover":
              'background-color: $main-hover-color;',
            '.sv_main .sv_q_imgsel.checked label>div': 'background-color: $main-color;',
            '.sv_main.sv_bootstrapmaterial_css .sv_q_file_remove:hover': 'color: $main-color;',
            '.sv_main.sv_bootstrapmaterial_css .form-group input[type=file]':
              'position: relative; opacity: 1;',
            '.sv_main.sv_bootstrapmaterial_css .progress': 'width: 60%; height: 1.5em;',
            '.sv_main.sv_bootstrapmaterial_css .progress-bar':
              'width: auto; margin-left: 2px; margin-right: 2px;',
            '.sv_main .sv-progress': 'background-color: $header-background-color;',
            '.sv_main .sv-progress__bar': 'background-color: $main-color;',
            '.sv_main .sv-paneldynamic__prev-btn.sv-paneldynamic__prev-btn--disabled, .sv_main .sv-paneldynamic__next-btn.sv-paneldynamic__next-btn--disabled':
              'fill: $disable-color;',
            '.sv_main .sv-paneldynamic__progress-text': 'color: $progress-text-color;',
            '.sv_main .sv-paneldynamic__prev-btn, .sv_main .sv-paneldynamic__next-btn':
              'fill: $text-color',
            '.sv_main .sv-boolean .checkbox-decorator': 'display: none;',
            '.sv_main .sv-boolean__switch': 'background-color: $main-color;',
            '.sv_main .sv-boolean__slider': 'background-color: $slider-color;',
            '.sv_main .sv-boolean__label.sv-boolean__label--disabled':
              'color: $disabled-label-color;',
            '.sv_main .sv-boolean__label': 'color: $text-color;',
            '.sv_main .sv-boolean--disabled .sv-boolean__switch':
              'background-color: $disabled-switch-color;',
            '.sv_main .sv-boolean--disabled  .sv-boolean__slider':
              'background-color: $disabled-slider-color;',
            '.sv_main .sv_matrix_detail_row':
              'background-color: #ededed; border-top: 1px solid $header-background-color; border-bottom: 1px solid $header-background-color;',
          }),
          (e.Enabled = !0),
          e
        );
      })();
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(6),
        o = n(12),
        a = n(2),
        s = n(1),
        l = n(10),
        u = n(4);
      n.d(t, 'h', function () {
        return c;
      }),
        n.d(t, 'f', function () {
          return p;
        }),
        n.d(t, 'a', function () {
          return d;
        }),
        n.d(t, 'd', function () {
          return h;
        }),
        n.d(t, 'g', function () {
          return f;
        }),
        n.d(t, 'c', function () {
          return m;
        }),
        n.d(t, 'e', function () {
          return g;
        }),
        n.d(t, 'b', function () {
          return y;
        }),
        n.d(t, 'i', function () {
          return v;
        });
      var c = (function () {
          function e(e, t) {
            void 0 === t && (t = null), (this.value = e), (this.error = t);
          }
          return e;
        })(),
        p = (function (e) {
          function t() {
            var t = e.call(this) || this;
            return t.createLocalizableString('text', t, !0), t;
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'text', {
              get: function () {
                return this.getLocalizableStringText('text');
              },
              set: function (e) {
                this.setLocalizableStringText('text', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isValidateAllValues', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locText', {
              get: function () {
                return this.getLocalizableString('text');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getErrorText = function (e) {
              return this.text ? this.text : this.getDefaultErrorText(e);
            }),
            (t.prototype.getDefaultErrorText = function (e) {
              return '';
            }),
            (t.prototype.validate = function (e, t, n, r) {
              return (
                void 0 === t && (t = null),
                void 0 === n && (n = null),
                void 0 === r && (r = null),
                null
              );
            }),
            Object.defineProperty(t.prototype, 'isRunning', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isAsync', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getLocale = function () {
              return this.errorOwner ? this.errorOwner.getLocale() : '';
            }),
            (t.prototype.getMarkdownHtml = function (e, t) {
              return this.errorOwner ? this.errorOwner.getMarkdownHtml(e, t) : null;
            }),
            (t.prototype.getProcessedText = function (e) {
              return this.errorOwner ? this.errorOwner.getProcessedText(e) : e;
            }),
            (t.prototype.createCustomError = function (e) {
              return new o.b(this.getErrorText(e), this.errorOwner);
            }),
            (t.prototype.toString = function () {
              var e = this.getType().replace('validator', '');
              return this.text && (e += ', ' + this.text), e;
            }),
            t
          );
        })(i.b),
        d = (function () {
          function e() {}
          return (
            (e.prototype.run = function (e) {
              var t = this,
                n = [],
                r = null,
                i = null;
              this.prepareAsyncValidators();
              for (var o = [], a = e.getValidators(), s = 0; s < a.length; s++) {
                var l = a[s];
                !r &&
                  l.isValidateAllValues &&
                  ((r = e.getDataFilteredValues()), (i = e.getDataFilteredProperties())),
                  l.isAsync &&
                    (this.asyncValidators.push(l),
                    (l.onAsyncCompleted = function (e) {
                      if ((e && e.error && o.push(e.error), t.onAsyncCompleted)) {
                        for (var n = 0; n < t.asyncValidators.length; n++)
                          if (t.asyncValidators[n].isRunning) return;
                        t.onAsyncCompleted(o);
                      }
                    }));
              }
              a = e.getValidators();
              for (var s = 0; s < a.length; s++) {
                var l = a[s],
                  u = l.validate(e.validatedValue, e.getValidatorTitle(), r, i);
                u && u.error && n.push(u.error);
              }
              return (
                0 == this.asyncValidators.length &&
                  this.onAsyncCompleted &&
                  this.onAsyncCompleted([]),
                n
              );
            }),
            (e.prototype.prepareAsyncValidators = function () {
              if (this.asyncValidators)
                for (var e = 0; e < this.asyncValidators.length; e++)
                  this.asyncValidators[e].onAsyncCompleted = null;
              this.asyncValidators = [];
            }),
            e
          );
        })(),
        h = (function (e) {
          function t(t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.call(this) || this;
            return (r.minValue = t), (r.maxValue = n), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'numericvalidator';
            }),
            (t.prototype.validate = function (e, t, n, r) {
              if (
                (void 0 === t && (t = null),
                void 0 === n && (n = null),
                void 0 === r && (r = null),
                u.a.isValueEmpty(e))
              )
                return null;
              if (!u.a.isNumber(e)) return new c(null, new o.d(null, this.errorOwner));
              var i = new c(parseFloat(e));
              return null !== this.minValue && this.minValue > i.value
                ? ((i.error = this.createCustomError(t)), i)
                : null !== this.maxValue && this.maxValue < i.value
                ? ((i.error = this.createCustomError(t)), i)
                : 'number' == typeof e
                ? null
                : i;
            }),
            (t.prototype.getDefaultErrorText = function (e) {
              var t = e || a.a.getString('value');
              return null !== this.minValue && null !== this.maxValue
                ? a.a.getString('numericMinMax').format(t, this.minValue, this.maxValue)
                : null !== this.minValue
                ? a.a.getString('numericMin').format(t, this.minValue)
                : a.a.getString('numericMax').format(t, this.maxValue);
            }),
            Object.defineProperty(t.prototype, 'minValue', {
              get: function () {
                return this.getPropertyValue('minValue');
              },
              set: function (e) {
                this.setPropertyValue('minValue', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'maxValue', {
              get: function () {
                return this.getPropertyValue('maxValue');
              },
              set: function (e) {
                this.setPropertyValue('maxValue', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(p),
        f = (function (e) {
          function t(t, n, r) {
            void 0 === t && (t = 0), void 0 === n && (n = 0), void 0 === r && (r = !0);
            var i = e.call(this) || this;
            return (i.minLength = t), (i.maxLength = n), (i.allowDigits = r), i;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'textvalidator';
            }),
            (t.prototype.validate = function (e, t, n, r) {
              if (
                (void 0 === t && (t = null),
                void 0 === n && (n = null),
                void 0 === r && (r = null),
                '' !== e && u.a.isValueEmpty(e))
              )
                return null;
              if (!this.allowDigits) {
                if (!/^[A-Za-z\s]*$/.test(e)) return new c(null, this.createCustomError(t));
              }
              return this.minLength > 0 && e.length < this.minLength
                ? new c(null, this.createCustomError(t))
                : this.maxLength > 0 && e.length > this.maxLength
                ? new c(null, this.createCustomError(t))
                : null;
            }),
            (t.prototype.getDefaultErrorText = function (e) {
              return this.minLength > 0 && this.maxLength > 0
                ? a.a.getString('textMinMaxLength').format(this.minLength, this.maxLength)
                : this.minLength > 0
                ? a.a.getString('textMinLength').format(this.minLength)
                : a.a.getString('textMaxLength').format(this.maxLength);
            }),
            Object.defineProperty(t.prototype, 'minLength', {
              get: function () {
                return this.getPropertyValue('minLength');
              },
              set: function (e) {
                this.setPropertyValue('minLength', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'maxLength', {
              get: function () {
                return this.getPropertyValue('maxLength');
              },
              set: function (e) {
                this.setPropertyValue('maxLength', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'allowDigits', {
              get: function () {
                return this.getPropertyValue('allowDigits', !0);
              },
              set: function (e) {
                this.setPropertyValue('allowDigits', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(p),
        m = (function (e) {
          function t(t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.call(this) || this;
            return (r.minCount = t), (r.maxCount = n), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'answercountvalidator';
            }),
            (t.prototype.validate = function (e, t, n, r) {
              if (
                (void 0 === t && (t = null),
                void 0 === n && (n = null),
                void 0 === r && (r = null),
                null == e || e.constructor != Array)
              )
                return null;
              var i = e.length;
              return this.minCount && i < this.minCount
                ? new c(
                    null,
                    this.createCustomError(a.a.getString('minSelectError').format(this.minCount))
                  )
                : this.maxCount && i > this.maxCount
                ? new c(
                    null,
                    this.createCustomError(a.a.getString('maxSelectError').format(this.maxCount))
                  )
                : null;
            }),
            (t.prototype.getDefaultErrorText = function (e) {
              return e;
            }),
            Object.defineProperty(t.prototype, 'minCount', {
              get: function () {
                return this.getPropertyValue('minCount');
              },
              set: function (e) {
                this.setPropertyValue('minCount', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'maxCount', {
              get: function () {
                return this.getPropertyValue('maxCount');
              },
              set: function (e) {
                this.setPropertyValue('maxCount', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(p),
        g = (function (e) {
          function t(t) {
            void 0 === t && (t = null);
            var n = e.call(this) || this;
            return (n.regex = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'regexvalidator';
            }),
            (t.prototype.validate = function (e, t, n, r) {
              if (
                (void 0 === t && (t = null),
                void 0 === n && (n = null),
                void 0 === r && (r = null),
                !this.regex || u.a.isValueEmpty(e))
              )
                return null;
              var i = new RegExp(this.regex);
              if (Array.isArray(e))
                for (var o = 0; o < e.length; o++) {
                  var a = this.hasError(i, e[o], t);
                  if (a) return a;
                }
              return this.hasError(i, e, t);
            }),
            (t.prototype.hasError = function (e, t, n) {
              return e.test(t) ? null : new c(t, this.createCustomError(n));
            }),
            Object.defineProperty(t.prototype, 'regex', {
              get: function () {
                return this.getPropertyValue('regex');
              },
              set: function (e) {
                this.setPropertyValue('regex', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(p),
        y = (function (e) {
          function t() {
            var t = e.call(this) || this;
            return (
              (t.re = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()=[\]\.,;:\s@\"]+\.)+[^<>()=[\]\.,;:\s@\"]{2,})$/i),
              t
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'emailvalidator';
            }),
            (t.prototype.validate = function (e, t, n, r) {
              return (
                void 0 === t && (t = null),
                void 0 === n && (n = null),
                void 0 === r && (r = null),
                e ? (this.re.test(e) ? null : new c(e, this.createCustomError(t))) : null
              );
            }),
            (t.prototype.getDefaultErrorText = function (e) {
              return a.a.getString('invalidEmail');
            }),
            t
          );
        })(p),
        v = (function (e) {
          function t(t) {
            void 0 === t && (t = null);
            var n = e.call(this) || this;
            return (n.conditionRunner = null), (n.isRunningValue = !1), (n.expression = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'expressionvalidator';
            }),
            Object.defineProperty(t.prototype, 'isValidateAllValues', {
              get: function () {
                return !0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isAsync', {
              get: function () {
                return !!this.ensureConditionRunner() && this.conditionRunner.isAsync;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isRunning', {
              get: function () {
                return this.isRunningValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.validate = function (e, t, n, r) {
              var i = this;
              if (
                (void 0 === t && (t = null),
                void 0 === n && (n = null),
                void 0 === r && (r = null),
                !this.ensureConditionRunner())
              )
                return null;
              (this.conditionRunner.onRunComplete = function (t) {
                (i.isRunningValue = !1),
                  i.onAsyncCompleted && i.onAsyncCompleted(i.generateError(t, e));
              }),
                (this.isRunningValue = !0);
              var o = this.conditionRunner.run(n, r);
              return this.conditionRunner.isAsync
                ? null
                : ((this.isRunningValue = !1), this.generateError(o, e));
            }),
            (t.prototype.generateError = function (e, t) {
              return e ? null : new c(t, this.createCustomError(name));
            }),
            (t.prototype.getDefaultErrorText = function (e) {
              return a.a.getString('invalidExpression').format(this.expression);
            }),
            (t.prototype.ensureConditionRunner = function () {
              return this.conditionRunner
                ? ((this.conditionRunner.expression = this.expression), !0)
                : !!this.expression && ((this.conditionRunner = new l.a(this.expression)), !0);
            }),
            Object.defineProperty(t.prototype, 'expression', {
              get: function () {
                return this.getPropertyValue('expression');
              },
              set: function (e) {
                this.setPropertyValue('expression', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(p);
      s.b.addClass('surveyvalidator', [{ name: 'text', serializationProperty: 'locText' }]),
        s.b.addClass(
          'numericvalidator',
          ['minValue:number', 'maxValue:number'],
          function () {
            return new h();
          },
          'surveyvalidator'
        ),
        s.b.addClass(
          'textvalidator',
          ['minLength:number', 'maxLength:number', 'allowDigits:boolean'],
          function () {
            return new f();
          },
          'surveyvalidator'
        ),
        s.b.addClass(
          'answercountvalidator',
          ['minCount:number', 'maxCount:number'],
          function () {
            return new m();
          },
          'surveyvalidator'
        ),
        s.b.addClass(
          'regexvalidator',
          ['regex'],
          function () {
            return new g();
          },
          'surveyvalidator'
        ),
        s.b.addClass(
          'emailvalidator',
          [],
          function () {
            return new y();
          },
          'surveyvalidator'
        ),
        s.b.addClass(
          'expressionvalidator',
          ['expression:condition'],
          function () {
            return new v();
          },
          'surveyvalidator'
        );
    },
    function (e, t, n) {
      'use strict';
      var r = n(14);
      n.d(t, 'a', function () {
        return i;
      });
      var i = {
        root: 'sv_main sv_bootstrap_css',
        container: 'sv_container',
        header: 'panel-heading card-header',
        body: 'panel-body card-block mt-4',
        bodyEmpty: 'panel-body card-block mt-4 sv_body_empty',
        footer: 'panel-footer card-footer',
        title: '',
        description: '',
        logo: 'sv_logo',
        logoImage: 'sv_logo__image',
        headerText: 'sv_header__text',
        navigationButton: '',
        completedPage: '',
        navigation: {
          complete: 'btn sv_complete_btn',
          prev: 'btn sv_prev_btn',
          next: 'btn sv_next_btn',
          start: 'btn sv_start_btn',
          preview: 'btn sv_preview_btn',
          edit: 'btn sv_edit_btn',
        },
        progress: 'progress center-block mx-auto mb-4',
        progressBar: 'progress-bar',
        progressTextUnderBar: 'sv-hidden',
        progressButtonsContainerCenter: 'sv_progress-buttons__container-center',
        progressButtonsContainer: 'sv_progress-buttons__container',
        progressButtonsImageButtonLeft: 'sv_progress-buttons__image-button-left',
        progressButtonsImageButtonRight: 'sv_progress-buttons__image-button-right',
        progressButtonsImageButtonHidden: 'sv_progress-buttons__image-button--hidden',
        progressButtonsListContainer: 'sv_progress-buttons__list-container',
        progressButtonsList: 'sv_progress-buttons__list',
        progressButtonsListElementPassed: 'sv_progress-buttons__list-element--passed',
        progressButtonsListElementCurrent: 'sv_progress-buttons__list-element--current',
        progressButtonsPageTitle: 'sv_progress-buttons__page-title',
        progressButtonsPageDescription: 'sv_progress-buttons__page-description',
        page: { root: '', title: '', description: 'small' },
        pageTitle: '',
        pageDescription: 'small',
        row: 'sv_row',
        question: {
          mainRoot: 'sv_qstn',
          flowRoot: 'sv_q_flow sv_qstn',
          header: '',
          headerLeft: 'title-left',
          content: '',
          contentLeft: 'content-left',
          titleLeftRoot: 'sv_qstn_left',
          title: '',
          titleExpandable: 'sv_q_title_expandable',
          number: 'sv_q_num',
          description: 'small',
          descriptionUnderInput: 'small',
          requiredText: 'sv_q_required_text',
          comment: 'form-control',
          required: '',
          titleRequired: '',
          hasError: 'has-error',
          indent: 20,
          formGroup: 'form-group',
        },
        panel: {
          title: 'sv_p_title',
          titleExpandable: 'sv_p_title_expandable',
          titleOnError: '',
          icon: 'sv_panel_icon',
          iconExpanded: 'sv_expanded',
          description: 'small sv_p_description',
          container: 'sv_p_container',
          footer: 'sv_p_footer',
          number: 'sv_q_num',
          requiredText: 'sv_q_required_text',
        },
        error: {
          root: 'alert alert-danger',
          icon: 'glyphicon glyphicon-exclamation-sign',
          item: '',
          locationTop: 'sv_qstn_error_top',
          locationBottom: 'sv_qstn_error_bottom',
        },
        boolean: {
          root: 'sv_qbln form-inline checkbox',
          item: 'sv-boolean',
          control: 'sv-visuallyhidden',
          itemChecked: 'sv-boolean--checked checked',
          itemIndeterminate: 'sv-boolean--indeterminate',
          itemDisabled: 'sv-boolean--disabled',
          switch: 'sv-boolean__switch',
          slider: 'sv-boolean__slider',
          label: 'sv-boolean__label ',
          disabledLabel: 'sv-boolean__label--disabled',
          materialDecorator: 'sv-item__decorator sv-boolean__decorator ',
          itemDecorator: 'sv-item__svg  sv-boolean__svg',
          checkedPath: 'sv-boolean__checked-path',
          uncheckedPath: 'sv-boolean__unchecked-path',
          indeterminatePath: 'sv-boolean__indeterminate-path',
        },
        checkbox: {
          root: 'sv_qcbc sv_qcbx form-inline',
          item: 'checkbox',
          itemChecked: 'checked',
          itemSelectAll: 'sv_q_checkbox_selectall',
          itemNone: 'sv_q_checkbox_none',
          itemInline: 'sv_q_checkbox_inline',
          itemControl: '',
          itemDecorator: 'sv-hidden',
          label: '',
          labelChecked: '',
          controlLabel: '',
          materialDecorator: 'checkbox-material',
          other: 'sv_q_checkbox_other form-control',
          column: 'sv_q_select_column',
        },
        comment: 'form-control',
        dropdown: { root: '', control: 'form-control', other: 'sv_q_dd_other form-control' },
        html: { root: '' },
        image: { root: 'sv_q_image', image: 'sv_image_image' },
        matrix: {
          root: 'table sv_q_matrix',
          label: 'sv_q_m_label',
          itemChecked: 'checked',
          itemDecorator: 'sv-hidden',
          cellText: 'sv_q_m_cell_text',
          cellTextSelected: 'sv_q_m_cell_selected bg-primary',
          cellLabel: 'sv_q_m_cell_label',
        },
        matrixdropdown: {
          root: 'table',
          cell: 'sv_matrix_cell',
          headerCell: 'sv_matrix_cell_header',
          row: 'sv_matrix_row',
          detailRow: 'sv_matrix_detail_row',
          detailRowText: 'sv_matrix_cell_detail_rowtext',
          detailCell: 'sv_matrix_cell_detail',
          detailButton: 'sv_matrix_cell_detail_button',
          detailButtonExpanded: 'sv_matrix_cell_detail_button_expanded',
          detailIcon: 'sv_detail_panel_icon',
          detailIconExpanded: 'sv_detail_expanded',
          detailPanelCell: 'sv_matrix_cell_detail_panel',
        },
        matrixdynamic: {
          root: 'table',
          button: 'button',
          buttonAdd: '',
          buttonRemove: '',
          iconAdd: '',
          iconRemove: '',
          headerCell: 'sv_matrix_cell_header',
          row: 'sv_matrix_row',
          detailRow: 'sv_matrix_detail_row',
          detailCell: 'sv_matrix_cell_detail',
          detailButton: 'sv_matrix_cell_detail_button',
          detailButtonExpanded: 'sv_matrix_cell_detail_button_expanded',
          detailIcon: 'sv_detail_panel_icon',
          detailIconExpanded: 'sv_detail_expanded',
          detailPanelCell: 'sv_matrix_cell_detail_panel',
        },
        paneldynamic: {
          root: '',
          navigation: 'sv-paneldynamic__navigation',
          progressTop: 'sv-paneldynamic__progress sv-paneldynamic__progress--top',
          progressBottom: 'sv-paneldynamic__progress sv-paneldynamic__progress--bottom',
          title: 'sv-title sv-question__title',
          button: 'button',
          buttonAdd: 'button sv-paneldynamic__add-btn',
          buttonRemove: 'button sv-paneldynamic__remove-btn',
          buttonPrev: 'sv-paneldynamic__prev-btn',
          buttonNext: 'sv-paneldynamic__next-btn',
          progressContainer: 'sv-paneldynamic__progress-container',
          progress: 'sv-progress',
          progressBar: 'sv-progress__bar',
          progressText: 'sv-paneldynamic__progress-text',
        },
        multipletext: {
          root: 'table',
          itemTitle: '',
          itemValue: 'sv_q_mt_item_value form-control',
        },
        radiogroup: {
          root: 'sv_qcbc form-inline',
          item: 'radio',
          itemChecked: 'checked',
          itemInline: 'sv_q_radiogroup_inline',
          label: '',
          labelChecked: '',
          itemControl: '',
          itemDecorator: 'sv-hidden',
          controlLabel: '',
          materialDecorator: 'circle',
          other: 'sv_q_radiogroup_other form-control',
          clearButton: 'sv_q_radiogroup_clear button',
          column: 'sv_q_select_column',
        },
        imagepicker: {
          root: 'sv_imgsel',
          item: 'sv_q_imgsel',
          itemChecked: 'checked',
          itemInline: 'sv_q_imagepicker_inline',
          label: 'sv_q_imgsel_label',
          itemControl: 'sv_q_imgsel_control_item',
          image: 'sv_q_imgsel_image',
          itemText: 'sv_q_imgsel_text',
          clearButton: 'sv_q_radiogroup_clear',
        },
        rating: {
          root: 'btn-group',
          item: 'btn btn-default btn-secondary',
          selected: 'active',
          minText: 'sv_q_rating_min_text',
          itemText: 'sv_q_rating_item_text',
          maxText: 'sv_q_rating_max_text',
          disabled: '',
        },
        text: 'form-control',
        expression: 'form-control',
        file: {
          root: 'sv_q_file',
          placeholderInput: 'sv_q_file_placeholder',
          preview: 'sv_q_file_preview',
          removeButton: 'sv_q_file_remove_button',
          fileInput: 'sv_q_file_input',
          removeFile: 'sv_q_file_remove',
          removeFileSvg: 'sv-hidden',
          fileDecorator: 'sv-hidden',
          fileSignBottom: 'sv-hidden',
          removeButtonBottom: 'sv-hidden',
        },
        signaturepad: {
          root: 'sv_q_signaturepad sjs_sp_container',
          controls: 'sjs_sp_controls',
          clearButton: 'sjs_sp_clear',
        },
        saveData: {
          root: '',
          saving: 'alert alert-info',
          error: 'alert alert-danger',
          success: 'alert alert-success',
          saveAgainButton: '',
        },
        window: {
          root: 'modal-content',
          body: 'modal-body',
          header: {
            root: 'modal-header panel-title',
            title: 'pull-left',
            button: 'glyphicon pull-right',
            buttonExpanded: 'glyphicon pull-right glyphicon-chevron-up',
            buttonCollapsed: 'glyphicon pull-right glyphicon-chevron-down',
          },
        },
      };
      r.b.bootstrap = i;
    },
    function (e, t, n) {
      'use strict';
      var r = n(14);
      n.d(t, 'a', function () {
        return i;
      });
      var i = {
        root: 'sv_main sv_bootstrapmaterial_css',
        container: 'sv_container',
        header: 'card-heading',
        body: 'card-body',
        bodyEmpty: 'card-body sv_body_empty',
        footer: 'card-footer',
        title: '',
        description: '',
        logo: 'sv_logo',
        logoImage: 'sv_logo__image',
        headerText: 'sv_header__text',
        navigationButton: '',
        completedPage: '',
        navigation: {
          complete: 'btn sv_complete_btn btn-primary',
          prev: 'btn sv_prev_btn btn-primary',
          next: 'btn sv_next_btn btn-primary',
          start: 'btn sv_start_btn btn-primary',
          preview: 'btn sv_preview_btn btn-primary',
          edit: 'btn sv_edit_btn btn-primary',
        },
        progress: 'progress center-block mx-auto mb-4',
        progressBar: 'progress-bar',
        progressTextUnderBar: 'sv-hidden',
        progressButtonsContainerCenter: 'sv_progress-buttons__container-center',
        progressButtonsContainer: 'sv_progress-buttons__container',
        progressButtonsImageButtonLeft: 'sv_progress-buttons__image-button-left',
        progressButtonsImageButtonRight: 'sv_progress-buttons__image-button-right',
        progressButtonsImageButtonHidden: 'sv_progress-buttons__image-button--hidden',
        progressButtonsListContainer: 'sv_progress-buttons__list-container',
        progressButtonsList: 'sv_progress-buttons__list',
        progressButtonsListElementPassed: 'sv_progress-buttons__list-element--passed',
        progressButtonsListElementCurrent: 'sv_progress-buttons__list-element--current',
        progressButtonsPageTitle: 'sv_progress-buttons__page-title',
        progressButtonsPageDescription: 'sv_progress-buttons__page-description',
        page: { root: '', title: '', description: 'small' },
        pageTitle: '',
        pageDescription: 'small',
        row: 'sv_row',
        question: {
          mainRoot: 'sv_qstn form-group bmd-form-group',
          flowRoot: 'sv_q_flow form-group bmd-form-group',
          header: '',
          headerLeft: 'title-left',
          content: '',
          contentLeft: 'content-left',
          titleLeftRoot: 'sv_qstn_left',
          requiredText: 'sv_q_required_text',
          title: '',
          titleExpandable: 'sv_q_title_expandable',
          number: 'sv_q_num',
          description: 'small',
          descriptionUnderInput: 'small',
          comment: 'form-control',
          required: '',
          titleRequired: '',
          hasError: 'has-error',
          indent: 20,
          formGroup: 'form-group bmd-form-group',
        },
        panel: {
          title: 'sv_p_title',
          titleExpandable: 'sv_p_title_expandable',
          titleOnError: '',
          icon: 'sv_panel_icon',
          iconExpanded: 'sv_expanded',
          description: 'small sv_p_description',
          container: 'sv_p_container',
          footer: 'sv_p_footer',
          number: 'sv_q_num',
          requiredText: 'sv_q_required_text',
        },
        error: {
          root: 'alert alert-danger',
          icon: 'glyphicon glyphicon-exclamation-sign',
          item: '',
          locationTop: 'sv_qstn_error_top',
          locationBottom: 'sv_qstn_error_bottom',
        },
        boolean: {
          root: 'sv_qbln checkbox',
          item: 'sv-boolean',
          control: 'sv-visuallyhidden',
          itemChecked: 'sv-boolean--checked checked',
          itemIndeterminate: 'sv-boolean--indeterminate',
          itemDisabled: 'sv-boolean--disabled',
          switch: 'sv-boolean__switch',
          slider: 'sv-boolean__slider',
          label: 'sv-boolean__label ',
          disabledLabel: 'sv-boolean__label--disabled',
          materialDecorator: 'sv-item__decorator sv-boolean__decorator ',
          itemDecorator: 'sv-item__svg  sv-boolean__svg',
          checkedPath: 'sv-boolean__checked-path',
          uncheckedPath: 'sv-boolean__unchecked-path',
          indeterminatePath: 'sv-boolean__indeterminate-path',
        },
        checkbox: {
          root: 'sv_qcbx',
          item: 'checkbox',
          itemChecked: 'checked',
          itemSelectAll: 'sv_q_checkbox_selectall',
          itemNone: 'sv_q_checkbox_none',
          itemInline: 'sv_q_checkbox_inline',
          itemDecorator: 'sv-hidden',
          itemControl: '',
          label: '',
          labelChecked: '',
          controlLabel: '',
          materialDecorator: 'checkbox-decorator',
          other: 'sv_q_checkbox_other form-control',
          column: 'sv_q_select_column',
        },
        comment: 'form-control',
        dropdown: { root: '', control: 'form-control', other: 'sv_q_dd_other form-control' },
        html: { root: '' },
        image: { root: 'sv_q_image', image: 'sv_image_image' },
        matrix: {
          root: 'table sv_q_matrix',
          row: 'form-group bmd-form-group',
          label: 'sv_q_m_label radio-inline',
          cellText: 'sv_q_m_cell_text',
          cellTextSelected: 'sv_q_m_cell_selected bg-primary',
          cellLabel: 'sv_q_m_cell_label',
          itemValue: 'form-control',
          itemChecked: 'checked',
          itemDecorator: 'sv-hidden',
          materialDecorator: 'bmd-radio',
        },
        matrixdropdown: {
          root: 'table',
          itemValue: 'form-group bmd-form-group',
          headerCell: 'sv_matrix_cell_header',
          row: 'sv_matrix_row',
          detailRow: 'sv_matrix_detail_row',
          detailRowText: 'sv_matrix_cell_detail_rowtext',
          detailCell: 'sv_matrix_cell_detail',
          detailButton: 'sv_matrix_cell_detail_button',
          detailButtonExpanded: 'sv_matrix_cell_detail_button_expanded',
          detailIcon: 'sv_detail_panel_icon',
          detailIconExpanded: 'sv_detail_expanded',
          detailPanelCell: 'sv_matrix_cell_detail_panel',
        },
        matrixdynamic: {
          mainRoot: 'sv_qstn',
          flowRoot: 'sv_q_flow',
          root: 'table',
          button: 'btn btn-primary',
          itemValue: 'form-group bmd-form-group',
          buttonAdd: '',
          buttonRemove: '',
          iconAdd: '',
          iconRemove: '',
          headerCell: 'sv_matrix_cell_header',
          row: 'sv_matrix_row',
          detailRow: 'sv_matrix_detail_row',
          detailCell: 'sv_matrix_cell_detail',
          detailButton: 'sv_matrix_cell_detail_button',
          detailButtonExpanded: 'sv_matrix_cell_detail_button_expanded',
          detailIcon: 'sv_detail_panel_icon',
          detailIconExpanded: 'sv_detail_expanded',
          detailPanelCell: 'sv_matrix_cell_detail_panel',
        },
        paneldynamic: {
          root: '',
          navigation: 'sv-paneldynamic__navigation',
          progressTop: 'sv-paneldynamic__progress sv-paneldynamic__progress--top',
          progressBottom: 'sv-paneldynamic__progress sv-paneldynamic__progress--bottom',
          title: 'sv-title sv-question__title',
          button: 'button',
          buttonAdd: 'button sv-paneldynamic__add-btn btn btn-primary',
          buttonRemove: 'button sv-paneldynamic__remove-btn btn btn-primary',
          buttonPrev: 'sv-paneldynamic__prev-btn',
          buttonNext: 'sv-paneldynamic__next-btn',
          progressContainer: 'sv-paneldynamic__progress-container',
          progress: 'sv-progress',
          progressBar: 'sv-progress__bar',
          progressText: 'sv-paneldynamic__progress-text',
        },
        multipletext: {
          root: 'table',
          itemTitle: '',
          row: 'form-group bmd-form-group',
          itemValue: 'sv_q_mt_item_value form-control',
        },
        radiogroup: {
          root: '',
          item: 'radio',
          itemChecked: 'checked',
          itemInline: 'sv_q_radiogroup_inline',
          itemDecorator: 'sv-hidden',
          label: '',
          labelChecked: '',
          itemControl: '',
          controlLabel: 'sv_q_radiogroup_control_label',
          materialDecorator: 'bmd-radio',
          other: 'sv_q_radiogroup_other form-control',
          clearButton: 'sv_q_radiogroup_clear button btn btn-primary',
          column: 'sv_q_select_column',
        },
        imagepicker: {
          root: 'sv_imgsel',
          item: 'sv_q_imgsel',
          itemChecked: 'checked',
          itemInline: 'sv_q_imagepicker_inline',
          label: 'sv_q_imgsel_label',
          itemControl: 'sv_q_imgsel_control_item',
          image: 'sv_q_imgsel_image',
          itemText: 'sv_q_imgsel_text',
          clearButton: 'sv_q_radiogroup_clear',
        },
        rating: {
          root: 'btn-group',
          item: 'btn btn-default btn-secondary',
          selected: 'active',
          minText: 'sv_q_rating_min_text',
          itemText: 'sv_q_rating_item_text',
          maxText: 'sv_q_rating_max_text',
          disabled: '',
        },
        text: 'form-control',
        expression: 'form-control',
        file: {
          root: 'form-group bmd-form-group is-fileinput sv_q_file',
          placeholderInput: '',
          preview: 'sv_q_file_preview',
          removeButton: 'sv_q_file_remove_button',
          fileInput: 'sv_q_file_input',
          removeFile: 'sv_q_file_remove',
          removeFileSvg: 'sv-hidden',
          fileDecorator: 'sv-hidden',
          fileSignBottom: 'sv-hidden',
          removeButtonBottom: 'sv-hidden',
        },
        signaturepad: {
          root: 'sv_q_signaturepad sjs_sp_container',
          controls: 'sjs_sp_controls',
          clearButton: 'sjs_sp_clear',
        },
        saveData: {
          root: '',
          saving: 'alert alert-info',
          error: 'alert alert-danger',
          success: 'alert alert-success',
          saveAgainButton: '',
        },
        window: {
          root: 'modal-content',
          body: 'modal-body',
          header: {
            root: 'modal-header card-title',
            title: 'pull-left',
            button: 'glyphicon pull-right',
            buttonExpanded: 'glyphicon pull-right glyphicon-chevron-up',
            buttonCollapsed: 'glyphicon pull-right glyphicon-chevron-down',
          },
        },
      };
      r.b.bootstrapmaterial = i;
    },
    function (e, t, n) {
      'use strict';
      var r = n(14);
      n.d(t, 'a', function () {
        return i;
      });
      var i = {
        root: 'sv-root-modern',
        container: 'sv-container-modern',
        header: 'sv-title sv-container-modern__title',
        body: 'sv-body',
        bodyEmpty: 'sv-body sv-body--empty',
        footer: 'sv-footer sv-body__footer sv-clearfix',
        title: '',
        description: '',
        logo: 'sv-logo',
        logoImage: 'sv-logo__image',
        headerText: 'sv-header__text',
        navigationButton: '',
        completedPage: 'sv-completedpage',
        navigation: {
          complete: 'sv-btn sv-footer__complete-btn',
          prev: 'sv-btn sv-footer__prev-btn',
          next: 'sv-btn sv-footer__next-btn',
          start: 'sv-btn sv-footer__start-btn',
          preview: 'sv-btn sv-footer__preview-btn',
          edit: 'sv-btn sv-footer__edit-btn',
        },
        panel: {
          title: 'sv-title sv-panel__title',
          titleExpandable: 'sv-panel__title--expandable',
          titleOnError: 'sv-panel__title--error',
          description: 'sv-description sv-panel__description',
          container: 'sv-panel sv-row__panel',
          content: 'sv-panel__content',
          icon: 'sv-panel__icon',
          iconExpanded: 'sv-panel__icon--expanded',
          footer: 'sv-panel__footer',
          requiredText: 'sv-panel__required-text',
          number: 'sv-question__num',
        },
        paneldynamic: {
          root: 'sv-paneldynamic',
          navigation: 'sv-paneldynamic__navigation',
          title: 'sv-title sv-question__title',
          button: 'sv-btn',
          buttonRemove: 'sv-paneldynamic__remove-btn',
          buttonAdd: 'sv-paneldynamic__add-btn',
          progressTop: 'sv-paneldynamic__progress sv-paneldynamic__progress--top',
          progressBottom: 'sv-paneldynamic__progress sv-paneldynamic__progress--bottom',
          buttonPrev: 'sv-paneldynamic__prev-btn',
          buttonNext: 'sv-paneldynamic__next-btn',
          progressContainer: 'sv-paneldynamic__progress-container',
          progress: 'sv-progress',
          progressBar: 'sv-progress__bar',
          progressText: 'sv-paneldynamic__progress-text',
          separator: 'sv-paneldynamic__separator',
        },
        progress: 'sv-progress sv-body__progress',
        progressBar: 'sv-progress__bar',
        progressText: 'sv-progress__text',
        progressTextInBar: 'sv-hidden',
        progressButtonsContainerCenter: 'sv_progress-buttons__container-center',
        progressButtonsContainer: 'sv_progress-buttons__container',
        progressButtonsImageButtonLeft: 'sv_progress-buttons__image-button-left',
        progressButtonsImageButtonRight: 'sv_progress-buttons__image-button-right',
        progressButtonsImageButtonHidden: 'sv_progress-buttons__image-button--hidden',
        progressButtonsListContainer: 'sv_progress-buttons__list-container',
        progressButtonsList: 'sv_progress-buttons__list',
        progressButtonsListElementPassed: 'sv_progress-buttons__list-element--passed',
        progressButtonsListElementCurrent: 'sv_progress-buttons__list-element--current',
        progressButtonsPageTitle: 'sv_progress-buttons__page-title',
        progressButtonsPageDescription: 'sv_progress-buttons__page-description',
        page: {
          root: 'sv-page sv-body__page',
          title: 'sv-title sv-page__title',
          description: 'sv-description sv-page__description',
        },
        pageTitle: 'sv-title sv-page__title',
        pageDescription: 'sv-description sv-page__description',
        row: 'sv-row sv-clearfix',
        question: {
          mainRoot: 'sv-question sv-row__question',
          flowRoot: 'sv-question sv-row__question sv-row__question--flow',
          asCell: 'sv-table__cell',
          header: 'sv-question__header',
          headerLeft: 'sv-question__header--location--left',
          headerTop: 'sv-question__header--location--top',
          headerBottom: 'sv-question__header--location--bottom',
          content: 'sv-question__content',
          contentLeft: 'sv-question__content--left',
          titleLeftRoot: '',
          titleOnAnswer: 'sv-question__title--answer',
          titleOnError: 'sv-question__title--error',
          title: 'sv-title sv-question__title',
          titleExpandable: 'sv-question__title--expandable',
          icon: 'sv-question__icon',
          iconExpanded: 'sv-question__icon--expanded',
          requiredText: 'sv-question__required-text',
          number: 'sv-question__num',
          description: 'sv-description sv-question__description',
          descriptionUnderInput: 'sv-description sv-question__description',
          comment: 'sv-comment',
          required: 'sv-question--required',
          titleRequired: 'sv-question__title--required',
          indent: 20,
          footer: 'sv-question__footer',
          formGroup: 'sv-question__form-group',
          hasError: '',
          disabled: 'sv-question--disabled',
        },
        image: { root: 'sv-image', image: 'sv_image_image' },
        error: {
          root: 'sv-question__erbox',
          icon: '',
          item: '',
          locationTop: 'sv-question__erbox--location--top',
          locationBottom: 'sv-question__erbox--location--bottom',
        },
        checkbox: {
          root: 'sv-selectbase',
          item: 'sv-item sv-checkbox sv-selectbase__item',
          itemSelectAll: 'sv-checkbox--selectall',
          itemNone: 'sv-checkbox--none',
          itemDisabled: 'sv-item--disabled sv-checkbox--disabled',
          itemChecked: 'sv-checkbox--checked',
          itemHover: 'sv-checkbox--allowhover',
          itemInline: 'sv-selectbase__item--inline',
          label: 'sv-selectbase__label',
          labelChecked: '',
          itemControl: 'sv-visuallyhidden sv-item__control',
          itemDecorator: 'sv-item__svg sv-checkbox__svg',
          controlLabel: 'sv-item__control-label',
          materialDecorator: 'sv-item__decorator sv-checkbox__decorator',
          other: 'sv-comment sv-question__other',
          column: 'sv-selectbase__column',
        },
        radiogroup: {
          root: 'sv-selectbase',
          item: 'sv-item sv-radio sv-selectbase__item',
          itemInline: 'sv-selectbase__item--inline',
          label: 'sv-selectbase__label',
          labelChecked: '',
          itemDisabled: 'sv-item--disabled sv-radio--disabled',
          itemChecked: 'sv-radio--checked',
          itemHover: 'sv-radio--allowhover',
          itemControl: 'sv-visuallyhidden sv-item__control',
          itemDecorator: 'sv-item__svg sv-radio__svg',
          controlLabel: 'sv-item__control-label',
          materialDecorator: 'sv-item__decorator sv-radio__decorator',
          other: 'sv-comment sv-question__other',
          clearButton: 'sv-btn sv-selectbase__clear-btn',
          column: 'sv-selectbase__column',
        },
        boolean: {
          root: 'sv_qbln',
          small: 'sv-row__question--small',
          item: 'sv-boolean sv-item',
          control: 'sv-visuallyhidden',
          itemChecked: 'sv-boolean--checked',
          itemIndeterminate: 'sv-boolean--indeterminate',
          itemDisabled: 'sv-item--disabled sv-boolean--disabled',
          switch: 'sv-boolean__switch',
          slider: 'sv-boolean__slider',
          label: 'sv-boolean__label ',
          disabledLabel: 'sv-boolean__label--disabled',
          materialDecorator: 'sv-item__decorator sv-boolean__decorator ',
          itemDecorator: 'sv-item__svg  sv-boolean__svg',
          checkedPath: 'sv-boolean__checked-path',
          uncheckedPath: 'sv-boolean__unchecked-path',
          indeterminatePath: 'sv-boolean__indeterminate-path',
        },
        text: { root: 'sv-text', small: 'sv-row__question--small', onError: 'sv-text--error' },
        multipletext: {
          root: 'sv-multipletext',
          item: 'sv-multipletext__item',
          itemTitle: 'sv-multipletext__item-title',
          row: 'sv-multipletext__row',
          cell: 'sv-multipletext__cell',
        },
        dropdown: {
          root: '',
          small: 'sv-row__question--small',
          control: 'sv-dropdown',
          selectWrapper: '',
          other: 'sv-comment sv-question__other',
          onError: 'sv-dropdown--error',
        },
        imagepicker: {
          root: 'sv-imagepicker',
          item: 'sv-imagepicker__item',
          itemInline: 'sv-imagepicker__item--inline',
          itemChecked: 'sv-imagepicker__item--checked',
          itemDisabled: 'sv-imagepicker__item--disabled',
          itemHover: 'sv-imagepicker__item--allowhover',
          label: 'sv-imagepicker__label',
          itemControl: 'sv-imagepicker__control',
          image: 'sv-imagepicker__image',
          itemText: 'sv-imagepicker__text',
          clearButton: 'sv-btn',
          other: 'sv-comment sv-question__other',
        },
        matrix: {
          tableWrapper: 'sv-matrix',
          root: 'sv-table',
          rowError: 'sv-matrix__row--error',
          cell: 'sv-table__cell sv-matrix__cell',
          headerCell: 'sv-table__cell sv-table__cell--header',
          label: 'sv-item sv-radio sv-matrix__label',
          itemValue: 'sv-visuallyhidden sv-item__control sv-radio__control',
          itemChecked: 'sv-radio--checked',
          itemDisabled: 'sv-item--disabled sv-radio--disabled',
          itemHover: 'sv-radio--allowhover',
          materialDecorator: 'sv-item__decorator sv-radio__decorator',
          itemDecorator: 'sv-item__svg sv-radio__svg',
          cellText: 'sv-matrix__text',
          cellTextSelected: 'sv-matrix__text--checked',
          cellTextDisabled: 'sv-matrix__text--disabled',
        },
        matrixdropdown: {
          root: 'sv-table',
          cell: 'sv-table__cell',
          headerCell: 'sv-table__cell sv-table__cell--header',
          row: 'sv-table__row',
          detailRow: 'sv-table__row sv-table__row--detail',
          detailRowText: 'sv-table__cell--detail-rowtext',
          detailCell: 'sv-table__cell--detail',
          detailButton: 'sv-table__cell--detail-button',
          detailButtonExpanded: 'sv-table__cell--detail-button--expanded',
          detailIcon: 'sv-detail-panel__icon',
          detailIconExpanded: 'sv-detail-panel__icon--expanded',
          detailPanelCell: 'sv-table__cell--detail-panel',
        },
        matrixdynamic: {
          root: 'sv-table sv-matrixdynamic',
          cell: 'sv-table__cell',
          headerCell: 'sv-table__cell sv-table__cell--header',
          button: 'sv-btn',
          buttonAdd: 'sv-matrixdynamic__add-btn',
          buttonRemove: 'sv-matrixdynamic__remove-btn',
          iconAdd: '',
          iconRemove: '',
          row: 'sv-table__row',
          detailRow: 'sv-table__row sv-table__row--detail',
          detailCell: 'sv-table__cell--detail',
          detailButton: 'sv-table__cell--detail-button',
          detailButtonExpanded: 'sv-table__cell--detail-button--expanded',
          detailIcon: 'sv-detail-panel__icon',
          detailIconExpanded: 'sv-detail-panel__icon--expanded',
          detailPanelCell: 'sv-table__cell--detail-panel',
        },
        rating: {
          root: 'sv-rating',
          item: 'sv-rating__item',
          selected: 'sv-rating__item--selected',
          minText: 'sv-rating__min-text',
          itemText: 'sv-rating__item-text',
          maxText: 'sv-rating__max-text',
          disabled: 'sv-rating--disabled',
        },
        comment: { root: 'sv-comment', small: 'sv-row__question--small' },
        expression: '',
        file: {
          root: 'sv-file',
          other: 'sv-comment sv-question__other',
          placeholderInput: 'sv-visuallyhidden',
          preview: 'sv-file__preview',
          fileSign: 'sv-hidden',
          fileSignBottom: 'sv-file__sign',
          fileDecorator: 'sv-file__decorator',
          fileInput: 'sv-visuallyhidden',
          noFileChosen: 'sv-description sv-file__no-file-chosen',
          chooseFile: 'sv-btn sv-file__choose-btn',
          disabled: 'sv-file__choose-btn--disabled',
          removeButton: 'sv-hidden',
          removeButtonBottom: 'sv-btn sv-file__clean-btn',
          removeFile: 'sv-hidden',
          removeFileSvg: 'sv-file__remove-svg',
          wrapper: 'sv-file__wrapper',
        },
        signaturepad: {
          root: 'sv-signaturepad sjs_sp_container',
          small: 'sv-row__question--small',
          controls: 'sjs_sp_controls',
          clearButton: 'sjs_sp_clear',
        },
        saveData: { root: '', saving: '', error: '', success: '', saveAgainButton: '' },
        window: {
          root: 'sv_window',
          body: 'sv_window_content',
          header: {
            root: 'sv_window_title',
            title: '',
            button: '',
            buttonExpanded: '',
            buttonCollapsed: '',
          },
        },
      };
      r.b.modern = i;
    },
    function (e, t, n) {
      'use strict';
      n(158),
        n(159),
        n(160),
        n(161),
        n(162),
        n(163),
        n(164),
        n(165),
        n(166),
        n(167),
        n(168),
        n(169),
        n(170),
        n(171),
        n(172),
        n(173),
        n(174),
        n(175),
        n(176),
        n(177),
        n(178),
        n(179),
        n(180),
        n(181),
        n(183),
        n(182),
        n(184),
        n(185),
        n(186),
        n(187),
        n(189),
        n(191),
        n(193),
        n(192),
        n(194),
        n(190),
        n(188),
        n(195);
    },
    function (e, t, n) {
      'use strict';
      var r = n(72),
        i = (n.n(r), n(73)),
        o = (n.n(i), n(7));
      n.d(t, 'b', function () {
        return o.a;
      });
      var a = n(4);
      n.d(t, 'c', function () {
        return a.a;
      });
      var s = n(36);
      n.d(t, 'd', function () {
        return s.c;
      }),
        n.d(t, 'e', function () {
          return s.b;
        }),
        n.d(t, 'f', function () {
          return s.d;
        }),
        n.d(t, 'g', function () {
          return s.e;
        }),
        n.d(t, 'h', function () {
          return s.f;
        }),
        n.d(t, 'i', function () {
          return s.g;
        }),
        n.d(t, 'j', function () {
          return s.h;
        }),
        n.d(t, 'k', function () {
          return s.i;
        }),
        n.d(t, 'l', function () {
          return s.a;
        });
      var l = n(11);
      n.d(t, 'm', function () {
        return l.a;
      });
      var u = n(6);
      n.d(t, 'n', function () {
        return u.b;
      }),
        n.d(t, 'o', function () {
          return u.c;
        }),
        n.d(t, 'p', function () {
          return u.d;
        }),
        n.d(t, 'q', function () {
          return u.a;
        });
      var c = n(122);
      n.d(t, 'r', function () {
        return c.a;
      });
      var p = n(12);
      n.d(t, 's', function () {
        return p.b;
      }),
        n.d(t, 't', function () {
          return p.c;
        }),
        n.d(t, 'u', function () {
          return p.a;
        }),
        n.d(t, 'v', function () {
          return p.d;
        }),
        n.d(t, 'w', function () {
          return p.l;
        });
      var d = n(13);
      n.d(t, 'x', function () {
        return d.a;
      });
      var h = n(123);
      n.d(t, 'y', function () {
        return h.a;
      }),
        n.d(t, 'z', function () {
          return h.b;
        });
      var f = n(42);
      n.d(t, 'A', function () {
        return f.a;
      });
      var m = n(28);
      n.d(t, 'B', function () {
        return m.a;
      }),
        n.d(t, 'C', function () {
          return m.b;
        });
      var g = n(10);
      n.d(t, 'D', function () {
        return g.a;
      }),
        n.d(t, 'E', function () {
          return g.b;
        });
      var y = n(27);
      n.d(t, 'F', function () {
        return y.g;
      }),
        n.d(t, 'G', function () {
          return y.c;
        }),
        n.d(t, 'H', function () {
          return y.f;
        }),
        n.d(t, 'I', function () {
          return y.d;
        }),
        n.d(t, 'J', function () {
          return y.a;
        }),
        n.d(t, 'K', function () {
          return y.e;
        });
      var v = n(43);
      n.d(t, 'L', function () {
        return v.a;
      });
      var b = n(16);
      n.d(t, 'M', function () {
        return b.a;
      });
      var x = n(1);
      n.d(t, 'N', function () {
        return x.f;
      }),
        n.d(t, 'O', function () {
          return x.g;
        }),
        n.d(t, 'P', function () {
          return x.h;
        }),
        n.d(t, 'Q', function () {
          return x.i;
        }),
        n.d(t, 'R', function () {
          return x.j;
        }),
        n.d(t, 'S', function () {
          return x.k;
        }),
        n.d(t, 'T', function () {
          return x.c;
        }),
        n.d(t, 'U', function () {
          return x.d;
        }),
        n.d(t, 'V', function () {
          return x.l;
        }),
        n.d(t, 'W', function () {
          return x.m;
        }),
        n.d(t, 'X', function () {
          return x.b;
        });
      var C = n(24);
      n.d(t, 'Y', function () {
        return C.c;
      }),
        n.d(t, 'Z', function () {
          return C.d;
        }),
        n.d(t, '_0', function () {
          return C.e;
        }),
        n.d(t, '_1', function () {
          return C.b;
        }),
        n.d(t, '_2', function () {
          return C.a;
        });
      var w = n(61);
      n.d(t, '_3', function () {
        return w.b;
      }),
        n.d(t, '_4', function () {
          return w.a;
        });
      var P = n(62);
      n.d(t, '_5', function () {
        return P.b;
      }),
        n.d(t, '_6', function () {
          return P.a;
        });
      var V = n(60);
      n.d(t, '_7', function () {
        return V.b;
      }),
        n.d(t, '_8', function () {
          return V.c;
        }),
        n.d(t, '_9', function () {
          return V.a;
        });
      var k = n(63);
      n.d(t, '_10', function () {
        return k.a;
      }),
        n.d(t, '_11', function () {
          return k.b;
        });
      var _ = n(18);
      n.d(t, '_12', function () {
        return _.b;
      }),
        n.d(t, '_13', function () {
          return _.d;
        }),
        n.d(t, '_14', function () {
          return _.a;
        });
      var T = n(45);
      n.d(t, '_15', function () {
        return T.a;
      });
      var S = n(29);
      n.d(t, '_16', function () {
        return S.a;
      });
      var R = n(9);
      n.d(t, '_17', function () {
        return R.a;
      });
      var q = n(34);
      n.d(t, '_18', function () {
        return q.a;
      });
      var E = n(55);
      n.d(t, '_19', function () {
        return E.a;
      });
      var O = n(23);
      n.d(t, '_20', function () {
        return O.a;
      }),
        n.d(t, '_21', function () {
          return O.b;
        });
      var I = n(52);
      n.d(t, '_22', function () {
        return I.a;
      });
      var L = n(53);
      n.d(t, '_23', function () {
        return L.a;
      });
      var A = n(31);
      n.d(t, '_24', function () {
        return A.a;
      });
      var D = n(3);
      n.d(t, '_25', function () {
        return D.b;
      }),
        n.d(t, '_26', function () {
          return D.a;
        });
      var N = n(56);
      n.d(t, '_27', function () {
        return N.a;
      });
      var j = n(57);
      n.d(t, '_28', function () {
        return j.a;
      });
      var F = n(65);
      n.d(t, '_29', function () {
        return F.a;
      });
      var M = n(66);
      n.d(t, '_30', function () {
        return M.a;
      });
      var B = n(32);
      n.d(t, '_31', function () {
        return B.a;
      });
      var z = n(33);
      n.d(t, '_32', function () {
        return z.a;
      });
      var Q = n(51);
      n.d(t, '_33', function () {
        return Q.a;
      });
      var H = n(59);
      n.d(t, '_34', function () {
        return H.a;
      });
      var $ = n(58);
      n.d(t, '_35', function () {
        return $.a;
      });
      var U = n(67);
      n.d(t, '_36', function () {
        return U.a;
      });
      var W = n(64);
      n.d(t, '_37', function () {
        return W.a;
      }),
        n.d(t, '_38', function () {
          return W.b;
        });
      var J = n(70);
      n.d(t, '_39', function () {
        return J.a;
      });
      var G = n(68);
      n.d(t, '_40', function () {
        return G.a;
      });
      var K = n(21);
      n.d(t, '_41', function () {
        return K.a;
      });
      var Z = n(196);
      n.d(t, '_42', function () {
        return Z.a;
      }),
        n.d(t, '_43', function () {
          return Z.b;
        }),
        n.d(t, '_44', function () {
          return Z.c;
        }),
        n.d(t, '_45', function () {
          return Z.d;
        }),
        n.d(t, '_46', function () {
          return Z.e;
        }),
        n.d(t, '_47', function () {
          return Z.f;
        }),
        n.d(t, '_48', function () {
          return Z.g;
        });
      var X = n(69);
      n.d(t, '_49', function () {
        return X.a;
      });
      var Y = n(25);
      n.d(t, '_50', function () {
        return Y.a;
      });
      var ee = n(44);
      n.d(t, '_51', function () {
        return ee.a;
      });
      var te = n(49);
      n.d(t, '_52', function () {
        return te.a;
      });
      var ne = n(2);
      n.d(t, '_53', function () {
        return ne.a;
      }),
        n.d(t, '_54', function () {
          return ne.b;
        });
      var re = n(30);
      n.d(t, '_55', function () {
        return re.b;
      }),
        n.d(t, '_56', function () {
          return re.a;
        });
      var ie = n(54);
      n.d(t, '_57', function () {
        return ie.a;
      }),
        n.d(t, '_58', function () {
          return ie.b;
        }),
        n.d(t, '_59', function () {
          return ie.d;
        }),
        n.d(t, '_60', function () {
          return ie.c;
        });
      var oe = n(35);
      n.d(t, '_61', function () {
        return oe.a;
      }),
        n.d(t, 'a', function () {
          return ae;
        });
      var ae;
      ae = '1.8.25';
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(6),
        o = n(11),
        a = n(1),
        s = n(12),
        l = n(7);
      n.d(t, 'a', function () {
        return c;
      });
      var u = (function () {
          function e() {
            this.parser = new DOMParser();
          }
          return (
            (e.prototype.assignValue = function (e, t, n) {
              Array.isArray(e[t])
                ? e[t].push(n)
                : void 0 !== e[t]
                ? (e[t] = [e[t]].concat(n))
                : 'object' == typeof n && 1 === Object.keys(n).length && Object.keys(n)[0] === t
                ? (e[t] = n[t])
                : (e[t] = n);
            }),
            (e.prototype.xml2Json = function (e, t) {
              if (e.children && e.children.length > 0)
                for (var n = 0; n < e.children.length; n++) {
                  var r = e.children[n],
                    i = {};
                  this.xml2Json(r, i), this.assignValue(t, r.nodeName, i);
                }
              else this.assignValue(t, e.nodeName, e.textContent);
            }),
            (e.prototype.parseXmlString = function (e) {
              var t = this.parser.parseFromString(e, 'text/xml'),
                n = {};
              return this.xml2Json(t, n), n;
            }),
            e
          );
        })(),
        c = (function (e) {
          function t() {
            var t = e.call(this) || this;
            return (
              (t.lastObjHash = ''),
              (t.isRunningValue = !1),
              (t.processedUrl = ''),
              (t.processedPath = ''),
              (t.isUsingCacheFromUrl = void 0),
              (t.error = null),
              t
            );
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t, 'EncodeParameters', {
              get: function () {
                return l.a.webserviceEncodeParameters;
              },
              set: function (e) {
                l.a.webserviceEncodeParameters = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.clearCache = function () {
              (t.itemsResult = {}), (t.sendingSameRequests = {});
            }),
            (t.addSameRequest = function (e) {
              if (!e.isUsingCache) return !1;
              var n = e.objHash,
                r = t.sendingSameRequests[n];
              return r ? (r.push(e), !0) : ((t.sendingSameRequests[e.objHash] = []), !1);
            }),
            (t.unregisterSameRequests = function (e, n) {
              if (e.isUsingCache) {
                var r = t.sendingSameRequests[e.objHash];
                delete t.sendingSameRequests[e.objHash];
                for (var i = 0; i < r.length; i++)
                  r[i].getResultCallback && r[i].getResultCallback(n);
              }
            }),
            (t.getCachedItemsResult = function (e) {
              var n = e.objHash,
                r = t.itemsResult[n];
              return !!r && (e.getResultCallback && e.getResultCallback(r), !0);
            }),
            (t.prototype.run = function (e) {
              if ((void 0 === e && (e = null), this.url && this.getResultCallback)) {
                if ((this.processedText(e), !this.processedUrl))
                  return this.doEmptyResultCallback({}), void (this.lastObjHash = this.objHash);
                this.lastObjHash !== this.objHash &&
                  ((this.lastObjHash = this.objHash),
                  (this.error = null),
                  this.useChangedItemsResults() || t.addSameRequest(this) || this.sendRequest());
              }
            }),
            Object.defineProperty(t.prototype, 'isUsingCache', {
              get: function () {
                return (
                  !0 === this.isUsingCacheFromUrl ||
                  (!1 !== this.isUsingCacheFromUrl && l.a.useCachingForChoicesRestfull)
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isRunning', {
              get: function () {
                return this.isRunningValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isWaitingForParameters', {
              get: function () {
                return this.url && !this.processedUrl;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.useChangedItemsResults = function () {
              return t.getCachedItemsResult(this);
            }),
            (t.prototype.doEmptyResultCallback = function (e) {
              var t = [];
              this.updateResultCallback && (t = this.updateResultCallback(t, e)),
                this.getResultCallback(t);
            }),
            (t.prototype.processedText = function (e) {
              var n = this.url;
              if ((n && (n = n.replace(t.cacheText, '').replace(t.noCacheText, '')), e)) {
                var r = e.processTextEx(n, !1, l.a.webserviceEncodeParameters),
                  i = e.processTextEx(this.path, !1, l.a.webserviceEncodeParameters);
                r.hasAllValuesOnLastRun && i.hasAllValuesOnLastRun
                  ? ((this.processedUrl = r.text), (this.processedPath = i.text))
                  : ((this.processedUrl = ''), (this.processedPath = ''));
              } else (this.processedUrl = n), (this.processedPath = this.path);
              this.onProcessedUrlCallback &&
                this.onProcessedUrlCallback(this.processedUrl, this.processedPath);
            }),
            (t.prototype.parseResponse = function (e) {
              var t;
              if (e && 'function' == typeof e.indexOf && 0 === e.indexOf('<')) {
                var n = new u();
                t = n.parseXmlString(e);
              } else
                try {
                  t = JSON.parse(e);
                } catch (n) {
                  t = (e || '')
                    .split('\n')
                    .map(function (e) {
                      return e.trim(' ');
                    })
                    .filter(function (e) {
                      return !!e;
                    });
                }
              return t;
            }),
            (t.prototype.sendRequest = function () {
              this.isRunningValue = !0;
              var e = new XMLHttpRequest();
              e.open('GET', this.processedUrl),
                e.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
              var n = this,
                r = this.objHash;
              e.onload = function () {
                (n.isRunningValue = !1),
                  200 === e.status
                    ? n.onLoad(n.parseResponse(e.response), r)
                    : n.onError(e.statusText, e.responseText);
              };
              var i = { request: e };
              t.onBeforeSendRequest && t.onBeforeSendRequest(this, i),
                this.beforeSendRequest(),
                i.request.send();
            }),
            (t.prototype.getType = function () {
              return 'choicesByUrl';
            }),
            Object.defineProperty(t.prototype, 'isEmpty', {
              get: function () {
                return !(
                  this.url ||
                  this.path ||
                  this.valueName ||
                  this.titleName ||
                  this.imageLinkName
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getCustomPropertiesNames = function () {
              for (var e = this.getCustomProperties(), t = new Array(), n = 0; n < e.length; n++)
                t.push(this.getCustomPropertyName(e[n].name));
              return t;
            }),
            (t.prototype.getCustomPropertyName = function (e) {
              return e + 'Name';
            }),
            (t.prototype.getCustomProperties = function () {
              for (var e = a.b.getProperties(this.itemValueType), t = [], n = 0; n < e.length; n++)
                'value' !== e[n].name &&
                  'text' !== e[n].name &&
                  'visibleIf' !== e[n].name &&
                  'enableIf' !== e[n].name &&
                  t.push(e[n]);
              return t;
            }),
            (t.prototype.setData = function (e) {
              this.clear(),
                e.url && (this.url = e.url),
                e.path && (this.path = e.path),
                e.valueName && (this.valueName = e.valueName),
                e.titleName && (this.titleName = e.titleName),
                e.imageLinkName && (this.imageLinkName = e.imageLinkName),
                void 0 !== e.allowEmptyResponse && (this.allowEmptyResponse = e.allowEmptyResponse),
                void 0 !== e.attachOriginalItems &&
                  (this.attachOriginalItems = e.attachOriginalItems);
              for (var t = this.getCustomPropertiesNames(), n = 0; n < t.length; n++)
                e[t[n]] && (this[t[n]] = e[t[n]]);
            }),
            (t.prototype.getData = function () {
              if (this.isEmpty) return null;
              var e = {};
              this.url && (e.url = this.url),
                this.path && (e.path = this.path),
                this.valueName && (e.valueName = this.valueName),
                this.titleName && (e.titleName = this.titleName),
                this.imageLinkName && (e.imageLinkName = this.imageLinkName),
                this.allowEmptyResponse && (e.allowEmptyResponse = this.allowEmptyResponse),
                this.attachOriginalItems && (e.attachOriginalItems = this.attachOriginalItems);
              for (var t = this.getCustomPropertiesNames(), n = 0; n < t.length; n++)
                this[t[n]] && (e[t[n]] = this[t[n]]);
              return e;
            }),
            Object.defineProperty(t.prototype, 'url', {
              get: function () {
                return this.getPropertyValue('url', '');
              },
              set: function (e) {
                this.setPropertyValue('url', e),
                  (this.isUsingCacheFromUrl = void 0),
                  e &&
                    (e.indexOf(t.cacheText) > -1
                      ? (this.isUsingCacheFromUrl = !0)
                      : e.indexOf(t.noCacheText) > -1 && (this.isUsingCacheFromUrl = !1));
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'path', {
              get: function () {
                return this.getPropertyValue('path', '');
              },
              set: function (e) {
                this.setPropertyValue('path', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'valueName', {
              get: function () {
                return this.getPropertyValue('valueName', '');
              },
              set: function (e) {
                this.setPropertyValue('valueName', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'titleName', {
              get: function () {
                return this.getPropertyValue('titleName', '');
              },
              set: function (e) {
                this.setPropertyValue('titleName', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'imageLinkName', {
              get: function () {
                return this.getPropertyValue('imageLinkName', '');
              },
              set: function (e) {
                this.setPropertyValue('imageLinkName', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'allowEmptyResponse', {
              get: function () {
                return this.getPropertyValue('allowEmptyResponse', !1);
              },
              set: function (e) {
                this.setPropertyValue('allowEmptyResponse', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'attachOriginalItems', {
              get: function () {
                return this.getPropertyValue('attachOriginalItems', !1);
              },
              set: function (e) {
                this.setPropertyValue('attachOriginalItems', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'itemValueType', {
              get: function () {
                if (!this.owner) return 'itemvalue';
                var e = a.b.findProperty(this.owner.getType(), 'choices');
                return e ? ('itemvalue[]' == e.type ? 'itemvalue' : e.type) : 'itemvalue';
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.clear = function () {
              (this.url = ''),
                (this.path = ''),
                (this.valueName = ''),
                (this.titleName = ''),
                (this.imageLinkName = '');
              for (var e = this.getCustomPropertiesNames(), t = 0; t < e.length; t++)
                this[e[t]] && (this[e[t]] = '');
            }),
            (t.prototype.beforeSendRequest = function () {
              this.beforeSendRequestCallback && this.beforeSendRequestCallback();
            }),
            (t.prototype.onLoad = function (e, n) {
              void 0 === n && (n = null), n || (n = this.objHash);
              var r = new Array(),
                i = this.getResultAfterPath(e);
              if (i && i.length)
                for (var a = 0; a < i.length; a++) {
                  var l = i[a];
                  if (l) {
                    var u = this.getItemValueCallback
                        ? this.getItemValueCallback(l)
                        : this.getValue(l),
                      c = this.getTitle(l),
                      p = new o.a(u, c);
                    this.setCustomProperties(p, l),
                      this.attachOriginalItems && (p.originalItem = l);
                    var d = this.getImageLink(l);
                    d && (p.imageLink = d), r.push(p);
                  }
                }
              else this.allowEmptyResponse || (this.error = new s.f(null, this.owner));
              this.updateResultCallback && (r = this.updateResultCallback(r, e)),
                this.isUsingCache && (t.itemsResult[n] = r),
                this.callResultCallback(r, n),
                t.unregisterSameRequests(this, r);
            }),
            (t.prototype.callResultCallback = function (e, t) {
              t == this.objHash && this.getResultCallback(e);
            }),
            (t.prototype.setCustomProperties = function (e, t) {
              for (var n = this.getCustomProperties(), r = 0; r < n.length; r++) {
                var i = n[r],
                  o = this.getValueCore(t, this.getPropertyBinding(i.name));
                this.isValueEmpty(o) || (e[i.name] = o);
              }
            }),
            (t.prototype.getPropertyBinding = function (e) {
              return this[this.getCustomPropertyName(e)]
                ? this[this.getCustomPropertyName(e)]
                : this[e]
                ? this[e]
                : e;
            }),
            (t.prototype.onError = function (e, n) {
              (this.error = new s.g(e, n, this.owner)),
                this.doEmptyResultCallback(n),
                t.unregisterSameRequests(this, []);
            }),
            (t.prototype.getResultAfterPath = function (e) {
              if (!e) return e;
              if (!this.processedPath) return e;
              for (var t = this.getPathes(), n = 0; n < t.length; n++)
                if (!(e = e[t[n]])) return null;
              return e;
            }),
            (t.prototype.getPathes = function () {
              var e = [];
              return (
                (e =
                  this.processedPath.indexOf(';') > -1
                    ? this.path.split(';')
                    : this.processedPath.split(',')),
                0 == e.length && e.push(this.processedPath),
                e
              );
            }),
            (t.prototype.getValue = function (e) {
              return e
                ? this.valueName
                  ? this.getValueCore(e, this.valueName)
                  : e instanceof Object
                  ? Object.keys(e).length < 1
                    ? null
                    : e[Object.keys(e)[0]]
                  : e
                : null;
            }),
            (t.prototype.getTitle = function (e) {
              var t = this.titleName ? this.titleName : 'title';
              return this.getValueCore(e, t);
            }),
            (t.prototype.getImageLink = function (e) {
              var t = this.imageLinkName ? this.imageLinkName : 'imageLink';
              return this.getValueCore(e, t);
            }),
            (t.prototype.getValueCore = function (e, t) {
              if (!e) return null;
              if (t.indexOf('.') < 0) return e[t];
              for (var n = t.split('.'), r = 0; r < n.length; r++) if (!(e = e[n[r]])) return null;
              return e;
            }),
            Object.defineProperty(t.prototype, 'objHash', {
              get: function () {
                return (
                  this.processedUrl +
                  ';' +
                  this.processedPath +
                  ';' +
                  this.valueName +
                  ';' +
                  this.titleName +
                  ';' +
                  this.imageLinkName
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.cacheText = '{CACHE}'),
            (t.noCacheText = '{NOCACHE}'),
            (t.itemsResult = {}),
            (t.sendingSameRequests = {}),
            t
          );
        })(i.b);
      a.b.addClass(
        'choicesByUrl',
        [
          'url',
          'path',
          'valueName',
          'titleName',
          {
            name: 'imageLinkName',
            visibleIf: function (e) {
              return !!e && !!e.owner && 'imagepicker' == e.owner.getType();
            },
          },
          { name: 'allowEmptyResponse:boolean', default: !1 },
          { name: 'attachOriginalItems:boolean', default: !1, visible: !1 },
        ],
        function () {
          return new c();
        }
      );
    },
    function (e, t, n) {
      'use strict';
      var r = n(124);
      n.d(t, 'a', function () {
        return o;
      });
      var i = (function () {
          function e(e, t) {
            (this.at = e), (this.code = t);
          }
          return e;
        })(),
        o = (function () {
          function e() {}
          return (
            (e.prototype.patchExpression = function (e) {
              return e
                .replace(/=>/g, '>=')
                .replace(/=</g, '<=')
                .replace(/<>/g, '!=')
                .replace(/==/g, '= ')
                .replace(/equals/g, 'equal ')
                .replace(/notequals/g, 'notequal ');
            }),
            (e.prototype.createCondition = function (e) {
              return this.parseExpression(e);
            }),
            (e.prototype.parseExpression = function (t) {
              try {
                var o = e.parserCache[t];
                return (
                  void 0 === o &&
                    ((o = n.i(r.a)(this.patchExpression(t))),
                    o.hasAsyncFunction() || (e.parserCache[t] = o)),
                  o
                );
              } catch (e) {
                e instanceof r.b &&
                  (this.conditionError = new i(e.location.start.offset, e.message));
              }
            }),
            Object.defineProperty(e.prototype, 'error', {
              get: function () {
                return this.conditionError;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.parserCache = {}),
            e
          );
        })();
    },
    function (e, t, n) {
      'use strict';
      var r = n(7);
      n.d(t, 'a', function () {
        return i;
      });
      var i = (function () {
        function e() {}
        return (
          Object.defineProperty(e, 'serviceUrl', {
            get: function () {
              return r.a.surveyServiceUrl;
            },
            set: function (e) {
              r.a.surveyServiceUrl = e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (e.prototype.loadSurvey = function (t, n) {
            var r = new XMLHttpRequest();
            r.open('GET', e.serviceUrl + '/getSurvey?surveyId=' + t),
              r.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded'),
              (r.onload = function () {
                var e = JSON.parse(r.response);
                n(200 == r.status, e, r.response);
              }),
              r.send();
          }),
          (e.prototype.getSurveyJsonAndIsCompleted = function (t, n, r) {
            var i = new XMLHttpRequest();
            i.open(
              'GET',
              e.serviceUrl + '/getSurveyAndIsCompleted?surveyId=' + t + '&clientId=' + n
            ),
              i.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded'),
              (i.onload = function () {
                var e = JSON.parse(i.response),
                  t = e ? e.survey : null,
                  n = e ? e.isCompleted : null;
                r(200 == i.status, t, n, i.response);
              }),
              i.send();
          }),
          (e.prototype.sendResult = function (t, n, r, i, o) {
            void 0 === i && (i = null), void 0 === o && (o = !1);
            var a = new XMLHttpRequest();
            a.open('POST', e.serviceUrl + '/post/'),
              a.setRequestHeader('Content-Type', 'application/json; charset=utf-8');
            var s = { postId: t, surveyResult: JSON.stringify(n) };
            i && (s.clientId = i), o && (s.isPartialCompleted = !0);
            var l = JSON.stringify(s);
            (a.onload = a.onerror = function () {
              r && r(200 === a.status, a.response, a);
            }),
              a.send(l);
          }),
          (e.prototype.sendFile = function (t, n, r) {
            var i = new XMLHttpRequest();
            (i.onload = i.onerror = function () {
              r && r(200 == i.status, JSON.parse(i.response));
            }),
              i.open('POST', e.serviceUrl + '/upload/', !0);
            var o = new FormData();
            o.append('file', n), o.append('postId', t), i.send(o);
          }),
          (e.prototype.getResult = function (t, n, r) {
            var i = new XMLHttpRequest(),
              o = 'resultId=' + t + '&name=' + n;
            i.open('GET', e.serviceUrl + '/getResult?' + o),
              i.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
            (i.onload = function () {
              var e = null,
                t = null;
              if (200 == i.status) {
                (e = JSON.parse(i.response)), (t = []);
                for (var n in e.QuestionResult) {
                  var o = { name: n, value: e.QuestionResult[n] };
                  t.push(o);
                }
              }
              r(200 == i.status, e, t, i.response);
            }),
              i.send();
          }),
          (e.prototype.isCompleted = function (t, n, r) {
            var i = new XMLHttpRequest(),
              o = 'resultId=' + t + '&clientId=' + n;
            i.open('GET', e.serviceUrl + '/isCompleted?' + o),
              i.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
            (i.onload = function () {
              var e = null;
              200 == i.status && (e = JSON.parse(i.response)), r(200 == i.status, e, i.response);
            }),
              i.send();
          }),
          e
        );
      })();
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(18);
      n.d(t, 'a', function () {
        return a;
      });
      var a = (function (e) {
        function t(t) {
          void 0 === t && (t = '');
          var n = e.call(this, t) || this;
          n.createLocalizableString('content', n, !0);
          var r = n;
          return (
            n.registerFunctionOnPropertyValueChanged('content', function () {
              r.onContentChanged();
            }),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getType = function () {
            return 'flowpanel';
          }),
          (t.prototype.getChildrenLayoutType = function () {
            return 'flow';
          }),
          (t.prototype.onSurveyLoad = function () {
            e.prototype.onSurveyLoad.call(this), this.onContentChanged();
          }),
          Object.defineProperty(t.prototype, 'content', {
            get: function () {
              return this.getLocalizableStringText('content');
            },
            set: function (e) {
              this.setLocalizableStringText('content', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locContent', {
            get: function () {
              return this.getLocalizableString('content');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'html', {
            get: function () {
              return this.getPropertyValue('html', '');
            },
            set: function (e) {
              this.setPropertyValue('html', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.onContentChanged = function () {
            var e = '';
            (e = this.onCustomHtmlProducing ? this.onCustomHtmlProducing() : this.produceHtml()),
              (this.html = e),
              this.contentChangedCallback && this.contentChangedCallback();
          }),
          (t.prototype.produceHtml = function () {
            for (
              var e = [], t = /{(.*?(element:)[^$].*?)}/g, n = this.content, r = 0, i = null;
              null !== (i = t.exec(n));

            ) {
              i.index > r && (e.push(n.substr(r, i.index - r)), (r = i.index));
              var o = this.getQuestionFromText(i[0]);
              o
                ? e.push(this.getHtmlForQuestion(o))
                : e.push(n.substr(r, i.index + i[0].length - r)),
                (r = i.index + i[0].length);
            }
            return (
              r < n.length && e.push(n.substr(r, n.length - r)),
              e.join('').replace(new RegExp('<br>', 'g'), '<br/>')
            );
          }),
          (t.prototype.getQuestionFromText = function (e) {
            return (
              (e = e.substr(1, e.length - 2)),
              (e = e.replace(t.contentElementNamePrefix, '').trim()),
              this.getQuestionByName(e)
            );
          }),
          (t.prototype.getHtmlForQuestion = function (e) {
            return this.onGetHtmlForQuestion ? this.onGetHtmlForQuestion(e) : '';
          }),
          (t.prototype.getQuestionHtmlId = function (e) {
            return this.name + '_' + e.id;
          }),
          (t.prototype.onAddElement = function (t, n) {
            e.prototype.onAddElement.call(this, t, n),
              this.addElementToContent(t),
              (t.renderWidth = '');
          }),
          (t.prototype.onRemoveElement = function (t) {
            var n = this.getElementContentText(t);
            (this.content = this.content.replace(n, '')), e.prototype.onRemoveElement.call(this, t);
          }),
          (t.prototype.dragDropMoveElement = function (e, t, n) {}),
          (t.prototype.addElementToContent = function (e) {
            if (!this.isLoadingFromJson) {
              var t = this.getElementContentText(e);
              this.insertTextAtCursor(t) || (this.content = this.content + t);
            }
          }),
          (t.prototype.insertTextAtCursor = function (e, t) {
            if (
              (void 0 === t && (t = null),
              !this.isDesignMode || 'undefined' == typeof document || !window.getSelection)
            )
              return !1;
            var n = window.getSelection();
            if (n.getRangeAt && n.rangeCount) {
              var r = n.getRangeAt(0);
              r.deleteContents(), r.insertNode(document.createTextNode(e));
              var i = this;
              if (i.getContent) {
                var o = i.getContent(t);
                this.content = o;
              }
              return !0;
            }
            return !1;
          }),
          (t.prototype.getElementContentText = function (e) {
            return '{' + t.contentElementNamePrefix + e.name + '}';
          }),
          (t.contentElementNamePrefix = 'element:'),
          t
        );
      })(o.b);
      i.b.addClass(
        'flowpanel',
        [{ name: 'content:html', serializationProperty: 'locContent' }],
        function () {
          return new a();
        },
        'panel'
      );
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(6)),
        a = n(61),
        s = n(24),
        l = n(1),
        u = n(3),
        c = n(8),
        p = n(15),
        d = n(22);
      n.d(t, 'a', function () {
        return h;
      });
      var h = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this,
              r = n;
            return (
              (n.koCellAfterRender = function (e, t) {
                return r.cellAfterRender(e, t);
              }),
              (n.koCellQuestionAfterRender = function (e, t) {
                return r.cellQuestionAfterRender(e, t);
              }),
              (n.koRecalc = i.observable(0)),
              (n.koTable = i.pureComputed(function () {
                return r.koRecalc(), r.question.renderedTable;
              })),
              (n.question.onRenderedTableCreatedCallback = function (e) {
                new p.a(e);
              }),
              (n.question.onRenderedTableResetCallback = function () {
                r.koRecalc(r.koRecalc() + 1);
              }),
              (n.koAddRowClick = function () {
                r.addRow();
              }),
              (n.koRemoveRowClick = function (e) {
                r.removeRow(e.row);
              }),
              (n.koIsAddRowOnTop = i.pureComputed(function () {
                return r.koRecalc(), r.isAddRowTop();
              })),
              (n.koIsAddRowOnBottom = i.pureComputed(function () {
                return r.koRecalc(), r.isAddRowBottom();
              })),
              (n.question.koTable = n.koTable),
              (n.question.koCellAfterRender = n.koCellAfterRender),
              (n.question.koCellQuestionAfterRender = n.koCellQuestionAfterRender),
              (n.question.koAddRowClick = n.koAddRowClick),
              (n.question.koRemoveRowClick = n.koRemoveRowClick),
              (n.question.koIsAddRowOnTop = n.koIsAddRowOnTop),
              (n.question.koIsAddRowOnBottom = n.koIsAddRowOnBottom),
              (n.question.koPanelAfterRender = function (e, t) {
                r.panelAfterRender(e, t);
              }),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getQuestionTemplate = function () {
              return 'matrixdynamic';
            }),
            (t.prototype.cellAfterRender = function (e, t) {
              if (this.question.survey) {
                var n = o.a.GetFirstNonTextElement(e);
                if (n) {
                  var r = t;
                  r.question.customWidget &&
                    (r.question.customWidget.afterRender(r.question, n),
                    i.utils.domNodeDisposal.addDisposeCallback(n, function () {
                      r.question.customWidget.willUnmount(r.question, n);
                    }));
                  var a = {
                    cell: r.cell,
                    cellQuestion: r.question,
                    htmlElement: n,
                    row: r.row,
                    column: r.cell ? r.cell.column : null,
                  };
                  this.question.survey.matrixAfterCellRender(this.question, a);
                }
              }
            }),
            (t.prototype.cellQuestionAfterRender = function (e, t) {
              if (this.question.survey) {
                var n = o.a.GetFirstNonTextElement(e);
                if (n) {
                  t.question.afterRenderQuestionElement(n);
                }
              }
            }),
            (t.prototype.isAddRowTop = function () {
              return !1;
            }),
            (t.prototype.isAddRowBottom = function () {
              return !1;
            }),
            (t.prototype.canRemoveRows = function () {
              return !1;
            }),
            (t.prototype.addRow = function () {}),
            (t.prototype.removeRow = function (e) {}),
            (t.prototype.panelAfterRender = function (e, t) {
              if (this.question && this.question.survey) {
                var n = o.a.GetFirstNonTextElement(e);
                this.question.survey.afterRenderPanel(t, n);
              }
            }),
            (t.prototype.dispose = function () {
              e.prototype.dispose.call(this),
                this.koTemplateName.dispose(),
                this.koTable.dispose(),
                this.koIsAddRowOnTop.dispose(),
                this.koIsAddRowOnBottom.dispose(),
                (this.question.onRenderedTableCreatedCallback = void 0),
                (this.question.onRenderedTableResetCallback = void 0);
            }),
            t
          );
        })(c.a),
        f = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), (this._implementor = new h(this));
            }),
            (t.prototype.createNewDetailPanel = function () {
              return new d.b();
            }),
            (t.prototype.dispose = function () {
              this._implementor.dispose(), (this._implementor = void 0);
            }),
            t
          );
        })(a.a);
      l.b.overrideClassCreator('matrixdropdown', function () {
        return new f('');
      }),
        u.b.Instance.registerQuestion('matrixdropdown', function (e) {
          var t = new f(e);
          return (
            (t.choices = [1, 2, 3, 4, 5]), (t.rows = u.b.DefaultRows), s.a.addDefaultColumns(t), t
          );
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(33),
        o = n(1),
        a = n(3),
        s = n(8);
      n.d(t, 'a', function () {
        return u;
      });
      var l = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.question = t), n;
          }
          return n.i(r.b)(t, e), t;
        })(s.a),
        u = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new l(this);
            }),
            t
          );
        })(i.a);
      o.b.overrideClassCreator('text', function () {
        return new u('');
      }),
        a.b.Instance.registerQuestion('text', function (e) {
          return new u(e);
        });
    },
    function (e, t, n) {
      'use strict';
      n.d(t, 'b', function () {
        return r;
      }),
        n.d(t, 'a', function () {
          return i;
        });
      var r = n(119),
        i = (function () {
          function e() {}
          return (
            (e.prototype.addText = function (e, t, n) {
              (t = this.getId(t, n)),
                (this.text = this.text + '<script type="text/html" ' + t + '>' + e + '</script>');
            }),
            (e.prototype.replaceText = function (e, t, n) {
              void 0 === n && (n = null);
              var r = this.getId(t, n),
                i = this.text.indexOf(r);
              if (i < 0) return void this.addText(e, t, n);
              if (!((i = this.text.indexOf('>', i)) < 0)) {
                var o = i + 1;
                (i = this.text.indexOf('</script>', o)),
                  i < 0 || (this.text = this.text.substr(0, o) + e + this.text.substr(i));
              }
            }),
            (e.prototype.getId = function (e, t) {
              var n = 'id="survey-' + e;
              return t && (n += '-' + t), n + '"';
            }),
            Object.defineProperty(e.prototype, 'text', {
              get: function () {
                return r;
              },
              set: function (e) {
                r = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            e
          );
        })();
    },
    function (e, t, n) {
      'use strict';
      n.d(t, 'a', function () {
        return r;
      });
      var r = {
        pagePrevText: 'Previous',
        pageNextText: 'Next',
        completeText: 'Complete',
        previewText: 'Preview',
        editText: 'Edit',
        startSurveyText: 'Start',
        otherItemText: 'Other (describe)',
        noneItemText: 'None',
        selectAllItemText: 'Select All',
        progressText: 'Page {0} of {1}',
        panelDynamicProgressText: 'Record {0} of {1}',
        questionsProgressText: 'Answered {0}/{1} questions',
        emptySurvey: 'There is no visible page or question in the survey.',
        completingSurvey: 'Thank you for completing the survey!',
        completingSurveyBefore: 'Our records show that you have already completed this survey.',
        loadingSurvey: 'Loading Survey...',
        optionsCaption: 'Choose...',
        value: 'value',
        requiredError: 'Please answer the question.',
        requiredErrorInPanel: 'Please answer at least one question.',
        requiredInAllRowsError: 'Please answer questions in all rows.',
        numericError: 'The value should be numeric.',
        textMinLength: 'Please enter at least {0} character(s).',
        textMaxLength: 'Please enter less than {0} character(s).',
        textMinMaxLength: 'Please enter more than {0} and less than {1} characters.',
        minRowCountError: 'Please fill in at least {0} row(s).',
        minSelectError: 'Please select at least {0} variant(s).',
        maxSelectError: 'Please select no more than {0} variant(s).',
        numericMinMax: "The '{0}' should be equal or more than {1} and equal or less than {2}",
        numericMin: "The '{0}' should be equal or more than {1}",
        numericMax: "The '{0}' should be equal or less than {1}",
        invalidEmail: 'Please enter a valid e-mail address.',
        invalidExpression: "The expression: {0} should return 'true'.",
        urlRequestError: "The request returned error '{0}'. {1}",
        urlGetChoicesError: "The request returned empty data or the 'path' property is incorrect",
        exceedMaxSize: 'The file size should not exceed {0}.',
        otherRequiredError: 'Please enter the other value.',
        uploadingFile: 'Your file is uploading. Please wait several seconds and try again.',
        loadingFile: 'Loading...',
        chooseFile: 'Choose file(s)...',
        noFileChosen: 'No file chosen',
        confirmDelete: 'Do you want to delete the record?',
        keyDuplicationError: 'This value should be unique.',
        addColumn: 'Add column',
        addRow: 'Add row',
        removeRow: 'Remove',
        addPanel: 'Add new',
        removePanel: 'Remove',
        choices_Item: 'item',
        matrix_column: 'Column',
        matrix_row: 'Row',
        savingData: 'The results are saving on the server...',
        savingDataError: 'An error occurred and we could not save the results.',
        savingDataSuccess: 'The results were saved successfully!',
        saveAgainButton: 'Try again',
        timerMin: 'min',
        timerSec: 'sec',
        timerSpentAll: 'You have spent {0} on this page and {1} in total.',
        timerSpentPage: 'You have spent {0} on this page.',
        timerSpentSurvey: 'You have spent {0} in total.',
        timerLimitAll: 'You have spent {0} of {1} on this page and {2} of {3} in total.',
        timerLimitPage: 'You have spent {0} of {1} on this page.',
        timerLimitSurvey: 'You have spent {0} of {1} in total.',
        cleanCaption: 'Clean',
        clearCaption: 'Clear',
        chooseFileCaption: 'Choose file',
        removeFileCaption: 'Remove this file',
        booleanCheckedLabel: 'Yes',
        booleanUncheckedLabel: 'No',
        confirmRemoveFile: 'Are you sure that you want to remove this file: {0}?',
        confirmRemoveAllFiles: 'Are you sure that you want to remove all files?',
        questionTitlePatternText: 'Question Title',
        modalCancelButtonText: 'Cancel',
        modalApplyButtonText: 'Apply',
      };
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(11),
        o = n(9),
        a = n(1),
        s = n(10),
        l = n(4);
      n.d(t, 'a', function () {
        return u;
      });
      var u = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          return (
            (n.name = t),
            (n.generatedVisibleRows = null),
            (n.generatedTotalRow = null),
            (n.filteredRows = null),
            (n.filteredColumns = null),
            (n.columns = n.createColumnValues()),
            (n.rows = n.createItemValues('rows')),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.createColumnValues = function () {
            return this.createItemValues('columns');
          }),
          (t.prototype.getType = function () {
            return 'matrixbase';
          }),
          Object.defineProperty(t.prototype, 'isCompositeQuestion', {
            get: function () {
              return !0;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'showHeader', {
            get: function () {
              return this.getPropertyValue('showHeader');
            },
            set: function (e) {
              this.setPropertyValue('showHeader', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'columns', {
            get: function () {
              return this.getPropertyValue('columns');
            },
            set: function (e) {
              this.setPropertyValue('columns', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'visibleColumns', {
            get: function () {
              return this.filteredColumns ? this.filteredColumns : this.columns;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'rows', {
            get: function () {
              return this.getPropertyValue('rows');
            },
            set: function (e) {
              var t = this.processRowsOnSet(e);
              this.setPropertyValue('rows', t), this.filterItems();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.processRowsOnSet = function (e) {
            return e;
          }),
          (t.prototype.getVisibleRows = function () {
            return [];
          }),
          Object.defineProperty(t.prototype, 'visibleRows', {
            get: function () {
              return this.getVisibleRows();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'rowsVisibleIf', {
            get: function () {
              return this.getPropertyValue('rowsVisibleIf', '');
            },
            set: function (e) {
              this.setPropertyValue('rowsVisibleIf', e), this.filterItems();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'columnsVisibleIf', {
            get: function () {
              return this.getPropertyValue('columnsVisibleIf', '');
            },
            set: function (e) {
              this.setPropertyValue('columnsVisibleIf', e), this.filterItems();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.runCondition = function (t, n) {
            e.prototype.runCondition.call(this, t, n), this.runItemsCondition(t, n);
          }),
          (t.prototype.filterItems = function () {
            return this.areInvisibleElementsShowing
              ? (this.onRowsChanged(), !1)
              : !(this.isLoadingFromJson || !this.data) &&
                  this.runItemsCondition(
                    this.getDataFilteredValues(),
                    this.getDataFilteredProperties()
                  );
          }),
          (t.prototype.onColumnsChanged = function () {}),
          (t.prototype.onRowsChanged = function () {
            this.fireCallback(this.visibleRowsChangedCallback);
          }),
          (t.prototype.shouldRunColumnExpression = function () {
            return !this.survey || !this.survey.areInvisibleElementsShowing;
          }),
          (t.prototype.hasRowsAsItems = function () {
            return !0;
          }),
          (t.prototype.runItemsCondition = function (e, t) {
            var n = null;
            if (this.filteredRows && !l.a.isValueEmpty(this.defaultValue)) {
              n = [];
              for (var r = 0; r < this.filteredRows.length; r++) n.push(this.filteredRows[r]);
            }
            var i = this.hasRowsAsItems() && this.runConditionsForRows(e, t),
              o = this.runConditionsForColumns(e, t);
            return (
              (i = o || i),
              i &&
                (this.survey &&
                  this.survey.isClearValueOnHidden &&
                  (this.filteredColumns || this.filteredRows) &&
                  this.clearIncorrectValues(),
                n && this.restoreNewVisibleRowsValues(n),
                this.clearGeneratedRows(),
                o && this.onColumnsChanged(),
                this.onRowsChanged()),
              i
            );
          }),
          (t.prototype.clearGeneratedRows = function () {
            this.generatedVisibleRows = null;
          }),
          (t.prototype.runConditionsForRows = function (e, t) {
            var n = !!this.survey && this.survey.areInvisibleElementsShowing,
              r = !n && this.rowsVisibleIf ? new s.a(this.rowsVisibleIf) : null;
            this.filteredRows = [];
            var o = i.a.runConditionsForItems(this.rows, this.filteredRows, r, e, t, !n);
            return this.filteredRows.length === this.rows.length && (this.filteredRows = null), o;
          }),
          (t.prototype.runConditionsForColumns = function (e, t) {
            var n = !!this.survey && !this.survey.areInvisibleElementsShowing,
              r = n && this.columnsVisibleIf ? new s.a(this.columnsVisibleIf) : null;
            this.filteredColumns = [];
            var o = i.a.runConditionsForItems(
              this.columns,
              this.filteredColumns,
              r,
              e,
              t,
              this.shouldRunColumnExpression()
            );
            return (
              this.filteredColumns.length === this.columns.length && (this.filteredColumns = null),
              o
            );
          }),
          (t.prototype.clearIncorrectValues = function () {
            var t = this.value;
            if (t) {
              var n = null,
                r = !1,
                o = this.filteredRows ? this.filteredRows : this.rows,
                a = this.filteredColumns ? this.filteredColumns : this.columns;
              for (var s in t)
                i.a.getItemByValue(o, s) && i.a.getItemByValue(a, t[s])
                  ? (null == n && (n = {}), (n[s] = t[s]))
                  : (r = !0);
              r && (this.value = n), e.prototype.clearIncorrectValues.call(this);
            }
          }),
          (t.prototype.clearInvisibleValuesInRows = function () {
            if (!this.isEmpty()) {
              for (
                var e = this.getUnbindValue(this.value), t = this.rows, n = 0;
                n < t.length;
                n++
              ) {
                var r = t[n].value;
                e[r] && !t[n].isVisible && delete e[r];
              }
              l.a.isTwoValueEquals(e, this.value) || (this.value = e);
            }
          }),
          (t.prototype.restoreNewVisibleRowsValues = function (e) {
            var t = this.filteredRows ? this.filteredRows : this.rows,
              n = this.defaultValue,
              r = this.getUnbindValue(this.value),
              o = !1;
            for (var a in n)
              i.a.getItemByValue(t, a) &&
                !i.a.getItemByValue(e, a) &&
                (null == r && (r = {}), (r[a] = n[a]), (o = !0));
            o && (this.value = r);
          }),
          t
        );
      })(o.a);
      a.b.addClass(
        'matrixbase',
        [
          'columnsVisibleIf:condition',
          'rowsVisibleIf:condition',
          { name: 'showHeader:boolean', default: !0 },
        ],
        void 0,
        'question'
      );
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(3),
        o = n(1),
        a = n(9),
        s = n(2);
      n.d(t, 'a', function () {
        return l;
      });
      var l = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          return (
            (n.name = t),
            n.createLocalizableString('labelFalse', n, !0),
            n.createLocalizableString('labelTrue', n, !0),
            (n.locLabelFalse.onGetTextCallback = function (e) {
              return e || s.a.getString('booleanUncheckedLabel');
            }),
            (n.locLabelTrue.onGetTextCallback = function (e) {
              return e || s.a.getString('booleanCheckedLabel');
            }),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getType = function () {
            return 'boolean';
          }),
          (t.prototype.isLayoutTypeSupported = function (e) {
            return !0;
          }),
          Object.defineProperty(t.prototype, 'isIndeterminate', {
            get: function () {
              return this.isEmpty();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasTitle', {
            get: function () {
              return !0;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'checkedValue', {
            get: function () {
              return this.isEmpty() ? null : this.value == this.getValueTrue();
            },
            set: function (e) {
              this.isReadOnly ||
                (this.isValueEmpty(e)
                  ? (this.value = null)
                  : (this.value = 1 == e ? this.getValueTrue() : this.getValueFalse()));
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'defaultValue', {
            get: function () {
              return this.getPropertyValue('defaultValue');
            },
            set: function (e) {
              !0 === e && (e = 'true'),
                !1 === e && (e = 'false'),
                this.setPropertyValue('defaultValue', e),
                this.updateValueWithDefaults();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getDefaultValue = function () {
            return 'indeterminate' == this.defaultValue
              ? null
              : void 0 === this.defaultValue
              ? null
              : 'true' == this.defaultValue
              ? this.getValueTrue()
              : this.getValueFalse();
          }),
          Object.defineProperty(t.prototype, 'locTitle', {
            get: function () {
              return this.showTitle || this.isValueEmpty(this.locLabel.text)
                ? this.getLocalizableString('title')
                : this.locLabel;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locDisplayLabel', {
            get: function () {
              return this.locLabel.text
                ? this.locLabel
                : this.showTitle
                ? this.locLabel
                : this.locTitle;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'labelTrue', {
            get: function () {
              return this.getLocalizableStringText('labelTrue');
            },
            set: function (e) {
              this.setLocalizableStringText('labelTrue', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locLabelTrue', {
            get: function () {
              return this.getLocalizableString('labelTrue');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'labelFalse', {
            get: function () {
              return this.getLocalizableStringText('labelFalse');
            },
            set: function (e) {
              this.setLocalizableStringText('labelFalse', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locLabelFalse', {
            get: function () {
              return this.getLocalizableString('labelFalse');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getValueTrue = function () {
            return !this.valueTrue || this.valueTrue;
          }),
          (t.prototype.getValueFalse = function () {
            return !!this.valueFalse && this.valueFalse;
          }),
          (t.prototype.setDefaultValue = function () {
            'true' == this.defaultValue && (this.checkedValue = !0),
              'false' == this.defaultValue && (this.checkedValue = !1),
              'indeterminate' == this.defaultValue && (this.value = null);
          }),
          n.i(r.c)([n.i(o.e)({ localizable: !0 })], t.prototype, 'label', void 0),
          n.i(r.c)([n.i(o.e)()], t.prototype, 'showTitle', void 0),
          n.i(r.c)([n.i(o.e)()], t.prototype, 'valueTrue', void 0),
          n.i(r.c)([n.i(o.e)()], t.prototype, 'valueFalse', void 0),
          t
        );
      })(a.a);
      o.b.addClass(
        'boolean',
        [
          { name: 'label:text', serializationProperty: 'locLabel' },
          { name: 'labelTrue:text', serializationProperty: 'locLabelTrue' },
          { name: 'labelFalse:text', serializationProperty: 'locLabelFalse' },
          'showTitle:boolean',
          'valueTrue',
          'valueFalse',
          { name: 'renderAs', default: 'default', visible: !1 },
        ],
        function () {
          return new l('');
        },
        'question'
      ),
        i.b.Instance.registerQuestion('boolean', function (e) {
          return new l(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(3),
        a = n(23),
        s = n(4),
        l = n(11),
        u = n(2);
      n.d(t, 'a', function () {
        return c;
      });
      var c = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          (n.name = t),
            (n.noneItemValue = new l.a('none')),
            (n.selectAllItemValue = new l.a('selectall')),
            (n.invisibleOldValues = {}),
            (n.isChangingValueOnClearIncorrect = !1);
          var r = n.createLocalizableString('noneText', n, !0);
          (r.onGetTextCallback = function (e) {
            return e || u.a.getString('noneItemText');
          }),
            (n.noneItemValue.locOwner = n),
            n.noneItemValue.setLocText(r);
          var i = n.createLocalizableString('selectAllText', n, !0);
          (i.onGetTextCallback = function (e) {
            return e || u.a.getString('selectAllItemText');
          }),
            (n.selectAllItem.locOwner = n),
            n.selectAllItem.setLocText(i);
          var o = n;
          return (
            n.registerFunctionOnPropertiesValueChanged(
              ['hasNone', 'noneText', 'hasSelectAll', 'selectAllText'],
              function () {
                o.onVisibleChoicesChanged();
              }
            ),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          Object.defineProperty(t.prototype, 'ariaRole', {
            get: function () {
              return 'group';
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getType = function () {
            return 'checkbox';
          }),
          (t.prototype.onCreating = function () {
            e.prototype.onCreating.call(this),
              this.createNewArray('renderedValue'),
              this.createNewArray('value');
          }),
          (t.prototype.getFirstInputElementId = function () {
            return this.inputId + '_0';
          }),
          Object.defineProperty(t.prototype, 'selectAllItem', {
            get: function () {
              return this.selectAllItemValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'noneItem', {
            get: function () {
              return this.noneItemValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'noneText', {
            get: function () {
              return this.getLocalizableStringText('noneText', u.a.getString('noneItemText'));
            },
            set: function (e) {
              this.setLocalizableStringText('noneText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locNoneText', {
            get: function () {
              return this.getLocalizableString('noneText');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'selectAllText', {
            get: function () {
              return this.getLocalizableStringText(
                'selectAllText',
                u.a.getString('selectAllItemText')
              );
            },
            set: function (e) {
              this.setLocalizableStringText('selectAllText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locSelectAllText', {
            get: function () {
              return this.getLocalizableString('selectAllText');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'hasSelectAll', {
            get: function () {
              return this.getPropertyValue('hasSelectAll', !1);
            },
            set: function (e) {
              this.setPropertyValue('hasSelectAll', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isAllSelected', {
            get: function () {
              var e = this.value;
              if (!e || !Array.isArray(e)) return !1;
              if (this.isItemSelected(this.noneItem)) return !1;
              var t = this.visibleChoices.length;
              this.hasOther && t--, this.hasNone && t--, this.hasSelectAll && t--;
              var n = e.length;
              return this.isItemSelected(this.otherItem) && n--, n === t;
            },
            set: function (e) {
              e ? this.selectAll() : this.clearValue();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.toggleSelectAll = function () {
            this.isAllSelected = !this.isAllSelected;
          }),
          (t.prototype.selectAll = function () {
            for (var e = [], t = 0; t < this.visibleChoices.length; t++) {
              var n = this.visibleChoices[t];
              n !== this.noneItem &&
                n !== this.otherItem &&
                n !== this.selectAllItem &&
                e.push(n.value);
            }
            this.value = e;
          }),
          Object.defineProperty(t.prototype, 'hasNone', {
            get: function () {
              return this.getPropertyValue('hasNone', !1);
            },
            set: function (e) {
              this.setPropertyValue('hasNone', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.isItemSelected = function (e) {
            if (e === this.selectAllItem) return this.isAllSelected;
            var t = this.renderedValue;
            if (!t || !Array.isArray(t)) return !1;
            for (var n = 0; n < t.length; n++) if (s.a.isTwoValueEquals(t[n], e.value)) return !0;
            return !1;
          }),
          Object.defineProperty(t.prototype, 'maxSelectedChoices', {
            get: function () {
              return this.getPropertyValue('maxSelectedChoices', 0);
            },
            set: function (e) {
              e < 0 && (e = 0), this.setPropertyValue('maxSelectedChoices', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.onEnableItemCallBack = function (e) {
            return !this.shouldCheckMaxSelectedChoices() || this.isItemSelected(e);
          }),
          (t.prototype.onAfterRunItemsEnableCondition = function () {
            this.maxSelectedChoices < 1 ||
              (this.hasSelectAll &&
                this.selectAllItem.setIsEnabled(
                  this.maxSelectedChoices >= this.activeChoices.length
                ),
              this.hasOther &&
                this.otherItem.setIsEnabled(
                  !this.shouldCheckMaxSelectedChoices() || this.isOtherSelected
                ));
          }),
          (t.prototype.shouldCheckMaxSelectedChoices = function () {
            if (this.maxSelectedChoices < 1) return !1;
            var e = this.value;
            return (Array.isArray(e) ? e.length : 0) >= this.maxSelectedChoices;
          }),
          (t.prototype.getItemClass = function (e) {
            var t = (this.value, this.isItemSelected(e)),
              n = this.isReadOnly || !e.isEnabled,
              r = !t && !n,
              i = e === this.selectAllItem,
              o = e === this.noneItem,
              a = this.cssClasses.item;
            return (
              this.hasColumns ||
                (a +=
                  0 === this.colCount
                    ? ' ' + this.cssClasses.itemInline
                    : ' sv-q-col-' + this.colCount),
              n && this.cssClasses.itemDisabled && (a += ' ' + this.cssClasses.itemDisabled),
              t && this.cssClasses.itemChecked && (a += ' ' + this.cssClasses.itemChecked),
              r && this.cssClasses.itemHover && (a += ' ' + this.cssClasses.itemHover),
              i && this.cssClasses.itemSelectAll && (a += ' ' + this.cssClasses.itemSelectAll),
              o && this.cssClasses.itemNone && (a += ' ' + this.cssClasses.itemNone),
              a
            );
          }),
          (t.prototype.setNewValue = function (t) {
            this.isChangingValueOnClearIncorrect || (this.invisibleOldValues = []),
              (t = this.valueFromData(t));
            var n = this.value;
            if ((t || (t = []), n || (n = []), !s.a.isTwoValueEquals(n, t))) {
              if (this.hasNone) {
                var r = this.noneIndexInArray(n),
                  i = this.noneIndexInArray(t);
                r > -1
                  ? i > -1 && t.length > 1 && t.splice(i, 1)
                  : i > -1 && (t.splice(0, t.length), t.push(this.noneItem.value));
              }
              e.prototype.setNewValue.call(this, this.rendredValueToData(t));
            }
          }),
          (t.prototype.getIsMultipleValue = function () {
            return !0;
          }),
          (t.prototype.getCommentFromValue = function (e) {
            var t = this.getFirstUnknownIndex(e);
            return t < 0 ? '' : e[t];
          }),
          (t.prototype.setOtherValueIntoValue = function (e) {
            var t = this.getFirstUnknownIndex(e);
            return t < 0 ? e : (e.splice(t, 1, this.otherItem.value), e);
          }),
          (t.prototype.getFirstUnknownIndex = function (e) {
            if (!Array.isArray(e)) return -1;
            for (var t = 0; t < e.length; t++) if (this.hasUnknownValue(e[t])) return t;
            return -1;
          }),
          (t.prototype.noneIndexInArray = function (e) {
            if (!e || !Array.isArray(e)) return -1;
            for (var t = this.noneItem.value, n = 0; n < e.length; n++) if (e[n] == t) return n;
            return -1;
          }),
          (t.prototype.canUseFilteredChoices = function () {
            return (
              !this.hasNone && !this.hasSelectAll && e.prototype.canUseFilteredChoices.call(this)
            );
          }),
          (t.prototype.addToVisibleChoices = function (t) {
            this.hasSelectAll && t.unshift(this.selectAllItem),
              e.prototype.addToVisibleChoices.call(this, t),
              this.hasNone && t.push(this.noneItem);
          }),
          (t.prototype.getDisplayValueCore = function (t, n) {
            if (!Array.isArray(n)) return e.prototype.getDisplayValueCore.call(this, t, n);
            for (var r = this.visibleChoices, i = '', o = 0; o < n.length; o++) {
              var a = this.getChoicesDisplayValue(r, n[o]);
              a && (i && (i += ', '), (i += a));
            }
            return i;
          }),
          (t.prototype.clearIncorrectValuesCore = function () {
            this.clearIncorrectAndDisabledValues(!1);
          }),
          (t.prototype.clearDisabledValuesCore = function () {
            this.clearIncorrectAndDisabledValues(!0);
          }),
          (t.prototype.clearIncorrectAndDisabledValues = function (e) {
            var t = this.value,
              n = !1,
              r = this.restoreValuesFromInvisible();
            if (t || 0 != r.length) {
              if (!Array.isArray(t) || 0 == t.length) {
                if (
                  ((this.isChangingValueOnClearIncorrect = !0),
                  e || (this.hasComment ? (this.value = null) : this.clearValue()),
                  (this.isChangingValueOnClearIncorrect = !1),
                  0 == r.length)
                )
                  return;
                t = [];
              }
              for (var i = [], o = 0; o < t.length; o++) {
                var a = this.canClearValueAnUnknow(t[o]);
                (!e && !a) || (e && !this.isValueDisabled(t[o]))
                  ? i.push(t[o])
                  : ((n = !0), a && (this.invisibleOldValues[t[o]] = !0));
              }
              for (var o = 0; o < r.length; o++) i.push(r[o]), (n = !0);
              n &&
                ((this.isChangingValueOnClearIncorrect = !0),
                0 == i.length ? this.clearValue() : (this.value = i),
                (this.isChangingValueOnClearIncorrect = !1));
            }
          }),
          (t.prototype.restoreValuesFromInvisible = function () {
            for (var e = [], t = this.visibleChoices, n = 0; n < t.length; n++) {
              var r = t[n].value;
              this.invisibleOldValues[r] && (e.push(r), delete this.invisibleOldValues[r]);
            }
            return e;
          }),
          (t.prototype.getConditionJson = function (t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.prototype.getConditionJson.call(this);
            return ('contains' != t && 'notcontains' != t) || (r.type = 'radiogroup'), r;
          }),
          (t.prototype.isAnswerCorrect = function () {
            return s.a.isArrayContainsEqual(this.value, this.correctAnswer);
          }),
          (t.prototype.setDefaultValueWithOthers = function () {
            this.value = this.renderedValueFromDataCore(this.defaultValue);
          }),
          (t.prototype.getHasOther = function (e) {
            return !(!e || !Array.isArray(e)) && e.indexOf(this.otherItem.value) >= 0;
          }),
          (t.prototype.valueFromData = function (t) {
            if (!t) return t;
            if (!Array.isArray(t)) return [e.prototype.valueFromData.call(this, t)];
            for (var n = [], r = 0; r < t.length; r++) {
              var i = l.a.getItemByValue(this.activeChoices, t[r]);
              i ? n.push(i.value) : n.push(t[r]);
            }
            return n;
          }),
          (t.prototype.renderedValueFromDataCore = function (e) {
            (e && Array.isArray(e)) || (e = []);
            for (var t = 0; t < e.length; t++) {
              if (e[t] == this.otherItem.value) return e;
              if (this.hasUnknownValue(e[t])) {
                this.comment = e[t];
                var n = e.slice();
                return (n[t] = this.otherItem.value), n;
              }
            }
            return e;
          }),
          (t.prototype.rendredValueToDataCore = function (e) {
            if (!e || !e.length) return e;
            for (var t = 0; t < e.length; t++)
              if (e[t] == this.otherItem.value && this.getQuestionComment()) {
                var n = e.slice();
                return (n[t] = this.getQuestionComment()), n;
              }
            return e;
          }),
          (t.prototype.hasUnknownValue = function (t, n) {
            return (
              void 0 === n && (n = !1),
              (!this.hasNone || t != this.noneItemValue.value) &&
                e.prototype.hasUnknownValue.call(this, t, n)
            );
          }),
          t
        );
      })(a.a);
      i.b.addClass(
        'checkbox',
        [
          'hasSelectAll:boolean',
          'hasNone:boolean',
          { name: 'maxSelectedChoices', default: 0 },
          { name: 'noneText', serializationProperty: 'locNoneText' },
          { name: 'selectAllText', serializationProperty: 'locSelectAllText' },
        ],
        function () {
          return new c('');
        },
        'checkboxbase'
      ),
        o.b.Instance.registerQuestion('checkbox', function (e) {
          var t = new c(e);
          return (t.choices = o.b.DefaultChoices), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(9),
        o = n(1),
        a = n(3),
        s = n(4);
      n.d(t, 'a', function () {
        return l;
      });
      var l = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          return (n.name = t), n.createLocalizableString('placeHolder', n), n;
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.isTextValue = function () {
            return !0;
          }),
          Object.defineProperty(t.prototype, 'maxLength', {
            get: function () {
              return this.getPropertyValue('maxLength');
            },
            set: function (e) {
              this.setPropertyValue('maxLength', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getMaxLength = function () {
            return s.a.getMaxLength(this.maxLength, this.survey ? this.survey.maxTextLength : -1);
          }),
          Object.defineProperty(t.prototype, 'placeHolder', {
            get: function () {
              return this.getLocalizableStringText('placeHolder');
            },
            set: function (e) {
              this.setLocalizableStringText('placeHolder', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locPlaceHolder', {
            get: function () {
              return this.getLocalizableString('placeHolder');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'rows', {
            get: function () {
              return this.getPropertyValue('rows');
            },
            set: function (e) {
              this.setPropertyValue('rows', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cols', {
            get: function () {
              return this.getPropertyValue('cols');
            },
            set: function (e) {
              this.setPropertyValue('cols', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getType = function () {
            return 'comment';
          }),
          (t.prototype.isEmpty = function () {
            return e.prototype.isEmpty.call(this) || '' === this.value;
          }),
          Object.defineProperty(t.prototype, 'textUpdateMode', {
            get: function () {
              return this.getPropertyValue('textUpdateMode');
            },
            set: function (e) {
              this.setPropertyValue('textUpdateMode', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isSurveyInputTextUpdate', {
            get: function () {
              return 'default' == this.textUpdateMode
                ? !!this.survey && this.survey.isUpdateValueTextOnTyping
                : 'onTyping' == this.textUpdateMode;
            },
            enumerable: !0,
            configurable: !0,
          }),
          t
        );
      })(i.a);
      o.b.addClass(
        'comment',
        [
          { name: 'maxLength:number', default: -1 },
          { name: 'cols:number', default: 50 },
          { name: 'rows:number', default: 4 },
          { name: 'placeHolder', serializationProperty: 'locPlaceHolder' },
          {
            name: 'textUpdateMode',
            default: 'default',
            choices: ['default', 'onBlur', 'onTyping'],
          },
        ],
        function () {
          return new l('');
        },
        'question'
      ),
        a.b.Instance.registerQuestion('comment', function (e) {
          return new l(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(9),
        o = n(1),
        a = n(6),
        s = n(4),
        l = n(25);
      n.d(t, 'd', function () {
        return u;
      }),
        n.d(t, 'c', function () {
          return c;
        }),
        n.d(t, 'a', function () {
          return d;
        }),
        n.d(t, 'b', function () {
          return f;
        });
      var u = (function () {
          function e(e, t) {
            (this.name = e), (this.json = t);
            var n = this;
            o.b.addClass(
              e,
              [],
              function (e) {
                return c.Instance.createQuestion(e ? e.name : '', n);
              },
              'question'
            ),
              this.onInit();
          }
          return (
            (e.prototype.onInit = function () {
              this.json.onInit && this.json.onInit();
            }),
            (e.prototype.onCreated = function (e) {
              this.json.onCreated && this.json.onCreated(e);
            }),
            (e.prototype.onLoaded = function (e) {
              this.json.onLoaded && this.json.onLoaded(e);
            }),
            (e.prototype.onAfterRender = function (e, t) {
              this.json.onAfterRender && this.json.onAfterRender(e, t);
            }),
            (e.prototype.onAfterRenderContentElement = function (e, t, n) {
              this.json.onAfterRenderContentElement &&
                this.json.onAfterRenderContentElement(e, t, n);
            }),
            (e.prototype.onPropertyChanged = function (e, t, n) {
              this.json.onPropertyChanged && this.json.onPropertyChanged(e, t, n);
            }),
            (e.prototype.onValueChanged = function (e, t, n) {
              this.json.onValueChanged && this.json.onValueChanged(e, t, n);
            }),
            (e.prototype.onItemValuePropertyChanged = function (e, t, n, r, i) {
              this.json.onItemValuePropertyChanged &&
                this.json.onItemValuePropertyChanged(e, {
                  obj: t,
                  propertyName: n,
                  name: r,
                  newValue: i,
                });
            }),
            Object.defineProperty(e.prototype, 'isComposite', {
              get: function () {
                return !!this.json.elementsJSON || !!this.json.createElements;
              },
              enumerable: !0,
              configurable: !0,
            }),
            e
          );
        })(),
        c = (function () {
          function e() {
            this.customQuestionValues = [];
          }
          return (
            (e.prototype.add = function (e) {
              if (e) {
                var t = e.name;
                if (!t) throw 'Attribute name is missed';
                if (((t = t.toLowerCase()), this.getCustomQuestionByName(t)))
                  throw "There is already registered custom question with name '" + t + "'";
                if (o.b.findClass(t)) throw "There is already class with name '" + t + "'";
                var n = new u(t, e);
                this.onAddingJson && this.onAddingJson(t, n.isComposite),
                  this.customQuestionValues.push(n);
              }
            }),
            Object.defineProperty(e.prototype, 'items', {
              get: function () {
                return this.customQuestionValues;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getCustomQuestionByName = function (e) {
              for (var t = 0; t < this.customQuestionValues.length; t++)
                if (this.customQuestionValues[t].name == e) return this.customQuestionValues[t];
              return null;
            }),
            (e.prototype.clear = function () {
              for (var e = 0; e < this.customQuestionValues.length; e++)
                o.b.removeClass(this.customQuestionValues[e].name);
              this.customQuestionValues = [];
            }),
            (e.prototype.createQuestion = function (e, t) {
              return t.isComposite ? this.createCompositeModel(e, t) : this.createCustomModel(e, t);
            }),
            (e.prototype.createCompositeModel = function (e, t) {
              return this.onCreateComposite ? this.onCreateComposite(e, t) : new f(e, t);
            }),
            (e.prototype.createCustomModel = function (e, t) {
              return this.onCreateCustom ? this.onCreateCustom(e, t) : new d(e, t);
            }),
            (e.Instance = new e()),
            e
          );
        })(),
        p = (function (e) {
          function t(t, n) {
            var r = e.call(this, t) || this;
            return (
              (r.name = t),
              (r.customQuestion = n),
              o.a.createProperties(r),
              (a.a.CreateDisabledDesignElements = !0),
              r.createWrapper(),
              (a.a.CreateDisabledDesignElements = !1),
              r.customQuestion && r.customQuestion.onCreated(r),
              r
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return this.customQuestion ? this.customQuestion.name : 'custom';
            }),
            (t.prototype.createWrapper = function () {}),
            (t.prototype.onPropertyValueChanged = function (t, n, r) {
              e.prototype.onPropertyValueChanged.call(this, t, n, r),
                this.customQuestion &&
                  !this.isLoadingFromJson &&
                  this.customQuestion.onPropertyChanged(this, t, r);
            }),
            (t.prototype.itemValuePropertyChanged = function (t, n, r, i) {
              e.prototype.itemValuePropertyChanged.call(this, t, n, r, i),
                this.customQuestion &&
                  !this.isLoadingFromJson &&
                  this.customQuestion.onItemValuePropertyChanged(
                    this,
                    t,
                    t.ownerPropertyName,
                    n,
                    i
                  );
            }),
            (t.prototype.onFirstRendering = function () {
              var t = this.getElement();
              t && t.onFirstRendering(), e.prototype.onFirstRendering.call(this);
            }),
            (t.prototype.initElement = function (e) {
              e && (e.setSurveyImpl(this), (e.disableDesignActions = !0));
            }),
            (t.prototype.setSurveyImpl = function (t) {
              e.prototype.setSurveyImpl.call(this, t), this.initElement(this.getElement());
            }),
            (t.prototype.onSurveyLoad = function () {
              e.prototype.onSurveyLoad.call(this),
                this.getElement() &&
                  (this.getElement().onSurveyLoad(), this.customQuestion.onLoaded(this));
            }),
            (t.prototype.afterRenderQuestionElement = function (e) {}),
            (t.prototype.afterRender = function (t) {
              e.prototype.afterRender.call(this, t),
                this.customQuestion && this.customQuestion.onAfterRender(this, t);
            }),
            (t.prototype.setQuestionValue = function (t, n) {
              void 0 === n && (n = !0),
                e.prototype.setQuestionValue.call(this, t, n),
                this.updateElementCss();
            }),
            (t.prototype.setNewValue = function (t) {
              e.prototype.setNewValue.call(this, t), this.updateElementCss();
            }),
            (t.prototype.geSurveyData = function () {
              return this;
            }),
            (t.prototype.getSurvey = function () {
              return this.survey;
            }),
            (t.prototype.getTextProcessor = function () {
              return this.textProcessor;
            }),
            (t.prototype.getValue = function (e) {
              return this.value;
            }),
            (t.prototype.setValue = function (e, t, n, r) {
              if (this.data) {
                var i = this.convertDataName(e);
                this.data.setValue(i, this.convertDataValue(e, t), n, r),
                  this.updateIsAnswered(),
                  this.updateElementCss(),
                  this.customQuestion && this.customQuestion.onValueChanged(this, e, t);
              }
            }),
            (t.prototype.convertDataName = function (e) {
              return this.getValueName();
            }),
            (t.prototype.convertDataValue = function (e, t) {
              return t;
            }),
            (t.prototype.getVariable = function (e) {
              return this.data ? this.data.getVariable(e) : null;
            }),
            (t.prototype.setVariable = function (e, t) {
              this.data && this.data.setVariable(e, t);
            }),
            (t.prototype.getComment = function (e) {
              return this.data ? this.data.getComment(this.getValueName()) : '';
            }),
            (t.prototype.setComment = function (e, t, n) {
              this.data && this.data.setComment(this.getValueName(), t, n);
            }),
            (t.prototype.getAllValues = function () {
              return this.data ? this.data.getAllValues() : {};
            }),
            (t.prototype.getFilteredValues = function () {
              return this.data ? this.data.getFilteredValues() : {};
            }),
            (t.prototype.getFilteredProperties = function () {
              return this.data ? this.data.getFilteredProperties() : {};
            }),
            (t.prototype.addElement = function (e, t) {}),
            (t.prototype.removeElement = function (e) {
              return !1;
            }),
            (t.prototype.getQuestionTitleLocation = function () {
              return 'left';
            }),
            (t.prototype.getQuestionStartIndex = function () {
              return this.getStartIndex();
            }),
            (t.prototype.getChildrenLayoutType = function () {
              return 'row';
            }),
            (t.prototype.elementWidthChanged = function (e) {}),
            Object.defineProperty(t.prototype, 'elements', {
              get: function () {
                return [];
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.indexOf = function (e) {
              return -1;
            }),
            t
          );
        })(i.a),
        d = (function (e) {
          function t() {
            return (null !== e && e.apply(this, arguments)) || this;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getTemplate = function () {
              return 'custom';
            }),
            (t.prototype.createWrapper = function () {
              this.questionWrapper = this.createQuestion();
            }),
            (t.prototype.getElement = function () {
              return this.contentQuestion;
            }),
            (t.prototype.onAnyValueChanged = function (t) {
              e.prototype.onAnyValueChanged.call(this, t),
                this.contentQuestion && this.contentQuestion.onAnyValueChanged(t);
            }),
            (t.prototype.hasErrors = function (t, n) {
              if ((void 0 === t && (t = !0), void 0 === n && (n = null), !this.contentQuestion))
                return !1;
              var r = this.contentQuestion.hasErrors(t, n);
              this.errors = [];
              for (var i = 0; i < this.contentQuestion.errors.length; i++)
                this.errors.push(this.contentQuestion.errors[i]);
              return r || (r = e.prototype.hasErrors.call(this, t, n)), this.updateElementCss(), r;
            }),
            (t.prototype.focus = function (t) {
              void 0 === t && (t = !1),
                this.contentQuestion
                  ? this.contentQuestion.focus(t)
                  : e.prototype.focus.call(this, t);
            }),
            Object.defineProperty(t.prototype, 'contentQuestion', {
              get: function () {
                return this.questionWrapper;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.createQuestion = function () {
              var e = this.customQuestion.json,
                t = null;
              if (e.questionJSON) {
                var n = e.questionJSON.type;
                if (!n || !o.b.findClass(n))
                  throw 'type attribute in questionJSON is empty or incorrect';
                (t = o.b.createClass(n)), this.initElement(t), t.fromJSON(e.questionJSON);
              } else e.createQuestion && ((t = e.createQuestion()), this.initElement(t));
              return t && !t.name && (t.name = 'question'), t;
            }),
            (t.prototype.onSurveyLoad = function () {
              e.prototype.onSurveyLoad.call(this),
                this.contentQuestion &&
                  this.isEmpty() &&
                  !this.contentQuestion.isEmpty() &&
                  (this.value = this.contentQuestion.value);
            }),
            (t.prototype.runCondition = function (t, n) {
              e.prototype.runCondition.call(this, t, n),
                this.contentQuestion && this.contentQuestion.runCondition(t, n);
            }),
            (t.prototype.convertDataName = function (t) {
              if (!this.contentQuestion) return e.prototype.convertDataName.call(this, t);
              var n = t.replace(this.contentQuestion.getValueName(), this.getValueName());
              return 0 == n.indexOf(this.getValueName())
                ? n
                : e.prototype.convertDataName.call(this, t);
            }),
            (t.prototype.convertDataValue = function (t, n) {
              return this.convertDataName(t) == e.prototype.convertDataName.call(this, t)
                ? this.contentQuestion.value
                : n;
            }),
            (t.prototype.setQuestionValue = function (t, n) {
              void 0 === n && (n = !0),
                e.prototype.setQuestionValue.call(this, t, n),
                this.contentQuestion &&
                  !s.a.isTwoValueEquals(this.contentQuestion.value, t) &&
                  (this.contentQuestion.value = t);
            }),
            (t.prototype.onSurveyValueChanged = function (t) {
              e.prototype.onSurveyValueChanged.call(this, t),
                this.contentQuestion && this.contentQuestion.onSurveyValueChanged(t);
            }),
            (t.prototype.initElement = function (t) {
              var n = this;
              e.prototype.initElement.call(this, t),
                t &&
                  ((t.parent = this),
                  (t.afterRenderQuestionCallback = function (e, t) {
                    n.customQuestion && n.customQuestion.onAfterRenderContentElement(n, e, t);
                  }));
            }),
            (t.prototype.updateElementCssCore = function (t) {
              this.contentQuestion && (t = this.contentQuestion.cssClasses),
                e.prototype.updateElementCssCore.call(this, t);
            }),
            t
          );
        })(p),
        h = (function (e) {
          function t(t, n) {
            var r = e.call(this, n) || this;
            return (r.composite = t), (r.variableName = n), r;
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'survey', {
              get: function () {
                return this.composite.survey;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'panel', {
              get: function () {
                return this.composite.contentPanel;
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(l.b),
        f = (function (e) {
          function t(n, r) {
            var i = e.call(this, n, r) || this;
            return (
              (i.name = n),
              (i.customQuestion = r),
              (i.settingNewValue = !1),
              (i.textProcessing = new h(i, t.ItemVariableName)),
              i
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.createWrapper = function () {
              this.panelWrapper = this.createPanel();
            }),
            (t.prototype.getTemplate = function () {
              return 'composite';
            }),
            (t.prototype.getCssType = function () {
              return 'composite';
            }),
            (t.prototype.getElement = function () {
              return this.contentPanel;
            }),
            Object.defineProperty(t.prototype, 'contentPanel', {
              get: function () {
                return this.panelWrapper;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.hasErrors = function (t, n) {
              void 0 === t && (t = !0), void 0 === n && (n = null);
              var r = e.prototype.hasErrors.call(this, t, n);
              return this.contentPanel ? this.contentPanel.hasErrors(t, !1, n) || r : r;
            }),
            (t.prototype.updateElementCss = function () {
              e.prototype.updateElementCss.call(this),
                this.contentPanel && this.contentPanel.updateElementCss();
            }),
            (t.prototype.getTextProcessor = function () {
              return this.textProcessing;
            }),
            (t.prototype.clearValueIfInvisible = function () {
              e.prototype.clearValueIfInvisible.call(this);
              for (var t = this.contentPanel.questions, n = 0; n < t.length; n++)
                t[n].clearValueIfInvisible();
            }),
            (t.prototype.onAnyValueChanged = function (t) {
              e.prototype.onAnyValueChanged.call(this, t);
              for (var n = this.contentPanel.questions, r = 0; r < n.length; r++)
                n[r].onAnyValueChanged(t);
            }),
            (t.prototype.createPanel = function () {
              var e = o.b.createClass('panel');
              (e.showQuestionNumbers = 'off'), (e.renderWidth = '100%');
              var t = this.customQuestion.json;
              return (
                t.elementsJSON && e.fromJSON({ elements: t.elementsJSON }),
                t.createElements && t.createElements(e, this),
                this.initElement(e),
                (e.readOnly = this.isReadOnly),
                this.setAfterRenderCallbacks(e),
                e
              );
            }),
            (t.prototype.onReadOnlyChanged = function () {
              this.contentPanel && (this.contentPanel.readOnly = this.isReadOnly),
                e.prototype.onReadOnlyChanged.call(this);
            }),
            (t.prototype.onSurveyLoad = function () {
              this.contentPanel &&
                ((this.contentPanel.readOnly = this.isReadOnly),
                this.setIsContentElement(this.contentPanel)),
                e.prototype.onSurveyLoad.call(this);
            }),
            (t.prototype.setIsContentElement = function (e) {
              e.isContentElement = !0;
              for (var t = e.elements, n = 0; n < t.length; n++) {
                var r = t[n];
                r.isPanel ? this.setIsContentElement(r) : (r.isContentElement = !0);
              }
            }),
            (t.prototype.setVisibleIndex = function (t) {
              var n = e.prototype.setVisibleIndex.call(this, t);
              return (
                this.isVisible && this.contentPanel && (n += this.contentPanel.setVisibleIndex(t)),
                n
              );
            }),
            (t.prototype.runCondition = function (n, r) {
              if ((e.prototype.runCondition.call(this, n, r), this.contentPanel)) {
                var i = n[t.ItemVariableName];
                (n[t.ItemVariableName] = this.contentPanel.getValue()),
                  this.contentPanel.runCondition(n, r),
                  delete n[t.ItemVariableName],
                  i && (n[t.ItemVariableName] = i);
              }
            }),
            (t.prototype.getValue = function (e) {
              var t = this.value;
              return t ? t[e] : null;
            }),
            (t.prototype.setValue = function (t, n, r, i) {
              if (
                !this.settingNewValue &&
                (e.prototype.setValue.call(this, t, n, r, i), this.contentPanel)
              ) {
                var o = this.contentPanel.getQuestionByName(t);
                o &&
                  !s.a.isTwoValueEquals(n, o.value) &&
                  ((this.settingNewValue = !0), (o.value = n), (this.settingNewValue = !1));
              }
            }),
            (t.prototype.addConditionObjectsByContext = function (e, t) {
              if (this.contentPanel)
                for (
                  var n = this.contentPanel.questions, r = this.name, i = this.title, o = 0;
                  o < n.length;
                  o++
                )
                  e.push({ name: r + '.' + n[o].name, text: i + '.' + n[o].title, question: n[o] });
            }),
            (t.prototype.convertDataValue = function (e, t) {
              var n = this.value;
              return (
                n || (n = {}),
                s.a.isValueEmpty(t) && !this.isEditingSurveyElement(n) ? delete n[e] : (n[e] = t),
                n
              );
            }),
            (t.prototype.setQuestionValue = function (t, n) {
              void 0 === n && (n = !0),
                e.prototype.setQuestionValue.call(this, t, n),
                (this.settingNewValue = !0);
              for (var r = this.contentPanel.questions, i = 0; i < r.length; i++) {
                var o = r[i].getValueName();
                r[i].value = t ? t[o] : void 0;
              }
              this.settingNewValue = !1;
            }),
            (t.prototype.setAfterRenderCallbacks = function (e) {
              var t = this;
              if (e && this.customQuestion)
                for (var n = e.questions, r = 0; r < n.length; r++)
                  n[r].afterRenderQuestionCallback = function (e, n) {
                    t.customQuestion.onAfterRenderContentElement(t, e, n);
                  };
            }),
            (t.ItemVariableName = 'composite'),
            t
          );
        })(p);
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(9);
      n.d(t, 'a', function () {
        return a;
      });
      var a = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          return (n.name = t), n;
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getType = function () {
            return 'empty';
          }),
          t
        );
      })(o.a);
      i.b.addClass(
        'empty',
        [],
        function () {
          return new a('');
        },
        'question'
      );
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(9),
        o = n(1),
        a = n(3),
        s = n(6),
        l = n(12),
        u = n(2);
      n.d(t, 'a', function () {
        return c;
      });
      var c = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          return (
            (n.name = t),
            (n.isUploading = !1),
            (n.onStateChanged = new s.c()),
            (n.previewValue = []),
            (n.currentState = 'empty'),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getType = function () {
            return 'file';
          }),
          (t.prototype.clearOnDeletingContainer = function () {
            this.survey.clearFiles(this, this.name, this.value, null, function () {});
          }),
          Object.defineProperty(t.prototype, 'showPreview', {
            get: function () {
              return this.getPropertyValue('showPreview');
            },
            set: function (e) {
              this.setPropertyValue('showPreview', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'allowMultiple', {
            get: function () {
              return this.getPropertyValue('allowMultiple', !1);
            },
            set: function (e) {
              this.setPropertyValue('allowMultiple', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'imageHeight', {
            get: function () {
              return this.getPropertyValue('imageHeight');
            },
            set: function (e) {
              this.setPropertyValue('imageHeight', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'imageWidth', {
            get: function () {
              return this.getPropertyValue('imageWidth');
            },
            set: function (e) {
              this.setPropertyValue('imageWidth', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'acceptedTypes', {
            get: function () {
              return this.getPropertyValue('acceptedTypes');
            },
            set: function (e) {
              this.setPropertyValue('acceptedTypes', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'storeDataAsText', {
            get: function () {
              return this.getPropertyValue('storeDataAsText');
            },
            set: function (e) {
              this.setPropertyValue('storeDataAsText', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'waitForUpload', {
            get: function () {
              return this.getPropertyValue('waitForUpload', !1);
            },
            set: function (e) {
              this.setPropertyValue('waitForUpload', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'allowImagesPreview', {
            get: function () {
              return this.getPropertyValue('allowImagesPreview');
            },
            set: function (e) {
              this.setPropertyValue('allowImagesPreview', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'maxSize', {
            get: function () {
              return this.getPropertyValue('maxSize', 0);
            },
            set: function (e) {
              this.setPropertyValue('maxSize', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'needConfirmRemoveFile', {
            get: function () {
              return this.getPropertyValue('needConfirmRemoveFile');
            },
            set: function (e) {
              this.setPropertyValue('needConfirmRemoveFile', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getConfirmRemoveMessage = function (e) {
            return u.a.getString('confirmRemoveFile').format(e);
          }),
          Object.defineProperty(t.prototype, 'confirmRemoveAllMessage', {
            get: function () {
              return u.a.getString('confirmRemoveAllFiles');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'noFileChosenCaption', {
            get: function () {
              return u.a.getString('noFileChosen');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'chooseButtonCaption', {
            get: function () {
              return u.a.getString('chooseFileCaption');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'cleanButtonCaption', {
            get: function () {
              return u.a.getString('cleanCaption');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'removeFileCaption', {
            get: function () {
              return u.a.getString('removeFileCaption');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'inputTitle', {
            get: function () {
              return this.isUploading
                ? u.a.getString('loadingFile')
                : this.isEmpty()
                ? u.a.getString('chooseFile')
                : ' ';
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.clear = function (e) {
            var t = this;
            this.survey.clearFiles(this, this.name, this.value, null, function (n, r) {
              'success' === n && ((t.value = void 0), (t.errors = []), !!e && e());
            });
          }),
          (t.prototype.removeFile = function (e) {
            var t = this;
            this.survey.clearFiles(this, this.name, this.value, e.name, function (n, r) {
              if ('success' === n) {
                var i = t.value;
                Array.isArray(i)
                  ? (t.value = i.filter(function (t) {
                      return t.name !== e.name;
                    }))
                  : (t.value = void 0);
              }
            });
          }),
          (t.prototype.loadFiles = function (e) {
            var t = this;
            if (this.survey && ((this.errors = []), this.allFilesOk(e))) {
              this.stateChanged('loading');
              var n = function () {
                var n = [];
                t.storeDataAsText
                  ? e.forEach(function (r) {
                      var i = new FileReader();
                      (i.onload = function (o) {
                        (n = n.concat([{ name: r.name, type: r.type, content: i.result }])),
                          n.length === e.length && (t.value = (t.value || []).concat(n));
                      }),
                        i.readAsDataURL(r);
                    })
                  : t.survey.uploadFiles(t, t.name, e, function (e, n) {
                      'error' === e && t.stateChanged('error'),
                        'success' === e &&
                          (t.value = (t.value || []).concat(
                            n.map(function (e) {
                              return { name: e.file.name, type: e.file.type, content: e.content };
                            })
                          ));
                    });
              };
              this.allowMultiple ? n() : this.clear(n);
            }
          }),
          (t.prototype.canPreviewImage = function (e) {
            return this.allowImagesPreview && !!e && this.isFileImage(e);
          }),
          (t.prototype.setQuestionValue = function (t, n) {
            var r = this;
            void 0 === n && (n = !0),
              e.prototype.setQuestionValue.call(this, t, n),
              (this.previewValue = []);
            var i =
              (!Array.isArray(t) && t) || (Array.isArray(t) && t.length > 0)
                ? this.showPreview
                  ? 'loading'
                  : 'loaded'
                : 'empty';
            if ((this.stateChanged(i), this.showPreview && t)) {
              var o = Array.isArray(t) ? t : t ? [t] : [];
              this.storeDataAsText
                ? (o.forEach(function (e) {
                    var t = e.content || e;
                    r.previewValue = r.previewValue.concat([
                      { name: e.name, type: e.type, content: t },
                    ]);
                  }),
                  'loading' === i && this.stateChanged('loaded'))
                : o.forEach(function (e) {
                    e.content;
                    r.survey.downloadFile(r.name, e, function (t, n) {
                      'success' === t
                        ? ((r.previewValue = r.previewValue.concat([
                            { content: n, name: e.name, type: e.type },
                          ])),
                          r.previewValue.length === o.length && r.stateChanged('loaded'))
                        : r.stateChanged('error');
                    });
                  });
            }
          }),
          (t.prototype.onCheckForErrors = function (t, n) {
            e.prototype.onCheckForErrors.call(this, t, n),
              this.isUploading &&
                this.waitForUpload &&
                t.push(new l.k(u.a.getString('uploadingFile'), this));
          }),
          (t.prototype.stateChanged = function (e) {
            'loading' === e && (this.isUploading = !0),
              'loaded' === e && (this.isUploading = !1),
              'error' === e && (this.isUploading = !1),
              (this.currentState = e),
              this.onStateChanged.fire(this, { state: e });
          }),
          (t.prototype.allFilesOk = function (e) {
            var t = this,
              n = this.errors ? this.errors.length : 0;
            return (
              (e || []).forEach(function (e) {
                t.maxSize > 0 && e.size > t.maxSize && t.errors.push(new l.l(t.maxSize, t));
              }),
              n === this.errors.length
            );
          }),
          (t.prototype.isFileImage = function (e) {
            if (!e) return !1;
            var t = 'data:image',
              n = e.content && e.content.substr(0, t.length);
            return (
              (n = n && n.toLowerCase()) === t ||
              (!!e.type && 0 === e.type.toLowerCase().indexOf('image/'))
            );
          }),
          (t.prototype.getPlainData = function (t) {
            void 0 === t && (t = { includeEmpty: !0 });
            var n = e.prototype.getPlainData.call(this, t);
            if (n && !this.isEmpty()) {
              n.isNode = !1;
              var r = Array.isArray(this.value) ? this.value : [this.value];
              n.data = r.map(function (e, t) {
                return {
                  name: t,
                  title: 'File',
                  value: (e.content && e.content) || e,
                  displayValue: (e.name && e.name) || e,
                  getString: function (e) {
                    return 'object' == typeof e ? JSON.stringify(e) : e;
                  },
                  isNode: !1,
                };
              });
            }
            return n;
          }),
          (t.prototype.supportComment = function () {
            return !0;
          }),
          t
        );
      })(i.a);
      o.b.addClass(
        'file',
        [
          { name: 'hasComment:switch', layout: 'row' },
          {
            name: 'commentText',
            dependsOn: 'hasComment',
            visibleIf: function (e) {
              return e.hasComment;
            },
            serializationProperty: 'locCommentText',
            layout: 'row',
          },
          { name: 'showPreview:boolean', default: !0 },
          'allowMultiple:boolean',
          { name: 'allowImagesPreview:boolean', default: !0 },
          'imageHeight',
          'imageWidth',
          'acceptedTypes',
          { name: 'storeDataAsText:boolean', default: !0 },
          { name: 'waitForUpload:boolean', default: !1 },
          'maxSize:number',
          { name: 'defaultValue', visible: !1 },
          { name: 'correctAnswer', visible: !1 },
          { name: 'validators', visible: !1 },
          { name: 'needConfirmRemoveFile:boolean', visible: !0, default: !1 },
        ],
        function () {
          return new c('');
        },
        'question'
      ),
        a.b.Instance.registerQuestion('file', function (e) {
          return new c(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(34),
        o = n(1),
        a = n(3);
      n.d(t, 'a', function () {
        return s;
      });
      var s = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          n.name = t;
          var r = n.createLocalizableString('html', n),
            i = n;
          return (
            (r.onGetTextCallback = function (e) {
              return i.survey ? i.survey.processHtml(e) : e;
            }),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getType = function () {
            return 'html';
          }),
          Object.defineProperty(t.prototype, 'isCompositeQuestion', {
            get: function () {
              return !0;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'html', {
            get: function () {
              return this.getLocalizableStringText('html', '');
            },
            set: function (e) {
              this.setLocalizableStringText('html', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locHtml', {
            get: function () {
              return this.getLocalizableString('html');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'processedHtml', {
            get: function () {
              return this.survey ? this.survey.processHtml(this.html) : this.html;
            },
            enumerable: !0,
            configurable: !0,
          }),
          t
        );
      })(i.a);
      o.b.addClass(
        'html',
        [{ name: 'html:html', serializationProperty: 'locHtml' }],
        function () {
          return new s('');
        },
        'nonvalue'
      ),
        a.b.Instance.registerQuestion('html', function (e) {
          return new s(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(34),
        o = n(1),
        a = n(3);
      n.d(t, 'a', function () {
        return s;
      });
      var s = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          return (
            (n.name = t),
            n.createLocalizableString('imageLink', n, !1),
            n.createLocalizableString('text', n, !1),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getType = function () {
            return 'image';
          }),
          Object.defineProperty(t.prototype, 'isCompositeQuestion', {
            get: function () {
              return !1;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'imageLink', {
            get: function () {
              return this.getLocalizableStringText('imageLink');
            },
            set: function (e) {
              this.setLocalizableStringText('imageLink', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locImageLink', {
            get: function () {
              return this.getLocalizableString('imageLink');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'text', {
            get: function () {
              return this.getLocalizableStringText('text');
            },
            set: function (e) {
              this.setLocalizableStringText('text', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locText', {
            get: function () {
              return this.getLocalizableString('text');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'imageHeight', {
            get: function () {
              return this.getPropertyValue('imageHeight');
            },
            set: function (e) {
              this.setPropertyValue('imageHeight', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'imageWidth', {
            get: function () {
              return this.getPropertyValue('imageWidth');
            },
            set: function (e) {
              this.setPropertyValue('imageWidth', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'imageFit', {
            get: function () {
              return this.getPropertyValue('imageFit');
            },
            set: function (e) {
              this.setPropertyValue('imageFit', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'contentMode', {
            get: function () {
              return this.getPropertyValue('contentMode');
            },
            set: function (e) {
              this.setPropertyValue('contentMode', e), 'video' === e && (this.showLabel = !0);
            },
            enumerable: !0,
            configurable: !0,
          }),
          t
        );
      })(i.a);
      o.b.addClass(
        'image',
        [
          { name: 'imageLink', serializationProperty: 'locImageLink' },
          { name: 'text', serializationProperty: 'locText' },
          { name: 'contentMode', default: 'image', choices: ['image', 'video'] },
          { name: 'imageFit', default: 'contain', choices: ['none', 'contain', 'cover', 'fill'] },
          { name: 'imageHeight:number', default: 150, minValue: 0 },
          { name: 'imageWidth:number', default: 200, minValue: 0 },
        ],
        function () {
          return new s('');
        },
        'nonvalue'
      ),
        a.b.Instance.registerQuestion('image', function (e) {
          return new s(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(3),
        a = n(23),
        s = n(11),
        l = n(4);
      n.d(t, 'a', function () {
        return c;
      });
      var u = (function (e) {
          function t(t, n, r) {
            void 0 === n && (n = null), void 0 === r && (r = 'imageitemvalue');
            var i = e.call(this, t, n, r) || this;
            return (i.typeName = r), i.createLocalizableString('imageLink', i, !1), i;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return this.typeName ? this.typeName : 'itemvalue';
            }),
            Object.defineProperty(t.prototype, 'imageLink', {
              get: function () {
                return this.getLocalizableStringText('imageLink');
              },
              set: function (e) {
                this.setLocalizableStringText('imageLink', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locImageLink', {
              get: function () {
                return this.getLocalizableString('imageLink');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getLocale = function () {
              return this.locOwner ? this.locOwner.getLocale() : '';
            }),
            (t.prototype.getMarkdownHtml = function (e, t) {
              return this.locOwner ? this.locOwner.getMarkdownHtml(e, t) : e;
            }),
            (t.prototype.getProcessedText = function (e) {
              return this.locOwner ? this.locOwner.getProcessedText(e) : e;
            }),
            t
          );
        })(s.a),
        c = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), (n.colCount = 0), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'imagepicker';
            }),
            (t.prototype.supportGoNextPageAutomatic = function () {
              return !0;
            }),
            Object.defineProperty(t.prototype, 'hasSingleInput', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getItemValueType = function () {
              return 'imageitemvalue';
            }),
            Object.defineProperty(t.prototype, 'isCompositeQuestion', {
              get: function () {
                return !0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.supportOther = function () {
              return !1;
            }),
            Object.defineProperty(t.prototype, 'multiSelect', {
              get: function () {
                return this.getPropertyValue('multiSelect', !1);
              },
              set: function (e) {
                this.setPropertyValue('multiSelect', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.isItemSelected = function (e) {
              var t = this.value;
              if (l.a.isValueEmpty(t)) return !1;
              if (!this.multiSelect) return l.a.isTwoValueEquals(t, e.value);
              if (!Array.isArray(t)) return !1;
              for (var n = 0; n < t.length; n++) if (l.a.isTwoValueEquals(t[n], e.value)) return !0;
              return !1;
            }),
            (t.prototype.clearIncorrectValues = function () {
              if (this.multiSelect) {
                var t = this.value;
                if (!t) return;
                if (!Array.isArray(t) || 0 == t.length) return void this.clearValue();
                for (var n = [], r = 0; r < t.length; r++)
                  this.hasUnknownValue(t[r], !0) || n.push(t[r]);
                if (n.length == t.length) return;
                0 == n.length ? this.clearValue() : (this.value = n);
              } else e.prototype.clearIncorrectValues.call(this);
            }),
            Object.defineProperty(t.prototype, 'showLabel', {
              get: function () {
                return this.getPropertyValue('showLabel', !1);
              },
              set: function (e) {
                this.setPropertyValue('showLabel', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.endLoadingFromJson = function () {
              e.prototype.endLoadingFromJson.call(this),
                !this.isDesignMode &&
                  this.multiSelect &&
                  (this.createNewArray('renderedValue'), this.createNewArray('value'));
            }),
            (t.prototype.getValueCore = function () {
              var t = e.prototype.getValueCore.call(this);
              return void 0 !== t ? t : this.multiSelect ? [] : t;
            }),
            (t.prototype.convertValToArrayForMultSelect = function (e) {
              return this.multiSelect ? (l.a.isValueEmpty(e) || Array.isArray(e) ? e : [e]) : e;
            }),
            (t.prototype.renderedValueFromDataCore = function (e) {
              return this.convertValToArrayForMultSelect(e);
            }),
            (t.prototype.rendredValueToDataCore = function (e) {
              return this.convertValToArrayForMultSelect(e);
            }),
            Object.defineProperty(t.prototype, 'imageHeight', {
              get: function () {
                return this.getPropertyValue('imageHeight');
              },
              set: function (e) {
                this.setPropertyValue('imageHeight', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'imageWidth', {
              get: function () {
                return this.getPropertyValue('imageWidth');
              },
              set: function (e) {
                this.setPropertyValue('imageWidth', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'imageFit', {
              get: function () {
                return this.getPropertyValue('imageFit');
              },
              set: function (e) {
                this.setPropertyValue('imageFit', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'contentMode', {
              get: function () {
                return this.getPropertyValue('contentMode');
              },
              set: function (e) {
                this.setPropertyValue('contentMode', e), 'video' === e && (this.showLabel = !0);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getItemClass = function (e) {
              var t =
                  this.cssClasses.item +
                  (0 === this.colCount
                    ? ' ' + this.cssClasses.itemInline
                    : ' sv-q-col-' + this.colCount),
                n = this.isItemSelected(e),
                r = this.isReadOnly || !e.isEnabled,
                i = !n && !r;
              return (
                n && this.cssClasses.itemChecked && (t += ' ' + this.cssClasses.itemChecked),
                r && this.cssClasses.itemDisabled && (t += ' ' + this.cssClasses.itemDisabled),
                i && this.cssClasses.itemHover && (t += ' ' + this.cssClasses.itemHover),
                t
              );
            }),
            (t.prototype.convertDefaultValue = function (e) {
              return e;
            }),
            t
          );
        })(a.a);
      i.b.addClass(
        'imageitemvalue',
        [],
        function (e) {
          return new u(e);
        },
        'itemvalue'
      ),
        i.b.addProperty('imageitemvalue', {
          name: 'imageLink',
          serializationProperty: 'locImageLink',
        }),
        i.b.addClass(
          'imagepicker',
          [
            { name: 'hasOther', visible: !1 },
            { name: 'otherText', visible: !1 },
            { name: 'optionsCaption', visible: !1 },
            { name: 'otherErrorText', visible: !1 },
            { name: 'storeOthersAsComment', visible: !1 },
            { name: 'contentMode', default: 'image', choices: ['image', 'video'] },
            { name: 'imageFit', default: 'contain', choices: ['none', 'contain', 'cover', 'fill'] },
            { name: 'imageHeight:number', default: 150, minValue: 0 },
            { name: 'imageWidth:number', default: 200, minValue: 0 },
          ],
          function () {
            return new c('');
          },
          'checkboxbase'
        ),
        i.b.addProperty('imagepicker', { name: 'showLabel:boolean', default: !1 }),
        i.b.addProperty('imagepicker', {
          name: 'colCount:number',
          default: 0,
          choices: [0, 1, 2, 3, 4, 5],
        }),
        i.b.addProperty('imagepicker', { name: 'multiSelect:boolean', default: !1 }),
        i.b.addProperty('imagepicker', { name: 'choices:imageitemvalue[]' }),
        o.b.Instance.registerQuestion('imagepicker', function (e) {
          return new c(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(4),
        o = n(11),
        a = n(50),
        s = n(1),
        l = n(6),
        u = n(2),
        c = n(12),
        p = n(3),
        d = n(13),
        h = n(31),
        f = n(7);
      n.d(t, 'b', function () {
        return m;
      }),
        n.d(t, 'c', function () {
          return g;
        }),
        n.d(t, 'a', function () {
          return y;
        });
      var m = (function (e) {
          function t(t, n, r, i) {
            var o = e.call(this) || this;
            return (
              (o.fullName = n),
              (o.item = t),
              (o.data = r),
              (o.value = i),
              (o.cellClick = function (e) {
                o.value = e.value;
              }),
              o.registerFunctionOnPropertyValueChanged('value', function () {
                o.data && o.data.onMatrixRowChanged(o);
              }),
              o
            );
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'name', {
              get: function () {
                return this.item.value;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'text', {
              get: function () {
                return this.item.text;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locText', {
              get: function () {
                return this.item.locText;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'value', {
              get: function () {
                return this.getPropertyValue('value');
              },
              set: function (e) {
                (e = this.data.getCorrectedRowValue(e)), this.setPropertyValue('value', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'rowClasses', {
              get: function () {
                var e = this.data.cssClasses,
                  t = e.row ? e.row : '',
                  n = e.rowError ? e.rowError : '',
                  r = !!this.data.getErrorByType('requiredinallrowserror'),
                  o = t;
                return n && r && i.a.isValueEmpty(this.value) && (o && (o += ' '), (o += n)), o;
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(l.b),
        g = (function () {
          function e(e) {
            (this.cellsOwner = e), (this.values = {});
          }
          return (
            Object.defineProperty(e.prototype, 'isEmpty', {
              get: function () {
                return 0 == Object.keys(this.values).length;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.setCellText = function (e, t, n) {
              if (
                ((e = this.getCellRowColumnValue(e, this.rows)),
                (t = this.getCellRowColumnValue(t, this.columns)),
                e && t)
              )
                if (n)
                  this.values[e] || (this.values[e] = {}),
                    this.values[e][t] || (this.values[e][t] = this.createString()),
                    (this.values[e][t].text = n);
                else if (this.values[e] && this.values[e][t]) {
                  var r = this.values[e][t];
                  (r.text = ''),
                    r.isEmpty &&
                      (delete this.values[e][t],
                      0 == Object.keys(this.values[e]).length && delete this.values[e]);
                }
            }),
            (e.prototype.setDefaultCellText = function (e, t) {
              this.setCellText(f.a.matrixDefaultRowName, e, t);
            }),
            (e.prototype.getCellLocText = function (e, t) {
              return (
                (e = this.getCellRowColumnValue(e, this.rows)),
                (t = this.getCellRowColumnValue(t, this.columns)),
                e && t && this.values[e] && this.values[e][t] ? this.values[e][t] : null
              );
            }),
            (e.prototype.getDefaultCellLocText = function (e, t) {
              return this.getCellLocText(f.a.matrixDefaultRowName, e);
            }),
            (e.prototype.getCellDisplayLocText = function (e, t) {
              var n = this.getCellLocText(e, t);
              return n && !n.isEmpty
                ? n
                : (n = this.getCellLocText(f.a.matrixDefaultRowName, t)) && !n.isEmpty
                ? n
                : ('number' == typeof t &&
                    (t = t >= 0 && t < this.columns.length ? this.columns[t] : null),
                  t && t.locText ? t.locText : null);
            }),
            (e.prototype.getCellText = function (e, t) {
              var n = this.getCellLocText(e, t);
              return n ? n.calculatedText : null;
            }),
            (e.prototype.getDefaultCellText = function (e) {
              var t = this.getCellLocText(f.a.matrixDefaultRowName, e);
              return t ? t.calculatedText : null;
            }),
            (e.prototype.getCellDisplayText = function (e, t) {
              var n = this.getCellDisplayLocText(e, t);
              return n ? n.calculatedText : null;
            }),
            Object.defineProperty(e.prototype, 'rows', {
              get: function () {
                return this.cellsOwner ? this.cellsOwner.getRows() : [];
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'columns', {
              get: function () {
                return this.cellsOwner ? this.cellsOwner.getColumns() : [];
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.getCellRowColumnValue = function (e, t) {
              if (null === e || void 0 === e) return null;
              if ('number' == typeof e) {
                if (e < 0 || e >= t.length) return null;
                e = t[e].value;
              }
              return e.value ? e.value : e;
            }),
            (e.prototype.getJson = function () {
              if (this.isEmpty) return null;
              var e = {};
              for (var t in this.values) {
                var n = {},
                  r = this.values[t];
                for (var i in r) n[i] = r[i].getJson();
                e[t] = n;
              }
              return e;
            }),
            (e.prototype.setJson = function (e) {
              if (((this.values = {}), e))
                for (var t in e)
                  if ('pos' != t) {
                    var n = e[t];
                    this.values[t] = {};
                    for (var r in n)
                      if ('pos' != r) {
                        var i = this.createString();
                        i.setJson(n[r]), (this.values[t][r] = i);
                      }
                  }
            }),
            (e.prototype.createString = function () {
              return new d.a(this.cellsOwner, !0);
            }),
            e
          );
        })(),
        y = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            (n.name = t),
              (n.isRowChanging = !1),
              (n.emptyLocalizableString = new d.a(n)),
              (n.cellsValue = new g(n));
            var r = n;
            return (
              n.registerFunctionOnPropertyValueChanged('columns', function () {
                r.onColumnsChanged();
              }),
              n.registerFunctionOnPropertyValueChanged('rows', function () {
                r.filterItems() || r.onRowsChanged();
              }),
              n.registerFunctionOnPropertyValueChanged('hideIfRowsEmpty', function () {
                r.updateVisibilityBasedOnRows();
              }),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'matrix';
            }),
            Object.defineProperty(t.prototype, 'hasSingleInput', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isAllRowRequired', {
              get: function () {
                return this.getPropertyValue('isAllRowRequired', !1);
              },
              set: function (e) {
                this.setPropertyValue('isAllRowRequired', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'hasRows', {
              get: function () {
                return this.rows.length > 0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'rowsOrder', {
              get: function () {
                return this.getPropertyValue('rowsOrder');
              },
              set: function (e) {
                (e = e.toLowerCase()) != this.rowsOrder &&
                  (this.setPropertyValue('rowsOrder', e), this.onRowsChanged());
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'hideIfRowsEmpty', {
              get: function () {
                return this.getPropertyValue('hideIfRowsEmpty', !1);
              },
              set: function (e) {
                this.setPropertyValue('hideIfRowsEmpty', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getRows = function () {
              return this.rows;
            }),
            (t.prototype.getColumns = function () {
              return this.visibleColumns;
            }),
            (t.prototype.getItemClass = function (e, t) {
              var n = e.value == t.value,
                r = this.isReadOnly,
                i = !n && !r,
                o = this.hasCellText
                  ? this.cssClasses.cellTextDisabled
                  : this.cssClasses.itemDisabled,
                a = this.hasCellText
                  ? this.cssClasses.cellTextSelected
                  : this.cssClasses.itemChecked,
                s = this.hasCellText ? '' : this.cssClasses.itemHover,
                l = this.hasCellText ? this.cssClasses.cellText : this.cssClasses.label,
                u = this.hasCellText && this.cssClasses.cell ? this.cssClasses.cell + ' ' : '';
              return (u += l + (n ? ' ' + a : '') + (r ? ' ' + o : '') + (i ? ' ' + s : ''));
            }),
            (t.prototype.getQuizQuestionCount = function () {
              for (var e = 0, t = 0; t < this.rows.length; t++)
                this.isValueEmpty(this.correctAnswer[this.rows[t].value]) || e++;
              return e;
            }),
            (t.prototype.getCorrectAnswerCount = function () {
              for (var e = 0, t = this.value, n = 0; n < this.rows.length; n++) {
                var r = this.rows[n].value;
                !i.a.isValueEmpty(t[r]) && i.a.isTwoValueEquals(this.correctAnswer[r], t[r]) && e++;
              }
              return e;
            }),
            (t.prototype.getVisibleRows = function () {
              var e = new Array(),
                t = this.value;
              t || (t = {});
              for (
                var n = this.filteredRows ? this.filteredRows : this.rows, r = 0;
                r < n.length;
                r++
              ) {
                var i = n[r];
                this.isValueEmpty(i.value) ||
                  e.push(
                    this.createMatrixRow(
                      i,
                      this.id + '_' + i.value.toString().replace(/\s/g, '_'),
                      t[i.value]
                    )
                  );
              }
              return (
                0 != e.length ||
                  this.filteredRows ||
                  e.push(this.createMatrixRow(new o.a(null), this.name.replace(/\s/g, '_'), t)),
                (this.generatedVisibleRows = e),
                e
              );
            }),
            (t.prototype.sortVisibleRows = function (e) {
              return 'random' === this.rowsOrder.toLowerCase() ? i.a.randomizeArray(e) : e;
            }),
            (t.prototype.endLoadingFromJson = function () {
              e.prototype.endLoadingFromJson.call(this),
                (this.rows = this.sortVisibleRows(this.rows)),
                this.updateVisibilityBasedOnRows();
            }),
            (t.prototype.processRowsOnSet = function (e) {
              return this.sortVisibleRows(e);
            }),
            Object.defineProperty(t.prototype, 'visibleRows', {
              get: function () {
                return this.getVisibleRows();
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'cells', {
              get: function () {
                return this.cellsValue;
              },
              set: function (e) {
                this.cells.setJson(e && e.getJson ? e.getJson() : null);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'hasCellText', {
              get: function () {
                return !this.cells.isEmpty;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setCellText = function (e, t, n) {
              this.cells.setCellText(e, t, n);
            }),
            (t.prototype.getCellText = function (e, t) {
              return this.cells.getCellText(e, t);
            }),
            (t.prototype.setDefaultCellText = function (e, t) {
              this.cells.setDefaultCellText(e, t);
            }),
            (t.prototype.getDefaultCellText = function (e) {
              return this.cells.getDefaultCellText(e);
            }),
            (t.prototype.getCellDisplayText = function (e, t) {
              return this.cells.getCellDisplayText(e, t);
            }),
            (t.prototype.getCellDisplayLocText = function (e, t) {
              var n = this.cells.getCellDisplayLocText(e, t);
              return n || this.emptyLocalizableString;
            }),
            (t.prototype.supportGoNextPageAutomatic = function () {
              return this.hasValuesInAllRows();
            }),
            (t.prototype.onCheckForErrors = function (t, n) {
              e.prototype.onCheckForErrors.call(this, t, n),
                (!n || this.errors.length > 0) &&
                  this.hasErrorInRows() &&
                  t.push(new c.j(null, this));
            }),
            (t.prototype.hasErrorInRows = function () {
              return !!this.isAllRowRequired && !this.hasValuesInAllRows();
            }),
            (t.prototype.hasValuesInAllRows = function () {
              var e = this.generatedVisibleRows;
              if ((e || (e = this.visibleRows), !e)) return !0;
              for (var t = 0; t < e.length; t++) if (i.a.isValueEmpty(e[t].value)) return !1;
              return !0;
            }),
            (t.prototype.getIsAnswered = function () {
              return e.prototype.getIsAnswered.call(this) && this.hasValuesInAllRows();
            }),
            (t.prototype.createMatrixRow = function (e, t, n) {
              var r = new m(e, t, this, n);
              return this.onMatrixRowCreated(r), r;
            }),
            (t.prototype.onMatrixRowCreated = function (e) {}),
            (t.prototype.setQuestionValue = function (t, n) {
              if (
                (void 0 === n && (n = !0),
                e.prototype.setQuestionValue.call(this, t, this.isRowChanging || n),
                this.generatedVisibleRows && 0 != this.generatedVisibleRows.length)
              ) {
                this.isRowChanging = !0;
                var r = this.value;
                if ((r || (r = {}), 0 == this.rows.length)) this.generatedVisibleRows[0].value = r;
                else
                  for (var o = 0; o < this.generatedVisibleRows.length; o++) {
                    var a = this.generatedVisibleRows[o],
                      s = r[a.name];
                    i.a.isValueEmpty(s) && (s = null), (this.generatedVisibleRows[o].value = s);
                  }
                this.updateIsAnswered(), (this.isRowChanging = !1);
              }
            }),
            (t.prototype.getDisplayValueCore = function (e, t) {
              var n = {};
              for (var r in t) {
                var i = e ? o.a.getTextOrHtmlByValue(this.rows, r) : r;
                i || (i = r);
                var a = o.a.getTextOrHtmlByValue(this.columns, t[r]);
                a || (a = t[r]), (n[i] = a);
              }
              return n;
            }),
            (t.prototype.getPlainData = function (t) {
              var n = this;
              void 0 === t && (t = { includeEmpty: !0 });
              var r = e.prototype.getPlainData.call(this, t);
              if (r) {
                var i = this.createValueCopy();
                (r.isNode = !0),
                  (r.data = Object.keys(i || {}).map(function (e) {
                    var r = n.rows.filter(function (t) {
                        return t.value === e;
                      })[0],
                      a = {
                        name: e,
                        title: r ? r.text : 'row',
                        value: i[e],
                        displayValue: o.a.getTextOrHtmlByValue(n.visibleColumns, i[e]),
                        getString: function (e) {
                          return 'object' == typeof e ? JSON.stringify(e) : e;
                        },
                        isNode: !1,
                      },
                      s = o.a.getItemByValue(n.visibleColumns, i[e]);
                    return (
                      s &&
                        (t.calculations || []).forEach(function (e) {
                          a[e.propertyName] = s[e.propertyName];
                        }),
                      a
                    );
                  }));
              }
              return r;
            }),
            (t.prototype.addConditionObjectsByContext = function (e, t) {
              for (var n = 0; n < this.rows.length; n++) {
                var r = this.rows[n];
                r.value &&
                  e.push({
                    name: this.getValueName() + '.' + r.value,
                    text: this.processedTitle + '.' + r.calculatedText,
                    question: this,
                  });
              }
            }),
            (t.prototype.getConditionJson = function (t, n) {
              if ((void 0 === t && (t = null), void 0 === n && (n = null), !n))
                return e.prototype.getConditionJson.call(this);
              var r = new h.a(n);
              r.choices = this.columns;
              var i = new s.c().toJsonObject(r);
              return (i.type = r.getType()), i;
            }),
            (t.prototype.clearValueIfInvisible = function () {
              e.prototype.clearValueIfInvisible.call(this),
                this.hasRows && this.clearInvisibleValuesInRows();
            }),
            (t.prototype.getFirstInputElementId = function () {
              var t = this.generatedVisibleRows;
              return (
                t || (t = this.visibleRows),
                t.length > 0 && this.visibleColumns.length > 0
                  ? this.inputId + '_' + t[0].name + '_0'
                  : e.prototype.getFirstInputElementId.call(this)
              );
            }),
            (t.prototype.onRowsChanged = function () {
              this.updateVisibilityBasedOnRows(), e.prototype.onRowsChanged.call(this);
            }),
            (t.prototype.updateVisibilityBasedOnRows = function () {
              this.hideIfRowsEmpty &&
                (this.visible =
                  this.rows.length > 0 && (!this.filteredRows || this.filteredRows.length > 0));
            }),
            (t.prototype.onMatrixRowChanged = function (e) {
              if (!this.isRowChanging) {
                if (((this.isRowChanging = !0), this.hasRows)) {
                  var t = this.value;
                  t || (t = {}), (t[e.name] = e.value), this.setNewValue(t);
                } else this.setNewValue(e.value);
                this.isRowChanging = !1;
              }
            }),
            (t.prototype.getCorrectedRowValue = function (e) {
              for (var t = 0; t < this.columns.length; t++)
                if (e === this.columns[t].value) return e;
              for (var t = 0; t < this.columns.length; t++)
                if (i.a.isTwoValueEquals(e, this.columns[t].value)) return this.columns[t].value;
              return e;
            }),
            t
          );
        })(a.a);
      s.b.addClass(
        'matrix',
        [
          {
            name: 'columns:itemvalue[]',
            baseValue: function () {
              return u.a.getString('matrix_column');
            },
          },
          {
            name: 'rows:itemvalue[]',
            baseValue: function () {
              return u.a.getString('matrix_row');
            },
          },
          { name: 'cells:cells', serializationProperty: 'cells' },
          { name: 'rowsOrder', default: 'initial', choices: ['initial', 'random'] },
          'isAllRowRequired:boolean',
          'hideIfRowsEmpty:boolean',
        ],
        function () {
          return new y('');
        },
        'matrixbase'
      ),
        p.b.Instance.registerQuestion('matrix', function (e) {
          var t = new y(e);
          return (t.rows = p.b.DefaultRows), (t.columns = p.b.DefaultColums), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(24),
        o = n(1),
        a = n(11),
        s = n(3);
      n.d(t, 'b', function () {
        return l;
      }),
        n.d(t, 'a', function () {
          return u;
        });
      var l = (function (e) {
          function t(t, n, r, i) {
            var o = e.call(this, r, i) || this;
            return (o.name = t), (o.item = n), o.buildCells(i), o;
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'rowName', {
              get: function () {
                return this.name;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'text', {
              get: function () {
                return this.item.text;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locText', {
              get: function () {
                return this.item.locText;
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(i.b),
        u = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            (n.name = t), n.createLocalizableString('totalText', n, !0);
            var r = n;
            return (
              n.registerFunctionOnPropertyValueChanged('rows', function () {
                r.clearGeneratedRows(), r.resetRenderedTable(), r.filterItems();
              }),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'matrixdropdown';
            }),
            Object.defineProperty(t.prototype, 'totalText', {
              get: function () {
                return this.getLocalizableStringText('totalText', '');
              },
              set: function (e) {
                this.setLocalizableStringText('totalText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locTotalText', {
              get: function () {
                return this.getLocalizableString('totalText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getFooterText = function () {
              return this.locTotalText;
            }),
            Object.defineProperty(t.prototype, 'rowTitleWidth', {
              get: function () {
                return this.getPropertyValue('rowTitleWidth', '');
              },
              set: function (e) {
                this.setPropertyValue('rowTitleWidth', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getRowTitleWidth = function () {
              return this.rowTitleWidth;
            }),
            (t.prototype.getDisplayValueCore = function (e, t) {
              var n = this.createValueCopy();
              if (!n) return n;
              for (var r = this.visibleRows, i = {}, o = 0; o < r.length; o++) {
                var s = this.rows[o].value,
                  l = n[s];
                if (l) {
                  if (e) {
                    var u = a.a.getTextOrHtmlByValue(this.rows, s);
                    u && (s = u);
                  }
                  i[s] = this.getRowDisplayValue(r[o], l);
                }
              }
              return n;
            }),
            (t.prototype.addConditionObjectsByContext = function (e, t) {
              for (var n = !!t && this.columns.indexOf(t) > -1, r = 0; r < this.rows.length; r++) {
                var i = this.rows[r];
                if (i.value)
                  for (
                    var o = this.getValueName() + '.' + i.value + '.',
                      a = this.processedTitle + '.' + i.calculatedText + '.',
                      s = 0;
                    s < this.columns.length;
                    s++
                  ) {
                    var l = this.columns[s];
                    e.push({ name: o + l.name, text: a + l.fullTitle, question: this });
                  }
              }
              if (n)
                for (var r = 0; r < this.columns.length; r++) {
                  var l = this.columns[r];
                  l != t &&
                    e.push({ name: 'row.' + l.name, text: 'row.' + l.fullTitle, question: this });
                }
            }),
            (t.prototype.clearIncorrectValues = function () {
              var t = this.value;
              if (t) {
                var n = null,
                  r = !1,
                  i = this.filteredRows ? this.filteredRows : this.rows;
                for (var o in t)
                  a.a.getItemByValue(i, o) ? (null == n && (n = {}), (n[o] = t[o])) : (r = !0);
                r && (this.value = n), e.prototype.clearIncorrectValues.call(this);
              }
            }),
            (t.prototype.clearValueIfInvisible = function () {
              e.prototype.clearValueIfInvisible.call(this), this.clearInvisibleValuesInRows();
            }),
            (t.prototype.generateRows = function () {
              var e = new Array(),
                t = this.filteredRows ? this.filteredRows : this.rows;
              if (!t || 0 === t.length) return e;
              var n = this.value;
              n || (n = {});
              for (var r = 0; r < t.length; r++)
                t[r].value && e.push(this.createMatrixRow(t[r], n[t[r].value]));
              return e;
            }),
            (t.prototype.createMatrixRow = function (e, t) {
              return new l(e.value, e, this, t);
            }),
            t
          );
        })(i.a);
      o.b.addClass(
        'matrixdropdown',
        [
          { name: 'rows:itemvalue[]' },
          'rowsVisibleIf:condition',
          'rowTitleWidth',
          { name: 'totalText', serializationProperty: 'locTotalText' },
        ],
        function () {
          return new u('');
        },
        'matrixdropdownbase'
      ),
        s.b.Instance.registerQuestion('matrixdropdown', function (e) {
          var t = new u(e);
          return (
            (t.choices = [1, 2, 3, 4, 5]), (t.rows = s.b.DefaultColums), i.a.addDefaultColumns(t), t
          );
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(24),
        o = n(1),
        a = n(3),
        s = n(2),
        l = n(12),
        u = n(4),
        c = n(7),
        p = n(26);
      n.d(t, 'b', function () {
        return d;
      }),
        n.d(t, 'a', function () {
          return h;
        });
      var d = (function (e) {
          function t(t, n, r) {
            var i = e.call(this, n, r) || this;
            return (i.index = t), i.buildCells(r), i;
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'rowName', {
              get: function () {
                return this.id;
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(i.b),
        h = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (
              (n.name = t),
              (n.rowCounter = 0),
              (n.initialRowCount = 2),
              (n.setRowCountValueFromData = !1),
              n.createLocalizableString('confirmDeleteText', n),
              n.createLocalizableString('keyDuplicationError', n),
              n.createLocalizableString('addRowText', n),
              n.createLocalizableString('removeRowText', n),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'matrixdynamic';
            }),
            Object.defineProperty(t.prototype, 'isRowsDynamic', {
              get: function () {
                return !0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'confirmDelete', {
              get: function () {
                return this.getPropertyValue('confirmDelete', !1);
              },
              set: function (e) {
                this.setPropertyValue('confirmDelete', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'keyName', {
              get: function () {
                return this.getPropertyValue('keyName', '');
              },
              set: function (e) {
                this.setPropertyValue('keyName', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'defaultRowValue', {
              get: function () {
                return this.getPropertyValue('defaultRowValue');
              },
              set: function (e) {
                this.setPropertyValue('defaultRowValue', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'defaultValueFromLastRow', {
              get: function () {
                return this.getPropertyValue('defaultValueFromLastRow', !1);
              },
              set: function (e) {
                this.setPropertyValue('defaultValueFromLastRow', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.isDefaultValueEmpty = function () {
              return (
                e.prototype.isDefaultValueEmpty.call(this) &&
                this.isValueEmpty(this.defaultRowValue)
              );
            }),
            (t.prototype.valueFromData = function (t) {
              if (this.minRowCount < 1) return e.prototype.valueFromData.call(this, t);
              Array.isArray(t) || (t = []);
              for (var n = t.length; n < this.minRowCount; n++) t.push({});
              return t;
            }),
            (t.prototype.setDefaultValue = function () {
              if (this.isValueEmpty(this.defaultRowValue) || !this.isValueEmpty(this.defaultValue))
                return void e.prototype.setDefaultValue.call(this);
              if (this.isEmpty() && 0 != this.rowCount) {
                for (var t = [], n = 0; n < this.rowCount; n++) t.push(this.defaultRowValue);
                this.value = t;
              }
            }),
            (t.prototype.isEditingSurveyElement = function (t) {
              return (
                !(!this.survey || !this.survey.isEditingSurveyElement) ||
                e.prototype.isEditingSurveyElement.call(this, t)
              );
            }),
            Object.defineProperty(t.prototype, 'rowCount', {
              get: function () {
                return this.rowCountValue;
              },
              set: function (e) {
                if (!(e < 0 || e > c.a.matrixMaximumRowCount)) {
                  this.setRowCountValueFromData = !1;
                  var t = this.rowCountValue;
                  if (((this.rowCountValue = e), this.value && this.value.length > e)) {
                    var n = this.value;
                    n.splice(e), (this.value = n);
                  }
                  if (this.isLoadingFromJson) return void (this.initialRowCount = e);
                  if (this.generatedVisibleRows) {
                    this.generatedVisibleRows.splice(e);
                    for (var r = t; r < e; r++) {
                      var i = this.createMatrixRow(this.getValueForNewRow());
                      this.generatedVisibleRows.push(i), this.onMatrixRowCreated(i);
                    }
                  }
                  this.onRowsChanged();
                }
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'rowCountValue', {
              get: function () {
                return this.getPropertyValue('rowCount', 2);
              },
              set: function (e) {
                this.setPropertyValue('rowCount', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getValueForNewRow = function () {
              var e = null;
              return (
                this.onGetValueForNewRowCallBack && (e = this.onGetValueForNewRowCallBack(this)), e
              );
            }),
            Object.defineProperty(t.prototype, 'minRowCount', {
              get: function () {
                return this.getPropertyValue('minRowCount', 0);
              },
              set: function (e) {
                e < 0 && (e = 0),
                  this.setPropertyValue('minRowCount', e),
                  e > this.maxRowCount && (this.maxRowCount = e),
                  this.rowCount < e && (this.rowCount = e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'maxRowCount', {
              get: function () {
                return this.getPropertyValue('maxRowCount', c.a.matrixMaximumRowCount);
              },
              set: function (e) {
                e <= 0 ||
                  (e > c.a.matrixMaximumRowCount && (e = c.a.matrixMaximumRowCount),
                  e != this.maxRowCount &&
                    (this.setPropertyValue('maxRowCount', e),
                    e < this.minRowCount && (this.minRowCount = e),
                    this.rowCount > e && (this.rowCount = e)));
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'allowAddRows', {
              get: function () {
                return this.getPropertyValue('allowAddRows', !0);
              },
              set: function (e) {
                this.setPropertyValue('allowAddRows', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'allowRemoveRows', {
              get: function () {
                return this.getPropertyValue('allowRemoveRows', !0);
              },
              set: function (e) {
                this.setPropertyValue('allowRemoveRows', e),
                  this.isLoadingFromJson || this.resetRenderedTable();
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'canAddRow', {
              get: function () {
                return this.allowAddRows && !this.isReadOnly && this.rowCount < this.maxRowCount;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'canRemoveRows', {
              get: function () {
                return this.allowRemoveRows && !this.isReadOnly && this.rowCount > this.minRowCount;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.canRemoveRow = function (e) {
              return !this.survey || this.survey.matrixAllowRemoveRow(this, e.index, e);
            }),
            (t.prototype.addRow = function () {
              var e = { question: this, canAddRow: this.canAddRow };
              this.survey && this.survey.matrixBeforeRowAdded(e),
                e.canAddRow &&
                  (this.onStartRowAddingRemoving(), this.addRowCore(), this.onEndRowAdding());
            }),
            (t.prototype.hasRowsAsItems = function () {
              return !1;
            }),
            (t.prototype.unbindValue = function () {
              this.clearGeneratedRows(),
                this.clearPropertyValue('value'),
                (this.rowCountValue = 0),
                e.prototype.unbindValue.call(this);
            }),
            (t.prototype.addRowCore = function () {
              var e = this.rowCount;
              this.rowCount = this.rowCount + 1;
              var t = this.getDefaultRowValue(!0),
                n = null;
              if (
                (this.isValueEmpty(t) ||
                  ((n = this.createNewValue()),
                  n.length == this.rowCount && ((n[n.length - 1] = t), (this.value = n))),
                this.data)
              ) {
                this.runCellsCondition(
                  this.getDataFilteredValues(),
                  this.getDataFilteredProperties()
                );
                var r = this.visibleRows[this.rowCount - 1];
                u.a.isValueEmpty(r.value) ||
                  (n || (n = this.createNewValue()),
                  this.isEditingSurveyElement(n) ||
                    u.a.isTwoValueEquals(n[n.length - 1], r.value) ||
                    ((n[n.length - 1] = r.value), (this.value = n)));
              }
              this.survey &&
                e + 1 == this.rowCount &&
                (this.survey.matrixRowAdded(this, this.visibleRows[this.visibleRows.length - 1]),
                this.onRowsChanged());
            }),
            (t.prototype.getDefaultRowValue = function (e) {
              for (var t = null, n = 0; n < this.columns.length; n++) {
                var r = this.columns[n].templateQuestion;
                r &&
                  !this.isValueEmpty(r.getDefaultValue()) &&
                  ((t = t || {}), (t[this.columns[n].name] = r.getDefaultValue()));
              }
              if (!this.isValueEmpty(this.defaultRowValue))
                for (var i in this.defaultRowValue) (t = t || {}), (t[i] = this.defaultRowValue[i]);
              if (e && this.defaultValueFromLastRow) {
                var o = this.value;
                if (o && Array.isArray(o) && o.length >= this.rowCount - 1) {
                  var a = o[this.rowCount - 2];
                  for (var i in a) (t = t || {}), (t[i] = a[i]);
                }
              }
              return t;
            }),
            (t.prototype.removeRowUI = function (e) {
              if (e && e.rowName) {
                var t = this.visibleRows.indexOf(e);
                if (t < 0) return;
                e = t;
              }
              (this.isRequireConfirmOnRowDelete(e) && !n.i(p.c)(this.confirmDeleteText)) ||
                this.removeRow(e);
            }),
            (t.prototype.isRequireConfirmOnRowDelete = function (e) {
              if (!this.confirmDelete) return !1;
              if (e < 0 || e >= this.rowCount) return !1;
              var t = this.createNewValue();
              return (
                !(u.a.isValueEmpty(t) || !Array.isArray(t)) &&
                !(e >= t.length) &&
                !u.a.isValueEmpty(t[e])
              );
            }),
            (t.prototype.removeRow = function (e) {
              if (this.canRemoveRows && !(e < 0 || e >= this.rowCount)) {
                var t =
                  this.visibleRows && e < this.visibleRows.length ? this.visibleRows[e] : null;
                this.onStartRowAddingRemoving(), this.removeRowCore(e), this.onEndRowRemoving(t);
              }
            }),
            (t.prototype.removeRowCore = function (e) {
              var t = this.generatedVisibleRows ? this.generatedVisibleRows[e] : null;
              if (
                (this.generatedVisibleRows &&
                  e < this.generatedVisibleRows.length &&
                  this.generatedVisibleRows.splice(e, 1),
                this.rowCountValue--,
                this.value)
              ) {
                var n = [];
                (n =
                  Array.isArray(this.value) && e < this.value.length
                    ? this.createValueCopy()
                    : this.createNewValue()),
                  n.splice(e, 1),
                  (n = this.deleteRowValue(n, null)),
                  (this.isRowChanging = !0),
                  (this.value = n),
                  (this.isRowChanging = !1);
              }
              this.onRowsChanged(), this.survey && this.survey.matrixRowRemoved(this, e, t);
            }),
            Object.defineProperty(t.prototype, 'confirmDeleteText', {
              get: function () {
                return this.getLocalizableStringText(
                  'confirmDeleteText',
                  s.a.getString('confirmDelete')
                );
              },
              set: function (e) {
                this.setLocalizableStringText('confirmDeleteText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locConfirmDeleteText', {
              get: function () {
                return this.getLocalizableString('confirmDeleteText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'keyDuplicationError', {
              get: function () {
                return this.getLocalizableStringText(
                  'keyDuplicationError',
                  s.a.getString('keyDuplicationError')
                );
              },
              set: function (e) {
                this.setLocalizableStringText('keyDuplicationError', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locKeyDuplicationError', {
              get: function () {
                return this.getLocalizableString('keyDuplicationError');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'addRowText', {
              get: function () {
                var e = this.isColumnLayoutHorizontal ? 'addRow' : 'addColumn';
                return this.getLocalizableStringText('addRowText', s.a.getString(e));
              },
              set: function (e) {
                this.setLocalizableStringText('addRowText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locAddRowText', {
              get: function () {
                return this.getLocalizableString('addRowText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'addRowLocation', {
              get: function () {
                return this.getPropertyValue('addRowLocation');
              },
              set: function (e) {
                this.setPropertyValue('addRowLocation', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isAddRowOnTop', {
              get: function () {
                return (
                  !!this.canAddRow &&
                  ('default' === this.addRowLocation
                    ? 'vertical' === this.columnLayout
                    : 'bottom' !== this.addRowLocation)
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isAddRowOnBottom', {
              get: function () {
                return (
                  !!this.canAddRow &&
                  ('default' === this.addRowLocation
                    ? 'horizontal' === this.columnLayout
                    : 'top' !== this.addRowLocation)
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'removeRowText', {
              get: function () {
                return this.getLocalizableStringText('removeRowText', s.a.getString('removeRow'));
              },
              set: function (e) {
                this.setLocalizableStringText('removeRowText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locRemoveRowText', {
              get: function () {
                return this.getLocalizableString('removeRowText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getDisplayValueCore = function (e, t) {
              var n = this.createValueCopy();
              if (!n || !Array.isArray(n)) return n;
              for (var r = this.visibleRows, i = 0; i < r.length && i < n.length; i++) {
                var o = n[i];
                o && (n[i] = this.getRowDisplayValue(r[i], o));
              }
              return n;
            }),
            (t.prototype.addConditionObjectsByContext = function (e, t) {
              for (
                var n = !!t && this.columns.indexOf(t) > -1, r = 0;
                r < this.columns.length;
                r++
              ) {
                var i = this.columns[r];
                this.addColumnIntoaddConditionObjectsByContext(e, 0, i),
                  n && i != t && this.addColumnIntoaddConditionObjectsByContext(e, -1, i);
                for (var o = 1; o < Math.min(c.a.matrixMaxRowCountInCondition, this.rowCount); o++)
                  this.addColumnIntoaddConditionObjectsByContext(e, o, i);
              }
            }),
            (t.prototype.addColumnIntoaddConditionObjectsByContext = function (e, t, n) {
              var r = t > -1 ? '[' + t.toString() + '].' : 'row.';
              e.push({
                name: (t > -1 ? this.getValueName() + r : r) + n.name,
                text: (t > -1 ? this.processedTitle + r : r) + n.fullTitle,
                question: this,
              });
            }),
            (t.prototype.supportGoNextPageAutomatic = function () {
              return !1;
            }),
            Object.defineProperty(t.prototype, 'hasRowText', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onCheckForErrors = function (t, n) {
              e.prototype.onCheckForErrors.call(this, t, n),
                !n && this.hasErrorInMinRows() && t.push(new l.i(this.minRowCount, this));
            }),
            (t.prototype.hasErrors = function (t, n) {
              void 0 === t && (t = !0), void 0 === n && (n = null);
              var r = e.prototype.hasErrors.call(this, t, n);
              return this.isValueDuplicated() || r;
            }),
            (t.prototype.hasErrorInMinRows = function () {
              if (this.minRowCount <= 0 || !this.isRequired || !this.generatedVisibleRows)
                return !1;
              for (var e = 0, t = 0; t < this.generatedVisibleRows.length; t++) {
                this.generatedVisibleRows[t].isEmpty || e++;
              }
              return e < this.minRowCount;
            }),
            (t.prototype.isValueDuplicated = function () {
              if (!this.keyName || !this.generatedVisibleRows) return !1;
              var e = this.getColumnByName(this.keyName);
              if (!e) return !1;
              for (var t = [], n = !1, r = 0; r < this.generatedVisibleRows.length; r++)
                n = this.isValueDuplicatedInRow(this.generatedVisibleRows[r], e, t) || n;
              return n;
            }),
            (t.prototype.isValueDuplicatedInRow = function (e, t, n) {
              var r = e.getQuestionByColumn(t);
              if (!r || r.isEmpty()) return !1;
              for (var i = r.value, o = 0; o < n.length; o++)
                if (i == n[o]) return r.addError(new l.h(this.keyDuplicationError, this)), !0;
              return n.push(i), !1;
            }),
            (t.prototype.generateRows = function () {
              var e = new Array();
              if (0 === this.rowCount) return e;
              for (var t = this.createNewValue(), n = 0; n < this.rowCount; n++)
                e.push(this.createMatrixRow(this.getRowValueByIndex(t, n)));
              return this.isValueEmpty(this.getDefaultRowValue(!1)) || (this.value = t), e;
            }),
            (t.prototype.createMatrixRow = function (e) {
              return new d(this.rowCounter++, this, e);
            }),
            (t.prototype.onBeforeValueChanged = function (e) {
              if (e && Array.isArray(e)) {
                var t = e.length;
                t != this.rowCount &&
                  ((!this.setRowCountValueFromData && t < this.initialRowCount) ||
                    ((this.setRowCountValueFromData = !0),
                    (this.rowCountValue = t),
                    this.generatedVisibleRows &&
                      (this.clearGeneratedRows(),
                      (this.generatedVisibleRows = this.visibleRows),
                      this.onRowsChanged())));
              }
            }),
            (t.prototype.createNewValue = function () {
              var e = this.createValueCopy();
              (e && Array.isArray(e)) || (e = []),
                e.length > this.rowCount && e.splice(this.rowCount);
              var t = this.getDefaultRowValue(!1);
              t = t || {};
              for (var n = e.length; n < this.rowCount; n++) e.push(this.getUnbindValue(t));
              return e;
            }),
            (t.prototype.deleteRowValue = function (e, t) {
              for (var n = !0, r = 0; r < e.length; r++)
                if (this.isObject(e[r]) && Object.keys(e[r]).length > 0) {
                  n = !1;
                  break;
                }
              return n ? null : e;
            }),
            (t.prototype.getRowValueByIndex = function (e, t) {
              return Array.isArray(e) && t >= 0 && t < e.length ? e[t] : null;
            }),
            (t.prototype.getRowValueCore = function (e, t, n) {
              if ((void 0 === n && (n = !1), !this.generatedVisibleRows)) return {};
              var r = this.getRowValueByIndex(t, this.generatedVisibleRows.indexOf(e));
              return !r && n && (r = {}), r;
            }),
            t
          );
        })(i.a);
      o.b.addClass(
        'matrixdynamic',
        [
          { name: 'rowsVisibleIf:condition', visible: !1 },
          { name: 'allowAddRows:boolean', default: !0 },
          { name: 'allowRemoveRows:boolean', default: !0 },
          { name: 'rowCount:number', default: 2, minValue: 0, isBindable: !0 },
          { name: 'minRowCount:number', default: 0, minValue: 0 },
          { name: 'maxRowCount:number', default: c.a.matrixMaximumRowCount },
          { name: 'keyName' },
          { name: 'keyDuplicationError', serializationProperty: 'locKeyDuplicationError' },
          'defaultRowValue:rowvalue',
          'defaultValueFromLastRow:boolean',
          { name: 'confirmDelete:boolean' },
          {
            name: 'confirmDeleteText',
            dependsOn: 'confirmDelete',
            visibleIf: function (e) {
              return !e || e.confirmDelete;
            },
            serializationProperty: 'locConfirmDeleteText',
          },
          {
            name: 'addRowLocation',
            default: 'default',
            choices: ['default', 'top', 'bottom', 'topBottom'],
          },
          { name: 'addRowText', serializationProperty: 'locAddRowText' },
          { name: 'removeRowText', serializationProperty: 'locRemoveRowText' },
        ],
        function () {
          return new h('');
        },
        'matrixdropdownbase'
      ),
        a.b.Instance.registerQuestion('matrixdynamic', function (e) {
          var t = new h(e);
          return (t.choices = [1, 2, 3, 4, 5]), i.a.addDefaultColumns(t), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(6),
        o = n(9),
        a = n(33),
        s = n(1),
        l = n(3),
        u = n(4);
      n.d(t, 'a', function () {
        return c;
      }),
        n.d(t, 'b', function () {
          return p;
        });
      var c = (function (e) {
          function t(t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.call(this) || this;
            return (
              (r.editorValue = r.createEditor(t)),
              (r.editor.questionTitleTemplateCallback = function () {
                return '';
              }),
              (r.editor.titleLocation = 'left'),
              n && (r.title = n),
              r
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'multipletextitem';
            }),
            Object.defineProperty(t.prototype, 'id', {
              get: function () {
                return this.editor.id;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'name', {
              get: function () {
                return this.editor.name;
              },
              set: function (e) {
                this.editor.name = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'question', {
              get: function () {
                return this.data;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'editor', {
              get: function () {
                return this.editorValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.createEditor = function (e) {
              return new a.a(e);
            }),
            (t.prototype.addUsedLocales = function (t) {
              e.prototype.addUsedLocales.call(this, t), this.editor.addUsedLocales(t);
            }),
            (t.prototype.locStrsChanged = function () {
              e.prototype.locStrsChanged.call(this), this.editor.locStrsChanged();
            }),
            (t.prototype.setData = function (e) {
              (this.data = e),
                e &&
                  ((this.editor.defaultValue = e.getItemDefaultValue(this.name)),
                  this.editor.setSurveyImpl(this),
                  (this.editor.parent = e));
            }),
            Object.defineProperty(t.prototype, 'isRequired', {
              get: function () {
                return this.editor.isRequired;
              },
              set: function (e) {
                this.editor.isRequired = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'inputType', {
              get: function () {
                return this.editor.inputType;
              },
              set: function (e) {
                this.editor.inputType = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'title', {
              get: function () {
                return this.editor.title;
              },
              set: function (e) {
                this.editor.title = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locTitle', {
              get: function () {
                return this.editor.locTitle;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'fullTitle', {
              get: function () {
                return this.editor.fullTitle;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'maxLength', {
              get: function () {
                return this.editor.maxLength;
              },
              set: function (e) {
                this.editor.maxLength = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getMaxLength = function () {
              var e = this.getSurvey();
              return u.a.getMaxLength(this.maxLength, e ? e.maxTextLength : -1);
            }),
            Object.defineProperty(t.prototype, 'placeHolder', {
              get: function () {
                return this.editor.placeHolder;
              },
              set: function (e) {
                this.editor.placeHolder = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locPlaceHolder', {
              get: function () {
                return this.editor.locPlaceHolder;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'requiredErrorText', {
              get: function () {
                return this.editor.requiredErrorText;
              },
              set: function (e) {
                this.editor.requiredErrorText = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locRequiredErrorText', {
              get: function () {
                return this.editor.locRequiredErrorText;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'validators', {
              get: function () {
                return this.editor.validators;
              },
              set: function (e) {
                this.editor.validators = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getValidators = function () {
              return this.validators;
            }),
            Object.defineProperty(t.prototype, 'value', {
              get: function () {
                return this.data ? this.data.getMultipleTextValue(this.name) : null;
              },
              set: function (e) {
                null != this.data && this.data.setMultipleTextValue(this.name, e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.isEmpty = function () {
              return u.a.isValueEmpty(this.value);
            }),
            (t.prototype.onValueChanged = function (e) {
              this.valueChangedCallback && this.valueChangedCallback(e);
            }),
            (t.prototype.geSurveyData = function () {
              return this;
            }),
            (t.prototype.getSurvey = function () {
              return this.data ? this.data.getSurvey() : null;
            }),
            (t.prototype.getTextProcessor = function () {
              return this.data ? this.data.getTextProcessor() : null;
            }),
            (t.prototype.getValue = function (e) {
              return this.data ? this.data.getMultipleTextValue(e) : null;
            }),
            (t.prototype.setValue = function (e, t) {
              this.data && this.data.setMultipleTextValue(e, t);
            }),
            (t.prototype.getVariable = function (e) {}),
            (t.prototype.setVariable = function (e, t) {}),
            (t.prototype.getComment = function (e) {
              return null;
            }),
            (t.prototype.setComment = function (e, t) {}),
            (t.prototype.getAllValues = function () {
              return this.data ? this.data.getAllValues() : this.value;
            }),
            (t.prototype.getFilteredValues = function () {
              return this.getAllValues();
            }),
            (t.prototype.getFilteredProperties = function () {
              return { survey: this.getSurvey() };
            }),
            (t.prototype.getValidatorTitle = function () {
              return this.title;
            }),
            Object.defineProperty(t.prototype, 'validatedValue', {
              get: function () {
                return this.value;
              },
              set: function (e) {
                this.value = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getDataFilteredValues = function () {
              return this.getFilteredValues();
            }),
            (t.prototype.getDataFilteredProperties = function () {
              return this.getFilteredProperties();
            }),
            t
          );
        })(i.b),
        p = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            (n.name = t), (n.isMultipleItemValueChanging = !1);
            var r = n;
            return (
              n.createNewArray('items', function (e) {
                e.setData(r);
              }),
              n.registerFunctionOnPropertyValueChanged('items', function () {
                r.fireCallback(r.colCountChangedCallback);
              }),
              n.registerFunctionOnPropertyValueChanged('colCount', function () {
                r.fireCallback(r.colCountChangedCallback);
              }),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'multipletext';
            }),
            (t.prototype.setSurveyImpl = function (t) {
              e.prototype.setSurveyImpl.call(this, t);
              for (var n = 0; n < this.items.length; n++) this.items[n].setData(this);
            }),
            Object.defineProperty(t.prototype, 'isAllowTitleLeft', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'hasSingleInput', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onSurveyLoad = function () {
              this.editorsOnSurveyLoad(),
                e.prototype.onSurveyLoad.call(this),
                this.fireCallback(this.colCountChangedCallback);
            }),
            (t.prototype.setQuestionValue = function (t, n) {
              void 0 === n && (n = !0), e.prototype.setQuestionValue.call(this, t, n);
              for (var r = 0; r < this.items.length; r++) {
                var i = this.items[r];
                i.editor && i.editor.updateValueFromSurvey(i.value);
              }
              this.updateIsAnswered();
            }),
            (t.prototype.onSurveyValueChanged = function (t) {
              e.prototype.onSurveyValueChanged.call(this, t);
              for (var n = 0; n < this.items.length; n++) {
                var r = this.items[n];
                r.editor && r.editor.onSurveyValueChanged(r.value);
              }
            }),
            (t.prototype.editorsOnSurveyLoad = function () {
              for (var e = 0; e < this.items.length; e++) {
                var t = this.items[e];
                t.editor && t.editor.onSurveyLoad();
              }
            }),
            Object.defineProperty(t.prototype, 'items', {
              get: function () {
                return this.getPropertyValue('items');
              },
              set: function (e) {
                this.setPropertyValue('items', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.addItem = function (e, t) {
              void 0 === t && (t = null);
              var n = this.createTextItem(e, t);
              return this.items.push(n), n;
            }),
            (t.prototype.getItemByName = function (e) {
              for (var t = 0; t < this.items.length; t++)
                if (this.items[t].name == e) return this.items[t];
              return null;
            }),
            (t.prototype.addConditionObjectsByContext = function (e, t) {
              for (var n = 0; n < this.items.length; n++) {
                var r = this.items[n];
                e.push({
                  name: this.getValueName() + '.' + r.name,
                  text: this.processedTitle + '.' + r.fullTitle,
                  question: this,
                });
              }
            }),
            (t.prototype.getConditionJson = function (t, n) {
              if ((void 0 === t && (t = null), void 0 === n && (n = null), !n))
                return e.prototype.getConditionJson.call(this);
              var r = this.getItemByName(n);
              if (!r) return null;
              var i = new s.c().toJsonObject(r);
              return (i.type = 'text'), i;
            }),
            (t.prototype.locStrsChanged = function () {
              e.prototype.locStrsChanged.call(this);
              for (var t = 0; t < this.items.length; t++) this.items[t].locStrsChanged();
            }),
            (t.prototype.supportGoNextPageAutomatic = function () {
              for (var e = 0; e < this.items.length; e++) if (this.items[e].isEmpty()) return !1;
              return !0;
            }),
            Object.defineProperty(t.prototype, 'colCount', {
              get: function () {
                return this.getPropertyValue('colCount');
              },
              set: function (e) {
                e < 1 || e > 5 || this.setPropertyValue('colCount', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'itemSize', {
              get: function () {
                return this.getPropertyValue('itemSize');
              },
              set: function (e) {
                this.setPropertyValue('itemSize', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getRows = function () {
              for (var e = this.colCount, t = this.items, n = [], r = 0, i = 0; i < t.length; i++)
                0 == r && n.push([]), n[n.length - 1].push(t[i]), ++r >= e && (r = 0);
              return n;
            }),
            (t.prototype.onValueChanged = function () {
              e.prototype.onValueChanged.call(this), this.onItemValueChanged();
            }),
            (t.prototype.createTextItem = function (e, t) {
              return new c(e, t);
            }),
            (t.prototype.onItemValueChanged = function () {
              if (!this.isMultipleItemValueChanging)
                for (var e = 0; e < this.items.length; e++) {
                  var t = null;
                  this.value &&
                    this.items[e].name in this.value &&
                    (t = this.value[this.items[e].name]),
                    this.items[e].onValueChanged(t);
                }
            }),
            (t.prototype.getIsRunningValidators = function () {
              if (e.prototype.getIsRunningValidators.call(this)) return !0;
              for (var t = 0; t < this.items.length; t++)
                if (this.items[t].editor.isRunningValidators) return !0;
              return !1;
            }),
            (t.prototype.hasErrors = function (t, n) {
              var r = this;
              void 0 === t && (t = !0), void 0 === n && (n = null);
              for (var i = !1, o = 0; o < this.items.length; o++)
                (this.items[o].editor.onCompletedAsyncValidators = function (e) {
                  r.raiseOnCompletedAsyncValidators();
                }),
                  (n && !0 === n.isOnValueChanged && this.items[o].editor.isEmpty()) ||
                    (i = this.items[o].editor.hasErrors(t, n) || i);
              return e.prototype.hasErrors.call(this, t) || i;
            }),
            (t.prototype.getAllErrors = function () {
              for (var t = e.prototype.getAllErrors.call(this), n = 0; n < this.items.length; n++) {
                var r = this.items[n].editor.getAllErrors();
                r && r.length > 0 && (t = t.concat(r));
              }
              return t;
            }),
            (t.prototype.clearErrors = function () {
              e.prototype.clearErrors.call(this);
              for (var t = 0; t < this.items.length; t++) this.items[t].editor.clearErrors();
            }),
            (t.prototype.getContainsErrors = function () {
              var t = e.prototype.getContainsErrors.call(this);
              if (t) return t;
              for (var n = this.items, r = 0; r < n.length; r++)
                if (n[r].editor.containsErrors) return !0;
              return !1;
            }),
            (t.prototype.getIsAnswered = function () {
              if (!e.prototype.getIsAnswered.call(this)) return !1;
              for (var t = 0; t < this.items.length; t++) {
                var n = this.items[t].editor;
                if (n.isVisible && !n.isAnswered) return !1;
              }
              return !0;
            }),
            (t.prototype.getProgressInfo = function () {
              for (var e = [], t = 0; t < this.items.length; t++) e.push(this.items[t].editor);
              return i.a.getProgressInfoByElements(e, this.isRequired);
            }),
            (t.prototype.getMultipleTextValue = function (e) {
              return this.value ? this.value[e] : null;
            }),
            (t.prototype.setMultipleTextValue = function (e, t) {
              this.isMultipleItemValueChanging = !0;
              var n = this.value;
              n || (n = {}),
                (n[e] = t),
                this.setNewValue(n),
                (this.isMultipleItemValueChanging = !1);
            }),
            (t.prototype.getItemDefaultValue = function (e) {
              return this.defaultValue ? this.defaultValue[e] : null;
            }),
            (t.prototype.getSurvey = function () {
              return this.survey;
            }),
            (t.prototype.getTextProcessor = function () {
              return this.textProcessor;
            }),
            (t.prototype.getAllValues = function () {
              return this.data ? this.data.getAllValues() : null;
            }),
            (t.prototype.getIsRequiredText = function () {
              return this.survey ? this.survey.requiredText : '';
            }),
            (t.prototype.addElement = function (e, t) {}),
            (t.prototype.removeElement = function (e) {
              return !1;
            }),
            (t.prototype.getQuestionTitleLocation = function () {
              return 'left';
            }),
            (t.prototype.getQuestionStartIndex = function () {
              return this.getStartIndex();
            }),
            (t.prototype.getChildrenLayoutType = function () {
              return 'row';
            }),
            (t.prototype.elementWidthChanged = function (e) {}),
            Object.defineProperty(t.prototype, 'elements', {
              get: function () {
                return [];
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.indexOf = function (e) {
              return -1;
            }),
            t
          );
        })(o.a);
      s.b.addClass(
        'multipletextitem',
        [
          'name',
          'isRequired:boolean',
          { name: 'placeHolder', serializationProperty: 'locPlaceHolder' },
          {
            name: 'inputType',
            default: 'text',
            choices: [
              'color',
              'date',
              'datetime',
              'datetime-local',
              'email',
              'month',
              'number',
              'password',
              'range',
              'tel',
              'text',
              'time',
              'url',
              'week',
            ],
          },
          { name: 'title', serializationProperty: 'locTitle' },
          { name: 'maxLength:number', default: -1 },
          { name: 'requiredErrorText:text', serializationProperty: 'locRequiredErrorText' },
          {
            name: 'validators:validators',
            baseClassName: 'surveyvalidator',
            classNamePart: 'validator',
          },
        ],
        function () {
          return new c('');
        }
      ),
        s.b.addClass(
          'multipletext',
          [
            { name: '!items:textitems', className: 'multipletextitem' },
            { name: 'itemSize:number', default: 25, minValue: 0 },
            { name: 'colCount:number', default: 1, choices: [1, 2, 3, 4, 5] },
          ],
          function () {
            return new p('');
          },
          'question'
        ),
        l.b.Instance.registerQuestion('multipletext', function (e) {
          var t = new p(e);
          return t.addItem('text1'), t.addItem('text2'), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(4),
        o = n(6),
        a = n(2),
        s = n(25),
        l = n(9),
        u = n(18),
        c = n(1),
        p = n(3),
        d = n(12),
        h = n(7),
        f = n(26);
      n.d(t, 'b', function () {
        return g;
      }),
        n.d(t, 'a', function () {
          return v;
        });
      var m = (function (e) {
          function t(t, n, r) {
            var i = e.call(this, r) || this;
            return (i.data = t), (i.panelItem = n), (i.variableName = r), i;
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'survey', {
              get: function () {
                return this.panelItem.getSurvey();
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'panel', {
              get: function () {
                return this.panelItem.panel;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'panelIndex', {
              get: function () {
                return this.data ? this.data.getItemIndex(this.panelItem) : -1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getValues = function () {
              return this.panelItem.getAllValues();
            }),
            (t.prototype.getQuestionByName = function (t) {
              var n = e.prototype.getQuestionByName.call(this, t);
              if (n) return n;
              var r = this.panelIndex;
              return r > -1 ? this.data.getSharedQuestionFromArray(t, r) : null;
            }),
            (t.prototype.onCustomProcessText = function (e) {
              if (e.name == g.IndexVariableName) {
                var n = this.panelIndex;
                if (n > -1) return (e.isExists = !0), (e.value = n + 1), !0;
              }
              if (0 == e.name.indexOf(g.ParentItemVariableName + '.')) {
                var r = this.data;
                if (r && r.parentQuestion && r.parent) {
                  var i = r.parent.data,
                    o = new t(r.parentQuestion, i, g.ItemVariableName),
                    a = e.name.replace(g.ParentItemVariableName, g.ItemVariableName),
                    s = o.processValue(a, e.returnDisplayValue);
                  (e.isExists = s.isExists), (e.value = s.value);
                }
                return !0;
              }
              return !1;
            }),
            t
          );
        })(s.b),
        g = (function () {
          function e(t, n) {
            (this.data = t),
              (this.panelValue = n),
              (this.textPreProcessor = new m(t, this, e.ItemVariableName)),
              this.setSurveyImpl();
          }
          return (
            Object.defineProperty(e.prototype, 'panel', {
              get: function () {
                return this.panelValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.setSurveyImpl = function () {
              this.panel.setSurveyImpl(this);
            }),
            (e.prototype.getValue = function (e) {
              return this.getAllValues()[e];
            }),
            (e.prototype.setValue = function (e, t) {
              this.data.setPanelItemData(this, e, t);
            }),
            (e.prototype.getVariable = function (e) {}),
            (e.prototype.setVariable = function (e, t) {}),
            (e.prototype.getComment = function (e) {
              var t = this.getValue(e + h.a.commentPrefix);
              return t || '';
            }),
            (e.prototype.setComment = function (e, t, n) {
              this.setValue(e + h.a.commentPrefix, t);
            }),
            (e.prototype.getAllValues = function () {
              return this.data.getPanelItemData(this);
            }),
            (e.prototype.getFilteredValues = function () {
              var t = {},
                n =
                  this.data && this.data.getRootData()
                    ? this.data.getRootData().getFilteredValues()
                    : {};
              for (var r in n) t[r] = n[r];
              return (t[e.ItemVariableName] = this.getAllValues()), t;
            }),
            (e.prototype.getFilteredProperties = function () {
              return this.data && this.data.getRootData()
                ? this.data.getRootData().getFilteredProperties()
                : { survey: this.getSurvey() };
            }),
            (e.prototype.geSurveyData = function () {
              return this;
            }),
            (e.prototype.getSurvey = function () {
              return this.data ? this.data.getSurvey() : null;
            }),
            (e.prototype.getTextProcessor = function () {
              return this.textPreProcessor;
            }),
            (e.ItemVariableName = 'panel'),
            (e.ParentItemVariableName = 'parentPanel'),
            (e.IndexVariableName = 'panelIndex'),
            e
          );
        })(),
        y = (function () {
          function e(e) {
            this.data = e;
          }
          return (
            (e.prototype.geSurveyData = function () {
              return null;
            }),
            (e.prototype.getSurvey = function () {
              return this.data.getSurvey();
            }),
            (e.prototype.getTextProcessor = function () {
              return null;
            }),
            e
          );
        })(),
        v = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            (n.name = t),
              (n.loadingPanelCount = 0),
              (n.currentIndexValue = -1),
              (n.isAddingNewPanels = !1),
              n.createNewArray('panels'),
              (n.templateValue = n.createAndSetupNewPanelObject()),
              (n.template.renderWidth = '100%'),
              (n.template.selectedElementInDesign = n);
            var r = n;
            return (
              (n.template.addElementCallback = function (e) {
                r.addOnPropertyChangedCallback(e), r.rebuildPanels();
              }),
              (n.template.removeElementCallback = function (e) {
                r.rebuildPanels();
              }),
              n.createLocalizableString('confirmDeleteText', n),
              n.createLocalizableString('keyDuplicationError', n),
              n.createLocalizableString('panelAddText', n),
              n.createLocalizableString('panelRemoveText', n),
              n.createLocalizableString('panelPrevText', n),
              n.createLocalizableString('panelNextText', n),
              n.registerFunctionOnPropertyValueChanged('panelsState', function () {
                r.setPanelsState();
              }),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'hasSingleInput', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setSurveyImpl = function (t) {
              e.prototype.setSurveyImpl.call(this, t),
                this.setTemplatePanelSurveyImpl(),
                this.setPanelsSurveyImpl();
            }),
            (t.prototype.assignOnPropertyChangedToTemplate = function () {
              for (var e = this.template.elements, t = 0; t < e.length; t++)
                this.addOnPropertyChangedCallback(e[t]);
            }),
            (t.prototype.addOnPropertyChangedCallback = function (e) {
              var t = this;
              e.onPropertyChanged.add(function (e, n) {
                t.onTemplateElementPropertyChanged(e, n);
              }),
                e.isPanel &&
                  (e.addElementCallback = function (e) {
                    t.addOnPropertyChangedCallback(e);
                  });
            }),
            (t.prototype.onTemplateElementPropertyChanged = function (e, t) {
              if (!this.isLoadingFromJson && !this.isDesignMode && 0 != this.panels.length) {
                if (c.b.findProperty(e.getType(), t.name))
                  for (var n = this.panels, r = 0; r < n.length; r++) {
                    var i = n[r].getQuestionByName(e.name);
                    i && i[t.name] !== t.newValue && (i[t.name] = t.newValue);
                  }
              }
            }),
            (t.prototype.getType = function () {
              return 'paneldynamic';
            }),
            Object.defineProperty(t.prototype, 'isCompositeQuestion', {
              get: function () {
                return !0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.clearOnDeletingContainer = function () {
              this.panels.forEach(function (e) {
                e.clearOnDeletingContainer();
              });
            }),
            Object.defineProperty(t.prototype, 'isAllowTitleLeft', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.removeElement = function (e) {
              return this.template.removeElement(e);
            }),
            Object.defineProperty(t.prototype, 'template', {
              get: function () {
                return this.templateValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getPanel = function () {
              return this.template;
            }),
            Object.defineProperty(t.prototype, 'templateElements', {
              get: function () {
                return this.template.elements;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'templateTitle', {
              get: function () {
                return this.template.title;
              },
              set: function (e) {
                this.template.title = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locTemplateTitle', {
              get: function () {
                return this.template.locTitle;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'templateDescription', {
              get: function () {
                return this.template.description;
              },
              set: function (e) {
                this.template.description = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locTemplateDescription', {
              get: function () {
                return this.template.locDescription;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'items', {
              get: function () {
                for (var e = [], t = 0; t < this.panels.length; t++) e.push(this.panels[t].data);
                return e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'panels', {
              get: function () {
                return this.getPropertyValue('panels');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'currentIndex', {
              get: function () {
                return this.isRenderModeList
                  ? -1
                  : this.isDesignMode
                  ? 0
                  : (this.currentIndexValue < 0 &&
                      this.panelCount > 0 &&
                      (this.currentIndexValue = 0),
                    this.currentIndexValue >= this.panelCount &&
                      (this.currentIndexValue = this.panelCount - 1),
                    this.currentIndexValue);
              },
              set: function (e) {
                this.currentIndexValue !== e &&
                  (e >= this.panelCount && (e = this.panelCount - 1),
                  (this.currentIndexValue = e),
                  this.fireCallback(this.currentIndexChangedCallback));
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'currentPanel', {
              get: function () {
                var e = this.currentIndex;
                return e < 0 || e >= this.panels.length ? null : this.panels[e];
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'confirmDelete', {
              get: function () {
                return this.getPropertyValue('confirmDelete', !1);
              },
              set: function (e) {
                this.setPropertyValue('confirmDelete', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'keyName', {
              get: function () {
                return this.getPropertyValue('keyName', '');
              },
              set: function (e) {
                this.setPropertyValue('keyName', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'confirmDeleteText', {
              get: function () {
                return this.getLocalizableStringText(
                  'confirmDeleteText',
                  a.a.getString('confirmDelete')
                );
              },
              set: function (e) {
                this.setLocalizableStringText('confirmDeleteText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locConfirmDeleteText', {
              get: function () {
                return this.getLocalizableString('confirmDeleteText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'keyDuplicationError', {
              get: function () {
                return this.getLocalizableStringText(
                  'keyDuplicationError',
                  a.a.getString('keyDuplicationError')
                );
              },
              set: function (e) {
                this.setLocalizableStringText('keyDuplicationError', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locKeyDuplicationError', {
              get: function () {
                return this.getLocalizableString('keyDuplicationError');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'panelPrevText', {
              get: function () {
                return this.getLocalizableStringText(
                  'panelPrevText',
                  a.a.getString('pagePrevText')
                );
              },
              set: function (e) {
                this.setLocalizableStringText('panelPrevText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locPanelPrevText', {
              get: function () {
                return this.getLocalizableString('panelPrevText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'panelNextText', {
              get: function () {
                return this.getLocalizableStringText(
                  'panelNextText',
                  a.a.getString('pageNextText')
                );
              },
              set: function (e) {
                this.setLocalizableStringText('panelNextText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locPanelNextText', {
              get: function () {
                return this.getLocalizableString('panelNextText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'panelAddText', {
              get: function () {
                return this.getLocalizableStringText('panelAddText', a.a.getString('addPanel'));
              },
              set: function (e) {
                this.setLocalizableStringText('panelAddText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locPanelAddText', {
              get: function () {
                return this.getLocalizableString('panelAddText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'panelRemoveText', {
              get: function () {
                return this.getLocalizableStringText(
                  'panelRemoveText',
                  a.a.getString('removePanel')
                );
              },
              set: function (e) {
                this.setLocalizableStringText('panelRemoveText', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locPanelRemoveText', {
              get: function () {
                return this.getLocalizableString('panelRemoveText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isProgressTopShowing', {
              get: function () {
                return 'progressTop' == this.renderMode || 'progressTopBottom' == this.renderMode;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isProgressBottomShowing', {
              get: function () {
                return (
                  'progressBottom' == this.renderMode || 'progressTopBottom' == this.renderMode
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isPrevButtonShowing', {
              get: function () {
                return this.currentIndex > 0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isNextButtonShowing', {
              get: function () {
                return this.currentIndex >= 0 && this.currentIndex < this.panelCount - 1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isRangeShowing', {
              get: function () {
                return this.showRangeInProgress && this.currentIndex >= 0 && this.panelCount > 1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getElementsInDesign = function (e) {
              return void 0 === e && (e = !1), e ? [this.template] : this.templateElements;
            }),
            (t.prototype.prepareValueForPanelCreating = function () {
              (this.addingNewPanelsValue = this.value),
                (this.isAddingNewPanels = !0),
                (this.isNewPanelsValueChanged = !1);
            }),
            (t.prototype.setValueAfterPanelsCreating = function () {
              (this.isAddingNewPanels = !1),
                this.isNewPanelsValueChanged &&
                  ((this.isValueChangingInternally = !0),
                  (this.value = this.addingNewPanelsValue),
                  (this.isValueChangingInternally = !1));
            }),
            (t.prototype.getValueCore = function () {
              return this.isAddingNewPanels
                ? this.addingNewPanelsValue
                : e.prototype.getValueCore.call(this);
            }),
            (t.prototype.setValueCore = function (t) {
              this.isAddingNewPanels
                ? ((this.isNewPanelsValueChanged = !0), (this.addingNewPanelsValue = t))
                : e.prototype.setValueCore.call(this, t);
            }),
            Object.defineProperty(t.prototype, 'panelCount', {
              get: function () {
                return this.isLoadingFromJson || this.isDesignMode
                  ? this.loadingPanelCount
                  : this.panels.length;
              },
              set: function (e) {
                if (!(e < 0)) {
                  if (this.isLoadingFromJson || this.isDesignMode)
                    return void (this.loadingPanelCount = e);
                  if (e != this.panels.length && !this.isDesignMode) {
                    this.updateBindings('panelCount', e), this.prepareValueForPanelCreating();
                    for (var t = this.panelCount; t < e; t++) {
                      var n = this.createNewPanel();
                      this.panels.push(n),
                        'list' == this.renderMode &&
                          'default' != this.panelsState &&
                          ('expand' === this.panelsState ? n.expand() : n.title && n.collapse());
                    }
                    e < this.panelCount && this.panels.splice(e, this.panelCount - e),
                      this.setValueAfterPanelsCreating(),
                      this.setValueBasedOnPanelCount(),
                      this.reRunCondition(),
                      this.fireCallback(this.panelCountChangedCallback);
                  }
                }
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'panelsState', {
              get: function () {
                return this.getPropertyValue('panelsState');
              },
              set: function (e) {
                this.setPropertyValue('panelsState', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setTemplatePanelSurveyImpl = function () {
              this.template.setSurveyImpl(this.isDesignMode ? this.surveyImpl : new y(this));
            }),
            (t.prototype.setPanelsSurveyImpl = function () {
              for (var e = 0; e < this.panels.length; e++) {
                var t = this.panels[e];
                t != this.template && t.setSurveyImpl(t.data);
              }
            }),
            (t.prototype.setPanelsState = function () {
              if (!this.isDesignMode && 'list' == this.renderMode && this.templateTitle)
                for (var e = 0; e < this.panels.length; e++) {
                  var t = this.panelsState;
                  'firstExpanded' === t && (t = 0 === e ? 'expanded' : 'collapsed'),
                    (this.panels[e].state = t);
                }
            }),
            (t.prototype.setValueBasedOnPanelCount = function () {
              var e = this.value;
              if (((e && Array.isArray(e)) || (e = []), e.length != this.panelCount)) {
                for (var t = e.length; t < this.panelCount; t++) e.push({});
                e.length > this.panelCount && e.splice(this.panelCount, e.length - this.panelCount),
                  (this.isValueChangingInternally = !0),
                  (this.value = e),
                  (this.isValueChangingInternally = !1);
              }
            }),
            Object.defineProperty(t.prototype, 'minPanelCount', {
              get: function () {
                return this.getPropertyValue('minPanelCount', 0);
              },
              set: function (e) {
                e < 0 && (e = 0),
                  e != this.minPanelCount &&
                    (this.setPropertyValue('minPanelCount', e),
                    e > this.maxPanelCount && (this.maxPanelCount = e),
                    this.panelCount < e && (this.panelCount = e));
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'maxPanelCount', {
              get: function () {
                return this.getPropertyValue('maxPanelCount', h.a.panelMaximumPanelCount);
              },
              set: function (e) {
                e <= 0 ||
                  (e > h.a.panelMaximumPanelCount && (e = h.a.panelMaximumPanelCount),
                  e != this.maxPanelCount &&
                    (this.setPropertyValue('maxPanelCount', e),
                    e < this.minPanelCount && (this.minPanelCount = e),
                    this.panelCount > e && (this.panelCount = e)));
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'allowAddPanel', {
              get: function () {
                return this.getPropertyValue('allowAddPanel');
              },
              set: function (e) {
                this.setPropertyValue('allowAddPanel', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'allowRemovePanel', {
              get: function () {
                return this.getPropertyValue('allowRemovePanel');
              },
              set: function (e) {
                this.setPropertyValue('allowRemovePanel', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'templateTitleLocation', {
              get: function () {
                return this.getPropertyValue('templateTitleLocation');
              },
              set: function (e) {
                this.setPropertyValue('templateTitleLocation', e.toLowerCase());
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'showQuestionNumbers', {
              get: function () {
                return this.getPropertyValue('showQuestionNumbers');
              },
              set: function (e) {
                this.setPropertyValue('showQuestionNumbers', e),
                  !this.isLoadingFromJson &&
                    this.survey &&
                    this.survey.questionVisibilityChanged(this, this.visible);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'showRangeInProgress', {
              get: function () {
                return this.getPropertyValue('showRangeInProgress', !0);
              },
              set: function (e) {
                this.setPropertyValue('showRangeInProgress', e),
                  this.fireCallback(this.currentIndexChangedCallback);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'renderMode', {
              get: function () {
                return this.getPropertyValue('renderMode');
              },
              set: function (e) {
                this.setPropertyValue('renderMode', e),
                  this.fireCallback(this.renderModeChangedCallback);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isRenderModeList', {
              get: function () {
                return 'list' == this.renderMode;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setVisibleIndex = function (t) {
              if (!this.isVisible) return 0;
              for (
                var n = 'onSurvey' == this.showQuestionNumbers ? t : 0, r = 0;
                r < this.panels.length;
                r++
              ) {
                var i = this.setPanelVisibleIndex(
                  this.panels[r],
                  n,
                  'off' != this.showQuestionNumbers
                );
                'onSurvey' == this.showQuestionNumbers && (n += i);
              }
              return (
                e.prototype.setVisibleIndex.call(
                  this,
                  'onSurvey' != this.showQuestionNumbers ? t : -1
                ),
                'onSurvey' != this.showQuestionNumbers ? 1 : n - t
              );
            }),
            (t.prototype.setPanelVisibleIndex = function (e, t, n) {
              return n ? e.setVisibleIndex(t) : (e.setVisibleIndex(-1), 0);
            }),
            Object.defineProperty(t.prototype, 'canAddPanel', {
              get: function () {
                return (
                  (!this.survey || !this.survey.isDesignMode) &&
                  this.allowAddPanel &&
                  !this.isReadOnly &&
                  this.panelCount < this.maxPanelCount
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'canRemovePanel', {
              get: function () {
                return (
                  (!this.survey || !this.survey.isDesignMode) &&
                  this.allowRemovePanel &&
                  !this.isReadOnly &&
                  this.panelCount > this.minPanelCount
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.rebuildPanels = function () {
              var e;
              if (!this.isLoadingFromJson) {
                this.prepareValueForPanelCreating();
                var t = [];
                if (this.isDesignMode) new g(this, this.template), t.push(this.template);
                else for (var i = 0; i < this.panelCount; i++) t.push(this.createNewPanel());
                (e = this.panels).splice.apply(e, n.i(r.d)([0, this.panels.length], t)),
                  this.setValueAfterPanelsCreating(),
                  this.setPanelsState(),
                  this.reRunCondition(),
                  this.fireCallback(this.panelCountChangedCallback);
              }
            }),
            Object.defineProperty(t.prototype, 'defaultPanelValue', {
              get: function () {
                return this.getPropertyValue('defaultPanelValue');
              },
              set: function (e) {
                this.setPropertyValue('defaultPanelValue', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'defaultValueFromLastPanel', {
              get: function () {
                return this.getPropertyValue('defaultValueFromLastPanel', !1);
              },
              set: function (e) {
                this.setPropertyValue('defaultValueFromLastPanel', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.isDefaultValueEmpty = function () {
              return (
                e.prototype.isDefaultValueEmpty.call(this) &&
                this.isValueEmpty(this.defaultPanelValue)
              );
            }),
            (t.prototype.setDefaultValue = function () {
              if (
                this.isValueEmpty(this.defaultPanelValue) ||
                !this.isValueEmpty(this.defaultValue)
              )
                return void e.prototype.setDefaultValue.call(this);
              if (this.isEmpty() && 0 != this.panelCount) {
                for (var t = [], n = 0; n < this.panelCount; n++) t.push(this.defaultPanelValue);
                this.value = t;
              }
            }),
            (t.prototype.isEmpty = function () {
              var e = this.value;
              if (!e || !Array.isArray(e)) return !0;
              for (var t = 0; t < e.length; t++) if (!this.isRowEmpty(e[t])) return !1;
              return !0;
            }),
            (t.prototype.getProgressInfo = function () {
              return o.a.getProgressInfoByElements(this.panels, this.isRequired);
            }),
            (t.prototype.isRowEmpty = function (e) {
              for (var t in e) if (e.hasOwnProperty(t)) return !1;
              return !0;
            }),
            (t.prototype.addPanelUI = function () {
              if (!this.canAddPanel) return null;
              var e = this.addPanel();
              return 'list' === this.renderMode && 'default' !== this.panelsState && e.expand(), e;
            }),
            (t.prototype.addPanel = function () {
              this.panelCount++, this.isRenderModeList || (this.currentIndex = this.panelCount - 1);
              var e = this.value,
                t = !1;
              return (
                this.isValueEmpty(this.defaultPanelValue) ||
                  (e &&
                    Array.isArray(e) &&
                    e.length == this.panelCount &&
                    ((t = !0), this.copyValue(e[e.length - 1], this.defaultPanelValue))),
                this.defaultValueFromLastPanel &&
                  e &&
                  Array.isArray(e) &&
                  e.length > 1 &&
                  e.length == this.panelCount &&
                  ((t = !0), this.copyValue(e[e.length - 1], e[e.length - 2])),
                t && (this.value = e),
                this.survey && this.survey.dynamicPanelAdded(this),
                this.panels[this.panelCount - 1]
              );
            }),
            (t.prototype.copyValue = function (e, t) {
              for (var n in t) e[n] = t[n];
            }),
            (t.prototype.removePanelUI = function (e) {
              this.canRemovePanel &&
                ((this.confirmDelete && !n.i(f.c)(this.confirmDeleteText)) || this.removePanel(e));
            }),
            (t.prototype.goToNextPanel = function () {
              ('list' !== this.renderMode && this.currentPanel.hasErrors()) || this.currentIndex++;
            }),
            (t.prototype.goToPrevPanel = function () {
              this.currentIndex--;
            }),
            (t.prototype.removePanel = function (e) {
              var t = this.getPanelIndex(e);
              if (!(t < 0 || t >= this.panelCount)) {
                var n = this.panels[t];
                this.panels.splice(t, 1), this.updateBindings('panelCount', this.panelCount);
                var e = this.value;
                !e ||
                  !Array.isArray(e) ||
                  t >= e.length ||
                  ((this.isValueChangingInternally = !0),
                  e.splice(t, 1),
                  (this.value = e),
                  this.fireCallback(this.panelCountChangedCallback),
                  this.survey && this.survey.dynamicPanelRemoved(this, t, n),
                  (this.isValueChangingInternally = !1));
              }
            }),
            (t.prototype.getPanelIndex = function (e) {
              if (i.a.isNumber(e)) return e;
              for (var t = this.items, n = 0; n < this.panels.length; n++)
                if (this.panels[n] === e || t[n] === e) return n;
              return -1;
            }),
            (t.prototype.locStrsChanged = function () {
              e.prototype.locStrsChanged.call(this);
              for (var t = this.panels, n = 0; n < t.length; n++) t[n].locStrsChanged();
            }),
            (t.prototype.clearIncorrectValues = function () {
              for (var e = 0; e < this.panels.length; e++) this.clearIncorrectValuesInPanel(e);
            }),
            (t.prototype.clearErrors = function () {
              e.prototype.clearErrors.call(this);
              for (var t = 0; t < this.panels.length; t++) this.panels[t].clearErrors();
            }),
            (t.prototype.getQuestionFromArray = function (e, t) {
              return t >= this.panelCount ? null : this.panels[t].getQuestionByName(e);
            }),
            (t.prototype.clearIncorrectValuesInPanel = function (e) {
              var t = this.panels[e];
              t.clearIncorrectValues();
              var n = this.value,
                r = n && e < n.length ? n[e] : null;
              if (r) {
                var i = !1;
                for (var o in r)
                  if (!this.getSharedQuestionFromArray(o, e)) {
                    var a = t.getQuestionByName(o);
                    a ||
                      this.iscorrectValueWithPostPrefix(t, o, h.a.commentPrefix) ||
                      this.iscorrectValueWithPostPrefix(t, o, h.a.matrixTotalValuePostFix) ||
                      (delete r[o], (i = !0));
                  }
                i && ((n[e] = r), (this.value = n));
              }
            }),
            (t.prototype.iscorrectValueWithPostPrefix = function (e, t, n) {
              return (
                t.indexOf(n) === t.length - n.length &&
                !!e.getQuestionByName(t.substr(0, t.indexOf(n)))
              );
            }),
            (t.prototype.getSharedQuestionFromArray = function (e, t) {
              return this.survey && this.valueName
                ? this.survey.getQuestionByValueNameFromArray(this.valueName, e, t)
                : null;
            }),
            (t.prototype.addConditionObjectsByContext = function (e, t) {
              for (
                var n = !!t && this.template.questions.indexOf(t) > -1,
                  r = this.getValueName() + '[0].',
                  i = this.processedTitle + '[0].',
                  o = new Array(),
                  a = this.template.questions,
                  s = 0;
                s < a.length;
                s++
              )
                a[s].addConditionObjectsByContext(o, t);
              for (var s = 0; s < o.length; s++)
                e.push({ name: r + o[s].name, text: i + o[s].text, question: o[s].question });
              if (n)
                for (var s = 0; s < o.length; s++)
                  o[s].question != t &&
                    e.push({
                      name: 'panel.' + o[s].name,
                      text: 'panel.' + o[s].text,
                      question: o[s].question,
                    });
            }),
            (t.prototype.getConditionJson = function (t, n) {
              if ((void 0 === t && (t = null), void 0 === n && (n = null), !n))
                return e.prototype.getConditionJson.call(this, t, n);
              var r = n,
                i = n.indexOf('.');
              i > -1 && ((r = n.substr(0, i)), (n = n.substr(i + 1)));
              var o = this.template.getQuestionByName(r);
              return o ? o.getConditionJson(t, n) : null;
            }),
            (t.prototype.onReadOnlyChanged = function () {
              var t = this.isReadOnly;
              this.template.readOnly = t;
              for (var n = 0; n < this.panels.length; n++) this.panels[n].readOnly = t;
              e.prototype.onReadOnlyChanged.call(this);
            }),
            (t.prototype.onSurveyLoad = function () {
              (this.template.readOnly = this.isReadOnly),
                this.template.onSurveyLoad(),
                this.loadingPanelCount > 0 && (this.panelCount = this.loadingPanelCount),
                this.isDesignMode && this.rebuildPanels(),
                this.setPanelsSurveyImpl(),
                this.setPanelsState(),
                this.assignOnPropertyChangedToTemplate(),
                e.prototype.onSurveyLoad.call(this);
            }),
            (t.prototype.onFirstRendering = function () {
              this.template.onFirstRendering();
              for (var t = 0; t < this.panels.length; t++) this.panels[t].onFirstRendering();
              e.prototype.onFirstRendering.call(this);
            }),
            (t.prototype.runCondition = function (t, n) {
              e.prototype.runCondition.call(this, t, n), this.runPanelsCondition(t, n);
            }),
            (t.prototype.reRunCondition = function () {
              this.data &&
                this.runCondition(this.getDataFilteredValues(), this.getDataFilteredProperties());
            }),
            (t.prototype.runPanelsCondition = function (e, t) {
              var n = {};
              e && e instanceof Object && (n = JSON.parse(JSON.stringify(e)));
              for (var r = 0; r < this.panels.length; r++) {
                var o = this.getPanelItemData(this.panels[r].data),
                  a = i.a.createCopy(n);
                (a[g.ItemVariableName.toLowerCase()] = o),
                  (a[g.IndexVariableName.toLowerCase()] = r),
                  this.panels[r].runCondition(a, t);
              }
            }),
            (t.prototype.onAnyValueChanged = function (t) {
              e.prototype.onAnyValueChanged.call(this, t);
              for (var n = 0; n < this.panels.length; n++)
                this.panels[n].onAnyValueChanged(t),
                  this.panels[n].onAnyValueChanged(g.ItemVariableName);
            }),
            (t.prototype.hasKeysDuplicated = function (e, t) {
              void 0 === t && (t = null);
              for (var n, r = [], i = 0; i < this.panels.length; i++)
                n = this.isValueDuplicated(this.panels[i], r, t, e) || n;
              return n;
            }),
            (t.prototype.updatePanelsContainsErrors = function () {
              for (var e = this.changingValueQuestion, t = e.parent; t; )
                t.updateContainsErrors(), (t = t.parent);
              this.updateContainsErrors();
            }),
            (t.prototype.hasErrors = function (t, n) {
              if (
                (void 0 === t && (t = !0),
                void 0 === n && (n = null),
                this.isValueChangingInternally)
              )
                return !1;
              var r = !1;
              if (this.changingValueQuestion) {
                var r = this.changingValueQuestion.hasErrors(t, n);
                return (
                  (r = this.hasKeysDuplicated(t, n) || r), this.updatePanelsContainsErrors(), r
                );
              }
              var i = this.hasErrorInPanels(t, n);
              return e.prototype.hasErrors.call(this, t) || i;
            }),
            (t.prototype.getContainsErrors = function () {
              var t = e.prototype.getContainsErrors.call(this);
              if (t) return t;
              for (var n = this.panels, r = 0; r < n.length; r++)
                if (n[r].containsErrors) return !0;
              return !1;
            }),
            (t.prototype.getIsAnswered = function () {
              if (!e.prototype.getIsAnswered.call(this)) return !1;
              for (var t = this.panels, n = 0; n < t.length; n++) {
                var r = [];
                t[n].addQuestionsToList(r, !0);
                for (var i = 0; i < r.length; i++) if (!r[i].isAnswered) return !1;
              }
              return !0;
            }),
            (t.prototype.clearValueIfInvisible = function () {
              for (var t = 0; t < this.panels.length; t++)
                for (var n = this.panels[t].questions, r = 0; r < n.length; r++)
                  n[r].clearValueIfInvisible();
              e.prototype.clearValueIfInvisible.call(this);
            }),
            (t.prototype.getIsRunningValidators = function () {
              if (e.prototype.getIsRunningValidators.call(this)) return !0;
              for (var t = 0; t < this.panels.length; t++)
                for (var n = this.panels[t].questions, r = 0; r < n.length; r++)
                  if (n[r].isRunningValidators) return !0;
              return !1;
            }),
            (t.prototype.getAllErrors = function () {
              for (var t = e.prototype.getAllErrors.call(this), n = 0; n < this.panels.length; n++)
                for (var r = this.panels[n].questions, i = 0; i < r.length; i++) {
                  var o = r[i].getAllErrors();
                  o && o.length > 0 && (t = t.concat(o));
                }
              return t;
            }),
            (t.prototype.getDisplayValueCore = function (e, t) {
              var n = this.createValueCopy();
              if (!n || !Array.isArray(n)) return n;
              for (var r = 0; r < this.panels.length && r < n.length; r++) {
                var i = n[r];
                i && (n[r] = this.getPanelDisplayValue(r, i, e));
              }
              return n;
            }),
            (t.prototype.getPanelDisplayValue = function (e, t, n) {
              if (!t) return t;
              var r = this.panels[e];
              for (var i in t) {
                var o = r.getQuestionByValueName(i);
                o || (o = this.getSharedQuestionFromArray(i, e)),
                  o && (t[i] = o.getDisplayValue(n));
              }
              return t;
            }),
            (t.prototype.hasErrorInPanels = function (e, t) {
              for (var n = !1, r = this.panels, i = [], o = 0; o < r.length; o++)
                this.setOnCompleteAsyncInPanel(r[o]);
              for (var o = 0; o < r.length; o++) {
                var a = r[o].hasErrors(e, !!t && t.focuseOnFirstError, t);
                (a = this.isValueDuplicated(r[o], i, t) || a),
                  this.isRenderModeList || !a || n || (this.currentIndex = o),
                  (n = a || n);
              }
              return n;
            }),
            (t.prototype.setOnCompleteAsyncInPanel = function (e) {
              for (var t = this, n = e.questions, r = 0; r < n.length; r++)
                n[r].onCompletedAsyncValidators = function (e) {
                  t.raiseOnCompletedAsyncValidators();
                };
            }),
            (t.prototype.isValueDuplicated = function (e, t, n, r) {
              if (!this.keyName) return !1;
              var i = e.getQuestionByValueName(this.keyName);
              if (!i || i.isEmpty()) return !1;
              var o = i.value;
              this.changingValueQuestion && i != this.changingValueQuestion && i.hasErrors(r, n);
              for (var a = 0; a < t.length; a++)
                if (o == t[a])
                  return (
                    i.addError(new d.h(this.keyDuplicationError, this)),
                    n && !n.firstErrorQuestion && (n.firstErrorQuestion = i),
                    !0
                  );
              return t.push(o), !1;
            }),
            (t.prototype.createNewPanel = function () {
              var e = this.createAndSetupNewPanelObject(),
                t = this.template.toJSON();
              new c.c().toObject(t, e),
                (e.renderWidth = '100%'),
                e.updateCustomWidgets(),
                new g(this, e),
                e.onFirstRendering();
              for (var n = e.questions, r = 0; r < n.length; r++) n[r].setParentQuestion(this);
              return e;
            }),
            (t.prototype.createAndSetupNewPanelObject = function () {
              var e = this.createNewPanelObject(),
                t = this;
              return (
                (e.onGetQuestionTitleLocation = function () {
                  return t.getTemplateQuestionTitleLocation();
                }),
                e
              );
            }),
            (t.prototype.getTemplateQuestionTitleLocation = function () {
              return 'default' != this.templateTitleLocation
                ? this.templateTitleLocation
                : this.getTitleLocationCore();
            }),
            (t.prototype.createNewPanelObject = function () {
              return new u.b();
            }),
            (t.prototype.setPanelCountBasedOnValue = function () {
              if (!this.isValueChangingInternally && !this.isDesignMode) {
                var e = this.value,
                  t = e && Array.isArray(e) ? e.length : 0;
                0 == t && this.loadingPanelCount > 0 && (t = this.loadingPanelCount),
                  (this.panelCount = t);
              }
            }),
            (t.prototype.setQuestionValue = function (t) {
              e.prototype.setQuestionValue.call(this, t, !1), this.setPanelCountBasedOnValue();
              for (var n = 0; n < this.panels.length; n++)
                this.panelUpdateValueFromSurvey(this.panels[n]);
              this.updateIsAnswered();
            }),
            (t.prototype.onSurveyValueChanged = function (t) {
              e.prototype.onSurveyValueChanged.call(this, t);
              for (var n = 0; n < this.panels.length; n++)
                this.panelSurveyValueChanged(this.panels[n]);
              void 0 === t && this.setValueBasedOnPanelCount();
            }),
            (t.prototype.panelUpdateValueFromSurvey = function (e) {
              for (
                var t = e.questions, n = this.getPanelItemData(e.data), r = 0;
                r < t.length;
                r++
              ) {
                var i = t[r];
                i.updateValueFromSurvey(n[i.getValueName()]),
                  i.updateCommentFromSurvey(n[i.getValueName() + h.a.commentPrefix]);
              }
            }),
            (t.prototype.panelSurveyValueChanged = function (e) {
              for (
                var t = e.questions, n = this.getPanelItemData(e.data), r = 0;
                r < t.length;
                r++
              ) {
                var i = t[r];
                i.onSurveyValueChanged(n[i.getValueName()]);
              }
            }),
            (t.prototype.onSetData = function () {
              e.prototype.onSetData.call(this),
                this.isDesignMode && (this.setTemplatePanelSurveyImpl(), this.rebuildPanels());
            }),
            (t.prototype.getItemIndex = function (e) {
              return this.items.indexOf(e);
            }),
            (t.prototype.getPanelItemData = function (e) {
              var t = this.items,
                n = t.indexOf(e),
                r = this.value;
              return (
                n < 0 && Array.isArray(r) && r.length > t.length && (n = t.length),
                n < 0 ? {} : !r || !Array.isArray(r) || r.length <= n ? {} : r[n]
              );
            }),
            (t.prototype.setPanelItemData = function (e, t, n) {
              var r = this.items,
                i = r.indexOf(e);
              i < 0 && (i = r.length);
              var o = this.getUnbindValue(this.value);
              if (((o && Array.isArray(o)) || (o = []), o.length <= i))
                for (var a = o.length; a <= i; a++) o.push({});
              if (
                (o[i] || (o[i] = {}),
                this.isValueEmpty(n) ? delete o[i][t] : (o[i][t] = n),
                i >= 0 &&
                  i < this.panels.length &&
                  (this.changingValueQuestion = this.panels[i].getQuestionByValueName(t)),
                (this.value = o),
                (this.changingValueQuestion = null),
                this.survey)
              ) {
                var s = {
                  question: this,
                  panel: e.panel,
                  name: t,
                  itemIndex: i,
                  itemValue: o[i],
                  value: n,
                };
                this.survey.dynamicPanelItemValueChanged(this, s);
              }
            }),
            (t.prototype.getSurvey = function () {
              return this.survey;
            }),
            (t.prototype.getRootData = function () {
              return this.data;
            }),
            (t.prototype.getPlainData = function (t) {
              void 0 === t && (t = { includeEmpty: !0 });
              var n = e.prototype.getPlainData.call(this, t);
              return (
                n &&
                  ((n.isNode = !0),
                  (n.data = this.panels.map(function (e, n) {
                    var r = {
                      name: e.name || n,
                      title: e.title || 'Panel',
                      value: e.getValue(),
                      displayValue: e.getValue(),
                      getString: function (e) {
                        return 'object' == typeof e ? JSON.stringify(e) : e;
                      },
                      isNode: !0,
                      data: e.questions
                        .map(function (e) {
                          return e.getPlainData(t);
                        })
                        .filter(function (e) {
                          return !!e;
                        }),
                    };
                    return (
                      (t.calculations || []).forEach(function (t) {
                        r[t.propertyName] = e[t.propertyName];
                      }),
                      r
                    );
                  }))),
                n
              );
            }),
            (t.prototype.updateElementCss = function () {
              e.prototype.updateElementCss.call(this);
              for (var t = 0; t < this.panels.length; t++) {
                this.panels[t].updateElementCss();
              }
            }),
            Object.defineProperty(t.prototype, 'progressText', {
              get: function () {
                var e = this.panelCount;
                return a.a.getString('panelDynamicProgressText').format(this.currentIndex + 1, e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(l.a);
      c.b.addClass(
        'paneldynamic',
        [
          {
            name: 'templateElements',
            alternativeName: 'questions',
            visible: !1,
            isLightSerializable: !1,
          },
          { name: 'templateTitle:text', serializationProperty: 'locTemplateTitle' },
          { name: 'templateDescription:text', serializationProperty: 'locTemplateDescription' },
          { name: 'allowAddPanel:boolean', default: !0 },
          { name: 'allowRemovePanel:boolean', default: !0 },
          {
            name: 'panelCount:number',
            isBindable: !0,
            default: 0,
            choices: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
          },
          { name: 'minPanelCount:number', default: 0, minValue: 0 },
          { name: 'maxPanelCount:number', default: h.a.panelMaximumPanelCount },
          'defaultPanelValue:panelvalue',
          'defaultValueFromLastPanel:boolean',
          {
            name: 'panelsState',
            default: 'default',
            choices: ['default', 'collapsed', 'expanded', 'firstExpanded'],
          },
          { name: 'keyName' },
          { name: 'keyDuplicationError', serializationProperty: 'locKeyDuplicationError' },
          { name: 'confirmDelete:boolean' },
          { name: 'confirmDeleteText', serializationProperty: 'locConfirmDeleteText' },
          { name: 'panelAddText', serializationProperty: 'locPanelAddText' },
          { name: 'panelRemoveText', serializationProperty: 'locPanelRemoveText' },
          { name: 'panelPrevText', serializationProperty: 'locPanelPrevText' },
          { name: 'panelNextText', serializationProperty: 'locPanelNextText' },
          { name: 'showQuestionNumbers', default: 'off', choices: ['off', 'onPanel', 'onSurvey'] },
          { name: 'showRangeInProgress:boolean', default: !0 },
          {
            name: 'renderMode',
            default: 'list',
            choices: ['list', 'progressTop', 'progressBottom', 'progressTopBottom'],
          },
          {
            name: 'templateTitleLocation',
            default: 'default',
            choices: ['default', 'top', 'bottom', 'left'],
          },
        ],
        function () {
          return new v('');
        },
        'question'
      ),
        p.b.Instance.registerQuestion('paneldynamic', function (e) {
          return new v(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(3),
        a = n(23),
        s = n(2),
        l = n(11);
      n.d(t, 'a', function () {
        return u;
      });
      var u = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          return (n.name = t), n;
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getType = function () {
            return 'radiogroup';
          }),
          (t.prototype.getFirstInputElementId = function () {
            return this.inputId + '_0';
          }),
          Object.defineProperty(t.prototype, 'selectedItem', {
            get: function () {
              return this.isEmpty() ? null : l.a.getItemByValue(this.visibleChoices, this.value);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'showClearButton', {
            get: function () {
              return this.getPropertyValue('showClearButton', !1);
            },
            set: function (e) {
              this.setPropertyValue('showClearButton', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'canShowClearButton', {
            get: function () {
              return this.showClearButton && !this.isReadOnly;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'clearButtonCaption', {
            get: function () {
              return s.a.getString('clearCaption');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.supportGoNextPageAutomatic = function () {
            return !0;
          }),
          (t.prototype.getItemClass = function (e) {
            var t = this.cssClasses.item,
              n = this.isReadOnly || !e.isEnabled,
              r =
                e.value === this.value ||
                (this.isOtherSelected && this.otherItem.value === e.value),
              i = !n && !r;
            return (
              this.hasColumns ||
                (t +=
                  0 === this.colCount
                    ? ' ' + this.cssClasses.itemInline
                    : ' sv-q-col-' + this.colCount),
              n && this.cssClasses.itemDisabled && (t += ' ' + this.cssClasses.itemDisabled),
              r && this.cssClasses.itemChecked && (t += ' ' + this.cssClasses.itemChecked),
              i && this.cssClasses.itemHover && (t += ' ' + this.cssClasses.itemHover),
              t
            );
          }),
          t
        );
      })(a.a);
      i.b.addClass(
        'radiogroup',
        [{ name: 'showClearButton:boolean', default: !1 }],
        function () {
          return new u('');
        },
        'checkboxbase'
      ),
        o.b.Instance.registerQuestion('radiogroup', function (e) {
          var t = new u(e);
          return (t.choices = o.b.DefaultChoices), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(11),
        o = n(9),
        a = n(1),
        s = n(3),
        l = n(7);
      n.d(t, 'a', function () {
        return u;
      });
      var u = (function (e) {
        function t(t) {
          var n = e.call(this, t) || this;
          (n.name = t), n.createItemValues('rates');
          var r = n;
          n.registerFunctionOnPropertyValueChanged('rates', function () {
            r.fireCallback(r.rateValuesChangedCallback);
          }),
            n.onPropertyChanged.add(function (e, t) {
              ('rateMin' != t.name && 'rateMax' != t.name && 'rateStep' != t.name) ||
                r.fireCallback(r.rateValuesChangedCallback);
            });
          var i = n.createLocalizableString('minRateDescription', n, !0),
            o = n.createLocalizableString('maxRateDescription', n, !0);
          return (
            (i.onGetTextCallback = function (e) {
              return e ? e + ' ' : e;
            }),
            (o.onGetTextCallback = function (e) {
              return e ? ' ' + e : e;
            }),
            n
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.onSurveyLoad = function () {
            e.prototype.onSurveyLoad.call(this), this.fireCallback(this.rateValuesChangedCallback);
          }),
          Object.defineProperty(t.prototype, 'rateValues', {
            get: function () {
              return this.getPropertyValue('rates');
            },
            set: function (e) {
              this.setPropertyValue('rates', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'rateMin', {
            get: function () {
              return this.getPropertyValue('rateMin');
            },
            set: function (e) {
              !this.isLoadingFromJson &&
                e > this.rateMax - this.rateStep &&
                (e = this.rateMax - this.rateStep),
                this.setPropertyValue('rateMin', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'rateMax', {
            get: function () {
              return this.getPropertyValue('rateMax');
            },
            set: function (e) {
              !this.isLoadingFromJson &&
                e < this.rateMin + this.rateStep &&
                (e = this.rateMin + this.rateStep),
                this.setPropertyValue('rateMax', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'rateStep', {
            get: function () {
              return this.getPropertyValue('rateStep');
            },
            set: function (e) {
              e <= 0 && (e = 1),
                !this.isLoadingFromJson &&
                  e > this.rateMax - this.rateMin &&
                  (e = this.rateMax - this.rateMin),
                this.setPropertyValue('rateStep', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.getDisplayValueCore = function (e, t) {
            var n = i.a.getTextOrHtmlByValue(this.visibleRateValues, t);
            return n || t;
          }),
          Object.defineProperty(t.prototype, 'visibleRateValues', {
            get: function () {
              if (this.rateValues.length > 0) return this.rateValues;
              for (
                var e = [], t = this.rateMin, n = this.rateStep;
                t <= this.rateMax && e.length < l.a.ratingMaximumRateValueCount;

              )
                e.push(new i.a(t)), (t = this.correctValue(t + n, n));
              return e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.correctValue = function (e, t) {
            if (!e) return e;
            if (Math.round(e) == e) return e;
            for (var n = 0; Math.round(t) != t; ) (t *= 10), n++;
            return parseFloat(e.toFixed(n));
          }),
          (t.prototype.getType = function () {
            return 'rating';
          }),
          (t.prototype.getFirstInputElementId = function () {
            return this.inputId + '_0';
          }),
          (t.prototype.supportGoNextPageAutomatic = function () {
            return !0;
          }),
          (t.prototype.supportComment = function () {
            return !0;
          }),
          (t.prototype.supportOther = function () {
            return !0;
          }),
          Object.defineProperty(t.prototype, 'minRateDescription', {
            get: function () {
              return this.getLocalizableStringText('minRateDescription');
            },
            set: function (e) {
              this.setLocalizableStringText('minRateDescription', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locMinRateDescription', {
            get: function () {
              return this.getLocalizableString('minRateDescription');
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'maxRateDescription', {
            get: function () {
              return this.getLocalizableStringText('maxRateDescription');
            },
            set: function (e) {
              this.setLocalizableStringText('maxRateDescription', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locMaxRateDescription', {
            get: function () {
              return this.getLocalizableString('maxRateDescription');
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.valueToData = function (e) {
            if (this.rateValues.length > 0) {
              var t = i.a.getItemByValue(this.rateValues, e);
              return t ? t.value : e;
            }
            return isNaN(e) ? e : parseFloat(e);
          }),
          t
        );
      })(o.a);
      a.b.addClass(
        'rating',
        [
          { name: 'hasComment:switch', layout: 'row' },
          {
            name: 'commentText',
            dependsOn: 'hasComment',
            visibleIf: function (e) {
              return e.hasComment;
            },
            serializationProperty: 'locCommentText',
            layout: 'row',
          },
          { name: 'rateValues:itemvalue[]' },
          { name: 'rateMin:number', default: 1 },
          { name: 'rateMax:number', default: 5 },
          { name: 'rateStep:number', default: 1, minValue: 0.1 },
          {
            name: 'minRateDescription',
            alternativeName: 'mininumRateDescription',
            serializationProperty: 'locMinRateDescription',
          },
          {
            name: 'maxRateDescription',
            alternativeName: 'maximumRateDescription',
            serializationProperty: 'locMaxRateDescription',
          },
        ],
        function () {
          return new u('');
        },
        'question'
      ),
        s.b.Instance.registerQuestion('rating', function (e) {
          return new u(e);
        });
    },
    function (e, t, n) {
      'use strict';
      function r(e) {
        var t = e.getContext('2d'),
          n = window.devicePixelRatio || 1,
          r =
            t.webkitBackingStorePixelRatio ||
            t.mozBackingStorePixelRatio ||
            t.msBackingStorePixelRatio ||
            t.oBackingStorePixelRatio ||
            t.backingStorePixelRatio ||
            1,
          i = n / r,
          o = e.width,
          a = e.height;
        (e.width = o * i),
          (e.height = a * i),
          (e.style.width = o + 'px'),
          (e.style.height = a + 'px'),
          t.scale(i, i);
      }
      var i = n(0),
        o = n(1),
        a = n(9),
        s = n(2),
        l = n(121),
        u = n(3);
      n.d(t, 'a', function () {
        return d;
      });
      var c = 300,
        p = 200,
        d = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(i.b)(t, e),
            (t.prototype.getCssRoot = function (t) {
              var n = e.prototype.getCssRoot.call(this, t);
              return '' + this.width == '300' && (n += ' ' + t.small), n;
            }),
            (t.prototype.updateValue = function () {
              if (this.signaturePad) {
                var e = this.signaturePad.toDataURL(this.dataFormat);
                this.value = e;
              }
            }),
            (t.prototype.getType = function () {
              return 'signaturepad';
            }),
            (t.prototype.afterRenderQuestionElement = function (t) {
              t && this.initSignaturePad(t), e.prototype.afterRenderQuestionElement.call(this, t);
            }),
            (t.prototype.beforeDestroyQuestionElement = function (e) {
              e && this.destroySignaturePad(e);
            }),
            (t.prototype.initSignaturePad = function (e) {
              var t = this,
                n = e.getElementsByTagName('canvas')[0],
                i = e.getElementsByTagName('button')[0],
                o = new l.a(n, { backgroundColor: '#ffffff' });
              this.isReadOnly && o.off(),
                (i.onclick = function () {
                  t.value = void 0;
                }),
                (this.readOnlyChangedCallback = function () {
                  !t.allowClear || t.isReadOnly
                    ? (o.off(), (i.style.display = 'none'))
                    : (o.on(), (i.style.display = 'block'));
                }),
                (o.penColor = this.penColor),
                (o.onBegin = function () {
                  n.focus();
                }),
                (o.onEnd = function () {
                  t.updateValue();
                });
              var a = function () {
                var e = t.value;
                (n.width = t.width || c),
                  (n.height = t.height || p),
                  r(n),
                  e ? o.fromDataURL(e) : o.clear();
              };
              (this.valueChangedCallback = a),
                a(),
                this.readOnlyChangedCallback(),
                (this.signaturePad = o);
              var s = function (e, t) {
                ('width' !== t.name && 'height' !== t.name) || a();
              };
              this.onPropertyChanged.add(s), (this.signaturePad.propertyChangedHandler = s);
            }),
            (t.prototype.destroySignaturePad = function (e) {
              this.signaturePad &&
                (this.onPropertyChanged.remove(this.signaturePad.propertyChangedHandler),
                this.signaturePad.off()),
                (this.readOnlyChangedCallback = null),
                (this.signaturePad = null);
            }),
            Object.defineProperty(t.prototype, 'dataFormat', {
              get: function () {
                return this.getPropertyValue('dataFormat', '');
              },
              set: function (e) {
                this.setPropertyValue('dataFormat', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'width', {
              get: function () {
                return this.getPropertyValue('width', 300);
              },
              set: function (e) {
                this.setPropertyValue('width', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'height', {
              get: function () {
                return this.getPropertyValue('height', 200);
              },
              set: function (e) {
                this.setPropertyValue('height', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'allowClear', {
              get: function () {
                return this.getPropertyValue('allowClear', !0);
              },
              set: function (e) {
                this.setPropertyValue('allowClear', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'penColor', {
              get: function () {
                return this.getPropertyValue('penColor', '#1ab394');
              },
              set: function (e) {
                this.setPropertyValue('penColor', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'clearButtonCaption', {
              get: function () {
                return s.a.getString('clearCaption');
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(a.a);
      o.b.addClass(
        'signaturepad',
        [
          { name: 'width:number', category: 'general', default: 300 },
          { name: 'height:number', category: 'general', default: 200 },
          { name: 'allowClear:boolean', category: 'general', default: !0 },
          { name: 'penColor:color', category: 'general', default: '#1ab394' },
          {
            name: 'dataFormat',
            category: 'general',
            default: '',
            choices: [
              { value: '', text: 'PNG' },
              { value: 'image/jpeg', text: 'JPEG' },
              { value: 'image/svg+xml', text: 'SVG' },
            ],
          },
          { name: 'defaultValue', visible: !1 },
          { name: 'correctAnswer', visible: !1 },
        ],
        function () {
          return new d('');
        },
        'question'
      ),
        u.b.Instance.registerQuestion('signaturepad', function (e) {
          return new d(e);
        });
    },
    function (e, t, n) {
      'use strict';
      n.d(t, 'a', function () {
        return r;
      });
      var r = (function () {
        function e(e) {
          this.survey = e;
        }
        return (
          (e.prototype.getListElementCss = function (e) {
            if (!(e >= this.survey.visiblePages.length)) {
              var t = this.survey.visiblePages[e].passed
                ? this.survey.css.progressButtonsListElementPassed
                : '';
              return (
                this.survey.currentPageNo === e &&
                  ((t += t ? ' ' : ''), (t += this.survey.css.progressButtonsListElementCurrent)),
                t
              );
            }
          }),
          (e.prototype.clickListElement = function (e) {
            if (!this.survey.isDesignMode)
              if (e < this.survey.currentPageNo) this.survey.currentPageNo = e;
              else if (e > this.survey.currentPageNo)
                for (var t = this.survey.currentPageNo; t < e && this.survey.nextPage(); t++)
                  this.survey.visiblePages[t].passed = !0;
          }),
          e
        );
      })();
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(6),
        o = n(21);
      n.d(t, 'a', function () {
        return a;
      });
      var a = (function (e) {
        function t(t, n) {
          void 0 === n && (n = null);
          var r = e.call(this) || this;
          (r.closeOnCompleteTimeout = 0),
            (r.surveyValue = n || r.createSurvey(t)),
            (r.surveyValue.showTitle = !1),
            'undefined' != typeof document && (r.windowElement = document.createElement('div'));
          var i = r;
          return (
            r.survey.onComplete.add(function (e, t) {
              i.onSurveyComplete();
            }),
            r
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.getType = function () {
            return 'window';
          }),
          Object.defineProperty(t.prototype, 'survey', {
            get: function () {
              return this.surveyValue;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'isShowing', {
            get: function () {
              return this.getPropertyValue('isShowing', !1);
            },
            set: function (e) {
              this.isShowing != e &&
                (this.setPropertyValue('isShowing', e),
                this.showingChangedCallback && this.showingChangedCallback());
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.show = function () {
            this.isShowing = !0;
          }),
          (t.prototype.hide = function () {
            this.isShowing = !1;
          }),
          Object.defineProperty(t.prototype, 'isExpanded', {
            get: function () {
              return this.getPropertyValue('isExpanded', !1);
            },
            set: function (e) {
              this.setPropertyValue('isExpanded', e),
                !this.isLoadingFromJson &&
                  this.expandedChangedCallback &&
                  this.expandedChangedCallback();
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'title', {
            get: function () {
              return this.survey.title;
            },
            set: function (e) {
              this.survey.title = e;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'locTitle', {
            get: function () {
              return this.survey.locTitle;
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.expand = function () {
            this.expandcollapse(!0);
          }),
          (t.prototype.collapse = function () {
            this.expandcollapse(!1);
          }),
          (t.prototype.createSurvey = function (e) {
            return new o.a(e);
          }),
          (t.prototype.expandcollapse = function (e) {
            this.isExpanded = e;
          }),
          (t.prototype.onSurveyComplete = function () {
            if (!(this.closeOnCompleteTimeout < 0))
              if (0 == this.closeOnCompleteTimeout) this.closeWindowOnComplete();
              else {
                var e = this,
                  t = null,
                  n = function () {
                    e.closeWindowOnComplete(),
                      'undefined' != typeof window && window.clearInterval(t);
                  };
                t =
                  'undefined' != typeof window
                    ? window.setInterval(n, 1e3 * this.closeOnCompleteTimeout)
                    : 0;
              }
          }),
          (t.prototype.closeWindowOnComplete = function () {
            this.closeWindowOnCompleteCallback && this.closeWindowOnCompleteCallback();
          }),
          (t.surveyElementName = 'windowSurveyJS'),
          t
        );
      })(i.b);
    },
    function (e, t, n) {
      'use strict';
      var r = n(6);
      n.d(t, 'a', function () {
        return o;
      });
      var i = {
          setTimeout: function (e) {
            return 'undefined' == typeof window ? 0 : window.setTimeout(e, 1e3);
          },
          clearTimeout: function (e) {
            'undefined' != typeof window && window.clearTimeout(e);
          },
        },
        o = (function () {
          function e() {
            (this.listenerCounter = 0), (this.timerId = -1), (this.onTimer = new r.c());
          }
          return (
            Object.defineProperty(e, 'instance', {
              get: function () {
                return e.instanceValue || (e.instanceValue = new e()), e.instanceValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.start = function (e) {
              var t = this;
              void 0 === e && (e = null),
                e && this.onTimer.add(e),
                this.timerId < 0 &&
                  (this.timerId = i.setTimeout(function () {
                    t.doTimer();
                  })),
                this.listenerCounter++;
            }),
            (e.prototype.stop = function (e) {
              void 0 === e && (e = null),
                e && this.onTimer.remove(e),
                0 == --this.listenerCounter &&
                  this.timerId > -1 &&
                  (i.clearTimeout(this.timerId), (this.timerId = -1));
            }),
            (e.prototype.doTimer = function () {
              var e = this;
              this.timerId < 0 ||
                (this.onTimer.fire(this, {}),
                (this.timerId = i.setTimeout(function () {
                  e.doTimer();
                })));
            }),
            (e.instanceValue = null),
            e
          );
        })();
    },
    function (e, t, n) {
      'use strict';
      var r = (n(41), n(40), n(0), n(14), n(37), n(38), n(39), n(20));
      n.d(t, 'a', function () {
        return r.b;
      });
      var i =
        (n(15),
        n(22),
        n(139),
        n(8),
        n(17),
        n(141),
        n(142),
        n(144),
        n(147),
        n(148),
        n(151),
        n(46),
        n(152),
        n(154),
        n(153),
        n(155),
        n(156),
        n(47),
        n(140),
        n(145),
        n(146),
        n(150),
        n(138));
      n.d(t, 'b', function () {
        return i.a;
      });
      var o =
          (n(48),
          n(149),
          n(157),
          n(143),
          n(129),
          n(130),
          n(131),
          n(132),
          n(134),
          n(135),
          n(136),
          n(137),
          n(19),
          n(5)),
        a = (n.n(o), n(21));
      n.i(r.a)(o, a.a.platform);
    },
    function (e, t) {},
    function (e, t) {},
    function (e, t) {
      e.exports =
        '<button\n  class="sv-action-bar-item sv-action-bar-item-dropdown"\n  data-bind="click: action, css: { \'sv-action-bar-item--active\': !!ko.unwrap($data.isActive) }, class: $data.innerCss, attr: { disabled: $data.enabled !== undefined && !ko.unwrap($data.enabled), title: $data.tooltip || $data.title}"\n>\n  \x3c!-- ko if: $data.icon --\x3e\n  <svc-svg-icon\n    class="sv-action-bar-item__icon"\n    params="iconName: icon, size: 24"\n  ></svc-svg-icon>\n  \x3c!-- /ko --\x3e\n  \x3c!-- ko if: $data.showTitle === undefined || (!!$data.icon && ko.unwrap($data.showTitle)) --\x3e\n  <span class="sv-action-bar-item__title" data-bind="text: title, css: { \'sv-action-bar-item__title--with-icon\': !!$data.icon }"></span>\n  \x3c!-- /ko --\x3e\n  <sv-popup params= "contentComponentName: name, contentComponentData: model, isVisible: isVisible, verticalPosition: verticalPosition, horizontalPosition: horizontalPosition, showPointer: showPointer"></sv-popup>\n</button>\n';
    },
    function (e, t) {
      e.exports =
        '<button\n  class="sv-action-bar-item sv-action-bar-item-modal"\n  data-bind="click: function(){koIsVisible(true);}"\n>\n  \x3c!-- ko if: icon --\x3e\n  <svc-svg-icon\n    class="sv-action-bar-item__icon"\n    params="iconName: icon, size: 24"\n  ></svc-svg-icon>\n  \x3c!-- /ko --\x3e\n\n  <sv-popup\n    params="contentComponentName: contentComponentName, contentComponentData: contentComponentData, isVisible: koIsVisible, isModal: true, onCancel: onCancel, onApply: onApply, onShow: onShow, contentTemplateName: contentTemplateName, horizontalPosition: \'left\'"\n  >\n  </sv-popup>\n</button>\n';
    },
    function (e, t) {
      e.exports =
        '<button\n  class="sv-action-bar-item"\n  data-bind="click: action, css: { \'sv-action-bar-item--active\': !!ko.unwrap($data.isActive) }, class: $data.innerCss, attr: { disabled: $data.enabled !== undefined && !ko.unwrap($data.enabled), title: $data.tooltip || $data.title}"\n>\n  \x3c!-- ko if: $data.icon --\x3e\n  <svc-svg-icon\n    class="sv-action-bar-item__icon"\n    params="iconName: icon, size: 24"\n  ></svc-svg-icon>\n  \x3c!-- /ko --\x3e\n  \x3c!-- ko if: $data.showTitle === undefined || (!!$data.icon && ko.unwrap($data.showTitle)) --\x3e\n  <span class="sv-action-bar-item__title" data-bind="text: title, css: { \'sv-action-bar-item__title--with-icon\': !!$data.icon }"></span>\n  \x3c!-- /ko --\x3e\n</button>\n';
    },
    function (e, t) {
      e.exports = '<div class="sv-action-bar-separator" data-bind="css: $data.css"></div>';
    },
    function (e, t) {
      e.exports =
        '\x3c!-- ko if: hasItems--\x3e\n\x3c!-- ko foreach: items--\x3e\n<span class="sv-action"\n  data-bind="css: $data.css, visible: $data.visible === undefined || !!ko.unwrap($data.visible), attr: { id: $data.id }">\n  \x3c!-- ko ifnot: $data.template--\x3e\n  \x3c!-- ko component: { name: $data.component || \'sv-action-bar-item\', params: { item: $data } } --\x3e\n  \x3c!-- /ko --\x3e\n  \x3c!-- /ko --\x3e\n  \x3c!-- ko if: $data.template  --\x3e\n  \x3c!-- ko template: { name: $data.template, data: $data.data || $data } --\x3e\n  \x3c!-- /ko --\x3e\n  \x3c!-- /ko --\x3e\n</span>\n\x3c!--/ko--\x3e\n\x3c!--/ko--\x3e';
    },
    function (e, t) {
      e.exports =
        '<div data-bind="css: question.koCss().root">\n  <label data-bind="css: question.getItemCss()">\n    <input\n      type="checkbox"\n      data-bind="css: question.koCss().control, attr: {name: question.name, id: question.inputId, \'aria-required\': question.isRequired, \'aria-label\': question.locTitle.renderedHtml, \'aria-invalid\': question.errors.length > 0, \'aria-describedby\': question.errors.length > 0 ? question.id + \'_errors\' : null}, checked: question.checkedValue, surveyProp: {indeterminate: question.isIndeterminate}, enable: !question.isReadOnly"\n    />\n    <span data-bind="css: question.koCss().materialDecorator">\n      <svg viewBox="0 0 24 24" data-bind="css:question.koCss().itemDecorator">\n        <rect\n          data-bind="css:question.koCss().uncheckedPath"\n          x="5"\n          y="10"\n          width="14"\n          height="4"\n        />\n        <polygon\n          data-bind="css:question.koCss().checkedPath"\n          points="19,10 14,10 14,5 10,5 10,10 5,10 5,14 10,14 10,19 14,19 14,14 19,14 "\n        />\n        <path\n          data-bind="css:question.koCss().indeterminatePath"\n          d="M22,0H2C0.9,0,0,0.9,0,2v20c0,1.1,0.9,2,2,2h20c1.1,0,2-0.9,2-2V2C24,0.9,23.1,0,22,0z M21,18L6,3h15V18z M3,6l15,15H3V6z"\n        />\n      </svg>\n      <span class="check"></span>\n    </span>\n    <span\n      data-bind="if: question.titleLocation === \'hidden\', css: question.koCss().label"\n    >\n      \x3c!-- ko template: { name: \'survey-string\', data: question.locDisplayLabel } --\x3e\n      \x3c!-- /ko --\x3e\n    </span>\n  </label>\n</div>\n';
    },
    function (e, t) {
      e.exports =
        "\x3c!-- ko template: { name: 'survey-question-title-content', data: element } --\x3e\x3c!--/ko--\x3e\n\x3c!-- ko if: element.isCollapsed || element.isExpanded --\x3e\n<span\n    data-bind=\"css: getIconClass(), event: { keyup: element.toggleStateByKeyUp }, attr:{'aria-expanded': element.isCollapsed ? 'false': 'true'}\"\n    tabindex=\"0\"></span>\n\x3c!-- /ko --\x3e";
    },
    function (e, t) {
      e.exports =
        '<div class="sv-title-actions">\n    \x3c!-- ko template: { name: \'survey-question-title-content\', data: question } --\x3e\x3c!-- /ko --\x3e\n    <sv-action-bar params="items: items"></sv-action-bar>\n</div>';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-comment">\n  \x3c!--ko if: !question.isReadOnlyRenderDiv() --\x3e\n      \x3c!--ko if: !question.isInputTextUpdate --\x3e\n      <textarea type="text" data-bind="disable: question.isReadOnly, value: $data.question.comment, visible: $data.visible, css: question.koCss().other, attr: {maxLength: question.getOthersMaxLength(), \'aria-label\': !!question.locTitle && question.locTitle.renderedHtml, placeholder: question.otherPlaceHolder}"></textarea>\n      \x3c!--/ko--\x3e\n      \x3c!--ko if: question.isInputTextUpdate --\x3e\n      <textarea type="text" data-bind="disable: question.isReadOnly, textInput: $data.question.comment, visible: $data.visible, css: question.koCss().other, attr: {maxLength: question.getOthersMaxLength(), \'aria-label\': !!question.locTitle && question.locTitle.renderedHtml, placeholder: question.otherPlaceHolder}"></textarea>\n      \x3c!--/ko--\x3e\n   \x3c!--/ko--\x3e\n\n    \x3c!--ko if: question.isReadOnlyRenderDiv() --\x3e\n        <div data-bind="text: question.comment"></div>\n    \x3c!--/ko--\x3e\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-flowpanel">\n  <div data-bind="style:{ flexBasis: renderWidth, flexGrow: 1, flexShrink: 1, width: renderWidth, minWidth: $data.minWidth, maxWidth: $data.maxWidth }, attr: {id: id}, css: cssClasses.panel.container">\n      <h4 data-bind="visible: (processedTitle.length > 0), css: cssClasses.panel.title">\n          \x3c!-- ko template: { name: \'survey-string\', data: locTitle } --\x3e\n          \x3c!-- /ko --\x3e\n      </h4>\n      <div data-bind="css: cssClasses.panel.description">\n          \x3c!-- ko template: { name: \'survey-string\', data: locDescription } --\x3e\n          \x3c!-- /ko --\x3e\n      </div>\n      <div data-bind="style: { paddingLeft: innerPaddingLeft}"></div>\n      <f-panel params="question: question" data-bind="attr: {contenteditable : question.isDesignMode, placeHolder: question.placeHolder}"></f-panel>\n  </div>\n</script>\n<script type="text/html" id="survey-flowpanel-question">\n  \x3c!-- ko with: $parent.question.getQuestionByName($data) --\x3e\n    \x3c!-- ko template: { name: koElementType(), data: $data, as: \'question\', afterRender: parent.koElementAfterRender } --\x3e\x3c!-- /ko --\x3e\n  \x3c!-- /ko --\x3e\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-header">\n  \x3c!-- ko if: (locTitle.koRenderedHtml().length > 0) && showTitle || hasLogo --\x3e\n  <div data-bind="css: css.header">\n\n      \x3c!-- ko if: isLogoBefore --\x3e\n      <div data-bind="css: logoClassNames">\n          <img data-bind="css: css.logoImage, attr: { src: locLogo.koRenderedHtml, width: logoWidth ? logoWidth + \'px\' : undefined, height: logoHeight ? logoHeight + \'px\' : undefined }, style: { objectFit: logoFit }">\n      </div>\n      \x3c!-- /ko --\x3e\n\n      \x3c!-- ko if: (locTitle.koRenderedHtml().length > 0) && showTitle || isDesignMode --\x3e\n      <div data-bind="css: css.headerText, style: { maxWidth: titleMaxWidth }">\n          <h3 data-bind="css: css.title">\x3c!-- ko template: { name: \'survey-string\', data: locTitle } --\x3e\x3c!-- /ko --\x3e</h3>\n          <h5 data-bind="css: css.description">\x3c!-- ko template: { name: \'survey-string\', data: locDescription } --\x3e\x3c!-- /ko --\x3e</h5>\n      </div>\n      \x3c!-- /ko --\x3e\n\n      \x3c!-- ko if: isLogoAfter --\x3e\n      <div data-bind="css: logoClassNames">\n        <img data-bind="css: css.logoImage, attr: { src: locLogo.koRenderedHtml, width: logoWidth ? logoWidth + \'px\' : undefined, height: logoHeight ? logoHeight + \'px\' : undefined }, style: { objectFit: logoFit }">\n      </div>\n      <div class="sv-logo--right-tail"></div>\n      \x3c!-- /ko --\x3e\n\n  </div>\n  \x3c!-- /ko --\x3e\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-content">\n  <div data-bind="css: css.root, afterRender: koEventAfterRender">\n      <form onsubmit="return false;">\n          <div class="sv_custom_header" data-bind="visible: !hasLogo"></div>\n          <div data-bind="css: css.container">\n              \x3c!-- ko template: { name: koTitleTemplate, afterRender: koAfterRenderHeader } --\x3e\n              \x3c!-- /ko --\x3e\n              \x3c!-- ko if: koState() == "starting" --\x3e\n              <div data-bind="css: css.body">\n                  \x3c!-- ko if: isNavigationButtonsShowing === \'top\' || isNavigationButtonsShowing === \'both\' --\x3e\n                  \x3c!-- ko template: { name: \'survey-navigation-start\' } --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- /ko --\x3e\n                  <div data-bind="attr: { id: startedPage.id }, template: { name: \'survey-page\', data: startedPage, afterRender: koAfterRenderPage }"></div>\n                  \x3c!-- ko if: isNavigationButtonsShowing === \'bottom\' || isNavigationButtonsShowing === \'both\' --\x3e\n                  \x3c!-- ko template: { name: \'survey-navigation-start\' } --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- /ko --\x3e\n              </div>\n              \x3c!-- /ko --\x3e\n              \x3c!-- ko if: koState() == "running" || koState() == "preview" --\x3e\n              <div data-bind="css: css.body">\n                  \x3c!-- ko if: isTimerPanelShowingOnTop --\x3e\n                  \x3c!-- ko template: { name: \'survey-timerpanel\' } --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- ko if: isShowProgressBarOnTop --\x3e\n                  \x3c!-- ko component: { name: \'sv-progress-\' + progressBarType, params: { model: $data } } --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- ko if: isNavigationButtonsShowing === \'top\' || isNavigationButtonsShowing === \'both\' --\x3e\n                  \x3c!-- ko template: { name: \'survey-navigation\' } --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- ko if: koCurrentPage --\x3e\n                    <div data-bind="attr: { id: koCurrentPage().id }, template: { name: \'survey-page\', data: koCurrentPage, afterRender: koAfterRenderPage }"></div>\n                    \x3c!-- ko if: isCurrentPageEmpty() && $data.emptyPageTemplate --\x3e\n                    \x3c!-- ko template: { name: emptyPageTemplate, data: $data.emptyPageTemplateData || $data } --\x3e\n                    \x3c!-- /ko --\x3e\n                    \x3c!-- /ko --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- ko if: isShowProgressBarOnBottom --\x3e\n                  \x3c!-- ko component: { name: \'sv-progress-\' + progressBarType, params: { model: $data } } --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- ko if: isTimerPanelShowingOnBottom --\x3e\n                  \x3c!-- ko template: { name: \'survey-timerpanel\' } --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- ko if: isNavigationButtonsShowing === \'bottom\' || isNavigationButtonsShowing === \'both\' --\x3e\n                  \x3c!-- ko template: { name: \'survey-navigation\' } --\x3e\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- /ko --\x3e\n              </div>\n              \x3c!-- /ko --\x3e\n              \x3c!-- ko if: koState() == "completed" && showCompletedPage --\x3e\n              <div data-bind="html: processedCompletedHtml, css: completedCss"></div>\n              <div data-bind="visible: koCompletedState() != \'\', css: css.saveData.root">\n                  <div data-bind="css: koCompletedStateCss">\n                      <span data-bind="text:koCompletedStateText"></span>\n                      <input type="button" data-bind="visible: koCompletedState() == \'error\', value: getLocString(\'saveAgainButton\'), click: doComplete, css: css.saveData.saveAgainButton"/>\n                  </div>\n              </div>\n              \x3c!-- /ko --\x3e\n              \x3c!-- ko if: koState() == "completedbefore" --\x3e\n              <div data-bind="html: processedCompletedBeforeHtml, css: css.body"></div>\n              \x3c!-- /ko --\x3e\n              \x3c!-- ko if: koState() == "loading" --\x3e\n              <div data-bind="html: processedLoadingHtml, css: css.body"></div>\n              \x3c!-- /ko --\x3e\n              \x3c!-- ko if: koState() == "empty" --\x3e\n              <div data-bind="text:emptySurveyText, css: css.bodyEmpty"></div>\n              \x3c!-- /ko --\x3e\n          </div>\n      </form>\n  </div>\n</script>\n<script type="text/html" id="survey-navigation">\n  <div data-bind="css: css.footer">\n      <input type="button" data-bind="event: { mousedown: navigationMouseDown }, value: pagePrevText, click: prevPage, visible: !koIsFirstPage() && isShowPrevButton, css: cssNavigationPrev" />\n      <input type="button" data-bind="event: { mousedown: nextPageMouseDown }, value: pageNextText, click: nextPageUIClick, visible: !koIsLastPage(), css: cssNavigationNext" />\n      <input type="button" data-bind="event: { mousedown: navigationMouseDown }, value: previewText, click: showPreview, visible: koIsLastPage() && isPreviewButtonVisible, css: cssNavigationPreview" />\n      <input type="button" data-bind="event: { mousedown: navigationMouseDown }, value: completeText, click: completeLastPage, visible: koIsLastPage() && isCompleteButtonVisible, css: cssNavigationComplete" />\n  </div>\n</script>\n<script type="text/html" id="survey-navigation-start">\n  <div data-bind="css: css.footer">\n      <input type="button" data-bind="value: startSurveyText, click: start, css: cssNavigationStart"/>\n  </div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-page">\n  <div data-bind="css: cssClasses.page.root">\n      \x3c!-- ko if: _showTitle --\x3e\n      <h4 data-bind="css: cssClasses.page.title">\n          \x3c!-- ko template: { name: \'survey-string\', data: locTitle } --\x3e\n          \x3c!-- /ko --\x3e\n      </h4>\n      \x3c!-- /ko --\x3e\n      \x3c!-- ko if: _showDescription--\x3e\n      <div data-bind="visible: data.showPageTitles, css: cssClasses.page.description">\n          \x3c!-- ko template: { name: \'survey-string\', data: locDescription } --\x3e\n          \x3c!-- /ko --\x3e\n      </div>\n      \x3c!-- /ko --\x3e\n      \x3c!-- ko template: { name: \'survey-rows\', data: $data} --\x3e\n      \x3c!-- /ko --\x3e\n  </div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-panel">\n  <div\n    data-bind="style: { flexBasis: renderWidth, flexGrow: 1, flexShrink: 1, width: renderWidth, minWidth: $data.minWidth, maxWidth: $data.maxWidth }, attr: { id: id }, css: cssClasses.panel.container"\n  >\n    <h4\n      data-bind="visible: !!locTitle.koRenderedHtml(), css: $data.getTitleStyle(), click: toggleState"\n    >\n      \x3c!-- ko component: { name: getTitleComponentName(), params: { element: $data } } --\x3e\x3c!-- /ko --\x3e\n    </h4>\n    <div data-bind="css: cssClasses.panel.description">\n      \x3c!-- ko template: { name: \'survey-string\', data: locDescription } --\x3e\n      \x3c!-- /ko --\x3e\n    </div>\n    \x3c!-- ko template: { name: \'survey-question-errors\', data: $data } --\x3e\n    \x3c!-- /ko --\x3e\n    <div\n      data-bind="visible: !koIsCollapsed(), style: { paddingLeft: innerPaddingLeft }, css: cssClasses.panel.content, attr: {id:contentId}"\n    >\n      \x3c!-- ko template: { name: \'survey-rows\', data: $data} --\x3e\n      \x3c!-- /ko --\x3e\n      <div data-bind="visible: hasEditButton, css: cssClasses.panel.footer">\n        <input\n          type="button"\n          data-bind="value: survey.editText, click: cancelPreview, css: survey.cssNavigationEdit"\n        />\n      </div>\n    </div>\n  </div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-boolean">\n  <div data-bind="css: question.koCss().root">\n    <label data-bind="css: question.getItemCss()">\n      <input\n        type="checkbox"\n        data-bind="css: question.koCss().control, attr: {name: question.name, id: question.inputId, \'aria-required\': question.isRequired, \'aria-label\': question.locTitle.renderedHtml, \'aria-invalid\': question.errors.length > 0, \'aria-describedby\': question.errors.length > 0 ? question.id + \'_errors\' : null}, checked: question.checkedValue, surveyProp: {indeterminate: question.isIndeterminate}, enable: !question.isReadOnly"\n      />\n      <span\n        data-bind="css: question.getUncheckedLabelCss(), click: onFalseLabelClick"\n        >\x3c!-- ko template: { name: \'survey-string\', data: locLabelFalse } --\x3e\x3c!-- /ko --\x3e</span\n      >\n      <div data-bind="css: question.koCss().switch, click: onSwitchClick">\n        <span data-bind="css: question.koCss().slider"></span>\n      </div>\n      <span\n        data-bind="css: question.getCheckedLabelCss(), click: onTrueLabelClick"\n        >\x3c!-- ko template: { name: \'survey-string\', data: locLabelTrue } --\x3e\x3c!-- /ko --\x3e</span\n      >\n    </label>\n  </div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-checkbox">\n  <fieldset data-bind="css: question.koCss().root">\n      <legend data-bind="attr: { \'aria-label\': question.locTitle.renderedHtml }"></legend>\n      \x3c!-- ko ifnot: question.hasColumns  --\x3e\n        \x3c!-- ko foreach: { data: question.visibleChoices, as: \'item\', afterRender: question.koAfterRender }  --\x3e\n            \x3c!-- ko template: \'survey-checkbox-item\' --\x3e\n            \x3c!-- /ko --\x3e\n        \x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- ko if: question.hasColumns  --\x3e\n        \x3c!-- ko foreach: question.columns --\x3e\n            <div data-bind="css: question.getColumnClass()">\n            \x3c!-- ko foreach: { data: $data, as: \'item\', afterRender: question.koAfterRender }  --\x3e\n                \x3c!-- ko template: \'survey-checkbox-item\' --\x3e\n                \x3c!-- /ko --\x3e\n            \x3c!-- /ko --\x3e\n            </div>\n        \x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n    </fieldset>\n</script>\n<script type="text/html" id="survey-checkbox-item">\n  <div data-bind="css: question.getItemClass(item)">\n      <label data-bind="css: question.getLabelClass(item)">\n          \x3c!-- ko if: item == question.selectAllItem --\x3e\n          <input type="checkbox" data-bind="attr: {name: question.name, id: question.inputId + \'_\' + question.getItemIndex(item), \'aria-required\': question.isRequired, \'aria-label\': item.locText.renderedHtml, \'aria-invalid\': question.errors.length > 0, \'aria-describedby\': question.errors.length > 0 ? question.id + \'_errors\' : null }, checked: question.koAllSelected, enable: !question.isReadOnly && item.isEnabled, css: question.koCss().itemControl"/>\n          \x3c!-- /ko --\x3e\n          \x3c!-- ko if: item != question.selectAllItem --\x3e\n          <input type="checkbox" data-bind="attr: {name: question.name, id: question.inputId + \'_\' + question.getItemIndex(item), \'aria-required\': question.isRequired, \'aria-label\': item.locText.renderedHtml, \'aria-invalid\': question.errors.length > 0, \'aria-describedby\': question.errors.length > 0 ? question.id + \'_errors\' : null }, checkedValue: item.value, checked: question.koValue, enable: !question.isReadOnly && item.isEnabled, css: question.koCss().itemControl"/>\n          \x3c!-- /ko --\x3e\n          <span data-bind="css: question.koCss().materialDecorator">\n            <svg viewBox="0 0 24 24" data-bind="css: question.koCss().itemDecorator">\n              <path d="M5,13l2-2l3,3l7-7l2,2l-9,9L5,13z"/>\n            </svg>\n            <span class="check"></span>\n          </span>\n          <span data-bind="visible: !item.hideCaption, css: question.koCss().controlLabel, attr: { title: item.locText.koRenderedHtml }">\n              \x3c!-- ko template: { name: \'survey-string\', data: item.locText } --\x3e\n              \x3c!-- /ko --\x3e\n          </span>\n      </label>\n      \x3c!-- ko if: question.hasOther && (item.value == question.otherItem.value) --\x3e\n      <div class="form-group" data-bind="template: { name: \'survey-comment\', data: {\'question\': question, \'visible\': question.isOtherSelected } }"></div>\n      \x3c!-- /ko --\x3e\n  </div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        "<script type=\"text/html\" id=\"survey-question-comment\">\n  \x3c!--ko if: !question.isReadOnlyRenderDiv() --\x3e\n\x3c!--ko if: !question.isInputTextUpdate --\x3e\n<textarea type=\"text\"\n  data-bind=\"attr: {'readonly': question.isReadOnly, 'disabled': question.isReadOnly, maxLength: question.getMaxLength(), cols: question.cols, rows: question.rows, id: question.inputId, placeholder: question.isReadOnly ? undefined : question.placeHolder, 'aria-required': question.isRequired, 'aria-label': question.locTitle.renderedHtml, 'aria-invalid': question.errors.length > 0, 'aria-describedby': question.errors.length > 0 ? question.id + '_errors' : null}, value:question.value, css: question.koCss().root\"></textarea>\n\x3c!--/ko--\x3e\n\x3c!--ko if: question.isInputTextUpdate --\x3e\n<textarea type=\"text\"\n  data-bind=\"attr: {'readonly': question.isReadOnly, 'disabled': question.isReadOnly, maxLength: question.getMaxLength(), cols: question.cols, rows: question.rows, id: question.inputId, placeholder: question.isReadOnly ? undefined : question.placeHolder, 'aria-required': question.isRequired, 'aria-label': question.locTitle.renderedHtml, 'aria-invalid': question.errors.length > 0, 'aria-describedby': question.errors.length > 0 ? question.id + '_errors' : null}, textInput:question.value, css: question.koCss().root\"></textarea>\n\x3c!--/ko--\x3e\n\x3c!--/ko--\x3e\n\n\x3c!--ko if: question.isReadOnlyRenderDiv() --\x3e\n<div data-bind=\"text: question.value\"></div>\n\x3c!--/ko--\x3e\n</script>";
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-composite">\n  \x3c!-- ko template: { name: \'survey-panel\', data: question.contentPanel, as: \'question\', afterRender: question.contentPanel.koPanelAfterRender } --\x3e\n  \x3c!-- /ko --\x3e\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-custom">\n  \x3c!-- ko template: { name: question.contentQuestion.koTemplateName(), data: question.contentQuestion, as: \'question\', afterRender: question.contentQuestion.koQuestionAfterRender } --\x3e\n  \x3c!-- /ko --\x3e\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-dropdown">\n<div data-bind="css: question.koCss().selectWrapper">\n  \x3c!-- ko ifnot: question.isReadOnly --\x3e\n  <select\n    data-bind="if: true, attr: {id: question.inputId, \'aria-label\': question.locTitle.renderedHtml, \'aria-invalid\': question.errors.length > 0, \'aria-describedby\': question.errors.length > 0 ? question.id + \'_errors\' : null}, value: question.renderedValue, valueAllowUnset: true, css: question.koControlClass">\n    \x3c!-- ko if: question.showOptionsCaption --\x3e\n    <option data-bind="text:question.optionsCaption, value: null"></option>\n    \x3c!-- /ko --\x3e\n    \x3c!-- ko foreach: question.visibleChoices --\x3e\n    <option data-bind="value: $data.value, text: $data.text, attr: { disabled: !$data.isEnabled }"></option>\n    \x3c!-- /ko --\x3e\n  </select>\n  \x3c!-- /ko --\x3e\n  \x3c!-- ko if: question.isReadOnly --\x3e\n    <div disabled\n    data-bind="attr: {id: question.inputId}, text: question.hasOther && question.isOtherSelected ? question.otherText : question.displayValue, css: question.koCss().control">\n    </div>\n  \x3c!-- /ko --\x3e\n</div>\n\x3c!-- ko if: question.hasOther --\x3e\n<div class="form-group"\n  data-bind="template: { name: \'survey-comment\', data: {\'question\': question, \'visible\': question.isOtherSelected } }, style: {display: question.isFlowLayout ? \'inline\': \'\'}">\n</div>\n\x3c!-- /ko --\x3e\n</script>';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-empty">\n    <div></div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-errors">\n        <div role="alert" data-bind="visible: errors.length > 0, foreach: { data: errors, as: \'error\' }, css: koErrorClass(), attr: { id: question.id + \'_errors\' }">\n            <div>   \n                <span aria-hidden="true" data-bind="css: question.koCss().error.icon"></span>\n                <span data-bind="css: question.koCss().error.item">\n                    \x3c!-- ko template: { name: \'survey-string\', data: error.locText } --\x3e\x3c!-- /ko --\x3e\n                </span>\n            </div>\n        </div>\n</script>';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-expression">\n  <div data-bind="text:question.displayValue, css: question.koCss().root"></div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-file">\n  <div data-bind="css: question.koCss().root">\n      \x3c!-- ko ifnot: question.isReadOnly --\x3e\n      <input type="file" data-bind="css: question.cssClasses.fileInput, attr: {id: question.inputId, \'aria-required\': question.isRequired, \'aria-label\': question.locTitle.renderedHtml, \'aria-invalid\': question.errors.length > 0, \'aria-describedby\': question.errors.length > 0 ? question.id + \'_errors\' : null, multiple: question.allowMultiple ? \'multiple\' : undefined, title: koInputTitle, accept: question.acceptedTypes }, event: {change: question.dochange}">\n      \x3c!-- /ko --\x3e\n      \x3c!-- ko if: question.isReadOnly --\x3e\n      <input type="file" disabled data-bind="css: \'form-control \' + question.cssClasses.placeholderInput, attr: { placeholder: question.title }" style="color: transparent;"/>\n      \x3c!-- /ko --\x3e\n      <div data-bind="css:question.koCss().fileDecorator, event:{ dragover: question.ondragover, drop: question.ondrop}">\n        <div data-bind="css: question.koCss().wrapper">\n          <label role="button" data-bind="css: question.koChooseFileClass, attr: {for: question.inputId, \'aria-label\': question.chooseButtonCaption}, text: question.chooseButtonCaption"></label>\n          <span data-bind="css: question.koCss().noFileChosen, visible: !question.koHasValue(), text: question.noFileChosenCaption"></span>\n        </div>\n      </div>\n      \x3c!-- ko if: question.koHasValue() && !question.isReadOnly --\x3e\n      <button type="button" data-bind="css: question.cssClasses.removeButton, click: question.doclean, text: question.cleanButtonCaption"></button>\n      \x3c!-- /ko --\x3e\n      <div data-bind="foreach: question.koData, visible: question.koHasValue">\n          <span data-bind="visible: question.showPreview, css: question.cssClasses.preview">\n              \x3c!-- ko if: $data.name --\x3e\n              <div data-bind="css: question.koCss().fileSign">\n              <a data-bind="style: { width: question.imageWidth + \'px\' }, click: question.dodownload, text: $data.name,\n              attr: { href: $data.content, title: $data.name, download: $data.name }"></a>\n              </div>\n              \x3c!-- /ko --\x3e\n              \x3c!-- ko if: question.canPreviewImage($data) --\x3e\n              <img data-bind="attr: { src: $data.content, height: question.imageHeight, width: question.imageWidth }" alt="File preview">\n              \x3c!-- /ko --\x3e\n              \x3c!-- ko if: $data.name --\x3e\n              \x3c!-- ko ifnot: question.isReadOnly --\x3e\n              <div>\n                  <span data-bind="css: question.cssClasses.removeFile, click: question.doremovefile, text: question.removeFileCaption"></span>\n                  <svg data-bind="css: question.cssClasses.removeFileSvg, click: question.doremovefile" viewBox="0 0 16 16">\n                    <path d="M8,2C4.7,2,2,4.7,2,8s2.7,6,6,6s6-2.7,6-6S11.3,2,8,2z M11,10l-1,1L8,9l-2,2l-1-1l2-2L5,6l1-1l2,2l2-2l1,1L9,8 L11,10z"/>\n                  </svg>\n              </div>\n              \x3c!-- /ko --\x3e\n              \x3c!-- ko if: $data.name --\x3e\n              <div data-bind="css: question.koCss().fileSignBottom">\n                <a data-bind="style: { width: question.imageWidth + \'px\' }, click: question.dodownload, text: $data.name, attr: { href: $data.content, title: $data.name, download: $data.name }"></a>\n              </div>\n              \x3c!-- /ko --\x3e\n              \x3c!-- /ko --\x3e\n          </span>\n      </div>\n      \x3c!-- ko if: question.koHasValue() && !question.isReadOnly --\x3e\n      <button type="button" data-bind="css: question.cssClasses.removeButtonBottom, click: question.doclean, text: question.cleanButtonCaption"></button>\n      \x3c!-- /ko --\x3e\n  </div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-html">\n  <div data-bind="html: question.locHtml.koRenderedHtml, css: question.koCss().root"></div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-image">\n  <div data-bind="css: question.koCss().root">\n    \x3c!-- ko if: question.contentMode === "image" --\x3e\n    <img data-bind="css: question.koCss().image, attr: { src: $data.imageLink, width: question.imageWidth ? question.imageWidth + \'px\' : undefined, height: question.imageHeight ? question.imageHeight + \'px\' : undefined, alt: question.text || question.title }, style: { objectFit: question.imageFit }"/>\n    \x3c!-- /ko --\x3e\n    \x3c!-- ko if: question.contentMode === "video" --\x3e\n    <embed data-bind="css: question.koCss().image, attr: { src: $data.imageLink, width: question.imageWidth ? question.imageWidth + \'px\' : undefined, height: question.imageHeight ? question.imageHeight + \'px\' : undefined }, style: { objectFit: question.imageFit }"/>\n    \x3c!-- /ko --\x3e\n  </div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        "<script type=\"text/html\" id=\"survey-question-imagepicker\">\n  <fieldset data-bind=\"css: question.koCss().root\">\n      <legend data-bind=\"attr: { 'aria-label': question.locTitle.renderedHtml }\"></legend>\n      \x3c!-- ko foreach: { data: question.visibleChoices, as: 'item', afterRender: question.koAfterRender}  --\x3e\n      <div data-bind=\"css: question.getItemClass(item)\">\n          <label data-bind=\"css: question.koCss().label\">\n              <input style=\"display: none;\" data-bind=\"attr: {type: question.multiSelect ? 'checkbox' : 'radio', name: question.name + '_' + question.id, value: item.value, id: question.inputId + '_' + item.value, 'aria-required': question.isRequired, 'aria-label': question.locTitle.renderedHtml, 'aria-invalid': question.errors.length > 0, 'aria-describedby': question.errors.length > 0 ? question.id + '_errors' : null}, checked: question.koValue, enable: !question.isReadOnly && item.isEnabled, css: question.koCss().itemControl\"\n              />\n              <div>\n                  \x3c!-- ko if: question.contentMode === \"image\" --\x3e\n                  <img data-bind=\"css: question.koCss().image, attr: { src: $data.locImageLink.koRenderedHtml, width: question.imageWidth ? question.imageWidth + 'px' : undefined, height: question.imageHeight ? question.imageHeight + 'px' : undefined, alt: item.locText.koRenderedHtml }, style: { objectFit: question.imageFit }\"/>\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- ko if: question.contentMode === \"video\" --\x3e\n                  <embed data-bind=\"css: question.koCss().image, attr: { src: $data.locImageLink.koRenderedHtml, width: question.imageWidth ? question.imageWidth + 'px' : undefined, height: question.imageHeight ? question.imageHeight + 'px' : undefined }, style: { objectFit: question.imageFit }\"/>\n                  \x3c!-- /ko --\x3e\n                  \x3c!-- ko if: question.showLabel --\x3e\n                  <span data-bind=\"css: question.koCss().itemText, attr: { title: item.locText.koRenderedHtml }\">\n                    \x3c!-- ko template: { name: 'survey-string', data: item.locText } --\x3e\n                    \x3c!-- /ko --\x3e\n                </span>\n                  \x3c!-- /ko --\x3e\n              </div>\n          </label>\n      </div>\n      \x3c!-- /ko --\x3e\n  </fieldset>\n</script>\n";
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-matrix">\n    <div data-bind="css: question.koCss().tableWrapper">\n        <fieldset>\n            <legend data-bind="attr: { \'aria-label\': question.locTitle.renderedHtml }"></legend>\n            <table data-bind="css: question.koCss().root">\n                \x3c!-- ko if: question.showHeader --\x3e\n                <thead>\n                    <tr>\n                        <td data-bind="visible: question.hasRows"></td>\n                        \x3c!-- ko foreach: question.koVisibleColumns --\x3e\n                        <th data-bind="css: question.koCss().headerCell">\n                            \x3c!-- ko template: { name: \'survey-string\', data: locText } --\x3e\n                            \x3c!-- /ko --\x3e\n                        </th>\n                        \x3c!-- /ko --\x3e\n                    </tr>\n                </thead>\n                \x3c!-- /ko --\x3e\n                <tbody>\n                    \x3c!-- ko foreach: { data: question.koVisibleRows, as: \'row\' } --\x3e\n                    <tr data-bind="css: row.rowClasses">\n                        <td data-bind="visible: question.hasRows, css: question.koCss().cell">\n                            \x3c!-- ko template: { name: \'survey-string\', data: row.locText } --\x3e\n                            \x3c!-- /ko --\x3e\n                        </td>\n                        \x3c!-- ko foreach: question.koVisibleColumns --\x3e\n                        \x3c!-- ko if: question.hasCellText --\x3e\n                        <td data-bind="css: question.getItemClass(row, $data), click: function() { if (!question.isReadOnly) { row.cellClick($data); } }">\n                            \x3c!-- ko template: { name: \'survey-string\', data: question.getCellDisplayLocText(row.name, $data) } --\x3e\n                            \x3c!-- /ko --\x3e\n                        </td>\n                        \x3c!-- /ko --\x3e\n                        \x3c!-- ko if: !question.hasCellText --\x3e\n                        <td data-bind="attr: { headers: $data.locText.renderedHtml }, css: question.koCss().cell">\n                            <label data-bind="css: question.getItemClass(row, $data)">\n                                <input type="radio" data-bind="css: question.koCss().itemValue, attr: { name: row.fullName, \'aria-required\': question.isRequired, \'aria-label\': question.locTitle.renderedHtml, id: question.inputId + \'_\' + row.name + \'_\' + $index() }, checkedValue: $data.value, checked: row.value, enable: !question.isReadOnly"/>\n                                <span data-bind="css: question.koCss().materialDecorator">\n                                <svg data-bind="css:question.koCss().itemDecorator" viewBox="-12 -12 24 24">\n                                    <circle r="6" cx="0" cy="0">\n                                </svg>\n                                </span>\n                                <span class="circle"></span>\n                                <span class="check"></span>\n                                <span style="display: none" data-bind="text: $data.locText.text"></span>\n                            </label>\n                        </td>\n                        \x3c!-- /ko --\x3e\n                        \x3c!-- /ko --\x3e\n                    </tr>\n                    \x3c!-- /ko --\x3e\n                </tbody>\n            </table>\n        </fieldset>\n    </div>\n</script>\n<script type="text/html" id="survey-text">\n    <span data-bind="text:$data.renderedHtml"></span>\n</script>';
    },
    function (e, t) {
      e.exports =
        '\ufeff<script type="text/html" id="survey-question-matrixdynamic">\n  <div\n    data-bind="visible: question.koIsAddRowOnTop(), css: question.koCss().footer"\n  >\n    <button\n      type="button"\n      data-bind="click:question.koAddRowClick, css: question.koCss().button + \' \' + question.koCss().buttonAdd"\n    >\n      <span data-bind="text: question.addRowText"></span>\n      <span data-bind="css: question.koCss().iconAdd"></span>\n    </button>\n  </div>\n  <div data-bind="style: {overflowX: question.horizontalScroll? \'scroll\': \'\'}">\n    <table data-bind="css: question.koCss().root">\n      \x3c!-- ko if: question.koTable().showHeader --\x3e\n      <thead>\n        <tr>\n          \x3c!-- ko foreach: question.koTable().headerRow.cells --\x3e\n          <th\n            data-bind="style: { minWidth: $data.minWidth, width: $data.width }, css: question.koCss().headerCell"\n          >\n            \x3c!-- ko template: { name: \'survey-string\', data: $data.locTitle } --\x3e\x3c!-- /ko --\x3e\n            \x3c!-- ko if: $data.requiredText --\x3e\n            <span data-bind="text: $data.requiredText"></span>\n            \x3c!-- /ko --\x3e\n          </th>\n          \x3c!-- /ko --\x3e\n        </tr>\n      </thead>\n      \x3c!-- /ko --\x3e\n      <tbody>\n        \x3c!-- ko foreach: question.koTable().rows --\x3e\n        \x3c!-- ko ifnot: $parent.detailPanelMode === "popup" && $data.isDetailRow --\x3e\n        <tr data-bind="css:$data.className">\n          \x3c!-- ko foreach: $data.cells --\x3e\n          \x3c!-- ko if: $data.isShowHideDetail --\x3e\n          <td data-bind="css:$data.className">\n            <button\n              type="button"\n              data-bind="css:question.getDetailPanelButtonCss($data.row), click:row.showHideDetailPanelClick, attr:{ \'aria-expanded\': question.getIsDetailPanelShowing($data.row) ? \'true\': \'false\', \'aria-controls\': question.getIsDetailPanelShowing($data.row) ? row.detailPanelId: false}"\n            >\n              <span\n                data-bind="css: question.getDetailPanelIconCss($data.row)"\n              ></span>\n            </button>\n            \x3c!-- ko if: question.detailPanelMode === "popup" && question.getKoPopupIsVisible($parent.row) --\x3e\n            <sv-popup\n              params="contentComponentName: \'sv-panel\', contentComponentData: { question: $parent.row.detailPanel, onItemSelect: function(){debugger;} }, isVisible: question.getKoPopupIsVisible($parent.row), isModal: true, onHide: function() { row.hideDetailPanel() }, verticalPosition: \'top\', horizontalPosition: \'right\'"\n            ></sv-popup>\n            \x3c!-- /ko --\x3e\n          </td>\n          \x3c!-- /ko --\x3e\n          \x3c!-- ko if: $data.isEmpty --\x3e\n          <td data-bind="attr: { colspan: $data.colSpans }"></td>\n          \x3c!-- /ko --\x3e\n          \x3c!-- ko if: $data.hasPanel --\x3e\n          <td data-bind="css:$data.className, attr: {colspan: $data.colSpans}">\n            \x3c!-- ko template: { name: $data.panel.koElementType, data: $data.panel, afterRender: question.koPanelAfterRender } --\x3e\n            \x3c!-- /ko --\x3e\n          </td>\n          \x3c!-- /ko --\x3e\n          \x3c!-- ko if: $data.hasQuestion --\x3e\n          \x3c!-- ko template: { name: \'survey-matrixcell\', afterRender: function(els) { $data.matrix.koCellAfterRender(els, $data); } } --\x3e\x3c!-- /ko --\x3e\n          \x3c!-- /ko --\x3e\n          \x3c!-- ko if: $data.hasTitle --\x3e\n          <td\n            data-bind="css: $data.className, style: { minWidth: $data.minWidth, width: $data.width }"\n          >\n            \x3c!-- ko template: { name: \'survey-string\', data: $data.locTitle } --\x3e\x3c!-- /ko --\x3e\n            \x3c!-- ko if: $data.requiredText --\x3e\n            <span data-bind="text: $data.requiredText"></span>\n            \x3c!-- /ko --\x3e\n          </td>\n          \x3c!-- /ko --\x3e\n          \x3c!-- ko if: $data.isRemoveRow --\x3e\n          <td data-bind="css: $data.className">\n            <button\n              type="button"\n              data-bind="click:question.koRemoveRowClick, css: question.koCss().button + \' \' + question.koCss().buttonRemove"\n            >\n              <span data-bind="text: question.removeRowText"></span>\n              <span data-bind="css: question.koCss().iconRemove"></span>\n            </button>\n          </td>\n          \x3c!-- /ko --\x3e\n          \x3c!-- /ko --\x3e\n        </tr>\n        \x3c!-- /ko --\x3e\n        \x3c!-- /ko --\x3e\n      </tbody>\n      \x3c!-- ko if:question.koTable().hasFooter --\x3e\n      <tfoot>\n        <tr>\n          \x3c!-- ko foreach: question.koTable().footerRow.cells --\x3e\n          <td>\n            \x3c!-- ko if: $data.hasQuestion --\x3e\n            \x3c!-- ko template: { name: \'survey-matrixcell\', afterRender: function(els) { $data.matrix.koCellAfterRender(els, $data); } } --\x3e\x3c!-- /ko --\x3e\n            \x3c!-- /ko --\x3e\n            \x3c!-- ko if: $data.hasTitle --\x3e\x3c!-- ko template: { name: \'survey-string\', data: $data.locTitle } --\x3e\x3c!-- /ko --\x3e\x3c!-- /ko --\x3e\n          </td>\n          \x3c!-- /ko --\x3e\n        </tr>\n      </tfoot>\n      \x3c!-- /ko --\x3e\n    </table>\n  </div>\n  <div\n    data-bind="visible: question.koIsAddRowOnBottom(), css: question.koCss().footer"\n  >\n    <button\n      type="button"\n      data-bind="click:question.koAddRowClick, css: question.koCss().button + \' \' + question.koCss().buttonAdd"\n    >\n      <span data-bind="text: question.addRowText"></span>\n      <span data-bind="css: question.koCss().iconAdd"></span>\n    </button>\n  </div>\n</script>\n<script type="text/html" id="survey-matrixcell">\n  <td\n    data-bind="css: $data.className + (question.errors.length > 0 ? \' \' + question.koCss().hasError : \'\'), style: {\'text-align\': $data.isChoice ? \'center\': \'\', minWidth: $data.minWidth, width: $data.width}, attr: { headers: question.isVisible ? question.locTitle.renderedHtml : \'\' }"\n  >\n    <div data-bind="visible: question.isVisible">\n      \x3c!-- ko if: $data.showErrorOnTop --\x3e\n      \x3c!-- ko template: { name: \'survey-question-errors\', data: question, as: \'question\' } --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- ko if: $data.isChoice --\x3e\n      \x3c!-- ko if: $data.isCheckbox --\x3e\n      \x3c!-- ko template: {name: \'survey-checkbox-item\', templateOptions: {hideCaption: true} }  --\x3e\x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- ko ifnot: $data.isCheckbox --\x3e\n      \x3c!-- ko template: {name: \'survey-radiogroup-item\', templateOptions: {hideCaption: true} }  --\x3e\x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- ko ifnot: $data.isChoice --\x3e\n      \x3c!-- ko if: question.isDefaultRendering() --\x3e\n      \x3c!-- ko template: { name: question.koTemplateName(), data: question, as: \'question\', afterRender: function(el) { $data.matrix.koCellQuestionAfterRender(el, $data); } } --\x3e\x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- ko ifnot: question.isDefaultRendering() --\x3e\n      \x3c!-- ko component: { name: question.getComponentName(), params: { question: question } } --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- ko if: $data.showErrorOnBottom --\x3e\n      \x3c!-- ko template: { name: \'survey-question-errors\', data: question, as: \'question\' } --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n    </div>\n  </td>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        "<script type=\"text/html\" id=\"survey-question-multipletext\">\n  <table data-bind=\"css: question.koCss().root, foreach: { data:  question.koRows, as: 'row' }\">\n      <tr data-bind=\"foreach: { data: row, as: 'item' }, css: question.koCss().row\">\n          <td data-bind=\"css: question.koCss().itemTitle + ' ' + question.koCss().cell\">\n            \x3c!-- ko template: { name: 'survey-question-title-content', data: item.editor, as: 'question' } --\x3e\x3c!-- /ko --\x3e\n          </td>\n          <td data-bind=\"css: question.koCss().item + ' ' + question.koCss().cell\">\n            \x3c!-- ko if: item.editor.errorLocation == 'top' --\x3e\n            <div data-bind=\"css: question.koCss().error.locationTop\">\n                \x3c!-- ko template: { name: 'survey-question-errors', data: item.editor } --\x3e\n                \x3c!-- /ko --\x3e\n            </div>\n            \x3c!-- /ko --\x3e\n            \x3c!-- ko template: { name: item.editor.koTemplateName(), data: item.editor, as: 'question', afterRender: item.editor.koQuestionAfterRender } --\x3e\n            \x3c!-- /ko --\x3e\n            \x3c!-- ko if: item.editor.errorLocation == 'bottom' --\x3e\n              \x3c!-- ko template: { name: 'survey-question-errors', data: item.editor } --\x3e\n              \x3c!-- /ko --\x3e\n            \x3c!-- /ko --\x3e\n        </td>\n      </tr>\n  </table>\n</script>\n";
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-paneldynamic-navigator">\n  <div style="clear:both;">\n        <div data-bind="css: question.koCss().progressContainer">\n          <div data-bind="title: question.panelPrevText, click: question.koPrevPanelClick">\n            <svg viewBox="0 0 10 10" data-bind="css: question.koButtonPrevCss">\n              <polygon points="2,2 0,4 5,9 10,4 8,2 5,5 " />\n            </svg>\n          </div>\n\n          \x3c!-- ko if: question.koIsRange --\x3e\n          <div data-bind="css: question.koCss().progress">\n            <div\n              data-bind="css: question.koCss().progressBar, style: {width: question.koProgress}"\n              role="progressbar"\n            ></div>\n          </div>\n          \x3c!-- /ko --\x3e\n\n          <div data-bind="title:question.panelNextText, click: question.koNextPanelClick">\n            <svg viewBox="0 0 10 10" data-bind="css: question.koButtonNextCss">\n              <polygon points="2,2 0,4 5,9 10,4 8,2 5,5 " />\n            </svg>\n          </div>\n        </div>\n\n        \x3c!-- ko if: question.canAddPanel --\x3e\n        <input\n          type="button"\n          data-bind="value: question.panelAddText, css:question.koCss().button  + \' \' + question.koCss().buttonAdd, click:question.koAddPanelClick"\n        />\n        \x3c!-- /ko --\x3e\n\n        <div data-bind="text:question.koProgressText, css:question.koCss().progressText"></div>\n      </div>\n</script>';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-paneldynamic">\n  <div data-bind="css: question.koCss().root">\n      \x3c!-- ko if: question.koIsList() --\x3e\n        \x3c!-- ko foreach: { data: question.panels } --\x3e\n        \x3c!-- ko template: { name: \'survey-panel\', data: $data, as: \'question\', afterRender: question.koPanelAfterRender } --\x3e\n        \x3c!-- /ko --\x3e\n        <input type="button" data-bind="visible: question.koCanRemovePanel() && !$data.koIsCollapsed(), click:question.koRemovePanelClick, css: question.koCss().button + \' \' + question.koCss().buttonRemove, value: question.panelRemoveText"\n        />\n        <hr data-bind="css: question.koCss().separator"/>\n        \x3c!-- /ko --\x3e\n        <input type="button" data-bind="visible:question.koCanAddPanel, click:question.koAddPanelClick, css: question.koButtonAddCss, value: question.panelAddText"\n        />\n        \x3c!-- /ko --\x3e\n      \x3c!-- ko ifnot: question.koIsList()  --\x3e\n        <div data-bind="visible:question.koIsProgressTop, css: question.koCss().progressTop">\n            \x3c!-- ko template: { name: \'survey-question-paneldynamic-navigator\', data: question, as: \'question\'} --\x3e\n            \x3c!-- /ko --\x3e\n        </div>\n        \x3c!-- ko if: question.koPanel() --\x3e\n            \x3c!-- ko template: { name: \'survey-panel\', data: question.koPanel(), as: \'question\', afterRender: question.koPanelAfterRender } --\x3e\n            \x3c!-- /ko --\x3e\n        \x3c!-- /ko --\x3e\n        <input type="button" data-bind="visible: question.koCanRemovePanel, click:question.koRemovePanelClick, css: question.koCss().button + \' \' + question.koCss().buttonRemove, value: question.panelRemoveText"\n        />\n        <div data-bind="visible:question.koIsProgressBottom,  css: question.koCss().progressBottom">\n            \x3c!-- ko template: { name: \'survey-question-paneldynamic-navigator\', data: question, as: \'question\'} --\x3e\n            \x3c!-- /ko --\x3e\n        </div>\n      \x3c!-- /ko --\x3e\n  </div>\n</script>';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-radiogroup">\n  <fieldset data-bind="css: question.koCss().root" role="radiogroup">\n    <legend\n      data-bind="attr: { \'aria-label\': question.locTitle.renderedHtml }"\n    ></legend>\n    \x3c!-- ko ifnot: question.hasColumns --\x3e\n    \x3c!-- ko foreach: { data: question.visibleChoices, as: \'item\', afterRender: question.koAfterRender } --\x3e\n    \x3c!-- ko template: \'survey-radiogroup-item\' --\x3e\n    \x3c!-- /ko --\x3e\n    \x3c!-- /ko --\x3e\n    \x3c!-- /ko --\x3e\n    \x3c!-- ko if: question.hasColumns --\x3e\n    \x3c!-- ko foreach: question.columns --\x3e\n    <div data-bind="css: question.getColumnClass()">\n      \x3c!-- ko foreach: { data: $data, as: \'item\', afterRender: question.koAfterRender } --\x3e\n      \x3c!-- ko template: \'survey-radiogroup-item\' --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n    </div>\n    \x3c!-- /ko --\x3e\n    \x3c!-- /ko --\x3e\n    \x3c!-- ko if: question.canShowClearButton --\x3e\n    <div>\n      <input\n        type="button"\n        data-bind="click:question.clearValue, css: question.koCss().clearButton, value: question.clearButtonCaption"\n      />\n    </div>\n    \x3c!-- /ko --\x3e\n  </fieldset>\n</script>\n<script type="text/html" id="survey-radiogroup-item">\n  <div data-bind="css: question.getItemClass(item)">\n      <label data-bind="css: question.getLabelClass(item)">\n          <input type="radio" data-bind="attr: { name: question.name + \'_\' + question.id, id: question.inputId + \'_\' + question.getItemIndex(item), \'aria-required\': question.isRequired, \'aria-label\': item.locText.renderedHtml, role: \'radio\', \'aria-invalid\': question.errors.length > 0, \'aria-describedby\': question.errors.length > 0 ? question.id + \'_errors\' : null}, checkedValue: item.value, checked: question.renderedValue, enable: !question.isReadOnly && item.isEnabled, css: question.koCss().itemControl"/>\n          <span data-bind="css: question.koCss().materialDecorator">\n            <svg data-bind="css:question.koCss().itemDecorator" viewBox="-12 -12 24 24">\n                <circle r="6" cx="0" cy="0">\n            </svg>\n          </span>\n          <span class="check"></span>\n          <span data-bind="visible: !item.hideCaption, css: question.getControlLabelClass(item), attr: { title: item.locText.koRenderedHtml }">\n              \x3c!-- ko template: { name: \'survey-string\', data: item.locText } --\x3e\n              \x3c!-- /ko --\x3e\n          </span>\n      </label>\n      \x3c!-- ko if: question.hasOther && (item.value == question.otherItem.value) --\x3e\n      <div class="form-group" data-bind="template: { name: \'survey-comment\', data: {\'question\': question, \'visible\': question.isOtherSelected}}"></div>\n      \x3c!-- /ko --\x3e\n  </div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-rating">\n  <div data-bind="css: question.koCss().root">\n      \x3c!-- ko foreach: question.koVisibleRateValues --\x3e\n      <label data-bind="css: question.koGetCss($data)">\n          <input type="radio" class="sv-visuallyhidden" data-bind="attr: { name: question.name, id: question.inputId + \'_\' + $index(), value: $data.value, \'aria-required\': question.isRequired, \'aria-label\': $data.locText.text, \'aria-invalid\': question.errors.length > 0, \'aria-describedby\': question.errors.length > 0 ? question.id + \'_errors\' : null }, checkedValue: $data.value, checked: question.value, enable: !question.isReadOnly"/>\n          \x3c!-- ko if: $index() == 0 --\x3e\n          <span data-bind="css: question.koCss().minText">\n              \x3c!-- ko template: { name: \'survey-string\', data: question.locMinRateDescription } --\x3e\n              \x3c!-- /ko --\x3e\n          </span>\n          \x3c!-- /ko --\x3e\n\n          <span data-bind="css: question.koCss().itemText">\n              \x3c!-- ko template: { name: \'survey-string\', data: $data.locText } --\x3e\n              \x3c!-- /ko --\x3e\n          </span>\n\n          \x3c!-- ko if: $index() == (question.koVisibleRateValues().length-1) --\x3e\n          <span data-bind="css: question.koCss().maxText">\n              \x3c!-- ko template: { name: \'survey-string\', data: question.locMaxRateDescription } --\x3e\n              \x3c!-- /ko --\x3e\n          </span>\n          \x3c!-- /ko --\x3e\n      </label>\n      \x3c!-- /ko --\x3e\n  </div>\n  <div data-bind="visible: question.hasOther">\n      <div data-bind="template: { name: \'survey-comment\', data: { \'question\': question } }"></div>\n  </div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-signaturepad">\n    <div data-bind="css: question.koCss().root">\n        <div>\n            <canvas tabindex=\'0\'></canvas>\n        </div>\n        <div data-bind="css: question.koCss().controls">\n            <button type=\'button\' data-bind="css: question.koCss().clearButton, attr: { title: question.clearButtonCaption }">✖</button>\n        </div>\n    </div>\n</script>';
    },
    function (e, t) {
      e.exports =
        "<script type=\"text/html\" id=\"survey-question-text\">\n  \x3c!--ko if: !question.isInputTextUpdate --\x3e\n  <input\n    data-bind=\"disable: question.isReadOnly, attr: {type: question.inputType, size: question.size, id: question.inputId, placeholder: question.inputType === 'range' || question.isReadOnly ? undefined : question.placeHolder, maxLength: question.getMaxLength(), min: question.renderedMin, max: question.renderedMax, step: question.renderedStep, 'aria-required': question.isRequired, 'aria-label': question.locTitle.renderedHtml, 'aria-invalid': question.errors.length > 0, 'aria-describedby': question.errors.length > 0 ? question.id + '_errors' : null, autoComplete: question.autoComplete, list:question.dataListId}, value: question.value, css: question.koCss().root + (question.errors.length > 0 ? ' ' + question.koCss().onError: '')\"\n  />\n  \x3c!--/ko--\x3e\n  \x3c!--ko if: question.isInputTextUpdate --\x3e\n  <input\n    data-bind=\"disable: question.isReadOnly, attr: {type: question.inputType, size: question.size, id: question.inputId, placeholder: question.inputType === 'range' || question.isReadOnly ? undefined : question.placeHolder, maxLength: question.getMaxLength(), min: question.renderedMin, max: question.renderedMax, step: question.renderedStep, 'aria-required': question.isRequired, 'aria-label': question.locTitle.renderedHtml, 'aria-invalid': question.errors.length > 0, 'aria-describedby': question.errors.length > 0 ? question.id + '_errors' : null, autoComplete: question.autoComplete, list:question.dataListId}, textInput: question.value, css: question.koCss().root + (question.errors.length > 0 ? ' ' + question.koCss().onError: '')\"\n  />\n  \x3c!--/ko--\x3e\n  \x3c!--ko if: question.dataListId --\x3e\n  <datalist data-bind=\"attr: {id: question.dataListId}\">\n    \x3c!-- ko foreach: question.dataList --\x3e\n      <option data-bind=\"value:$data\"></option>\n    \x3c!--/ko--\x3e\n  </datalist>\n  \x3c!--/ko--\x3e\n</script>\n";
    },
    function (e, t) {
      e.exports =
        "<script type=\"text/html\" id=\"survey-question\">\n  <div data-bind=\"css: question.koRootCss(), style: { paddingLeft: question.paddingLeft, paddingRight: question.paddingRight, flexBasis: renderWidth, flexGrow: 1, flexShrink: 1, width: renderWidth, minWidth:  $data.minWidth, maxWidth: $data.maxWidth}, attr: { id: question.id, name: question.name, role: question.ariaRole, 'aria-labelledby': question.hasTitle ? question.ariaTitleId : null }\">\n      \x3c!-- ko if: question.hasTitleOnLeftTop --\x3e\n      \x3c!--ko template: { name: 'survey-question-title', data: question  } --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n      <div data-bind=\"visible: !question.isCollapsed, css: question.cssContent\" >\n          \x3c!-- ko if: question.errorLocation == 'top' --\x3e\n              \x3c!-- ko template: { name: 'survey-question-errors', data: question } --\x3e\n              \x3c!-- /ko --\x3e\n          \x3c!-- /ko --\x3e\n          \x3c!-- ko if: question.isDefaultRendering() --\x3e\n            \x3c!-- ko template: { name: question.koTemplateName(), data: question, afterRender: question.koQuestionAfterRender } --\x3e\n            \x3c!-- /ko --\x3e\n          \x3c!-- /ko --\x3e\n          \x3c!-- ko ifnot: question.isDefaultRendering() --\x3e\n            \x3c!-- ko component: { name: getComponentName(), params: { question: question } } --\x3e\n            \x3c!-- /ko --\x3e\n          \x3c!-- /ko --\x3e\n          \x3c!-- ko if: question.hasComment --\x3e\n          <div data-bind=\"css: question.koCss().formGroup\">\n            \x3c!-- ko template: { name: 'survey-string', data: question.locCommentText } --\x3e\n            \x3c!-- /ko --\x3e\n            <div data-bind=\"template: { name: 'survey-comment', data: {'question': question, 'visible': true } }\"></div>\n          </div>\n          \x3c!-- /ko --\x3e\n          \x3c!-- ko if: question.errorLocation == 'bottom' --\x3e\n              \x3c!-- ko template: { name: 'survey-question-errors', data: question } --\x3e\n              \x3c!-- /ko --\x3e\n          \x3c!-- /ko --\x3e\n          <div data-bind=\"visible: question.hasDescriptionUnderInput, css: koCss().descriptionUnderInput\">\n              \x3c!-- ko template: { name: 'survey-string', data: locDescription } --\x3e\x3c!-- /ko --\x3e\n          </div>\n      </div>\n      \x3c!-- ko if: question.hasTitleOnBottom --\x3e\n      \x3c!--ko template: { name: 'survey-question-title', data: question  } --\x3e\n      \x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n  </div>\n</script>\n";
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-question-title">\n  <div data-bind="css: question.cssHeader, click: question.clickTitleFunction">\n    <h5\n      data-bind="css: cssTitle, attr: { \'aria-label\': question.locTitle.renderedHtml, id: ariaTitleId}, click: toggleStateByClick"\n    >\n      \x3c!-- ko component: { name: getTitleComponentName(), params: { element: $data } } --\x3e\x3c!-- /ko --\x3e\n    </h5>\n    <div\n      data-bind="visible: question.hasDescriptionUnderTitle, css: koCss().description"\n    >\n      \x3c!-- ko template: { name: \'survey-string\', data: locDescription } --\x3e\x3c!-- /ko --\x3e\n    </div>\n  </div>\n</script>\n<script type="text/html" id="survey-question-title-content">\n  \x3c!-- ko if: $data.isRequireTextOnStart --\x3e\n  <span\n    data-bind="css: koCss().requiredText || koCss().panel.requiredText, text: $data.requiredText, attr: { \'aria-hidden\': true }"\n  ></span>\n  \x3c!-- /ko --\x3e\n  \x3c!-- ko if: no --\x3e\n  <span\n    style="position: static;"\n    data-bind="css: koCss().number, text: no, attr: { \'aria-hidden\': true }"\n  ></span>\n  \x3c!-- /ko --\x3e\n  \x3c!-- ko if: $data.isRequireTextBeforeTitle --\x3e\n  <span\n    data-bind="css: koCss().requiredText || koCss().panel.requiredText, text: $data.requiredText, attr: { \'aria-hidden\': true }"\n  ></span>\n  \x3c!-- /ko --\x3e\n  \x3c!-- ko template: { name: \'survey-string\', data: locTitle } --\x3e\x3c!-- /ko --\x3e\n  \x3c!-- ko if: $data.isRequireTextAfterTitle --\x3e\n  <span\n    data-bind="css: koCss().requiredText || koCss().panel.requiredText, text: $data.requiredText, attr: { \'aria-hidden\': true }"\n  ></span>\n  \x3c!-- /ko --\x3e\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-rows">\n  \x3c!-- ko foreach: { data: rows, as: \'row\'} --\x3e\n  \x3c!-- ko if: row.visible --\x3e\n  <div data-bind="css: panel.cssClasses.row">\n    \x3c!-- ko template: { name: "survey-row-content", afterRender: rowAfterRender } --\x3e\n    \x3c!-- /ko --\x3e\n  </div>\n  \x3c!-- /ko --\x3e\n  \x3c!-- /ko --\x3e\n</script>\n<script type="text/html" id="survey-row-content">\n  \x3c!-- ko if: row.isNeedRender --\x3e\n  \x3c!-- ko foreach: { data: row.elements, as: \'question\' , afterRender: row.koAfterRender } --\x3e\n  \x3c!-- ko if: question.isVisible --\x3e\n  \x3c!-- ko template: { name: question.koElementType, data: question, afterRender: $parent.koElementAfterRender } --\x3e\n  \x3c!-- /ko --\x3e\n  \x3c!-- /ko --\x3e\n  \x3c!-- /ko --\x3e\n  \x3c!-- /ko --\x3e\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-string">\n    \x3c!-- ko ifnot: koHasHtml --\x3e\n    <span style="position: static;" data-bind="text: koRenderedHtml"></span>\n    \x3c!-- /ko --\x3e\n    \x3c!-- ko if: koHasHtml --\x3e\n    <span style="position: static;" data-bind="html: koRenderedHtml"></span>\n    \x3c!-- /ko --\x3e\n</script>\n';
    },
    function (e, t) {
      e.exports =
        '<script type="text/html" id="survey-timerpanel">\n    <div data-bind="text:koTimerInfoText"></div>\n</script>\n';
    },
    function (e, t) {
      e.exports =
        "\x3c!-- ko template: { name: 'survey-panel', data: question, as: 'question', afterRender: question.koPanelAfterRender } --\x3e\x3c!-- /ko --\x3e";
    },
    function (e, t) {
      e.exports =
        '<div class="sv-popup" data-bind="visible: isVisible, click: clickOutside">\n  <div\n    class="sv-popup__container"\n    data-bind="style: { left: left, top: top }, class: styleClass, click: function() { return true; }, clickBubble: false"\n  >\n    \x3c!-- ko if: showPointer  --\x3e\n    <span\n      data-bind="style: {left: pointerTarget().left, top: pointerTarget().top }"\n      class="sv-popup__pointer"\n    ></span>\n    \x3c!-- /ko --\x3e\n\n    <div class="sv-popup__header"></div>\n\n    <div class="sv-popup__content">\n      \x3c!-- ko if: contentComponentName  --\x3e\n      \x3c!-- ko component: { name: contentComponentName, params: contentComponentData } --\x3e\x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n\n      \x3c!-- ko if: contentTemplateName  --\x3e\n      \x3c!-- ko template: { name: contentTemplateName, data: contentComponentData } --\x3e\x3c!-- /ko --\x3e\n      \x3c!-- /ko --\x3e\n    </div>\n\n    <div class="sv-popup__footer">\n      \x3c!-- ko if: isModal  --\x3e\n      <button data-bind="click: cancel, text: cancelButtonText">Cancel</button>\n      <button data-bind="click: apply, text: applyButtonText">Apply</button>\n      \x3c!-- /ko --\x3e\n    </div>\n  </div>\n</div>\n';
    },
    function (e, t) {
      e.exports =
        '<div data-bind="css: model.css.progressButtonsContainerCenter">\n    <div data-bind="css: model.css.progressButtonsContainer">\n        <div data-bind="css: getScrollButtonCss(true), click: clickScrollButton.bind($data, $element.nextElementSibling, true)" role="button"></div>\n        <div data-bind="css: model.css.progressButtonsListContainer">\n            <ul data-bind="foreach: model.visiblePages, css: model.css.progressButtonsList">\n                <li data-bind="css: $parent.getListElementCss($index), click: $parent.clickListElement.bind($parent, $index)">\n                    <div data-bind="css: css.progressButtonsPageTitle, text: locNavigationTitle.koRenderedHtml() || name, attr: { title: locNavigationTitle.koRenderedHtml() || name }"></div>\n                    <div data-bind="css: css.progressButtonsPageDescription, text: locNavigationDescription.koRenderedHtml(), attr: { title: locNavigationDescription.koRenderedHtml() }"></div>\n                </li>\n            </ul>\n        </div>\n        <div data-bind="css: getScrollButtonCss(false), click: clickScrollButton.bind($data, $element.previousElementSibling, false)" role="button"></div>\n    </div>\n</div>';
    },
    function (e, t) {
      e.exports =
        '<div data-bind="css: model.css.progress">\n    <div data-bind="css: model.css.progressBar, style:{ width: model.progressValue + \'%\' }"\n        role="progressbar" aria-valuemin="0" aria-valuemax="100">\n        <span data-bind="text: model.progressText, css: model.css.progressText + \' \' + model.css.progressTextInBar"></span>\n    </div>\n    <span data-bind="text: model.progressText, css: model.css.progressText + \' \' + model.css.progressTextUnderBar"></span>\n</div>';
    },
    function (e, t, n) {
      e.exports =
        n(82) +
        '\n' +
        n(83) +
        '\n' +
        n(84) +
        '\n' +
        n(85) +
        '\n' +
        n(86) +
        '\n' +
        n(87) +
        '\n' +
        n(112) +
        '\n' +
        n(113) +
        '\n' +
        n(114) +
        '\n' +
        n(110) +
        '\n' +
        n(111) +
        '\n' +
        n(88) +
        '\n' +
        n(89) +
        '\n' +
        n(90) +
        '\n' +
        n(91) +
        '\n' +
        n(92) +
        '\n' +
        n(93) +
        '\n' +
        n(94) +
        '\n' +
        n(95) +
        '\n' +
        n(96) +
        '\n' +
        n(97) +
        '\n' +
        n(98) +
        '\n' +
        n(99) +
        '\n' +
        n(100) +
        '\n' +
        n(101) +
        '\n' +
        n(102) +
        '\n' +
        n(103) +
        '\n' +
        n(105) +
        '\n' +
        n(104) +
        '\n' +
        n(106) +
        '\n' +
        n(107) +
        '\n' +
        n(108) +
        '\n' +
        n(109) +
        '\n';
    },
    function (e, t) {
      e.exports =
        '<div style="position: fixed; bottom: 3px; right: 10px; max-width: 60%;" data-bind="css: css.window.root">\n    <div data-bind="css: css.window.header.root">\n        <span data-bind="click:doExpand" style="width: 100%; cursor: pointer;">\n            <span style="padding-right:10px" data-bind="css: css.window.header.title, text: locTitle.koRenderedHtml"></span>\n            <span aria-hidden="true" data-bind="css: koExpandedCss"></span>\n        </span>\n        <span data-bind="visible:koExpanded, click:doExpand" style="float: right; cursor: pointer;">\n            <span style="padding-right:10px" data-bind="css: css.window.header.title">X</span>\n        </span>\n    </div>\n    <div data-bind="visible:koExpanded, css: css.window.body">\n        <div id="windowSurveyJS"></div>\n    </div>\n</div>';
    },
    function (e, t, n) {
      'use strict'
      /*!
       * Signature Pad v2.3.2
       * https://github.com/szimek/signature_pad
       *
       * Copyright 2017 Szymon Nowak
       * Released under the MIT license
       *
       * The main idea and some parts of the code (e.g. drawing variable width Bézier curve) are taken from:
       * http://corner.squareup.com/2012/07/smoother-signatures.html
       *
       * Implementation of interpolation using cubic Bézier curves is taken from:
       * http://benknowscode.wordpress.com/2012/09/14/path-interpolation-using-cubic-bezier-and-control-point-estimation-in-javascript
       *
       * Algorithm for approximated length of a Bézier curve is taken from:
       * http://www.lemoda.net/maths/bezier-length/index.html
       *
       */;
      function r(e, t, n) {
        (this.x = e), (this.y = t), (this.time = n || new Date().getTime());
      }
      function i(e, t, n, r) {
        (this.startPoint = e), (this.control1 = t), (this.control2 = n), (this.endPoint = r);
      }
      function o(e, t, n) {
        var r,
          i,
          o,
          a = null,
          s = 0;
        n || (n = {});
        var l = function () {
          (s = !1 === n.leading ? 0 : Date.now()),
            (a = null),
            (o = e.apply(r, i)),
            a || (r = i = null);
        };
        return function () {
          var u = Date.now();
          s || !1 !== n.leading || (s = u);
          var c = t - (u - s);
          return (
            (r = this),
            (i = arguments),
            c <= 0 || c > t
              ? (a && (clearTimeout(a), (a = null)),
                (s = u),
                (o = e.apply(r, i)),
                a || (r = i = null))
              : a || !1 === n.trailing || (a = setTimeout(l, c)),
            o
          );
        };
      }
      function a(e, t) {
        var n = this,
          r = t || {};
        (this.velocityFilterWeight = r.velocityFilterWeight || 0.7),
          (this.minWidth = r.minWidth || 0.5),
          (this.maxWidth = r.maxWidth || 2.5),
          (this.throttle = 'throttle' in r ? r.throttle : 16),
          (this.minDistance = 'minDistance' in r ? r.minDistance : 5),
          this.throttle
            ? (this._strokeMoveUpdate = o(a.prototype._strokeUpdate, this.throttle))
            : (this._strokeMoveUpdate = a.prototype._strokeUpdate),
          (this.dotSize =
            r.dotSize ||
            function () {
              return (this.minWidth + this.maxWidth) / 2;
            }),
          (this.penColor = r.penColor || 'black'),
          (this.backgroundColor = r.backgroundColor || 'rgba(0,0,0,0)'),
          (this.onBegin = r.onBegin),
          (this.onEnd = r.onEnd),
          (this._canvas = e),
          (this._ctx = e.getContext('2d')),
          this.clear(),
          (this._handleMouseDown = function (e) {
            1 === e.which && ((n._mouseButtonDown = !0), n._strokeBegin(e));
          }),
          (this._handleMouseMove = function (e) {
            n._mouseButtonDown && n._strokeMoveUpdate(e);
          }),
          (this._handleMouseUp = function (e) {
            1 === e.which && n._mouseButtonDown && ((n._mouseButtonDown = !1), n._strokeEnd(e));
          }),
          (this._handleTouchStart = function (e) {
            if (1 === e.targetTouches.length) {
              var t = e.changedTouches[0];
              n._strokeBegin(t);
            }
          }),
          (this._handleTouchMove = function (e) {
            e.preventDefault();
            var t = e.targetTouches[0];
            n._strokeMoveUpdate(t);
          }),
          (this._handleTouchEnd = function (e) {
            e.target === n._canvas && (e.preventDefault(), n._strokeEnd(e));
          }),
          this.on();
      }
      (r.prototype.velocityFrom = function (e) {
        return this.time !== e.time ? this.distanceTo(e) / (this.time - e.time) : 1;
      }),
        (r.prototype.distanceTo = function (e) {
          return Math.sqrt(Math.pow(this.x - e.x, 2) + Math.pow(this.y - e.y, 2));
        }),
        (r.prototype.equals = function (e) {
          return this.x === e.x && this.y === e.y && this.time === e.time;
        }),
        (i.prototype.length = function () {
          for (var e = 10, t = 0, n = void 0, r = void 0, i = 0; i <= e; i += 1) {
            var o = i / e,
              a = this._point(
                o,
                this.startPoint.x,
                this.control1.x,
                this.control2.x,
                this.endPoint.x
              ),
              s = this._point(
                o,
                this.startPoint.y,
                this.control1.y,
                this.control2.y,
                this.endPoint.y
              );
            if (i > 0) {
              var l = a - n,
                u = s - r;
              t += Math.sqrt(l * l + u * u);
            }
            (n = a), (r = s);
          }
          return t;
        }),
        (i.prototype._point = function (e, t, n, r, i) {
          return (
            t * (1 - e) * (1 - e) * (1 - e) +
            3 * n * (1 - e) * (1 - e) * e +
            3 * r * (1 - e) * e * e +
            i * e * e * e
          );
        }),
        (a.prototype.clear = function () {
          var e = this._ctx,
            t = this._canvas;
          (e.fillStyle = this.backgroundColor),
            e.clearRect(0, 0, t.width, t.height),
            e.fillRect(0, 0, t.width, t.height),
            (this._data = []),
            this._reset(),
            (this._isEmpty = !0);
        }),
        (a.prototype.fromDataURL = function (e) {
          var t = this,
            n = arguments.length > 1 && void 0 !== arguments[1] ? arguments[1] : {},
            r = new Image(),
            i = n.ratio || window.devicePixelRatio || 1,
            o = n.width || this._canvas.width / i,
            a = n.height || this._canvas.height / i;
          this._reset(),
            (r.src = e),
            (r.onload = function () {
              t._ctx.drawImage(r, 0, 0, o, a);
            }),
            (this._isEmpty = !1);
        }),
        (a.prototype.toDataURL = function (e) {
          var t;
          switch (e) {
            case 'image/svg+xml':
              return this._toSVG();
            default:
              for (var n = arguments.length, r = Array(n > 1 ? n - 1 : 0), i = 1; i < n; i++)
                r[i - 1] = arguments[i];
              return (t = this._canvas).toDataURL.apply(t, [e].concat(r));
          }
        }),
        (a.prototype.on = function () {
          this._handleMouseEvents(), this._handleTouchEvents();
        }),
        (a.prototype.off = function () {
          this._canvas.removeEventListener('mousedown', this._handleMouseDown),
            this._canvas.removeEventListener('mousemove', this._handleMouseMove),
            document.removeEventListener('mouseup', this._handleMouseUp),
            this._canvas.removeEventListener('touchstart', this._handleTouchStart),
            this._canvas.removeEventListener('touchmove', this._handleTouchMove),
            this._canvas.removeEventListener('touchend', this._handleTouchEnd);
        }),
        (a.prototype.isEmpty = function () {
          return this._isEmpty;
        }),
        (a.prototype._strokeBegin = function (e) {
          this._data.push([]),
            this._reset(),
            this._strokeUpdate(e),
            'function' == typeof this.onBegin && this.onBegin(e);
        }),
        (a.prototype._strokeUpdate = function (e) {
          var t = e.clientX,
            n = e.clientY,
            r = this._createPoint(t, n),
            i = this._data[this._data.length - 1],
            o = i && i[i.length - 1],
            a = o && r.distanceTo(o) < this.minDistance;
          if (!o || !a) {
            var s = this._addPoint(r),
              l = s.curve,
              u = s.widths;
            l && u && this._drawCurve(l, u.start, u.end),
              this._data[this._data.length - 1].push({
                x: r.x,
                y: r.y,
                time: r.time,
                color: this.penColor,
              });
          }
        }),
        (a.prototype._strokeEnd = function (e) {
          var t = this.points.length > 2,
            n = this.points[0];
          if ((!t && n && this._drawDot(n), n)) {
            var r = this._data[this._data.length - 1],
              i = r[r.length - 1];
            n.equals(i) || r.push({ x: n.x, y: n.y, time: n.time, color: this.penColor });
          }
          'function' == typeof this.onEnd && this.onEnd(e);
        }),
        (a.prototype._handleMouseEvents = function () {
          (this._mouseButtonDown = !1),
            this._canvas.addEventListener('mousedown', this._handleMouseDown),
            this._canvas.addEventListener('mousemove', this._handleMouseMove),
            document.addEventListener('mouseup', this._handleMouseUp);
        }),
        (a.prototype._handleTouchEvents = function () {
          (this._canvas.style.msTouchAction = 'none'),
            (this._canvas.style.touchAction = 'none'),
            this._canvas.addEventListener('touchstart', this._handleTouchStart),
            this._canvas.addEventListener('touchmove', this._handleTouchMove),
            this._canvas.addEventListener('touchend', this._handleTouchEnd);
        }),
        (a.prototype._reset = function () {
          (this.points = []),
            (this._lastVelocity = 0),
            (this._lastWidth = (this.minWidth + this.maxWidth) / 2),
            (this._ctx.fillStyle = this.penColor);
        }),
        (a.prototype._createPoint = function (e, t, n) {
          var i = this._canvas.getBoundingClientRect();
          return new r(e - i.left, t - i.top, n || new Date().getTime());
        }),
        (a.prototype._addPoint = function (e) {
          var t = this.points,
            n = void 0;
          if ((t.push(e), t.length > 2)) {
            3 === t.length && t.unshift(t[0]),
              (n = this._calculateCurveControlPoints(t[0], t[1], t[2]));
            var r = n.c2;
            n = this._calculateCurveControlPoints(t[1], t[2], t[3]);
            var o = n.c1,
              a = new i(t[1], r, o, t[2]),
              s = this._calculateCurveWidths(a);
            return t.shift(), { curve: a, widths: s };
          }
          return {};
        }),
        (a.prototype._calculateCurveControlPoints = function (e, t, n) {
          var i = e.x - t.x,
            o = e.y - t.y,
            a = t.x - n.x,
            s = t.y - n.y,
            l = { x: (e.x + t.x) / 2, y: (e.y + t.y) / 2 },
            u = { x: (t.x + n.x) / 2, y: (t.y + n.y) / 2 },
            c = Math.sqrt(i * i + o * o),
            p = Math.sqrt(a * a + s * s),
            d = l.x - u.x,
            h = l.y - u.y,
            f = p / (c + p),
            m = { x: u.x + d * f, y: u.y + h * f },
            g = t.x - m.x,
            y = t.y - m.y;
          return { c1: new r(l.x + g, l.y + y), c2: new r(u.x + g, u.y + y) };
        }),
        (a.prototype._calculateCurveWidths = function (e) {
          var t = e.startPoint,
            n = e.endPoint,
            r = { start: null, end: null },
            i =
              this.velocityFilterWeight * n.velocityFrom(t) +
              (1 - this.velocityFilterWeight) * this._lastVelocity,
            o = this._strokeWidth(i);
          return (
            (r.start = this._lastWidth),
            (r.end = o),
            (this._lastVelocity = i),
            (this._lastWidth = o),
            r
          );
        }),
        (a.prototype._strokeWidth = function (e) {
          return Math.max(this.maxWidth / (e + 1), this.minWidth);
        }),
        (a.prototype._drawPoint = function (e, t, n) {
          var r = this._ctx;
          r.moveTo(e, t), r.arc(e, t, n, 0, 2 * Math.PI, !1), (this._isEmpty = !1);
        }),
        (a.prototype._drawCurve = function (e, t, n) {
          var r = this._ctx,
            i = n - t,
            o = Math.floor(e.length());
          r.beginPath();
          for (var a = 0; a < o; a += 1) {
            var s = a / o,
              l = s * s,
              u = l * s,
              c = 1 - s,
              p = c * c,
              d = p * c,
              h = d * e.startPoint.x;
            (h += 3 * p * s * e.control1.x),
              (h += 3 * c * l * e.control2.x),
              (h += u * e.endPoint.x);
            var f = d * e.startPoint.y;
            (f += 3 * p * s * e.control1.y),
              (f += 3 * c * l * e.control2.y),
              (f += u * e.endPoint.y);
            var m = t + u * i;
            this._drawPoint(h, f, m);
          }
          r.closePath(), r.fill();
        }),
        (a.prototype._drawDot = function (e) {
          var t = this._ctx,
            n = 'function' == typeof this.dotSize ? this.dotSize() : this.dotSize;
          t.beginPath(), this._drawPoint(e.x, e.y, n), t.closePath(), t.fill();
        }),
        (a.prototype._fromData = function (e, t, n) {
          for (var i = 0; i < e.length; i += 1) {
            var o = e[i];
            if (o.length > 1)
              for (var a = 0; a < o.length; a += 1) {
                var s = o[a],
                  l = new r(s.x, s.y, s.time),
                  u = s.color;
                if (0 === a) (this.penColor = u), this._reset(), this._addPoint(l);
                else if (a !== o.length - 1) {
                  var c = this._addPoint(l),
                    p = c.curve,
                    d = c.widths;
                  p && d && t(p, d, u);
                }
              }
            else {
              this._reset();
              n(o[0]);
            }
          }
        }),
        (a.prototype._toSVG = function () {
          var e = this,
            t = this._data,
            n = this._canvas,
            r = Math.max(window.devicePixelRatio || 1, 1),
            i = 0,
            o = 0,
            a = n.width / r,
            s = n.height / r,
            l = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          l.setAttributeNS(null, 'width', n.width),
            l.setAttributeNS(null, 'height', n.height),
            this._fromData(
              t,
              function (e, t, n) {
                var r = document.createElement('path');
                if (
                  !(
                    isNaN(e.control1.x) ||
                    isNaN(e.control1.y) ||
                    isNaN(e.control2.x) ||
                    isNaN(e.control2.y)
                  )
                ) {
                  var i =
                    'M ' +
                    e.startPoint.x.toFixed(3) +
                    ',' +
                    e.startPoint.y.toFixed(3) +
                    ' C ' +
                    e.control1.x.toFixed(3) +
                    ',' +
                    e.control1.y.toFixed(3) +
                    ' ' +
                    e.control2.x.toFixed(3) +
                    ',' +
                    e.control2.y.toFixed(3) +
                    ' ' +
                    e.endPoint.x.toFixed(3) +
                    ',' +
                    e.endPoint.y.toFixed(3);
                  r.setAttribute('d', i),
                    r.setAttribute('stroke-width', (2.25 * t.end).toFixed(3)),
                    r.setAttribute('stroke', n),
                    r.setAttribute('fill', 'none'),
                    r.setAttribute('stroke-linecap', 'round'),
                    l.appendChild(r);
                }
              },
              function (t) {
                var n = document.createElement('circle'),
                  r = 'function' == typeof e.dotSize ? e.dotSize() : e.dotSize;
                n.setAttribute('r', r),
                  n.setAttribute('cx', t.x),
                  n.setAttribute('cy', t.y),
                  n.setAttribute('fill', t.color),
                  l.appendChild(n);
              }
            );
          var u = 'data:image/svg+xml;base64,',
            c =
              '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="' +
              i +
              ' ' +
              o +
              ' ' +
              a +
              ' ' +
              s +
              '" width="' +
              a +
              '" height="' +
              s +
              '">',
            p = l.innerHTML;
          if (void 0 === p) {
            var d = document.createElement('dummy'),
              h = l.childNodes;
            d.innerHTML = '';
            for (var f = 0; f < h.length; f += 1) d.appendChild(h[f].cloneNode(!0));
            p = d.innerHTML;
          }
          var m = '</svg>',
            g = c + p + m;
          return u + btoa(g);
        }),
        (a.prototype.fromData = function (e) {
          var t = this;
          this.clear(),
            this._fromData(
              e,
              function (e, n) {
                return t._drawCurve(e, n.start, n.end);
              },
              function (e) {
                return t._drawDot(e);
              }
            ),
            (this._data = e);
        }),
        (a.prototype.toData = function () {
          return this._data;
        }),
        (t.a = a);
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(4),
        o = n(6),
        a = n(10),
        s = n(1);
      n.d(t, 'a', function () {
        return l;
      });
      var l = (function (e) {
        function t(t, n) {
          void 0 === t && (t = null), void 0 === n && (n = null);
          var r = e.call(this) || this;
          return (
            (r.expressionIsRunning = !1),
            (r.isCalculated = !1),
            t && (r.name = t),
            n && (r.expression = n),
            r
          );
        }
        return (
          n.i(r.b)(t, e),
          (t.prototype.setOwner = function (e) {
            (this.data = e), this.rerunExpression();
          }),
          (t.prototype.getType = function () {
            return 'calculatedvalue';
          }),
          Object.defineProperty(t.prototype, 'owner', {
            get: function () {
              return this.data;
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'name', {
            get: function () {
              return this.getPropertyValue('name', '');
            },
            set: function (e) {
              this.setPropertyValue('name', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'includeIntoResult', {
            get: function () {
              return this.getPropertyValue('includeIntoResult', !1);
            },
            set: function (e) {
              this.setPropertyValue('includeIntoResult', e);
            },
            enumerable: !0,
            configurable: !0,
          }),
          Object.defineProperty(t.prototype, 'expression', {
            get: function () {
              return this.getPropertyValue('expression', '');
            },
            set: function (e) {
              this.setPropertyValue('expression', e), this.rerunExpression();
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.locCalculation = function () {
            this.expressionIsRunning = !0;
          }),
          (t.prototype.unlocCalculation = function () {
            this.expressionIsRunning = !1;
          }),
          (t.prototype.resetCalculation = function () {
            this.isCalculated = !1;
          }),
          (t.prototype.doCalculation = function (e, t, n) {
            this.isCalculated || (this.runExpressionCore(e, t, n), (this.isCalculated = !0));
          }),
          (t.prototype.runExpression = function (e, t) {
            this.runExpressionCore(null, e, t);
          }),
          Object.defineProperty(t.prototype, 'value', {
            get: function () {
              if (this.data) return this.data.getVariable(this.name);
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.setValue = function (e) {
            this.data && this.data.setVariable(this.name, e);
          }),
          Object.defineProperty(t.prototype, 'canRunExpression', {
            get: function () {
              return !(
                !this.data ||
                this.isLoadingFromJson ||
                !this.expression ||
                this.expressionIsRunning ||
                !this.name
              );
            },
            enumerable: !0,
            configurable: !0,
          }),
          (t.prototype.rerunExpression = function () {
            this.canRunExpression &&
              this.runExpression(this.data.getFilteredValues(), this.data.getFilteredProperties());
          }),
          (t.prototype.runExpressionCore = function (e, t, n) {
            this.canRunExpression &&
              (this.ensureExpression(t),
              this.locCalculation(),
              e && this.runDependentExpressions(e, t, n),
              this.expressionRunner.run(t, n));
          }),
          (t.prototype.runDependentExpressions = function (e, t, n) {
            var r = this.expressionRunner.getVariables();
            if (r)
              for (var i = 0; i < e.length; i++) {
                var o = e[i];
                o === this ||
                  r.indexOf(o.name) < 0 ||
                  (o.doCalculation(e, t, n), (t[o.name] = o.value));
              }
          }),
          (t.prototype.ensureExpression = function (e) {
            var t = this;
            this.expressionRunner ||
              ((this.expressionRunner = new a.b(this.expression)),
              (this.expressionRunner.onRunComplete = function (e) {
                i.a.isTwoValueEquals(e, t.value) || t.setValue(e), t.unlocCalculation();
              }));
          }),
          t
        );
      })(o.b);
      s.b.addClass(
        'calculatedvalue',
        [{ name: '!name', isUnique: !0 }, 'expression:expression', 'includeIntoResult:boolean'],
        function () {
          return new l();
        },
        'base'
      );
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(6),
        a = n(10);
      n.d(t, 'a', function () {
        return l;
      }),
        n.d(t, 'b', function () {
          return u;
        });
      var s = (function (e) {
          function t(t) {
            void 0 === t && (t = null);
            var n = e.call(this) || this;
            return n.createLocalizableString('html', n), (n.expression = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'expressionitem';
            }),
            (t.prototype.runCondition = function (e, t) {
              return !!this.expression && new a.a(this.expression).run(e, t);
            }),
            Object.defineProperty(t.prototype, 'expression', {
              get: function () {
                return this.getPropertyValue('expression', '');
              },
              set: function (e) {
                this.setPropertyValue('expression', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locHtml', {
              get: function () {
                return this.getLocalizableString('html');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getLocale = function () {
              return this.locOwner ? this.locOwner.getLocale() : '';
            }),
            (t.prototype.getMarkdownHtml = function (e, t) {
              return this.locOwner ? this.locOwner.getMarkdownHtml(e, t) : null;
            }),
            (t.prototype.getProcessedText = function (e) {
              return this.locOwner ? this.locOwner.getProcessedText(e) : e;
            }),
            t
          );
        })(o.b),
        l = (function (e) {
          function t(t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.call(this, t) || this;
            return r.createLocalizableString('html', r), (r.html = n), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'htmlconditionitem';
            }),
            Object.defineProperty(t.prototype, 'html', {
              get: function () {
                return this.getLocalizableStringText('html');
              },
              set: function (e) {
                this.setLocalizableStringText('html', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locHtml', {
              get: function () {
                return this.getLocalizableString('html');
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(s),
        u = (function (e) {
          function t(t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.call(this, t) || this;
            return r.createLocalizableString('url', r), (r.url = n), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'urlconditionitem';
            }),
            Object.defineProperty(t.prototype, 'url', {
              get: function () {
                return this.getLocalizableStringText('url');
              },
              set: function (e) {
                this.setLocalizableStringText('url', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'locUrl', {
              get: function () {
                return this.getLocalizableString('url');
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(s);
      i.b.addClass(
        'expressionitem',
        ['expression:condition'],
        function () {
          return new s();
        },
        'base'
      ),
        i.b.addClass(
          'htmlconditionitem',
          [{ name: 'html:html', serializationProperty: 'locHtml' }],
          function () {
            return new l();
          },
          'expressionitem'
        ),
        i.b.addClass(
          'urlconditionitem',
          [{ name: 'url:string', serializationProperty: 'locUrl' }],
          function () {
            return new u();
          },
          'expressionitem'
        );
    },
    function (e, t, n) {
      'use strict';
      function r(e, t) {
        function n() {
          return e.substring(nr, tr);
        }
        function r(e, t) {
          return { type: 'literal', text: e, ignoreCase: t };
        }
        function i(e, t, n) {
          return { type: 'class', parts: e, inverted: t, ignoreCase: n };
        }
        function s() {
          return { type: 'end' };
        }
        function l(e) {
          return { type: 'other', description: e };
        }
        function u(t) {
          var n,
            r = rr[t];
          if (r) return r;
          for (n = t - 1; !rr[n]; ) n--;
          for (r = rr[n], r = { line: r.line, column: r.column }; n < t; )
            10 === e.charCodeAt(n) ? (r.line++, (r.column = 1)) : r.column++, n++;
          return (rr[t] = r), r;
        }
        function c(e, t) {
          var n = u(e),
            r = u(t);
          return {
            start: { offset: e, line: n.line, column: n.column },
            end: { offset: t, line: r.line, column: r.column },
          };
        }
        function p(e) {
          tr < ir || (tr > ir && ((ir = tr), (or = [])), or.push(e));
        }
        function d(e, t, n) {
          return new a(a.buildMessage(e, t), e, t, n);
        }
        function h() {
          var e,
            t,
            n,
            r,
            i,
            o,
            a,
            s,
            l,
            u = 34 * tr + 0,
            c = sr[u];
          if (c) return (tr = c.nextPos), c.result;
          if (((e = tr), (t = U()) !== K))
            if ((n = m()) !== K) {
              for (
                r = [],
                  i = tr,
                  o = U(),
                  o !== K
                    ? ((a = f()),
                      a !== K
                        ? ((s = U()),
                          s !== K
                            ? ((l = m()),
                              l !== K ? ((o = [o, a, s, l]), (i = o)) : ((tr = i), (i = K)))
                            : ((tr = i), (i = K)))
                        : ((tr = i), (i = K)))
                    : ((tr = i), (i = K));
                i !== K;

              )
                r.push(i),
                  (i = tr),
                  (o = U()),
                  o !== K
                    ? ((a = f()),
                      a !== K
                        ? ((s = U()),
                          s !== K
                            ? ((l = m()),
                              l !== K ? ((o = [o, a, s, l]), (i = o)) : ((tr = i), (i = K)))
                            : ((tr = i), (i = K)))
                        : ((tr = i), (i = K)))
                    : ((tr = i), (i = K));
              r !== K
                ? ((i = U()), i !== K ? ((nr = e), (t = Y(n, r)), (e = t)) : ((tr = e), (e = K)))
                : ((tr = e), (e = K));
            } else (tr = e), (e = K);
          else (tr = e), (e = K);
          return (sr[u] = { nextPos: tr, result: e }), e;
        }
        function f() {
          var t,
            n,
            r = 34 * tr + 1,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              e.substr(tr, 2) === ee ? ((n = ee), (tr += 2)) : ((n = K), 0 === ar && p(te)),
              n === K &&
                (e.substr(tr, 2).toLowerCase() === ne
                  ? ((n = e.substr(tr, 2)), (tr += 2))
                  : ((n = K), 0 === ar && p(re))),
              n !== K && ((nr = t), (n = ie())),
              (t = n),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function m() {
          var e,
            t,
            n,
            r,
            i,
            o,
            a,
            s,
            l = 34 * tr + 2,
            u = sr[l];
          if (u) return (tr = u.nextPos), u.result;
          if (((e = tr), (t = y()) !== K)) {
            for (
              n = [],
                r = tr,
                i = U(),
                i !== K
                  ? ((o = g()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = y()),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
              r !== K;

            )
              n.push(r),
                (r = tr),
                (i = U()),
                i !== K
                  ? ((o = g()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = y()),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
            n !== K ? ((nr = e), (t = Y(t, n)), (e = t)) : ((tr = e), (e = K));
          } else (tr = e), (e = K);
          return (sr[l] = { nextPos: tr, result: e }), e;
        }
        function g() {
          var t,
            n,
            r = 34 * tr + 3,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              e.substr(tr, 2) === oe ? ((n = oe), (tr += 2)) : ((n = K), 0 === ar && p(ae)),
              n === K &&
                (e.substr(tr, 3).toLowerCase() === se
                  ? ((n = e.substr(tr, 3)), (tr += 3))
                  : ((n = K), 0 === ar && p(le))),
              n !== K && ((nr = t), (n = ue())),
              (t = n),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function y() {
          var e,
            t,
            n,
            r,
            i,
            o,
            a,
            s,
            l = 34 * tr + 4,
            u = sr[l];
          if (u) return (tr = u.nextPos), u.result;
          if (((e = tr), (t = b()) !== K)) {
            for (
              n = [],
                r = tr,
                i = U(),
                i !== K
                  ? ((o = v()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = b()),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
              r !== K;

            )
              n.push(r),
                (r = tr),
                (i = U()),
                i !== K
                  ? ((o = v()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = b()),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
            n !== K ? ((nr = e), (t = ce(t, n)), (e = t)) : ((tr = e), (e = K));
          } else (tr = e), (e = K);
          return (sr[l] = { nextPos: tr, result: e }), e;
        }
        function v() {
          var t,
            n,
            r = 34 * tr + 5,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              e.substr(tr, 2) === pe ? ((n = pe), (tr += 2)) : ((n = K), 0 === ar && p(de)),
              n === K &&
                (e.substr(tr, 11).toLowerCase() === he
                  ? ((n = e.substr(tr, 11)), (tr += 11))
                  : ((n = K), 0 === ar && p(fe))),
              n !== K && ((nr = t), (n = me())),
              (t = n),
              t === K &&
                ((t = tr),
                e.substr(tr, 2) === ge ? ((n = ge), (tr += 2)) : ((n = K), 0 === ar && p(ye)),
                n === K &&
                  (e.substr(tr, 14).toLowerCase() === ve
                    ? ((n = e.substr(tr, 14)), (tr += 14))
                    : ((n = K), 0 === ar && p(be))),
                n !== K && ((nr = t), (n = xe())),
                (t = n) === K &&
                  ((t = tr),
                  61 === e.charCodeAt(tr) ? ((n = Ce), tr++) : ((n = K), 0 === ar && p(we)),
                  n === K &&
                    (e.substr(tr, 5).toLowerCase() === Pe
                      ? ((n = e.substr(tr, 5)), (tr += 5))
                      : ((n = K), 0 === ar && p(Ve))),
                  n !== K && ((nr = t), (n = ke())),
                  (t = n) === K &&
                    ((t = tr),
                    e.substr(tr, 2) === _e ? ((n = _e), (tr += 2)) : ((n = K), 0 === ar && p(Te)),
                    n === K &&
                      (e.substr(tr, 8).toLowerCase() === Se
                        ? ((n = e.substr(tr, 8)), (tr += 8))
                        : ((n = K), 0 === ar && p(Re))),
                    n !== K && ((nr = t), (n = qe())),
                    (t = n) === K &&
                      ((t = tr),
                      60 === e.charCodeAt(tr) ? ((n = Ee), tr++) : ((n = K), 0 === ar && p(Oe)),
                      n === K &&
                        (e.substr(tr, 4).toLowerCase() === Ie
                          ? ((n = e.substr(tr, 4)), (tr += 4))
                          : ((n = K), 0 === ar && p(Le))),
                      n !== K && ((nr = t), (n = Ae())),
                      (t = n) === K &&
                        ((t = tr),
                        62 === e.charCodeAt(tr) ? ((n = De), tr++) : ((n = K), 0 === ar && p(Ne)),
                        n === K &&
                          (e.substr(tr, 7).toLowerCase() === je
                            ? ((n = e.substr(tr, 7)), (tr += 7))
                            : ((n = K), 0 === ar && p(Fe))),
                        n !== K && ((nr = t), (n = Me())),
                        (t = n)))))),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function b() {
          var e,
            t,
            n,
            r,
            i,
            o,
            a,
            s,
            l = 34 * tr + 6,
            u = sr[l];
          if (u) return (tr = u.nextPos), u.result;
          if (((e = tr), (t = C()) !== K)) {
            for (
              n = [],
                r = tr,
                i = U(),
                i !== K
                  ? ((o = x()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = C()),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
              r !== K;

            )
              n.push(r),
                (r = tr),
                (i = U()),
                i !== K
                  ? ((o = x()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = C()),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
            n !== K ? ((nr = e), (t = Y(t, n)), (e = t)) : ((tr = e), (e = K));
          } else (tr = e), (e = K);
          return (sr[l] = { nextPos: tr, result: e }), e;
        }
        function x() {
          var t,
            n,
            r = 34 * tr + 7,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              43 === e.charCodeAt(tr) ? ((n = Be), tr++) : ((n = K), 0 === ar && p(ze)),
              n !== K && ((nr = t), (n = Qe())),
              (t = n),
              t === K &&
                ((t = tr),
                45 === e.charCodeAt(tr) ? ((n = He), tr++) : ((n = K), 0 === ar && p($e)),
                n !== K && ((nr = t), (n = Ue())),
                (t = n)),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function C() {
          var e,
            t,
            n,
            r,
            i,
            o,
            a,
            s,
            l = 34 * tr + 8,
            u = sr[l];
          if (u) return (tr = u.nextPos), u.result;
          if (((e = tr), (t = P()) !== K)) {
            for (
              n = [],
                r = tr,
                i = U(),
                i !== K
                  ? ((o = w()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = P()),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
              r !== K;

            )
              n.push(r),
                (r = tr),
                (i = U()),
                i !== K
                  ? ((o = w()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = P()),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
            n !== K ? ((nr = e), (t = Y(t, n)), (e = t)) : ((tr = e), (e = K));
          } else (tr = e), (e = K);
          return (sr[l] = { nextPos: tr, result: e }), e;
        }
        function w() {
          var t,
            n,
            r = 34 * tr + 9,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              42 === e.charCodeAt(tr) ? ((n = We), tr++) : ((n = K), 0 === ar && p(Je)),
              n !== K && ((nr = t), (n = Ge())),
              (t = n),
              t === K &&
                ((t = tr),
                47 === e.charCodeAt(tr) ? ((n = Ke), tr++) : ((n = K), 0 === ar && p(Ze)),
                n !== K && ((nr = t), (n = Xe())),
                (t = n) === K &&
                  ((t = tr),
                  37 === e.charCodeAt(tr) ? ((n = Ye), tr++) : ((n = K), 0 === ar && p(et)),
                  n !== K && ((nr = t), (n = tt())),
                  (t = n))),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function P() {
          var e,
            t,
            n,
            r,
            i,
            o,
            a,
            s,
            l = 34 * tr + 10,
            u = sr[l];
          if (u) return (tr = u.nextPos), u.result;
          if (((e = tr), (t = k()) !== K)) {
            for (
              n = [],
                r = tr,
                i = U(),
                i !== K
                  ? ((o = V()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = k()),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
              r !== K;

            )
              n.push(r),
                (r = tr),
                (i = U()),
                i !== K
                  ? ((o = V()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = k()),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
            n !== K ? ((nr = e), (t = Y(t, n)), (e = t)) : ((tr = e), (e = K));
          } else (tr = e), (e = K);
          return (sr[l] = { nextPos: tr, result: e }), e;
        }
        function V() {
          var t,
            n,
            r = 34 * tr + 11,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              94 === e.charCodeAt(tr) ? ((n = nt), tr++) : ((n = K), 0 === ar && p(rt)),
              n === K &&
                (e.substr(tr, 5).toLowerCase() === it
                  ? ((n = e.substr(tr, 5)), (tr += 5))
                  : ((n = K), 0 === ar && p(ot))),
              n !== K && ((nr = t), (n = at())),
              (t = n),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function k() {
          var e,
            t,
            n,
            r,
            i,
            o,
            a,
            s,
            l = 34 * tr + 12,
            u = sr[l];
          if (u) return (tr = u.nextPos), u.result;
          if (((e = tr), (t = T()) !== K)) {
            for (
              n = [],
                r = tr,
                i = U(),
                i !== K
                  ? ((o = _()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = T()),
                            s === K && (s = null),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
              r !== K;

            )
              n.push(r),
                (r = tr),
                (i = U()),
                i !== K
                  ? ((o = _()),
                    o !== K
                      ? ((a = U()),
                        a !== K
                          ? ((s = T()),
                            s === K && (s = null),
                            s !== K ? ((i = [i, o, a, s]), (r = i)) : ((tr = r), (r = K)))
                          : ((tr = r), (r = K)))
                      : ((tr = r), (r = K)))
                  : ((tr = r), (r = K));
            n !== K ? ((nr = e), (t = ce(t, n)), (e = t)) : ((tr = e), (e = K));
          } else (tr = e), (e = K);
          return (sr[l] = { nextPos: tr, result: e }), e;
        }
        function _() {
          var t,
            n,
            r = 34 * tr + 13,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              e.substr(tr, 2) === st ? ((n = st), (tr += 2)) : ((n = K), 0 === ar && p(lt)),
              n === K &&
                (e.substr(tr, 8).toLowerCase() === ut
                  ? ((n = e.substr(tr, 8)), (tr += 8))
                  : ((n = K), 0 === ar && p(ct)),
                n === K &&
                  (e.substr(tr, 7).toLowerCase() === pt
                    ? ((n = e.substr(tr, 7)), (tr += 7))
                    : ((n = K), 0 === ar && p(dt)))),
              n !== K && ((nr = t), (n = ht())),
              (t = n),
              t === K &&
                ((t = tr),
                e.substr(tr, 11).toLowerCase() === ft
                  ? ((n = e.substr(tr, 11)), (tr += 11))
                  : ((n = K), 0 === ar && p(mt)),
                n === K &&
                  (e.substr(tr, 10).toLowerCase() === gt
                    ? ((n = e.substr(tr, 10)), (tr += 10))
                    : ((n = K), 0 === ar && p(yt))),
                n !== K && ((nr = t), (n = vt())),
                (t = n) === K &&
                  ((t = tr),
                  e.substr(tr, 5).toLowerCase() === bt
                    ? ((n = e.substr(tr, 5)), (tr += 5))
                    : ((n = K), 0 === ar && p(xt)),
                  n !== K && ((nr = t), (n = Ct())),
                  (t = n) === K &&
                    ((t = tr),
                    e.substr(tr, 5).toLowerCase() === wt
                      ? ((n = e.substr(tr, 5)), (tr += 5))
                      : ((n = K), 0 === ar && p(Pt)),
                    n !== K && ((nr = t), (n = Vt())),
                    (t = n)))),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function T() {
          var t,
            n,
            r,
            i,
            o,
            a,
            s = 34 * tr + 14,
            l = sr[s];
          return l
            ? ((tr = l.nextPos), l.result)
            : ((t = tr),
              40 === e.charCodeAt(tr) ? ((n = kt), tr++) : ((n = K), 0 === ar && p(_t)),
              n !== K
                ? ((r = U()),
                  r !== K
                    ? ((i = h()),
                      i !== K
                        ? ((o = U()),
                          o !== K
                            ? (41 === e.charCodeAt(tr)
                                ? ((a = Tt), tr++)
                                : ((a = K), 0 === ar && p(St)),
                              a === K && (a = null),
                              a !== K ? ((nr = t), (n = Rt(i)), (t = n)) : ((tr = t), (t = K)))
                            : ((tr = t), (t = K)))
                        : ((tr = t), (t = K)))
                    : ((tr = t), (t = K)))
                : ((tr = t), (t = K)),
              t === K && (t = S()) === K && (t = R()) === K && (t = E()) === K && (t = I()),
              (sr[s] = { nextPos: tr, result: t }),
              t);
        }
        function S() {
          var t,
            n,
            r,
            i,
            o,
            a = 34 * tr + 15,
            s = sr[a];
          return s
            ? ((tr = s.nextPos), s.result)
            : ((t = tr),
              (n = z()),
              n !== K
                ? (40 === e.charCodeAt(tr) ? ((r = kt), tr++) : ((r = K), 0 === ar && p(_t)),
                  r !== K
                    ? ((i = L()),
                      i !== K
                        ? (41 === e.charCodeAt(tr)
                            ? ((o = Tt), tr++)
                            : ((o = K), 0 === ar && p(St)),
                          o === K && (o = null),
                          o !== K ? ((nr = t), (n = qt(n, i)), (t = n)) : ((tr = t), (t = K)))
                        : ((tr = t), (t = K)))
                    : ((tr = t), (t = K)))
                : ((tr = t), (t = K)),
              (sr[a] = { nextPos: tr, result: t }),
              t);
        }
        function R() {
          var t,
            n,
            r,
            i,
            o = 34 * tr + 16,
            a = sr[o];
          return a
            ? ((tr = a.nextPos), a.result)
            : ((t = tr),
              33 === e.charCodeAt(tr) ? ((n = Et), tr++) : ((n = K), 0 === ar && p(Ot)),
              n === K &&
                (e.substr(tr, 6).toLowerCase() === It
                  ? ((n = e.substr(tr, 6)), (tr += 6))
                  : ((n = K), 0 === ar && p(Lt))),
              n !== K
                ? ((r = U()),
                  r !== K
                    ? ((i = h()), i !== K ? ((nr = t), (n = At(i)), (t = n)) : ((tr = t), (t = K)))
                    : ((tr = t), (t = K)))
                : ((tr = t), (t = K)),
              t === K &&
                ((t = tr),
                (n = E()),
                n !== K
                  ? ((r = U()),
                    r !== K
                      ? ((i = q()),
                        i !== K ? ((nr = t), (n = Dt(n, i)), (t = n)) : ((tr = t), (t = K)))
                      : ((tr = t), (t = K)))
                  : ((tr = t), (t = K))),
              (sr[o] = { nextPos: tr, result: t }),
              t);
        }
        function q() {
          var t,
            n,
            r = 34 * tr + 17,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              e.substr(tr, 5).toLowerCase() === Nt
                ? ((n = e.substr(tr, 5)), (tr += 5))
                : ((n = K), 0 === ar && p(jt)),
              n !== K && ((nr = t), (n = Ft())),
              (t = n),
              t === K &&
                ((t = tr),
                e.substr(tr, 8).toLowerCase() === Mt
                  ? ((n = e.substr(tr, 8)), (tr += 8))
                  : ((n = K), 0 === ar && p(Bt)),
                n !== K && ((nr = t), (n = zt())),
                (t = n)),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function E() {
          var t,
            n,
            r,
            i,
            o,
            a = 34 * tr + 18,
            s = sr[a];
          return s
            ? ((tr = s.nextPos), s.result)
            : ((t = tr),
              (n = U()),
              n !== K
                ? (e.substr(tr, 9) === Qt ? ((r = Qt), (tr += 9)) : ((r = K), 0 === ar && p(Ht)),
                  r === K &&
                    (e.substr(tr, 4) === $t ? ((r = $t), (tr += 4)) : ((r = K), 0 === ar && p(Ut))),
                  r !== K ? ((nr = t), (n = Wt()), (t = n)) : ((tr = t), (t = K)))
                : ((tr = t), (t = K)),
              t === K &&
                ((t = tr),
                (n = U()),
                n !== K
                  ? ((r = O()), r !== K ? ((nr = t), (n = Jt(r)), (t = n)) : ((tr = t), (t = K)))
                  : ((tr = t), (t = K)),
                t === K &&
                  ((t = tr),
                  (n = U()),
                  n !== K
                    ? (123 === e.charCodeAt(tr) ? ((r = Gt), tr++) : ((r = K), 0 === ar && p(Kt)),
                      r !== K
                        ? ((i = j()),
                          i !== K
                            ? (125 === e.charCodeAt(tr)
                                ? ((o = Zt), tr++)
                                : ((o = K), 0 === ar && p(Xt)),
                              o !== K ? ((nr = t), (n = Yt(i)), (t = n)) : ((tr = t), (t = K)))
                            : ((tr = t), (t = K)))
                        : ((tr = t), (t = K)))
                    : ((tr = t), (t = K)))),
              (sr[a] = { nextPos: tr, result: t }),
              t);
        }
        function O() {
          var t,
            n,
            r,
            i,
            o = 34 * tr + 19,
            a = sr[o];
          return a
            ? ((tr = a.nextPos), a.result)
            : ((t = tr),
              (n = A()),
              n !== K && ((nr = t), (n = en(n))),
              (t = n),
              t === K &&
                ((t = tr),
                (n = D()),
                n !== K && ((nr = t), (n = en(n))),
                (t = n) === K &&
                  ((t = tr),
                  (n = z()),
                  n !== K && ((nr = t), (n = en(n))),
                  (t = n) === K &&
                    ((t = tr),
                    39 === e.charCodeAt(tr) ? ((n = tn), tr++) : ((n = K), 0 === ar && p(nn)),
                    n !== K
                      ? ((r = F()),
                        r !== K
                          ? (39 === e.charCodeAt(tr)
                              ? ((i = tn), tr++)
                              : ((i = K), 0 === ar && p(nn)),
                            i !== K ? ((nr = t), (n = rn(r)), (t = n)) : ((tr = t), (t = K)))
                          : ((tr = t), (t = K)))
                      : ((tr = t), (t = K)),
                    t === K &&
                      ((t = tr),
                      34 === e.charCodeAt(tr) ? ((n = on), tr++) : ((n = K), 0 === ar && p(an)),
                      n !== K
                        ? ((r = F()),
                          r !== K
                            ? (34 === e.charCodeAt(tr)
                                ? ((i = on), tr++)
                                : ((i = K), 0 === ar && p(an)),
                              i !== K ? ((nr = t), (n = rn(r)), (t = n)) : ((tr = t), (t = K)))
                            : ((tr = t), (t = K)))
                        : ((tr = t), (t = K)))))),
              (sr[o] = { nextPos: tr, result: t }),
              t);
        }
        function I() {
          var t,
            n,
            r,
            i,
            o = 34 * tr + 20,
            a = sr[o];
          return a
            ? ((tr = a.nextPos), a.result)
            : ((t = tr),
              91 === e.charCodeAt(tr) ? ((n = sn), tr++) : ((n = K), 0 === ar && p(ln)),
              n !== K
                ? ((r = L()),
                  r !== K
                    ? (93 === e.charCodeAt(tr) ? ((i = un), tr++) : ((i = K), 0 === ar && p(cn)),
                      i !== K ? ((nr = t), (n = pn(r)), (t = n)) : ((tr = t), (t = K)))
                    : ((tr = t), (t = K)))
                : ((tr = t), (t = K)),
              (sr[o] = { nextPos: tr, result: t }),
              t);
        }
        function L() {
          var t,
            n,
            r,
            i,
            o,
            a,
            s,
            l,
            u = 34 * tr + 21,
            c = sr[u];
          if (c) return (tr = c.nextPos), c.result;
          if (((t = tr), (n = h()), n === K && (n = null), n !== K)) {
            for (
              r = [],
                i = tr,
                o = U(),
                o !== K
                  ? (44 === e.charCodeAt(tr) ? ((a = dn), tr++) : ((a = K), 0 === ar && p(hn)),
                    a !== K
                      ? ((s = U()),
                        s !== K
                          ? ((l = h()),
                            l !== K ? ((o = [o, a, s, l]), (i = o)) : ((tr = i), (i = K)))
                          : ((tr = i), (i = K)))
                      : ((tr = i), (i = K)))
                  : ((tr = i), (i = K));
              i !== K;

            )
              r.push(i),
                (i = tr),
                (o = U()),
                o !== K
                  ? (44 === e.charCodeAt(tr) ? ((a = dn), tr++) : ((a = K), 0 === ar && p(hn)),
                    a !== K
                      ? ((s = U()),
                        s !== K
                          ? ((l = h()),
                            l !== K ? ((o = [o, a, s, l]), (i = o)) : ((tr = i), (i = K)))
                          : ((tr = i), (i = K)))
                      : ((tr = i), (i = K)))
                  : ((tr = i), (i = K));
            r !== K ? ((nr = t), (n = fn(n, r)), (t = n)) : ((tr = t), (t = K));
          } else (tr = t), (t = K);
          return (sr[u] = { nextPos: tr, result: t }), t;
        }
        function A() {
          var t,
            n,
            r = 34 * tr + 22,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              e.substr(tr, 4).toLowerCase() === mn
                ? ((n = e.substr(tr, 4)), (tr += 4))
                : ((n = K), 0 === ar && p(gn)),
              n !== K && ((nr = t), (n = yn())),
              (t = n),
              t === K &&
                ((t = tr),
                e.substr(tr, 5).toLowerCase() === vn
                  ? ((n = e.substr(tr, 5)), (tr += 5))
                  : ((n = K), 0 === ar && p(bn)),
                n !== K && ((nr = t), (n = xn())),
                (t = n)),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function D() {
          var t,
            n,
            r,
            i = 34 * tr + 23,
            o = sr[i];
          return o
            ? ((tr = o.nextPos), o.result)
            : ((t = tr),
              e.substr(tr, 2) === Cn ? ((n = Cn), (tr += 2)) : ((n = K), 0 === ar && p(wn)),
              n !== K
                ? ((r = Q()), r !== K ? ((nr = t), (n = Pn()), (t = n)) : ((tr = t), (t = K)))
                : ((tr = t), (t = K)),
              t === K &&
                ((t = tr),
                Vn.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p(kn)),
                n === K && (n = null),
                n !== K
                  ? ((r = N()), r !== K ? ((nr = t), (n = _n(n, r)), (t = n)) : ((tr = t), (t = K)))
                  : ((tr = t), (t = K))),
              (sr[i] = { nextPos: tr, result: t }),
              t);
        }
        function N() {
          var t,
            n,
            r,
            i,
            o = 34 * tr + 24,
            a = sr[o];
          return a
            ? ((tr = a.nextPos), a.result)
            : ((t = tr),
              (n = Q()),
              n !== K
                ? (46 === e.charCodeAt(tr) ? ((r = Tn), tr++) : ((r = K), 0 === ar && p(Sn)),
                  r !== K
                    ? ((i = Q()), i !== K ? ((nr = t), (n = Rn()), (t = n)) : ((tr = t), (t = K)))
                    : ((tr = t), (t = K)))
                : ((tr = t), (t = K)),
              t === K &&
                ((t = tr),
                (n = H()),
                n !== K
                  ? ((r = Q()),
                    r === K && (r = null),
                    r !== K ? ((nr = t), (n = qn()), (t = n)) : ((tr = t), (t = K)))
                  : ((tr = t), (t = K)),
                t === K &&
                  ((t = tr),
                  48 === e.charCodeAt(tr) ? ((n = En), tr++) : ((n = K), 0 === ar && p(On)),
                  n !== K && ((nr = t), (n = In())),
                  (t = n))),
              (sr[o] = { nextPos: tr, result: t }),
              t);
        }
        function j() {
          var e,
            t,
            n,
            r = 34 * tr + 25,
            i = sr[r];
          if (i) return (tr = i.nextPos), i.result;
          if (((e = tr), (t = []), (n = B()) !== K)) for (; n !== K; ) t.push(n), (n = B());
          else t = K;
          return (
            t !== K && ((nr = e), (t = Ln(t))), (e = t), (sr[r] = { nextPos: tr, result: e }), e
          );
        }
        function F() {
          var e,
            t,
            n,
            r = 34 * tr + 26,
            i = sr[r];
          if (i) return (tr = i.nextPos), i.result;
          if (((e = tr), (t = []), (n = M()) !== K)) for (; n !== K; ) t.push(n), (n = M());
          else t = K;
          return (
            t !== K && ((nr = e), (t = Ln(t))), (e = t), (sr[r] = { nextPos: tr, result: e }), e
          );
        }
        function M() {
          var t,
            n,
            r = 34 * tr + 27,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              e.substr(tr, 2) === An ? ((n = An), (tr += 2)) : ((n = K), 0 === ar && p(Dn)),
              n !== K && ((nr = t), (n = Nn())),
              (t = n),
              t === K &&
                ((t = tr),
                e.substr(tr, 2) === jn ? ((n = jn), (tr += 2)) : ((n = K), 0 === ar && p(Fn)),
                n !== K && ((nr = t), (n = Mn())),
                (t = n) === K &&
                  ((t = tr),
                  Bn.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p(zn)),
                  n !== K && ((nr = t), (n = Qn())),
                  (t = n))),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function B() {
          var t,
            n,
            r = 34 * tr + 28,
            i = sr[r];
          return i
            ? ((tr = i.nextPos), i.result)
            : ((t = tr),
              Hn.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p($n)),
              n !== K && ((nr = t), (n = Qn())),
              (t = n),
              (sr[r] = { nextPos: tr, result: t }),
              t);
        }
        function z() {
          var e,
            t,
            n,
            r,
            i,
            o,
            a,
            s = 34 * tr + 29,
            l = sr[s];
          if (l) return (tr = l.nextPos), l.result;
          if (((e = tr), (t = $()) !== K)) {
            if (((n = []), (r = tr), (i = Q()) !== K)) {
              for (o = [], a = $(); a !== K; ) o.push(a), (a = $());
              o !== K ? ((i = [i, o]), (r = i)) : ((tr = r), (r = K));
            } else (tr = r), (r = K);
            for (; r !== K; )
              if ((n.push(r), (r = tr), (i = Q()) !== K)) {
                for (o = [], a = $(); a !== K; ) o.push(a), (a = $());
                o !== K ? ((i = [i, o]), (r = i)) : ((tr = r), (r = K));
              } else (tr = r), (r = K);
            n !== K ? ((nr = e), (t = Qn()), (e = t)) : ((tr = e), (e = K));
          } else (tr = e), (e = K);
          return (sr[s] = { nextPos: tr, result: e }), e;
        }
        function Q() {
          var t,
            n,
            r = 34 * tr + 30,
            i = sr[r];
          if (i) return (tr = i.nextPos), i.result;
          if (
            ((t = []),
            Un.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p(Wn)),
            n !== K)
          )
            for (; n !== K; )
              t.push(n),
                Un.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p(Wn));
          else t = K;
          return (sr[r] = { nextPos: tr, result: t }), t;
        }
        function H() {
          var t,
            n,
            r = 34 * tr + 31,
            i = sr[r];
          if (i) return (tr = i.nextPos), i.result;
          if (
            ((t = []),
            Jn.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p(Gn)),
            n !== K)
          )
            for (; n !== K; )
              t.push(n),
                Jn.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p(Gn));
          else t = K;
          return (sr[r] = { nextPos: tr, result: t }), t;
        }
        function $() {
          var t,
            n,
            r = 34 * tr + 32,
            i = sr[r];
          if (i) return (tr = i.nextPos), i.result;
          if (
            ((t = []),
            Kn.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p(Zn)),
            n !== K)
          )
            for (; n !== K; )
              t.push(n),
                Kn.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p(Zn));
          else t = K;
          return (sr[r] = { nextPos: tr, result: t }), t;
        }
        function U() {
          var t,
            n,
            r = 34 * tr + 33,
            i = sr[r];
          if (i) return (tr = i.nextPos), i.result;
          for (
            ar++,
              t = [],
              Yn.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p(er));
            n !== K;

          )
            t.push(n),
              Yn.test(e.charAt(tr)) ? ((n = e.charAt(tr)), tr++) : ((n = K), 0 === ar && p(er));
          return (
            ar--, t === K && ((n = K), 0 === ar && p(Xn)), (sr[r] = { nextPos: tr, result: t }), t
          );
        }
        function W(e, t, n) {
          return (
            void 0 === n && (n = !1),
            t.reduce(function (e, t) {
              return new o.f(t[1], e, t[3], n);
            }, e)
          );
        }
        function J(e) {
          return [].concat.apply([], e);
        }
        t = void 0 !== t ? t : {};
        var G,
          K = {},
          Z = { Expression: h },
          X = h,
          Y = function (e, t) {
            return W(e, t, !0);
          },
          ee = '||',
          te = r('||', !1),
          ne = 'or',
          re = r('or', !0),
          ie = function () {
            return 'or';
          },
          oe = '&&',
          ae = r('&&', !1),
          se = 'and',
          le = r('and', !0),
          ue = function () {
            return 'and';
          },
          ce = function (e, t) {
            return W(e, t);
          },
          pe = '<=',
          de = r('<=', !1),
          he = 'lessorequal',
          fe = r('lessorequal', !0),
          me = function () {
            return 'lessorequal';
          },
          ge = '>=',
          ye = r('>=', !1),
          ve = 'greaterorequal',
          be = r('greaterorequal', !0),
          xe = function () {
            return 'greaterorequal';
          },
          Ce = '=',
          we = r('=', !1),
          Pe = 'equal',
          Ve = r('equal', !0),
          ke = function () {
            return 'equal';
          },
          _e = '!=',
          Te = r('!=', !1),
          Se = 'notequal',
          Re = r('notequal', !0),
          qe = function () {
            return 'notequal';
          },
          Ee = '<',
          Oe = r('<', !1),
          Ie = 'less',
          Le = r('less', !0),
          Ae = function () {
            return 'less';
          },
          De = '>',
          Ne = r('>', !1),
          je = 'greater',
          Fe = r('greater', !0),
          Me = function () {
            return 'greater';
          },
          Be = '+',
          ze = r('+', !1),
          Qe = function () {
            return 'plus';
          },
          He = '-',
          $e = r('-', !1),
          Ue = function () {
            return 'minus';
          },
          We = '*',
          Je = r('*', !1),
          Ge = function () {
            return 'mul';
          },
          Ke = '/',
          Ze = r('/', !1),
          Xe = function () {
            return 'div';
          },
          Ye = '%',
          et = r('%', !1),
          tt = function () {
            return 'mod';
          },
          nt = '^',
          rt = r('^', !1),
          it = 'power',
          ot = r('power', !0),
          at = function () {
            return 'power';
          },
          st = '*=',
          lt = r('*=', !1),
          ut = 'contains',
          ct = r('contains', !0),
          pt = 'contain',
          dt = r('contain', !0),
          ht = function () {
            return 'contains';
          },
          ft = 'notcontains',
          mt = r('notcontains', !0),
          gt = 'notcontain',
          yt = r('notcontain', !0),
          vt = function () {
            return 'notcontains';
          },
          bt = 'anyof',
          xt = r('anyof', !0),
          Ct = function () {
            return 'anyof';
          },
          wt = 'allof',
          Pt = r('allof', !0),
          Vt = function () {
            return 'allof';
          },
          kt = '(',
          _t = r('(', !1),
          Tt = ')',
          St = r(')', !1),
          Rt = function (e) {
            return e;
          },
          qt = function (e, t) {
            return new o.a(e, t);
          },
          Et = '!',
          Ot = r('!', !1),
          It = 'negate',
          Lt = r('negate', !0),
          At = function (e) {
            return new o.b(e, 'negate');
          },
          Dt = function (e, t) {
            return new o.b(e, t);
          },
          Nt = 'empty',
          jt = r('empty', !0),
          Ft = function () {
            return 'empty';
          },
          Mt = 'notempty',
          Bt = r('notempty', !0),
          zt = function () {
            return 'notempty';
          },
          Qt = 'undefined',
          Ht = r('undefined', !1),
          $t = 'null',
          Ut = r('null', !1),
          Wt = function () {
            return null;
          },
          Jt = function (e) {
            return new o.c(e);
          },
          Gt = '{',
          Kt = r('{', !1),
          Zt = '}',
          Xt = r('}', !1),
          Yt = function (e) {
            return new o.d(e);
          },
          en = function (e) {
            return e;
          },
          tn = "'",
          nn = r("'", !1),
          rn = function (e) {
            return "'" + e + "'";
          },
          on = '"',
          an = r('"', !1),
          sn = '[',
          ln = r('[', !1),
          un = ']',
          cn = r(']', !1),
          pn = function (e) {
            return e;
          },
          dn = ',',
          hn = r(',', !1),
          fn = function (e, t) {
            if (null == e) return new o.e([]);
            var n = [e];
            if (Array.isArray(t)) for (var r = J(t), i = 3; i < r.length; i += 4) n.push(r[i]);
            return new o.e(n);
          },
          mn = 'true',
          gn = r('true', !0),
          yn = function () {
            return !0;
          },
          vn = 'false',
          bn = r('false', !0),
          xn = function () {
            return !1;
          },
          Cn = '0x',
          wn = r('0x', !1),
          Pn = function () {
            return parseInt(n(), 16);
          },
          Vn = /^[\-]/,
          kn = i(['-'], !1, !1),
          _n = function (e, t) {
            return null == e ? t : -t;
          },
          Tn = '.',
          Sn = r('.', !1),
          Rn = function () {
            return parseFloat(n());
          },
          qn = function () {
            return parseInt(n(), 10);
          },
          En = '0',
          On = r('0', !1),
          In = function () {
            return 0;
          },
          Ln = function (e) {
            return e.join('');
          },
          An = "\\'",
          Dn = r("\\'", !1),
          Nn = function () {
            return "'";
          },
          jn = '\\"',
          Fn = r('\\"', !1),
          Mn = function () {
            return '"';
          },
          Bn = /^[^"']/,
          zn = i(['"', "'"], !0, !1),
          Qn = function () {
            return n();
          },
          Hn = /^[^{}]/,
          $n = i(['{', '}'], !0, !1),
          Un = /^[0-9]/,
          Wn = i([['0', '9']], !1, !1),
          Jn = /^[1-9]/,
          Gn = i([['1', '9']], !1, !1),
          Kn = /^[a-zA-Z]/,
          Zn = i(
            [
              ['a', 'z'],
              ['A', 'Z'],
            ],
            !1,
            !1
          ),
          Xn = l('whitespace'),
          Yn = /^[ \t\n\r]/,
          er = i([' ', '\t', '\n', '\r'], !1, !1),
          tr = 0,
          nr = 0,
          rr = [{ line: 1, column: 1 }],
          ir = 0,
          or = [],
          ar = 0,
          sr = {};
        if (void 0 !== t.startRule) {
          if (!(t.startRule in Z))
            throw new Error('Can\'t start parsing from rule "' + t.startRule + '".');
          X = Z[t.startRule];
        }
        if ((G = X()) !== K && tr === e.length) return G;
        throw (
          (G !== K && tr < e.length && p(s()),
          d(or, ir < e.length ? e.charAt(ir) : null, ir < e.length ? c(ir, ir + 1) : c(ir, ir)))
        );
      }
      var i = n(0),
        o = n(27);
      n.d(t, 'b', function () {
        return a;
      }),
        n.d(t, 'a', function () {
          return s;
        });
      var a = (function (e) {
          function t(n, r, i, o) {
            var a = e.call(this) || this;
            return (
              (a.message = n),
              (a.expected = r),
              (a.found = i),
              (a.location = o),
              (a.name = 'SyntaxError'),
              'function' == typeof Error.captureStackTrace && Error.captureStackTrace(a, t),
              a
            );
          }
          return (
            n.i(i.b)(t, e),
            (t.buildMessage = function (e, t) {
              function n(e) {
                return e.charCodeAt(0).toString(16).toUpperCase();
              }
              function r(e) {
                return e
                  .replace(/\\/g, '\\\\')
                  .replace(/"/g, '\\"')
                  .replace(/\0/g, '\\0')
                  .replace(/\t/g, '\\t')
                  .replace(/\n/g, '\\n')
                  .replace(/\r/g, '\\r')
                  .replace(/[\x00-\x0F]/g, function (e) {
                    return '\\x0' + n(e);
                  })
                  .replace(/[\x10-\x1F\x7F-\x9F]/g, function (e) {
                    return '\\x' + n(e);
                  });
              }
              function i(e) {
                return e
                  .replace(/\\/g, '\\\\')
                  .replace(/\]/g, '\\]')
                  .replace(/\^/g, '\\^')
                  .replace(/-/g, '\\-')
                  .replace(/\0/g, '\\0')
                  .replace(/\t/g, '\\t')
                  .replace(/\n/g, '\\n')
                  .replace(/\r/g, '\\r')
                  .replace(/[\x00-\x0F]/g, function (e) {
                    return '\\x0' + n(e);
                  })
                  .replace(/[\x10-\x1F\x7F-\x9F]/g, function (e) {
                    return '\\x' + n(e);
                  });
              }
              function o(e) {
                switch (e.type) {
                  case 'literal':
                    return '"' + r(e.text) + '"';
                  case 'class':
                    var t = e.parts.map(function (e) {
                      return Array.isArray(e) ? i(e[0]) + '-' + i(e[1]) : i(e);
                    });
                    return '[' + (e.inverted ? '^' : '') + t + ']';
                  case 'any':
                    return 'any character';
                  case 'end':
                    return 'end of input';
                  case 'other':
                    return e.description;
                }
              }
              function a(e) {
                var t,
                  n,
                  r = e.map(o);
                if ((r.sort(), r.length > 0)) {
                  for (t = 1, n = 1; t < r.length; t++) r[t - 1] !== r[t] && ((r[n] = r[t]), n++);
                  r.length = n;
                }
                switch (r.length) {
                  case 1:
                    return r[0];
                  case 2:
                    return r[0] + ' or ' + r[1];
                  default:
                    return r.slice(0, -1).join(', ') + ', or ' + r[r.length - 1];
                }
              }
              function s(e) {
                return e ? '"' + r(e) + '"' : 'end of input';
              }
              return 'Expected ' + a(e) + ' but ' + s(t) + ' found.';
            }),
            t
          );
        })(Error),
        s = r;
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(74));
      r.components.register('sv-action-bar-item-dropdown', {
        viewModel: {
          createViewModel: function (e, t) {
            var n = r.observable(!1);
            return {
              id: e.item.id,
              title: e.item.title,
              icon: e.item.icon,
              action: function () {
                n(!n());
              },
              model: {
                onItemSelect: function (t) {
                  e.item.action(t);
                },
                items: e.item.items,
                selectedItem: e.item.selectedItem,
              },
              isVisible: n,
              name: 'svc-list',
              verticalPosition: e.item.verticalPosition || 'middle',
              horizontalPosition: e.item.horizontalPosition || 'right',
              showPointer: !0,
            };
          },
        },
        template: i,
      });
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(75)),
        o = (function () {
          function e(e) {
            (this._item = e), (this.koIsVisible = r.observable(!1));
          }
          return (
            Object.defineProperty(e.prototype, 'icon', {
              get: function () {
                return this._item.icon;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'contentComponentName', {
              get: function () {
                return this._item.data.contentComponentName;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'contentComponentData', {
              get: function () {
                return this._item.data.contentComponentData;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'contentTemplateName', {
              get: function () {
                return this._item.data.contentTemplateName;
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'onShow', {
              get: function () {
                return this._item.data.onShow || function () {};
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'onApply', {
              get: function () {
                return this._item.data.onApply || function () {};
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'onCancel', {
              get: function () {
                return this._item.data.onCancel || function () {};
              },
              enumerable: !0,
              configurable: !0,
            }),
            e
          );
        })();
      r.components.register('sv-action-bar-item-modal', {
        viewModel: {
          createViewModel: function (e, t) {
            return new o(e.item);
          },
        },
        template: i,
      });
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(76));
      r.components.register('sv-action-bar-item', {
        viewModel: {
          createViewModel: function (e, t) {
            return e.item;
          },
        },
        template: i,
      });
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(77));
      r.components.register('sv-action-bar-separator', {
        viewModel: {
          createViewModel: function (e, t) {
            return { css: e.item.innerCss };
          },
        },
        template: i,
      });
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(127), n(128), n(125), n(126), n(78)),
        o = (function () {
          function e(e) {
            var t = this;
            (this.items = r.observableArray()),
              (this.itemsSubscription = r.computed(function () {
                e.forEach(function (e) {
                  var n = new Object();
                  Object.getOwnPropertyNames(e).forEach(function (t) {
                    Object.defineProperty(n, t, {
                      get: function () {
                        var n = e[t];
                        return 'function' == typeof n && 'action' != t ? n() : n;
                      },
                    });
                  }),
                    t.items.push(n);
                });
              }));
          }
          return (
            Object.defineProperty(e.prototype, 'hasItems', {
              get: function () {
                return (r.unwrap(this.items) || []).length > 0;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.dispose = function () {
              this.itemsSubscription.dispose();
            }),
            e
          );
        })();
      r.components.register('sv-action-bar', {
        viewModel: {
          createViewModel: function (e, t) {
            return new o(e.items);
          },
        },
        template: i,
      });
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(19)),
        o = n(79);
      r.components.register('sv-boolean-checkbox', {
        viewModel: {
          createViewModel: function (e, t) {
            return { question: e.question };
          },
        },
        template: o,
      }),
        i.a.Instance.registerRenderer('boolean', 'checkbox', 'sv-boolean-checkbox');
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(19)),
        o = n(80),
        a = (function () {
          function e(e) {
            this.element = e;
          }
          return (
            (e.prototype.getIconClass = function () {
              var e = this.element,
                t = e.isPanel ? e.cssClasses.panel : e.cssClasses;
              return t.icon + (e.isCollapsed ? '' : ' ' + t.iconExpanded);
            }),
            e
          );
        })();
      r.components.register('sv-default-title', {
        viewModel: {
          createViewModel: function (e, t) {
            return new a(e.element);
          },
        },
        template: o,
      }),
        i.a.Instance.registerRenderer('element', 'default-title', 'sv-default-title');
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(115)),
        o = (function () {
          function e(e, t) {
            (this.question = e), (this.targetElement = t);
          }
          return e;
        })();
      r.components.register('sv-panel', {
        viewModel: {
          createViewModel: function (e, t) {
            return new o(e.question, t.element.parentElement);
          },
        },
        template: i,
      });
    },
    function (e, t, n) {
      'use strict';
      n.d(t, 'a', function () {
        return r;
      });
      var r = (function () {
        function e() {}
        return (
          (e.calculatePosition = function (e, t, n, r, i, o) {
            if ('center' == i) var a = (e.left + e.right - n) / 2;
            else a = 'left' == i ? e.left - n : e.right;
            if ('middle' == r) var s = (e.top + e.bottom - t) / 2;
            else s = 'top' == r ? e.top - t : e.bottom;
            return (
              o &&
                'center' != i &&
                'middle' != r &&
                ('top' == r ? (s += e.height) : (s -= e.height)),
              { left: a, top: s }
            );
          }),
          (e.calculatePopupDirection = function (e, t) {
            var n;
            return 'center' == t && 'middle' != e ? (n = e) : 'center' != t && (n = t), n;
          }),
          (e.calculatePointerTarget = function (e, t, n, r, i) {
            var o = {};
            return (
              'center' != i
                ? ((o.top = e.top + e.height / 2), (o.left = e[i]))
                : 'middle' != r && ((o.top = e[r]), (o.left = e.left + e.width / 2)),
              (o.left = o.left - n),
              (o.top = o.top - t),
              o
            );
          }),
          e
        );
      })();
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(133)),
        o = n(2),
        a = n(116),
        s = (function () {
          function e(e, t, n, i, o, s, l, u, c, p, d, h, f, m) {
            var g = this;
            void 0 === u && (u = !1),
              void 0 === c && (c = function () {}),
              void 0 === p && (p = function () {}),
              void 0 === d && (d = function () {}),
              void 0 === h && (h = function () {}),
              void 0 === f && (f = ''),
              (this.contentComponentName = e),
              (this.contentComponentData = t),
              (this.contentTemplateName = n),
              (this.isVisible = i),
              (this.verticalPosition = o),
              (this.horizontalPosition = s),
              (this.showPointer = l),
              (this.isModal = u),
              (this.onCancel = c),
              (this.onApply = p),
              (this.onHide = d),
              (this.onShow = h),
              (this.cssClass = f),
              (this.targetElement = m),
              (this.top = r.observable()),
              (this.left = r.observable()),
              (this.popupDirection = r.observable()),
              (this.pointerTarget = r.observable({})),
              (this.container = document.createElement('div')),
              document.body.appendChild(this.container),
              (this.container.innerHTML = a),
              r.applyBindings(this, this.container),
              (this.showSubscription = this.isVisible.subscribe(function (e) {
                g.onIsVisibleChanged(e);
              }));
          }
          return (
            Object.defineProperty(e.prototype, 'styleClass', {
              get: function () {
                var e = this.cssClass;
                return (
                  this.showPointer &&
                    ((e += ' sv-popup--show-pointer'),
                    (e += ' sv-popup--' + this.popupDirection())),
                  e
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.onIsVisibleChanged = function (e) {
              e ? (this.setupPopup(), this.onShow()) : this.onHide();
            }),
            (e.prototype.setupPopup = function () {
              var e = this.targetElement.getBoundingClientRect(),
                t = this.container.children[0].children[0];
              this.popupDirection(
                i.a.calculatePopupDirection(this.verticalPosition, this.horizontalPosition)
              );
              do {
                var n = t.offsetHeight,
                  r = t.offsetWidth,
                  o = i.a.calculatePosition(
                    e,
                    n,
                    r,
                    this.verticalPosition,
                    this.horizontalPosition,
                    this.showPointer
                  );
                this.left(o.left),
                  this.top(o.top),
                  this.showPointer &&
                    this.pointerTarget(
                      i.a.calculatePointerTarget(
                        e,
                        o.top,
                        o.left,
                        this.verticalPosition,
                        this.horizontalPosition
                      )
                    );
              } while (t.offsetWidth != r || t.offsetHeight != n);
            }),
            (e.prototype.clickOutside = function () {
              this.isModal || this.isVisible(!1);
            }),
            (e.prototype.cancel = function () {
              this.onCancel(), this.isVisible(!1);
            }),
            (e.prototype.apply = function () {
              this.onApply(), this.isVisible(!1);
            }),
            Object.defineProperty(e.prototype, 'cancelButtonText', {
              get: function () {
                return o.a.getString('modalCancelButtonText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(e.prototype, 'applyButtonText', {
              get: function () {
                return o.a.getString('modalApplyButtonText');
              },
              enumerable: !0,
              configurable: !0,
            }),
            (e.prototype.dispose = function () {
              this.showSubscription.dispose(), r.cleanNode(this.container), this.container.remove();
            }),
            e
          );
        })();
      r.components.register('sv-popup', {
        viewModel: {
          createViewModel: function (e, t) {
            return new s(
              e.contentComponentName,
              e.contentComponentData,
              e.contentTemplateName,
              e.isVisible,
              e.verticalPosition,
              e.horizontalPosition,
              e.showPointer,
              e.isModal,
              e.onCancel,
              e.onApply,
              e.onHide,
              e.onShow,
              e.cssClass,
              t.element.parentElement
            );
          },
        },
        template: '<div></div>',
      });
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(68)),
        o = n(117),
        a = (function () {
          function e(e, t) {
            var n = this;
            (this.model = e),
              (this.scrollButtonCssKo = void 0),
              (this.hasScroller = r.observable(!1)),
              (this.updateScroller = void 0),
              (this.progressButtonsModel = new i.a(e)),
              (this.updateScroller = setInterval(function () {
                var r = t.querySelector('.' + e.css.progressButtonsListContainer);
                r && n.hasScroller(r.scrollWidth > r.offsetWidth);
              }, 100));
          }
          return (
            (e.prototype.getListElementCss = function (e) {
              return this.progressButtonsModel.getListElementCss(e());
            }),
            (e.prototype.clickListElement = function (e) {
              this.progressButtonsModel.clickListElement(e());
            }),
            (e.prototype.getScrollButtonCss = function (e) {
              var t = this;
              return (
                (this.scrollButtonCssKo = r.computed(function () {
                  var n = e
                    ? t.model.css.progressButtonsImageButtonLeft
                    : t.model.css.progressButtonsImageButtonRight;
                  return (
                    t.hasScroller() || (n += ' ' + t.model.css.progressButtonsImageButtonHidden), n
                  );
                }, this)),
                this.scrollButtonCssKo
              );
            }),
            (e.prototype.clickScrollButton = function (e, t) {
              e.scrollLeft += 70 * (t ? -1 : 1);
            }),
            (e.prototype.dispose = function () {
              void 0 !== this.updateScroller &&
                (clearInterval(this.updateScroller), (this.updateScroller = void 0)),
                void 0 !== this.scrollButtonCssKo &&
                  (this.scrollButtonCssKo.dispose(), (this.scrollButtonCssKo = void 0));
            }),
            e
          );
        })();
      r.components.register('sv-progress-buttons', {
        viewModel: {
          createViewModel: function (e, t) {
            return new a(e.model, t.element.nextElementSibling);
          },
        },
        template: o,
      });
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(118)),
        o = function (e) {
          return { model: e.model };
        };
      r.components.register('sv-progress-progress', { viewModel: o, template: i });
      var a =
        "\x3c!-- ko component: { name: 'sv-progress-progress', params: $data } --\x3e\x3c!-- /ko --\x3e";
      r.components.register('sv-progress-pages', { viewModel: o, template: a }),
        r.components.register('sv-progress-questions', { viewModel: o, template: a }),
        r.components.register('sv-progress-correctquestions', { viewModel: o, template: a });
    },
    function (e, t, n) {
      'use strict';
      var r = n(5),
        i = (n.n(r), n(19)),
        o = n(81);
      r.components.register('sv-title-actions', {
        viewModel: {
          createViewModel: function (e, t) {
            var n = e.element;
            return { element: n, items: n.getTitleActions() };
          },
        },
        template: o,
      }),
        i.a.Instance.registerRenderer('element', 'title-actions', 'sv-title-actions');
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(69)),
        a = n(20);
      n.d(t, 'a', function () {
        return l;
      });
      var s = n(120),
        l = (function (e) {
          function t(t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            (r.koExpanded = i.observable(!1)), (r.koExpandedCss = i.observable(r.getButtonCss()));
            var o = r;
            return (
              (r.expandedChangedCallback = function () {
                o.koExpanded(o.isExpanded), o.koExpandedCss(o.getButtonCss());
              }),
              (r.showingChangedCallback = function () {
                o.doShowingChanged();
              }),
              (r.doExpand = function () {
                o.changeExpanded();
              }),
              r
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.createSurvey = function (e) {
              return new a.b(e);
            }),
            (t.prototype.closeWindowOnComplete = function () {
              this.hide();
            }),
            Object.defineProperty(t.prototype, 'template', {
              get: function () {
                return this.templateValue ? this.templateValue : this.getDefaultTemplate();
              },
              set: function (e) {
                this.templateValue = e;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.doShowingChanged = function () {
              this.isShowing
                ? ((this.windowElement.innerHTML = this.template),
                  i.cleanNode(this.windowElement),
                  i.applyBindings(this, this.windowElement),
                  document.body.appendChild(this.windowElement),
                  this.survey.render(t.surveyElementName))
                : (document.body.removeChild(this.windowElement),
                  (this.windowElement.innerHTML = ''));
            }),
            (t.prototype.getDefaultTemplate = function () {
              return s;
            }),
            Object.defineProperty(t.prototype, 'css', {
              get: function () {
                return this.survey.css;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.changeExpanded = function () {
              this.expandcollapse(!this.isExpanded);
            }),
            (t.prototype.getButtonCss = function () {
              return this.koExpanded()
                ? this.css.window.header.buttonCollapsed
                : this.css.window.header.buttonExpanded;
            }),
            t
          );
        })(o.a);
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(45)),
        a = n(1),
        s = n(15),
        l = n(6),
        u = (function (e) {
          function t(t) {
            void 0 === t && (t = '');
            var n = e.call(this, t) || this;
            (n.koElementType = i.observable('survey-flowpanel')), new s.a(n), n.onCreating();
            var r = n;
            return (
              (n.koElementAfterRender = function (e, t) {
                return r.elementAfterRender(e, t);
              }),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onCreating = function () {}),
            (t.prototype.getHtmlForQuestion = function (e) {
              return (
                '<span question="true" contenteditable="false" id="flowpanel_' +
                e.name +
                '">\x3c!-- ko template: { name: "survey-flowpanel-question", data: "' +
                e.name +
                '"} --\x3e\x3c!-- /ko --\x3e</span>'
              );
            }),
            (t.prototype.elementAfterRender = function (e, t) {
              if (this.survey) {
                var n = l.a.GetFirstNonTextElement(e);
                n && this.survey.afterRenderQuestion(t, n);
              }
            }),
            t
          );
        })(o.a);
      a.b.overrideClassCreator('flowpanel', function () {
        return new u();
      }),
        i.components.register('f-panel', {
          viewModel: {
            createViewModel: function (e, t) {
              var n = this,
                r = i.unwrap(e.question);
              (n.element = t.element),
                (n.element.innerHTML = r.html),
                (n.isOnFocus = !1),
                (n.wasChanged = !1),
                (n.isContentUpdating = !1),
                (r.contentChangedCallback = function () {
                  n.isContentUpdating ||
                    (i.cleanNode(n.element),
                    (n.element.innerHTML = r.html),
                    i.applyBindings({ question: r }, n.element),
                    i.tasks.runEarly());
                }),
                (n.element.onfocus = function () {
                  n.isOnFocus = !0;
                }),
                (n.element.onblur = function () {
                  n.wasChanged && n.updateContent(), (n.isOnFocus = !1), (n.wasChanged = !1);
                }),
                (n.element.ondragend = function (e) {
                  var t = /{(.*?(element:)[^$].*?)}/g,
                    i = n.element.innerHTML,
                    o = t.exec(i);
                  if (null !== o) {
                    var a = r.getQuestionFromText(o[0]);
                    a && (r.content = n.getContent(a.name));
                  }
                }),
                (n.updateContent = function () {
                  (n.isContentUpdating = !0),
                    (r.content = n.getContent()),
                    (n.isContentUpdating = !1);
                }),
                (r.getContent = n.getContent = function (e) {
                  var t = document.createElement('DIV');
                  t.innerHTML = n.element.innerHTML;
                  for (
                    var i = t.querySelectorAll('span[question="true"]'), o = 0;
                    o < i.length;
                    o++
                  ) {
                    var a = i[o].id.replace('flowpanel_', ''),
                      s = '';
                    if (a !== e) {
                      var l = r.getQuestionByName(a);
                      s = l ? r.getElementContentText(l) : '';
                    }
                    i[o].outerHTML = s;
                  }
                  return t.innerHTML;
                });
              var o = { characterData: !0, attributes: !0, childList: !0, subtree: !0 },
                a = function (e, t) {
                  n.isOnFocus && (n.wasChanged = !0);
                };
              return new MutationObserver(a).observe(n.element, o), { question: r };
            },
          },
          template: '<div></div>',
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(51),
        o = n(1),
        a = n(3),
        s = n(8),
        l = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new s.a(this);
            }),
            (t.prototype.getItemCss = function (e, t) {
              var n = this.checkedValue,
                r = this.isReadOnly,
                i = this.cssClasses.item;
              return (
                r && (i += ' ' + this.cssClasses.itemDisabled),
                n
                  ? (i += ' ' + this.cssClasses.itemChecked)
                  : null === n && (i += ' ' + this.cssClasses.itemIndeterminate),
                i
              );
            }),
            (t.prototype.getCheckedLabelCss = function () {
              return this.getLabelClass(!0);
            }),
            (t.prototype.getUncheckedLabelCss = function () {
              return this.getLabelClass(!1);
            }),
            (t.prototype.getLabelClass = function (e) {
              return (
                this.cssClasses.label +
                (this.checkedValue === !e || this.isReadOnly
                  ? ' ' + this.cssClasses.disabledLabel
                  : '')
              );
            }),
            (t.prototype.preventDefaults = function (e) {
              e.preventDefault(), e.stopPropagation();
            }),
            (t.prototype.onLabelClick = function (e, t) {
              return this.isIndeterminate && (this.preventDefaults(e), (this.checkedValue = t)), !0;
            }),
            (t.prototype.onSwitchClick = function (e, t) {
              if (this.isIndeterminate) {
                this.preventDefaults(t);
                var n = t.offsetX / t.target.offsetWidth > 0.5,
                  r = 'rtl' == document.defaultView.getComputedStyle(t.target).direction;
                return void (this.checkedValue = r ? !n : n);
              }
              return !0;
            }),
            (t.prototype.onTrueLabelClick = function (e, t) {
              return this.onLabelClick(t, !0);
            }),
            (t.prototype.onFalseLabelClick = function (e, t) {
              return this.onLabelClick(t, !1);
            }),
            t
          );
        })(i.a);
      o.b.overrideClassCreator('boolean', function () {
        return new l('');
      }),
        a.b.Instance.registerQuestion('boolean', function (e) {
          return new l(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(17)),
        a = n(1),
        s = n(3),
        l = n(52),
        u = (function (e) {
          function t(t) {
            return e.call(this, t) || this;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getKoValue = function () {
              return this.question.renderedValue;
            }),
            t
          );
        })(o.a),
        c = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            (n.name = t),
              (n.isAllSelectedUpdating = !1),
              (n.koAllSelected = i.observable(n.isAllSelected));
            var r = n;
            return (
              n.koAllSelected.subscribe(function (e) {
                r.isAllSelectedUpdating || (e ? r.selectAll() : r.clearValue());
              }),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new u(this);
            }),
            (t.prototype.onSurveyValueChanged = function (t) {
              e.prototype.onSurveyValueChanged.call(this, t), this.updateAllSelected();
            }),
            (t.prototype.onVisibleChoicesChanged = function () {
              e.prototype.onVisibleChoicesChanged.call(this), this.updateAllSelected();
            }),
            (t.prototype.updateAllSelected = function () {
              (this.isAllSelectedUpdating = !0),
                this.koAllSelected(this.isAllSelected),
                (this.isAllSelectedUpdating = !1);
            }),
            t
          );
        })(l.a);
      a.b.overrideClassCreator('checkbox', function () {
        return new c('');
      }),
        s.b.Instance.registerQuestion('checkbox', function (e) {
          var t = new c(e);
          return (t.choices = s.b.DefaultChoices), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(3),
        a = n(53),
        s = n(8),
        l = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new s.a(this);
            }),
            t
          );
        })(a.a);
      i.b.overrideClassCreator('comment', function () {
        return new l('');
      }),
        o.b.Instance.registerQuestion('comment', function (e) {
          return new l(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(54),
        o = n(8),
        a = (function (e) {
          function t(t, n) {
            var r = e.call(this, t, n) || this;
            return (r.name = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new o.a(this);
            }),
            t
          );
        })(i.a),
        s = (function (e) {
          function t(t, n) {
            var r = e.call(this, t, n) || this;
            return (r.name = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new o.a(this);
            }),
            t
          );
        })(i.b);
      (i.c.Instance.onCreateCustom = function (e, t) {
        return new a(e, t);
      }),
        (i.c.Instance.onCreateComposite = function (e, t) {
          return new s(e, t);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(31)),
        a = n(1),
        s = n(3),
        l = n(17),
        u = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (
              (n.koControlClass = i.pureComputed(function () {
                return (
                  n.question.koCss().control +
                  (n.question.errors.length > 0 ? ' ' + n.question.koCss().onError : '')
                );
              })),
              (n.question.koControlClass = n.koControlClass),
              n
            );
          }
          return n.i(r.b)(t, e), t;
        })(l.b),
        c = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new u(this);
            }),
            t
          );
        })(o.a);
      a.b.overrideClassCreator('dropdown', function () {
        return new c('');
      }),
        s.b.Instance.registerQuestion('dropdown', function (e) {
          var t = new c(e);
          return (t.choices = s.b.DefaultChoices), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(55),
        o = n(1),
        a = n(8),
        s = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new a.a(this);
            }),
            t
          );
        })(i.a);
      o.b.overrideClassCreator('empty', function () {
        return new s('');
      });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(32),
        o = n(1),
        a = n(3),
        s = n(8),
        l = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.question = t), n;
          }
          return n.i(r.b)(t, e), t;
        })(s.a),
        u = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new l(this);
            }),
            t
          );
        })(i.a);
      o.b.overrideClassCreator('expression', function () {
        return new u('');
      }),
        a.b.Instance.registerQuestion('expression', function (e) {
          return new u(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(1)),
        a = n(3),
        s = n(56),
        l = n(8),
        u = n(26),
        c = (function (e) {
          function t(t) {
            var r = e.call(this, t) || this;
            (r.name = t),
              (r.koState = i.observable('empty')),
              (r.koHasValue = i.computed(function () {
                return 'loaded' === r.koState();
              })),
              (r.koData = i.computed(function () {
                return r.koHasValue() ? r.previewValue : [];
              })),
              (r.koInputTitle = i.observable()),
              (r.koChooseFileClass = i.pureComputed(function () {
                return r.koCss().chooseFile + (r.isReadOnly ? ' ' + r.koCss().disabled : '');
              }));
            var o = r,
              a = function (e) {
                r.koState(e), r.koInputTitle(r.inputTitle);
              };
            return (
              r.onStateChanged.add(function (e, t) {
                a(t.state);
              }),
              (r.ondrop = function (e, t) {
                if (r.isReadOnly) return !1;
                t.preventDefault();
                var n = t.originalEvent ? t.originalEvent.dataTransfer : t.dataTransfer;
                r.onChange(n);
              }),
              (r.ondragover = function (e, t) {
                if (r.isReadOnly) return (t.returnValue = !1), !1;
                (t.dataTransfer.dropEffect = 'copy'), t.preventDefault();
              }),
              (r.dochange = function (e, t) {
                var n = t.target || t.srcElement;
                o.onChange(n);
              }),
              (r.doclean = function (e, t) {
                var i = t.target || t.srcElement;
                if (r.needConfirmRemoveFile) {
                  if (!n.i(u.c)(r.confirmRemoveAllMessage)) return;
                }
                var o = i.parentElement.querySelectorAll('input')[0];
                r.clear(), (o.value = '');
              }),
              (r.doremovefile = function (e, t) {
                if (r.needConfirmRemoveFile) {
                  if (!n.i(u.c)(r.getConfirmRemoveMessage(e.name))) return;
                }
                r.removeFile(e);
              }),
              (r.dodownload = function (e, t) {
                if (!n.i(u.d)()) return !0;
                n.i(u.e)(e.content, e.name);
              }),
              r
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new l.a(this);
            }),
            (t.prototype.onChange = function (e) {
              if (window.FileReader && e && e.files && !(e.files.length < 1)) {
                for (var t = [], n = this.allowMultiple ? e.files.length : 1, r = 0; r < n; r++)
                  t.push(e.files[r]);
                (e.value = ''), this.loadFiles(t);
              }
            }),
            t
          );
        })(s.a);
      o.b.overrideClassCreator('file', function () {
        return new c('');
      }),
        a.b.Instance.registerQuestion('file', function (e) {
          return new c(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(3),
        a = n(8),
        s = n(57),
        l = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new a.a(this);
            }),
            t
          );
        })(s.a);
      i.b.overrideClassCreator('html', function () {
        return new l('');
      }),
        o.b.Instance.registerQuestion('html', function (e) {
          return new l(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(1),
        o = n(3),
        a = n(8),
        s = n(58),
        l = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new a.a(this);
            }),
            t
          );
        })(s.a);
      i.b.overrideClassCreator('image', function () {
        return new l('');
      }),
        o.b.Instance.registerQuestion('image', function (e) {
          return new l(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(59),
        o = n(1),
        a = n(3),
        s = n(17),
        l = (function (e) {
          function t(t) {
            return e.call(this, t) || this;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getKoValue = function () {
              return this.question.renderedValue;
            }),
            t
          );
        })(s.a),
        u = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new l(this);
            }),
            t
          );
        })(i.a);
      o.b.overrideClassCreator('imagepicker', function () {
        return new u('');
      }),
        a.b.Instance.registerQuestion('imagepicker', function (e) {
          return new u(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(60)),
        a = n(8),
        s = n(15),
        l = n(1),
        u = n(3),
        c = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (
              (n.name = t),
              (n.koVisibleRows = i.observableArray()),
              (n.koVisibleColumns = i.observableArray()),
              n.koVisibleRows(n.visibleRows),
              n.koVisibleColumns(n.visibleColumns),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new a.a(this);
            }),
            (t.prototype.onColumnsChanged = function () {
              e.prototype.onColumnsChanged.call(this), this.koVisibleColumns(this.visibleColumns);
            }),
            (t.prototype.onRowsChanged = function () {
              e.prototype.onRowsChanged.call(this), this.koVisibleRows(this.visibleRows);
            }),
            (t.prototype.onSurveyLoad = function () {
              e.prototype.onSurveyLoad.call(this), this.onRowsChanged();
            }),
            (t.prototype.onMatrixRowCreated = function (e) {
              new s.a(e);
            }),
            (t.prototype.getVisibleRows = function () {
              var t = e.prototype.getVisibleRows.call(this);
              return this.koVisibleRows(t), t;
            }),
            t
          );
        })(o.a);
      l.b.overrideClassCreator('matrix', function () {
        return new c('');
      }),
        u.b.Instance.registerQuestion('matrix', function (e) {
          var t = new c(e);
          return (t.rows = u.b.DefaultRows), (t.columns = u.b.DefaultColums), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(1)),
        a = n(3),
        s = n(46),
        l = n(62),
        u = n(24),
        c = n(22),
        p = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.question.getKoPopupIsVisible = n.getKoPopupIsVisible), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.isAddRowTop = function () {
              return this.question.isAddRowOnTop;
            }),
            (t.prototype.isAddRowBottom = function () {
              return this.question.isAddRowOnBottom;
            }),
            (t.prototype.canRemoveRows = function () {
              return !this.question.isReadOnly && this.question.canRemoveRows;
            }),
            (t.prototype.addRow = function () {
              this.question.addRow();
            }),
            (t.prototype.removeRow = function (e) {
              this.question.removeRowUI(e);
            }),
            (t.prototype.getKoPopupIsVisible = function (e) {
              return i.observable(e.isDetailPanelShowing);
            }),
            t
          );
        })(s.a),
        d = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new p(this);
            }),
            (t.prototype.createNewDetailPanel = function () {
              return new c.b();
            }),
            t
          );
        })(l.a);
      o.b.overrideClassCreator('matrixdynamic', function () {
        return new d('');
      }),
        a.b.Instance.registerQuestion('matrixdynamic', function (e) {
          var t = new d(e);
          return (t.choices = [1, 2, 3, 4, 5]), (t.rowCount = 2), u.a.addDefaultColumns(t), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(63)),
        a = n(8),
        s = n(47),
        l = n(1),
        u = n(3),
        c = (function (e) {
          function t(t, n) {
            void 0 === t && (t = null), void 0 === n && (n = null);
            var r = e.call(this, t, n) || this;
            return (r.name = t), r;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.createEditor = function (e) {
              return new s.a(e);
            }),
            t
          );
        })(o.a),
        p = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (
              (n.name = t),
              (n.koRows = i.observableArray(n.getRows())),
              (n.colCountChangedCallback = function () {
                n.onColCountChanged();
              }),
              n.onColCountChanged(),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new a.a(this);
            }),
            (t.prototype.onColCountChanged = function () {
              this.koRows(this.getRows());
            }),
            (t.prototype.createTextItem = function (e, t) {
              return new c(e, t);
            }),
            t
          );
        })(o.b);
      l.b.overrideClassCreator('multipletextitem', function () {
        return new c('');
      }),
        l.b.overrideClassCreator('multipletext', function () {
          return new p('');
        }),
        u.b.Instance.registerQuestion('multipletext', function (e) {
          var t = new p(e);
          return t.addItem('text1'), t.addItem('text2'), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(6)),
        a = n(1),
        s = n(3),
        l = n(8),
        u = n(64),
        c = n(22),
        p = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            n.koRecalc = i.observable(0);
            var r = n;
            return (
              (n.koAddPanelClick = function () {
                r.addPanel();
              }),
              (n.koRemovePanelClick = function (e) {
                r.removePanel(e);
              }),
              (n.koPrevPanelClick = function () {
                r.question.goToPrevPanel();
              }),
              (n.koNextPanelClick = function () {
                r.question.goToNextPanel();
              }),
              (n.koCanAddPanel = i.pureComputed(function () {
                return r.koRecalc(), r.question.canAddPanel;
              })),
              (n.koCanRemovePanel = i.pureComputed(function () {
                return r.koRecalc(), r.question.canRemovePanel;
              })),
              (n.koIsPrevButton = i.pureComputed(function () {
                return r.koRecalc(), r.question.isPrevButtonShowing;
              })),
              (n.koIsNextButton = i.pureComputed(function () {
                return r.koRecalc(), r.question.isNextButtonShowing;
              })),
              (n.koIsRange = i.pureComputed(function () {
                return r.koRecalc(), r.question.isRangeShowing;
              })),
              (n.koPanel = i.pureComputed(function () {
                return r.koRecalc(), r.question.currentPanel;
              })),
              (n.koIsList = i.pureComputed(function () {
                return r.koRecalc(), r.question.isRenderModeList;
              })),
              (n.koIsProgressTop = i.pureComputed(function () {
                return r.koRecalc(), r.question.isProgressTopShowing;
              })),
              (n.koIsProgressBottom = i.pureComputed(function () {
                return r.koRecalc(), r.question.isProgressBottomShowing;
              })),
              (n.koRangeValue = i.observable(r.question.currentIndex)),
              n.koRangeValue.subscribe(function (e) {
                r.question.currentIndex = e;
              }),
              (n.koRangeMax = i.pureComputed(function () {
                return r.koRecalc(), r.question.panelCount - 1;
              })),
              (n.koButtonAddCss = i.pureComputed(function () {
                return r.koRecalc(), r.buttonAddCss;
              })),
              (n.koButtonNextCss = i.pureComputed(function () {
                return r.koRecalc(), r.buttonNextCss;
              })),
              (n.koButtonPrevCss = i.pureComputed(function () {
                return r.koRecalc(), r.buttonPrevCss;
              })),
              (n.koProgressText = i.pureComputed(function () {
                return r.koRecalc(), r.question.progressText;
              })),
              (n.koProgress = i.pureComputed(function () {
                return r.koRecalc(), r.progress;
              })),
              (n.question.koAddPanelClick = n.koAddPanelClick),
              (n.question.koRemovePanelClick = n.koRemovePanelClick),
              (n.question.koPrevPanelClick = n.koPrevPanelClick),
              (n.question.koNextPanelClick = n.koNextPanelClick),
              (n.question.koCanAddPanel = n.koCanAddPanel),
              (n.question.koCanRemovePanel = n.koCanRemovePanel),
              (n.question.koProgressText = n.koProgressText),
              (n.question.koProgress = n.koProgress),
              (n.question.koPanel = n.koPanel),
              (n.question.koIsList = n.koIsList),
              (n.question.koIsProgressTop = n.koIsProgressTop),
              (n.question.koIsProgressBottom = n.koIsProgressBottom),
              (n.question.koIsPrevButton = n.koIsPrevButton),
              (n.question.koIsNextButton = n.koIsNextButton),
              (n.question.koIsRange = n.koIsRange),
              (n.question.koRangeValue = n.koRangeValue),
              (n.question.koRangeMax = n.koRangeMax),
              (n.question.koButtonAddCss = n.koButtonAddCss),
              (n.question.koButtonNextCss = n.koButtonNextCss),
              (n.question.koButtonPrevCss = n.koButtonPrevCss),
              (n.question.koPanelAfterRender = function (e, t) {
                r.panelAfterRender(e, t);
              }),
              (n.question.panelCountChangedCallback = function () {
                r.onPanelCountChanged();
              }),
              (n.question.renderModeChangedCallback = function () {
                r.onRenderModeChanged();
              }),
              (n.question.currentIndexChangedCallback = function () {
                r.onCurrentIndexChanged();
              }),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onPanelCountChanged = function () {
              this.onCurrentIndexChanged();
            }),
            (t.prototype.onRenderModeChanged = function () {
              this.onCurrentIndexChanged();
            }),
            (t.prototype.onCurrentIndexChanged = function () {
              this.koRecalc(this.koRecalc() + 1), this.koRangeValue(this.question.currentIndex);
            }),
            (t.prototype.addPanel = function () {
              this.question.addPanelUI();
            }),
            (t.prototype.removePanel = function (e) {
              var t = this.question;
              t.isRenderModeList || (e = t.currentPanel), t.removePanelUI(e);
            }),
            (t.prototype.panelAfterRender = function (e, t) {
              if (this.question && this.question.survey) {
                var n = o.a.GetFirstNonTextElement(e);
                this.question.survey.afterRenderPanel(t, n);
              }
            }),
            Object.defineProperty(t.prototype, 'buttonAddCss', {
              get: function () {
                var e = this.question,
                  t = e.cssClasses.button + ' ' + e.cssClasses.buttonAdd;
                return (
                  'list' === this.question.renderMode &&
                    (t += ' ' + e.cssClasses.buttonAdd + '--list-mode'),
                  t
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'buttonPrevCss', {
              get: function () {
                var e = this.question,
                  t = e.cssClasses.buttonPrev;
                return (
                  e.isPrevButtonShowing || (t += ' ' + e.cssClasses.buttonPrev + '--disabled'), t
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'buttonNextCss', {
              get: function () {
                var e = this.question,
                  t = e.cssClasses.buttonNext;
                return (
                  e.isNextButtonShowing || (t += ' ' + e.cssClasses.buttonNext + '--disabled'), t
                );
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'progress', {
              get: function () {
                var e = this.question.panelCount - 1;
                return (this.question.currentIndex / e) * 100 + '%';
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(l.a),
        d = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new p(this);
            }),
            (t.prototype.createNewPanelObject = function () {
              return new c.b();
            }),
            t
          );
        })(u.a);
      a.b.overrideClassCreator('paneldynamic', function () {
        return new d('');
      }),
        s.b.Instance.registerQuestion('paneldynamic', function (e) {
          return new d(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(65),
        o = n(1),
        a = n(3),
        s = n(17),
        l = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new s.a(this);
            }),
            t
          );
        })(i.a);
      o.b.overrideClassCreator('radiogroup', function () {
        return new l('');
      }),
        a.b.Instance.registerQuestion('radiogroup', function (e) {
          var t = new l(e);
          return (t.choices = a.b.DefaultChoices), t;
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(8)),
        a = n(66),
        s = n(1),
        l = n(3),
        u = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            (n.koVisibleRateValues = i.observableArray()),
              (n.question.koVisibleRateValues = n.koVisibleRateValues);
            var r = n;
            return (
              (n.question.rateValuesChangedCallback = function () {
                r.onRateValuesChanged();
              }),
              (n.question.koGetCss = function (e) {
                var t = r.question.cssClasses.item,
                  i = r.question.cssClasses.selected,
                  o = r.question.cssClasses.disabled,
                  a = t;
                return (
                  n.question.value == e.value && (a = a + ' ' + i),
                  n.question.isReadOnly && (a = a + ' ' + o),
                  a
                );
              }),
              n
            );
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onRateValuesChanged = function () {
              this.koVisibleRateValues(this.getValues());
            }),
            (t.prototype.getValues = function () {
              return this.question.visibleRateValues;
            }),
            t
          );
        })(o.a),
        c = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new u(this);
            }),
            t
          );
        })(a.a);
      s.b.overrideClassCreator('rating', function () {
        return new c('');
      }),
        l.b.Instance.registerQuestion('rating', function (e) {
          return new c(e);
        });
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(67),
        o = n(1),
        a = n(8),
        s = (function (e) {
          function t(t) {
            var n = e.call(this, t) || this;
            return (n.name = t), n;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.onBaseCreating = function () {
              e.prototype.onBaseCreating.call(this), new a.a(this);
            }),
            t
          );
        })(i.a);
      o.b.overrideClassCreator('signaturepad', function () {
        return new s('');
      });
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'السابق',
          pageNextText: 'التالي',
          completeText: 'إرسال البيانات',
          previewText: 'معاينة',
          editText: 'تعديل',
          startSurveyText: 'بداية',
          otherItemText: 'نص آخر',
          noneItemText: 'لا شيء',
          selectAllItemText: 'اختر الكل',
          progressText: '{1} صفحة {0} من',
          panelDynamicProgressText: 'سجل {0} من {1}',
          questionsProgressText: 'تمت الإجابة على أسئلة {0} / {1}',
          emptySurvey: 'لا توجد صفحة مرئية أو سؤال في النموذج',
          completingSurvey: 'شكرا لكم لاستكمال النموذج!',
          completingSurveyBefore: 'تظهر سجلاتنا أنك قد أكملت هذا الاستطلاع بالفعل.',
          loadingSurvey: '...يتم تحميل النموذج',
          optionsCaption: '...اختر',
          value: 'القيمة',
          requiredError: '.يرجى الإجابة على السؤال',
          requiredErrorInPanel: 'الرجاء الإجابة على سؤال واحد على الأقل.',
          requiredInAllRowsError: 'يرجى الإجابة على الأسئلة في جميع الصفوف',
          numericError: 'يجب أن تكون القيمة رقمية.',
          textMinLength: 'الرجاء إدخال ما لا يقل عن {0} حروف',
          textMaxLength: 'الرجاء إدخال أقل من {0} حروف',
          textMinMaxLength: 'يرجى إدخال أكثر من {0} وأقل من {1} حروف',
          minRowCountError: 'يرجى ملء ما لا يقل عن {0} الصفوف',
          minSelectError: 'يرجى تحديد ما لا يقل عن {0} المتغيرات',
          maxSelectError: 'يرجى تحديد ما لا يزيد عن {0} المتغيرات',
          numericMinMax: "و'{0}' يجب أن تكون مساوية أو أكثر من {1} وتساوي أو أقل من {2}ا",
          numericMin: "و'{0}' يجب أن تكون مساوية أو أكثر من {1}ا",
          numericMax: "و'{0}' يجب أن تكون مساوية أو أقل من {1}ا",
          invalidEmail: 'الرجاء إدخال بريد الكتروني صحيح',
          invalidExpression: "يجب أن يعرض التعبير: {0} 'صواب'.",
          urlRequestError: "طلب إرجاع خطأ '{0}'. {1}ا",
          urlGetChoicesError: "عاد طلب البيانات فارغ أو 'المسار' غير صحيح ",
          exceedMaxSize: 'ينبغي ألا يتجاوز حجم الملف {0}ا',
          otherRequiredError: 'الرجاء إدخال قيمة أخرى',
          uploadingFile: 'تحميل الملف الخاص بك. يرجى الانتظار عدة ثوان والمحاولة لاحقًا',
          loadingFile: 'جار التحميل...',
          chooseFile: 'اختر الملفات...',
          noFileChosen: 'لم تقم باختيار ملف',
          confirmDelete: 'هل تريد حذف السجل؟',
          keyDuplicationError: 'يجب أن تكون هذه القيمة فريدة.',
          addColumn: 'أضف العمود',
          addRow: 'اضافة صف',
          removeRow: 'إزالة صف',
          addPanel: 'اضف جديد',
          removePanel: 'إزالة',
          choices_Item: 'بند',
          matrix_column: 'عمود',
          matrix_row: 'صف',
          savingData: 'يتم حفظ النتائج على الخادم ...',
          savingDataError: 'حدث خطأ ولم نتمكن من حفظ النتائج.',
          savingDataSuccess: 'تم حفظ النتائج بنجاح!',
          saveAgainButton: 'حاول مجددا',
          timerMin: 'دقيقة',
          timerSec: 'ثانية',
          timerSpentAll: 'لقد أنفقت {0} على هذه الصفحة و {1} إجمالاً.',
          timerSpentPage: 'لقد أنفقت {0} على هذه الصفحة.',
          timerSpentSurvey: 'لقد أنفقت {0} إجمالاً.',
          timerLimitAll: 'لقد أنفقت {0} من {1} في هذه الصفحة و {2} من إجمالي {3}.',
          timerLimitPage: 'لقد أنفقت {0} من {1} في هذه الصفحة.',
          timerLimitSurvey: 'لقد أنفقت {0} من إجمالي {1}.',
          cleanCaption: 'نظيف',
          clearCaption: 'واضح',
          chooseFileCaption: 'اختر ملف',
          removeFileCaption: 'قم بإزالة هذا الملف',
          booleanCheckedLabel: 'نعم',
          booleanUncheckedLabel: 'لا',
          confirmRemoveFile: 'هل أنت متأكد أنك تريد إزالة هذا الملف: {0}؟',
          confirmRemoveAllFiles: 'هل أنت متأكد أنك تريد إزالة كافة الملفات؟',
          questionTitlePatternText: 'عنوان السؤال',
        };
      (r.a.locales.ar = i), (r.a.localeNames.ar = 'العربية');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Назад',
          pageNextText: 'Напред',
          completeText: 'Край',
          previewText: 'Визуализация',
          editText: 'редактиране',
          startSurveyText: 'Начало',
          otherItemText: 'Друго (опишете)',
          noneItemText: 'Нито един',
          selectAllItemText: 'Всички',
          progressText: 'стр. {0}, общо стр. {1}',
          panelDynamicProgressText: 'Запис {0} от {1}',
          questionsProgressText: 'Отговорени на {0} / {1} въпроса',
          emptySurvey: 'Анкетата не съдържа видими страници или въпроси.',
          completingSurvey: 'Благодарим ви за участието в анкетата!',
          completingSurveyBefore: 'Изглежда, че вие вече сте попълнили анкетата.',
          loadingSurvey: 'Зареждане на анкетата...',
          optionsCaption: 'Изберете...',
          value: 'value',
          requiredError: 'Моля, отговорете на следния въпрос.',
          requiredErrorInPanel: 'Моля, отговорете поне на един от въпросите.',
          requiredInAllRowsError: 'Моля, отговорете на въпросите на всички редове.',
          numericError: 'Стойността следва да бъде число.',
          textMinLength: 'Моля, използвайте поне {0} символа.',
          textMaxLength: 'Моля, използвайте не повече от {0} символа.',
          textMinMaxLength: 'Моля, използвайте повече от {0} и по-малко от {1} символа.',
          minRowCountError: 'Моля, попълнете поне {0} реда.',
          minSelectError: 'Моля, изберете поне {0} варианта.',
          maxSelectError: 'Моля, изберете не повече от {0} варианта.',
          numericMinMax:
            "Стойността '{0}' следва да бъде равна или по-голяма от {1} и равна или по-малка от {2}",
          numericMin: "Стойността '{0}' следва да бъде равна или по-голяма от {1}",
          numericMax: "Стойността '{0}' следва да бъде равна или по-малка от {1}",
          invalidEmail: 'Моля, въведете валиден адрес на електронна поща.',
          invalidExpression: "Изразът: {0} трябва да дава резултат 'true' (истина).",
          urlRequestError: "Заявката води до грешка '{0}'. {1}",
          urlGetChoicesError:
            "Заявката не връща данни или частта 'path' (път до търсения ресурс на сървъра) е неправилно зададена",
          exceedMaxSize: 'Размерът на файла следва да не превишава {0}.',
          otherRequiredError: 'Моля, въведете другата стойност.',
          uploadingFile:
            'Вашит файл се зарежда на сървъра. Моля, изчакайте няколко секунди и тогава опитвайте отново.',
          loadingFile: 'Зареждане...',
          chooseFile: 'Изберете файл(ове)...',
          noFileChosen: 'Няма избран файл',
          confirmDelete: 'Желаете ли да изтриете записа?',
          keyDuplicationError: 'Стойността следва да бъде уникална.',
          addColumn: 'Добавяне на колона',
          addRow: 'Добавяне на ред',
          removeRow: 'Премахване на ред',
          addPanel: 'Добавяне на панел',
          removePanel: 'Премахване на панел',
          choices_Item: 'елемент',
          matrix_column: 'Колона',
          matrix_row: 'Ред',
          savingData: 'Резултатите се запазват на сървъра...',
          savingDataError: 'Поради възникнала грешка резултатите не можаха да бъдат запазени.',
          savingDataSuccess: 'Резултатите бяха запазени успешно!',
          saveAgainButton: 'Нов опит',
          timerMin: 'мин',
          timerSec: 'сек',
          timerSpentAll: 'Вие използвахте {0} на тази страница и общо {1}.',
          timerSpentPage: 'Вие използвахте {0} на тази страница.',
          timerSpentSurvey: 'Вие използвахте общо {0}.',
          timerLimitAll: 'Вие изпозвахте {0} от {1} на тази страница и общо {2} от {3}.',
          timerLimitPage: 'Вие използвахте {0} от {1} на тази страница.',
          timerLimitSurvey: 'Вие използвахте общо {0} от {1}.',
          cleanCaption: 'Изчистване',
          clearCaption: 'Начално състояние',
          chooseFileCaption: 'Изберете файл',
          removeFileCaption: 'Премахване на файла',
          booleanCheckedLabel: 'Да',
          booleanUncheckedLabel: 'Не',
          confirmRemoveFile: 'Наистина ли искате да премахнете този файл: {0}?',
          confirmRemoveAllFiles: 'Наистина ли искате да премахнете всички файлове?',
          questionTitlePatternText: 'Заглавие на въпроса',
        };
      (r.a.locales.bg = i), (r.a.localeNames.bg = 'Bulgarian');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Anterior',
          pageNextText: 'Següent',
          completeText: 'Complet',
          otherItemText: 'Un altre (descrigui)',
          progressText: 'Pàgina {0} de {1}',
          emptySurvey: "No hi ha cap pàgina visible o pregunta a l'enquesta.",
          completingSurvey: "Gràcies per completar l'enquesta!",
          loadingSurvey: "L'enquesta s'està carregant ...",
          optionsCaption: 'Selecciona ...',
          requiredError: 'Si us plau contesti la pregunta.',
          requiredInAllRowsError: 'Si us plau contesti les preguntes de cada filera.',
          numericError: "L'estimació ha de ser numèrica.",
          textMinLength: 'Si us plau entre almenys {0} símbols.',
          textMaxLength: 'Si us plau entre menys de {0} símbols.',
          textMinMaxLength: 'Si us plau entre més de {0} i menys de {1} símbols.',
          minRowCountError: 'Si us plau ompli almenys {0} fileres.',
          minSelectError: 'Si us plau seleccioni almenys {0} variants.',
          maxSelectError: 'Si us plau seleccioni no més de {0} variants.',
          numericMinMax: "El '{0}' deu ser igual o més de {1} i igual o menys de {2}",
          numericMin: "El '{0}' ha de ser igual o més de {1}",
          numericMax: "El '{0}' ha de ser igual o menys de {1}",
          invalidEmail: 'Si us plau afegiu un correu electrònic vàlid.',
          urlRequestError: "La sol·licitud va tornar error '{0}'. {1}",
          urlGetChoicesError:
            "La sol·licitud va tornar buida de dates o la propietat 'trajectòria' no és correcta",
          exceedMaxSize: "La mida de l'arxiu no pot excedir {0}.",
          otherRequiredError: "Si us plau afegiu l'altra estimació.",
          uploadingFile:
            "El seu arxiu s'està pujant. Si us plau esperi uns segons i intenteu-ho de nou.",
          addRow: 'Afegiu una filera',
          removeRow: 'Eliminar una filera',
          choices_firstItem: 'primer article',
          choices_secondItem: 'segon article',
          choices_thirdItem: 'tercer article',
          matrix_column: 'Columna',
          matrix_row: 'Filera',
        };
      (r.a.locales.ca = i), (r.a.localeNames.ca = 'català');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Předchozí',
          pageNextText: 'Další',
          completeText: 'Hotovo',
          previewText: 'Náhled',
          editText: 'Upravit',
          startSurveyText: 'Start',
          otherItemText: 'Jiná odpověď (napište)',
          noneItemText: 'Žádný',
          selectAllItemText: 'Vybrat vše',
          progressText: 'Strana {0} z {1}',
          panelDynamicProgressText: 'Záznam {0} z {1}',
          questionsProgressText: 'Zodpovězené otázky: {0} / {1}',
          emptySurvey: 'Průzkumu neobsahuje žádné otázky.',
          completingSurvey: 'Děkujeme za vyplnění průzkumu!',
          completingSurveyBefore: 'Naše záznamy ukazují, že jste tento průzkum již dokončili.',
          loadingSurvey: 'Probíhá načítání průzkumu...',
          optionsCaption: 'Vyber...',
          value: 'hodnota',
          requiredError: 'Odpovězte prosím na otázku.',
          requiredErrorInPanel: 'Please answer at least one question.',
          requiredInAllRowsError: 'Odpovězte prosím na všechny otázky.',
          numericError: 'V tomto poli lze zadat pouze čísla.',
          textMinLength: 'Zadejte prosím alespoň {0} znaků.',
          textMaxLength: 'Zadejte prosím méně než {0} znaků.',
          textMinMaxLength: 'Zadejte prosím více než {0} a méně než {1} znaků.',
          minRowCountError: 'Vyplňte prosím alespoň {0} řádků.',
          minSelectError: 'Vyberte prosím alespoň {0} varianty.',
          maxSelectError: 'Nevybírejte prosím více než {0} variant.',
          numericMinMax: "Odpověď '{0}' by mělo být větší nebo rovno {1} a menší nebo rovno {2}",
          numericMin: "Odpověď '{0}' by mělo být větší nebo rovno {1}",
          numericMax: "Odpověď '{0}' by mělo být menší nebo rovno {1}",
          invalidEmail: 'Zadejte prosím platnou e-mailovou adresu.',
          invalidExpression: 'Výraz: {0} by měl vrátit hodnotu „true“.',
          urlRequestError: "Požadavek vrátil chybu '{0}'. {1}",
          urlGetChoicesError: 'Požadavek nevrátil data nebo cesta je neplatná',
          exceedMaxSize: 'Velikost souboru by neměla být větší než {0}.',
          otherRequiredError: 'Zadejte prosím jinou hodnotu.',
          uploadingFile: 'Váš soubor se nahrává. Zkuste to prosím za několik sekund.',
          loadingFile: 'Načítání...',
          chooseFile: 'Vyberte soubory ...',
          noFileChosen: 'Není zvolený žádný soubor',
          confirmDelete: 'Chcete smazat záznam?',
          keyDuplicationError: 'Tato hodnota by měla být jedinečná.',
          addColumn: 'Přidat sloupec',
          addRow: 'Přidat řádek',
          removeRow: 'Odstranit',
          addPanel: 'Přidat nový',
          removePanel: 'Odstranit',
          choices_Item: 'položka',
          matrix_column: 'Sloupec',
          matrix_row: 'Řádek',
          savingData: 'Výsledky se ukládají na server ...',
          savingDataError: 'Došlo k chybě a výsledky jsme nemohli uložit.',
          savingDataSuccess: 'Výsledky byly úspěšně uloženy!',
          saveAgainButton: 'Zkus to znovu',
          timerMin: 'min',
          timerSec: 'sek',
          timerSpentAll: 'Na této stránce jste utratili celkem {0} a celkem {1}.',
          timerSpentPage: 'Na této stránce jste utratili {0}.',
          timerSpentSurvey: 'Celkem jste utratili {0}.',
          timerLimitAll: 'Na této stránce jste utratili {0} z {1} a celkem {2} z {3}.',
          timerLimitPage: 'Na této stránce jste strávili {0} z {1}.',
          timerLimitSurvey: 'Celkově jste utratili {0} z {1}.',
          cleanCaption: 'Čistý',
          clearCaption: 'Průhledná',
          chooseFileCaption: 'Vyberte soubor',
          removeFileCaption: 'Odeberte tento soubor',
          booleanCheckedLabel: 'Ano',
          booleanUncheckedLabel: 'Ne',
          confirmRemoveFile: 'Opravdu chcete odebrat tento soubor: {0}?',
          confirmRemoveAllFiles: 'Opravdu chcete odstranit všechny soubory?',
          questionTitlePatternText: 'Název otázky',
        };
      (r.a.locales.cs = i), (r.a.localeNames.cs = 'čeština');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Tilbage',
          pageNextText: 'Videre',
          completeText: 'Færdig',
          previewText: 'Forpremiere',
          editText: 'Redigér',
          startSurveyText: 'Start',
          otherItemText: 'Valgfrit svar...',
          noneItemText: 'Ingen',
          selectAllItemText: 'Vælg alle',
          progressText: 'Side {0} af {1}',
          panelDynamicProgressText: 'Optag {0} af {1}',
          questionsProgressText: 'Besvarede {0} / {1} spørgsmål',
          emptySurvey: 'Der er ingen synlige spørgsmål.',
          completingSurvey: 'Mange tak for din besvarelse!',
          completingSurveyBefore:
            'Vores data viser at du allerede har gennemført dette spørgeskema.',
          loadingSurvey: 'Spørgeskemaet hentes fra serveren...',
          optionsCaption: 'Vælg...',
          value: 'værdi',
          requiredError: 'Besvar venligst spørgsmålet.',
          requiredErrorInPanel: 'Besvar venligst mindst ét spørgsmål.',
          requiredInAllRowsError: 'Besvar venligst spørgsmål i alle rækker.',
          numericError: 'Angiv et tal.',
          textMinLength: 'Angiv mindst {0} tegn.',
          textMaxLength: 'Please enter less than {0} characters.',
          textMinMaxLength: 'Angiv mere end {0} og mindre end {1} tegn.',
          minRowCountError: 'Udfyld mindst {0} rækker.',
          minSelectError: 'Vælg venligst mindst {0} svarmulighed(er).',
          maxSelectError: 'Vælg venligst færre {0} svarmuligheder(er).',
          numericMinMax:
            "'{0}' skal være lig med eller større end {1} og lig med eller mindre end {2}",
          numericMin: "'{0}' skal være lig med eller større end {1}",
          numericMax: "'{0}' skal være lig med eller mindre end {1}",
          invalidEmail: 'Angiv venligst en gyldig e-mail adresse.',
          invalidExpression: "Udtrykket: {0} skal returnere 'true'.",
          urlRequestError: "Forespørgslen returnerede fejlen '{0}'. {1}",
          urlGetChoicesError:
            "Forespørgslen returnerede ingen data eller 'path' parameteren er forkert",
          exceedMaxSize: 'Filstørrelsen må ikke overstige {0}.',
          otherRequiredError: 'Angiv en værdi for dit valgfrie svar.',
          uploadingFile: 'Din fil bliver uploadet. Vent nogle sekunder og prøv eventuelt igen.',
          loadingFile: 'Indlæser...',
          chooseFile: 'Vælg fil(er)...',
          noFileChosen: 'Ingen fil er valgt',
          confirmDelete: 'Vil du fjerne den?',
          keyDuplicationError: 'Denne værdi skal være unik.',
          addColumn: 'Tilføj kolonne',
          addRow: 'Tilføj række',
          removeRow: 'Fjern',
          addPanel: 'Tilføj ny',
          removePanel: 'Fjern',
          choices_Item: 'valg',
          matrix_column: 'Kolonne',
          matrix_row: 'Række',
          savingData: 'Resultaterne bliver gemt på serveren...',
          savingDataError: 'Der opstod en fejl og vi kunne ikke gemme resultatet.',
          savingDataSuccess: 'Resultatet blev gemt!',
          saveAgainButton: 'Prøv igen',
          timerMin: 'min',
          timerSec: 'sek',
          timerSpentAll: 'Du har brugt {0} på denne side og {1} i alt.',
          timerSpentPage: 'Du har brugt {0} på denne side.',
          timerSpentSurvey: 'Du har brugt {0} i alt.',
          timerLimitAll: 'Du har brugt {0} af {1} på denne side og {2} af {3} i alt.',
          timerLimitPage: 'Du har brugt {0} af {1} på denne side.',
          timerLimitSurvey: 'Du har brugt {0} af {1} i alt.',
          cleanCaption: 'Rens',
          clearCaption: 'Fjern',
          chooseFileCaption: 'Vælg fil',
          removeFileCaption: 'Fjern denne fil',
          booleanCheckedLabel: 'Ja',
          booleanUncheckedLabel: 'Ingen',
          confirmRemoveFile: 'Er du sikker på, at du vil fjerne denne fil: {0}?',
          confirmRemoveAllFiles: 'Er du sikker på, at du vil fjerne alle filer?',
          questionTitlePatternText: 'Spørgsmåls titel',
        };
      (r.a.locales.da = i), (r.a.localeNames.da = 'dansk');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Vorige',
          pageNextText: 'Volgende',
          completeText: 'Verzenden',
          previewText: 'Voorbeeld',
          editText: 'Bewerk',
          startSurveyText: 'Begin met',
          otherItemText: 'Anders, nl.',
          noneItemText: 'Geen',
          selectAllItemText: 'Selecteer Alles',
          progressText: 'Pagina {0} van {1}',
          panelDynamicProgressText: 'Record {0} of {1}',
          questionsProgressText: 'Geantwoord {0}/{1} vragen',
          emptySurvey: 'Er is geen zichtbare pagina of vraag in deze vragenlijst',
          completingSurvey: 'Bedankt voor het invullen van de vragenlijst',
          completingSurveyBefore:
            'Onze gegevens tonen aan dat je deze vragenlijst reeds beantwoord hebt.',
          loadingSurvey: 'De vragenlijst is aan het laden...',
          optionsCaption: 'Kies...',
          value: 'waarde',
          requiredError: 'Dit is een vereiste vraag',
          requiredErrorInPanel: 'Gelieve ten minste een vraag te beantwoorden.',
          requiredInAllRowsError: 'Deze vraag vereist één antwoord per rij',
          numericError: 'Het antwoord moet een getal zijn',
          textMinLength: 'Vul minstens {0} karakters in',
          textMaxLength: 'Gelieve minder dan {0} karakters in te vullen.',
          textMinMaxLength: 'Gelieve meer dan {0} en minder dan {1} karakters in te vullen.',
          minRowCountError: 'Gelieve ten minste {0} rijen in te vullen.',
          minSelectError: 'Selecteer minimum {0} antwoorden',
          maxSelectError: 'Selecteer niet meer dan {0} antwoorden',
          numericMinMax:
            "Uw antwoord '{0}' moet groter of gelijk zijn aan {1} en kleiner of gelijk aan {2}",
          numericMin: "Uw antwoord '{0}' moet groter of gelijk zijn aan {1}",
          numericMax: "Uw antwoord '{0}' moet groter of gelijk zijn aan {1}",
          invalidEmail: 'Vul een geldig e-mailadres in',
          invalidExpression: "De uitdrukking: {0} moet 'waar' teruggeven.",
          urlRequestError: "De vraag keerde een fout terug '{0}'. {1}",
          urlGetChoicesError:
            "De vraag gaf een leeg antwoord terug of de 'pad' eigenschap is niet correct",
          exceedMaxSize: 'De grootte van het bestand mag niet groter zijn dan {0}',
          otherRequiredError: "Vul het veld 'Anders, nl.' in",
          uploadingFile:
            'Uw bestand wordt opgeladen. Gelieve enkele seconden te wachten en opnieuw te proberen.',
          loadingFile: 'Opladen...',
          chooseFile: 'Kies uw bestand(en)...',
          noFileChosen: 'Geen bestand gekozen',
          confirmDelete: 'Wil je deze gegevens verwijderen?',
          keyDuplicationError: 'Deze waarde moet uniek zijn.',
          addColumn: 'Voeg kolom toe',
          addRow: 'Voeg rij toe',
          removeRow: 'Verwijder',
          addPanel: 'Nieuwe toevoegen',
          removePanel: 'Verwijder',
          choices_Item: 'onderwerp',
          matrix_column: 'Kolom',
          matrix_row: 'Rij',
          savingData: 'De resultaten worden bewaard op de server...',
          savingDataError: 'Er was een probleem en we konden de resultaten niet bewaren.',
          savingDataSuccess: 'De resultaten werden succesvol bewaard!',
          saveAgainButton: 'Probeer opnieuw',
          timerMin: 'minimum',
          timerSec: 'sec',
          timerSpentAll: 'U heeft {0} gespendeerd op deze pagina en {1} in totaal.',
          timerSpentPage: 'U heeft {0} op deze pagina gespendeerd.',
          timerSpentSurvey: 'U heeft in totaal {0} gespendeerd.',
          timerLimitAll: 'U heeft {0} van {1} op deze pagina gespendeerd en {2} van {3} in totaal.',
          timerLimitPage: 'U heeft {0} van {1} gespendeerd op deze pagina.',
          timerLimitSurvey: 'U heeft {0} van {1} in het totaal.',
          cleanCaption: 'Kuis op',
          clearCaption: 'Kuis op',
          chooseFileCaption: 'Gekozen bestand',
          removeFileCaption: 'Verwijder deze file',
          booleanCheckedLabel: 'Ja',
          booleanUncheckedLabel: 'Neen',
          confirmRemoveFile: 'Bent u zeker dat u deze file wilt verwijderen: {0}?',
          confirmRemoveAllFiles: 'Bent u zeker dat u al deze files wilt verwijderen?',
          questionTitlePatternText: 'Titel van de vraag',
        };
      (r.a.locales.nl = i), (r.a.localeNames.nl = 'nederlands');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Tagasi',
          pageNextText: 'Edasi',
          completeText: 'Lõpeta',
          previewText: 'Eelvaade',
          editText: 'Muuda',
          startSurveyText: 'Alusta',
          otherItemText: 'Muu (täpsusta)',
          noneItemText: 'Mitte midagi',
          selectAllItemText: 'Vali kõik',
          progressText: 'Lehekülg {0}/{1}',
          panelDynamicProgressText: 'Kirje {0}/{1}',
          questionsProgressText: 'Vastatud {0} küsimust {1}-st',
          emptySurvey: 'Selles uuringus ei ole ühtki nähtavat lehekülge või küsimust.',
          completingSurvey: 'Aitäh, et vastasid ankeedile!',
          completingSurveyBefore: 'Meie andmetel oled sa sellele ankeedile juba vastanud.',
          loadingSurvey: 'Laen ankeeti...',
          optionsCaption: 'Vali...',
          value: 'väärtus',
          requiredError: 'Palun vasta küsimusele.',
          requiredErrorInPanel: 'Palun vasta vähemalt ühele küsimusele.',
          requiredInAllRowsError: 'Palun anna vastus igal real.',
          numericError: 'See peaks olema numbriline väärtus.',
          textMinLength: 'Palun sisesta vähemalt {0} tähemärki.',
          textMaxLength: 'Palun ära sisesta rohkem kui {0} tähemärki.',
          textMinMaxLength: 'Sisesta palun {0} - {1} tähemärki.',
          minRowCountError: 'Sisesta plaun vähemalt {0} rida.',
          minSelectError: 'Palun vali vähemalt {0} varianti.',
          maxSelectError: 'Palun vali kõige rohkem {0} varianti.',
          numericMinMax:
            "'{0}' peaks olema võrdne või suurem kui {1} ja võrdne või väiksem kui {2}",
          numericMin: "'{0}' peaks olema võrdne või suurem kui {1}",
          numericMax: "'{0}' peaks olema võrnde või väiksem kui {1}",
          invalidEmail: 'Sisesta palun korrektne e-posti aadress.',
          invalidExpression: 'Avaldis: {0} peaks tagastama tõese.',
          urlRequestError: 'Taotlus tagastas vea „{0}”. {1}',
          urlGetChoicesError: "Taotlus tagastas tühjad andmed või atribuut 'path' on vale",
          exceedMaxSize: 'Faili suurus ei tohi ületada {0}.',
          otherRequiredError: 'Sisesta palun muu vastus.',
          uploadingFile:
            'Sinu fail laeb üles. Palun oota mõned sekundid ning proovi seejärel uuesti.',
          loadingFile: 'Laen...',
          chooseFile: 'Vali fail(id)...',
          noFileChosen: 'Faili pole valitud',
          confirmDelete: 'Kas tahad kirje kustutada?',
          keyDuplicationError: 'See väärtus peab olema unikaalne.',
          addColumn: 'Lisa veerg',
          addRow: 'Lisa rida',
          removeRow: 'Eemalda',
          addPanel: 'Lisa uus',
          removePanel: 'Eemalda',
          choices_Item: 'üksus',
          matrix_column: 'Veerg',
          matrix_row: 'Rida',
          savingData: 'Salvestan andmed serveris...',
          savingDataError: 'Tekkis viga ning me ei saanud vastuseid salvestada.',
          savingDataSuccess: 'Vastuste salvestamine õnnestus!',
          saveAgainButton: 'Proovi uuesti',
          timerMin: 'min',
          timerSec: 'sek',
          timerSpentAll: 'Oled veetnud {0} sellel lehel ning kokku {1}.',
          timerSpentPage: 'Oled veetnud {0} sellel lehel.',
          timerSpentSurvey: 'Oled veetnud {0} kokku.',
          timerLimitAll:
            'Oled kulutanud {0} võimalikust {1} sellel lehel ning {2} võimalikust {3} kokku.',
          timerLimitPage: 'Oled kulutanud {0} võimalikust {1} sellel lehel.',
          timerLimitSurvey: 'Oled kulutanud {0} võimalikust {1} koguajast.',
          cleanCaption: 'Puhasta',
          clearCaption: 'Puhasta',
          chooseFileCaption: 'Vali fail',
          removeFileCaption: 'Eemalda see fail',
          booleanCheckedLabel: 'Jah',
          booleanUncheckedLabel: 'Ei',
          confirmRemoveFile: 'Oled sa kindel, et soovid selle faili eemaldada: {0}?',
          confirmRemoveAllFiles: 'Oled sa kindel, et soovid eemaldada kõik failid?',
          questionTitlePatternText: 'Küsimuse pealkiri',
        };
      (r.a.locales.et = i), (r.a.localeNames.et = 'eesti keel');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Edellinen',
          pageNextText: 'Seuraava',
          completeText: 'Valmis',
          previewText: 'Esikatselu',
          editText: 'Muokata',
          startSurveyText: 'alkaa',
          otherItemText: 'Muu (kuvaile)',
          noneItemText: 'Ei mitään',
          selectAllItemText: 'Valitse kaikki',
          progressText: 'Sivu {0}/{1}',
          panelDynamicProgressText: 'Nauhoita {0} / {1}',
          questionsProgressText: 'Vastasi kysymyksiin {0} / {1}',
          emptySurvey: 'Tässä kyselyssä ei ole yhtäkään näkyvillä olevaa sivua tai kysymystä.',
          completingSurvey: 'Kiitos kyselyyn vastaamisesta!',
          completingSurveyBefore: 'Tietojemme mukaan olet jo suorittanut tämän kyselyn.',
          loadingSurvey: 'Kyselyä ladataan palvelimelta...',
          optionsCaption: 'Valitse...',
          value: 'arvo',
          requiredError: 'Vastaa kysymykseen, kiitos.',
          requiredErrorInPanel: 'Vastaa ainakin yhteen kysymykseen.',
          requiredInAllRowsError: 'Vastaa kysymyksiin kaikilla riveillä.',
          numericError: 'Arvon tulee olla numeerinen.',
          textMinLength: 'Ole hyvä ja syötä vähintään {0} merkkiä.',
          textMaxLength: 'Anna alle {0} merkkiä.',
          textMinMaxLength: 'Anna yli {0} ja alle {1} merkkiä.',
          minRowCountError: 'Täytä vähintään {0} riviä.',
          minSelectError: 'Ole hyvä ja valitse vähintään {0} vaihtoehtoa.',
          maxSelectError: 'Ole hyvä ja valitse enintään {0} vaihtoehtoa.',
          numericMinMax:
            "'{0}' täytyy olla enemmän tai yhtä suuri kuin {1} ja vähemmän tai yhtä suuri kuin {2}",
          numericMin: "'{0}' täytyy olla enemmän tai yhtä suuri kuin {1}",
          numericMax: "'{0}' täytyy olla vähemmän tai yhtä suuri kuin {1}",
          invalidEmail: 'Syötä validi sähköpostiosoite.',
          invalidExpression: "Lausekkeen: {0} pitäisi palauttaa 'true'.",
          urlRequestError: 'Pyyntö palautti virheen {0}. {1}',
          urlGetChoicesError: 'Pyyntö palautti tyhjät tiedot tai polku-ominaisuus on väärä',
          exceedMaxSize: 'Tiedoston koko ei saa olla suurempi kuin {0}.',
          otherRequiredError: 'Anna toinen arvo.',
          uploadingFile: 'Tiedostoa ladataan. Odota muutama sekunti ja yritä uudelleen.',
          loadingFile: 'Ladataan...',
          chooseFile: 'Valitse tiedosto (t) ...',
          noFileChosen: 'Ei tiedostoa valittuna',
          confirmDelete: 'Haluatko poistaa tietueen?',
          keyDuplicationError: 'Tämän arvon tulisi olla ainutlaatuinen.',
          addColumn: 'Lisää sarake',
          addRow: 'Lisää rivi',
          removeRow: 'Poista',
          addPanel: 'Lisää uusi',
          removePanel: 'Poista',
          choices_Item: 'kohde',
          matrix_column: 'Sarake',
          matrix_row: 'Rivi',
          savingData: 'Tulokset tallennetaan palvelimelle ...',
          savingDataError: 'Tapahtui virhe, emmekä voineet tallentaa tuloksia.',
          savingDataSuccess: 'Tulokset tallennettiin onnistuneesti!',
          saveAgainButton: 'Yritä uudelleen',
          timerMin: 'min',
          timerSec: 'sek',
          timerSpentAll: 'Olet käyttänyt {0} tällä sivulla ja yhteensä {1}.',
          timerSpentPage: 'Olet viettänyt {0} tällä sivulla.',
          timerSpentSurvey: 'Olet käyttänyt yhteensä {0}.',
          timerLimitAll: 'Olet käyttänyt tällä sivulla {0} / {1} ja yhteensä {2} / {3}.',
          timerLimitPage: 'Olet käyttänyt {0} / {1} tällä sivulla.',
          timerLimitSurvey: 'Olet käyttänyt yhteensä {0} / {1}.',
          cleanCaption: 'Puhdas',
          clearCaption: 'Asia selvä',
          chooseFileCaption: 'Valitse tiedosto',
          removeFileCaption: 'Poista tämä tiedosto',
          booleanCheckedLabel: 'Joo',
          booleanUncheckedLabel: 'Ei',
          confirmRemoveFile: 'Haluatko varmasti poistaa tämän tiedoston: {0}?',
          confirmRemoveAllFiles: 'Haluatko varmasti poistaa kaikki tiedostot?',
          questionTitlePatternText: 'Kysymyksen otsikko',
        };
      (r.a.locales.fi = i), (r.a.localeNames.fi = 'suomalainen');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Précédent',
          pageNextText: 'Suivant',
          completeText: 'Terminer',
          previewText: 'Aperçu',
          editText: 'Éditer',
          startSurveyText: 'Commencer',
          otherItemText: 'Autre (préciser)',
          noneItemText: 'Aucun',
          selectAllItemText: 'Tout sélectionner',
          progressText: 'Page {0} sur {1}',
          panelDynamicProgressText: 'Enregistrement {0} sur {1}',
          questionsProgressText: '{0}/{1} question(s) répondue(s)',
          emptySurvey: "Il n'y a ni page visible ni question visible dans ce questionnaire",
          completingSurvey: "Merci d'avoir répondu au questionnaire !",
          completingSurveyBefore:
            'Nos données indiquent que vous avez déjà rempli ce questionnaire.',
          loadingSurvey: 'Le questionnaire est en cours de chargement...',
          optionsCaption: 'Choisissez...',
          value: 'valeur',
          requiredError: 'La réponse à cette question est obligatoire.',
          requiredErrorInPanel: 'Merci de répondre au moins à une question.',
          requiredInAllRowsError: 'Toutes les lignes sont obligatoires',
          numericError: 'La réponse doit être un nombre.',
          textMinLength: 'Merci de saisir au moins {0} caractères.',
          textMaxLength: 'Merci de saisir moins de {0} caractères.',
          textMinMaxLength: 'Merci de saisir entre {0} et {1} caractères.',
          minRowCountError: 'Merci de compléter au moins {0} lignes.',
          minSelectError: 'Merci de sélectionner au minimum {0} réponses.',
          maxSelectError: 'Merci de sélectionner au maximum {0} réponses.',
          numericMinMax:
            "Votre réponse '{0}' doit être supérieure ou égale à {1} et inférieure ou égale à {2}",
          numericMin: "Votre réponse '{0}' doit être supérieure ou égale à {1}",
          numericMax: "Votre réponse '{0}' doit être inférieure ou égale à {1}",
          invalidEmail: "Merci d'entrer une adresse mail valide.",
          invalidExpression: "L'expression: {0} doit retourner 'true'.",
          urlRequestError: "La requête a renvoyé une erreur '{0}'. {1}",
          urlGetChoicesError:
            "La requête a renvoyé des données vides ou la propriété 'path' est incorrecte",
          exceedMaxSize: 'La taille du fichier ne doit pas excéder {0}.',
          otherRequiredError: "Merci de préciser le champ 'Autre'.",
          uploadingFile:
            "Votre fichier est en cours de chargement. Merci d'attendre quelques secondes et de réessayer.",
          loadingFile: 'Chargement...',
          chooseFile: 'Ajouter des fichiers...',
          noFileChosen: 'Aucun fichier ajouté',
          confirmDelete: 'Voulez-vous supprimer cet enregistrement ?',
          keyDuplicationError: 'Cette valeur doit être unique.',
          addColumn: 'Ajouter une colonne',
          addRow: 'Ajouter une ligne',
          removeRow: 'Supprimer',
          addPanel: 'Ajouter',
          removePanel: 'Supprimer',
          choices_Item: 'item',
          matrix_column: 'Colonne',
          matrix_row: 'Ligne',
          savingData: 'Les résultats sont en cours de sauvegarde sur le serveur...',
          savingDataError: 'Une erreur est survenue et a empêché la sauvegarde des résultats.',
          savingDataSuccess: 'Les résultats ont bien été enregistrés !',
          saveAgainButton: 'Réessayer',
          timerMin: 'min',
          timerSec: 'sec',
          timerSpentAll: 'Vous avez passé {0} sur cette page et {1} au total.',
          timerSpentPage: 'Vous avez passé {0} sur cette page.',
          timerSpentSurvey: 'Vous avez passé {0} au total.',
          timerLimitAll: 'Vous avez passé {0} sur {1} sur cette page et {2} sur {3} au total.',
          timerLimitPage: 'Vous avez passé {0} sur {1} sur cette page.',
          timerLimitSurvey: 'Vous avez passé {0} sur {1} au total.',
          cleanCaption: 'Nettoyer',
          clearCaption: 'Vider',
          chooseFileCaption: 'Ajouter un fichier',
          removeFileCaption: 'Enlever ce fichier',
          booleanCheckedLabel: 'Oui',
          booleanUncheckedLabel: 'Non',
          confirmRemoveFile: 'Êtes-vous certains de vouloir supprimer ce fichier : {0}?',
          confirmRemoveAllFiles: 'Êtes-vous certains de vouloir supprimer tous les fichiers?',
          questionTitlePatternText: 'Titre de la question',
        };
      (r.a.locales.fr = i), (r.a.localeNames.fr = 'français');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'უკან',
          pageNextText: 'შემდეგ',
          completeText: 'დასრულება',
          progressText: 'გვერდი {0} / {1}',
          emptySurvey: 'არცერთი კითხვა არ არის.',
          completingSurvey: 'გმადლობთ კითხვარის შევსებისთვის!',
          loadingSurvey: 'ჩატვირთვა სერვერიდან...',
          otherItemText: 'სხვა (გთხოვთ მიუთითეთ)',
          optionsCaption: 'არჩევა...',
          requiredError: 'გთხოვთ უპასუხეთ კითხვას.',
          numericError: 'პასუხი უნდა იყოს რიცხვი.',
          textMinLength: 'გთხოვთ შეიყვანეთ არანაკლებ {0} სიმბოლო.',
          minSelectError: 'გთხოვთ აირჩიეთ არანაკლებ {0} ვარიანტი.',
          maxSelectError: 'გთხოვთ აირჩიეთ არაუმეტეს {0} ვარიანტი.',
          numericMinMax: "'{0}' უნდა იყოს მეტი ან ტოლი, ვიდრე {1}, და ნაკლები ან ტოლი ვიდრე {2}",
          numericMin: "'{0}' უნდა იყოს მეტი ან ტოლი ვიდრე {1}",
          numericMax: "'{0}' უნდა იყოს ნაკლები ან ტოლი ვიდრე {1}",
          invalidEmail: 'გთხოვთ შეიყვანოთ ელ. ფოსტის რეალური მისამართი.',
          otherRequiredEror: "გთხოვთ შეავსეთ ველი 'სხვა'",
        };
      (r.a.locales.ka = i), (r.a.localeNames.ka = 'ქართული');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Zurück',
          pageNextText: 'Weiter',
          completeText: 'Abschließen',
          previewText: 'Vorschau',
          editText: 'Bearbeiten',
          startSurveyText: 'Start',
          otherItemText: 'Sonstiges (Bitte angeben)',
          noneItemText: 'Nichts trifft zu',
          selectAllItemText: 'Alles auswählen',
          progressText: 'Seite {0} von {1}',
          panelDynamicProgressText: 'Eintrag {0} von {1}',
          questionsProgressText: '{0}/{1} Fragen beantwortet',
          emptySurvey: 'Es sind keine Fragen vorhanden.',
          completingSurvey: 'Vielen Dank, dass Sie die Umfrage abgeschlossen haben!',
          completingSurveyBefore:
            'Wir haben festgestellt, dass Sie diese Umfrage bereits abgeschlossen haben.',
          loadingSurvey: 'Umfrage wird geladen...',
          optionsCaption: 'Bitte auswählen...',
          value: 'Wert',
          requiredError: 'Bitte beantworten Sie diese Frage.',
          requiredErrorInPanel: 'Bitte beantworten Sie mindestens eine Frage.',
          requiredInAllRowsError: 'Bitte beantworten Sie alle Fragen.',
          numericError: 'Der Wert muss eine Zahl sein.',
          textMinLength: 'Bitte geben Sie mindestens {0} Zeichen ein.',
          textMaxLength: 'Bitte geben Sie nicht mehr als {0} Zeichen ein.',
          textMinMaxLength: 'Bitte geben Sie mindestens {0} und maximal {1} Zeichen ein.',
          minRowCountError: 'Bitte machen Sie in mindestens {0} Zeilen eine Eingabe.',
          minSelectError: 'Bitte wählen Sie mindestens {0} Antwort(en) aus.',
          maxSelectError: 'Bitte wählen Sie nicht mehr als {0} Antwort(en) aus.',
          numericMinMax: "'{0}' muss größer oder gleich {1} und kleiner oder gleich {2} sein",
          numericMin: "'{0}' muss größer oder gleich {1} sein",
          numericMax: "'{0}' muss kleiner oder gleich {1} sein",
          invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
          invalidExpression: "Der Ausdruck: {0} muss den Wert 'wahr' zurückgeben.",
          urlRequestError: "Ein Netzwerkdienst hat folgenden Fehler zurückgegeben '{0}'. {1}",
          urlGetChoicesError: 'Eine Netzwerkdienst hat ungültige Daten zurückgegeben',
          exceedMaxSize: 'Die Datei darf nicht größer als {0} sein.',
          otherRequiredError: 'Bitte geben Sie einen Wert an.',
          uploadingFile: 'Bitte warten Sie bis der Upload Ihrer Dateien abgeschlossen ist.',
          loadingFile: 'Wird hochgeladen...',
          chooseFile: 'Datei(en) auswählen...',
          noFileChosen: 'Keine Datei ausgewählt',
          confirmDelete: 'Wollen Sie den Eintrag löschen?',
          keyDuplicationError: 'Dieser Wert muss einmalig sein.',
          addColumn: 'Spalte hinzufügen',
          addRow: 'Zeile hinzufügen',
          removeRow: 'Entfernen',
          addPanel: 'Neu hinzufügen',
          removePanel: 'Entfernen',
          choices_Item: 'Element',
          matrix_column: 'Spalte',
          matrix_row: 'Zeile',
          savingData: 'Die Ergebnisse werden auf dem Server gespeichert...',
          savingDataError:
            'Es ist ein Fehler aufgetreten. Die Ergebnisse konnten nicht gespeichert werden.',
          savingDataSuccess: 'Die Ergebnisse wurden gespeichert!',
          saveAgainButton: 'Erneut absenden',
          timerMin: 'Min.',
          timerSec: 'Sek.',
          timerSpentAll: 'Sie waren {0} auf dieser Seite und brauchten insgesamt {1}.',
          timerSpentPage: 'Sie waren {0} auf dieser Seite.',
          timerSpentSurvey: 'Sie haben insgesamt {0} gebraucht.',
          timerLimitAll:
            'Sie waren {0} von {1} auf dieser Seite und brauchten insgesamt {2} von {3}.',
          timerLimitPage: 'Sie waren {0} von {1} auf dieser Seite.',
          timerLimitSurvey: 'Sie haben insgesamt {0} von {1} gebraucht.',
          cleanCaption: 'Alles löschen',
          clearCaption: 'Auswahl entfernen',
          chooseFileCaption: 'Datei auswählen',
          removeFileCaption: 'Datei löschen',
          booleanCheckedLabel: 'Ja',
          booleanUncheckedLabel: 'Nein',
          confirmRemoveFile: 'Sind Sie sicher, dass Sie diese Datei löschen möchten: {0}?',
          confirmRemoveAllFiles: 'Sind Sie sicher, dass Sie alle Dateien löschen möchten?',
          questionTitlePatternText: 'Fragentitel',
        };
      (r.a.locales.de = i), (r.a.localeNames.de = 'deutsch');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Προηγούμενο',
          pageNextText: 'Επόμενο',
          completeText: 'Ολοκλήρωση',
          previewText: 'Προεπισκόπηση',
          editText: 'Επεξεργασία',
          startSurveyText: 'Αρχή',
          otherItemText: 'Άλλο (παρακαλώ διευκρινίστε)',
          noneItemText: 'Κανένας',
          selectAllItemText: 'Επιλογή όλων',
          progressText: 'Σελίδα {0} από {1}',
          panelDynamicProgressText: 'Εγγραφή {0} από {1}',
          questionsProgressText: 'Απαντήθηκαν {0} / {1} ερωτήσεις',
          emptySurvey: 'Δεν υπάρχει καμία ορατή σελίδα ή ορατή ερώτηση σε αυτό το ερωτηματολόγιο.',
          completingSurvey: 'Ευχαριστούμε για την συμπλήρωση αυτού του ερωτηματολογίου!',
          completingSurveyBefore:
            'Τα αρχεία μας δείχνουν ότι έχετε ήδη ολοκληρώσει αυτήν την έρευνα.',
          loadingSurvey: 'Το ερωτηματολόγιο φορτώνεται απο το διακομιστή...',
          optionsCaption: 'Επιλέξτε...',
          value: 'αξία',
          requiredError: 'Παρακαλώ απαντήστε στην ερώτηση.',
          requiredErrorInPanel: 'Απαντήστε σε τουλάχιστον μία ερώτηση.',
          requiredInAllRowsError: 'Παρακαλώ απαντήστε στις ερωτήσεις σε όλες τις γραμμές.',
          numericError: 'Η τιμή πρέπει να είναι αριθμητική.',
          textMinLength: 'Παρακαλώ συμπληρώστε τουλάχιστον {0} σύμβολα.',
          textMaxLength: 'Εισαγάγετε λιγότερους από {0} χαρακτήρες.',
          textMinMaxLength: 'Εισαγάγετε περισσότερους από {0} και λιγότερους από {1} χαρακτήρες.',
          minRowCountError: 'Παρακαλώ συμπληρώστε τουλάχιστον {0} γραμμές.',
          minSelectError: 'Παρακαλώ επιλέξτε τουλάχιστον {0} παραλλαγές.',
          maxSelectError: 'Παρακαλώ επιλέξτε όχι παραπάνω απο {0} παραλλαγές.',
          numericMinMax:
            "Το '{0}' θα πρέπει να είναι ίσο ή μεγαλύτερο απο το {1} και ίσο ή μικρότερο απο το {2}",
          numericMin: "Το '{0}' πρέπει να είναι μεγαλύτερο ή ισο με το {1}",
          numericMax: "Το '{0}' πρέπει να είναι μικρότερο ή ίσο απο το {1}",
          invalidEmail: 'Παρακαλώ δώστε μια αποδεκτή διεύθυνση e-mail.',
          invalidExpression: "Η έκφραση: {0} θα πρέπει να επιστρέψει 'true'.",
          urlRequestError: "Η αίτηση επέστρεψε σφάλμα '{0}'. {1}",
          urlGetChoicesError:
            "Η αίτηση επέστρεψε κενά δεδομένα ή η ιδιότητα 'μονοπάτι/path' είναι εσφαλμένη",
          exceedMaxSize: 'Το μέγεθος δεν μπορεί να υπερβαίνει τα {0}.',
          otherRequiredError: "Παρακαλώ συμπληρώστε την τιμή για το πεδίο 'άλλο'.",
          uploadingFile:
            'Το αρχείο σας ανεβαίνει. Παρακαλώ περιμένετε καποια δευτερόλεπτα και δοκιμάστε ξανά.',
          loadingFile: 'Φόρτωση...',
          chooseFile: 'Επιλογή αρχείων ...',
          noFileChosen: 'Δεν έχει επιλεγεί αρχείο',
          confirmDelete: 'Θέλετε να διαγράψετε την εγγραφή;',
          keyDuplicationError: 'Αυτή η τιμή πρέπει να είναι μοναδική.',
          addColumn: 'Προσθήκη στήλης',
          addRow: 'Προσθήκη γραμμής',
          removeRow: 'Αφαίρεση',
          addPanel: 'Προσθεσε νεο',
          removePanel: 'Αφαιρώ',
          choices_Item: 'είδος',
          matrix_column: 'Στήλη',
          matrix_row: 'Σειρά',
          savingData: 'Τα αποτελέσματα αποθηκεύονται στον διακομιστή ...',
          savingDataError:
            'Παρουσιάστηκε σφάλμα και δεν ήταν δυνατή η αποθήκευση των αποτελεσμάτων.',
          savingDataSuccess: 'Τα αποτελέσματα αποθηκεύτηκαν με επιτυχία!',
          saveAgainButton: 'Προσπάθησε ξανά',
          timerMin: 'ελάχ',
          timerSec: 'δευτ',
          timerSpentAll: 'Έχετε δαπανήσει {0} σε αυτήν τη σελίδα και {1} συνολικά.',
          timerSpentPage: 'Έχετε ξοδέψει {0} σε αυτήν τη σελίδα.',
          timerSpentSurvey: 'Έχετε ξοδέψει συνολικά {0}.',
          timerLimitAll: 'Έχετε δαπανήσει {0} από {1} σε αυτήν τη σελίδα και {2} από {3} συνολικά.',
          timerLimitPage: 'Έχετε ξοδέψει {0} από {1} σε αυτήν τη σελίδα.',
          timerLimitSurvey: 'Έχετε ξοδέψει {0} από {1} συνολικά.',
          cleanCaption: 'ΚΑΘΑΡΗ',
          clearCaption: 'Σαφή',
          chooseFileCaption: 'Επιλέξτε το αρχείο',
          removeFileCaption: 'Καταργήστε αυτό το αρχείο',
          booleanCheckedLabel: 'Ναί',
          booleanUncheckedLabel: 'Οχι',
          confirmRemoveFile: 'Είστε βέβαιοι ότι θέλετε να καταργήσετε αυτό το αρχείο: {0};',
          confirmRemoveAllFiles: 'Είστε βέβαιοι ότι θέλετε να καταργήσετε όλα τα αρχεία;',
          questionTitlePatternText: 'Τίτλος ερώτησης',
        };
      (r.a.locales.gr = i), (r.a.localeNames.gr = 'ελληνικά');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'אחורה',
          pageNextText: 'קדימה',
          completeText: 'סיום',
          previewText: 'תצוגה מקדימה',
          editText: 'לַעֲרוֹך',
          startSurveyText: 'הַתחָלָה',
          otherItemText: 'אחר (נא לתאר)',
          noneItemText: 'אף אחד',
          selectAllItemText: 'בחר הכל',
          progressText: 'דף {1} מתוך {0}',
          panelDynamicProgressText: 'הקלטה {0} מתוך {1}',
          questionsProgressText: 'ענה על שאלות',
          emptySurvey: 'אין שאלות',
          completingSurvey: 'תודה על מילוי השאלון!',
          completingSurveyBefore: 'הרשומות שלנו מראות שכבר סיימת את הסקר הזה.',
          loadingSurvey: 'טעינה מהשרת...',
          optionsCaption: 'בחר...',
          value: 'ערך',
          requiredError: 'אנא השב על השאלה',
          requiredErrorInPanel: 'אנא ענה לפחות על שאלה אחת.',
          requiredInAllRowsError: 'אנא ענה על שאלות בכל השורות.',
          numericError: 'התשובה צריכה להיות מספר.',
          textMinLength: 'הזן לפחות {0} תווים.',
          textMaxLength: 'הזן פחות מ- {0} תווים.',
          textMinMaxLength: 'הזן יותר מ- {0} ופחות מ- {1} תווים.',
          minRowCountError: 'אנא מלא לפחות {0} שורות.',
          minSelectError: 'בחר לפחות {0} אפשרויות.',
          maxSelectError: 'בחר עד {0} אפשרויות.',
          numericMinMax: "'{0}' חייב להיות שווה או גדול מ {1}, ושווה ל- {2} או פחות מ- {}}",
          numericMin: "'{0}' חייב להיות שווה או גדול מ {1}",
          numericMax: "'{0}' חייב להיות שווה או קטן מ {1}",
          invalidEmail: 'הזן כתובת דוא"ל חוקית.',
          invalidExpression: "הביטוי: {0} צריך להחזיר 'אמת'.",
          urlRequestError: "הבקשה החזירה את השגיאה '{0}'. {1}",
          urlGetChoicesError: "הבקשה החזירה נתונים ריקים או שהמאפיין 'נתיב' שגוי",
          exceedMaxSize: 'גודל הקובץ לא יעלה על {0}.',
          otherRequiredError: 'נא להזין נתונים בשדה "אחר"',
          uploadingFile: 'הקובץ שלך נטען. המתן מספר שניות ונסה שוב.',
          loadingFile: 'טוען...',
          chooseFile: 'לבחור קבצים...',
          noFileChosen: 'לא נבחר קובץ',
          confirmDelete: 'האם אתה רוצה למחוק את הרשומה?',
          keyDuplicationError: 'ערך זה צריך להיות ייחודי.',
          addColumn: 'הוסף עמודה',
          addRow: 'להוסיף שורה',
          removeRow: 'לְהַסִיר',
          addPanel: 'הוסף חדש',
          removePanel: 'לְהַסִיר',
          choices_Item: 'פריט',
          matrix_column: 'טור',
          matrix_row: 'שׁוּרָה',
          savingData: 'התוצאות נשמרות בשרת ...',
          savingDataError: 'אירעה שגיאה ולא הצלחנו לשמור את התוצאות.',
          savingDataSuccess: 'התוצאות נשמרו בהצלחה!',
          saveAgainButton: 'נסה שוב',
          timerMin: 'דקה',
          timerSec: 'שניות',
          timerSpentAll: 'הוצאת {0} בדף זה ובסך הכל {1}.',
          timerSpentPage: 'הוצאת {0} בדף זה.',
          timerSpentSurvey: 'הוצאת סכום כולל של {0}.',
          timerLimitAll: 'הוצאת {0} מתוך {1} בדף זה ו- {2} מתוך {3} בסך הכל.',
          timerLimitPage: 'הוצאת {0} מתוך {1} בדף זה.',
          timerLimitSurvey: 'הוצאת סכום כולל של {0} מתוך {1}.',
          cleanCaption: 'לְנַקוֹת',
          clearCaption: 'ברור',
          chooseFileCaption: 'בחר קובץ',
          removeFileCaption: 'הסר קובץ זה',
          booleanCheckedLabel: 'כן',
          booleanUncheckedLabel: 'לא',
          confirmRemoveFile: 'האם אתה בטוח שברצונך להסיר קובץ זה: {0}?',
          confirmRemoveAllFiles: 'האם אתה בטוח שברצונך להסיר את כל הקבצים?',
          questionTitlePatternText: 'כותרת שאלה',
        };
      (r.a.locales.he = i), (r.a.localeNames.he = 'עברית');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Vissza',
          pageNextText: 'Tovább',
          completeText: 'Kész',
          previewText: 'Előnézet',
          editText: 'Szerkesztés',
          startSurveyText: 'Rajt',
          otherItemText: 'Egyéb (adja meg)',
          noneItemText: 'Egyik sem',
          selectAllItemText: 'Mindet kiválaszt',
          progressText: '{0}./{1} oldal',
          panelDynamicProgressText: '{0} / {1} rekord',
          questionsProgressText: 'Válaszolt kérdések: {0} / {1}',
          emptySurvey: 'There is no visible page or question in the survey.',
          completingSurvey: 'Köszönjük, hogy kitöltötte felmérésünket!',
          completingSurveyBefore: 'Már kitöltötte a felmérést.',
          loadingSurvey: 'Felmérés betöltése...',
          optionsCaption: 'Válasszon...',
          value: 'érték',
          requiredError: 'Kérjük, válaszolja meg ezt a kérdést!',
          requiredErrorInPanel: 'Kérjük, válaszoljon legalább egy kérdésre.',
          requiredInAllRowsError: 'Kérjük adjon választ minden sorban!',
          numericError: 'Az érték szám kell, hogy legyen!',
          textMinLength: 'Adjon meg legalább {0} karaktert!',
          textMaxLength: 'Legfeljebb {0} karaktert adjon meg!',
          textMinMaxLength: 'Adjon meg legalább {0}, de legfeljebb {1} karaktert!',
          minRowCountError: 'Töltsön ki minimum {0} sort!',
          minSelectError: 'Válasszon ki legalább {0} lehetőséget!',
          maxSelectError: 'Ne válasszon többet, mint {0} lehetőség!',
          numericMinMax:
            "'{0}' legyen nagyobb, vagy egyenlő, mint {1} és kisebb, vagy egyenlő, mint {2}!",
          numericMin: "'{0}' legyen legalább {1}!",
          numericMax: "The '{0}' ne legyen nagyobb, mint {1}!",
          invalidEmail: 'Adjon meg egy valós email címet!',
          invalidExpression: 'A következő kifejezés: {0} vissza kell adnia az „igaz” értéket.',
          urlRequestError: "A lekérdezés hibával tért vissza: '{0}'. {1}",
          urlGetChoicesError:
            "A lekérdezés üres adattal tért vissza, vagy a 'path' paraméter helytelen.",
          exceedMaxSize: 'A méret nem lehet nagyobb, mint {0}.',
          otherRequiredError: 'Adja meg az egyéb értéket!',
          uploadingFile: 'Feltöltés folyamatban. Várjon pár másodpercet, majd próbálja újra.',
          loadingFile: 'Betöltés...',
          chooseFile: 'Fájlok kiválasztása ...',
          noFileChosen: 'Nincs kiválasztva fájl',
          confirmDelete: 'Törli ezt a rekordot?',
          keyDuplicationError: 'Az értéknek egyedinek kell lennie.',
          addColumn: 'Oszlop hozzáadása',
          addRow: 'Sor hozzáadása',
          removeRow: 'Eltávolítás',
          addPanel: 'Új hozzáadása',
          removePanel: 'Eltávolítás',
          choices_Item: 'elem',
          matrix_column: 'Oszlop',
          matrix_row: 'Sor',
          savingData: 'Eredmény mentése a szerverre...',
          savingDataError: 'Egy hiba folytán nem tudtuk elmenteni az eredményt.',
          savingDataSuccess: 'Eredmény sikeresen mentve!',
          saveAgainButton: 'Próbálja újra',
          timerMin: 'min',
          timerSec: 'sec',
          timerSpentAll: 'Ön {0} összeget költött ezen az oldalon, és összesen {1}.',
          timerSpentPage: '{0} összeget költött ezen az oldalon.',
          timerSpentSurvey: 'Összesen {0} költött.',
          timerLimitAll: 'Ön {0} / {1} összeget költött ezen az oldalon, és összesen {2} / {3}.',
          timerLimitPage: 'Ön {0} / {1} összeget költött ezen az oldalon.',
          timerLimitSurvey: 'Összesen {0} / {1} összeget költött el.',
          cleanCaption: 'Tiszta',
          clearCaption: 'Egyértelmű',
          chooseFileCaption: 'Válassz fájlt',
          removeFileCaption: 'Távolítsa el ezt a fájlt',
          booleanCheckedLabel: 'Igen',
          booleanUncheckedLabel: 'Nem',
          confirmRemoveFile: 'Biztosan eltávolítja ezt a fájlt: {0}?',
          confirmRemoveAllFiles: 'Biztosan el akarja távolítani az összes fájlt?',
          questionTitlePatternText: 'Kérdés címe',
        };
      (r.a.locales.hu = i), (r.a.localeNames.hu = 'magyar');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Tilbaka',
          pageNextText: 'Áfram',
          completeText: 'Lokið',
          previewText: 'Forskoða',
          editText: 'Breyta',
          startSurveyText: 'Byrjaðu',
          otherItemText: 'Hinn (skýring)',
          noneItemText: 'Enginn',
          selectAllItemText: 'Velja allt',
          progressText: 'Síða {0} of {1}',
          panelDynamicProgressText: 'Taka upp {0} af {1}',
          questionsProgressText: 'Svarað {0} / {1} spurningum',
          emptySurvey: 'Það er enginn síða eða spurningar í þessari könnun.',
          completingSurvey: 'Takk fyrir að fyllja út þessa könnun!',
          completingSurveyBefore: 'Skrár okkar sýna að þú hefur þegar lokið þessari könnun.',
          loadingSurvey: 'Könnunin er að hlaða...',
          optionsCaption: 'Veldu...',
          value: 'gildi',
          requiredError: 'Vinsamlegast svarið spurningunni.',
          requiredErrorInPanel: 'Vinsamlegast svaraðu að minnsta kosti einni spurningu.',
          requiredInAllRowsError: 'Vinsamlegast svarið spurningum í öllum röðum.',
          numericError: 'Þetta gildi verður að vera tala.',
          textMinLength: 'Það ætti að vera minnst {0} tákn.',
          textMaxLength: 'Það ætti að vera mest {0} tákn.',
          textMinMaxLength: 'Það ætti að vera fleiri en {0} og færri en {1} tákn.',
          minRowCountError: 'Vinsamlegast fyllið úr að minnsta kosti {0} raðir.',
          minSelectError: 'Vinsamlegast veljið að minnsta kosti {0} möguleika.',
          maxSelectError: 'Vinsamlegast veljið ekki fleiri en {0} möguleika.',
          numericMinMax: "'{0}' ætti að vera meira en eða jafnt og {1} minna en eða jafnt og {2}",
          numericMin: "{0}' ætti að vera meira en eða jafnt og {1}",
          numericMax: "'{0}' ætti að vera minna en eða jafnt og {1}",
          invalidEmail: 'Vinsamlegast sláið inn gilt netfang.',
          invalidExpression: "Tjáningin: {0} ætti að skila 'satt'.",
          urlRequestError: "Beiðninn skilaði eftirfaranadi villu '{0}'. {1}",
          urlGetChoicesError: 'Beiðninng skilaði engum gögnum eða slóðinn var röng',
          exceedMaxSize: 'Skráinn skal ekki vera stærri en {0}.',
          otherRequiredError: 'Vinamlegast fyllið út hitt gildið.',
          uploadingFile:
            'Skráinn þín var send. Vinsamlegast bíðið í nokkrar sekúndur og reynið aftur.',
          loadingFile: 'Hleður ...',
          chooseFile: 'Veldu skrár ...',
          noFileChosen: 'Engin skrá valin',
          confirmDelete: 'Viltu eyða skránni?',
          keyDuplicationError: 'Þetta gildi ætti að vera einstakt.',
          addColumn: 'Bæta við dálki',
          addRow: 'Bæta við röð',
          removeRow: 'Fjarlægja',
          addPanel: 'Bæta við nýju',
          removePanel: 'Fjarlægðu',
          choices_Item: 'hlutur',
          matrix_column: 'Dálkur',
          matrix_row: 'Röð',
          savingData: 'Niðurstöðurnar eru að spara á netþjóninum ... ',
          savingDataError: 'Villa kom upp og við gátum ekki vistað niðurstöðurnar.',
          savingDataSuccess: 'Árangurinn var vistaður með góðum árangri!',
          saveAgainButton: 'Reyndu aftur',
          timerMin: 'mín',
          timerSec: 'sek',
          timerSpentAll: 'Þú hefur eytt {0} á þessari síðu og {1} samtals.',
          timerSpentPage: 'Þú hefur eytt {0} á þessari síðu.',
          timerSpentSurvey: 'Þú hefur eytt {0} samtals.',
          timerLimitAll: 'Þú hefur eytt {0} af {1} á þessari síðu og {2} af {3} samtals.',
          timerLimitPage: 'Þú hefur eytt {0} af {1} á þessari síðu.',
          timerLimitSurvey: 'Þú hefur eytt {0} af {1} samtals.',
          cleanCaption: 'Hreint',
          clearCaption: 'Hreinsa',
          chooseFileCaption: 'Veldu skrá',
          removeFileCaption: 'Fjarlægðu þessa skrá',
          booleanCheckedLabel: 'Já',
          booleanUncheckedLabel: 'Nei',
          confirmRemoveFile: 'Ertu viss um að þú viljir fjarlægja þessa skrá: {0}?',
          confirmRemoveAllFiles: 'Ertu viss um að þú viljir fjarlægja allar skrár?',
          questionTitlePatternText: 'Spurningartitill',
        };
      (r.a.locales.is = i), (r.a.localeNames.is = 'íslenska');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Sebelumnya',
          pageNextText: 'Selanjutnya',
          completeText: 'Selesai',
          previewText: 'Pratinjau',
          editText: 'Sunting',
          startSurveyText: 'Mulai',
          otherItemText: 'Lainnya (jelaskan)',
          noneItemText: 'Tidak Ada',
          selectAllItemText: 'Pilih Semua',
          progressText: 'Halaman {0} dari {1}',
          panelDynamicProgressText: 'Rekam {0} dari {1}',
          questionsProgressText: 'Menjawab pertanyaan {0} / {1}',
          emptySurvey: 'Tidak ada halaman atau pertanyaan dalam survei.',
          completingSurvey: 'Terima kasih telah menyelesaikan survei!',
          completingSurveyBefore:
            'Catatan kami menunjukkan bahwa Anda telah menyelesaikan survei ini.',
          loadingSurvey: 'Memuat survei...',
          optionsCaption: 'Pilih...',
          value: 'nilai',
          requiredError: 'Silahkan jawab pertanyaan berikut.',
          requiredErrorInPanel: 'Silahkan jawab setidaknya satu petanyaan.',
          requiredInAllRowsError: 'Silahkan jawab pertanyaan pada semua baris.',
          numericError: 'Nilai harus berupa angka.',
          textMinLength: 'Silahkan masukkan setidaknya {0} karakter.',
          textMaxLength: 'Silahkan masukkan kurang {0} karakter.',
          textMinMaxLength: 'PSilahkan masukkan lebih dari {0} dan kurang dari {1} karakter.',
          minRowCountError: 'Silahkan isi setidaknya {0} baris.',
          minSelectError: 'Silahkan pilih setidaknya {0} varian.',
          maxSelectError: 'Silahkan pilih tidak lebih dari {0} varian.',
          numericMinMax:
            "'{0}' harus sama dengan atau lebih dari {1} dan harus sama dengan atau kurang dari {2}",
          numericMin: "'{0}' harus sama dengan atau lebih dari {1}",
          numericMax: "'{0}' harus sama dengan atau kurang dari {1}",
          invalidEmail: 'Silahkan masukkan e-mail yang benar.',
          invalidExpression: "Ekspresi: {0} harus mengembalikan 'benar'.",
          urlRequestError: "Permintaan mengembalikan kesalahan '{0}'. {1}",
          urlGetChoicesError: "Permintaan mengembalikan data kosong atau properti 'path' salah.",
          exceedMaxSize: 'Ukuran berkas tidak boleh melebihi {0}.',
          otherRequiredError: 'Silahkan masukkan nilai lainnnya.',
          uploadingFile:
            'Berkas Anda sedang diunggah. Silahkan tunggu beberapa saat atau coba lagi.',
          loadingFile: 'Memuat...',
          chooseFile: 'Pilih berkas...',
          noFileChosen: 'Tidak ada file yang dipilih',
          confirmDelete: 'Apakah Anda ingin menghapus catatan?',
          keyDuplicationError: 'Nilai harus unik.',
          addColumn: 'Tambah kolom',
          addRow: 'Tambah baris',
          removeRow: 'Hapus',
          addPanel: 'Tambah baru',
          removePanel: 'Hapus',
          choices_Item: 'item',
          matrix_column: 'Kolom',
          matrix_row: 'Baris',
          savingData: 'Hasil sedang disimpan pada server...',
          savingDataError: 'Kesalahan terjadi dan kami tidak dapat menyimpan hasil.',
          savingDataSuccess: 'Hasil telah sukses disimpan!',
          saveAgainButton: 'Coba lagi',
          timerMin: 'menit',
          timerSec: 'detik',
          timerSpentAll: 'Anda telah menghabiskan {0} pada halaman ini dan {1} secara keseluruhan.',
          timerSpentPage: 'YAnda telah menghabiskan {0} pada halaman ini.',
          timerSpentSurvey: 'Anda telah menghabiskan {0} secara keseluruhan.',
          timerLimitAll:
            'Anda telah menghabiskan {0} dari {1} pada halaman ini dan {2} dari {3} secara keseluruhan.',
          timerLimitPage: 'Anda telah menghabiskan {0} dari {1} pada halaman ini.',
          timerLimitSurvey: 'Anda telah menghabiskan {0} dari {1} secara keseluruhan.',
          cleanCaption: 'Bersihkan',
          clearCaption: 'Bersihkan',
          chooseFileCaption: 'Pilih File',
          removeFileCaption: 'Hapus berkas ini',
          booleanCheckedLabel: 'Iya',
          booleanUncheckedLabel: 'Tidak',
          confirmRemoveFile: 'Anda yakin ingin menghapus file ini: {0}?',
          confirmRemoveAllFiles: 'Anda yakin ingin menghapus semua file?',
          questionTitlePatternText: 'Judul pertanyaan',
        };
      (r.a.locales.id = i), (r.a.localeNames.id = 'Bahasa Indonesia');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Precedente',
          pageNextText: 'Successivo',
          completeText: 'Salva',
          previewText: 'Anteprima',
          editText: 'Modifica',
          startSurveyText: 'Inizio',
          otherItemText: 'Altro (descrivi)',
          noneItemText: 'Nessuno',
          selectAllItemText: 'Seleziona tutti',
          progressText: 'Pagina {0} di {1}',
          panelDynamicProgressText: 'Record di {0} di {1}',
          questionsProgressText: 'Risposte a {0}/{1} domande',
          emptySurvey: 'Non ci sono pagine o domande visibili nel questionario.',
          completingSurvey: 'Grazie per aver completato il questionario!',
          completingSurveyBefore:
            'I nostri records mostrano che hai già completato questo questionario.',
          loadingSurvey: 'Caricamento del questionario in corso...',
          optionsCaption: 'Scegli...',
          value: 'valore',
          requiredError: 'Campo obbligatorio',
          requiredErrorInPanel: 'Per Favore, rispondi ad almeno una domanda.',
          requiredInAllRowsError: 'Completare tutte le righe',
          numericError: 'Il valore deve essere numerico',
          textMinLength: 'Inserire almeno {0} caratteri',
          textMaxLength: 'Lunghezza massima consentita {0} caratteri',
          textMinMaxLength: 'Inserire una stringa con minimo {0} e massimo {1} caratteri',
          minRowCountError: 'Completare almeno {0} righe.',
          minSelectError: 'Selezionare almeno {0} varianti.',
          maxSelectError: 'Selezionare massimo {0} varianti.',
          numericMinMax: "'{0}' deve essere uguale o superiore a {1} e uguale o inferiore a {2}",
          numericMin: "'{0}' deve essere uguale o superiore a {1}",
          numericMax: "'{0}' deve essere uguale o inferiore a {1}",
          invalidEmail: 'Inserire indirizzo mail valido',
          invalidExpression: "L'espressione: {0} dovrebbe tornare 'vero'.",
          urlRequestError: "La richiesta ha risposto con un errore '{0}'. {1}",
          urlGetChoicesError: 'La richiesta ha risposto null oppure il percorso non è corretto',
          exceedMaxSize: 'Il file non può eccedere {0}',
          otherRequiredError: "Inserire il valore 'altro'",
          uploadingFile: 'File in caricamento. Attendi alcuni secondi e riprova',
          loadingFile: 'Caricamento...',
          chooseFile: 'Selezionare file(s)...',
          noFileChosen: 'Nessun file selezionato',
          confirmDelete: 'Sei sicuro di voler elminare il record?',
          keyDuplicationError: 'Questo valore deve essere univoco.',
          addColumn: 'Aggiungi colonna',
          addRow: 'Aggiungi riga',
          removeRow: 'Rimuovi riga',
          addPanel: 'Aggiungi riga',
          removePanel: 'Elimina',
          choices_Item: 'Elemento',
          matrix_column: 'Colonna',
          matrix_row: 'Riga',
          savingData: 'Salvataggio dati sul server...',
          savingDataError: 'Si è verificato un errore e non è stato possibile salvare i risultati.',
          savingDataSuccess: 'I risultati sono stati salvati con successo!',
          saveAgainButton: 'Riprova',
          timerMin: 'min',
          timerSec: 'sec',
          timerSpentAll: 'Hai impiegato {0} su questa pagina e {1} in totale.',
          timerSpentPage: 'Hai impiegato {0} su questa pagina.',
          timerSpentSurvey: 'Hai impiegato {0} in totale.',
          timerLimitAll: 'Hai impiegato {0} di {1} su questa pagina e {2} di {3} in totale.',
          timerLimitPage: 'Hai impiegato {0} di {1} su questa pagina.',
          timerLimitSurvey: 'Hai impiegato {0} di {1} in totale.',
          cleanCaption: 'Pulisci',
          clearCaption: 'Cancella',
          chooseFileCaption: 'Scegliere il file',
          removeFileCaption: 'Rimuovere questo file',
          booleanCheckedLabel: 'Sì',
          booleanUncheckedLabel: 'No',
          confirmRemoveFile: 'Sei sicuro di voler elminare questo file: {0}?',
          confirmRemoveAllFiles: 'Sei sicuro di voler elminare tutti i files?',
          questionTitlePatternText: 'Titolo della domanda',
        };
      (r.a.locales.it = i), (r.a.localeNames.it = 'italiano');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: '前へ',
          pageNextText: '次へ',
          completeText: '完了',
          previewText: 'プレビュー',
          editText: '編集',
          startSurveyText: 'スタート',
          otherItemText: 'その他（説明）',
          noneItemText: 'なし',
          selectAllItemText: 'すべて選択',
          progressText: '{0}/{1}頁',
          panelDynamicProgressText: '{1}の{0}を記録する',
          questionsProgressText: '{0}/{1}の質問に回答しました。',
          emptySurvey: 'この調査に表示できるページや質問はありません',
          completingSurvey: '調査を完了してくれてありがとうございました',
          completingSurveyBefore: '当社の記録によると、この調査はすでに完了しています。',
          loadingSurvey: '調査をダウンロード中',
          optionsCaption: '選択',
          value: '値打ち',
          requiredError: '質問にお答え下さい',
          requiredErrorInPanel: '最低でも1つの質問に答えてください。',
          requiredInAllRowsError: '質問には全列で回答してください。',
          numericError: '数字でご記入下さい',
          textMinLength: '{0} 文字以上で入力して下さい',
          textMaxLength: '{0}文字以下で入力してください。',
          textMinMaxLength: '{0}以上{1}未満の文字を入力してください。',
          minRowCountError: '{0}行以上で入力して下さい',
          minSelectError: '{0}種類以上を選択して下さい',
          maxSelectError: '{0}以上のバリアントを選択しないでください。',
          numericMinMax: '{0}は{1}以上であり、{2}以下であることが望ましい。',
          numericMin: "'{0}' は同等か{1}より大きくなければなりません",
          numericMax: "'{0}' は同等か{1}より小さくなければなりません",
          invalidEmail: '有効なメールアドレスをご記入下さい',
          invalidExpression: "式は {0}は'true'を返すべきです。",
          urlRequestError: "リクエストはエラー '{0}' を返しました。{1}",
          urlGetChoicesError:
            "リクエストが空のデータを返したか、'path' プロパティが正しくありません。",
          exceedMaxSize: 'ファイルのサイズは{0}を超えてはいけません',
          otherRequiredError: 'その他の値を入力してください。',
          uploadingFile: 'ファイルをアップロード中です。しばらくしてから再度お試し下さい',
          loadingFile: '読み込み中',
          chooseFile: 'ファイルを選択',
          noFileChosen: '選択されたファイルはありません',
          confirmDelete: 'レコードを削除しますか？',
          keyDuplicationError: 'この値は一意でなければなりません。',
          addColumn: '列の追加',
          addRow: '追加行',
          removeRow: '除去',
          addPanel: '新規追加',
          removePanel: '除去',
          choices_Item: '品目',
          matrix_column: 'コラム',
          matrix_row: '行',
          savingData: '結果はサーバーに保存されています...。',
          savingDataError: 'エラーが発生し、結果を保存できませんでした。',
          savingDataSuccess: '結果は無事に保存されました',
          saveAgainButton: 'もう一度試してみてください。',
          timerMin: '僅少',
          timerSec: 'セック',
          timerSpentAll: 'あなたはこのページに{0}を費やし、合計で{1}を費やしました。',
          timerSpentPage: 'あなたはこのページに{0}を費やしました。',
          timerSpentSurvey: '合計で{0}を使ったことになります。',
          timerLimitAll: 'このページに{1}のうち{0}を費やし、{3}のうち{2}を合計で費やしました。',
          timerLimitPage: 'このページで{1}の{0}を使ったことがあります。',
          timerLimitSurvey: '合計で{1}の{0}を使ったことがあります。',
          cleanCaption: '削除',
          clearCaption: '空白',
          chooseFileCaption: 'ファイルを選択',
          removeFileCaption: 'このファイルを削除',
          booleanCheckedLabel: '噫',
          booleanUncheckedLabel: '否',
          confirmRemoveFile: 'このファイルを削除してもよろしいですか？{0}?',
          confirmRemoveAllFiles: 'すべてのファイルを削除してもよろしいですか？',
          questionTitlePatternText: '質問名',
        };
      (r.a.locales.ja = i), (r.a.localeNames.ja = '日本語');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: '이전',
          pageNextText: '다음',
          completeText: '완료',
          previewText: '시사',
          editText: '편집하다',
          startSurveyText: '시작',
          otherItemText: '기타(설명)',
          noneItemText: '없음',
          selectAllItemText: '모두 선택',
          progressText: '페이지 {1} 중 {0}',
          panelDynamicProgressText: '{0} / {1} 기록',
          questionsProgressText: '{0} / {1} 개의 질문에 답변 함',
          emptySurvey: '설문지에 보여지는 페이지나 질문이 없습니다',
          completingSurvey: '설문 조사를 완료해 주셔서 감사합니다!',
          completingSurveyBefore: '기록에 따르면 이미 설문 조사를 마치셨습니다.',
          loadingSurvey: '설문조사가 로드중입니다...',
          optionsCaption: '선택하십시오...',
          value: '값',
          requiredError: '질문에 답하시오.',
          requiredErrorInPanel: '하나 이상의 질문에 답하십시오.',
          requiredInAllRowsError: '모든 행에 있는 질문에 답하십시오.',
          numericError: '값은 숫자여야 합니다.',
          textMinLength: '답변의 길이는 최소 {0}자여야 입니다.',
          textMaxLength: '답변의 길이는 {0}자를 초과 할 수 없습니다.',
          textMinMaxLength: '답변의 길이는 {0} - {1}자 사이여야 합니다.',
          minRowCountError: '최소 {0}개의 행을 채우십시오',
          minSelectError: '최소 {0}개의 변수를 선택하십시오.',
          maxSelectError: '최대 {0}개의 변수를 선택하십시오.',
          numericMinMax: "'{0}'은 {1}보다 크거나 같고 {2}보다 작거나 같아야합니다.",
          numericMin: "'{0}'은 {1}보다 크거나 같아야합니다.",
          numericMax: "'{0}'은 {1}보다 작거나 같아야합니다.",
          invalidEmail: '올바른 이메일 주소를 입력하십시오.',
          invalidExpression: "표현식: {0}은 '참'이어야 합니다.",
          urlRequestError: "'{0}'으로 잘못된 요청입니다. {1}",
          urlGetChoicesError: '비어있는 데이터를 요청했거나 잘못된 속성의 경로입니다.',
          exceedMaxSize: '파일 크기가 {0}을 초과 할 수 없습니다.',
          otherRequiredError: '다른 질문을 작성하십시오.',
          uploadingFile: '파일 업로드 중입니다. 잠시 후 다시 시도하십시오.',
          loadingFile: '로드 중...',
          chooseFile: '파일 선택...',
          noFileChosen: '선택된 파일이 없습니다',
          confirmDelete: '기록을 삭제하시겠습니까?',
          keyDuplicationError: ' 이 값은 고유해야합니다.',
          addColumn: '열 추가',
          addRow: '행 추가',
          removeRow: '제거',
          addPanel: '새롭게 추가',
          removePanel: '제거',
          choices_Item: '항목',
          matrix_column: '열',
          matrix_row: '행',
          savingData: '결과가 서버에 저장 중입니다...',
          savingDataError: '오류가 발생하여 결과를 저장할 수 없습니다.',
          savingDataSuccess: '결과가 성공적으로 저장되었습니다!',
          saveAgainButton: '다시 시도하십시오',
          timerMin: '분',
          timerSec: '초',
          timerSpentAll: '현재 페이지에서 {0}을 소요해 총 {1}이 걸렸습니다.',
          timerSpentPage: '현재 페이지에서 {0]이 걸렸습니다',
          timerSpentSurvey: '총 {0}이 걸렸습니다.',
          timerLimitAll: '현재 페이지에서 {0}/{1}을 소요해 총 {2}/{3}이 걸렸습니다.',
          timerLimitPage: '현재 페이지에서 {0}/{1}이 걸렸습니다.',
          timerLimitSurvey: '총 {0}/{1}이 걸렸습니다.',
          cleanCaption: '닦기',
          clearCaption: '지우기',
          chooseFileCaption: '파일을 선택',
          removeFileCaption: '이 파일 제거',
          booleanCheckedLabel: '예',
          booleanUncheckedLabel: '아니',
          confirmRemoveFile: '{0} 파일을 제거 하시겠습니까?',
          confirmRemoveAllFiles: '모든 파일을 제거 하시겠습니까?',
          questionTitlePatternText: '질문 제목',
        };
      (r.a.locales.ko = i), (r.a.localeNames.ko = '한국어');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Atpakaļ',
          pageNextText: 'Tālāk',
          completeText: 'Pabeigt',
          previewText: 'Priekšskatījums',
          editText: 'Rediģēt',
          startSurveyText: 'Sākt',
          otherItemText: 'Cits (lūdzu, aprakstiet!)',
          noneItemText: 'Nav',
          selectAllItemText: 'Izvēlēties visus',
          progressText: 'Lappuse {0} no {1}',
          panelDynamicProgressText: 'Ierakstīt {0} no {1}',
          questionsProgressText: 'Atbildēja uz jautājumiem {0} / {1}',
          emptySurvey: 'Nav neviena jautājuma.',
          completingSurvey: 'Pateicamies Jums par anketas aizpildīšanu!',
          completingSurveyBefore: 'Mūsu ieraksti liecina, ka jūs jau esat aizpildījis šo aptauju.',
          loadingSurvey: 'Ielāde no servera...',
          optionsCaption: 'Izvēlēties...',
          value: 'value',
          requiredError: 'Lūdzu, atbildiet uz jautājumu!',
          requiredErrorInPanel: 'Lūdzu, atbildiet uz vismaz vienu jautājumu.',
          requiredInAllRowsError: 'Lūdzu, atbildiet uz jautājumiem visās rindās.',
          numericError: 'Atbildei ir jābūt skaitlim.',
          textMinLength: 'Lūdzu, ievadiet vismaz {0} simbolus.',
          textMaxLength: 'Lūdzu, ievadiet mazāk nekā {0} rakstzīmes.',
          textMinMaxLength:
            'Lūdzu, ievadiet vairāk nekā {0} rakstzīmes un mazāk nekā {1} rakstzīmes.',
          minRowCountError: 'Lūdzu, aizpildiet vismaz {0} rindas.',
          minSelectError: 'Lūdzu, izvēlieties vismaz {0} variantu.',
          maxSelectError: 'Lūdzu, izvēlieties ne vairak par {0} variantiem.',
          numericMinMax:
            "'{0}' jābūt vienādam vai lielākam nekā {1}, un vienādam vai mazākam, nekā {2}",
          numericMin: "'{0}' jābūt vienādam vai lielākam {1}",
          numericMax: "'{0}' jābūt vienādam vai lielākam {1}",
          invalidEmail: 'Lūdzu, ievadiet patiesu e-pasta adresi!',
          invalidExpression: 'Izteicienam: {0} jāatgriež “true”.',
          urlRequestError: 'Pieprasījumā tika atgriezta kļūda “{0}”. {1}',
          urlGetChoicesError:
            'Pieprasījums atgrieza tukšus datus vai rekvizīts “path” ir nepareizs',
          exceedMaxSize: 'Faila lielums nedrīkst pārsniegt {0}.',
          otherRequiredError: "Lūdzu, ievadiet datus laukā 'Cits'",
          uploadingFile:
            'Jūsu fails tiek augšupielādēts. Lūdzu, uzgaidiet vairākas sekundes un mēģiniet vēlreiz.',
          loadingFile: 'Notiek ielāde ...',
          chooseFile: 'Izvēlieties failus ...',
          noFileChosen: 'Nav izvēlēts neviens fails',
          confirmDelete: 'Vai vēlaties izdzēst ierakstu?',
          keyDuplicationError: 'Šai vērtībai jābūt unikālai.',
          addColumn: 'Pievienot kolonnu',
          addRow: 'Pievienot rindu',
          removeRow: 'Noņemt',
          addPanel: 'Pievieno jaunu',
          removePanel: 'Noņemt',
          choices_Item: 'lieta',
          matrix_column: 'Sleja',
          matrix_row: 'Rinda',
          savingData: 'Rezultāti tiek saglabāti serverī ...',
          savingDataError: 'Radās kļūda, un mēs nevarējām saglabāt rezultātus.',
          savingDataSuccess: 'Rezultāti tika veiksmīgi saglabāti!',
          saveAgainButton: 'Mēģini vēlreiz',
          timerMin: 'min',
          timerSec: 'sek',
          timerSpentAll: 'Šajā lapā esat iztērējis {0} un kopā {1}.',
          timerSpentPage: 'Šajā lapā esat iztērējis {0}.',
          timerSpentSurvey: 'Kopā esat iztērējis {0}.',
          timerLimitAll: 'Šajā lapā esat iztērējis {0} no {1} un kopā {2} no {3}.',
          timerLimitPage: 'Šajā lapā esat iztērējis {0} no {1}.',
          timerLimitSurvey: 'Kopā esat iztērējis {0} no {1}.',
          cleanCaption: 'Tīrs',
          clearCaption: 'Skaidrs',
          chooseFileCaption: 'Izvēlēties failu',
          removeFileCaption: 'Noņemiet šo failu',
          booleanCheckedLabel: 'Jā',
          booleanUncheckedLabel: 'Nē',
          confirmRemoveFile: 'Vai tiešām vēlaties noņemt šo failu: {0}?',
          confirmRemoveAllFiles: 'Vai tiešām vēlaties noņemt visus failus?',
          questionTitlePatternText: 'Jautājuma nosaukums',
        };
      (r.a.locales.lv = i), (r.a.localeNames.lv = 'latviešu');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Atgal',
          pageNextText: 'Toliau',
          completeText: 'Baigti',
          previewText: 'Peržiūra',
          editText: 'Redaguoti',
          startSurveyText: 'Pradėti',
          otherItemText: 'Kita (įvesti)',
          noneItemText: 'Nėra',
          selectAllItemText: 'Pasirinkti visus',
          progressText: 'Puslapis {0} iš {1}',
          panelDynamicProgressText: 'Įrašyti {0} iš {1}',
          questionsProgressText: 'Atsakė į {0} / {1} klausimus',
          emptySurvey: 'Apklausoje nėra matomo puslapio ar klausimo.',
          completingSurvey: 'Dėkojame už dalyvavimą apklausoje!',
          completingSurveyBefore: 'Mūsų įrašai rodo, kad jau atlikote šią apklausą.',
          loadingSurvey: 'Prašome palaukti...',
          optionsCaption: 'Pasirinkti...',
          value: 'reikšmė',
          requiredError: 'Būtina atsakyti į šį klausimą.',
          requiredErrorInPanel: 'Būtina atsakyti bent į vieną klausimą.',
          requiredInAllRowsError: 'Prašome atsakyti į klausimus visose eilutėse.',
          numericError: 'Turi būti skaičiai.',
          textMinLength: 'Prašome suvesti bent {0} simbolius.',
          textMaxLength: 'Prašome suvesti mažiau nei {0} simbolių.',
          textMinMaxLength: 'Prašome suvesti daugiau nei {0} ir mažiau nei {1} simbolių.',
          minRowCountError: 'Prašome suvesti ne mažiau nei {0} eilučių.',
          minSelectError: 'Prašome pasirinkti bent {0} variantų.',
          maxSelectError: 'Pasirinkite ne daugiau kaip {0} variantus.',
          numericMinMax:
            "'{0}' turi būti lygus arba didesnis nei {1} ir lygus arba mažesnis nei {2}",
          numericMin: "'{0}' turėtų būti lygus arba didesnis nei {1}",
          numericMax: "'{0}' turėtų būti lygus ar mažesnis už {1}",
          invalidEmail: 'Prašome įvesti galiojantį elektroninio pašto adresą.',
          invalidExpression: "Reikšmė: {0} turi grąžinti 'true'.",
          urlRequestError: "Užklausa grąžino klaidą'{0}'. {1}",
          urlGetChoicesError: "Užklausa grąžino tuščius duomenis arba 'path' savybė yra neteisinga",
          exceedMaxSize: 'Failo dydis neturi viršyti {0}.',
          otherRequiredError: 'Įveskite kitą reikšmę.',
          uploadingFile:
            'Jūsų failas yra keliamas. Palaukite keletą sekundžių ir bandykite dar kartą.',
          loadingFile: 'Prašome palaukti...',
          chooseFile: 'Pasirinkti failą(us)...',
          noFileChosen: 'Nepasirinktas joks failas',
          confirmDelete: 'Ar norite ištrinti įrašą?',
          keyDuplicationError: 'Ši reikšmė turėtų būti unikali.',
          addColumn: 'Pridėti stulpelį',
          addRow: 'Pridėti eilutę',
          removeRow: 'Ištrinti',
          addPanel: 'Pridėti naują',
          removePanel: 'Ištrinti',
          choices_Item: 'elementas',
          matrix_column: 'Stulpelis',
          matrix_row: 'Eilutė',
          savingData: 'Rezultatai saugomi serveryje...',
          savingDataError: 'Įvyko klaida ir mes negalėjome išsaugoti rezultatų.',
          savingDataSuccess: 'Rezultatai buvo išsaugoti sėkmingai!',
          saveAgainButton: 'Bandyti dar kartą',
          timerMin: 'min',
          timerSec: 'sek',
          timerSpentAll: 'Praleidote {0} šiame puslapyje ir {1} iš viso.',
          timerSpentPage: 'Praleidote {0} šiame puslapyje.',
          timerSpentSurvey: 'Praleidote {0} iš viso.',
          timerLimitAll: 'Praleidote {0} iš {1} šiame puslapyje ir {2} iš {3} iš viso.',
          timerLimitPage: 'Praleidote {0} iš {1} šiame puslapyje.',
          timerLimitSurvey: 'Praleidote {0} iš {1} iš viso.',
          cleanCaption: 'Išvalyti',
          clearCaption: 'Valyti',
          chooseFileCaption: 'Pasirinkti failą',
          removeFileCaption: 'Ištrinti šį failą',
          booleanCheckedLabel: 'Taip',
          booleanUncheckedLabel: 'Ne',
          confirmRemoveFile: 'Ar tikrai norite pašalinti šį failą: {0}?',
          confirmRemoveAllFiles: 'Ar tikrai norite pašalinti visus failus?',
          questionTitlePatternText: 'Klausimo pavadinimas',
        };
      (r.a.locales.lt = i), (r.a.localeNames.lt = 'Lietuvių');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Forrige',
          pageNextText: 'Neste',
          completeText: 'Fullfør',
          previewText: 'Forhåndsvisning',
          editText: 'Redigere',
          startSurveyText: 'Start',
          otherItemText: 'Annet (beskriv)',
          noneItemText: 'Ingen',
          selectAllItemText: 'Velg alle',
          progressText: 'Side {0} av {1}',
          panelDynamicProgressText: 'Ta opp {0} av {1}',
          questionsProgressText: 'Besvarte {0} / {1} spørsmål',
          emptySurvey: 'Det er ingen synlig side eller spørsmål i undersøkelsen.',
          completingSurvey: 'Takk for at du fullførte undersøkelsen!',
          completingSurveyBefore:
            'Våre data viser at du allerede har gjennomført denne undersøkelsen.',
          loadingSurvey: 'Undersøkelsen laster...',
          optionsCaption: 'Velg...',
          value: 'verdi',
          requiredError: 'Vennligst svar på spørsmålet.',
          requiredErrorInPanel: 'Vennligst svar på minst ett spørsmål.',
          requiredInAllRowsError: 'Vennligst svar på spørsmål i alle rader.',
          numericError: 'Verdien skal være numerisk.',
          textMinLength: 'Vennligst skriv inn minst {0} tegn.',
          textMaxLength: 'Vennligst skriv inn mindre enn {0} tegn.',
          textMinMaxLength: 'Vennligst skriv inn mer enn {0} og mindre enn {1} tegn.',
          minRowCountError: 'Vennligst fyll inn minst {0} rader.',
          minSelectError: 'Vennligst velg minst {0} varianter.',
          maxSelectError: 'Vennligst ikke velg mer enn {0} varianter.',
          numericMinMax: "'{0}' bør være lik eller mer enn {1} og lik eller mindre enn {2}",
          numericMin: "'{0}' bør være lik eller mer enn {1}",
          numericMax: "'{0}' bør være lik eller mindre enn {1}",
          invalidEmail: 'Vennligst skriv inn en gyldig e-post adresse.',
          invalidExpression: "Uttrykket: {0} skal returnere 'sant'.",
          urlRequestError: "Forespørselen returnerte feilen '{0}'. {1}",
          urlGetChoicesError:
            "Forespørselen returnerte tomme data, eller 'sti' -egenskapen er feil",
          exceedMaxSize: 'Filstørrelsen bør ikke overstige {0}.',
          otherRequiredError: 'Vennligst skriv inn den andre verdien.',
          uploadingFile: 'Filen din lastes opp. Vennligst vent noen sekunder og prøv igjen.',
          loadingFile: 'Laster inn ...',
          chooseFile: 'Velg fil (er) ...',
          noFileChosen: 'Ingen fil valgt',
          confirmDelete: 'Ønsker du å slette posten?',
          keyDuplicationError: 'Denne verdien skal være unik.',
          addColumn: 'Legg til kolonne',
          addRow: 'Legg til rad',
          removeRow: 'Fjern',
          addPanel: 'Legg til ny',
          removePanel: 'Fjerne',
          choices_Item: 'element',
          matrix_column: 'Kolonne',
          matrix_row: 'Rad',
          savingData: 'Resultatene lagres på serveren ...',
          savingDataError: 'Det oppsto en feil, og vi kunne ikke lagre resultatene.',
          savingDataSuccess: 'Resultatene ble lagret!',
          saveAgainButton: 'Prøv igjen',
          timerMin: 'min',
          timerSec: 'sek',
          timerSpentAll: 'Du har tilbrakt {0} på denne siden og {1} totalt.',
          timerSpentPage: 'Du har tilbrakt {0} på denne siden.',
          timerSpentSurvey: 'Du har tilbrakt {0} totalt.',
          timerLimitAll: 'Du har tilbrakt {0} av {1} på denne siden og totalt {2} av {3}.',
          timerLimitPage: 'Du har tilbrakt {0} av {1} på denne siden.',
          timerLimitSurvey: 'Du har tilbrakt {0} av {1} totalt.',
          cleanCaption: 'Rens',
          clearCaption: 'Klar',
          chooseFileCaption: 'Velg Fil',
          removeFileCaption: 'Fjern denne filen',
          booleanCheckedLabel: 'Ja',
          booleanUncheckedLabel: 'Nei',
          confirmRemoveFile: 'Er du sikker på at du vil fjerne denne filen: {0}?',
          confirmRemoveAllFiles: 'Er du sikker på at du vil fjerne alle filene?',
          questionTitlePatternText: 'Spørsmålstittel',
        };
      (r.a.locales.no = i), (r.a.localeNames.no = 'norsk');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'قبلی',
          pageNextText: 'بعدی',
          completeText: 'تکمیل',
          previewText: 'پیش نمایش',
          editText: 'ویرایش',
          startSurveyText: 'شروع',
          otherItemText: 'دیگر(توضیح)',
          noneItemText: 'هیچ',
          selectAllItemText: 'انتخاب همه',
          progressText: 'صفحه {0} از {1}',
          panelDynamicProgressText: 'مورد {0} از {1}',
          questionsProgressText: 'تعداد پاسخ {0}/{1} سوال',
          emptySurvey: 'صفحه ای یا گزینه ای برای این پرسشنامه موجود نیست.',
          completingSurvey: 'از شما بابت تکمیل این پرسشنامه متشکریم',
          completingSurveyBefore: 'به نظر می رسد هم هم اکنون پرسشنامه را تکمیل کرده اید.',
          loadingSurvey: 'درحال ایجاد پرسشنامه',
          optionsCaption: 'انتخاب کنید...',
          value: 'مقدار',
          requiredError: 'لطفا به سوال پاسخ دهید',
          requiredErrorInPanel: 'لطفا حداقل به یک سوال پاسخ دهید.',
          requiredInAllRowsError: 'لطفا سوالات تمام سطرها را پاسخ دهید.',
          numericError: 'مقدار باید عددی باشد',
          textMinLength: 'لطفا حداقل  {0} حرف وارد کنید',
          textMaxLength: 'لطفا کمتر از  {0} حرف وارد کنید.',
          textMinMaxLength: 'لطفا بیشتر از  {0} حرف و کمتر از {1} حرف وارد کنید.',
          minRowCountError: 'لطفا حداقل {0} سطر وارد کنید.',
          minSelectError: 'حداقل {0} انتخاب کنید.',
          maxSelectError: 'لطفا بیشتر از  {0} انتخاب کنید.',
          numericMinMax: "'{0}' باید بین {1} و {2} باشد",
          numericMin: "'{0}' بزرگتر مساوی {1} باشد",
          numericMax: "'{0}' باید کوچکتر یا مساوی {1} باشد",
          invalidEmail: 'لطفا ایمیل صحیح درج کنید',
          invalidExpression: "عبارت: {0} پاسخ باید 'true' باشد.",
          urlRequestError: "درخواست با خطا روبرو شد: '{0}'. {1}",
          urlGetChoicesError: 'درخواست مسیری خالی بازگشت داده یا مسیر درست تنظیم نشده',
          exceedMaxSize: 'بیشترین حجم مجاز فایل: {0}',
          otherRequiredError: "مقدار 'دیگر' را وارد کنید",
          uploadingFile: 'فایل در حال آیلود است. لطفا صبر کنید.',
          loadingFile: 'بارگیری...',
          chooseFile: 'انتخاب فایل(ها)...',
          noFileChosen: 'هیچ فایلی انتخاب نشده',
          confirmDelete: 'آیا مایل به حذف این ردیف هستید؟',
          keyDuplicationError: 'این مقدار باید غیر تکراری باشد',
          addColumn: 'ستون جدید',
          addRow: 'سطر جدید',
          removeRow: 'حذف',
          addPanel: 'جدید',
          removePanel: 'حذف',
          choices_Item: 'آیتم',
          matrix_column: 'ستون',
          matrix_row: 'سطر',
          savingData: 'نتایج در حال ذخیره سازی در سرور است',
          savingDataError: 'خطایی در ذخیره سازی نتایج رخ داده است',
          savingDataSuccess: 'نتایج با موفقیت ذخیره شد',
          saveAgainButton: 'مجدد تلاش کنید',
          timerMin: 'دقیقه',
          timerSec: 'ثانیه',
          timerSpentAll: 'شما مدت {0} در این صفحه و مدت {1} را در مجموع سپری کرده اید.',
          timerSpentPage: 'شما مدت {0} را در این صفحه سپری کرده اید.',
          timerSpentSurvey: 'شما مدت {0} را در مجموع سپری کرده اید.',
          timerLimitAll:
            'شما مدت {0} از {1} در این صفحه و مدت {2} از {3} را در مجموع سپری کرده اید.',
          timerLimitPage: 'شما مدت {0} از {1} را در این صفحه سپری کرده اید.',
          timerLimitSurvey: 'شما مدت {0} از {1} را در مجموع سپری کرده اید.',
          cleanCaption: 'پاکسازی',
          clearCaption: 'خالی کردن',
          chooseFileCaption: 'انتخاب فایل',
          removeFileCaption: 'حذف این فایل',
          booleanCheckedLabel: 'بله',
          booleanUncheckedLabel: 'خیر',
          confirmRemoveFile: 'آیا میخواهید این فایل را پاک کنید: {0}?',
          confirmRemoveAllFiles: 'آیا میخواهید تمام فایل ها را پاک کنید?',
          questionTitlePatternText: 'عنوان سوال',
        };
      (r.a.locales.fa = i), (r.a.localeNames.fa = 'فارْسِى');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Wstecz',
          pageNextText: 'Dalej',
          completeText: 'Gotowe',
          previewText: 'Premiera',
          editText: 'Edycja',
          startSurveyText: 'Start',
          otherItemText: 'Inna odpowiedź (wpisz)',
          noneItemText: 'Brak',
          selectAllItemText: 'Wybierz wszystkie',
          progressText: 'Strona {0} z {1}',
          panelDynamicProgressText: 'Zapis {0} z {1}',
          questionsProgressText: 'Odpowiedzi na {0}/{1} pytania',
          emptySurvey: 'Nie ma widocznych pytań.',
          completingSurvey: 'Dziękujemy za wypełnienie ankiety!',
          completingSurveyBefore: 'Z naszych zapisów wynika, że wypełniłeś już tę ankietę.',
          loadingSurvey: 'Trwa wczytywanie ankiety...',
          optionsCaption: 'Wybierz...',
          value: 'Wartość',
          requiredError: 'Proszę odpowiedzieć na to pytanie.',
          requiredErrorInPanel: 'Proszę odpowiedzieć na co najmniej jedno pytanie.',
          requiredInAllRowsError: 'Proszę odpowiedzieć na wszystkie pytania.',
          numericError: 'W tym polu można wpisać tylko liczby.',
          textMinLength: 'Proszę wpisać co najmniej {0} znaków.',
          textMaxLength: 'Proszę wpisać mniej niż {0} znaków.',
          textMinMaxLength: 'Proszę wpisać więcej niż {0} i mniej niż {1} znaków.',
          minRowCountError: 'Proszę uzupełnić przynajmniej {0} wierszy.',
          minSelectError: 'Proszę wybrać co najmniej {0} pozycji.',
          maxSelectError: 'Proszę wybrać nie więcej niż {0} pozycji.',
          numericMinMax:
            "Odpowiedź '{0}' powinna być większa lub równa {1} oraz mniejsza lub równa {2}",
          numericMin: "Odpowiedź '{0}' powinna być większa lub równa {1}",
          numericMax: "Odpowiedź '{0}' powinna być mniejsza lub równa {1}",
          invalidEmail: 'Proszę podać prawidłowy adres email.',
          invalidExpression: "Wyrażenie: {0} powinno wracać 'prawdziwe'.",
          urlRequestError: "Żądanie zwróciło błąd '{0}'. {1}",
          urlGetChoicesError: 'Żądanie nie zwróciło danych albo ścieżka jest nieprawidłowa',
          exceedMaxSize: 'Rozmiar przesłanego pliku nie może przekraczać {0}.',
          otherRequiredError: 'Proszę podać inną odpowiedź.',
          uploadingFile:
            'Trwa przenoszenie Twojego pliku, proszę spróbować ponownie za kilka sekund.',
          loadingFile: 'Ładowanie...',
          chooseFile: 'Wybierz plik(i)...',
          noFileChosen: 'Nie wybrano żadnego pliku',
          confirmDelete: 'Chcesz skasować nagranie?',
          keyDuplicationError: 'Ta wartość powinna być wyjątkowa.',
          addColumn: 'Dodaj kolumnę',
          addRow: 'Dodaj wiersz',
          removeRow: 'Usuń',
          addPanel: 'Dodaj panel',
          removePanel: 'Usuń',
          choices_Item: 'element',
          matrix_column: 'Kolumna',
          matrix_row: 'Wiersz',
          savingData: 'Zapisuję wyniki ankiety na serwerze...',
          savingDataError: 'Wystąpił błąd i wyniki nie mogły zostać zapisane.',
          savingDataSuccess: 'Wyniki zostały poprawnie zapisane!',
          saveAgainButton: 'Spróbuj ponownie',
          timerMin: 'min',
          timerSec: 'sek',
          timerSpentAll: 'Spędziłeś {0} na tej stronie a w sumie {1}.',
          timerSpentPage: 'Spędziłeś {0} na tej stronie.',
          timerSpentSurvey: 'Spędziłeś w sumie {0}.',
          timerLimitAll: 'Spędziłeś {0} z {1} na tej stronie a w sumie {2} z {3}.',
          timerLimitPage: 'Spędziłeś {0} z {1} na tej stronie',
          timerLimitSurvey: 'Spędziłeś {0} z {1}.',
          cleanCaption: 'Wyczyść',
          clearCaption: 'Jasne',
          chooseFileCaption: 'Wybierz plik',
          removeFileCaption: 'Usuń ten plik',
          booleanCheckedLabel: 'Tak',
          booleanUncheckedLabel: 'Nie',
          confirmRemoveFile: 'Jesteś pewien, że chcesz usunąć ten plik: {0}?',
          confirmRemoveAllFiles: 'Jesteś pewien, że chcesz usunąć wszystkie pliki?',
          questionTitlePatternText: 'Tytuł pytania',
        };
      (r.a.locales.pl = i), (r.a.localeNames.pl = 'polski');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Anterior',
          pageNextText: 'Próximo',
          completeText: 'Finalizar',
          previewText: 'Pré-visualização',
          editText: 'Editar',
          startSurveyText: 'Começar',
          otherItemText: 'Outros (descrever)',
          noneItemText: 'Nenhum',
          selectAllItemText: 'Selecionar Todos',
          progressText: 'Página {0} de {1}',
          panelDynamicProgressText: 'Registo {0} de {1}',
          questionsProgressText: 'Respostas {0}/{1} perguntas',
          emptySurvey: 'Não há página visível ou pergunta na pesquisa.',
          completingSurvey: 'Obrigado por finalizar a pesquisa!',
          completingSurveyBefore: 'Nossos registros mostram que você já finalizou a pesquisa.',
          loadingSurvey: 'A pesquisa está carregando...',
          optionsCaption: 'Selecione...',
          value: 'valor',
          requiredError: 'Por favor, responda a pergunta.',
          requiredErrorInPanel: 'Por favor, responda pelo menos uma pergunta.',
          requiredInAllRowsError: 'Por favor, responda as perguntas em todas as linhas.',
          numericError: 'O valor deve ser numérico.',
          textMinLength: 'Por favor, insira pelo menos {0} caracteres.',
          textMaxLength: 'Por favor, insira menos de {0} caracteres.',
          textMinMaxLength: 'Por favor, insira mais de {0} e menos de {1} caracteres.',
          minRowCountError: 'Preencha pelo menos {0} linhas.',
          minSelectError: 'Selecione pelo menos {0} opções.',
          maxSelectError: 'Por favor, selecione não mais do que {0} opções.',
          numericMinMax: "O '{0}' deve ser igual ou superior a {1} e igual ou menor que {2}",
          numericMin: "O '{0}' deve ser igual ou superior a {1}",
          numericMax: "O '{0}' deve ser igual ou inferior a {1}",
          invalidEmail: 'Por favor, informe um e-mail válido.',
          invalidExpression: "A expressão: {0} deve retornar 'verdadeiro'.",
          urlRequestError: "A requisição retornou o erro '{0}'. {1}",
          urlGetChoicesError:
            "A requisição não retornou dados ou o 'caminho' da requisição não está correto",
          exceedMaxSize: 'O tamanho do arquivo não deve exceder {0}.',
          otherRequiredError: 'Por favor, informe o outro valor.',
          uploadingFile:
            'Seu arquivo está sendo carregado. Por favor, aguarde alguns segundos e tente novamente.',
          loadingFile: 'Carregando...',
          chooseFile: 'Selecione o(s) arquivo(s)...',
          noFileChosen: 'Nenhum arquivo escolhido',
          confirmDelete: 'Tem certeza que deseja deletar?',
          keyDuplicationError: 'Esse valor deve ser único.',
          addColumn: 'Adicionar coluna',
          addRow: 'Adicionar linha',
          removeRow: 'Remover linha',
          addPanel: 'Adicionar novo',
          removePanel: 'Remover',
          choices_Item: 'item',
          matrix_column: 'Coluna',
          matrix_row: 'Linha',
          savingData: 'Os resultados esto sendo salvos no servidor...',
          savingDataError: 'Ocorreu um erro e não foi possível salvar os resultados.',
          savingDataSuccess: 'Os resultados foram salvos com sucesso!',
          saveAgainButton: 'Tente novamente',
          timerMin: 'min',
          timerSec: 'seg',
          timerSpentAll: 'Você gastou {0} nesta página e {1} no total.',
          timerSpentPage: 'Você gastou {0} nesta página.',
          timerSpentSurvey: 'Você gastou {0} no total.',
          timerLimitAll: 'Você gastou {0} de {1} nesta página e {2} de {3} no total.',
          timerLimitPage: 'Você gastou {0} de {1} nesta página.',
          timerLimitSurvey: 'Você gastou {0} de {1} no total.',
          cleanCaption: 'Limpar',
          clearCaption: 'Limpar',
          chooseFileCaption: 'Escolher arquivo',
          removeFileCaption: 'Remover este arquivo',
          booleanCheckedLabel: 'Sim',
          booleanUncheckedLabel: 'Não',
          confirmRemoveFile: 'Tem certeza que deseja remover este arquivo: {0}?',
          confirmRemoveAllFiles: 'Tem certeza que deseja remover todos os arquivos?',
          questionTitlePatternText: 'Título da questão',
        };
      (r.a.locales['pt-br'] = i), (r.a.localeNames['pt-br'] = 'português brasileiro');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Anterior',
          pageNextText: 'Próximo',
          completeText: 'Finalizar',
          previewText: 'Pré-visualização',
          editText: 'Editar',
          startSurveyText: 'Começar',
          otherItemText: 'Outros (descrever)',
          noneItemText: 'Nenhum',
          selectAllItemText: 'Selecionar Todos',
          progressText: 'Página {0} de {1}',
          panelDynamicProgressText: 'Registo {0} de {1}',
          questionsProgressText: 'Respostas {0}/{1} perguntas',
          emptySurvey: 'Não há página visível ou pergunta na pesquisa.',
          completingSurvey: 'Obrigado por finalizar a pesquisa!',
          completingSurveyBefore: 'Nossos registros mostram que você já finalizou a pesquisa.',
          loadingSurvey: 'A pesquisa está carregando...',
          optionsCaption: 'Selecione...',
          value: 'valor',
          requiredError: 'Por favor, responda a pergunta.',
          requiredErrorInPanel: 'Por favor, responda pelo menos uma pergunta.',
          requiredInAllRowsError: 'Por favor, responda as perguntas em todas as linhas.',
          numericError: 'O valor deve ser numérico.',
          textMinLength: 'Por favor, insira pelo menos {0} caracteres.',
          textMaxLength: 'Por favor, insira menos de {0} caracteres.',
          textMinMaxLength: 'Por favor, insira mais de {0} e menos de {1} caracteres.',
          minRowCountError: 'Preencha pelo menos {0} linhas.',
          minSelectError: 'Selecione pelo menos {0} opções.',
          maxSelectError: 'Por favor, selecione não mais do que {0} opções.',
          numericMinMax: "O '{0}' deve ser igual ou superior a {1} e igual ou menor que {2}",
          numericMin: "O '{0}' deve ser igual ou superior a {1}",
          numericMax: "O '{0}' deve ser igual ou inferior a {1}",
          invalidEmail: 'Por favor, informe um e-mail válido.',
          invalidExpression: "A expressão: {0} deve retornar 'verdadeiro'.",
          urlRequestError: "A requisição retornou o erro '{0}'. {1}",
          urlGetChoicesError:
            "A requisição não retornou dados ou o 'caminho' da requisição não está correto",
          exceedMaxSize: 'O tamanho do arquivo não deve exceder {0}.',
          otherRequiredError: 'Por favor, informe o outro valor.',
          uploadingFile:
            'Seu arquivo está sendo carregado. Por favor, aguarde alguns segundos e tente novamente.',
          loadingFile: 'Carregando...',
          chooseFile: 'Selecione o(s) arquivo(s)...',
          noFileChosen: 'Nenhum ficheiro escolhido',
          confirmDelete: 'Tem certeza que deseja deletar?',
          keyDuplicationError: 'Esse valor deve ser único.',
          addColumn: 'Adicionar coluna',
          addRow: 'Adicionar linha',
          removeRow: 'Remover linha',
          addPanel: 'Adicionar novo',
          removePanel: 'Remover',
          choices_Item: 'item',
          matrix_column: 'Coluna',
          matrix_row: 'Linha',
          savingData: 'Os resultados esto sendo salvos no servidor...',
          savingDataError: 'Ocorreu um erro e não foi possível salvar os resultados.',
          savingDataSuccess: 'Os resultados foram salvos com sucesso!',
          saveAgainButton: 'Tente novamente',
          timerMin: 'min',
          timerSec: 'seg',
          timerSpentAll: 'Você gastou {0} nesta página e {1} no total.',
          timerSpentPage: 'Você gastou {0} nesta página.',
          timerSpentSurvey: 'Você gastou {0} no total.',
          timerLimitAll: 'Você gastou {0} de {1} nesta página e {2} de {3} no total.',
          timerLimitPage: 'Você gastou {0} de {1} nesta página.',
          timerLimitSurvey: 'Você gastou {0} de {1} no total.',
          cleanCaption: 'Limpar',
          clearCaption: 'Limpar',
          chooseFileCaption: 'Escolher ficheiro',
          removeFileCaption: 'Remover este arquivo',
          booleanCheckedLabel: 'Sim',
          booleanUncheckedLabel: 'Não',
          confirmRemoveFile: 'Tem certeza que deseja remover este arquivo: {0}?',
          confirmRemoveAllFiles: 'Tem certeza que deseja remover todos os arquivos?',
          questionTitlePatternText: 'Título da questão',
        };
      (r.a.locales.pt = i), (r.a.localeNames.pt = 'português');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Precedent',
          pageNextText: 'Următor',
          completeText: 'Finalizare',
          previewText: 'previzualizare',
          editText: 'Editați',
          startSurveyText: 'start',
          otherItemText: 'Altul(precizaţi)',
          noneItemText: 'Nici unul',
          selectAllItemText: 'Selectează tot',
          progressText: 'Pagina {0} din {1}',
          panelDynamicProgressText: 'Înregistrare {0} din {1}',
          questionsProgressText: 'Răspunsuri la {0} / {1} întrebări',
          emptySurvey: 'Nu sunt întrebări pentru acest chestionar',
          completingSurvey: 'Vă mulţumim pentru timpul acordat!',
          completingSurveyBefore:
            'Din înregistrările noastre reiese că ați completat deja acest chestionar.',
          loadingSurvey: 'Chestionarul se încarcă...',
          optionsCaption: 'Alegeţi...',
          value: 'valoare',
          requiredError: 'Răspunsul la această întrebare este obligatoriu.',
          requiredErrorInPanel: 'Vă rugăm să răspundeți la cel puțin o întrebare.',
          requiredInAllRowsError: 'Toate răspunsurile sunt obligatorii',
          numericError: 'Răspunsul trebuie să fie numeric.',
          textMinLength: 'Trebuie să introduceți minim {0} caractere.',
          textMaxLength: 'Trebuie să introduceți maxim {0} caractere.',
          textMinMaxLength: 'Trebuie să introduceți mai mult de {0} și mai puțin de {1} caractere.',
          minRowCountError: 'Trebuie să completați minim {0} rânduri.',
          minSelectError: 'Trebuie să selectați minim {0} opţiuni.',
          maxSelectError: 'Trebuie să selectați maxim {0} opţiuni.',
          numericMinMax:
            "Răspunsul '{0}' trebuie să fie mai mare sau egal ca {1} şî mai mic sau egal cu {2}",
          numericMin: "Răspunsul '{0}' trebuie să fie mai mare sau egal ca {1}",
          numericMax: "Răspunsul '{0}' trebuie să fie mai mic sau egal ca {1}",
          invalidEmail: 'Trebuie să introduceţi o adresa de email validă.',
          invalidExpression: 'Expresia: {0} ar trebui să returneze „adevărat”.',
          urlRequestError: "Request-ul a returnat eroarea '{0}'. {1}",
          urlGetChoicesError:
            "Request-ul nu a returnat date sau proprietatea 'path' este incorectă",
          exceedMaxSize: 'Dimensiunea fişierului nu trebuie să depăşească {0}.',
          otherRequiredError: "Trebuie să completați câmpul 'Altul'.",
          uploadingFile:
            'Fișierul dumneavoastră este în curs de încărcare. Vă rugăm așteptați câteva secunde și reveniți apoi.',
          loadingFile: 'Se încarcă...',
          chooseFile: 'Alege fisierele...',
          noFileChosen: 'Niciun fișier ales',
          confirmDelete: 'Sunteți sigur că doriți să ștergeți înregistrarea?',
          keyDuplicationError: 'Valoarea trebuie să fie unică.',
          addColumn: 'Adăugați coloană',
          addRow: 'Adăugare rând',
          removeRow: 'Ștergere',
          addPanel: 'Adăugare',
          removePanel: 'Ștergere',
          choices_Item: 'opțiune',
          matrix_column: 'Coloană',
          matrix_row: 'Rând',
          savingData: 'Rezultatele sunt în curs de salvare...',
          savingDataError: 'A intervenit o eroare, rezultatele nu au putut fi salvate.',
          savingDataSuccess: 'Rezultatele au fost salvate cu succes!',
          saveAgainButton: 'Încercați din nou',
          timerMin: 'min',
          timerSec: 'sec',
          timerSpentAll: 'Ați petrecut {0} pe această pagină și {1} în total.',
          timerSpentPage: 'Ați petrecut {0} pe această pagină.',
          timerSpentSurvey: 'Ați petrecut {0} în total.',
          timerLimitAll: 'Ați petrecut {0} din {1} pe această pagină și {2} din {3} în total.',
          timerLimitPage: 'Ați petrecut {0} din {1} pe această pagină.',
          timerLimitSurvey: 'Ați petrecut {0} din {1} în total.',
          cleanCaption: 'Curat',
          clearCaption: 'clar',
          chooseFileCaption: 'Alege fișierul',
          removeFileCaption: 'Eliminați acest fișier',
          booleanCheckedLabel: 'da',
          booleanUncheckedLabel: 'Nu',
          confirmRemoveFile: 'Sigur doriți să eliminați acest fișier: {0}?',
          confirmRemoveAllFiles: 'Sigur doriți să eliminați toate fișierele?',
          questionTitlePatternText: 'Titlul intrebarii',
        };
      (r.a.locales.ro = i), (r.a.localeNames.ro = 'română');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Назад',
          pageNextText: 'Далее',
          completeText: 'Готово',
          previewText: 'Предварительный просмотр',
          editText: 'Редактирование',
          startSurveyText: 'Начать',
          otherItemText: 'Другое (пожалуйста, опишите)',
          noneItemText: 'Нет',
          selectAllItemText: 'Выбрать всё',
          progressText: 'Страница {0} из {1}',
          panelDynamicProgressText: 'Запись {0} из {1}',
          questionsProgressText: 'Ответы на {0}/{1}вопросы',
          emptySurvey: 'Нет ни одного вопроса.',
          completingSurvey: 'Благодарим Вас за заполнение анкеты!',
          completingSurveyBefore: 'Вы уже проходили этот опрос.',
          loadingSurvey: 'Загрузка с сервера...',
          optionsCaption: 'Выбрать...',
          value: 'значение',
          requiredError: 'Пожалуйста, ответьте на вопрос.',
          requiredErrorInPanel: 'Пожалуйста, ответьте по крайней мере на один вопрос.',
          requiredInAllRowsError: 'Пожалуйста, ответьте на вопросы в каждой строке.',
          numericError: 'Ответ должен быть числом.',
          textMinLength: 'Пожалуйста введите больше {0} символов.',
          textMaxLength: 'Пожалуйста введите меньше {0} символов.',
          textMinMaxLength: 'Пожалуйста введите больше {0} и меньше {1} символов.',
          minRowCountError: 'Пожалуйста, заполните не меньше {0} строк.',
          minSelectError: 'Пожалуйста, выберите хотя бы {0} вариантов.',
          maxSelectError: 'Пожалуйста, выберите не более {0} вариантов.',
          numericMinMax: "'{0}' должно быть не меньше чем {1}, и не больше чем {2}",
          numericMin: "'{0}' должно быть не меньше чем {1}",
          numericMax: "'{0}' должно быть не больше чем {1}",
          invalidEmail: 'Пожалуйста, введите действительный адрес электронной почты.',
          invalidExpression: "Выражение {0} должно возвращать 'true'.",
          urlRequestError: "Запрос вернул ошибку '{0}'. {1}",
          urlGetChoicesError: "Ответ на запрос пришел пустой или свойство 'path' указано неверно",
          exceedMaxSize: 'Размер файла не должен превышать {0}.',
          otherRequiredError: "Пожалуйста, введите данные в поле 'Другое'",
          uploadingFile: 'Ваш файл загружается. Подождите несколько секунд и попробуйте снова.',
          loadingFile: 'Загрузка...',
          chooseFile: 'Выберите файл(ы)...',
          noFileChosen: 'Файл не выбран',
          confirmDelete: 'Вы точно хотите удалить запись?',
          keyDuplicationError: 'Это значение должно быть уникальным.',
          addColumn: 'Добавить колонку',
          addRow: 'Добавить строку',
          removeRow: 'Удалить',
          addPanel: 'Добавить новую',
          removePanel: 'Удалить',
          choices_Item: 'Вариант',
          matrix_column: 'Колонка',
          matrix_row: 'Строка',
          savingData: 'Результаты сохраняются на сервер...',
          savingDataError: 'Произошла ошибка, результат не был сохранён.',
          savingDataSuccess: 'Результат успешно сохранён!',
          saveAgainButton: 'Попробовать снова',
          timerMin: 'мин',
          timerSec: 'сек',
          timerSpentAll: 'Вы потратили {0} на этой странице и {1} всего.',
          timerSpentPage: 'Вы потратили {0} на этой странице.',
          timerSpentSurvey: 'Вы потратили {0} в течение теста.',
          timerLimitAll: 'Вы потратили {0} из {1} на этой странице и {2} из {3} для всего теста.',
          timerLimitPage: 'Вы потратили {0} из {1} на этой странице.',
          timerLimitSurvey: 'Вы потратили {0} из {1} для всего теста.',
          cleanCaption: 'Очистить',
          clearCaption: 'Очистить',
          chooseFileCaption: 'Выберите файл',
          removeFileCaption: 'Удалить файл',
          booleanCheckedLabel: 'Да',
          booleanUncheckedLabel: 'Нет',
          confirmRemoveFile: 'Вы уверены, что хотите удалить этот файл: {0}?',
          confirmRemoveAllFiles: 'Вы уверены, что хотите удалить все файлы?',
          questionTitlePatternText: 'Название вопроса',
        };
      (r.a.locales.ru = i), (r.a.localeNames.ru = 'русский');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: '上一页',
          pageNextText: '下一页',
          completeText: '提交问卷',
          previewText: '预览',
          editText: '编辑',
          startSurveyText: '开始问卷',
          otherItemText: '填写其他答案',
          noneItemText: '无',
          selectAllItemText: '选择全部',
          progressText: '第 {0} 页, 共 {1} 页',
          panelDynamicProgressText: '{0} of {1}',
          questionsProgressText: '第 {0}/{1} 题',
          emptySurvey: '问卷中没有问题或页面',
          completingSurvey: '感谢您的参与!',
          completingSurveyBefore: '你已完成问卷.',
          loadingSurvey: '问卷正在加载中...',
          optionsCaption: '请选择...',
          value: '值',
          requiredError: '请填写此问题',
          requiredErrorInPanel: '至少回答一题.',
          requiredInAllRowsError: '请填写所有行中问题',
          numericError: '答案必须是个数字',
          textMinLength: '答案长度至少 {0} 个字符',
          textMaxLength: '答案长度不能超过 {0} 个字符',
          textMinMaxLength: '答案长度必须在 {0} - {1} 个字符之间',
          minRowCountError: '最少需要填写 {0} 行答案',
          minSelectError: '最少需要选择 {0} 项答案',
          maxSelectError: '最多只能选择 {0} 项答案',
          numericMinMax: "答案 '{0}' 必须大于等于 {1} 且小于等于 {2}",
          numericMin: "答案 '{0}' 必须大于等于 {1}",
          numericMax: "答案 '{0}' 必须小于等于 {1}",
          invalidEmail: '请输入有效的 Email 地址',
          invalidExpression: '公式: {0} 无效.',
          urlRequestError: "载入选项时发生错误 '{0}': {1}",
          urlGetChoicesError: '未能载入有效的选项或请求参数路径有误',
          exceedMaxSize: '文件大小不能超过 {0}',
          otherRequiredError: '请完成其他问题',
          uploadingFile: '文件上传中... 请耐心等待几秒后重试',
          loadingFile: '加载...',
          chooseFile: '选择文件...',
          noFileChosen: '未选择文件',
          confirmDelete: '删除记录?',
          keyDuplicationError: '主键不能重复',
          addColumn: '添加列',
          addRow: '添加行',
          removeRow: '删除答案',
          addPanel: '新添',
          removePanel: '删除',
          choices_Item: '选项',
          matrix_column: '列',
          matrix_row: '行',
          savingData: '正在将结果保存到服务器...',
          savingDataError: '在保存结果过程中发生了错误，结果未能保存',
          savingDataSuccess: '结果保存成功!',
          saveAgainButton: '请重试',
          timerMin: '分',
          timerSec: '秒',
          timerSpentAll: '本页用时 {0} 总计用时{1} .',
          timerSpentPage: '本页用时{0} .',
          timerSpentSurvey: '总计用时 {0} .',
          timerLimitAll: '本页用时 {0} 共 {1}， 总计用时 {2} 共 {3} .',
          timerLimitPage: '本页用时 {0} 共 {1} .',
          timerLimitSurvey: '总计用时 {0} 共 {1}.',
          cleanCaption: '清理',
          clearCaption: '清除',
          chooseFileCaption: '选择文件',
          removeFileCaption: '移除文件',
          booleanCheckedLabel: '是',
          booleanUncheckedLabel: '否',
          confirmRemoveFile: '删除文件: {0}?',
          confirmRemoveAllFiles: '删除所有文件?',
          questionTitlePatternText: '标题',
        };
      (r.a.locales['zh-cn'] = i), (r.a.localeNames['zh-cn'] = '简体中文');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Anterior',
          pageNextText: 'Siguiente',
          completeText: 'Completar',
          previewText: 'Vista previa',
          editText: 'Edita',
          startSurveyText: 'Comienza',
          otherItemText: 'Otro (describa)',
          noneItemText: 'Ninguno',
          selectAllItemText: 'Seleccionar todo',
          progressText: 'Página {0} de {1}',
          panelDynamicProgressText: 'Registro {0} de {1}',
          questionsProgressText: 'Respondió a {0}/{1} preguntas',
          emptySurvey: 'No hay página visible o pregunta en la encuesta.',
          completingSurvey: 'Gracias por completar la encuesta!',
          completingSurveyBefore: 'Nuestros registros muestran que ya ha completado esta encuesta.',
          loadingSurvey: 'La encuesta está cargando...',
          optionsCaption: 'Seleccione...',
          value: 'valor',
          requiredError: 'Por favor conteste la pregunta.',
          requiredErrorInPanel: 'Por favor, responda al menos una pregunta.',
          requiredInAllRowsError: 'Por favor conteste las preguntas en cada hilera.',
          numericError: 'La estimación debe ser numérica.',
          textMinLength: 'Por favor entre por lo menos {0} símbolos.',
          textMaxLength: 'Por favor entre menos de {0} símbolos.',
          textMinMaxLength: 'Por favor entre más de {0} y menos de {1} símbolos.',
          minRowCountError: 'Por favor llene por lo menos {0} hileras.',
          minSelectError: 'Por favor seleccione por lo menos {0} variantes.',
          maxSelectError: 'Por favor seleccione no más de {0} variantes.',
          numericMinMax: "El '{0}' debe de ser igual o más de {1} y igual o menos de {2}",
          numericMin: "El '{0}' debe ser igual o más de {1}",
          numericMax: "El '{0}' debe ser igual o menos de {1}",
          invalidEmail: 'Por favor agregue un correo electrónico válido.',
          invalidExpression: "La expresión: {0} debería devolver 'verdadero'.",
          urlRequestError: "La solicitud regresó error '{0}'. {1}",
          urlGetChoicesError:
            "La solicitud regresó vacío de data o la propiedad 'trayectoria' no es correcta",
          exceedMaxSize: 'El tamaño del archivo no debe de exceder {0}.',
          otherRequiredError: 'Por favor agregue la otra estimación.',
          uploadingFile:
            'Su archivo se está subiendo. Por favor espere unos segundos e intente de nuevo.',
          loadingFile: 'Cargando...',
          chooseFile: 'Elija archivo(s)...',
          noFileChosen: 'No se ha elegido ningún archivo',
          confirmDelete: '¿Quieres borrar el registro?',
          keyDuplicationError: 'Este valor debe ser único.',
          addColumn: 'Añadir columna',
          addRow: 'Agregue una hilera',
          removeRow: 'Eliminar una hilera',
          addPanel: 'Añadir nuevo',
          removePanel: 'Retire',
          choices_Item: 'artículo',
          matrix_column: 'Columna',
          matrix_row: 'Hilera',
          savingData: 'Los resultados se están guardando en el servidor...',
          savingDataError: 'Los resultados se están guardando en el servidor...',
          savingDataSuccess: '¡Los resultados se guardaron con éxito!',
          saveAgainButton: 'Inténtalo de nuevo.',
          timerMin: 'min',
          timerSec: 'sec',
          timerSpentAll: 'Has gastado {0} en esta página y {1} en total.',
          timerSpentPage: 'Usted ha pasado {0} en esta página.',
          timerSpentSurvey: 'Has gastado en total.',
          timerLimitAll: 'Has gastado {0} de {1} en esta página y {2} de {3} en total.',
          timerLimitPage: 'Has gastado {0} de {1} en esta página.',
          timerLimitSurvey: 'Usted ha gastado {0} de {1} en total.',
          cleanCaption: 'Limpia',
          clearCaption: 'Despejen',
          chooseFileCaption: 'Elija el archivo',
          removeFileCaption: 'Elimina este archivo',
          booleanCheckedLabel: 'Sí',
          booleanUncheckedLabel: 'No',
          confirmRemoveFile: '¿Estás seguro de que quieres eliminar este archivo: {0}?',
          confirmRemoveAllFiles: '¿Estás seguro de que quieres eliminar todos los archivos?',
          questionTitlePatternText: 'Título de la pregunta',
        };
      (r.a.locales.es = i), (r.a.localeNames.es = 'español');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Iliyotangulia',
          pageNextText: 'Ifuatayo',
          completeText: 'Kamili',
          previewText: 'Hakiki',
          editText: 'Hariri',
          startSurveyText: 'Anza',
          otherItemText: 'Nyingine (eleza)',
          noneItemText: 'Hakuna',
          selectAllItemText: 'Chagua Zote',
          progressText: 'Ukurasa {0} wa {1}',
          panelDynamicProgressText: 'Rekodi {0} ya {1}',
          questionsProgressText: 'Yaliyojibiwa {0}/{1} maswali',
          emptySurvey: 'Hakuna ukurasa unaoonekana au swali katika utafiti.',
          completingSurvey: 'Asanti kwa kukamilisha utafiti!',
          completingSurveyBefore: 'Recodi zetu zinatuonyesha tayari umekamilisha utafiti.',
          loadingSurvey: 'Tunaandaa utafiti...',
          optionsCaption: 'Chagua...',
          value: 'thamani',
          requiredError: 'Tafadhali jibu hili swali.',
          requiredErrorInPanel: 'Tafadhali jibu swali angalau moja.',
          requiredInAllRowsError: 'Tafadhali jibu maswali katika safu zote.',
          numericError: 'Thamani inapaswa kuwa ya nambari.',
          textMinLength: 'Tafadhali ingiza angalau{0} husika.',
          textMaxLength: 'Tafadhali ingiza isiozidi {0} husika.',
          textMinMaxLength: 'Tafadhali ingiza kiwango zaidi ya {0} na kisichopungua {1} husika.',
          minRowCountError: 'Tafadhali jaza isiopungua {0} safu.',
          minSelectError: 'Tafadhali chagua angalau {0} lahaja.',
          maxSelectError: 'Tafadhali changua isiozidi {0} lahaja.',
          numericMinMax: " '{0}' inapaswa kuwa sawa au zaidi ya {1} na sawa au chini ya {2}",
          numericMin: " '{0}'inapaswa kuwa sawa au zaidi ya {1}",
          numericMax: " '{0}'inapaswa kuwa sawa au chini ya {1}",
          invalidEmail: 'Tafadhali ingiza anwani halali ya barua-pepe.',
          invalidExpression: "Usemi:{0} inapaswa kurudi 'kweli'.",
          urlRequestError: "Ombi lina kosa '{0}'. {1}",
          urlGetChoicesError: "Ombi lilirudisha data tupu au the 'path' mali ya njia sio sahihi",
          exceedMaxSize: 'Saizi ya faili haipaswi kuzidi {0}.',
          otherRequiredError: 'Tafadhali ingiza thamani nyingine.',
          uploadingFile: 'Faili yako inapakia.Tafadhali subiri sekunde kadhaa na ujaribu tena.',
          loadingFile: 'Inapakia...',
          chooseFile: 'Chagua faili...',
          noFileChosen: 'Hujachagua faili',
          confirmDelete: 'Je! Unataka kufuta rekodi?',
          keyDuplicationError: 'Thamani hii inapaswa kuwa ya kipekee.',
          addColumn: 'Ongeza Kolamu',
          addRow: 'Ongeza safu',
          removeRow: 'Toa',
          addPanel: 'Ongeza mpya',
          removePanel: 'Toa',
          choices_Item: 'kitu',
          matrix_column: 'Kolamu',
          matrix_row: 'Safu',
          savingData: 'Matokeo yamehifadhiwa kwa seva...',
          savingDataError: 'Kosa limetokea na hatukuweza kuhifadhi matokeo.',
          savingDataSuccess: 'Matokeo yamehifadhiwa!',
          saveAgainButton: 'Jaribu tena',
          timerMin: 'dakika',
          timerSec: 'sekunde',
          timerSpentAll: 'Umetumia {0} kwenye ukurasa huu na {1} kwa jumla.',
          timerSpentPage: 'Umetumia {0} kwenye ukurasa huu.',
          timerSpentSurvey: 'Umetumia {0} kwa jumla.',
          timerLimitAll: 'Umetumia {0} ya {1} kwenye ukurasa huu {2} wa {3} kwa jumla.',
          timerLimitPage: 'Umetumia {0} ya {1} kwenye ukurasa huu.',
          timerLimitSurvey: 'Umetumia {0} ya {1} kwa jumla.',
          cleanCaption: 'Safisha',
          clearCaption: 'Ondoa',
          chooseFileCaption: 'Chagua faili',
          removeFileCaption: 'Ondoa faili',
          booleanCheckedLabel: 'Ndio',
          booleanUncheckedLabel: 'Hapana',
          confirmRemoveFile: 'Je! Una uhakika kuwa unataka kuondoa faili hii: {0}?',
          confirmRemoveAllFiles: 'Je! Una uhakika kuwa unataka kuondoa faili zote?',
          questionTitlePatternText: 'Kichwa cha Swali',
        };
      (r.a.locales.sw = i), (r.a.localeNames.sw = 'Swahili');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Föregående',
          pageNextText: 'Nästa',
          completeText: 'Färdig',
          previewText: 'Förhandsvisning',
          editText: 'Redigera',
          startSurveyText: 'Start',
          otherItemText: 'Annat (beskriv)',
          noneItemText: 'Ingen',
          selectAllItemText: 'Välj alla',
          progressText: 'Sida {0} av {1}',
          panelDynamicProgressText: 'Spela in {0} av {1}',
          questionsProgressText: 'Besvarade {0} / {1} frågor',
          emptySurvey: 'Det finns ingen synlig sida eller fråga i enkäten.',
          completingSurvey: 'Tack för att du genomfört enkäten!!',
          completingSurveyBefore:
            'Våra register visar att du redan har slutfört denna undersökning.',
          loadingSurvey: 'Enkäten laddas...',
          optionsCaption: 'Välj...',
          value: 'värde',
          requiredError: 'Var vänlig besvara frågan.',
          requiredErrorInPanel: 'Vänligen svara på minst en fråga.',
          requiredInAllRowsError: 'Var vänlig besvara frågorna på alla rader.',
          numericError: 'Värdet ska vara numeriskt.',
          textMinLength: 'Var vänlig ange minst {0} tecken.',
          textMaxLength: 'Ange färre än {0} tecken.',
          textMinMaxLength: 'Ange mer än {0} och färre än {1} tecken.',
          minRowCountError: 'Var vänlig fyll i minst {0} rader.',
          minSelectError: 'Var vänlig välj åtminstone {0} varianter.',
          maxSelectError: 'Var vänlig välj inte fler än {0} varianter.',
          numericMinMax:
            "'{0}' ska vara lika med eller mer än {1} samt lika med eller mindre än {2}",
          numericMin: "'{0}' ska vara lika med eller mer än {1}",
          numericMax: "'{0}' ska vara lika med eller mindre än {1}",
          invalidEmail: 'Var vänlig ange en korrekt e-postadress.',
          invalidExpression: "Uttrycket: {0} ska returnera 'true'.",
          urlRequestError: "Förfrågan returnerade felet '{0}'. {1}",
          urlGetChoicesError:
            "Antingen returnerade förfrågan ingen data eller så är egenskapen 'path' inte korrekt",
          exceedMaxSize: 'Filstorleken får ej överstiga {0}.',
          otherRequiredError: 'Var vänlig ange det andra värdet.',
          uploadingFile:
            'Din fil laddas upp. Var vänlig vänta några sekunder och försök sedan igen.',
          loadingFile: 'Läser in...',
          chooseFile: 'Välj fil (er) ...',
          noFileChosen: 'Ingen fil vald',
          confirmDelete: 'Vill du radera posten?',
          keyDuplicationError: 'Detta värde ska vara unikt.',
          addColumn: 'Lägg till kolumn',
          addRow: 'Lägg till rad',
          removeRow: 'Ta bort',
          addPanel: 'Lägg till ny',
          removePanel: 'Ta bort',
          choices_Item: 'Artikel',
          matrix_column: 'Kolumn',
          matrix_row: 'Rad',
          savingData: 'Resultaten sparas på servern ...',
          savingDataError: 'Ett fel inträffade och vi kunde inte spara resultaten.',
          savingDataSuccess: 'Resultaten sparades framgångsrikt!',
          saveAgainButton: 'Försök igen',
          timerMin: 'min',
          timerSec: 'sek',
          timerSpentAll: 'Du har spenderat {0} på den här sidan och {1} totalt.',
          timerSpentPage: 'Du har spenderat {0} på den här sidan.',
          timerSpentSurvey: 'Du har spenderat {0} totalt.',
          timerLimitAll: 'Du har spenderat {0} av {1} på den här sidan och {2} av {3} totalt.',
          timerLimitPage: 'Du har spenderat {0} av {1} på den här sidan.',
          timerLimitSurvey: 'Du har spenderat {0} av {1} totalt.',
          cleanCaption: 'Rena',
          clearCaption: 'Klar',
          chooseFileCaption: 'Välj FIL',
          removeFileCaption: 'Ta bort den här filen',
          booleanCheckedLabel: 'Ja',
          booleanUncheckedLabel: 'Nej',
          confirmRemoveFile: 'Är du säker på att du vill ta bort den här filen: {0}?',
          confirmRemoveAllFiles: 'Är du säker på att du vill ta bort alla filer?',
          questionTitlePatternText: 'Frågetitel',
        };
      (r.a.locales.sv = i), (r.a.localeNames.sv = 'svenska');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Бозгашт',
          pageNextText: 'Оянда',
          completeText: 'Иҷро шуд',
          startSurveyText: 'Оғоз',
          otherItemText: 'Дигар (лутфан тавсиф кунед)',
          noneItemText: 'Не',
          selectAllItemText: 'Ҳамаро интихоб кардан',
          progressText: 'Саҳифаи {0} аз {1}',
          emptySurvey: 'Ягон савол вуҷуд надорад.',
          completingSurvey: 'Ташаккур барои пур кардани саволнома!',
          completingSurveyBefore: 'Шумо аллакай ин пурсишро анҷом додаед.',
          loadingSurvey: 'Боргирӣ аз сервер...',
          optionsCaption: 'Интихоб кардан...',
          value: 'қиммат',
          requiredError: 'Илтимос, ба савол ҷавоб диҳед.',
          requiredErrorInPanel: 'Илтимос, ақалан ба як савол ҷавоб диҳед.',
          requiredInAllRowsError: 'Илтимос, ба ҳамаи саволҳо дар ҳамаи сатрҳо ҷавоб диҳед.',
          numericError: 'Ҷавоб бояд рақам бошад.',
          textMinLength: 'Илтимос, аз {0} зиёдтар рамз ворид кунед.',
          textMaxLength: 'Илтимос, аз {0} камтар рамз ворид кунед.',
          textMinMaxLength: 'Илтимос, аз {0} зиёдтар ва аз {1} камтар рамз ворид кунед.',
          minRowCountError: 'Илтимос, на камтар аз {0} сатр пур кунед.',
          minSelectError: 'Илтимос, ақалан {0} вариант интихоб кунед.',
          maxSelectError: 'Илтимос, на зиёдтар аз {0} вариант интихоб кунед.',
          numericMinMax: "'{0}' бояд на кам аз {1} ва на бисёр аз {2} бошад",
          numericMin: "'{0}' бояд на кам аз {1} бошад",
          numericMax: "'{0}' бояд на зиёд аз {1} бошад",
          invalidEmail: 'Илтимос, почтаи электронии воқеиро ворид кунед.',
          invalidExpression: "Ифодаи {0} бояд 'true' баргардонад.",
          urlRequestError: "Дархост хатогӣ бозгардонд '{0}'. {1}",
          urlGetChoicesError:
            "Ҷавоб ба дархост холӣ омад ё хосияти 'path' нодуруст муайян карда шудааст",
          exceedMaxSize: 'Андозаи файл бояд на калон аз {0} бошад.',
          otherRequiredError: "Илтимос, ба майдони 'Дигар' додаҳоро ворид кунед",
          uploadingFile:
            'Файли шумо бор шуда истодааст. Якчанд сония интизор шавед ва бори дигар кӯшиш кунед.',
          loadingFile: 'Боркунӣ...',
          chooseFile: 'Файл(ҳо)-ро интихоб кунед...',
          confirmDelete: 'Шумо мутмаин ҳастед, ки мехоҳед воридро тоза кунед?',
          keyDuplicationError: 'Ин арзиш бояд беназир бошад.',
          addColumn: 'Иловаи сутун',
          addRow: 'Иловаи сатр',
          removeRow: 'Нест кардан',
          addPanel: 'Илова кардан',
          removePanel: 'Нест кардан',
          choices_Item: 'Вариант',
          matrix_column: 'Сутун',
          matrix_row: 'Сатр',
          savingData: 'Натиҷа ба сервер сабт шуда истодаанд...',
          savingDataError: 'Хатогӣ ба амал омад, натиҷа сабт нашуд.',
          savingDataSuccess: 'Натиҷа бомуваффакият сабт шуд!',
          saveAgainButton: 'Бори дигар кӯшиш карданд',
          timerMin: 'дақ',
          timerSec: 'сон',
          timerSpentAll: 'Шумо {0} дар ин саҳифа ва {1} дар умум сарф кардед.',
          timerSpentPage: 'Шумо {0} дар ин саҳифа сарф кардед.',
          timerSpentSurvey: 'Шумо {0} дар ин тест сарф намудед.',
          timerLimitAll:
            'Шумо {0} аз {1} дар ин саҳифа ва {2} аз {3} дар умум сарф кардед дар дохили ин тест.',
          timerLimitPage: 'Шумо {0} аз {1} дар ин саҳифа сарф кардед.',
          timerLimitSurvey: 'Шумо {0} аз {1} дар ҳамаи тест сарф кардед.',
          cleanCaption: 'Тоза кардан',
          clearCaption: 'Тоза кардан',
          removeFileCaption: 'Файлро нест кардан',
        };
      (r.a.locales.tg = i), (r.a.localeNames.tg = 'тоҷикӣ');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'ก่อนหน้า',
          pageNextText: 'ถัดไป',
          completeText: 'สำเร็จ',
          previewText: 'ดูตัวอย่าง',
          editText: 'แก้ไข',
          startSurveyText: 'เริ่ม',
          otherItemText: 'อื่นๆ (โปรดระบุ)',
          noneItemText: 'ไม่มี',
          selectAllItemText: 'เลือกทั้งหมด',
          progressText: 'หน้าที่ {0} จาก {1}',
          panelDynamicProgressText: 'รายการที่ {0} จาก {1}',
          questionsProgressText: 'คำตอบที่ {0}/{1} จำนวนคำถาม',
          emptySurvey: 'ไม่มีหน้าเพจที่มองเห็น หรือ คำถามใน survey นี้',
          completingSurvey: 'ขอบคุณที่ทำ survey จนเสร็จ',
          completingSurveyBefore: 'รายการของเราแสดงว่าคุณได้ทำ survey เสร็จเรียบร้อยแล้ว',
          loadingSurvey: 'กำลังโหลด Survey...',
          optionsCaption: 'เลือก...',
          value: 'ข้อมูล',
          requiredError: 'กรุณาตอบคำถาม',
          requiredErrorInPanel: 'กรุณาตอบขั้นต่ำหนึ่งคำถาม',
          requiredInAllRowsError: 'กรุณาตอบคำถามในทุกๆแถว',
          numericError: 'ข้อมูลที่ใส่ต้องเป็นตัวเลข',
          textMinLength: 'กรุณาใส่ขั้นต่ำจำนวน {0} ตัวอักษร',
          textMaxLength: 'กรุณาใส่ไม่เกินจำนวน {0} ตัวอักษร',
          textMinMaxLength: 'กรุณาใส่ขั้นต่ำจำนวน {0} และไม่เกินจำนวน {1} ตัวอักษร',
          minRowCountError: 'กรุณาใส่ขั้นต่ำจำนวน {0} แถว',
          minSelectError: 'กรุณาเลือกอย่างน้อย {0} รายการ',
          maxSelectError: 'กรุณาเลือกไม่เกิน {0} รายการ',
          numericMinMax: "'{0}' ต้องมากกว่าหรือเท่ากับ {1} และน้อยกว่าหรือเท่ากับ {2}",
          numericMin: "'{0}' ต้องมากกว่าหรือเท่ากับ {1}",
          numericMax: "'{0}' น้อยกว่าหรือเท่ากับ {1}",
          invalidEmail: 'กรุณาใส่อีเมล์แอดเดรสที่ถูกต้อง',
          invalidExpression: "The expression: {0} ต้องรีเทิร์น 'true'.",
          urlRequestError: "รีเควสรีเทิร์น error '{0}'. {1}",
          urlGetChoicesError: "รีเควสรีเทิร์นข้อมูลว่างเปล่า หรือ 'path' property ไม่ถูกต้อง",
          exceedMaxSize: 'ขนาดไฟล์ต้องไม่เกิน {0}.',
          otherRequiredError: 'กรุณาใส่ค่าอื่น',
          uploadingFile: 'ไฟล์ของคุณกำลังอัพโหลดอยู่. กรุณารอสักครู่แล้วทำการลองอีกครั้ง',
          loadingFile: 'กำลังโหลด...',
          chooseFile: 'เลือกไฟล์...',
          noFileChosen: 'ไม่ไฟล์ที่เลือก',
          confirmDelete: 'คุณต้องการลบรายการนี้จริงหรือไม่?',
          keyDuplicationError: 'ข้อมูลนี้ต้องเป็น unique.',
          addColumn: 'เพิ่มคอลัมน์',
          addRow: 'เพิ่มแถว',
          removeRow: 'ลบ',
          addPanel: 'เพิ่ม',
          removePanel: 'ลบ',
          choices_Item: 'ชิ้น',
          matrix_column: 'คอลัมน์',
          matrix_row: 'แถว',
          savingData: 'ผลลัพท์กำลังบันทึกลงที่เซิร์ฟเวอร์...',
          savingDataError: 'มีความผิดพลาดเกิดขึ้นส่งผลให้ไม่สามารถบันทึกผลได้',
          savingDataSuccess: 'บันทึกสำเร็จแล้ว',
          saveAgainButton: 'รบกวนลองอีกครั้ง',
          timerMin: 'นาที',
          timerSec: 'วินาที',
          timerSpentAll: 'คุณใช้เวลา {0} บนหน้านี้และ {1} รวมทั้งหมด',
          timerSpentPage: 'คุณใช้เวลา {0} บนหน้านี้',
          timerSpentSurvey: 'คุณใช้เวลา {0} รวมทั้งหมด',
          timerLimitAll: 'คุณใช้เวลา {0} ของ {1} บนหน้านี้และ {2} ของ {3} รวมทั้งหมด',
          timerLimitPage: 'คุณใช้เวลา {0} ของ {1} บนหน้านี้',
          timerLimitSurvey: 'คุณใช้เวลา {0} ของ {1} รวมทั้งหมด',
          cleanCaption: 'คลีน',
          clearCaption: 'เคลียร์',
          chooseFileCaption: 'เลือกไฟล์',
          removeFileCaption: 'นำไฟล์นี้ออก',
          booleanCheckedLabel: 'ใช่',
          booleanUncheckedLabel: 'ไม่ใช่',
          confirmRemoveFile: 'คุณแน่ใจที่จะนำไฟล์นี้ออกใช่หรือไม่: {0}?',
          confirmRemoveAllFiles: 'คุณแน่ใจที่จะนำไฟล์ทั้งหมดออกใช่หรือไม่',
          questionTitlePatternText: 'ชื่อคำถาม',
        };
      (r.a.locales.th = i), (r.a.localeNames.th = 'ไทย');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: '上一頁',
          pageNextText: '下一頁',
          completeText: '提交問卷',
          otherItemText: '填寫其他答案',
          progressText: '第 {0} 頁, 共 {1} 頁',
          emptySurvey: '問卷中沒有問題或頁面',
          completingSurvey: '感謝您的參與!',
          loadingSurvey: '問卷載入中...',
          optionsCaption: '請選擇...',
          requiredError: '請填寫此問題',
          requiredInAllRowsError: '請填寫所有行中問題',
          numericError: '答案必須是個數字',
          textMinLength: '答案長度至少 {0} 個字元',
          textMaxLength: '答案長度不能超過 {0} 個字元',
          textMinMaxLength: '答案長度必須在 {0} - {1} 個字元之間',
          minRowCountError: '最少需要填寫 {0} 行答案',
          minSelectError: '最少需要選擇 {0} 項答案',
          maxSelectError: '最多只能選擇 {0} 項答案',
          numericMinMax: "答案 '{0}' 必須大於等於 {1} 且小於等於 {2}",
          numericMin: "答案 '{0}' 必須大於等於 {1}",
          numericMax: "答案 '{0}' 必須小於等於 {1}",
          invalidEmail: '請輸入有效的 Email 地址',
          urlRequestError: "載入選項時發生錯誤 '{0}': {1}",
          urlGetChoicesError: '未能載入有效的選項或請求參數路徑有誤',
          exceedMaxSize: '文件大小不能超過 {0}',
          otherRequiredError: '請完成其他問題',
          uploadingFile: '文件上傳中... 請耐心等待幾秒後重試',
          addRow: '添加答案',
          removeRow: '刪除答案',
          choices_Item: '選項',
          matrix_column: '列',
          matrix_row: '行',
          savingData: '正在將結果保存到服務器...',
          savingDataError: '在保存結果過程中發生了錯誤，結果未能保存',
          savingDataSuccess: '結果保存成功!',
          saveAgainButton: '請重試',
        };
      (r.a.locales['zh-tw'] = i), (r.a.localeNames['zh-tw'] = '繁體中文');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Geri',
          pageNextText: 'İleri',
          completeText: 'Anketi Tamamla',
          previewText: 'Ön izleme',
          editText: 'Düzenle',
          startSurveyText: 'Başlat',
          otherItemText: 'Diğer (açıklayınız)',
          noneItemText: 'Yok',
          selectAllItemText: 'Hepsini seç',
          progressText: 'Sayfa {0} / {1}',
          panelDynamicProgressText: 'Kayıt {0} / {1}',
          questionsProgressText: 'Soruları cevapladı {0} / {1}',
          emptySurvey: 'Ankette görüntülenecek sayfa ya da soru mevcut değil.',
          completingSurvey: 'Anketimizi tamamladığınız için teşekkür ederiz.',
          completingSurveyBefore: 'Kayıtlarımız, bu anketi zaten tamamladığınızı gösteriyor.',
          loadingSurvey: 'Anket sunucudan yükleniyor ...',
          optionsCaption: 'Seçiniz ...',
          value: 'değer',
          requiredError: 'Lütfen soruya cevap veriniz',
          requiredErrorInPanel: 'Lütfen en az bir soruyu yanıtlayın.',
          requiredInAllRowsError: 'Lütfen tüm satırlardaki soruları cevaplayınız.',
          numericError: 'Girilen değer numerik olmalıdır',
          textMinLength: 'En az {0} sembol giriniz.',
          textMaxLength: 'Lütfen {0} karakterden az girin.',
          textMinMaxLength: 'Lütfen {0} ’den fazla ve {1} ’den az karakter girin.',
          minRowCountError: 'Lütfen en az {0} satırı doldurun.',
          minSelectError: 'Lütfen en az {0} seçeneği seçiniz.',
          maxSelectError: 'Lütfen {0} adetten fazla seçmeyiniz.',
          numericMinMax: "The '{0}' should be equal or more than {1} and equal or less than {2}",
          numericMin: "'{0}' değeri {1} değerine eşit veya büyük olmalıdır",
          numericMax: "'{0}' değeri {1} değerine eşit ya da küçük olmalıdır.",
          invalidEmail: 'Lütfen geçerli bir eposta adresi giriniz.',
          invalidExpression: "İfade: {0} 'true' döndürmelidir.",
          urlRequestError: "Talebi şu hatayı döndü '{0}'. {1}",
          urlGetChoicesError: "Talep herhangi bir veri dönmedi ya da 'path' özelliği hatalı.",
          exceedMaxSize: 'Dosya boyutu {0} değerini geçemez.',
          otherRequiredError: 'Lütfen diğer değerleri giriniz.',
          uploadingFile: 'Dosyanız yükleniyor. LÜtfen birkaç saniye bekleyin ve tekrar deneyin.',
          loadingFile: 'Yükleniyor...',
          chooseFile: 'Dosyaları seçin ...',
          noFileChosen: 'Dosya seçili değil',
          confirmDelete: 'Kaydı silmek istiyor musunuz?',
          keyDuplicationError: 'Bu değer benzersiz olmalıdır.',
          addColumn: 'Sütun ekleyin',
          addRow: 'Satır Ekle',
          removeRow: 'Kaldır',
          addPanel: 'Yeni ekle',
          removePanel: 'Kaldırmak',
          choices_Item: 'eşya',
          matrix_column: 'Sütun',
          matrix_row: 'Kürek çekmek',
          savingData: 'Sonuçlar sunucuya kaydediliyor ...',
          savingDataError: 'Bir hata oluştu ve sonuçları kaydedemedik.',
          savingDataSuccess: 'Sonuçlar başarıyla kaydedildi!',
          saveAgainButton: 'Tekrar deneyin',
          timerMin: 'min',
          timerSec: 'saniye',
          timerSpentAll: 'Bu sayfada {0} ve toplamda {1} harcadınız.',
          timerSpentPage: 'Bu sayfaya {0} harcadınız.',
          timerSpentSurvey: 'Toplamda {0} harcadınız.',
          timerLimitAll: 'Bu sayfaya {0} / {1} ve toplamda {2} / {3} harcadınız.',
          timerLimitPage: 'Bu sayfaya {0} / {1} harcadınız.',
          timerLimitSurvey: 'Toplamda {0} / {1} harcadınız.',
          cleanCaption: 'Temiz',
          clearCaption: 'Açık',
          chooseFileCaption: 'Dosya seçin',
          removeFileCaption: 'Bu dosyayı kaldır',
          booleanCheckedLabel: 'Evet',
          booleanUncheckedLabel: 'Hayır',
          confirmRemoveFile: 'Bu dosyayı kaldırmak istediğinizden emin misiniz: {0}?',
          confirmRemoveAllFiles: 'Tüm dosyaları kaldırmak istediğinizden emin misiniz?',
          questionTitlePatternText: 'Soru başlığı',
        };
      (r.a.locales.tr = i), (r.a.localeNames.tr = 'türkçe');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Назад',
          pageNextText: 'Далі',
          completeText: 'Завершити',
          previewText: 'Попередній перегляд',
          editText: 'Редагувати',
          startSurveyText: 'Почати',
          otherItemText: 'Інше (будь ласка, опишіть)',
          noneItemText: 'Жоден',
          selectAllItemText: 'Вибрати все',
          progressText: 'Сторінка {0} з {1}',
          panelDynamicProgressText: 'Запис {0} із {1}',
          questionsProgressText: 'Відповіли на {0}/{1} питань',
          emptySurvey: 'Немає жодного питання.',
          completingSurvey: 'Дякуємо Вам за заповнення анкети!',
          completingSurveyBefore: 'Ви вже проходили це опитування.',
          loadingSurvey: 'Завантаження опитування...',
          optionsCaption: 'Вибрати...',
          value: 'значення',
          requiredError: 'Будь ласка, дайте відповідь.',
          requiredErrorInPanel: 'Будь ласка, дайте відповідь хоча б на одне питання.',
          requiredInAllRowsError: 'Будь ласка, дайте відповідь на питання в кожному рядку.',
          numericError: 'Відповідь повинна бути числом.',
          textMinLength: 'Будь ласка введіть більше {0} символів.',
          textMaxLength: 'Будь ласка введіть менше {0} символів.',
          textMinMaxLength: 'Будь ласка введіть більше {0} и менше {1} символів.',
          minRowCountError: 'Будь ласка, заповніть не менше {0} рядків.',
          minSelectError: 'Будь ласка, виберіть хоча б {0} варіантів.',
          maxSelectError: 'Будь ласка, виберіть не більше {0} варіантів.',
          numericMinMax: "'{0}' повинно бути не менше ніж {1}, і не більше ніж {2}",
          numericMin: "'{0}' повинно бути не менше ніж {1}",
          numericMax: "'{0}' повинно бути не більше ніж {1}",
          invalidEmail: 'Будь ласка, введіть дійсну адресу електронної пошти.',
          invalidExpression: "Вираз {0} повинен повертати 'true'.",
          urlRequestError: "Запит повернув помилку '{0}'. {1}",
          urlGetChoicesError:
            "Відповідь на запит повернулась порожньою або властивіть 'path' вказано невірно",
          exceedMaxSize: 'Розмір файлу не повинен перевищувати {0}.',
          otherRequiredError: "Будь ласка, введіть дані в поле 'Інше'",
          uploadingFile: 'Ваш файл завантажується. Зачекайте декілька секунд і спробуйте знову.',
          loadingFile: 'Завантаження...',
          chooseFile: 'Виберіть файл(и)...',
          noFileChosen: 'Файл не вибрано',
          confirmDelete: 'Ви хочете видалити запис?',
          keyDuplicationError: 'Це значення повинно бути унікальним.',
          addColumn: 'Додати колонку',
          addRow: 'Додати рядок',
          removeRow: 'Видалити',
          addPanel: 'Додати нову',
          removePanel: 'Видалити',
          choices_Item: 'Варіант',
          matrix_column: 'Колонка',
          matrix_row: 'Рядок',
          savingData: 'Результати зберігаються на сервер...',
          savingDataError: 'Відбулася помилка, результат не був збережений.',
          savingDataSuccess: 'Резвультат успішно збережений!',
          saveAgainButton: 'Спробувати знову',
          timerMin: 'хв',
          timerSec: 'сек',
          timerSpentAll: 'Ви витратили {0} на цій сторінці і {1} загалом.',
          timerSpentPage: 'Ви витратили {0} на цій сторінці.',
          timerSpentSurvey: 'Ви витратили {0} протягом тесту.',
          timerLimitAll: 'Ви витратили {0} з {1} на цій сторінці і {2} з {3} для всього тесту.',
          timerLimitPage: 'Ви витратили {0} з {1} на цій сторінці.',
          timerLimitSurvey: 'Ви витратили {0} з {1} для всього тесту.',
          cleanCaption: 'Очистити',
          clearCaption: 'Очистити',
          chooseFileCaption: 'Виберіть файл',
          removeFileCaption: 'Видалити файл',
          booleanCheckedLabel: 'Так',
          booleanUncheckedLabel: 'Ні',
          confirmRemoveFile: 'Ви впевнені, що хочете видалити цей файл: {0}?',
          confirmRemoveAllFiles: 'Ви впевнені, що хочете видалити всі файли?',
          questionTitlePatternText: 'Назва запитання',
        };
      (r.a.locales.ua = i), (r.a.localeNames.ua = 'українська');
    },
    function (e, t, n) {
      'use strict';
      var r = n(2),
        i = {
          pagePrevText: 'Blaenorol',
          pageNextText: 'Nesaf',
          completeText: 'Cwblhau',
          previewText: 'Rhagolwg',
          editText: 'Golygu',
          startSurveyText: 'Dechrau',
          otherItemText: 'Arall (disgrifiwch)',
          noneItemText: 'Dim',
          selectAllItemText: 'Dewis y Cyfan ',
          progressText: 'Tudalen {0} o {1}',
          panelDynamicProgressText: 'Cofnod {0} o {1}',
          questionsProgressText: 'Wedi ateb {0}/{1} cwestiwn',
          emptySurvey: 'Does dim modd gweld tudalen na chwestiwn yn yr arolwg.',
          completingSurvey: 'Diolch am lenwi’r holiadur!',
          completingSurveyBefore:
            'Rydych chi wedi llenwi’r arolwg hwn yn barod yn ôl ein cofnodion.',
          loadingSurvey: 'Wrthi’n Llwytho’r Arolwg...',
          optionsCaption: 'Dewiswch...',
          value: 'gwerth',
          requiredError: 'Atebwch y cwestiwn.',
          requiredErrorInPanel: 'Atebwch o leiaf un cwestiwn.',
          requiredInAllRowsError: 'Atebwch y cwestiynau ym mhob rhes.',
          numericError: 'Dylai’r gwerth fod yn rhif.',
          textMinLength: 'Rhowch o leiaf {0} nod.',
          textMaxLength: 'Rhowch lai na {0} nod.',
          textMinMaxLength: 'Rhowch o leiaf {0} nod ond dim mwy na {1}.',
          minRowCountError: 'Llenwch o leiaf {0} rhes.',
          minSelectError: 'Dewiswch o leiaf {0} amrywiolyn.',
          maxSelectError: 'Peidiwch â dewis mwy na {0} amrywiolyn.',
          numericMinMax: "Dylai’r '{0}' fod yr un fath â {1} neu’n fwy, a’r fath â {2} neu’n llai",
          numericMin: "Dylai’r '{0}' fod yr un fath â {1} neu’n fwy",
          numericMax: "Dylai’r '{0}' fod yr un fath â {1} neu’n llai",
          invalidEmail: 'Rhowch gyfeiriad e-bost dilys.',
          invalidExpression: "Dylai’r mynegiad {0} arwain at 'true'.",
          urlRequestError: "Roedd y cais wedi arwain at y gwall '{0}'. {1}",
          urlGetChoicesError:
            'Roedd y cais wedi arwain at ddata gwag neu mae priodwedd y ‘path’ yn anghywir ',
          exceedMaxSize: 'Ddylai’r ffeil ddim bod yn fwy na {0}.',
          otherRequiredError: 'Rhowch y gwerth arall.',
          uploadingFile:
            'Mae eich ffeil wrthi’n llwytho i fyny. Arhoswch ychydig o eiliadau a rhoi cynnig arall arni.',
          loadingFile: 'Wrthi’n llwytho...',
          chooseFile: 'Dewiswch ffeil(iau)...',
          noFileChosen: 'Heb ddewis ffeil ',
          confirmDelete: 'Ydych chi am ddileu’r cofnod?',
          keyDuplicationError: 'Dylai’r gwerth hwn fod yn unigryw.',
          addColumn: 'Ychwanegu colofn ',
          addRow: 'Ychwanegu rhes',
          removeRow: 'Tynnu',
          addPanel: 'Ychwanegu o’r newydd',
          removePanel: 'Tynnu',
          choices_Item: 'eitem',
          matrix_column: 'Colofn',
          matrix_row: 'Rhes',
          savingData: 'Mae’r canlyniadau’n cael eu cadw ar y gweinydd...',
          savingDataError: 'Roedd gwall a doedd dim modd cadw’r canlyniadau.',
          savingDataSuccess: 'Wedi llwyddo i gadw’r canlyniadau!',
          saveAgainButton: 'Rhowch gynnig arall arni',
          timerMin: 'mun',
          timerSec: 'eil',
          timerSpentAll: 'Rydych chi wedi treulio {0} ar y dudalen hon a {1} gyda’i gilydd.',
          timerSpentPage: 'Rydych chi wedi treulio {0} ar y dudalen hon.',
          timerSpentSurvey: 'Rydych chi wedi treulio {0} gyda’i gilydd.',
          timerLimitAll:
            'Rydych chi wedi treulio {0} o {1} ar y dudalen hon a {2} o {3} gyda’i gilydd.',
          timerLimitPage: 'Rydych chi wedi treulio {0} o {1} ar y dudalen hon.',
          timerLimitSurvey: 'Rydych chi wedi treulio {0} o {1} gyda’i gilydd.',
          cleanCaption: 'Glanhau',
          clearCaption: 'Clirio',
          chooseFileCaption: 'Dewiswch ffeil ',
          removeFileCaption: 'Tynnu’r ffeil hon ',
          booleanCheckedLabel: 'Iawn',
          booleanUncheckedLabel: 'Na',
          confirmRemoveFile: 'Ydych chi’n siŵr eich bod am dynnu’r ffeil hon: {0}?',
          confirmRemoveAllFiles: 'Ydych chi’n siŵr eich bod am dynnu pob ffeil?',
          questionTitlePatternText: 'Teitl y Cwestiwn ',
        };
      (r.a.locales.cy = i), (r.a.localeNames.cy = 'cymraeg');
    },
    function (e, t, n) {
      'use strict';
      var r = n(0),
        i = n(4),
        o = n(6),
        a = n(1),
        s = n(10),
        l = n(27),
        u = n(16),
        c = n(7);
      n.d(t, 'g', function () {
        return p;
      }),
        n.d(t, 'a', function () {
          return d;
        }),
        n.d(t, 'd', function () {
          return h;
        }),
        n.d(t, 'b', function () {
          return f;
        }),
        n.d(t, 'c', function () {
          return m;
        }),
        n.d(t, 'f', function () {
          return y;
        }),
        n.d(t, 'e', function () {
          return v;
        });
      var p = (function (e) {
          function t() {
            var t = e.call(this) || this;
            t.usedNames = [];
            var n = t;
            return (
              t.registerFunctionOnPropertiesValueChanged(
                ['operator', 'value', 'name'],
                function () {
                  n.oldPropertiesChanged();
                }
              ),
              t.registerFunctionOnPropertyValueChanged('expression', function () {
                n.onExpressionChanged();
              }),
              t
            );
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t, 'operators', {
              get: function () {
                return null != t.operatorsValue
                  ? t.operatorsValue
                  : ((t.operatorsValue = {
                      empty: function (e, t) {
                        return !e;
                      },
                      notempty: function (e, t) {
                        return !!e;
                      },
                      equal: function (e, t) {
                        return e == t;
                      },
                      notequal: function (e, t) {
                        return e != t;
                      },
                      contains: function (e, t) {
                        return e && e.indexOf && e.indexOf(t) > -1;
                      },
                      notcontains: function (e, t) {
                        return !e || !e.indexOf || -1 == e.indexOf(t);
                      },
                      greater: function (e, t) {
                        return e > t;
                      },
                      less: function (e, t) {
                        return e < t;
                      },
                      greaterorequal: function (e, t) {
                        return e >= t;
                      },
                      lessorequal: function (e, t) {
                        return e <= t;
                      },
                    }),
                    t.operatorsValue);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getType = function () {
              return 'triggerbase';
            }),
            (t.prototype.toString = function () {
              var e = this.getType().replace('trigger', ''),
                t = this.expression ? this.expression : this.buildExpression();
              return t && (e += ', ' + t), e;
            }),
            Object.defineProperty(t.prototype, 'operator', {
              get: function () {
                return this.getPropertyValue('operator', 'equal');
              },
              set: function (e) {
                e &&
                  ((e = e.toLowerCase()), t.operators[e] && this.setPropertyValue('operator', e));
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'value', {
              get: function () {
                return this.getPropertyValue('value', null);
              },
              set: function (e) {
                this.setPropertyValue('value', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'name', {
              get: function () {
                return this.getPropertyValue('name', '');
              },
              set: function (e) {
                this.setPropertyValue('name', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'expression', {
              get: function () {
                return this.getPropertyValue('expression', '');
              },
              set: function (e) {
                this.setPropertyValue('expression', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.checkExpression = function (e, t, n) {
              void 0 === n && (n = null),
                this.isCheckRequired(e) && this.conditionRunner && this.perform(t, n);
            }),
            (t.prototype.check = function (e) {
              t.operators[this.operator](e, this.value)
                ? this.onSuccess({}, null)
                : this.onFailure();
            }),
            (t.prototype.perform = function (e, t) {
              var n = this;
              (this.conditionRunner.onRunComplete = function (r) {
                n.triggerResult(r, e, t);
              }),
                this.conditionRunner.run(e, t);
            }),
            (t.prototype.triggerResult = function (e, t, n) {
              e ? this.onSuccess(t, n) : this.onFailure();
            }),
            (t.prototype.onSuccess = function (e, t) {}),
            (t.prototype.onFailure = function () {}),
            (t.prototype.endLoadingFromJson = function () {
              e.prototype.endLoadingFromJson.call(this), this.oldPropertiesChanged();
            }),
            (t.prototype.oldPropertiesChanged = function () {
              this.onExpressionChanged();
            }),
            (t.prototype.onExpressionChanged = function () {
              (this.usedNames = []), (this.hasFunction = !1), (this.conditionRunner = null);
            }),
            (t.prototype.buildExpression = function () {
              return this.name
                ? i.a.isValueEmpty(this.value) && this.isRequireValue
                  ? ''
                  : '{' + this.name + '} ' + this.operator + ' ' + l.h.toOperandString(this.value)
                : '';
            }),
            (t.prototype.isCheckRequired = function (e) {
              if (!e) return !1;
              if ((this.buildUsedNames(), !0 === this.hasFunction)) return !0;
              for (var t = new u.a(), n = 0; n < this.usedNames.length; n++) {
                var r = this.usedNames[n];
                if (e.hasOwnProperty(r)) return !0;
                var o = t.getFirstName(r);
                if (e.hasOwnProperty(o)) {
                  if (r == o) return !0;
                  var a = e[o];
                  if (!a.hasOwnProperty('oldValue') || !a.hasOwnProperty('newValue')) return !0;
                  var s = {};
                  s[o] = a.oldValue;
                  var l = t.getValue(r, s);
                  s[o] = a.newValue;
                  var c = t.getValue(r, s);
                  return !i.a.isTwoValueEquals(l, c);
                }
              }
              return !1;
            }),
            (t.prototype.buildUsedNames = function () {
              if (!this.conditionRunner) {
                var e = this.expression;
                e || (e = this.buildExpression()),
                  e &&
                    ((this.conditionRunner = new s.a(e)),
                    (this.hasFunction = this.conditionRunner.hasFunction()),
                    (this.usedNames = this.conditionRunner.getVariables()));
              }
            }),
            Object.defineProperty(t.prototype, 'isRequireValue', {
              get: function () {
                return 'empty' !== this.operator && 'notempty' != this.operator;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.operatorsValue = null),
            t
          );
        })(o.b),
        d = (function (e) {
          function t() {
            var t = e.call(this) || this;
            return (t.ownerValue = null), t;
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'owner', {
              get: function () {
                return this.ownerValue;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.setOwner = function (e) {
              this.ownerValue = e;
            }),
            Object.defineProperty(t.prototype, 'isOnNextPage', {
              get: function () {
                return !1;
              },
              enumerable: !0,
              configurable: !0,
            }),
            t
          );
        })(p),
        h = (function (e) {
          function t() {
            var t = e.call(this) || this;
            return (t.pages = []), (t.questions = []), t;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'visibletrigger';
            }),
            (t.prototype.onSuccess = function (e, t) {
              this.onTrigger(this.onItemSuccess);
            }),
            (t.prototype.onFailure = function () {
              this.onTrigger(this.onItemFailure);
            }),
            (t.prototype.onTrigger = function (e) {
              if (this.owner)
                for (
                  var t = this.owner.getObjects(this.pages, this.questions), n = 0;
                  n < t.length;
                  n++
                )
                  e(t[n]);
            }),
            (t.prototype.onItemSuccess = function (e) {
              e.visible = !0;
            }),
            (t.prototype.onItemFailure = function (e) {
              e.visible = !1;
            }),
            t
          );
        })(d),
        f = (function (e) {
          function t() {
            return e.call(this) || this;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'completetrigger';
            }),
            Object.defineProperty(t.prototype, 'isOnNextPage', {
              get: function () {
                return !c.a.executeCompleteTriggerOnValueChanged;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onSuccess = function (e, t) {
              this.owner && this.owner.setCompleted();
            }),
            t
          );
        })(d),
        m = (function (e) {
          function t() {
            return e.call(this) || this;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'setvaluetrigger';
            }),
            Object.defineProperty(t.prototype, 'setToName', {
              get: function () {
                return this.getPropertyValue('setToName', '');
              },
              set: function (e) {
                this.setPropertyValue('setToName', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'setValue', {
              get: function () {
                return this.getPropertyValue('setValue');
              },
              set: function (e) {
                this.setPropertyValue('setValue', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isVariable', {
              get: function () {
                return this.getPropertyValue('isVariable', !1);
              },
              set: function (e) {
                this.setPropertyValue('isVariable', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onSuccess = function (e, t) {
              this.setToName &&
                this.owner &&
                this.owner.setTriggerValue(this.setToName, this.setValue, this.isVariable);
            }),
            t
          );
        })(d),
        g = (function (e) {
          function t() {
            return e.call(this) || this;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'skiptrigger';
            }),
            Object.defineProperty(t.prototype, 'gotoName', {
              get: function () {
                return this.getPropertyValue('gotoName', '');
              },
              set: function (e) {
                this.setPropertyValue('gotoName', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'isOnNextPage', {
              get: function () {
                return !c.a.executeSkipTriggerOnValueChanged;
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onSuccess = function (e, t) {
              this.gotoName && this.owner && this.owner.focusQuestion(this.gotoName);
            }),
            t
          );
        })(d),
        y = (function (e) {
          function t() {
            return e.call(this) || this;
          }
          return (
            n.i(r.b)(t, e),
            (t.prototype.getType = function () {
              return 'runexpressiontrigger';
            }),
            Object.defineProperty(t.prototype, 'setToName', {
              get: function () {
                return this.getPropertyValue('setToName', '');
              },
              set: function (e) {
                this.setPropertyValue('setToName', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'runExpression', {
              get: function () {
                return this.getPropertyValue('runExpression', '');
              },
              set: function (e) {
                this.setPropertyValue('runExpression', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.onSuccess = function (e, t) {
              var n = this;
              if (this.owner && this.runExpression) {
                var r = new s.b(this.runExpression);
                r.canRun &&
                  ((r.onRunComplete = function (e) {
                    n.onCompleteRunExpression(e);
                  }),
                  r.run(e, t));
              }
            }),
            (t.prototype.onCompleteRunExpression = function (e) {
              (this.setToName && void 0 === e) || this.owner.setTriggerValue(this.setToName, e, !1);
            }),
            t
          );
        })(d),
        v = (function (e) {
          function t() {
            return e.call(this) || this;
          }
          return (
            n.i(r.b)(t, e),
            Object.defineProperty(t.prototype, 'setToName', {
              get: function () {
                return this.getPropertyValue('setToName', '');
              },
              set: function (e) {
                this.setPropertyValue('setToName', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            Object.defineProperty(t.prototype, 'fromName', {
              get: function () {
                return this.getPropertyValue('fromName', '');
              },
              set: function (e) {
                this.setPropertyValue('fromName', e);
              },
              enumerable: !0,
              configurable: !0,
            }),
            (t.prototype.getType = function () {
              return 'copyvaluetrigger';
            }),
            (t.prototype.onSuccess = function (e, t) {
              this.setToName &&
                this.owner &&
                this.owner.copyTriggerValue(this.setToName, this.fromName);
            }),
            t
          );
        })(d);
      a.b.addClass('trigger', [
        { name: 'operator', default: 'equal', visible: !1 },
        { name: 'value', visible: !1 },
        'expression:condition',
      ]),
        a.b.addClass('surveytrigger', [{ name: 'name', visible: !1 }], null, 'trigger'),
        a.b.addClass(
          'visibletrigger',
          ['pages:pages', 'questions:questions'],
          function () {
            return new h();
          },
          'surveytrigger'
        ),
        a.b.addClass(
          'completetrigger',
          [],
          function () {
            return new f();
          },
          'surveytrigger'
        ),
        a.b.addClass(
          'setvaluetrigger',
          [
            { name: '!setToName:questionvalue' },
            {
              name: 'setValue:triggervalue',
              dependsOn: 'setToName',
              visibleIf: function (e) {
                return !!e && !!e.setToName;
              },
            },
            { name: 'isVariable:boolean', visible: !1 },
          ],
          function () {
            return new m();
          },
          'surveytrigger'
        ),
        a.b.addClass(
          'copyvaluetrigger',
          [{ name: '!setToName:questionvalue' }, { name: '!fromName:questionvalue' }],
          function () {
            return new v();
          },
          'surveytrigger'
        ),
        a.b.addClass(
          'skiptrigger',
          [{ name: '!gotoName:question' }],
          function () {
            return new g();
          },
          'surveytrigger'
        ),
        a.b.addClass(
          'runexpressiontrigger',
          [{ name: 'setToName:questionvalue' }, 'runExpression:expression'],
          function () {
            return new y();
          },
          'surveytrigger'
        );
    },
    function (e, t, n) {
      'use strict';
      Object.defineProperty(t, '__esModule', { value: !0 });
      var r = n(0),
        i = n(5),
        o = (n.n(i), n(41));
      n.d(t, 'Version', function () {
        return o.a;
      }),
        n.d(t, 'settings', function () {
          return o.b;
        }),
        n.d(t, 'Helpers', function () {
          return o.c;
        }),
        n.d(t, 'AnswerCountValidator', function () {
          return o.d;
        }),
        n.d(t, 'EmailValidator', function () {
          return o.e;
        }),
        n.d(t, 'NumericValidator', function () {
          return o.f;
        }),
        n.d(t, 'RegexValidator', function () {
          return o.g;
        }),
        n.d(t, 'SurveyValidator', function () {
          return o.h;
        }),
        n.d(t, 'TextValidator', function () {
          return o.i;
        }),
        n.d(t, 'ValidatorResult', function () {
          return o.j;
        }),
        n.d(t, 'ExpressionValidator', function () {
          return o.k;
        }),
        n.d(t, 'ValidatorRunner', function () {
          return o.l;
        }),
        n.d(t, 'ItemValue', function () {
          return o.m;
        }),
        n.d(t, 'Base', function () {
          return o.n;
        }),
        n.d(t, 'Event', function () {
          return o.o;
        }),
        n.d(t, 'SurveyError', function () {
          return o.p;
        }),
        n.d(t, 'SurveyElement', function () {
          return o.q;
        }),
        n.d(t, 'CalculatedValue', function () {
          return o.r;
        }),
        n.d(t, 'CustomError', function () {
          return o.s;
        }),
        n.d(t, 'AnswerRequiredError', function () {
          return o.t;
        }),
        n.d(t, 'OneAnswerRequiredError', function () {
          return o.u;
        }),
        n.d(t, 'RequreNumericError', function () {
          return o.v;
        }),
        n.d(t, 'ExceedSizeError', function () {
          return o.w;
        }),
        n.d(t, 'LocalizableString', function () {
          return o.x;
        }),
        n.d(t, 'HtmlConditionItem', function () {
          return o.y;
        }),
        n.d(t, 'UrlConditionItem', function () {
          return o.z;
        }),
        n.d(t, 'ChoicesRestfull', function () {
          return o.A;
        }),
        n.d(t, 'FunctionFactory', function () {
          return o.B;
        }),
        n.d(t, 'registerFunction', function () {
          return o.C;
        }),
        n.d(t, 'ConditionRunner', function () {
          return o.D;
        }),
        n.d(t, 'ExpressionRunner', function () {
          return o.E;
        }),
        n.d(t, 'Operand', function () {
          return o.F;
        }),
        n.d(t, 'Const', function () {
          return o.G;
        }),
        n.d(t, 'BinaryOperand', function () {
          return o.H;
        }),
        n.d(t, 'Variable', function () {
          return o.I;
        }),
        n.d(t, 'FunctionOperand', function () {
          return o.J;
        }),
        n.d(t, 'ArrayOperand', function () {
          return o.K;
        }),
        n.d(t, 'ConditionsParser', function () {
          return o.L;
        }),
        n.d(t, 'ProcessValue', function () {
          return o.M;
        }),
        n.d(t, 'JsonError', function () {
          return o.N;
        }),
        n.d(t, 'JsonIncorrectTypeError', function () {
          return o.O;
        }),
        n.d(t, 'JsonMetadata', function () {
          return o.P;
        }),
        n.d(t, 'JsonMetadataClass', function () {
          return o.Q;
        }),
        n.d(t, 'JsonMissingTypeError', function () {
          return o.R;
        }),
        n.d(t, 'JsonMissingTypeErrorBase', function () {
          return o.S;
        }),
        n.d(t, 'JsonObject', function () {
          return o.T;
        }),
        n.d(t, 'JsonObjectProperty', function () {
          return o.U;
        }),
        n.d(t, 'JsonRequiredPropertyError', function () {
          return o.V;
        }),
        n.d(t, 'JsonUnknownPropertyError', function () {
          return o.W;
        }),
        n.d(t, 'Serializer', function () {
          return o.X;
        }),
        n.d(t, 'MatrixDropdownCell', function () {
          return o.Y;
        }),
        n.d(t, 'MatrixDropdownColumn', function () {
          return o.Z;
        }),
        n.d(t, 'matrixDropdownColumnTypes', function () {
          return o._0;
        }),
        n.d(t, 'MatrixDropdownRowModelBase', function () {
          return o._1;
        }),
        n.d(t, 'QuestionMatrixDropdownModelBase', function () {
          return o._2;
        }),
        n.d(t, 'MatrixDropdownRowModel', function () {
          return o._3;
        }),
        n.d(t, 'QuestionMatrixDropdownModel', function () {
          return o._4;
        }),
        n.d(t, 'MatrixDynamicRowModel', function () {
          return o._5;
        }),
        n.d(t, 'QuestionMatrixDynamicModel', function () {
          return o._6;
        }),
        n.d(t, 'MatrixRowModel', function () {
          return o._7;
        }),
        n.d(t, 'MartrixCells', function () {
          return o._8;
        }),
        n.d(t, 'QuestionMatrixModel', function () {
          return o._9;
        }),
        n.d(t, 'MultipleTextItemModel', function () {
          return o._10;
        }),
        n.d(t, 'QuestionMultipleTextModel', function () {
          return o._11;
        }),
        n.d(t, 'PanelModel', function () {
          return o._12;
        }),
        n.d(t, 'PanelModelBase', function () {
          return o._13;
        }),
        n.d(t, 'QuestionRowModel', function () {
          return o._14;
        }),
        n.d(t, 'FlowPanelModel', function () {
          return o._15;
        }),
        n.d(t, 'PageModel', function () {
          return o._16;
        }),
        n.d(t, 'Question', function () {
          return o._17;
        }),
        n.d(t, 'QuestionNonValue', function () {
          return o._18;
        }),
        n.d(t, 'QuestionEmptyModel', function () {
          return o._19;
        }),
        n.d(t, 'QuestionCheckboxBase', function () {
          return o._20;
        }),
        n.d(t, 'QuestionSelectBase', function () {
          return o._21;
        }),
        n.d(t, 'QuestionCheckboxModel', function () {
          return o._22;
        }),
        n.d(t, 'QuestionCommentModel', function () {
          return o._23;
        }),
        n.d(t, 'QuestionDropdownModel', function () {
          return o._24;
        }),
        n.d(t, 'QuestionFactory', function () {
          return o._25;
        }),
        n.d(t, 'ElementFactory', function () {
          return o._26;
        }),
        n.d(t, 'QuestionFileModel', function () {
          return o._27;
        }),
        n.d(t, 'QuestionHtmlModel', function () {
          return o._28;
        }),
        n.d(t, 'QuestionRadiogroupModel', function () {
          return o._29;
        }),
        n.d(t, 'QuestionRatingModel', function () {
          return o._30;
        }),
        n.d(t, 'QuestionExpressionModel', function () {
          return o._31;
        }),
        n.d(t, 'QuestionTextModel', function () {
          return o._32;
        }),
        n.d(t, 'QuestionBooleanModel', function () {
          return o._33;
        }),
        n.d(t, 'QuestionImagePickerModel', function () {
          return o._34;
        }),
        n.d(t, 'QuestionImageModel', function () {
          return o._35;
        }),
        n.d(t, 'QuestionSignaturePadModel', function () {
          return o._36;
        }),
        n.d(t, 'QuestionPanelDynamicModel', function () {
          return o._37;
        }),
        n.d(t, 'QuestionPanelDynamicItem', function () {
          return o._38;
        }),
        n.d(t, 'SurveyTimer', function () {
          return o._39;
        }),
        n.d(t, 'SurveyProgressButtonsModel', function () {
          return o._40;
        }),
        n.d(t, 'SurveyModel', function () {
          return o._41;
        }),
        n.d(t, 'SurveyTrigger', function () {
          return o._42;
        }),
        n.d(t, 'SurveyTriggerComplete', function () {
          return o._43;
        }),
        n.d(t, 'SurveyTriggerSetValue', function () {
          return o._44;
        }),
        n.d(t, 'SurveyTriggerVisible', function () {
          return o._45;
        }),
        n.d(t, 'SurveyTriggerCopyValue', function () {
          return o._46;
        }),
        n.d(t, 'SurveyTriggerRunExpression', function () {
          return o._47;
        }),
        n.d(t, 'Trigger', function () {
          return o._48;
        }),
        n.d(t, 'SurveyWindowModel', function () {
          return o._49;
        }),
        n.d(t, 'TextPreProcessor', function () {
          return o._50;
        }),
        n.d(t, 'dxSurveyService', function () {
          return o._51;
        }),
        n.d(t, 'englishStrings', function () {
          return o._52;
        }),
        n.d(t, 'surveyLocalization', function () {
          return o._53;
        }),
        n.d(t, 'surveyStrings', function () {
          return o._54;
        }),
        n.d(t, 'QuestionCustomWidget', function () {
          return o._55;
        }),
        n.d(t, 'CustomWidgetCollection', function () {
          return o._56;
        }),
        n.d(t, 'QuestionCustomModel', function () {
          return o._57;
        }),
        n.d(t, 'QuestionCompositeModel', function () {
          return o._58;
        }),
        n.d(t, 'ComponentQuestionJSON', function () {
          return o._59;
        }),
        n.d(t, 'ComponentCollection', function () {
          return o._60;
        }),
        n.d(t, 'StylesManager', function () {
          return o._61;
        });
      var a = (n(40), n(0));
      n.d(t, '__assign', function () {
        return a.a;
      }),
        n.d(t, '__extends', function () {
          return a.b;
        }),
        n.d(t, '__decorate', function () {
          return a.c;
        }),
        n.d(t, '__spreadArrays', function () {
          return a.d;
        });
      var s = n(14);
      n.d(t, 'defaultStandardCss', function () {
        return s.a;
      });
      var l = n(37);
      n.d(t, 'defaultBootstrapCss', function () {
        return l.a;
      });
      var u = n(38);
      n.d(t, 'defaultBootstrapMaterialCss', function () {
        return u.a;
      });
      var c = n(39);
      n.d(t, 'modernCss', function () {
        return c.a;
      });
      var p = n(71);
      n.d(t, 'Model', function () {
        return p.a;
      });
      var d = n(20),
        h = n(21);
      n.d(t, 'ReactSurveyModel', function () {
        return f;
      }),
        n.d(t, 'SurveyNG', function () {
          return m;
        }),
        n.d(t, 'SurveyWindowNG', function () {
          return g;
        });
      var f = (function (e) {
          function t(t, n, r) {
            void 0 === t && (t = null), void 0 === n && (n = null), void 0 === r && (r = null);
            var i = e.call(this, t, n, r) || this;
            return (
              console.warn(
                'ReactSurveyModel is depricated in this context. Use Survey.Model instead.'
              ),
              i
            );
          }
          return n.i(r.b)(t, e), t;
        })(p.a),
        m = (function () {
          function e() {}
          return (
            (e.render = function (e, t) {
              var n = 'string' == typeof e ? document.getElementById(e) : e,
                r = t.model;
              r.updateSurvey(t), r.render(n);
            }),
            e
          );
        })(),
        g = (function () {
          function e() {}
          return (
            (e.render = function (e, t) {
              var n = t.model;
              n.updateSurvey(t);
              var r = new p.b(null, n);
              void 0 !== t.expanded && (r.isExpanded = t.expanded),
                void 0 !== t.isExpanded && (r.isExpanded = t.isExpanded),
                r.show();
            }),
            e
          );
        })();
      (h.a.platform = 'angular'), n.i(d.a)(i, h.a.platform);
    },
  ]);
});

/*! nouislider - 9.2.0 - 2017-01-11 10:35:34 */

(function (factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module.
    define([], factory);
  } else if (typeof exports === 'object') {
    // Node/CommonJS
    module.exports = factory();
  } else {
    // Browser globals
    window.noUiSlider = factory();
  }
})(function () {
  'use strict';

  var VERSION = '9.2.0';

  // Creates a node, adds it to target, returns the new node.
  function addNodeTo(target, className) {
    var div = document.createElement('div');
    addClass(div, className);
    target.appendChild(div);
    return div;
  }

  // Removes duplicates from an array.
  function unique(array) {
    return array.filter(function (a) {
      return !this[a] ? (this[a] = true) : false;
    }, {});
  }

  // Round a value to the closest 'to'.
  function closest(value, to) {
    return Math.round(value / to) * to;
  }

  // Current position of an element relative to the document.
  function offset(elem, orientation) {
    var rect = elem.getBoundingClientRect(),
      doc = elem.ownerDocument,
      docElem = doc.documentElement,
      pageOffset = getPageOffset();

    // getBoundingClientRect contains left scroll in Chrome on Android.
    // I haven't found a feature detection that proves this. Worst case
    // scenario on mis-match: the 'tap' feature on horizontal sliders breaks.
    if (/webkit.*Chrome.*Mobile/i.test(navigator.userAgent)) {
      pageOffset.x = 0;
    }

    return orientation
      ? rect.top + pageOffset.y - docElem.clientTop
      : rect.left + pageOffset.x - docElem.clientLeft;
  }

  // Checks whether a value is numerical.
  function isNumeric(a) {
    return typeof a === 'number' && !isNaN(a) && isFinite(a);
  }

  // Sets a class and removes it after [duration] ms.
  function addClassFor(element, className, duration) {
    if (duration > 0) {
      addClass(element, className);
      setTimeout(function () {
        removeClass(element, className);
      }, duration);
    }
  }

  // Limits a value to 0 - 100
  function limit(a) {
    return Math.max(Math.min(a, 100), 0);
  }

  // Wraps a variable as an array, if it isn't one yet.
  // Note that an input array is returned by reference!
  function asArray(a) {
    return Array.isArray(a) ? a : [a];
  }

  // Counts decimals
  function countDecimals(numStr) {
    numStr = String(numStr);
    var pieces = numStr.split('.');
    return pieces.length > 1 ? pieces[1].length : 0;
  }

  // http://youmightnotneedjquery.com/#add_class
  function addClass(el, className) {
    if (el.classList) {
      el.classList.add(className);
    } else {
      el.className += ' ' + className;
    }
  }

  // http://youmightnotneedjquery.com/#remove_class
  function removeClass(el, className) {
    if (el.classList) {
      el.classList.remove(className);
    } else {
      el.className = el.className.replace(
        new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'),
        ' '
      );
    }
  }

  // https://plainjs.com/javascript/attributes/adding-removing-and-testing-for-classes-9/
  function hasClass(el, className) {
    return el.classList
      ? el.classList.contains(className)
      : new RegExp('\\b' + className + '\\b').test(el.className);
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/Window/scrollY#Notes
  function getPageOffset() {
    var supportPageOffset = window.pageXOffset !== undefined,
      isCSS1Compat = (document.compatMode || '') === 'CSS1Compat',
      x = supportPageOffset
        ? window.pageXOffset
        : isCSS1Compat
        ? document.documentElement.scrollLeft
        : document.body.scrollLeft,
      y = supportPageOffset
        ? window.pageYOffset
        : isCSS1Compat
        ? document.documentElement.scrollTop
        : document.body.scrollTop;

    return {
      x: x,
      y: y,
    };
  }

  // we provide a function to compute constants instead
  // of accessing window.* as soon as the module needs it
  // so that we do not compute anything if not needed
  function getActions() {
    // Determine the events to bind. IE11 implements pointerEvents without
    // a prefix, which breaks compatibility with the IE10 implementation.
    return window.navigator.pointerEnabled
      ? {
          start: 'pointerdown',
          move: 'pointermove',
          end: 'pointerup',
        }
      : window.navigator.msPointerEnabled
      ? {
          start: 'MSPointerDown',
          move: 'MSPointerMove',
          end: 'MSPointerUp',
        }
      : {
          start: 'mousedown touchstart',
          move: 'mousemove touchmove',
          end: 'mouseup touchend',
        };
  }

  // Value calculation

  // Determine the size of a sub-range in relation to a full range.
  function subRangeRatio(pa, pb) {
    return 100 / (pb - pa);
  }

  // (percentage) How many percent is this value of this range?
  function fromPercentage(range, value) {
    return (value * 100) / (range[1] - range[0]);
  }

  // (percentage) Where is this value on this range?
  function toPercentage(range, value) {
    return fromPercentage(range, range[0] < 0 ? value + Math.abs(range[0]) : value - range[0]);
  }

  // (value) How much is this percentage on this range?
  function isPercentage(range, value) {
    return (value * (range[1] - range[0])) / 100 + range[0];
  }

  // Range conversion

  function getJ(value, arr) {
    var j = 1;

    while (value >= arr[j]) {
      j += 1;
    }

    return j;
  }

  // (percentage) Input a value, find where, on a scale of 0-100, it applies.
  function toStepping(xVal, xPct, value) {
    if (value >= xVal.slice(-1)[0]) {
      return 100;
    }

    var j = getJ(value, xVal),
      va,
      vb,
      pa,
      pb;

    va = xVal[j - 1];
    vb = xVal[j];
    pa = xPct[j - 1];
    pb = xPct[j];

    return pa + toPercentage([va, vb], value) / subRangeRatio(pa, pb);
  }

  // (value) Input a percentage, find where it is on the specified range.
  function fromStepping(xVal, xPct, value) {
    // There is no range group that fits 100
    if (value >= 100) {
      return xVal.slice(-1)[0];
    }

    var j = getJ(value, xPct),
      va,
      vb,
      pa,
      pb;

    va = xVal[j - 1];
    vb = xVal[j];
    pa = xPct[j - 1];
    pb = xPct[j];

    return isPercentage([va, vb], (value - pa) * subRangeRatio(pa, pb));
  }

  // (percentage) Get the step that applies at a certain value.
  function getStep(xPct, xSteps, snap, value) {
    if (value === 100) {
      return value;
    }

    var j = getJ(value, xPct),
      a,
      b;

    // If 'snap' is set, steps are used as fixed points on the slider.
    if (snap) {
      a = xPct[j - 1];
      b = xPct[j];

      // Find the closest position, a or b.
      if (value - a > (b - a) / 2) {
        return b;
      }

      return a;
    }

    if (!xSteps[j - 1]) {
      return value;
    }

    return xPct[j - 1] + closest(value - xPct[j - 1], xSteps[j - 1]);
  }

  // Entry parsing

  function handleEntryPoint(index, value, that) {
    var percentage;

    // Wrap numerical input in an array.
    if (typeof value === 'number') {
      value = [value];
    }

    // Reject any invalid input, by testing whether value is an array.
    if (Object.prototype.toString.call(value) !== '[object Array]') {
      throw new Error('noUiSlider (' + VERSION + "): 'range' contains invalid value.");
    }

    // Covert min/max syntax to 0 and 100.
    if (index === 'min') {
      percentage = 0;
    } else if (index === 'max') {
      percentage = 100;
    } else {
      percentage = parseFloat(index);
    }

    // Check for correct input.
    if (!isNumeric(percentage) || !isNumeric(value[0])) {
      throw new Error('noUiSlider (' + VERSION + "): 'range' value isn't numeric.");
    }

    // Store values.
    that.xPct.push(percentage);
    that.xVal.push(value[0]);

    // NaN will evaluate to false too, but to keep
    // logging clear, set step explicitly. Make sure
    // not to override the 'step' setting with false.
    if (!percentage) {
      if (!isNaN(value[1])) {
        that.xSteps[0] = value[1];
      }
    } else {
      that.xSteps.push(isNaN(value[1]) ? false : value[1]);
    }

    that.xHighestCompleteStep.push(0);
  }

  function handleStepPoint(i, n, that) {
    // Ignore 'false' stepping.
    if (!n) {
      return true;
    }

    // Factor to range ratio
    that.xSteps[i] =
      fromPercentage([that.xVal[i], that.xVal[i + 1]], n) /
      subRangeRatio(that.xPct[i], that.xPct[i + 1]);

    var totalSteps = (that.xVal[i + 1] - that.xVal[i]) / that.xNumSteps[i];
    var highestStep = Math.ceil(Number(totalSteps.toFixed(3)) - 1);
    var step = that.xVal[i] + that.xNumSteps[i] * highestStep;

    that.xHighestCompleteStep[i] = step;
  }

  // Interface

  // The interface to Spectrum handles all direction-based
  // conversions, so the above values are unaware.

  function Spectrum(entry, snap, direction, singleStep) {
    this.xPct = [];
    this.xVal = [];
    this.xSteps = [singleStep || false];
    this.xNumSteps = [false];
    this.xHighestCompleteStep = [];

    this.snap = snap;
    this.direction = direction;

    var index,
      ordered = [
        /* [0, 'min'], [1, '50%'], [2, 'max'] */
      ];

    // Map the object keys to an array.
    for (index in entry) {
      if (entry.hasOwnProperty(index)) {
        ordered.push([entry[index], index]);
      }
    }

    // Sort all entries by value (numeric sort).
    if (ordered.length && typeof ordered[0][0] === 'object') {
      ordered.sort(function (a, b) {
        return a[0][0] - b[0][0];
      });
    } else {
      ordered.sort(function (a, b) {
        return a[0] - b[0];
      });
    }

    // Convert all entries to subranges.
    for (index = 0; index < ordered.length; index++) {
      handleEntryPoint(ordered[index][1], ordered[index][0], this);
    }

    // Store the actual step values.
    // xSteps is sorted in the same order as xPct and xVal.
    this.xNumSteps = this.xSteps.slice(0);

    // Convert all numeric steps to the percentage of the subrange they represent.
    for (index = 0; index < this.xNumSteps.length; index++) {
      handleStepPoint(index, this.xNumSteps[index], this);
    }
  }

  Spectrum.prototype.getMargin = function (value) {
    var step = this.xNumSteps[0];

    if (step && (value / step) % 1 !== 0) {
      throw new Error(
        'noUiSlider (' + VERSION + "): 'limit', 'margin' and 'padding' must be divisible by step."
      );
    }

    return this.xPct.length === 2 ? fromPercentage(this.xVal, value) : false;
  };

  Spectrum.prototype.toStepping = function (value) {
    value = toStepping(this.xVal, this.xPct, value);

    return value;
  };

  Spectrum.prototype.fromStepping = function (value) {
    return fromStepping(this.xVal, this.xPct, value);
  };

  Spectrum.prototype.getStep = function (value) {
    value = getStep(this.xPct, this.xSteps, this.snap, value);

    return value;
  };

  Spectrum.prototype.getNearbySteps = function (value) {
    var j = getJ(value, this.xPct);

    return {
      stepBefore: {
        startValue: this.xVal[j - 2],
        step: this.xNumSteps[j - 2],
        highestStep: this.xHighestCompleteStep[j - 2],
      },
      thisStep: {
        startValue: this.xVal[j - 1],
        step: this.xNumSteps[j - 1],
        highestStep: this.xHighestCompleteStep[j - 1],
      },
      stepAfter: {
        startValue: this.xVal[j - 0],
        step: this.xNumSteps[j - 0],
        highestStep: this.xHighestCompleteStep[j - 0],
      },
    };
  };

  Spectrum.prototype.countStepDecimals = function () {
    var stepDecimals = this.xNumSteps.map(countDecimals);
    return Math.max.apply(null, stepDecimals);
  };

  // Outside testing
  Spectrum.prototype.convert = function (value) {
    return this.getStep(this.toStepping(value));
  };

  /*	Every input option is tested and parsed. This'll prevent
	endless validation in internal methods. These tests are
	structured with an item for every option available. An
	option can be marked as required by setting the 'r' flag.
	The testing function is provided with three arguments:
		- The provided value for the option;
		- A reference to the options object;
		- The name for the option;

	The testing function returns false when an error is detected,
	or true when everything is OK. It can also modify the option
	object, to make sure all values can be correctly looped elsewhere. */

  var defaultFormatter = {
    to: function (value) {
      return value !== undefined && value.toFixed(2);
    },
    from: Number,
  };

  function testStep(parsed, entry) {
    if (!isNumeric(entry)) {
      throw new Error('noUiSlider (' + VERSION + "): 'step' is not numeric.");
    }

    // The step option can still be used to set stepping
    // for linear sliders. Overwritten if set in 'range'.
    parsed.singleStep = entry;
  }

  function testRange(parsed, entry) {
    // Filter incorrect input.
    if (typeof entry !== 'object' || Array.isArray(entry)) {
      throw new Error('noUiSlider (' + VERSION + "): 'range' is not an object.");
    }

    // Catch missing start or end.
    if (entry.min === undefined || entry.max === undefined) {
      throw new Error('noUiSlider (' + VERSION + "): Missing 'min' or 'max' in 'range'.");
    }

    // Catch equal start or end.
    if (entry.min === entry.max) {
      throw new Error('noUiSlider (' + VERSION + "): 'range' 'min' and 'max' cannot be equal.");
    }

    parsed.spectrum = new Spectrum(entry, parsed.snap, parsed.dir, parsed.singleStep);
  }

  function testStart(parsed, entry) {
    entry = asArray(entry);

    // Validate input. Values aren't tested, as the public .val method
    // will always provide a valid location.
    if (!Array.isArray(entry) || !entry.length) {
      throw new Error('noUiSlider (' + VERSION + "): 'start' option is incorrect.");
    }

    // Store the number of handles.
    parsed.handles = entry.length;

    // When the slider is initialized, the .val method will
    // be called with the start options.
    parsed.start = entry;
  }

  function testSnap(parsed, entry) {
    // Enforce 100% stepping within subranges.
    parsed.snap = entry;

    if (typeof entry !== 'boolean') {
      throw new Error('noUiSlider (' + VERSION + "): 'snap' option must be a boolean.");
    }
  }

  function testAnimate(parsed, entry) {
    // Enforce 100% stepping within subranges.
    parsed.animate = entry;

    if (typeof entry !== 'boolean') {
      throw new Error('noUiSlider (' + VERSION + "): 'animate' option must be a boolean.");
    }
  }

  function testAnimationDuration(parsed, entry) {
    parsed.animationDuration = entry;

    if (typeof entry !== 'number') {
      throw new Error('noUiSlider (' + VERSION + "): 'animationDuration' option must be a number.");
    }
  }

  function testConnect(parsed, entry) {
    var connect = [false];
    var i;

    // Map legacy options
    if (entry === 'lower') {
      entry = [true, false];
    } else if (entry === 'upper') {
      entry = [false, true];
    }

    // Handle boolean options
    if (entry === true || entry === false) {
      for (i = 1; i < parsed.handles; i++) {
        connect.push(entry);
      }

      connect.push(false);
    }

    // Reject invalid input
    else if (!Array.isArray(entry) || !entry.length || entry.length !== parsed.handles + 1) {
      throw new Error('noUiSlider (' + VERSION + "): 'connect' option doesn't match handle count.");
    } else {
      connect = entry;
    }

    parsed.connect = connect;
  }

  function testOrientation(parsed, entry) {
    // Set orientation to an a numerical value for easy
    // array selection.
    switch (entry) {
      case 'horizontal':
        parsed.ort = 0;
        break;
      case 'vertical':
        parsed.ort = 1;
        break;
      default:
        throw new Error('noUiSlider (' + VERSION + "): 'orientation' option is invalid.");
    }
  }

  function testMargin(parsed, entry) {
    if (!isNumeric(entry)) {
      throw new Error('noUiSlider (' + VERSION + "): 'margin' option must be numeric.");
    }

    // Issue #582
    if (entry === 0) {
      return;
    }

    parsed.margin = parsed.spectrum.getMargin(entry);

    if (!parsed.margin) {
      throw new Error(
        'noUiSlider (' + VERSION + "): 'margin' option is only supported on linear sliders."
      );
    }
  }

  function testLimit(parsed, entry) {
    if (!isNumeric(entry)) {
      throw new Error('noUiSlider (' + VERSION + "): 'limit' option must be numeric.");
    }

    parsed.limit = parsed.spectrum.getMargin(entry);

    if (!parsed.limit || parsed.handles < 2) {
      throw new Error(
        'noUiSlider (' +
          VERSION +
          "): 'limit' option is only supported on linear sliders with 2 or more handles."
      );
    }
  }

  function testPadding(parsed, entry) {
    if (!isNumeric(entry)) {
      throw new Error('noUiSlider (' + VERSION + "): 'padding' option must be numeric.");
    }

    if (entry === 0) {
      return;
    }

    parsed.padding = parsed.spectrum.getMargin(entry);

    if (!parsed.padding) {
      throw new Error(
        'noUiSlider (' + VERSION + "): 'padding' option is only supported on linear sliders."
      );
    }

    if (parsed.padding < 0) {
      throw new Error('noUiSlider (' + VERSION + "): 'padding' option must be a positive number.");
    }

    if (parsed.padding >= 50) {
      throw new Error(
        'noUiSlider (' + VERSION + "): 'padding' option must be less than half the range."
      );
    }
  }

  function testDirection(parsed, entry) {
    // Set direction as a numerical value for easy parsing.
    // Invert connection for RTL sliders, so that the proper
    // handles get the connect/background classes.
    switch (entry) {
      case 'ltr':
        parsed.dir = 0;
        break;
      case 'rtl':
        parsed.dir = 1;
        break;
      default:
        throw new Error('noUiSlider (' + VERSION + "): 'direction' option was not recognized.");
    }
  }

  function testBehaviour(parsed, entry) {
    // Make sure the input is a string.
    if (typeof entry !== 'string') {
      throw new Error(
        'noUiSlider (' + VERSION + "): 'behaviour' must be a string containing options."
      );
    }

    // Check if the string contains any keywords.
    // None are required.
    var tap = entry.indexOf('tap') >= 0;
    var drag = entry.indexOf('drag') >= 0;
    var fixed = entry.indexOf('fixed') >= 0;
    var snap = entry.indexOf('snap') >= 0;
    var hover = entry.indexOf('hover') >= 0;

    if (fixed) {
      if (parsed.handles !== 2) {
        throw new Error(
          'noUiSlider (' + VERSION + "): 'fixed' behaviour must be used with 2 handles"
        );
      }

      // Use margin to enforce fixed state
      testMargin(parsed, parsed.start[1] - parsed.start[0]);
    }

    parsed.events = {
      tap: tap || snap,
      drag: drag,
      fixed: fixed,
      snap: snap,
      hover: hover,
    };
  }

  function testTooltips(parsed, entry) {
    if (entry === false) {
      return;
    } else if (entry === true) {
      parsed.tooltips = [];

      for (var i = 0; i < parsed.handles; i++) {
        parsed.tooltips.push(true);
      }
    } else {
      parsed.tooltips = asArray(entry);

      if (parsed.tooltips.length !== parsed.handles) {
        throw new Error('noUiSlider (' + VERSION + '): must pass a formatter for all handles.');
      }

      parsed.tooltips.forEach(function (formatter) {
        if (
          typeof formatter !== 'boolean' &&
          (typeof formatter !== 'object' || typeof formatter.to !== 'function')
        ) {
          throw new Error(
            'noUiSlider (' + VERSION + "): 'tooltips' must be passed a formatter or 'false'."
          );
        }
      });
    }
  }

  function testFormat(parsed, entry) {
    parsed.format = entry;

    // Any object with a to and from method is supported.
    if (typeof entry.to === 'function' && typeof entry.from === 'function') {
      return true;
    }

    throw new Error('noUiSlider (' + VERSION + "): 'format' requires 'to' and 'from' methods.");
  }

  function testCssPrefix(parsed, entry) {
    if (entry !== undefined && typeof entry !== 'string' && entry !== false) {
      throw new Error('noUiSlider (' + VERSION + "): 'cssPrefix' must be a string or `false`.");
    }

    parsed.cssPrefix = entry;
  }

  function testCssClasses(parsed, entry) {
    if (entry !== undefined && typeof entry !== 'object') {
      throw new Error('noUiSlider (' + VERSION + "): 'cssClasses' must be an object.");
    }

    if (typeof parsed.cssPrefix === 'string') {
      parsed.cssClasses = {};

      for (var key in entry) {
        if (!entry.hasOwnProperty(key)) {
          continue;
        }

        parsed.cssClasses[key] = parsed.cssPrefix + entry[key];
      }
    } else {
      parsed.cssClasses = entry;
    }
  }

  function testUseRaf(parsed, entry) {
    if (entry === true || entry === false) {
      parsed.useRequestAnimationFrame = entry;
    } else {
      throw new Error(
        'noUiSlider (' +
          VERSION +
          "): 'useRequestAnimationFrame' option should be true (default) or false."
      );
    }
  }

  // Test all developer settings and parse to assumption-safe values.
  function testOptions(options) {
    // To prove a fix for #537, freeze options here.
    // If the object is modified, an error will be thrown.
    // Object.freeze(options);

    var parsed = {
      margin: 0,
      limit: 0,
      padding: 0,
      animate: true,
      animationDuration: 300,
      format: defaultFormatter,
    };

    // Tests are executed in the order they are presented here.
    var tests = {
      step: { r: false, t: testStep },
      start: { r: true, t: testStart },
      connect: { r: true, t: testConnect },
      direction: { r: true, t: testDirection },
      snap: { r: false, t: testSnap },
      animate: { r: false, t: testAnimate },
      animationDuration: { r: false, t: testAnimationDuration },
      range: { r: true, t: testRange },
      orientation: { r: false, t: testOrientation },
      margin: { r: false, t: testMargin },
      limit: { r: false, t: testLimit },
      padding: { r: false, t: testPadding },
      behaviour: { r: true, t: testBehaviour },
      format: { r: false, t: testFormat },
      tooltips: { r: false, t: testTooltips },
      cssPrefix: { r: false, t: testCssPrefix },
      cssClasses: { r: false, t: testCssClasses },
      useRequestAnimationFrame: { r: false, t: testUseRaf },
    };

    var defaults = {
      connect: false,
      direction: 'ltr',
      behaviour: 'tap',
      orientation: 'horizontal',
      cssPrefix: 'noUi-',
      cssClasses: {
        target: 'target',
        base: 'base',
        origin: 'origin',
        handle: 'handle',
        handleLower: 'handle-lower',
        handleUpper: 'handle-upper',
        horizontal: 'horizontal',
        vertical: 'vertical',
        background: 'background',
        connect: 'connect',
        ltr: 'ltr',
        rtl: 'rtl',
        draggable: 'draggable',
        drag: 'state-drag',
        tap: 'state-tap',
        active: 'active',
        tooltip: 'tooltip',
        pips: 'pips',
        pipsHorizontal: 'pips-horizontal',
        pipsVertical: 'pips-vertical',
        marker: 'marker',
        markerHorizontal: 'marker-horizontal',
        markerVertical: 'marker-vertical',
        markerNormal: 'marker-normal',
        markerLarge: 'marker-large',
        markerSub: 'marker-sub',
        value: 'value',
        valueHorizontal: 'value-horizontal',
        valueVertical: 'value-vertical',
        valueNormal: 'value-normal',
        valueLarge: 'value-large',
        valueSub: 'value-sub',
      },
      useRequestAnimationFrame: true,
    };

    // Run all options through a testing mechanism to ensure correct
    // input. It should be noted that options might get modified to
    // be handled properly. E.g. wrapping integers in arrays.
    Object.keys(tests).forEach(function (name) {
      // If the option isn't set, but it is required, throw an error.
      if (options[name] === undefined && defaults[name] === undefined) {
        if (tests[name].r) {
          throw new Error('noUiSlider (' + VERSION + "): '" + name + "' is required.");
        }

        return true;
      }

      tests[name].t(parsed, options[name] === undefined ? defaults[name] : options[name]);
    });

    // Forward pips options
    parsed.pips = options.pips;

    var styles = [
      ['left', 'top'],
      ['right', 'bottom'],
    ];

    // Pre-define the styles.
    parsed.style = styles[parsed.dir][parsed.ort];
    parsed.styleOposite = styles[parsed.dir ? 0 : 1][parsed.ort];

    return parsed;
  }

  function closure(target, options, originalOptions) {
    var actions = getActions();

    // All variables local to 'closure' are prefixed with 'scope_'
    var scope_Target = target;
    var scope_Locations = [];
    var scope_Base;
    var scope_Handles;
    var scope_HandleNumbers = [];
    var scope_ActiveHandle = false;
    var scope_Connects;
    var scope_Spectrum = options.spectrum;
    var scope_Values = [];
    var scope_Events = {};
    var scope_Self;

    // Append a origin to the base
    function addOrigin(base, handleNumber) {
      var origin = addNodeTo(base, options.cssClasses.origin);
      var handle = addNodeTo(origin, options.cssClasses.handle);

      handle.setAttribute('data-handle', handleNumber);

      if (handleNumber === 0) {
        addClass(handle, options.cssClasses.handleLower);
      } else if (handleNumber === options.handles - 1) {
        addClass(handle, options.cssClasses.handleUpper);
      }

      return origin;
    }

    // Insert nodes for connect elements
    function addConnect(base, add) {
      if (!add) {
        return false;
      }

      return addNodeTo(base, options.cssClasses.connect);
    }

    // Add handles to the slider base.
    function addElements(connectOptions, base) {
      scope_Handles = [];
      scope_Connects = [];

      scope_Connects.push(addConnect(base, connectOptions[0]));

      // [::::O====O====O====]
      // connectOptions = [0, 1, 1, 1]

      for (var i = 0; i < options.handles; i++) {
        // Keep a list of all added handles.
        scope_Handles.push(addOrigin(base, i));
        scope_HandleNumbers[i] = i;
        scope_Connects.push(addConnect(base, connectOptions[i + 1]));
      }
    }

    // Initialize a single slider.
    function addSlider(target) {
      // Apply classes and data to the target.
      addClass(target, options.cssClasses.target);

      if (options.dir === 0) {
        addClass(target, options.cssClasses.ltr);
      } else {
        addClass(target, options.cssClasses.rtl);
      }

      if (options.ort === 0) {
        addClass(target, options.cssClasses.horizontal);
      } else {
        addClass(target, options.cssClasses.vertical);
      }

      scope_Base = addNodeTo(target, options.cssClasses.base);
    }

    function addTooltip(handle, handleNumber) {
      if (!options.tooltips[handleNumber]) {
        return false;
      }

      return addNodeTo(handle.firstChild, options.cssClasses.tooltip);
    }

    // The tooltips option is a shorthand for using the 'update' event.
    function tooltips() {
      // Tooltips are added with options.tooltips in original order.
      var tips = scope_Handles.map(addTooltip);

      bindEvent('update', function (values, handleNumber, unencoded) {
        if (!tips[handleNumber]) {
          return;
        }

        var formattedValue = values[handleNumber];

        if (options.tooltips[handleNumber] !== true) {
          formattedValue = options.tooltips[handleNumber].to(unencoded[handleNumber]);
        }

        tips[handleNumber].innerHTML = formattedValue;
      });
    }

    function getGroup(mode, values, stepped) {
      // Use the range.
      if (mode === 'range' || mode === 'steps') {
        return scope_Spectrum.xVal;
      }

      if (mode === 'count') {
        if (!values) {
          throw new Error('noUiSlider (' + VERSION + "): 'values' required for mode 'count'.");
        }

        // Divide 0 - 100 in 'count' parts.
        var spread = 100 / (values - 1);
        var v;
        var i = 0;

        values = [];

        // List these parts and have them handled as 'positions'.
        while ((v = i++ * spread) <= 100) {
          values.push(v);
        }

        mode = 'positions';
      }

      if (mode === 'positions') {
        // Map all percentages to on-range values.
        return values.map(function (value) {
          return scope_Spectrum.fromStepping(stepped ? scope_Spectrum.getStep(value) : value);
        });
      }

      if (mode === 'values') {
        // If the value must be stepped, it needs to be converted to a percentage first.
        if (stepped) {
          return values.map(function (value) {
            // Convert to percentage, apply step, return to value.
            return scope_Spectrum.fromStepping(
              scope_Spectrum.getStep(scope_Spectrum.toStepping(value))
            );
          });
        }

        // Otherwise, we can simply use the values.
        return values;
      }
    }

    function generateSpread(density, mode, group) {
      function safeIncrement(value, increment) {
        // Avoid floating point variance by dropping the smallest decimal places.
        return (value + increment).toFixed(7) / 1;
      }

      var indexes = {};
      var firstInRange = scope_Spectrum.xVal[0];
      var lastInRange = scope_Spectrum.xVal[scope_Spectrum.xVal.length - 1];
      var ignoreFirst = false;
      var ignoreLast = false;
      var prevPct = 0;

      // Create a copy of the group, sort it and filter away all duplicates.
      group = unique(
        group.slice().sort(function (a, b) {
          return a - b;
        })
      );

      // Make sure the range starts with the first element.
      if (group[0] !== firstInRange) {
        group.unshift(firstInRange);
        ignoreFirst = true;
      }

      // Likewise for the last one.
      if (group[group.length - 1] !== lastInRange) {
        group.push(lastInRange);
        ignoreLast = true;
      }

      group.forEach(function (current, index) {
        // Get the current step and the lower + upper positions.
        var step;
        var i;
        var q;
        var low = current;
        var high = group[index + 1];
        var newPct;
        var pctDifference;
        var pctPos;
        var type;
        var steps;
        var realSteps;
        var stepsize;

        // When using 'steps' mode, use the provided steps.
        // Otherwise, we'll step on to the next subrange.
        if (mode === 'steps') {
          step = scope_Spectrum.xNumSteps[index];
        }

        // Default to a 'full' step.
        if (!step) {
          step = high - low;
        }

        // Low can be 0, so test for false. If high is undefined,
        // we are at the last subrange. Index 0 is already handled.
        if (low === false || high === undefined) {
          return;
        }

        // Make sure step isn't 0, which would cause an infinite loop (#654)
        step = Math.max(step, 0.0000001);

        // Find all steps in the subrange.
        for (i = low; i <= high; i = safeIncrement(i, step)) {
          // Get the percentage value for the current step,
          // calculate the size for the subrange.
          newPct = scope_Spectrum.toStepping(i);
          pctDifference = newPct - prevPct;

          steps = pctDifference / density;
          realSteps = Math.round(steps);

          // This ratio represents the ammount of percentage-space a point indicates.
          // For a density 1 the points/percentage = 1. For density 2, that percentage needs to be re-devided.
          // Round the percentage offset to an even number, then divide by two
          // to spread the offset on both sides of the range.
          stepsize = pctDifference / realSteps;

          // Divide all points evenly, adding the correct number to this subrange.
          // Run up to <= so that 100% gets a point, event if ignoreLast is set.
          for (q = 1; q <= realSteps; q += 1) {
            // The ratio between the rounded value and the actual size might be ~1% off.
            // Correct the percentage offset by the number of points
            // per subrange. density = 1 will result in 100 points on the
            // full range, 2 for 50, 4 for 25, etc.
            pctPos = prevPct + q * stepsize;
            indexes[pctPos.toFixed(5)] = ['x', 0];
          }

          // Determine the point type.
          type = group.indexOf(i) > -1 ? 1 : mode === 'steps' ? 2 : 0;

          // Enforce the 'ignoreFirst' option by overwriting the type for 0.
          if (!index && ignoreFirst) {
            type = 0;
          }

          if (!(i === high && ignoreLast)) {
            // Mark the 'type' of this point. 0 = plain, 1 = real value, 2 = step value.
            indexes[newPct.toFixed(5)] = [i, type];
          }

          // Update the percentage count.
          prevPct = newPct;
        }
      });

      return indexes;
    }

    function addMarking(spread, filterFunc, formatter) {
      var element = document.createElement('div');
      var out = '';
      var valueSizeClasses = [
        options.cssClasses.valueNormal,
        options.cssClasses.valueLarge,
        options.cssClasses.valueSub,
      ];
      var markerSizeClasses = [
        options.cssClasses.markerNormal,
        options.cssClasses.markerLarge,
        options.cssClasses.markerSub,
      ];
      var valueOrientationClasses = [
        options.cssClasses.valueHorizontal,
        options.cssClasses.valueVertical,
      ];
      var markerOrientationClasses = [
        options.cssClasses.markerHorizontal,
        options.cssClasses.markerVertical,
      ];

      addClass(element, options.cssClasses.pips);
      addClass(
        element,
        options.ort === 0 ? options.cssClasses.pipsHorizontal : options.cssClasses.pipsVertical
      );

      function getClasses(type, source) {
        var a = source === options.cssClasses.value;
        var orientationClasses = a ? valueOrientationClasses : markerOrientationClasses;
        var sizeClasses = a ? valueSizeClasses : markerSizeClasses;

        return source + ' ' + orientationClasses[options.ort] + ' ' + sizeClasses[type];
      }

      function getTags(offset, source, values) {
        return (
          'class="' +
          getClasses(values[1], source) +
          '" style="' +
          options.style +
          ': ' +
          offset +
          '%"'
        );
      }

      function addSpread(offset, values) {
        // Apply the filter function, if it is set.
        values[1] = values[1] && filterFunc ? filterFunc(values[0], values[1]) : values[1];

        // Add a marker for every point
        out += '<div ' + getTags(offset, options.cssClasses.marker, values) + '></div>';

        // Values are only appended for points marked '1' or '2'.
        if (values[1]) {
          out +=
            '<div ' +
            getTags(offset, options.cssClasses.value, values) +
            '>' +
            formatter.to(values[0]) +
            '</div>';
        }
      }

      // Append all points.
      Object.keys(spread).forEach(function (a) {
        addSpread(a, spread[a]);
      });

      element.innerHTML = out;

      return element;
    }

    function pips(grid) {
      var mode = grid.mode;
      var density = grid.density || 1;
      var filter = grid.filter || false;
      var values = grid.values || false;
      var stepped = grid.stepped || false;
      var group = getGroup(mode, values, stepped);
      var spread = generateSpread(density, mode, group);
      var format = grid.format || {
        to: Math.round,
      };

      return scope_Target.appendChild(addMarking(spread, filter, format));
    }

    // Shorthand for base dimensions.
    function baseSize() {
      var rect = scope_Base.getBoundingClientRect(),
        alt = 'offset' + ['Width', 'Height'][options.ort];
      return options.ort === 0 ? rect.width || scope_Base[alt] : rect.height || scope_Base[alt];
    }

    // Handler for attaching events trough a proxy.
    function attachEvent(events, element, callback, data) {
      // This function can be used to 'filter' events to the slider.
      // element is a node, not a nodeList

      var method = function (e) {
        if (scope_Target.hasAttribute('disabled')) {
          return false;
        }

        // Stop if an active 'tap' transition is taking place.
        if (hasClass(scope_Target, options.cssClasses.tap)) {
          return false;
        }

        e = fixEvent(e, data.pageOffset);

        // Handle reject of multitouch
        if (!e) {
          return false;
        }

        // Ignore right or middle clicks on start #454
        if (events === actions.start && e.buttons !== undefined && e.buttons > 1) {
          return false;
        }

        // Ignore right or middle clicks on start #454
        if (data.hover && e.buttons) {
          return false;
        }

        e.calcPoint = e.points[options.ort];

        // Call the event handler with the event [ and additional data ].
        callback(e, data);
      };

      var methods = [];

      // Bind a closure on the target for every event type.
      events.split(' ').forEach(function (eventName) {
        element.addEventListener(eventName, method, false);
        methods.push([eventName, method]);
      });

      return methods;
    }

    // Provide a clean event with standardized offset values.
    function fixEvent(e, pageOffset) {
      // Prevent scrolling and panning on touch events, while
      // attempting to slide. The tap event also depends on this.
      e.preventDefault();

      // Filter the event to register the type, which can be
      // touch, mouse or pointer. Offset changes need to be
      // made on an event specific basis.
      var touch = e.type.indexOf('touch') === 0;
      var mouse = e.type.indexOf('mouse') === 0;
      var pointer = e.type.indexOf('pointer') === 0;
      var x;
      var y;

      // IE10 implemented pointer events with a prefix;
      if (e.type.indexOf('MSPointer') === 0) {
        pointer = true;
      }

      if (touch) {
        // Fix bug when user touches with two or more fingers on mobile devices.
        // It's useful when you have two or more sliders on one page,
        // that can be touched simultaneously.
        // #649, #663, #668
        if (e.touches.length > 1) {
          return false;
        }

        // noUiSlider supports one movement at a time,
        // so we can select the first 'changedTouch'.
        x = e.changedTouches[0].pageX;
        y = e.changedTouches[0].pageY;
      }

      pageOffset = pageOffset || getPageOffset();

      if (mouse || pointer) {
        x = e.clientX + pageOffset.x;
        y = e.clientY + pageOffset.y;
      }

      e.pageOffset = pageOffset;
      e.points = [x, y];
      e.cursor = mouse || pointer; // Fix #435

      return e;
    }

    // Translate a coordinate in the document to a percentage on the slider
    function calcPointToPercentage(calcPoint) {
      var location = calcPoint - offset(scope_Base, options.ort);
      var proposal = (location * 100) / baseSize();
      return options.dir ? 100 - proposal : proposal;
    }

    // Find handle closest to a certain percentage on the slider
    function getClosestHandle(proposal) {
      var closest = 100;
      var handleNumber = false;

      scope_Handles.forEach(function (handle, index) {
        // Disabled handles are ignored
        if (handle.hasAttribute('disabled')) {
          return;
        }

        var pos = Math.abs(scope_Locations[index] - proposal);

        if (pos < closest) {
          handleNumber = index;
          closest = pos;
        }
      });

      return handleNumber;
    }

    // Moves handle(s) by a percentage
    // (bool, % to move, [% where handle started, ...], [index in scope_Handles, ...])
    function moveHandles(upward, proposal, locations, handleNumbers) {
      var proposals = locations.slice();

      var b = [!upward, upward];
      var f = [upward, !upward];

      // Copy handleNumbers so we don't change the dataset
      handleNumbers = handleNumbers.slice();

      // Check to see which handle is 'leading'.
      // If that one can't move the second can't either.
      if (upward) {
        handleNumbers.reverse();
      }

      // Step 1: get the maximum percentage that any of the handles can move
      if (handleNumbers.length > 1) {
        handleNumbers.forEach(function (handleNumber, o) {
          var to = checkHandlePosition(
            proposals,
            handleNumber,
            proposals[handleNumber] + proposal,
            b[o],
            f[o]
          );

          // Stop if one of the handles can't move.
          if (to === false) {
            proposal = 0;
          } else {
            proposal = to - proposals[handleNumber];
            proposals[handleNumber] = to;
          }
        });
      }

      // If using one handle, check backward AND forward
      else {
        b = f = [true];
      }

      var state = false;

      // Step 2: Try to set the handles with the found percentage
      handleNumbers.forEach(function (handleNumber, o) {
        state = setHandle(handleNumber, locations[handleNumber] + proposal, b[o], f[o]) || state;
      });

      // Step 3: If a handle moved, fire events
      if (state) {
        handleNumbers.forEach(function (handleNumber) {
          fireEvent('update', handleNumber);
          fireEvent('slide', handleNumber);
        });
      }
    }

    // External event handling
    function fireEvent(eventName, handleNumber, tap) {
      Object.keys(scope_Events).forEach(function (targetEvent) {
        var eventType = targetEvent.split('.')[0];

        if (eventName === eventType) {
          scope_Events[targetEvent].forEach(function (callback) {
            callback.call(
              // Use the slider public API as the scope ('this')
              scope_Self,
              // Return values as array, so arg_1[arg_2] is always valid.
              scope_Values.map(options.format.to),
              // Handle index, 0 or 1
              handleNumber,
              // Unformatted slider values
              scope_Values.slice(),
              // Event is fired by tap, true or false
              tap || false,
              // Left offset of the handle, in relation to the slider
              scope_Locations.slice()
            );
          });
        }
      });
    }

    // Fire 'end' when a mouse or pen leaves the document.
    function documentLeave(event, data) {
      if (
        event.type === 'mouseout' &&
        event.target.nodeName === 'HTML' &&
        event.relatedTarget === null
      ) {
        eventEnd(event, data);
      }
    }

    // Handle movement on document for handle and range drag.
    function eventMove(event, data) {
      // Fix #498
      // Check value of .buttons in 'start' to work around a bug in IE10 mobile (data.buttonsProperty).
      // https://connect.microsoft.com/IE/feedback/details/927005/mobile-ie10-windows-phone-buttons-property-of-pointermove-event-always-zero
      // IE9 has .buttons and .which zero on mousemove.
      // Firefox breaks the spec MDN defines.
      if (
        navigator.appVersion.indexOf('MSIE 9') === -1 &&
        event.buttons === 0 &&
        data.buttonsProperty !== 0
      ) {
        return eventEnd(event, data);
      }

      // Check if we are moving up or down
      var movement = (options.dir ? -1 : 1) * (event.calcPoint - data.startCalcPoint);

      // Convert the movement into a percentage of the slider width/height
      var proposal = (movement * 100) / data.baseSize;

      moveHandles(movement > 0, proposal, data.locations, data.handleNumbers);
    }

    // Unbind move events on document, call callbacks.
    function eventEnd(event, data) {
      // The handle is no longer active, so remove the class.
      if (scope_ActiveHandle) {
        removeClass(scope_ActiveHandle, options.cssClasses.active);
        scope_ActiveHandle = false;
      }

      // Remove cursor styles and text-selection events bound to the body.
      if (event.cursor) {
        document.body.style.cursor = '';
        document.body.removeEventListener('selectstart', document.body.noUiListener);
      }

      // Unbind the move and end events, which are added on 'start'.
      document.documentElement.noUiListeners.forEach(function (c) {
        document.documentElement.removeEventListener(c[0], c[1]);
      });

      // Remove dragging class.
      removeClass(scope_Target, options.cssClasses.drag);

      setZindex();

      data.handleNumbers.forEach(function (handleNumber) {
        fireEvent('set', handleNumber);
        fireEvent('change', handleNumber);
        fireEvent('end', handleNumber);
      });
    }

    // Bind move events on document.
    function eventStart(event, data) {
      if (data.handleNumbers.length === 1) {
        var handle = scope_Handles[data.handleNumbers[0]];

        // Ignore 'disabled' handles
        if (handle.hasAttribute('disabled')) {
          return false;
        }

        // Mark the handle as 'active' so it can be styled.
        scope_ActiveHandle = handle.children[0];
        addClass(scope_ActiveHandle, options.cssClasses.active);
      }

      // Fix #551, where a handle gets selected instead of dragged.
      event.preventDefault();

      // A drag should never propagate up to the 'tap' event.
      event.stopPropagation();

      // Attach the move and end events.
      var moveEvent = attachEvent(actions.move, document.documentElement, eventMove, {
        startCalcPoint: event.calcPoint,
        baseSize: baseSize(),
        pageOffset: event.pageOffset,
        handleNumbers: data.handleNumbers,
        buttonsProperty: event.buttons,
        locations: scope_Locations.slice(),
      });

      var endEvent = attachEvent(actions.end, document.documentElement, eventEnd, {
        handleNumbers: data.handleNumbers,
      });

      var outEvent = attachEvent('mouseout', document.documentElement, documentLeave, {
        handleNumbers: data.handleNumbers,
      });

      document.documentElement.noUiListeners = moveEvent.concat(endEvent, outEvent);

      // Text selection isn't an issue on touch devices,
      // so adding cursor styles can be skipped.
      if (event.cursor) {
        // Prevent the 'I' cursor and extend the range-drag cursor.
        document.body.style.cursor = getComputedStyle(event.target).cursor;

        // Mark the target with a dragging state.
        if (scope_Handles.length > 1) {
          addClass(scope_Target, options.cssClasses.drag);
        }

        var f = function () {
          return false;
        };

        document.body.noUiListener = f;

        // Prevent text selection when dragging the handles.
        document.body.addEventListener('selectstart', f, false);
      }

      data.handleNumbers.forEach(function (handleNumber) {
        fireEvent('start', handleNumber);
      });
    }

    // Move closest handle to tapped location.
    function eventTap(event) {
      // The tap event shouldn't propagate up
      event.stopPropagation();

      var proposal = calcPointToPercentage(event.calcPoint);
      var handleNumber = getClosestHandle(proposal);

      // Tackle the case that all handles are 'disabled'.
      if (handleNumber === false) {
        return false;
      }

      // Flag the slider as it is now in a transitional state.
      // Transition takes a configurable amount of ms (default 300). Re-enable the slider after that.
      if (!options.events.snap) {
        addClassFor(scope_Target, options.cssClasses.tap, options.animationDuration);
      }

      setHandle(handleNumber, proposal, true, true);

      setZindex();

      fireEvent('slide', handleNumber, true);
      fireEvent('set', handleNumber, true);
      fireEvent('change', handleNumber, true);
      fireEvent('update', handleNumber, true);

      if (options.events.snap) {
        eventStart(event, { handleNumbers: [handleNumber] });
      }
    }

    // Fires a 'hover' event for a hovered mouse/pen position.
    function eventHover(event) {
      var proposal = calcPointToPercentage(event.calcPoint);

      var to = scope_Spectrum.getStep(proposal);
      var value = scope_Spectrum.fromStepping(to);

      Object.keys(scope_Events).forEach(function (targetEvent) {
        if ('hover' === targetEvent.split('.')[0]) {
          scope_Events[targetEvent].forEach(function (callback) {
            callback.call(scope_Self, value);
          });
        }
      });
    }

    // Attach events to several slider parts.
    function bindSliderEvents(behaviour) {
      // Attach the standard drag event to the handles.
      if (!behaviour.fixed) {
        scope_Handles.forEach(function (handle, index) {
          // These events are only bound to the visual handle
          // element, not the 'real' origin element.
          attachEvent(actions.start, handle.children[0], eventStart, {
            handleNumbers: [index],
          });
        });
      }

      // Attach the tap event to the slider base.
      if (behaviour.tap) {
        attachEvent(actions.start, scope_Base, eventTap, {});
      }

      // Fire hover events
      if (behaviour.hover) {
        attachEvent(actions.move, scope_Base, eventHover, { hover: true });
      }

      // Make the range draggable.
      if (behaviour.drag) {
        scope_Connects.forEach(function (connect, index) {
          if (connect === false || index === 0 || index === scope_Connects.length - 1) {
            return;
          }

          var handleBefore = scope_Handles[index - 1];
          var handleAfter = scope_Handles[index];
          var eventHolders = [connect];

          addClass(connect, options.cssClasses.draggable);

          // When the range is fixed, the entire range can
          // be dragged by the handles. The handle in the first
          // origin will propagate the start event upward,
          // but it needs to be bound manually on the other.
          if (behaviour.fixed) {
            eventHolders.push(handleBefore.children[0]);
            eventHolders.push(handleAfter.children[0]);
          }

          eventHolders.forEach(function (eventHolder) {
            attachEvent(actions.start, eventHolder, eventStart, {
              handles: [handleBefore, handleAfter],
              handleNumbers: [index - 1, index],
            });
          });
        });
      }
    }

    // Split out the handle positioning logic so the Move event can use it, too
    function checkHandlePosition(reference, handleNumber, to, lookBackward, lookForward) {
      // For sliders with multiple handles, limit movement to the other handle.
      // Apply the margin option by adding it to the handle positions.
      if (scope_Handles.length > 1) {
        if (lookBackward && handleNumber > 0) {
          to = Math.max(to, reference[handleNumber - 1] + options.margin);
        }

        if (lookForward && handleNumber < scope_Handles.length - 1) {
          to = Math.min(to, reference[handleNumber + 1] - options.margin);
        }
      }

      // The limit option has the opposite effect, limiting handles to a
      // maximum distance from another. Limit must be > 0, as otherwise
      // handles would be unmoveable.
      if (scope_Handles.length > 1 && options.limit) {
        if (lookBackward && handleNumber > 0) {
          to = Math.min(to, reference[handleNumber - 1] + options.limit);
        }

        if (lookForward && handleNumber < scope_Handles.length - 1) {
          to = Math.max(to, reference[handleNumber + 1] - options.limit);
        }
      }

      // The padding option keeps the handles a certain distance from the
      // edges of the slider. Padding must be > 0.
      if (options.padding) {
        if (handleNumber === 0) {
          to = Math.max(to, options.padding);
        }

        if (handleNumber === scope_Handles.length - 1) {
          to = Math.min(to, 100 - options.padding);
        }
      }

      to = scope_Spectrum.getStep(to);

      // Limit percentage to the 0 - 100 range
      to = limit(to);

      // Return false if handle can't move
      if (to === reference[handleNumber]) {
        return false;
      }

      return to;
    }

    function toPct(pct) {
      return pct + '%';
    }

    // Updates scope_Locations and scope_Values, updates visual state
    function updateHandlePosition(handleNumber, to) {
      // Update locations.
      scope_Locations[handleNumber] = to;

      // Convert the value to the slider stepping/range.
      scope_Values[handleNumber] = scope_Spectrum.fromStepping(to);

      // Called synchronously or on the next animationFrame
      var stateUpdate = function () {
        scope_Handles[handleNumber].style[options.style] = toPct(to);
        updateConnect(handleNumber);
        updateConnect(handleNumber + 1);
      };

      // Set the handle to the new position.
      // Use requestAnimationFrame for efficient painting.
      // No significant effect in Chrome, Edge sees dramatic performace improvements.
      // Option to disable is useful for unit tests, and single-step debugging.
      if (window.requestAnimationFrame && options.useRequestAnimationFrame) {
        window.requestAnimationFrame(stateUpdate);
      } else {
        stateUpdate();
      }
    }

    function setZindex() {
      scope_HandleNumbers.forEach(function (handleNumber) {
        // Handles before the slider middle are stacked later = higher,
        // Handles after the middle later is lower
        // [[7] [8] .......... | .......... [5] [4]
        var dir = scope_Locations[handleNumber] > 50 ? -1 : 1;
        var zIndex = 3 + (scope_Handles.length + dir * handleNumber);
        scope_Handles[handleNumber].childNodes[0].style.zIndex = zIndex;
      });
    }

    // Test suggested values and apply margin, step.
    function setHandle(handleNumber, to, lookBackward, lookForward) {
      to = checkHandlePosition(scope_Locations, handleNumber, to, lookBackward, lookForward);

      if (to === false) {
        return false;
      }

      updateHandlePosition(handleNumber, to);

      return true;
    }

    // Updates style attribute for connect nodes
    function updateConnect(index) {
      // Skip connects set to false
      if (!scope_Connects[index]) {
        return;
      }

      var l = 0;
      var h = 100;

      if (index !== 0) {
        l = scope_Locations[index - 1];
      }

      if (index !== scope_Connects.length - 1) {
        h = scope_Locations[index];
      }

      scope_Connects[index].style[options.style] = toPct(l);
      scope_Connects[index].style[options.styleOposite] = toPct(100 - h);
    }

    // ...
    function setValue(to, handleNumber) {
      // Setting with null indicates an 'ignore'.
      // Inputting 'false' is invalid.
      if (to === null || to === false) {
        return;
      }

      // If a formatted number was passed, attemt to decode it.
      if (typeof to === 'number') {
        to = String(to);
      }

      to = options.format.from(to);

      // Request an update for all links if the value was invalid.
      // Do so too if setting the handle fails.
      if (to !== false && !isNaN(to)) {
        setHandle(handleNumber, scope_Spectrum.toStepping(to), false, false);
      }
    }

    // Set the slider value.
    function valueSet(input, fireSetEvent) {
      var values = asArray(input);
      var isInit = scope_Locations[0] === undefined;

      // Event fires by default
      fireSetEvent = fireSetEvent === undefined ? true : !!fireSetEvent;

      values.forEach(setValue);

      // Animation is optional.
      // Make sure the initial values were set before using animated placement.
      if (options.animate && !isInit) {
        addClassFor(scope_Target, options.cssClasses.tap, options.animationDuration);
      }

      // Now that all base values are set, apply constraints
      scope_HandleNumbers.forEach(function (handleNumber) {
        setHandle(handleNumber, scope_Locations[handleNumber], true, false);
      });

      setZindex();

      scope_HandleNumbers.forEach(function (handleNumber) {
        fireEvent('update', handleNumber);

        // Fire the event only for handles that received a new value, as per #579
        if (values[handleNumber] !== null && fireSetEvent) {
          fireEvent('set', handleNumber);
        }
      });
    }

    // Reset slider to initial values
    function valueReset(fireSetEvent) {
      valueSet(options.start, fireSetEvent);
    }

    // Get the slider value.
    function valueGet() {
      var values = scope_Values.map(options.format.to);

      // If only one handle is used, return a single value.
      if (values.length === 1) {
        return values[0];
      }

      return values;
    }

    // Removes classes from the root and empties it.
    function destroy() {
      for (var key in options.cssClasses) {
        if (!options.cssClasses.hasOwnProperty(key)) {
          continue;
        }
        removeClass(scope_Target, options.cssClasses[key]);
      }

      while (scope_Target.firstChild) {
        scope_Target.removeChild(scope_Target.firstChild);
      }

      delete scope_Target.noUiSlider;
    }

    // Get the current step size for the slider.
    function getCurrentStep() {
      // Check all locations, map them to their stepping point.
      // Get the step point, then find it in the input list.
      return scope_Locations.map(function (location, index) {
        var nearbySteps = scope_Spectrum.getNearbySteps(location);
        var value = scope_Values[index];
        var increment = nearbySteps.thisStep.step;
        var decrement = null;

        // If the next value in this step moves into the next step,
        // the increment is the start of the next step - the current value
        if (increment !== false) {
          if (value + increment > nearbySteps.stepAfter.startValue) {
            increment = nearbySteps.stepAfter.startValue - value;
          }
        }

        // If the value is beyond the starting point
        if (value > nearbySteps.thisStep.startValue) {
          decrement = nearbySteps.thisStep.step;
        } else if (nearbySteps.stepBefore.step === false) {
          decrement = false;
        }

        // If a handle is at the start of a step, it always steps back into the previous step first
        else {
          decrement = value - nearbySteps.stepBefore.highestStep;
        }

        // Now, if at the slider edges, there is not in/decrement
        if (location === 100) {
          increment = null;
        } else if (location === 0) {
          decrement = null;
        }

        // As per #391, the comparison for the decrement step can have some rounding issues.
        var stepDecimals = scope_Spectrum.countStepDecimals();

        // Round per #391
        if (increment !== null && increment !== false) {
          increment = Number(increment.toFixed(stepDecimals));
        }

        if (decrement !== null && decrement !== false) {
          decrement = Number(decrement.toFixed(stepDecimals));
        }

        return [decrement, increment];
      });
    }

    // Attach an event to this slider, possibly including a namespace
    function bindEvent(namespacedEvent, callback) {
      scope_Events[namespacedEvent] = scope_Events[namespacedEvent] || [];
      scope_Events[namespacedEvent].push(callback);

      // If the event bound is 'update,' fire it immediately for all handles.
      if (namespacedEvent.split('.')[0] === 'update') {
        scope_Handles.forEach(function (a, index) {
          fireEvent('update', index);
        });
      }
    }

    // Undo attachment of event
    function removeEvent(namespacedEvent) {
      var event = namespacedEvent && namespacedEvent.split('.')[0];
      var namespace = event && namespacedEvent.substring(event.length);

      Object.keys(scope_Events).forEach(function (bind) {
        var tEvent = bind.split('.')[0],
          tNamespace = bind.substring(tEvent.length);

        if ((!event || event === tEvent) && (!namespace || namespace === tNamespace)) {
          delete scope_Events[bind];
        }
      });
    }

    // Updateable: margin, limit, padding, step, range, animate, snap
    function updateOptions(optionsToUpdate, fireSetEvent) {
      // Spectrum is created using the range, snap, direction and step options.
      // 'snap' and 'step' can be updated, 'direction' cannot, due to event binding.
      // If 'snap' and 'step' are not passed, they should remain unchanged.
      var v = valueGet();

      var updateAble = ['margin', 'limit', 'padding', 'range', 'animate', 'snap', 'step', 'format'];

      // Only change options that we're actually passed to update.
      updateAble.forEach(function (name) {
        if (optionsToUpdate[name] !== undefined) {
          originalOptions[name] = optionsToUpdate[name];
        }
      });

      var newOptions = testOptions(originalOptions);

      // Load new options into the slider state
      updateAble.forEach(function (name) {
        if (optionsToUpdate[name] !== undefined) {
          options[name] = newOptions[name];
        }
      });

      // Save current spectrum direction as testOptions in testRange call
      // doesn't rely on current direction
      newOptions.spectrum.direction = scope_Spectrum.direction;
      scope_Spectrum = newOptions.spectrum;

      // Limit, margin and padding depend on the spectrum but are stored outside of it. (#677)
      options.margin = newOptions.margin;
      options.limit = newOptions.limit;
      options.padding = newOptions.padding;

      // Invalidate the current positioning so valueSet forces an update.
      scope_Locations = [];
      valueSet(optionsToUpdate.start || v, fireSetEvent);
    }

    // Throw an error if the slider was already initialized.
    if (scope_Target.noUiSlider) {
      throw new Error('noUiSlider (' + VERSION + '): Slider was already initialized.');
    }

    // Create the base element, initialise HTML and set classes.
    // Add handles and connect elements.
    addSlider(scope_Target);
    addElements(options.connect, scope_Base);

    scope_Self = {
      destroy: destroy,
      steps: getCurrentStep,
      on: bindEvent,
      off: removeEvent,
      get: valueGet,
      set: valueSet,
      reset: valueReset,
      // Exposed for unit testing, don't use this in your application.
      __moveHandles: function (a, b, c) {
        moveHandles(a, b, scope_Locations, c);
      },
      options: originalOptions, // Issue #600, #678
      updateOptions: updateOptions,
      target: scope_Target, // Issue #597
      pips: pips, // Issue #594
    };

    // Attach user events.
    bindSliderEvents(options.events);

    // Use the public value method to set the start values.
    valueSet(options.start);

    if (options.pips) {
      pips(options.pips);
    }

    if (options.tooltips) {
      tooltips();
    }

    return scope_Self;
  }

  // Run the standard initializer
  function initialize(target, originalOptions) {
    if (!target.nodeName) {
      throw new Error('noUiSlider (' + VERSION + '): create requires a single element.');
    }

    // Test the options and create the slider environment;
    var options = testOptions(originalOptions, target);
    var api = closure(target, options, originalOptions);

    target.noUiSlider = api;

    return api;
  }

  // Use an object instead of a function for future expansibility;
  return {
    version: VERSION,
    create: initialize,
  };
});

/*!
 * surveyjs-widgets - Widgets for the SurveyJS library v1.8.25
 * Copyright (c) 2015-2019 Devsoft Baltic OÜ  - http://surveyjs.io/
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */
!(function (e, t) {
  'object' == typeof exports && 'object' == typeof module
    ? (module.exports = t(
        require('inputmask'),
        require('nouislider'),
        require('sortablejs'),
        require('bootstrap-slider')
      ))
    : 'function' == typeof define && define.amd
    ? define('surveyjs-widgets', ['inputmask', 'nouislider', 'sortablejs', 'bootstrap-slider'], t)
    : 'object' == typeof exports
    ? (exports['surveyjs-widgets'] = t(
        require('inputmask'),
        require('nouislider'),
        require('sortablejs'),
        require('bootstrap-slider')
      ))
    : (e['surveyjs-widgets'] = t(e.Inputmask, e.noUiSlider, e.Sortable, e.Slider));
})('undefined' != typeof self ? self : this, function (e, t, n, i) {
  return (function (e) {
    function t(i) {
      if (n[i]) return n[i].exports;
      var o = (n[i] = { i: i, l: !1, exports: {} });
      return e[i].call(o.exports, o, o.exports, t), (o.l = !0), o.exports;
    }
    var n = {};
    return (
      (t.m = e),
      (t.c = n),
      (t.d = function (e, n, i) {
        t.o(e, n) || Object.defineProperty(e, n, { configurable: !1, enumerable: !0, get: i });
      }),
      (t.n = function (e) {
        var n =
          e && e.__esModule
            ? function () {
                return e.default;
              }
            : function () {
                return e;
              };
        return t.d(n, 'a', n), n;
      }),
      (t.o = function (e, t) {
        return Object.prototype.hasOwnProperty.call(e, t);
      }),
      (t.p = ''),
      t((t.s = 21))
    );
  })([
    function (e, t, n) {
      'use strict';
      function i(e) {
        return 'string' == typeof e ? (e || '').replace(/(['])/g, '\\$1') : e;
      }
      function o(e, t) {
        t = t || window.$;
        var n = {
          className: 'iradio_square-blue',
          checkboxClass: 'iradio_square-blue',
          radioClass: 'iradio_square-blue',
          name: 'icheck',
          widgetIsLoaded: function () {
            return 'function' == typeof t && !!t.fn.iCheck;
          },
          isFit: function (e) {
            var t = e.getType();
            return 'radiogroup' === t || 'checkbox' === t || 'matrix' === t;
          },
          isDefaultRender: !0,
          afterRender: function (e, n) {
            function o(e, t) {
              if (!Array.isArray(e)) return -1;
              for (var n = 0; n < e.length; n++) {
                if (e[n] == t) return n;
                if (e[n] && e[n].toString().toLowerCase() == t) return n;
              }
              return -1;
            }
            function a(e) {
              e.iCheck('uncheck'), e[0].parentElement.classList.remove('checked');
            }
            var r = this,
              d = t(n);
            d.find('.sv-item__decorator').hide(), d.find('input').data({ iCheck: void 0 });
            var s = !1,
              c = function () {
                var t = d.find('input');
                t.iCheck({
                  checkboxClass: e.checkboxClass || r.checkboxClass || r.className,
                  radioClass: e.radioClass || r.radioClass || r.className,
                }),
                  t.on('ifChecked', function (t) {
                    if (!s)
                      if ('matrix' === e.getType())
                        e.generatedVisibleRows.forEach(function (e, n, i) {
                          e.fullName === t.target.name && (e.value = t.target.value);
                        });
                      else if ('checkbox' === e.getType()) {
                        var n = e.value || [],
                          i = o(n, t.target.value);
                        -1 === i && (e.value = n.concat([t.target.value]));
                      } else e.value = t.target.value;
                  }),
                  t.on('ifUnchecked', function (t) {
                    if (!s && 'checkbox' === e.getType()) {
                      var n = (e.value || []).slice(),
                        i = o(n, t.target.value);
                      i >= 0 && (n.splice(i, 1), (e.value = n));
                    }
                  });
              },
              u = function () {
                if (((s = !0), 'matrix' !== e.getType())) {
                  var r = e.value;
                  if ((Array.isArray(r) || (r = [r]), 'checkbox' == e.getType())) {
                    var c = e.value;
                    e.visibleChoices.forEach(function (e) {
                      var t = d.find("input[value='" + i(e.value) + "']");
                      if (t) {
                        if (o(c, e.value) > -1) t.iCheck('check');
                        else {
                          !!t[0].checked &&
                            (t.removeAttr('checked'),
                            t.parent().hasClass('checked')
                              ? a(t)
                              : setTimeout(function () {
                                  a(t);
                                }));
                        }
                      }
                    });
                  } else
                    r.forEach(function (e) {
                      d.find("input[value='" + i(e) + "']").iCheck('check');
                    });
                } else
                  e.generatedVisibleRows.forEach(function (e, o, a) {
                    e.value &&
                      t(n)
                        .find("input[name='" + e.fullName + "'][value='" + i(e.value) + "']")
                        .iCheck('check');
                  });
                s = !1;
              };
            c(),
              (e.visibleChoicesChangedCallback = function () {
                c(), d.find('.sv-item__decorator').hide();
              }),
              (e.valueChangedCallback = u),
              u();
          },
          willUnmount: function (e, n) {
            t(n).find('input').iCheck('destroy'), (e.visibleChoicesChangedCallback = null);
          },
        };
        e.JsonObject.metaData.addProperty('radiogroup', {
          name: 'radioClass',
          category: 'general',
        }),
          e.JsonObject.metaData.addProperty('checkbox', {
            name: 'checkboxClass',
            category: 'general',
          }),
          e.CustomWidgetCollection.Instance.addCustomWidget(n, 'type');
      }
      Object.defineProperty(t, '__esModule', { value: !0 }),
        'undefined' != typeof Survey && o(Survey, window.$),
        (t.default = o);
    },
    function (e, t, n) {
      'use strict';
      function i(e, t) {
        t = t || window.$;
        var n = {
          activatedBy: 'property',
          name: 'select2',
          widgetIsLoaded: function () {
            return 'function' == typeof t && !!t.fn.select2;
          },
          isFit: function (e) {
            return 'property' == n.activatedBy
              ? 'select2' === e.renderAs && 'dropdown' === e.getType()
              : 'type' == n.activatedBy
              ? 'dropdown' === e.getType()
              : 'customtype' == n.activatedBy && 'select2' === e.getType();
          },
          activatedByChanged: function (t) {
            this.widgetIsLoaded() &&
              ((n.activatedBy = t),
              e.JsonObject.metaData.removeProperty('dropdown', 'renderAs'),
              'property' == t &&
                (e.JsonObject.metaData.addProperty('dropdown', {
                  name: 'renderAs',
                  category: 'general',
                  default: 'default',
                  choices: ['select2', 'default'],
                }),
                e.JsonObject.metaData.addProperty('dropdown', {
                  dependsOn: 'renderAs',
                  category: 'general',
                  name: 'select2Config',
                  visibleIf: function (e) {
                    return 'select2' == e.renderAs;
                  },
                })),
              'customtype' == t &&
                (e.JsonObject.metaData.addClass('select2', [], null, 'dropdown'),
                e.JsonObject.metaData.addProperty('select2', {
                  name: 'select2Config',
                  category: 'general',
                  default: null,
                })));
          },
          htmlTemplate: "<div><select style='width: 100%;'></select><textarea></textarea></div>",
          afterRender: function (e, n) {
            var i = e.select2Config,
              o = i && 'string' == typeof i ? JSON.parse(i) : i;
            o || (o = {});
            var a = t(n).is('select') ? t(n) : t(n).find('select'),
              r = t(n).find('textarea');
            r.addClass(e.cssClasses.other),
              r.bind('input propertychange', function () {
                s || (e.comment = r.val());
              });
            var d = function () {
                r.val(e.comment), e.isOtherSelected ? r.show() : r.hide();
              },
              s = !1,
              c = function () {
                if (!s) {
                  if (((s = !0), a.find("option[value='" + (e.value || '') + "']").length))
                    a.val(e.value).trigger('change');
                  else if (null !== e.value && void 0 !== e.value) {
                    var t = new Option(e.value, e.value, !0, !0);
                    a.append(t).trigger('change');
                  }
                  d(), (s = !1);
                }
              },
              u = function () {
                if (
                  (a.select2().empty(),
                  !o.placeholder &&
                    e.showOptionsCaption &&
                    ((o.placeholder = e.optionsCaption), (o.allowClear = !0)),
                  o.theme || (o.theme = 'classic'),
                  (o.disabled = e.isReadOnly),
                  o.ajax)
                )
                  a.select2(o), (e.keepIncorrectValues = !0);
                else {
                  var t = [];
                  (o.placeholder || e.showOptionsCaption) && t.push({ id: '', text: '' }),
                    (o.data = t.concat(
                      e.visibleChoices.map(function (e) {
                        return { id: e.value, text: e.text };
                      })
                    )),
                    e.clearIncorrectValues(),
                    a.select2(o);
                }
                n.querySelector('.select2') && (n.querySelector('.select2').style.width = '100%'),
                  n.nextElementSibling && (n.nextElementSibling.style.marginBottom = '1px'),
                  c();
              };
            r.prop('disabled', e.isReadOnly),
              (e.readOnlyChangedCallback = function () {
                a.prop('disabled', e.isReadOnly), r.prop('disabled', e.isReadOnly);
              }),
              e.registerFunctionOnPropertyValueChanged('visibleChoices', function () {
                u();
              }),
              u(),
              a.on('select2:select', function (t) {
                setTimeout(function () {
                  (e.renderedValue = t.target.value), d();
                }, 1);
              }),
              a.on('select2:opening', function (e) {
                t(this).data('unselecting') &&
                  (t(this).removeData('unselecting'), e.preventDefault());
              }),
              a.on('select2:unselecting', function (n) {
                t(this).data('unselecting', !0),
                  setTimeout(function () {
                    (e.renderedValue = null), d();
                  }, 1);
              }),
              (e.valueChangedCallback = c),
              c();
          },
          willUnmount: function (e, n) {
            (e.readOnlyChangedCallback = null), (e.valueChangedCallback = null);
            var i = t(n).find('select');
            i.data('select2') &&
              i
                .off('select2:select')
                .off('select2:unselecting')
                .off('select2:opening')
                .select2('destroy');
          },
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(n);
      }
      Object.defineProperty(t, '__esModule', { value: !0 }),
        'undefined' != typeof Survey && i(Survey, window.$),
        (t.default = i);
    },
    function (e, t, n) {
      'use strict';
      function i(e) {
        var t = {
          name: 'maskedit',
          numericGroupSeparator: ',',
          numericAutoGroup: !0,
          numericDigits: 2,
          numericDigitsOptional: !1,
          numericPlaceholder: '0',
          autoUnmask: !0,
          clearIncomplete: !0,
          widgetIsLoaded: function () {
            return void 0 !== a.a;
          },
          isFit: function (e) {
            return (
              'multipletext' == e.getType() ||
              ('text' == e.getType() && ('none' != e.inputMask || e.inputFormat))
            );
          },
          isDefaultRender: !0,
          activatedByChanged: function (t) {
            if (!e.JsonObject.metaData.findProperty('text', 'inputMask')) {
              var n = [
                { name: 'inputFormat', category: 'general' },
                { name: 'prefix', category: 'general', visible: !1 },
                { name: 'suffix', category: 'general', visible: !1 },
                { name: 'autoUnmask:boolean', category: 'general', default: !0 },
                { name: 'clearIncomplete:boolean', category: 'general', default: !0 },
                {
                  name: 'inputMask',
                  category: 'general',
                  default: 'none',
                  choices: ['none', 'datetime', 'currency', 'decimal', 'email', 'phone', 'ip'],
                },
              ];
              e.JsonObject.metaData.addProperties('text', n),
                e.JsonObject.metaData.addProperties('matrixdropdowncolumn', n),
                e.JsonObject.metaData.addProperties('multipletextitem', n);
            }
          },
          applyInputMask: function (e, t) {
            var n = this,
              i = 'none' != e.inputMask ? e.inputMask : e.inputFormat,
              o = {
                autoUnmask: void 0 !== e.autoUnmask ? e.autoUnmask : n.autoUnmask,
                clearIncomplete:
                  void 0 !== e.clearIncomplete ? e.clearIncomplete : n.clearIncomplete,
              };
            'none' != e.inputMask && (o.inputFormat = e.inputFormat),
              ('currency' != e.inputMask && 'decimal' != e.inputMask) ||
                ((o.groupSeparator = n.numericGroupSeparator), (o.autoGroup = n.numericAutoGroup)),
              'currency' == e.inputMask &&
                ((o.digits = n.numericDigits),
                (o.digitsOptional = n.numericDigitsOptional),
                (o.prefix = e.prefix || ''),
                (o.suffix = e.suffix || ''),
                (o.placeholder = n.numericPlaceholder)),
              'phone' == e.inputMask && e.inputFormat && (i = e.inputFormat),
              a()(i, o).mask(t),
              (t.onblur = function () {
                t.inputmask && e.value === t.inputmask.getemptymask() && (e.value = '');
              });
            var r =
              'multipletextitem' === e.getType()
                ? e.editorValue.customWidgetData
                : e.customWidgetData;
            t.oninput = function () {
              r.isNeedRender = !0;
            };
            var d = function () {
              t.inputmask &&
                (t.inputmask.isComplete()
                  ? (e.value = o.autoUnmask ? t.inputmask.unmaskedvalue() : t.value)
                  : (e.value = null));
            };
            t.onfocusout = t.onchange = d;
            var s = function () {
              t.value = void 0 === e.value || null === e.value ? '' : e.value;
            };
            (e.valueChangedCallback = s), s();
          },
          afterRender: function (e, t) {
            if ('multipletext' != e.getType()) {
              var n = t.querySelector('input') || t;
              this.applyInputMask(e, n);
            } else
              for (var i = 0; i < e.items.length; i++) {
                var o = e.items[i];
                if ('none' != o.inputMask || o.inputFormat) {
                  var n = t.querySelector('#' + o.editor.inputId);
                  n && this.applyInputMask(o, n);
                }
              }
          },
          willUnmount: function (e, t) {
            var n = t.querySelector('input') || t;
            n && n.inputmask && n.inputmask.remove();
          },
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(t);
      }
      Object.defineProperty(t, '__esModule', { value: !0 });
      var o = n(3),
        a = n.n(o);
      'undefined' != typeof Survey && i(Survey), (t.default = i);
    },
    function (t, n) {
      t.exports = e;
    },
    function (e, t, n) {
      'use strict';
      function i(e, t) {
        t = t || window.$;
        var n = {
          name: 'barrating',
          title: 'Bar rating',
          iconName: 'icon-barrating',
          widgetIsLoaded: function () {
            return 'function' == typeof t && !!t.fn.barrating;
          },
          defaultJSON: { choices: [1, 2, 3, 4, 5] },
          isFit: function (e) {
            return 'barrating' === e.getType();
          },
          isDefaultRender: !0,
          activatedByChanged: function (t) {
            e.JsonObject.metaData.addClass(
              'barrating',
              [
                { name: 'hasOther', visible: !1 },
                { name: 'otherText', visible: !1 },
                { name: 'optionsCaption', visible: !1 },
                { name: 'otherErrorText', visible: !1 },
                { name: 'storeOthersAsComment', visible: !1 },
                { name: 'renderAs', visible: !1 },
                { name: 'select2Config', visible: !1 },
              ],
              null,
              'dropdown'
            ),
              e.JsonObject.metaData.addProperty('barrating', {
                name: 'showValues:boolean',
                default: !1,
                category: 'general',
              }),
              e.JsonObject.metaData.addProperty('barrating', {
                name: 'ratingTheme',
                category: 'general',
                default: 'css-stars',
                choices: [
                  'fontawesome-stars',
                  'css-stars',
                  'bars-pill',
                  'bars-1to10',
                  'bars-movie',
                  'bars-reversed',
                  'bars-horizontal',
                  'fontawesome-stars-o',
                ],
              });
          },
          afterRender: function (e, n) {
            var i,
              o,
              a = t(n).is('select') ? t(n).parent().parent()[0] : t(n).parent()[0],
              r = function () {
                (i = t("<select class='sv-widget-select'></select>")),
                  e.visibleChoices.forEach(function (e) {
                    i.append('<option value="{0}">{1}</option>'.format(e.value, e.text));
                  }),
                  (o = t(a).find('[id="{0}"]'.format(e.inputId))),
                  o.css('display', 'none'),
                  o.after(i),
                  (i[0].selectedIndex = -1);
              },
              d = function () {
                o.css('display', ''), i.barrating('destroy'), i.remove();
              },
              s = function () {
                i.barrating('show', {
                  theme: e.ratingTheme,
                  initialRating: e.value,
                  showValues: e.showValues,
                  showSelectedRating: !1,
                  readonly: e.isReadOnly,
                  onSelect: function (t, n) {
                    (c = !0), (e.value = t), (c = !1);
                  },
                });
              };
            r(), s(), i.parents()[0] && (i.parents()[0].style.marginBottom = '3px');
            var c = !1;
            (e.valueChangedCallback = function () {
              c ||
                t(a).find('select.sv-widget-select')[0].value === e.value ||
                t(a).find('select.sv-widget-select').barrating('set', e.value);
            }),
              (e.__barratingOnPropertyChangedCallback = function (e, t) {
                'ratingTheme' == t.name && (i.barrating('destroy'), s());
              }),
              e.onPropertyChanged.add(e.__barratingOnPropertyChangedCallback),
              (e.readOnlyChangedCallback = function () {
                d(), r(), s();
              }),
              (e.visibleChoicesChangedCallback = function () {
                s();
              });
          },
          willUnmount: function (e, n) {
            var i = t(n).is('select') ? t(n).parent().parent() : t(n).parent(),
              o = i.find('select.sv-widget-select');
            o.barrating('destroy'),
              o.remove(),
              (e.valueChangedCallback = void 0),
              e.onPropertyChanged.remove(e.__barratingOnPropertyChangedCallback),
              (e.__barratingOnPropertyChangedCallback = void 0);
          },
          pdfQuestionType: 'dropdown',
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(n, 'customtype');
      }
      Object.defineProperty(t, '__esModule', { value: !0 }),
        'undefined' != typeof Survey && i(Survey, window.$),
        (t.default = i);
    },
    function (e, t, n) {
      'use strict';
      function i(e, t) {
        (t = t || window.$),
          !t.fn.bootstrapDP &&
            t.fn.datepicker &&
            t.fn.datepicker.noConflict &&
            ((t.fn.bootstrapDP = t.fn.datepicker.noConflict()),
            t.fn.datepicker || (t.fn.datepicker = t.fn.bootstrapDP));
        var n = {
          name: 'datepicker',
          title: 'Date picker',
          iconName: 'icon-datepicker',
          widgetIsLoaded: function () {
            return !!t && !!t.fn.datepicker && !t.fn.datepicker.noConflict;
          },
          isFit: function (e) {
            return 'datepicker' === e.getType();
          },
          htmlTemplate: "<input class='form-control widget-datepicker' type='text'>",
          activatedByChanged: function (t) {
            e.JsonObject.metaData.addClass(
              'datepicker',
              [
                { name: 'inputType', visible: !1 },
                { name: 'inputFormat', visible: !1 },
                { name: 'inputMask', visible: !1 },
              ],
              null,
              'text'
            ),
              e.JsonObject.metaData.addProperty('datepicker', {
                name: 'dateFormat',
                category: 'general',
              }),
              e.JsonObject.metaData.addProperty('datepicker', {
                name: 'config',
                category: 'general',
                visible: !1,
                default: null,
              }),
              e.JsonObject.metaData.addProperty('datepicker', {
                name: 'maxDate',
                category: 'general',
              }),
              e.JsonObject.metaData.addProperty('datepicker', {
                name: 'minDate',
                category: 'general',
              });
          },
          afterRender: function (e, n) {
            function i() {
              e.value ? d.datepicker('setDate', e.value) : d.datepicker('setDate', null);
            }
            var o = t(n).is('.widget-datepicker') ? t(n) : t(n).find('.widget-datepicker');
            o.addClass(e.css.text.root);
            var a = !1,
              r = t.extend(!0, {}, e.config || {});
            e.placeHolder && o.attr('placeholder', e.placeHolder),
              void 0 === r.dateFormat && (r.dateFormat = e.dateFormat ? e.dateFormat : void 0),
              void 0 === r.option && (r.option = { minDate: null, maxDate: null }),
              e.minDate && (r.minDate = e.minDate),
              e.maxDate && (r.maxDate = e.maxDate),
              (r.disabled = e.isReadOnly),
              void 0 === r.onSelect &&
                (r.onSelect = function (t) {
                  (a = !0), (e.value = t), (a = !1), (this.fixFocusIE = !0);
                }),
              (r.fixFocusIE = !1),
              (r.onClose = function (e, t) {
                this.fixFocusIE = !0;
              }),
              (r.beforeShow = function (e, t) {
                var n = !navigator.userAgent.match(/Trident\/7\./) || !this.fixFocusIE;
                return (this.fixFocusIE = !1), n;
              });
            var d = o.datepicker(r);
            o.keyup(function (e) {
              (8 != e.keyCode && 46 != e.keyCode) || t.datepicker._clearDate(this);
            }),
              (e.readOnlyChangedCallback = function () {
                o.datepicker('option', 'disabled', e.isReadOnly);
              }),
              e.registerFunctionOnPropertyValueChanged('dateFormat', function () {
                e.dateFormat && d.datepicker('option', 'dateFormat', e.dateFormat), i();
              }),
              (e.valueChangedCallback = function () {
                a || (i(), o.blur());
              }),
              e.valueChangedCallback();
          },
          willUnmount: function (e, n) {
            (t(n).is('.widget-datepicker') ? t(n) : t(n).find('.widget-datepicker')).datepicker(
              'destroy'
            );
          },
          pdfQuestionType: 'text',
        };
        (e.matrixDropdownColumnTypes.datepicker = { properties: ['placeHolder'] }),
          e.CustomWidgetCollection.Instance.addCustomWidget(n, 'customtype');
      }
      Object.defineProperty(t, '__esModule', { value: !0 }),
        'undefined' != typeof Survey && i(Survey, window.$),
        (t.default = i);
    },
    function (e, t, n) {
      'use strict';
      function i(e) {
        var t = {
          name: 'nouislider',
          title: 'noUiSlider',
          iconName: 'icon-nouislider',
          widgetIsLoaded: function () {
            return void 0 !== a.a;
          },
          isFit: function (e) {
            return 'nouislider' === e.getType();
          },
          htmlTemplate: '<div><div></div></div><style>.noUi-origin { width: 0; }</style>',
          activatedByChanged: function (t) {
            e.JsonObject.metaData.addClass('nouislider', [], null, 'empty'),
              e.JsonObject.metaData.addProperties('nouislider', [
                { name: 'step:number', category: 'slider', categoryIndex: 1, default: 1 },
                { name: 'rangeMin:number', category: 'slider', default: 0 },
                { name: 'rangeMax:number', category: 'slider', default: 100 },
                { name: 'pipsMode', category: 'slider', default: 'positions' },
                {
                  name: 'pipsValues:itemvalues',
                  category: 'slider',
                  default: [0, 25, 50, 75, 100],
                },
                { name: 'pipsText:itemvalues', category: 'slider', default: [0, 25, 50, 75, 100] },
                { name: 'pipsDensity:number', category: 'slider', default: 5 },
                {
                  name: 'orientation',
                  category: 'slider',
                  default: 'horizontal',
                  choices: ['horizontal', 'vertical'],
                },
                { name: 'direction:string', category: 'slider', default: 'ltr' },
                { name: 'tooltips:boolean', category: 'slider', default: !0 },
              ]);
          },
          afterRender: function (e, t) {
            (t.style.paddingBottom = '19px'),
              (t.style.paddingLeft = '20px'),
              (t.style.paddingRight = '20px'),
              (t.style.paddingTop = '44px'),
              (t = t.children[0]),
              (t.style.marginBottom = '60px'),
              'vertical' === e.orientation && (t.style.height = '250px');
            var n = a.a.create(t, {
              start: e.value || (e.rangeMin + e.rangeMax) / 2,
              connect: [!0, !1],
              step: e.step,
              tooltips: e.tooltips,
              pips: {
                mode: e.pipsMode || 'positions',
                values: e.pipsValues.map(function (e) {
                  var t = e;
                  return void 0 !== e.value && (t = e.value), parseInt(t);
                }),
                density: e.pipsDensity || 5,
                format: {
                  to: function (t) {
                    var n = t;
                    return (
                      e.pipsText.map(function (e) {
                        void 0 !== e.text && t === e.value && (n = e.text);
                      }),
                      n
                    );
                  },
                },
              },
              range: { min: e.rangeMin, max: e.rangeMax },
              orientation: e.orientation,
              direction: e.direction,
            });
            n.on('change', function () {
              e.value = Number(n.get());
            });
            var i = function () {
              n.set(e.value);
            };
            e.isReadOnly && t.setAttribute('disabled', !0),
              i(),
              (e.noUiSlider = n),
              (e.valueChangedCallback = i),
              (e.readOnlyChangedCallback = function () {
                e.isReadOnly ? t.setAttribute('disabled', !0) : t.removeAttribute('disabled');
              });
          },
          willUnmount: function (e, t) {
            e.noUiSlider && (e.noUiSlider.destroy(), (e.noUiSlider = null)),
              (e.readOnlyChangedCallback = null);
          },
          pdfRender: function (e, t) {
            if ('nouislider' === t.question.getType()) {
              var n = t.module.SurveyHelper.createPoint(
                t.module.SurveyHelper.mergeRects.apply(null, t.bricks)
              );
              (n.xLeft += t.controller.unitWidth),
                (n.yTop += t.controller.unitHeight * t.module.FlatQuestion.CONTENT_GAP_VERT_SCALE);
              var i = t.module.SurveyHelper.createTextFieldRect(n, t.controller),
                o = new t.module.TextFieldBrick(
                  t.question,
                  t.controller,
                  i,
                  !0,
                  t.question.id,
                  t.question.value || t.question.defaultValue || '',
                  '',
                  t.question.isReadOnly,
                  !1,
                  'text'
                );
              t.bricks.push(o);
            }
          },
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(t, 'customtype');
      }
      Object.defineProperty(t, '__esModule', { value: !0 });
      var o = n(7),
        a = n.n(o);
      'undefined' != typeof Survey && i(Survey), (t.default = i);
    },
    function (e, n) {
      e.exports = t;
    },
    function (e, t, n) {
      'use strict';
      function i(e, t) {
        t = t || window.$;
        var n = {
          name: 'tagbox',
          title: 'Tag box',
          iconName: 'icon-tagbox',
          widgetIsLoaded: function () {
            return 'function' == typeof t && !!t.fn.select2;
          },
          defaultJSON: { choices: ['Item 1', 'Item 2', 'Item 3'] },
          htmlTemplate:
            "<div><select multiple='multiple' style='width: 100%;'></select><textarea></textarea></div>",
          isFit: function (e) {
            return 'tagbox' === e.getType();
          },
          activatedByChanged: function (t) {
            e.JsonObject.metaData.addClass(
              'tagbox',
              [
                { name: 'hasOther:boolean', visible: !1 },
                { name: 'hasSelectAll:boolean', visible: !1 },
                { name: 'hasNone:boolean', visible: !1 },
                { name: 'otherText', visible: !1 },
                { name: 'selectAllText', visible: !1 },
                { name: 'noneText', visible: !1 },
              ],
              null,
              'checkbox'
            ),
              e.JsonObject.metaData.addProperty('tagbox', {
                name: 'select2Config',
                category: 'general',
                default: null,
              }),
              e.JsonObject.metaData.addProperty('tagbox', {
                name: 'placeholder',
                category: 'general',
                default: '',
              }),
              e.JsonObject.metaData.addProperty('tagbox', {
                name: 'allowAddNewTag:boolean',
                category: 'general',
                default: !1,
              }),
              (e.matrixDropdownColumnTypes.tagbox = {
                properties: [
                  'choices',
                  'choicesOrder',
                  'choicesByUrl',
                  'optionsCaption',
                  'otherText',
                  'choicesVisibleIf',
                ],
              });
          },
          fixStyles: function (e) {
            e.parentElement.querySelector('.select2-search__field').style.border = 'none';
          },
          afterRender: function (e, n) {
            var i = this,
              o = e.select2Config,
              a = o && 'string' == typeof o ? JSON.parse(o) : o,
              r = t(n).is('select') ? t(n) : t(n).find('select');
            i.willUnmount(e, n),
              a || (a = {}),
              (a.placeholder = e.placeholder),
              (a.tags = e.allowAddNewTag),
              (a.disabled = e.isReadOnly),
              (a.theme = 'classic'),
              r.select2(a);
            var d = t(n).find('textarea');
            e.survey &&
              e.survey.css &&
              e.survey.css.checkbox &&
              d.addClass(e.survey.css.checkbox.other),
              d.bind('input propertychange', function () {
                e.comment = d.val();
              });
            var s = function () {
              d.val(e.comment), e.isOtherSelected ? d.show() : d.hide();
            };
            i.fixStyles(n);
            var e,
              c = function () {
                e.hasSelectAll && e.isAllSelected
                  ? r.val([e.selectAllItemValue.value].concat(e.value)).trigger('change')
                  : r.val(e.value).trigger('change'),
                  i.fixStyles(n),
                  s();
              },
              u = function () {
                r.select2().empty(),
                  a.ajax
                    ? r.select2(a)
                    : ((a.data = e.visibleChoices.map(function (e) {
                        return { id: e.value, text: e.text };
                      })),
                      r.select2(a)),
                  c();
              },
              l = function (t) {
                return e.hasSelectAll && t === e.selectAllItemValue.value;
              };
            (e._propertyValueChangedFnSelect2 = function () {
              u();
            }),
              d.prop('disabled', e.isReadOnly),
              (e.readOnlyChangedCallback = function () {
                r.prop('disabled', e.isReadOnly), d.prop('disabled', e.isReadOnly);
              }),
              e.registerFunctionOnPropertyValueChanged(
                'visibleChoices',
                e._propertyValueChangedFnSelect2
              ),
              (e.valueChangedCallback = c),
              r.on('select2:select', function (t) {
                l(t.params.data.id)
                  ? e.selectAll()
                  : (e.value = (e.value || []).concat(t.params.data.id)),
                  s();
              }),
              r.on('select2:unselect', function (t) {
                var n = (e.value || []).indexOf(t.params.data.id);
                if (l(t.params.data.id)) e.clearValue();
                else if (-1 !== n) {
                  var i = [].concat(e.value);
                  i.splice(n, 1), (e.value = i);
                }
                s();
              }),
              u();
          },
          willUnmount: function (e, n) {
            if (e._propertyValueChangedFnSelect2) {
              var i = t(n).find('select');
              i.data('select2') && i.off('select2:select').select2('destroy'),
                (e.readOnlyChangedCallback = null),
                (e.valueChangedCallback = null),
                e.unRegisterFunctionOnPropertyValueChanged(
                  'visibleChoices',
                  e._propertyValueChangedFnSelect2
                ),
                (e._propertyValueChangedFnSelect2 = void 0);
            }
          },
          pdfQuestionType: 'checkbox',
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(n, 'customtype');
      }
      Object.defineProperty(t, '__esModule', { value: !0 }),
        'undefined' != typeof Survey && i(Survey, window.$),
        (t.default = i);
    },
    function (e, t, n) {
      'use strict';
      function i(e) {
        var t = {
          name: 'sortablelist',
          title: 'Sortable list',
          iconName: 'icon-sortablejs',
          widgetIsLoaded: function () {
            return void 0 !== a.a;
          },
          defaultJSON: { choices: ['Item 1', 'Item 2', 'Item 3'] },
          rootStyle: 'width:100%:',
          areaStyle: 'border: 1px solid #1ab394; width:100%; min-height:50px; margin-top:10px;',
          itemStyle: 'background-color:#1ab394;color:#fff;margin:5px;padding:10px;',
          isFit: function (e) {
            return 'sortablelist' === e.getType();
          },
          htmlTemplate: '<div></div>',
          activatedByChanged: function (t) {
            e.JsonObject.metaData.addClass(
              'sortablelist',
              [
                { name: 'hasOther', visible: !1 },
                { name: 'storeOthersAsComment', visible: !1 },
                { name: 'hasNone', visible: !1 },
                { name: 'renderAs', visible: !1 },
                { name: 'checkboxClass', visible: !1 },
                { name: 'hasSelectAll', visible: !1 },
                { name: 'noneText', visible: !1 },
                { name: 'selectAllText', visible: !1 },
              ],
              null,
              'checkbox'
            ),
              e.JsonObject.metaData.addProperty('sortablelist', {
                name: 'emptyText',
                default: 'Move items here.',
                category: 'general',
              }),
              e.JsonObject.metaData.addProperty('sortablelist', {
                name: 'useDefaultTheme:switch',
                default: !0,
                category: 'general',
              }),
              e.JsonObject.metaData.addProperty('sortablelist', {
                name: 'maxAnswersCount:number',
                default: -1,
                category: 'general',
              });
          },
          afterRender: function (t, n) {
            var i = this;
            t.useDefaultTheme || ((i.rootStyle = ''), (i.itemStyle = ''), (i.areaStyle = '')),
              (n.style.cssText = i.rootStyle),
              (n.className = 'sjs-sortablejs-root');
            var o,
              r,
              d = document.createElement('div'),
              s = document.createElement('span'),
              c = document.createElement('div');
            (d.style.cssText = i.areaStyle),
              (d.style.boxSizing = 'border-box'),
              (d.className = 'sjs-sortablejs-result'),
              (s.innerHTML = t.emptyText),
              d.appendChild(s),
              (c.style.cssText = i.areaStyle),
              (c.style.boxSizing = 'border-box'),
              (c.className = 'sjs-sortablejs-source'),
              n.appendChild(d),
              n.appendChild(c);
            var u = function (e) {
                var n = t.value;
                if (!Array.isArray(n)) return !1;
                for (var i = 0; i < n.length; i++) if (n[i] == e) return !0;
                return !1;
              },
              l = function (e, t) {
                var n = t ? d : c,
                  o = document.createElement('div');
                (o.className = 'sjs-sortablejs-item'),
                  (o.style.cssText = i.itemStyle),
                  (o.innerText = e.text),
                  (o.dataset.value = e.value),
                  n.appendChild(o),
                  e.onPropertyChanged.add(function (e, t) {
                    o.innerText = e.text;
                  });
              },
              f = function () {
                var e = [];
                return (
                  t.visibleChoices.forEach(function (t) {
                    u(t.value) || e.push(t);
                  }),
                  e
                );
              },
              m = function () {
                var n = [],
                  i = t.value;
                if (!Array.isArray(i)) return n;
                for (var o = 0; o < i.length; o++) {
                  var a = e.ItemValue.getItemByValue(t.visibleChoices, i[o]);
                  a && n.push(a);
                }
                return n;
              },
              p = !1,
              g = function () {
                if (!p) {
                  (d.innerHTML = ''), d.appendChild(s), (c.innerHTML = '');
                  var e = f(),
                    t = m();
                  (s.style.display = t.length > 0 ? 'none' : ''),
                    t.forEach(function (e) {
                      l(e, !0);
                    }),
                    e.forEach(function (e) {
                      l(e, !1);
                    });
                }
              };
            (r = t.resultEl = a.a.create(d, {
              animation: 150,
              disabled: t.isReadOnly,
              group: {
                name: t.name,
                put: function (e, n) {
                  return (
                    e.options.group &&
                    n.options.group &&
                    e.options.group.name === n.options.group.name &&
                    (t.maxAnswersCount < 0 || e.el.children.length <= t.maxAnswersCount)
                  );
                },
              },
              onSort: function (e) {
                var n = [];
                if (1 === d.children.length) s.style.display = '';
                else {
                  s.style.display = 'none';
                  for (var i = 0; i < d.children.length; i++)
                    void 0 !== d.children[i].dataset.value && n.push(d.children[i].dataset.value);
                }
                (p = !0), (t.value = n), (p = !1);
              },
            })),
              (o = t.sourceEl = a.a.create(c, {
                animation: 150,
                disabled: t.isReadOnly,
                group: t.name,
              })),
              (t.valueChangedCallback = g),
              t.onPropertyChanged.add(function (e, n) {
                'emptyText' == n.name && (s.innerHTML = t.emptyText);
              }),
              (t.readOnlyChangedCallback = function () {
                t.isReadOnly
                  ? ((r.options.disabled = !0), (o.options.disabled = !0))
                  : ((r.options.disabled = !1), (o.options.disabled = !1));
              }),
              t.registerFunctionOnPropertyValueChanged('visibleChoices', g),
              g();
          },
          willUnmount: function (e, t) {
            e.resultEl.destroy(), e.sourceEl.destroy(), (e.readOnlyChangedCallback = null);
          },
          pdfQuestionType: 'checkbox',
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(t, 'customtype');
      }
      Object.defineProperty(t, '__esModule', { value: !0 });
      var o = n(10),
        a = n.n(o);
      'undefined' != typeof Survey && i(Survey), (t.default = i);
    },
    function (e, t) {
      e.exports = n;
    },
    function (e, t, n) {
      'use strict';
      function i(e) {
        var t = {
          name: 'editor',
          title: 'Editor',
          iconName: 'icon-editor',
          widgetIsLoaded: function () {
            return 'undefined' != typeof CKEDITOR;
          },
          isFit: function (e) {
            return 'editor' === e.getType();
          },
          htmlTemplate: "<textarea rows='10' cols='80' style: {width:'100%'}></textarea>",
          activatedByChanged: function (t) {
            e.JsonObject.metaData.addClass('editor', [], null, 'empty'),
              e.JsonObject.metaData.addProperty('editor', {
                name: 'height',
                default: 300,
                category: 'general',
              });
          },
          afterRender: function (e, t) {
            var n = e.name;
            (CKEDITOR.editorConfig = function (t) {
              (t.language = 'es'), (t.height = e.height), (t.toolbarCanCollapse = !0);
            }),
              (t.name = n),
              CKEDITOR.instances[n] &&
                (CKEDITOR.instances[n].removeAllListeners(),
                CKEDITOR.remove(CKEDITOR.instances[n]));
            var i = CKEDITOR.replace(t);
            CKEDITOR.instances[n].config.readOnly = e.isReadOnly;
            var o = !1,
              a = function () {
                o || void 0 === e.value || i.setData(e.value);
              };
            i.on('change', function () {
              (o = !0), (e.value = i.getData()), (o = !1);
            }),
              (e.valueChangedCallback = a),
              (e.readOnlyChangedCallback = function () {
                e.isReadOnly ? i.setReadOnly(!0) : i.setReadOnly(!1);
              }),
              a();
          },
          willUnmount: function (e, t) {
            (e.readOnlyChangedCallback = null), CKEDITOR.instances[e.name].destroy(!1);
          },
          pdfRender: function (t, n) {
            if ('editor' === n.question.getType()) {
              var i = new e.LocalizableString(t, !0);
              (i.text = n.question.value), (n.question.locHtml = i), (n.question.renderAs = 'auto');
              var o = n.repository.create(t, n.question, n.controller, 'html');
              return new Promise(function (e) {
                o.generateFlats(n.point).then(function (t) {
                  (n.bricks = t), e();
                });
              });
            }
          },
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(t, 'customtype');
      }
      Object.defineProperty(t, '__esModule', { value: !0 }),
        'undefined' != typeof Survey && i(Survey),
        (t.default = i);
    },
    function (e, t, n) {
      'use strict';
      function i(e, t) {
        t = t || window.$;
        var n = {
          name: 'autocomplete',
          widgetIsLoaded: function () {
            return 'function' == typeof t && !!t.fn.easyAutocomplete;
          },
          isFit: function (e) {
            return 'text' === e.getType();
          },
          isDefaultRender: !0,
          activatedByChanged: function (t) {
            null === e.JsonObject.metaData.findProperty('text', 'choices') &&
              null === e.JsonObject.metaData.findProperty('text', 'choicesByUrl') &&
              (e.JsonObject.metaData.addProperty('text', {
                name: 'choices:itemvalues',
                category: 'choices',
                categoryIndex: 1,
              }),
              e.JsonObject.metaData.addProperty('text', {
                name: 'choicesByUrl:restfull',
                className: 'ChoicesRestfull',
                category: 'choicesByUrl',
                categoryIndex: 2,
              }),
              e.JsonObject.metaData.addProperty('text', {
                name: 'config',
                category: 'general',
                default: null,
              }),
              Array.prototype.push.apply(e.matrixDropdownColumnTypes.text.properties, [
                'choices',
                'choicesOrder',
                'choicesByUrl',
                'otherText',
              ]));
          },
          afterRender: function (e, n) {
            var i = t(n).is('input') ? t(n) : t(n).find('input'),
              o = (function (e) {
                return e ? e.replace(/(^\s*)|(\s+)/g, '.') : '';
              })(e.cssRoot),
              a = i.parents(o)[0];
            o && a && (a.style.overflow = 'visible');
            var r = e.config,
              d = r && 'string' == typeof r ? JSON.parse(r) : r;
            d || (d = {}),
              (d.data = (e.choices || []).map(function (e) {
                return e.text;
              })),
              void 0 === d.adjustWidth && (d.adjustWidth = !1),
              d.list || (d.list = { sort: { enabled: !0 }, match: { enabled: !0 } }),
              d.placeholder || (d.placeholder = e.placeholder),
              e.choicesByUrl &&
                ((d.url = function (t) {
                  return e.choicesByUrl.url;
                }),
                (d.getValue = e.choicesByUrl.valueName)),
              i.easyAutocomplete(d),
              (i[0].oninput = function () {
                e.customWidgetData.isNeedRender = !0;
              });
            var s = function () {
              i[0].value = void 0 === e.value ? '' : e.value;
            };
            (e.valueChangedCallback = s), s();
          },
          willUnmount: function (e, t) {},
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(n, 'type');
      }
      Object.defineProperty(t, '__esModule', { value: !0 }),
        'undefined' != typeof Survey && i(Survey, window.$),
        (t.default = i);
    },
    function (e, t, n) {
      'use strict';
      function i(e) {
        var t = {
          settings: {
            supportedTypes: ['radiogroup', 'checkbox', 'boolean'],
            radiogroup: {
              rootClass: 'pretty p-default p-round',
              inputType: 'radio',
              states: [{ stateClass: 'state p-success', addOn: '' }],
            },
            checkbox: {
              rootClass: 'pretty p-default',
              inputType: 'checkbox',
              states: [{ stateClass: 'state p-success', addOn: '' }],
            },
            boolean: {
              rootClass: 'pretty p-icon p-default p-has-indeterminate',
              inputType: 'checkbox',
              states: [
                { stateClass: 'state p-success', addOn: '' },
                {
                  stateClass: 'state p-success p-is-indeterminate',
                  iconClass: 'icon mdi mdi-minus',
                  addOn: '',
                },
              ],
            },
          },
          name: 'pretty-checkbox',
          activatedBy: 'property',
          widgetIsLoaded: function () {
            return !0;
          },
          htmlTemplate: '<fieldset></fieldset>',
          isFit: function (e) {
            var n = -1 !== t.settings.supportedTypes.indexOf(e.getType());
            return 'property' === t.activatedBy
              ? 'prettycheckbox' === e.renderAs && n
              : 'type' === t.activatedBy && n;
          },
          activatedByChanged: function (n) {
            this.widgetIsLoaded() &&
              ((t.activatedBy = n),
              t.settings.supportedTypes.forEach(function (t) {
                e.JsonObject.metaData.removeProperty(t, 'renderAs'),
                  'property' === n &&
                    e.JsonObject.metaData.addProperty(t, {
                      name: 'renderAs',
                      category: 'general',
                      default: 'default',
                      choices: ['default', 'prettycheckbox'],
                    });
              }));
          },
          isDefaultRender: !1,
          afterRender: function (e, t) {
            var n = {},
              i = e.getType(),
              o = this.settings[i],
              a = 'checkbox' === i,
              r = 'radiogroup' === i,
              d = 'boolean' === i,
              s = !1,
              c = function (t) {
                s = !0;
                try {
                  var n = arguments[0].target,
                    i = n.value,
                    o = n.checked;
                  if (a) {
                    var d = e.createValueCopy() || [],
                      c = d.indexOf(i);
                    o ? -1 === c && d.push(i) : -1 !== c && d.splice(c, 1), (e.value = d);
                  } else e.value = r ? i : o;
                } finally {
                  s = !1;
                }
              },
              u = e.colCount > 0 ? 100 / e.colCount + '%' : '';
            (d ? [{ locText: e.locTitle, value: !!e.value }] : e.visibleChoices).forEach(function (
              r,
              s
            ) {
              var l = document.createElement('input');
              (l.type = o.inputType),
                (l.name = e.name + (a ? '' + s : '')),
                (l.onchange = c),
                (l.value = r.value),
                d && null === e.value && (l.indeterminate = 'indeterminate' === e.defaultValue);
              var f = document.createElement('div');
              (f.className = o.rootClass),
                f.appendChild(l),
                o.states.forEach(function (e) {
                  var t = document.createElement('div');
                  if (((t.className = e.stateClass), e.iconClass)) {
                    var n = document.createElement('i');
                    (n.className = e.iconClass), t.appendChild(n);
                  }
                  var i = document.createElement('label');
                  r.locText.hasHtml
                    ? (i.innerHTML = r.locText.html)
                    : (i.textContent = r.locText.renderedText),
                    t.appendChild(i),
                    f.appendChild(t),
                    e.addOn && t.insertAdjacentHTML('afterbegin', e.addOn);
                });
              var m = document.createElement('div');
              (m.className = 'sv_cw_pretty_checkbox_' + i),
                (m.style.display = 'inline-block'),
                (m.style.width = u),
                m.appendChild(f),
                t.appendChild(m),
                (n[r.value] = l);
            });
            var l = function (e) {
                if (!s) {
                  var t = e || [];
                  (r || d) && (t = [e && e.toString()]),
                    Object.values(n).forEach(function (e) {
                      -1 !== t.indexOf(e.value)
                        ? e.setAttribute('checked', void 0)
                        : e.removeAttribute('checked');
                    });
                }
              },
              f = function () {
                Object.values(n).forEach(function (t) {
                  e.isReadOnly ? t.setAttribute('disabled', !0) : t.removeAttribute('disabled');
                });
              };
            (e.valueChangedCallback = l), (e.readOnlyChangedCallback = f), l(e.value), f();
          },
          willUnmount: function (e, t) {
            (e.valueChangedCallback = void 0), (e.readOnlyChangedCallback = void 0);
          },
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(t, 'property');
      }
      Object.defineProperty(t, '__esModule', { value: !0 }),
        'undefined' != typeof Survey && i(Survey),
        (t.default = i);
    },
    function (e, t, n) {
      'use strict';
      function i(e) {
        var t = {
          name: 'bootstrapslider',
          title: 'Bootstrap Slider',
          iconName: 'icon-bootstrap-slider',
          widgetIsLoaded: function () {
            return void 0 !== o;
          },
          isFit: function (e) {
            return 'bootstrapslider' === e.getType();
          },
          htmlTemplate: '<div></div>',
          activatedByChanged: function (t) {
            e.JsonObject.metaData.addClass('bootstrapslider', [], null, 'empty'),
              e.JsonObject.metaData.addProperties('bootstrapslider', [
                { name: 'step:number', default: 1, category: 'general' },
                { name: 'rangeMin:number', default: 0, category: 'general' },
                { name: 'rangeMax:number', default: 100, category: 'general' },
                {
                  name: 'orientation',
                  default: 'horizontal',
                  choices: ['horizontal', 'vertical'],
                  category: 'general',
                },
              ]),
              e.JsonObject.metaData.addProperty('bootstrapslider', {
                name: 'config',
                default: null,
                category: 'general',
              });
          },
          afterRender: function (e, t) {
            (t.style.paddingTop = '20px'),
              (t.style.paddingBottom = '17px'),
              (t.style.paddingLeft = '10px');
            var n = document.createElement('input');
            (n.id = e.id),
              (n.type = 'text'),
              n.setAttribute('data-slider-id', e.name + '_' + e.id),
              n.setAttribute('data-slider-min', e.rangeMin),
              n.setAttribute('data-slider-max', e.rangeMax),
              n.setAttribute('data-slider-step', e.step),
              'vertical' == e.orientation && n.setAttribute('data-slider-orientation', 'vertical'),
              n.setAttribute('data-slider-step', e.step),
              n.setAttribute('data-slider-value', e.value || e.rangeMin),
              t.appendChild(n);
            var i = e.config || {};
            void 0 === i.id && (i.id = e.name + '_' + e.id),
              void 0 === i.min && (i.min = e.rangeMin),
              void 0 === i.max && (i.max = e.rangeMax),
              void 0 === i.step && (i.step = e.step),
              void 0 === i.enabled && (i.enabled = !e.isReadOnly),
              void 0 === i.value && (i.value = e.value || e.rangeMin);
            var a = new o(n, i);
            a.on('change', function (t) {
              e.value = a.getValue();
            });
            var r = function () {
              a.setValue(e.value || e.rangeMin);
            };
            (e.readOnlyChangedCallback = function () {
              e.isReadOnly ? a.disable() : a.enable();
            }),
              (e.bootstrapSlider = a),
              (e.valueChangedCallback = r);
          },
          willUnmount: function (e, t) {
            e.bootstrapSlider && e.bootstrapSlider.destroy(),
              (e.bootstrapSlider = null),
              (e.readOnlyChangedCallback = null);
          },
          pdfRender: function (e, t) {
            if ('bootstrapslider' === t.question.getType()) {
              var n = t.module.SurveyHelper.createPoint(
                t.module.SurveyHelper.mergeRects.apply(null, t.bricks)
              );
              (n.xLeft += t.controller.unitWidth),
                (n.yTop += t.controller.unitHeight * t.module.FlatQuestion.CONTENT_GAP_VERT_SCALE);
              var i = t.module.SurveyHelper.createTextFieldRect(n, t.controller),
                o = new t.module.TextFieldBrick(
                  t.question,
                  t.controller,
                  i,
                  !0,
                  t.question.id,
                  (t.question.value || t.question.defaultValue || '').toString(),
                  '',
                  t.question.isReadOnly,
                  !1,
                  'text'
                );
              t.bricks.push(o);
            }
          },
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(t, 'customtype');
      }
      Object.defineProperty(t, '__esModule', { value: !0 });
      var o = n(15);
      'undefined' != typeof Survey && i(Survey), (t.default = i);
    },
    function (e, t) {
      e.exports = i;
    },
    function (e, t, n) {
      'use strict';
      function i(e) {
        var t = {
          name: 'microphone',
          title: 'Microphone',
          iconName: 'icon-microphone',
          widgetIsLoaded: function () {
            return void 0 !== a.a;
          },
          isFit: function (e) {
            return 'microphone' === e.getType();
          },
          htmlTemplate:
            "<div style='height: 39px'><button type='button'  title='Record' style='vertical-align: top; margin-top: 3px' ><i class='fa fa-microphone' aria-hidden='true'></i></button>&nbsp;<button type='button' title='Save' style='vertical-align: top; margin-top: 3px'><i class='fa fa-cloud' aria-hidden='true' ></i></button>&nbsp;<audio style='vertical-align: top;margin-left: 10px;height:35px;-moz-box-shadow: 2px 2px 4px 0px #006773;-webkit-box-shadow:  2px 2px 4px 0px #006773;box-shadow: 2px 2px 4px 0px #006773;' controls='true' ></audio></div>",
          activatedByChanged: function (t) {
            e.JsonObject.metaData.addClass('microphone', [], null, 'empty');
          },
          afterRender: function (e, t) {
            var n = t.getElementsByTagName('button')[0],
              i = t.getElementsByTagName('button')[1],
              o = t.getElementsByTagName('audio')[0],
              r = function (t) {
                var n = {
                  type: 'audio',
                  mimeType: 'audio/webm',
                  audioBitsPerSecond: 44100,
                  sampleRate: 44100,
                  bufferSize: 16384,
                  numberOfAudioChannels: 1,
                };
                console.log('successCallback'),
                  (e.survey.mystream = t),
                  (e.survey.recordRTC = a()(e.survey.mystream, n)),
                  void 0 !== e.survey.recordRTC &&
                    (console.log('startRecording'), e.survey.recordRTC.startRecording());
              },
              d = function () {
                alert('No microphone'), (e.survey.recordRTC = void 0), (e.survey.mystream = void 0);
              },
              s = function (t) {
                console.log('processAudio');
                var n = e.survey.recordRTC.getBlob(),
                  i = new FileReader();
                (i.onload = function (t) {
                  var n = t.target.result;
                  console.log('dataUri: ' + n),
                    (e.value = n),
                    (o.src = n),
                    console.log('cleaning'),
                    (e.survey.recordRTC = void 0),
                    (e.survey.mystream = void 0);
                }),
                  i.readAsDataURL(n);
              },
              c = function () {
                (e.value = void 0),
                  void 0 !== e.survey.recordRTC &&
                    (e.survey.recordRTC.stopRecording(f),
                    void 0 !== e.survey.mystream &&
                      e.survey.mystream.getAudioTracks().forEach(function (e) {
                        e.stop();
                      }));
                var t = { video: !1, audio: !0 };
                navigator.mediaDevices.getUserMedia(t).then(r.bind(this), d.bind(this));
              },
              u = function () {
                console.log('stopRecording'),
                  void 0 !== e.survey.recordRTC &&
                    (e.survey.recordRTC.stopRecording(s.bind(this)),
                    void 0 !== e.survey.mystream &&
                      e.survey.mystream.getAudioTracks().forEach(function (e) {
                        e.stop();
                      }));
              };
            e.isReadOnly ? n.parentNode.removeChild(n) : (n.onclick = c),
              e.isReadOnly ? i.parentNode.removeChild(i) : (i.onclick = u),
              (o.src = e.value);
            var l = function () {},
              f = function () {};
            e.valueChangedCallback = l;
          },
          willUnmount: function (e, t) {
            console.log('unmount microphone no record '),
              void 0 !== e.survey.recordRTC &&
                (e.survey.recordRTC.stopRecording(doNothingHandler),
                void 0 !== e.survey.mystream &&
                  e.survey.mystream.getAudioTracks().forEach(function (e) {
                    e.stop();
                  }),
                (e.value = void 0),
                (e.survey.recordRTC = void 0),
                (e.survey.mystream = void 0));
          },
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(t, 'customtype');
      }
      Object.defineProperty(t, '__esModule', { value: !0 });
      var o = n(17),
        a = n.n(o);
      'undefined' != typeof Survey && i(Survey), (t.default = i);
    },
    function (e, t, n) {
      'use strict';
      (function (n, i) {
        /**
         * {@link https://github.com/muaz-khan/RecordRTC|RecordRTC} is a WebRTC JavaScript library for audio/video as well as screen activity recording. It supports Chrome, Firefox, Opera, Android, and Microsoft Edge. Platforms: Linux, Mac and Windows.
         * @summary Record audio, video or screen inside the browser.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef RecordRTC
         * @class
         * @example
         * var recorder = RecordRTC(mediaStream or [arrayOfMediaStream], {
         *     type: 'video', // audio or video or gif or canvas
         *     recorderType: MediaStreamRecorder || CanvasRecorder || StereoAudioRecorder || Etc
         * });
         * recorder.startRecording();
         * @see For further information:
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {MediaStream} mediaStream - Single media-stream object, array of media-streams, html-canvas-element, etc.
         * @param {object} config - {type:"video", recorderType: MediaStreamRecorder, disableLogs: true, numberOfAudioChannels: 1, bufferSize: 0, sampleRate: 0, desiredSampRate: 16000, video: HTMLVideoElement, etc.}
         */
        function o(e, t) {
          function n(n) {
            return (
              t.disableLogs || console.log('RecordRTC version: ', b.version),
              n && (t = new a(e, n)),
              t.disableLogs || console.log('started recording ' + t.type + ' stream.'),
              v
                ? (v.clearRecordedData(), v.record(), g('recording'), b.recordingDuration && p(), b)
                : (i(function () {
                    b.recordingDuration && p();
                  }),
                  b)
            );
          }
          function i(n) {
            n &&
              (t.initCallback = function () {
                n(), (n = t.initCallback = null);
              });
            var i = new r(e, t);
            (v = new i(e, t)),
              v.record(),
              g('recording'),
              t.disableLogs ||
                console.log(
                  'Initialized recorderType:',
                  v.constructor.name,
                  'for output-type:',
                  t.type
                );
          }
          function d(e) {
            function n(n) {
              if (!v) return void ('function' == typeof e.call ? e.call(b, '') : e(''));
              Object.keys(v).forEach(function (e) {
                'function' != typeof v[e] && (b[e] = v[e]);
              });
              var i = v.blob;
              if (!i) {
                if (!n) throw 'Recording failed.';
                v.blob = i = n;
              }
              if ((i && !t.disableLogs && console.log(i.type, '->', s(i.size)), e)) {
                var o;
                try {
                  o = M.createObjectURL(i);
                } catch (e) {}
                'function' == typeof e.call ? e.call(b, o) : e(o);
              }
              t.autoWriteToDisk &&
                m(function (e) {
                  var n = {};
                  (n[t.type + 'Blob'] = e), N.Store(n);
                });
            }
            return (
              (e = e || function () {}),
              v
                ? 'paused' === b.state
                  ? (b.resumeRecording(),
                    void setTimeout(function () {
                      d(e);
                    }, 1))
                  : ('recording' === b.state ||
                      t.disableLogs ||
                      console.warn(
                        'Recording state should be: "recording", however current state is: ',
                        b.state
                      ),
                    t.disableLogs || console.log('Stopped recording ' + t.type + ' stream.'),
                    'gif' !== t.type ? v.stop(n) : (v.stop(), n()),
                    void g('stopped'))
                : void h()
            );
          }
          function u() {
            return v
              ? 'recording' !== b.state
                ? void (
                    t.disableLogs ||
                    console.warn('Unable to pause the recording. Recording state: ', b.state)
                  )
                : (g('paused'), v.pause(), void (t.disableLogs || console.log('Paused recording.')))
              : void h();
          }
          function l() {
            return v
              ? 'paused' !== b.state
                ? void (
                    t.disableLogs ||
                    console.warn('Unable to resume the recording. Recording state: ', b.state)
                  )
                : (g('recording'),
                  v.resume(),
                  void (t.disableLogs || console.log('Resumed recording.')))
              : void h();
          }
          function f(e) {
            postMessage(new FileReaderSync().readAsDataURL(e));
          }
          function m(e, n) {
            if (!e) throw 'Pass a callback function over getDataURL.';
            var i = n ? n.blob : (v || {}).blob;
            if (!i)
              return (
                t.disableLogs || console.warn('Blob encoder did not finish its job yet.'),
                void setTimeout(function () {
                  m(e, n);
                }, 1e3)
              );
            if ('undefined' == typeof Worker || navigator.mozGetUserMedia) {
              var o = new FileReader();
              o.readAsDataURL(i),
                (o.onload = function (t) {
                  e(t.target.result);
                });
            } else {
              var a = (function (e) {
                try {
                  var t = M.createObjectURL(
                      new Blob(
                        [
                          e.toString(),
                          'this.onmessage =  function (eee) {' + e.name + '(eee.data);}',
                        ],
                        { type: 'application/javascript' }
                      )
                    ),
                    n = new Worker(t);
                  return M.revokeObjectURL(t), n;
                } catch (e) {}
              })(f);
              (a.onmessage = function (t) {
                e(t.data);
              }),
                a.postMessage(i);
            }
          }
          function p(e) {
            if (((e = e || 0), 'paused' === b.state))
              return void setTimeout(function () {
                p(e);
              }, 1e3);
            if ('stopped' !== b.state) {
              if (e >= b.recordingDuration) return void d(b.onRecordingStopped);
              (e += 1e3),
                setTimeout(function () {
                  p(e);
                }, 1e3);
            }
          }
          function g(e) {
            b &&
              ((b.state = e),
              'function' == typeof b.onStateChanged.call
                ? b.onStateChanged.call(b, e)
                : b.onStateChanged(e));
          }
          function h() {
            !0 !== t.disableLogs && console.warn(y);
          }
          if (!e) throw 'First parameter is required.';
          (t = t || { type: 'video' }), (t = new a(e, t));
          var v,
            b = this,
            y =
              'It seems that recorder is destroyed or "startRecording" is not invoked for ' +
              t.type +
              ' recorder.',
            w = {
              startRecording: n,
              stopRecording: d,
              pauseRecording: u,
              resumeRecording: l,
              initRecorder: i,
              setRecordingDuration: function (e, t) {
                if (void 0 === e) throw 'recordingDuration is required.';
                if ('number' != typeof e) throw 'recordingDuration must be a number.';
                return (
                  (b.recordingDuration = e),
                  (b.onRecordingStopped = t || function () {}),
                  {
                    onRecordingStopped: function (e) {
                      b.onRecordingStopped = e;
                    },
                  }
                );
              },
              clearRecordedData: function () {
                if (!v) return void h();
                v.clearRecordedData(), t.disableLogs || console.log('Cleared old recorded data.');
              },
              getBlob: function () {
                return v ? v.blob : void h();
              },
              getDataURL: m,
              toURL: function () {
                return v ? M.createObjectURL(v.blob) : void h();
              },
              getInternalRecorder: function () {
                return v;
              },
              save: function (e) {
                if (!v) return void h();
                c(v.blob, e);
              },
              getFromDisk: function (e) {
                if (!v) return void h();
                o.getFromDisk(t.type, e);
              },
              setAdvertisementArray: function (e) {
                t.advertisement = [];
                for (var n = e.length, i = 0; i < n; i++)
                  t.advertisement.push({ duration: i, image: e[i] });
              },
              blob: null,
              bufferSize: 0,
              sampleRate: 0,
              buffer: null,
              reset: function () {
                'recording' !== b.state ||
                  t.disableLogs ||
                  console.warn('Stop an active recorder.'),
                  v && 'function' == typeof v.clearRecordedData && v.clearRecordedData(),
                  (v = null),
                  g('inactive'),
                  (b.blob = null);
              },
              onStateChanged: function (e) {
                t.disableLogs || console.log('Recorder state changed:', e);
              },
              state: 'inactive',
              getState: function () {
                return b.state;
              },
              destroy: function () {
                var e = t.disableLogs;
                (t = { disableLogs: !0 }),
                  b.reset(),
                  g('destroyed'),
                  (w = b = null),
                  P.AudioContextConstructor &&
                    (P.AudioContextConstructor.close(), (P.AudioContextConstructor = null)),
                  (t.disableLogs = e),
                  t.disableLogs || console.log('RecordRTC is destroyed.');
              },
              version: '5.5.9',
            };
          if (!this) return (b = w), w;
          for (var C in w) this[C] = w[C];
          return (b = this), w;
        }
        /**
         * {@link RecordRTCConfiguration} is an inner/private helper for {@link RecordRTC}.
         * @summary It configures the 2nd parameter passed over {@link RecordRTC} and returns a valid "config" object.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef RecordRTCConfiguration
         * @class
         * @example
         * var options = RecordRTCConfiguration(mediaStream, options);
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {MediaStream} mediaStream - MediaStream object fetched using getUserMedia API or generated using captureStreamUntilEnded or WebAudio API.
         * @param {object} config - {type:"video", disableLogs: true, numberOfAudioChannels: 1, bufferSize: 0, sampleRate: 0, video: HTMLVideoElement, getNativeBlob:true, etc.}
         */
        function a(e, t) {
          return (
            t.recorderType ||
              t.type ||
              (t.audio && t.video ? (t.type = 'video') : t.audio && !t.video && (t.type = 'audio')),
            t.recorderType &&
              !t.type &&
              (t.recorderType === b ||
              t.recorderType === v ||
              (void 0 !== k && t.recorderType === k)
                ? (t.type = 'video')
                : t.recorderType === y
                ? (t.type = 'gif')
                : t.recorderType === h
                ? (t.type = 'audio')
                : t.recorderType === g &&
                  (l(e, 'audio').length && l(e, 'video').length
                    ? (t.type = 'video')
                    : !l(e, 'audio').length && l(e, 'video').length
                    ? (t.type = 'video')
                    : l(e, 'audio').length && !l(e, 'video').length && (t.type = 'audio'))),
            void 0 !== g &&
              'undefined' != typeof MediaRecorder &&
              'requestData' in MediaRecorder.prototype &&
              (t.mimeType || (t.mimeType = 'video/webm'),
              t.type || (t.type = t.mimeType.split('/')[0]),
              t.bitsPerSecond),
            t.type ||
              (t.mimeType && (t.type = t.mimeType.split('/')[0]), t.type || (t.type = 'audio')),
            t
          );
        }
        /**
         * {@link GetRecorderType} is an inner/private helper for {@link RecordRTC}.
         * @summary It returns best recorder-type available for your browser.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef GetRecorderType
         * @class
         * @example
         * var RecorderType = GetRecorderType(options);
         * var recorder = new RecorderType(options);
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {MediaStream} mediaStream - MediaStream object fetched using getUserMedia API or generated using captureStreamUntilEnded or WebAudio API.
         * @param {object} config - {type:"video", disableLogs: true, numberOfAudioChannels: 1, bufferSize: 0, sampleRate: 0, video: HTMLVideoElement, etc.}
         */
        function r(e, t) {
          var n;
          return (
            (j || L || U) && (n = h),
            'undefined' != typeof MediaRecorder &&
              'requestData' in MediaRecorder.prototype &&
              !j &&
              (n = g),
            'video' === t.type &&
              (j || U) &&
              ((n = b), void 0 !== k && 'undefined' != typeof ReadableStream && (n = k)),
            'gif' === t.type && (n = y),
            'canvas' === t.type && (n = v),
            p() &&
              n !== v &&
              n !== y &&
              'undefined' != typeof MediaRecorder &&
              'requestData' in MediaRecorder.prototype &&
              (l(e, 'video').length || l(e, 'audio').length) &&
              ('audio' === t.type
                ? 'function' == typeof MediaRecorder.isTypeSupported &&
                  MediaRecorder.isTypeSupported('audio/webm') &&
                  (n = g)
                : 'function' == typeof MediaRecorder.isTypeSupported &&
                  MediaRecorder.isTypeSupported('video/webm') &&
                  (n = g)),
            e instanceof Array && e.length && (n = C),
            t.recorderType && (n = t.recorderType),
            !t.disableLogs &&
              n &&
              n.name &&
              console.log('Using recorderType:', n.name || n.constructor.name),
            !n && B && (n = g),
            n
          );
        }
        /**
         * MRecordRTC runs on top of {@link RecordRTC} to bring multiple recordings in a single place, by providing simple API.
         * @summary MRecordRTC stands for "Multiple-RecordRTC".
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef MRecordRTC
         * @class
         * @example
         * var recorder = new MRecordRTC();
         * recorder.addStream(MediaStream);
         * recorder.mediaType = {
         *     audio: true, // or StereoAudioRecorder or MediaStreamRecorder
         *     video: true, // or WhammyRecorder or MediaStreamRecorder or WebAssemblyRecorder or CanvasRecorder
         *     gif: true    // or GifRecorder
         * };
         * // mimeType is optional and should be set only in advance cases.
         * recorder.mimeType = {
         *     audio: 'audio/wav',
         *     video: 'video/webm',
         *     gif:   'image/gif'
         * };
         * recorder.startRecording();
         * @see For further information:
         * @see {@link https://github.com/muaz-khan/RecordRTC/tree/master/MRecordRTC|MRecordRTC Source Code}
         * @param {MediaStream} mediaStream - MediaStream object fetched using getUserMedia API or generated using captureStreamUntilEnded or WebAudio API.
         * @requires {@link RecordRTC}
         */
        function d(e) {
          (this.addStream = function (t) {
            t && (e = t);
          }),
            (this.mediaType = { audio: !0, video: !0 }),
            (this.startRecording = function () {
              var t,
                n = this.mediaType,
                i = this.mimeType || { audio: null, video: null, gif: null };
              if (
                ('function' != typeof n.audio && p() && !l(e, 'audio').length && (n.audio = !1),
                'function' != typeof n.video && p() && !l(e, 'video').length && (n.video = !1),
                'function' != typeof n.gif && p() && !l(e, 'video').length && (n.gif = !1),
                !n.audio && !n.video && !n.gif)
              )
                throw 'MediaStream must have either audio or video tracks.';
              if (
                (n.audio &&
                  ((t = null),
                  'function' == typeof n.audio && (t = n.audio),
                  (this.audioRecorder = new o(e, {
                    type: 'audio',
                    bufferSize: this.bufferSize,
                    sampleRate: this.sampleRate,
                    numberOfAudioChannels: this.numberOfAudioChannels || 2,
                    disableLogs: this.disableLogs,
                    recorderType: t,
                    mimeType: i.audio,
                    timeSlice: this.timeSlice,
                    onTimeStamp: this.onTimeStamp,
                  })),
                  n.video || this.audioRecorder.startRecording()),
                n.video)
              ) {
                (t = null), 'function' == typeof n.video && (t = n.video);
                var a = e;
                if (p() && n.audio && 'function' == typeof n.audio) {
                  var r = l(e, 'video')[0];
                  I
                    ? ((a = new E()), a.addTrack(r), t && t === b && (t = g))
                    : ((a = new E()), a.addTrack(r));
                }
                (this.videoRecorder = new o(a, {
                  type: 'video',
                  video: this.video,
                  canvas: this.canvas,
                  frameInterval: this.frameInterval || 10,
                  disableLogs: this.disableLogs,
                  recorderType: t,
                  mimeType: i.video,
                  timeSlice: this.timeSlice,
                  onTimeStamp: this.onTimeStamp,
                  workerPath: this.workerPath,
                  webAssemblyPath: this.webAssemblyPath,
                  frameRate: this.frameRate,
                  bitrate: this.bitrate,
                })),
                  n.audio || this.videoRecorder.startRecording();
              }
              if (n.audio && n.video) {
                var d = this,
                  s = !0 === p();
                n.audio instanceof h && n.video
                  ? (s = !1)
                  : !0 !== n.audio && !0 !== n.video && n.audio !== n.video && (s = !1),
                  !0 === s
                    ? ((d.audioRecorder = null), d.videoRecorder.startRecording())
                    : d.videoRecorder.initRecorder(function () {
                        d.audioRecorder.initRecorder(function () {
                          d.videoRecorder.startRecording(), d.audioRecorder.startRecording();
                        });
                      });
              }
              n.gif &&
                ((t = null),
                'function' == typeof n.gif && (t = n.gif),
                (this.gifRecorder = new o(e, {
                  type: 'gif',
                  frameRate: this.frameRate || 200,
                  quality: this.quality || 10,
                  disableLogs: this.disableLogs,
                  recorderType: t,
                  mimeType: i.gif,
                })),
                this.gifRecorder.startRecording());
            }),
            (this.stopRecording = function (e) {
              (e = e || function () {}),
                this.audioRecorder &&
                  this.audioRecorder.stopRecording(function (t) {
                    e(t, 'audio');
                  }),
                this.videoRecorder &&
                  this.videoRecorder.stopRecording(function (t) {
                    e(t, 'video');
                  }),
                this.gifRecorder &&
                  this.gifRecorder.stopRecording(function (t) {
                    e(t, 'gif');
                  });
            }),
            (this.pauseRecording = function () {
              this.audioRecorder && this.audioRecorder.pauseRecording(),
                this.videoRecorder && this.videoRecorder.pauseRecording(),
                this.gifRecorder && this.gifRecorder.pauseRecording();
            }),
            (this.resumeRecording = function () {
              this.audioRecorder && this.audioRecorder.resumeRecording(),
                this.videoRecorder && this.videoRecorder.resumeRecording(),
                this.gifRecorder && this.gifRecorder.resumeRecording();
            }),
            (this.getBlob = function (e) {
              var t = {};
              return (
                this.audioRecorder && (t.audio = this.audioRecorder.getBlob()),
                this.videoRecorder && (t.video = this.videoRecorder.getBlob()),
                this.gifRecorder && (t.gif = this.gifRecorder.getBlob()),
                e && e(t),
                t
              );
            }),
            (this.destroy = function () {
              this.audioRecorder && (this.audioRecorder.destroy(), (this.audioRecorder = null)),
                this.videoRecorder && (this.videoRecorder.destroy(), (this.videoRecorder = null)),
                this.gifRecorder && (this.gifRecorder.destroy(), (this.gifRecorder = null));
            }),
            (this.getDataURL = function (e) {
              function t(e, t) {
                if ('undefined' != typeof Worker) {
                  var i = n(function (e) {
                    postMessage(new FileReaderSync().readAsDataURL(e));
                  });
                  (i.onmessage = function (e) {
                    t(e.data);
                  }),
                    i.postMessage(e);
                } else {
                  var o = new FileReader();
                  o.readAsDataURL(e),
                    (o.onload = function (e) {
                      t(e.target.result);
                    });
                }
              }
              function n(e) {
                var t,
                  n = M.createObjectURL(
                    new Blob(
                      [
                        e.toString(),
                        'this.onmessage =  function (eee) {' + e.name + '(eee.data);}',
                      ],
                      { type: 'application/javascript' }
                    )
                  ),
                  i = new Worker(n);
                if (void 0 !== M) t = M;
                else {
                  if ('undefined' == typeof webkitURL) throw 'Neither URL nor webkitURL detected.';
                  t = webkitURL;
                }
                return t.revokeObjectURL(n), i;
              }
              this.getBlob(function (n) {
                n.audio && n.video
                  ? t(n.audio, function (i) {
                      t(n.video, function (t) {
                        e({ audio: i, video: t });
                      });
                    })
                  : n.audio
                  ? t(n.audio, function (t) {
                      e({ audio: t });
                    })
                  : n.video &&
                    t(n.video, function (t) {
                      e({ video: t });
                    });
              });
            }),
            (this.writeToDisk = function () {
              o.writeToDisk({
                audio: this.audioRecorder,
                video: this.videoRecorder,
                gif: this.gifRecorder,
              });
            }),
            (this.save = function (e) {
              (e = e || { audio: !0, video: !0, gif: !0 }),
                e.audio &&
                  this.audioRecorder &&
                  this.audioRecorder.save('string' == typeof e.audio ? e.audio : ''),
                e.video &&
                  this.videoRecorder &&
                  this.videoRecorder.save('string' == typeof e.video ? e.video : ''),
                e.gif &&
                  this.gifRecorder &&
                  this.gifRecorder.save('string' == typeof e.gif ? e.gif : '');
            });
        }
        function s(e) {
          var t = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
          if (0 === e) return '0 Bytes';
          var n = parseInt(Math.floor(Math.log(e) / Math.log(1e3)), 10);
          return (e / Math.pow(1e3, n)).toPrecision(3) + ' ' + t[n];
        }
        function c(e, t) {
          if (!e) throw 'Blob object is required.';
          if (!e.type)
            try {
              e.type = 'video/webm';
            } catch (e) {}
          var n = (e.type || 'video/webm').split('/')[1];
          if (t && -1 !== t.indexOf('.')) {
            var i = t.split('.');
            (t = i[0]), (n = i[1]);
          }
          var o = (t || Math.round(9999999999 * Math.random()) + 888888888) + '.' + n;
          if (void 0 !== navigator.msSaveOrOpenBlob) return navigator.msSaveOrOpenBlob(e, o);
          if (void 0 !== navigator.msSaveBlob) return navigator.msSaveBlob(e, o);
          var a = document.createElement('a');
          (a.href = M.createObjectURL(e)),
            (a.download = o),
            (a.style = 'display:none;opacity:0;color:transparent;'),
            (document.body || document.documentElement).appendChild(a),
            'function' == typeof a.click
              ? a.click()
              : ((a.target = '_blank'),
                a.dispatchEvent(
                  new MouseEvent('click', { view: window, bubbles: !0, cancelable: !0 })
                )),
            M.revokeObjectURL(a.href);
        }
        function u() {
          return (
            ('undefined' != typeof window &&
              'object' == typeof window.process &&
              'renderer' === window.process.type) ||
            !(void 0 === i || 'object' != typeof i.versions || !i.versions.electron) ||
            ('object' == typeof navigator &&
              'string' == typeof navigator.userAgent &&
              navigator.userAgent.indexOf('Electron') >= 0)
          );
        }
        function l(e, t) {
          return e && e.getTracks
            ? e.getTracks().filter(function (e) {
                return e.kind === (t || 'audio');
              })
            : [];
        }
        function f(e, t) {
          'srcObject' in t
            ? (t.srcObject = e)
            : 'mozSrcObject' in t
            ? (t.mozSrcObject = e)
            : (t.srcObject = e);
        }
        function m(e, t) {
          if ('undefined' == typeof EBML)
            throw new Error('Please link: https://www.webrtc-experiment.com/EBML.js');
          var n = new EBML.Reader(),
            i = new EBML.Decoder(),
            o = EBML.tools,
            a = new FileReader();
          (a.onload = function (e) {
            i.decode(this.result).forEach(function (e) {
              n.read(e);
            }),
              n.stop();
            var a = o.makeMetadataSeekable(n.metadatas, n.duration, n.cues),
              r = this.result.slice(n.metadataSize),
              d = new Blob([a, r], { type: 'video/webm' });
            t(d);
          }),
            a.readAsArrayBuffer(e);
        }
        function p() {
          if (I || B || L) return !0;
          var e,
            t,
            n = (navigator.appVersion, navigator.userAgent),
            i = '' + parseFloat(navigator.appVersion),
            o = parseInt(navigator.appVersion, 10);
          return (
            (j || U) && ((e = n.indexOf('Chrome')), (i = n.substring(e + 7))),
            -1 !== (t = i.indexOf(';')) && (i = i.substring(0, t)),
            -1 !== (t = i.indexOf(' ')) && (i = i.substring(0, t)),
            (o = parseInt('' + i, 10)),
            isNaN(o) &&
              ((i = '' + parseFloat(navigator.appVersion)),
              (o = parseInt(navigator.appVersion, 10))),
            o >= 49
          );
        }
        /**
         * MediaStreamRecorder is an abstraction layer for {@link https://w3c.github.io/mediacapture-record/MediaRecorder.html|MediaRecorder API}. It is used by {@link RecordRTC} to record MediaStream(s) in both Chrome and Firefox.
         * @summary Runs top over {@link https://w3c.github.io/mediacapture-record/MediaRecorder.html|MediaRecorder API}.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://github.com/muaz-khan|Muaz Khan}
         * @typedef MediaStreamRecorder
         * @class
         * @example
         * var config = {
         *     mimeType: 'video/webm', // vp8, vp9, h264, mkv, opus/vorbis
         *     audioBitsPerSecond : 256 * 8 * 1024,
         *     videoBitsPerSecond : 256 * 8 * 1024,
         *     bitsPerSecond: 256 * 8 * 1024,  // if this is provided, skip above two
         *     checkForInactiveTracks: true,
         *     timeSlice: 1000, // concatenate intervals based blobs
         *     ondataavailable: function() {} // get intervals based blobs
         * }
         * var recorder = new MediaStreamRecorder(mediaStream, config);
         * recorder.record();
         * recorder.stop(function(blob) {
         *     video.src = URL.createObjectURL(blob);
         *
         *     // or
         *     var blob = recorder.blob;
         * });
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {MediaStream} mediaStream - MediaStream object fetched using getUserMedia API or generated using captureStreamUntilEnded or WebAudio API.
         * @param {object} config - {disableLogs:true, initCallback: function, mimeType: "video/webm", timeSlice: 1000}
         * @throws Will throw an error if first argument "MediaStream" is missing. Also throws error if "MediaRecorder API" are not supported by the browser.
         */
        function g(e, t) {
          function n() {
            r.timestamps.push(new Date().getTime()),
              'function' == typeof t.onTimeStamp &&
                t.onTimeStamp(r.timestamps[r.timestamps.length - 1], r.timestamps);
          }
          function i(e) {
            return u && u.mimeType ? u.mimeType : e.mimeType || 'video/webm';
          }
          function o() {
            (c = []), (u = null), (r.timestamps = []);
          }
          function a() {
            if ('active' in e) {
              if (!e.active) return !1;
            } else if ('ended' in e && e.ended) return !1;
            return !0;
          }
          var r = this;
          if (void 0 === e) throw 'First argument "MediaStream" is required.';
          if ('undefined' == typeof MediaRecorder)
            throw 'Your browser does not support the Media Recorder API. Please try other modules e.g. WhammyRecorder or StereoAudioRecorder.';
          if (((t = t || { mimeType: 'video/webm' }), 'audio' === t.type)) {
            if (l(e, 'video').length && l(e, 'audio').length) {
              var d;
              navigator.mozGetUserMedia
                ? ((d = new E()), d.addTrack(l(e, 'audio')[0]))
                : (d = new E(l(e, 'audio'))),
                (e = d);
            }
            (t.mimeType && -1 !== t.mimeType.toString().toLowerCase().indexOf('audio')) ||
              (t.mimeType = j ? 'audio/webm' : 'audio/ogg'),
              t.mimeType &&
                'audio/ogg' !== t.mimeType.toString().toLowerCase() &&
                navigator.mozGetUserMedia &&
                (t.mimeType = 'audio/ogg');
          }
          var c = [];
          (this.getArrayOfBlobs = function () {
            return c;
          }),
            (this.record = function () {
              (r.blob = null), r.clearRecordedData(), (r.timestamps = []), (f = []), (c = []);
              var o = t;
              t.disableLogs || console.log('Passing following config over MediaRecorder API.', o),
                u && (u = null),
                j && !p() && (o = 'video/vp8'),
                'function' == typeof MediaRecorder.isTypeSupported &&
                  o.mimeType &&
                  (MediaRecorder.isTypeSupported(o.mimeType) ||
                    (t.disableLogs ||
                      console.warn(
                        'MediaRecorder API seems unable to record mimeType:',
                        o.mimeType
                      ),
                    (o.mimeType = 'audio' === t.type ? 'audio/webm' : 'video/webm')));
              try {
                (u = new MediaRecorder(e, o)), (t.mimeType = o.mimeType);
              } catch (t) {
                u = new MediaRecorder(e);
              }
              o.mimeType &&
                !MediaRecorder.isTypeSupported &&
                'canRecordMimeType' in u &&
                !1 === u.canRecordMimeType(o.mimeType) &&
                (t.disableLogs ||
                  console.warn('MediaRecorder API seems unable to record mimeType:', o.mimeType)),
                (u.ondataavailable = function (e) {
                  if (
                    (e.data && f.push('ondataavailable: ' + s(e.data.size)),
                    'number' != typeof t.timeSlice)
                  ) {
                    if (!e.data || !e.data.size || e.data.size < 100 || r.blob)
                      return void (
                        r.recordingCallback &&
                        (r.recordingCallback(new Blob([], { type: i(o) })),
                        (r.recordingCallback = null))
                      );
                    (r.blob = t.getNativeBlob ? e.data : new Blob([e.data], { type: i(o) })),
                      r.recordingCallback &&
                        (r.recordingCallback(r.blob), (r.recordingCallback = null));
                  } else if (
                    e.data &&
                    e.data.size &&
                    e.data.size > 100 &&
                    (c.push(e.data), n(), 'function' == typeof t.ondataavailable)
                  ) {
                    var a = t.getNativeBlob ? e.data : new Blob([e.data], { type: i(o) });
                    t.ondataavailable(a);
                  }
                }),
                (u.onstart = function () {
                  f.push('started');
                }),
                (u.onpause = function () {
                  f.push('paused');
                }),
                (u.onresume = function () {
                  f.push('resumed');
                }),
                (u.onstop = function () {
                  f.push('stopped');
                }),
                (u.onerror = function (e) {
                  e &&
                    (e.name || (e.name = 'UnknownError'),
                    f.push('error: ' + e),
                    t.disableLogs ||
                      (-1 !== e.name.toString().toLowerCase().indexOf('invalidstate')
                        ? console.error(
                            'The MediaRecorder is not in a state in which the proposed operation is allowed to be executed.',
                            e
                          )
                        : -1 !== e.name.toString().toLowerCase().indexOf('notsupported')
                        ? console.error('MIME type (', o.mimeType, ') is not supported.', e)
                        : -1 !== e.name.toString().toLowerCase().indexOf('security')
                        ? console.error('MediaRecorder security error', e)
                        : 'OutOfMemory' === e.name
                        ? console.error(
                            'The UA has exhaused the available memory. User agents SHOULD provide as much additional information as possible in the message attribute.',
                            e
                          )
                        : 'IllegalStreamModification' === e.name
                        ? console.error(
                            'A modification to the stream has occurred that makes it impossible to continue recording. An example would be the addition of a Track while recording is occurring. User agents SHOULD provide as much additional information as possible in the message attribute.',
                            e
                          )
                        : 'OtherRecordingError' === e.name
                        ? console.error(
                            'Used for an fatal error other than those listed above. User agents SHOULD provide as much additional information as possible in the message attribute.',
                            e
                          )
                        : 'GenericError' === e.name
                        ? console.error(
                            'The UA cannot provide the codec or recording option that has been requested.',
                            e
                          )
                        : console.error('MediaRecorder Error', e)),
                    (function (e) {
                      if (!r.manuallyStopped && u && 'inactive' === u.state)
                        return delete t.timeslice, void u.start(6e5);
                      setTimeout(void 0, 1e3);
                    })(),
                    'inactive' !== u.state && 'stopped' !== u.state && u.stop());
                }),
                'number' == typeof t.timeSlice ? (n(), u.start(t.timeSlice)) : u.start(36e5),
                t.initCallback && t.initCallback();
            }),
            (this.timestamps = []),
            (this.stop = function (e) {
              (e = e || function () {}),
                (r.manuallyStopped = !0),
                u &&
                  ((this.recordingCallback = e),
                  'recording' === u.state && u.stop(),
                  'number' == typeof t.timeSlice &&
                    setTimeout(function () {
                      (r.blob = new Blob(c, { type: i(t) })), r.recordingCallback(r.blob);
                    }, 100));
            }),
            (this.pause = function () {
              u && 'recording' === u.state && u.pause();
            }),
            (this.resume = function () {
              u && 'paused' === u.state && u.resume();
            }),
            (this.clearRecordedData = function () {
              u && 'recording' === u.state && r.stop(o), o();
            });
          var u;
          (this.getInternalRecorder = function () {
            return u;
          }),
            (this.blob = null),
            (this.getState = function () {
              return u ? u.state || 'inactive' : 'inactive';
            });
          var f = [];
          (this.getAllStates = function () {
            return f;
          }),
            void 0 === t.checkForInactiveTracks && (t.checkForInactiveTracks = !1);
          var r = this;
          !(function e() {
            if (u && !1 !== t.checkForInactiveTracks)
              return !1 === a()
                ? (t.disableLogs || console.log('MediaStream seems stopped.'), void r.stop())
                : void setTimeout(e, 1e3);
          })(),
            (this.name = 'MediaStreamRecorder'),
            (this.toString = function () {
              return this.name;
            });
        }
        /**
         * StereoAudioRecorder is a standalone class used by {@link RecordRTC} to bring "stereo" audio-recording in chrome.
         * @summary JavaScript standalone object for stereo audio recording.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef StereoAudioRecorder
         * @class
         * @example
         * var recorder = new StereoAudioRecorder(MediaStream, {
         *     sampleRate: 44100,
         *     bufferSize: 4096
         * });
         * recorder.record();
         * recorder.stop(function(blob) {
         *     video.src = URL.createObjectURL(blob);
         * });
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {MediaStream} mediaStream - MediaStream object fetched using getUserMedia API or generated using captureStreamUntilEnded or WebAudio API.
         * @param {object} config - {sampleRate: 44100, bufferSize: 4096, numberOfAudioChannels: 1, etc.}
         */
        function h(e, t) {
          function n() {
            if (!1 === t.checkForInactiveTracks) return !0;
            if ('active' in e) {
              if (!e.active) return !1;
            } else if ('ended' in e && e.ended) return !1;
            return !0;
          }
          function i(e, t) {
            function n(e, t) {
              function n(e, t, n) {
                var o = Math.round(e.length * (t / n)),
                  a = [],
                  r = Number((e.length - 1) / (o - 1));
                a[0] = e[0];
                for (var d = 1; d < o - 1; d++) {
                  var s = d * r,
                    c = Number(Math.floor(s)).toFixed(),
                    u = Number(Math.ceil(s)).toFixed(),
                    l = s - c;
                  a[d] = i(e[c], e[u], l);
                }
                return (a[o - 1] = e[e.length - 1]), a;
              }
              function i(e, t, n) {
                return e + (t - e) * n;
              }
              function o(e, t) {
                for (var n = new Float64Array(t), i = 0, o = e.length, a = 0; a < o; a++) {
                  var r = e[a];
                  n.set(r, i), (i += r.length);
                }
                return n;
              }
              function a(e, t, n) {
                for (var i = n.length, o = 0; o < i; o++) e.setUint8(t + o, n.charCodeAt(o));
              }
              var r = e.numberOfAudioChannels,
                d = e.leftBuffers.slice(0),
                s = e.rightBuffers.slice(0),
                c = e.sampleRate,
                u = e.internalInterleavedLength,
                l = e.desiredSampRate;
              2 === r && ((d = o(d, u)), (s = o(s, u)), l && ((d = n(d, l, c)), (s = n(s, l, c)))),
                1 === r && ((d = o(d, u)), l && (d = n(d, l, c))),
                l && (c = l);
              var f;
              2 === r &&
                (f = (function (e, t) {
                  for (var n = e.length + t.length, i = new Float64Array(n), o = 0, a = 0; a < n; )
                    (i[a++] = e[o]), (i[a++] = t[o]), o++;
                  return i;
                })(d, s)),
                1 === r && (f = d);
              var m = f.length,
                p = 44 + 2 * m,
                g = new ArrayBuffer(p),
                h = new DataView(g);
              a(h, 0, 'RIFF'),
                h.setUint32(4, 36 + 2 * m, !0),
                a(h, 8, 'WAVE'),
                a(h, 12, 'fmt '),
                h.setUint32(16, 16, !0),
                h.setUint16(20, 1, !0),
                h.setUint16(22, r, !0),
                h.setUint32(24, c, !0),
                h.setUint32(28, 2 * c, !0),
                h.setUint16(32, 2 * r, !0),
                h.setUint16(34, 16, !0),
                a(h, 36, 'data'),
                h.setUint32(40, 2 * m, !0);
              for (var v = m, b = 44, y = 0; y < v; y++) h.setInt16(b, 32767 * f[y], !0), (b += 2);
              if (t) return t({ buffer: g, view: h });
              postMessage({ buffer: g, view: h });
            }
            if (e.noWorker)
              return void n(e, function (e) {
                t(e.buffer, e.view);
              });
            var i = a(n);
            (i.onmessage = function (e) {
              t(e.data.buffer, e.data.view), M.revokeObjectURL(i.workerURL), i.terminate();
            }),
              i.postMessage(e);
          }
          function a(e) {
            var t = M.createObjectURL(
                new Blob(
                  [e.toString(), ';this.onmessage =  function (eee) {' + e.name + '(eee.data);}'],
                  { type: 'application/javascript' }
                )
              ),
              n = new Worker(t);
            return (n.workerURL = t), n;
          }
          function r() {
            (m = []),
              (p = []),
              (h = 0),
              (S = !1),
              (g = !1),
              (T = !1),
              (y = null),
              (f.leftchannel = m),
              (f.rightchannel = p),
              (f.numberOfAudioChannels = v),
              (f.desiredSampRate = b),
              (f.sampleRate = k),
              (f.recordingLength = h),
              (x = { left: [], right: [], recordingLength: 0 });
          }
          function d() {
            u && ((u.onaudioprocess = null), u.disconnect(), (u = null)),
              w && (w.disconnect(), (w = null)),
              r();
          }
          function s(e) {
            if (!T) {
              if (
                (!1 === n() &&
                  (t.disableLogs || console.log('MediaStream seems stopped.'),
                  u.disconnect(),
                  (g = !1)),
                !g)
              )
                return void (w && (w.disconnect(), (w = null)));
              S ||
                ((S = !0),
                t.onAudioProcessStarted && t.onAudioProcessStarted(),
                t.initCallback && t.initCallback());
              var i = e.inputBuffer.getChannelData(0),
                o = new Float32Array(i);
              if ((m.push(o), 2 === v)) {
                var a = e.inputBuffer.getChannelData(1),
                  r = new Float32Array(a);
                p.push(r);
              }
              (h += R),
                (f.recordingLength = h),
                void 0 !== t.timeSlice &&
                  ((x.recordingLength += R), x.left.push(o), 2 === v && x.right.push(r));
            }
          }
          function c() {
            g &&
              'function' == typeof t.ondataavailable &&
              void 0 !== t.timeSlice &&
              (x.left.length
                ? (i(
                    {
                      desiredSampRate: b,
                      sampleRate: k,
                      numberOfAudioChannels: v,
                      internalInterleavedLength: x.recordingLength,
                      leftBuffers: x.left,
                      rightBuffers: 1 === v ? [] : x.right,
                    },
                    function (e, n) {
                      var i = new Blob([n], { type: 'audio/wav' });
                      t.ondataavailable(i), setTimeout(c, t.timeSlice);
                    }
                  ),
                  (x = { left: [], right: [], recordingLength: 0 }))
                : setTimeout(c, t.timeSlice));
          }
          if (!l(e, 'audio').length) throw 'Your stream has no audio tracks.';
          t = t || {};
          var u,
            f = this,
            m = [],
            p = [],
            g = !1,
            h = 0,
            v = 2,
            b = t.desiredSampRate;
          !0 === t.leftChannel && (v = 1),
            1 === t.numberOfAudioChannels && (v = 1),
            (!v || v < 1) && (v = 2),
            t.disableLogs ||
              console.log('StereoAudioRecorder is set to record number of channels: ' + v),
            void 0 === t.checkForInactiveTracks && (t.checkForInactiveTracks = !0),
            (this.record = function () {
              if (!1 === n()) throw 'Please make sure MediaStream is active.';
              r(), (S = T = !1), (g = !0), void 0 !== t.timeSlice && c();
            }),
            (this.stop = function (e) {
              (e = e || function () {}),
                (g = !1),
                i(
                  {
                    desiredSampRate: b,
                    sampleRate: k,
                    numberOfAudioChannels: v,
                    internalInterleavedLength: h,
                    leftBuffers: m,
                    rightBuffers: 1 === v ? [] : p,
                    noWorker: t.noWorker,
                  },
                  function (t, n) {
                    (f.blob = new Blob([n], { type: 'audio/wav' })),
                      (f.buffer = new ArrayBuffer(n.buffer.byteLength)),
                      (f.view = n),
                      (f.sampleRate = b || k),
                      (f.bufferSize = R),
                      (f.length = h),
                      (S = !1),
                      e && e(f.blob);
                  }
                );
            }),
            void 0 === o.Storage &&
              (o.Storage = {
                AudioContextConstructor: null,
                AudioContext: window.AudioContext || window.webkitAudioContext,
              }),
            (o.Storage.AudioContextConstructor &&
              'closed' !== o.Storage.AudioContextConstructor.state) ||
              (o.Storage.AudioContextConstructor = new o.Storage.AudioContext());
          var y = o.Storage.AudioContextConstructor,
            w = y.createMediaStreamSource(e),
            C = [0, 256, 512, 1024, 2048, 4096, 8192, 16384],
            R = void 0 === t.bufferSize ? 4096 : t.bufferSize;
          if (
            (-1 === C.indexOf(R) &&
              (t.disableLogs ||
                console.log('Legal values for buffer-size are ' + JSON.stringify(C, null, '\t'))),
            y.createJavaScriptNode)
          )
            u = y.createJavaScriptNode(R, v, v);
          else {
            if (!y.createScriptProcessor) throw 'WebAudio API has no support on this browser.';
            u = y.createScriptProcessor(R, v, v);
          }
          w.connect(u), t.bufferSize || (R = u.bufferSize);
          var k = void 0 !== t.sampleRate ? t.sampleRate : y.sampleRate || 44100;
          (k < 22050 || k > 96e3) &&
            (t.disableLogs || console.log('sample-rate must be under range 22050 and 96000.')),
            t.disableLogs ||
              (t.desiredSampRate && console.log('Desired sample-rate: ' + t.desiredSampRate));
          var T = !1;
          (this.pause = function () {
            T = !0;
          }),
            (this.resume = function () {
              if (!1 === n()) throw 'Please make sure MediaStream is active.';
              if (!g)
                return (
                  t.disableLogs || console.log('Seems recording has been restarted.'),
                  void this.record()
                );
              T = !1;
            }),
            (this.clearRecordedData = function () {
              (t.checkForInactiveTracks = !1), g && this.stop(d), d();
            }),
            (this.name = 'StereoAudioRecorder'),
            (this.toString = function () {
              return this.name;
            });
          var S = !1;
          (u.onaudioprocess = s),
            y.createMediaStreamDestination
              ? u.connect(y.createMediaStreamDestination())
              : u.connect(y.destination),
            (this.leftchannel = m),
            (this.rightchannel = p),
            (this.numberOfAudioChannels = v),
            (this.desiredSampRate = b),
            (this.sampleRate = k),
            (f.recordingLength = h);
          var x = { left: [], right: [], recordingLength: 0 };
        }
        /**
         * CanvasRecorder is a standalone class used by {@link RecordRTC} to bring HTML5-Canvas recording into video WebM. It uses HTML2Canvas library and runs top over {@link Whammy}.
         * @summary HTML2Canvas recording into video WebM.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef CanvasRecorder
         * @class
         * @example
         * var recorder = new CanvasRecorder(htmlElement, { disableLogs: true, useWhammyRecorder: true });
         * recorder.record();
         * recorder.stop(function(blob) {
         *     video.src = URL.createObjectURL(blob);
         * });
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {HTMLElement} htmlElement - querySelector/getElementById/getElementsByTagName[0]/etc.
         * @param {object} config - {disableLogs:true, initCallback: function}
         */
        function v(e, t) {
          function n() {
            (h.frames = []), (f = !1), (m = !1);
          }
          function i() {
            var t = document.createElement('canvas'),
              n = t.getContext('2d');
            return (t.width = e.width), (t.height = e.height), n.drawImage(e, 0, 0), t;
          }
          function o() {
            if (m) return (p = new Date().getTime()), setTimeout(o, 500);
            if ('canvas' === e.nodeName.toLowerCase()) {
              var n = new Date().getTime() - p;
              return (
                (p = new Date().getTime()),
                h.frames.push({ image: i(), duration: n }),
                void (f && setTimeout(o, t.frameInterval))
              );
            }
            html2canvas(e, {
              grabMouse: void 0 === t.showMousePointer || t.showMousePointer,
              onrendered: function (e) {
                var n = new Date().getTime() - p;
                if (!n) return setTimeout(o, t.frameInterval);
                (p = new Date().getTime()),
                  h.frames.push({ image: e.toDataURL('image/webp', 1), duration: n }),
                  f && setTimeout(o, t.frameInterval);
              },
            });
          }
          if ('undefined' == typeof html2canvas)
            throw 'Please link: https://www.webrtc-experiment.com/screenshot.js';
          (t = t || {}), t.frameInterval || (t.frameInterval = 10);
          var a = !1;
          ['captureStream', 'mozCaptureStream', 'webkitCaptureStream'].forEach(function (e) {
            e in document.createElement('canvas') && (a = !0);
          });
          var r = !(
              (!window.webkitRTCPeerConnection && !window.webkitGetUserMedia) ||
              !window.chrome
            ),
            d = 50,
            s = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
          r && s && s[2] && (d = parseInt(s[2], 10)),
            r && d < 52 && (a = !1),
            t.useWhammyRecorder && (a = !1);
          var c, u;
          if (a)
            if (
              (t.disableLogs ||
                console.log(
                  'Your browser supports both MediRecorder API and canvas.captureStream!'
                ),
              e instanceof HTMLCanvasElement)
            )
              c = e;
            else {
              if (!(e instanceof CanvasRenderingContext2D))
                throw 'Please pass either HTMLCanvasElement or CanvasRenderingContext2D.';
              c = e.canvas;
            }
          else
            navigator.mozGetUserMedia &&
              (t.disableLogs || console.error('Canvas recording is NOT supported in Firefox.'));
          var f;
          (this.record = function () {
            if (((f = !0), a && !t.useWhammyRecorder)) {
              var e;
              'captureStream' in c
                ? (e = c.captureStream(25))
                : 'mozCaptureStream' in c
                ? (e = c.mozCaptureStream(25))
                : 'webkitCaptureStream' in c && (e = c.webkitCaptureStream(25));
              try {
                var n = new E();
                n.addTrack(l(e, 'video')[0]), (e = n);
              } catch (e) {}
              if (!e) throw 'captureStream API are NOT available.';
              (u = new g(e, { mimeType: t.mimeType || 'video/webm' })), u.record();
            } else (h.frames = []), (p = new Date().getTime()), o();
            t.initCallback && t.initCallback();
          }),
            (this.getWebPImages = function (n) {
              if ('canvas' !== e.nodeName.toLowerCase()) return void n();
              var i = h.frames.length;
              h.frames.forEach(function (e, n) {
                var o = i - n;
                t.disableLogs || console.log(o + '/' + i + ' frames remaining'),
                  t.onEncodingCallback && t.onEncodingCallback(o, i);
                var a = e.image.toDataURL('image/webp', 1);
                h.frames[n].image = a;
              }),
                t.disableLogs || console.log('Generating WebM'),
                n();
            }),
            (this.stop = function (e) {
              f = !1;
              var n = this;
              if (a && u) return void u.stop(e);
              this.getWebPImages(function () {
                h.compile(function (i) {
                  t.disableLogs || console.log('Recording finished!'),
                    (n.blob = i),
                    n.blob.forEach && (n.blob = new Blob([], { type: 'video/webm' })),
                    e && e(n.blob),
                    (h.frames = []);
                });
              });
            });
          var m = !1;
          (this.pause = function () {
            if (((m = !0), u instanceof g)) return void u.pause();
          }),
            (this.resume = function () {
              if (((m = !1), u instanceof g)) return void u.resume();
              f || this.record();
            }),
            (this.clearRecordedData = function () {
              f && this.stop(n), n();
            }),
            (this.name = 'CanvasRecorder'),
            (this.toString = function () {
              return this.name;
            });
          var p = new Date().getTime(),
            h = new F.Video(100);
        }
        /**
         * WhammyRecorder is a standalone class used by {@link RecordRTC} to bring video recording in Chrome. It runs top over {@link Whammy}.
         * @summary Video recording feature in Chrome.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef WhammyRecorder
         * @class
         * @example
         * var recorder = new WhammyRecorder(mediaStream);
         * recorder.record();
         * recorder.stop(function(blob) {
         *     video.src = URL.createObjectURL(blob);
         * });
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {MediaStream} mediaStream - MediaStream object fetched using getUserMedia API or generated using captureStreamUntilEnded or WebAudio API.
         * @param {object} config - {disableLogs: true, initCallback: function, video: HTMLVideoElement, etc.}
         */
        function b(e, t) {
          function n(e) {
            e = void 0 !== e ? e : 10;
            var t = new Date().getTime() - c;
            return t
              ? d
                ? ((c = new Date().getTime()), setTimeout(n, 100))
                : ((c = new Date().getTime()),
                  s.paused && s.play(),
                  m.drawImage(s, 0, 0, l.width, l.height),
                  u.frames.push({ duration: t, image: l.toDataURL('image/webp') }),
                  void (r || setTimeout(n, e, e)))
              : setTimeout(n, e, e);
          }
          function i(e) {
            var t = -1,
              n = e.length;
            !(function i() {
              if (++t === n) return void e.callback();
              setTimeout(function () {
                e.functionToLoop(i, t);
              }, 1);
            })();
          }
          function o(e, t, n, o, a) {
            var r = document.createElement('canvas');
            (r.width = l.width), (r.height = l.height);
            var d = r.getContext('2d'),
              s = [],
              c = -1 === t,
              u = t && t > 0 && t <= e.length ? t : e.length,
              f = { r: 0, g: 0, b: 0 },
              m = Math.sqrt(Math.pow(255, 2) + Math.pow(255, 2) + Math.pow(255, 2)),
              p = n && n >= 0 && n <= 1 ? n : 0,
              g = o && o >= 0 && o <= 1 ? o : 0,
              h = !1;
            i({
              length: u,
              functionToLoop: function (t, n) {
                var i,
                  o,
                  a,
                  r = function () {
                    (!h && a - i <= a * g) || (c && (h = !0), s.push(e[n])), t();
                  };
                if (h) r();
                else {
                  var u = new Image();
                  (u.onload = function () {
                    d.drawImage(u, 0, 0, l.width, l.height);
                    var e = d.getImageData(0, 0, l.width, l.height);
                    (i = 0), (o = e.data.length), (a = e.data.length / 4);
                    for (var t = 0; t < o; t += 4) {
                      var n = { r: e.data[t], g: e.data[t + 1], b: e.data[t + 2] };
                      Math.sqrt(
                        Math.pow(n.r - f.r, 2) + Math.pow(n.g - f.g, 2) + Math.pow(n.b - f.b, 2)
                      ) <=
                        m * p && i++;
                    }
                    r();
                  }),
                    (u.src = e[n].image);
                }
              },
              callback: function () {
                (s = s.concat(e.slice(u))), s.length <= 0 && s.push(e[e.length - 1]), a(s);
              },
            });
          }
          function a() {
            (u.frames = []), (r = !0), (d = !1);
          }
          (t = t || {}),
            t.frameInterval || (t.frameInterval = 10),
            t.disableLogs || console.log('Using frames-interval:', t.frameInterval),
            (this.record = function () {
              t.width || (t.width = 320),
                t.height || (t.height = 240),
                t.video || (t.video = { width: t.width, height: t.height }),
                t.canvas || (t.canvas = { width: t.width, height: t.height }),
                (l.width = t.canvas.width || 320),
                (l.height = t.canvas.height || 240),
                (m = l.getContext('2d')),
                t.video && t.video instanceof HTMLVideoElement
                  ? ((s = t.video.cloneNode()), t.initCallback && t.initCallback())
                  : ((s = document.createElement('video')),
                    f(e, s),
                    (s.onloadedmetadata = function () {
                      t.initCallback && t.initCallback();
                    }),
                    (s.width = t.video.width),
                    (s.height = t.video.height)),
                (s.muted = !0),
                s.play(),
                (c = new Date().getTime()),
                (u = new F.Video()),
                t.disableLogs ||
                  (console.log('canvas resolutions', l.width, '*', l.height),
                  console.log('video width/height', s.width || l.width, '*', s.height || l.height)),
                n(t.frameInterval);
            });
          var r = !1;
          this.stop = function (e) {
            (e = e || function () {}), (r = !0);
            var n = this;
            setTimeout(function () {
              o(u.frames, -1, null, null, function (i) {
                (u.frames = i),
                  t.advertisement &&
                    t.advertisement.length &&
                    (u.frames = t.advertisement.concat(u.frames)),
                  u.compile(function (t) {
                    (n.blob = t),
                      n.blob.forEach && (n.blob = new Blob([], { type: 'video/webm' })),
                      e && e(n.blob);
                  });
              });
            }, 10);
          };
          var d = !1;
          (this.pause = function () {
            d = !0;
          }),
            (this.resume = function () {
              (d = !1), r && this.record();
            }),
            (this.clearRecordedData = function () {
              r || this.stop(a), a();
            }),
            (this.name = 'WhammyRecorder'),
            (this.toString = function () {
              return this.name;
            });
          var s,
            c,
            u,
            l = document.createElement('canvas'),
            m = l.getContext('2d');
        }
        /**
         * GifRecorder is standalone calss used by {@link RecordRTC} to record video or canvas into animated gif.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef GifRecorder
         * @class
         * @example
         * var recorder = new GifRecorder(mediaStream || canvas || context, { onGifPreview: function, onGifRecordingStarted: function, width: 1280, height: 720, frameRate: 200, quality: 10 });
         * recorder.record();
         * recorder.stop(function(blob) {
         *     img.src = URL.createObjectURL(blob);
         * });
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {MediaStream} mediaStream - MediaStream object or HTMLCanvasElement or CanvasRenderingContext2D.
         * @param {object} config - {disableLogs:true, initCallback: function, width: 320, height: 240, frameRate: 200, quality: 10}
         */
        function y(e, t) {
          function n() {
            p && (p.stream().bin = []);
          }
          if ('undefined' == typeof GIFEncoder) {
            var i = document.createElement('script');
            (i.src = 'https://www.webrtc-experiment.com/gif-recorder.js'),
              (document.body || document.documentElement).appendChild(i);
          }
          t = t || {};
          var o = e instanceof CanvasRenderingContext2D || e instanceof HTMLCanvasElement;
          (this.record = function () {
            function e(n) {
              if (!0 !== h.clearedRecordedData) {
                if (a)
                  return setTimeout(function () {
                    e(n);
                  }, 100);
                (g = x(e)),
                  void 0 === typeof m && (m = n),
                  n - m < 90 ||
                    (!o && c.paused && c.play(),
                    o || d.drawImage(c, 0, 0, r.width, r.height),
                    t.onGifPreview && t.onGifPreview(r.toDataURL('image/png')),
                    p.addFrame(d),
                    (m = n));
              }
            }
            return 'undefined' == typeof GIFEncoder
              ? void setTimeout(h.record, 1e3)
              : s
              ? (o ||
                  (t.width || (t.width = c.offsetWidth || 320),
                  t.height || (t.height = c.offsetHeight || 240),
                  t.video || (t.video = { width: t.width, height: t.height }),
                  t.canvas || (t.canvas = { width: t.width, height: t.height }),
                  (r.width = t.canvas.width || 320),
                  (r.height = t.canvas.height || 240),
                  (c.width = t.video.width || 320),
                  (c.height = t.video.height || 240)),
                (p = new GIFEncoder()),
                p.setRepeat(0),
                p.setDelay(t.frameRate || 200),
                p.setQuality(t.quality || 10),
                p.start(),
                'function' == typeof t.onGifRecordingStarted && t.onGifRecordingStarted(),
                (u = Date.now()),
                (g = x(e)),
                void (t.initCallback && t.initCallback()))
              : void setTimeout(h.record, 1e3);
          }),
            (this.stop = function (e) {
              (e = e || function () {}),
                g && D(g),
                (l = Date.now()),
                (this.blob = new Blob([new Uint8Array(p.stream().bin)], { type: 'image/gif' })),
                e(this.blob),
                (p.stream().bin = []);
            });
          var a = !1;
          (this.pause = function () {
            a = !0;
          }),
            (this.resume = function () {
              a = !1;
            }),
            (this.clearRecordedData = function () {
              (h.clearedRecordedData = !0), n();
            }),
            (this.name = 'GifRecorder'),
            (this.toString = function () {
              return this.name;
            });
          var r = document.createElement('canvas'),
            d = r.getContext('2d');
          o &&
            (e instanceof CanvasRenderingContext2D
              ? ((d = e), (r = d.canvas))
              : e instanceof HTMLCanvasElement && ((d = e.getContext('2d')), (r = e)));
          var s = !0;
          if (!o) {
            var c = document.createElement('video');
            (c.muted = !0),
              (c.autoplay = !0),
              (c.playsInline = !0),
              (s = !1),
              (c.onloadedmetadata = function () {
                s = !0;
              }),
              f(e, c),
              c.play();
          }
          var u,
            l,
            m,
            p,
            g = null,
            h = this;
        }
        function w(e, t) {
          function i(e, t) {
            'srcObject' in t
              ? (t.srcObject = e)
              : 'mozSrcObject' in t
              ? (t.mozSrcObject = e)
              : (t.srcObject = e);
          }
          function a() {
            if (!m) {
              var e = f.length,
                t = !1,
                n = [];
              if (
                (f.forEach(function (e) {
                  e.stream || (e.stream = {}), e.stream.fullcanvas ? (t = e) : n.push(e);
                }),
                t)
              )
                (p.width = t.stream.width), (p.height = t.stream.height);
              else if (n.length) {
                p.width = e > 1 ? 2 * n[0].width : n[0].width;
                var i = 1;
                (3 !== e && 4 !== e) || (i = 2),
                  (5 !== e && 6 !== e) || (i = 3),
                  (7 !== e && 8 !== e) || (i = 4),
                  (9 !== e && 10 !== e) || (i = 5),
                  (p.height = n[0].height * i);
              } else (p.width = h.width || 360), (p.height = h.height || 240);
              t && t instanceof HTMLVideoElement && r(t),
                n.forEach(function (e, t) {
                  r(e, t);
                }),
                setTimeout(a, h.frameInterval);
            }
          }
          function r(e, t) {
            if (!m) {
              var n = 0,
                i = 0,
                o = e.width,
                a = e.height;
              1 === t && (n = e.width),
                2 === t && (i = e.height),
                3 === t && ((n = e.width), (i = e.height)),
                4 === t && (i = 2 * e.height),
                5 === t && ((n = e.width), (i = 2 * e.height)),
                6 === t && (i = 3 * e.height),
                7 === t && ((n = e.width), (i = 3 * e.height)),
                void 0 !== e.stream.left && (n = e.stream.left),
                void 0 !== e.stream.top && (i = e.stream.top),
                void 0 !== e.stream.width && (o = e.stream.width),
                void 0 !== e.stream.height && (a = e.stream.height),
                g.drawImage(e, n, i, o, a),
                'function' == typeof e.stream.onRender && e.stream.onRender(g, n, i, o, a, t);
            }
          }
          function d() {
            m = !1;
            var t = s(),
              n = c();
            n &&
              n
                .getTracks()
                .filter(function (e) {
                  return 'audio' === e.kind;
                })
                .forEach(function (e) {
                  t.addTrack(e);
                });
            var i;
            return (
              e.forEach(function (e) {
                e.fullcanvas && (i = !0);
              }),
              t
            );
          }
          function s() {
            l();
            var e;
            'captureStream' in p
              ? (e = p.captureStream())
              : 'mozCaptureStream' in p
              ? (e = p.mozCaptureStream())
              : h.disableLogs ||
                console.error(
                  'Upgrade to latest Chrome or otherwise enable this flag: chrome://flags/#enable-experimental-web-platform-features'
                );
            var t = new y();
            return (
              e
                .getTracks()
                .filter(function (e) {
                  return 'video' === e.kind;
                })
                .forEach(function (e) {
                  t.addTrack(e);
                }),
              (p.stream = t),
              t
            );
          }
          function c() {
            w.AudioContextConstructor || (w.AudioContextConstructor = new w.AudioContext()),
              (h.audioContext = w.AudioContextConstructor),
              (h.audioSources = []),
              !0 === h.useGainNode &&
                ((h.gainNode = h.audioContext.createGain()),
                h.gainNode.connect(h.audioContext.destination),
                (h.gainNode.gain.value = 0));
            var t = 0;
            if (
              (e.forEach(function (e) {
                if (
                  e.getTracks().filter(function (e) {
                    return 'audio' === e.kind;
                  }).length
                ) {
                  t++;
                  var n = h.audioContext.createMediaStreamSource(e);
                  !0 === h.useGainNode && n.connect(h.gainNode), h.audioSources.push(n);
                }
              }),
              t)
            )
              return (
                (h.audioDestination = h.audioContext.createMediaStreamDestination()),
                h.audioSources.forEach(function (e) {
                  e.connect(h.audioDestination);
                }),
                h.audioDestination.stream
              );
          }
          function u(e) {
            var n = document.createElement('video');
            return (
              i(e, n),
              (n.className = t),
              (n.muted = !0),
              (n.volume = 0),
              (n.width = e.width || h.width || 360),
              (n.height = e.height || h.height || 240),
              n.play(),
              n
            );
          }
          function l(t) {
            (f = []),
              (t = t || e),
              t.forEach(function (e) {
                if (
                  e.getTracks().filter(function (e) {
                    return 'video' === e.kind;
                  }).length
                ) {
                  var t = u(e);
                  (t.stream = e), f.push(t);
                }
              });
          }
          !(function (e) {
            void 0 === o &&
              e &&
              'undefined' == typeof window &&
              void 0 !== n &&
              ((n.navigator = {
                userAgent:
                  'Fake/5.0 (FakeOS) AppleWebKit/123 (KHTML, like Gecko) Fake/12.3.4567.89 Fake/123.45',
                getUserMedia: function () {},
              }),
              n.console || (n.console = {}),
              (void 0 !== n.console.log && void 0 !== n.console.error) ||
                (n.console.error = n.console.log =
                  n.console.log ||
                  function () {
                    console.log(arguments);
                  }),
              'undefined' == typeof document &&
                ((e.document = {
                  documentElement: {
                    appendChild: function () {
                      return '';
                    },
                  },
                }),
                (document.createElement = document.captureStream = document.mozCaptureStream = function () {
                  var e = {
                    getContext: function () {
                      return e;
                    },
                    play: function () {},
                    pause: function () {},
                    drawImage: function () {},
                    toDataURL: function () {
                      return '';
                    },
                    style: {},
                  };
                  return e;
                }),
                (e.HTMLVideoElement = function () {})),
              'undefined' == typeof location &&
                (e.location = { protocol: 'file:', href: '', hash: '' }),
              'undefined' == typeof screen && (e.screen = { width: 0, height: 0 }),
              void 0 === b &&
                (e.URL = {
                  createObjectURL: function () {
                    return '';
                  },
                  revokeObjectURL: function () {
                    return '';
                  },
                }),
              (e.window = n));
          })(void 0 !== n ? n : null),
            (t = t || 'multi-streams-mixer');
          var f = [],
            m = !1,
            p = document.createElement('canvas'),
            g = p.getContext('2d');
          (p.style.opacity = 0),
            (p.style.position = 'absolute'),
            (p.style.zIndex = -1),
            (p.style.top = '-1000em'),
            (p.style.left = '-1000em'),
            (p.className = t),
            (document.body || document.documentElement).appendChild(p),
            (this.disableLogs = !1),
            (this.frameInterval = 10),
            (this.width = 360),
            (this.height = 240),
            (this.useGainNode = !0);
          var h = this,
            v = window.AudioContext;
          void 0 === v &&
            ('undefined' != typeof webkitAudioContext && (v = webkitAudioContext),
            'undefined' != typeof mozAudioContext && (v = mozAudioContext));
          var b = window.URL;
          void 0 === b && 'undefined' != typeof webkitURL && (b = webkitURL),
            'undefined' != typeof navigator &&
              void 0 === navigator.getUserMedia &&
              (void 0 !== navigator.webkitGetUserMedia &&
                (navigator.getUserMedia = navigator.webkitGetUserMedia),
              void 0 !== navigator.mozGetUserMedia &&
                (navigator.getUserMedia = navigator.mozGetUserMedia));
          var y = window.MediaStream;
          void 0 === y && 'undefined' != typeof webkitMediaStream && (y = webkitMediaStream),
            void 0 !== y &&
              void 0 === y.prototype.stop &&
              (y.prototype.stop = function () {
                this.getTracks().forEach(function (e) {
                  e.stop();
                });
              });
          var w = {};
          void 0 !== v
            ? (w.AudioContext = v)
            : 'undefined' != typeof webkitAudioContext && (w.AudioContext = webkitAudioContext),
            (this.startDrawingFrames = function () {
              a();
            }),
            (this.appendStreams = function (t) {
              if (!t) throw 'First parameter is required.';
              t instanceof Array || (t = [t]),
                t.forEach(function (t) {
                  var n = new y();
                  if (
                    t.getTracks().filter(function (e) {
                      return 'video' === e.kind;
                    }).length
                  ) {
                    var i = u(t);
                    (i.stream = t),
                      f.push(i),
                      n.addTrack(
                        t.getTracks().filter(function (e) {
                          return 'video' === e.kind;
                        })[0]
                      );
                  }
                  if (
                    t.getTracks().filter(function (e) {
                      return 'audio' === e.kind;
                    }).length
                  ) {
                    var o = h.audioContext.createMediaStreamSource(t);
                    (h.audioDestination = h.audioContext.createMediaStreamDestination()),
                      o.connect(h.audioDestination),
                      n.addTrack(
                        h.audioDestination.stream.getTracks().filter(function (e) {
                          return 'audio' === e.kind;
                        })[0]
                      );
                  }
                  e.push(n);
                });
            }),
            (this.releaseStreams = function () {
              (f = []),
                (m = !0),
                h.gainNode && (h.gainNode.disconnect(), (h.gainNode = null)),
                h.audioSources.length &&
                  (h.audioSources.forEach(function (e) {
                    e.disconnect();
                  }),
                  (h.audioSources = [])),
                h.audioDestination &&
                  (h.audioDestination.disconnect(), (h.audioDestination = null)),
                h.audioContext && h.audioContext.close(),
                (h.audioContext = null),
                g.clearRect(0, 0, p.width, p.height),
                p.stream && (p.stream.stop(), (p.stream = null));
            }),
            (this.resetVideoStreams = function (e) {
              !e || e instanceof Array || (e = [e]), l(e);
            }),
            (this.name = 'MultiStreamsMixer'),
            (this.toString = function () {
              return this.name;
            }),
            (this.getMixedStream = d);
        }
        /**
         * MultiStreamRecorder can record multiple videos in single container.
         * @summary Multi-videos recorder.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef MultiStreamRecorder
         * @class
         * @example
         * var options = {
         *     mimeType: 'video/webm'
         * }
         * var recorder = new MultiStreamRecorder(ArrayOfMediaStreams, options);
         * recorder.record();
         * recorder.stop(function(blob) {
         *     video.src = URL.createObjectURL(blob);
         *
         *     // or
         *     var blob = recorder.blob;
         * });
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {MediaStreams} mediaStreams - Array of MediaStreams.
         * @param {object} config - {disableLogs:true, frameInterval: 1, mimeType: "video/webm"}
         */
        function C(e, t) {
          function n() {
            var t = [];
            return (
              e.forEach(function (e) {
                l(e, 'video').forEach(function (e) {
                  t.push(e);
                });
              }),
              t
            );
          }
          e = e || [];
          var i,
            o,
            a = this;
          (t = t || {
            elementClass: 'multi-streams-mixer',
            mimeType: 'video/webm',
            video: { width: 360, height: 240 },
          }),
            t.frameInterval || (t.frameInterval = 10),
            t.video || (t.video = {}),
            t.video.width || (t.video.width = 360),
            t.video.height || (t.video.height = 240),
            (this.record = function () {
              (i = new w(e, t.elementClass || 'multi-streams-mixer')),
                n().length &&
                  ((i.frameInterval = t.frameInterval || 10),
                  (i.width = t.video.width || 360),
                  (i.height = t.video.height || 240),
                  i.startDrawingFrames()),
                t.previewStream &&
                  'function' == typeof t.previewStream &&
                  t.previewStream(i.getMixedStream()),
                (o = new g(i.getMixedStream(), t)),
                o.record();
            }),
            (this.stop = function (e) {
              o &&
                o.stop(function (t) {
                  (a.blob = t), e(t), a.clearRecordedData();
                });
            }),
            (this.pause = function () {
              o && o.pause();
            }),
            (this.resume = function () {
              o && o.resume();
            }),
            (this.clearRecordedData = function () {
              o && (o.clearRecordedData(), (o = null)), i && (i.releaseStreams(), (i = null));
            }),
            (this.addStreams = function (n) {
              if (!n) throw 'First parameter is required.';
              n instanceof Array || (n = [n]),
                e.concat(n),
                o &&
                  i &&
                  (i.appendStreams(n),
                  t.previewStream &&
                    'function' == typeof t.previewStream &&
                    t.previewStream(i.getMixedStream()));
            }),
            (this.resetVideoStreams = function (e) {
              i && (!e || e instanceof Array || (e = [e]), i.resetVideoStreams(e));
            }),
            (this.getMixer = function () {
              return i;
            }),
            (this.name = 'MultiStreamRecorder'),
            (this.toString = function () {
              return this.name;
            });
        }
        /**
         * RecordRTCPromisesHandler adds promises support in {@link RecordRTC}. Try a {@link https://github.com/muaz-khan/RecordRTC/blob/master/simple-demos/RecordRTCPromisesHandler.html|demo here}
         * @summary Promises for {@link RecordRTC}
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef RecordRTCPromisesHandler
         * @class
         * @example
         * var recorder = new RecordRTCPromisesHandler(mediaStream, options);
         * recorder.startRecording()
         *         .then(successCB)
         *         .catch(errorCB);
         * // Note: You can access all RecordRTC API using "recorder.recordRTC" e.g.
         * recorder.recordRTC.onStateChanged = function(state) {};
         * recorder.recordRTC.setRecordingDuration(5000);
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {MediaStream} mediaStream - Single media-stream object, array of media-streams, html-canvas-element, etc.
         * @param {object} config - {type:"video", recorderType: MediaStreamRecorder, disableLogs: true, numberOfAudioChannels: 1, bufferSize: 0, sampleRate: 0, video: HTMLVideoElement, etc.}
         * @throws Will throw an error if "new" keyword is not used to initiate "RecordRTCPromisesHandler". Also throws error if first argument "MediaStream" is missing.
         * @requires {@link RecordRTC}
         */
        function R(e, t) {
          if (!this) throw 'Use "new RecordRTCPromisesHandler()"';
          if (void 0 === e) throw 'First argument "MediaStream" is required.';
          var n = this;
          (n.recordRTC = new o(e, t)),
            (this.startRecording = function () {
              return new Promise(function (e, t) {
                try {
                  n.recordRTC.startRecording(), e();
                } catch (e) {
                  t(e);
                }
              });
            }),
            (this.stopRecording = function () {
              return new Promise(function (e, t) {
                try {
                  n.recordRTC.stopRecording(function (i) {
                    if (((n.blob = n.recordRTC.getBlob()), !n.blob || !n.blob.size))
                      return void t('Empty blob.', n.blob);
                    e(i);
                  });
                } catch (e) {
                  t(e);
                }
              });
            }),
            (this.pauseRecording = function () {
              return new Promise(function (e, t) {
                try {
                  n.recordRTC.pauseRecording(), e();
                } catch (e) {
                  t(e);
                }
              });
            }),
            (this.resumeRecording = function () {
              return new Promise(function (e, t) {
                try {
                  n.recordRTC.resumeRecording(), e();
                } catch (e) {
                  t(e);
                }
              });
            }),
            (this.getDataURL = function (e) {
              return new Promise(function (e, t) {
                try {
                  n.recordRTC.getDataURL(function (t) {
                    e(t);
                  });
                } catch (e) {
                  t(e);
                }
              });
            }),
            (this.getBlob = function () {
              return new Promise(function (e, t) {
                try {
                  e(n.recordRTC.getBlob());
                } catch (e) {
                  t(e);
                }
              });
            }),
            (this.getInternalRecorder = function () {
              return new Promise(function (e, t) {
                try {
                  e(n.recordRTC.getInternalRecorder());
                } catch (e) {
                  t(e);
                }
              });
            }),
            (this.reset = function () {
              return new Promise(function (e, t) {
                try {
                  e(n.recordRTC.reset());
                } catch (e) {
                  t(e);
                }
              });
            }),
            (this.destroy = function () {
              return new Promise(function (e, t) {
                try {
                  e(n.recordRTC.destroy());
                } catch (e) {
                  t(e);
                }
              });
            }),
            (this.getState = function () {
              return new Promise(function (e, t) {
                try {
                  e(n.recordRTC.getState());
                } catch (e) {
                  t(e);
                }
              });
            }),
            (this.blob = null),
            (this.version = '5.5.9');
        }
        /**
         * WebAssemblyRecorder lets you create webm videos in JavaScript via WebAssembly. The library consumes raw RGBA32 buffers (4 bytes per pixel) and turns them into a webm video with the given framerate and quality. This makes it compatible out-of-the-box with ImageData from a CANVAS. With realtime mode you can also use webm-wasm for streaming webm videos.
         * @summary Video recording feature in Chrome, Firefox and maybe Edge.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef WebAssemblyRecorder
         * @class
         * @example
         * var recorder = new WebAssemblyRecorder(mediaStream);
         * recorder.record();
         * recorder.stop(function(blob) {
         *     video.src = URL.createObjectURL(blob);
         * });
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         * @param {MediaStream} mediaStream - MediaStream object fetched using getUserMedia API or generated using captureStreamUntilEnded or WebAudio API.
         * @param {object} config - {webAssemblyPath:'webm-wasm.wasm',workerPath: 'webm-worker.js', frameRate: 30, width: 1920, height: 1080, bitrate: 1024}
         */
        function k(e, t) {
          function n() {
            return new ReadableStream({
              start: function (n) {
                var i = document.createElement('canvas'),
                  o = document.createElement('video');
                (o.srcObject = e),
                  (o.onplaying = function () {
                    (i.width = t.width), (i.height = t.height);
                    var e = i.getContext('2d'),
                      a = 1e3 / t.frameRate;
                    setTimeout(function i() {
                      e.drawImage(o, 0, 0),
                        n.enqueue(e.getImageData(0, 0, t.width, t.height)),
                        setTimeout(i, a);
                    }, a);
                  }),
                  o.play();
              },
            });
          }
          function i(e, o) {
            if (!t.workerPath && !o)
              return void fetch('https://unpkg.com/webm-wasm@latest/dist/webm-worker.js').then(
                function (t) {
                  t.arrayBuffer().then(function (t) {
                    i(e, t);
                  });
                }
              );
            if (!t.workerPath && o instanceof ArrayBuffer) {
              var s = new Blob([o], { type: 'text/javascript' });
              t.workerPath = M.createObjectURL(s);
            }
            t.workerPath || console.error('workerPath parameter is missing.'),
              (a = new Worker(t.workerPath)),
              a.postMessage(
                t.webAssemblyPath || 'https://unpkg.com/webm-wasm@latest/dist/webm-wasm.wasm'
              ),
              a.addEventListener('message', function (e) {
                'READY' === e.data
                  ? (a.postMessage({
                      width: t.width,
                      height: t.height,
                      bitrate: t.bitrate || 1200,
                      timebaseDen: t.frameRate || 30,
                      realtime: !0,
                    }),
                    n().pipeTo(
                      new WritableStream({
                        write: function (e) {
                          a && a.postMessage(e.data.buffer, [e.data.buffer]);
                        },
                      })
                    ))
                  : e.data && (r || d.push(e.data));
              });
          }
          function o() {
            a && (a.postMessage(null), a.terminate(), (a = null));
          }
          ('undefined' != typeof ReadableStream && 'undefined' != typeof WritableStream) ||
            console.error(
              'Following polyfill is strongly recommended: https://unpkg.com/@mattiasbuelens/web-streams-polyfill/dist/polyfill.min.js'
            ),
            (t = t || {}),
            (t.width = t.width || 640),
            (t.height = t.height || 480),
            (t.frameRate = t.frameRate || 30),
            (t.bitrate = t.bitrate || 1200);
          var a;
          this.record = function () {
            (d = []),
              (r = !1),
              (this.blob = null),
              i(e),
              'function' == typeof t.initCallback && t.initCallback();
          };
          var r;
          (this.pause = function () {
            r = !0;
          }),
            (this.resume = function () {
              r = !1;
            });
          var d = [];
          (this.stop = function (e) {
            o(), (this.blob = new Blob(d, { type: 'video/webm' })), e(this.blob);
          }),
            (this.name = 'WebAssemblyRecorder'),
            (this.toString = function () {
              return this.name;
            }),
            (this.clearRecordedData = function () {
              (d = []), (r = !1), (this.blob = null);
            }),
            (this.blob = null);
        }
        var T, S, T, S;
        (o.version = '5.5.9'),
          (e.exports = o),
          (T = []),
          void 0 !==
            (S = function () {
              return o;
            }.apply(t, T)) && (e.exports = S),
          (o.getFromDisk = function (e, t) {
            if (!t) throw 'callback is mandatory.';
            console.log(
              'Getting recorded ' + ('all' === e ? 'blobs' : e + ' blob ') + ' from disk!'
            ),
              N.Fetch(function (n, i) {
                'all' !== e && i === e + 'Blob' && t && t(n),
                  'all' === e && t && t(n, i.replace('Blob', ''));
              });
          }),
          (o.writeToDisk = function (e) {
            console.log('Writing recorded blob(s) to disk!'),
              (e = e || {}),
              e.audio && e.video && e.gif
                ? e.audio.getDataURL(function (t) {
                    e.video.getDataURL(function (n) {
                      e.gif.getDataURL(function (e) {
                        N.Store({ audioBlob: t, videoBlob: n, gifBlob: e });
                      });
                    });
                  })
                : e.audio && e.video
                ? e.audio.getDataURL(function (t) {
                    e.video.getDataURL(function (e) {
                      N.Store({ audioBlob: t, videoBlob: e });
                    });
                  })
                : e.audio && e.gif
                ? e.audio.getDataURL(function (t) {
                    e.gif.getDataURL(function (e) {
                      N.Store({ audioBlob: t, gifBlob: e });
                    });
                  })
                : e.video && e.gif
                ? e.video.getDataURL(function (t) {
                    e.gif.getDataURL(function (e) {
                      N.Store({ videoBlob: t, gifBlob: e });
                    });
                  })
                : e.audio
                ? e.audio.getDataURL(function (e) {
                    N.Store({ audioBlob: e });
                  })
                : e.video
                ? e.video.getDataURL(function (e) {
                    N.Store({ videoBlob: e });
                  })
                : e.gif &&
                  e.gif.getDataURL(function (e) {
                    N.Store({ gifBlob: e });
                  });
          }),
          (d.getFromDisk = o.getFromDisk),
          (d.writeToDisk = o.writeToDisk),
          void 0 !== o && (o.MRecordRTC = d);
        !(function (e) {
          e &&
            'undefined' == typeof window &&
            void 0 !== n &&
            ((n.navigator = {
              userAgent:
                'Fake/5.0 (FakeOS) AppleWebKit/123 (KHTML, like Gecko) Fake/12.3.4567.89 Fake/123.45',
              getUserMedia: function () {},
            }),
            n.console || (n.console = {}),
            (void 0 !== n.console.log && void 0 !== n.console.error) ||
              (n.console.error = n.console.log =
                n.console.log ||
                function () {
                  console.log(arguments);
                }),
            'undefined' == typeof document &&
              ((e.document = {
                documentElement: {
                  appendChild: function () {
                    return '';
                  },
                },
              }),
              (document.createElement = document.captureStream = document.mozCaptureStream = function () {
                var e = {
                  getContext: function () {
                    return e;
                  },
                  play: function () {},
                  pause: function () {},
                  drawImage: function () {},
                  toDataURL: function () {
                    return '';
                  },
                  style: {},
                };
                return e;
              }),
              (e.HTMLVideoElement = function () {})),
            'undefined' == typeof location &&
              (e.location = { protocol: 'file:', href: '', hash: '' }),
            'undefined' == typeof screen && (e.screen = { width: 0, height: 0 }),
            void 0 === M &&
              (e.URL = {
                createObjectURL: function () {
                  return '';
                },
                revokeObjectURL: function () {
                  return '';
                },
              }),
            (e.window = n));
        })(void 0 !== n ? n : null);
        var x = window.requestAnimationFrame;
        if (void 0 === x)
          if ('undefined' != typeof webkitRequestAnimationFrame) x = webkitRequestAnimationFrame;
          else if ('undefined' != typeof mozRequestAnimationFrame) x = mozRequestAnimationFrame;
          else if ('undefined' != typeof msRequestAnimationFrame) x = msRequestAnimationFrame;
          else if (void 0 === x) {
            var O = 0;
            x = function (e, t) {
              var n = new Date().getTime(),
                i = Math.max(0, 16 - (n - O)),
                o = setTimeout(function () {
                  e(n + i);
                }, i);
              return (O = n + i), o;
            };
          }
        var D = window.cancelAnimationFrame;
        void 0 === D &&
          ('undefined' != typeof webkitCancelAnimationFrame
            ? (D = webkitCancelAnimationFrame)
            : 'undefined' != typeof mozCancelAnimationFrame
            ? (D = mozCancelAnimationFrame)
            : 'undefined' != typeof msCancelAnimationFrame
            ? (D = msCancelAnimationFrame)
            : void 0 === D &&
              (D = function (e) {
                clearTimeout(e);
              }));
        var A = window.AudioContext;
        void 0 === A &&
          ('undefined' != typeof webkitAudioContext && (A = webkitAudioContext),
          'undefined' != typeof mozAudioContext && (A = mozAudioContext));
        var M = window.URL;
        void 0 === M && 'undefined' != typeof webkitURL && (M = webkitURL),
          'undefined' != typeof navigator &&
            void 0 === navigator.getUserMedia &&
            (void 0 !== navigator.webkitGetUserMedia &&
              (navigator.getUserMedia = navigator.webkitGetUserMedia),
            void 0 !== navigator.mozGetUserMedia &&
              (navigator.getUserMedia = navigator.mozGetUserMedia));
        var L = !(
            -1 === navigator.userAgent.indexOf('Edge') ||
            (!navigator.msSaveBlob && !navigator.msSaveOrOpenBlob)
          ),
          U = !!window.opera || -1 !== navigator.userAgent.indexOf('OPR/'),
          I =
            navigator.userAgent.toLowerCase().indexOf('firefox') > -1 &&
            'netscape' in window &&
            / rv:/.test(navigator.userAgent),
          j =
            (!U && !L && !!navigator.webkitGetUserMedia) ||
            u() ||
            -1 !== navigator.userAgent.toLowerCase().indexOf('chrome/'),
          B = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        B && !j && -1 !== navigator.userAgent.indexOf('CriOS') && ((B = !1), (j = !0));
        var E = window.MediaStream;
        void 0 === E && 'undefined' != typeof webkitMediaStream && (E = webkitMediaStream),
          void 0 !== E &&
            void 0 === E.prototype.stop &&
            (E.prototype.stop = function () {
              this.getTracks().forEach(function (e) {
                e.stop();
              });
            }),
          void 0 !== o &&
            ((o.invokeSaveAsDialog = c),
            (o.getTracks = l),
            (o.getSeekableBlob = m),
            (o.bytesToSize = s),
            (o.isElectron = u));
        /**
         * Storage is a standalone object used by {@link RecordRTC} to store reusable objects e.g. "new AudioContext".
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @example
         * Storage.AudioContext === webkitAudioContext
         * @property {webkitAudioContext} AudioContext - Keeps a reference to AudioContext object.
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         */
        var P = {};
        void 0 !== A
          ? (P.AudioContext = A)
          : 'undefined' != typeof webkitAudioContext && (P.AudioContext = webkitAudioContext),
          void 0 !== o && (o.Storage = P),
          void 0 !== o && (o.MediaStreamRecorder = g),
          void 0 !== o && (o.StereoAudioRecorder = h),
          void 0 !== o && (o.CanvasRecorder = v),
          void 0 !== o && (o.WhammyRecorder = b);
        /**
         * Whammy is a standalone class used by {@link RecordRTC} to bring video recording in Chrome. It is written by {@link https://github.com/antimatter15|antimatter15}
         * @summary A real time javascript webm encoder based on a canvas hack.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @typedef Whammy
         * @class
         * @example
         * var recorder = new Whammy().Video(15);
         * recorder.add(context || canvas || dataURL);
         * var output = recorder.compile();
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         */
        var F = (function () {
          function e(e) {
            (this.frames = []), (this.duration = e || 1), (this.quality = 0.8);
          }
          function t(e) {
            var t = M.createObjectURL(
                new Blob(
                  [e.toString(), 'this.onmessage =  function (eee) {' + e.name + '(eee.data);}'],
                  { type: 'application/javascript' }
                )
              ),
              n = new Worker(t);
            return M.revokeObjectURL(t), n;
          }
          function n(e) {
            function t(e) {
              var t = i(e);
              if (!t) return [];
              for (
                var o = [
                    {
                      id: 440786851,
                      data: [
                        { data: 1, id: 17030 },
                        { data: 1, id: 17143 },
                        { data: 4, id: 17138 },
                        { data: 8, id: 17139 },
                        { data: 'webm', id: 17026 },
                        { data: 2, id: 17031 },
                        { data: 2, id: 17029 },
                      ],
                    },
                    {
                      id: 408125543,
                      data: [
                        {
                          id: 357149030,
                          data: [
                            { data: 1e6, id: 2807729 },
                            { data: 'whammy', id: 19840 },
                            { data: 'whammy', id: 22337 },
                            { data: f(t.duration), id: 17545 },
                          ],
                        },
                        {
                          id: 374648427,
                          data: [
                            {
                              id: 174,
                              data: [
                                { data: 1, id: 215 },
                                { data: 1, id: 29637 },
                                { data: 0, id: 156 },
                                { data: 'und', id: 2274716 },
                                { data: 'V_VP8', id: 134 },
                                { data: 'VP8', id: 2459272 },
                                { data: 1, id: 131 },
                                {
                                  id: 224,
                                  data: [
                                    { data: t.width, id: 176 },
                                    { data: t.height, id: 186 },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                  a = 0,
                  r = 0;
                a < e.length;

              ) {
                var s = [],
                  c = 0;
                do {
                  s.push(e[a]), (c += e[a].duration), a++;
                } while (a < e.length && c < 3e4);
                var u = { id: 524531317, data: n(r, 0, s) };
                o[1].data.push(u), (r += c);
              }
              return d(o);
            }
            function n(e, t, n) {
              return [{ data: e, id: 231 }].concat(
                n.map(function (e) {
                  var n = s({
                    discardable: 0,
                    frame: e.data.slice(4),
                    invisible: 0,
                    keyframe: 1,
                    lacing: 0,
                    trackNum: 1,
                    timecode: Math.round(t),
                  });
                  return (t += e.duration), { data: n, id: 163 };
                })
              );
            }
            function i(e) {
              if (!e[0])
                return void postMessage({
                  error:
                    'Something went wrong. Maybe WebP format is not supported in the current browser.',
                });
              for (var t = e[0].width, n = e[0].height, i = e[0].duration, o = 1; o < e.length; o++)
                i += e[o].duration;
              return { duration: i, width: t, height: n };
            }
            function o(e) {
              for (var t = []; e > 0; ) t.push(255 & e), (e >>= 8);
              return new Uint8Array(t.reverse());
            }
            function a(e) {
              return new Uint8Array(
                e.split('').map(function (e) {
                  return e.charCodeAt(0);
                })
              );
            }
            function r(e) {
              var t = [];
              e = (e.length % 8 ? new Array(9 - (e.length % 8)).join('0') : '') + e;
              for (var n = 0; n < e.length; n += 8) t.push(parseInt(e.substr(n, 8), 2));
              return new Uint8Array(t);
            }
            function d(e) {
              for (var t = [], n = 0; n < e.length; n++) {
                var i = e[n].data;
                'object' == typeof i && (i = d(i)),
                  'number' == typeof i && (i = r(i.toString(2))),
                  'string' == typeof i && (i = a(i));
                var s = i.size || i.byteLength || i.length,
                  c = Math.ceil(Math.ceil(Math.log(s) / Math.log(2)) / 8),
                  u = s.toString(2),
                  l = new Array(7 * c + 7 + 1 - u.length).join('0') + u,
                  f = new Array(c).join('0') + '1' + l;
                t.push(o(e[n].id)), t.push(r(f)), t.push(i);
              }
              return new Blob(t, { type: 'video/webm' });
            }
            function s(e) {
              var t = 0;
              if (
                (e.keyframe && (t |= 128),
                e.invisible && (t |= 8),
                e.lacing && (t |= e.lacing << 1),
                e.discardable && (t |= 1),
                e.trackNum > 127)
              )
                throw 'TrackNumber > 127 not supported';
              return (
                [128 | e.trackNum, e.timecode >> 8, 255 & e.timecode, t]
                  .map(function (e) {
                    return String.fromCharCode(e);
                  })
                  .join('') + e.frame
              );
            }
            function c(e) {
              for (var t = e.RIFF[0].WEBP[0], n = t.indexOf('*'), i = 0, o = []; i < 4; i++)
                o[i] = t.charCodeAt(n + 3 + i);
              var a, r, d;
              return (
                (d = (o[1] << 8) | o[0]),
                (a = 16383 & d),
                (d = (o[3] << 8) | o[2]),
                (r = 16383 & d),
                { width: a, height: r, data: t, riff: e }
              );
            }
            function u(e, t) {
              return parseInt(
                e
                  .substr(t + 4, 4)
                  .split('')
                  .map(function (e) {
                    var t = e.charCodeAt(0).toString(2);
                    return new Array(8 - t.length + 1).join('0') + t;
                  })
                  .join(''),
                2
              );
            }
            function l(e) {
              for (var t = 0, n = {}; t < e.length; ) {
                var i = e.substr(t, 4),
                  o = u(e, t),
                  a = e.substr(t + 4 + 4, o);
                (t += 8 + o),
                  (n[i] = n[i] || []),
                  'RIFF' === i || 'LIST' === i ? n[i].push(l(a)) : n[i].push(a);
              }
              return n;
            }
            function f(e) {
              return [].slice
                .call(new Uint8Array(new Float64Array([e]).buffer), 0)
                .map(function (e) {
                  return String.fromCharCode(e);
                })
                .reverse()
                .join('');
            }
            var m = new t(
              e.map(function (e) {
                var t = c(l(atob(e.image.slice(23))));
                return (t.duration = e.duration), t;
              })
            );
            postMessage(m);
          }
          return (
            (e.prototype.add = function (e, t) {
              if (
                ('canvas' in e && (e = e.canvas),
                'toDataURL' in e && (e = e.toDataURL('image/webp', this.quality)),
                !/^data:image\/webp;base64,/gi.test(e))
              )
                throw 'Input must be formatted properly as a base64 encoded DataURI of type image/webp';
              this.frames.push({ image: e, duration: t || this.duration });
            }),
            (e.prototype.compile = function (e) {
              var i = t(n);
              (i.onmessage = function (t) {
                if (t.data.error) return void console.error(t.data.error);
                e(t.data);
              }),
                i.postMessage(this.frames);
            }),
            { Video: e }
          );
        })();
        void 0 !== o && (o.Whammy = F);
        /**
         * DiskStorage is a standalone object used by {@link RecordRTC} to store recorded blobs in IndexedDB storage.
         * @summary Writing blobs into IndexedDB.
         * @license {@link https://github.com/muaz-khan/RecordRTC/blob/master/LICENSE|MIT}
         * @author {@link https://MuazKhan.com|Muaz Khan}
         * @example
         * DiskStorage.Store({
         *     audioBlob: yourAudioBlob,
         *     videoBlob: yourVideoBlob,
         *     gifBlob  : yourGifBlob
         * });
         * DiskStorage.Fetch(function(dataURL, type) {
         *     if(type === 'audioBlob') { }
         *     if(type === 'videoBlob') { }
         *     if(type === 'gifBlob')   { }
         * });
         * // DiskStorage.dataStoreName = 'recordRTC';
         * // DiskStorage.onError = function(error) { };
         * @property {function} init - This method must be called once to initialize IndexedDB ObjectStore. Though, it is auto-used internally.
         * @property {function} Fetch - This method fetches stored blobs from IndexedDB.
         * @property {function} Store - This method stores blobs in IndexedDB.
         * @property {function} onError - This function is invoked for any known/unknown error.
         * @property {string} dataStoreName - Name of the ObjectStore created in IndexedDB storage.
         * @see {@link https://github.com/muaz-khan/RecordRTC|RecordRTC Source Code}
         */
        var N = {
          init: function () {
            function e(e) {
              e.createObjectStore(n.dataStoreName);
            }
            function t() {
              function e(e) {
                t.objectStore(n.dataStoreName).get(e).onsuccess = function (t) {
                  n.callback && n.callback(t.target.result, e);
                };
              }
              var t = i.transaction([n.dataStoreName], 'readwrite');
              n.videoBlob && t.objectStore(n.dataStoreName).put(n.videoBlob, 'videoBlob'),
                n.gifBlob && t.objectStore(n.dataStoreName).put(n.gifBlob, 'gifBlob'),
                n.audioBlob && t.objectStore(n.dataStoreName).put(n.audioBlob, 'audioBlob'),
                e('audioBlob'),
                e('videoBlob'),
                e('gifBlob');
            }
            var n = this;
            if ('undefined' == typeof indexedDB || void 0 === indexedDB.open)
              return void console.error('IndexedDB API are not available in this browser.');
            var i,
              o = this.dbName || location.href.replace(/\/|:|#|%|\.|\[|\]/g, ''),
              a = indexedDB.open(o, 1);
            (a.onerror = n.onError),
              (a.onsuccess = function () {
                if (((i = a.result), (i.onerror = n.onError), i.setVersion))
                  if (1 !== i.version) {
                    var o = i.setVersion(1);
                    o.onsuccess = function () {
                      e(i), t();
                    };
                  } else t();
                else t();
              }),
              (a.onupgradeneeded = function (t) {
                e(t.target.result);
              });
          },
          Fetch: function (e) {
            return (this.callback = e), this.init(), this;
          },
          Store: function (e) {
            return (
              (this.audioBlob = e.audioBlob),
              (this.videoBlob = e.videoBlob),
              (this.gifBlob = e.gifBlob),
              this.init(),
              this
            );
          },
          onError: function (e) {
            console.error(JSON.stringify(e, null, '\t'));
          },
          dataStoreName: 'recordRTC',
          dbName: null,
        };
        void 0 !== o && (o.DiskStorage = N),
          void 0 !== o && (o.GifRecorder = y),
          void 0 === o &&
            ((e.exports = w),
            (T = []),
            void 0 !==
              (S = function () {
                return w;
              }.apply(t, T)) && (e.exports = S)),
          void 0 !== o && (o.MultiStreamRecorder = C),
          void 0 !== o && (o.RecordRTCPromisesHandler = R),
          void 0 !== o && (o.WebAssemblyRecorder = k);
      }.call(t, n(18), n(19)));
    },
    function (e, t) {
      var n;
      n = (function () {
        return this;
      })();
      try {
        n = n || Function('return this')() || (0, eval)('this');
      } catch (e) {
        'object' == typeof window && (n = window);
      }
      e.exports = n;
    },
    function (e, t) {
      function n() {
        throw new Error('setTimeout has not been defined');
      }
      function i() {
        throw new Error('clearTimeout has not been defined');
      }
      function o(e) {
        if (u === setTimeout) return setTimeout(e, 0);
        if ((u === n || !u) && setTimeout) return (u = setTimeout), setTimeout(e, 0);
        try {
          return u(e, 0);
        } catch (t) {
          try {
            return u.call(null, e, 0);
          } catch (t) {
            return u.call(this, e, 0);
          }
        }
      }
      function a(e) {
        if (l === clearTimeout) return clearTimeout(e);
        if ((l === i || !l) && clearTimeout) return (l = clearTimeout), clearTimeout(e);
        try {
          return l(e);
        } catch (t) {
          try {
            return l.call(null, e);
          } catch (t) {
            return l.call(this, e);
          }
        }
      }
      function r() {
        g && m && ((g = !1), m.length ? (p = m.concat(p)) : (h = -1), p.length && d());
      }
      function d() {
        if (!g) {
          var e = o(r);
          g = !0;
          for (var t = p.length; t; ) {
            for (m = p, p = []; ++h < t; ) m && m[h].run();
            (h = -1), (t = p.length);
          }
          (m = null), (g = !1), a(e);
        }
      }
      function s(e, t) {
        (this.fun = e), (this.array = t);
      }
      function c() {}
      var u,
        l,
        f = (e.exports = {});
      !(function () {
        try {
          u = 'function' == typeof setTimeout ? setTimeout : n;
        } catch (e) {
          u = n;
        }
        try {
          l = 'function' == typeof clearTimeout ? clearTimeout : i;
        } catch (e) {
          l = i;
        }
      })();
      var m,
        p = [],
        g = !1,
        h = -1;
      (f.nextTick = function (e) {
        var t = new Array(arguments.length - 1);
        if (arguments.length > 1)
          for (var n = 1; n < arguments.length; n++) t[n - 1] = arguments[n];
        p.push(new s(e, t)), 1 !== p.length || g || o(d);
      }),
        (s.prototype.run = function () {
          this.fun.apply(null, this.array);
        }),
        (f.title = 'browser'),
        (f.browser = !0),
        (f.env = {}),
        (f.argv = []),
        (f.version = ''),
        (f.versions = {}),
        (f.on = c),
        (f.addListener = c),
        (f.once = c),
        (f.off = c),
        (f.removeListener = c),
        (f.removeAllListeners = c),
        (f.emit = c),
        (f.prependListener = c),
        (f.prependOnceListener = c),
        (f.listeners = function (e) {
          return [];
        }),
        (f.binding = function (e) {
          throw new Error('process.binding is not supported');
        }),
        (f.cwd = function () {
          return '/';
        }),
        (f.chdir = function (e) {
          throw new Error('process.chdir is not supported');
        }),
        (f.umask = function () {
          return 0;
        });
    },
    function (e, t, n) {
      'use strict';
      function i(e, t) {
        t = t || window.$;
        var n = {
          name: 'emotionsratings',
          title: 'Emotions Ratings',
          iconName: 'icon-emotionsratings',
          widgetIsLoaded: function () {
            return 'function' == typeof t && !!t.fn.emotionsRating;
          },
          defaultJSON: { choices: [1, 2, 3, 4, 5] },
          isFit: function (e) {
            return 'emotionsratings' === e.getType();
          },
          isDefaultRender: !1,
          htmlTemplate: "<div style='height: 33px'></div>",
          activatedByChanged: function (t) {
            e.JsonObject.metaData.addClass(
              'emotionsratings',
              [
                { name: 'hasOther', visible: !1 },
                { name: 'otherText', visible: !1 },
                { name: 'optionsCaption', visible: !1 },
                { name: 'otherErrorText', visible: !1 },
                { name: 'storeOthersAsComment', visible: !1 },
                { name: 'renderAs', visible: !1 },
              ],
              null,
              'dropdown'
            ),
              e.JsonObject.metaData.addProperties('emotionsratings', [
                {
                  name: 'emotions:itemvalues',
                  category: 'emotions',
                  categoryIndex: 1,
                  default: ['angry', 'disappointed', 'meh', 'happy', 'inLove'],
                },
                { name: 'emotionSize:number', category: 'emotions', default: 30 },
                { name: 'emotionsCount:number', category: 'emotions', default: 5 },
                { name: 'bgEmotion', category: 'emotions', default: 'happy' },
                { name: 'emotionColor', category: 'emotions', default: '#FF0066' },
              ]);
          },
          afterRender: function (e, n) {
            function i() {
              (n.innerHTML = '<div></div>'),
                t(n).off(),
                (a.initialRating = e.value || 0),
                (a.disabled = e.isReadOnly),
                t(n).find('div').emotionsRating(a);
            }
            var o = (e.emotions || []).map(function (e) {
              return e.value;
            });
            0 === o.length && (o = ['angry', 'disappointed', 'meh', 'happy', 'inLove']);
            var a = {
              emotionSize: e.emotionSize,
              bgEmotion: e.bgEmotion,
              emotions: o,
              initialRating: e.value,
              color: e.emotionColor,
              count: e.emotionsCount,
              onUpdate: function (t) {
                e.value = t;
              },
            };
            i(), (e.valueChangedCallback = i), (e.readOnlyChangedCallback = i);
          },
          willUnmount: function (e, n) {
            (n.innerHTML = null),
              t(n).off(),
              (e.readOnlyChangedCallback = null),
              (e.valueChangedCallback = null);
          },
          pdfQuestionType: 'dropdown',
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(n, 'customtype');
      }
      Object.defineProperty(t, '__esModule', { value: !0 }),
        'undefined' != typeof Survey && i(Survey, window.$),
        (t.default = i);
    },
    function (e, t, n) {
      'use strict';
      Object.defineProperty(t, '__esModule', { value: !0 });
      var i = n(0);
      n.d(t, 'icheck', function () {
        return i.default;
      });
      var o = n(1);
      n.d(t, 'select2', function () {
        return o.default;
      });
      var a = n(2);
      n.d(t, 'inputmask', function () {
        return a.default;
      });
      var r = n(4);
      n.d(t, 'jquerybarrating', function () {
        return r.default;
      });
      var d = n(5);
      n.d(t, 'jqueryuidatepicker', function () {
        return d.default;
      });
      var s = n(6);
      n.d(t, 'nouislider', function () {
        return s.default;
      });
      var c = n(8);
      n.d(t, 'select2tagbox', function () {
        return c.default;
      });
      var u = n(9);
      n.d(t, 'sortablejs', function () {
        return u.default;
      });
      var l = n(11);
      n.d(t, 'ckeditor', function () {
        return l.default;
      });
      var f = n(12);
      n.d(t, 'autocomplete', function () {
        return f.default;
      });
      var m = n(13);
      n.d(t, 'prettycheckbox', function () {
        return m.default;
      });
      var p = n(14);
      n.d(t, 'bootstrapslider', function () {
        return p.default;
      });
      var g = n(16);
      n.d(t, 'microphone', function () {
        return g.default;
      });
      var h = n(20);
      n.d(t, 'emotionsratings', function () {
        return h.default;
      });
      var v = n(22);
      n.d(t, 'bootstrapdatepicker', function () {
        return v.a;
      });
    },
    function (e, t, n) {
      'use strict';
      function i(e, t) {
        (t = t || window.$),
          !t.fn.bootstrapDP &&
            t.fn.datepicker &&
            t.fn.datepicker.noConflict &&
            ((t.fn.bootstrapDP = t.fn.datepicker.noConflict()),
            t.fn.datepicker || (t.fn.datepicker = t.fn.bootstrapDP));
        var n = {
          name: 'bootstrapdatepicker',
          title: 'Date picker',
          iconName: 'icon-datepicker',
          widgetIsLoaded: function () {
            return !!t.fn.bootstrapDP;
          },
          isFit: function (e) {
            return 'bootstrapdatepicker' === e.getType();
          },
          htmlTemplate:
            "<input class='form-control widget-datepicker' type='text' style='width: 100%;'>",
          activatedByChanged: function (t) {
            e.JsonObject.metaData.addClass(
              'bootstrapdatepicker',
              [
                { name: 'inputType', visible: !1 },
                { name: 'inputFormat', visible: !1 },
                { name: 'inputMask', visible: !1 },
              ],
              null,
              'text'
            ),
              e.JsonObject.metaData.addProperties('bootstrapdatepicker', [
                { name: 'dateFormat', category: 'general', default: 'mm/dd/yyyy' },
                { name: 'startDate', category: 'general', default: '' },
                { name: 'endDate', category: 'general', default: '' },
                { name: 'todayHighlight:boolean', category: 'general', default: !0 },
                { name: 'weekStart:number', category: 'general', default: 0 },
                { name: 'clearBtn:boolean', category: 'general', default: !1 },
                { name: 'autoClose:boolean', category: 'general', default: !0 },
                { name: 'daysOfWeekHighlighted:string', category: 'general', default: '' },
                { name: 'disableTouchKeyboard:boolean', category: 'general', default: !0 },
              ]);
          },
          afterRender: function (e, n) {
            var i = t(n).is('.widget-datepicker') ? t(n) : t(n).find('.widget-datepicker'),
              o = i
                .bootstrapDP({
                  enableOnReadonly: !1,
                  format: e.dateFormat,
                  startDate: e.startDate,
                  endDate: e.endDate,
                  todayHighlight: e.todayHighlight,
                  weekStart: e.weekStart,
                  clearBtn: e.clearBtn,
                  autoclose: e.autoClose,
                  daysOfWeekHighlighted: e.daysOfWeekHighlighted,
                  disableTouchKeyboard: e.disableTouchKeyboard,
                })
                .on('change', function (t) {
                  var n = o.bootstrapDP('getUTCDate'),
                    i = n && n.toUTCString();
                  e.value != i && (e.value = i);
                });
            (e.valueChangedCallback = function () {
              o.bootstrapDP('setUTCDate', e.value ? new Date(e.value) : '');
            }),
              e.valueChangedCallback(),
              (e.readOnlyChangedCallback = function () {
                e.isReadOnly ? i.prop('readonly', !0) : i.removeAttr('readonly');
              }),
              e.readOnlyChangedCallback();
          },
          willUnmount: function (e, n) {
            (t(n).is('.widget-datepicker') ? t(n) : t(n).find('.widget-datepicker')).bootstrapDP(
              'destroy'
            ),
              (e.readOnlyChangedCallback = void 0),
              (e.valueChangedCallback = void 0);
          },
          pdfQuestionType: 'text',
        };
        e.CustomWidgetCollection.Instance.addCustomWidget(n, 'customtype');
      }
      'undefined' != typeof Survey && i(Survey, window.$), (t.a = i);
    },
  ]);
});
//# sourceMappingURL=surveyjs-widgets.min.js.map
